/* ============================================================================
   APP.JS - Memora Main Application
   ============================================================================
   
   TABLE OF CONTENTS (Ctrl+G to jump to line number)
   ──────────────────────────────────────────────────
   
   SECTION 1: CORE UTILITIES & STATE (~1-710)
   ├── Utilities ($, helpers)                    ~1-80
   ├── Audio Visualizer                          ~80-315
   ├── IndexedDB Helper (IDB)                    ~350-480
   ├── Global State Variables                    ~480-600
   └── DOM Element References (els)              ~600-710
   
   SECTION 2: INITIALIZATION (~710-770)
   └── init() function
   
   SECTION 3: GAMIFICATION (~770-1450)
   ├── XP & Achievement Definitions              ~510-570
   ├── loadGamificationData / saveGamificationData
   ├── awardXP, toasts, celebrations
   ├── Mystery Card System
   ├── Power-ups
   └── Achievements Modal
   
   SECTION 4: FILE IMPORT & SESSION (~1450-1900)
   ├── Universal Import Handler                  ~1450-1560
   ├── SessionManager (save, load, history)      ~1560-1800
   ├── startNewSession, enterQuizMode
   └── SRS Algorithm (SM-2)
   
   SECTION 5: SOURCE PANEL & EVIDENCE (~1900-2100)
   ├── updateSourceDropdown
   ├── Evidence item creation
   ├── Connection line drawing
   └── Drag & resize handlers
   
   SECTION 6: AI TOOLS (~2100-2800)
   ├── AI Dropdown handlers
   ├── openAI (ChatGPT/Gemini)
   ├── Quiz Generation from PDF
   └── Deep Dive explanations
   
   SECTION 7: THEME SYSTEM (~2800-3200)
   ├── Gradient presets
   ├── Color pickers
   ├── Ambient Mode
   ├── Border Mode
   ├── Zen Mode
   └── Theme persistence
   
   SECTION 8: QUIZ LOGIC (~3200-4500)
   ├── Keyboard shortcuts                        ~3200-3400
   ├── showQuiz / showFlashcards                 ~3460
   ├── Flashcard Grid                            ~3470-3500
   ├── Saved Questions Modal                     ~3500-3700
   ├── renderQuestion                            ~3800-4000
   ├── handleAnswer                              ~4000-4140
   ├── Results Screen                            ~4250-4480
   └── toggleViewer, toggleTrio                  ~4480-4700
   
   SECTION 9: UI & LAYOUT (~4700-7617)
   ├── Panel resize system
   ├── Gestures (swipe, touch)
   ├── Stats Modal
   ├── Global Fullscreen
   └── Document event listeners
   
   ============================================================================ */

/* --- UTILS --- */
const $ = id => document.getElementById(id);

// All modal-like surfaces use this coordinator for top-layer ownership. The
// registry is deliberately stored behind one window property because the
// classic-script bundles share a single global scope.
window.MemoraModalCoordinator = (() => {
    const entries = new Map();
    const order = [];
    const focusableSelector = [
        'a[href]',
        'button:not([disabled])',
        'input:not([disabled])',
        'select:not([disabled])',
        'textarea:not([disabled])',
        '[contenteditable="true"]',
        '[tabindex]:not([tabindex="-1"])'
    ].join(',');

    const isConnected = (element) => !!element
        && (element.isConnected !== false)
        && (!!document.documentElement?.contains?.(element) || !!document.body?.contains?.(element));

    const resolveFocusable = (element) => {
        if (!element || !isConnected(element)) return [];
        return Array.from(element.querySelectorAll?.(focusableSelector) || [])
            .filter((candidate) => {
                if (!isConnected(candidate) || candidate.disabled || candidate.tabIndex < 0) return false;
                if (candidate.closest?.('[hidden], [inert], [aria-hidden="true"]')) return false;
                if (!Array.from(candidate.getClientRects?.() || []).some(rect => rect.width > 0 && rect.height > 0)) return false;
                const style = window.getComputedStyle?.(candidate);
                return style?.display !== 'none' && style?.visibility !== 'hidden'
                    && style?.visibility !== 'collapse' && style?.opacity !== '0';
            });
    };

    const isEntryActive = (entry) => {
        if (!entry?.element || !isConnected(entry.element)) return false;
        if (typeof entry.isOpen === 'function') return !!entry.isOpen(entry);
        if (entry.element.hidden) return false;
        const style = window.getComputedStyle?.(entry.element);
        return entry.element.classList?.contains('active')
            || (style?.display !== 'none' && style?.visibility !== 'hidden' && Number(style?.opacity || 1) > 0.01);
    };

    const getOpenEntries = () => order
        .map((id) => entries.get(id))
        .filter((entry) => {
            if (!entry?.open) return false;
            if (!isEntryActive(entry)) {
                entry.open = false;
                return false;
            }
            return true;
        });

    const focusEntry = (entry) => {
        if (!entry?.element || !isConnected(entry.element)) return false;
        if (typeof entry.focus === 'function') {
            try {
                if (entry.focus(entry) !== false) return true;
            } catch (_error) { }
        }
        const focusables = resolveFocusable(entry.element);
        // A dialog may name the control it opens on (a key input rather than Close).
        const initial = typeof entry.initialFocus === 'function' ? entry.initialFocus(entry) : entry.initialFocus;
        const target = (initial && focusables.includes(initial) ? initial : null) || focusables[0] || entry.element;
        try {
            target.focus({ preventScroll: true });
        } catch (_error) {
            target.focus?.();
        }
        return true;
    };

    const restoreFocus = (entry) => {
        if (entry?.restoreFocus === false) return false;
        const opener = entry?.opener;
        if (isConnected(opener)
            && opener !== document.body
            && opener !== document.documentElement
            && !opener.disabled) {
            try {
                opener.focus({ preventScroll: true });
            } catch (_error) {
                opener.focus?.();
            }
            return true;
        }

        const parent = entries.get(entry?.parentLayerId);
        return focusEntry(parent);
    };

    const scheduleFocus = (entry) => {
        const focus = () => {
            if (entry.open && isEntryActive(entry)) focusEntry(entry);
        };
        if (typeof window.requestAnimationFrame === 'function') window.requestAnimationFrame(focus);
        else setTimeout(focus, 0);
    };

    const register = (config = {}) => {
        const id = String(config.id || '').trim();
        if (!id) return null;
        const existing = entries.get(id);
        const entry = existing || {
            id,
            element: null,
            opener: null,
            close: () => true,
            restoreFocus: true,
            parentLayerId: null,
            trapFocus: true,
            isOpen: null,
            open: false
        };
        Object.keys(config).forEach((key) => {
            if (config[key] !== undefined) entry[key] = config[key];
        });
        entries.set(id, entry);
        if (!order.includes(id)) order.push(id);
        return entry;
    };

    const open = (config = {}, options = {}) => {
        const normalized = typeof config === 'string' ? { ...options, id: config } : config;
        const entry = register(normalized);
        if (!entry) return false;
        if (!options.preserveOpener && normalized.opener !== undefined) {
            entry.opener = normalized.opener;
        } else if (!options.preserveOpener && !entry.open) {
            entry.opener = document.activeElement;
        }
        entry.open = true;
        const index = order.indexOf(entry.id);
        if (index >= 0) order.splice(index, 1);
        order.push(entry.id);
        scheduleFocus(entry);
        return true;
    };

    const update = (id, changes = {}) => {
        const entry = entries.get(String(id || '').trim());
        if (!entry) return false;
        Object.keys(changes).forEach((key) => {
            if (changes[key] !== undefined) entry[key] = changes[key];
        });
        return true;
    };

    const markClosed = (id, { restore = false } = {}) => {
        const entry = entries.get(String(id || '').trim());
        if (!entry) return false;
        entry.open = false;
        if (restore) restoreFocus(entry);
        return true;
    };

    const close = (id, reason = 'programmatic', options = {}) => {
        const entry = entries.get(String(id || '').trim());
        if (!entry || !entry.open || !isEntryActive(entry)) {
            if (entry) entry.open = false;
            return false;
        }
        const result = typeof entry.close === 'function'
            ? entry.close(reason, options)
            : true;
        if (result === false || result?.closed === false) return true;
        entry.open = false;
        if (options.restoreFocus !== false) restoreFocus(entry);
        return true;
    };

    const closeTop = (reason = 'escape', options = {}) => {
        const top = getOpenEntries().at(-1);
        return top ? close(top.id, reason, options) : false;
    };

    const isOpen = (id) => getOpenEntries().some((entry) => entry.id === String(id || '').trim());

    const handleKeydown = (event) => {
        const top = getOpenEntries().at(-1);
        if (!top) return false;

        if (event.key === 'Escape') {
            const savedExportMenu = top.id === 'saved-modal' ? document.getElementById('saved-export-menu') : null;
            if (savedExportMenu?.open) {
                event.preventDefault();
                savedExportMenu.open = false;
                event.stopImmediatePropagation();
                return true;
            }
            event.preventDefault();
            const owned = closeTop('escape');
            if (owned) event.stopImmediatePropagation();
            return owned;
        }

        if (event.key !== 'Tab' || top.trapFocus === false) return false;
        const focusables = resolveFocusable(top.element);
        if (!focusables.length) {
            event.preventDefault();
            focusEntry(top);
            event.stopImmediatePropagation();
            return true;
        }
        const current = document.activeElement;
        const currentIndex = focusables.indexOf(current);
        const nextIndex = event.shiftKey
            ? (currentIndex <= 0 ? focusables.length - 1 : currentIndex - 1)
            : (currentIndex < 0 || currentIndex === focusables.length - 1 ? 0 : currentIndex + 1);
        event.preventDefault();
        // Enhanced selects and controls being replaced can refuse focus. Skip them.
        for (let offset = 0; offset < focusables.length; offset += 1) {
            const index = (nextIndex + (event.shiftKey ? -offset : offset) + focusables.length) % focusables.length;
            focusables[index]?.focus?.({ preventScroll: true });
            if (document.activeElement === focusables[index]) break;
        }
        event.stopImmediatePropagation();
        return true;
    };

    document.addEventListener('keydown', handleKeydown, true);
    document.addEventListener('focusin', (event) => {
        const top = getOpenEntries().at(-1);
        if (!top || top.trapFocus === false || top.element?.contains?.(event.target)) return;
        focusEntry(top);
    }, true);

    return {
        register,
        open,
        update,
        close,
        closeTop,
        markClosed,
        isOpen,
        focus: (id) => focusEntry(entries.get(String(id || '').trim())),
        getStack: () => getOpenEntries().map((entry) => entry.id)
    };
})();
const DEMO_QUERY = new URLSearchParams(window.location.search || '');
const IS_EMBED_DEMO = (() => {
    const raw = (DEMO_QUERY.get('demo') || '').trim().toLowerCase();
    return raw === '1' || raw === 'true' || raw === 'yes' || raw === 'on';
})();
const SMOOTH_MODE_STORAGE_KEY = 'memora.smooth_mode_v1';

function getStoredSmoothModePreference() {
    if (IS_EMBED_DEMO) return null;
    try {
        const value = localStorage.getItem(SMOOTH_MODE_STORAGE_KEY);
        return value === '1' || value === '0' ? value : null;
    } catch (_) {
        return null;
    }
}

function getDefaultSmoothModeEnabled() {
    const stored = getStoredSmoothModePreference();
    if (stored !== null) return stored === '1';
    return !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
}

function isSmoothModeEnabled() {
    return !!document.body?.classList.contains('smooth-mode');
}

function syncSmoothModeToggleUi() {
    const button = $('smooth-mode-toggle');
    const label = $('smooth-mode-toggle-label');
    const enabled = isSmoothModeEnabled();
    if (button) {
        button.setAttribute('aria-pressed', String(enabled));
        button.title = enabled
            ? 'Smooth Mode is on. Use fewer motion and heavy visual effects.'
            : 'Turn on Smooth Mode to reduce motion and heavy visual effects.';
    }
    if (label) label.textContent = enabled ? 'ON' : 'OFF';
}

function setSmoothMode(enabled, { persist = true } = {}) {
    const nextEnabled = !!enabled;
    document.body?.classList.toggle('smooth-mode', nextEnabled);
    syncMemoraMotionMode();
    if (persist && !IS_EMBED_DEMO) {
        try {
            localStorage.setItem(SMOOTH_MODE_STORAGE_KEY, nextEnabled ? '1' : '0');
        } catch (_) {
            // The visual preference still applies when storage is unavailable.
        }
    }
    syncSmoothModeToggleUi();
    document.dispatchEvent(new CustomEvent('memora:smooth-mode-changed', {
        detail: { enabled: nextEnabled }
    }));
    return nextEnabled;
}

function bindSmoothModeToggle() {
    const button = $('smooth-mode-toggle');
    if (!button || button.dataset.smoothModeBound === '1') return;
    button.dataset.smoothModeBound = '1';
    button.addEventListener('click', () => {
        setSmoothMode(!isSmoothModeEnabled());
    });
    syncSmoothModeToggleUi();
}

// Motion policy: one effective mode for CSS, WAAPI and frame-driven work (tokens in base.css).
// Reduced - the OS preference or the app's Reduce Motion - always wins over Smooth Mode's calm
// presentation, even when both stored preferences are off.
const MEMORA_MOTION_REDUCED_QUERY = (() => {
    try {
        return window.matchMedia?.('(prefers-reduced-motion: reduce)') || null;
    } catch (_) {
        return null;
    }
})();
let memoraMotionMode = '';
let memoraMotionDurations = null;

function resolveMemoraMotionMode() {
    const body = document.body;
    if (MEMORA_MOTION_REDUCED_QUERY?.matches || body?.classList.contains('a11y-reduce-motion')) return 'reduced';
    return body?.classList.contains('smooth-mode') ? 'calm' : 'standard';
}

function syncMemoraMotionMode() {
    const nextMode = resolveMemoraMotionMode();
    if (nextMode === memoraMotionMode) return nextMode;
    const previousMode = memoraMotionMode;
    memoraMotionMode = nextMode;
    memoraMotionDurations = null;
    document.documentElement.dataset.memoraMotion = nextMode;
    // Owners with presentation work in flight finish it and commit their final state.
    if (previousMode) {
        document.dispatchEvent(new CustomEvent('memora:motion-mode-changed', {
            detail: { mode: nextMode, previousMode }
        }));
    }
    return nextMode;
}

function getMemoraMotionMode() {
    return memoraMotionMode || syncMemoraMotionMode();
}

// Calm keeps fades but drops large structural travel; reduced allows no spatial motion at all.
function memoraMotionAllowsSpatial(family = '') {
    const mode = getMemoraMotionMode();
    if (mode === 'reduced') return false;
    return !(mode === 'calm' && family === 'structural');
}

// Milliseconds for a family and phase, e.g. ('modal', 'in'), ('press', ''), ('expand', 'far').
// Read from the resolved base.css tokens, so a JS timer can never outlast the CSS it guards.
function getMemoraMotionDuration(family, phase = 'in') {
    if (getMemoraMotionMode() === 'reduced') return 0;
    const key = phase ? `${family}-${phase}` : String(family);
    if (!memoraMotionDurations) memoraMotionDurations = new Map();
    if (!memoraMotionDurations.has(key)) {
        const style = getComputedStyle(document.documentElement);
        const raw = String(style.getPropertyValue(`--memora-motion-${key}`)
            || style.getPropertyValue(`--memora-motion-${family}`) || '').trim();
        const value = Number.parseFloat(raw);
        const ms = !Number.isFinite(value) ? 0 : (/[^m]s$/.test(raw) ? value * 1000 : value);
        memoraMotionDurations.set(key, ms);
    }
    return memoraMotionDurations.get(key);
}

// The easing tokens are fixed in base.css, so each is read once: reading one inside a pane click
// forced a style flush in the middle of the transition setup.
const memoraMotionEasings = new Map();
function getMemoraMotionEasing(kind = 'out') {
    if (memoraMotionEasings.has(kind)) return memoraMotionEasings.get(kind);
    const raw = String(getComputedStyle(document.documentElement).getPropertyValue(`--memora-motion-ease-${kind}`) || '').trim();
    if (raw) memoraMotionEasings.set(kind, raw);
    return raw || 'linear';
}

// The shared soft fade for a surface that has just landed (a destination, a module switch). It fades
// from transparent to the surface's own opacity, so it composes with whatever the owner sets.
function memoraMotionFadeIn(node, { delay = 0, family = 'landing' } = {}) {
    if (!node || typeof node.animate !== 'function' || getMemoraMotionMode() === 'reduced') return null;
    // 'backwards' holds the transparent start through any delay, so a staggered row never flashes.
    return node.animate([{ opacity: 0 }, {}], {
        duration: getMemoraMotionDuration(family, 'in'),
        delay,
        easing: getMemoraMotionEasing('soft'),
        fill: 'backwards'
    });
}

window.MemoraMotion = {
    mode: getMemoraMotionMode,
    isReduced: () => getMemoraMotionMode() === 'reduced',
    allowsSpatial: memoraMotionAllowsSpatial,
    duration: getMemoraMotionDuration,
    easing: getMemoraMotionEasing,
    fadeIn: memoraMotionFadeIn
};
MEMORA_MOTION_REDUCED_QUERY?.addEventListener?.('change', syncMemoraMotionMode);

window.MemoraSmoothMode = {
    isEnabled: isSmoothModeEnabled,
    set: setSmoothMode
};
setSmoothMode(getDefaultSmoothModeEnabled(), { persist: false });
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bindSmoothModeToggle, { once: true });
} else {
    bindSmoothModeToggle();
}

function fireSessionConfetti(options = {}) {
    const nextOptions = isSmoothModeEnabled()
        ? {
            ...options,
            particleCount: Math.min(18, Math.max(8, Math.round(Number(options.particleCount) || 12))),
            spread: Math.min(42, Number(options.spread) || 32),
            scalar: Math.min(0.78, Number(options.scalar) || 0.78),
            disableForReducedMotion: true
        }
        : options;
    const launch = () => {
        if (typeof window.confetti !== 'function') return false;
        try {
            window.confetti(nextOptions);
            return true;
        } catch (_error) {
            return false;
        }
    };
    if (launch()) return;
    void window.MemoraRuntime.ensureConfettiLoaded()
        .then(launch)
        .catch((error) => console.warn('Confetti could not be loaded:', error));
}

function demoUnavailableFeature(featureLabel = 'This feature') {
    // The embedded product walkthrough intentionally exercises local, deterministic
    // stand-ins for AI surfaces. Do not surface the production "AI requests are
    // disabled" toast when a background route is fail-closed in demo mode.
    if (IS_EMBED_DEMO && /^AI requests$/i.test(String(featureLabel || '').trim())) return;
    showPopup(`${featureLabel} is disabled in demo mode. Download Memoraful for full access.`, null, true);
}

/* --- SYSTEM AUDIO VISUALIZER (SCREEN SHARE API) --- */
let audioCtx, analyser, dataArray, source, stream;
let isSysSyncActive = false;
let visualizerFrame;
let lastBeatTime = 0;
let currentThemeIndex = 0;
// Beat Color Shift Variables
let beatColors = [];
let beatColorStage = 0;
let beatColorT = 0;
let beatPaletteThemeKey = '';
let visualAutoGain = 1;
let visualEnergyEMA = 0.2;
let visualBassEMA = 0.2;
let visualBeatPulse = 0;
let visualBarLevels = [];
let visualBarCaps = [];

// PDF Text Index (disabled - Smart Saver removed)
const pdfTextIndex = {}; // Intentionally left empty; PDF text search indexing is disabled.
// Smart Saver removed; keep effects on by default.
const SMART_SAVER_MODE_KEY = 'isMemorySaver';
let isMemorySaver = false;

// The lazy PDF runtime loader configures the PDF.js worker when PDF intent is detected.

// Coalesces high-frequency drag/resize mousemove work to one update per animation
// frame (perf: avoids layout thrash on >60Hz pointing devices). Callers must keep
// their own "is active" guard in the raw event listener so idle mousemove stays free,
// and must call .flush() at drag end (while drag state is still active) so the final
// sub-frame delta is never dropped.
function createRafDragThrottle(handler) {
    let rafId = 0;
    let lastEvent = null;
    const run = () => {
        rafId = 0;
        const evt = lastEvent;
        lastEvent = null;
        if (evt) handler(evt);
    };
    const throttled = (event) => {
        lastEvent = event;
        if (!rafId) rafId = window.requestAnimationFrame(run);
    };
    throttled.flush = () => {
        if (!rafId) return;
        window.cancelAnimationFrame(rafId);
        run();
    };
    throttled.cancel = () => {
        if (rafId) window.cancelAnimationFrame(rafId);
        rafId = 0;
        lastEvent = null;
    };
    return throttled;
}

const PDF_CANVAS_READBACK_CONTEXT_OPTIONS = Object.freeze({ willReadFrequently: true });

function getPdfCanvas2dContextAttributes(options = {}) {
    if (options && typeof options === 'object') {
        if (options.willReadFrequently === true) return options;
        return { ...options, willReadFrequently: true };
    }
    return PDF_CANVAS_READBACK_CONTEXT_OPTIONS;
}

function createPdfJsReadbackCanvasFactory(ownerDocument = document) {
    const doc = ownerDocument || document;
    return {
        create(width, height) {
            if (width <= 0 || height <= 0) {
                throw new Error('Invalid canvas size');
            }
            const canvas = doc.createElement('canvas');
            canvas.width = width;
            canvas.height = height;
            return {
                canvas,
                context: canvas.getContext('2d', getPdfCanvas2dContextAttributes())
            };
        },
        reset(canvasAndContext, width, height) {
            if (!canvasAndContext?.canvas) {
                throw new Error('Canvas is not specified');
            }
            if (width <= 0 || height <= 0) {
                throw new Error('Invalid canvas size');
            }
            canvasAndContext.canvas.width = width;
            canvasAndContext.canvas.height = height;
            canvasAndContext.context = canvasAndContext.canvas.getContext('2d', getPdfCanvas2dContextAttributes());
        },
        destroy(canvasAndContext) {
            if (!canvasAndContext?.canvas) {
                throw new Error('Canvas is not specified');
            }
            canvasAndContext.canvas.width = 0;
            canvasAndContext.canvas.height = 0;
            canvasAndContext.canvas = null;
            canvasAndContext.context = null;
        }
    };
}

function getPdfJsDocumentWithReadbackCanvas(src) {
    if (typeof pdfjsLib === 'undefined' || typeof pdfjsLib.getDocument !== 'function') {
        throw new Error('PDF.js library not loaded');
    }

    const canvasFactory = createPdfJsReadbackCanvasFactory(document);
    const runtimeUrls = window.MemoraRuntime?.resourceUrls || {};
    // An explicitly supplied worker is caller-owned in PDF.js: destroying one document
    // releases its transport without terminating the shared worker used by other documents.
    let sharedWorker = getPdfJsDocumentWithReadbackCanvas.sharedWorker;
    if (typeof pdfjsLib.PDFWorker === 'function'
        && (!sharedWorker || sharedWorker.destroyed || getPdfJsDocumentWithReadbackCanvas.workerLibrary !== pdfjsLib)) {
        sharedWorker = new pdfjsLib.PDFWorker();
        getPdfJsDocumentWithReadbackCanvas.sharedWorker = sharedWorker;
        getPdfJsDocumentWithReadbackCanvas.workerLibrary = pdfjsLib;
    }
    const pdfDefaults = {
        ...(sharedWorker ? { worker: sharedWorker } : {}),
        isEvalSupported: false,
        ...(runtimeUrls.pdfCMapUrl ? {
            cMapUrl: new URL(runtimeUrls.pdfCMapUrl, document.baseURI).href,
            cMapPacked: true
        } : {}),
        ...(runtimeUrls.pdfStandardFontDataUrl ? {
            standardFontDataUrl: new URL(runtimeUrls.pdfStandardFontDataUrl, document.baseURI).href
        } : {})
    };
    const options = src && typeof src === 'object' && !(src instanceof URL) && !(src instanceof ArrayBuffer) && !ArrayBuffer.isView(src)
        ? { ...pdfDefaults, ...src, canvasFactory: src.canvasFactory || canvasFactory, isEvalSupported: false }
        : typeof src === 'string' || src instanceof URL
            ? { ...pdfDefaults, url: src, canvasFactory, isEvalSupported: false }
            : { ...pdfDefaults, data: src, canvasFactory, isEvalSupported: false };
    const task = pdfjsLib.getDocument(options);
    task.__memoraSourceUrl = options.url ? String(options.url) : '';
    // Source swaps may destroy the viewer while Ask/Study Guide renders a borrowed page.
    // Stop new borrowers immediately, then let existing renders return their leases.
    const destroy = task.destroy.bind(task);
    task.destroy = async function () {
        task.__memoraClosing = true;
        if (task.__memoraImageUsers > 0) await task.__memoraImageIdle;
        return destroy();
    };
    return task;
}

function isPdfJsViewerCanvas(canvas) {
    return !!(canvas
        && typeof canvas.closest === 'function'
        && canvas.closest('#pdfjs-pages .page, #pdfjs-pages .canvasWrapper'));
}

function patchPdfJsViewerCanvasReadbackContexts() {
    const canvasProto = window.HTMLCanvasElement?.prototype;
    if (!canvasProto || typeof canvasProto.getContext !== 'function') return;
    if (canvasProto.__memoraPdfReadbackGetContextWrapped) return;

    const originalGetContext = canvasProto.getContext;
    canvasProto.getContext = function (type, options, ...args) {
        if (String(type || '').toLowerCase() === '2d' && isPdfJsViewerCanvas(this)) {
            return originalGetContext.call(this, type, getPdfCanvas2dContextAttributes(options), ...args);
        }
        return originalGetContext.call(this, type, options, ...args);
    };
    Object.defineProperty(canvasProto, '__memoraPdfReadbackGetContextWrapped', {
        value: true,
        configurable: false,
        enumerable: false,
        writable: false
    });
}

if (typeof window !== 'undefined') {
    window.getPdfCanvas2dContextAttributes = getPdfCanvas2dContextAttributes;
    window.createPdfJsReadbackCanvasFactory = createPdfJsReadbackCanvasFactory;
    window.getPdfJsDocumentWithReadbackCanvas = getPdfJsDocumentWithReadbackCanvas;
}


async function extractPdfText(file, filename) {
    console.log(`PDF text indexing disabled; skipping extraction for ${filename}.`);
}
function getRandomColor() {
    const letters = '0123456789ABCDEF';
    let color = '#';
    for (let i = 0; i < 6; i++) {
        color += letters[Math.floor(Math.random() * 16)];
    }
    return color;
}

function lerpColor(a, b, amount) {
    const ah = parseInt(a.replace(/#/g, ''), 16),
        bh = parseInt(b.replace(/#/g, ''), 16);

    const ar = ah >> 16, ag = ah >> 8 & 0xff, ab = ah & 0xff,
        br = bh >> 16, bg = bh >> 8 & 0xff, bb = bh & 0xff,
        rr = ar + amount * (br - ar),
        rg = ag + amount * (bg - ag),
        rb = ab + amount * (bb - ab);

    return '#' + ((1 << 24) + (Math.round(rr) << 16) + (Math.round(rg) << 8) + Math.round(rb)).toString(16).slice(1);
}

function normalizeHexColorForCanvas(raw, fallback) {
    const color = (raw || '').trim();
    if (/^#[0-9a-f]{8}$/i.test(color)) return color.slice(0, 7);
    if (/^#[0-9a-f]{6}$/i.test(color)) return color;
    return fallback;
}

function resolveVisualizerColors(useBeatColors = false) {
    const styles = getComputedStyle(document.documentElement);
    if (useBeatColors) {
        return {
            c1: normalizeHexColorForCanvas(styles.getPropertyValue('--beat-c1'), '#60a5fa'),
            c2: normalizeHexColorForCanvas(styles.getPropertyValue('--beat-c2'), '#f472b6')
        };
    }

    // Default visualizer palette follows the active theme gradient first.
    const g1 = normalizeHexColorForCanvas(styles.getPropertyValue('--g1'), '');
    const g2 = normalizeHexColorForCanvas(styles.getPropertyValue('--g2'), '');
    const amb1 = normalizeHexColorForCanvas(styles.getPropertyValue('--amb-c1'), '#60a5fa');
    const amb2 = normalizeHexColorForCanvas(styles.getPropertyValue('--amb-c2'), '#f472b6');
    return {
        c1: g1 || amb1,
        c2: g2 || amb2
    };
}

function getThemeBeatKey() {
    const styles = getComputedStyle(document.documentElement);
    const g1 = normalizeHexColorForCanvas(styles.getPropertyValue('--g1'), '#60a5fa');
    const g2 = normalizeHexColorForCanvas(styles.getPropertyValue('--g2'), '#f472b6');
    return `${g1}|${g2}`;
}

function mixHexColors(a, b, t) {
    const ratio = clampNum(Number(t), 0, 1);
    const ah = parseInt(a.replace(/#/g, ''), 16);
    const bh = parseInt(b.replace(/#/g, ''), 16);
    const ar = (ah >> 16) & 0xff;
    const ag = (ah >> 8) & 0xff;
    const ab = ah & 0xff;
    const br = (bh >> 16) & 0xff;
    const bg = (bh >> 8) & 0xff;
    const bb = bh & 0xff;
    const rr = Math.round(ar + ((br - ar) * ratio));
    const rg = Math.round(ag + ((bg - ag) * ratio));
    const rb = Math.round(ab + ((bb - ab) * ratio));
    return `#${[rr, rg, rb].map(v => v.toString(16).padStart(2, '0')).join('')}`;
}

function hexToHsl(hex) {
    const parsed = parseInt((hex || '#000000').replace('#', ''), 16);
    const r = ((parsed >> 16) & 255) / 255;
    const g = ((parsed >> 8) & 255) / 255;
    const b = (parsed & 255) / 255;
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    const l = (max + min) / 2;
    let h = 0;
    let s = 0;

    if (max !== min) {
        const d = max - min;
        s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
        switch (max) {
            case r: h = ((g - b) / d) + (g < b ? 6 : 0); break;
            case g: h = ((b - r) / d) + 2; break;
            default: h = ((r - g) / d) + 4; break;
        }
        h /= 6;
    }

    return { h: h * 360, s, l };
}

function hslToHex(h, s, l) {
    const hue = ((((h % 360) + 360) % 360) / 360);
    let r, g, b;
    if (s === 0) {
        r = g = b = l;
    } else {
        const hue2rgb = (p, q, t) => {
            let tt = t;
            if (tt < 0) tt += 1;
            if (tt > 1) tt -= 1;
            if (tt < 1 / 6) return p + (q - p) * 6 * tt;
            if (tt < 1 / 2) return q;
            if (tt < 2 / 3) return p + (q - p) * ((2 / 3) - tt) * 6;
            return p;
        };
        const q = l < 0.5 ? l * (1 + s) : (l + s - (l * s));
        const p = (2 * l) - q;
        r = hue2rgb(p, q, hue + (1 / 3));
        g = hue2rgb(p, q, hue);
        b = hue2rgb(p, q, hue - (1 / 3));
    }
    return `#${[r, g, b].map(v => Math.round(v * 255).toString(16).padStart(2, '0')).join('')}`;
}

function shiftHexHue(hex, degrees, satMul = 1, lightMul = 1) {
    const { h, s, l } = hexToHsl(hex);
    const ns = clampNum(s * satMul, 0.15, 0.95);
    const nl = clampNum(l * lightMul, 0.2, 0.78);
    return hslToHex(h + degrees, ns, nl);
}

function buildThemeBeatPalette() {
    const styles = getComputedStyle(document.documentElement);
    const g1 = normalizeHexColorForCanvas(styles.getPropertyValue('--g1'), '#60a5fa');
    const g2 = normalizeHexColorForCanvas(styles.getPropertyValue('--g2'), '#f472b6');
    const mid = mixHexColors(g1, g2, 0.5);
    const palette = [
        g1,
        shiftHexHue(g1, 14, 1.05, 1.02),
        mixHexColors(g1, g2, 0.22),
        mixHexColors(g1, g2, 0.4),
        mid,
        mixHexColors(g1, g2, 0.6),
        mixHexColors(g1, g2, 0.78),
        shiftHexHue(g2, -14, 1.05, 1.02),
        g2,
        shiftHexHue(mid, 24, 1.08, 1.0)
    ];
    const unique = [];
    const seen = new Set();
    palette.forEach((color) => {
        const key = normalizeHexColorForCanvas(color, '').toLowerCase();
        if (!key || seen.has(key)) return;
        seen.add(key);
        unique.push(key);
    });
    return unique.length >= 5 ? unique : [g1, mid, g2];
}

function clampNum(v, min, max) {
    return Math.min(max, Math.max(min, v));
}

function resetVisualizerDynamics() {
    visualAutoGain = 1;
    visualEnergyEMA = 0.2;
    visualBassEMA = 0.2;
    visualBeatPulse = 0;
    visualBarLevels = [];
    visualBarCaps = [];
    beatPaletteThemeKey = '';
}

/* The visualizer loop runs every frame during System Audio sync whether or not its canvas is
 * shown, and the live shell hides the canvas unless a visualizer setting is on. Reading
 * offsetWidth on each frame forced a synchronous layout whenever anything else had changed the
 * page. A ResizeObserver records the size after layout instead; a hidden canvas reports 0 x 0. */
let visualizerCanvasSizeObserver = null;
let visualizerCanvasObserved = null;
let visualizerCanvasObservedSize = null;

function getVisualizerCanvasCssSize(canvas) {
    if (canvas !== visualizerCanvasObserved && typeof ResizeObserver === 'function') {
        visualizerCanvasSizeObserver?.disconnect();
        visualizerCanvasObserved = canvas;
        visualizerCanvasObservedSize = null;
        visualizerCanvasSizeObserver = new ResizeObserver(() => {
            // Layout is current when the observer runs, so these reads do not force a reflow.
            visualizerCanvasObservedSize = {
                width: Math.max(0, Math.floor(canvas.offsetWidth || 0)),
                height: Math.max(0, Math.floor(canvas.offsetHeight || 0))
            };
        });
        visualizerCanvasSizeObserver.observe(canvas);
    }
    if (canvas === visualizerCanvasObserved && visualizerCanvasObservedSize) return visualizerCanvasObservedSize;
    return {
        width: Math.max(0, Math.floor(canvas.offsetWidth || 0)),
        height: Math.max(0, Math.floor(canvas.offsetHeight || 0))
    };
}

function isVisualizerCanvasRendered(canvas) {
    const size = getVisualizerCanvasCssSize(canvas);
    return size.width > 0 && size.height > 0;
}

function resizeVisualizerCanvas(canvas) {
    if (!canvas) return;
    const { width: nextWidth, height: nextHeight } = getVisualizerCanvasCssSize(canvas);
    if (nextWidth && nextHeight && (canvas.width !== nextWidth || canvas.height !== nextHeight)) {
        canvas.width = nextWidth;
        canvas.height = nextHeight;
    }
}

function syncAmbientVisualizerBodyFlags() {
    if (!document.body) return;
    document.body.classList.toggle('ambient-on', !!isAmbientMode);
    document.body.classList.toggle('visualizer-on', !!isSysSyncActive);
}

async function toggleSysSync() {
    const btn = $('sys-audio-sync-btn');
    const canvas = $('audio-visualizer');
    if (!btn || !canvas || !els.quizCard) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    if (isSysSyncActive) {
        // --- TURN OFF ---
        isSysSyncActive = false;
        syncAmbientVisualizerBodyFlags();

        // Stop all tracks to stop the "Sharing" indicator in browser
        if (stream) stream.getTracks().forEach(track => track.stop());
        if (audioCtx) audioCtx.close();
        if (visualizerFrame) { cancelAnimationFrame(visualizerFrame); visualizerFrame = 0; }
        resetVisualizerDynamics();

        btn.innerHTML = "🖥️ Sync System Audio";
        btn.classList.remove('active');

        // Reset Visuals
        els.quizCard.classList.remove('visualizer-active');
        els.quizCard.style.transform = '';
        els.quizCard.style.filter = '';
        canvas.style.transform = '';
        canvas.style.borderColor = '';
        canvas.style.background = '';
        canvas.style.boxShadow = '';
        ctx.clearRect(0, 0, canvas.width, canvas.height);

        if (isAmbientMode) applyAmbientMode();
        return;
    }

    try {
        // --- TURN ON ---
        // Request Screen Share with Audio
        stream = await navigator.mediaDevices.getDisplayMedia({
            video: true, // Video is required to get audio in most browsers
            audio: {
                echoCancellation: false,
                noiseSuppression: false,
                autoGainControl: false
            }
        });

        // Check if user actually shared audio
        if (stream.getAudioTracks().length === 0) {
            showPopupText("No audio detected. In the sharing pop-up, turn on 'Share system audio' (Windows) or 'Share tab audio' (Chrome), then try again.", null, true, { sticky: true, dismissible: true });
            stream.getTracks().forEach(t => t.stop());
            return;
        }

        audioCtx = new (window.AudioContext || window.webkitAudioContext)();
        analyser = audioCtx.createAnalyser();

        // Connect the Stream to the Analyzer
        source = audioCtx.createMediaStreamSource(stream);
        source.connect(analyser);

        // Setup Visualizer
        analyser.fftSize = 256;
        analyser.smoothingTimeConstant = 0.58;
        analyser.minDecibels = -92;
        analyser.maxDecibels = -14;
        const bufferLength = analyser.frequencyBinCount;
        dataArray = new Uint8Array(bufferLength);
        resetVisualizerDynamics();

        if (visualizerFrame) { cancelAnimationFrame(visualizerFrame); visualizerFrame = 0; }
        isSysSyncActive = true;
        syncAmbientVisualizerBodyFlags();
        btn.innerHTML = "🖥️ Stop Syncing";
        btn.classList.add('active');
        els.quizCard.classList.remove('visualizer-active');
        els.quizCard.style.transform = '';
        els.quizCard.style.filter = '';
        if (isAmbientMode) applyAmbientMode();
        resizeVisualizerCanvas(canvas);

        // Detect if user clicks "Stop Sharing" on the browser native bar
        stream.getVideoTracks()[0].onended = () => {
            if (isSysSyncActive) toggleSysSync(); // Auto-shutdown
        };

        visualizerLoop();

    } catch (err) {
        isSysSyncActive = false;
        resetVisualizerDynamics();
        syncAmbientVisualizerBodyFlags();
        btn.innerHTML = "🖥️ Sync System Audio";
        btn.classList.remove('active');
        canvas.style.transform = '';
        canvas.style.borderColor = '';
        canvas.style.background = '';
        canvas.style.boxShadow = '';
        if (isAmbientMode) applyAmbientMode();
        console.error("System Audio Error:", err);
    }
}

/* --- UPDATED VISUALIZER LOOP --- */
function visualizerLoop() {
    if (!isSysSyncActive || !analyser || !dataArray) return;
    visualizerFrame = requestAnimationFrame(visualizerLoop);

    const now = performance.now();
    analyser.getByteFrequencyData(dataArray);

    // 1. Energy + beat tracking (snappier sync with adaptive punch)
    const binCount = dataArray.length;
    const bassBins = Math.max(4, Math.floor(binCount * 0.08));
    const midEnd = Math.max(bassBins + 6, Math.floor(binCount * 0.35));
    let bassTotal = 0;
    let midTotal = 0;
    let highTotal = 0;

    for (let i = 0; i < binCount; i++) {
        const v = dataArray[i];
        if (i < bassBins) bassTotal += v;
        else if (i < midEnd) midTotal += v;
        else highTotal += v;
    }

    const bassAvg = bassTotal / Math.max(1, bassBins);
    const midAvg = midTotal / Math.max(1, midEnd - bassBins);
    const highAvg = highTotal / Math.max(1, binCount - midEnd);
    const normBass = bassAvg / 255;
    const normMid = midAvg / 255;
    const normHigh = highAvg / 255;
    const normEnergy = ((bassAvg * 0.55) + (midAvg * 0.3) + (highAvg * 0.15)) / 255;

    visualEnergyEMA = (visualEnergyEMA * 0.88) + (normEnergy * 0.12);
    visualBassEMA = (visualBassEMA * 0.86) + (normBass * 0.14);

    const transient = Math.max(0, normBass - visualBassEMA);
    const beatThreshold = Math.max(0.08, visualBassEMA * 1.18);
    const isBeat = normBass > beatThreshold && transient > 0.02 && (now - lastBeatTime) > 95;
    if (isBeat) {
        lastBeatTime = now;
        visualBeatPulse = Math.min(0.62, Math.max(visualBeatPulse, 0.5 + (transient * 1.4)));
    } else {
        visualBeatPulse = Math.max(0, (visualBeatPulse * 0.84) - 0.024);
    }

    const punch = clampNum(
        (Math.pow(normBass, 1.45) * 1.55) + (visualBeatPulse * 0.42) + (transient * 1.35),
        0,
        1.55
    );
    const targetGain = clampNum(0.9 / Math.max(0.1, visualEnergyEMA), 1.0, 2.6);
    visualAutoGain = (visualAutoGain * 0.92) + (targetGain * 0.08);

    // 2. Beat Color Shift (Slow, subtle - only affects peaks bar + border, not main theme)
    const beatColorToggle = $('beat-color-toggle');
    const isColorShift = !!beatColorToggle?.checked;
    if (isColorShift) {
        const themeKey = getThemeBeatKey();
        if (!beatColors.length || beatColors.length < 5 || beatPaletteThemeKey !== themeKey) {
            beatColors = buildThemeBeatPalette();
            beatPaletteThemeKey = themeKey;
            beatColorStage = 0;
            beatColorT = 0;
        }
        beatColorT += 0.0018 + (punch * 0.0015);
        if (isBeat) beatColorT += 0.014;

        if (beatColorT >= 1) {
            beatColorT = 0;
            beatColorStage = (beatColorStage + 1) % beatColors.length;
        }

        const c1 = beatColors[beatColorStage];
        const c2 = beatColors[(beatColorStage + 1) % beatColors.length];
        const currentHex = lerpColor(c1, c2, beatColorT);
        const nextHex = lerpColor(c1, c2, Math.min(beatColorT + 0.3, 1));

        // Only update beat-specific variables (peaks bar + border), NOT main theme gradient
        document.documentElement.style.setProperty('--beat-c1', currentHex);
        document.documentElement.style.setProperty('--beat-c2', nextHex);

        // Apply beat colors to border mode gradient when enabled (uses separate variable)
        if (isBorderMode) {
            const beatGradient = `linear-gradient(135deg, ${currentHex}, ${nextHex})`;
            document.documentElement.style.setProperty('--border-g', beatGradient);
        }
    }

    // 3. Visual Mode Handling
    const visualModeSelect = $('visual-mode-select');
    const visualMode = visualModeSelect?.value || 'bars';
    const canvas = $('audio-visualizer');
    if (!canvas) return;
    // A hidden canvas cannot show this frame; skip its drawing and per-frame style writes.
    if (!isVisualizerCanvasRendered(canvas)) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    resizeVisualizerCanvas(canvas);
    if (!canvas.width || !canvas.height) return;

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    const useBeatColors = !!beatColorToggle?.checked;
    const { c1, c2 } = resolveVisualizerColors(useBeatColors);
    const width = canvas.width;
    const height = canvas.height;
    const beatBump = 1 + Math.min(0.045, (visualBeatPulse * 0.028) + (punch * 0.01));
    const beatLift = (beatBump - 1) * 4.8;
    const isFogMode = visualMode === 'fog';
    canvas.style.transformOrigin = 'center bottom';
    canvas.style.transform = `translateY(-${beatLift.toFixed(2)}px) scaleY(${beatBump.toFixed(4)})`;
    const boxGlow = 8 + (visualEnergyEMA * 14) + (punch * 12);
    canvas.style.borderColor = isFogMode ? (c1 + '7A') : 'rgba(148, 163, 184, 0.28)';
    canvas.style.background = isFogMode
        ? `radial-gradient(circle at 20% 0%, ${c1}55, transparent 45%), radial-gradient(circle at 80% 0%, ${c2}55, transparent 45%), linear-gradient(180deg, rgba(15, 23, 42, 0.22), rgba(15, 23, 42, 0.04))`
        : 'linear-gradient(180deg, rgba(15, 23, 42, 0.26), rgba(15, 23, 42, 0.08))';
    canvas.style.boxShadow = isFogMode
        ? `inset 0 0 18px rgba(15, 23, 42, 0.42), 0 0 ${Math.round(boxGlow)}px ${c2}55`
        : 'inset 0 0 12px rgba(15, 23, 42, 0.34), 0 4px 12px rgba(2, 6, 23, 0.22)';

    if (isFogMode) {
        // --- FOG MODE ---
        const baseY = height - 1;
        const waveHeight = Math.max(6, (0.2 + visualEnergyEMA + (visualBeatPulse * 0.24)) * height * 0.72);

        const bg = ctx.createLinearGradient(0, 0, 0, height);
        bg.addColorStop(0, c1 + '2A');
        bg.addColorStop(1, 'transparent');
        ctx.fillStyle = bg;
        ctx.fillRect(0, 0, width, height);

        ctx.beginPath();
        ctx.moveTo(0, baseY);
        const points = 42;
        for (let i = 0; i <= points; i++) {
            const t = i / points;
            const idx = Math.floor(t * (binCount - 1));
            const local = dataArray[idx] / 255;
            const midFocus = 1 - Math.abs((t * 2) - 1);
            const globalBaseline = (normEnergy * 0.34) + (visualBeatPulse * 0.055) + (transient * 0.12);
            const bandLift = clampNum(
                (normBass * (0.16 + ((1 - t) * 0.22))) +
                (normMid * (0.14 + (midFocus * 0.26))) +
                (normHigh * (0.14 + (t * 0.22))),
                0,
                1
            );
            const texture = Math.sin((now * 0.01) + (i * 1.37)) * (0.018 + (normEnergy * 0.024));
            const blended = clampNum((local * 0.54) + (bandLift * 0.32) + globalBaseline + texture, 0, 1);
            const amp = Math.pow(clampNum(blended * visualAutoGain, 0, 1), 0.68);
            const y = baseY - (amp * waveHeight);
            ctx.lineTo(t * width, y);
        }
        ctx.lineTo(width, baseY);
        ctx.closePath();

        const fogFill = ctx.createLinearGradient(0, 0, 0, height);
        fogFill.addColorStop(0, c2 + '66');
        fogFill.addColorStop(1, c1 + '08');
        ctx.fillStyle = fogFill;
        ctx.fill();

        ctx.lineWidth = 2;
        ctx.strokeStyle = c2 + 'DD';
        ctx.shadowBlur = 12;
        ctx.shadowColor = c2;
        ctx.stroke();
        ctx.shadowBlur = 0;

    } else {
        // --- PEAKS MODE (line only; no fog glow layer) ---
        const pointCount = Math.floor(clampNum(width / 5, 60, 150));
        const baseY = height - 1;
        const usableHeight = Math.max(14, height * 0.97);

        if (visualBarLevels.length !== pointCount) {
            visualBarLevels = Array(pointCount).fill(0);
            visualBarCaps = Array(pointCount).fill(0);
        }

        const xs = [];
        const ys = [];
        const capYs = [];
        for (let i = 0; i < pointCount; i++) {
            const t = pointCount > 1 ? (i / (pointCount - 1)) : 0;
            const center = Math.max(0, Math.min(binCount - 1, Math.floor(t * (binCount - 1))));
            const spread = 2;
            let sum = 0;
            let count = 0;
            for (let b = center - spread; b <= center + spread; b++) {
                if (b < 0 || b >= binCount) continue;
                sum += dataArray[b];
                count++;
            }
            const raw = (sum / Math.max(1, count)) / 255;
            const midFocus = 1 - Math.abs((t * 2) - 1);
            const globalBaseline = (normEnergy * 0.36) + (visualBeatPulse * 0.065) + (transient * 0.14);
            const bandLift = clampNum(
                (normBass * (0.18 + ((1 - t) * 0.28))) +
                (normMid * (0.16 + (midFocus * 0.34))) +
                (normHigh * (0.18 + (t * 0.26))),
                0,
                1
            );
            const texture = (
                Math.sin((now * 0.011) + (i * 0.83)) +
                Math.sin((now * 0.017) + (i * 1.71))
            ) * (0.012 + (normEnergy * 0.026) + (visualBeatPulse * 0.006));
            const blended = clampNum((raw * 0.48) + (bandLift * 0.34) + globalBaseline + texture, 0, 1);
            const regionalSensitivity = 0.9 + (midFocus * 0.18) + ((1 - t) * 0.12) + (t * 0.1);
            const freqBoost = regionalSensitivity + (normEnergy * 0.12);
            const boosted = clampNum(blended * visualAutoGain * freqBoost, 0, 1);
            const shaped = Math.pow(boosted, 0.58);
            const baseFloor = 0.055 + (normEnergy * 0.1) + (visualBeatPulse * 0.012);
            const targetLevel = clampNum(
                shaped * (0.3 + (1.04 * (0.45 + visualEnergyEMA))) +
                (visualBeatPulse * (0.035 + (midFocus * 0.032))) +
                (normEnergy * 0.1),
                baseFloor,
                0.82
            );

            const prevLevel = visualBarLevels[i];
            const attack = 0.48 + (visualBeatPulse * 0.08);
            const release = 0.28;
            const nextLevel = targetLevel > prevLevel
                ? prevLevel + ((targetLevel - prevLevel) * attack)
                : prevLevel - ((prevLevel - targetLevel) * release);
            visualBarLevels[i] = nextLevel;

            let cap = visualBarCaps[i];
            if (nextLevel > cap) cap = nextLevel;
            else cap = Math.max(0, cap - (0.014 + (i * 0.0002)));
            visualBarCaps[i] = cap;

            const x = t * width;
            xs.push(x);
            ys.push(baseY - Math.max(2, nextLevel * usableHeight));
            capYs.push(baseY - Math.max(2, cap * usableHeight));
        }

        // Main continuous line
        ctx.beginPath();
        if (xs.length > 0) ctx.moveTo(xs[0], ys[0]);
        for (let i = 1; i < xs.length; i++) ctx.lineTo(xs[i], ys[i]);
        const lineGrad = ctx.createLinearGradient(0, 0, width, 0);
        lineGrad.addColorStop(0, c1 + 'D8');
        lineGrad.addColorStop(0.5, c2 + 'EE');
        lineGrad.addColorStop(1, c1 + 'D8');
        ctx.strokeStyle = lineGrad;
        ctx.lineWidth = 2.4;
        ctx.shadowBlur = 0;
        ctx.stroke();

        // Peak cap trace (subtle second line)
        ctx.beginPath();
        if (xs.length > 0) ctx.moveTo(xs[0], Math.max(1, capYs[0] - 1));
        for (let i = 1; i < xs.length; i++) ctx.lineTo(xs[i], Math.max(1, capYs[i] - 1));
        ctx.strokeStyle = c2 + '99';
        ctx.lineWidth = 1.1;
        ctx.globalAlpha = 0.7;
        ctx.stroke();
        ctx.globalAlpha = 1;

        // Fade edges
        ctx.globalCompositeOperation = 'destination-in';
        const maskGrad = ctx.createLinearGradient(0, 0, width, 0);
        maskGrad.addColorStop(0, 'rgba(0,0,0,0)');
        maskGrad.addColorStop(0.08, 'rgba(0,0,0,1)');
        maskGrad.addColorStop(0.92, 'rgba(0,0,0,1)');
        maskGrad.addColorStop(1, 'rgba(0,0,0,0)');
        ctx.fillStyle = maskGrad;
        ctx.fillRect(0, 0, width, height);
        ctx.globalCompositeOperation = 'source-over';
    }
}

// Beat Color Toggle Listener
const beatColorToggleEl = $('beat-color-toggle');

// Beat colors write only their own variables (peaks bar and border, above), never the
// theme gradient, so switching them off clears just those. Restoring a gradient captured
// when Beat was switched on overwrote any newer choice and could undo Reset Defaults.
function clearBeatColorPresentation() {
    beatPaletteThemeKey = '';
    beatColorStage = 0;
    beatColorT = 0;
    ['--beat-c1', '--beat-c2', '--border-g'].forEach((prop) => document.documentElement.style.removeProperty(prop));
}

if (beatColorToggleEl) {
    beatColorToggleEl.addEventListener('change', (e) => {
        if (e.target.checked) {
            // Generate a theme-matched multi-color cycle.
            beatColors = buildThemeBeatPalette();
            beatPaletteThemeKey = getThemeBeatKey();
            beatColorStage = 0;
            beatColorT = 0;
        } else {
            clearBeatColorPresentation();
        }
    });
}

/* --- SERVER HEARTBEAT --- */
// Keep the local server alive while the browser is open
// Server will auto-shutdown 10 seconds after last heartbeat
(function startServerHeartbeat() {
    const isLocalHost = /^(localhost|127\.0\.0\.1)$/i.test(window.location.hostname || '');
    const isExpectedPort = String(window.location.port || '') === '5555';
    if (!isLocalHost || !isExpectedPort) return;

    const SERVER_URL = `${window.location.protocol}//${window.location.hostname}:5555`;
    let heartbeatTimer = null;
    let heartbeatDisabled = false;

    const ping = () => {
        if (heartbeatDisabled) return;
        fetch(`${SERVER_URL}/heartbeat`, { method: 'GET', mode: 'cors' }).catch(() => {
            // If server is unavailable, stop trying to avoid repeated console/network errors.
            heartbeatDisabled = true;
            if (heartbeatTimer) {
                clearInterval(heartbeatTimer);
                heartbeatTimer = null;
            }
        });
    };

    heartbeatTimer = setInterval(ping, 30000); // Ping every 30 seconds
    ping(); // Initial heartbeat
})();


/* --- INDEXEDDB HELPER --- */
// Single choke point that keeps the derived saved-session index in step with
// every write to the `sessions` store, wherever that write comes from.
function syncSessionIndexAfterWrite(operation, payload) {
    try {
        if (operation === 'put') upsertSessionIndexEntry(payload);
        else if (operation === 'del') removeSessionIndexEntries([payload]);
        else if (operation === 'clear') resetSessionIndexToEmpty();
    } catch (error) {
        sessionIndexValidated = false;
        // A failed index update must never break the write it followed. The next
        // read validates against the session keys and rebuilds if needed.
        console.warn('Saved-session index update skipped:', error);
    }
}

// Storage problems stay on screen until dismissed, without blocking the page. Early
// in boot the popup surface may not exist yet, so the console carries the message.
function notifyStorageProblem(message) {
    console.error(message);
    try {
        if (typeof showPopupText === 'function' && typeof els !== 'undefined' && els?.popup) {
            showPopupText(message, null, true, { sticky: true, dismissible: true });
        }
    } catch (_) { }
}

const IDB = {
    db: null,
    openPromise: null,
    dbName: 'QuizReviewProDB',
    preferenceDefaultsResetPromise: null,

    async ensureDB() {
        if (!this.db) await this.open();
        else await this.ensurePreferenceDefaultsReset();
    },
    async open() {
        if (this.db) {
            await this.ensurePreferenceDefaultsReset();
            return;
        }
        if (this.openPromise) return this.openPromise;

        this.openPromise = new Promise((resolve, reject) => {
            if (!window.indexedDB) {
                console.error("IndexedDB not supported");
                reject(new Error("IndexedDB not supported"));
                return;
            }
            // Collection v3 cannot be written by older catalog code: versionchange closes its connection.
            const req = indexedDB.open(this.dbName, 7);

            req.onblocked = () => {
                console.error("DB BLOCKED: Close other tabs.");
                notifyStorageProblem('Storage is held by another open Memoraful tab. Close that tab, then reload this one.');
                reject(new Error("DB Blocked"));
            };

            req.onupgradeneeded = (e) => {
                const db = e.target.result;
                if (!db.objectStoreNames.contains('files')) db.createObjectStore('files');
                if (!db.objectStoreNames.contains('questions')) db.createObjectStore('questions');
                if (!db.objectStoreNames.contains('sessions')) db.createObjectStore('sessions', { keyPath: 'id' });
                if (!db.objectStoreNames.contains('settings')) db.createObjectStore('settings');
                if (!db.objectStoreNames.contains('images')) db.createObjectStore('images');  // For extracted quiz images
            };

            req.onsuccess = async (e) => {
                this.db = e.target.result;
                this.db.onversionchange = () => {
                    this.db.close();
                    this.db = null;
                    console.warn("DB Version change detected. Closing.");
                    notifyStorageProblem('A newer version of Memoraful opened in another tab, so this tab can no longer save. Reload it to keep saving; anything unsaved stays on screen until then.');
                };
                await this.ensurePreferenceDefaultsReset();
                resolve();
            };

            req.onerror = (e) => {
                console.error("DB Open Error", e);
                reject(e);
            };
        });

        try {
            await this.openPromise;
        } finally {
            this.openPromise = null;
        }
    },
    async ensurePreferenceDefaultsReset() {
        const config = window.__MEMORA_PREFERENCE_DEFAULTS_RESET__;
        if (!this.db || !config?.required || config.completedForPage) return true;
        if (this.preferenceDefaultsResetPromise) return this.preferenceDefaultsResetPromise;

        const exactKeys = new Set(Array.isArray(config.indexedSettingsKeys) ? config.indexedSettingsKeys : []);
        const keyPrefixes = Array.isArray(config.indexedSettingsKeyPrefixes)
            ? config.indexedSettingsKeyPrefixes
            : [];

        this.preferenceDefaultsResetPromise = new Promise((resolve, reject) => {
            let transaction;
            try {
                transaction = this.db.transaction('settings', 'readwrite');
                const store = transaction.objectStore('settings');
                const cursorRequest = store.openCursor();
                cursorRequest.onsuccess = () => {
                    const cursor = cursorRequest.result;
                    if (!cursor) return;
                    const key = String(cursor.key || '');
                    if (exactKeys.has(key) || keyPrefixes.some((prefix) => key.startsWith(prefix))) {
                        cursor.delete();
                    }
                    cursor.continue();
                };
            } catch (error) {
                reject(error);
                return;
            }

            transaction.oncomplete = () => {
                let markerWritten = false;
                if (config.localStorageResetSucceeded) {
                    try {
                        localStorage.setItem(config.markerKey, config.version);
                        markerWritten = localStorage.getItem(config.markerKey) === config.version;
                    } catch (_error) { }
                }
                config.completedForPage = true;
                config.required = !markerWritten;
                resolve(markerWritten);
            };
            transaction.onerror = () => reject(transaction.error || new Error('Preference defaults reset failed.'));
            transaction.onabort = () => reject(transaction.error || new Error('Preference defaults reset was aborted.'));
        }).catch((error) => {
            console.warn('Preference defaults reset will retry on the next page load:', error);
            config.completedForPage = true;
            return false;
        });

        return this.preferenceDefaultsResetPromise;
    },
    async put(storeName, key, value, timeoutOrOptions = undefined) {
        return this.writeBatch([{ storeName, key, value }], timeoutOrOptions);
    },
    // Compatibility alias. The canonical write contract lives in writeBatch().
    async putCommitted(storeName, key, value, timeoutOrOptions = undefined) {
        return this.put(storeName, key, value, timeoutOrOptions);
    },
    // Every entry is written in a single readwrite transaction, so the batch commits
    // or fails as a whole, and the promise settles only on that transaction's
    // completion: request success is not a commit.
    async writeBatch(entries, timeoutOrOptions = undefined) {
        const writes = (Array.isArray(entries) ? entries : []).filter((entry) => entry && entry.storeName);
        if (!writes.length) return;
        const storeNames = Array.from(new Set(writes.map((entry) => entry.storeName)));
        return this.runWriteTransaction(storeNames, (tx, write) => {
            writes.forEach((entry) => write(entry));
        }, timeoutOrOptions);
    },
    // Reads the stored session and writes what `compute` derives from it inside one
    // readwrite transaction, so no other write can land between the read and the
    // write. `compute(stored)` runs synchronously and returns { record, ... } to
    // write, or { error } to write nothing; the resolved value is its result.
    async compareAndPut(sessionId, compute, extraWrites = [], timeoutOrOptions = undefined) {
        const writes = (Array.isArray(extraWrites) ? extraWrites : []).filter((entry) => entry && entry.storeName);
        const storeNames = Array.from(new Set(['sessions', ...writes.map((entry) => entry.storeName)]));
        return this.runWriteTransaction(storeNames, (tx, write, abortWith, setResult) => {
            writes.forEach((entry) => write(entry));
            const readRequest = tx.objectStore('sessions').get(sessionId);
            readRequest.onsuccess = () => {
                let outcome;
                try {
                    outcome = compute(readRequest.result ?? null);
                } catch (error) {
                    abortWith(error);
                    return;
                }
                if (!outcome || outcome.error) {
                    abortWith(outcome?.error || new Error('The session write was refused.'));
                    return;
                }
                write({ storeName: 'sessions', key: null, value: outcome.record });
                setResult(outcome);
            };
        }, timeoutOrOptions);
    },
    // The one IndexedDB write primitive underneath both. `enqueue(tx, write, abortWith,
    // setResult)` issues the transaction's requests; the promise resolves with the
    // value passed to setResult once the transaction completes, and rejects with a
    // real Error on request error, transaction error, abort, or timeout.
    async runWriteTransaction(storeNames, enqueue, timeoutOrOptions = undefined) {
        const storeLabel = storeNames.join(', ') || 'no store';
        const options = typeof timeoutOrOptions === 'number'
            ? { timeoutMs: timeoutOrOptions }
            : ((timeoutOrOptions && typeof timeoutOrOptions === 'object') ? timeoutOrOptions : {});
        const requestedTimeoutMs = Number(options.timeoutMs);
        const timeoutMs = Number.isFinite(requestedTimeoutMs) && requestedTimeoutMs > 0
            ? requestedTimeoutMs
            : null;
        const startedAt = (typeof performance !== 'undefined' && typeof performance.now === 'function')
            ? performance.now()
            : Date.now();
        const deadline = timeoutMs === null ? null : startedAt + timeoutMs;
        const now = () => (typeof performance !== 'undefined' && typeof performance.now === 'function')
            ? performance.now()
            : Date.now();
        const toStorageError = (candidate, fallbackMessage) => {
            const source = candidate?.target?.error || candidate;
            if (source instanceof Error) return source;
            const error = new Error(source?.message || fallbackMessage);
            if (source?.name) error.name = source.name;
            if (source && Object.prototype.hasOwnProperty.call(source, 'cause')) error.cause = source.cause;
            return error;
        };
        const createTimeoutError = () => {
            const error = new Error(`IndexedDB put timed out for ${storeLabel}.`);
            error.name = 'TimeoutError';
            return error;
        };
        const remainingMs = () => deadline === null ? null : deadline - now();

        try {
            if (deadline !== null && remainingMs() <= 0) throw createTimeoutError();
            if (deadline === null) {
                await this.ensureDB();
            } else {
                const timeoutToken = Symbol('idb-put-timeout');
                const remaining = Math.max(1, remainingMs());
                let initializationTimer = null;
                const timeoutPromise = new Promise((resolve) => {
                    initializationTimer = window.setTimeout(() => resolve(timeoutToken), remaining);
                });
                try {
                    const initialized = await Promise.race([this.ensureDB(), timeoutPromise]);
                    if (initialized === timeoutToken) throw createTimeoutError();
                } finally {
                    if (initializationTimer !== null) window.clearTimeout(initializationTimer);
                }
            }
        } catch (error) {
            throw toStorageError(error, `IndexedDB is unavailable for ${storeLabel}.`);
        }

        if (!this.db) throw new Error(`IndexedDB is unavailable for ${storeLabel}.`);

        return new Promise((resolve, reject) => {
            let tx = null;
            let timer = null;
            let settled = false;
            let lastRequestError = null;
            let abortReason = null;
            let result;
            const sessionValues = [];
            const finish = (callback, settledValue) => {
                if (settled) return;
                settled = true;
                if (timer !== null) window.clearTimeout(timer);
                if (tx) {
                    tx.oncomplete = null;
                    tx.onerror = null;
                    tx.onabort = null;
                }
                callback(settledValue);
            };
            // While the deadline or a refused compute is aborting the transaction, the
            // error events that abort raises report that reason, not a generic abort.
            let timingOut = false;
            const fail = (error) => finish(reject, abortReason || (timingOut ? createTimeoutError() : error));
            const rejectForTimeout = () => {
                if (settled) return;
                if (tx) {
                    timingOut = true;
                    try {
                        tx.abort();
                    } catch (_) {
                        // Already committing: its complete or abort event decides the
                        // outcome, so a write that may still commit is not reported lost.
                        timingOut = false;
                        return;
                    }
                }
                finish(reject, createTimeoutError());
            };
            const write = (entry) => {
                const store = tx.objectStore(entry.storeName);
                const req = (entry.storeName === 'sessions') ? store.put(entry.value) : store.put(entry.value, entry.key);
                if (entry.storeName === 'sessions') sessionValues.push(entry.value);
                req.onerror = (event) => {
                    lastRequestError = req.error || lastRequestError;
                    fail(toStorageError(req.error || event, `IndexedDB put failed for ${entry.storeName}.`));
                };
            };
            const abortWith = (error) => {
                abortReason = error;
                try {
                    tx.abort();
                } catch (_) {
                    finish(reject, error);
                }
            };
            const setResult = (value) => { result = value; };

            try {
                const remaining = remainingMs();
                if (remaining !== null && remaining <= 0) {
                    rejectForTimeout();
                    return;
                }
                tx = this.db.transaction(storeNames.length === 1 ? storeNames[0] : storeNames, 'readwrite');
                tx.oncomplete = () => {
                    sessionValues.forEach((value) => {
                        try {
                            Promise.resolve(syncSessionIndexAfterWrite('put', value)).catch((error) => {
                                sessionIndexValidated = false;
                                console.warn('Saved-session index refresh failed after commit:', error);
                            });
                        } catch (error) {
                            sessionIndexValidated = false;
                            console.warn('Saved-session index refresh failed after commit:', error);
                        }
                    });
                    finish(resolve, result);
                };
                tx.onerror = () => fail(toStorageError(tx.error || lastRequestError, `IndexedDB transaction failed for ${storeLabel}.`));
                tx.onabort = () => fail(toStorageError(tx.error || lastRequestError, `IndexedDB transaction aborted for ${storeLabel}.`));
                enqueue(tx, write, abortWith, setResult);
                if (remaining !== null) timer = window.setTimeout(rejectForTimeout, Math.max(1, remaining));
            } catch (error) {
                finish(reject, toStorageError(error, `IndexedDB put failed for ${storeLabel}.`));
            }
        });
    },
    async getAllKeys(storeName) {
        await this.ensureDB();
        if (!this.db) return [];
        return new Promise((resolve, reject) => {
            try {
                const tx = this.db.transaction(storeName, 'readonly');
                const store = tx.objectStore(storeName);
                const req = store.getAllKeys();
                req.onsuccess = () => resolve(req.result);
                req.onerror = (e) => reject(e);
            } catch (e) { reject(e); }
        });
    },
    async getAll(storeName) {
        await this.ensureDB();
        if (!this.db) return [];
        return new Promise((resolve, reject) => {
            try {
                const tx = this.db.transaction(storeName, 'readonly');
                const store = tx.objectStore(storeName);
                const req = store.getAll();
                req.onsuccess = () => resolve(req.result);
                req.onerror = (e) => reject(e);
            } catch (e) { reject(e); }
        });
    },
    // One readonly transaction for a batch of keys. Restoring a multi-source
    // session used to open a separate transaction per key-alias per quiz bank.
    async getMany(storeName, keys) {
        await this.ensureDB();
        const list = Array.isArray(keys) ? keys : [];
        if (!this.db || list.length === 0) return list.map(() => undefined);
        return new Promise((resolve, reject) => {
            try {
                const tx = this.db.transaction(storeName, 'readonly');
                const store = tx.objectStore(storeName);
                const results = new Array(list.length);
                list.forEach((key, index) => {
                    const req = store.get(key);
                    req.onsuccess = () => { results[index] = req.result; };
                    req.onerror = () => { results[index] = undefined; };
                });
                tx.oncomplete = () => resolve(results);
                tx.onerror = () => reject(tx.error || new Error('getMany failed'));
                tx.onabort = () => reject(tx.error || new Error('getMany aborted'));
            } catch (e) { reject(e); }
        });
    },
    async get(storeName, key) {
        await this.ensureDB();
        if (!this.db) return null;
        return new Promise((resolve, reject) => {
            try {
                const tx = this.db.transaction(storeName, 'readonly');
                const store = tx.objectStore(storeName);
                const req = store.get(key);
                req.onsuccess = () => resolve(req.result);
                req.onerror = (e) => reject(e);
            } catch (e) { reject(e); }
        });
    },
    async del(storeName, key) {
        await this.ensureDB();
        if (!this.db) return;
        return new Promise((resolve, reject) => {
            try {
                const tx = this.db.transaction(storeName, 'readwrite');
                const store = tx.objectStore(storeName);
                const req = store.delete(key);
                req.onsuccess = () => {
                    if (storeName === 'sessions') syncSessionIndexAfterWrite('del', key);
                    resolve();
                };
                req.onerror = (e) => reject(e);
            } catch (e) { reject(e); }
        });
    },
    // Reads a settings value and writes what `compute(stored)` derives from it in one
    // readwrite transaction, so no other write can land between the read and the write.
    // `compute` runs synchronously and returns { value } to store, { remove: true } to
    // delete the key, { skip: true } to commit nothing, or { error } to change nothing; the
    // resolved value is that result. A failed read rejects instead of writing.
    async compareAndPutSetting(key, compute, timeoutOrOptions = undefined) {
        return this.runWriteTransaction(['settings'], (tx, write, abortWith, setResult) => {
            const request = tx.objectStore('settings').get(key);
            request.onsuccess = () => {
                let outcome;
                try {
                    outcome = compute(request.result ?? null);
                } catch (error) {
                    abortWith(error);
                    return;
                }
                if (!outcome || outcome.error) {
                    abortWith(outcome?.error || new Error('The setting write was refused.'));
                    return;
                }
                if (outcome.remove) tx.objectStore('settings').delete(key);
                else if (!outcome.skip) write({ storeName: 'settings', key, value: outcome.value });
                setResult(outcome);
            };
        }, timeoutOrOptions);
    },
    // Moves a session into a recovery list in one readwrite transaction spanning both
    // stores, so a crash, failed read or concurrent move can neither drop a recovery
    // entry nor archive a stale copy. `compute(stored, storedList)` runs synchronously on
    // the latest committed values and returns { list, sessionKey } to write the list and
    // delete the session, { skip: true } to commit nothing, or { error } to change nothing.
    async moveSessionToRecovery(sessionId, recoveryKey, compute, timeoutOrOptions = undefined) {
        const outcome = await this.runWriteTransaction(['sessions', 'settings'], (tx, write, abortWith, setResult) => {
            const sessionRequest = tx.objectStore('sessions').get(sessionId);
            sessionRequest.onsuccess = () => {
                const listRequest = tx.objectStore('settings').get(recoveryKey);
                listRequest.onsuccess = () => {
                    let result;
                    try {
                        result = compute(sessionRequest.result ?? null, listRequest.result ?? null);
                    } catch (error) {
                        abortWith(error);
                        return;
                    }
                    if (!result || result.error) {
                        abortWith(result?.error || new Error('The session move was refused.'));
                        return;
                    }
                    if (result.skip) {
                        setResult(result);
                        return;
                    }
                    write({ storeName: 'settings', key: recoveryKey, value: result.list });
                    tx.objectStore('sessions').delete(result.sessionKey);
                    setResult(result);
                };
            };
        }, timeoutOrOptions);
        if (outcome.skip) return outcome;
        try {
            Promise.resolve(syncSessionIndexAfterWrite('del', outcome.sessionKey)).catch((error) => {
                sessionIndexValidated = false;
                console.warn('Saved-session index refresh failed after commit:', error);
            });
        } catch (error) {
            sessionIndexValidated = false;
            console.warn('Saved-session index refresh failed after commit:', error);
        }
        return outcome;
    },
    async clear(storeName) {
        await this.ensureDB();
        if (!this.db) return;
        return new Promise((resolve, reject) => {
            try {
                const tx = this.db.transaction(storeName, 'readwrite');
                const store = tx.objectStore(storeName);
                const req = store.clear();
                req.onsuccess = () => {
                    if (storeName === 'sessions') syncSessionIndexAfterWrite('clear');
                    resolve();
                };
                req.onerror = (e) => reject(e);
            } catch (e) { reject(e); }
        });
    },
    // Simple key-value shortcuts using 'settings' store
    async set(key, value) {
        return this.put('settings', key, value);
    },
    async getKey(key) {
        return this.get('settings', key);
    }
};

// Blob URLs for idb:// question images, least recently used first. The cache is
// bounded by entry count and decoded bytes; an image larger than the byte budget is
// shown from a transient URL instead of occupying it. Eviction never revokes a URL
// an image on screen is still showing. Clearing advances the generation, so a
// lookup that was in flight when a session ended cannot repopulate the cache.
const IMAGE_BLOB_URL_CACHE_LIMITS = Object.freeze({ maxEntries: 120, maxBytes: 48 * 1024 * 1024 });
const imageBlobUrlCache = new Map();
const transientImageBlobUrls = new Set();
const imageBlobUrlLoads = new Map();
let imageBlobUrlCacheGeneration = 0;

function isImageBlobUrlOnScreen(url) {
    if (typeof document === 'undefined' || !url) return false;
    try {
        return Array.from(document.images || []).some((image) => image.src === url);
    } catch (_) {
        return false;
    }
}

function trimImageBlobUrlCache(limits = IMAGE_BLOB_URL_CACHE_LIMITS) {
    let totalBytes = 0;
    imageBlobUrlCache.forEach((entry) => { totalBytes += entry.bytes; });
    for (const [key, entry] of imageBlobUrlCache) {
        if (imageBlobUrlCache.size <= limits.maxEntries && totalBytes <= limits.maxBytes) break;
        if (isImageBlobUrlOnScreen(entry.url)) continue;
        try { URL.revokeObjectURL(entry.url); } catch (_) { }
        imageBlobUrlCache.delete(key);
        totalBytes -= entry.bytes;
    }
}

/**
 * Get a displayable URL for a question image.
 * Handles both regular URLs and idb:// URLs (stored in IndexedDB).
 * @param {string} imgUrl - The image URL (may be idb://key or regular URL)
 * @returns {Promise<string|null>} - Blob URL for display, or null if not found
 */
async function getImageBlobUrl(imgUrl) {
    if (!imgUrl) return null;

    // If it's not an IndexedDB reference, return as-is
    if (!imgUrl.startsWith('idb://')) {
        return imgUrl;
    }

    // Extract key from idb://key format
    const key = imgUrl.substring(6);

    const cached = imageBlobUrlCache.get(key);
    if (cached) {
        imageBlobUrlCache.delete(key);
        imageBlobUrlCache.set(key, cached);
        return cached.url;
    }

    // Overlapping cold reads of one key share a single decode, so only one Blob URL
    // is ever made for it and the cache slot can always revoke what it handed out.
    const pending = imageBlobUrlLoads.get(key);
    if (pending && pending.generation === imageBlobUrlCacheGeneration) return pending.promise;
    const generation = imageBlobUrlCacheGeneration;
    const promise = loadImageBlobUrl(key, generation);
    imageBlobUrlLoads.set(key, { generation, promise });
    try {
        return await promise;
    } finally {
        if (imageBlobUrlLoads.get(key)?.promise === promise) imageBlobUrlLoads.delete(key);
    }
}

async function loadImageBlobUrl(key, generation) {
    try {
        const base64Data = await IDB.get('images', key);
        if (generation !== imageBlobUrlCacheGeneration) return null;
        if (!base64Data) {
            console.warn(`Image not found in IndexedDB: ${key}`);
            return null;
        }

        // Convert base64 to blob URL (WebP format)
        const binaryString = atob(base64Data);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.charCodeAt(i);
        }
        const blob = new Blob([bytes], { type: 'image/webp' });
        const existing = imageBlobUrlCache.get(key);
        if (existing) return existing.url;
        const blobUrl = URL.createObjectURL(blob);

        if (bytes.length > IMAGE_BLOB_URL_CACHE_LIMITS.maxBytes) {
            transientImageBlobUrls.add(blobUrl);
            return blobUrl;
        }
        imageBlobUrlCache.set(key, { url: blobUrl, bytes: bytes.length });
        trimImageBlobUrlCache();
        return blobUrl;
    } catch (e) {
        console.error(`Failed to load image from IndexedDB: ${key}`, e);
        return null;
    }
}

/* --- QUESTION IMAGE RECLAMATION ---
   Extracted quiz images live in the `images` store under keys that questions name as
   idb://<key>. Nothing deleted them, so they outlived every session that used them.
   This mark-and-sweep keeps any image named by a live session, a question bank (with
   its regeneration history), a Recently Deleted entry, any settings record, any
   LocalStorage value, or anything still only in memory, and deletes the rest. A pass
   that cannot read every container, or that overlaps quiz generation or an
   uncommitted bank, deletes nothing. */
const QUESTION_IMAGE_REFERENCE_PATTERN = /idb:\/\/([^"'\\\s)]+)/g;
let questionImageCollectionTimer = null;
let questionImageCollectionRunning = false;

function collectQuestionImageReferences(value, references) {
    let text = '';
    try {
        text = typeof value === 'string' ? value : JSON.stringify(value);
    } catch (error) {
        throw new Error(`An image reference container could not be read (${error?.message || error}).`);
    }
    if (!text || !text.includes('idb://')) return;
    for (const match of text.matchAll(QUESTION_IMAGE_REFERENCE_PATTERN)) references.add(match[1]);
}

function isQuestionImageCollectionBlocked() {
    if (typeof quizGenRunning !== 'undefined' && quizGenRunning) return 'quiz generation is running';
    if (typeof pendingQuestionBankWrites !== 'undefined' && pendingQuestionBankWrites.size > 0) return 'a question bank has not committed yet';
    return '';
}

async function collectUnreferencedQuestionImages({ reason = '' } = {}) {
    if (IS_EMBED_DEMO || questionImageCollectionRunning) return { removed: 0, skipped: 'already running' };
    const blocked = isQuestionImageCollectionBlocked();
    if (blocked) return { removed: 0, skipped: blocked };
    questionImageCollectionRunning = true;
    try {
        const imageKeys = await IDB.getAllKeys('images');
        if (!imageKeys.length) return { removed: 0, kept: 0 };

        const references = new Set();
        const [sessions, banks, settings] = await Promise.all([
            IDB.getAll('sessions'),
            IDB.getAll('questions'),
            IDB.getAll('settings')
        ]);
        [sessions, banks, settings].forEach((records) => {
            (records || []).forEach((record) => collectQuestionImageReferences(record, references));
        });
        try {
            for (let i = 0; i < localStorage.length; i++) {
                collectQuestionImageReferences(localStorage.getItem(localStorage.key(i)), references);
            }
        } catch (error) {
            throw new Error(`LocalStorage could not be read (${error?.message || error}).`);
        }
        collectQuestionImageReferences(loadedQuestionSets, references);
        collectQuestionImageReferences(questions, references);

        const unreferenced = imageKeys.filter((key) => !references.has(String(key)));
        // Generation or an import may have started while the containers were read.
        const blockedNow = isQuestionImageCollectionBlocked();
        if (blockedNow) return { removed: 0, skipped: blockedNow };
        if (unreferenced.length) {
            await IDB.runWriteTransaction(['images'], (tx) => {
                const store = tx.objectStore('images');
                unreferenced.forEach((key) => store.delete(key));
            }, 30000);
        }
        const outcome = { removed: unreferenced.length, kept: imageKeys.length - unreferenced.length, reason };
        if (outcome.removed) {
            console.info('Reclaimed unreferenced question images:', outcome);
            try {
                const estimate = await navigator.storage?.estimate?.();
                if (estimate) outcome.storage = { usage: estimate.usage, quota: estimate.quota };
            } catch (_) { }
        }
        return outcome;
    } catch (error) {
        console.warn('Question image reclamation skipped this pass:', error);
        return { removed: 0, skipped: String(error?.message || error) };
    } finally {
        questionImageCollectionRunning = false;
    }
}

// Permanent deletions call this; passes coalesce and run off the interaction.
function scheduleQuestionImageCollection(reason = '') {
    scheduleUnreferencedSourceCollection(reason);
    if (questionImageCollectionTimer) clearTimeout(questionImageCollectionTimer);
    questionImageCollectionTimer = setTimeout(() => {
        questionImageCollectionTimer = null;
        void collectUnreferencedQuestionImages({ reason });
    }, 4000);
}
window.scheduleQuestionImageCollection = scheduleQuestionImageCollection;

/* --- SAVED SOURCE AND QUIZ BANK RECLAMATION ---
   Permanent deletion removed a session's record but left its stored PDFs (`files`) and
   quiz banks (`questions`) behind, so deleting sessions never freed disk space. Only
   the app's own stored copy is removed here; the student's file on their computer is
   never touched. A source or bank goes only when nothing names it: no live or Recently
   Deleted session, no other bank, no settings record except caches derived from it, no
   LocalStorage value, and nothing in this tab's memory. It must also be found
   unreferenced on two passes at least ten minutes apart, so a write still in flight in
   another tab is never mistaken for an orphan. A read failure, or overlapping
   generation, import or upload, cancels the pass and deletes nothing. */
const UNREFERENCED_SOURCE_LEDGER_KEY = 'memora_unreferenced_sources_v1';
const UNREFERENCED_SOURCE_GRACE_MS = 10 * 60 * 1000;
const RECLAIMABLE_SOURCE_FILE_PATTERN = /\.(pdf|pptx?)$/i;
// Caches computed from one source or bank: they describe it but never keep it alive.
// Written as literals because their owning constants load after this point.
const SOURCE_DERIVED_SETTINGS_PREFIXES = [
    UNREFERENCED_SOURCE_LEDGER_KEY,
    'memora_source_revision_v1::',
    'memora_slide_digest::',
    'memora_digest_ledger::',
    'memora_slide_extraction::',
    'memora_slide_index_file_v1::',
    'memora_lexical_ocr_tag::',
    'memora_upload_batch::',
    'ask_memora_embedding_vector_',
    'pdf_explanation',
    'question_duplicate_archive::',
    'question_duplicate_report::'
];
let unreferencedSourceCollectionTimer = null;
let unreferencedSourceCollectionRunning = false;

function isUnreferencedSourceCollectionBlocked() {
    const imageBlock = isQuestionImageCollectionBlocked();
    if (imageBlock) return imageBlock;
    if (typeof pendingMemoraSourceFileClaims !== 'undefined' && pendingMemoraSourceFileClaims.size > 0) return 'a source upload is being saved';
    if (Object.keys(window.pendingQuizGenPdfs || {}).length > 0) return 'a source is waiting for quiz generation';
    return '';
}

// Every string and object key reachable from value, so a name stored anywhere counts.
function collectStoredReferenceStrings(value, names, seen = new WeakSet(), depth = 0) {
    if (typeof value === 'string') { names.add(value); return; }
    if (!value || typeof value !== 'object' || depth > 40 || seen.has(value)) return;
    if (value instanceof Blob || ArrayBuffer.isView(value) || value instanceof ArrayBuffer) return;
    seen.add(value);
    if (value instanceof Map || value instanceof Set) {
        value.forEach((entry, key) => {
            collectStoredReferenceStrings(key, names, seen, depth + 1);
            collectStoredReferenceStrings(entry, names, seen, depth + 1);
        });
        return;
    }
    for (const key of Object.keys(value)) {
        names.add(key);
        collectStoredReferenceStrings(value[key], names, seen, depth + 1);
    }
}

function getLiveSourceReferences() {
    return {
        manifest: typeof sessionManifest === 'undefined' ? null : sessionManifest,
        links: typeof pdfAssociations === 'undefined' ? null : pdfAssociations,
        pdfs: Object.keys(loadedPdfFiles || {}),
        banks: Object.keys(loadedQuestionSets || {}),
        active: [...(activeQuestionSetKeys || [])],
        used: typeof collectUsedQuestionSetKeys === 'function' ? [...collectUsedQuestionSetKeys()] : [],
        tutor: typeof getPdfTutorPdfNames === 'function' ? getPdfTutorPdfNames() : []
    };
}

async function collectUnreferencedSavedSources({ reason = '', dryRun = false } = {}) {
    if (IS_EMBED_DEMO || unreferencedSourceCollectionRunning) return { removed: [], skipped: 'already running' };
    const blocked = isUnreferencedSourceCollectionBlocked();
    if (blocked) return { removed: [], skipped: blocked };
    if (typeof IDB.runWriteTransaction !== 'function') return { removed: [], skipped: 'storage transactions are unavailable' };
    unreferencedSourceCollectionRunning = true;
    try {
        // This tab's memory and LocalStorage are read first; stored records are read,
        // judged and deleted inside one transaction so no other writer can interleave.
        const outsideNames = new Set();
        const outsideTexts = [];
        collectStoredReferenceStrings(getLiveSourceReferences(), outsideNames);
        try {
            for (let i = 0; i < localStorage.length; i++) {
                const key = localStorage.key(i);
                const value = localStorage.getItem(key);
                outsideNames.add(key);
                outsideNames.add(value);
                outsideTexts.push(key, value);
            }
        } catch (error) {
            throw new Error(`LocalStorage could not be read (${error?.message || error}).`);
        }
        outsideTexts.push(JSON.stringify(getLiveSourceReferences()));
        const blockedNow = isUnreferencedSourceCollectionBlocked();
        if (blockedNow) return { removed: [], skipped: blockedNow };

        const now = Date.now();
        const outcome = await IDB.runWriteTransaction(['files', 'questions', 'sessions', 'settings'], (tx, write, abortWith, setResult) => {
            const fileKeys = [];
            const bankRecords = [];
            const containers = [];
            let ledger = {};
            let pending = 4;
            const scan = (storeName, { keysOnly = false } = {}, onEntry) => {
                const store = tx.objectStore(storeName);
                const request = keysOnly ? store.openKeyCursor() : store.openCursor();
                request.onerror = () => abortWith(request.error);
                request.onsuccess = () => {
                    const cursor = request.result;
                    if (!cursor) {
                        if (--pending === 0) judge();
                        return;
                    }
                    onEntry(String(cursor.key), keysOnly ? undefined : cursor.value);
                    cursor.continue();
                };
            };
            scan('files', { keysOnly: true }, (key) => {
                if (RECLAIMABLE_SOURCE_FILE_PATTERN.test(key)) fileKeys.push(key);
            });
            scan('questions', {}, (key, value) => bankRecords.push({ key, value }));
            scan('sessions', {}, (key, value) => containers.push({ key: `sessions\n${key}`, value }));
            scan('settings', {}, (key, value) => {
                if (key === UNREFERENCED_SOURCE_LEDGER_KEY) {
                    if (value && typeof value === 'object') ledger = value;
                    return;
                }
                if (SOURCE_DERIVED_SETTINGS_PREFIXES.some((prefix) => key.startsWith(prefix))) return;
                containers.push({ key: `settings\n${key}`, value });
            });

            function judge() {
                const names = new Set(outsideNames);
                containers.forEach((entry) => collectStoredReferenceStrings(entry.value, names));
                // A bank never keeps itself alive, so each bank's names are kept apart.
                const bankNames = new Map();
                bankRecords.forEach((bank) => {
                    const own = new Set();
                    collectStoredReferenceStrings(bank.value, own);
                    bankNames.set(bank.key, own);
                });
                const namedByOtherBank = (name, selfKey = '') => {
                    for (const [key, own] of bankNames) if (key !== selfKey && own.has(name)) return true;
                    return false;
                };
                // Names can also sit inside composite strings ("Lecture.pdf::12"), so an exact
                // miss is confirmed with a substring search before it counts as unreferenced.
                let texts = null;
                let bankTexts = null;
                const appearsInText = (name, selfKey = '') => {
                    if (!texts) {
                        texts = [...outsideTexts];
                        containers.forEach((entry) => texts.push(JSON.stringify(entry.value ?? null)));
                        bankTexts = bankRecords.map((bank) => ({ key: bank.key, text: JSON.stringify(bank.value ?? null) }));
                    }
                    const forms = [name, JSON.stringify(name).slice(1, -1), encodeURIComponent(name)];
                    const found = (text) => forms.some((form) => text.includes(form));
                    return texts.some(found) || bankTexts.some((bank) => bank.key !== selfKey && found(bank.text));
                };
                const unreferenced = [];
                fileKeys.forEach((key) => {
                    if (names.has(key) || namedByOtherBank(key) || appearsInText(key)) return;
                    unreferenced.push({ store: 'files', key });
                });
                bankRecords.forEach(({ key }) => {
                    // Interrupted generation resumes from its ::temp bank, and the demo bank is built in.
                    if (key.endsWith('::temp') || key === DEMO_QUESTION_SET_KEY) return;
                    if (names.has(key) || namedByOtherBank(key, key) || appearsInText(key, key)) return;
                    unreferenced.push({ store: 'questions', key });
                });

                const nextLedger = {};
                const due = [];
                const waiting = [];
                unreferenced.forEach((item) => {
                    const id = `${item.store}\n${item.key}`;
                    const firstSeen = Number(ledger[id]) || 0;
                    if (firstSeen && now - firstSeen >= UNREFERENCED_SOURCE_GRACE_MS) {
                        due.push(item);
                    } else {
                        nextLedger[id] = firstSeen || now;
                        waiting.push(item);
                    }
                });
                if (dryRun) {
                    setResult({ removed: [], due, waiting, dryRun: true });
                    return;
                }
                const settingsStore = tx.objectStore('settings');
                if (Object.keys(nextLedger).length) settingsStore.put(nextLedger, UNREFERENCED_SOURCE_LEDGER_KEY);
                else settingsStore.delete(UNREFERENCED_SOURCE_LEDGER_KEY);
                const removed = [];
                due.forEach((item) => {
                    if (item.store === 'questions') {
                        tx.objectStore('questions').delete(item.key);
                        removed.push(item);
                        return;
                    }
                    // Only a stored document is removed, never a record that merely has a document's name.
                    const filesStore = tx.objectStore('files');
                    const read = filesStore.get(item.key);
                    read.onerror = () => abortWith(read.error);
                    read.onsuccess = () => {
                        if (!(read.result instanceof Blob) && !(read.result instanceof ArrayBuffer)) return;
                        filesStore.delete(item.key);
                        removed.push(item);
                    };
                });
                setResult({ removed, waiting });
            }
        }, 60000);

        const removed = outcome?.removed || [];
        removed.forEach((item) => {
            if (item.store === 'files') {
                if (typeof forgetMemoraSourceRevision === 'function') forgetMemoraSourceRevision(item.key);
            } else if (typeof clearQuestionDuplicateReport === 'function') {
                void clearQuestionDuplicateReport(item.key, { clearArchive: true }).catch(() => { });
            }
        });
        const result = {
            removed: removed.map((item) => `${item.store}: ${item.key}`),
            waiting: (outcome?.waiting || []).map((item) => `${item.store}: ${item.key}`),
            ...(outcome?.dryRun ? { due: outcome.due.map((item) => `${item.store}: ${item.key}`), dryRun: true } : {}),
            reason
        };
        if (result.removed.length) console.info('Reclaimed unreferenced saved sources and quiz banks:', result);
        // Items seen for the first time are confirmed on a later pass, after the grace period.
        if (!result.dryRun && result.waiting.length) scheduleUnreferencedSourceCollection('grace-recheck', UNREFERENCED_SOURCE_GRACE_MS + 15000);
        return result;
    } catch (error) {
        console.warn('Saved source reclamation skipped this pass:', error);
        return { removed: [], skipped: String(error?.message || error) };
    } finally {
        unreferencedSourceCollectionRunning = false;
    }
}
window.previewUnreferencedSavedSources = () => collectUnreferencedSavedSources({ reason: 'preview', dryRun: true });

function scheduleUnreferencedSourceCollection(reason = '', delayMs = 4000) {
    if (unreferencedSourceCollectionTimer) clearTimeout(unreferencedSourceCollectionTimer);
    unreferencedSourceCollectionTimer = setTimeout(() => {
        unreferencedSourceCollectionTimer = null;
        void collectUnreferencedSavedSources({ reason });
    }, delayMs);
}
// Sources orphaned before this pass existed, or left by a closed tab, are found after startup settles.
if (!IS_EMBED_DEMO) scheduleUnreferencedSourceCollection('startup', 90000);

/* --- STATE --- */
let loadedPdfFiles = {};
let loadedQuestionSets = {};
let activeQuestionSetKeys = new Set();
let questions = [];
let filteredIndices = [];
let pdfAssociations = {};
let themeConfirmResolver = null;
let sessionNamePromptResolver = null;
let pdfAssociationsPersistChain = Promise.resolve();
let lastWelcomeSessionId = '';
let isWelcomeSessionLoadPending = false;
let continueLastSessionPromise = null;
let welcomeSessionLoadEpoch = 0;
const THEME_STORAGE_KEY = `memora.theme.v2:${window.location.pathname}`;
const LEGACY_THEME_STORAGE_KEY = 'QuizTheme';
const ACCESSIBILITY_PREFS_KEY = `memora.accessibility.v1:${window.location.pathname}`;
const STUDY_PLANNER_FEATURE_ENABLED = false;
const STUDY_DASHBOARD_AUTO_OPEN_DISABLED_KEY = `memora.studyDashboard.autoOpenDisabled.v1:${window.location.pathname}`;
const ANSWER_CHOICE_EXPLANATION_NOTICE_DISMISSED_KEY = 'memora_answer_choice_explanation_notice_dismissed_v1';
const ANSWER_CHOICE_EXPLANATION_NOTICE_ANSWER_THRESHOLD = 3;
const DEFAULT_ACCESSIBILITY_PREFS = Object.freeze({
    largeText: false,
    highContrast: false,
    reduceMotion: false,
    voiceAgent: false,
    manualImport: false
});
const STUDY_PLANNER_STATE_VERSION = 1;
const STUDY_SETUP_DEFAULT_ANSWERS = Object.freeze({
    examTiming: '',
    preparedness: '',
    timeToday: '',
    priority: '',
    goal: ''
});
const STUDY_ACTION_KEYS = Object.freeze({
    dashboard: 'study_dashboard',
    setup: 'study_setup',
    quiz: 'start_quiz',
    flashcards: 'start_flashcards',
    urgentFocus: 'start_urgent_focus_set',
    quizReviewCards: 'quiz_review_cards',
    quizSpacedReview: 'quiz_spaced_review',
    flashcardSpacedReview: 'flashcard_spaced_review',
    history: 'open_history',
    resume: 'resume_last_mode'
});
const STUDY_ACTION_LABELS = Object.freeze({
    [STUDY_ACTION_KEYS.dashboard]: 'Study Dashboard',
    [STUDY_ACTION_KEYS.setup]: 'Study Setup',
    [STUDY_ACTION_KEYS.quiz]: 'Start Quiz',
    [STUDY_ACTION_KEYS.flashcards]: 'Quiz Review Cards',
    [STUDY_ACTION_KEYS.urgentFocus]: 'Start Urgent Focus Set',
    [STUDY_ACTION_KEYS.quizReviewCards]: 'Quiz Review Cards',
    [STUDY_ACTION_KEYS.quizSpacedReview]: 'Start Quiz Spaced Review (Anki-style)',
    [STUDY_ACTION_KEYS.flashcardSpacedReview]: 'Quiz Review Cards',
    [STUDY_ACTION_KEYS.history]: 'Open History or Load Another Session',
    [STUDY_ACTION_KEYS.resume]: 'Resume Last Mode'
});
const STUDY_SETUP_ALLOWED_VALUES = Object.freeze({
    examTiming: new Set(['none', 'within_1_week', 'within_2_weeks', 'within_1_month', 'later']),
    preparedness: new Set(['not_ready', 'somewhat_ready', 'mostly_ready']),
    timeToday: new Set(['15', '30', '45', '60_plus']),
    priority: new Set(['weak_cards', 'due_cards', 'coverage']),
    goal: new Set(['cram', 'balanced', 'retention'])
});
let accessibilityPrefs = { ...DEFAULT_ACCESSIBILITY_PREFS };

function createDefaultStudyDashboardState(overrides = {}) {
    const next = (overrides && typeof overrides === 'object') ? overrides : {};
    const lastModeKey = typeof next.lastModeKey === 'string' ? next.lastModeKey.trim() : '';
    const lastModeLabel = typeof next.lastModeLabel === 'string' ? next.lastModeLabel.trim() : '';
    const lastOpenedAt = Number(next.lastOpenedAt) || 0;
    const lastLaunchedAt = Number(next.lastLaunchedAt) || 0;
    const updatedAt = Number(next.updatedAt) || Math.max(lastOpenedAt, lastLaunchedAt, 0);
    return {
        version: STUDY_PLANNER_STATE_VERSION,
        hasSeenDashboard: !!next.hasSeenDashboard,
        lastOpenedAt,
        lastModeKey,
        lastModeLabel,
        lastLaunchedAt,
        updatedAt
    };
}

function normalizeStudySetupAnswers(rawAnswers = {}) {
    const raw = (rawAnswers && typeof rawAnswers === 'object') ? rawAnswers : {};
    const normalized = { ...STUDY_SETUP_DEFAULT_ANSWERS };
    Object.keys(normalized).forEach((key) => {
        const value = typeof raw[key] === 'string' ? raw[key].trim() : '';
        normalized[key] = STUDY_SETUP_ALLOWED_VALUES[key]?.has(value) ? value : '';
    });
    return normalized;
}

function createDefaultStudySetupState(overrides = {}) {
    const next = (overrides && typeof overrides === 'object') ? overrides : {};
    const completedAt = Number(next.completedAt) || 0;
    const updatedAt = Number(next.updatedAt) || completedAt || 0;
    return {
        version: STUDY_PLANNER_STATE_VERSION,
        answers: normalizeStudySetupAnswers(next.answers),
        completedAt,
        updatedAt
    };
}

function createDefaultStudyPlanViewSnapshot(overrides = {}) {
    const next = (overrides && typeof overrides === 'object') ? overrides : {};
    const filteredQuestionKeys = Array.isArray(next.filteredQuestionKeys)
        ? next.filteredQuestionKeys.map((key) => String(key || '').trim()).filter(Boolean)
        : [];
    return {
        sessionId: normalizeSessionId(next.sessionId),
        surface: ['dashboard', 'setup', 'flashcards', 'quiz'].includes(String(next.surface || '').trim())
            ? String(next.surface || '').trim()
            : 'quiz',
        filteredQuestionKeys,
        currentQuestionKey: String(next.currentQuestionKey || '').trim(),
        currentQuestionViewIndex: Math.max(0, Number(next.currentQuestionViewIndex) || 0),
        isFilterActive: !!next.isFilterActive,
        isFlashcardMode: !!next.isFlashcardMode,
        isAdaptiveReviewMode: !!next.isAdaptiveReviewMode,
        isFlashcardAdaptiveReviewMode: !!next.isFlashcardAdaptiveReviewMode,
        fcPage: Math.max(0, Number(next.fcPage) || 0),
        condensedQuizState: normalizeCondensedQuizState(next.condensedQuizState),
        highYieldQuizState: normalizeHighYieldQuizState(next.highYieldQuizState),
        adaptivePracticeState: normalizeAdaptivePracticeState(next.adaptivePracticeState),
        capturedAt: Number(next.capturedAt) || 0
    };
}

function createDefaultStudyPlanState(overrides = {}) {
    const next = (overrides && typeof overrides === 'object') ? overrides : {};
    const generatedAt = Number(next.generatedAt) || 0;
    const appliedAt = Number(next.appliedAt) || 0;
    const lastUndoneAt = Number(next.lastUndoneAt) || 0;
    const updatedAt = Number(next.updatedAt) || Math.max(generatedAt, appliedAt, lastUndoneAt, 0);
    const recommendation = next.recommendation && typeof next.recommendation === 'object'
        ? { ...next.recommendation }
        : null;
    const metricsSnapshot = next.metricsSnapshot && typeof next.metricsSnapshot === 'object'
        ? { ...next.metricsSnapshot }
        : null;
    const preApplySnapshot = next.preApplySnapshot && typeof next.preApplySnapshot === 'object'
        ? createDefaultStudyPlanViewSnapshot(next.preApplySnapshot)
        : null;
    const activeActionKey = typeof next.activeActionKey === 'string' ? next.activeActionKey.trim() : '';
    const activeActionLabel = typeof next.activeActionLabel === 'string' ? next.activeActionLabel.trim() : '';
    return {
        version: STUDY_PLANNER_STATE_VERSION,
        generatedAt,
        recommendation,
        metricsSnapshot,
        activeActionKey,
        activeActionLabel,
        appliedAt,
        lastUndoneAt,
        preApplySnapshot,
        updatedAt
    };
}

// Session Data
let currentSessionId = null;
// Counts session openings, so work that outlives a switch away and back can tell its
// session was reopened in between (the id alone cannot).
let sessionOpenEpoch = 0;
let currentSessionCustomName = '';
let currentSessionNumber = null;
/* A session the student created and named on purpose is kept while it is still empty.
   One that exists only because the app opened makes no such claim and is not kept. */
let currentSessionExplicit = false;
let sessionDirty = false;
let sessionStateRevision = 0;
let sessionLoadInFlight = null;
// The session the in-flight load is reading, and the latest different pick made
// while it runs. SessionManager.load runs that pick next instead of dropping it.
let sessionLoadRunningId = '';
let sessionLoadQueuedRequest = null;
let sessionHistoryLoadInFlight = null;
const sessionFolderMembershipCache = new Map();

function rememberSessionFolderMembership(sessionId, folderId) {
    const sessionKey = String(sessionId ?? '').trim();
    if (!sessionKey) return null;
    const normalizedFolderId = String(folderId ?? '').trim() || null;
    sessionFolderMembershipCache.set(sessionKey, normalizedFolderId);
    return normalizedFolderId;
}

function resolveSessionFolderMembershipForSave(sessionId, storedSession = null) {
    const sessionKey = String(sessionId ?? '').trim();
    if (!sessionKey) return null;
    if (sessionFolderMembershipCache.has(sessionKey)) {
        return sessionFolderMembershipCache.get(sessionKey);
    }
    return String(storedSession?.folderId ?? '').trim() || null;
}

function markSessionDirty() {
    sessionDirty = true;
    sessionStateRevision += 1;
}

let userAnswers = {};
let savedQuestions = {};
let knowledgeState = {};
let sessionManifest = { jsons: [], pdfs: [] };
const CONDENSED_QUIZ_TOGGLE_BTN_ID = 'condensed-quiz-toggle-btn';
const CONDENSED_QUIZ_STATE_VERSION = 1;
let highYieldQuizState = normalizeHighYieldQuizState();
let adaptivePracticeState = normalizeAdaptivePracticeState();
let highYieldLastAppliedPool = null;
let condensedQuizState = {
    version: CONDENSED_QUIZ_STATE_VERSION,
    questionKeys: [],
    selectedSetKeys: [],
    targetCount: 0,
    sourceQuestionCount: 0,
    promptRequest: '',
    enabled: false,
    updatedAt: 0
};

// NEW: SRS & Stats Data
let srsData = {}; // { qIdx: { interval: 1, repetition: 0, ef: 2.5, nextReview: timestamp } }
let statsData = {}; // { qIdx: [ { date: timestamp, duration: ms, correct: bool } ] }
let currentQuestionStartTime = 0;
let forcedChoiceOrderByQuestion = {}; // { qIdx: [choiceIndexOrder] }
let mixedChoiceOrderByQuestion = {}; // Stable per-question order while Mix Answers is enabled
let isAdaptiveReviewMode = false;
let isFlashcardAdaptiveReviewMode = false;
// 'due' is the spaced-review queue; 'all' is a straight pass through the deck.
let flashcardReviewScope = 'due';
let flashcardPassComplete = false;
let flashcardPassRatings = new Map();
// Keys of a deliberately narrowed pass (Review the missed); null for a whole-deck pass.
let flashcardPassSubsetKeys = null;
// Straight-pass position, ratings and undo per deck (keyed by its active set keys), by question
// key, saved with the session so Resume continues a pass instead of restarting it.
let flashcardPassStateByDeck = {};
// The deck the open pass belongs to, fixed when the pass starts: a bank switch changes the active
// set keys before the pool is rebuilt, so the current keys cannot name the leaving pass.
let flashcardPassDeckId = '';
let pendingAdaptiveRating = null;
let adaptiveAnswerRevealedAt = 0;
const ADAPTIVE_MIN_REVIEW_MS = 0;
let adaptiveSnapshotRankFilters = new Set();
let adaptiveSnapshotCollapsedRanks = new Set();
let adaptiveReviewSnapshot = {};
let aiQuestionCacheByIndex = {};
let adaptiveSessionSeenIndices = new Set();
let adaptiveLastRenderedIdx = null;
let adaptiveClearedAnswersBackup = {};
let adaptiveResumeAnchorIdx = null;
const ADAPTIVE_CONFIDENCE_TO_RANK = { again: 1, shaky: 2, good: 3, easy: 4 };
const ADAPTIVE_RANK_LABELS = {
    1: 'Rank 1 (Again)',
    2: 'Rank 2 (Hard)',
    3: 'Rank 3 (Good)',
    4: 'Rank 4 (Easy)'
};
const ADAPTIVE_CONFIDENCE_STORE_KEY = 'memora.adaptive.confidence.v1';
const SESSION_QUICK_SNAPSHOT_KEY = `memora.session.quickSnapshot.v1:${window.location.pathname}`;
const WELCOME_WARMUP_STORAGE_KEY = 'memora_welcome_warmup_questions_v1';
const WELCOME_WARMUP_STORE_VERSION = 1;
const WELCOME_WARMUP_UPDATED_EVENT = 'memora:warmup-questions-updated';
const WELCOME_WARMUP_REVEAL_DELAY_MS = 1050;
const WELCOME_STATUS_REVEAL_DELAY_MS = 2100;
const SESSION_PROGRESS_META_VERSION = 1;
let adaptiveConfidenceByKey = {};
let adaptiveConfidenceSaveTimer = null;
let adaptiveConfidenceHydrated = false;
let adaptiveConfidenceHydrationInFlight = null;
let sessionProgressRevision = 0;
let sessionProgressUpdatedAt = 0;
let studyDashboardState = createDefaultStudyDashboardState();
let studySetupState = createDefaultStudySetupState();
let studyPlanState = createDefaultStudyPlanState();
try {
    const rawStore = localStorage.getItem(ADAPTIVE_CONFIDENCE_STORE_KEY);
    const parsedStore = rawStore ? JSON.parse(rawStore) : {};
    adaptiveConfidenceByKey = (parsedStore && typeof parsedStore === 'object') ? parsedStore : {};
} catch (_) {
    adaptiveConfidenceByKey = {};
}

// XP & Achievements System
let xpData = { totalXP: 0, level: 1 };
let achievementsData = {}; // { badgeId: unlockTimestamp }
let sessionXP = 0; // XP earned this session
let sessionMementos = 0; // Mementos granted directly this session (daily bonus, chests, milestones)
const MAX_STREAK_SHIELDS = 2;
let sessionAchievements = []; // Achievements unlocked this session
let totalQuestionsAnswered = 0; // For achievement tracking

// A handful of level milestones; the level badge in the header already shows every level.
const LEVEL_ACHIEVEMENT_MILESTONES = [5, 10, 25, 50, 75, 100];

const LEVEL_ACHIEVEMENT_RARITY = { 5: 'common', 10: 'common', 25: 'uncommon', 50: 'rare', 75: 'epic', 100: 'legendary' };

const LEVEL_ACHIEVEMENT_ENTRIES = Object.fromEntries(
    LEVEL_ACHIEVEMENT_MILESTONES.map((level) => [
        `level-${level}`,
        {
            name: `Level ${level}`,
            track: 'level',
            rarity: LEVEL_ACHIEVEMENT_RARITY[level] || 'rare',
            condition: () => xpData.level >= level
        }
    ])
);

// Achievement definitions. `track` picks the line glyph (see achievementGlyph).
const ACHIEVEMENTS = {
    // Mode participation stays separate from verified mastery evidence.
    'cards-reviewed-100': { name: 'A hundred reviews', track: 'volume', rarity: 'rare', condition: () => (xpData.gamePath?.activity?.flashcards || 0) >= 100 },
    'clinic-case-closed': { name: 'First case closed', track: 'precision', rarity: 'rare', condition: () => (xpData.gamePath?.completions?.clinic || 0) >= 1 },
    'guide-reader': { name: 'Guide reader', track: 'volume', rarity: 'rare', condition: () => (xpData.gamePath?.activity?.guide || 0) >= 100 },
    'occlusion-reviewed-100': { name: 'Image recall', track: 'volume', rarity: 'rare', condition: () => (xpData.gamePath?.activity?.occlusion || 0) >= 100 },
    // Answers
    'first-answer': { name: 'First answer', track: 'volume', rarity: 'common', condition: () => totalQuestionsAnswered >= 1 },
    'getting-started': { name: 'Warming up', track: 'volume', rarity: 'common', condition: () => totalQuestionsAnswered >= 25 },
    '100-questions': { name: 'Hundred club', track: 'volume', rarity: 'common', condition: () => totalQuestionsAnswered >= 100 },
    '500-questions': { name: 'Five hundred in', track: 'volume', rarity: 'uncommon', condition: () => totalQuestionsAnswered >= 500 },
    '1000-questions': { name: 'Thousand club', track: 'volume', rarity: 'rare', condition: () => totalQuestionsAnswered >= 1000 },
    '2500-questions': { name: 'Deep bench', track: 'volume', rarity: 'epic', condition: () => totalQuestionsAnswered >= 2500 },
    '5000-questions': { name: 'Five thousand strong', track: 'volume', rarity: 'epic', condition: () => totalQuestionsAnswered >= 5000 },

    // Correct in a row
    'streak-5': { name: 'On a roll', track: 'streak', rarity: 'common', condition: () => currentStreak >= 5 },
    'streak-10': { name: 'Ten straight', track: 'streak', rarity: 'common', condition: () => currentStreak >= 10 },
    'streak-25': { name: 'Locked in', track: 'streak', rarity: 'uncommon', condition: () => currentStreak >= 25 },
    'streak-40': { name: 'In the zone', track: 'streak', rarity: 'rare', condition: () => currentStreak >= 40 },
    'streak-50': { name: 'Fifty straight', track: 'streak', rarity: 'rare', condition: () => currentStreak >= 50 },
    'streak-75': { name: 'Unshaken', track: 'streak', rarity: 'rare', condition: () => currentStreak >= 75 },
    'streak-100': { name: 'Hundred straight', track: 'streak', rarity: 'epic', condition: () => currentStreak >= 100 },
    'streak-150': { name: 'Unbroken', track: 'streak', rarity: 'legendary', condition: () => currentStreak >= 150 },
    'streak-200': { name: 'Two hundred straight', track: 'streak', rarity: 'mythic', condition: () => currentStreak >= 200 },

    // Quick and correct
    'tempo-5': { name: 'Quick recall', track: 'tempo', rarity: 'common', condition: () => quickAnswerStreak >= 5 },
    'tempo-15': { name: 'Fast and right', track: 'tempo', rarity: 'rare', condition: () => quickAnswerStreak >= 15 },
    'tempo-30': { name: 'Reflexes', track: 'tempo', rarity: 'epic', condition: () => quickAnswerStreak >= 30 },

    ...LEVEL_ACHIEVEMENT_ENTRIES,

    // Study days and time of day
    'dedicated': { name: 'Three days running', track: 'days', rarity: 'common', condition: () => studyDays >= 3 },
    'week-warrior': { name: 'A full week', track: 'days', rarity: 'uncommon', condition: () => studyDays >= 7 },
    'fortnight-force': { name: 'Two weeks', track: 'days', rarity: 'rare', condition: () => studyDays >= 14 },
    'month-master': { name: 'A month of study', track: 'days', rarity: 'epic', condition: () => studyDays >= 30 },
    'cadence-90': { name: 'A season of study', track: 'days', rarity: 'legendary', condition: () => studyDays >= 90 },
    'night-owl': { name: 'Night owl', track: 'night', rarity: 'uncommon', condition: () => new Date().getHours() >= 22 },
    'early-bird': { name: 'Early bird', track: 'dawn', rarity: 'uncommon', condition: () => new Date().getHours() < 7 },

    // Fully answered quiz banks with at least five scored questions, once per set per day.
    'mastery-3': { name: 'Mastery III', track: 'precision', rarity: 'rare', condition: () => (globalThis.memoraGameStudy?.masteryCount(xpData) ?? xpData.masteryQuizzes?.count ?? 0) >= 3 },
    'mastery-10': { name: 'Mastery X', track: 'precision', rarity: 'epic', condition: () => (globalThis.memoraGameStudy?.masteryCount(xpData) ?? xpData.masteryQuizzes?.count ?? 0) >= 10 },
    'mastery-25': { name: 'Mastery XXV', track: 'precision', rarity: 'legendary', condition: () => (globalThis.memoraGameStudy?.masteryCount(xpData) ?? xpData.masteryQuizzes?.count ?? 0) >= 25 },
    'mastery-50': { name: 'Mastery L', track: 'precision', rarity: 'mythic', condition: () => (globalThis.memoraGameStudy?.masteryCount(xpData) ?? xpData.masteryQuizzes?.count ?? 0) >= 50 },

    // Learning quality
    'resilient': { name: 'Bounced back', track: 'recovery', rarity: 'rare', condition: () => resilienceCount >= 3 },
    'resilient-10': { name: 'Comeback habit', track: 'recovery', rarity: 'epic', condition: () => resilienceCount >= 10 },
    'deep-diver': { name: 'Checked the source', track: 'sources', rarity: 'uncommon', condition: () => evidenceViews >= 20 },
    'evidence-master': { name: 'Source reader', track: 'sources', rarity: 'legendary', condition: () => evidenceViews >= 100 },
    'precision-10': { name: 'Clean ten', track: 'precision', rarity: 'uncommon', condition: () => flawlessStreak >= 10 },
    'flawless-25': { name: 'Clean twenty-five', track: 'precision', rarity: 'epic', condition: () => flawlessStreak >= 25 },
    'precision-40': { name: 'Clean forty', track: 'precision', rarity: 'legendary', condition: () => flawlessStreak >= 40 },

    // Session feats
    'perfect-session': { name: 'Perfect session', track: 'session', rarity: 'rare', condition: 'session' },
    'marathon': { name: 'Marathon', track: 'session', rarity: 'epic', condition: 'session' },
    'ultra-marathon': { name: 'Ultramarathon', track: 'session', rarity: 'legendary', condition: 'session' },

    // The long goal
    'apex-ascendant': {
        name: 'Summit',
        track: 'apex',
        rarity: 'mythic',
        condition: () =>
            totalQuestionsAnswered >= 10000 &&
            xpData.level >= 100 &&
            studyDays >= 90 &&
            currentStreak >= 150 &&
            evidenceViews >= 300
    }
};

const ACHIEVEMENT_RARITY_ORDER = {
    common: 0,
    uncommon: 1,
    rare: 2,
    epic: 3,
    legendary: 4,
    mythic: 5
};

const ACHIEVEMENT_RARITY_LABELS = {
    common: 'Common',
    uncommon: 'Uncommon',
    rare: 'Rare',
    epic: 'Epic',
    legendary: 'Legendary',
    mythic: 'Mythic'
};

// Every achievement pays a small Memento reward, scaled by how hard it is.
const ACHIEVEMENT_MEMENTOS = { common: 5, uncommon: 10, rare: 20, epic: 40, legendary: 75, mythic: 120 };

function getAchievementMementos(ach) {
    return ACHIEVEMENT_MEMENTOS[ach && ach.rarity] || ACHIEVEMENT_MEMENTOS.common;
}

const ACHIEVEMENT_GLYPH_PATHS = {
    volume: '<path d="M5 7h14M5 12h14M5 17h9"/>',
    streak: '<path d="M12 3c.6 3.2 4.5 5 4.5 9.5a4.5 4.5 0 0 1-9 0c0-2.2 1-3.6 2.2-4.6.1 1.9.9 3 2 3.3-.5-2.8-.4-5.2.3-8.2z"/>',
    tempo: '<path d="M13 3 6 13.5h5.5L10.5 21 18 10.5h-5.5z"/>',
    level: '<path d="m6 13 6-6 6 6M6 19l6-6 6 6"/>',
    days: '<rect x="4" y="5.5" width="16" height="14.5" rx="2.5"/><path d="M4 10.5h16M9 3.5v4M15 3.5v4"/>',
    night: '<path d="M19.5 14.5A7.5 7.5 0 1 1 9.5 4.5a6 6 0 0 0 10 10z"/>',
    dawn: '<path d="M4 18h16M7.5 18a4.5 4.5 0 0 1 9 0M12 6.5v3M6 10.5l1.8 1.5M18 10.5l-1.8 1.5"/>',
    recovery: '<path d="M4.5 12a7.5 7.5 0 1 0 2.2-5.3M4.5 4v4h4"/>',
    sources: '<path d="M4 5.5h5.5A2.5 2.5 0 0 1 12 8v12a2 2 0 0 0-2-2H4zM20 5.5h-5.5A2.5 2.5 0 0 0 12 8v12a2 2 0 0 1 2-2h6z"/>',
    precision: '<circle cx="12" cy="12" r="8"/><circle cx="12" cy="12" r="3.5"/><circle cx="12" cy="12" r=".6"/>',
    session: '<circle cx="12" cy="12" r="8"/><path d="m8.5 12 2.5 2.5 4.5-5"/>',
    apex: '<path d="m3 19 6.5-11 4 6.5 2-3L21 19z"/>'
};

// A quiet line glyph per achievement track, drawn in currentColor.
function achievementGlyph(ach) {
    const paths = ACHIEVEMENT_GLYPH_PATHS[ach && ach.track] || ACHIEVEMENT_GLYPH_PATHS.session;
    return `<svg class="gm-ach-glyph" viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${paths}</svg>`;
}


// Additional tracking variables for new achievements
let flawlessStreak = 0; // First-try correct answers in a row
let resilienceCount = 0; // Times gotten 5 correct after 3 wrong
let wrongStreak = 0; // Consecutive wrong answers
let recoveryCorrect = 0; // Correct answers since last wrong streak
let quickAnswerStreak = 0; // Fast answers in a row
let evidenceViews = 0; // Evidence panel opened count
let studyDays = 0; // Consecutive days studied
let lastStudyDate = null; // For tracking daily streaks

// Game Mode Toggle (XP & Achievements)
let isGameMode = false;

let currentQIndex = 0;
const QUIZ_COMPLETION_COHORT_VERSION = 1;
let quizCompletionCohortState = {
    version: QUIZ_COMPLETION_COHORT_VERSION,
    activeQuestionKeys: [],
    pendingNewQuestionKeys: [],
    updatedAt: 0
};
let currentStreak = 0; // New Variable

// Mystery Card System
let mysteryCardThreshold = 5;
let mysteryCardPending = false;

// Growth Bonus Tracking - questions answered wrong before. Entries are question
// persistence keys, so a miss follows the question itself: a new session or a
// reordered bank cannot hand one question's miss to whatever now sits at its index.
// Callers may still pass an index into the active bank; it resolves to that key.
class QuestionKeyHistorySet extends Set {
    static toKey(value) {
        if (typeof value === 'string') return value.trim();
        const idx = Number(value);
        return Number.isInteger(idx) ? buildQuestionPersistenceKeyByIndex(idx) : '';
    }
    add(value) { const key = QuestionKeyHistorySet.toKey(value); if (key) super.add(key); return this; }
    has(value) { const key = QuestionKeyHistorySet.toKey(value); return !!key && super.has(key); }
    delete(value) { const key = QuestionKeyHistorySet.toKey(value); return !!key && super.delete(key); }
}
// Numeric entries from older builds name no bank, so they cannot be attributed and are dropped.
function createWrongAnswerHistory(values) {
    const history = new QuestionKeyHistorySet();
    (Array.isArray(values) ? values : []).forEach((value) => { if (typeof value === 'string') history.add(value); });
    return history;
}
let wrongAnswerHistory = new QuestionKeyHistorySet();

const MYSTERY_STREAK_PROFILES = {
    core: { label: 'Core Streak', detail: 'Consistent correct answers', min: 5, max: 10 },
    tempo: { label: 'Tempo Streak', detail: 'Fast answer chain', min: 4, max: 8 },
    precision: { label: 'Precision Streak', detail: 'First-try accuracy chain', min: 6, max: 11 },
    cadence: { label: 'Cadence Streak', detail: 'Daily consistency run', min: 5, max: 9 }
};

function getCurrentMysteryStreakProfile() {
    if (quickAnswerStreak >= 8) return 'tempo';
    if (flawlessStreak >= 12) return 'precision';
    if (studyDays >= 7) return 'cadence';
    return 'core';
}

function getRandomMysteryThreshold() {
    const profileKey = getCurrentMysteryStreakProfile();
    const profile = MYSTERY_STREAK_PROFILES[profileKey] || MYSTERY_STREAK_PROFILES.core;
    return Math.floor(Math.random() * (profile.max - profile.min + 1)) + profile.min;
}
mysteryCardThreshold = getRandomMysteryThreshold();


// Filters & Modes
let isFilterActive = false;
let isShuffled = false;
let isHardMode = false;
let isMixAnswers = false;
let isAmbientMode = false;
let isZenMode = false;
let isDarkMode = false;
let isFlashcardMode = false;
let isBorderMode = false;

// Auto Modes
let autoSaveWrong = true;
let autoAdvance = false;
let autoEvidence = false;
let autoExplanation = false;
let autoEvidenceTimer = null;
let autoExplanationTimer = null;
let suppressAutoStudyOpenOnNextQuestionRender = false;
// A solo landing holds automatic sources off for the question it lands on, across however many
// times that question re-renders while the quiz opens; the next question resumes them.
let suppressAutoStudyOpenUntilQuestionChange = false;
let suppressedAutoStudyQuestionRealIdx = null;

function clearAutoStudyTimers() {
    if (autoEvidenceTimer) {
        clearTimeout(autoEvidenceTimer);
        autoEvidenceTimer = null;
    }
    if (autoExplanationTimer) {
        clearTimeout(autoExplanationTimer);
        autoExplanationTimer = null;
    }
}
let questionRenderToken = 0;
let lastRenderedQuestionRealIdx = null;

const PUBLIC_TOUR_SAMPLE_QUESTION = Object.freeze({
    q: 'Sample Question — this is where a question from your own lecture will appear. Which of these is the demo answer?',
    c: Object.freeze([
        'Sample choice A',
        'Sample choice B',
        'Sample choice C',
        'Sample choice D'
    ]),
    a: 1,
    e: 'Sample explanation. Real questions explain why the correct answer is right, with the source slide linked beside it.',
    w: Object.freeze({
        0: 'Sample feedback. When you pick a wrong answer, Memoraful explains why that specific choice misses.',
        2: 'Sample feedback. When you pick a wrong answer, Memoraful explains why that specific choice misses.',
        3: 'Sample feedback. When you pick a wrong answer, Memoraful explains why that specific choice misses.'
    })
});
let publicTourQuizDemoRestore = null;

function isPublicTourQuizDemoActive() {
    return !!publicTourQuizDemoRestore;
}

function loadPublicTourSampleQuiz() {
    if (!publicTourQuizDemoRestore) {
        publicTourQuizDemoRestore = {
            questions,
            filteredIndices,
            currentQIndex,
            userAnswers,
            lastRenderedQuestionRealIdx,
            isAdaptiveReviewMode,
            isHardMode
        };
    }

    questions = [PUBLIC_TOUR_SAMPLE_QUESTION];
    filteredIndices = [0];
    currentQIndex = 0;
    userAnswers = {};
    isAdaptiveReviewMode = false;
    isHardMode = false;
    lastRenderedQuestionRealIdx = null;
    suppressAutoStudyOpenOnNextQuestionRender = true;
    renderQuestion();
    return true;
}

function restorePublicTourSampleQuiz() {
    const restore = publicTourQuizDemoRestore;
    if (!restore) return false;

    publicTourQuizDemoRestore = null;
    questions = restore.questions;
    filteredIndices = restore.filteredIndices;
    currentQIndex = restore.currentQIndex;
    userAnswers = restore.userAnswers;
    isAdaptiveReviewMode = restore.isAdaptiveReviewMode;
    isHardMode = restore.isHardMode;
    lastRenderedQuestionRealIdx = restore.lastRenderedQuestionRealIdx;
    suppressAutoStudyOpenOnNextQuestionRender = true;
    renderQuestion();
    return true;
}

window.isPublicTourQuizDemoActive = isPublicTourQuizDemoActive;
window.loadPublicTourSampleQuiz = loadPublicTourSampleQuiz;
window.restorePublicTourSampleQuiz = restorePublicTourSampleQuiz;

// Config
let ambientSize = 'medium';
let ambientType = 'dynamic';
let fcPage = 0;
let fcLimit = 10;

const DEMO_QUESTION_SET_KEY = 'Demo_Anatomy_Quiz.json';
const DEMO_PDF_NAME = 'Demo Lecture.pdf';
const DEMO_QUESTIONS_TEMPLATE = [
    {
        q: 'Which gray matter column is primarily responsible for autonomic outflow and appears only in specific spinal segments?',
        c: ['Posterior gray column', 'Anterior gray column', 'Lateral gray column', 'Central canal'],
        a: 2,
        e: 'The lateral gray column contains autonomic neurons and is classically present in T1-L2 (sympathetic) and S2-S4 (parasympathetic).',
        demoHint: 'Look for the option tied to autonomic output rather than sensory input or somatic motor control. Also focus on a structure that is only present in specific cord levels.',
        demoExplain: `- **Correct:** C is right because the lateral gray column houses autonomic preganglionic neurons.
- **Why others fail:** Posterior is sensory; anterior is somatic motor; central canal is a CSF space, not an autonomic column.
- **Takeaway:** Segment-limited anatomy is a major clue for autonomic pathways.`,
        demoDeepDive: `## Core Concept
The **lateral gray column** is the spinal gray matter region linked to autonomic output. It is not present at every level of the cord, which makes it anatomically distinctive.

## Key Details
- Found prominently in **T1-L2** for sympathetic outflow.
- Also functionally represented in **S2-S4** for parasympathetic pathways.
- Posterior horn handles sensory input; anterior horn handles somatic motor output.
- This distinction is high-yield for neuroanatomy questions.

## Connections
- Connects directly to autonomic chain and prevertebral ganglia concepts.
- Links to visceral reflex pathways and organ-level control.
- Commonly tested beside dorsal/ventral horn function.

## Why It Matters
If you can separate **somatic vs autonomic** structures quickly, you answer spinal cord questions faster and with fewer errors.`,
        sourceFile: DEMO_QUESTION_SET_KEY,
        add: 'Slide 1'
    },
    {
        q: 'What is the core function of the blood-brain barrier?',
        c: ['Produce cerebrospinal fluid', 'Regulate transport into the CNS', 'Generate action potentials', 'Store neurotransmitters'],
        a: 1,
        e: 'The BBB selectively regulates movement of substances from blood into CNS tissue.',
        demoHint: 'Pick the choice that describes selective control of what enters neural tissue. The BBB is primarily a filtration and regulation interface.',
        demoExplain: `- **Correct:** B is correct because the BBB controls molecular and ionic entry into CNS tissue.
- **Why others fail:** CSF production is mostly choroid plexus; action potentials are neuronal; neurotransmitter storage is synaptic.
- **Takeaway:** BBB questions usually test selective permeability, not signal generation.`,
        demoDeepDive: `## Core Concept
The **blood-brain barrier (BBB)** protects neural tissue by tightly regulating exchange between blood and the CNS.

## Key Details
- Built from endothelial tight junctions, basement membrane, and astrocyte support.
- Allows essential nutrients while blocking many toxins and pathogens.
- Maintains ionic stability required for neuronal signaling.
- BBB disruption can increase inflammation and neurologic risk.

## Connections
- Connected to CNS infection pharmacology and drug delivery limitations.
- Linked to neuroinflammation and edema in pathology.
- Complements meningeal and CSF protective systems.

## Why It Matters
Understanding BBB function explains why some therapies fail to reach brain tissue and why barrier breakdown worsens neurologic disease.`,
        sourceFile: DEMO_QUESTION_SET_KEY,
        add: 'Page 23'
    },
    {
        q: 'Which cranial nerve has the broadest visceral distribution?',
        c: ['Trigeminal (CN V)', 'Facial (CN VII)', 'Vagus (CN X)', 'Hypoglossal (CN XII)'],
        a: 2,
        e: 'CN X travels from brainstem into thorax and abdomen, making it the most extensive cranial nerve distribution.',
        demoHint: 'Choose the cranial nerve that "wanders" far beyond the head and neck into thoracic and abdominal organs.',
        demoExplain: `- **Correct:** C is correct because CN X has the most extensive parasympathetic and visceral reach.
- **Why others fail:** CN V is facial sensation/mastication, CN VII is facial expression/taste, CN XII controls tongue movement.
- **Takeaway:** Broad organ innervation usually points to the vagus nerve.`,
        demoDeepDive: `## Core Concept
The **vagus nerve (CN X)** has the widest distribution of any cranial nerve, extending from brainstem to abdominal viscera.

## Key Details
- Provides parasympathetic control to heart, lungs, and GI tract.
- Carries visceral sensory input back to the CNS.
- Influences voice and swallowing via laryngeal/pharyngeal branches.
- Central to autonomic homeostasis.

## Connections
- Ties into baroreceptor reflex and heart-rate modulation.
- Related to gut-brain communication and vagal tone.
- Appears in both neuroanatomy and physiology exam blocks.

## Why It Matters
Recognizing CN X quickly helps in clinical localization, autonomic questions, and interpreting cardiorespiratory reflex findings.`,
        sourceFile: DEMO_QUESTION_SET_KEY,
        add: 'Page 89'
    },
    {
        q: 'The corpus callosum primarily connects which structures?',
        c: ['Thalamus and hypothalamus', 'Left and right cerebral hemispheres', 'Cerebrum and cerebellum', 'Brainstem and spinal cord'],
        a: 1,
        e: 'It is the major commissural tract for interhemispheric communication.',
        demoHint: 'Look for the structure that unifies cortical processing between both sides of the cerebrum.',
        demoExplain: `- **Correct:** B is correct because the corpus callosum is the largest commissural tract between hemispheres.
- **Why others fail:** The other options describe unrelated axes of connection.
- **Takeaway:** "Commissural tract" is the key phrase for interhemispheric linkage.`,
        demoDeepDive: `## Core Concept
The **corpus callosum** is the principal white-matter bridge connecting left and right cerebral hemispheres.

## Key Details
- Enables rapid interhemispheric communication.
- Composed of millions of crossing axons.
- Supports integrated motor, sensory, and cognitive function.
- Classically divided into rostrum, genu, body, and splenium.

## Connections
- Connects to split-brain phenomena after callosotomy.
- Related to lateralization and hemispheric specialization topics.
- Often contrasted with projection and association fibers.

## Why It Matters
If commissural pathways are damaged, cross-hemispheric coordination declines, which has direct clinical and cognitive consequences.`,
        sourceFile: DEMO_QUESTION_SET_KEY,
        add: 'Page 34'
    },
    {
        q: 'Which neuroglial cell type forms myelin in the CNS?',
        c: ['Astrocytes', 'Microglia', 'Oligodendrocytes', 'Schwann cells'],
        a: 2,
        e: 'Oligodendrocytes myelinate CNS axons; Schwann cells do this in the PNS.',
        demoHint: 'Differentiate CNS glia from PNS glia. One cell type can myelinate multiple CNS axons.',
        demoExplain: `- **Correct:** C is correct because oligodendrocytes produce CNS myelin.
- **Why others fail:** Astrocytes support/metabolic roles, microglia are immune cells, Schwann cells are PNS myelin.
- **Takeaway:** CNS myelin equals oligodendrocytes; PNS myelin equals Schwann cells.`,
        demoDeepDive: `## Core Concept
**Oligodendrocytes** are the CNS glial cells responsible for myelin sheath formation.

## Key Details
- One oligodendrocyte can myelinate segments on multiple axons.
- Myelin improves conduction velocity and signal efficiency.
- Schwann cells are the analogous myelinating cells in the PNS.
- Demyelinating disorders often target oligodendrocyte-related pathways.

## Connections
- Linked to saltatory conduction and node of Ranvier physiology.
- Appears alongside neuroglia classification questions.
- Connects to CNS demyelinating disease frameworks.

## Why It Matters
Correctly identifying glial roles prevents common neuro confusion and improves rapid elimination on board-style questions.`,
        sourceFile: DEMO_QUESTION_SET_KEY,
        add: 'Page 15'
    }
];

let demoPdfBlobUrlCache = null;
const demoAiMemoryCache = new Map();

function escapePdfTextForContentStream(text) {
    return String(text || '')
        .replace(/\\/g, '\\\\')
        .replace(/\(/g, '\\(')
        .replace(/\)/g, '\\)');
}

function createDemoPdfBlobUrl() {
    if (demoPdfBlobUrlCache) return demoPdfBlobUrlCache;

    const stream = `q\nBT /F1 46 Tf 145 380 Td (${escapePdfTextForContentStream('Sample Lecture')}) Tj ET\nQ`;
    const objects = [
        '1 0 obj\n<< /Type /Catalog /Pages 2 0 R >>\nendobj',
        '2 0 obj\n<< /Type /Pages /Kids [3 0 R] /Count 1 >>\nendobj',
        '3 0 obj\n<< /Type /Page /Parent 2 0 R /MediaBox [0 0 612 792] /Resources << /Font << /F1 5 0 R >> >> /Contents 4 0 R >>\nendobj',
        `4 0 obj\n<< /Length ${stream.length} >>\nstream\n${stream}\nendstream\nendobj`,
        '5 0 obj\n<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica-Bold >>\nendobj'
    ];

    let pdf = '%PDF-1.4\n%MemoraDemo\n';
    const offsets = [0];
    for (const obj of objects) {
        offsets.push(pdf.length);
        pdf += `${obj}\n`;
    }

    const xrefOffset = pdf.length;
    pdf += `xref\n0 ${objects.length + 1}\n`;
    pdf += '0000000000 65535 f \n';
    for (let i = 1; i <= objects.length; i++) {
        pdf += `${String(offsets[i]).padStart(10, '0')} 00000 n \n`;
    }
    pdf += `trailer\n<< /Size ${objects.length + 1} /Root 1 0 R >>\nstartxref\n${xrefOffset}\n%%EOF`;

    demoPdfBlobUrlCache = URL.createObjectURL(new Blob([new TextEncoder().encode(pdf)], { type: 'application/pdf' }));
    return demoPdfBlobUrlCache;
}

function getDemoQuestions() {
    return DEMO_QUESTIONS_TEMPLATE.map((q) => ({
        ...q,
        c: Array.isArray(q.c) ? [...q.c] : []
    }));
}

function normalizeDemoQuestionText(text) {
    return String(text || '').toLowerCase().replace(/\s+/g, ' ').trim();
}

const DEMO_QUESTION_LOOKUP = new Map(
    DEMO_QUESTIONS_TEMPLATE.map((entry) => [normalizeDemoQuestionText(entry.q), entry])
);

function getDemoTemplateForQuestion(qIndex = currentQIndex) {
    const qText = questions?.[qIndex]?.q;
    const direct = DEMO_QUESTION_LOOKUP.get(normalizeDemoQuestionText(qText));
    if (direct) return direct;
    return DEMO_QUESTIONS_TEMPLATE[qIndex % DEMO_QUESTIONS_TEMPLATE.length] || DEMO_QUESTIONS_TEMPLATE[0];
}

function applyDemoUiState() {
    if (!IS_EMBED_DEMO) return;

    document.body.classList.add('demo-mode');
    setWelcomeActionsPending(false);

    if (els.continueBtn) els.continueBtn.style.display = 'none';
    if (els.welcomeHistoryBtn) els.welcomeHistoryBtn.style.display = 'none';

    if (els.newSessionBtn) {
        els.newSessionBtn.style.display = 'inline-flex';
        const label = els.newSessionBtn.querySelector('.welcome-btn-text');
        if (label) label.textContent = 'Restart Demo';
    }

    document.getElementById('demo-mode-badge')?.remove();
}

function getEmbeddedDemoPdfAssetUrl() {
    try {
        return new URL('assets/demo-lecture.pdf', document.baseURI).href;
    } catch (_) {
        return 'assets/demo-lecture.pdf';
    }
}

function ensureEmbeddedDemoLecturePdf() {
    if (!IS_EMBED_DEMO) return false;

    const demoPdfAssetUrl = getEmbeddedDemoPdfAssetUrl();
    if (!loadedPdfFiles || typeof loadedPdfFiles !== 'object') loadedPdfFiles = {};
    if (!loadedPdfFiles[DEMO_PDF_NAME]) loadedPdfFiles[DEMO_PDF_NAME] = demoPdfAssetUrl;

    if (!sessionManifest || typeof sessionManifest !== 'object') {
        sessionManifest = { jsons: [], pdfs: [] };
    }
    if (!Array.isArray(sessionManifest.pdfs)) sessionManifest.pdfs = [];
    if (!sessionManifest.pdfs.includes(DEMO_PDF_NAME)) sessionManifest.pdfs.push(DEMO_PDF_NAME);

    const demoPdfUrl = loadedPdfFiles[DEMO_PDF_NAME];
    currentPdfBlobUrl = demoPdfUrl;
    if (lastPdfState?.fileName !== DEMO_PDF_NAME) {
        lastPdfState = {
            src: demoPdfUrl,
            title: DEMO_PDF_NAME,
            fileName: DEMO_PDF_NAME,
            page: 1,
            searchTerm: ''
        };
    } else if (!lastPdfState.src) {
        lastPdfState = { ...lastPdfState, src: demoPdfUrl };
    }
    if (els.pdfTitle && (!els.pdfTitle.innerText || els.pdfTitle.innerText === 'Add a file to see it here')) {
        els.pdfTitle.innerText = DEMO_PDF_NAME;
    }
    if (IS_EMBED_DEMO) {
        // The landing session starts with import language, but Create is the
        // next action once the walkthrough has staged its bundled lecture.
        quizEmptyStateTitleOverride = 'Click Create to start';
        if (typeof syncQuizEmptyStateMessage === 'function') syncQuizEmptyStateMessage();
    }
    if (/^file:/i.test(demoPdfAssetUrl)) pdfForceIframeMode = true;
    return true;
}

window.ensureEmbeddedDemoLecturePdf = ensureEmbeddedDemoLecturePdf;

function loadEmbeddedDemoSession(showNotice = false, options = {}) {
    if (!IS_EMBED_DEMO) return;

    const shouldOpenViewer = options?.openViewer !== false;
    const shouldLoadQuestions = options?.loadQuestions !== false;

    demoAiMemoryCache.clear();
    currentSessionId = 'demo-session';
    currentSessionCustomName = 'Demo Session';
    currentSessionNumber = null;
    syncWorkingSessionTitle();
    userAnswers = {};
    savedQuestions = {};
    knowledgeState = {};
    srsData = {};
    statsData = {};
    forcedChoiceOrderByQuestion = {};
    mixedChoiceOrderByQuestion = {};
    condensedQuizState = createDefaultCondensedQuizState();
    highYieldQuizState = normalizeHighYieldQuizState();
    adaptivePracticeState = normalizeAdaptivePracticeState();
    highYieldLastAppliedPool = null;
    isAdaptiveReviewMode = false;
    isFlashcardAdaptiveReviewMode = false;
    pendingAdaptiveRating = null;
    adaptiveAnswerRevealedAt = 0;
    adaptiveSnapshotRankFilters.clear();
    adaptiveSessionSeenIndices.clear();
    adaptiveLastRenderedIdx = null;
    adaptiveClearedAnswersBackup = {};
    adaptiveResumeAnchorIdx = null;
    aiQuestionCacheByIndex = {};
    currentQIndex = 0;
    currentStreak = 0;
    aiResponseCache = { index: -1, explanation: null, deepDive: null, hint: null };

    loadedQuestionSets = shouldLoadQuestions
        ? { [DEMO_QUESTION_SET_KEY]: getDemoQuestions() }
        : {};
    activeQuestionSetKeys = shouldLoadQuestions
        ? new Set([DEMO_QUESTION_SET_KEY])
        : new Set();
    sessionManifest = {
        jsons: shouldLoadQuestions ? [DEMO_QUESTION_SET_KEY] : [],
        pdfs: [DEMO_PDF_NAME]
    };
    pdfAssociations = shouldLoadQuestions
        ? { [DEMO_QUESTION_SET_KEY]: [DEMO_PDF_NAME] }
        : {};
    // Point the demo at the real lecture immediately. Fetching it into a blob
    // is still useful for generation, but a failed fetch must never expose the
    // one-page Sample Lecture placeholder in the viewer.
    const demoPdfAssetUrl = (() => {
        try {
            return new URL('assets/demo-lecture.pdf', document.baseURI).href;
        } catch (_) {
            return 'assets/demo-lecture.pdf';
        }
    })();
    // PDF.js cannot fetch a file:// URL, but the browser's native PDF frame can
    // display it. Set this before any later Source click so the live viewer
    // chooses that renderer instead of attempting a blocked PDF.js request.
    if (/^file:/i.test(demoPdfAssetUrl)) pdfForceIframeMode = true;
    loadedPdfFiles = { [DEMO_PDF_NAME]: demoPdfAssetUrl };
    currentPdfBlobUrl = demoPdfAssetUrl;
    lastPdfState = {
        src: demoPdfAssetUrl,
        title: DEMO_PDF_NAME,
        fileName: DEMO_PDF_NAME,
        page: 1,
        searchTerm: ''
    };
    if (els.pdfTitle) els.pdfTitle.innerText = DEMO_PDF_NAME;

    const hydrateDemoPdfAsset = async () => {
        try {
            const response = await fetch('assets/demo-lecture.pdf');
            if (!response.ok) return;
            const pdfBlob = await response.blob();
            if (!pdfBlob.size) return;

            const pdfUrl = URL.createObjectURL(pdfBlob);
            const isCurrentDemoLoad = IS_EMBED_DEMO
                && currentSessionId === 'demo-session'
                && loadedPdfFiles[DEMO_PDF_NAME] === demoPdfAssetUrl;
            if (!isCurrentDemoLoad) {
                URL.revokeObjectURL(pdfUrl);
                return;
            }

            loadedPdfFiles[DEMO_PDF_NAME] = pdfUrl;
            currentPdfBlobUrl = pdfUrl;
            if (lastPdfState?.fileName === DEMO_PDF_NAME) {
                lastPdfState = { ...lastPdfState, src: pdfUrl };
            }
            if (!shouldOpenViewer) return;
            loadPdfInPane(DEMO_PDF_NAME, {
                page: lastPdfState?.page || 1,
                searchTerm: lastPdfState?.searchTerm || '',
                smooth: false,
                ensureViewer: true,
                forceReload: true
            });
        } catch (_) {
            // Keep the direct static URL so the demo-only native PDF fallback
            // can still render it when fetch() is unavailable (for example,
            // when the prototype is opened from file://).
        }
    };
    // Make the real static lecture asset the synchronization point for the
    // generator and pane transitions. The fallback only covers initialization
    // while a local file server is responding.
    loadEmbeddedDemoSession._pdfAssetReady = hydrateDemoPdfAsset();
    void loadEmbeddedDemoSession._pdfAssetReady;

    if (shouldLoadQuestions) {
        rebuildActiveQuestions();
        enterQuizMode();
    } else {
        questions = [];
        filteredIndices = [];
        isFilterActive = false;
        showEmptySessionImportLanding();
    }
    if (shouldOpenViewer) toggleViewer(true);
    setLastSessionId(null);
    applyDemoUiState();
    updateImportButtonState();

    window.syncPdfTutorSegmentZoomMenuButton?.();

    if (showNotice) {
        showPopup('Demo loaded: 5 questions, five-slide lecture PDF, and simulated Hint/Explanation generation enabled.', null, false);
    }
}

let startTime;
let currentPdfBlobUrl = null;
let currentPopupBtn = null;
let toolsMenuPositionFrame = 0;
let isViewerOpen = false;
const PANEL_ORDER_DEFAULT = ['quiz', 'pdf', 'ai'];
let panelOrder = [...PANEL_ORDER_DEFAULT];

/* --- DOM ELEMENTS --- */
const els = {
    welcome: $('welcome-screen'),
    quizContainer: $('quiz-mode-container'),
    quizCard: $('quiz-card'),
    workingSessionTitle: $('working-session-title'),
    workingSessionTitleText: $('working-session-title-text'),
    workingSessionTitleEdit: $('working-session-title-edit'),
    appHomeTitleText: $('app-home-title-text'),
    appHomeTitleEdit: $('app-home-title-edit'),
    modeBtn: $('mode-toggle'),
    autoSaveBtn: $('as-toggle-small'),
    autoAdvanceBtn: $('aa-toggle-small'),
    autoEvidenceBtn: $('ae-toggle-small'),
    autoExplanationBtn: $('ax-toggle-small'),
    notesBtn: $('notes-btn-inline'),
    quizExpandLockBtn: $('quiz-expand-toggle-btn-inline'),
    resetQBtn: $('reset-q-small'),
    refreshOptionsDropdown: $('refresh-options-dropdown'),
    refreshQuestionBtn: $('refresh-question-btn'),
    refreshQuizBtn: $('refresh-quiz-btn'),

    // Menus
    themeTrigger: $('theme-trigger-btn'),
    themeModal: $('theme-modal'), // Replaces dropdown
    menuTrigger: $('menu-trigger-btn'),
    menuDropdown: $('menu-dropdown'),
    calendarToggleBtn: $('calendar-toggle-btn'),
    sourceWrapper: $('source-wrapper'),
    sourceTrigger: $('source-trigger-btn'),
    sourceDropdown: $('source-dropdown'),
    searchWrapper: $('.search-wrapper'),
    searchInput: $('search-input'),
    searchFinderReopenBtn: $('search-finder-reopen-btn'),
    searchDropdown: $('search-dropdown'),

    // Theme Controls
    colCardBg: $('col-card-bg'),
    colCardBgTransparentBtn: $('col-card-bg-transparent-btn'),
    bgControlsBtn: $('bg-controls-btn'),
    bgControlsStatus: $('bg-controls-status'),
    bgControlsPanel: $('bg-controls-panel'),
    bgGamma: $('bg-gamma'),
    bgGammaValue: $('bg-gamma-value'),
    bgDim: $('bg-dim'),
    bgDimValue: $('bg-dim-value'),
    bgAccent: $('bg-accent'),
    bgAccentValue: $('bg-accent-value'),
    bgControlsResetBtn: $('bg-controls-reset-btn'),
    colTextQ: $('col-text-q'),
    colTextA: $('col-text-a'),
    colOptBg: $('col-opt-bg'),
    colAIBold: $('col-ai-bold'),
    fontSelect: $('font-select'),
    gradStart: $('grad-start'),
    gradMid: $('grad-mid'),
    gradEnd: $('grad-end'),
    gradModeBtns: document.querySelectorAll('[data-gradient-mode]'),
    gradientIntensity: $('gradient-intensity'),
    gradientIntensityValue: $('gradient-intensity-value'),
    resetThemeBtn: $('reset-theme-btn'),
    ambientBtn: $('ambient-btn'),
    ambientModeStatus: $('ambient-mode-status'),
    ambientControls: $('ambient-controls'),
    ambSize: $('amb-size'),
    ambStyle: $('amb-style'),
    borderModeBtn: $('border-mode-btn'),
    borderModeStatus: $('border-mode-status'),
    darkModeBtn: $('dark-mode-toggle'),
    darkModeStatus: $('dark-mode-status'),
    zenModeBtn: $('zen-mode-toggle'),
    exitZenBtn: $('exit-zen-btn'),

    // Tools
    shuffleBtn: $('shuffle-btn'),
    hardModeBtn: $('hard-mode-toggle'),
    mixAnswersBtn: $('mix-answers-toggle'),
    saveSessionBtn: $('save-session-btn'),
    historyMenuBtn: $('history-menu-btn'),
    studyDashboardMenuBtn: $('study-dashboard-menu-btn'),
    studyDashboardAutoOpenToggle: $('study-dashboard-auto-open-toggle'),
    studySetupMenuBtn: $('study-setup-menu-btn'),
    statsMenuBtn: $('stats-menu-btn'),
    srsModeBtn: $('srs-mode-btn'),
    flashcardSrsModeBtn: $('flashcard-srs-mode-btn'),
    databaseBtn: $('database-btn'),
    studyDashboardSurface: $('study-dashboard-surface'),
    studyDashboardShell: $('study-dashboard-shell'),
    studySetupSurface: $('study-setup-surface'),
    studySetupForm: $('study-setup-form'),
    studySetupCloseBtn: $('study-setup-close-btn'),
    studySetupBackBtn: $('study-setup-back-btn'),
    studySetupExamTiming: $('study-setup-exam-timing'),
    studySetupPreparedness: $('study-setup-preparedness'),
    studySetupTimeToday: $('study-setup-time-today'),
    studySetupPriority: $('study-setup-priority'),
    studySetupGoal: $('study-setup-goal'),
    // Flashcards
    fcContainer: $('flashcard-container'),
    fcGrid: $('fc-grid'),
    fcLimit: $('fc-limit'),
    fcPrev: $('fc-prev'),
    fcNext: $('fc-next'),
    fcInfo: $('fc-page-info'),
    fcSrsModeBtn: $('fc-srs-mode-btn'),
    quizUnknownBtn: $('quiz-unknown-btn'),
    quizSavedBtn: $('quiz-saved-btn'),

    // Main Controls
    quizSavedMainBtn: $('quiz-saved-main-btn'),

    // Quiz
    quizContent: $('quiz-content-wrapper'),
    quizScaleShell: $('quiz-scale-shell'),
    resultsView: $('results-view'),
    scoreCircle: $('score-circle'),
    resultsList: $('results-list'),
    qText: $('question'),
    choices: $('choices'),
    expl: $('explanation'),
    popup: $('wrong-popup'),
    img: $('q-image'),
    prev: $('prev-btn'),
    next: $('next-btn'),
    save: $('save-btn'),
    jump: $('jump-select'),
    prog: $('progress-text'),
    quickActionsDock: $('quiz-quick-actions-dock'),
    quickActionsTrigger: $('quiz-quick-actions-trigger'),
    quickActionsMenu: $('quiz-quick-actions-menu'),
    filterBanner: $('filter-banner'),
    exitFilter: $('exit-filter'),
    adaptiveSkipUndoneBtn: $('adaptive-skip-undone-btn'),
    progressFill: $('progress-fill'),
    progressTrack: $('progress-bar-container'),

    // PDF
    workArea: $('work-area'),
    pdfPane: $('pdf-pane'),
    pdfJsHost: $('pdfjs-host'),
    pdfJsPages: $('pdfjs-pages'),
    pptxViewer: $('pptx-viewer'),
    pdfEmptyState: $('pdf-empty-state'),
    pdfFrame: $('pdf-frame'),
    pdfTitle: $('pdf-title'),
    pdfPrevPageBtn: $('pdf-prev-page-btn'),
    pdfNextPageBtn: $('pdf-next-page-btn'),
    pdfPageIndicator: $('pdf-page-indicator'),
    pdfPageUnit: $('pdf-page-unit'),
    pdfPageInput: $('pdf-page-input'),
    pdfPageTotal: $('pdf-page-total'),
    pdfZoomOutBtn: $('pdf-zoom-out-btn'),
    pdfZoomInBtn: $('pdf-zoom-in-btn'),
    pdfZoomLabel: $('pdf-zoom-label'),
    pdfMoreWrap: $('pdf-more-wrap'),
    pdfMoreBtn: $('pdf-more-btn'),
    pdfMoreMenu: $('pdf-more-menu'),
    pdfTutorFullscreenMenuBtn: $('pdf-tutor-fullscreen-menu-btn'),
    pdfExpandHeaderBtn: $('pdf-expand-header-btn'),
    pdfRendererToggleBtn: $('pdf-renderer-toggle-btn'),
    pdfFitPageBtn: $('pdf-fit-page-btn'),
    pdfFitWidthBtn: $('pdf-fit-width-btn'),
    pdfExpandBtn: $('pdf-expand-btn'),

    // Welcome & History & Saved
    continueBtn: $('continue-session-btn'),
    newSessionBtn: $('new-session-btn'),
    welcomeHistoryBtn: $('welcome-history-btn'),
    welcomeWarmupPanel: $('welcome-warmup-panel'),
    welcomeWarmupCard: $('welcome-warmup-card'),
    welcomeWarmupSession: $('welcome-warmup-session'),
    welcomeWarmupSource: $('welcome-warmup-source'),
    welcomeWarmupQuestion: $('welcome-warmup-question'),
    welcomeWarmupChoices: $('welcome-warmup-choices'),
    welcomeWarmupFeedback: $('welcome-warmup-feedback'),
    welcomeWarmupNextBtn: $('welcome-warmup-next-btn'),
    historyModal: $('history-modal'),
    historyList: $('history-list'),
    historyDeleteAllBtn: $('history-delete-all-btn'),
    savedModal: $('saved-modal'),
    savedList: $('saved-list'),
    btnQuizSavedModal: $('btn-quiz-saved-modal'),
    btnDownloadPdf: $('btn-download-pdf'),
    statsModal: $('stats-modal'),

    // AI Dropdown
    aiTrigger: $('ai-trigger-btn'),
    aiDropdown: $('ai-dropdown'),

    // Theme Confirm Modal
    themeConfirmModal: $('theme-confirm-modal'),
    themeConfirmTitle: $('theme-confirm-title'),
    themeConfirmMessage: $('theme-confirm-message'),
    themeConfirmOk: $('theme-confirm-ok'),
    themeConfirmCancel: $('theme-confirm-cancel'),

    // Session Name Prompt
    sessionNameModal: $('session-name-modal'),
    sessionNameForm: $('session-name-form'),
    sessionNameInput: $('session-name-input'),
    sessionNameError: $('session-name-error'),
    sessionNameOk: $('session-name-ok'),
    sessionNameCancel: $('session-name-cancel'),

    // Adaptive Review Modal
    srsRatingModal: $('srs-rating-modal'),
    srsRatingTitle: $('srs-rating-title'),
    srsRatingSubtitle: $('srs-rating-subtitle')
};

if (els.popup && els.popup.parentElement !== document.body) {
    document.body.appendChild(els.popup);
}

function portalPriorityDropdowns() {
    ['menuDropdown', 'sourceDropdown', 'themeModal'].forEach((key) => {
        const dropdown = els[key];
        if (dropdown && dropdown.parentElement !== document.body) {
            document.body.appendChild(dropdown);
        }
    });
}
portalPriorityDropdowns();

function setupHomeTitleInteractions() {
    const orbTrigger = $('memora-voice-orb-trigger');

    if (orbTrigger && orbTrigger.dataset.orbTriggerBound !== '1') {
        orbTrigger.dataset.orbTriggerBound = '1';
        /* The connected Astra opens the conversation; the footer gear owns Voice settings. */
        const toggleVoiceControls = async (event) => {
            event.preventDefault();
            event.stopPropagation();
            if ($('voice-agent-modal')?.classList.contains('active') && $('voice-agent-modal').classList.contains('voice-transcript-enlarged')) {
                window.closeVoiceAgentModal?.('button');
                return;
            }
            try {
                // The canonical conversation styles belong to the lazy Agent feature.
                await window.MemoraRuntime.ensureFeature('agent');
                window.openVoiceAgentModal?.({ focusInput: true, view: 'transcript' });
            } catch (error) {
                console.error('Voice conversation could not open:', error);
                window.showPopup?.('Voice controls could not open. Please try again.', null, true);
            }
        };
        orbTrigger.addEventListener('mousedown', (event) => {
            event.stopPropagation();
        });
        orbTrigger.addEventListener('click', toggleVoiceControls);
        orbTrigger.addEventListener('keydown', (event) => {
            if (event.key !== 'Enter' && event.key !== ' ') return;
            toggleVoiceControls(event);
        });
    }
}
setupHomeTitleInteractions();

let welcomeWarmupActiveItem = null;
let welcomeWarmupCompletionPending = false;
let welcomeWarmupKeepOpenOnNextRender = false;
let welcomeScreenSyncEpoch = 0;

function scheduleWelcomeSurfaceReveal(surface, delayMs = 0) {
    if (!surface) return;
    if (surface._welcomeRevealTimer) clearTimeout(surface._welcomeRevealTimer);
    surface.classList.add('welcome-progressive-surface', 'is-pending-reveal');

    const reduceMotion = getMemoraMotionMode() === 'reduced';
    const safeDelay = reduceMotion ? 0 : Math.max(0, Number(delayMs) || 0);
    surface._welcomeRevealTimer = setTimeout(() => {
        surface._welcomeRevealTimer = null;
        requestAnimationFrame(() => surface.classList.remove('is-pending-reveal'));
    }, safeDelay);
}

function setSearchDropdownOpen(isOpen) {
    const open = !!isOpen;
    if (els.searchDropdown) els.searchDropdown.classList.toggle('active', open);
    document.body?.classList.toggle('search-dropdown-open', open);
}

function normalizeSessionId(sessionId) {
    return (typeof sessionId === 'string' ? sessionId.trim() : String(sessionId || '').trim()) || '';
}

function normalizeSessionNameInput(value) {
    return String(value || '').replace(/\s+/g, ' ').trim().slice(0, 80);
}

/* Session names and folder names are each unique, ignoring case and spacing, so two rows
   the student has to tell apart never read the same. The rename and create surfaces check
   first to keep their field open, and the stores refuse a clash too, so a caller that
   skips the check still cannot save one. */
const DUPLICATE_NAME_ERROR = 'DuplicateNameError';

function nameUniquenessKey(value) {
    return String(value || '').replace(/\s+/g, ' ').trim().toLocaleLowerCase();
}

function duplicateNameMessage(kind, name) {
    return `A ${kind} named “${name}” already exists. Choose a different name.`;
}

function makeDuplicateNameError(kind, name) {
    return Object.assign(new Error(duplicateNameMessage(kind, name)), { name: DUPLICATE_NAME_ERROR });
}

function isDuplicateNameError(error) {
    return error?.name === DUPLICATE_NAME_ERROR;
}

// Resolves to the name of the session that already uses `name`, or '' when it is free.
async function findSessionNameConflict(name, exceptSessionId = '') {
    const key = nameUniquenessKey(name);
    if (!key) return '';
    const exceptId = normalizeSessionId(exceptSessionId);
    const openId = normalizeSessionId(currentSessionId);
    // The open session may be unsaved, or saved under an older name, so its name comes from memory.
    if (openId && openId !== exceptId) {
        const openName = getCurrentSessionDisplayLabel();
        if (nameUniquenessKey(openName) === key) return openName;
    }
    let entries = [];
    try {
        entries = await readSavedSessionSummaries({ timeoutMs: 3000 });
    } catch (error) {
        console.warn('Could not read saved sessions to check the name:', error);
    }
    for (const entry of Array.isArray(entries) ? entries : []) {
        const id = normalizeSessionId(entry?.id);
        if (!id || id === exceptId || id === openId) continue;
        const label = formatWelcomeSessionDisplayName(entry);
        if (nameUniquenessKey(label) === key) return label;
    }
    return '';
}

async function assertSessionNameAvailable(name, exceptSessionId = '') {
    const clash = await findSessionNameConflict(name, exceptSessionId);
    if (clash) throw makeDuplicateNameError('session', clash);
}

function findSessionFolderNameConflict(folders, name, exceptFolderId = '') {
    const key = nameUniquenessKey(String(name || '').slice(0, 60));
    if (!key) return '';
    const clash = (Array.isArray(folders) ? folders : [])
        .find((folder) => folder?.id !== exceptFolderId && nameUniquenessKey(folder?.name) === key);
    return clash ? clash.name : '';
}

const SESSION_NUMBER_STORAGE_KEY = 'memoraNextSessionNumber';
let nextSessionNumberInMemory = 0;

function normalizeSessionNumber(value) {
    const number = Number(value);
    return Number.isSafeInteger(number) && number > 0 ? number : 0;
}

function getStoredNextSessionNumber() {
    try {
        const stored = normalizeSessionNumber(localStorage.getItem(SESSION_NUMBER_STORAGE_KEY));
        return stored || 1;
    } catch (_) {
        return 1;
    }
}

function rememberSessionNumber(sessionNumber) {
    const number = normalizeSessionNumber(sessionNumber);
    if (!number) return 0;
    nextSessionNumberInMemory = Math.max(nextSessionNumberInMemory, number + 1);
    try {
        const storedNext = getStoredNextSessionNumber();
        if (number >= storedNext) localStorage.setItem(SESSION_NUMBER_STORAGE_KEY, String(number + 1));
    } catch (_) { /* best effort when storage is unavailable */ }
    return number;
}

function getNextSessionNumber() {
    const next = Math.max(nextSessionNumberInMemory + 1, getStoredNextSessionNumber());
    nextSessionNumberInMemory = next;
    try {
        localStorage.setItem(SESSION_NUMBER_STORAGE_KEY, String(next + 1));
    } catch (_) { /* in-memory fallback keeps this page's sequence usable */ }
    return next;
}

function syncSessionNumberCounterFromSessions(sessions = []) {
    (Array.isArray(sessions) ? sessions : []).forEach((session) => {
        rememberSessionNumber(session?.sessionNumber);
    });
}

function getDefaultNewSessionName(sessionNumber = 1) {
    const number = normalizeSessionNumber(sessionNumber) || 1;
    return `Session ${number}`;
}

function syncWorkingSessionTitle() {
    if (!els) return;
    const displayName = getCurrentSessionDisplayLabel();
    if (els.workingSessionTitleText) {
        els.workingSessionTitleText.textContent = displayName;
        els.workingSessionTitleText.title = displayName;
    }
    if (els.appHomeTitleText) {
        els.appHomeTitleText.textContent = displayName;
        els.appHomeTitleText.title = displayName;
    }
    if (els.workingSessionTitleEdit) {
        els.workingSessionTitleEdit.setAttribute('aria-label', `Rename ${displayName}`);
    }
    if (els.appHomeTitleEdit) {
        els.appHomeTitleEdit.setAttribute('aria-label', `Rename ${displayName}`);
    }
}

function setContinueButtonSessionLabel(session = null) {
    if (!els.continueBtn) return;
    const label = els.continueBtn.querySelector('.welcome-btn-text');
    const detail = els.continueBtn.querySelector('strong');
    const displayName = session ? formatWelcomeSessionDisplayName(session) : '';
    if (label) label.textContent = 'Continue Last Session';
    if (detail) detail.textContent = displayName || 'Pick up where you stopped';
    els.continueBtn.title = displayName ? `Continue Last Session (${displayName})` : 'Continue Last Session';
}

function getLastSessionIdSafe() {
    try {
        return localStorage.getItem('lastSessionId') || '';
    } catch (_) {
        return '';
    }
}

function syncContinueButton(sessionId = null) {
    if (IS_EMBED_DEMO || !els.continueBtn) return;
    const resolved = normalizeSessionId(sessionId === null ? getLastSessionIdSafe() : sessionId);
    els.continueBtn.style.display = resolved ? 'inline-flex' : 'none';
    els.continueBtn.classList.toggle('welcome-btn-empty-session', !resolved);
    if (!resolved) setContinueButtonSessionLabel(null);
}

function getSessionFileDisplayName(fileName) {
    const cleaned = String(fileName || '')
        .replace(/\.[^.]+$/, '')
        .replace(/[_-]+/g, ' ')
        // A browser's duplicate-download suffix, as in "Lecture 5 (3).pdf".
        .replace(/\s*\(\d{1,2}\)\s*$/, '')
        .replace(/\s+/g, ' ')
        .trim();
    return cleaned;
}

function condenseWelcomeWarmupSourceLabel(sourceLabel) {
    const cleaned = getSessionFileDisplayName(sourceLabel)
        .replace(/\s*-\s*(?:\d+\s*)?warm-?ups?\s+(?:ready|question).*$/i, '')
        .replace(/\bwarm-?up\s+quiz\b/gi, '')
        .replace(/\s+/g, ' ')
        .trim();
    const replacements = [
        [/\bchapter\b/gi, 'Ch.'],
        [/\blecture\b/gi, 'Lec.'],
        [/\bbiochemistry\b/gi, 'Biochem'],
        [/\bcarbohydrates?\b/gi, 'Carb'],
        [/\bmetabolism\b/gi, 'Metab'],
        [/\bphysiology\b/gi, 'Physio'],
        [/\bpathophysiology\b/gi, 'Pathophys'],
        [/\bpharmacology\b/gi, 'Pharm'],
        [/\bmicrobiology\b/gi, 'Micro']
    ];
    let condensed = cleaned || 'Recent quiz';
    replacements.forEach(([pattern, replacement]) => {
        condensed = condensed.replace(pattern, replacement);
    });
    condensed = condensed.replace(/\s+/g, ' ').trim();
    if (condensed.length <= 38) return condensed;

    let clipped = '';
    for (const word of condensed.split(' ')) {
        const next = clipped ? `${clipped} ${word}` : word;
        if (next.length > 35) break;
        clipped = next;
    }
    return `${(clipped || condensed.slice(0, 35)).trim()}...`;
}

function normalizeWelcomeSourceQuestion(rawQuestion, sourceKey = '') {
    const q = (rawQuestion && typeof rawQuestion === 'object' && !Array.isArray(rawQuestion))
        ? { ...rawQuestion }
        : {};
    if (!q.q && q.question) q.q = q.question;
    if (!Array.isArray(q.c)) {
        const fallbackChoices = [q.choices, q.o, q.options].find(Array.isArray);
        q.c = fallbackChoices ? [...fallbackChoices] : [];
    }
    if (q.a === undefined) {
        q.a = getWelcomeWarmupAnswerIndex(q, q.c);
        if (q.a < 0) q.a = 0;
    }
    if (q.explanation && !q.e) q.e = q.explanation;
    if (q.page && !q.add) q.add = q.page;
    if (!q.sourceFile && q.src) q.sourceFile = q.src;
    q.__welcomeSourceKey = sourceKey;
    return q;
}

function getWelcomeSessionManifestJsons(session) {
    return getSessionQuestionLookupNames(session);
}

function getWelcomeSessionActiveSetLookup(session) {
    const activeSets = Array.isArray(session?.activeSets)
        ? session.activeSets.map((key) => String(key || '').trim()).filter(Boolean)
        : [];
    const lookup = new Set(activeSets);
    activeSets.forEach((key) => {
        const stripped = key.replace(/\.(json|txt)$/i, '');
        if (stripped) lookup.add(stripped);
    });
    return lookup;
}

function isWelcomeSessionSetActive(setKey, activeLookup) {
    if (!activeLookup || activeLookup.size === 0) return true;
    const key = String(setKey || '').trim();
    const stripped = key.replace(/\.(json|txt)$/i, '');
    return activeLookup.has(key) || activeLookup.has(stripped);
}

async function loadWelcomeSessionQuestionSet(setKey) {
    const key = String(setKey || '').trim();
    if (!key) return null;
    const { questions: normalized } = await readStoredQuestionCollectionForSessionKey(key);
    return Array.isArray(normalized) ? normalized : null;
}

async function buildWelcomeSessionQuestionBank(session) {
    const manifestJsons = getWelcomeSessionManifestJsons(session);
    const activeLookup = getWelcomeSessionActiveSetLookup(session);
    const activeQuestions = [];

    for (const setKey of manifestJsons) {
        if (!isWelcomeSessionSetActive(setKey, activeLookup)) continue;
        const qSet = await loadWelcomeSessionQuestionSet(setKey);
        if (!Array.isArray(qSet) || !qSet.length) continue;
        qSet.forEach((rawQuestion) => {
            const realIdx = activeQuestions.length;
            activeQuestions.push({
                realIdx,
                sourceKey: setKey,
                question: normalizeWelcomeSourceQuestion(rawQuestion, setKey)
            });
        });
    }

    if (!activeQuestions.length) {
        const recoveredQuestions = recoverEmbeddedSessionQuestions(session, formatWelcomeSessionDisplayName(session));
        recoveredQuestions.forEach((rawQuestion) => {
            const realIdx = activeQuestions.length;
            activeQuestions.push({
                realIdx,
                sourceKey: rawQuestion.sourceFile || formatWelcomeSessionDisplayName(session),
                question: normalizeWelcomeSourceQuestion(rawQuestion, rawQuestion.sourceFile || formatWelcomeSessionDisplayName(session))
            });
        });
    }

    return activeQuestions;
}

function transformWelcomeIndexedValue(rawValue, transformValue, idx) {
    return (typeof transformValue === 'function') ? transformValue(rawValue, idx) : rawValue;
}

function buildWelcomeIndexedState(activeQuestions, indexStateRaw, keyedBucketsRaw, transformValue = null) {
    const legacyState = {};
    const sourceState = (indexStateRaw && typeof indexStateRaw === 'object') ? indexStateRaw : {};
    Object.entries(sourceState).forEach(([idxKey, rawValue]) => {
        const idx = Number(idxKey);
        if (!Number.isInteger(idx) || idx < 0 || idx >= activeQuestions.length) return;
        const value = transformWelcomeIndexedValue(rawValue, transformValue, idx);
        if (value !== undefined) legacyState[idx] = value;
    });

    const keyedBuckets = sanitizeQuestionKeyBucketObject(keyedBucketsRaw);
    const keyedState = {};
    const bucketCursor = {};
    activeQuestions.forEach((entry, idx) => {
        const qKey = buildQuestionPersistenceKeyFromQuestion(entry.question);
        if (!qKey || !Array.isArray(keyedBuckets[qKey])) return;
        const cursor = Number(bucketCursor[qKey]) || 0;
        if (cursor >= keyedBuckets[qKey].length) return;
        const value = transformWelcomeIndexedValue(keyedBuckets[qKey][cursor], transformValue, idx);
        bucketCursor[qKey] = cursor + 1;
        if (value !== undefined) keyedState[idx] = value;
    });

    if (Object.keys(keyedState).length === 0) return legacyState;
    const merged = { ...keyedState };
    Object.entries(legacyState).forEach(([idxKey, value]) => {
        const idx = Number(idxKey);
        if (merged[idx] !== undefined) return;
        const qKey = buildQuestionPersistenceKeyFromQuestion(activeQuestions[idx]?.question);
        if (!qKey || !Object.prototype.hasOwnProperty.call(keyedBuckets, qKey)) {
            merged[idx] = value;
        }
    });
    return merged;
}

function buildWelcomeQuestionKeyBuckets(activeQuestions, indexState) {
    const buckets = {};
    if (!indexState || typeof indexState !== 'object') return buckets;
    Object.entries(indexState).forEach(([idxKey, value]) => {
        const idx = Number(idxKey);
        if (!Number.isInteger(idx) || idx < 0 || idx >= activeQuestions.length) return;
        const qKey = buildQuestionPersistenceKeyFromQuestion(activeQuestions[idx]?.question);
        if (!qKey) return;
        if (!Array.isArray(buckets[qKey])) buckets[qKey] = [];
        buckets[qKey].push(value);
    });
    return buckets;
}

function updateWelcomeSrsRecord(existingRecord, isCorrect) {
    const now = Date.now();
    const data = {
        interval: Number.isFinite(existingRecord?.interval) ? existingRecord.interval : 0,
        repetitions: Number.isFinite(existingRecord?.repetitions) ? existingRecord.repetitions : 0,
        ef: Number.isFinite(existingRecord?.ef) ? existingRecord.ef : 2.5,
        nextReview: Number.isFinite(existingRecord?.nextReview) ? existingRecord.nextReview : 0,
        lapses: Number.isFinite(existingRecord?.lapses) ? existingRecord.lapses : 0,
        lastQuality: Number.isFinite(existingRecord?.lastQuality) ? existingRecord.lastQuality : null,
        lastConfidence: typeof existingRecord?.lastConfidence === 'string' ? existingRecord.lastConfidence : null,
        lastReviewed: Number.isFinite(existingRecord?.lastReviewed) ? existingRecord.lastReviewed : 0
    };
    const q = isCorrect ? 5 : 2;

    if (q >= 3) {
        if (data.repetitions === 0) data.interval = 1;
        else if (data.repetitions === 1) data.interval = 6;
        else data.interval = Math.max(1, Math.round(data.interval * data.ef));
        data.repetitions += 1;
    } else {
        data.repetitions = 0;
        data.interval = 1;
        data.lapses += 1;
    }

    data.ef = Math.max(1.3, Math.min(2.8, data.ef + (0.1 - (5 - q) * (0.08 + (5 - q) * 0.02))));
    data.lastQuality = q;
    data.lastConfidence = isCorrect ? 'good' : 'again';
    data.lastReviewed = now;
    data.nextReview = now + (data.interval * SRS_DAY_MS);
    return data;
}

function getWelcomeSessionAnswerState(activeQuestions, sessionData) {
    return buildWelcomeIndexedState(activeQuestions, sessionData?.userAnswers, sessionData?.userAnswersByQuestionKey, (rawValue) => {
        const parsed = Number(rawValue);
        return Number.isInteger(parsed) && parsed >= 0 ? parsed : undefined;
    });
}

function findWelcomeSourceQuestion(activeQuestions, sessionData) {
    if (!Array.isArray(activeQuestions) || !activeQuestions.length) return null;
    const answerState = getWelcomeSessionAnswerState(activeQuestions, sessionData);
    const total = activeQuestions.length;
    const rawStart = Number(sessionData?.currentQIndex);
    const start = Number.isInteger(rawStart) && rawStart >= 0 ? rawStart % total : 0;

    for (let step = 0; step < total; step++) {
        const realIdx = (start + step) % total;
        if (answerState[realIdx] !== undefined) continue;
        const question = activeQuestions[realIdx]?.question;
        const choices = getWelcomeWarmupChoices(question);
        const correctIdx = getWelcomeWarmupAnswerIndex(question, choices);
        if (!question || !String(question.q || '').trim() || choices.length < 2 || correctIdx < 0 || correctIdx >= choices.length) continue;
        return {
            ...activeQuestions[realIdx],
            remainingCount: activeQuestions.reduce((count, _entry, idx) => count + (answerState[idx] === undefined ? 1 : 0), 0),
            totalCount: total
        };
    }
    return null;
}

async function buildWelcomeSourceRenderState(recentSession) {
    if (!recentSession) return null;
    const sessionData = (recentSession.data && typeof recentSession.data === 'object') ? recentSession.data : {};
    const activeQuestions = await buildWelcomeSessionQuestionBank(recentSession);
    const sourceEntry = findWelcomeSourceQuestion(activeQuestions, sessionData);
    if (!sourceEntry) return null;
    const sourceQuestion = sourceEntry.question;
    return {
        mode: 'question',
        questionKind: 'source',
        session: recentSession,
        sessionId: normalizeSessionId(recentSession.id),
        sessionName: formatWelcomeSessionDisplayName(recentSession),
        question: sourceQuestion,
        realIdx: sourceEntry.realIdx,
        sourceLabel: sourceQuestion.sourceFile || sourceQuestion.src || sourceEntry.sourceKey || 'Recent quiz',
        remainingCount: sourceEntry.remainingCount,
        totalCount: sourceEntry.totalCount
    };
}

function setWelcomeActionsPending(isPending) {
    if (!document.body) return;
    const pending = !!isPending;
    document.body.classList.toggle('welcome-actions-pending', pending);
    if (!pending) {
        // The html-level marker protects the first paint before this script has
        // hydrated storage. Release it only from the canonical action-state
        // synchronizer, never from the early button fallback.
        document.documentElement.classList.remove('welcome-actions-prepaint-pending');
    }
    const actions = els.welcome?.querySelector?.('.welcome-actions');
    if (actions) {
        actions.setAttribute('aria-busy', pending ? 'true' : 'false');
    }
}

function setSessionHistoryLoadPending(isPending) {
    const pending = !!isPending;
    [els.continueBtn, els.welcomeHistoryBtn, els.historyMenuBtn].forEach((button) => {
        if (!button) return;
        const label = button.querySelector('.welcome-btn-text, .gradient-text');
        const preserveWelcomeLoadState = isWelcomeSessionLoadPending
            && (button === els.continueBtn || button === els.welcomeHistoryBtn);
        if (pending) {
            if (!preserveWelcomeLoadState && !button.dataset.memoraHistoryIdleLabel && label) {
                button.dataset.memoraHistoryIdleLabel = label.textContent;
            }
            if (!preserveWelcomeLoadState && !button.dataset.memoraHistoryIdleTitle) {
                button.dataset.memoraHistoryIdleTitle = button.title || '';
            }
            button.disabled = true;
            button.setAttribute('aria-disabled', 'true');
            button.setAttribute('aria-busy', 'true');
            if (!preserveWelcomeLoadState) {
                button.title = 'Loading saved sessions';
                if (label) label.textContent = 'Loading previous sessions...';
            }
        } else {
            const keepWelcomeLoadDisabled = isWelcomeSessionLoadPending
                && (button === els.continueBtn || button === els.welcomeHistoryBtn);
            button.disabled = keepWelcomeLoadDisabled;
            if (keepWelcomeLoadDisabled) button.setAttribute('aria-disabled', 'true');
            else button.removeAttribute('aria-disabled');
            button.removeAttribute('aria-busy');
            if (label && button.dataset.memoraHistoryIdleLabel) {
                label.textContent = button.dataset.memoraHistoryIdleLabel;
            }
            button.title = button.dataset.memoraHistoryIdleTitle || button.title;
            delete button.dataset.memoraHistoryIdleLabel;
            delete button.dataset.memoraHistoryIdleTitle;
        }
    });
    document.querySelectorAll('.hist-meta').forEach((meta) => {
        meta.style.pointerEvents = pending ? 'none' : '';
        meta.setAttribute('aria-busy', pending ? 'true' : 'false');
    });
}

function setWelcomeSessionLoadPending(isPending) {
    isWelcomeSessionLoadPending = !!isPending;
    document.body?.classList.toggle('welcome-session-loading', isWelcomeSessionLoadPending);
    const actions = els.welcome?.querySelector?.('.welcome-actions');
    if (actions) actions.setAttribute('aria-busy', isWelcomeSessionLoadPending ? 'true' : 'false');

    [els.continueBtn, els.welcomeHistoryBtn].forEach((button) => {
        if (!button) return;
        button.disabled = isWelcomeSessionLoadPending;
    });
    if (els.newSessionBtn) {
        els.newSessionBtn.disabled = false;
        els.newSessionBtn.removeAttribute('aria-disabled');
    }

    if (!els.continueBtn) return;
    els.continueBtn.classList.toggle('is-loading', isWelcomeSessionLoadPending);
    if (isWelcomeSessionLoadPending) {
        const label = els.continueBtn.querySelector('.welcome-btn-text');
        const detail = els.continueBtn.querySelector('strong');
        if (label) label.textContent = 'Opening Session';
        if (detail) detail.textContent = 'Restoring saved progress';
        els.continueBtn.title = 'Opening saved session';
        els.continueBtn.setAttribute('aria-disabled', 'true');
    } else {
        els.continueBtn.removeAttribute('aria-disabled');
    }
}

function invalidateWelcomeSessionLoad() {
    welcomeSessionLoadEpoch += 1;
    setWelcomeSessionLoadPending(false);
    setWelcomeActionsPending(false);
    return welcomeSessionLoadEpoch;
}

function isCurrentWelcomeSessionLoad(epoch) {
    return !!epoch && epoch === welcomeSessionLoadEpoch;
}

async function getMemoraStoreKeysForDebug(storeName) {
    try {
        const keys = await readSessionStoreKeysWithTimeout(storeName, 3000);
        return Array.isArray(keys) ? keys.map(key => String(key)) : [];
    } catch (error) {
        console.warn(`MemoraDebugDump could not read ${storeName} keys:`, error);
        return [];
    }
}

window.MemoraDebugDump = async function MemoraDebugDump() {
    let sessions = [];
    try {
        sessions = await readSessionStoreAllWithTimeout('sessions', 3000) || [];
    } catch (error) {
        console.warn('MemoraDebugDump could not read sessions:', error);
        sessions = [];
    }

    const questionKeys = await getMemoraStoreKeysForDebug('questions');
    const fileKeys = await getMemoraStoreKeysForDebug('files');
    const settingsKeys = await getMemoraStoreKeysForDebug('settings');
    const dump = {
        location: window.location.href,
        scriptTokens: Array.from(document.scripts || [])
            .map(script => script.src || '')
            .filter(src => /app\.(core|quizgen)\.js/i.test(src)),
        bodyClassName: document.body?.className || '',
        welcomeActionsPending: !!document.body?.classList?.contains('welcome-actions-pending'),
        welcomeSessionLoading: !!document.body?.classList?.contains('welcome-session-loading'),
        welcomeButtonDisplays: {
            continue: els.continueBtn?.style?.display || '',
            startNew: els.newSessionBtn?.style?.display || '',
            loadPrevious: els.welcomeHistoryBtn?.style?.display || ''
        },
        lastSessionId: getLastSessionIdSafe(),
        lastWelcomeSessionId: normalizeSessionId(lastWelcomeSessionId),
        currentSessionId: normalizeSessionId(currentSessionId),
        currentSessionCustomName: currentSessionCustomName || '',
        isWelcomeSessionLoadPending,
        sessions: {
            count: Array.isArray(sessions) ? sessions.length : 0,
            keys: (Array.isArray(sessions) ? sessions : []).map(session => normalizeSessionId(session?.id)).filter(Boolean),
            records: (Array.isArray(sessions) ? sessions : []).map(session => ({
                id: normalizeSessionId(session?.id),
                name: formatWelcomeSessionDisplayName(session),
                timestamp: session?.timestamp || '',
                activeSetsCount: Array.isArray(session?.activeSets) ? session.activeSets.length : 0,
                manifestJsonCount: getSessionManifestJsonNames(session).length,
                manifestPdfCount: getSessionManifestPdfNames(session).length
            }))
        },
        questionsStore: {
            count: questionKeys.length,
            keys: questionKeys
        },
        filesStore: {
            count: fileKeys.length,
            keys: fileKeys
        },
        settingsStore: {
            count: settingsKeys.length,
            keys: settingsKeys
        },
        sessionManifest: {
            jsons: Array.isArray(sessionManifest?.jsons) ? [...sessionManifest.jsons] : [],
            pdfs: Array.isArray(sessionManifest?.pdfs) ? [...sessionManifest.pdfs] : []
        },
        loadedQuestionSetKeys: Object.keys(loadedQuestionSets || {}),
        activeQuestionSetKeys: Array.from(activeQuestionSetKeys || []),
        questionsLength: Array.isArray(questions) ? questions.length : 0,
        filteredIndicesLength: Array.isArray(filteredIndices) ? filteredIndices.length : 0
    };
    console.log('MemoraDebugDump', dump);
    return dump;
};

function getWelcomeStatusElements() {
    return {
        panel: $('welcome-status-panel') || document.querySelector('.welcome-status'),
        sessionValue: $('welcome-status-session'),
        accuracyValue: $('welcome-status-accuracy'),
        remainingValue: $('welcome-status-remaining')
    };
}

function formatWelcomeSessionDisplayName(session) {
    const customName = String(session?.customName || '').trim();
    if (customName) return customName;

    const legacySessionName = String(session?.sessionName || session?.displayName || session?.data?.customName || session?.data?.sessionName || session?.data?.displayName || '').trim();
    if (legacySessionName) return legacySessionName;

    const directName = String(session?.name || '').trim();
    if (directName) return directName;

    const dataName = String(session?.data?.name || session?.data?.title || '').trim();
    if (dataName) return dataName;

    const manifest = (session?.data && typeof session.data === 'object' && session.data.manifest && typeof session.data.manifest === 'object')
        ? session.data.manifest
        : ((session?.manifest && typeof session.manifest === 'object') ? session.manifest : {});
    const jsons = Array.isArray(manifest?.jsons) ? manifest.jsons : [];
    const pdfs = Array.isArray(manifest?.pdfs) ? manifest.pdfs : [];
    const activeSets = Array.isArray(session?.activeSets) ? session.activeSets : [];
    const rawName = String(jsons[0] || pdfs[0] || activeSets[0] || '').trim();
    return getSessionFileDisplayName(rawName) || 'Recent Session';
}

function deriveWelcomeSessionSummary(sessionData) {
    const state = (sessionData && typeof sessionData === 'object') ? sessionData : {};
    const statsMap = (state.statsData && typeof state.statsData === 'object') ? state.statsData : {};
    let answeredCount = 0;
    let correctCount = 0;

    Object.values(statsMap).forEach((history) => {
        if (!Array.isArray(history) || history.length === 0) return;
        const lastAttempt = history[history.length - 1];
        if (!lastAttempt || typeof lastAttempt.correct !== 'boolean') return;
        answeredCount += 1;
        if (lastAttempt.correct) correctCount += 1;
    });

    if (!answeredCount) {
        answeredCount = countStateEntries(state.userAnswers);
    }

    const filteredCount = Array.isArray(state.filteredIndices)
        ? state.filteredIndices.length
        : Math.max(0, Number(state?.progressMeta?.filteredCount) || 0);
    const remainingCount = filteredCount > 0 ? Math.max(0, filteredCount - answeredCount) : null;
    const accuracyPct = answeredCount > 0 ? Math.round((correctCount / answeredCount) * 100) : null;

    return {
        answeredCount,
        accuracyPct,
        remainingCount
    };
}

function getWelcomeWarmupChoices(question) {
    if (!question || typeof question !== 'object') return [];
    if (Array.isArray(question.c)) return question.c;
    if (Array.isArray(question.o)) return question.o;
    if (Array.isArray(question.choices)) return question.choices;
    if (Array.isArray(question.options)) return question.options;
    return [];
}

function getWelcomeWarmupAnswerIndex(question, choices = []) {
    const candidates = [question?.a, question?.answer];
    for (const candidate of candidates) {
        if (Number.isInteger(candidate)) return candidate;
        if (typeof candidate === 'number' && Number.isFinite(candidate)) return Math.trunc(candidate);
        if (typeof candidate !== 'string') continue;

        const trimmed = candidate.trim();
        if (/^\d+$/.test(trimmed)) return Number(trimmed);
        const normalizedToken = trimmed.replace(/^answer\s*[:\-]?\s*/i, '').trim();
        if (/^[A-H][\.\)]?$/i.test(normalizedToken)) return normalizedToken[0].toUpperCase().charCodeAt(0) - 65;

        const normalizedCandidate = normalizedToken.toLowerCase().replace(/^[A-H][\.\)]\s*/i, '').replace(/\s+/g, ' ').trim();
        const matchedIdx = choices.findIndex((choice) => (
            String(choice || '').toLowerCase().replace(/^[A-H][\.\)]\s*/i, '').replace(/\s+/g, ' ').trim() === normalizedCandidate
        ));
        if (matchedIdx >= 0) return matchedIdx;
    }
    return -1;
}

function normalizeWelcomeWarmupQuestion(rawQuestion, fallbackIndex = 0) {
    if (!rawQuestion || typeof rawQuestion !== 'object' || Array.isArray(rawQuestion)) return null;

    const questionText = String(rawQuestion.q || rawQuestion.question || '')
        .replace(/\s+/g, ' ')
        .trim();
    const choices = getWelcomeWarmupChoices(rawQuestion)
        .map((choice) => String(choice || '').replace(/\s+/g, ' ').trim())
        .filter(Boolean);
    const answerIndex = getWelcomeWarmupAnswerIndex(rawQuestion, choices);
    if (!questionText || choices.length < 2 || answerIndex < 0 || answerIndex >= choices.length) return null;

    const normalized = {
        id: String(rawQuestion.id || rawQuestion.questionKey || '').trim(),
        q: questionText,
        c: choices.slice(),
        o: choices.slice(),
        a: answerIndex,
        e: String(rawQuestion.e || rawQuestion.explanation || rawQuestion.rationale || '')
            .replace(/\s+/g, ' ')
            .trim(),
        add: String(rawQuestion.add || rawQuestion.page || '').trim(),
        src: String(rawQuestion.src || rawQuestion.sourceFile || '').trim(),
        sourceFile: String(rawQuestion.sourceFile || rawQuestion.src || '').trim(),
        genModel: String(rawQuestion.genModel || rawQuestion.model || '').trim()
    };

    if (!normalized.id && typeof buildQuestionPersistenceKeyFromQuestion === 'function') {
        normalized.id = buildQuestionPersistenceKeyFromQuestion(normalized);
    }
    if (!normalized.id) {
        normalized.id = `warmup-${fallbackIndex}-${normalizeQuestionPersistencePart(questionText).slice(0, 90)}-${answerIndex}`;
    }
    return normalized;
}

function normalizeWelcomeWarmupBatch(rawBatch = {}) {
    const raw = (rawBatch && typeof rawBatch === 'object') ? rawBatch : {};
    let fallbackSessionId = '';
    try {
        fallbackSessionId = normalizeSessionId(currentSessionId || getLastSessionIdSafe());
    } catch (_err) {
        fallbackSessionId = normalizeSessionId(currentSessionId);
    }

    const sessionId = normalizeSessionId(raw.sessionId || fallbackSessionId);
    const quizName = String(raw.quizName || raw.setKey || '').trim();
    const sourcePdf = String(raw.sourcePdf || raw.pdfName || raw.sourceFile || '').trim();
    const sourceLabel = String(raw.sourceLabel || getSessionFileDisplayName(sourcePdf || quizName) || 'Recent quiz').trim();
    const questions = Array.isArray(raw.questions)
        ? raw.questions
            .map((question, index) => normalizeWelcomeWarmupQuestion(question, index))
            .filter(Boolean)
        : [];
    if (!questions.length) return null;

    const questionIds = new Set(questions.map((question) => question.id).filter(Boolean));
    const shownQuestionIds = Array.isArray(raw.shownQuestionIds)
        ? raw.shownQuestionIds.map((id) => String(id || '').trim()).filter((id) => id && questionIds.has(id))
        : [];
    const batchId = String(raw.id || `${sessionId || 'no-session'}::${quizName || sourcePdf || sourceLabel}`).trim();
    const createdAt = Number(raw.createdAt) || Date.now();

    return {
        id: batchId,
        version: WELCOME_WARMUP_STORE_VERSION,
        sessionId,
        quizName,
        sourcePdf,
        sourceLabel,
        generatedFromModel: String(raw.generatedFromModel || raw.usedModel || '').trim(),
        createdAt,
        updatedAt: Number(raw.updatedAt) || createdAt,
        questions,
        shownQuestionIds
    };
}

function normalizeWelcomeWarmupStore(rawStore = {}) {
    const raw = (rawStore && typeof rawStore === 'object') ? rawStore : {};
    const batches = Array.isArray(raw.batches)
        ? raw.batches.map(normalizeWelcomeWarmupBatch).filter(Boolean)
        : [];
    batches.sort((a, b) => (Number(b.createdAt) || 0) - (Number(a.createdAt) || 0));
    return {
        version: WELCOME_WARMUP_STORE_VERSION,
        updatedAt: Number(raw.updatedAt) || 0,
        batches
    };
}

async function loadWelcomeWarmupStore() {
    try {
        const rawStore = await IDB.get('settings', WELCOME_WARMUP_STORAGE_KEY);
        return normalizeWelcomeWarmupStore(rawStore);
    } catch (error) {
        console.warn('Failed to load welcome warm-up questions:', error);
        return normalizeWelcomeWarmupStore();
    }
}

async function persistWelcomeWarmupStore(store) {
    const normalized = normalizeWelcomeWarmupStore({
        ...store,
        updatedAt: Date.now()
    });
    await IDB.put('settings', WELCOME_WARMUP_STORAGE_KEY, normalized);
    return normalized;
}

function isSameWelcomeWarmupBatch(left, right) {
    if (!left || !right) return false;
    if (left.id && right.id && left.id === right.id) return true;
    const leftSession = normalizeSessionId(left.sessionId);
    const rightSession = normalizeSessionId(right.sessionId);
    if (!leftSession || leftSession !== rightSession) return false;
    return !!left.quizName && left.quizName === right.quizName;
}

async function saveWelcomeWarmupBatch(rawBatch = {}) {
    const nextBatch = normalizeWelcomeWarmupBatch(rawBatch);
    if (!nextBatch) return null;

    const store = await loadWelcomeWarmupStore();
    const retainedBatches = store.batches.filter((batch) => !isSameWelcomeWarmupBatch(batch, nextBatch));
    const nextStore = await persistWelcomeWarmupStore({
        ...store,
        batches: [nextBatch, ...retainedBatches].slice(0, 80)
    });

    window.dispatchEvent(new CustomEvent(WELCOME_WARMUP_UPDATED_EVENT, {
        detail: {
            sessionId: nextBatch.sessionId,
            quizName: nextBatch.quizName,
            questionCount: nextBatch.questions.length
        }
    }));
    return nextStore.batches.find((batch) => isSameWelcomeWarmupBatch(batch, nextBatch)) || nextBatch;
}

function getWelcomeWarmupSessionBatches(store, sessionId) {
    const resolvedSessionId = normalizeSessionId(sessionId);
    if (!resolvedSessionId) return [];
    return (store?.batches || [])
        .filter((batch) => normalizeSessionId(batch.sessionId) === resolvedSessionId)
        .sort((a, b) => (Number(b.createdAt) || 0) - (Number(a.createdAt) || 0));
}

function getWelcomeWarmupSessionCounts(batches = []) {
    return batches.reduce((counts, batch) => {
        const shown = new Set(batch.shownQuestionIds || []);
        const questionsForBatch = Array.isArray(batch.questions) ? batch.questions : [];
        counts.total += questionsForBatch.length;
        questionsForBatch.forEach((question) => {
            if (!shown.has(question.id)) counts.remaining += 1;
        });
        return counts;
    }, { total: 0, remaining: 0 });
}

async function getWelcomeWarmupRenderState(store, recentSession = null) {
    const sessionId = normalizeSessionId(recentSession?.id || lastWelcomeSessionId);
    if (!sessionId) return { mode: 'hidden' };

    const batches = getWelcomeWarmupSessionBatches(store, sessionId);
    const counts = getWelcomeWarmupSessionCounts(batches);
    for (const batch of batches) {
        const shown = new Set(batch.shownQuestionIds || []);
        const questionsForBatch = Array.isArray(batch.questions) ? batch.questions : [];
        const questionIndex = questionsForBatch.findIndex((question) => question?.id && !shown.has(question.id));
        if (questionIndex >= 0) {
            return {
                mode: 'question',
                questionKind: 'bonus',
                session: recentSession,
                sessionId,
                sessionName: formatWelcomeSessionDisplayName(recentSession),
                batch,
                question: questionsForBatch[questionIndex],
                questionIndex,
                sourceLabel: batch?.sourceLabel || getSessionFileDisplayName(batch?.quizName || batch?.sourcePdf || '') || 'Recent quiz',
                remainingCount: counts.remaining,
                totalCount: counts.total
            };
        }
    }

    const sourceRenderState = await buildWelcomeSourceRenderState(recentSession);
    if (sourceRenderState) return sourceRenderState;

    if (!welcomeWarmupCompletionPending) return { mode: 'hidden' };
    return {
        mode: 'complete',
        session: recentSession,
        sessionId,
        sessionName: formatWelcomeSessionDisplayName(recentSession),
        batches,
        remainingCount: 0,
        totalCount: counts.total
    };
}

function hideWelcomeWarmupPanel() {
    welcomeWarmupActiveItem = null;
    welcomeWarmupKeepOpenOnNextRender = false;
    if (els.welcomeWarmupPanel) els.welcomeWarmupPanel.hidden = true;
}

function getWelcomeWarmupFeedbackText(question, selectedIdx, correctIdx) {
    const choices = getWelcomeWarmupChoices(question);
    const correctText = correctIdx >= 0 && correctIdx < choices.length ? choices[correctIdx] : '';
    if (selectedIdx === correctIdx) {
        const explanation = String(question?.e || question?.explanation || '').replace(/\s+/g, ' ').trim();
        return explanation || 'Correct.';
    }
    return correctText
        ? `The best answer is ${correctText}.`
        : 'This question is worth reviewing.';
}

function setWelcomeWarmupActionForAnswerState(isAnswered) {
    if (!els.welcomeWarmupNextBtn) return;
    els.welcomeWarmupNextBtn.hidden = !isAnswered;
    els.welcomeWarmupNextBtn.textContent = isAnswered ? 'Next question' : 'Answer';
    els.welcomeWarmupNextBtn.title = isAnswered ? 'Show the next question' : 'Show answer choices';
}

function renderWelcomeWarmupChoices(renderState) {
    const choicesHost = els.welcomeWarmupChoices;
    if (!choicesHost) return;
    choicesHost.innerHTML = '';
    const choices = getWelcomeWarmupChoices(renderState?.question);
    const correctIdx = getWelcomeWarmupAnswerIndex(renderState?.question, choices);

    choices.forEach((choice, choiceIdx) => {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'welcome-warmup-choice';
        btn.dataset.idx = String(choiceIdx);

        const label = document.createElement('span');
        label.className = 'welcome-warmup-choice-label';
        label.textContent = `${String.fromCharCode(65 + choiceIdx)}.`;

        const text = document.createElement('span');
        text.className = 'welcome-warmup-choice-text';
        text.textContent = String(choice || '').replace(/^[A-F][\.\)]\s*/i, '');

        btn.append(label, text);
        btn.addEventListener('click', (event) => {
            event.preventDefault();
            event.stopPropagation();
            void handleWelcomeWarmupChoiceSelection(choiceIdx, btn);
        });
        choicesHost.appendChild(btn);
    });

    if (els.welcomeWarmupFeedback) {
        els.welcomeWarmupFeedback.hidden = true;
        els.welcomeWarmupFeedback.textContent = '';
        els.welcomeWarmupFeedback.classList.remove('is-correct', 'is-incorrect');
    }
    choicesHost.dataset.correctIdx = String(correctIdx);
}

function renderWelcomeWarmupState(renderState) {
    const panel = els.welcomeWarmupPanel;
    if (!panel || !els.welcomeWarmupQuestion || !els.welcomeWarmupNextBtn) return;
    if (!renderState || renderState.mode === 'hidden') {
        hideWelcomeWarmupPanel();
        return;
    }

    panel.hidden = false;
    panel.dataset.warmupMode = renderState.mode;
    if (els.welcomeWarmupCard) {
        const shouldKeepQuestionOpen = renderState.mode === 'question' && welcomeWarmupKeepOpenOnNextRender;
        els.welcomeWarmupCard.classList.toggle('is-open', shouldKeepQuestionOpen);
        els.welcomeWarmupCard.setAttribute('aria-expanded', shouldKeepQuestionOpen ? 'true' : 'false');
        welcomeWarmupKeepOpenOnNextRender = false;
    }

    if (renderState.mode === 'complete') {
        const sessionName = renderState.sessionName || formatWelcomeSessionDisplayName(renderState.session);
        welcomeWarmupActiveItem = { mode: 'complete', sessionId: renderState.sessionId };
        if (els.welcomeWarmupSession) {
            els.welcomeWarmupSession.textContent = sessionName;
            els.welcomeWarmupSession.title = sessionName;
        }
        if (els.welcomeWarmupSource) {
            els.welcomeWarmupSource.textContent = 'All set';
            els.welcomeWarmupSource.title = 'All set';
        }
        els.welcomeWarmupQuestion.textContent = `Continue with ${sessionName}.`;
        if (els.welcomeWarmupChoices) els.welcomeWarmupChoices.innerHTML = '';
        if (els.welcomeWarmupFeedback) {
            els.welcomeWarmupFeedback.hidden = true;
            els.welcomeWarmupFeedback.textContent = '';
            els.welcomeWarmupFeedback.classList.remove('is-correct', 'is-incorrect');
        }
        els.welcomeWarmupNextBtn.textContent = 'Continue';
        els.welcomeWarmupNextBtn.title = 'Continue last session';
        els.welcomeWarmupNextBtn.hidden = false;
        return;
    }

    const { batch, question } = renderState;
    const choices = getWelcomeWarmupChoices(question);
    const sourceLabel = renderState.sourceLabel || batch?.sourceLabel || getSessionFileDisplayName(batch?.quizName || batch?.sourcePdf || '') || 'Recent quiz';
    const condensedSourceLabel = condenseWelcomeWarmupSourceLabel(sourceLabel);
    const sessionName = renderState.sessionName || formatWelcomeSessionDisplayName(renderState.session);

    welcomeWarmupActiveItem = {
        mode: 'question',
        questionKind: renderState.questionKind || 'bonus',
        sessionId: renderState.sessionId,
        batchId: batch?.id || '',
        questionId: question.id || '',
        realIdx: Number.isInteger(renderState.realIdx) ? renderState.realIdx : -1,
        startedAt: Date.now(),
        answered: false,
        question
    };
    if (els.welcomeWarmupSession) {
        els.welcomeWarmupSession.textContent = sessionName;
        els.welcomeWarmupSession.title = sessionName;
    }
    if (els.welcomeWarmupSource) {
        els.welcomeWarmupSource.textContent = condensedSourceLabel;
        els.welcomeWarmupSource.title = sourceLabel;
    }
    els.welcomeWarmupQuestion.textContent = question.q;
    renderWelcomeWarmupChoices(renderState);
    setWelcomeWarmupActionForAnswerState(false);
}

async function renderWelcomeWarmupPanelForSession(recentSession = null) {
    if (!els.welcomeWarmupPanel) return;
    if (!recentSession) {
        hideWelcomeWarmupPanel();
        return;
    }

    const timeoutToken = { timedOut: true };
    const renderPromise = (async () => {
        const store = await loadWelcomeWarmupStore();
        return getWelcomeWarmupRenderState(store, recentSession);
    })();
    const timeoutPromise = new Promise((resolve) => {
        setTimeout(() => resolve(timeoutToken), 1500);
    });

    try {
        const renderState = await Promise.race([renderPromise, timeoutPromise]);
        if (renderState === timeoutToken) {
            hideWelcomeWarmupPanel();
            renderPromise.then((lateState) => {
                if (!document.body?.classList?.contains('welcome-mode')) return;
                if (normalizeSessionId(recentSession?.id) !== normalizeSessionId(lastWelcomeSessionId)) return;
                renderWelcomeWarmupState(lateState);
            }).catch((error) => {
                console.warn('Welcome warm-up preview failed after timeout:', error);
            });
            return;
        }
        renderWelcomeWarmupState(renderState);
    } catch (error) {
        console.warn('Welcome warm-up preview failed:', error);
        hideWelcomeWarmupPanel();
    }
}

async function markWelcomeWarmupQuestionShown(activeItem) {
    if (!activeItem || activeItem.mode !== 'question' || !activeItem.questionId) return false;
    const store = await loadWelcomeWarmupStore();
    const targetBatch = store.batches.find((batch) => (
        batch.id === activeItem.batchId
        && normalizeSessionId(batch.sessionId) === normalizeSessionId(activeItem.sessionId)
    ));
    if (!targetBatch) return false;

    if (!Array.isArray(targetBatch.shownQuestionIds)) targetBatch.shownQuestionIds = [];
    if (!targetBatch.shownQuestionIds.includes(activeItem.questionId)) {
        targetBatch.shownQuestionIds.push(activeItem.questionId);
        targetBatch.updatedAt = Date.now();
        await persistWelcomeWarmupStore(store);
    }
    return true;
}

async function refreshWelcomeStatusSummaryForSession(sessionId) {
    const resolvedSessionId = normalizeSessionId(sessionId);
    if (!resolvedSessionId) return;
    const session = await IDB.get('sessions', resolvedSessionId);
    if (!session) return;
    const statusEls = getWelcomeStatusElements();
    if (!statusEls.panel || statusEls.panel.hidden) return;
    const summary = deriveWelcomeSessionSummary(session.data);
    if (statusEls.sessionValue) statusEls.sessionValue.textContent = formatWelcomeSessionDisplayName(session);
    if (statusEls.accuracyValue) statusEls.accuracyValue.textContent = summary.accuracyPct === null ? '—' : `${summary.accuracyPct}%`;
    if (statusEls.remainingValue) statusEls.remainingValue.textContent = summary.remainingCount === null ? '—' : String(summary.remainingCount);
}

async function recordWelcomeSourceQuestionAnswer(activeItem, selectedIdx) {
    const sessionId = normalizeSessionId(activeItem?.sessionId);
    const realIdx = Number(activeItem?.realIdx);
    if (!sessionId || !Number.isInteger(realIdx) || realIdx < 0) return null;

    const session = await IDB.get('sessions', sessionId);
    if (!session) return null;
    const activeQuestions = await buildWelcomeSessionQuestionBank(session);
    const entry = activeQuestions[realIdx];
    if (!entry?.question) return null;

    const sessionData = (session.data && typeof session.data === 'object') ? session.data : {};
    const question = entry.question;
    const choices = getWelcomeWarmupChoices(question);
    const correctIdx = getWelcomeWarmupAnswerIndex(question, choices);
    const isCorrect = selectedIdx === correctIdx;
    const duration = Math.max(0, Date.now() - (Number(activeItem.startedAt) || Date.now()));

    const userAnswerState = getWelcomeSessionAnswerState(activeQuestions, sessionData);
    if (userAnswerState[realIdx] !== undefined) {
        return { isCorrect: userAnswerState[realIdx] === correctIdx, correctIdx, recorded: false };
    }
    userAnswerState[realIdx] = selectedIdx;

    const statsState = buildWelcomeIndexedState(
        activeQuestions,
        sessionData.statsData,
        sessionData.statsDataByQuestionKey,
        (rawValue) => (Array.isArray(rawValue) ? rawValue.slice() : undefined)
    );
    const attempts = Array.isArray(statsState[realIdx]) ? statsState[realIdx].slice() : [];
    attempts.push({ date: Date.now(), duration, correct: isCorrect });
    statsState[realIdx] = attempts;

    const srsState = buildWelcomeIndexedState(
        activeQuestions,
        sessionData.srsData,
        sessionData.srsDataByQuestionKey,
        (rawValue) => (rawValue && typeof rawValue === 'object' ? { ...rawValue } : undefined)
    );
    srsState[realIdx] = updateWelcomeSrsRecord(srsState[realIdx], isCorrect);

    const savedQuestionState = buildWelcomeIndexedState(
        activeQuestions,
        sessionData.savedQuestions,
        sessionData.savedQuestionsByQuestionKey,
        (rawValue) => {
            const parsed = Number(rawValue);
            return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined;
        }
    );
    if (!isCorrect && sessionData?.toolState?.as && savedQuestionState[realIdx] === undefined) {
        savedQuestionState[realIdx] = Date.now();
    }

    const now = Date.now();
    const nextSessionData = {
        ...sessionData,
        userAnswers: userAnswerState,
        userAnswersByQuestionKey: buildWelcomeQuestionKeyBuckets(activeQuestions, userAnswerState),
        statsData: statsState,
        statsDataByQuestionKey: buildWelcomeQuestionKeyBuckets(activeQuestions, statsState),
        srsData: srsState,
        srsDataByQuestionKey: buildWelcomeQuestionKeyBuckets(activeQuestions, srsState),
        savedQuestions: savedQuestionState,
        savedQuestionsByQuestionKey: buildWelcomeQuestionKeyBuckets(activeQuestions, savedQuestionState),
        currentQIndex: activeQuestions.length ? ((realIdx + 1) % activeQuestions.length) : 0
    };
    const existingMeta = buildSessionProgressMetaFromContainer(sessionData, session.timestamp);
    nextSessionData.progressMeta = buildSessionProgressMetaFromContainer(nextSessionData, null, {
        revision: (Number(existingMeta.revision) || 0) + 1,
        updatedAt: now
    });

    await IDB.put('sessions', null, {
        ...session,
        timestamp: new Date(now).toISOString(),
        data: nextSessionData
    });

    setLastSessionId(sessionId, { refreshWelcome: false });
    await refreshWelcomeStatusSummaryForSession(sessionId);

    return { isCorrect, correctIdx, recorded: true };
}

async function handleWelcomeWarmupChoiceSelection(selectedIdx, selectedButton = null) {
    const activeItem = welcomeWarmupActiveItem;
    if (!activeItem || activeItem.mode !== 'question' || activeItem.answered) return;

    const choices = getWelcomeWarmupChoices(activeItem.question);
    const correctIdx = getWelcomeWarmupAnswerIndex(activeItem.question, choices);
    const isCorrect = selectedIdx === correctIdx;
    activeItem.answered = true;
    activeItem.selectedIdx = selectedIdx;

    const choiceButtons = Array.from(els.welcomeWarmupChoices?.querySelectorAll?.('.welcome-warmup-choice') || []);
    choiceButtons.forEach((button) => {
        const btnIdx = Number(button.dataset.idx);
        button.disabled = true;
        button.classList.add('is-locked');
        if (btnIdx === correctIdx) button.classList.add('is-correct');
        if (btnIdx === selectedIdx && selectedIdx !== correctIdx) button.classList.add('is-incorrect');
    });
    selectedButton?.classList?.add('is-selected');

    if (activeItem.questionKind === 'source') {
        try {
            const recorded = await recordWelcomeSourceQuestionAnswer(activeItem, selectedIdx);
            if (!recorded) throw new Error('Session answer was not saved.');
        } catch (error) {
            console.warn('Failed to save welcome source answer:', error);
            activeItem.answered = false;
            choiceButtons.forEach((button) => {
                button.disabled = false;
                button.classList.remove('is-locked', 'is-correct', 'is-incorrect', 'is-selected');
            });
            if (els.welcomeWarmupFeedback) {
                els.welcomeWarmupFeedback.textContent = 'Could not save that answer. Try again.';
                els.welcomeWarmupFeedback.hidden = false;
                els.welcomeWarmupFeedback.classList.remove('is-correct');
                els.welcomeWarmupFeedback.classList.add('is-incorrect');
            }
            setWelcomeWarmupActionForAnswerState(false);
            return;
        }
    } else {
        const marked = await markWelcomeWarmupQuestionShown(activeItem);
        if (!marked) console.warn('Welcome bonus question was answered but not marked complete.');
    }

    if (els.welcomeWarmupFeedback) {
        els.welcomeWarmupFeedback.textContent = getWelcomeWarmupFeedbackText(activeItem.question, selectedIdx, correctIdx);
        els.welcomeWarmupFeedback.hidden = false;
        els.welcomeWarmupFeedback.classList.toggle('is-correct', isCorrect);
        els.welcomeWarmupFeedback.classList.toggle('is-incorrect', !isCorrect);
    }
    setWelcomeWarmupActionForAnswerState(true);
}

let warmupMouseleaveLocked = false;

function closeWelcomeWarmupReveal() {
    if (els.welcomeWarmupCard && els.welcomeWarmupCard.classList.contains('is-open')) {
        els.welcomeWarmupCard.classList.remove('is-open');
        els.welcomeWarmupCard.setAttribute('aria-expanded', 'false');
    }
}

async function handleWelcomeWarmupAction() {
    if (welcomeWarmupActiveItem?.mode === 'question') {
        if (!welcomeWarmupActiveItem.answered) {
            if (els.welcomeWarmupCard) {
                els.welcomeWarmupCard.classList.add('is-open');
                els.welcomeWarmupCard.setAttribute('aria-expanded', 'true');
            }
            return;
        }
        warmupMouseleaveLocked = true;
        setTimeout(() => { warmupMouseleaveLocked = false; }, 400);
        welcomeWarmupKeepOpenOnNextRender = true;
        welcomeWarmupCompletionPending = true;
        await syncWelcomeScreenState();
        return;
    }
    await continueLastSessionFromWelcome();
}

function bindWelcomeWarmupPanel() {
    if (els.welcomeWarmupNextBtn && els.welcomeWarmupNextBtn.dataset.bound !== '1') {
        els.welcomeWarmupNextBtn.addEventListener('click', (event) => {
            event.preventDefault();
            event.stopPropagation();
            void handleWelcomeWarmupAction();
        });
        els.welcomeWarmupNextBtn.dataset.bound = '1';
    }

    if (els.welcomeWarmupCard && els.welcomeWarmupCard.dataset.bound !== '1') {
        const toggleWarmupReveal = (event = null) => {
            if (els.welcomeWarmupPanel?.dataset.warmupMode !== 'question') return;
            if (event?.target?.closest?.('.welcome-warmup-reveal, .welcome-warmup-source')) return;
            const isOpen = !els.welcomeWarmupCard.classList.contains('is-open');
            els.welcomeWarmupCard.classList.toggle('is-open', isOpen);
            els.welcomeWarmupCard.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
        };
        els.welcomeWarmupCard.addEventListener('click', toggleWarmupReveal);
        els.welcomeWarmupCard.addEventListener('keydown', (event) => {
            if (event.target !== els.welcomeWarmupCard) return;
            if (event.key !== 'Enter' && event.key !== ' ') return;
            event.preventDefault();
            toggleWarmupReveal(event);
        });
        els.welcomeWarmupCard.dataset.bound = '1';
    }

    if (els.welcomeWarmupPanel && els.welcomeWarmupPanel.dataset.boundMouseleave !== '1') {
        els.welcomeWarmupPanel.addEventListener('mouseleave', () => {
            if (warmupMouseleaveLocked) {
                setTimeout(() => {
                    if (!els.welcomeWarmupPanel?.matches?.(':hover')) closeWelcomeWarmupReveal();
                }, 420);
                return;
            }
            closeWelcomeWarmupReveal();
        });
        els.welcomeWarmupPanel.dataset.boundMouseleave = '1';
    }
}

window.MemoraWelcomeWarmup = {
    saveBatch: saveWelcomeWarmupBatch,
    refresh: () => syncWelcomeScreenState(),
    loadStore: loadWelcomeWarmupStore
};

window.addEventListener(WELCOME_WARMUP_UPDATED_EVENT, () => {
    if (!IS_EMBED_DEMO && document.body?.classList?.contains('welcome-mode')) {
        void syncWelcomeScreenState();
    }
});

// The warm-up preview needs the session's saved answer state, which the summary
// index deliberately does not carry. Hydrate exactly one record, and only after
// the Welcome buttons are already live.
async function hydrateWelcomeSessionRecord(sessionEntry) {
    if (!sessionEntry) return null;
    if (sessionEntry.data && typeof sessionEntry.data === 'object') return sessionEntry;
    const sessionId = normalizeSessionId(sessionEntry.id);
    if (!sessionId) return sessionEntry;
    const full = await readSessionStoreValueWithTimeout('sessions', sessionId, 3000);
    return full || sessionEntry;
}

function applyWelcomeStatusSummary(statusEls, session) {
    if (!statusEls?.panel || !session) return;
    const summary = getWelcomeSessionSummaryForEntry(session);
    if (statusEls.sessionValue) statusEls.sessionValue.textContent = formatWelcomeSessionDisplayName(session);
    if (statusEls.accuracyValue) statusEls.accuracyValue.textContent = summary.accuracyPct === null ? '—' : `${summary.accuracyPct}%`;
    if (statusEls.remainingValue) statusEls.remainingValue.textContent = summary.remainingCount === null ? '—' : String(summary.remainingCount);
}

async function syncWelcomeScreenState(options = {}) {
    if (IS_EMBED_DEMO) {
        setWelcomeActionsPending(false);
        return;
    }
    if (isWelcomeSessionLoadPending) return;
    const syncEpoch = ++welcomeScreenSyncEpoch;
    const progressive = !!options?.progressive;
    const progressiveStartedAt = performance.now();

    // Summaries only: the Welcome surface needs names, timestamps and counts, not
    // the saved answer state of every session. The one session we actually show is
    // hydrated below, off the button-enabling path.
    let sessions = [];
    try {
        sessions = await readSavedSessionSummaries({ timeoutMs: 3000 }) || [];
    } catch (_) {
        sessions = [];
    }

    if (!Array.isArray(sessions)) sessions = [];
    sessions = sessions.filter((session) => hasPersistableSavedSessionContent(session));
    syncSessionNumberCounterFromSessions(sessions);

    const isPhoneWelcome = document.documentElement.classList.contains('phone-device');
    let recentlyDeletedSessions = [];
    if (isPhoneWelcome) {
        try {
            const storedDeleted = await IDB.get('settings', 'deletedSessions');
            let legacyDeleted = [];
            try {
                const legacyRaw = localStorage.getItem('deletedSessions');
                legacyDeleted = legacyRaw ? JSON.parse(legacyRaw) : [];
            } catch (_) {
                legacyDeleted = [];
            }
            recentlyDeletedSessions = [
                ...(Array.isArray(storedDeleted) ? storedDeleted : []),
                ...(Array.isArray(legacyDeleted) ? legacyDeleted : [])
            ];
        } catch (_) {
            recentlyDeletedSessions = [];
        }
    }
    const recentlyDeletedCutoff = Date.now() - (15 * 24 * 60 * 60 * 1000);
    const hasRecentlyDeletedSession = recentlyDeletedSessions.some((entry) => {
        const deletedAt = Number(entry?.deletedAt);
        return !deletedAt || deletedAt >= recentlyDeletedCutoff;
    });

    const storedId = normalizeSessionId(getLastSessionIdSafe());
    const preferredSession = storedId
        ? sessions.find((session) => normalizeSessionId(session?.id) === storedId)
        : null;
    const recentSession = preferredSession || sessions[0] || null;
    const hasSavedSession = !!recentSession;
    lastWelcomeSessionId = hasSavedSession ? normalizeSessionId(recentSession?.id) : '';

    if (els.continueBtn) {
        els.continueBtn.style.display = hasSavedSession ? 'inline-flex' : 'none';
        els.continueBtn.classList.toggle('primary-action', hasSavedSession);
        setContinueButtonSessionLabel(recentSession);
    }
    if (els.newSessionBtn) {
        els.newSessionBtn.style.display = 'inline-flex';
        els.newSessionBtn.classList.toggle('primary-action', !hasSavedSession);
    }
    if (els.welcomeHistoryBtn) {
        els.welcomeHistoryBtn.style.display = (!isPhoneWelcome || sessions.length > 0 || hasRecentlyDeletedSession)
            ? 'inline-flex'
            : 'none';
    }

    // Buttons are the critical recovery path. Do not let slow legacy-session
    // preview reconstruction keep the Welcome screen visually locked.
    setWelcomeActionsPending(false);

    const statusEls = getWelcomeStatusElements();
    if (!statusEls.panel) {
        return;
    }

    statusEls.panel.hidden = !hasSavedSession;
    if (!hasSavedSession) {
        hideWelcomeWarmupPanel();
        setWelcomeActionsPending(false);
        return;
    }

    const summary = getWelcomeSessionSummaryForEntry(recentSession);
    if (statusEls.sessionValue) {
        statusEls.sessionValue.textContent = formatWelcomeSessionDisplayName(recentSession);
    }
    if (statusEls.accuracyValue) {
        statusEls.accuracyValue.textContent = summary.accuracyPct === null ? '—' : `${summary.accuracyPct}%`;
    }
    if (statusEls.remainingValue) {
        statusEls.remainingValue.textContent = summary.remainingCount === null ? '—' : String(summary.remainingCount);
    }
    if (progressive) {
        const elapsed = performance.now() - progressiveStartedAt;
        scheduleWelcomeSurfaceReveal(statusEls.panel, WELCOME_STATUS_REVEAL_DELAY_MS - elapsed);
        setTimeout(() => {
            if (syncEpoch !== welcomeScreenSyncEpoch || !document.body?.classList?.contains('welcome-mode')) return;
            void hydrateWelcomeSessionRecord(recentSession).then((hydratedSession) => {
                if (syncEpoch !== welcomeScreenSyncEpoch || !document.body?.classList?.contains('welcome-mode')) return null;
                applyWelcomeStatusSummary(statusEls, hydratedSession);
                return renderWelcomeWarmupPanelForSession(hydratedSession);
            }).then(() => {
                if (syncEpoch !== welcomeScreenSyncEpoch || !els.welcomeWarmupPanel || els.welcomeWarmupPanel.hidden) return;
                const warmupElapsed = performance.now() - progressiveStartedAt;
                scheduleWelcomeSurfaceReveal(els.welcomeWarmupPanel, WELCOME_WARMUP_REVEAL_DELAY_MS - warmupElapsed);
            });
        }, Math.min(650, WELCOME_WARMUP_REVEAL_DELAY_MS));
    } else {
        const hydratedSession = await hydrateWelcomeSessionRecord(recentSession);
        applyWelcomeStatusSummary(statusEls, hydratedSession);
        await renderWelcomeWarmupPanelForSession(hydratedSession);
        statusEls.panel.classList.remove('is-pending-reveal');
        els.welcomeWarmupPanel?.classList?.remove('is-pending-reveal');
    }
    setWelcomeActionsPending(false);
}

function primeWelcomeButtonsFallback() {
    if (IS_EMBED_DEMO) return;
    if (els.newSessionBtn) {
        els.newSessionBtn.style.display = 'inline-flex';
        if (!els.newSessionBtn.onclick) {
            els.newSessionBtn.onclick = () => { void startNewSessionWithNamePrompt(true); };
        }
    }
    if (els.continueBtn) {
        syncContinueButton();
        if (!els.continueBtn.onclick) {
            els.continueBtn.onclick = () => { void continueLastSessionFromWelcome(); };
        }
    }
    if (els.welcomeHistoryBtn) {
        els.welcomeHistoryBtn.style.display = document.documentElement.classList.contains('phone-device')
            ? 'none'
            : 'inline-flex';
        if (!els.welcomeHistoryBtn.onclick) {
            els.welcomeHistoryBtn.onclick = () => { void SessionManager.showHistory(); };
        }
    }
}

function setLastSessionId(sessionId, options = {}) {
    const resolved = normalizeSessionId(sessionId);
    try {
        if (resolved) localStorage.setItem('lastSessionId', resolved);
        else localStorage.removeItem('lastSessionId');
    } catch (error) {
        console.warn('Could not persist the last-session startup hint:', error);
    }
    if (resolved) lastWelcomeSessionId = resolved;
    else lastWelcomeSessionId = '';
    syncContinueButton(resolved);
    const shouldRefreshWelcome = Object.prototype.hasOwnProperty.call(options, 'refreshWelcome')
        ? !!options.refreshWelcome
        : !!document.body?.classList?.contains('welcome-mode');
    if (shouldRefreshWelcome) void syncWelcomeScreenState();
    return resolved;
}

async function resolveLastSessionId(options = {}) {
    const { preferStored = true, refreshWelcome = true } = options;
    const storedId = normalizeSessionId(getLastSessionIdSafe());
    let sessions = [];
    try {
        sessions = await readSavedSessionSummaries({ timeoutMs: 3000 }) || [];
    } catch (_) {
        sessions = [];
    }

    if (!Array.isArray(sessions) || sessions.length === 0) {
        setLastSessionId(null, { refreshWelcome });
        return null;
    }

    const availableIds = new Set();
    sessions.forEach((session) => {
        const id = normalizeSessionId(session?.id);
        if (id) availableIds.add(id);
    });

    if (preferStored && storedId && availableIds.has(storedId)) {
        setLastSessionId(storedId, { refreshWelcome });
        return storedId;
    }

    sessions.sort((a, b) => new Date(b?.timestamp || 0) - new Date(a?.timestamp || 0));
    const fallbackId = normalizeSessionId(sessions[0]?.id);
    if (!fallbackId) {
        setLastSessionId(null, { refreshWelcome });
        return null;
    }
    setLastSessionId(fallbackId, { refreshWelcome });
    return fallbackId;
}

async function continueLastSessionFromWelcome() {
    if (continueLastSessionPromise) return continueLastSessionPromise;
    const loadEpoch = ++welcomeSessionLoadEpoch;

    continueLastSessionPromise = (async () => {
        setWelcomeSessionLoadPending(true);
        await new Promise((resolve) => requestAnimationFrame(resolve));
        if (!isCurrentWelcomeSessionLoad(loadEpoch)) return false;

        const cachedSessionId = normalizeSessionId(lastWelcomeSessionId);
        const sessionId = cachedSessionId || await resolveLastSessionId({
            preferStored: true,
            refreshWelcome: false
        });
        if (!isCurrentWelcomeSessionLoad(loadEpoch)) return false;
        if (!sessionId) {
            showPopup('No saved sessions found.', null, true);
            return false;
        }

        if (normalizeSessionId(currentSessionId) === sessionId && hasPersistableSessionContent()) {
            writeQuickSessionSnapshot();
        }

        try {
            return await SessionManager.load(sessionId, {
                fromWelcomeContinue: true,
                welcomeSessionLoadEpoch: loadEpoch
            });
        } catch (error) {
            if (!isCurrentWelcomeSessionLoad(loadEpoch)) return false;
            console.error('Failed to continue last session:', error);
            showPopup('Could not reopen the saved session. Try loading it from History.', null, true);
            return false;
        }
    })().finally(() => {
        const stillWelcome = document.body?.classList?.contains('welcome-mode');
        const stillCurrent = isCurrentWelcomeSessionLoad(loadEpoch);
        setWelcomeSessionLoadPending(false);
        continueLastSessionPromise = null;
        if (stillWelcome && stillCurrent) void syncWelcomeScreenState();
    });

    return continueLastSessionPromise;
}

let sessionLoadFadeOverlay = null;
let sessionLoadFadeTimer = null;
let sessionChoiceLoadTimer = null;
let memoraSurfaceSettleTimer = null;
let memoraSurfaceSettleFrame = 0;

function shouldSkipSessionLoadFade() {
    return getMemoraMotionMode() === 'reduced';
}

function clearSessionLoadFade(options = {}) {
    const keepChoiceAnimation = !!options?.keepChoiceAnimation;
    if (sessionLoadFadeTimer) {
        clearTimeout(sessionLoadFadeTimer);
        sessionLoadFadeTimer = null;
    }
    if (!keepChoiceAnimation && sessionChoiceLoadTimer) {
        clearTimeout(sessionChoiceLoadTimer);
        sessionChoiceLoadTimer = null;
    }
    if (!keepChoiceAnimation) document.body?.classList.remove('memora-session-choice-loading');
    document.body?.classList.remove('memora-main-screen-entering');
    if (sessionLoadFadeOverlay && sessionLoadFadeOverlay.parentNode) {
        sessionLoadFadeOverlay.remove();
    }
    sessionLoadFadeOverlay = null;
}

function triggerSessionChoiceLoadAnimation(durationMs = 6800) {
    const body = document.body;
    if (!body || shouldSkipSessionLoadFade()) return;
    if (sessionChoiceLoadTimer) {
        clearTimeout(sessionChoiceLoadTimer);
        sessionChoiceLoadTimer = null;
    }
    body.classList.remove('memora-session-choice-loading');
    void body.offsetWidth;
    body.classList.add('memora-session-choice-loading');
    sessionChoiceLoadTimer = setTimeout(() => {
        body.classList.remove('memora-session-choice-loading');
        sessionChoiceLoadTimer = null;
    }, Math.max(2800, Number(durationMs) || 6800));
}

function clearMemoraSurfaceSettle() {
    if (memoraSurfaceSettleTimer) {
        clearTimeout(memoraSurfaceSettleTimer);
        memoraSurfaceSettleTimer = null;
    }
    if (memoraSurfaceSettleFrame) {
        cancelAnimationFrame(memoraSurfaceSettleFrame);
        memoraSurfaceSettleFrame = 0;
    }
    document.body?.classList.remove(
        'memora-surface-settling',
        'memora-surface-settle-main',
        'memora-surface-settle-pane',
        'memora-surface-settle-welcome'
    );
}

function syncMemoraSurfaceAfterSettle({ restoreQuizGeometry = true } = {}) {
    if (restoreQuizGeometry && !isQuizCardExpanded && typeof restoreQuizCardGeometryForCurrentLayout === 'function') restoreQuizCardGeometryForCurrentLayout();
    if (typeof scheduleQuizCardHeightNormalization === 'function') scheduleQuizCardHeightNormalization();
    if (typeof scheduleQuizCardContentScaleSync === 'function') scheduleQuizCardContentScaleSync();
    if (typeof scheduleQuickActionsDockPosition === 'function') scheduleQuickActionsDockPosition();
}

function triggerMemoraSurfaceSettle(mode = 'main', durationMs = 680) {
    const body = document.body;
    if (!body) return;

    clearMemoraSurfaceSettle();
    const normalizedMode = ['main', 'pane', 'welcome'].includes(String(mode)) ? String(mode) : 'main';
    const duration = Math.max(120, Number(durationMs) || 680);
    if (shouldSkipSessionLoadFade()) {
        syncMemoraSurfaceAfterSettle();
        return;
    }

    body.classList.add('memora-surface-settling', `memora-surface-settle-${normalizedMode}`);
    memoraSurfaceSettleFrame = requestAnimationFrame(() => {
        memoraSurfaceSettleFrame = requestAnimationFrame(() => {
            memoraSurfaceSettleFrame = 0;
            syncMemoraSurfaceAfterSettle();
        });
    });
    memoraSurfaceSettleTimer = setTimeout(() => {
        // Geometry is resolved in the pre-paint pass above. Re-restoring it at
        // the end of this timer can make the solo card's bottom edge jump after
        // a page load or a duo/trio collapse has already visually settled.
        syncMemoraSurfaceAfterSettle({ restoreQuizGeometry: false });
        clearMemoraSurfaceSettle();
    }, duration);
}
window.triggerMemoraSurfaceSettle = triggerMemoraSurfaceSettle;

// A motion change finishes a pending settle now, in its natural order: the pre-paint geometry
// restore runs before pane owners (app.panes.js, registered later) commit their final layout.
document.addEventListener('memora:motion-mode-changed', (event) => {
    if (event.detail?.mode === 'standard') return;
    if (!memoraSurfaceSettleFrame && !memoraSurfaceSettleTimer) return;
    const restoreQuizGeometry = !!memoraSurfaceSettleFrame;
    clearMemoraSurfaceSettle();
    syncMemoraSurfaceAfterSettle({ restoreQuizGeometry });
});

function triggerSessionLoadFade() {
    if (!document.body) return;
    if (shouldSkipSessionLoadFade()) {
        triggerMemoraSurfaceSettle('main', 120);
        return;
    }

    clearSessionLoadFade();
    document.body.classList.remove('memora-main-screen-entering');
    void document.body.offsetWidth;
    document.body.classList.add('memora-main-screen-entering');
    triggerMemoraSurfaceSettle('main', 720);
    const overlay = document.createElement('div');
    overlay.className = 'memora-session-load-fade-overlay';
    overlay.setAttribute('aria-hidden', 'true');
    sessionLoadFadeOverlay = overlay;
    document.body.appendChild(overlay);

    const cleanup = () => {
        if (sessionLoadFadeOverlay !== overlay) return;
        document.body.classList.remove('memora-main-screen-entering');
        clearSessionLoadFade();
    };

    overlay.addEventListener('animationend', cleanup, { once: true });
    sessionLoadFadeTimer = setTimeout(cleanup, 900);
}

function getModeToggleButtonText(isFlashcardActive = isFlashcardMode) {
    return isFlashcardActive ? '\u{1F3B4} Switch to Quiz' : '\u{1F3B4} Switch to Flashcards';
}

function setModeToggleButtonLabel(isFlashcardActive = isFlashcardMode) {
    const labelEl = els.modeBtn?.querySelector('.gradient-text');
    if (labelEl) labelEl.innerText = getModeToggleButtonText(isFlashcardActive);
}

function getSessionStudyPlannerBranchTimestamp(branch, fallbackKeys = []) {
    const state = (branch && typeof branch === 'object') ? branch : {};
    const timestamps = [Number(state.updatedAt) || 0];
    fallbackKeys.forEach((key) => {
        timestamps.push(Number(state?.[key]) || 0);
    });
    return Math.max(...timestamps, 0);
}

function mergeStudyDashboardState(existingRaw, incomingRaw) {
    const existing = createDefaultStudyDashboardState(existingRaw);
    const incoming = createDefaultStudyDashboardState(incomingRaw);
    const existingTs = getSessionStudyPlannerBranchTimestamp(existing, ['lastOpenedAt', 'lastLaunchedAt']);
    const incomingTs = getSessionStudyPlannerBranchTimestamp(incoming, ['lastOpenedAt', 'lastLaunchedAt']);
    if (incomingTs > existingTs) return incoming;
    if (existingTs > incomingTs) return existing;
    return createDefaultStudyDashboardState({
        ...existing,
        ...incoming,
        hasSeenDashboard: existing.hasSeenDashboard || incoming.hasSeenDashboard,
        lastOpenedAt: Math.max(existing.lastOpenedAt, incoming.lastOpenedAt),
        lastLaunchedAt: Math.max(existing.lastLaunchedAt, incoming.lastLaunchedAt),
        updatedAt: Math.max(existing.updatedAt, incoming.updatedAt)
    });
}

function mergeStudySetupState(existingRaw, incomingRaw) {
    const existing = createDefaultStudySetupState(existingRaw);
    const incoming = createDefaultStudySetupState(incomingRaw);
    const existingTs = getSessionStudyPlannerBranchTimestamp(existing, ['completedAt']);
    const incomingTs = getSessionStudyPlannerBranchTimestamp(incoming, ['completedAt']);
    if (incomingTs > existingTs) return incoming;
    if (existingTs > incomingTs) return existing;

    const mergedAnswers = { ...existing.answers };
    Object.entries(incoming.answers || {}).forEach(([key, value]) => {
        if (typeof value === 'string' && value) mergedAnswers[key] = value;
    });

    return createDefaultStudySetupState({
        ...existing,
        ...incoming,
        answers: mergedAnswers,
        completedAt: Math.max(existing.completedAt, incoming.completedAt),
        updatedAt: Math.max(existing.updatedAt, incoming.updatedAt)
    });
}

function mergeStudyPlanState(existingRaw, incomingRaw) {
    const existing = createDefaultStudyPlanState(existingRaw);
    const incoming = createDefaultStudyPlanState(incomingRaw);
    const existingTs = getSessionStudyPlannerBranchTimestamp(existing, ['generatedAt', 'appliedAt', 'lastUndoneAt']);
    const incomingTs = getSessionStudyPlannerBranchTimestamp(incoming, ['generatedAt', 'appliedAt', 'lastUndoneAt']);
    if (incomingTs > existingTs) return incoming;
    if (existingTs > incomingTs) return existing;
    return createDefaultStudyPlanState({
        ...existing,
        ...incoming,
        generatedAt: Math.max(existing.generatedAt, incoming.generatedAt),
        appliedAt: Math.max(existing.appliedAt, incoming.appliedAt),
        lastUndoneAt: Math.max(existing.lastUndoneAt, incoming.lastUndoneAt),
        updatedAt: Math.max(existing.updatedAt, incoming.updatedAt),
        activeActionKey: incoming.activeActionKey || existing.activeActionKey,
        activeActionLabel: incoming.activeActionLabel || existing.activeActionLabel,
        recommendation: incoming.recommendation || existing.recommendation,
        metricsSnapshot: incoming.metricsSnapshot || existing.metricsSnapshot,
        preApplySnapshot: incoming.preApplySnapshot || existing.preApplySnapshot
    });
}

function getCurrentSessionContentPresence() {
    const questionCount = Array.isArray(questions) ? questions.length : 0;
    const quizSetCount = Array.isArray(sessionManifest?.jsons) ? sessionManifest.jsons.length : 0;
    const pdfCount = Array.isArray(sessionManifest?.pdfs) ? sessionManifest.pdfs.length : 0;
    const hasNamedSession = !!normalizeSessionNameInput(currentSessionCustomName);
    const hasStudyContent = questionCount > 0 || quizSetCount > 0 || pdfCount > 0;
    return {
        questionCount,
        quizSetCount,
        pdfCount,
        hasNamedSession,
        hasQuestionContent: questionCount > 0,
        hasSessionContent: hasStudyContent
    };
}

function shouldOfferStudyDashboard() {
    if (!STUDY_PLANNER_FEATURE_ENABLED) return false;
    if (IS_EMBED_DEMO || window.__MEMORA_SMOKE_MODE__) return false;
    if (!normalizeSessionId(currentSessionId)) return false;
    return getCurrentSessionContentPresence().hasSessionContent;
}

function syncStudyPlannerFeatureVisibility() {
    const isVisible = !!STUDY_PLANNER_FEATURE_ENABLED;
    const dashboardPrefRow = els.studyDashboardAutoOpenToggle?.closest('.menu-inline-checkbox');
    if (els.studyDashboardMenuBtn) els.studyDashboardMenuBtn.style.display = isVisible ? '' : 'none';
    if (els.studySetupMenuBtn) els.studySetupMenuBtn.style.display = isVisible ? '' : 'none';
    if (els.studyDashboardMenuBtn) syncToolsMenuHelpWrapVisibility(els.studyDashboardMenuBtn);
    if (els.studySetupMenuBtn) syncToolsMenuHelpWrapVisibility(els.studySetupMenuBtn);
    if (dashboardPrefRow) dashboardPrefRow.style.display = isVisible ? '' : 'none';
    if (!isVisible) hideStudyPlannerSurfaces();
}

function getStudyDashboardAutoOpenDisabled() {
    try {
        return localStorage.getItem(STUDY_DASHBOARD_AUTO_OPEN_DISABLED_KEY) === '1';
    } catch (error) {
        return false;
    }
}

function syncStudyDashboardAutoOpenUI() {
    const isDisabled = getStudyDashboardAutoOpenDisabled();
    if (els.studyDashboardAutoOpenToggle) els.studyDashboardAutoOpenToggle.checked = isDisabled;
    const overlayToggle = els.studyDashboardShell?.querySelector('[data-study-dashboard-auto-open-toggle]');
    if (overlayToggle) overlayToggle.checked = isDisabled;
}

function setStudyDashboardAutoOpenDisabled(disabled) {
    try {
        if (disabled) localStorage.setItem(STUDY_DASHBOARD_AUTO_OPEN_DISABLED_KEY, '1');
        else localStorage.removeItem(STUDY_DASHBOARD_AUTO_OPEN_DISABLED_KEY);
    } catch (error) {
        console.warn('Unable to persist study dashboard auto-open preference:', error);
    }
    syncStudyDashboardAutoOpenUI();
}

function shouldAutoOpenStudyDashboard() {
    return shouldOfferStudyDashboard() && !getStudyDashboardAutoOpenDisabled();
}

function isStudySetupComplete(rawAnswers = studySetupState.answers) {
    const answers = normalizeStudySetupAnswers(rawAnswers);
    return Object.values(answers).every((value) => !!value);
}

function getStudySetupAnswerLabel(key, value) {
    const maps = {
        examTiming: {
            none: 'No exam date yet',
            within_1_week: 'Within 1 week',
            within_2_weeks: 'Within 2 weeks',
            within_1_month: 'Within 1 month',
            later: 'Later than 1 month'
        },
        preparedness: {
            not_ready: 'Not ready',
            somewhat_ready: 'Somewhat ready',
            mostly_ready: 'Mostly ready'
        },
        timeToday: {
            15: '15 minutes',
            30: '30 minutes',
            45: '45 minutes',
            '60_plus': '60+ minutes'
        },
        priority: {
            weak_cards: 'Weak cards',
            due_cards: 'Due cards',
            coverage: 'Coverage'
        },
        goal: {
            cram: 'Cram',
            balanced: 'Balanced',
            retention: 'Retention'
        }
    };
    return maps?.[key]?.[value] || '';
}

function getStudySetupTimeBudgetMinutes(value) {
    switch (String(value || '').trim()) {
        case '15':
            return 15;
        case '30':
            return 30;
        case '45':
            return 45;
        case '60_plus':
            return 75;
        default:
            return 30;
    }
}

function getCurrentSessionDisplayLabel() {
    const explicitName = String(currentSessionCustomName || '').trim();
    if (explicitName) return explicitName;

    const firstQuiz = Array.isArray(sessionManifest?.jsons) ? String(sessionManifest.jsons[0] || '').trim() : '';
    if (firstQuiz) return getSessionFileDisplayName(firstQuiz) || firstQuiz;

    const firstPdf = Array.isArray(sessionManifest?.pdfs) ? String(sessionManifest.pdfs[0] || '').trim() : '';
    if (firstPdf) return getSessionFileDisplayName(firstPdf) || firstPdf;

    const normalizedSessionId = normalizeSessionId(currentSessionId);
    if (normalizedSessionId) return `Session ${normalizedSessionId.slice(-6)}`;
    return getDefaultNewSessionName(currentSessionNumber);
}

function getSessionOnlyConfidenceForIndex(idx) {
    const data = srsData[idx];
    const confRaw = typeof data?.lastConfidence === 'string' ? data.lastConfidence.toLowerCase() : '';
    if (confRaw === 'again' || confRaw === 'shaky' || confRaw === 'good' || confRaw === 'easy') return confRaw;

    const snap = adaptiveReviewSnapshot?.[idx];
    const snapConf = typeof snap?.confidence === 'string' ? snap.confidence.toLowerCase() : '';
    if (snapConf === 'again' || snapConf === 'shaky' || snapConf === 'good' || snapConf === 'easy') return snapConf;

    return '';
}

function hasSessionSpacedReviewHistoryForIndex(idx) {
    const snap = adaptiveReviewSnapshot?.[idx];
    if (snap && typeof snap === 'object') return true;

    const data = srsData[idx];
    if (!data || typeof data !== 'object') return false;

    const lastReviewed = Number(data.lastReviewed) || 0;
    const lastQuality = Number(data.lastQuality);
    const repetitions = Number(data.repetitions) || 0;
    const lapses = Number(data.lapses) || 0;
    const interval = Number(data.interval) || 0;
    const nextReview = Number(data.nextReview) || 0;
    const lastConfidence = typeof data.lastConfidence === 'string' ? data.lastConfidence.toLowerCase() : '';

    return lastReviewed > 0
        || Number.isFinite(lastQuality)
        || repetitions > 0
        || lapses > 0
        || interval > 0
        || nextReview > 0
        || lastConfidence === 'again'
        || lastConfidence === 'shaky'
        || lastConfidence === 'good'
        || lastConfidence === 'easy';
}

function getCurrentSessionAverageAttemptMs() {
    let totalDuration = 0;
    let totalAttempts = 0;
    Object.values(statsData || {}).forEach((attempts) => {
        if (!Array.isArray(attempts)) return;
        attempts.forEach((attempt) => {
            const duration = Number(attempt?.duration) || 0;
            if (duration <= 0) return;
            totalDuration += duration;
            totalAttempts += 1;
        });
    });
    return totalAttempts > 0 ? Math.round(totalDuration / totalAttempts) : 45000;
}

function estimateStudyCatchUpMinutes(dueNowCount, againHardCount, averageAttemptMs) {
    const boundedAttemptMs = Math.max(20000, Math.min(90000, Number(averageAttemptMs) || 45000));
    const weightedUnits = Math.max(0, Number(dueNowCount) || 0) + (Math.max(0, Number(againHardCount) || 0) * 0.55);
    if (weightedUnits <= 0) return 0;
    return Math.max(1, Math.ceil((weightedUnits * boundedAttemptMs) / 60000));
}

function buildCurrentSessionStudyMetrics(now = Date.now()) {
    const presence = getCurrentSessionContentPresence();
    const savedCount = Object.keys(savedQuestions || {})
        .map((key) => Number(key))
        .filter((idx) => Number.isInteger(idx) && idx >= 0 && idx < presence.questionCount)
        .length;

    let wrongCount = 0;
    let unseenCount = 0;
    let dueNowCount = 0;
    let againHardCount = 0;
    let spacedHistoryCount = 0;

    for (let idx = 0; idx < presence.questionCount; idx += 1) {
        const attempts = Array.isArray(statsData?.[idx]) ? statsData[idx] : [];
        if (attempts.some((attempt) => attempt && attempt.correct === false)) wrongCount += 1;

        const hasSpacedHistory = hasSessionSpacedReviewHistoryForIndex(idx);
        if (hasSpacedHistory) spacedHistoryCount += 1;

        const confidence = getSessionOnlyConfidenceForIndex(idx);
        if (confidence === 'again' || confidence === 'shaky') againHardCount += 1;

        const nextReview = Number(srsData?.[idx]?.nextReview) || 0;
        if (hasSpacedHistory && nextReview > 0 && nextReview <= now) dueNowCount += 1;

        const hasAnyAttempt = attempts.length > 0 || userAnswers[idx] !== undefined || hasSpacedHistory;
        if (!hasAnyAttempt) unseenCount += 1;
    }

    const hasSpacedReviewHistory = spacedHistoryCount > 0;
    const averageAttemptMs = getCurrentSessionAverageAttemptMs();
    const catchUpMinutes = hasSpacedReviewHistory
        ? estimateStudyCatchUpMinutes(dueNowCount, againHardCount, averageAttemptMs)
        : 0;

    return {
        ...presence,
        savedCount,
        wrongCount,
        unseenCount,
        dueNowCount,
        againHardCount,
        spacedHistoryCount,
        hasSpacedReviewHistory,
        averageAttemptMs,
        catchUpMinutes,
        sessionTitle: getCurrentSessionDisplayLabel()
    };
}

function getStudyActionLabel(actionKey) {
    return STUDY_ACTION_LABELS[actionKey] || 'Continue';
}

function getDashboardRecommendedActionFromMetrics(metrics) {
    if (!metrics?.hasQuestionContent) return '';
    if (metrics.againHardCount > 0) return STUDY_ACTION_KEYS.quizReviewCards;
    if (metrics.dueNowCount > 0) return STUDY_ACTION_KEYS.quizSpacedReview;
    if (metrics.unseenCount > 0) return STUDY_ACTION_KEYS.quiz;
    return STUDY_ACTION_KEYS.flashcards;
}

function buildFallbackDashboardRecommendation(metrics) {
    const nextActionKey = getDashboardRecommendedActionFromMetrics(metrics);
    const starterCount = metrics?.hasQuestionContent
        ? Math.min(metrics.questionCount, Math.max(metrics.againHardCount || metrics.dueNowCount || 10, 10))
        : 0;
    return {
        summary: metrics?.hasSpacedReviewHistory
            ? 'This session already has review history. Use the next step below or complete Study Setup for a personalized plan.'
            : 'Take Study Setup to generate a personalized plan for this session.',
        todayTarget: starterCount > 0 ? `Start with ${starterCount} items.` : 'Import or generate questions to build a study target.',
        recommendedOrder: metrics?.hasQuestionContent ? ['Weak cards', 'Due cards', 'Coverage'] : [],
        suggestedModeKey: nextActionKey,
        suggestedModeLabel: nextActionKey ? getStudyActionLabel(nextActionKey) : '',
        nextActionKey,
        nextActionLabel: nextActionKey ? getStudyActionLabel(nextActionKey) : '',
        stretchGoal: metrics?.unseenCount > 0 ? `If you still have time, add ${Math.min(metrics.unseenCount, 10)} unseen cards.` : '',
        timePressureNote: ''
    };
}

function getStudyRecommendationBucketOrder(rawAnswers) {
    const answers = normalizeStudySetupAnswers(rawAnswers);
    const examSoon = answers.examTiming === 'within_1_week' || answers.examTiming === 'within_2_weeks';
    const lowPreparedness = answers.preparedness === 'not_ready';
    const priority = answers.priority;
    const goal = answers.goal;

    let orderBuckets = ['coverage', 'due', 'again_hard', 'saved'];
    if (priority === 'due_cards') {
        orderBuckets = ['due', 'again_hard', 'saved', 'coverage'];
    } else if (priority === 'weak_cards') {
        orderBuckets = ['again_hard', 'due', 'saved', 'coverage'];
    } else if (priority === 'coverage') {
        orderBuckets = ['coverage', 'due', 'again_hard', 'saved'];
    }

    if (examSoon && lowPreparedness) {
        orderBuckets = ['again_hard', 'due', 'saved', 'coverage'];
    }

    if (goal === 'retention') {
        orderBuckets = orderBuckets.filter((bucket) => bucket !== 'due');
        orderBuckets.unshift('due');
    }

    if (goal === 'cram' && priority !== 'due_cards') {
        orderBuckets = orderBuckets.filter((bucket) => bucket !== 'saved');
        const coverageIndex = orderBuckets.indexOf('coverage');
        if (coverageIndex >= 0) {
            orderBuckets.splice(coverageIndex, 0, 'saved');
        } else {
            orderBuckets.push('saved');
        }
    }

    return orderBuckets;
}

function shouldOfferUrgentFocusSet(rawAnswers = studySetupState.answers, metrics = buildCurrentSessionStudyMetrics()) {
    const answers = normalizeStudySetupAnswers(rawAnswers);
    if (!metrics?.hasQuestionContent || !isStudySetupComplete(answers)) return false;

    const timeBudgetMinutes = getStudySetupTimeBudgetMinutes(answers.timeToday);
    const examTiming = answers.examTiming;
    const cramGoal = answers.goal === 'cram';

    if (examTiming === 'within_1_week' && timeBudgetMinutes <= 45) return true;
    if (examTiming === 'within_1_week' && cramGoal) return true;
    if (examTiming === 'within_2_weeks' && cramGoal && timeBudgetMinutes <= 30) return true;
    return false;
}

function getUrgentFocusTargetCount(rawAnswers = studySetupState.answers, questionCount = questions.length) {
    const answers = normalizeStudySetupAnswers(rawAnswers);
    const timeBudgetMinutes = getStudySetupTimeBudgetMinutes(answers.timeToday);
    const baseTarget = timeBudgetMinutes <= 15
        ? 12
        : timeBudgetMinutes <= 30
            ? 20
            : timeBudgetMinutes <= 45
                ? 30
                : 40;
    return Math.min(Math.max(0, Number(questionCount) || 0), baseTarget);
}

function buildUrgentFocusCondensedState(rawAnswers = studySetupState.answers) {
    const answers = normalizeStudySetupAnswers(rawAnswers);
    const metrics = buildCurrentSessionStudyMetrics();
    if (!shouldOfferUrgentFocusSet(answers, metrics)) return null;

    const targetCount = getUrgentFocusTargetCount(answers, metrics.questionCount);
    if (targetCount <= 0) return null;

    const bucketOrder = getStudyRecommendationBucketOrder(answers);
    const now = Date.now();
    const bucketIndices = {
        again_hard: [],
        due: [],
        saved: [],
        coverage: [],
        remaining: []
    };

    for (let idx = 0; idx < questions.length; idx++) {
        const confidence = getSessionOnlyConfidenceForIndex(idx);
        const knowledge = normalizeKnowledgeStateValue(knowledgeState[idx]);
        const isAgainHard = confidence === 'again' || confidence === 'shaky' || isKnowledgeReviewFocusState(knowledge);
        const hasSpacedHistory = hasSessionSpacedReviewHistoryForIndex(idx);
        const nextReview = Number(srsData?.[idx]?.nextReview) || 0;
        const isDue = hasSpacedHistory && nextReview > 0 && nextReview <= now;
        const isSaved = Number(savedQuestions?.[idx]) > 0;
        const attempts = Array.isArray(statsData?.[idx]) ? statsData[idx] : [];
        const hasAnyAttempt = attempts.length > 0 || userAnswers[idx] !== undefined || hasSpacedHistory;
        const isCoverage = !hasAnyAttempt;

        if (isAgainHard) {
            bucketIndices.again_hard.push(idx);
        } else if (isDue) {
            bucketIndices.due.push(idx);
        } else if (isSaved) {
            bucketIndices.saved.push(idx);
        } else if (isCoverage) {
            bucketIndices.coverage.push(idx);
        } else {
            bucketIndices.remaining.push(idx);
        }
    }

    const selectedIndices = [];
    const seen = new Set();
    const addIndices = (indices = []) => {
        for (const rawIdx of indices) {
            const idx = Number(rawIdx);
            if (!Number.isInteger(idx) || idx < 0 || idx >= questions.length || seen.has(idx)) continue;
            seen.add(idx);
            selectedIndices.push(idx);
            if (selectedIndices.length >= targetCount) return true;
        }
        return false;
    };

    for (const bucket of bucketOrder) {
        if (addIndices(bucketIndices[bucket])) break;
    }
    if (selectedIndices.length < targetCount) addIndices(bucketIndices.remaining);

    const questionKeys = selectedIndices
        .map((idx) => buildQuestionPersistenceKeyByIndex(idx))
        .filter(Boolean);
    if (questionKeys.length === 0) return null;

    return createDefaultCondensedQuizState({
        questionKeys,
        selectedSetKeys: Array.from(activeQuestionSetKeys || []).map((key) => String(key || '').trim()).filter(Boolean),
        targetCount: questionKeys.length,
        sourceQuestionCount: metrics.questionCount,
        promptRequest: 'Urgent focus subset generated from Study Setup.',
        enabled: true,
        updatedAt: Date.now()
    });
}

function buildStudyPlanRecommendation(metrics, rawAnswers) {
    const answers = normalizeStudySetupAnswers(rawAnswers);
    if (!isStudySetupComplete(answers) || !metrics?.hasQuestionContent) return null;

    const timeBudgetMinutes = getStudySetupTimeBudgetMinutes(answers.timeToday);
    const examSoon = answers.examTiming === 'within_1_week' || answers.examTiming === 'within_2_weeks';
    const examImmediate = answers.examTiming === 'within_1_week';
    const lowPreparedness = answers.preparedness === 'not_ready';
    const priority = answers.priority;
    const goal = answers.goal;
    const cramGoal = goal === 'cram';
    const retentionGoal = goal === 'retention';
    const orderBuckets = getStudyRecommendationBucketOrder(answers);

    const bucketLabels = {
        again_hard: 'Again / Hard cards',
        due: 'Due cards',
        saved: 'Saved questions',
        coverage: 'Coverage / unseen questions'
    };

    const nextActionByBucket = {
        again_hard: priority === 'weak_cards' ? STUDY_ACTION_KEYS.quizReviewCards : STUDY_ACTION_KEYS.quizSpacedReview,
        due: STUDY_ACTION_KEYS.quizSpacedReview,
        saved: STUDY_ACTION_KEYS.quiz,
        coverage: timeBudgetMinutes <= 30 ? STUDY_ACTION_KEYS.flashcards : STUDY_ACTION_KEYS.quiz
    };

    const urgentFocusState = shouldOfferUrgentFocusSet(answers, metrics)
        ? buildUrgentFocusCondensedState(answers)
        : null;

    let nextActionKey = '';
    for (const bucket of orderBuckets) {
        if (bucket === 'again_hard' && metrics.againHardCount > 0) {
            nextActionKey = nextActionByBucket[bucket];
            break;
        }
        if (bucket === 'due' && metrics.dueNowCount > 0) {
            nextActionKey = nextActionByBucket[bucket];
            break;
        }
        if (bucket === 'saved' && metrics.savedCount > 0) {
            nextActionKey = nextActionByBucket[bucket];
            break;
        }
        if (bucket === 'coverage' && metrics.questionCount > 0) {
            nextActionKey = nextActionByBucket[bucket];
            break;
        }
    }
    if (!nextActionKey) nextActionKey = getDashboardRecommendedActionFromMetrics(metrics);
    if (urgentFocusState && urgentFocusState.questionKeys.length > 0 && urgentFocusState.questionKeys.length < metrics.questionCount) {
        nextActionKey = STUDY_ACTION_KEYS.urgentFocus;
    }

    const targetUnits = Math.max(
        5,
        Math.min(
            metrics.questionCount,
            Math.round(timeBudgetMinutes / (cramGoal ? 0.7 : (retentionGoal ? 1.0 : 0.85)))
        )
    );

    let todayTarget = `Work through ${targetUnits} questions or cards today.`;
    if (nextActionKey === STUDY_ACTION_KEYS.urgentFocus) {
        todayTarget = `Run the urgent focus subset first: ${urgentFocusState?.questionKeys?.length || getUrgentFocusTargetCount(answers, metrics.questionCount)} high-yield questions.`;
    } else if (nextActionKey === STUDY_ACTION_KEYS.quizReviewCards) {
        todayTarget = `Hit ${Math.min(Math.max(metrics.againHardCount, 5), targetUnits)} weak cards first.`;
    } else if (nextActionKey === STUDY_ACTION_KEYS.quizSpacedReview) {
        todayTarget = `Clear ${Math.min(Math.max(metrics.dueNowCount, 5), targetUnits)} due cards first.`;
    } else if (nextActionKey === STUDY_ACTION_KEYS.flashcards) {
        todayTarget = `Scan ${Math.min(metrics.questionCount, targetUnits)} cards for fast coverage today.`;
    }

    let summary = 'Balanced review is the best fit for this session.';
    if (examSoon && lowPreparedness) {
        summary = 'The exam is close and readiness is low, so Memoraful should focus on weak cards before broad coverage.';
    } else if (priority === 'due_cards') {
        summary = 'Due cards should come first so you clear the review queue before adding more coverage.';
    } else if (priority === 'weak_cards') {
        summary = 'Weak cards should come first so the session attacks the highest-friction material immediately.';
    } else if (priority === 'coverage') {
        summary = 'Coverage should come first so you touch more of the session before narrowing down.';
    } else if (retentionGoal) {
        summary = 'Retention is the goal, so due review should stay ahead of unseen material.';
    } else if (cramGoal) {
        summary = 'This plan leans toward cramming: weak and saved cards come before broad coverage.';
    }

    let stretchGoal = '';
    if (timeBudgetMinutes >= 45 && metrics.unseenCount > 0) {
        stretchGoal = `If you finish early, add ${Math.min(metrics.unseenCount, Math.max(8, Math.round(targetUnits * 0.4)))} unseen questions for coverage.`;
    } else if (metrics.savedCount > 0 && cramGoal) {
        stretchGoal = `If you have extra time, sweep ${Math.min(metrics.savedCount, 10)} saved questions after the core plan.`;
    }

    const timePressureNote = (examImmediate && timeBudgetMinutes <= 30 && metrics.questionCount > 60)
        ? 'Time pressure is high. Keep the session narrow and consider condensed high-yield review before broad coverage.'
        : '';

    return {
        summary,
        todayTarget,
        recommendedOrder: orderBuckets.map((bucket) => bucketLabels[bucket]).filter(Boolean),
        suggestedModeKey: nextActionKey,
        suggestedModeLabel: getStudyActionLabel(nextActionKey),
        nextActionKey,
        nextActionLabel: getStudyActionLabel(nextActionKey),
        stretchGoal,
        timePressureNote,
        answersSummary: {
            examTiming: getStudySetupAnswerLabel('examTiming', answers.examTiming),
            preparedness: getStudySetupAnswerLabel('preparedness', answers.preparedness),
            timeToday: getStudySetupAnswerLabel('timeToday', answers.timeToday),
            priority: getStudySetupAnswerLabel('priority', answers.priority),
            goal: getStudySetupAnswerLabel('goal', answers.goal)
        }
    };
}

function generateStudyPlanFromCurrentSession(rawAnswers = studySetupState.answers) {
    const answers = normalizeStudySetupAnswers(rawAnswers);
    const metrics = buildCurrentSessionStudyMetrics();
    const recommendation = buildStudyPlanRecommendation(metrics, answers);
    const now = Date.now();
    studyPlanState = createDefaultStudyPlanState({
        generatedAt: now,
        recommendation,
        metricsSnapshot: {
            questionCount: metrics.questionCount,
            pdfCount: metrics.pdfCount,
            savedCount: metrics.savedCount,
            wrongCount: metrics.wrongCount,
            dueNowCount: metrics.dueNowCount,
            againHardCount: metrics.againHardCount,
            unseenCount: metrics.unseenCount,
            catchUpMinutes: metrics.catchUpMinutes,
            hasSpacedReviewHistory: metrics.hasSpacedReviewHistory,
            generatedForSessionId: normalizeSessionId(currentSessionId)
        },
        updatedAt: now
    });
    return studyPlanState;
}

function persistStudyPlannerSessionMutation() {
    if (IS_EMBED_DEMO || !normalizeSessionId(currentSessionId)) return;
    markSessionStateMutation();
    saveSessionDebounced();
}

function isStudyDashboardVisible() {
    return !!(els.studyDashboardSurface && els.studyDashboardSurface.style.display !== 'none');
}

function isStudySetupVisible() {
    return !!(els.studySetupSurface && els.studySetupSurface.style.display !== 'none');
}

function captureStudyPlanExecutionSnapshot() {
    const currentRealIdx = Number.isInteger(filteredIndices?.[currentQIndex]) ? filteredIndices[currentQIndex] : -1;
    const surface = isStudyDashboardVisible()
        ? 'dashboard'
        : (isStudySetupVisible() ? 'setup' : (isFlashcardMode ? 'flashcards' : 'quiz'));
    return createDefaultStudyPlanViewSnapshot({
        sessionId: currentSessionId,
        surface,
        filteredQuestionKeys: buildFilteredQuestionKeysForPersistence(filteredIndices),
        currentQuestionKey: currentRealIdx >= 0 ? buildQuestionPersistenceKeyByIndex(currentRealIdx) : '',
        currentQuestionViewIndex: Number.isInteger(currentQIndex) ? currentQIndex : 0,
        isFilterActive,
        isFlashcardMode,
        isAdaptiveReviewMode,
        isFlashcardAdaptiveReviewMode,
        fcPage,
        condensedQuizState,
        highYieldQuizState,
        adaptivePracticeState,
        capturedAt: Date.now()
    });
}

function getStudyPlanSnapshotCurrentIndex(snapshot, nextFilteredIndices) {
    const queue = Array.isArray(nextFilteredIndices) ? nextFilteredIndices : [];
    if (queue.length === 0) return 0;

    const currentQuestionKey = String(snapshot?.currentQuestionKey || '').trim();
    if (currentQuestionKey) {
        const nextIndex = queue.findIndex((realIdx) => buildQuestionPersistenceKeyByIndex(realIdx) === currentQuestionKey);
        if (nextIndex >= 0) return nextIndex;
    }

    return Math.min(
        Math.max(Number(snapshot?.currentQuestionViewIndex) || 0, 0),
        Math.max(queue.length - 1, 0)
    );
}

function restoreStudyPlanExecutionSnapshot(snapshotRaw, options = {}) {
    const snapshot = createDefaultStudyPlanViewSnapshot(snapshotRaw);
    if (!snapshot.sessionId || snapshot.sessionId !== normalizeSessionId(currentSessionId)) return false;

    const restoredFilteredIndices = buildFilteredIndicesFromQuestionKeyList(snapshot.filteredQuestionKeys);
    const fallbackIndices = getDefaultQuestionPoolIndices();
    const nextFilteredIndices = restoredFilteredIndices.length > 0 ? restoredFilteredIndices : fallbackIndices;
    const nextCurrentQIndex = getStudyPlanSnapshotCurrentIndex(snapshot, nextFilteredIndices);

    pendingAdaptiveRating = null;
    adaptiveAnswerRevealedAt = 0;
    closeAdaptiveRatingModal();
    closeAdaptiveSnapshotModal();
    restoreAdaptiveClearedAnswers();
    adaptiveClearedAnswersBackup = {};
    adaptiveResumeAnchorIdx = null;

    isAdaptiveReviewMode = false;
    isFlashcardAdaptiveReviewMode = false;
    condensedQuizState = normalizeCondensedQuizState(snapshot.condensedQuizState);
    highYieldQuizState = normalizeHighYieldQuizState(snapshot.highYieldQuizState);
    adaptivePracticeState = normalizeAdaptivePracticeState(snapshot.adaptivePracticeState);
    highYieldLastAppliedPool = null;
    syncCondensedQuizToggleButton();

    if (snapshot.isAdaptiveReviewMode) {
        isAdaptiveReviewMode = true;
        filteredIndices = nextFilteredIndices;
        isFilterActive = true;
        currentQIndex = nextCurrentQIndex;
        adaptiveResumeAnchorIdx = Number.isInteger(filteredIndices[currentQIndex]) ? filteredIndices[currentQIndex] : null;
        stashAndClearAdaptiveAnswers(filteredIndices);
        isFlashcardMode = false;
        setModeToggleButtonLabel(false);
        syncAdaptiveModeUI();
        showQuiz();
        if (snapshot.surface === 'dashboard') showStudyDashboard({ persist: false, recordOpen: false, keepBackground: false });
    } else if (snapshot.isFlashcardAdaptiveReviewMode) {
        isFlashcardAdaptiveReviewMode = true;
        filteredIndices = nextFilteredIndices;
        isFilterActive = true;
        currentQIndex = nextCurrentQIndex;
        isFlashcardMode = true;
        fcPage = Math.min(Math.max(snapshot.fcPage, 0), Math.max(Math.ceil(filteredIndices.length / fcLimit) - 1, 0));
        setModeToggleButtonLabel(true);
        syncAdaptiveModeUI();
        showFlashcards();
        if (snapshot.surface === 'dashboard') showStudyDashboard({ persist: false, recordOpen: false, keepBackground: false });
    } else {
        filteredIndices = nextFilteredIndices;
        isFilterActive = !!snapshot.isFilterActive;
        currentQIndex = nextCurrentQIndex;
        isFlashcardMode = !!snapshot.isFlashcardMode;
        fcPage = Math.min(Math.max(snapshot.fcPage, 0), Math.max(Math.ceil(filteredIndices.length / fcLimit) - 1, 0));
        setModeToggleButtonLabel(isFlashcardMode);
        syncAdaptiveModeUI();
        if (snapshot.surface === 'dashboard') {
            if (isFlashcardMode) showFlashcards();
            else showQuiz();
            showStudyDashboard({ persist: false, recordOpen: false, keepBackground: false });
        } else if (snapshot.surface === 'setup') {
            showStudySetup();
        } else if (isFlashcardMode) {
            showFlashcards();
        } else {
            showQuiz();
        }
    }

    if (options?.persist !== false) {
        markSessionStateMutation();
        saveSessionDebounced();
    }
    return true;
}

function clearAppliedStudyPlanState({ markUndone = false } = {}) {
    const now = Date.now();
    studyPlanState = createDefaultStudyPlanState({
        ...studyPlanState,
        activeActionKey: '',
        activeActionLabel: '',
        appliedAt: 0,
        lastUndoneAt: markUndone ? now : studyPlanState.lastUndoneAt,
        preApplySnapshot: null,
        updatedAt: now
    });
}

function applyGeneratedStudyPlan() {
    const recommendation = studyPlanState?.recommendation;
    const actionKey = typeof recommendation?.nextActionKey === 'string' ? recommendation.nextActionKey.trim() : '';
    if (!actionKey) {
        showPopup('No generated plan is ready to apply for this session yet.', null, true);
        return false;
    }

    const snapshot = captureStudyPlanExecutionSnapshot();
    const launched = launchStudyAction(actionKey, { showNotice: true });
    if (!launched) return false;

    const now = Date.now();
    studyPlanState = createDefaultStudyPlanState({
        ...studyPlanState,
        activeActionKey: actionKey,
        activeActionLabel: getStudyActionLabel(actionKey),
        appliedAt: now,
        preApplySnapshot: snapshot,
        updatedAt: now
    });
    persistStudyPlannerSessionMutation();
    if (isStudyDashboardVisible()) renderStudyDashboard();
    return true;
}

function undoAppliedStudyPlan() {
    const snapshot = studyPlanState?.preApplySnapshot;
    if (!snapshot) {
        showPopup('There is no applied study plan to undo.', null, true);
        return false;
    }

    const restored = restoreStudyPlanExecutionSnapshot(snapshot, { persist: true });
    if (!restored) {
        showPopup('Memoraful could not restore the previous study view for this session.', null, true);
        return false;
    }

    clearAppliedStudyPlanState({ markUndone: true });
    persistStudyPlannerSessionMutation();
    if (isStudyDashboardVisible()) renderStudyDashboard();
    showPopup('Study plan undone. Your previous session view was restored without removing review progress.', null, false);
    return true;
}

function setStudyDashboardLastMode(actionKey) {
    const normalizedActionKey = typeof actionKey === 'string' ? actionKey.trim() : '';
    if (!normalizedActionKey) return;
    const now = Date.now();
    studyDashboardState = createDefaultStudyDashboardState({
        ...studyDashboardState,
        lastModeKey: normalizedActionKey,
        lastModeLabel: getStudyActionLabel(normalizedActionKey),
        lastLaunchedAt: now,
        updatedAt: now
    });
    persistStudyPlannerSessionMutation();
}

function isStudyActionAvailable(actionKey, metrics = buildCurrentSessionStudyMetrics()) {
    switch (actionKey) {
        case STUDY_ACTION_KEYS.quiz:
        case STUDY_ACTION_KEYS.flashcards:
        case STUDY_ACTION_KEYS.quizSpacedReview:
        case STUDY_ACTION_KEYS.flashcardSpacedReview:
            return !!metrics?.hasQuestionContent;
        case STUDY_ACTION_KEYS.urgentFocus: {
            const urgentFocusState = buildUrgentFocusCondensedState(studySetupState.answers);
            return !!metrics?.hasQuestionContent
                && !!urgentFocusState
                && Array.isArray(urgentFocusState.questionKeys)
                && urgentFocusState.questionKeys.length > 0
                && urgentFocusState.questionKeys.length < metrics.questionCount;
        }
        case STUDY_ACTION_KEYS.quizReviewCards:
            return !!metrics?.hasQuestionContent && (metrics.againHardCount > 0 || metrics.hasSpacedReviewHistory);
        case STUDY_ACTION_KEYS.history:
        case STUDY_ACTION_KEYS.dashboard:
        case STUDY_ACTION_KEYS.setup:
            return !!metrics?.hasSessionContent;
        default:
            return false;
    }
}

function populateStudySetupForm() {
    const answers = normalizeStudySetupAnswers(studySetupState.answers);
    if (els.studySetupExamTiming) els.studySetupExamTiming.value = answers.examTiming;
    if (els.studySetupPreparedness) els.studySetupPreparedness.value = answers.preparedness;
    if (els.studySetupTimeToday) els.studySetupTimeToday.value = answers.timeToday;
    if (els.studySetupPriority) els.studySetupPriority.value = answers.priority;
    if (els.studySetupGoal) els.studySetupGoal.value = answers.goal;
}

function collectStudySetupFormAnswers() {
    return normalizeStudySetupAnswers({
        examTiming: els.studySetupExamTiming?.value,
        preparedness: els.studySetupPreparedness?.value,
        timeToday: els.studySetupTimeToday?.value,
        priority: els.studySetupPriority?.value,
        goal: els.studySetupGoal?.value
    });
}

function hideStudyPlannerSurfaces() {
    if (els.studyDashboardSurface) els.studyDashboardSurface.style.display = 'none';
    if (els.studySetupSurface) els.studySetupSurface.style.display = 'none';
    document.body.classList.remove('study-dashboard-open');
}

function clearQuestionAutoToolTimers() {
    questionRenderToken++;
    clearAutoStudyTimers();
}

function renderStudyDashboard() {
    const shell = els.studyDashboardShell;
    if (!shell) return;

    const metrics = buildCurrentSessionStudyMetrics();
    const hasPlan = !!studyPlanState?.recommendation;
    const recommendation = hasPlan ? studyPlanState.recommendation : buildFallbackDashboardRecommendation(metrics);
    const hasAppliedPlan = !!studyPlanState?.appliedAt && !!studyPlanState?.preApplySnapshot && !!studyPlanState?.activeActionKey;
    const questionsLeftToCover = Math.max(0, metrics.unseenCount);
    const updatedLabel = hasPlan && studyPlanState.generatedAt
        ? `Updated ${new Date(studyPlanState.generatedAt).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}`
        : 'Setup not finished';
    const planStatusLabel = hasAppliedPlan
        ? `Applied: ${studyPlanState.activeActionLabel || getStudyActionLabel(studyPlanState.activeActionKey)}`
        : (hasPlan ? 'Not applied yet' : 'Waiting for setup');
    const nextActionKey = typeof recommendation?.nextActionKey === 'string' ? recommendation.nextActionKey.trim() : '';
    const canApplyPlan = hasPlan && !!nextActionKey && isStudyActionAvailable(nextActionKey, metrics);
    const summaryText = hasAppliedPlan
        ? `${studyPlanState.activeActionLabel || getStudyActionLabel(studyPlanState.activeActionKey)} is active. Undo restores the previous view without removing review progress.`
        : (hasPlan
            ? (recommendation.summary || 'A session plan is ready to apply.')
            : 'Use Study Setup to develop a quick plan for this session.');
    const targetText = hasPlan
        ? (recommendation.todayTarget || 'Take Study Setup to generate a target.')
        : 'No study plan has been generated yet.';
    const hideDashboardAutoOpen = getStudyDashboardAutoOpenDisabled();
    const compactStatsMarkup = [
        ['Questions', metrics.questionCount],
        ['Left', questionsLeftToCover],
        [metrics.hasSpacedReviewHistory ? 'Due now' : 'Review', metrics.hasSpacedReviewHistory ? metrics.dueNowCount : 'Not started'],
        ['Streak', currentStreak]
    ].map(([label, value]) => `
        <div class="study-dashboard-mini-stat">
            <span class="study-dashboard-mini-label">${escapeHtml(String(label))}</span>
            <strong class="study-dashboard-mini-value">${escapeHtml(String(value))}</strong>
        </div>
    `).join('');

    shell.innerHTML = `
        <div class="study-dashboard-popup-card compact-overlay" role="dialog" aria-modal="true" aria-labelledby="study-dashboard-title">
            <button type="button" class="theme-close-btn study-dashboard-dismiss-btn" data-study-plan-action="dismiss" aria-label="Close study dashboard">&times;</button>
            <div class="study-dashboard-overlay-layout">
                <div class="study-dashboard-overlay-main">
                    <div class="study-dashboard-plan-head compact overlay-inline">
                        <div>
                            <h2 id="study-dashboard-title" class="study-dashboard-popup-title">Dashboard</h2>
                            <div class="study-dashboard-plan-kicker">${hasPlan ? 'Study Plan' : 'Study Setup'}</div>
                        </div>
                        <span class="study-dashboard-pill">${escapeHtml(updatedLabel)}</span>
                    </div>

                    <div class="study-dashboard-overlay-panel">
                        <div class="study-plan-summary compact">${escapeHtml(summaryText)}</div>
                        <div class="study-dashboard-overlay-target">
                            <span class="study-dashboard-plan-row-label">Today</span>
                            <strong class="study-dashboard-overlay-target-value">${escapeHtml(targetText)}</strong>
                        </div>
                        ${recommendation.timePressureNote ? `<div class="study-dashboard-tip compact">${escapeHtml(recommendation.timePressureNote)}</div>` : ''}

                        <div class="study-dashboard-actions study-dashboard-actions-primary compact">
                            <button type="button" class="welcome-btn" data-study-action="${STUDY_ACTION_KEYS.setup}">
                                <span class="gradient-text">${escapeHtml(studySetupState.completedAt ? 'Edit Study Setup' : 'Develop Study Plan')}</span>
                            </button>
                            ${canApplyPlan ? `
                                <button type="button" class="welcome-btn primary" data-study-plan-action="apply">Apply Plan</button>
                            ` : ''}
                            ${hasAppliedPlan ? `
                                <button type="button" class="welcome-btn" data-study-plan-action="undo">
                                    <span class="gradient-text">Undo Plan</span>
                                </button>
                            ` : ''}
                            <button type="button" class="welcome-btn" data-study-plan-action="dismiss">
                                <span class="gradient-text">Continue</span>
                            </button>
                        </div>

                        <label class="study-dashboard-pref-toggle">
                            <input type="checkbox" data-study-dashboard-auto-open-toggle ${hideDashboardAutoOpen ? 'checked' : ''}>
                            <span>Don't show again</span>
                        </label>
                    </div>
                </div>
                <aside class="study-dashboard-overlay-side">
                    <div class="study-dashboard-mini-grid">${compactStatsMarkup}</div>
                    <div class="study-dashboard-side-status">${escapeHtml(planStatusLabel)}</div>
                </aside>
            </div>
        </div>
    `;
    syncStudyDashboardAutoOpenUI();
}

function showStudyDashboard(options = {}) {
    if (!shouldOfferStudyDashboard()) return false;

    document.body.classList.remove('welcome-mode');
    if (els.welcome) els.welcome.style.display = 'none';
    if (options.keepBackground !== false) {
        const flashcardsVisible = !!(els.fcContainer && els.fcContainer.style.display !== 'none');
        const quizVisible = !!(els.quizContainer && els.quizContainer.style.display !== 'none');
        const hasRenderableQuestions = Array.isArray(questions) && questions.length > 0
            && Array.isArray(filteredIndices) && filteredIndices.length > 0;
        if (!flashcardsVisible && !quizVisible && hasRenderableQuestions) {
            if (isFlashcardMode) showFlashcards();
            else showQuiz();
        }
    }
    if (els.studySetupSurface) els.studySetupSurface.style.display = 'none';
    if (els.studyDashboardSurface) els.studyDashboardSurface.style.display = 'flex';
    document.body.classList.add('study-dashboard-open');

    renderStudyDashboard();

    if (options.recordOpen !== false) {
        const now = Date.now();
        studyDashboardState = createDefaultStudyDashboardState({
            ...studyDashboardState,
            hasSeenDashboard: true,
            lastOpenedAt: now,
            updatedAt: now
        });
        if (options.persist !== false) persistStudyPlannerSessionMutation();
    }

    updateSourceDropdown();
    return true;
}

function showStudySetup() {
    if (!shouldOfferStudyDashboard()) {
        showPopup('Load or create a session with content first.', null, true);
        return false;
    }

    document.body.classList.remove('welcome-mode');
    document.body.classList.remove('study-dashboard-open');
    if (els.welcome) els.welcome.style.display = 'none';
    if (els.quizContainer) els.quizContainer.style.display = 'none';
    if (els.fcContainer) els.fcContainer.style.display = 'none';
    if (els.studyDashboardSurface) els.studyDashboardSurface.style.display = 'none';
    if (els.studySetupSurface) els.studySetupSurface.style.display = 'block';
    populateStudySetupForm();
    return true;
}

function openStudyDashboardFromTools() {
    closeMenuDropdown();
    const opened = showStudyDashboard();
    if (!opened) showPopup('Load or create a session with content first.', null, true);
    return opened;
}

function openStudySetupFromTools() {
    closeMenuDropdown();
    return showStudySetup();
}

function isDedicatedLearnFlashcardPool() {
    return Array.isArray(questions)
        && questions.length > 0
        && questions.every((question) => question?.flashcardOnly === true);
}

function launchQuizReviewCards({ showNotice = false } = {}) {
    if (!questions.length) {
        showPopup('Load questions first to review flashcards.', null, true);
        return false;
    }
    if (isDedicatedLearnFlashcardPool()) {
        showPopup('Quiz Review Cards are available for quiz-derived flashcards only.', null, false);
        return false;
    }

    const toStudy = [];
    questions.forEach((_, idx) => {
        const confidence = getSessionOnlyConfidenceForIndex(idx);
        const knowledge = normalizeKnowledgeStateValue(knowledgeState[idx]);
        if (
            confidence === 'again'
            || confidence === 'shaky'
            || isKnowledgeReviewFocusState(knowledge)
        ) {
            toStudy.push(idx);
        }
    });
    if (!toStudy.length) {
        showPopup('No Again/Hard flashcards to review right now.', null, false);
        return false;
    }

    if (isAdaptiveReviewMode) deactivateAdaptiveReviewMode(false);
    if (isFlashcardAdaptiveReviewMode) deactivateFlashcardAdaptiveReviewMode(false);

    filteredIndices = toStudy;
    isFilterActive = true;
    if (isShuffled) applySmartShuffle({ showNotice: false });
    currentQIndex = 0;
    isFlashcardMode = false;
    setModeToggleButtonLabel(false);
    syncAdaptiveModeUI();
    if (els.filterBanner) els.filterBanner.style.display = 'flex';
    const bannerText = getFilterBannerTextEl();
    if (bannerText) {
        bannerText.innerHTML = `<b>Again/Hard Review:</b> ${filteredIndices.length} flashcards in the focused quiz.`;
    }
    if (els.exitFilter) els.exitFilter.textContent = 'Exit Study Mode';
    setAdaptiveSnapshotButtonVisible(false);
    setAdaptiveSkipUndoneButtonVisible(false);
    showQuiz();
    setStudyDashboardLastMode(STUDY_ACTION_KEYS.quizReviewCards);
    if (showNotice) {
        showPopup(`Quiz Review Cards started: ${filteredIndices.length} Again/Hard card${filteredIndices.length === 1 ? '' : 's'}.`, null, false);
    }
    return true;
}

function launchBaseQuizMode(showNotice = false) {
    if (!questions.length) {
        showPopup('Load questions first to start Quiz mode.', null, true);
        return false;
    }
    if (isAdaptiveReviewMode) deactivateAdaptiveReviewMode(false);
    if (isFlashcardAdaptiveReviewMode) deactivateFlashcardAdaptiveReviewMode(false);
    resetFilter();
    if (!isShuffled) currentQIndex = 0;
    isFlashcardMode = false;
    setModeToggleButtonLabel(false);
    showQuiz();
    setStudyDashboardLastMode(STUDY_ACTION_KEYS.quiz);
    if (showNotice) showPopup('Quiz mode ready.', null, false);
    return true;
}

function launchBaseFlashcardMode(showNotice = false) {
    if (!questions.length) {
        showPopup('Load questions first to start Flashcards.', null, true);
        return false;
    }
    // "Open Flashcards" is a straight pass through the deck. Ratings still schedule each card;
    // the due-only queue is "Start Spaced Review" (activateFlashcardAdaptiveReviewMode).
    if (isAdaptiveReviewMode) deactivateAdaptiveReviewMode(false);
    isFlashcardAdaptiveReviewMode = false;
    flashcardReviewScope = 'all';
    isFlashcardMode = true;
    setModeToggleButtonLabel(true);
    syncAdaptiveModeUI();
    // Resume continues this deck's saved pass; Start over (the pass summary) begins a new one.
    if (!restoreFlashcardStraightPass()) startFlashcardStraightPass(questions.map((_, idx) => idx), { render: false });
    showFlashcards();
    setStudyDashboardLastMode(STUDY_ACTION_KEYS.flashcards);
    if (showNotice) showPopup(`Flashcards: ${filteredIndices.length} card${filteredIndices.length === 1 ? '' : 's'} in this pass.`, null, false);
    markSessionStateMutation();
    saveSessionDebounced();
    return true;
}

function startFlashcardStraightPass(indices = [], { render = true, subset = false } = {}) {
    filteredIndices = indices.slice();
    flashcardPassSubsetKeys = subset ? filteredIndices.map(idx => buildQuestionPersistenceKeyByIndex(idx)).filter(Boolean) : null;
    resetQuizCompletionCohort('flashcard-straight-pass');
    isFilterActive = true;
    currentQIndex = 0;
    fcPage = 0;
    flashcardPassComplete = false;
    flashcardPassRatings = new Map();
    if (typeof flashcardRevealedKeys !== 'undefined') flashcardRevealedKeys.clear();
    if (typeof flashcardDisplayAnchor !== 'undefined') flashcardDisplayAnchor = '';
    if (typeof flashcardUndoStack !== 'undefined') flashcardUndoStack = [];
    flashcardPassDeckId = getFlashcardPassDeckId();
    if (render) renderFlashcardGrid();
}

// A pass the learner starts (Review missed, Start over) replaces the saved one, so it is
// recorded and saved at once; a refresh before the next rating must resume it.
function beginFlashcardStraightPass(indices = [], options = {}) {
    startFlashcardStraightPass(indices, options);
    recordFlashcardStraightPass();
    markSessionStateMutation();
    saveSessionDebounced();
}

function getFlashcardPassDeckId() {
    return Array.from(activeQuestionSetKeys || []).map(String).sort().join('\u0001');
}

function isFlashcardStraightPassOpen() {
    return isFlashcardMode && !isFlashcardAdaptiveReviewMode && flashcardReviewScope === 'all'
        && !!flashcardPassDeckId && Array.isArray(filteredIndices) && filteredIndices.length > 0;
}

function recordFlashcardStraightPass() {
    if (!isFlashcardStraightPassOpen()) return;
    const deckId = flashcardPassDeckId;
    const keyOf = (idx) => buildQuestionPersistenceKeyByIndex(idx);
    flashcardPassStateByDeck[deckId] = {
        order: filteredIndices.map(keyOf),
        currentKey: keyOf(filteredIndices[currentQIndex]),
        ratings: [...flashcardPassRatings].map(([idx, rating]) => [keyOf(idx), rating]),
        complete: !!flashcardPassComplete,
        ...(flashcardPassSubsetKeys ? { subset: flashcardPassSubsetKeys.slice() } : {}),
        undo: (flashcardUndoStack || []).filter((entry) => entry.type !== 'delete' && Number.isInteger(entry.qIdx)).map((entry) => ({
            key: keyOf(entry.qIdx),
            srs: entry.srs || null,
            knowledge: entry.knowledge ?? null,
            order: (entry.filteredIndices || []).map(keyOf),
            currentQIndex: entry.currentQIndex,
            passComplete: !!entry.passComplete,
            passRating: entry.passRating ?? null,
            tierLevel: entry.tierLevel ?? null
        })),
        updatedAt: Date.now()
    };
}

// Restores the open deck's saved pass. Cards added since (a deck still generating) join the end.
function restoreFlashcardStraightPass() {
    const saved = flashcardPassStateByDeck[getFlashcardPassDeckId()];
    if (!saved || !Array.isArray(saved.order) || !saved.order.length) return false;
    const indexByKey = new Map();
    questions.forEach((q, idx) => {
        const key = buildQuestionPersistenceKeyFromQuestion(q);
        if (key && !indexByKey.has(key)) indexByKey.set(key, idx);
    });
    const toIndices = (keys) => (keys || []).filter((key) => indexByKey.has(key)).map((key) => indexByKey.get(key));
    const order = toIndices(saved.order);
    if (!order.length) return false;
    // A narrowed pass stays narrowed; only a whole-deck pass takes in cards added since.
    const subset = Array.isArray(saved.subset);
    if (!subset) {
        const inOrder = new Set(order);
        questions.forEach((_, idx) => { if (!inOrder.has(idx)) order.push(idx); });
    }
    startFlashcardStraightPass(order, { render: false, subset });
    flashcardPassRatings = new Map((saved.ratings || []).filter(([key]) => indexByKey.has(key)).map(([key, rating]) => [indexByKey.get(key), rating]));
    flashcardPassComplete = !!saved.complete && order.every((idx) => flashcardPassRatings.has(idx));
    const currentPos = order.indexOf(indexByKey.get(saved.currentKey));
    const firstUnrated = order.findIndex((idx) => !flashcardPassRatings.has(idx));
    currentQIndex = currentPos >= 0 ? currentPos : Math.max(firstUnrated, 0);
    flashcardUndoStack = (saved.undo || []).filter((entry) => indexByKey.has(entry.key)).map((entry) => ({
        qIdx: indexByKey.get(entry.key),
        srs: entry.srs || null,
        knowledge: entry.knowledge === null ? undefined : entry.knowledge,
        filteredIndices: toIndices(entry.order),
        currentQIndex: entry.currentQIndex,
        passComplete: !!entry.passComplete,
        passRating: entry.passRating === null ? undefined : entry.passRating,
        tierLevel: entry.tierLevel === null ? undefined : entry.tierLevel
    }));
    flashcardDisplayAnchor = getFlashcardDisplayKey(order[currentQIndex]);
    // The restored undo history belongs to this deck; the display's context-change reset must not clear it.
    flashcardDisplayContext = JSON.stringify([currentSessionId, Array.from(activeQuestionSetKeys || []).sort()]);
    return true;
}

function normalizeFlashcardPassStateByDeck(raw) {
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {};
    const clean = {};
    Object.entries(raw).forEach(([deckId, entry]) => {
        if (deckId && entry && typeof entry === 'object' && Array.isArray(entry.order)) clean[deckId] = entry;
    });
    return clean;
}

function launchUrgentFocusSet(showNotice = false) {
    if (!questions.length) {
        showPopup('Load questions first to build an urgent focus set.', null, true);
        return false;
    }

    const urgentFocusState = buildUrgentFocusCondensedState(studySetupState.answers);
    const urgentCount = Array.isArray(urgentFocusState?.questionKeys) ? urgentFocusState.questionKeys.length : 0;
    if (!urgentFocusState || urgentCount <= 0 || urgentCount >= questions.length) {
        showPopup('Urgent Focus Set is available after Study Setup when time pressure is high.', null, true);
        return false;
    }

    if (isAdaptiveReviewMode) deactivateAdaptiveReviewMode(false);
    if (isFlashcardAdaptiveReviewMode) deactivateFlashcardAdaptiveReviewMode(false);

    isFlashcardMode = false;
    setModeToggleButtonLabel(false);
    setCondensedQuizState(urgentFocusState, {
        enableView: true,
        refreshView: true,
        preserveCurrentQuestion: false,
        save: true,
        silent: true
    });
    showQuiz();
    setStudyDashboardLastMode(STUDY_ACTION_KEYS.urgentFocus);
    if (showNotice) showPopup(`Urgent Focus Set ready: ${urgentCount} high-yield question${urgentCount === 1 ? '' : 's'}.`, null, false);
    return true;
}

function launchStudyAction(actionKey, options = {}) {
    const showNotice = !!options?.showNotice;
    switch (actionKey) {
        case STUDY_ACTION_KEYS.quiz:
            return launchBaseQuizMode(showNotice);
        case STUDY_ACTION_KEYS.flashcards:
            return launchBaseFlashcardMode(showNotice);
        case STUDY_ACTION_KEYS.urgentFocus:
            return launchUrgentFocusSet(showNotice);
        case STUDY_ACTION_KEYS.quizReviewCards:
            return launchQuizReviewCards({ showNotice });
        case STUDY_ACTION_KEYS.quizSpacedReview:
            if (!questions.length) {
                showPopup('Load questions first to use Quiz Spaced Review (Anki-style).', null, true);
                return false;
            }
            if (activateAdaptiveReviewMode(showNotice)) {
                setStudyDashboardLastMode(STUDY_ACTION_KEYS.quizSpacedReview);
                return true;
            }
            return false;
        case STUDY_ACTION_KEYS.flashcardSpacedReview:
            if (!questions.length) {
                showPopup('Load questions first to use Flashcard Spaced Review (Anki-style).', null, true);
                return false;
            }
            if (activateFlashcardAdaptiveReviewMode(showNotice)) {
                setStudyDashboardLastMode(STUDY_ACTION_KEYS.flashcardSpacedReview);
                return true;
            }
            return false;
        case STUDY_ACTION_KEYS.setup:
            return showStudySetup();
        case STUDY_ACTION_KEYS.dashboard:
            return showStudyDashboard();
        case STUDY_ACTION_KEYS.history:
            void SessionManager.showHistory();
            return true;
        case STUDY_ACTION_KEYS.resume:
            if (!studyDashboardState?.lastModeKey) return false;
            return launchStudyAction(studyDashboardState.lastModeKey);
        default:
            return false;
    }
}

function handleStudySetupSubmit(event) {
    event.preventDefault();
    const answers = collectStudySetupFormAnswers();
    if (!isStudySetupComplete(answers)) {
        showPopup('Complete all Study Setup questions before generating the plan.', null, true);
        return;
    }

    const now = Date.now();
    studySetupState = createDefaultStudySetupState({
        answers,
        completedAt: now,
        updatedAt: now
    });
    generateStudyPlanFromCurrentSession(answers);
    persistStudyPlannerSessionMutation();
    showStudyDashboard({ persist: false });
    showPopup('Study plan updated for this session.', null, false);
}

if (els.studyDashboardShell) {
    els.studyDashboardShell.addEventListener('click', (event) => {
        const planActionBtn = event.target.closest('[data-study-plan-action]');
        if (planActionBtn && !planActionBtn.disabled) {
            const planAction = String(planActionBtn.dataset.studyPlanAction || '').trim();
            if (planAction === 'apply') applyGeneratedStudyPlan();
            if (planAction === 'undo') undoAppliedStudyPlan();
            if (planAction === 'dismiss') hideStudyPlannerSurfaces();
            return;
        }
        const actionBtn = event.target.closest('[data-study-action]');
        if (!actionBtn || actionBtn.disabled) return;
        const actionKey = String(actionBtn.dataset.studyAction || '').trim();
        if (actionKey) launchStudyAction(actionKey);
    });
    els.studyDashboardShell.addEventListener('change', (event) => {
        const toggle = event.target.closest('[data-study-dashboard-auto-open-toggle]');
        if (!toggle) return;
        setStudyDashboardAutoOpenDisabled(!!toggle.checked);
    });
}

if (els.studySetupForm) els.studySetupForm.addEventListener('submit', handleStudySetupSubmit);
if (els.studySetupCloseBtn) els.studySetupCloseBtn.onclick = () => { showStudyDashboard(); };
if (els.studySetupBackBtn) els.studySetupBackBtn.onclick = () => { showStudyDashboard(); };
if (els.studyDashboardAutoOpenToggle) {
    els.studyDashboardAutoOpenToggle.addEventListener('change', (event) => {
        setStudyDashboardAutoOpenDisabled(!!event.target.checked);
    });
}
syncStudyDashboardAutoOpenUI();
syncStudyPlannerFeatureVisibility();


/* ============================================================================
   SECTION 2: INITIALIZATION
   ============================================================================ */
// Startup maintenance reads (empty-session cleanup, confidence hydration,
// gamification restore, association snapshot) are not part of first paint and
// none of them gate Welcome. They are deferred to idle time, and re-deferred
// while a session restore is in flight, so a full-store scan can never sit
// ahead of the Continue read in the IndexedDB queue.
const STARTUP_MAINTENANCE_IDLE_TIMEOUT_MS = 2500;
const STARTUP_MAINTENANCE_RETRY_MS = 1500;
let startupMaintenanceScheduled = false;

async function runStartupMaintenance() {
    await Promise.allSettled([
        withMemoraStorageTimeout(() => pruneEmptySavedSessions(), 7000, 'Saved-session cleanup'),
        withMemoraStorageTimeout(() => hydrateAdaptiveConfidenceStoreFromSessionHistory(), 7000, 'Confidence history hydration'),
        withMemoraStorageTimeout(() => loadGamificationData(), 5000, 'Gamification restore'),
        (async () => {
            try {
                const savedAssociations = await readSessionStoreValueWithTimeout('settings', 'manual_pdf_links', 3000);
                if (savedAssociations && typeof savedAssociations === 'object') {
                    // A restored session already merged its own associations in; keep those.
                    pdfAssociations = { ...savedAssociations, ...pdfAssociations };
                    console.debug('Loaded PDF associations from IndexedDB:', Object.keys(pdfAssociations).length, 'total.');
                }
            } catch (error) {
                console.warn('Failed to load PDF associations from IndexedDB:', error);
            }
        })()
    ]);
    updateXPDisplay();
}

function scheduleStartupMaintenance() {
    if (startupMaintenanceScheduled) return;
    startupMaintenanceScheduled = true;
    const start = () => {
        if (isWelcomeSessionLoadPending || continueLastSessionPromise) {
            setTimeout(start, STARTUP_MAINTENANCE_RETRY_MS);
            return;
        }
        void runStartupMaintenance();
    };
    if (typeof window.requestIdleCallback === 'function') {
        window.requestIdleCallback(start, { timeout: STARTUP_MAINTENANCE_IDLE_TIMEOUT_MS });
    } else {
        setTimeout(start, 900);
    }
}

async function init() {
    console.debug("Initializing App...");

    // Theme should restore even if DB open is delayed/blocked.
    setupThemeHandlers();
    loadThemeState();
    syncStudyPlannerFeatureVisibility();
    primeWelcomeButtonsFallback();
    bindWelcomeWarmupPanel();
    try {
        await IDB.open();
        await hydrateStartupSettingsAfterDatabaseOpen();

        if (IS_EMBED_DEMO) {
            applyDemoUiState();
            els.newSessionBtn.onclick = () => startNewSession(true);
            // Use the app's normal new-session reset so the prototype lands in
            // the real empty Solo workspace. The lecture is staged only when
            // the walkthrough clicks Create and opens the generator.
            startNewSession(true, {
                customName: 'Demo Session',
                demoLanding: true,
                isTutorial: true,
                pulseImport: false
            });
        } else {
            if (els.continueBtn) {
                els.continueBtn.onclick = () => { void continueLastSessionFromWelcome(); };
            }
            els.newSessionBtn.style.display = 'inline-flex';
            els.newSessionBtn.onclick = () => { void startNewSessionWithNamePrompt(true); };
            await syncWelcomeScreenState({ progressive: true });
        }

        scheduleStartupMaintenance();

        setupGestures();
        setupPanelReorderDrag();
        $('quiz-pane').addEventListener('mouseenter', () => window.focus());

        updateSourceDropdown();
        setupDrag();
        setupResize(); // Initialize Quiz Card Resize Handle
        restoreQuizCardGeometryForCurrentLayout();
        scheduleQuizCardContentScaleSync();
        setupQuizCardDoubleClickExpand();
        setupQuizExpandLockButton();
        setupSourceResize(); // Initialize Evidence Dropdown Resize Handle

        // Inject hotkeys into iframe whenever it loads
        els.pdfFrame.onload = injectIframeHotkeys;
        loadPdfRendererPreference();
        setPdfPaneMode('pdf');
        setupPdfMiniToolbar();

        // System Audio Sync Listener
        $('sys-audio-sync-btn').onclick = toggleSysSync;

        // Update XP display in stats bar
        loadGameModePreference();
        updateXPDisplay();

        // Game Mode Toggle Button
        $('game-mode-toggle').onclick = toggleGameMode;

        // Smart Saver removed.
        loadAiModelChoicePreference();
        const aiModelChoiceBtn = $('ai-model-choice-toggle');
        if (aiModelChoiceBtn) aiModelChoiceBtn.onclick = toggleAiModelChoice;
        loadMemoraFastModelModePreference();
        const aiFastModelModeBtn = $('ai-fast-model-mode-toggle');
        if (aiFastModelModeBtn) aiFastModelModeBtn.onclick = toggleMemoraFastModelMode;
        loadQuizGenPromptCustomizationPreference();
        const quizGenPromptCustomizationBtn = $('quiz-gen-prompt-customization-toggle');
        if (quizGenPromptCustomizationBtn) quizGenPromptCustomizationBtn.onclick = toggleQuizGenPromptCustomization;
        loadAiBatchPerformancePreference();
        const aiBatchPerformanceBtn = $('ai-batch-performance-toggle');
        if (aiBatchPerformanceBtn) aiBatchPerformanceBtn.onclick = toggleAiBatchPerformanceChoice;
        loadAccessibilityPreferences();
        bindAccessibilityMenuButtons();
        bindSmoothModeToggle();

        // Achievements Panel Button
        $('achievements-btn').onclick = openAchievementsModal;

        // Level Badge Roadmap trigger (main menu Lv badge)
        const levelBadgeEl = $('level-badge');
        if (levelBadgeEl) {
            levelBadgeEl.onclick = openLevelBadgeRoadmapModal;
            levelBadgeEl.onkeydown = handleLevelBadgeRoadmapKeydown;
            levelBadgeEl.setAttribute('role', 'button');
            levelBadgeEl.setAttribute('tabindex', '0');
            levelBadgeEl.setAttribute('aria-label', 'Open level badge roadmap');
        }

        // AUTO-SAVE every 10 seconds (silent save)
        if (!IS_EMBED_DEMO) {
            setInterval(() => {
                if (sessionDirty && currentSessionId && hasPersistableSessionContent() && !document.hidden) {
                    void SessionManager.save(false); // Silent save only after a state mutation.
                }
            }, 10000); // 10 seconds
        }

    } catch (err) {
        setWelcomeActionsPending(false);
        console.error("Init Failed:", err);
    }
}


/* ============================================================================
   SECTION 3: GAMIFICATION SYSTEM
   XP, Achievements, Mystery Cards, Power-ups
   ============================================================================ */
let gamificationSaveQueue = Promise.resolve();
let gamificationSaveSnapshot = { total: 0, evidence: 0, wrong: [] };

function captureGamificationSaveState() {
    return { total: Math.max(0, Number(totalQuestionsAnswered) || 0), evidence: Math.max(0, Number(evidenceViews) || 0),
        studyDays: Math.max(0, Number(studyDays) || 0), lastStudyDate, wrong: Array.from(wrongAnswerHistory) };
}

async function loadGamificationData() {
    try {
        const savedXP = await IDB.get('files', 'xpData');
        if (savedXP) xpData = savedXP;
        getXPSyncChannel();

        const savedAch = await IDB.get('files', 'achievementsData');
        if (savedAch) achievementsData = savedAch;

        const savedTotal = await IDB.get('files', 'totalQuestionsAnswered');
        if (savedTotal) totalQuestionsAnswered = savedTotal;
        gamificationSaveSnapshot.total = totalQuestionsAnswered;

        // Load extended tracking data
        const savedExtended = await IDB.get('files', 'gamificationExtended');
        if (savedExtended) {
            evidenceViews = savedExtended.evidenceViews || 0;
            studyDays = savedExtended.studyDays || 0;
            lastStudyDate = savedExtended.lastStudyDate || null;
        }

        gamificationSaveSnapshot.evidence = evidenceViews;

        // Load power-ups
        const savedPowerUps = await IDB.get('files', 'activePowerUps');
        if (savedPowerUps) {
            Object.assign(activePowerUps, savedPowerUps);
            activePowerUps.streakShields = Math.max(0, Math.min(MAX_STREAK_SHIELDS, Number(activePowerUps.streakShields) || 0));
            updatePowerUpIndicator(); // Show any active power-ups
        }

        // Load wrong answer history
        const savedWrongHistory = await IDB.get('files', 'wrongAnswerHistory');
        if (savedWrongHistory && Array.isArray(savedWrongHistory)) {
            wrongAnswerHistory = createWrongAnswerHistory(savedWrongHistory);
        }

        gamificationSaveSnapshot.wrong = Array.from(wrongAnswerHistory);
        console.debug(`Loaded XP: ${xpData.totalXP}, Level: ${xpData.level}`);
        void claimDailyLoginRewardsIfStudied();
    } catch (e) {
        console.warn("Could not load gamification data:", e);
    }
}

// XP is shared by every open tab, so it is never written back whole: a tab holding an
// older total would erase awards another tab committed. Each change is applied to the
// stored record inside one readwrite transaction, then mirrored here and to other tabs.
const XP_SYNC_CHANNEL_NAME = 'memora-xp-data';
let xpSyncChannel = null;
let xpCommitQueue = Promise.resolve();
let xpPendingDelta = 0;
function getXPSyncChannel() {
    if (!xpSyncChannel && typeof BroadcastChannel === 'function') {
        try {
            xpSyncChannel = new BroadcastChannel(XP_SYNC_CHANNEL_NAME);
            xpSyncChannel.onmessage = (event) => applyCommittedXPRecord(event?.data?.record, event?.data?.powerUps);
        } catch (_err) { xpSyncChannel = null; }
    }
    return xpSyncChannel;
}
function applyCommittedXPRecord(record, powerUps = null) {
    if (!record || typeof record !== 'object') return;
    window.memoraGamePath?.receiveRecord(record.gamePath);
    if (record.gamePath && (!xpData.gamePath || record.gamePath.revision >= xpData.gamePath.revision)) xpData.gamePath = record.gamePath;
    if (record.gameEvents && (Number(record.gameEvents.revision) || 0) >= (Number(xpData.gameEvents?.revision) || 0)) { xpData.gameEvents = record.gameEvents; window.memoraGameEvents?.receive(record.gameEvents); }
    if (powerUps && Number(powerUps._revision || 0) >= Number(activePowerUps._revision || 0)) {
        Object.assign(activePowerUps, powerUps);
        updatePowerUpIndicator();
    }
    if (record.dailyLogin && (!xpData.dailyLogin?.lastDay || record.dailyLogin.lastDay >= xpData.dailyLogin.lastDay)) xpData.dailyLogin = record.dailyLogin;
    if ((Number(record.masteryQuizzes?.count) || 0) >= (Number(xpData.masteryQuizzes?.count) || 0) && record.masteryQuizzes) xpData.masteryQuizzes = record.masteryQuizzes;
    const committedXP = Math.max(0, Number(record.totalXP) || 0);
    // Committed totals only grow; a late message never rolls back an award made since.
    if (committedXP < (Number(xpData.totalXP) || 0) - xpPendingDelta) return;
    if (Number.isFinite(record.rewardStreak)) currentStreak = record.rewardStreak;
    xpData.totalXP = committedXP + xpPendingDelta;
    xpData.level = calculateLevel(xpData.totalXP);
    xpData.completionAwardKeys = Array.isArray(record.completionAwardKeys) ? record.completionAwardKeys.slice() : [];
    const today = new Date().toDateString();
    const answerRewards = { ...xpData.answerRewardDays };
    Object.entries(record.answerRewardDays || {}).forEach(([key, reward]) => {
        if (reward?.day !== today) return;
        const local = answerRewards[key];
        answerRewards[key] = { day: today, correct: reward.correct === true || (local?.day === today && local.correct === true),
            completionPaid: reward.completionPaid === true || (local?.day === today && local.completionPaid === true) };
    });
    xpData.answerRewardDays = Object.fromEntries(Object.entries(answerRewards).filter(([, reward]) => reward?.day === today));
    updateXPDisplay();
}
function commitXPRecordChange(change) {
    const { delta = 0, mutateKeys = null, answerReward = null, answerXP = null, gameStep = null, completionAwardKey = null, completionRewardKeys = null, onAccepted = null } = change || {};
    xpPendingDelta += delta;
    const run = async () => {
        let committed = null, accepted = false, previousLevel = 1, appliedDelta = delta;
        let receipt = { walletApplied: false, directMementos: 0, powerUps: null, shieldConsumed: false };
        try {
            if (window.memoraGameStudy && (gameStep || answerReward)) {
                if (!window.MemoraCompanionModel) throw new Error('Your collection is still loading. Study credit can be retried.');
                if (window.MemoraCompanions?.state?.loaded === false) await window.MemoraCompanions.refresh();
            }
            committed = await IDB.runWriteTransaction(['files'], (tx, write, abort, setResult) => {
                const files = tx.objectStore('files'), request = files.get('xpData');
                request.onerror = () => abort(request.error || new Error('Could not read XP.'));
                request.onsuccess = () => {
                    try {
                        const stored = request.result && typeof request.result === 'object' ? request.result : {};
                        if ((answerReward || gameStep) && !isGameModeRewardEligible()) { appliedDelta = 0; setResult(stored); return; }
                        const days = { ...stored.answerRewardDays };
                        const previous = answerReward?.key ? days[answerReward.key]
                            || Object.entries(days).find(([key, reward]) => answerReward.legacyPrefix && key.startsWith(answerReward.legacyPrefix)
                                && reward?.day === answerReward.day && reward.correct === true)?.[1] : null;
                        const duplicateAnswer = !!answerReward && previous?.day === answerReward.day && (previous.correct === true || answerReward.correct !== true);
                        // One timestamp decides both the receipt day and the weekend factor.
                        const rewardNow = new Date(), completionDay = rewardNow.toDateString();
                        const weekend = window.memoraGameStudy?.studyRewardMultiplier?.(rewardNow) || 1;
                        const freshCompletionKeys = Array.isArray(completionRewardKeys) ? [...new Set(completionRewardKeys)]
                            .filter(key => days[key]?.day === completionDay && days[key].completionPaid !== true) : null;
                        const duplicateCompletion = !!completionAwardKey && ((stored.completionAwardKeys || []).includes(completionAwardKey)
                            || (freshCompletionKeys && freshCompletionKeys.length < 5));
                        const proposedStep = gameStep || (answerReward?.correct === true && window.memoraGameStudy ? { mode: answerReward.mode || 'quizzes', key: answerReward.key, points: 10, mastery: true } : null);
                        const stepResult = proposedStep && !duplicateAnswer ? window.memoraGameStudy?.creditStep(stored.gamePath, proposedStep, answerReward?.day || completionDay) : null;
                        accepted = !duplicateAnswer && !duplicateCompletion && (!proposedStep || stepResult?.accepted === true);
                        previousLevel = calculateLevel(Math.max(0, Number(stored.totalXP) || 0));
                        if (answerReward?.key) {
                            Object.keys(days).forEach(key => { if (days[key]?.day !== answerReward.day) delete days[key]; });
                            days[answerReward.key] = { ...previous, day: answerReward.day, correct: answerReward.correct === true || (previous?.day === answerReward.day && previous.correct === true) };
                        }
                        const finishAnswer = rawPowers => {
                            try {
                                appliedDelta = accepted ? delta : 0;
                                if (accepted && answerReward) receipt.streak = Math.max(0, Math.floor(Number(stored.rewardStreak ?? currentStreak) || 0));
                                if (accepted && answerReward && (!answerReward.mode || ['quizzes', 'exam'].includes(answerReward.mode))) {
                                    const previousStreak = Math.max(0, Math.floor(Number(stored.rewardStreak ?? currentStreak) || 0));
                                    const computed = calculateGameModeAnswerXP(answerReward.correct === true, previous?.day === answerReward.day && previous.correct === false,
                                        answerReward.correct === true ? previousStreak + 1 : previousStreak, rawPowers, previousLevel);
                                    receipt.powerUps = computed.powerUps; receipt.shieldConsumed = computed.shieldConsumed;
                                    receipt.streak = answerReward.correct === true ? previousStreak + 1 : computed.shieldConsumed ? previousStreak : 0;
                                    const combo = Math.min(1.5, Math.max(1, Number(answerReward.combo) || 1));
                                    if (answerReward.correct === true) appliedDelta = Math.round(computed.amount * combo) * weekend;
                                    write({ storeName: 'files', key: 'activePowerUps', value: computed.powerUps });
                                } else if (accepted && gameStep) {
                                    const powers = { ...(rawPowers || {}) };
                                    const uses = Math.max(0, Math.floor(Number(powers.xpMultiplierQuestions) || 0));
                                    const boost = uses ? Math.max(1, Math.min(4, Number(powers.xpMultiplier) || 1)) : 1;
                                    appliedDelta = Math.round(delta * boost * getLevelXPBonus(previousLevel)) * weekend;
                                    if (uses) { powers.xpMultiplierQuestions = uses - 1; if (!powers.xpMultiplierQuestions) powers.xpMultiplier = 1; }
                                    powers._revision = Math.max(0, Number(powers._revision) || 0) + 1;
                                    receipt.powerUps = powers;
                                    write({ storeName: 'files', key: 'activePowerUps', value: powers });
                                }
                                if (accepted && completionAwardKey && freshCompletionKeys) {
                                    const accuracy = Math.round(freshCompletionKeys.filter(key => days[key].correct === true).length / freshCompletionKeys.length * 100);
                                    appliedDelta = buildSessionCompletionBonus(freshCompletionKeys.length, accuracy).total;
                                    freshCompletionKeys.forEach(key => { days[key] = { ...days[key], completionPaid: true }; });
                                }
                                const keys = Array.isArray(stored.completionAwardKeys) ? [...stored.completionAwardKeys] : [];
                                if (completionAwardKey && accepted) keys.push(completionAwardKey);
                                const next = { ...stored, totalXP: Math.max(0, Number(stored.totalXP) || 0) + appliedDelta, answerRewardDays: days,
                                    completionAwardKeys: (typeof mutateKeys === 'function' ? mutateKeys(keys) : keys).slice(-RESULTS_COMPLETION_AWARD_LIMIT) };
                                next.level = calculateLevel(next.totalXP);
                                if (accepted && answerReward) next.rewardStreak = receipt.streak;
                                if (accepted && stepResult) next.gamePath = stepResult.ledger;
                                else if (accepted && answerReward?.correct === true && window.memoraGamePath) next.gamePath = window.memoraGamePath.creditAnswer(stored.gamePath);
                                if (accepted && stepResult?.accepted && window.memoraGameEvents) next.gameEvents = window.memoraGameEvents.recordStep(stored.gameEvents, proposedStep, rewardNow.getTime());
                                const finish = () => { write({ storeName: 'files', key: 'xpData', value: next }); setResult(next); };
                                const model = window.MemoraCompanionModel;
                                if (accepted && appliedDelta > 0 && model) {
                                    const wallet = files.get('companionState');
                                    wallet.onerror = () => abort(wallet.error || new Error('Could not read Mementos.'));
                                    wallet.onsuccess = () => {
                                        try {
                                            const cached = window.MemoraCompanions?.state;
                                            const raw = wallet.result || (cached?.loaded ? cached : null);
                                            if (!raw && window.memoraGameStudy && proposedStep) throw new Error('Your collection could not load. No study reward was spent.');
                                            if (raw) {
                                                let outcome = model.reduceState(raw, { type: 'xp', amount: appliedDelta }, true);
                                                if (stepResult?.accepted && model.FRAGMENT_PROGRESS_POINTS) {
                                                    const progressed = model.reduceState(outcome.value, { type: 'study-progress', points: proposedStep.points }, true);
                                                    if (!progressed.ok) throw new Error('Could not save study milestone progress.');
                                                    outcome = progressed; receipt.fragments = progressed.receipt?.fragments || [];
                                                }
                                                const tier = getLevelTier(next.level);
                                                receipt.directMementos = tier !== getLevelTier(previousLevel) ? LEVEL_TIER_MEMENTOS[tier] || 0 : 0;
                                                if (receipt.directMementos) outcome = model.reduceState(outcome.value, { type: 'grant', amount: receipt.directMementos, reason: `Reached ${tier} tier` }, true);
                                                if (!outcome.ok) throw new Error('Could not save earned Mementos.');
                                                write({ storeName: 'files', key: 'companionState', value: outcome.value }); receipt.walletApplied = true;
                                            }
                                            finish();
                                        } catch (error) { abort(error); }
                                    };
                                } else finish();
                            } catch (error) { abort(error); }
                        };
                        if (accepted && (answerReward || gameStep)) {
                            const powers = files.get('activePowerUps');
                            powers.onerror = () => abort(powers.error || new Error('Could not read power-ups.'));
                            powers.onsuccess = () => finishAnswer(powers.result);
                        } else finishAnswer(null);
                    } catch (error) { abort(error); }
                };
            }, { timeoutMs: 10000 });
        } catch (error) { console.warn('Could not save XP:', error); }
        finally { xpPendingDelta -= delta; }
        const correction = committed ? delta - appliedDelta : delta;
        if (correction) { xpData.totalXP = Math.max(0, xpData.totalXP - correction); sessionXP = Math.max(0, sessionXP - correction); }
        if (committed) {
            applyCommittedXPRecord(committed, receipt.powerUps);
            try { getXPSyncChannel()?.postMessage({ record: committed, powerUps: receipt.powerUps }); } catch (_) { }
            if (accepted && typeof onAccepted === 'function') onAccepted(committed, previousLevel, { ...receipt, amount: appliedDelta });
        } else {
            xpData.level = calculateLevel(xpData.totalXP);
            if (answerReward?.key && xpData.answerRewardDays?.[answerReward.key]?.day === answerReward.day) delete xpData.answerRewardDays[answerReward.key];
            if (completionAwardKey) xpData.completionAwardKeys = (xpData.completionAwardKeys || []).filter(key => key !== completionAwardKey);
            updateXPDisplay();
        }
        return committed;
    };
    const task = xpCommitQueue.then(run);
    xpCommitQueue = task.catch(() => {});
    return task;
}

async function saveGamificationData() {
    const target = captureGamificationSaveState();
    const run = async () => {
        const base = gamificationSaveSnapshot;
        try {
            await IDB.runWriteTransaction(['files'], (tx, write, abort, setResult) => {
                const files = tx.objectStore('files'), values = {}; let remaining = 3;
                for (const key of ['totalQuestionsAnswered', 'gamificationExtended', 'wrongAnswerHistory']) {
                    const request = files.get(key);
                    request.onerror = () => abort(request.error || new Error('Could not read study achievements.'));
                    request.onsuccess = () => {
                        values[key] = request.result; if (--remaining) return;
                        try {
                            const total = Math.max(0, Number(values.totalQuestionsAnswered) || 0) + Math.max(0, target.total - base.total);
                            const saved = values.gamificationExtended || {};
                            const extended = { ...saved, evidenceViews: Math.max(0, Number(saved.evidenceViews) || 0) + Math.max(0, target.evidence - base.evidence),
                                studyDays: Math.max(0, Number(saved.studyDays) || 0), lastStudyDate: saved.lastStudyDate || null };
                            const day = value => value ? dailyLoginDayNumber(getDailyLoginDay(new Date(value))) : null;
                            const incomingDay = day(target.lastStudyDate), savedDay = day(saved.lastStudyDate);
                            if (incomingDay !== null && (savedDay === null || incomingDay >= savedDay)) {
                                extended.lastStudyDate = target.lastStudyDate;
                                extended.studyDays = incomingDay === savedDay ? Math.max(extended.studyDays, target.studyDays)
                                    : savedDay !== null && incomingDay - savedDay === 1 ? extended.studyDays + 1 : target.studyDays;
                            }
                            const wrong = new Set(Array.isArray(values.wrongAnswerHistory) ? values.wrongAnswerHistory : []);
                            const before = new Set(base.wrong), after = new Set(target.wrong);
                            for (const key of after) if (!before.has(key)) wrong.add(key);
                            for (const key of before) if (!after.has(key)) wrong.delete(key);
                            write({ storeName: 'files', key: 'totalQuestionsAnswered', value: total });
                            write({ storeName: 'files', key: 'gamificationExtended', value: extended });
                            write({ storeName: 'files', key: 'wrongAnswerHistory', value: [...wrong] });
                            setResult(true);
                        } catch (error) { abort(error); }
                    };
                }
            }, { timeoutMs: 10000 });
            gamificationSaveSnapshot = target;
            return true;
        } catch (error) { console.warn('Could not save gamification data:', error); return false; }
    };
    const task = gamificationSaveQueue.then(run);
    gamificationSaveQueue = task.catch(() => {});
    return task;
}

// Rising curve: going from level L to L+1 costs 100 + 25(L-1) XP, so early levels come
// quickly and later ones take longer. Total XP to reach level L, with n = L-1, is 100n + 12.5n(n-1).
function getXPForLevel(level) {
    const n = Math.max(0, Math.floor(level) - 1);
    return 100 * n + 12.5 * n * (n - 1);
}

function calculateLevel(xp) {
    const total = Math.max(0, Number(xp) || 0);
    let n = Math.max(0, Math.floor((-87.5 + Math.sqrt(87.5 * 87.5 + 50 * total)) / 25));
    while (n > 0 && getXPForLevel(n + 1) > total) n--;
    while (getXPForLevel(n + 2) <= total) n++;
    return n + 1;
}

// Progression bonuses are canonical and bounded; only accepted answer XP is multiplied.
const DAILY_LOGIN_REWARDS = Object.freeze(Array.from({length:30},(_,day)=>Object.freeze([25+day*5,20+day*4])));
function getLevelXPBonus(level) {
    return 1 + Math.min(25, Math.max(0, Math.floor(Number(level) || 1) - 1)) * .02;
}
function getDailyLoginDay(date = new Date()) {
    return `${date.getFullYear()}-${String(date.getMonth()+1).padStart(2,'0')}-${String(date.getDate()).padStart(2,'0')}`;
}
function dailyLoginDayNumber(day) {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(String(day))) return null;
    const [year,month,date] = day.split('-').map(Number), stamp = Date.UTC(year,month-1,date);
    return new Date(stamp).toISOString().slice(0,10) === day ? stamp/86400000 : null;
}
function dailyLoginRewardForStreak(streak) {
    streak=Math.max(1,Math.floor(Number(streak)||1));
    const cycleDay = (streak-1)%DAILY_LOGIN_REWARDS.length;
    const [xp,mementos] = DAILY_LOGIN_REWARDS[cycleDay];
    return {streak,cycleDay:cycleDay+1,xp,mementos};
}
function getDailyLoginSchedule(streak) {
    return DAILY_LOGIN_REWARDS.map((_,index)=>dailyLoginRewardForStreak(index+1));
}
// One missed day a week is forgiven: the run continues across it, and the day is remembered so a
// second gap inside the same seven days restarts it.
function buildDailyLoginReward(raw, day) {
    const current = dailyLoginDayNumber(day), previous = dailyLoginDayNumber(raw?.lastDay);
    if (current === null || (previous !== null && current <= previous)) return null;
    const lastGrace = dailyLoginDayNumber(raw?.graceDay);
    const graced = previous !== null && current-previous === 2 && !(lastGrace !== null && current-lastGrace < 7);
    const streak = previous !== null && (current-previous === 1 || graced) ? Math.max(0,Math.floor(Number(raw?.streak)||0))+1 : 1;
    const graceDay = graced ? new Date((current-1)*86400000).toISOString().slice(0,10) : raw?.graceDay;
    const cycleDay=streak>1 && raw?.cycleVersion===1 ? Math.max(1,Math.min(30,Math.floor(Number(raw.cycleDay)||0)))%30+1 : 1;
    return { ...dailyLoginRewardForStreak(cycleDay), streak, cycleVersion:1, lastDay:day, best:Math.max(streak,Math.floor(Number(raw?.best)||0)), ...(graceDay ? {graceDay} : {}) };
}
// The daily reward is paid for studying, not for opening the app: it waits for the day's first answer.
function claimDailyLoginRewardsIfStudied() {
    return lastStudyDate === new Date().toDateString() ? claimDailyLoginRewards() : Promise.resolve(null);
}
// Reads the card's schedule before the answer reschedules it. New cards and cards past their review
// time earn credit; a card answered again before it is due earns nothing. Quiz and flashcards share it.
function srsCardDueState(idx) {
    const record = srsData?.[idx], reviewed = Number(record?.lastReviewed) > 0;
    const due = !reviewed || !(Number(record?.nextReview) > Date.now());
    return { due, dueReview: reviewed && due };
}
// Combos and lucky finds come only from recalling due reviews, in any mode that schedules cards.
// A miss ends the combo; ×1.25 from 5 in a row, ×1.5 from 10. At most three finds a day.
// "Don't show this again" mutes these pop-ups; the multiplier and the XP still count.
function studyNudgesOn() { return window.MemoraReviewSchedule?.nudgesOn?.() !== false; }
let dueCombo = { day: '', count: 0 };
function noteDueRecall(dueReview, correct) {
    const day = new Date().toDateString();
    if (dueCombo.day !== day) dueCombo = { day, count: 0 };
    if (!dueReview) return 1;
    if (!correct) { dueCombo.count = 0; return 1; }
    dueCombo.count++;
    if ((dueCombo.count === 5 || dueCombo.count === 10) && studyNudgesOn()) showPowerUpNotification(`Combo ×${dueCombo.count === 5 ? '1.25' : '1.5'}`, `${dueCombo.count} due cards in a row`);
    return dueCombo.count >= 10 ? 1.5 : dueCombo.count >= 5 ? 1.25 : 1;
}
function maybeDueCardFind() {
    if (!isGameModeRewardEligible() || Math.random() >= 0.07) return;
    const day = new Date().toDateString();
    let log = { day, count: 0 };
    try { const saved = JSON.parse(localStorage.getItem('memora_due_drops_v1') || 'null'); if (saved?.day === day) log = saved; } catch { }
    if (log.count >= 3) return;
    const amount = 3 + Math.floor(Math.random() * 6);
    try { localStorage.setItem('memora_due_drops_v1', JSON.stringify({ day, count: log.count + 1 })); } catch { }
    if (grantGameReward({ type: 'grant', amount, reason: 'Lucky find' }) && studyNudgesOn()) showXPToast(0, 'Lucky find · due card', false, amount);
}
let dailyLoginClaimPromise = null;
function claimDailyLoginRewards() {
    if (dailyLoginClaimPromise) return dailyLoginClaimPromise;
    if (!isGameModeRewardEligible() || !window.MemoraCompanionModel || typeof IDB === 'undefined') return Promise.resolve(null);
    const work = async () => {
        try {
            if (window.MemoraCompanions?.state?.loaded === false) await window.MemoraCompanions.refresh();
            const day = getDailyLoginDay();
            const result = await IDB.runWriteTransaction(['files'], (tx, write, abort, setResult) => {
                const files=tx.objectStore('files'), values={}; let remaining=2;
                for (const key of ['xpData','companionState']) {
                    const request=files.get(key);
                    request.onerror=()=>abort(request.error||new Error('Could not read daily rewards.'));
                    request.onsuccess=()=>{
                        values[key]=request.result; if (--remaining) return;
                        try {
                            if (!isGameModeRewardEligible()) { setResult(null); return; }
                            const stored=values.xpData||{}, model=window.MemoraCompanionModel;
                            const cached=window.MemoraCompanions?.state;
                            const wallet=values.companionState||(cached?.loaded?cached:null);
                            if (!wallet) throw new Error('Your collection is still loading. Daily rewards can be retried.');
                            // An already-paid legacy gift cannot become a second reward today.
                            if (!stored.dailyLogin && (wallet?.wallet?.lastDaily===day || wallet?.wallet?.lastDaily===day.split('-').map(Number).join('-'))) {
                                const record={...stored,dailyLogin:{lastDay:day,streak:1,best:1,cycleDay:1,xp:0,mementos:0,legacy:true}};
                                write({storeName:'files',key:'xpData',value:record}); setResult({record,alreadyClaimed:true}); return;
                            }
                            const reward=buildDailyLoginReward(stored.dailyLogin,day);
                            if (!reward) { setResult(null); return; }
                            const previousLevel=calculateLevel(stored.totalXP||0);
                            const record={...stored,totalXP:Math.max(0,Number(stored.totalXP)||0)+reward.xp,dailyLogin:{...reward,claimedAt:Date.now()}};
                            record.level=calculateLevel(record.totalXP);
                            let outcome=model.reduceState(wallet,{type:'grant',amount:reward.mementos,reason:`Daily login · ${reward.streak}-day streak`},true);
                            if (model.dailyMilestone(reward.cycleDay)) {
                                outcome=model.reduceState(outcome.value,{type:'daily-milestone',day:reward.cycleDay},true);
                                if (!outcome.ok) throw new Error('Daily milestone could not be saved.');
                                reward.milestone=outcome.milestone;
                                record.dailyLogin.milestone=outcome.milestone;
                            }
                            const tier=getLevelTier(record.level), tierMementos=tier!==getLevelTier(previousLevel)?LEVEL_TIER_MEMENTOS[tier]||0:0;
                            if (tierMementos) outcome=model.reduceState(outcome.value,{type:'grant',amount:tierMementos,reason:`Reached ${tier} tier`},true);
                            if (!outcome.ok) throw new Error('Daily rewards could not be saved.');
                            outcome.value.wallet.lastDaily=day;
                            write({storeName:'files',key:'xpData',value:record});
                            write({storeName:'files',key:'companionState',value:outcome.value});
                            setResult({record,previousLevel,reward,mementos:reward.mementos+tierMementos+(reward.milestone?.mementos||0)});
                        } catch(error) { abort(error); }
                    };
                }
            }, {timeoutMs:10000});
            if (!result) return null;
            applyCommittedXPRecord(result.record);
            try { getXPSyncChannel()?.postMessage({record:result.record}); } catch(_) { }
            if (!result.alreadyClaimed) {
                sessionXP+=result.reward.xp; sessionMementos+=result.mementos;
                if (result.record.level>result.previousLevel) celebrateLevelUp(result.previousLevel);
                checkAchievements();
            }
            await window.MemoraCompanions?.refresh(true);
            return result;
        } catch(error) {
            console.warn('Daily login reward was not saved; it remains available to retry.',error);
            return null;
        }
    };
    const task=xpCommitQueue.then(work);
    xpCommitQueue=task.catch(()=>{});
    dailyLoginClaimPromise=task.finally(()=>{dailyLoginClaimPromise=null;});
    return dailyLoginClaimPromise;
}
// Bounty and flash-mission commands share the XP transaction: progress, XP and Mementos commit
// together, and a claim removes its reward from the claimable list before paying it once.
function commitGameEventCommand(command) {
    if (!isGameModeRewardEligible() || !window.memoraGameEvents || !window.MemoraCompanionModel || typeof IDB === 'undefined') return Promise.resolve({ok:false,error:'Turn on Game Mode to use bounties.'});
    const work = async () => {
        try {
            if (command?.type === 'claim' && window.MemoraCompanions?.state?.loaded === false) await window.MemoraCompanions.refresh();
            const result = await IDB.runWriteTransaction(['files'], (tx, write, abort, setResult) => {
                const files=tx.objectStore('files'), values={}; let remaining=2;
                for (const key of ['xpData','companionState']) {
                    const request=files.get(key);
                    request.onerror=()=>abort(request.error||new Error('Could not read missions.'));
                    request.onsuccess=()=>{
                        values[key]=request.result; if (--remaining) return;
                        try {
                            if (!isGameModeRewardEligible()) { setResult({ok:false,error:'Turn on Game Mode to use bounties.'}); return; }
                            const stored=values.xpData||{}, outcome=window.memoraGameEvents.apply(stored.gameEvents,command,Date.now());
                            if (outcome.error || !outcome.changed) { setResult({ok:!outcome.error,error:outcome.error,events:outcome.state}); return; }
                            const record={...stored,gameEvents:outcome.state}, reward=outcome.reward;
                            const previousLevel=calculateLevel(stored.totalXP||0);
                            let mementos=0;
                            if (reward) {
                                const cached=window.MemoraCompanions?.state;
                                const wallet=values.companionState||(cached?.loaded?cached:null);
                                if (!wallet) throw new Error('Your collection is still loading. The reward stays ready to claim.');
                                // Fixed rewards: no weekend, power-up or Mastery multiplier applies.
                                record.totalXP=Math.max(0,Number(stored.totalXP)||0)+reward.xp;
                                record.level=calculateLevel(record.totalXP);
                                const model=window.MemoraCompanionModel;
                                let paid=model.reduceState(wallet,{type:'grant',amount:reward.mementos,reason:reward.title},true);
                                const tier=getLevelTier(record.level), tierMementos=tier!==getLevelTier(previousLevel)?LEVEL_TIER_MEMENTOS[tier]||0:0;
                                if (tierMementos) paid=model.reduceState(paid.value,{type:'grant',amount:tierMementos,reason:`Reached ${tier} tier`},true);
                                if (!paid.ok) throw new Error('The reward could not be saved.');
                                write({storeName:'files',key:'companionState',value:paid.value});
                                mementos=reward.mementos+tierMementos;
                            }
                            write({storeName:'files',key:'xpData',value:record});
                            setResult({ok:true,events:outcome.state,reward,record,previousLevel,mementos});
                        } catch(error) { abort(error); }
                    };
                }
            }, {timeoutMs:10000});
            if (result?.record) {
                applyCommittedXPRecord(result.record);
                try { getXPSyncChannel()?.postMessage({record:result.record}); } catch(_) { }
            }
            if (result?.reward) {
                sessionXP+=result.reward.xp; sessionMementos+=result.mementos;
                if (result.record.level>result.previousLevel) celebrateLevelUp(result.previousLevel);
                checkAchievements();
                await window.MemoraCompanions?.refresh(true);
            }
            return result;
        } catch(error) {
            console.warn('Mission change was not saved.',error);
            return {ok:false,error:error?.message||'Could not save. Please retry.'};
        }
    };
    const task=xpCommitQueue.then(work);
    xpCommitQueue=task.catch(()=>{});
    return task;
}
window.addEventListener('memora:game-mode-changed',()=>{void claimDailyLoginRewardsIfStudied();});
window.addEventListener('focus',()=>{void claimDailyLoginRewardsIfStudied();});
document.addEventListener('visibilitychange',()=>{if(!document.hidden)void claimDailyLoginRewardsIfStudied();});


// Mementos paid once when a level-up crosses into a new tier.
const LEVEL_TIER_MEMENTOS = Object.freeze({
    silver: 50, gold: 100, platinum: 150, diamond: 200, master: 300, grandmaster: 500,
    legend: 800, mythic: 1000, immortal: 1200, transcendent: 1500, eternal: 2000
});

// Mementos live in the companions wallet, which listens for this event.
function grantGameReward(detail) {
    if (!detail || !isGameModeRewardEligible()) return false;
    if (detail.type === 'grant' || detail.type === 'daily') {
        sessionMementos += Math.max(0, Math.floor(Number(detail.amount) || 0));
    }
    window.dispatchEvent(new CustomEvent('memora:companion-reward', { detail }));
    return true;
}

// Power-ups are spent only where they can pay out: with Game Mode off no XP is granted.
function calculateGameModeAnswerXP(correct, growth, streak, raw = {}, level = 1) {
    const number = (value, fallback = 0) => Number.isFinite(Number(value)) ? Number(value) : fallback;
    const powers = { ...raw, xpMultiplier: Math.max(1, number(raw?.xpMultiplier, 1)), xpMultiplierQuestions: Math.max(0, Math.floor(number(raw?.xpMultiplierQuestions))),
        streakShields: Math.max(0, Math.min(MAX_STREAK_SHIELDS, Math.floor(number(raw?.streakShields)))),
        growthBoost: Math.max(0, number(raw?.growthBoost)), growthBoostQuestions: Math.max(0, Math.floor(number(raw?.growthBoostQuestions))),
        _revision: Math.max(0, Math.floor(number(raw?._revision))) + 1 };
    let amount = 0, shieldConsumed = false;
    if (correct) {
        let base = growth ? 25 : 15;
        if (growth && powers.growthBoostQuestions > 0) { base += powers.growthBoost; powers.growthBoostQuestions--; }
        if (powers.xpMultiplierQuestions > 0) {
            base = Math.floor(base * powers.xpMultiplier); powers.xpMultiplierQuestions--;
            if (!powers.xpMultiplierQuestions) powers.xpMultiplier = 1;
        }
        amount = Math.round((base + (streak >= 5 ? 5 : 0)) * getLevelXPBonus(level));
    } else if (powers.streakShields > 0) { powers.streakShields--; shieldConsumed = true; }
    return { amount, powerUps: powers, shieldConsumed };
}
function buildGameModeAnswerRewardIdentity(questionObj) {
    if (!questionObj || typeof questionObj !== 'object') return null;
    const stem = normalizeQuestionPersistencePart(questionObj.q || questionObj.question || '');
    const choices = getQuestionChoicesForPersistence(questionObj).map(choice => normalizeQuestionPersistencePart(choice));
    const answerIndex = getQuestionAnswerIndexForPersistence(questionObj);
    const answer = choices[answerIndex];
    if (!stem || !answer) return null;
    // Filenames, choice order and distractor edits do not make the same solved question new.
    return { key: `answer-v2:${JSON.stringify([stem, answer])}`, legacyPrefix: `nosource::${stem}::${answer}::` };
}
function isGameModeRewardEligible() {
    return !!isGameMode && window.isMemoraProductFeatureAllowed?.('game-mode') === true;
}

// options.quiet: routine answer XP shows only in the header bar, never as a toast.
function awardXP(amount, reason, options = {}) {
    if (!isGameModeRewardEligible()) return;
    if (!Number.isFinite(amount) || amount <= 0) return;
    xpData.totalXP += amount;
    const committed = commitXPRecordChange({ delta: amount, answerReward: options.answerReward, answerXP: options.answerXP, gameStep: options.gameStep,
        completionAwardKey: options.completionAwardKey, completionRewardKeys: options.completionRewardKeys, onAccepted: (record, previousLevel, receipt) => {
        options.onAccepted?.(record, previousLevel, receipt);
        if (options.gameStep) updateDailyStreak();
        const leveledUp = record.level > previousLevel;
        if (!options.quiet && !leveledUp) showXPToast(receipt.amount, reason, false, options.mementos || 0);
        if (leveledUp) {
            const tier = getLevelTier(record.level);
            if (!receipt.walletApplied && tier !== getLevelTier(previousLevel) && LEVEL_TIER_MEMENTOS[tier]) {
                grantGameReward({ type: 'grant', amount: LEVEL_TIER_MEMENTOS[tier], reason: `Reached ${tier} tier` });
            }
            celebrateLevelUp(previousLevel);
        }
        if (receipt.walletApplied) {
            sessionMementos += receipt.directMementos;
            void window.MemoraCompanions?.refresh(true).catch(error => console.warn('Could not refresh earned Mementos:', error));
        }
        window.dispatchEvent(new CustomEvent('memora:xp-awarded', { detail: { amount: receipt.amount, walletApplied: receipt.walletApplied } }));
        checkAchievements();
    } });
    sessionXP += amount;
    xpData.level = calculateLevel(xpData.totalXP);

    // Update display
    updateXPDisplay();

    // Save
    saveGamificationData();
    return committed;
}

// A small corner note: Mementos first (they buy things), XP second (it moves the level bar).
function showXPToast(amount, reason, leveledUp, mementos = 0) {
    const existing = document.querySelector('.xp-toast');
    if (existing) existing.remove();

    const parts = [];
    if (mementos > 0) parts.push(`<span class="xp-toast-mementos">+${mementos} Mementos</span>`);
    if (amount > 0) parts.push(`<span class="xp-toast-xp"><span class="xp-amount">+${amount}</span> XP</span>`);
    if (!parts.length) return;
    const toast = document.createElement('div');
    toast.className = 'xp-toast';
    toast.setAttribute('role', 'status');
    toast.innerHTML = `${parts.join('')}${reason ? `<span class="xp-toast-reason">${escapeHtml(reason)}</span>` : ''}${leveledUp ? '<span class="xp-toast-reason">Level up</span>' : ''}`;
    document.body.appendChild(toast);

    setTimeout(() => toast.classList.add('show'), 10);
    setTimeout(() => {
        toast.classList.remove('show');
        setTimeout(() => toast.remove(), 300);
    }, 2200);
}

// Achievement toasts queue one at a time so a burst of unlocks never stacks over the question.
const achievementToastQueue = [];
let achievementToastActive = false;

function showAchievementToast(badge) {
    if (!badge) return;
    achievementToastQueue.push(badge);
    if (!achievementToastActive) showNextAchievementToast();
}

function showNextAchievementToast() {
    const badge = achievementToastQueue.shift();
    if (!badge) {
        achievementToastActive = false;
        return;
    }
    achievementToastActive = true;
    const toast = document.createElement('div');
    toast.className = 'achievement-toast gm-ach-toast';
    toast.setAttribute('role', 'status');
    toast.innerHTML = `
        <span class="gm-ach-mark">${achievementGlyph(badge)}</span>
        <span class="gm-ach-toast-text">
            <span class="gm-ach-toast-label">Achievement</span>
            <span class="gm-ach-toast-name">${escapeHtml(badge.name)}</span>
        </span>
        <span class="gm-ach-toast-reward">+${getAchievementMementos(badge)} Mementos</span>
    `;
    document.body.appendChild(toast);

    setTimeout(() => toast.classList.add('show'), 10);
    setTimeout(() => {
        toast.classList.remove('show');
        setTimeout(() => {
            toast.remove();
            showNextAchievementToast();
        }, 300);
    }, 3000);
}
function checkAchievements() {
    if (!isGameModeRewardEligible()) return;
    for (const [id, badge] of Object.entries(ACHIEVEMENTS)) {
        // Skip if already unlocked or session-based
        if (achievementsData[id] || badge.condition === 'session') continue;

        // Check condition
        if (typeof badge.condition === 'function' && badge.condition()) {
            unlockAchievement(id);
        }
    }
}

async function unlockAchievement(id) {
    if (!isGameModeRewardEligible() || achievementsData[id] || !ACHIEVEMENTS[id]) return;
    const model = window.MemoraCompanionModel;
    if (!model) return;
    const pending = unlockAchievement.pending || (unlockAchievement.pending = new Set());
    if (pending.has(id)) return;
    pending.add(id);
    const badge = ACHIEVEMENTS[id];
    try {
        const result = await IDB.runWriteTransaction(['files'], (tx, write, abort, setResult) => {
            const files = tx.objectStore('files'), values = {};
            let remaining = 2;
            for (const key of ['achievementsData', 'companionState']) {
                const request = files.get(key);
                request.onerror = () => abort(request.error || new Error('Could not read achievement rewards.'));
                request.onsuccess = () => { values[key] = request.result; if (--remaining === 0) commit(); };
            }
            function commit() {
                try {
                    const achievements = values.achievementsData || {};
                    if (achievements[id]) { setResult({ achievements, added: false }); return; }
                    const raw = values.companionState || (window.MemoraCompanions?.state.loaded ? window.MemoraCompanions.state : null);
                    if (!raw) throw new Error('The collection is still loading.');
                    const amount = getAchievementMementos(badge);
                    const outcome = model.reduceState(raw, { type: 'grant', amount, reason: badge.name }, true);
                    if (!outcome.ok) throw new Error('Could not save achievement Mementos.');
                    const next = { ...achievements, [id]: Date.now() };
                    write({ storeName: 'files', key: 'achievementsData', value: next });
                    write({ storeName: 'files', key: 'companionState', value: outcome.value });
                    setResult({ achievements: next, added: true, amount });
                } catch (error) { abort(error); }
            }
        }, { timeoutMs: 10000 });
        achievementsData = { ...achievementsData, ...result.achievements };
        if (result.added) {
            sessionAchievements.push(id); sessionMementos += result.amount;
            showAchievementToast(badge);
            await window.MemoraCompanions.refresh(true);
        }
        updateXPDisplay();
    } catch (error) { console.warn('Could not save achievement reward:', error); }
    finally { pending.delete(id); }
}

function updateXPDisplay() {
    const xpEl = $('xp-val');
    const levelEl = $('level-badge');
    const xpStatItem = $('xp-stat-item');
    const xpDivider = $('xp-divider');

    // Hide XP UI if game mode is off
    if (!isGameMode || !window.isMemoraProductFeatureAllowed?.('game-mode')) {
        if (xpStatItem) xpStatItem.style.display = 'none';
        if (xpDivider) xpDivider.style.display = 'none';
        const achBtn = $('achievements-btn');
        if (achBtn) achBtn.style.display = 'none';
        return;
    }

    if (xpStatItem) xpStatItem.style.display = 'flex';
    if (xpDivider) xpDivider.style.display = 'block';

    // Show achievements button
    const achBtn = $('achievements-btn');
    if (achBtn) {
        achBtn.style.display = 'flex';
        const totalBadges = Object.keys(ACHIEVEMENTS).length;
        const unlockedBadges = Object.keys(ACHIEVEMENTS).filter((id) => !!achievementsData[id]).length;
        achBtn.innerHTML = `${achievementGlyph({ track: 'session' })}<span>${unlockedBadges}</span>`;
        achBtn.title = `Achievements (${unlockedBadges}/${totalBadges})`;
        achBtn.setAttribute('aria-label', `Achievements, ${unlockedBadges} of ${totalBadges} unlocked`);
    }

    if (xpEl) xpEl.innerText = xpData.totalXP;
    if (levelEl) {
        levelEl.innerText = `Lv ${xpData.level}`;
        // Apply prestige tier class
        applyLevelTier(levelEl, xpData.level);
        const tierEntry = getLevelBadgeTierEntryForLevel(xpData.level);
        const tierName = tierEntry ? tierEntry.name : 'Unknown';
        levelEl.title = `Level ${xpData.level} (${tierName}) - Click to view badge roadmap`;
    }

    // Update mini XP bar
    const xpBarMini = $('xp-bar-mini');
    const xpBar = $('xp-bar-fill');
    if (xpBar) {
        const currentLevelXP = getXPForLevel(xpData.level);
        const nextLevelXP = getXPForLevel(xpData.level + 1);
        const progress = ((xpData.totalXP - currentLevelXP) / (nextLevelXP - currentLevelXP)) * 100;
        xpBar.style.width = `${Math.min(progress, 100)}%`;
    }

    // Apply streak heat effect
    if (xpBarMini) {
        applyStreakHeat(xpBarMini, currentStreak);
    }

    const roadmapModal = $('level-roadmap-modal');
    if (roadmapModal?.classList.contains('show')) {
        const renderedLevel = Number(roadmapModal.dataset.levelRendered || 0);
        if (renderedLevel !== xpData.level) {
            renderLevelBadgeRoadmap();
        }
    }
}

const LEVEL_BADGE_ROADMAP_STOPS = [
    1, 6, 11, 16, 21, 31, 50, 100,
    125, 150, 175, 200, 225, 250,
    300, 350, 400, 450, 500,
    600, 700, 800, 900, 1000
];

const LEVEL_BADGE_ROADMAP_LABELS = {
    1: 'Bronze Ember',
    6: 'Silver Pulse',
    11: 'Gold Nova',
    16: 'Platinum Crest',
    21: 'Diamond Arc',
    31: 'Master Flux',
    50: 'Grandmaster Crown',
    100: 'Legend Catalyst',
    125: 'Legend Pulse',
    150: 'Mythic Surge',
    175: 'Mythic Orbit',
    200: 'Immortal Gate',
    225: 'Immortal Halo',
    250: 'Immortal Zenith',
    300: 'Transcendence I',
    350: 'Transcendence II',
    400: 'Transcendence III',
    450: 'Transcendence IV',
    500: 'Transcendence Prime',
    600: 'Eternal Spark',
    700: 'Eternal Crown',
    800: 'Eternal Nova',
    900: 'Eternal Rift',
    1000: 'Omega Ascendant'
};

const LEVEL_BADGE_CLASSIC_PALETTES = {
    1: { b1: '#d99a47', b2: '#b16827', b3: '#7f4316', edge: '#ffd8aa', aura: 'rgba(255, 170, 77, 0.45)', flare: 'rgba(255, 218, 160, 0.72)', accent: 'rgba(255, 168, 92, 0.75)', phaseClass: 'phase-classic' },
    6: { b1: '#eceff3', b2: '#c8d0da', b3: '#8895a4', edge: '#ffffff', aura: 'rgba(210, 225, 245, 0.5)', flare: 'rgba(255, 255, 255, 0.75)', accent: 'rgba(217, 230, 247, 0.8)', phaseClass: 'phase-classic' },
    11: { b1: '#fff06d', b2: '#ffcf31', b3: '#c88a00', edge: '#fff7b0', aura: 'rgba(255, 218, 52, 0.55)', flare: 'rgba(255, 248, 165, 0.78)', accent: 'rgba(255, 228, 82, 0.82)', phaseClass: 'phase-classic' },
    16: { b1: '#f8fafc', b2: '#dbe6f3', b3: '#9fb0c5', edge: '#ffffff', aura: 'rgba(230, 240, 255, 0.55)', flare: 'rgba(255, 255, 255, 0.82)', accent: 'rgba(214, 230, 252, 0.84)', phaseClass: 'phase-classic' },
    21: { b1: '#ddfbff', b2: '#9de8ff', b3: '#2ea6dc', edge: '#effcff', aura: 'rgba(126, 220, 255, 0.55)', flare: 'rgba(220, 250, 255, 0.78)', accent: 'rgba(157, 231, 255, 0.82)', phaseClass: 'phase-classic' },
    31: { b1: '#e78cff', b2: '#b94fff', b3: '#5d1daa', edge: '#f6c7ff', aura: 'rgba(196, 95, 255, 0.58)', flare: 'rgba(246, 190, 255, 0.8)', accent: 'rgba(213, 130, 255, 0.84)', phaseClass: 'phase-classic' },
    50: { b1: '#ffe95f', b2: '#f7bc1e', b3: '#986700', edge: '#fff6ad', aura: 'rgba(255, 212, 62, 0.6)', flare: 'rgba(255, 248, 175, 0.82)', accent: 'rgba(255, 218, 86, 0.86)', phaseClass: 'phase-classic' },
    100: { b1: '#ff8c36', b2: '#ff4a1f', b3: '#a3152f', edge: '#ffd1b0', aura: 'rgba(255, 82, 48, 0.62)', flare: 'rgba(255, 186, 142, 0.82)', accent: 'rgba(255, 116, 86, 0.88)', phaseClass: 'phase-classic' },
    125: { b1: '#ffc35f', b2: '#ff7e2a', b3: '#7d1f24', edge: '#ffdca9', aura: 'rgba(255, 141, 54, 0.62)', flare: 'rgba(255, 215, 148, 0.85)', accent: 'rgba(255, 152, 92, 0.9)', phaseClass: 'phase-ignite' },
    150: { b1: '#f6a4ff', b2: '#d04bff', b3: '#6f17b3', edge: '#fdd8ff', aura: 'rgba(217, 88, 255, 0.64)', flare: 'rgba(250, 196, 255, 0.86)', accent: 'rgba(228, 133, 255, 0.9)', phaseClass: 'phase-ignite' },
    175: { b1: '#9df6ff', b2: '#4ec9ff', b3: '#0051a2', edge: '#c9faff', aura: 'rgba(90, 214, 255, 0.64)', flare: 'rgba(190, 245, 255, 0.86)', accent: 'rgba(116, 222, 255, 0.9)', phaseClass: 'phase-ignite' },
    200: { b1: '#84f9ff', b2: '#38bdf8', b3: '#0f3e98', edge: '#d7fcff', aura: 'rgba(58, 194, 255, 0.68)', flare: 'rgba(193, 248, 255, 0.88)', accent: 'rgba(98, 222, 255, 0.92)', phaseClass: 'phase-orbit' },
    225: { b1: '#99d9ff', b2: '#4c7dff', b3: '#2d2f92', edge: '#d3e7ff', aura: 'rgba(92, 138, 255, 0.66)', flare: 'rgba(204, 226, 255, 0.86)', accent: 'rgba(131, 166, 255, 0.9)', phaseClass: 'phase-orbit' },
    250: { b1: '#96e2ff', b2: '#497dff', b3: '#1f3c90', edge: '#d4f2ff', aura: 'rgba(92, 158, 255, 0.68)', flare: 'rgba(208, 240, 255, 0.88)', accent: 'rgba(126, 194, 255, 0.92)', phaseClass: 'phase-orbit' },
    300: { b1: '#8ab5ff', b2: '#5556ff', b3: '#2b2f8c', edge: '#d4dcff', aura: 'rgba(112, 120, 255, 0.66)', flare: 'rgba(214, 220, 255, 0.84)', accent: 'rgba(141, 153, 255, 0.9)', phaseClass: 'phase-orbit' },
    350: { b1: '#9b96ff', b2: '#6e49ff', b3: '#3d1e8c', edge: '#dddbff', aura: 'rgba(129, 103, 255, 0.66)', flare: 'rgba(225, 218, 255, 0.84)', accent: 'rgba(162, 147, 255, 0.9)', phaseClass: 'phase-orbit' },
    400: { b1: '#c8a0ff', b2: '#9859ff', b3: '#52228f', edge: '#eedaff', aura: 'rgba(166, 99, 255, 0.66)', flare: 'rgba(239, 215, 255, 0.86)', accent: 'rgba(202, 151, 255, 0.9)', phaseClass: 'phase-cosmic' },
    450: { b1: '#f3a0ff', b2: '#d74cff', b3: '#6a1d89', edge: '#ffd8ff', aura: 'rgba(220, 94, 255, 0.67)', flare: 'rgba(255, 209, 255, 0.88)', accent: 'rgba(241, 152, 255, 0.92)', phaseClass: 'phase-cosmic' },
    500: { b1: '#a5b8ff', b2: '#8a7bff', b3: '#4f3ea8', edge: '#e1e6ff', aura: 'rgba(141, 131, 255, 0.7)', flare: 'rgba(224, 229, 255, 0.9)', accent: 'rgba(170, 180, 255, 0.92)', phaseClass: 'phase-cosmic' },
    600: { b1: '#9ec6ff', b2: '#7b8dff', b3: '#4e4aa8', edge: '#d7e5ff', aura: 'rgba(131, 151, 255, 0.7)', flare: 'rgba(214, 229, 255, 0.9)', accent: 'rgba(160, 182, 255, 0.92)', phaseClass: 'phase-cosmic' },
    700: { b1: '#c6a8ff', b2: '#a16dff', b3: '#5f32a8', edge: '#eedeff', aura: 'rgba(167, 113, 255, 0.7)', flare: 'rgba(243, 222, 255, 0.9)', accent: 'rgba(196, 150, 255, 0.92)', phaseClass: 'phase-cosmic' },
    800: { b1: '#ffb0e6', b2: '#e275ff', b3: '#7a3fa8', edge: '#ffdff4', aura: 'rgba(229, 121, 255, 0.7)', flare: 'rgba(255, 224, 245, 0.9)', accent: 'rgba(246, 166, 235, 0.92)', phaseClass: 'phase-cosmic' },
    900: { b1: '#ffc6b5', b2: '#ff8f96', b3: '#8a4a7f', edge: '#ffe7df', aura: 'rgba(255, 149, 171, 0.7)', flare: 'rgba(255, 231, 222, 0.9)', accent: 'rgba(255, 190, 182, 0.92)', phaseClass: 'phase-cosmic' },
    1000: { b1: '#f2d0b5', b2: '#c79ca9', b3: '#6f5b87', edge: '#fff0df', aura: 'rgba(210, 170, 214, 0.7)', flare: 'rgba(255, 240, 222, 0.9)', accent: 'rgba(232, 204, 188, 0.92)', phaseClass: 'phase-cosmic' }
};

function getLevelRoadmapBand(startLevel) {
    if (startLevel >= 500) return 'ascend100';
    if (startLevel >= 250) return 'ascend50';
    if (startLevel >= 100) return 'ascend25';
    return 'classic';
}

const LEVEL_BADGE_ROADMAP = LEVEL_BADGE_ROADMAP_STOPS.map((startLevel, idx, allLevels) => {
    const nextStart = allLevels[idx + 1];
    return {
        key: `lv-${startLevel}`,
        name: LEVEL_BADGE_ROADMAP_LABELS[startLevel] || `Level ${startLevel}`,
        min: startLevel,
        max: Number.isFinite(nextStart) ? nextStart - 1 : null,
        band: getLevelRoadmapBand(startLevel),
        glowSeed: idx
    };
});

const LEVEL_ROADMAP_BATCH_SIZE = 4;
let levelRoadmapEntryHTMLCache = [];
let levelRoadmapRenderedCount = 0;
let levelRoadmapFxObserver = null;
let levelRoadmapFxObserverRoot = null;

function wireLevelRoadmapDynamicFX(gridEl) {
    if (!gridEl) return;

    const cards = gridEl.querySelectorAll('.level-roadmap-card:not([data-fx-observed="1"])');
    if (!cards.length) return;

    if (!('IntersectionObserver' in window)) {
        cards.forEach((card) => {
            card.dataset.fxObserved = '1';
            card.classList.add('is-live');
        });
        return;
    }

    if (!levelRoadmapFxObserver || levelRoadmapFxObserverRoot !== gridEl) {
        if (levelRoadmapFxObserver) levelRoadmapFxObserver.disconnect();

        levelRoadmapFxObserver = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                const isLive = entry.isIntersecting && entry.intersectionRatio >= 0.18;
                entry.target.classList.toggle('is-live', isLive);
            });
        }, {
            root: gridEl,
            rootMargin: '120px 0px',
            threshold: [0, 0.18, 0.4, 0.65]
        });

        levelRoadmapFxObserverRoot = gridEl;
    }

    cards.forEach((card) => {
        card.dataset.fxObserved = '1';
        levelRoadmapFxObserver.observe(card);
    });
}

function appendLevelRoadmapBatch() {
    const gridEl = $('level-roadmap-grid');
    if (!gridEl) return;
    if (levelRoadmapRenderedCount >= levelRoadmapEntryHTMLCache.length) return;

    const nextCount = Math.min(
        levelRoadmapRenderedCount + LEVEL_ROADMAP_BATCH_SIZE,
        levelRoadmapEntryHTMLCache.length
    );
    const nextChunk = levelRoadmapEntryHTMLCache.slice(levelRoadmapRenderedCount, nextCount).join('');
    gridEl.insertAdjacentHTML('beforeend', nextChunk);
    levelRoadmapRenderedCount = nextCount;
    wireLevelRoadmapDynamicFX(gridEl);
}

function bindLevelRoadmapScrollLoader(gridEl) {
    if (!gridEl || gridEl.dataset.lazyBound === '1') return;

    gridEl.addEventListener('scroll', () => {
        const threshold = 220;
        const nearBottom = gridEl.scrollTop + gridEl.clientHeight >= gridEl.scrollHeight - threshold;
        if (nearBottom) appendLevelRoadmapBatch();
    }, { passive: true });

    gridEl.dataset.lazyBound = '1';
}

function formatLevelBadgeRange(entry) {
    if (!entry) return 'Lv ?';
    if (entry.max === null) return `Lv ${entry.min}+`;
    return `Lv ${entry.min}-${entry.max}`;
}

function getLevelBadgeTierEntryForLevel(level) {
    const numericLevel = Number(level);
    const safeLevel = Number.isFinite(numericLevel) ? numericLevel : 1;
    for (const entry of LEVEL_BADGE_ROADMAP) {
        if (safeLevel >= entry.min && (entry.max === null || safeLevel <= entry.max)) {
            return entry;
        }
    }
    return LEVEL_BADGE_ROADMAP[LEVEL_BADGE_ROADMAP.length - 1];
}

function getLevelBadgeVisual(entry) {
    const level = entry?.min || 1;
    const classicPalette = LEVEL_BADGE_CLASSIC_PALETTES[level];
    if (classicPalette) return classicPalette;

    if (entry?.band === 'ascend25') {
        const step = Math.max(0, Math.round((level - 100) / 25));
        const hue = (8 + step * 31) % 360;
        return {
            b1: `hsl(${hue} 100% 64%)`,
            b2: `hsl(${(hue + 34) % 360} 96% 52%)`,
            b3: `hsl(${(hue + 76) % 360} 90% 36%)`,
            edge: `hsl(${(hue + 14) % 360} 100% 78%)`,
            aura: `hsla(${hue} 100% 58% / 0.58)`,
            flare: `hsla(${(hue + 60) % 360} 100% 70% / 0.78)`,
            accent: `hsla(${hue} 100% 70% / 0.88)`,
            phaseClass: 'phase-ignite'
        };
    }

    if (entry?.band === 'ascend50') {
        const step = Math.max(0, Math.round((level - 250) / 50));
        const hue = (168 + step * 26) % 360;
        return {
            b1: `hsl(${hue} 98% 66%)`,
            b2: `hsl(${(hue + 26) % 360} 92% 52%)`,
            b3: `hsl(${(hue + 58) % 360} 85% 34%)`,
            edge: `hsl(${(hue + 10) % 360} 100% 80%)`,
            aura: `hsla(${hue} 96% 58% / 0.6)`,
            flare: `hsla(${(hue + 48) % 360} 100% 72% / 0.75)`,
            accent: `hsla(${hue} 95% 72% / 0.88)`,
            phaseClass: 'phase-orbit'
        };
    }

    const step = Math.max(0, Math.round((level - 500) / 100));
    const hue = (225 + step * 22) % 360;
    return {
        b1: `hsl(${hue} 100% 72%)`,
        b2: `hsl(${(hue + 34) % 360} 94% 58%)`,
        b3: `hsl(${(hue + 78) % 360} 88% 37%)`,
        edge: `hsl(${(hue + 18) % 360} 100% 84%)`,
        aura: `hsla(${hue} 100% 65% / 0.62)`,
        flare: `hsla(${(hue + 54) % 360} 100% 75% / 0.82)`,
        accent: `hsla(${hue} 100% 76% / 0.92)`,
        phaseClass: 'phase-cosmic'
    };
}

function getLevelRoadmapFxClass(level) {
    if (level >= 500) return 'fx-mythic';
    if (level >= 200) return 'fx-legendary';
    if (level >= 100) return 'fx-epic';
    return 'fx-classic';
}

function getLevelRoadmapVariantClass(level) {
    const variants = {
        100: 'v-inferno',
        125: 'v-blazecore',
        150: 'v-phoenix',
        175: 'v-solarflare',
        200: 'v-thunder',
        225: 'v-storm',
        250: 'v-arc',
        300: 'v-plasma',
        350: 'v-void',
        400: 'v-prism',
        450: 'v-nebula',
        500: 'v-celestial',
        600: 'v-crownfire',
        700: 'v-supersonic',
        800: 'v-astral',
        900: 'v-rift',
        1000: 'v-omega'
    };
    return variants[level] || 'v-core';
}

// Get prestige tier based on level
function getLevelTier(level) {
    if (level >= 1000) return 'eternal';      // 1000+
    if (level >= 500) return 'transcendent';  // 500-999
    if (level >= 200) return 'immortal';      // 200-499
    if (level >= 150) return 'mythic';        // 150-199
    if (level >= 100) return 'legend';        // 100-149
    if (level >= 50) return 'grandmaster';    // 50-99
    if (level >= 31) return 'master';         // 31-49
    if (level >= 21) return 'diamond';        // 21-30
    if (level >= 16) return 'platinum';       // 16-20
    if (level >= 11) return 'gold';           // 11-15
    if (level >= 6) return 'silver';          // 6-10
    return 'bronze';                          // 1-5
}

// Apply tier class to level badge
function applyLevelTier(element, level) {
    const tiers = [
        'tier-bronze', 'tier-silver', 'tier-gold', 'tier-platinum',
        'tier-diamond', 'tier-master', 'tier-grandmaster', 'tier-legend',
        'tier-mythic', 'tier-immortal', 'tier-transcendent', 'tier-eternal'
    ];
    tiers.forEach(t => element.classList.remove(t));
    element.classList.add(`tier-${getLevelTier(level)}`);
}


// Apply streak heat effect to XP bar
function applyStreakHeat(element, streak) {
    const heatClasses = ['streak-warm', 'streak-hot', 'streak-fire', 'streak-inferno'];
    heatClasses.forEach(c => element.classList.remove(c));

    if (streak >= 25) {
        element.classList.add('streak-inferno');
    } else if (streak >= 15) {
        element.classList.add('streak-fire');
    } else if (streak >= 10) {
        element.classList.add('streak-hot');
    } else if (streak >= 5) {
        element.classList.add('streak-warm');
    }
}

// Confetti celebration for level up
function getLevelUpEffectsRoot() {
    let root = $('level-up-effects-root');
    if (!root) {
        root = document.createElement('div');
        root.id = 'level-up-effects-root';
        root.className = 'level-up-effects-root';
        document.body.appendChild(root);
    }
    return root;
}

function triggerConfetti() {
    const fxRoot = getLevelUpEffectsRoot();
    const container = document.createElement('div');
    container.className = 'confetti-container';
    fxRoot.appendChild(container);

    const colors = ['#ffd700', '#ff6b6b', '#4ecdc4', '#45b7d1', '#96ceb4', '#ffeaa7', '#dfe6e9', '#a29bfe'];
    const shapes = ['square', 'circle', 'ribbon'];

    // Create confetti pieces
    for (let i = 0; i < 80; i++) {
        const confetti = document.createElement('div');
        confetti.className = `confetti ${shapes[Math.floor(Math.random() * shapes.length)]}`;
        confetti.style.left = `${Math.random() * 100}%`;
        confetti.style.backgroundColor = colors[Math.floor(Math.random() * colors.length)];
        confetti.style.animationDelay = `${Math.random() * 0.5}s`;
        confetti.style.animationDuration = `${2 + Math.random() * 2}s`;
        container.appendChild(confetti);
    }

    // Remove after animation
    setTimeout(() => container.remove(), 4000);
}

// Flash effect for level up
function triggerLevelUpFlash() {
    const fxRoot = getLevelUpEffectsRoot();
    const flash = document.createElement('div');
    flash.className = 'level-up-flash';
    fxRoot.appendChild(flash);
    setTimeout(() => flash.remove(), 1000);
}

// Enhanced level up celebration with popup
function celebrateLevelUp(previousLevel = xpData.level - 1) {
    triggerLevelUpFlash();
    triggerConfetti();
    showLevelUpPopup(previousLevel);
}

// Show level up popup with animation
function showLevelUpPopup(previousLevel = xpData.level - 1) {
    const fxRoot = getLevelUpEffectsRoot();
    fxRoot.querySelectorAll('.level-up-popup').forEach(item=>item.remove());
    const oldLevel = Math.max(1,previousLevel), newLevel = xpData.level;
    const oldTier = getLevelTier(oldLevel), newTier = getLevelTier(newLevel);
    const popup = document.createElement('div');
    popup.className = 'level-up-popup';
    popup.setAttribute('role','status');
    popup.innerHTML = `
        <button type="button" class="level-up-close" aria-label="Close level-up notification">×</button>
        <div class="level-up-content">
            <div class="level-up-title">Level Up</div>
            <div class="level-up-transition"><span class="old-level tier-${oldTier}">${oldLevel}</span><span class="level-arrow">→</span><span class="new-level tier-${newTier}">${newLevel}</span></div>
            <div class="level-up-tier">${newTier.charAt(0).toUpperCase()+newTier.slice(1)}</div>
            <p class="level-up-bonus">${getLevelXPBonus(newLevel).toFixed(2)}× study XP</p>
            <button type="button" class="level-up-badges">See badges</button>
        </div>`;
    fxRoot.appendChild(popup);
    let dismissTimer=0;
    const dismiss=()=>{clearTimeout(dismissTimer);popup.remove();};
    const schedule=()=>{clearTimeout(dismissTimer);dismissTimer=setTimeout(dismiss,10000);};
    popup.querySelector('.level-up-close').onclick=dismiss;
    popup.querySelector('.level-up-badges').onclick=()=>{dismiss();openStatsModal();switchStatsTab('badges');$('stats-tab-badges')?.focus();};
    popup.addEventListener('pointerenter',()=>clearTimeout(dismissTimer));
    popup.addEventListener('pointerleave',()=>{if(!popup.contains(document.activeElement))schedule();});
    popup.addEventListener('focusin',()=>clearTimeout(dismissTimer));
    popup.addEventListener('focusout',event=>{if(!popup.contains(event.relatedTarget))schedule();});
    requestAnimationFrame(()=>popup.classList.add('visible'));
    schedule();
}

function toggleGameMode() {
    if (!window.isMemoraProductFeatureAllowed?.('game-mode')) return;
    isGameMode = !isGameMode;
    const btn = $('game-mode-toggle');

    if (isGameMode) {
        btn.classList.add('active');
        showPopup("Game Mode: ON", null, false);
    } else {
        btn.classList.remove('active');
        showPopup("Game Mode: OFF", null, false);
    }

    updateXPDisplay();
    localStorage.setItem('isGameMode', isGameMode);
    if (isGameMode) void claimDailyLoginRewardsIfStudied();
}

function loadGameModePreference() {
    const saved = localStorage.getItem('isGameMode');
    const allowed = window.isMemoraProductFeatureAllowed?.('game-mode') === true;
    isGameMode = allowed && saved === 'true';
    const btn = $('game-mode-toggle');
    if (btn) { btn.hidden = !allowed; btn.classList.toggle('active', isGameMode); }
    document.body?.classList.toggle('memora-game-mode-locked', !allowed);
}
['memora:api-key-saved', 'memora:api-key-removed'].forEach(eventName => window.addEventListener(eventName, () => {
    loadGameModePreference(); updateXPDisplay();
    window.dispatchEvent(new CustomEvent('memora:game-mode-changed'));
}));

// Smart Saver Mode - combines memory and performance tuning
function clearPdfTextIndexCache() {
    for (const key in pdfTextIndex) {
        delete pdfTextIndex[key];
    }
}

// options.keepVisible keeps entries an image on screen still shows (Smart Saver
// runs while the learner is looking); ending a session clears everything.
function clearImageBlobUrlCache(options = {}) {
    const keepVisible = options?.keepVisible === true;
    imageBlobUrlCacheGeneration += 1;
    imageBlobUrlLoads.clear();
    for (const [key, entry] of imageBlobUrlCache) {
        if (keepVisible && isImageBlobUrlOnScreen(entry.url)) continue;
        try { URL.revokeObjectURL(entry.url); } catch (e) { /* ignore */ }
        imageBlobUrlCache.delete(key);
    }
    transientImageBlobUrls.forEach((url) => {
        if (keepVisible && isImageBlobUrlOnScreen(url)) return;
        try { URL.revokeObjectURL(url); } catch (e) { /* ignore */ }
        transientImageBlobUrls.delete(url);
    });
}
function revokeLoadedPdfBlobUrls() {
    for (const [filename, blobUrl] of Object.entries(loadedPdfFiles || {})) {
        if (typeof blobUrl === 'string' && blobUrl.startsWith('blob:')) {
            try { URL.revokeObjectURL(blobUrl); } catch (e) { /* ignore */ }
        }
        delete loadedPdfFiles[filename];
    }
}
function clearPdfEvidenceTextCache() {
    currentPdfText = null;
    currentPdfTextKey = null;
    if (typeof pdfTextCache === 'object' && pdfTextCache) {
        for (const key in pdfTextCache) {
            delete pdfTextCache[key];
        }
    }
}
// options.endSession: the session's sources are going away (load, reset, wipe), so
// the PowerPoint viewer and question-image URLs go with the PDF state. Smart Saver
// leaves it off because the learner is still looking at the session.
function clearAllPdfRuntimeCaches(options = {}) {
    const { revokeBlobUrls = false, endSession = false } = options;
    destroyAdvancedPdfDocument();
    clearPdfTextIndexCache();
    clearPdfEvidenceTextCache();
    if (revokeBlobUrls) revokeLoadedPdfBlobUrls();
    if (endSession) {
        if (typeof destroyMemoraPptxViewer === 'function') destroyMemoraPptxViewer();
        window.MemoraPdfCompressor?.clearPptxPageCache?.();
        clearImageBlobUrlCache();
        // Ask's page text lives in the lazily loaded AI module; nothing to clear if it
        // never loaded.
        if (typeof clearAskMemoraPdfPageCache === 'function') clearAskMemoraPdfPageCache();
    }
}

async function pruneIndexedAICache(maxEntries = 24) {
    try {
        const keys = await IDB.getAllKeys('settings');
        const aiKeys = keys.filter((k) => typeof k === 'string' && k.startsWith('ai_cache_v2_'));
        if (aiKeys.length <= maxEntries) return 0;

        const keep = new Set();
        const activeKey = getAICacheKey(currentQIndex);
        if (activeKey) keep.add(activeKey);

        for (let i = aiKeys.length - 1; i >= 0 && keep.size < maxEntries; i--) {
            keep.add(aiKeys[i]);
        }

        let removed = 0;
        for (const key of aiKeys) {
            if (keep.has(key)) continue;
            await IDB.del('settings', key);
            removed++;
        }
        return removed;
    } catch (e) {
        console.warn('Smart Saver AI cache prune failed:', e);
        return 0;
    }
}

function syncSmartSaverButton() {
    const btn = $('memory-mode-toggle');
    if (!btn) return;
    btn.classList.toggle('active', isMemorySaver);

    const label = btn.querySelector('.gradient-text');
    if (label) label.textContent = '\uD83D\uDCBE Smart Saver';
    btn.title = isMemorySaver
        ? 'Smart Saver ON: lighter visuals and lower memory usage'
        : 'Smart Saver OFF: full visuals and PDF text search';
}

function applySmartSaverState(enabled, options = {}) {
    const {
        showNotice = false,
        reindexPdfText = false,
        trimAICache = true
    } = options;

    isMemorySaver = !!enabled;
    document.body.classList.toggle('perf-lite', isMemorySaver);
    syncAmbientVisualizerBodyFlags();
    syncSmartSaverButton();

    localStorage.setItem(SMART_SAVER_MODE_KEY, isMemorySaver ? 'true' : 'false');

    if (isMemorySaver) {
        clearAllPdfRuntimeCaches({ revokeBlobUrls: false });
        clearImageBlobUrlCache({ keepVisible: true });

        if (isSysSyncActive) {
            try { toggleSysSync(); } catch (e) { /* ignore */ }
        }

        if (trimAICache) {
            pruneIndexedAICache().then((removed) => {
                if (showNotice && removed > 0) {
                    showPopup(`Smart Saver trimmed ${removed} cached AI entries`, null, false);
                }
            });
        }
    } else if (reindexPdfText) {
        showPopup('Indexing PDFs... This may take a moment.', null, false);
        for (const [filename, blobUrl] of Object.entries(loadedPdfFiles)) {
            fetch(blobUrl)
                .then((r) => r.blob())
                .then((blob) => extractPdfText(blob, filename))
                .catch(() => { /* ignore */ });
        }
    }

    if (!isSysSyncActive) {
        const canvas = $('audio-visualizer');
        const ctx = canvas?.getContext('2d');
        if (ctx) ctx.clearRect(0, 0, canvas.width, canvas.height);
    }

    if (showNotice) {
        const icon = '\uD83D\uDCBE';
        if (isMemorySaver) showPopup(`${icon} Smart Saver: ON - lighter visuals + lower memory`, null, false);
        else showPopup(`${icon} Smart Saver: OFF - full visuals + PDF search`, null, false);
    }
}

function toggleMemorySaver() {
    // If currently ON and user wants full mode, show warning first.
    if (isMemorySaver) {
        showMemorySaverWarning();
        return;
    }

    // Turn Smart Saver ON immediately.
    applySmartSaverState(true, { showNotice: true, reindexPdfText: false, trimAICache: true });
}

function showMemorySaverWarning() {
    const overlay = document.createElement('div');
    overlay.id = 'memory-saver-modal';
    overlay.className = 'modal-overlay';
    overlay.style.cssText = `
        position: fixed; top: 0; left: 0; width: 100%; height: 100%;
        background: rgba(0,0,0,0.8); z-index: 10000;
        display: flex; align-items: center; justify-content: center;
    `;

    const modal = document.createElement('div');
    modal.className = 'modal-content';
    modal.style.cssText = `
        background: var(--card-bg, #1e293b);
        border: 1px solid rgba(255,255,255,0.1);
        border-radius: 16px; padding: 24px; max-width: 440px;
        box-shadow: 0 20px 60px rgba(0,0,0,0.5);
    `;

    modal.innerHTML = `
        <h3 class="gradient-text" style="margin: 0 0 12px 0; font-size: 1.1rem;">
            Smart Saver Warning
        </h3>
        <p style="color: var(--text-main, #e2e8f0); font-size: 0.95rem; line-height: 1.6; margin: 0 0 16px 0;">
            <strong>Disabling Smart Saver will:</strong>
        </p>
        <ul style="color: var(--text-sub, #94a3b8); font-size: 0.9rem; margin: 0 0 20px 0; padding-left: 20px; line-height: 1.8;">
            <li>Enable full animations and heavier effects</li>
            <li>Extract and index all PDF text into memory</li>
            <li>Increase memory pressure on large sessions</li>
            <li>Potentially slow weaker devices</li>
        </ul>
        <p style="color: var(--text-sub, #64748b); font-size: 0.85rem; margin: 0 0 20px 0;">
            Recommended: keep Smart Saver ON unless you actively need PDF text search.
        </p>
        <div style="display: flex; gap: 12px; justify-content: flex-end;">
            <button id="memory-cancel-btn" class="glass-btn" style="padding: 8px 16px; border-radius: 8px; cursor: pointer; font-weight: 600;">
                <span class="gradient-text">Keep Smart Saver ON</span>
            </button>
            <button id="memory-confirm-btn" class="glass-btn" style="padding: 8px 16px; border-radius: 8px; cursor: pointer; font-weight: 600;">
                <span class="gradient-text">Disable Smart Saver</span>
            </button>
        </div>
    `;

    overlay.appendChild(modal);
    document.body.appendChild(overlay);

    overlay.onclick = (e) => { if (e.target === overlay) overlay.remove(); };
    modal.querySelector('#memory-cancel-btn').onclick = () => overlay.remove();
    modal.querySelector('#memory-confirm-btn').onclick = () => {
        overlay.remove();
        applySmartSaverState(false, { showNotice: true, reindexPdfText: true, trimAICache: false });
    };
}

function loadMemorySaverPreference() {
    const saved = localStorage.getItem(SMART_SAVER_MODE_KEY);
    // Default to ON when unset.
    const shouldEnable = saved !== 'false';
    applySmartSaverState(shouldEnable, { showNotice: false, reindexPdfText: false, trimAICache: false });
}

const AI_MODEL_CHOICE_KEY = 'memora_ai_model_choice_enabled';
const AI_BATCH_PERFORMANCE_CHOICE_KEY = 'memora_ai_batch_performance_enabled';
const QUIZ_GEN_PROMPT_CUSTOMIZATION_PREF_KEY = 'memora_quiz_prompt_customization_enabled';
const QUIZ_GEN_CORE_ONBOARDING_KEY = 'memora_quiz_gen_core_setup_seen_v1';

let quizGenQuestionTranscriptionPending = false;
const QUIZ_GEN_STEM_TRANSCRIPTION_WORD_DELAY_MS = 25;
const QUIZ_GEN_CHOICE_TRANSCRIPTION_STEP_DELAY_MS = 90;

function requestQuizGenQuestionTranscription() {
    if (document.documentElement.classList.contains('phone-device')) return false;
    quizGenQuestionTranscriptionPending = true;
    return true;
}

function consumeQuizGenQuestionTranscription() {
    const shouldAnimate = quizGenQuestionTranscriptionPending;
    quizGenQuestionTranscriptionPending = false;
    return shouldAnimate;
}

function cancelQuizGenQuestionTranscription() {
    quizGenQuestionTranscriptionPending = false;
    document.querySelectorAll('.stem-word-transcribe, .choice-transcribe-enter').forEach((element) => {
        element.classList.remove('stem-word-transcribe', 'choice-transcribe-enter');
        element.style.removeProperty('animation-delay');
    });
}

window.requestQuizGenQuestionTranscription = requestQuizGenQuestionTranscription;
window.cancelQuizGenQuestionTranscription = cancelQuizGenQuestionTranscription;

function getAiModelChoiceEnabled() {
    // Model selection is always available in Create on supported devices.
    return true;
}

function setAiModelChoiceEnabled(enabled, { silent = false } = {}) {
    const isEnabled = !!enabled;
    try {
        localStorage.setItem(AI_MODEL_CHOICE_KEY, isEnabled ? 'true' : 'false');
    } catch (_err) { }

    document.body.classList.toggle('ai-model-choice-enabled', isEnabled);
    document.body.classList.toggle('ai-model-choice-disabled', !isEnabled);

    const btn = $('ai-model-choice-toggle');
    if (btn) {
        btn.classList.toggle('active', isEnabled);
        btn.setAttribute('aria-pressed', isEnabled ? 'true' : 'false');
    }
    const label = $('ai-model-choice-label');
    if (label) {
        label.textContent = `\u2699\uFE0F Model Choice: ${isEnabled ? 'On' : 'Off'}`;
    }

    if (typeof window.syncQuizGenModelChoiceUi === 'function') {
        window.syncQuizGenModelChoiceUi(isEnabled);
    }

    if (!silent) {
        showPopup(`Model choice ${isEnabled ? 'enabled' : 'disabled'}`, null, false);
    }
}

function toggleAiModelChoice() {
    setAiModelChoiceEnabled(!getAiModelChoiceEnabled());
}

function loadAiModelChoicePreference() {
    setAiModelChoiceEnabled(true, { silent: true });
}

function setMemoraFastModelModeEnabled(enabled, { silent = false } = {}) {
    const wasEnabled = isMemoraFastModelModeEnabled();
    const requestedEnabled = !!enabled;
    try {
        localStorage.setItem(MEMORA_FAST_MODEL_MODE_KEY, requestedEnabled ? 'true' : 'false');
    } catch (_err) { }

    const isEnabled = isMemoraFastModelModeEnabled();
    document.body.classList.toggle('ai-fast-model-mode-enabled', isEnabled);
    document.body.classList.toggle('ai-fast-model-mode-disabled', !isEnabled);

    const btn = $('ai-fast-model-mode-toggle');
    if (btn) {
        btn.classList.toggle('active', isEnabled);
        btn.setAttribute('aria-pressed', isEnabled ? 'true' : 'false');
        btn.title = isEnabled
            ? 'Fast Mode uses low-latency models for supported AI features'
            : 'Use low-latency models for supported AI features';
    }
    const label = $('ai-fast-model-mode-label');
    if (label) {
        label.textContent = `\u26A1 Fast Mode: ${isEnabled ? 'On' : 'Off'}`;
    }

    if (typeof window.syncQuizGenModelChoiceUi === 'function') {
        window.syncQuizGenModelChoiceUi(null);
    }
    if (!silent && wasEnabled !== isEnabled
        && typeof setGeminiLiveThinkingLevelPreference === 'function'
        && typeof getGeminiLiveThinkingLevelPreference === 'function') {
        setGeminiLiveThinkingLevelPreference(getGeminiLiveThinkingLevelPreference());
    }

    if (!silent && !document.documentElement.classList.contains('phone-device')) {
        showPopup(`Fast Mode ${isEnabled ? 'enabled' : 'disabled'}`, null, false);
    }
}

function toggleMemoraFastModelMode() {
    if (document.documentElement.classList.contains('phone-device')) return;
    setMemoraFastModelModeEnabled(!isMemoraFastModelModeEnabled());
}

function loadMemoraFastModelModePreference() {
    setMemoraFastModelModeEnabled(getMemoraFastModelModePreference(), { silent: true });
}

function getQuizGenPromptCustomizationEnabled() {
    try {
        return localStorage.getItem(QUIZ_GEN_PROMPT_CUSTOMIZATION_PREF_KEY) === 'true';
    } catch (_err) {
        return false;
    }
}

function setQuizGenPromptCustomizationEnabled(enabled, { silent = false } = {}) {
    const isEnabled = !!enabled;
    try {
        localStorage.setItem(QUIZ_GEN_PROMPT_CUSTOMIZATION_PREF_KEY, isEnabled ? 'true' : 'false');
    } catch (_err) { }

    document.body.classList.toggle('quiz-gen-prompt-customization-enabled', isEnabled);
    document.body.classList.toggle('quiz-gen-prompt-customization-disabled', !isEnabled);

    const btn = $('quiz-gen-prompt-customization-toggle');
    if (btn) {
        btn.classList.toggle('active', isEnabled);
        btn.setAttribute('aria-pressed', isEnabled ? 'true' : 'false');
    }
    const label = $('quiz-gen-prompt-customization-label');
    if (label) {
        label.textContent = `Prompt Customization: ${isEnabled ? 'On' : 'Off'}`;
    }

    if (typeof window.syncQuizGenPromptCustomizationUi === 'function') {
        window.syncQuizGenPromptCustomizationUi(isEnabled);
    }

    if (!silent) {
        showPopup(`Prompt customization ${isEnabled ? 'enabled' : 'disabled'}`, null, false);
    }
}

function toggleQuizGenPromptCustomization() {
    setQuizGenPromptCustomizationEnabled(!getQuizGenPromptCustomizationEnabled());
}

function loadQuizGenPromptCustomizationPreference() {
    setQuizGenPromptCustomizationEnabled(true, { silent: true });
}

function getAiBatchPerformanceChoiceEnabled() {
    try {
        return localStorage.getItem(AI_BATCH_PERFORMANCE_CHOICE_KEY) === 'true';
    } catch (_err) {
        return false;
    }
}

function setAiBatchPerformanceChoiceEnabled(enabled, { silent = false } = {}) {
    const isEnabled = !!enabled;
    try {
        localStorage.setItem(AI_BATCH_PERFORMANCE_CHOICE_KEY, isEnabled ? 'true' : 'false');
    } catch (_err) { }

    document.body.classList.toggle('ai-batch-performance-enabled', isEnabled);
    document.body.classList.toggle('ai-batch-performance-disabled', !isEnabled);

    const btn = $('ai-batch-performance-toggle');
    if (btn) {
        btn.classList.toggle('active', isEnabled);
        btn.setAttribute('aria-pressed', isEnabled ? 'true' : 'false');
    }
    const label = $('ai-batch-performance-label');
    if (label) {
        label.textContent = `\u2699\uFE0F Batch Performance: ${isEnabled ? 'On' : 'Off'}`;
    }

    if (typeof window.syncQuizGenBatchPerformanceChoiceUi === 'function') {
        window.syncQuizGenBatchPerformanceChoiceUi(isEnabled);
    }

    if (!silent) {
        showPopup(`Batch performance controls ${isEnabled ? 'enabled' : 'disabled'}`, null, false);
    }
}

function toggleAiBatchPerformanceChoice() {
    setAiBatchPerformanceChoiceEnabled(!getAiBatchPerformanceChoiceEnabled());
}

function loadAiBatchPerformancePreference() {
    setAiBatchPerformanceChoiceEnabled(getAiBatchPerformanceChoiceEnabled(), { silent: true });
}

function persistAccessibilityPreferences() {
    try {
        localStorage.setItem(ACCESSIBILITY_PREFS_KEY, JSON.stringify(accessibilityPrefs));
    } catch (_err) { }
}

function getAccessibilityPreferencesSnapshot() {
    return {
        ...DEFAULT_ACCESSIBILITY_PREFS,
        ...(accessibilityPrefs && typeof accessibilityPrefs === 'object' ? accessibilityPrefs : {}),
        largeText: !!accessibilityPrefs.largeText
    };
}

function isAccessibilityVoiceAgentEnabled() {
    /* A saved preference is not product access. Under the free-tier limit Voice stays off however
     * the switch was left, so a stale preference cannot open the microphone or a Live socket. */
    return !!accessibilityPrefs.voiceAgent && isMemoraProductFeatureAllowed('voice-agent');
}

function isAccessibilityManualImportEnabled() {
    return !!accessibilityPrefs.manualImport;
}

(function initializeMemoraVoicePresence(global) {
    const documentRef = global.document;
    // Astral Veil geometry supplied by the user, driven by the existing audio frame.
const STATES = {
  ready: {
    label:"Ready", desc:"Warm, calm, present", status:"Ready Â· calm astral hold",
    tint:[214,168,138], accent:[235,202,178], edge:[248,223,207],
    spread:.18, fold:.24, pulse:.16, scan:0, phase:.12
  },
  listening: {
    label:"Listening", desc:"Open, receptive, attentive", status:"Listening Â· opening outward",
    tint:[202,176,150], accent:[230,209,185], edge:[246,232,214],
    spread:.42, fold:.26, pulse:.26, scan:.04, phase:.24
  },
  thinking: {
    label:"Thinking", desc:"Layered, inward, processing", status:"Thinking Â· folds compress inward",
    tint:[191,158,150], accent:[224,201,191], edge:[242,225,218],
    spread:.22, fold:.48, pulse:.14, scan:0, phase:.18
  },
  lookup: {
    label:"Looking it up", desc:"Focused, searching, resolving", status:"Looking it up Â· directional sweep",
    tint:[186,174,148], accent:[221,211,185], edge:[240,233,215],
    spread:.30, fold:.32, pulse:.20, scan:.42, phase:.30
  },
  speaking: {
    label:"Speaking", desc:"Expressive, confident, clear", status:"Speaking Â· rhythmic astral pulse",
    tint:[224,154,128], accent:[243,198,177], edge:[253,226,210],
    spread:.30, fold:.30, pulse:.58, scan:0, phase:.42
  },
  connecting: {
    tint:[183,173,172], accent:[222,210,200], edge:[243,234,222],
    spread:.20, fold:.30, pulse:.15, scan:.22, phase:.18
  },
  paused: {
    tint:[169,169,171], accent:[205,202,200], edge:[230,227,224],
    spread:.14, fold:.22, pulse:0, scan:0, phase:0
  },
  error: {
    tint:[204,137,141], accent:[228,178,178], edge:[246,213,209],
    spread:.16, fold:.36, pulse:.12, scan:0, phase:0
  }
};

function mix(a,b,t){return a+(b-a)*t}
function mixRGB(a,b,t){return [mix(a[0],b[0],t),mix(a[1],b[1],t),mix(a[2],b[2],t)]}
function rgba(rgb,a){return `rgba(${rgb.map(v=>Math.round(v)).join(",")},${a})`}
function clamp(v,min,max){return Math.max(min,Math.min(max,v))}
function smoothstep(a,b,x){const t=clamp((x-a)/(b-a),0,1);return t*t*(3-2*t)}
function easeInOutCubic(t){return t<.5?4*t*t*t:1-Math.pow(-2*t+2,3)/2}

let currentState='ready', previousState='ready', transition=1, burst=1, last=0, energy=0;
    let presenceNodes = [];
    let presenceScanAt = 0;
    let themeScanAt = 0;
    let themePalette = { tint: [214,168,138], accent: [235,202,178], key: '' };
    function readPresenceColor(value, fallback) {
        const raw = String(value || '').trim();
        const hex = raw.match(/^#([0-9a-f]{6}|[0-9a-f]{3})$/i);
        if (hex) {
            const digits = hex[1].length === 3 ? hex[1].split('').map((c) => c + c).join('') : hex[1];
            return [0, 2, 4].map((at) => parseInt(digits.slice(at, at + 2), 16));
        }
        const rgb = raw.match(/rgba?\(\s*(\d+)[,\s]+(\d+)[,\s]+(\d+)/i);
        return rgb ? rgb.slice(1, 4).map(Number) : fallback;
    }
    function speechEnvelope(time) { return Math.pow(.5-.5*Math.cos(time*Math.PI*2),2)*(.35+energy*.45); }
function lerpState(t){
  const A=STATES[previousState],B=STATES[currentState];
  return {
    tint:mixRGB(A.tint,B.tint,t),accent:mixRGB(A.accent,B.accent,t),edge:mixRGB(A.edge,B.edge,t),
    spread:mix(A.spread,B.spread,t),fold:mix(A.fold,B.fold,t),pulse:mix(A.pulse,B.pulse,t),
    scan:mix(A.scan,B.scan,t),phase:mix(A.phase,B.phase,t)
  };
}

function astralPath(ctx, radius, time, s, depth, burstAmp, small){
  const points=small?72:112;
  const breath=Math.sin(time*.72)*.012;
  ctx.beginPath();
  for(let i=0;i<=points;i++){
    const a=i/points*Math.PI*2;
    const r=radius*(1+Math.cos(a*4)*(.055+s.spread*.055)+Math.cos(a*8)*.006+breath);
    const x=Math.cos(a)*r,y=Math.sin(a)*r;
    if(i===0)ctx.moveTo(x,y);else ctx.lineTo(x,y);
  }
  ctx.closePath();
}

function drawPresence(ctx,w,h,time,{small=false,icon=false,detailed=false}={}){
  ctx.clearRect(0,0,w,h);
  const s=lerpState(easeInOutCubic(transition));
  if(currentState==='paused')time=0;
  const speech=currentState==='speaking'?speechEnvelope(time):0;
  const tint=mixRGB(themePalette.tint,s.tint,.23);
  const accent=mixRGB(themePalette.accent,s.accent,.28);
  const edge=mixRGB(themePalette.accent,s.edge,.68);
  const R=Math.min(w,h)*.33*(1+(s.spread-.18)*.10);
  const cx=w*.5,cy=h*.5;
  ctx.save();
  ctx.translate(cx,cy);
  // Every state change shares one eased pulse. Busy states share one calm motion;
  // speech breathes visibly even between syllables, with a small lift from audio energy.
  const blend=easeInOutCubic(transition);
  const busyState=state=>['connecting','thinking','lookup'].includes(state)?1:0;
  const busy=mix(busyState(previousState),busyState(currentState),blend);
  const talking=mix(previousState==='speaking'?1:0,currentState==='speaking'?1:0,blend);
  const breathing=Math.sin(time*Math.PI*2/1.55)*(.095+energy*.035)*talking;
  const pulse=Math.sin(Math.PI*transition)*.045;
  const scale=1+pulse+breathing+Math.sin(time*Math.PI*2/2.4)*.035*busy;
  ctx.scale(scale,scale);
  ctx.rotate(Math.sin(time*.9)*.13*busy);
  ctx.globalAlpha=currentState==='paused'?.42:1;

  const halo=ctx.createRadialGradient(0,0,R*.12,0,0,R*1.40);
  halo.addColorStop(0,rgba(tint,.09+speech*.025));
  halo.addColorStop(.62,rgba(tint,.025));
  halo.addColorStop(1,rgba(tint,0));
  ctx.fillStyle=halo;
  ctx.fillRect(-cx,-cy,w,h);

  // One clear silhouette; only larger orbs need faint inner contours.
  const layers=detailed?5:icon?1:small?2:3;
  for(let i=layers-1;i>=0;i--){
    const d=i/Math.max(1,layers-1),rr=R*(.82+d*.18);
    astralPath(ctx,rr,time,s,d,0,small);
    if(i===layers-1){
      const fill=ctx.createRadialGradient(-R*.12,-R*.16,0,0,0,R);
      fill.addColorStop(0,rgba(accent,.16+speech*.035));
      fill.addColorStop(.52,rgba(tint,.075));
      fill.addColorStop(1,rgba(tint,.012));
      ctx.fillStyle=fill;ctx.fill();
    }
    ctx.strokeStyle=rgba(edge,i===layers-1?.48+speech*.10:.11);
    ctx.lineWidth=icon?.8:small?.9:1.05;
    ctx.stroke();
  }

  const core=ctx.createRadialGradient(0,0,0,0,0,R*.33);
  core.addColorStop(0,rgba(accent,.20+speech*.06));
  core.addColorStop(.45,rgba(tint,.06));core.addColorStop(1,rgba(tint,0));
  ctx.fillStyle=core;ctx.beginPath();ctx.arc(0,0,R*.33,0,Math.PI*2);ctx.fill();
  ctx.restore();
}

    /* Beside the Ask composer the voice is a plain mic with four small level bars. The bars follow
     * the student's microphone while listening and the tutor's audio while it speaks, sit as dots
     * when there is nothing to hear, and roll in a soft wave while a reply is being prepared. */
    const dockBarHeights = [0, 0, 0, 0];
    let dockMicTarget = 0, dockMicAt = 0;
    function setDockMicLevel(rms) {
        // Speech RMS runs about .01-.15: the square root spreads it and the offset drops room noise.
        dockMicTarget = Math.min(1, Math.max(0, Math.sqrt(Math.max(0, Number(rms) || 0)) - .07) * 3.2);
        dockMicAt = performance.now();
    }
    function drawDockPresence(ctx, w, h, time, level, live) {
        ctx.clearRect(0, 0, w, h);
        const blend = easeInOutCubic(transition);
        const weight = (state) => (previousState === state ? 1 - blend : 0) + (currentState === state ? blend : 0);
        const busy = weight('connecting') + weight('thinking') + weight('lookup');
        const listening = weight('listening'), speaking = weight('speaking');
        const paused = weight('paused'), failed = weight('error');
        const white = [255,255,255];
        const ink = mixRGB(themePalette.tint, white, .62);
        const barInk = mixRGB(themePalette.accent, white, .3);
        const pill = (x, y, bw, bh, r) => {
            ctx.beginPath();
            if (ctx.roundRect) ctx.roundRect(x, y, bw, bh, r); else ctx.rect(x, y, bw, bh);
            ctx.fill();
        };
        const cy = h / 2, mx = Math.min(9, w * .24);
        ctx.save();
        ctx.globalAlpha = 1 - failed * .45;
        // The mic: a capsule over its cradle and stand, with a slash across it while paused.
        ctx.fillStyle = rgba(ink, .94); ctx.strokeStyle = rgba(ink, .94);
        ctx.lineWidth = 1.6; ctx.lineCap = 'round';
        pill(mx - 3, cy - 8, 6, 10, 3);
        ctx.beginPath(); ctx.arc(mx, cy - 2, 5.4, 0, Math.PI); ctx.stroke();
        ctx.beginPath(); ctx.moveTo(mx, cy + 3.4); ctx.lineTo(mx, cy + 6.6);
        ctx.moveTo(mx - 3, cy + 6.6); ctx.lineTo(mx + 3, cy + 6.6); ctx.stroke();
        if (paused > .01) {
            ctx.strokeStyle = rgba(ink, .94 * paused);
            ctx.beginPath(); ctx.moveTo(mx - 6, cy - 7); ctx.lineTo(mx + 6, cy + 7); ctx.stroke();
        }
        if (live && performance.now() - dockMicAt > 160) dockMicTarget *= .7;
        const drive = live ? Math.max(listening * dockMicTarget, speaking * Math.min(1, Math.max(0, level || 0))) : 0;
        const barW = 2.6, gap = 2.6, maxH = h - 6, x0 = w - 4 * barW - 3 * gap - 3;
        const shape = [.62, 1, .84, .55];
        for (let i = 0; i < 4; i++) {
            const flicker = .72 + .28 * Math.sin(time * (9 + i * 2.3) + i * 1.9);
            const wave = busy * (.18 + .14 * Math.sin(time * Math.PI * 2 / 1.1 - i * .8));
            const target = Math.max(drive * shape[i] * flicker, wave);
            const current = dockBarHeights[i];
            dockBarHeights[i] = live ? current + (target - current) * (target > current ? .55 : .22) : target;
            const barH = Math.max(barW, dockBarHeights[i] * maxH);
            ctx.fillStyle = rgba(barInk, Math.max(.2, .5 + .45 * Math.min(1, dockBarHeights[i] * 2.2) - paused * .3));
            pill(x0 + i * (barW + gap), cy - barH / 2, barW, barH, barW / 2);
        }
        ctx.restore();
    }

    /* `force` is for a surface that has just become visible: it skips the frame throttle and rescans
     * the orbs, so the veil is there on the first painted frame rather than the next loop tick. */
    function renderPresence(level = 0, now = performance.now(), { force = false } = {}) {
        if (documentRef.hidden || (!force && now - last < 33)) return;
        const dt = last ? Math.min(.1, Math.max(0, (now - last) / 1000)) : 0;
        last = Math.max(last, now);
        if (force || !presenceNodes.length || now - presenceScanAt > 1000) {
            presenceNodes = Array.from(documentRef.querySelectorAll('#memora-voice-orb-trigger .memora-voice-orb, #voice-agent-modal .memora-voice-orb, #ask-ai-input-voice-dock .memora-voice-orb, #memora-live-rail-profile .memora-voice-footer-orb'));
            presenceScanAt = now;
        }
        if (force || now - themeScanAt > 400) {
            const colors = global.getComputedStyle(documentRef.body);
            const tint = readPresenceColor(colors.getPropertyValue('--g1'), [214,168,138]);
            const accent = readPresenceColor(colors.getPropertyValue('--g2'), [235,202,178]);
            themePalette = { tint, accent, key: `${tint.join(',')}:${accent.join(',')}` };
            themeScanAt = now;
        }
        // Reader audio (voice previews, read-aloud) is speech outside any Live turn, so it outranks the Live state.
        const modal = documentRef.getElementById('voice-agent-modal');
        const state = modal?.dataset.readerState === 'speaking' ? 'speaking' : (modal?.dataset.liveState || 'ready');
        const next = STATES[state] ? state : 'ready';
        if (next !== currentState) { previousState=currentState; currentState=next; transition=0; burst=0; }
        const reduced = !!global.MemoraMotion?.isReduced?.();
        const targetEnergy = reduced || currentState !== 'speaking' ? 0 : Math.min(1, Math.max(0, level));
        energy = reduced ? 0 : energy + (targetEnergy-energy)*(1-Math.exp(-dt/.5));
        transition = reduced ? 1 : Math.min(1, transition + dt / 1.5);
        burst = reduced ? 1 : Math.min(1, burst + dt / 1.3);
        for (const orb of presenceNodes) {
            const lockedPresence = !!orb.closest('#memora-live-rail-profile[data-memora-locked]');
            const staticPresence = reduced || lockedPresence;
            if (!orb.isConnected || !orb.getClientRects().length) continue;
            const footerStatus = orb.parentElement?.querySelector?.('.memora-voice-footer-status');
            const activity = documentRef.getElementById('memora-voice-orb-activity');
            if (footerStatus) {
                const connected = global.MemoraVoiceAgent?.getState?.()?.connected === true;
                const reconnecting = footerStatus.dataset.voiceState === 'reconnecting';
                const label = reconnecting ? 'Reconnecting' : (connected ? (activity?.textContent || 'Ready') : 'Idle');
                if (!connected && !reconnecting) footerStatus.dataset.voiceState = 'idle';
                if (footerStatus.textContent !== label) footerStatus.textContent = label;
                if (activity && activity.textContent !== label) activity.textContent = label;
                if (activity && activity.dataset.voiceState !== footerStatus.dataset.voiceState) {
                    activity.dataset.voiceState = footerStatus.dataset.voiceState;
                }
                const footerButton = orb.closest('.memora-voice-footer-controls')?.querySelector('.memora-voice-footer-connect');
                if (footerButton) {
                    const footerVoiceLabel = connected ? 'Disconnect Voice Agent' : 'Connect Voice Agent';
                    // This runs every presence frame. Inside the rail the tooltip guard moves a title
                    // into data-memora-tooltip, so re-setting title here fought it ~40 times a second
                    // and woke every rail observer: write the rail's attribute, and only on change.
                    const tipAttribute = footerButton.closest('#controls-bar') ? 'data-memora-tooltip' : 'title';
                    if (footerButton.getAttribute('aria-label') !== footerVoiceLabel) footerButton.setAttribute('aria-label', footerVoiceLabel);
                    if (lockedPresence) footerButton.removeAttribute('title');
                    else if (footerButton.getAttribute(tipAttribute) !== footerVoiceLabel) footerButton.setAttribute(tipAttribute, footerVoiceLabel);
                    const connectLabel = footerButton.querySelector('.memora-live-rail-row-label');
                    const connectText = connected ? 'Disconnect' : 'Connect';
                    if (connectLabel && connectLabel.textContent !== connectText) connectLabel.textContent = connectText;
                }
            }
            const size = orb.getBoundingClientRect();
            if (!size.width || !size.height) continue;
            let canvas = orb.querySelector('canvas');
            if (!canvas) {
                canvas = documentRef.createElement('canvas');
                canvas.setAttribute('aria-hidden', 'true');
                orb.appendChild(canvas);
                orb.classList.add('memora-astral-veil');
            }
            const dpr = Math.min(global.devicePixelRatio || 1, 2);
            const width = Math.round(size.width * dpr), height = Math.round(size.height * dpr);
            if (canvas.width !== width || canvas.height !== height) { canvas.width=width; canvas.height=height; }
            const staticKey = `${currentState}:${themePalette.key}:${width}:${height}`;
            if (staticPresence && canvas.dataset.presenceFrame === staticKey) continue;
            canvas.dataset.presenceFrame = staticPresence ? staticKey : '';
            const ctx = canvas.getContext('2d');
            if (!ctx) continue;
            ctx.setTransform(dpr,0,0,dpr,0,0);
            if (orb.closest('#ask-ai-input-voice-dock')) drawDockPresence(ctx, size.width, size.height, staticPresence ? 0 : now * .001, staticPresence ? 0 : level, !staticPresence);
            else drawPresence(ctx,size.width,size.height,staticPresence ? 0 : now*.001,{small:size.width<80,icon:size.width<40,detailed:!!orb.closest('#voice-agent-modal.voice-transcript-enlarged')});
        }
    }

    global.MemoraVoicePresence = Object.freeze({ render: renderPresence, setMicLevel: setDockMicLevel, isTransitioning: () => transition < 1 });
    renderPresence(0, performance.now());
}(window));

const VOICE_ORB_IDLE_TIMEOUT_MS = 1500;
const VOICE_ORB_DEFAULT_SHIFT = 0.5;
let voiceOrbLevel = 0;
let voiceOrbTarget = 0;
let voiceOrbShift = VOICE_ORB_DEFAULT_SHIFT;
let voiceOrbShiftTarget = VOICE_ORB_DEFAULT_SHIFT;
let voiceOrbRafId = 0;
let voiceOrbLastSignalAt = 0;
let voiceOrbPhase = 0;
let voiceOrbLastFrameTs = 0;

function clampVoiceOrbValue(value, fallback = 0) {
    const parsed = Number(value);
    if (!Number.isFinite(parsed)) return fallback;
    return Math.min(1, Math.max(0, parsed));
}

function applyVoiceOrbCss(level, shift) {
    // The canvas Astra owns its motion. Inherited variables on <html> would invalidate
    // every app surface on every frame, even while Voice is only listening.
    if (window.MemoraVoicePresence) {
        document.body?.classList.toggle('voice-orb-speaking',
            document.body.classList.contains('voice-agent-active') && clampVoiceOrbValue(level) > 0.08);
        return;
    }
    const root = document.documentElement;
    const energy = clampVoiceOrbValue(level);
    const expressiveEnergy = Math.min(1, energy * 2.45);
    const drift = (shift - VOICE_ORB_DEFAULT_SHIFT) * 9;
    const paceSeconds = Math.max(1.8, 6.5 - (expressiveEnergy * 3.9));
    const glow = 0.22 + (expressiveEnergy * 0.72);
    const motion = 0.18 + (expressiveEnergy * 1.78);
    const swirlRadius = 3 + (expressiveEnergy * 19.2);
    const swirlX = Math.sin(voiceOrbPhase) * swirlRadius;
    const swirlY = Math.cos(voiceOrbPhase * 0.86) * (swirlRadius * 0.82);
    const mix = 0.26 + (expressiveEnergy * 1.52);
    const spin = voiceOrbPhase * 122;
    const counterSpin = -voiceOrbPhase * 98;
    root.style.setProperty('--voice-orb-level', level.toFixed(3));
    root.style.setProperty('--voice-orb-shift', shift.toFixed(3));
    root.style.setProperty('--voice-orb-shift-px', `${drift.toFixed(2)}px`);
    root.style.setProperty('--voice-orb-pace', `${paceSeconds.toFixed(2)}s`);
    root.style.setProperty('--voice-orb-glow', glow.toFixed(3));
    root.style.setProperty('--voice-orb-motion', motion.toFixed(3));
    root.style.setProperty('--voice-orb-swirl-x', `${swirlX.toFixed(2)}px`);
    root.style.setProperty('--voice-orb-swirl-y', `${swirlY.toFixed(2)}px`);
    root.style.setProperty('--voice-orb-mix', mix.toFixed(3));
    root.style.setProperty('--voice-orb-spin', `${spin.toFixed(2)}deg`);
    root.style.setProperty('--voice-orb-counter-spin', `${counterSpin.toFixed(2)}deg`);
    document.body?.classList.toggle('voice-orb-speaking',
        document.body.classList.contains('voice-agent-active') && energy > 0.08);
}

function runVoiceOrbFrame(timestamp = 0) {
    const liveConnected = window.MemoraVoiceAgent?.getState?.()?.connected === true;
    const readerSpeaking = document.getElementById('voice-agent-modal')?.dataset.readerState === 'speaking';
    const transitioning = window.MemoraVoicePresence?.isTransitioning?.() === true;
    if (!document.body.classList.contains('voice-agent-active') && !liveConnected && !readerSpeaking && !transitioning) {
        voiceOrbLevel = 0;
        voiceOrbTarget = 0;
        voiceOrbShift = VOICE_ORB_DEFAULT_SHIFT;
        voiceOrbShiftTarget = VOICE_ORB_DEFAULT_SHIFT;
        voiceOrbPhase = 0;
        voiceOrbLastFrameTs = 0;
        applyVoiceOrbCss(0, VOICE_ORB_DEFAULT_SHIFT);
        voiceOrbRafId = 0;
        return;
    }

    const now = Number(timestamp) || performance.now();
    const dt = voiceOrbLastFrameTs > 0 ? Math.min(48, Math.max(8, now - voiceOrbLastFrameTs)) : 16;
    voiceOrbLastFrameTs = now;

    voiceOrbLevel += (voiceOrbTarget - voiceOrbLevel) * 0.11;
    voiceOrbShift += (voiceOrbShiftTarget - voiceOrbShift) * 0.055;
    voiceOrbLevel = clampVoiceOrbValue(voiceOrbLevel);
    voiceOrbShift = clampVoiceOrbValue(voiceOrbShift, VOICE_ORB_DEFAULT_SHIFT);
    const millisSinceSignal = Date.now() - voiceOrbLastSignalAt;
    const idleFalloff = millisSinceSignal > VOICE_ORB_IDLE_TIMEOUT_MS ? 0.86 : 1;
    const motionEnergy = Math.min(1, Math.max(0.08, Math.max(voiceOrbLevel, voiceOrbTarget * idleFalloff)) * 2.4);
    const phaseSpeed = 0.0021 + (motionEnergy * 0.027);
    voiceOrbPhase = (voiceOrbPhase + (dt * phaseSpeed)) % (Math.PI * 2);
    applyVoiceOrbCss(voiceOrbLevel, voiceOrbShift);
    window.MemoraVoicePresence?.render?.(voiceOrbLevel, now);

    voiceOrbRafId = requestAnimationFrame(runVoiceOrbFrame);
}

/* The veil animates from the moment Voice is on, restored or switched on, not from the first
 * microphone frame. runVoiceOrbFrame ends the loop by itself once Voice is off. */
function startMemoraVoiceOrbLoop() {
    if (!voiceOrbRafId) voiceOrbRafId = requestAnimationFrame(runVoiceOrbFrame);
}

function setMemoraVoiceOrbSignal({ level = 0, shift = VOICE_ORB_DEFAULT_SHIFT } = {}) {
    const nextLevel = clampVoiceOrbValue(Math.min(1, Number(level || 0) * 2.34));
    const nextShift = clampVoiceOrbValue(shift, VOICE_ORB_DEFAULT_SHIFT);
    voiceOrbTarget += (nextLevel - voiceOrbTarget) * 0.5;
    voiceOrbShiftTarget += (nextShift - voiceOrbShiftTarget) * 0.16;
    voiceOrbLastSignalAt = Date.now();
    if (!voiceOrbRafId) {
        voiceOrbRafId = requestAnimationFrame(runVoiceOrbFrame);
    }
}

function stopMemoraVoiceOrbSignal() {
    voiceOrbTarget = 0;
    voiceOrbShiftTarget = VOICE_ORB_DEFAULT_SHIFT;
    voiceOrbLastSignalAt = Date.now();
    if (!voiceOrbRafId) {
        voiceOrbRafId = requestAnimationFrame(runVoiceOrbFrame);
    }
}

window.setMemoraVoiceOrbSignal = setMemoraVoiceOrbSignal;
window.stopMemoraVoiceOrbSignal = stopMemoraVoiceOrbSignal;

function notifyAccessibilityPreferencesChanged() {
    try {
        window.dispatchEvent(new CustomEvent('memora:accessibility-preferences-changed', {
            detail: getAccessibilityPreferencesSnapshot()
        }));
    } catch (_err) { }
}

window.getAccessibilityPreferences = getAccessibilityPreferencesSnapshot;
window.isAccessibilityVoiceAgentEnabled = isAccessibilityVoiceAgentEnabled;
window.isAccessibilityManualImportEnabled = isAccessibilityManualImportEnabled;

function syncAccessibilityButton(buttonId, enabled, labelText, onTitle, offTitle) {
    const btn = $(buttonId);
    if (!btn) return;
    btn.classList.toggle('active', !!enabled);
    btn.setAttribute('aria-pressed', enabled ? 'true' : 'false');
    btn.title = enabled ? onTitle : offTitle;
    const label = btn.querySelector('.gradient-text');
    if (label) label.textContent = `${labelText}${enabled ? ': On' : ': Off'}`;
}

function applyAccessibilityPreferences({ showNotice = false } = {}) {
    document.body.classList.toggle('a11y-large-text', !!accessibilityPrefs.largeText);
    document.body.classList.toggle('a11y-high-contrast', !!accessibilityPrefs.highContrast);
    document.body.classList.toggle('a11y-reduce-motion', !!accessibilityPrefs.reduceMotion);
    syncMemoraMotionMode();
    document.body.classList.toggle('voice-agent-active', !!accessibilityPrefs.voiceAgent);
    if (!accessibilityPrefs.voiceAgent) {
        window.stopMemoraVoiceOrbSignal?.();
    } else {
        startMemoraVoiceOrbLoop();
    }
    syncAccessibilityButton(
        'accessibility-voice-agent-btn',
        accessibilityPrefs.voiceAgent,
        '\uD83D\uDD0A Voice Agent',
        '\uD83D\uDD0A Voice Agent is on',
        '\uD83D\uDD0A Voice Agent is off'
    );
    syncAccessibilityButton(
        'accessibility-manual-import-btn',
        accessibilityPrefs.manualImport,
        '\uD83D\uDEE0\uFE0F Manual Import',
        '\uD83D\uDEE0\uFE0F Manual Import is on',
        '\uD83D\uDEE0\uFE0F Manual Import is off'
    );
    syncAccessibilityButton(
        'accessibility-high-contrast-btn',
        accessibilityPrefs.highContrast,
        '🌗 High Contrast',
        '🌗 High Contrast is on',
        '🌗 High Contrast is off'
    );
    syncAccessibilityButton(
        'accessibility-reduce-motion-btn',
        accessibilityPrefs.reduceMotion,
        '🎞️ Reduce Motion',
        '🎞️ Reduce Motion is on',
        '🎞️ Reduce Motion is off'
    );

    if (showNotice) {
        const enabledStates = [];
        if (accessibilityPrefs.largeText) enabledStates.push('Larger Text');
        if (accessibilityPrefs.highContrast) enabledStates.push('🌗 High Contrast');
        if (accessibilityPrefs.reduceMotion) enabledStates.push('🎞️ Reduce Motion');
        if (accessibilityPrefs.voiceAgent) enabledStates.push('Voice Agent');
        if (accessibilityPrefs.manualImport) enabledStates.push('Manual Import');
        const summary = enabledStates.length ? enabledStates.join(', ') : 'all accessibility toggles off';
        showPopup(`Accessibility updated: ${summary}`, null, false);
    }
    notifyAccessibilityPreferencesChanged();
}

function setAccessibilityPreference(key, nextValue, options = {}) {
    if (key === 'voiceAgent' && nextValue && typeof canStartGeminiLiveWithPatientVoice === 'function'
        && !canStartGeminiLiveWithPatientVoice()) {
        applyAccessibilityPreferences({ ...options, showNotice: false });
        return;
    }
    accessibilityPrefs[key] = !!nextValue;
    persistAccessibilityPreferences();
    applyAccessibilityPreferences(options);
    if (key === 'voiceAgent' && !accessibilityPrefs[key]) {
        try {
            window.disconnectGeminiLiveConversation?.({ keepMessages: true });
        } catch (_err) { }
    }
}

function toggleAccessibilityPreference(key) {
    const current = !!accessibilityPrefs[key];
    setAccessibilityPreference(key, !current, { showNotice: true });
}

function toggleAccessibilityVoiceAgentPreference() {
    // The product limit comes before the key prompt: a free profile has no Voice to set a key up for.
    if (!accessibilityPrefs.voiceAgent && !requireMemoraProductFeature('voice-agent')) return false;
    /* Voice runs on the student's own Gemini key; an anonymous relay session cannot open the Live
     * socket. Block the switch here so the failure is one clear prompt rather than a dead orb. */
    if (!accessibilityPrefs.voiceAgent
        && typeof window.requireMemoraVoiceByokKey === 'function'
        && !window.requireMemoraVoiceByokKey('accessibility-toggle')) {
        return false;
    }
    toggleAccessibilityPreference('voiceAgent');
    return !!accessibilityPrefs.voiceAgent;
}

function setAccessibilityVoiceAgentPreference(enabled, options = {}) {
    // Turning Voice on is the restricted feature; turning it off is always allowed.
    if (enabled && !requireMemoraProductFeature('voice-agent')) return false;
    setAccessibilityPreference('voiceAgent', !!enabled, options);
    return !!accessibilityPrefs.voiceAgent;
}

window.toggleAccessibilityVoiceAgentPreference = toggleAccessibilityVoiceAgentPreference;
window.setAccessibilityVoiceAgentPreference = setAccessibilityVoiceAgentPreference;

function bindAccessibilityMenuButtons() {
    const contrastBtn = $('accessibility-high-contrast-btn');
    if (contrastBtn && !contrastBtn.dataset.bound) {
        contrastBtn.dataset.bound = 'true';
        contrastBtn.onclick = () => toggleAccessibilityPreference('highContrast');
    }

    const motionBtn = $('accessibility-reduce-motion-btn');
    if (motionBtn && !motionBtn.dataset.bound) {
        motionBtn.dataset.bound = 'true';
        motionBtn.onclick = () => toggleAccessibilityPreference('reduceMotion');
    }

    const voiceAgentBtn = $('accessibility-voice-agent-btn');
    if (voiceAgentBtn && !voiceAgentBtn.dataset.bound) {
        voiceAgentBtn.dataset.bound = 'true';
        voiceAgentBtn.onclick = () => toggleAccessibilityVoiceAgentPreference();
    }

    const manualImportBtn = $('accessibility-manual-import-btn');
    if (manualImportBtn && !manualImportBtn.dataset.bound) {
        manualImportBtn.dataset.bound = 'true';
        manualImportBtn.onclick = () => toggleAccessibilityPreference('manualImport');
    }

}

function loadAccessibilityPreferences() {
    try {
        const saved = JSON.parse(localStorage.getItem(ACCESSIBILITY_PREFS_KEY) || '{}');
        accessibilityPrefs = {
            ...DEFAULT_ACCESSIBILITY_PREFS,
            ...(saved && typeof saved === 'object' ? saved : {})
        };
    } catch (_err) {
        accessibilityPrefs = { ...DEFAULT_ACCESSIBILITY_PREFS };
    }
    // Voice Agent never survives a reload: each visit starts with it off, whatever was saved.
    accessibilityPrefs.voiceAgent = false;
    applyAccessibilityPreferences({ showNotice: false });
}
// --- ACHIEVEMENTS PANEL ---
function openAchievementsModal() {
    const modal = $('achievements-modal');
    if (modal) {
        modal.classList.add('show');
        renderAchievementsGrid();
    }
}

function closeAchievementsModal(e) {
    const modal = $('achievements-modal');
    if (!modal) return;
    modal.classList.remove('show');
}

function handleLevelBadgeRoadmapKeydown(e) {
    if (!e || (e.key !== 'Enter' && e.key !== ' ')) return;
    e.preventDefault();
    openLevelBadgeRoadmapModal();
}

function openLevelBadgeRoadmapModal() {
    const modal = $('level-roadmap-modal');
    if (!modal) return;
    const gridEl = $('level-roadmap-grid');
    if (gridEl) gridEl.scrollTop = 0;
    modal.dataset.levelRendered = '';
    renderLevelBadgeRoadmap();
    modal.classList.add('fx-boot');
    modal.classList.add('show');
    requestAnimationFrame(() => {
        requestAnimationFrame(() => {
            modal.classList.remove('fx-boot');
        });
    });
}

function closeLevelBadgeRoadmapModal(e) {
    const modal = $('level-roadmap-modal');
    if (!modal) return;
    modal.classList.remove('show');
    modal.classList.remove('fx-boot');
    modal.dataset.levelRendered = '';
    if (levelRoadmapFxObserver) levelRoadmapFxObserver.disconnect();
    levelRoadmapFxObserverRoot = null;
}

function renderLevelBadgeRoadmap() {
    const gridEl = $('level-roadmap-grid');
    if (!gridEl) return;
    bindLevelRoadmapScrollLoader(gridEl);

    const currentLevelEl = $('level-roadmap-current-level');
    const currentTierEl = $('level-roadmap-current-tier');
    const nextTierEl = $('level-roadmap-next-tier');
    const unlockedTierCountEl = $('level-roadmap-unlocked-count');
    const roadmapModal = $('level-roadmap-modal');

    const safeLevel = Math.max(1, Number(xpData.level) || 1);
    const currentTier = getLevelBadgeTierEntryForLevel(safeLevel);
    const nextTier = LEVEL_BADGE_ROADMAP.find((entry) => entry.min > safeLevel) || null;
    const unlockedTiers = LEVEL_BADGE_ROADMAP.filter((entry) => safeLevel >= entry.min).length;

    if (currentLevelEl) currentLevelEl.textContent = `Lv ${safeLevel}`;
    if (currentTierEl) {
        currentTierEl.textContent = currentTier
            ? `${currentTier.name} (${formatLevelBadgeRange(currentTier)})`
            : 'Unknown';
    }
    if (nextTierEl) {
        nextTierEl.textContent = nextTier
            ? `${nextTier.name} at Lv ${nextTier.min}`
            : 'Max tier reached';
    }
    if (unlockedTierCountEl) {
        unlockedTierCountEl.textContent = `${unlockedTiers}/${LEVEL_BADGE_ROADMAP.length}`;
    }

    levelRoadmapEntryHTMLCache = LEVEL_BADGE_ROADMAP.map((entry) => {
        const isCurrent = currentTier && currentTier.key === entry.key;
        const isUnlocked = safeLevel >= entry.min;
        const stateClass = isCurrent ? 'current' : (isUnlocked ? 'unlocked' : 'locked');
        const stateLabel = isCurrent ? 'Current' : (isUnlocked ? 'Unlocked' : 'Locked');
        const startRequirement = `Unlocks at Lv ${entry.min}`;
        const fxClass = getLevelRoadmapFxClass(entry.min);
        const variantClass = getLevelRoadmapVariantClass(entry.min);
        const cadenceNote = entry.min >= 500
            ? '100-level cadence'
            : entry.min >= 250
                ? '50-level cadence'
                : entry.min >= 100
                    ? '25-level cadence'
                    : 'Foundational tier';
        const visual = getLevelBadgeVisual(entry);
        const styleVars = [
            `--road-b1:${visual.b1}`,
            `--road-b2:${visual.b2}`,
            `--road-b3:${visual.b3}`,
            `--road-edge:${visual.edge}`,
            `--road-aura:${visual.aura}`,
            `--road-flare:${visual.flare}`,
            `--road-accent:${visual.accent}`
        ].join(';');

        return `
            <div class="level-roadmap-card ${stateClass} ${visual.phaseClass} ${fxClass} ${variantClass} band-${entry.band} ms-${entry.min}" style="${styleVars}">
                <div class="level-roadmap-main">
                    <span class="xp-level-badge level-roadmap-badge">Lv ${entry.min}</span>
                    <div class="level-roadmap-info">
                        <div class="level-roadmap-name">${entry.name}</div>
                        <div class="level-roadmap-range">${formatLevelBadgeRange(entry)}</div>
                        <div class="level-roadmap-requirement">${startRequirement} - ${cadenceNote}</div>
                    </div>
                </div>
                <span class="level-roadmap-state">${stateLabel}</span>
            </div>
        `;
    });

    levelRoadmapRenderedCount = 0;
    if (levelRoadmapFxObserver) levelRoadmapFxObserver.disconnect();
    levelRoadmapFxObserverRoot = null;
    gridEl.innerHTML = '';
    appendLevelRoadmapBatch();

    // Defer extra initial chunks until after first paint to avoid open hitch.
    requestAnimationFrame(() => {
        if (gridEl.scrollHeight <= gridEl.clientHeight + 24) appendLevelRoadmapBatch();
    });
    requestAnimationFrame(() => {
        if (gridEl.scrollHeight <= gridEl.clientHeight + 24) appendLevelRoadmapBatch();
    });

    if (roadmapModal) {
        roadmapModal.dataset.levelRendered = String(safeLevel);
    }
}

// Achievement descriptions for the panel
const ACHIEVEMENT_DESCRIPTIONS = {
    'first-answer': 'Submit your first answer',
    'getting-started': 'Submit 25 answers',
    '100-questions': 'Submit 100 answers',
    '500-questions': 'Submit 500 answers',
    '1000-questions': 'Submit 1,000 answers',
    '2500-questions': 'Submit 2,500 answers',
    '5000-questions': 'Submit 5,000 answers',

    'streak-5': 'Reach a 5-question correct streak',
    'streak-10': 'Reach a 10-question correct streak',
    'streak-25': 'Reach a 25-question correct streak',
    'streak-40': 'Reach a 40-question correct streak',
    'streak-50': 'Reach a 50-question correct streak',
    'streak-75': 'Reach a 75-question correct streak',
    'streak-100': 'Reach a 100-question correct streak',
    'streak-150': 'Reach a 150-question correct streak',
    'streak-200': 'Reach a 200-question correct streak',

    'tempo-5': 'Get 5 correct answers under 10 seconds each in a row',
    'tempo-15': 'Get 15 correct answers under 10 seconds each in a row',
    'tempo-30': 'Get 30 correct answers under 10 seconds each in a row',

    'level-10': 'Reach Level 10',
    'level-25': 'Reach Level 25',
    'level-50': 'Reach Level 50',

    'dedicated': 'Study on 3 consecutive days',
    'week-warrior': 'Study on 7 consecutive days',
    'fortnight-force': 'Study on 14 consecutive days',
    'month-master': 'Study on 30 consecutive days',
    'cadence-90': 'Study on 90 consecutive days',
    'night-owl': 'Answer at least one question after 10 PM',
    'early-bird': 'Answer at least one question before 7 AM',

    'mastery-3': 'Earn 3 mastery units through quizzes or verified mode-specific study evidence',
    'mastery-10': 'Earn 10 mastery units through quizzes or verified mode-specific study evidence',
    'mastery-25': 'Earn 25 mastery units through quizzes or verified mode-specific study evidence',
    'mastery-50': 'Earn 50 mastery units through quizzes or verified mode-specific study evidence',

    'resilient': 'Complete 3 recovery loops after miss streaks',
    'resilient-10': 'Complete 10 recovery loops after miss streaks',
    'deep-diver': 'Open Sources 20 times',
    'evidence-master': 'Open Sources 100 times',
    'precision-10': 'Hit 10 first-try correct answers in a row',
    'flawless-25': 'Hit 25 first-try correct answers in a row',
    'precision-40': 'Hit 40 first-try correct answers in a row',

    'perfect-session': 'Finish a session at 100% with at least 5 answered',
    'marathon': 'Answer at least 50 questions in one session',
    'ultra-marathon': 'Answer at least 100 questions in one session',

    'apex-ascendant': 'Reach 10,000 answers, Level 100, 90 study days, a 150 streak and 300 source views'
};

function getLevelMilestoneFromAchievementId(id) {
    if (typeof id !== 'string' || !id.startsWith('level-')) return null;
    const parsed = Number(id.slice('level-'.length));
    if (!Number.isFinite(parsed) || parsed <= 0) return null;
    return parsed;
}

function getAchievementDescription(id) {
    const direct = ACHIEVEMENT_DESCRIPTIONS[id];
    if (direct) return direct;
    const levelMilestone = getLevelMilestoneFromAchievementId(id);
    if (levelMilestone) return `Reach Level ${levelMilestone}`;
    return '';
}

// One quiet achievement card, shared by the archive and the results screen.
function renderAchievementCard(entry, options = {}) {
    const { id, ach, isUnlocked } = entry;
    const progress = isUnlocked ? null : getAchievementProgress(id);
    const pct = progress ? Math.max(0, Math.min(100, Math.round((progress.current / progress.target) * 100))) : 0;
    const status = options.isNew
        ? 'New'
        : (isUnlocked
            ? (entry.unlockTs ? formatAchievementTimestamp(entry.unlockTs, false) : 'Unlocked')
            : (progress ? `${Math.min(progress.current, progress.target)}/${progress.target}` : 'Locked'));
    return `
        <article class="gm-ach ${isUnlocked ? 'is-unlocked' : 'is-locked'}${options.isNew ? ' is-new' : ''}" data-rarity="${entry.rarity}">
            <span class="gm-ach-mark">${achievementGlyph(ach)}</span>
            <span class="gm-ach-body">
                <span class="gm-ach-name">${escapeHtml(ach.name)}</span>
                <span class="gm-ach-desc">${escapeHtml(getAchievementDescription(id))}</span>
                ${progress ? `<span class="gm-ach-bar" aria-hidden="true"><i style="width: ${pct}%"></i></span>` : ''}
                <span class="gm-ach-meta"><span>${escapeHtml(status)}</span><span>+${getAchievementMementos(ach)} Mementos</span></span>
            </span>
        </article>
    `;
}

// Get progress for milestone achievements
function getAchievementProgress(id) {
    switch (id) {
        case 'first-answer': return { current: totalQuestionsAnswered, target: 1 };
        case 'getting-started': return { current: totalQuestionsAnswered, target: 25 };
        case '100-questions': return { current: totalQuestionsAnswered, target: 100 };
        case '500-questions': return { current: totalQuestionsAnswered, target: 500 };
        case '1000-questions': return { current: totalQuestionsAnswered, target: 1000 };
        case '2500-questions': return { current: totalQuestionsAnswered, target: 2500 };
        case '5000-questions': return { current: totalQuestionsAnswered, target: 5000 };

        case 'streak-5': return { current: currentStreak, target: 5 };
        case 'streak-10': return { current: currentStreak, target: 10 };
        case 'streak-25': return { current: currentStreak, target: 25 };
        case 'streak-40': return { current: currentStreak, target: 40 };
        case 'streak-50': return { current: currentStreak, target: 50 };
        case 'streak-75': return { current: currentStreak, target: 75 };
        case 'streak-100': return { current: currentStreak, target: 100 };
        case 'streak-150': return { current: currentStreak, target: 150 };
        case 'streak-200': return { current: currentStreak, target: 200 };

        case 'tempo-5': return { current: quickAnswerStreak || 0, target: 5 };
        case 'tempo-15': return { current: quickAnswerStreak || 0, target: 15 };
        case 'tempo-30': return { current: quickAnswerStreak || 0, target: 30 };

        case 'dedicated': return { current: studyDays || 0, target: 3 };
        case 'week-warrior': return { current: studyDays || 0, target: 7 };
        case 'fortnight-force': return { current: studyDays || 0, target: 14 };
        case 'month-master': return { current: studyDays || 0, target: 30 };
        case 'cadence-90': return { current: studyDays || 0, target: 90 };

        case 'resilient': return { current: resilienceCount || 0, target: 3 };
        case 'resilient-10': return { current: resilienceCount || 0, target: 10 };
        case 'deep-diver': return { current: evidenceViews || 0, target: 20 };
        case 'evidence-master': return { current: evidenceViews || 0, target: 100 };
        case 'precision-10': return { current: flawlessStreak || 0, target: 10 };
        case 'flawless-25': return { current: flawlessStreak || 0, target: 25 };
        case 'precision-40': return { current: flawlessStreak || 0, target: 40 };

        case 'apex-ascendant': {
            let checkpoints = 0;
            if (totalQuestionsAnswered >= 10000) checkpoints++;
            if (xpData.level >= 1000) checkpoints++;
            if (studyDays >= 90) checkpoints++;
            if (currentStreak >= 150) checkpoints++;
            if (evidenceViews >= 300) checkpoints++;
            return { current: checkpoints, target: 5 };
        }

        default: {
            const levelMilestone = getLevelMilestoneFromAchievementId(id);
            if (levelMilestone) return { current: xpData.level, target: levelMilestone };
            return null;
        }
    }
}

function getAchievementUnlockTimestamp(id) {
    const raw = achievementsData[id];
    if (typeof raw === 'number' && Number.isFinite(raw) && raw > 0) {
        return raw;
    }
    if (typeof raw === 'string') {
        const numericValue = Number(raw);
        if (Number.isFinite(numericValue) && numericValue > 0) {
            return numericValue;
        }
        const parsedDate = Date.parse(raw);
        if (!Number.isNaN(parsedDate)) {
            return parsedDate;
        }
    }
    return null;
}

function formatAchievementTimestamp(ts, includeTime = true) {
    if (!ts) return 'Date unavailable';
    const d = new Date(ts);
    if (Number.isNaN(d.getTime())) return 'Date unavailable';
    if (includeTime) {
        return d.toLocaleString(undefined, {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit'
        });
    }
    return d.toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
    });
}

function renderAchievementsGrid() {
    const grid = $('achievements-grid');
    const unlockedCountEl = $('ach-unlocked-count');
    const totalCountEl = $('ach-total-count');
    const completionRateEl = $('ach-completion-rate');
    const latestUnlockEl = $('ach-latest-unlock');
    const historyListEl = $('achievements-history-list');

    if (!grid) return;

    const achievements = Object.entries(ACHIEVEMENTS);
    const orderById = {};
    achievements.forEach(([id], idx) => { orderById[id] = idx; });

    const entries = achievements.map(([id, ach]) => {
        const isUnlocked = !!achievementsData[id];
        const unlockTs = getAchievementUnlockTimestamp(id);
        const rarity = ACHIEVEMENT_RARITY_ORDER[ach.rarity] !== undefined ? ach.rarity : 'common';
        return {
            id,
            ach,
            isUnlocked,
            unlockTs,
            rarity,
            order: orderById[id] ?? 0
        };
    });

    const unlockedCount = entries.filter((entry) => entry.isUnlocked).length;
    const completionRate = entries.length > 0 ? Math.round((unlockedCount / entries.length) * 100) : 0;
    const latestUnlock = entries
        .filter((entry) => entry.isUnlocked && entry.unlockTs)
        .sort((a, b) => b.unlockTs - a.unlockTs)[0] || null;

    if (unlockedCountEl) unlockedCountEl.textContent = unlockedCount;
    if (totalCountEl) totalCountEl.textContent = entries.length;
    if (completionRateEl) completionRateEl.textContent = `${completionRate}%`;
    if (latestUnlockEl) {
        latestUnlockEl.textContent = latestUnlock
            ? `${latestUnlock.ach.name} - ${formatAchievementTimestamp(latestUnlock.unlockTs)}`
            : 'No badges unlocked yet';
    }

    // Show unlocked first, then sort recent unlocks, then original order.
    const sorted = [...entries].sort((a, b) => {
        if (a.isUnlocked !== b.isUnlocked) return Number(b.isUnlocked) - Number(a.isUnlocked);
        if (a.isUnlocked && b.isUnlocked && a.unlockTs && b.unlockTs && a.unlockTs !== b.unlockTs) {
            return b.unlockTs - a.unlockTs;
        }
        return a.order - b.order;
    });

    grid.classList.add('gm-ach-grid');
    grid.innerHTML = sorted.map((entry) => renderAchievementCard(entry)).join('');

    if (!historyListEl) return;

    const historySorted = [...entries].sort((a, b) => {
        const aTs = a.unlockTs || 0;
        const bTs = b.unlockTs || 0;
        if (aTs !== bTs) return bTs - aTs;
        if (a.isUnlocked !== b.isUnlocked) return Number(b.isUnlocked) - Number(a.isUnlocked);
        return a.order - b.order;
    });

    historyListEl.innerHTML = historySorted.map((entry) => {
        const { id, ach, isUnlocked, unlockTs, rarity } = entry;
        const desc = getAchievementDescription(id);
        const statusText = isUnlocked
            ? (unlockTs ? formatAchievementTimestamp(unlockTs) : 'Unlocked (legacy data)')
            : 'Not unlocked yet';
        return `
            <div class="achievement-history-item rarity-${rarity} ${isUnlocked ? 'unlocked' : 'locked'}">
                <div class="achievement-history-main">
                    <div class="achievement-history-icon">${achievementGlyph(ach)}</div>
                    <div class="achievement-history-info">
                        <div class="achievement-history-name">${escapeHtml(ach.name)}</div>
                        <div class="achievement-history-desc">${escapeHtml(desc)}</div>
                    </div>
                </div>
                <div class="achievement-history-time">${escapeHtml(statusText)}</div>
            </div>
        `;
    }).join('');
}

// Track daily study streak for achievements + DAILY XP BONUS
function updateDailyStreak() {
    const today = new Date().toDateString();
    if (lastStudyDate === today) return;
    const current = dailyLoginDayNumber(getDailyLoginDay());
    const previous = lastStudyDate ? dailyLoginDayNumber(getDailyLoginDay(new Date(lastStudyDate))) : null;
    studyDays = previous !== null && current-previous === 1 ? studyDays+1 : 1;
    lastStudyDate = today;
    saveGamificationData();
    void claimDailyLoginRewards();
}

// --- GLOBAL HOTKEY INJECTION FOR IFRAME ---
function injectIframeHotkeys() {
    try {
        const win = els.pdfFrame.contentWindow;
        if (win) {
            win.addEventListener('keydown', (e) => {
                const k = e.key.toLowerCase();
                if ((k === 'arrowright' || k === 'arrowleft') && (els?.workArea || $('work-area'))?.classList.contains('pdf-tutor-view')) {
                    e.preventDefault();
                    const handledTutorCheckpoint = typeof window.handlePdfTutorQuizCheckpointArrowNavigation === 'function'
                        ? window.handlePdfTutorQuizCheckpointArrowNavigation(k === 'arrowright' ? 1 : -1, e)
                        : false;
                    if (handledTutorCheckpoint) {
                        e.stopImmediatePropagation?.();
                    }
                    return;
                }
                if (isMemoraPdfExplanationFeatureEnabledForCore() && matchesShortcutActionEvent(e, 'toggleAutoExplanation')) {
                    e.preventDefault();
                    e.stopImmediatePropagation?.();
                    toggleAutoExplanationMode({ openCurrent: true, notify: true, silentOpen: false });
                    return;
                }
                if (matchesShortcutActionEvent(e, 'toggleAutoEvidence')) {
                    e.preventDefault();
                    e.stopImmediatePropagation?.();
                    toggleAutoEvidenceMode({ openCurrent: true, notify: true });
                    return;
                }
                if (isMemoraPdfExplanationFeatureEnabledForCore() && matchesShortcutActionEvent(e, 'openPdfExplanation')) {
                    e.preventDefault();
                    e.stopImmediatePropagation?.();
                    void openPdfExplanationForCurrentQuestion({ silent: false, smooth: true, preferDuoAiPane: true });
                    return;
                }
                // Forward evidence key to parent
                if (matchesShortcutKeyEvent(e, getShortcut('evidence')) && !e.ctrlKey && !e.metaKey && !e.altKey && !e.shiftKey) {
                    e.preventDefault();
                    triggerEvidence();
                    return;
                }
                // Forward arrows
                if (k === 'arrowright') nextQuestion();
                if (k === 'arrowleft') prevQuestion();
            });
        }
    } catch (e) {
        // Cross-origin restrictions might block this if not using Blob URLs
        console.log("Could not inject hotkeys into PDF frame (likely CORS).");
    }
}


/* --- IMPORT DUPLICATE DETECTION UTILITIES --- */
const IMPORT_DUPLICATE_SETTINGS = {
    enabled: true,
    nearMatchThreshold: 0.9,
    choiceOverlapThreshold: 0.75,
    containmentSimilarityFloor: 0.78,
    conceptOverlapThreshold: 0.42,
    answerConceptSimilarityFloor: 0.86
};
const QUESTION_DUPLICATE_REPORT_STORE_PREFIX = 'question_duplicate_report::';
const QUESTION_DUPLICATE_ARCHIVE_STORE_PREFIX = 'question_duplicate_archive::';
let questionDuplicateReportsBySetKey = {};
let questionDuplicateReportLookupStateBySetKey = {};
let questionDuplicateArchivesBySetKey = {};
let questionDuplicateArchiveLookupStateBySetKey = {};
const DEDUP_CONCEPT_STOPWORDS = new Set([
    'what', 'which', 'when', 'where', 'why', 'how', 'following', 'best', 'most', 'least',
    'according', 'typically', 'directly', 'affected', 'formula', 'equation', 'calculate',
    'calculated', 'calculating', 'defined', 'definition', 'process', 'using', 'used',
    'resulting', 'result', 'value', 'high', 'low', 'and', 'has'
]);

function normalizeQuestionForDedup(text) {
    return String(text || '')
        .replace(/<[^>]+>/g, ' ')
        .replace(/&nbsp;|&#160;/gi, ' ')
        .toLowerCase()
        .replace(/\b[a-f]\.\s*/gi, ' ')
        // Greek letters and comparison signs carry meaning (β1 vs α1, > vs <).
        .replace(/[^a-z0-9\s\p{Script=Greek}<>=≤≥≠±]/gu, ' ')
        .replace(/\s+/g, ' ')
        .trim();
}

// Answer identity keeps what tells choices apart: Greek letters, < > =, charges, decimals.
// It ignores only markup, case, spacing, a leading "B." label and trailing punctuation.
// normalizeQuestionForDedup stays the fuzzy duplicate comparison; it is too lossy to say
// which choice is correct (it turns "β1" into "1" and "pH > 7.45" into "ph 7 45").
// Only real inline tags are markup; "pH < 7.35 and PaCO2 > 45" is content.
const CHOICE_IDENTITY_TAG_PATTERN = /<\/?(?:a|b|br|code|del|div|em|font|i|ins|li|mark|ol|p|s|small|span|strike|strong|sub|sup|u|ul)(?:\s[^<>]*)?\/?>/gi;

function normalizeChoiceIdentity(text) {
    return String(text ?? '')
        .replace(CHOICE_IDENTITY_TAG_PATTERN, ' ')
        .replace(/&nbsp;|&#160;/gi, ' ')
        .normalize('NFKC')
        .toLowerCase()
        .replace(/[−‒–]/g, '-')
        .replace(/^\s*\(?[a-h][.)]\s+/, '')
        .replace(/[\s.;,]+$/, '')
        .replace(/\s+/g, ' ')
        .trim();
}

// The choice an answer text names, or -1. Exact identity first, then identity without spaces,
// then the fuzzy comparison; a tier that matches several choices settles on preferredIndex
// (a consistent numeric answer) or gives up rather than taking the first.
function findChoiceIdentityIndex(answer, choices, preferredIndex = -1) {
    const tiers = [
        value => String(value ?? '').trim(),
        normalizeChoiceIdentity,
        value => normalizeChoiceIdentity(value).replace(/\s+/g, ''),
        normalizeQuestionForDedup
    ];
    for (const normalize of tiers) {
        const target = normalize(answer);
        if (!target) continue;
        const hits = [];
        (choices || []).forEach((choice, index) => {
            if (choice && normalize(choice) === target) hits.push(index);
        });
        if (hits.length === 1) return hits[0];
        if (hits.length > 1) return hits.includes(preferredIndex) ? preferredIndex : -1;
    }
    return -1;
}

function getQuestionChoicesForDedup(q) {
    const rawChoices = getQuestionRawChoicesForDedup(q);
    return rawChoices.map(choice => normalizeQuestionForDedup(choice)).filter(Boolean);
}

function getQuestionRawChoicesForDedup(q) {
    return Array.isArray(q?.c)
        ? q.c
        : (Array.isArray(q?.choices)
            ? q.choices
            : (Array.isArray(q?.o)
                ? q.o
                : (Array.isArray(q?.options) ? q.options : [])));
}

function getQuestionAnswerIndexForDedup(q) {
    const choices = getQuestionRawChoicesForDedup(q);
    const stableAnswerText = q?.answerText ?? q?.answer_text ?? q?.correctChoiceText ?? q?.correct_choice_text;
    if (stableAnswerText !== undefined && stableAnswerText !== null && String(stableAnswerText).trim()) {
        const stableIndex = findChoiceIdentityIndex(stableAnswerText, choices, Number.isInteger(q?.a) ? q.a : -1);
        if (stableIndex >= 0) return stableIndex;
    }
    if (Number.isInteger(q?.a)) return q.a;
    if (typeof q?.answer === 'number' && Number.isInteger(q.answer)) return q.answer;
    if (typeof q?.answer === 'string') {
        const letter = q.answer.trim().toUpperCase();
        if (/^[A-H]$/.test(letter)) return letter.charCodeAt(0) - 65;
        const normalizedAnswer = normalizeQuestionForDedup(q.answer);
        const answerTextIndex = choices.findIndex(choice => normalizeQuestionForDedup(choice) === normalizedAnswer);
        if (answerTextIndex >= 0) return answerTextIndex;
    }
    return -1;
}

function buildQuestionSignatureForDedup(q) {
    const stem = normalizeQuestionForDedup(q?.q || q?.question || '');
    if (!stem) return '';

    const choices = getQuestionChoicesForDedup(q);
    const answerIdx = getQuestionAnswerIndexForDedup(q);
    const answerText = (answerIdx >= 0 && answerIdx < choices.length) ? choices[answerIdx] : '';

    return `${stem}||${choices.slice().sort().join('|')}||${answerText}`;
}

function buildQuestionStemKeyForDedup(q) {
    const stem = normalizeQuestionForDedup(q?.q || q?.question || '');
    if (!stem) return '';

    return stem
        .replace(/\b(the|a|an|of|to|for|and|or|in|on|with|from|at|by)\b/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
}

function buildQuestionAnswerConceptKeyForDedup(q) {
    const answerIdx = getQuestionAnswerIndexForDedup(q);
    const rawChoices = getQuestionRawChoicesForDedup(q);
    const answerText = (answerIdx >= 0 && answerIdx < rawChoices.length) ? rawChoices[answerIdx] : '';
    return canonicalizeRatioExpressionForDedup(answerText);
}

function buildQuestionConceptTokensForDedup(q) {
    const answerIdx = getQuestionAnswerIndexForDedup(q);
    const rawChoices = getQuestionRawChoicesForDedup(q);
    const answerText = (answerIdx >= 0 && answerIdx < rawChoices.length) ? rawChoices[answerIdx] : '';
    const explanationText = typeof q?.e === 'string'
        ? q.e
        : (typeof q?.explanation === 'string' ? q.explanation : '');
    const tokenSet = new Set();
    [q?.q || q?.question || '', answerText, explanationText].forEach((part) => {
        tokenizeQuestionConceptForDedup(part).forEach((token) => tokenSet.add(token));
    });
    return Array.from(tokenSet);
}

function normalizeQuestionConceptTextForDedup(text) {
    return normalizeQuestionForDedup(text)
        .replace(/\bconfiguration factor\b/g, ' c factor ')
        .replace(/\bc factor\b/g, ' cfactor ')
        .replace(/\bnumber of\b/g, ' ')
        .replace(/\bdivid(?:e|ed|ing)\s+by\b/g, ' ratio ')
        .replace(/\bratio of\b/g, ' ratio ')
        .replace(/\bbonded walls?\b/g, ' bonded surfaces ')
        .replace(/\bunbonded walls?\b/g, ' unbonded surfaces ')
        .replace(/\bcalculat(?:e|ed|ing)\b/g, ' calculate ')
        .replace(/\bequation\b/g, ' formula ')
        .replace(/\s+/g, ' ')
        .trim();
}

function cleanupDedupConceptPhrase(text) {
    return String(text || '')
        .replace(/\b(the|a|an|of|to|for|is|are)\b/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
}

function canonicalizeRatioExpressionForDedup(text) {
    const normalized = cleanupDedupConceptPhrase(normalizeQuestionConceptTextForDedup(text));
    if (!normalized) return '';
    if (normalized.startsWith('ratio ')) return normalized.replace(/\s+/g, ' ').trim();
    const ratioIdx = normalized.indexOf(' ratio ');
    if (ratioIdx <= 0) return normalized;
    const left = cleanupDedupConceptPhrase(normalized.slice(0, ratioIdx));
    const right = cleanupDedupConceptPhrase(normalized.slice(ratioIdx + 7));
    if (!left || !right) return normalized;
    return `ratio ${left} ${right}`.replace(/\s+/g, ' ').trim();
}

function singularizeDedupConceptToken(token) {
    const value = String(token || '').trim();
    if (!value) return '';
    if (value === 'cfactor') return value;
    if (value.endsWith('ies') && value.length > 4) return `${value.slice(0, -3)}y`;
    if (value.endsWith('sses') && value.length > 5) return value.slice(0, -2);
    if (value.endsWith('s') && !value.endsWith('ss') && value.length > 4) return value.slice(0, -1);
    return value;
}

function tokenizeQuestionConceptForDedup(text) {
    const normalized = canonicalizeRatioExpressionForDedup(text);
    if (!normalized) return [];
    return normalized
        .split(/\s+/)
        .map(singularizeDedupConceptToken)
        .filter((token) => token.length > 2 && !/^\d+$/.test(token) && !DEDUP_CONCEPT_STOPWORDS.has(token));
}

function getQuestionSetKeyVariantsForDedup(setKey) {
    const raw = String(setKey || '').trim();
    if (!raw) return [];
    const stripped = raw.replace(/\.(json|txt)$/i, '');
    const variants = new Set([raw, stripped]);
    if (stripped && !/\.json$/i.test(raw)) variants.add(`${stripped}.json`);
    if (stripped && !/\.txt$/i.test(raw)) variants.add(`${stripped}.txt`);
    return Array.from(variants).filter(Boolean);
}

function cloneQuestionForDuplicateReport(q) {
    if (!q || typeof q !== 'object') return null;
    const rawChoices = Array.isArray(q.c)
        ? q.c
        : (Array.isArray(q.choices)
            ? q.choices
            : (Array.isArray(q.o)
                ? q.o
                : (Array.isArray(q.options) ? q.options : [])));
    const clone = {
        q: q.q ?? q.question ?? '',
        a: getQuestionAnswerIndexForDedup(q),
        c: rawChoices.map(choice => String(choice ?? '')),
        e: typeof q.e === 'string' ? q.e : (typeof q.explanation === 'string' ? q.explanation : ''),
        add: typeof q.add === 'string' ? q.add : (typeof q.page === 'string' ? q.page : ''),
        src: q.src || '',
        sourceFile: q.sourceFile || '',
        genModel: q.genModel || q.generationModel || q.generatedModel || q.aiModel || q.model || q.modelName || ''
    };
    if (q.w && typeof q.w === 'object' && !Array.isArray(q.w)) {
        clone.w = { ...q.w };
    }
    return clone;
}

function normalizeImportedQuizSetKey(value, fallback = 'Imported Quiz') {
    const normalized = String(value || '')
        .replace(/[\\/:*?"<>|]+/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
    return normalized || fallback;
}

function appendImportedQuizSetSuffix(baseKey, suffixNumber) {
    const normalizedBase = normalizeImportedQuizSetKey(baseKey);
    const match = normalizedBase.match(/^(.*?)(\.[^.]+)$/);
    if (match) return `${match[1]} (${suffixNumber})${match[2]}`;
    return `${normalizedBase} (${suffixNumber})`;
}

function allocateImportedQuizSetKey(baseKey, usedKeys) {
    const takenKeys = usedKeys instanceof Set ? usedKeys : new Set();
    const normalizedBase = normalizeImportedQuizSetKey(baseKey);
    let candidate = normalizedBase;
    let suffixNumber = 2;
    while (takenKeys.has(candidate)) {
        candidate = appendImportedQuizSetSuffix(normalizedBase, suffixNumber);
        suffixNumber += 1;
    }
    takenKeys.add(candidate);
    return candidate;
}

function deriveImportedQuizSetBaseKey(question, fallbackSetName) {
    const explicitSetKey = String(
        question?.quizSetKey
        || question?.setKey
        || question?.bankKey
        || question?.quizKey
        || ''
    ).trim();
    if (explicitSetKey) return explicitSetKey;

    const sourceLabel = String(question?.sourceFile || question?.src || '').trim();
    if (sourceLabel) {
        if (/\.pdf$/i.test(sourceLabel)) {
            return `${sourceLabel.replace(/\.pdf$/i, '')}_quiz.json`;
        }
        return sourceLabel;
    }

    return fallbackSetName;
}

// Every spelling of a live set key counts as taken (`A`, `A.json`, `A.txt`), so an
// import never lands on a key that dedup would treat as the same set as a live one.
function collectUsedQuestionSetKeys() {
    const liveKeys = [
        ...Object.keys(loadedQuestionSets || {}),
        ...(Array.isArray(sessionManifest?.jsons) ? sessionManifest.jsons : [])
    ].map((key) => String(key || '').trim()).filter(Boolean);
    return new Set(liveKeys.flatMap((key) => [key, ...getQuestionSetKeyVariantsForDedup(key)]));
}

// reservedKeys lets one import batch allocate keys across all of its files; without
// it, the keys already live in the session are the only ones reserved.
function buildImportedQuizSetGroups(questionArray, fallbackSetName, reservedKeys = null) {
    const sourceQuestions = Array.isArray(questionArray) ? questionArray : [];
    const groupsById = new Map();
    const orderedGroupIds = [];

    sourceQuestions.forEach((question) => {
        if (!question || typeof question !== 'object') return;
        const explicitSetKey = String(
            question.quizSetKey
            || question.setKey
            || question.bankKey
            || question.quizKey
            || ''
        ).trim();
        const sourceLabel = String(question.sourceFile || question.src || '').trim();
        // Rows without bank/source metadata belong to the file they came in, as one bank.
        const groupId = explicitSetKey || sourceLabel || `${fallbackSetName}::file`;

        if (!groupsById.has(groupId)) {
            groupsById.set(groupId, {
                id: groupId,
                baseKey: deriveImportedQuizSetBaseKey(question, fallbackSetName),
                sourceLabel: sourceLabel || '',
                questions: []
            });
            orderedGroupIds.push(groupId);
        }

        const group = groupsById.get(groupId);
        if (!group.sourceLabel && sourceLabel) group.sourceLabel = sourceLabel;
        group.questions.push(question);
    });

    const usedKeys = reservedKeys instanceof Set ? reservedKeys : collectUsedQuestionSetKeys();

    return orderedGroupIds.map((groupId) => {
        const group = groupsById.get(groupId);
        return {
            ...group,
            setKey: allocateImportedQuizSetKey(group?.baseKey || fallbackSetName, usedKeys)
        };
    });
}

// Flashcard-only records (imported card decks and Learn-generated cards) are study cards, not
// multiple-choice questions: their second "choice" is a placeholder.
function isFlashcardOnlyStudyItem(question) {
    return question?.flashcardOnly === true || question?.studyMode === 'flashcards';
}

function createQuestionDedupDescriptor(q, meta = {}) {
    // Cards and quiz questions about one concept are different study material, so every
    // duplicate-index key is partitioned by type. Quiz keys keep their original form.
    const typePrefix = isFlashcardOnlyStudyItem(q) ? 'card::' : '';
    const partition = (key) => (key ? `${typePrefix}${key}` : key);
    const signature = partition(buildQuestionSignatureForDedup(q));
    const stemKey = partition(buildQuestionStemKeyForDedup(q));
    const stemText = normalizeQuestionForDedup(q?.q || q?.question || '');
    const choices = getQuestionChoicesForDedup(q);
    const conceptChoices = getQuestionRawChoicesForDedup(q).map(choice => canonicalizeRatioExpressionForDedup(choice)).filter(Boolean);
    const answerIdx = getQuestionAnswerIndexForDedup(q);
    const answerConceptKey = partition(buildQuestionAnswerConceptKeyForDedup(q));
    return {
        question: q,
        snapshot: cloneQuestionForDuplicateReport(q),
        signature,
        stemKey,
        stemText,
        choices,
        conceptChoices,
        answerIdx,
        answerText: (answerIdx >= 0 && answerIdx < choices.length) ? choices[answerIdx] : '',
        answerConceptKey,
        conceptTokens: buildQuestionConceptTokensForDedup(q),
        setKey: meta.setKey || null,
        originalIndex: Number.isInteger(meta.originalIndex) ? meta.originalIndex : -1,
        keptIndex: Number.isInteger(meta.keptIndex) ? meta.keptIndex : -1
    };
}

function calculateChoiceOverlapForDedup(choicesA, choicesB) {
    const left = Array.isArray(choicesA) ? choicesA.filter(Boolean) : [];
    const right = Array.isArray(choicesB) ? choicesB.filter(Boolean) : [];
    if (!left.length || !right.length) return 0;
    const rightSet = new Set(right);
    let overlap = 0;
    left.forEach(choice => {
        if (rightSet.has(choice)) overlap += 1;
    });
    return overlap / Math.max(left.length, right.length);
}

function calculateTokenOverlapForDedup(tokensA, tokensB) {
    const left = Array.isArray(tokensA) ? tokensA.filter(Boolean) : [];
    const right = Array.isArray(tokensB) ? tokensB.filter(Boolean) : [];
    if (!left.length || !right.length) return 0;
    const rightSet = new Set(right);
    let overlap = 0;
    left.forEach((token) => {
        if (rightSet.has(token)) overlap += 1;
    });
    return overlap / Math.max(left.length, right.length);
}

function collectQuestionDedupCandidateBuckets(index, descriptor) {
    const buckets = [];
    if (!index || !descriptor) return buckets;
    if (descriptor.stemKey && index.byStem?.has(descriptor.stemKey)) {
        buckets.push(index.byStem.get(descriptor.stemKey));
    }
    if (descriptor.answerConceptKey && index.byAnswerConcept?.has(descriptor.answerConceptKey)) {
        buckets.push(index.byAnswerConcept.get(descriptor.answerConceptKey));
    }
    return buckets;
}

function findNearDuplicateDescriptorForDedup(candidateBuckets, candidate, threshold) {
    const buckets = Array.isArray(candidateBuckets) ? candidateBuckets : [candidateBuckets];
    if (!buckets.length || !candidate?.stemText) return null;

    let bestMatch = null;
    const seen = new Set();
    buckets.forEach((bucket) => {
        if (!Array.isArray(bucket) || !bucket.length) return;
        bucket.forEach(existing => {
            if (!existing?.stemText || seen.has(existing)) return;
            seen.add(existing);
            const stemSimilarity = existing.stemText === candidate.stemText
                ? 1
                : calculateSimilarity(existing.stemText, candidate.stemText);
            const choiceOverlap = calculateChoiceOverlapForDedup(
                existing.conceptChoices?.length ? existing.conceptChoices : existing.choices,
                candidate.conceptChoices?.length ? candidate.conceptChoices : candidate.choices
            );
            const answerMatch = !!(existing.answerText && candidate.answerText && existing.answerText === candidate.answerText);
            const answerConceptMatch = !!(
                existing.answerConceptKey &&
                candidate.answerConceptKey &&
                existing.answerConceptKey === candidate.answerConceptKey
            );
            const answerConceptSimilarity = answerConceptMatch
                ? 1
                : calculateSimilarity(existing.answerConceptKey, candidate.answerConceptKey);
            const conceptOverlap = calculateTokenOverlapForDedup(existing.conceptTokens, candidate.conceptTokens);
            const containsStem = existing.stemText.includes(candidate.stemText) || candidate.stemText.includes(existing.stemText);
            const score = (
                (stemSimilarity * 0.42) +
                (choiceOverlap * 0.16) +
                (conceptOverlap * 0.24) +
                (answerConceptSimilarity * 0.12) +
                (answerMatch ? 0.03 : 0) +
                (answerConceptMatch ? 0.03 : 0)
            );
            const isNearDuplicate = stemSimilarity >= threshold
                || (containsStem && stemSimilarity >= IMPORT_DUPLICATE_SETTINGS.containmentSimilarityFloor)
                || (stemSimilarity >= threshold - 0.08 && choiceOverlap >= IMPORT_DUPLICATE_SETTINGS.choiceOverlapThreshold && (answerMatch || answerConceptMatch))
                || (answerConceptMatch && conceptOverlap >= IMPORT_DUPLICATE_SETTINGS.conceptOverlapThreshold)
                || (
                    answerConceptSimilarity >= IMPORT_DUPLICATE_SETTINGS.answerConceptSimilarityFloor &&
                    conceptOverlap >= IMPORT_DUPLICATE_SETTINGS.conceptOverlapThreshold + 0.08 &&
                    choiceOverlap >= 0.35
                );

            if (!isNearDuplicate) return;
            if (!bestMatch || score > bestMatch.score) {
                bestMatch = {
                    descriptor: existing,
                    similarity: Math.max(stemSimilarity, score),
                    stemSimilarity,
                    choiceOverlap,
                    conceptOverlap,
                    answerMatch,
                    answerConceptMatch,
                    answerConceptSimilarity,
                    score
                };
            }
        });
    });

    return bestMatch;
}

function createQuestionDuplicateReportPayload(setKey, duplicates, stats = {}) {
    const entries = Array.isArray(duplicates) ? duplicates.map(entry => ({
        duplicateType: entry.duplicateType === 'near' ? 'near' : 'exact',
        similarity: Number.isFinite(entry.similarity) ? Number(entry.similarity.toFixed(4)) : null,
        stemSimilarity: Number.isFinite(entry.stemSimilarity) ? Number(entry.stemSimilarity.toFixed(4)) : null,
        choiceOverlap: Number.isFinite(entry.choiceOverlap) ? Number(entry.choiceOverlap.toFixed(4)) : null,
        conceptOverlap: Number.isFinite(entry.conceptOverlap) ? Number(entry.conceptOverlap.toFixed(4)) : null,
        answerMatch: !!entry.answerMatch,
        answerConceptMatch: !!entry.answerConceptMatch,
        duplicateOriginalIndex: Number.isInteger(entry.duplicateOriginalIndex) ? entry.duplicateOriginalIndex : -1,
        canonicalOriginalIndex: Number.isInteger(entry.canonicalOriginalIndex) ? entry.canonicalOriginalIndex : -1,
        duplicateSetKey: entry.duplicateSetKey || String(setKey || '').trim() || null,
        canonicalSetKey: entry.canonicalSetKey || null,
        duplicateQuestion: cloneQuestionForDuplicateReport(entry.duplicateQuestion),
        canonicalQuestion: cloneQuestionForDuplicateReport(entry.canonicalQuestion)
    })).filter(entry => entry.duplicateQuestion && entry.canonicalQuestion) : [];

    return {
        setKey: String(setKey || '').trim(),
        generatedAt: new Date().toISOString(),
        stats: {
            scanned: Number.isFinite(stats.scanned) ? stats.scanned : entries.length,
            kept: Number.isFinite(stats.kept) ? stats.kept : 0,
            removed: Number.isFinite(stats.removed) ? stats.removed : entries.length,
            exact: Number.isFinite(stats.exact) ? stats.exact : entries.filter(entry => entry.duplicateType === 'exact').length,
            near: Number.isFinite(stats.near) ? stats.near : entries.filter(entry => entry.duplicateType === 'near').length
        },
        entries
    };
}

function createQuestionDuplicateArchivePayload(setKey, entries, existingArchive = null) {
    const normalizedKey = String(setKey || '').trim();
    const mergedEntries = [];
    const seen = new Set();
    const pushEntries = (list, archivedAtFallback) => {
        (Array.isArray(list) ? list : []).forEach((entry) => {
            const duplicateQuestion = cloneQuestionForDuplicateReport(entry?.duplicateQuestion);
            const canonicalQuestion = cloneQuestionForDuplicateReport(entry?.canonicalQuestion);
            if (!duplicateQuestion || !canonicalQuestion) return;
            const normalizedEntry = {
                duplicateType: entry?.duplicateType === 'near' ? 'near' : 'exact',
                similarity: Number.isFinite(entry?.similarity) ? Number(entry.similarity) : null,
                stemSimilarity: Number.isFinite(entry?.stemSimilarity) ? Number(entry.stemSimilarity) : null,
                choiceOverlap: Number.isFinite(entry?.choiceOverlap) ? Number(entry.choiceOverlap) : null,
                conceptOverlap: Number.isFinite(entry?.conceptOverlap) ? Number(entry.conceptOverlap) : null,
                answerMatch: !!entry?.answerMatch,
                answerConceptMatch: !!entry?.answerConceptMatch,
                duplicateOriginalIndex: Number.isInteger(entry?.duplicateOriginalIndex) ? entry.duplicateOriginalIndex : -1,
                canonicalOriginalIndex: Number.isInteger(entry?.canonicalOriginalIndex) ? entry.canonicalOriginalIndex : -1,
                duplicateSetKey: entry?.duplicateSetKey || normalizedKey || null,
                canonicalSetKey: entry?.canonicalSetKey || null,
                duplicateQuestion,
                canonicalQuestion,
                archivedAt: String(entry?.archivedAt || archivedAtFallback || new Date().toISOString())
            };
            const entryId = getDuplicateEntryUniqueKey(normalizedEntry);
            if (!entryId || seen.has(entryId)) return;
            seen.add(entryId);
            mergedEntries.push(normalizedEntry);
        });
    };

    pushEntries(existingArchive?.entries, existingArchive?.updatedAt || existingArchive?.createdAt || new Date().toISOString());
    pushEntries(entries, new Date().toISOString());

    return {
        setKey: normalizedKey,
        createdAt: existingArchive?.createdAt || new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        entryCount: mergedEntries.length,
        entries: mergedEntries
    };
}

async function clearQuestionDuplicateArchive(setKey) {
    const variants = getQuestionSetKeyVariantsForDedup(setKey);
    variants.forEach((variant) => {
        delete questionDuplicateArchivesBySetKey[variant];
        questionDuplicateArchiveLookupStateBySetKey[variant] = true;
    });

    for (const variant of variants) {
        try {
            await IDB.del('settings', `${QUESTION_DUPLICATE_ARCHIVE_STORE_PREFIX}${variant}`);
        } catch (err) {
            console.warn(`Failed to clear duplicate archive for ${variant}:`, err);
        }
    }
}

async function clearQuestionDuplicateReport(setKey, options = {}) {
    const variants = getQuestionSetKeyVariantsForDedup(setKey);
    const clearArchive = !!options.clearArchive;
    variants.forEach(variant => {
        delete questionDuplicateReportsBySetKey[variant];
        questionDuplicateReportLookupStateBySetKey[variant] = true;
    });

    for (const variant of variants) {
        try {
            await IDB.del('settings', `${QUESTION_DUPLICATE_REPORT_STORE_PREFIX}${variant}`);
        } catch (err) {
            console.warn(`Failed to clear duplicate report for ${variant}:`, err);
        }
    }
    if (clearArchive) {
        await clearQuestionDuplicateArchive(setKey);
    }
}

async function saveQuestionDuplicateReport(setKey, duplicates, stats = {}, options = {}) {
    const normalizedKey = String(setKey || '').trim();
    if (!normalizedKey) return null;
    const preserveExistingOnEmpty = !!options.preserveExistingOnEmpty;

    const payload = createQuestionDuplicateReportPayload(normalizedKey, duplicates, stats);
    if (!payload.entries.length) {
        if (preserveExistingOnEmpty) {
            const existingReport = questionDuplicateReportsBySetKey[normalizedKey] || await loadQuestionDuplicateReport(normalizedKey);
            if (existingReport && Array.isArray(existingReport.entries) && existingReport.entries.length) {
                return existingReport;
            }
        }
        await clearQuestionDuplicateReport(normalizedKey);
        return null;
    }

    const variants = getQuestionSetKeyVariantsForDedup(normalizedKey);
    variants.forEach(variant => {
        questionDuplicateReportsBySetKey[variant] = payload;
        questionDuplicateReportLookupStateBySetKey[variant] = true;
    });

    try {
        await IDB.put('settings', `${QUESTION_DUPLICATE_REPORT_STORE_PREFIX}${normalizedKey}`, payload);
    } catch (err) {
        console.warn(`Failed to save duplicate report for ${normalizedKey}:`, err);
    }
    try {
        await saveQuestionDuplicateArchive(normalizedKey, payload.entries);
    } catch (err) {
        console.warn(`Failed to save duplicate archive for ${normalizedKey}:`, err);
    }
    return payload;
}

async function loadQuestionDuplicateReport(setKey) {
    const variants = getQuestionSetKeyVariantsForDedup(setKey);
    for (const variant of variants) {
        if (questionDuplicateReportsBySetKey[variant]) {
            return questionDuplicateReportsBySetKey[variant];
        }
    }
    if (variants.some(variant => questionDuplicateReportLookupStateBySetKey[variant])) {
        return null;
    }

    for (const variant of variants) {
        try {
            const stored = await IDB.get('settings', `${QUESTION_DUPLICATE_REPORT_STORE_PREFIX}${variant}`);
            if (stored && Array.isArray(stored.entries)) {
                variants.forEach(alias => {
                    questionDuplicateReportsBySetKey[alias] = stored;
                    questionDuplicateReportLookupStateBySetKey[alias] = true;
                });
                return stored;
            }
        } catch (err) {
            console.warn(`Failed to load duplicate report for ${variant}:`, err);
        }
    }

    variants.forEach(variant => {
        questionDuplicateReportLookupStateBySetKey[variant] = true;
    });
    return null;
}

async function saveQuestionDuplicateArchive(setKey, entries = []) {
    const normalizedKey = String(setKey || '').trim();
    if (!normalizedKey) return null;
    const existingArchive = await loadQuestionDuplicateArchive(normalizedKey);
    const payload = createQuestionDuplicateArchivePayload(normalizedKey, entries, existingArchive);
    if (!payload.entries.length) return existingArchive || null;

    const variants = getQuestionSetKeyVariantsForDedup(normalizedKey);
    variants.forEach((variant) => {
        questionDuplicateArchivesBySetKey[variant] = payload;
        questionDuplicateArchiveLookupStateBySetKey[variant] = true;
    });

    try {
        await IDB.put('settings', `${QUESTION_DUPLICATE_ARCHIVE_STORE_PREFIX}${normalizedKey}`, payload);
    } catch (err) {
        console.warn(`Failed to save duplicate archive for ${normalizedKey}:`, err);
    }
    return payload;
}

async function loadQuestionDuplicateArchive(setKey) {
    const variants = getQuestionSetKeyVariantsForDedup(setKey);
    for (const variant of variants) {
        if (questionDuplicateArchivesBySetKey[variant]) {
            return questionDuplicateArchivesBySetKey[variant];
        }
    }
    if (variants.some((variant) => questionDuplicateArchiveLookupStateBySetKey[variant])) {
        return null;
    }

    for (const variant of variants) {
        try {
            const stored = await IDB.get('settings', `${QUESTION_DUPLICATE_ARCHIVE_STORE_PREFIX}${variant}`);
            if (stored && Array.isArray(stored.entries)) {
                variants.forEach((alias) => {
                    questionDuplicateArchivesBySetKey[alias] = stored;
                    questionDuplicateArchiveLookupStateBySetKey[alias] = true;
                });
                return stored;
            }
        } catch (err) {
            console.warn(`Failed to load duplicate archive for ${variant}:`, err);
        }
    }

    variants.forEach((variant) => {
        questionDuplicateArchiveLookupStateBySetKey[variant] = true;
    });
    return null;
}

async function ensureQuestionDuplicateReportsLoaded(setKeys = []) {
    const keys = Array.from(new Set((Array.isArray(setKeys) ? setKeys : []).map(key => String(key || '').trim()).filter(Boolean)));
    for (const key of keys) {
        await loadQuestionDuplicateReport(key);
    }
}

async function ensureQuestionDuplicateArchivesLoaded(setKeys = []) {
    const keys = Array.from(new Set((Array.isArray(setKeys) ? setKeys : []).map(key => String(key || '').trim()).filter(Boolean)));
    for (const key of keys) {
        await loadQuestionDuplicateArchive(key);
    }
}

function indexQuestionForDedup(index, q, meta = {}) {
    const descriptor = createQuestionDedupDescriptor(q, meta);

    if (descriptor.signature && !index.bySignature.has(descriptor.signature)) {
        index.bySignature.set(descriptor.signature, descriptor);
    }

    if (descriptor.stemKey) {
        if (!index.byStem.has(descriptor.stemKey)) index.byStem.set(descriptor.stemKey, []);
        index.byStem.get(descriptor.stemKey).push(descriptor);
    }
    if (descriptor.answerConceptKey) {
        if (!index.byAnswerConcept.has(descriptor.answerConceptKey)) index.byAnswerConcept.set(descriptor.answerConceptKey, []);
        index.byAnswerConcept.get(descriptor.answerConceptKey).push(descriptor);
    }

    return descriptor;
}

function createQuestionDuplicateIndex(excludeSetKey = null) {
    const index = { bySignature: new Map(), byStem: new Map(), byAnswerConcept: new Map() };
    const excludeKeys = new Set(getQuestionSetKeyVariantsForDedup(excludeSetKey));
    const seenQuestionArrays = new Set();

    Object.entries(loadedQuestionSets || {}).forEach(([setKey, setQuestions]) => {
        if (excludeKeys.size && getQuestionSetKeyVariantsForDedup(setKey).some(variant => excludeKeys.has(variant))) return;
        if (!Array.isArray(setQuestions)) return;
        if (seenQuestionArrays.has(setQuestions)) return;
        seenQuestionArrays.add(setQuestions);

        setQuestions.forEach((q, questionIndex) => {
            indexQuestionForDedup(index, q, {
                setKey,
                originalIndex: questionIndex,
                keptIndex: questionIndex
            });
        });
    });

    return index;
}

function dedupeQuestionArray(questionArray, options = {}) {
    const input = Array.isArray(questionArray) ? questionArray : [];
    const stats = {
        scanned: input.length,
        kept: input.length,
        removed: 0,
        exact: 0,
        near: 0
    };
    const duplicates = [];

    const normalizedSetKey = String(options.setKey || options.excludeSetKey || '').trim() || null;
    const enabled = options.enabled !== undefined ? !!options.enabled : IMPORT_DUPLICATE_SETTINGS.enabled;
    if (!enabled || input.length === 0) {
        return {
            kept: input,
            stats,
            index: options.index || createQuestionDuplicateIndex(options.excludeSetKey || null),
            duplicates
        };
    }

    const index = options.index || createQuestionDuplicateIndex(options.excludeSetKey || null);
    const threshold = Number.isFinite(options.nearMatchThreshold) ? options.nearMatchThreshold : IMPORT_DUPLICATE_SETTINGS.nearMatchThreshold;

    const kept = [];
    input.forEach((q, inputIndex) => {
        const descriptor = createQuestionDedupDescriptor(q, {
            setKey: normalizedSetKey,
            originalIndex: inputIndex,
            keptIndex: kept.length
        });
        const signature = descriptor.signature;
        const stemKey = descriptor.stemKey;

        let duplicateType = null;
        let matchedDescriptor = null;
        let similarity = signature ? 1 : null;
        let stemSimilarity = signature ? 1 : null;
        let choiceOverlap = null;
        let conceptOverlap = null;
        let answerMatch = false;
        let answerConceptMatch = false;

        if (signature && index.bySignature.has(signature)) {
            duplicateType = 'exact';
            matchedDescriptor = index.bySignature.get(signature);
            similarity = 1;
            choiceOverlap = calculateChoiceOverlapForDedup(
                matchedDescriptor?.conceptChoices?.length ? matchedDescriptor.conceptChoices : matchedDescriptor?.choices,
                descriptor.conceptChoices?.length ? descriptor.conceptChoices : descriptor.choices
            );
            answerMatch = !!(matchedDescriptor?.answerText && descriptor.answerText && matchedDescriptor.answerText === descriptor.answerText);
            answerConceptMatch = !!(
                matchedDescriptor?.answerConceptKey &&
                descriptor.answerConceptKey &&
                matchedDescriptor.answerConceptKey === descriptor.answerConceptKey
            );
            conceptOverlap = calculateTokenOverlapForDedup(matchedDescriptor?.conceptTokens, descriptor.conceptTokens);
        } else {
            const candidateBuckets = collectQuestionDedupCandidateBuckets(index, descriptor);
            const nearMatch = findNearDuplicateDescriptorForDedup(candidateBuckets, descriptor, threshold);
            if (nearMatch) {
                duplicateType = 'near';
                matchedDescriptor = nearMatch.descriptor;
                similarity = nearMatch.similarity;
                stemSimilarity = nearMatch.stemSimilarity;
                choiceOverlap = nearMatch.choiceOverlap;
                conceptOverlap = nearMatch.conceptOverlap;
                answerMatch = nearMatch.answerMatch;
                answerConceptMatch = nearMatch.answerConceptMatch;
            }
        }

        if (duplicateType) {
            stats.removed += 1;
            stats[duplicateType] += 1;
            duplicates.push({
                duplicateType,
                similarity,
                stemSimilarity,
                choiceOverlap,
                conceptOverlap,
                answerMatch,
                answerConceptMatch,
                duplicateSetKey: normalizedSetKey,
                canonicalSetKey: matchedDescriptor?.setKey || null,
                duplicateOriginalIndex: inputIndex,
                canonicalOriginalIndex: Number.isInteger(matchedDescriptor?.originalIndex) ? matchedDescriptor.originalIndex : -1,
                duplicateQuestion: q,
                canonicalQuestion: matchedDescriptor?.question || null
            });
            return;
        }

        kept.push(q);
        indexQuestionForDedup(index, q, {
            setKey: normalizedSetKey,
            originalIndex: inputIndex,
            keptIndex: kept.length - 1
        });
    });

    stats.kept = kept.length;
    return { kept, stats, index, duplicates };
}

function applySmartImportDedup(setKey, questionArray) {
    const result = dedupeQuestionArray(questionArray, { excludeSetKey: setKey, setKey });
    return {
        questions: Array.isArray(result.kept) ? result.kept : [],
        stats: result.stats || { scanned: 0, kept: 0, removed: 0, exact: 0, near: 0 },
        duplicates: Array.isArray(result.duplicates) ? result.duplicates : []
    };
}

function analyzeCurrentQuestionDuplicates(questionArray) {
    const emptyIndex = { bySignature: new Map(), byStem: new Map(), byAnswerConcept: new Map() };
    const result = dedupeQuestionArray(questionArray, {
        index: emptyIndex,
        excludeSetKey: null,
        setKey: '__active__'
    });
    return Array.isArray(result.duplicates) ? result.duplicates : [];
}

function createDedupStatsTracker() {
    return { removed: 0, exact: 0, near: 0 };
}

function mergeDedupStats(tracker, stats) {
    if (!tracker || !stats) return;
    tracker.removed += Number.isFinite(stats.removed) ? stats.removed : 0;
    tracker.exact += Number.isFinite(stats.exact) ? stats.exact : 0;
    tracker.near += Number.isFinite(stats.near) ? stats.near : 0;
}

function formatDedupSummary(tracker) {
    const removed = Number.isFinite(tracker?.removed) ? tracker.removed : 0;
    if (removed <= 0) return '';
    const exact = Number.isFinite(tracker?.exact) ? tracker.exact : 0;
    const near = Number.isFinite(tracker?.near) ? tracker.near : 0;
    const parts = [];
    if (exact > 0) parts.push(`${exact} exact`);
    if (near > 0) parts.push(`${near} near`);
    const detail = parts.length ? ` (${parts.join(', ')})` : '';
    return `Smart duplicate detection removed ${removed} duplicate question${removed === 1 ? '' : 's'}${detail}.`;
}

function syncLoadedQuestionSetAliases(setKey, questionArray) {
    const normalizedSetKey = String(setKey || '').trim();
    if (!normalizedSetKey) return;
    getQuestionSetKeyVariantsForDedup(normalizedSetKey).forEach((variant) => {
        if (variant === normalizedSetKey || Object.prototype.hasOwnProperty.call(loadedQuestionSets, variant)) {
            loadedQuestionSets[variant] = questionArray;
        }
    });
}

function remapQuestionKeyThroughAliasMap(questionKey, aliasMap = {}) {
    let current = String(questionKey || '').trim();
    if (!current) return '';
    const seen = new Set();
    while (current && aliasMap && aliasMap[current] && !seen.has(current)) {
        seen.add(current);
        current = String(aliasMap[current] || '').trim();
    }
    return current;
}

function remapQuestionKeyListThroughAliasMap(questionKeys, aliasMap = {}) {
    if (!Array.isArray(questionKeys) || !questionKeys.length) return [];
    return questionKeys
        .map((questionKey) => remapQuestionKeyThroughAliasMap(questionKey, aliasMap))
        .filter(Boolean);
}

function mergeQuestionKeyBucketsThroughAliasMap(rawBuckets, aliasMap = {}) {
    const merged = sanitizeQuestionKeyBucketObject(rawBuckets);
    Object.entries(aliasMap || {}).forEach(([sourceKeyRaw, targetKeyRaw]) => {
        const sourceKey = String(sourceKeyRaw || '').trim();
        const targetKey = remapQuestionKeyThroughAliasMap(targetKeyRaw, aliasMap);
        if (!sourceKey || !targetKey || sourceKey === targetKey) return;
        const sourceValues = Array.isArray(merged[sourceKey]) ? merged[sourceKey] : [];
        if (!sourceValues.length) return;
        if (!Array.isArray(merged[targetKey])) merged[targetKey] = [];
        merged[targetKey] = merged[targetKey].concat(sourceValues);
        delete merged[sourceKey];
    });
    return merged;
}

function createActiveDuplicateDetectionSnapshot() {
    ensureQuestionNotesLegacyMigration();
    const currentRealIdx = Array.isArray(filteredIndices) && currentQIndex >= 0 && currentQIndex < filteredIndices.length
        ? filteredIndices[currentQIndex]
        : -1;

    return {
        userAnswersByQuestionKey: buildIndexedStateBucketsByQuestionKey(userAnswers),
        knowledgeStateByQuestionKey: buildIndexedStateBucketsByQuestionKey(knowledgeState),
        savedQuestionsByQuestionKey: buildIndexedStateBucketsByQuestionKey(savedQuestions),
        forcedChoiceOrderByQuestionKey: buildIndexedStateBucketsByQuestionKey(forcedChoiceOrderByQuestion),
        mixedChoiceOrderByQuestionKey: buildIndexedStateBucketsByQuestionKey(mixedChoiceOrderByQuestion),
        srsDataByQuestionKey: buildIndexedStateBucketsByQuestionKey(srsData),
        statsDataByQuestionKey: buildIndexedStateBucketsByQuestionKey(statsData),
        aiQuestionCacheByQuestionKey: buildIndexedStateBucketsByQuestionKey(aiQuestionCacheByIndex),
        adaptiveReviewSnapshotByQuestionKey: buildIndexedStateBucketsByQuestionKey(adaptiveReviewSnapshot),
        adaptiveClearedAnswersBackupByQuestionKey: buildIndexedStateBucketsByQuestionKey(adaptiveClearedAnswersBackup),
        filteredQuestionKeys: buildFilteredQuestionKeysForPersistence(filteredIndices),
        currentQuestionKey: Number.isInteger(currentRealIdx) && currentRealIdx >= 0
            ? buildQuestionPersistenceKeyByIndex(currentRealIdx)
            : '',
        currentQuestionViewIndex: Number.isInteger(currentQIndex) ? currentQIndex : 0,
        isFilterActive: !!isFilterActive,
        wrongAnswerQuestionKeys: Array.from(wrongAnswerHistory || []).filter(key => typeof key === 'string' && key),
        questionNotesByQuestionKey: { ...(questionNotesByQuestionKey || {}) }
    };
}

function restoreQuestionNotesAfterDuplicateDetection(snapshotNotesByQuestionKey, aliasMap = {}) {
    const nextNotes = {};
    Object.entries(snapshotNotesByQuestionKey || {}).forEach(([key, noteValue]) => {
        const normalizedKey = String(key || '').trim();
        const note = sanitizeQuestionNoteText(noteValue);
        if (!normalizedKey || !note) return;
        const remappedKey = remapQuestionKeyThroughAliasMap(normalizedKey, aliasMap);
        if (!remappedKey || remappedKey !== normalizedKey) return;
        nextNotes[remappedKey] = note;
    });
    Object.entries(snapshotNotesByQuestionKey || {}).forEach(([key, noteValue]) => {
        const normalizedKey = String(key || '').trim();
        const note = sanitizeQuestionNoteText(noteValue);
        if (!normalizedKey || !note) return;
        const remappedKey = remapQuestionKeyThroughAliasMap(normalizedKey, aliasMap);
        if (!remappedKey || remappedKey === normalizedKey) return;
        if (!nextNotes[remappedKey]) nextNotes[remappedKey] = note;
    });
    questionNotes = {};
    questionNotesByQuestionKey = nextNotes;
    questionNotesLegacyMigrated = true;
}

function restoreActiveDuplicateDetectionSnapshot(snapshot, aliasMap = {}) {
    const mappedFilteredQuestionKeys = remapQuestionKeyListThroughAliasMap(snapshot?.filteredQuestionKeys, aliasMap);
    const mappedCurrentQuestionKey = remapQuestionKeyThroughAliasMap(snapshot?.currentQuestionKey, aliasMap);
    const shouldRestoreFilteredView = !!snapshot?.isFilterActive && mappedFilteredQuestionKeys.length > 0;

    userAnswers = buildIndexStateFromQuestionKeyBuckets(
        mergeQuestionKeyBucketsThroughAliasMap(snapshot?.userAnswersByQuestionKey, aliasMap),
        (rawValue) => {
            const parsed = Number(rawValue);
            return Number.isInteger(parsed) && parsed >= 0 ? parsed : undefined;
        }
    );
    knowledgeState = buildIndexStateFromQuestionKeyBuckets(
        mergeQuestionKeyBucketsThroughAliasMap(snapshot?.knowledgeStateByQuestionKey, aliasMap),
        (rawValue) => {
            const value = normalizeKnowledgeStateValue(rawValue);
            return value || undefined;
        }
    );
    savedQuestions = buildIndexStateFromQuestionKeyBuckets(
        mergeQuestionKeyBucketsThroughAliasMap(snapshot?.savedQuestionsByQuestionKey, aliasMap),
        (rawValue) => {
            const parsed = Number(rawValue);
            return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined;
        }
    );
    forcedChoiceOrderByQuestion = buildIndexStateFromQuestionKeyBuckets(
        mergeQuestionKeyBucketsThroughAliasMap(snapshot?.forcedChoiceOrderByQuestionKey, aliasMap),
        (rawValue, idx) => {
            const expectedLength = Array.isArray(questions?.[idx]?.c) ? questions[idx].c.length : 0;
            if (!Array.isArray(rawValue) || rawValue.length !== expectedLength) return undefined;
            const normalized = rawValue.map((value) => Number(value)).filter((value) => Number.isInteger(value) && value >= 0);
            return normalized.length === expectedLength ? normalized : undefined;
        }
    );
    mixedChoiceOrderByQuestion = buildIndexStateFromQuestionKeyBuckets(
        mergeQuestionKeyBucketsThroughAliasMap(snapshot?.mixedChoiceOrderByQuestionKey, aliasMap),
        (rawValue, idx) => {
            const expectedLength = Array.isArray(questions?.[idx]?.c) ? questions[idx].c.length : 0;
            if (!Array.isArray(rawValue) || rawValue.length !== expectedLength) return undefined;
            const normalized = rawValue.map((value) => Number(value)).filter((value) => Number.isInteger(value) && value >= 0);
            return normalized.length === expectedLength ? normalized : undefined;
        }
    );
    srsData = buildIndexStateFromQuestionKeyBuckets(
        mergeQuestionKeyBucketsThroughAliasMap(snapshot?.srsDataByQuestionKey, aliasMap),
        (rawValue) => (rawValue && typeof rawValue === 'object') ? rawValue : undefined
    );
    statsData = buildIndexStateFromQuestionKeyBuckets(
        mergeQuestionKeyBucketsThroughAliasMap(snapshot?.statsDataByQuestionKey, aliasMap),
        (rawValue) => Array.isArray(rawValue) ? rawValue : undefined
    );
    aiQuestionCacheByIndex = buildIndexStateFromQuestionKeyBuckets(
        mergeQuestionKeyBucketsThroughAliasMap(snapshot?.aiQuestionCacheByQuestionKey, aliasMap),
        (rawValue) => (rawValue && typeof rawValue === 'object') ? rawValue : undefined
    );
    adaptiveReviewSnapshot = buildIndexStateFromQuestionKeyBuckets(
        mergeQuestionKeyBucketsThroughAliasMap(snapshot?.adaptiveReviewSnapshotByQuestionKey, aliasMap),
        (rawValue, idx) => {
            if (!rawValue || typeof rawValue !== 'object') return undefined;
            return { ...rawValue, idx };
        }
    );
    adaptiveClearedAnswersBackup = buildIndexStateFromQuestionKeyBuckets(
        mergeQuestionKeyBucketsThroughAliasMap(snapshot?.adaptiveClearedAnswersBackupByQuestionKey, aliasMap),
        (rawValue) => {
            const parsed = Number(rawValue);
            return Number.isInteger(parsed) && parsed >= 0 ? parsed : undefined;
        }
    );

    if (shouldRestoreFilteredView) {
        filteredIndices = buildFilteredIndicesFromQuestionKeyList(mappedFilteredQuestionKeys);
        isFilterActive = true;
    } else {
        filteredIndices = [];
        isFilterActive = false;
    }

    if (!Array.isArray(filteredIndices) || filteredIndices.length === 0) {
        resetFilter();
    }

    currentQIndex = 0;
    if (Array.isArray(filteredIndices) && filteredIndices.length > 0) {
        const nextViewIndex = mappedCurrentQuestionKey
            ? filteredIndices.findIndex((realIdx) => buildQuestionPersistenceKeyFromQuestion(questions[realIdx]) === mappedCurrentQuestionKey)
            : -1;
        if (nextViewIndex >= 0) {
            currentQIndex = nextViewIndex;
        } else {
            const fallbackIndex = Number(snapshot?.currentQuestionViewIndex);
            currentQIndex = Number.isInteger(fallbackIndex)
                ? Math.min(Math.max(fallbackIndex, 0), filteredIndices.length - 1)
                : 0;
        }
    }
    if (isShuffled) {
        applySmartShuffle({
            showNotice: false,
            shuffleUnanswered: false,
            preserveCurrentQuestion: true,
            sortAnswered: false
        });
    }

    wrongAnswerHistory = createWrongAnswerHistory(
        remapQuestionKeyListThroughAliasMap(snapshot?.wrongAnswerQuestionKeys, aliasMap)
    );
    restoreQuestionNotesAfterDuplicateDetection(snapshot?.questionNotesByQuestionKey, aliasMap);
}

async function runActiveQuizDuplicateDetection(options = {}) {
    const {
        silent = false,
        showNoDuplicatesPopup = false,
        openDuplicateView = false,
        refreshSavedModalIfOpen = true
    } = options || {};

    if (isSavedDuplicateDetectionRunning) {
        return { removed: 0, stats: createDedupStatsTracker(), duplicateEntries: [], skipped: true };
    }

    isSavedDuplicateDetectionRunning = true;
    updateSavedDetectDuplicatesButtonState();

    try {
        const activeSetKeys = Array.isArray(sessionManifest?.jsons)
            ? sessionManifest.jsons.filter((setKey) => {
                const normalizedSetKey = String(setKey || '').trim();
                return normalizedSetKey && activeQuestionSetKeys.has(normalizedSetKey) && Array.isArray(loadedQuestionSets[normalizedSetKey]);
            })
            : [];

        if (!activeSetKeys.length) {
            if (!silent) showPopup('No active question banks available for duplicate detection.', null, true);
            return { removed: 0, stats: createDedupStatsTracker(), duplicateEntries: [] };
        }

        const snapshot = createActiveDuplicateDetectionSnapshot();
        const sharedIndex = { bySignature: new Map(), byStem: new Map(), byAnswerConcept: new Map() };
        const aliasMap = {};
        const dedupTracker = createDedupStatsTracker();

        for (const setKey of activeSetKeys) {
            const existingQuestions = Array.isArray(loadedQuestionSets[setKey]) ? loadedQuestionSets[setKey] : [];
            const result = dedupeQuestionArray(existingQuestions, { index: sharedIndex, setKey });
            const keptQuestions = Array.isArray(result.kept) ? result.kept : [];

            mergeDedupStats(dedupTracker, result.stats);
            if (keptQuestions.length === 0) {
                await removeQuestionBankForCleanup(setKey, { storageKeys: [setKey] });
                continue;
            }
            syncLoadedQuestionSetAliases(setKey, keptQuestions);

            (Array.isArray(result.duplicates) ? result.duplicates : []).forEach((entry) => {
                const duplicateKey = buildQuestionPersistenceKeyFromQuestion(entry?.duplicateQuestion);
                const canonicalKey = buildQuestionPersistenceKeyFromQuestion(entry?.canonicalQuestion);
                if (duplicateKey && canonicalKey && duplicateKey !== canonicalKey) {
                    aliasMap[duplicateKey] = canonicalKey;
                }
            });

            await saveQuestionDuplicateReport(setKey, result.duplicates, result.stats, { preserveExistingOnEmpty: true });
            try {
                await IDB.put('questions', setKey, keptQuestions);
            } catch (err) {
                console.warn(`Failed to persist deduped question bank for ${setKey}:`, err);
            }
        }

        rebuildActiveQuestions(false);
        restoreActiveDuplicateDetectionSnapshot(snapshot, aliasMap);
        updateSourceDropdown();

        if (isFlashcardMode) renderFlashcardGrid();
        else renderQuestion();

        try {
            await IDB.put('files', 'wrongAnswerHistory', Array.from(wrongAnswerHistory));
        } catch (err) {
            console.warn('Failed to persist wrong answer history after duplicate detection:', err);
        }
        await persistQuestionNotesStore();
        if (currentSessionId) {
            markSessionStateMutation();
            await SessionManager.save(false);
        } else if (!IS_EMBED_DEMO && hasPersistableSessionContent()) {
            writeQuickSessionSnapshot();
        }

        const duplicateEntries = await collectDuplicateEntriesForQuestionSetKeys(activeSetKeys, {
            includeCurrentQuestions: true,
            updateCache: true
        });
        updateSavedDuplicateButtons(duplicateEntries.length);

        if (openDuplicateView) {
            savedDuplicateSetKeysOverride = activeSetKeys.slice();
            savedDuplicateIncludeCurrentOverride = true;
            savedSourceFilters = new Set();
            isSavedDuplicateView = true;
            await openSavedModal();
        } else if (refreshSavedModalIfOpen && els.savedModal?.classList.contains('active')) {
            await openSavedModal();
        }

        if (!silent && showNoDuplicatesPopup && dedupTracker.removed <= 0) {
            showPopup('No duplicate questions found in the current quiz.', null, false);
        }

        return {
            removed: dedupTracker.removed,
            stats: dedupTracker,
            duplicateEntries
        };
    } finally {
        isSavedDuplicateDetectionRunning = false;
        updateSavedDetectDuplicatesButtonState();
    }
}
/* --- STAGED QUESTION IMPORT --- */
// Every JSON entry point (native picker, Import modal and drop, manual paste) goes
// through these steps. Staging is pure: it validates each row through the shared
// normalizer with a strict answer, assigns final set keys and source names, and
// translates any progress a backup carries into stable question keys using that
// backup's own row order. Nothing live changes until commit, which applies the
// whole batch in one synchronous transition and persists it with the session.
const SESSION_PROGRESS_FAMILIES = Object.freeze(['userAnswers', 'knowledgeState', 'savedQuestions', 'srsData', 'statsData']);
const QUESTION_IMPORT_REPORT_NOTICE_ID = 'question-import-report-notice';

function shapeImportedQuestionRow(rawRow) {
    const parsed = parseLegacyQuestionPayload(rawRow);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return parsed;
    const row = { ...parsed };
    if (!row.genModel) {
        const importedModel = row.generationModel || row.generatedModel || row.aiModel || row.model || row.modelName;
        if (typeof importedModel === 'string' && importedModel.trim()) row.genModel = importedModel.trim();
    }
    return row;
}

function envelopeCarriesQuestionProgress(data) {
    if (!data || typeof data !== 'object' || Array.isArray(data)) return false;
    return SESSION_PROGRESS_FAMILIES.some((family) => {
        const indexed = data[family];
        const keyed = data[`${family}ByQuestionKey`];
        return (indexed && typeof indexed === 'object' && !Array.isArray(indexed) && Object.keys(indexed).length > 0)
            || (keyed && typeof keyed === 'object' && !Array.isArray(keyed) && Object.keys(keyed).length > 0);
    });
}

// Backup progress is keyed by question identity before grouping, renaming, or dedup
// can move rows. A numeric entry is only meaningful against the backup's own rows;
// keyed entries are carried across any source renaming staging applied.
function collectImportedQuestionProgress(batch, data, rowsByPosition, originalKeys, choiceSlotMaps = []) {
    if (!data || typeof data !== 'object' || Array.isArray(data)) return;
    const finalKeyByOriginalKey = new Map();
    rowsByPosition.forEach((question, position) => {
        const originalKey = originalKeys[position];
        if (question && originalKey && !finalKeyByOriginalKey.has(originalKey)) {
            finalKeyByOriginalKey.set(originalKey, buildQuestionPersistenceKeyFromQuestion(question));
        }
    });
    // An indexed selected answer is an index into the backup row's own choices; it moves with
    // its choice when blanks are dropped. A selection of a blank or missing slot is
    // not reinterpreted against the compacted choices: it stays unanswered.
    const remapSelection = (value, slotMap) => {
        if (!Array.isArray(slotMap)) return value;
        const slot = typeof value === 'number' ? value : (typeof value === 'string' && /^\d+$/.test(value.trim()) ? Number(value) : NaN);
        if (!Number.isInteger(slot)) return value;
        const mapped = slotMap[slot];
        return Number.isInteger(mapped) && mapped >= 0 ? mapped : undefined;
    };
    SESSION_PROGRESS_FAMILIES.forEach((family) => {
        const target = batch.progressBuckets[family];
        const add = (questionKey, value, slotMap) => {
            const stored = family === 'userAnswers' ? remapSelection(value, slotMap) : value;
            if (stored === undefined) {
                batch.unmappedProgress += 1;
                return;
            }
            if (!Array.isArray(target[questionKey])) target[questionKey] = [];
            target[questionKey].push(stored);
        };
        const keyed = sanitizeQuestionKeyBucketObject(data[`${family}ByQuestionKey`]);
        if (Object.keys(keyed).length) {
            batch.isBackup = true;
            Object.entries(keyed).forEach(([originalKey, values]) => {
                const finalKey = finalKeyByOriginalKey.get(originalKey);
                if (!finalKey) {
                    batch.unmappedProgress += values.length;
                    return;
                }
                // Keyed progress was written against the exporter's own (already compacted) choices.
                values.forEach((value) => add(finalKey, value, null));
            });
            return;
        }
        const indexed = data[family];
        if (!indexed || typeof indexed !== 'object' || Array.isArray(indexed)) return;
        const indexKeys = Object.keys(indexed);
        if (!indexKeys.length) return;
        batch.isBackup = true;
        indexKeys
            .map((indexKey) => ({ indexKey, position: /^\d+$/.test(indexKey) ? Number(indexKey) : -1 }))
            .sort((left, right) => left.position - right.position)
            .forEach(({ indexKey, position }) => {
                const question = position >= 0 ? rowsByPosition[position] : null;
                if (!question) {
                    batch.unmappedProgress += 1;
                    return;
                }
                add(buildQuestionPersistenceKeyFromQuestion(question), indexed[indexKey], choiceSlotMaps[position]);
            });
    });
}

// envelopes: [{ name, data, policy }]. policy.mode 'grouped' splits rows into sets
// by their own set/source fields (the native picker); 'single' puts every row in
// policy.setKey with policy.sourceFile (the Import modal and manual paste).
// storedKeys: every key in the shared questions store (readStoredQuestionSetKeys).
function stageQuestionImportBatch(envelopes, { storedKeys = [] } = {}) {
    const batch = {
        groups: [],
        rejected: [],
        errors: [],
        renamed: [],
        progressBuckets: {},
        unmappedProgress: 0,
        isBackup: false
    };
    SESSION_PROGRESS_FAMILIES.forEach((family) => { batch.progressBuckets[family] = {}; });
    const liveKeys = collectUsedQuestionSetKeys();
    // The questions store is shared by every session, so a key another session (or
    // Recently Deleted) holds is reserved too: an import never overwrites its bank.
    const storedOnlyKeys = new Set();
    (Array.isArray(storedKeys) ? storedKeys : []).forEach((key) => {
        getQuestionSetKeyVariantsForDedup(key).forEach((variant) => {
            if (!liveKeys.has(variant)) storedOnlyKeys.add(variant);
        });
    });
    const reservedKeys = new Set([...liveKeys, ...storedOnlyKeys]);
    (Array.isArray(envelopes) ? envelopes : []).forEach((envelope) => {
        const fileName = String(envelope?.name || 'Imported file');
        const policy = envelope?.policy || { mode: 'grouped' };
        const rawRows = extractLegacyQuestionArrayFromPayload(envelope?.data);
        const rows = Array.isArray(rawRows) ? rawRows : [];
        if (!rows.length) batch.errors.push({ file: fileName, message: 'No quiz questions were found in this file.' });

        const originalKeys = [];
        const choiceSlotMaps = [];
        const rowsByPosition = rows.map((rawRow, position) => {
            let reason = '';
            const normalized = normalizeLegacyQuizQuestion(shapeImportedQuestionRow(rawRow), '', {
                strictAnswer: true,
                onReject: (message) => { reason = message; },
                onChoiceSlots: (slotMap) => { choiceSlotMaps[position] = slotMap; }
            });
            if (!normalized) {
                batch.rejected.push({ file: fileName, position: position + 1, reason: reason || 'The row is not a question.', original: rawRow });
                originalKeys.push('');
                return null;
            }
            originalKeys.push(buildQuestionPersistenceKeyFromQuestion(normalized));
            return normalized;
        });
        const validRows = rowsByPosition.filter(Boolean);

        if (policy.mode === 'single') {
            let setKey = String(policy.setKey || fileName).trim() || fileName;
            // A key live in this session adds to that bank; a key only another session
            // holds is that session's bank, so this import takes a free name instead.
            if (storedOnlyKeys.has(setKey)) {
                const requestedKey = setKey;
                setKey = allocateImportedQuizSetKey(requestedKey, reservedKeys);
                batch.renamed.push({ file: fileName, from: requestedKey, to: setKey });
            }
            validRows.forEach((question) => { question.sourceFile = String(policy.sourceFile || setKey); });
            if (validRows.length) batch.groups.push({ file: fileName, setKey, questions: validRows });
        } else {
            const baseName = fileName.replace(/\.(json|txt)$/i, '');
            buildImportedQuizSetGroups(validRows, baseName, reservedKeys).forEach((group) => {
                const requestedKey = normalizeImportedQuizSetKey(group.baseKey || baseName);
                if (group.setKey !== requestedKey && storedOnlyKeys.has(requestedKey)) {
                    batch.renamed.push({ file: fileName, from: requestedKey, to: group.setKey });
                }
                const fallbackSourceLabel = String(group?.sourceLabel || '').trim();
                group.questions.forEach((question) => {
                    const preservedSourceFile = String(question.sourceFile || question.src || fallbackSourceLabel || '').trim();
                    question.quizSetKey = group.setKey;
                    question.sourceFile = preservedSourceFile || group.setKey;
                });
                batch.groups.push({ file: fileName, setKey: group.setKey, questions: group.questions });
            });
        }
        collectImportedQuestionProgress(batch, envelope?.data, rowsByPosition, originalKeys, choiceSlotMaps);
    });
    return batch;
}

// Fills imported progress onto the rebuilt pool by question key. Progress the open
// session already holds for a question is the learner's current work and wins.
function projectImportedQuestionProgress(progressBuckets) {
    const outcome = { applied: 0, keptExisting: 0, unmatched: 0 };
    SESSION_PROGRESS_FAMILIES.forEach((family) => {
        const buckets = sanitizeQuestionKeyBucketObject(progressBuckets?.[family]);
        const total = Object.values(buckets).reduce((sum, values) => sum + values.length, 0);
        if (!total) return;
        const [read, write] = getSessionProgressAccessor(family);
        const next = { ...(read() || {}) };
        const cursor = {};
        let consumed = 0;
        for (let idx = 0; idx < questions.length; idx++) {
            const questionKey = buildQuestionPersistenceKeyByIndex(idx);
            const values = questionKey ? buckets[questionKey] : null;
            if (!values) continue;
            const position = cursor[questionKey] || 0;
            if (position >= values.length) continue;
            cursor[questionKey] = position + 1;
            consumed += 1;
            if (next[idx] !== undefined) {
                outcome.keptExisting += 1;
                continue;
            }
            next[idx] = values[position];
            outcome.applied += 1;
        }
        outcome.unmatched += total - consumed;
        write(next);
    });
    return outcome;
}

// Applies a staged batch. A backup restores into a session of its own (the open one
// is saved first); ordinary banks join the open session. Returns once the live
// state is in place; `persisted` settles when the banks and the session record
// commit together, and a failure there is reported by SessionSaveStatus.
// Every bank key in the shared questions store. Staging is synchronous, so the import
// commit reads these first. A failed read returns null: unknown ownership is not an
// empty store, and the import waits rather than risk reusing another session's bank.
async function readStoredQuestionSetKeys() {
    try {
        const keys = typeof IDB.getAllKeys === 'function' ? await IDB.getAllKeys('questions') : [];
        return (Array.isArray(keys) ? keys : []).map((key) => String(key || '').trim()).filter(Boolean);
    } catch (error) {
        console.warn('Stored quiz names could not be read; the import was not applied.', error);
        return null;
    }
}

// Creates the bank for every name this import introduces, in one create-only
// transaction. Resolves true, 'taken' when another writer already holds one of the
// names, or false on any other storage failure. Banks live in this session add rows.
async function claimNewQuestionBankKeys(batch) {
    const liveKeys = collectUsedQuestionSetKeys();
    const claims = (batch?.groups || []).filter((group) => !liveKeys.has(group.setKey));
    if (!claims.length || typeof IDB.runWriteTransaction !== 'function') return true;
    try {
        await IDB.runWriteTransaction(['questions'], (tx, write, abortWith) => {
            const store = tx.objectStore('questions');
            claims.forEach((group) => {
                const request = store.add(group.questions, group.setKey);
                request.onerror = () => abortWith(request.error);
            });
        }, SESSION_SAVE_WITH_BANKS_TIMEOUT_MS);
        return true;
    } catch (error) {
        if (error?.name === 'ConstraintError') return 'taken';
        console.warn('A new quiz bank could not be created; the import was not applied.', error);
        return false;
    }
}

// An import belongs to the session open when the learner started it. Opening another
// session, even the same one again, bumps sessionOpenEpoch, so the owner is both.
function captureQuestionImportOwner() {
    return { sessionId: currentSessionId, openEpoch: sessionOpenEpoch };
}

function isQuestionImportOwnerDeparted(owner) {
    return !!owner && (currentSessionId !== owner.sessionId || sessionOpenEpoch !== owner.openEpoch);
}

const QUESTION_IMPORT_SESSION_MOVED_MESSAGE = 'The open session changed while the import was finishing, so nothing was imported. Try the import again.';

async function commitStagedQuestionImport(envelopes, stagedBatch, admittedOwner = null) {
    const nothing = { committed: false, importedCount: 0, dedupStats: createDedupStatsTracker(), progress: { applied: 0, keptExisting: 0, unmatched: 0 }, persisted: Promise.resolve(true) };
    const blocked = (reason) => ({
        ...nothing,
        blockedReason: reason,
        batch: { ...(stagedBatch || {}), groups: [], errors: [...(stagedBatch?.errors || []), { file: 'Import', message: reason }] }
    });
    // The import belongs to the session open when it was admitted (before any file read).
    // A switch during any await must not attach rows to, or wipe, the session moved to.
    let ownerSessionId = admittedOwner ? admittedOwner.sessionId : currentSessionId;
    let ownerEpoch = admittedOwner ? admittedOwner.openEpoch : sessionOpenEpoch;
    const ownerDeparted = () => isQuestionImportOwnerDeparted({ sessionId: ownerSessionId, openEpoch: ownerEpoch });
    const sessionMovedMessage = QUESTION_IMPORT_SESSION_MOVED_MESSAGE;
    if (ownerDeparted()) return blocked(sessionMovedMessage);
    // Re-stage against every stored bank so a name another session holds is never reused.
    const storedKeys = await readStoredQuestionSetKeys();
    if (ownerDeparted()) return blocked(sessionMovedMessage);
    if (storedKeys === null) {
        return blocked('Saved quiz names could not be read, so nothing was imported. Try the import again.');
    }
    let batch = storedKeys.length ? stageQuestionImportBatch(envelopes, { storedKeys }) : stagedBatch;
    if (!batch?.groups?.length) return { ...nothing, batch };
    let restoredIntoNewSession = false;
    if (batch.isBackup && hasPersistableSessionContent()) {
        // The open session must be safely saved before a backup replaces it on screen.
        const leavingKeys = Array.from(collectUsedQuestionSetKeys());
        if (await SessionManager.save(false) === false) {
            return blocked('Your open session could not be saved, so the backup was not restored. Your work is still open; try again after it saves.');
        }
        if (ownerDeparted()) return blocked(sessionMovedMessage);
        startNewSession(true, { quiet: true, pulseImport: false });
        ownerSessionId = currentSessionId;
        ownerEpoch = sessionOpenEpoch;
        restoredIntoNewSession = true;
        // Keys and sources are allocated against the session the rows land in; the
        // session just left owns its banks even if this second read fails.
        const keysAfterSwitch = await readStoredQuestionSetKeys();
        if (ownerDeparted()) return blocked(sessionMovedMessage);
        batch = stageQuestionImportBatch(envelopes, {
            storedKeys: Array.from(new Set([...(keysAfterSwitch || storedKeys), ...leavingKeys]))
        });
    } else if (!currentSessionId) {
        startNewSession(false, { quiet: true, pulseImport: false });
        ownerSessionId = currentSessionId;
        ownerEpoch = sessionOpenEpoch;
    }
    // Two tabs can read the same free name. Each new bank is created before it is used,
    // so the later tab finds the name taken, re-reads the stored names and takes another.
    for (let attempt = 0; ; attempt += 1) {
        const claim = await claimNewQuestionBankKeys(batch);
        if (ownerDeparted()) return blocked(sessionMovedMessage);
        if (claim === true) break;
        if (claim !== 'taken' || attempt >= 4) {
            return blocked('Your quiz could not be saved, so nothing was imported. Try the import again.');
        }
        const keysNow = await readStoredQuestionSetKeys();
        if (ownerDeparted()) return blocked(sessionMovedMessage);
        if (keysNow === null) {
            return blocked('Saved quiz names could not be read, so nothing was imported. Try the import again.');
        }
        batch = stageQuestionImportBatch(envelopes, { storedKeys: keysNow });
        if (!batch?.groups?.length) return { ...nothing, batch };
    }
    const destinationSessionId = currentSessionId;
    const dedupStats = createDedupStatsTracker();
    const committedSets = [];
    batch.groups.forEach((group) => {
        // A bank already live in this session gains rows; its earlier questions stay
        // first and are never dropped, and new rows are deduplicated against them.
        const existingRows = Array.isArray(loadedQuestionSets[group.setKey]) ? loadedQuestionSets[group.setKey] : [];
        const dedupeResult = applySmartImportDedup(group.setKey, existingRows.concat(group.questions));
        const existingRowSet = new Set(existingRows);
        const addedRows = dedupeResult.questions.filter((row) => !existingRowSet.has(row));
        mergeDedupStats(dedupStats, dedupeResult.stats);
        // A set whose rows were all duplicates adds nothing and removes nothing.
        if (!addedRows.length) return;
        const bankRows = existingRows.concat(addedRows);
        loadedQuestionSets[group.setKey] = bankRows;
        activeQuestionSetKeys.add(group.setKey);
        if (!sessionManifest.jsons.includes(group.setKey)) sessionManifest.jsons.push(group.setKey);
        committedSets.push({ ...group, questions: bankRows, addedCount: addedRows.length, duplicates: dedupeResult.duplicates, stats: dedupeResult.stats });
    });
    rebuildActiveQuestions();
    const progress = projectImportedQuestionProgress(batch.progressBuckets);
    committedSets.forEach((set) => {
        queueQuestionBankPersistence(destinationSessionId, set.setKey, set.questions);
        // The duplicate report is derived; it never decides whether the import saved.
        void Promise.resolve()
            .then(() => saveQuestionDuplicateReport(set.setKey, set.duplicates, set.stats))
            .catch((error) => console.warn('Duplicate report save skipped:', error));
    });
    markSessionDirty();
    const persisted = (committedSets.length || progress.applied) ? SessionManager.save(false) : Promise.resolve(true);
    return {
        committed: committedSets.length > 0 || progress.applied > 0,
        batch,
        destinationSessionId,
        owner: { sessionId: ownerSessionId, openEpoch: ownerEpoch },
        restoredIntoNewSession,
        importedCount: committedSets.reduce((sum, set) => sum + set.addedCount, 0),
        committedSets,
        dedupStats,
        progress,
        persisted
    };
}

function exportSkippedImportRows(rejected) {
    const rows = Array.isArray(rejected) ? rejected : [];
    if (!rows.length) return;
    const payload = {
        kind: 'memora-import-skipped-rows',
        version: 1,
        exportedAt: new Date().toISOString(),
        rows: rows.map((row) => ({ file: row.file, position: row.position, reason: row.reason, original: row.original }))
    };
    triggerBlobDownload(new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' }), 'memora-skipped-import-rows.json');
}

// Says what an import actually did. A clean import is a brief popup; anything the
// learner needs to know about (skipped rows, progress that did not land, files that
// could not be read) stays on screen with the skipped rows available to export.
function showQuestionImportReport(outcome, extraErrors = []) {
    const batch = outcome?.batch || {};
    const rejected = Array.isArray(batch.rejected) ? batch.rejected : [];
    const errors = (Array.isArray(batch.errors) ? batch.errors : []).concat(Array.isArray(extraErrors) ? extraErrors : []);
    const progress = outcome?.progress || { applied: 0, keptExisting: 0, unmatched: 0 };
    const unmatchedProgress = (Number(progress.unmatched) || 0) + (Number(batch.unmappedProgress) || 0);
    const plural = (count, one, many) => `${count} ${count === 1 ? one : many}`;
    const parts = [];
    const importedCount = Number(outcome?.importedCount) || 0;
    if (importedCount > 0) {
        parts.push(outcome?.restoredIntoNewSession
            ? `Restored ${plural(importedCount, 'question', 'questions')} into a new session.`
            : `Imported ${plural(importedCount, 'question', 'questions')}.`);
    } else {
        parts.push('No questions were imported.');
    }
    const dedupSummary = formatDedupSummary(outcome?.dedupStats);
    if (dedupSummary) parts.push(dedupSummary);
    if (progress.applied > 0) parts.push(`Restored progress on ${plural(progress.applied, 'question', 'questions')}.`);
    if (progress.keptExisting > 0) parts.push(`${plural(progress.keptExisting, 'question', 'questions')} kept the progress already in this session.`);
    if (unmatchedProgress > 0) parts.push(`${plural(unmatchedProgress, 'saved progress entry', 'saved progress entries')} did not match an imported question.`);
    if (rejected.length) parts.push(`${plural(rejected.length, 'row was', 'rows were')} skipped because the question or its correct answer could not be confirmed.`);
    const renamed = Array.isArray(batch.renamed) ? batch.renamed : [];
    renamed.forEach((entry) => parts.push(`Saved as ${entry.to}: another session already has a quiz named ${entry.from}.`));
    errors.forEach((error) => parts.push(`${error.file}: ${error.message}`));
    const summary = parts.join(' ');
    const needsAttention = rejected.length > 0 || errors.length > 0 || unmatchedProgress > 0 || importedCount === 0 || renamed.length > 0;

    document.getElementById(QUESTION_IMPORT_REPORT_NOTICE_ID)?.remove();
    if (!needsAttention) {
        showPopupText(summary, null, false);
        return;
    }
    const notice = document.createElement('div');
    notice.id = QUESTION_IMPORT_REPORT_NOTICE_ID;
    notice.className = 'quiz-action-toast question-import-report-notice show';
    notice.setAttribute('role', 'status');
    const message = document.createElement('span');
    message.className = 'quiz-action-toast-message';
    message.textContent = summary;
    notice.appendChild(message);
    const makeAction = (label, onClick) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'quiz-action-toast-action';
        button.textContent = label;
        button.addEventListener('click', onClick);
        notice.appendChild(button);
    };
    if (rejected.length) makeAction('Export skipped rows', () => exportSkippedImportRows(rejected));
    makeAction('Dismiss', () => notice.remove());
    document.body.appendChild(notice);
}

/* --- ROBUST IMPORT HANDLER --- */
$('universal-import').addEventListener('change', async function (e) {
    if (IS_EMBED_DEMO) {
        e.target.value = '';
        demoUnavailableFeature('Import');
        return;
    }
    // Admit the import against the session open now, before any conversion or file read.
    const importOwner = captureQuestionImportOwner();
    // Pictures arrive as one-page PDFs (convertMemoraImageSourceFiles).
    const files = await convertMemoraImageSourceFiles(Array.from(e.target.files));
    if (files.length === 0) return;
    const skipAutoPdfOpen = e.target.dataset.memoraSkipAutoPdfOpen === 'true';
    const keepLiveRailContextOpen = e.target.dataset.memoraKeepLiveRailContextOpen === 'true';
    delete e.target.dataset.memoraSkipAutoPdfOpen;
    delete e.target.dataset.memoraKeepLiveRailContextOpen;

    console.log(`Starting import of ${files.length} files...`);
    showPopup("Processing files...", null, false);

    const jsonFiles = files.filter(f => f.name.toLowerCase().endsWith('.json') || f.name.toLowerCase().endsWith('.txt'));
    const pdfFiles = files.filter(f => f.name.toLowerCase().endsWith('.pdf'));
    const presentationFiles = files.filter(f => {
        const name = f.name.toLowerCase();
        return name.endsWith('.pptx');
    });

    // PowerPoint uses the quiz-generation import owner so browser rendering,
    // storage, session manifests, and Gemini upload stay in one flow.
    if (presentationFiles.length > 0) {
        if (typeof handleImportFiles !== 'function') {
            showPopup('PowerPoint import is still loading. Please try again.', null, true);
            e.target.value = '';
            return;
        }
        try {
            await handleImportFiles(files, {
                pdfMode: keepLiveRailContextOpen ? 'source' : 'automatic',
                skipPdfRoutePrompt: true,
                skipAutoPdfOpen,
                sourceContext: keepLiveRailContextOpen ? 'live-rail-context' : '',
                importOwner
            });
        } catch (error) {
            console.error('PowerPoint import failed:', error);
            showPopupText(`PowerPoint import failed: ${error?.message || 'Unknown error'}`, null, true);
        }
        e.target.value = '';
        return;
    }

    let filesProcessed = 0;
    const hadSessionContentBeforeImport = getCurrentSessionContentPresence().hasSessionContent;

    try {
        // 1. Stage and commit question files first. A backup restores into a session
        // of its own, so the destination is settled before any source attaches.
        const readFile = (file) => new Promise((resolve) => {
            const reader = new FileReader();
            reader.onload = (event) => resolve({ content: event.target.result, filename: file.name });
            reader.onerror = () => resolve({ content: null, filename: file.name });
            reader.readAsText(file);
        });
        const results = await Promise.all(jsonFiles.map(readFile));
        const envelopes = [];
        const unreadableFiles = [];
        results.forEach((res) => {
            if (!res || typeof res.content !== 'string') {
                unreadableFiles.push({ file: res?.filename || 'A file', message: 'The file could not be read.' });
                return;
            }
            try {
                const text = res.content.replace(/```json/g, '').replace(/```/g, '').trim();
                envelopes.push({ name: res.filename, data: JSON.parse(text), policy: { mode: 'grouped' } });
                filesProcessed++;
            } catch (err) {
                console.error("Parse Error " + res.filename, err);
                unreadableFiles.push({ file: res.filename, message: `This is not valid JSON (${err.message}).` });
            }
        });
        const questionImport = jsonFiles.length
            ? await commitStagedQuestionImport(envelopes, stageQuestionImportBatch(envelopes), importOwner)
            : null;
        const qFoundTotal = questionImport?.importedCount || 0;
        // A committed import may have settled into a session of its own (backup, or none open).
        const sourceOwner = questionImport?.owner || importOwner;
        let importDeparted = isQuestionImportOwnerDeparted(sourceOwner);

        // 2. Process PDFs
        for (const pickedFile of pdfFiles) {
            if (importDeparted) break;
            console.log(`Processing PDF: ${pickedFile.name}`);
            if (isMemoraPdfSourceCurrentlyLoaded(pickedFile.name)) {
                showMemoraDuplicatePdfSourceWarning(pickedFile.name);
                continue;
            }
            const { file, renamedFrom } = await claimMemoraSourceFileName(pickedFile);
            if (isQuestionImportOwnerDeparted(sourceOwner)) {
                importDeparted = true;
                break;
            }
            if (renamedFrom) showMemoraSourceRenamedNotice(renamedFrom, file.name);
            recordMemoraSourceRevision(file.name, { byteLength: file.size });
            if (loadedPdfFiles[file.name]) URL.revokeObjectURL(loadedPdfFiles[file.name]);
            loadedPdfFiles[file.name] = URL.createObjectURL(file);

            // Durable capture is best-effort and must not block live import.
            void putMemoraSourceFileRecord(file.name, file)
                .catch((dbErr) => {
                    console.warn("DB Save Failed for PDF, using memory only", dbErr);
                    showMemoraSourceSaveFailure(file);
                });

            // Begin the Gemini upload optimization in the background as soon as a
            // large PDF is imported. The original PDF remains the viewing/storage copy.
            if (typeof window.startQuizGenPdfUploadPreparation === 'function') {
                void window.startQuizGenPdfUploadPreparation(file, file.name);
            }

            // PDF text indexing disabled.

            // Add to session manifest if not present
            if (!sessionManifest.pdfs.includes(file.name)) sessionManifest.pdfs.push(file.name);

            if (!skipAutoPdfOpen && !isViewerOpen) openPdf(file.name);
            filesProcessed++;
        }

        // Close import modal
        $('import-modal').classList.remove('active');

        // A departed import never enters quiz mode, opens Create or saves in the destination.
        if (importDeparted) {
            if (questionImport) showQuestionImportReport(questionImport, unreadableFiles);
            showPopupText('The open session changed while the import was finishing, so the remaining files were not added. Try the import again.', null, true, { dismissible: true });
            e.target.value = '';
            return;
        }

        const showImportedPdfGenerationModal = () => {
            const pdfFileArray = Object.keys(loadedPdfFiles).map(name => ({ name, url: loadedPdfFiles[name] }));
            console.log('Showing quiz gen modal with PDFs:', pdfFileArray);
            showQuizGenerationModal(pdfFileArray, { skipBackdropFade: true });
        };
        // The rail picker stays the surface when it started the import: it re-reads the
        // session and re-renders itself, so entering the quiz card here would pull the
        // learner out of the launcher they are still standing in.
        const enterImportedQuizSurface = () => {
            if (keepLiveRailContextOpen) return;
            if (!hadSessionContentBeforeImport) enterQuizMode({ surface: 'dashboard' });
            else enterQuizMode();
        };

        if (questionImport) showQuestionImportReport(questionImport, unreadableFiles);

        if (qFoundTotal > 0) {
            if (questionImport.batch?.isBackup) enterQuizMode();
            else enterImportedQuizSurface();
            if (pdfFiles.length > 0) {
                // Sources attached after the bank commit; save them once that save settles
                // so an older snapshot can never land after this one.
                void questionImport.persisted.then(() => SessionManager.save(false));
                if (!keepLiveRailContextOpen) showImportedPdfGenerationModal();
            }
        } else if (pdfFiles.length > 0) {
            // Create a session if one doesn't exist, so PDFs are saved
            if (!currentSessionId) currentSessionId = Date.now().toString();
            enterImportedQuizSurface();
            if (!skipAutoPdfOpen) toggleViewer(true);

            // Keep the quiz card from painting between PDF import and Create.
            // The modal handoff owns closing the import surface and suppresses
            // its backdrop animation while the workspace is already visible.
            if (!keepLiveRailContextOpen) showImportedPdfGenerationModal();

            // Save session immediately to persist PDFs
            try {
                await SessionManager.save(false);
            } catch (saveErr) {
                console.warn('Session save failed after PDF import:', saveErr);
            }
        }


    } catch (e) {
        console.error("CRITICAL IMPORT ERROR", e);
        showPopupText(`Import failed: ${e?.message || 'Unknown error'}`, null, true, { dismissible: true });
    }

    updateSourceDropdown();
    syncContentDependentControls();
    e.target.value = '';
});


/* ============================================================================
   SECTION 4: SESSION MANAGEMENT
   Save, Load, History, SRS Algorithm
   ============================================================================ */
function hasPersistableSessionContent() {
    const hasPdfs = Array.isArray(sessionManifest?.pdfs) && sessionManifest.pdfs.length > 0;
    const hasQuizzes = Object.keys(loadedQuestionSets || {}).length > 0;
    return hasPdfs || hasQuizzes || currentSessionExplicit === true
        || !!normalizeSessionNameInput(currentSessionCustomName);
}

function hasPersistableSavedSessionContent(session) {
    const manifest = getSessionManifestFromSavedSession(session);
    const activeSets = getSessionActiveSetNames(session);
    return (Array.isArray(manifest.pdfs) && manifest.pdfs.length > 0)
        || (Array.isArray(manifest.jsons) && manifest.jsons.length > 0)
        || activeSets.length > 0
        || session?.explicit === true
        || !!getSavedSessionDisplayNameValue(session);
}

async function pruneEmptySavedSessions() {
    if (IS_EMBED_DEMO) return 0;

    // Clean up stale temporary question sets from streaming
    try {
        const qKeys = await readSessionStoreKeysWithTimeout('questions', 3000) || [];
        for (const key of qKeys) {
            if (typeof key === 'string' && key.endsWith('::temp')) {
                await IDB.del('questions', key);
                console.log(`Pruned stale temporary question key: ${key}`);
            }
        }
    } catch (e) {
        console.warn('Failed to prune stale temp question keys:', e);
    }

    await pruneEmptyQuestionBanks({ saveCurrentSession: true });

    // Summaries carry the manifest and active-set names this check needs.
    const sessions = await readSavedSessionSummaries({ timeoutMs: 3000 }) || [];
    const emptySessions = sessions.filter((session) => !hasPersistableSavedSessionContent(session));
    if (!emptySessions.length) return 0;

    const emptySessionIds = new Set(emptySessions.map((session) => normalizeSessionId(session?.id)).filter(Boolean));
    for (const session of emptySessions) {
        await IDB.del('sessions', session.id);
    }

    const storedId = normalizeSessionId(localStorage.getItem('lastSessionId'));
    if (storedId && emptySessionIds.has(storedId)) {
        localStorage.removeItem('lastSessionId');
    }
    const snapshot = getQuickSessionSnapshot();
    if (snapshot && emptySessionIds.has(normalizeSessionId(snapshot.sessionId))) {
        clearQuickSessionSnapshot();
    }
    return emptySessions.length;
}

async function discardEmptyCurrentSessionPersistence() {
    if (IS_EMBED_DEMO || hasPersistableSessionContent()) return false;
    const sessionId = normalizeSessionId(currentSessionId);
    clearQuickSessionSnapshot(sessionId || null);
    if (!sessionId) return false;

    const storedSession = await IDB.get('sessions', sessionId);
    if (storedSession) {
        await IDB.del('sessions', sessionId);
    }

    if (normalizeSessionId(localStorage.getItem('lastSessionId')) === sessionId) {
        await resolveLastSessionId({ preferStored: false });
    } else if (storedSession) {
        void syncWelcomeScreenState();
    }
    return !!storedSession;
}

const SESSION_RESTORE_QUIZ_TIMEOUT_MS = 8000;
const SESSION_RESTORE_PDF_TIMEOUT_MS = 12000;
const SESSION_RESTORE_TUTOR_CHECKPOINT_TIMEOUT_MS = 4000;
const SESSION_RESTORE_SESSION_TIMEOUT_MS = 5000;

function withMemoraStorageTimeout(operation, timeoutMs = 5000, label = 'Storage operation') {
    const safeTimeoutMs = Math.max(1000, Number(timeoutMs) || 5000);
    return new Promise((resolve, reject) => {
        let settled = false;
        const finish = (callback, value) => {
            if (settled) return;
            settled = true;
            window.clearTimeout(timer);
            callback(value);
        };
        const timer = window.setTimeout(() => {
            finish(reject, new Error(`${label} timed out after ${safeTimeoutMs}ms`));
        }, safeTimeoutMs);

        let pending;
        try {
            pending = typeof operation === 'function' ? operation() : operation;
        } catch (error) {
            finish(reject, error);
            return;
        }
        Promise.resolve(pending).then(
            value => finish(resolve, value),
            error => finish(reject, error)
        );
    });
}

async function readSessionStoreValueWithTimeout(storeName, key, timeoutMs = SESSION_RESTORE_QUIZ_TIMEOUT_MS) {
    if (!key) return null;
    const timeoutToken = Symbol('session-restore-timeout');
    const timeoutPromise = new Promise((resolve) => {
        setTimeout(() => resolve(timeoutToken), Math.max(1000, Number(timeoutMs) || 1000));
    });

    try {
        const value = await Promise.race([
            IDB.get(storeName, key),
            timeoutPromise
        ]);
        if (value === timeoutToken) {
            console.warn(`Session restore timed out reading ${storeName}:${key}`);
            return null;
        }
        return value ?? null;
    } catch (error) {
        console.warn(`Session restore failed reading ${storeName}:${key}:`, error);
        return null;
    }
}

async function readSessionStoreKeysWithTimeout(storeName, timeoutMs = 3000) {
    const timeoutToken = Symbol('session-restore-keys-timeout');
    const timeoutPromise = new Promise((resolve) => {
        setTimeout(() => resolve(timeoutToken), Math.max(1000, Number(timeoutMs) || 1000));
    });

    try {
        const keys = await Promise.race([
            IDB.getAllKeys(storeName),
            timeoutPromise
        ]);
        if (keys === timeoutToken) {
            console.warn(`Session restore timed out reading ${storeName} keys`);
            return [];
        }
        return Array.isArray(keys) ? keys : [];
    } catch (error) {
        console.warn(`Session restore failed reading ${storeName} keys:`, error);
        return [];
    }
}

async function readSessionStoreAllWithTimeout(storeName, timeoutMs = 3000) {
    const timeoutToken = Symbol('session-restore-all-timeout');
    const timeoutPromise = new Promise((resolve) => {
        setTimeout(() => resolve(timeoutToken), Math.max(1000, Number(timeoutMs) || 1000));
    });

    try {
        const values = await Promise.race([
            IDB.getAll(storeName),
            timeoutPromise
        ]);
        if (values === timeoutToken) {
            console.warn(`Session restore timed out reading all ${storeName}`);
            return [];
        }
        return Array.isArray(values) ? values : [];
    } catch (error) {
        console.warn(`Session restore failed reading all ${storeName}:`, error);
        return [];
    }
}

function stripLegacyQuestionPayloadFence(text) {
    return String(text || '')
        .trim()
        .replace(/^```(?:json|ndjson)?\s*/i, '')
        .replace(/\s*```$/i, '')
        .trim();
}

function parseLegacyQuestionPayload(rawValue) {
    if (typeof rawValue !== 'string') return rawValue;
    const text = stripLegacyQuestionPayloadFence(rawValue);
    if (!text) return [];

    try {
        return JSON.parse(text);
    } catch (_) {
        const arrayMatch = text.match(/\[[\s\S]*\]/);
        if (arrayMatch) {
            try {
                return JSON.parse(arrayMatch[0]);
            } catch (_arrayErr) { }
        }

        const objectMatch = text.match(/\{[\s\S]*\}/);
        if (objectMatch) {
            try {
                return JSON.parse(objectMatch[0]);
            } catch (_objectErr) { }
        }

        const parsedLines = [];
        text.split(/\r?\n/)
            .map(line => line.trim())
            .filter(Boolean)
            .forEach((line) => {
                const cleaned = line.replace(/^,\s*/, '').replace(/,\s*$/, '');
                if (!cleaned || cleaned === '[' || cleaned === ']' || cleaned === ',') return;
                try {
                    const parsed = JSON.parse(cleaned);
                    if (Array.isArray(parsed)) parsedLines.push(...parsed);
                    else if (parsed && typeof parsed === 'object') parsedLines.push(parsed);
                } catch (_lineErr) { }
            });
        return parsedLines.length ? parsedLines : null;
    }
}

function sanitizeQuestionDisplayText(value) {
    if (value === null || value === undefined) return '';
    let text = typeof value === 'string' ? value : String(value);
    // A typed ">=" or "<=" reads as code in a stem or choice; show the real sign.
    text = text.replace(/>=/g, '\u2265').replace(/<=/g, '\u2264');
    // JSON decodes a model's LaTeX "\beta" as a backspace then "eta" ("\theta" as a tab then
    // "heta"), which renders as an empty box. Put the backslash back so the table below finds it.
    if (/[\u0008\u0009\u000c\u000d]/.test(text)) {
        text = text
            .replace(/\u0008(?=[a-zA-Z])/g, '\\b')
            .replace(/\u000c(?=[a-zA-Z])/g, '\\f')
            .replace(/\t(?=(?:heta|au|imes)(?![a-zA-Z]))/g, '\\t')
            .replace(/\r(?=ho(?![a-zA-Z]))/g, '\\r');
    }
    if (!text.includes('\\')) return text;

    // "\u00" that cannot complete a four-hex escape is always a dropped micro sign.
    text = text.replace(/\\+u00\s*(?=[g-zG-Z])/g, 'µ');
    // LaTeX micro/degree prefixes glued to a unit: "\mu m", "\mum", "\mu g".
    text = text.replace(/\\+(?:mu|mum)\s*(?=[a-zA-Z])/g, 'µ');
    text = text.replace(/\\+um\b/gi, 'µm');
    // Well-formed escapes that survived as literal text rather than being decoded.
    text = text.replace(/\\+u([0-9a-fA-F]{4})/g, (match, hex) => {
        const codePoint = parseInt(hex, 16);
        return Number.isFinite(codePoint) ? String.fromCharCode(codePoint) : match;
    });
    text = text.replace(/\\+x([0-9a-fA-F]{2})/g, (match, hex) => {
        const codePoint = parseInt(hex, 16);
        return Number.isFinite(codePoint) ? String.fromCharCode(codePoint) : match;
    });

    const latexSymbols = {
        leftrightarrow: '↔',
        rightarrow: '→',
        leftarrow: '←',
        approx: '≈',
        propto: '∝',
        degree: '°',
        lambda: 'λ',
        epsilon: 'ε',
        sigma: 'σ',
        omega: 'ω',
        Omega: 'Ω',
        Delta: 'Δ',
        alpha: 'α',
        beta: 'β',
        gamma: 'γ',
        delta: 'δ',
        kappa: 'κ',
        micro: 'µ',
        times: '×',
        infty: '∞',
        circ: '°',
        cdot: '·',
        phi: 'φ',
        tau: 'τ',
        theta: 'θ',
        rho: 'ρ',
        div: '÷',
        neq: '≠',
        geq: '≥',
        leq: '≤',
        sim: '~',
        pm: '±',
        mp: '∓',
        ne: '≠',
        ge: '≥',
        le: '≤',
        mu: 'µ',
        to: '→'
    };
    const commandNames = Object.keys(latexSymbols).sort((left, right) => right.length - left.length);
    const commandPattern = new RegExp(`\\\\+(${commandNames.join('|')})(?![a-zA-Z])`, 'g');
    text = text.replace(commandPattern, (match, command) => latexSymbols[command] || match);
    text = text.replace(/\\+(?:text|mathrm|mathbf|operatorname)\s*\{([^{}\n]*)\}/gi, '$1');
    return text;
}
window.sanitizeQuestionDisplayText = sanitizeQuestionDisplayText;

function normalizeLegacyTextValue(value) {
    if (value === null || value === undefined) return '';
    let rawStr = '';
    if (typeof value === 'object') {
        const textCandidate = ['text', 'label', 'value', 'answer', 'choice', 'content', 'html']
            .map(key => value?.[key])
            .find(candidate => candidate !== null && candidate !== undefined && String(candidate).trim());
        if (textCandidate === undefined) return '';
        rawStr = String(textCandidate).trim();
    } else {
        rawStr = String(value).trim();
    }
    return typeof sanitizeQuestionDisplayText === 'function'
        ? sanitizeQuestionDisplayText(rawStr)
        : rawStr;
}

function pickFirstLegacyText(rawQuestion, keys) {
    for (const key of keys) {
        const value = normalizeLegacyTextValue(rawQuestion?.[key]);
        if (value) return value;
    }
    return '';
}

function getLegacyChoiceSortValue(key) {
    const normalized = String(key || '').trim().toLowerCase().replace(/[^a-z0-9]+/g, '');
    const letterMatch = normalized.match(/^(?:choice|option|answer)?([a-h])$/);
    if (letterMatch) return { group: 0, value: letterMatch[1].charCodeAt(0) - 97 };
    const numberMatch = normalized.match(/^(?:choice|option|answer)?(\d+)$/);
    if (numberMatch) return { group: 1, value: Number(numberMatch[1]) };
    return { group: 2, value: normalized };
}

function compareLegacyChoiceKeys(a, b) {
    const left = getLegacyChoiceSortValue(a);
    const right = getLegacyChoiceSortValue(b);
    if (left.group !== right.group) return left.group - right.group;
    if (typeof left.value === 'number' && typeof right.value === 'number') return left.value - right.value;
    return String(left.value).localeCompare(String(right.value));
}

// Labeled choice keys of one family keep their own slots: letters (A, optionC,
// choice_d) by letter, numbers (2, option3) one-based unless a 0 is present. A
// missing first, middle, or last label leaves a blank slot, so an answer naming C or
// 3 still names that choice. Returns null for keys that are not one labeled family.
function getLegacyLabeledChoiceSlots(candidate) {
    if (!candidate || typeof candidate !== 'object' || Array.isArray(candidate)) return null;
    const keys = Object.keys(candidate);
    if (!keys.length) return null;
    const parsed = keys.map((key) => {
        const match = String(key).trim().match(/^(?:(choice|option|answer)[_\s-]?)?([a-h]|\d+)$/i);
        if (!match) return null;
        const prefix = (match[1] || '').toLowerCase();
        const label = match[2];
        return /^\d+$/.test(label)
            ? { key, family: `${prefix}#`, number: Number(label) }
            : { key, family: `${prefix}@`, slot: label.toUpperCase().charCodeAt(0) - 65 };
    });
    if (parsed.some((entry) => !entry) || new Set(parsed.map((entry) => entry.family)).size !== 1) return null;
    let base = 0;
    if (parsed[0].family.endsWith('#')) {
        const numbers = parsed.map((entry) => entry.number);
        base = numbers.includes(0) ? 0 : 1;
        parsed.forEach((entry) => { entry.slot = entry.number - base; });
    }
    const slots = parsed.map((entry) => entry.slot);
    if (new Set(slots).size !== slots.length || Math.min(...slots) < 0 || Math.max(...slots) > 7) return null; // A-H, as answer letters allow
    return { entries: parsed, base, numbered: parsed[0].family.endsWith('#') };
}

function legacyChoiceValuesFromCandidate(candidate) {
    if (Array.isArray(candidate)) return candidate;
    // Blank slots are dropped later with the answer and feedback remapped.
    const labeled = getLegacyLabeledChoiceSlots(candidate);
    if (labeled) {
        const values = Array(Math.max(...labeled.entries.map((entry) => entry.slot)) + 1).fill('');
        labeled.entries.forEach((entry) => { values[entry.slot] = candidate[entry.key]; });
        return values;
    }
    if (candidate && typeof candidate === 'object') {
        return Object.keys(candidate)
            .sort(compareLegacyChoiceKeys)
            .map(key => candidate[key]);
    }
    return [];
}

// options.keepBlank keeps blank choices as '' so an index or letter can be read
// against the choices as the row wrote them, before blanks are dropped.
function normalizeLegacyChoiceList(rawQuestion, options = {}) {
    const choiceCandidates = [
        rawQuestion?.c,
        rawQuestion?.choices,
        rawQuestion?.Choices,
        rawQuestion?.o,
        rawQuestion?.options,
        rawQuestion?.Options,
        rawQuestion?.answers,
        rawQuestion?.Answers,
        rawQuestion?.answerChoices,
        rawQuestion?.answer_choices,
        rawQuestion?.alternatives,
        rawQuestion?.responses
    ];
    const choiceCandidate = choiceCandidates
        .find(candidate => legacyChoiceValuesFromCandidate(candidate).length > 0);
    let choices = legacyChoiceValuesFromCandidate(choiceCandidate);

    if (!choices.length) {
        // Direct columns keep their slots: A, C, D leaves B blank so answer "C" still names C.
        const slotted = (readSlot) => {
            const values = [];
            for (let slot = 0; slot < 8; slot += 1) { // A-H, as answer letters allow
                const value = readSlot(slot);
                if (value !== undefined) values[slot] = value;
            }
            return Array.from(values, value => value ?? '');
        };
        choices = slotted((slot) => {
            const letter = String.fromCharCode(65 + slot);
            return rawQuestion?.[`choice${letter}`]
                ?? rawQuestion?.[`option${letter}`]
                ?? rawQuestion?.[`choice${letter.toLowerCase()}`]
                ?? rawQuestion?.[`option${letter.toLowerCase()}`]
                ?? rawQuestion?.[letter];
        });
        if (!choices.length) {
            choices = slotted(slot => rawQuestion?.[`choice${slot + 1}`] ?? rawQuestion?.[`option${slot + 1}`]);
        }
    }

    const texts = choices.map(normalizeLegacyTextValue);
    return options?.keepBlank ? texts : texts.filter(Boolean);
}

function isLegacyQuestionLikeObject(value) {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
    const hasStem = ['q', 'question', 'Question', 'questionText', 'question_text', 'prompt', 'stem', 'stemText', 'text'].some(key => normalizeLegacyTextValue(value[key]));
    const hasChoices = normalizeLegacyChoiceList(value).length > 0;
    return hasStem || hasChoices;
}

function extractLegacyQuestionArrayFromPayload(rawValue, seen = new Set()) {
    const value = parseLegacyQuestionPayload(rawValue);
    if (value === null || value === undefined) return null;
    if (Array.isArray(value)) return value;
    if (typeof value !== 'object') return null;
    if (seen.has(value)) return null;
    seen.add(value);

    const wrapperKeys = [
        'questions',
        'data',
        'qData',
        'items',
        'results',
        'quizQuestions',
        'generatedQuestions',
        'generated_questions',
        'questionSet',
        'questionBank',
        'questionSets',
        'questionBanks',
        'quiz',
        'quizData',
        'records',
        'entries',
        'rows',
        'payload',
        'response',
        'content',
        'value'
    ];
    for (const key of wrapperKeys) {
        if (!Object.prototype.hasOwnProperty.call(value, key)) continue;
        const extracted = extractLegacyQuestionArrayFromPayload(value[key], seen);
        if (Array.isArray(extracted)) return extracted;
    }

    if (isLegacyQuestionLikeObject(value)) return [value];

    const objectKeys = Object.keys(value);
    const indexedKeys = objectKeys.filter(key => /^\d+$/.test(key));
    if (indexedKeys.length > 0) {
        const indexedValues = indexedKeys
            .sort((a, b) => Number(a) - Number(b))
            .map(key => value[key]);
        if (indexedValues.some(item => item && typeof item === 'object')) return indexedValues;
    }

    const nestedCollections = [];
    for (const key of objectKeys) {
        const nestedValue = value[key];
        if (!nestedValue || typeof nestedValue !== 'object') continue;
        const extracted = extractLegacyQuestionArrayFromPayload(nestedValue, seen);
        if (Array.isArray(extracted) && extracted.length > 0) {
            nestedCollections.push(...extracted);
        }
    }
    if (nestedCollections.length > 0) return nestedCollections;

    return null;
}

function isLegacyTruthyAnswerFlag(value) {
    if (value === true) return true;
    if (typeof value === 'number') return value === 1;
    if (typeof value === 'string') {
        return /^(true|yes|y|1|correct|right|answer)$/i.test(value.trim());
    }
    return false;
}

function findLegacyEmbeddedCorrectChoiceIndex(rawQuestion) {
    const choiceCandidates = [
        rawQuestion?.c,
        rawQuestion?.choices,
        rawQuestion?.Choices,
        rawQuestion?.o,
        rawQuestion?.options,
        rawQuestion?.Options,
        rawQuestion?.answers,
        rawQuestion?.Answers,
        rawQuestion?.answerChoices,
        rawQuestion?.answer_choices,
        rawQuestion?.alternatives,
        rawQuestion?.responses
    ];
    const choiceCandidate = choiceCandidates
        .find(candidate => legacyChoiceValuesFromCandidate(candidate).length > 0);
    const rawChoices = legacyChoiceValuesFromCandidate(choiceCandidate);
    return rawChoices.findIndex(choice => (
        choice && typeof choice === 'object' && [
            choice.correct,
            choice.isCorrect,
            choice.is_correct,
            choice.correctAnswer,
            choice.isAnswer,
            choice.is_answer,
            choice.right
        ].some(isLegacyTruthyAnswerFlag)
    ));
}

// A choice whose content is itself a letter ("O", "A", "B", "AB") makes a one-letter
// answer ambiguous. The *Text fields hold the choice content (the app writes answerText
// from c[a]), so content wins there; elsewhere a disagreement between the content and
// the letter reading is settled by the numeric `a`, or left to the index fields.
function findLegacyAnswerIdentityIndex(rawQuestion, choices) {
    const candidates = [
        [rawQuestion?.answerText, true],
        [rawQuestion?.answer_text, true],
        [rawQuestion?.correctChoiceText, true],
        [rawQuestion?.correct_choice_text, true],
        [rawQuestion?.correctChoice, false],
        [rawQuestion?.correct_choice, false],
        [rawQuestion?.answer, false],
        [rawQuestion?.correctAnswer, false],
        [rawQuestion?.correct_answer, false]
    ];
    const numericIndex = Number.isInteger(rawQuestion?.a) ? rawQuestion.a : -1;

    for (const [candidate, isChoiceText] of candidates) {
        if (candidate === undefined || candidate === null || candidate === '') continue;
        const text = normalizeLegacyTextValue(candidate);
        if (!text) continue;

        const normalizedToken = text.replace(/^answer\s*[:\-]?\s*/i, '').trim();
        if (!normalizedToken) continue;
        // A whole number is an index in the index fields, but in a *Text field it is the
        // choice's own content ("23").
        if (/^-?\d+$/.test(normalizedToken)) {
            if (!isChoiceText) continue;
            const numericContentIndex = findChoiceIdentityIndex(normalizedToken, choices, numericIndex);
            if (numericContentIndex >= 0) return numericContentIndex;
            continue;
        }
        const contentIndex = findChoiceIdentityIndex(normalizedToken, choices, numericIndex);
        const letterMatch = normalizedToken.match(/^(?:choice|option|answer)?\s*([A-H])(?:[\.\)]|\s*)$/i);
        const rawLetterIndex = letterMatch ? letterMatch[1].toUpperCase().charCodeAt(0) - 65 : -1;
        const letterIndex = rawLetterIndex >= 0 && rawLetterIndex < choices.length ? rawLetterIndex : -1;

        if (contentIndex >= 0 && (letterIndex < 0 || letterIndex === contentIndex || isChoiceText)) return contentIndex;
        if (contentIndex >= 0) {
            if (numericIndex === contentIndex || numericIndex === letterIndex) return numericIndex;
            continue;
        }
        if (letterIndex >= 0) return letterIndex;
    }

    return -1;
}

function isLegacyOneBasedChoiceObject(rawQuestion) {
    const candidates = [
        rawQuestion?.c,
        rawQuestion?.choices,
        rawQuestion?.Choices,
        rawQuestion?.o,
        rawQuestion?.options,
        rawQuestion?.Options,
        rawQuestion?.answers,
        rawQuestion?.Answers,
        rawQuestion?.answerChoices,
        rawQuestion?.answer_choices,
        rawQuestion?.alternatives,
        rawQuestion?.responses
    ];
    const candidate = candidates.find(value => legacyChoiceValuesFromCandidate(value).length > 0);
    if (!candidate || Array.isArray(candidate) || typeof candidate !== 'object') return false;
    // Numbered labels without a 0 are one-based, so a numeric answer names the label
    // it shares ({2,3,4} with a: 3 is the choice keyed 3).
    const labeled = getLegacyLabeledChoiceSlots(candidate);
    return !!labeled && labeled.numbered && labeled.base === 1 && labeled.entries.length >= 2;
}

// Returns the zero-based correct choice, or -1 when the row does not prove one.
// Missing, unrecognized, ambiguous, fractional, and out-of-range evidence are all
// -1 (with the reason on `report`); nothing here defaults to the first choice.
function normalizeLegacyAnswerIndex(rawQuestion, choices, report = null) {
    const answerKeys = [
        'a',
        'answer',
        'ans',
        'correct',
        'correctAnswer',
        'correct_answer',
        'correctIndex',
        'correct_index',
        'answerIndex',
        'answer_index',
        'answerNumber',
        'answer_number',
        'correctOption',
        'correct_option',
        'correctLetter',
        'correct_letter',
        'solution',
        'key'
    ];
    let answerValue;
    let answerKey = '';
    for (const key of answerKeys) {
        if (rawQuestion?.[key] !== undefined && rawQuestion?.[key] !== null && rawQuestion?.[key] !== '') {
            answerValue = rawQuestion[key];
            answerKey = key;
            break;
        }
    }

    // A textual answer is a stable identity. It must win over a stale numeric
    // index when both fields exist, because choices may have been reserialized
    // or reordered between generation and restore.
    const identityIndex = findLegacyAnswerIdentityIndex(rawQuestion, choices);
    if (identityIndex >= 0) return identityIndex;

    const invalid = (reason) => {
        if (report && typeof report === 'object') report.reason = reason;
        return -1;
    };
    if (answerValue === undefined) {
        const embeddedChoiceIndex = findLegacyEmbeddedCorrectChoiceIndex(rawQuestion);
        if (embeddedChoiceIndex >= 0) return embeddedChoiceIndex;
        return invalid('No correct answer was given.');
    }
    if (answerValue && typeof answerValue === 'object') {
        answerValue = answerValue.index ?? answerValue.value ?? answerValue.letter ?? answerValue.text ?? answerValue.answer;
    }

    let answerIndex = -1;
    const oneBasedNumericField = ['answerNumber', 'answer_number', 'correctOption', 'correct_option'].includes(answerKey)
        && (typeof answerValue === 'number'
            || (typeof answerValue === 'string' && /^-?\d+$/.test(answerValue.trim())));
    const oneBasedChoiceObjectIndex = answerKey === 'a'
        && isLegacyOneBasedChoiceObject(rawQuestion)
        && (typeof answerValue === 'number'
            || (typeof answerValue === 'string' && /^-?\d+$/.test(answerValue.trim())));
    if (typeof answerValue === 'number') {
        if (!Number.isInteger(answerValue)) return invalid('The answer index is not a whole number.');
        answerIndex = answerValue;
    } else if (typeof answerValue === 'string') {
        const trimmed = answerValue.trim();
        const letterMatch = trimmed.match(/^(?:choice|option|answer)?\s*([A-H])$/i);
        if (letterMatch) {
            answerIndex = letterMatch[1].toUpperCase().charCodeAt(0) - 65;
        } else if (/^-?\d+$/.test(trimmed)) {
            answerIndex = Number.parseInt(trimmed, 10);
        } else {
            const normalizedAnswer = normalizeQuestionForDedup(trimmed);
            const matches = [];
            choices.forEach((choice, index) => {
                if (normalizeQuestionForDedup(choice) === normalizedAnswer) matches.push(index);
            });
            if (matches.length !== 1) {
                return invalid(matches.length
                    ? 'The answer text matches more than one choice.'
                    : 'The answer does not match any choice.');
            }
            answerIndex = matches[0];
        }
    } else {
        return invalid('The answer is not a letter, a number, or choice text.');
    }

    // These legacy fields conventionally store A/B/C/D as 1/2/3/4. The
    // canonical app field `a` remains zero-based; convert only the explicitly
    // one-based field names so current generated data is not shifted.
    if (oneBasedNumericField || oneBasedChoiceObjectIndex) {
        answerIndex -= 1;
    }

    if (!Number.isInteger(answerIndex) || answerIndex < 0 || answerIndex >= choices.length) {
        return invalid('The answer points outside the available choices.');
    }
    return answerIndex;
}

// options.strictAnswer: reject (return null) a row whose correct answer is not
// proven, reporting why through options.onReject. Import entry points use it.
// Without it, a stored record that fails the check still loads on its first
// choice, as it always has, but carries answerUnverified instead of passing as a
// confirmed answer; banks the app saved itself were validated when first stored.
function normalizeLegacyQuizQuestion(rawQuestion, sourceKey = '', options = {}) {
    const reject = (reason) => {
        if (typeof options?.onReject === 'function') options.onReject(reason);
        return null;
    };
    const parsedQuestion = parseLegacyQuestionPayload(rawQuestion);
    if (!parsedQuestion || typeof parsedQuestion !== 'object' || Array.isArray(parsedQuestion)) {
        return reject('The row is not a question.');
    }
    const question = { ...parsedQuestion };
    const stem = pickFirstLegacyText(question, ['q', 'question', 'Question', 'questionText', 'question_text', 'prompt', 'stem', 'stemText', 'text']);
    const choiceSlots = normalizeLegacyChoiceList(question, { keepBlank: true });
    const choices = choiceSlots.filter(Boolean);
    if (!stem) return reject('The question text is missing.');
    if (choices.length < 2) return reject('The question has fewer than two choices.');

    const answerReport = {};
    let answerIndex;
    if (choiceSlots.length === choices.length) {
        answerIndex = normalizeLegacyAnswerIndex(question, choices, answerReport);
    } else {
        // Blank choices are dropped, so an index or letter is read against the row's own
        // positions and then moved to the same choice's position without the blanks.
        const slotIndex = normalizeLegacyAnswerIndex(question, choiceSlots, answerReport);
        if (slotIndex >= 0 && !choiceSlots[slotIndex]) {
            answerReport.reason = 'The answer points at a blank choice.';
            answerIndex = -1;
        } else {
            answerIndex = slotIndex < 0 ? -1 : choiceSlots.slice(0, slotIndex).filter(Boolean).length;
        }
    }
    let answerUnverified = false;
    if (answerIndex < 0) {
        if (options?.strictAnswer) return reject(answerReport.reason || 'The correct answer could not be confirmed.');
        answerIndex = 0;
        answerUnverified = true;
    }
    const answerText = answerIndex >= 0 && answerIndex < choices.length
        ? choices[answerIndex]
        : '';
    // Anything else that stores a choice index (a backup's selected answer) reads the
    // row's own slots; this map moves it with its choice, -1 for a dropped blank.
    if (typeof options?.onChoiceSlots === 'function') {
        let next = 0;
        options.onChoiceSlots(choiceSlots.map((choice) => (choice ? next++ : -1)));
    }
    // Per-choice feedback is indexed like the row's own choices, so it moves with them.
    const wrongFeedback = choiceSlots.length !== choices.length
        ? remapLegacyWrongFeedbackSlots(question.w, choiceSlots)
        : question.w;
    const explanation = pickFirstLegacyText(question, ['e', 'explanation', 'expl', 'rationale', 'reason', 'feedback', 'analysis']);
    const citation = pickFirstLegacyText(question, ['add', 'page', 'pages', 'citation', 'sourcePage', 'source_page', 'slide', 'slideNumber', 'slide_number']);
    const sourceFile = pickFirstLegacyText(question, ['sourceFile', 'source_file', 'src', 'source', 'file', 'filename', 'fileName', 'setName']) || sourceKey;

    return {
        ...question,
        q: stem,
        c: choices,
        a: answerIndex,
        answerText,
        e: explanation,
        add: citation,
        sourceFile,
        ...(wrongFeedback !== question.w ? { w: wrongFeedback } : {}),
        ...(answerUnverified ? { answerUnverified: true } : {})
    };
}

// Moves index-keyed wrong-choice feedback from the row's slots (blanks included) to the
// positions left once blank choices are dropped; feedback for a blank slot is discarded.
function remapLegacyWrongFeedbackSlots(wrong, choiceSlots) {
    if (!wrong || typeof wrong !== 'object') return wrong;
    const target = [];
    let next = 0;
    choiceSlots.forEach((choice, slot) => { target[slot] = choice ? next++ : -1; });
    const isArray = Array.isArray(wrong);
    const remapped = isArray ? Array(next).fill('') : {};
    Object.keys(wrong).forEach((key) => {
        if (!/^\d+$/.test(key)) {
            if (!isArray) remapped[key] = wrong[key];
            return;
        }
        const index = target[Number(key)];
        if (Number.isInteger(index) && index >= 0) remapped[isArray ? index : String(index)] = wrong[key];
    });
    return remapped;
}

function normalizeStoredQuestionCollection(rawValue, sourceKey = '') {
    const rawQuestions = extractLegacyQuestionArrayFromPayload(rawValue);
    if (!Array.isArray(rawQuestions)) return null;
    return rawQuestions
        .map(question => normalizeLegacyQuizQuestion(question, sourceKey))
        .filter(Boolean);
}

function normalizeQuestionStoreLookupKey(key) {
    return String(key || '')
        .trim()
        .toLowerCase()
        .replace(/\.(json|txt)$/i, '');
}

function removeEmptyQuestionBankReferencesFromSavedSession(session, emptyQuestionKeyLookup) {
    if (!session || !emptyQuestionKeyLookup?.size) return false;

    const isEmptyQuestionBankReference = (value) => {
        const rawValue = String(value || '').trim();
        return !!rawValue
            && !/\.pdf$/i.test(rawValue)
            && emptyQuestionKeyLookup.has(normalizeQuestionStoreLookupKey(rawValue));
    };
    const filterList = (owner, property) => {
        if (!owner || !Array.isArray(owner[property])) return false;
        const next = owner[property].filter(value => !isEmptyQuestionBankReference(value));
        if (next.length === owner[property].length) return false;
        owner[property] = next;
        return true;
    };

    let changed = false;
    changed = filterList(session, 'activeSets') || changed;
    changed = filterList(session.data, 'activeSets') || changed;
    changed = filterList(session.manifest, 'jsons') || changed;
    changed = filterList(session.data?.manifest, 'jsons') || changed;
    return changed;
}

async function removeQuestionBankForCleanup(setKey, options = {}) {
    const key = String(setKey || '').trim();
    if (!key) return false;

    const lookupKey = normalizeQuestionStoreLookupKey(key);
    const variants = getQuestionSetKeyVariantsForDedup(key);
    const storageKeys = Array.from(new Set([
        key,
        ...(Array.isArray(options.storageKeys) ? options.storageKeys : [])
    ].map(value => String(value || '').trim()).filter(Boolean)));
    const hasNonEmptyLoadedAlias = Object.entries(loadedQuestionSets || {}).some(([candidate, setQuestions]) => (
        normalizeQuestionStoreLookupKey(candidate) === lookupKey
        && Array.isArray(setQuestions)
        && setQuestions.length > 0
    ));
    const hasNonEmptyExactKey = Array.isArray(loadedQuestionSets?.[key])
        && loadedQuestionSets[key].length > 0;
    let changed = false;

    Object.keys(loadedQuestionSets || {}).forEach((candidate) => {
        const sameBank = normalizeQuestionStoreLookupKey(candidate) === lookupKey;
        if (!sameBank || (hasNonEmptyLoadedAlias && candidate !== key)) return;
        const setQuestions = loadedQuestionSets[candidate];
        if ((!hasNonEmptyExactKey && candidate === key) || (!Array.isArray(setQuestions) || setQuestions.length === 0)) {
            delete loadedQuestionSets[candidate];
            changed = true;
        }
    });

    if (Array.isArray(sessionManifest?.jsons)) {
        const nextJsons = sessionManifest.jsons.filter((candidate) => {
            const rawCandidate = String(candidate || '').trim();
            if (!rawCandidate) return false;
            if (rawCandidate === key) return hasNonEmptyExactKey;
            return normalizeQuestionStoreLookupKey(rawCandidate) !== lookupKey || hasNonEmptyLoadedAlias;
        });
        if (nextJsons.length !== sessionManifest.jsons.length) {
            sessionManifest.jsons = nextJsons;
            changed = true;
        }
    }

    variants.forEach((variant) => {
        if (!hasNonEmptyLoadedAlias) {
            if (activeQuestionSetKeys?.delete(variant)) changed = true;
            if (pdfAssociations && Object.prototype.hasOwnProperty.call(pdfAssociations, variant)) {
                delete pdfAssociations[variant];
                changed = true;
            }
        }
    });

    for (const storageKey of options.storageAlreadyCleared ? [] : storageKeys) {
        try {
            await IDB.del('questions', storageKey);
        } catch (error) {
            console.warn(`Failed to remove empty question bank ${storageKey}:`, error);
        }
    }
    if (typeof clearQuestionDuplicateReport === 'function') {
        await clearQuestionDuplicateReport(key, { clearArchive: true });
    }
    if (options.persistManifestSetting) {
        try {
            await IDB.put('settings', 'session_manifest', sessionManifest);
        } catch (error) {
            console.warn('Failed to persist empty question-bank cleanup:', error);
        }
    }
    return changed || storageKeys.length > 0;
}

// Deletes a stored bank only if it is still empty when the delete runs, in the same
// transaction as the read: another tab may have filled it since the scan. Resolves true
// when the bank is gone (or never existed), false when it was kept or the read failed.
async function deleteQuestionBankIfStillEmpty(storageKey) {
    if (typeof IDB.runWriteTransaction !== 'function') return false;
    try {
        return await IDB.runWriteTransaction(['questions'], (tx, write, abortWith, setResult) => {
            const store = tx.objectStore('questions');
            const read = store.get(storageKey);
            read.onerror = () => abortWith(read.error);
            read.onsuccess = () => {
                const stored = normalizeStoredQuestionCollection(read.result, storageKey);
                if (read.result !== undefined && !(Array.isArray(stored) && stored.length === 0)) {
                    setResult(false);
                    return;
                }
                if (read.result !== undefined) store.delete(storageKey);
                setResult(true);
            };
        }, SESSION_SAVE_WITH_BANKS_TIMEOUT_MS);
    } catch (error) {
        console.warn(`Empty question bank ${storageKey} was not removed:`, error);
        return false;
    }
}

async function pruneEmptyQuestionBanks(options = {}) {
    if (IS_EMBED_DEMO) return { removedKeys: [], count: 0 };

    const emptyKeys = new Set();
    Object.entries(loadedQuestionSets || {}).forEach(([key, setQuestions]) => {
        if (Array.isArray(setQuestions) && setQuestions.length === 0) emptyKeys.add(String(key || '').trim());
    });

    try {
        const storedKeys = typeof IDB.getAllKeys === 'function'
            ? await IDB.getAllKeys('questions')
            : [];
        for (const rawKey of Array.isArray(storedKeys) ? storedKeys : []) {
            const key = String(rawKey || '').trim();
            if (!key || key.endsWith('::temp')) continue;
            const storedValue = await IDB.get('questions', rawKey);
            const normalizedQuestions = normalizeStoredQuestionCollection(storedValue, key);
            if (Array.isArray(normalizedQuestions) && normalizedQuestions.length === 0) emptyKeys.add(key);
        }
    } catch (error) {
        console.warn('Failed to scan question banks for empty records:', error);
    }

    const removedKeys = [];
    for (const key of Array.from(emptyKeys).filter(Boolean)) {
        if (await deleteQuestionBankIfStillEmpty(key)) removedKeys.push(key);
    }
    if (!removedKeys.length) return { removedKeys: [], count: 0 };
    const emptyQuestionKeyLookup = new Set(removedKeys.map(normalizeQuestionStoreLookupKey).filter(Boolean));
    let manifestChanged = false;
    for (const key of removedKeys) {
        const before = Array.isArray(sessionManifest?.jsons) ? sessionManifest.jsons.join('\u0000') : '';
        await removeQuestionBankForCleanup(key, {
            storageKeys: [key],
            storageAlreadyCleared: true,
            persistManifestSetting: false
        });
        const after = Array.isArray(sessionManifest?.jsons) ? sessionManifest.jsons.join('\u0000') : '';
        if (before !== after) manifestChanged = true;
    }

    let sessions = options.savedSessions;
    if (!Array.isArray(sessions)) {
        try {
            sessions = await IDB.getAll('sessions') || [];
        } catch (error) {
            sessions = [];
            console.warn('Failed to scan saved sessions for empty quiz references:', error);
        }
    }
    // The scan only picks candidates. Each write re-reads the latest record and removes
    // just the empty references, so progress another tab saved since the scan is kept and
    // a session deleted meanwhile stays deleted.
    for (const session of sessions) {
        if (!session?.id || !removeEmptyQuestionBankReferencesFromSavedSession(structuredClone(session), emptyQuestionKeyLookup)) continue;
        try {
            const outcome = await IDB.compareAndPut(session.id, (stored) => {
                if (!stored) return { error: Object.assign(new Error('Session not found.'), { name: 'NotFoundError' }) };
                const next = structuredClone(stored);
                if (!removeEmptyQuestionBankReferencesFromSavedSession(next, emptyQuestionKeyLookup)) {
                    return { error: Object.assign(new Error('Nothing to clean up.'), { name: 'NoChangeError' }) };
                }
                return { record: { ...next, revision: (Number(stored.revision) || 0) + 1 } };
            });
            announceSessionWrite(session.id, outcome.record.revision);
        } catch (error) {
            if (error?.name === 'NotFoundError' || error?.name === 'NoChangeError') continue;
            console.warn(`Failed to persist empty quiz cleanup for session ${session?.id || 'unknown'}:`, error);
        }
    }

    if (manifestChanged) {
        try {
            await IDB.put('settings', 'session_manifest', sessionManifest);
        } catch (error) {
            console.warn('Failed to persist current empty quiz cleanup:', error);
        }
        if (typeof updateSourceDropdown === 'function') updateSourceDropdown();
        if (options.saveCurrentSession && currentSessionId && hasPersistableSessionContent()) {
            try {
                await SessionManager.save(false);
            } catch (error) {
                console.warn('Failed to save current session after empty quiz cleanup:', error);
            }
        }
    }

    return { removedKeys, count: removedKeys.length };
}

function getSessionManifestFromSavedSession(session) {
    const dataManifest = (session?.data && typeof session.data === 'object' && session.data.manifest && typeof session.data.manifest === 'object')
        ? session.data.manifest
        : null;
    const rootManifest = (session?.manifest && typeof session.manifest === 'object')
        ? session.manifest
        : null;
    return dataManifest || rootManifest || {};
}

function getSessionActiveSetNames(session) {
    const rootActiveSets = Array.isArray(session?.activeSets) ? session.activeSets : [];
    const dataActiveSets = Array.isArray(session?.data?.activeSets) ? session.data.activeSets : [];
    return mergeStringList(rootActiveSets, dataActiveSets)
        .map(v => String(v || '').trim())
        .filter(Boolean);
}

function getSessionManifestJsonNames(session) {
    const manifest = getSessionManifestFromSavedSession(session);
    const manifestJsons = Array.isArray(manifest?.jsons) ? manifest.jsons : [];
    const activeJsons = getSessionActiveSetNames(session)
        .filter(name => !/\.pdf$/i.test(name));
    return mergeStringList(manifestJsons, activeJsons)
        .map(v => String(v || '').trim())
        .filter(Boolean);
}

function getSessionQuestionLookupNames(session) {
    const manifestNames = getSessionManifestJsonNames(session);
    const inferredNames = [
        session?.customName,
        session?.sessionName,
        session?.displayName,
        session?.name,
        session?.title,
        session?.data?.customName,
        session?.data?.sessionName,
        session?.data?.displayName,
        session?.data?.name,
        session?.data?.title
    ].filter(Boolean);
    return mergeStringList(manifestNames, inferredNames)
        .map(v => String(v || '').trim())
        .filter(Boolean);
}

function getSavedSessionDisplayNameValue(session) {
    return normalizeSessionNameInput(
        session?.customName ||
        session?.sessionName ||
        session?.displayName ||
        session?.name ||
        session?.title ||
        session?.data?.customName ||
        session?.data?.sessionName ||
        session?.data?.displayName ||
        session?.data?.name ||
        session?.data?.title ||
        ''
    );
}

function getSessionManifestPdfNames(session) {
    const manifest = getSessionManifestFromSavedSession(session);
    const manifestPdfs = Array.isArray(manifest?.pdfs) ? manifest.pdfs : [];
    const activePdfs = getSessionActiveSetNames(session)
        .filter(name => /\.pdf$/i.test(name));
    return mergeStringList(manifestPdfs, activePdfs)
        .map(v => String(v || '').trim())
        .filter(Boolean);
}

function assessSessionRestoreReadiness({
    questions = [],
    sessionManifest = {},
    missingQuestionPayloadNames = [],
    corruptQuestionPayloadNames = [],
    missingPdfFiles = [],
    session = null
} = {}) {
    const questionsAvailable = Array.isArray(questions)
        ? questions.filter(Boolean).length
        : 0;
    const manifestJsons = Array.isArray(sessionManifest?.jsons) ? sessionManifest.jsons : [];
    const manifestPdfs = Array.isArray(sessionManifest?.pdfs) ? sessionManifest.pdfs : [];
    const missingPdfs = Array.from(new Set((Array.isArray(missingPdfFiles) ? missingPdfFiles : [])
        .map(value => String(value || '').trim())
        .filter(Boolean)));
    const hasQuestionPayloadReference = manifestJsons.length > 0
        || manifestPdfs.length > 0
        || (Array.isArray(missingQuestionPayloadNames) && missingQuestionPayloadNames.length > 0)
        || (Array.isArray(corruptQuestionPayloadNames) && corruptQuestionPayloadNames.length > 0);
    const hasQuestions = questionsAvailable > 0;
    const hasCorruptPayload = Array.isArray(corruptQuestionPayloadNames)
        && corruptQuestionPayloadNames.length > 0;
    /* A deck imported without a quiz yet is a real, resumable session: the sources are saved, the
     * workspace opens on them, and Create is the next step. Requiring questions turned that into a
     * dead end - Continue refused to open it and told the student to start over, which is the one
     * action that loses the import. Corrupt quiz data is still a rejection, because there the
     * session promises questions it cannot produce. */
    const hasRestorableSources = manifestPdfs.length > 0;
    const hasNamedEmptySession = !hasQuestionPayloadReference
        && !!session && (session.explicit === true || !!getSavedSessionDisplayNameValue(session));
    const ok = hasQuestions || (hasRestorableSources && !hasCorruptPayload) || hasNamedEmptySession;
    return {
        ok,
        questionsAvailable,
        missingPdfFiles: missingPdfs,
        missingQuestionPayload: !hasQuestions && hasQuestionPayloadReference,
        reason: hasQuestions
            ? (missingPdfs.length ? 'pdf-partial' : 'ready')
            : (hasCorruptPayload ? 'corrupt' : (hasNamedEmptySession ? 'named-empty' : (ok ? 'sources-only' : 'questions-missing')))
    };
}

function getQuestionStoreAliasCandidates(setKey) {
    const key = String(setKey || '').trim();
    if (!key) return [];
    const stripped = key.replace(/\.(json|txt)$/i, '');
    return Array.from(new Set([
        key,
        stripped,
        `${stripped}.json`,
        `${stripped}.txt`
    ].map(v => String(v || '').trim()).filter(Boolean)));
}

async function readSessionStoreValuesWithTimeout(storeName, keys, timeoutMs = SESSION_RESTORE_QUIZ_TIMEOUT_MS) {
    const list = Array.from(new Set((Array.isArray(keys) ? keys : []).filter(Boolean)));
    if (!list.length) return new Map();
    const timeoutToken = Symbol('session-restore-batch-timeout');
    const timeoutPromise = new Promise((resolve) => {
        setTimeout(() => resolve(timeoutToken), Math.max(1000, Number(timeoutMs) || 1000));
    });
    try {
        const values = await Promise.race([IDB.getMany(storeName, list), timeoutPromise]);
        if (values === timeoutToken || !Array.isArray(values)) {
            console.warn(`Session restore timed out reading ${list.length} ${storeName} records`);
            return new Map();
        }
        return new Map(list.map((key, index) => [key, values[index] ?? null]));
    } catch (error) {
        console.warn(`Session restore failed reading ${storeName} records:`, error);
        return new Map();
    }
}

/* Restoring a multi-source session used to open one IndexedDB transaction per
   filename alias per quiz bank (four per bank), plus a full key scan per bank
   that missed. This resolves every bank in one batched read, then at most one
   key scan and one more batched read for the banks that only match by
   normalized key. */
async function readStoredQuestionCollectionsForSessionKeys(setKeys) {
    const names = Array.from(new Set((Array.isArray(setKeys) ? setKeys : [])
        .map(v => String(v || '').trim())
        .filter(Boolean)));
    const results = new Map();
    if (!names.length) return results;

    const candidatesByName = new Map(names.map(name => [name, getQuestionStoreAliasCandidates(name)]));
    const allCandidates = Array.from(new Set(Array.from(candidatesByName.values()).flat()));
    const rawByKey = await readSessionStoreValuesWithTimeout('questions', allCandidates);

    const unresolved = [];
    names.forEach((name) => {
        let foundEmptyDirectKey = '';
        let foundPayload = false;
        let resolved = null;
        for (const candidate of (candidatesByName.get(name) || [])) {
            const raw = rawByKey.get(candidate);
            if (raw === null || raw === undefined) continue;
            foundPayload = true;
            const questionsForCandidate = normalizeStoredQuestionCollection(raw, candidate);
            if (Array.isArray(questionsForCandidate) && questionsForCandidate.length > 0) {
                resolved = { questions: questionsForCandidate, usedKey: candidate, reason: 'ready' };
                break;
            }
            if (Array.isArray(questionsForCandidate)) foundEmptyDirectKey = foundEmptyDirectKey || candidate;
        }
        if (resolved) results.set(name, resolved);
        else unresolved.push({ name, foundEmptyDirectKey, matchedKey: '', foundPayload });
    });

    if (!unresolved.length) return results;

    let storedQuestionKeys = [];
    try {
        storedQuestionKeys = typeof IDB.getAllKeys === 'function'
            ? await readSessionStoreKeysWithTimeout('questions', 3000)
            : [];
    } catch (error) {
        console.warn('Failed to scan question store keys during session restore:', error);
    }
    const storedKeyByNormalized = new Map();
    (Array.isArray(storedQuestionKeys) ? storedQuestionKeys : [])
        .map(candidate => String(candidate || '').trim())
        .filter(candidate => candidate && !candidate.endsWith('::temp'))
        .forEach((candidate) => {
            const normalized = normalizeQuestionStoreLookupKey(candidate);
            if (normalized && !storedKeyByNormalized.has(normalized)) storedKeyByNormalized.set(normalized, candidate);
        });

    unresolved.forEach((entry) => {
        entry.matchedKey = storedKeyByNormalized.get(normalizeQuestionStoreLookupKey(entry.name)) || '';
    });
    const matchedRawByKey = await readSessionStoreValuesWithTimeout(
        'questions',
        unresolved.map(entry => entry.matchedKey).filter(Boolean)
    );

    unresolved.forEach((entry) => {
        let foundEmptyKey = entry.foundEmptyDirectKey;
        if (entry.matchedKey) {
            const raw = matchedRawByKey.get(entry.matchedKey);
            if (raw !== null && raw !== undefined) {
                const questionsForMatchedKey = normalizeStoredQuestionCollection(raw, entry.matchedKey);
                if (Array.isArray(questionsForMatchedKey) && questionsForMatchedKey.length > 0) {
                    results.set(entry.name, { questions: questionsForMatchedKey, usedKey: entry.matchedKey, reason: 'ready' });
                    return;
                }
                entry.foundPayload = true;
                foundEmptyKey = foundEmptyKey || entry.matchedKey;
            }
        }
        results.set(entry.name, foundEmptyKey
            ? { questions: [], usedKey: foundEmptyKey, reason: 'empty' }
            : { questions: null, usedKey: entry.name, reason: entry.foundPayload ? 'corrupt' : 'missing' });
    });

    return results;
}

async function readStoredQuestionCollectionForSessionKey(setKey) {
    const key = String(setKey || '').trim();
    if (!key) return { questions: null, usedKey: '', reason: 'missing' };
    const collections = await readStoredQuestionCollectionsForSessionKeys([key]);
    return collections.get(key) || { questions: null, usedKey: key, reason: 'missing' };
}

function recoverEmbeddedSessionQuestions(session, sourceKey = '') {
    const sourceLabel = String(sourceKey || formatWelcomeSessionDisplayName(session) || session?.id || 'Recovered session').trim();
    const candidates = [];
    const data = (session?.data && typeof session.data === 'object') ? session.data : {};
    const candidateRoots = [session, data];

    candidateRoots.forEach((root) => {
        if (!root || typeof root !== 'object') return;
        [
            'loadedQuestionSets',
            'questionSets',
            'questionsBySet',
            'quizSets',
            'questionBanks',
            'banks'
        ].forEach((key) => {
            if (root[key] !== undefined) candidates.push(root[key]);
        });
        [
            'questions',
            'qData',
            'quizData',
            'questionBank',
            'questionSet',
            'quizQuestions',
            'items',
            'results',
            'data'
        ].forEach((key) => {
            if (root[key] !== undefined && root[key] !== data) candidates.push(root[key]);
        });
    });

    candidates.push(session);

    for (const candidate of candidates) {
        const normalized = normalizeStoredQuestionCollection(candidate, sourceLabel);
        if (Array.isArray(normalized) && normalized.length > 0) return normalized;
    }
    return [];
}

/* Source documents are stored as Blobs. The browser keeps a stored Blob on disk and a read hands
 * back a disk-backed reference, so restoring a session no longer copies every deck into tab
 * memory. Records written before this change hold ArrayBuffers; every reader accepts both. */
// Source writes are whole-file blob copies queued behind every other files write, so a fixed
// 25 s deadline reported lectures lost while the browser was still saving them.
function memoraSourceSaveTimeoutMs(blob) {
    const megabytes = Math.max(0, Number(blob?.size) || 0) / (1024 * 1024);
    return Math.min(600000, 60000 + Math.ceil(megabytes) * 3000);
}
async function putMemoraSourceFileRecord(name, blob, timeoutMs = memoraSourceSaveTimeoutMs(blob)) {
    if (memoraSourceFilesStoredByClaim.has(blob)) {
        memoraSourceFilesStoredByClaim.delete(blob);
        return true;
    }
    if (memoraSourceFilesSavedCreateOnly.has(blob) && typeof IDB.runWriteTransaction === 'function') {
        const addRecord = (value) => IDB.runWriteTransaction(['files'], (tx, write, abortWith) => {
            const request = tx.objectStore('files').add(value, name);
            request.onerror = () => abortWith(request.error?.name === 'ConstraintError'
                ? new Error(`Another session already stores a source named ${name}.`)
                : request.error);
        }, timeoutMs);
        try {
            await addRecord(blob);
        } catch (error) {
            if (error?.name !== 'DataCloneError') throw error;
            await addRecord(await blob.arrayBuffer());
        }
        memoraSourceFilesSavedCreateOnly.delete(blob);
        return true;
    }
    try {
        return await IDB.put('files', name, blob, timeoutMs);
    } catch (_blobError) {
        // Some private-browsing IndexedDB implementations refuse Blob values.
        return IDB.put('files', name, await blob.arrayBuffer(), timeoutMs);
    }
}

async function restoreSessionPdfFilesInBackground(pdfNames = []) {
    const restoreSessionId = currentSessionId;
    const restorePdfFiles = loadedPdfFiles;
    const isCurrentRestore = () => currentSessionId === restoreSessionId && loadedPdfFiles === restorePdfFiles;
    const names = Array.from(new Set((Array.isArray(pdfNames) ? pdfNames : [])
        .map(name => String(name || '').trim())
        .filter(Boolean)));
    if (!names.length) return { restoredPdfFiles: [], missingPdfFiles: [] };

    let restoredCount = 0;
    const missingOrSlow = [];
    // Read one source at a time under one shared deadline. A legacy ArrayBuffer record is a full
    // in-memory copy of the deck, and reading them all at once held every deck in memory together.
    const restoreDeadline = Date.now() + SESSION_RESTORE_PDF_TIMEOUT_MS;

    for (const pdfName of names) {
        if (!isCurrentRestore()) return { restoredPdfFiles: [], missingPdfFiles: [], superseded: true };
        if (loadedPdfFiles[pdfName]) {
            restoredCount += 1;
            continue;
        }
        const storedData = await readSessionStoreValueWithTimeout(
            'files',
            pdfName,
            Math.max(1000, restoreDeadline - Date.now())
        );
        if (!isCurrentRestore()) return { restoredPdfFiles: [], missingPdfFiles: [], superseded: true };
        if (loadedPdfFiles[pdfName]) {
            restoredCount += 1;
            continue;
        }
        if (!storedData) {
            missingOrSlow.push(pdfName);
            continue;
        }

        try {
            const blob = storedData instanceof Blob
                ? storedData
                : new Blob([storedData], { type: getMemoraSourceMimeType(pdfName) });
            loadedPdfFiles[pdfName] = URL.createObjectURL(blob);
            restoredCount += 1;
            if (!(storedData instanceof Blob)) {
                void putMemoraSourceFileRecord(pdfName, blob).catch((error) => {
                    console.warn(`Could not convert stored source "${pdfName}" to disk-backed storage:`, error);
                });
            }
        } catch (error) {
            missingOrSlow.push(pdfName);
            console.warn(`Failed to restore source "${pdfName}" after session load:`, error);
        }
    }

    if (!isCurrentRestore()) return { restoredPdfFiles: [], missingPdfFiles: [], superseded: true };
    if (restoredCount > 0) updateSourceDropdown();
    updateImportButtonState();
    if (missingOrSlow.length > 0) {
        console.warn(`Session opened before ${missingOrSlow.length} document source(s) finished restoring:`, missingOrSlow);
        showPopupText(`Session opened, but ${missingOrSlow.length} source file${missingOrSlow.length === 1 ? '' : 's'} could not be restored. Re-import the missing source to use source tools.`, null, true, 7000);
    }
    return {
        restoredPdfFiles: names.filter(pdfName => !!loadedPdfFiles[pdfName]),
        missingPdfFiles: missingOrSlow
    };
}

function warnAndStayOnWelcomeForEmptySession(session, currentSessionManifest, readiness = null) {
    const sessionName = getSavedSessionDisplayNameValue(session) || formatWelcomeSessionDisplayName(session);
    console.warn('Session restore produced no usable questions:', {
        sessionId: session?.id,
        sessionName,
        manifestJsons: Array.isArray(currentSessionManifest?.jsons) ? currentSessionManifest.jsons : [],
        activeSets: Array.isArray(session?.activeSets) ? session.activeSets : [],
        readiness
    });
    const message = readiness?.reason === 'corrupt'
        ? `The saved quiz data for "${sessionName}" is corrupted or unreadable. Try importing the quiz again.`
        : `No quiz questions were found for "${sessionName}". Try Load Previous Session or start a new session.`;
    showPopupText(message, null, true, 7000);
}

/* ============================================================================
   SAVED-SESSION INDEX
   Welcome, Continue and the save-time retention cap only need id, timestamp,
   name, manifest and a small progress summary for each saved session. Reading
   the whole `sessions` store for that deserializes every saved answer map and
   manifest of every session, which is the most expensive read on the startup
   path and on every autosave.

   This index is a derived cache in the `settings` store. It is refreshed on
   save/rename/delete, and every read validates it against the `sessions` key
   list (a key-only read that never deserializes a record). Any mismatch falls
   back to the full read and rebuilds, so a stale or foreign write can never
   surface a session that no longer exists.
   ============================================================================ */

const SAVED_SESSION_RETENTION_LIMIT = 100;
const SESSION_INDEX_STORE_KEY = 'session_index_v1';
const SESSION_INDEX_VERSION = 1;
let sessionIndexCache = null;
let sessionIndexValidated = false;
let sessionIndexPersistChain = Promise.resolve();

function buildSessionIndexEntry(session) {
    const id = normalizeSessionId(session?.id);
    if (!id) return null;
    const manifest = getSessionManifestFromSavedSession(session);
    return {
        id,
        timestamp: session?.timestamp || null,
        sessionNumber: normalizeSessionNumber(session?.sessionNumber) || null,
        customName: session?.customName || null,
        folderId: session?.folderId || null,
        explicit: session?.explicit === true,
        activeSets: getSessionActiveSetNames(session),
        manifest: {
            jsons: Array.isArray(manifest?.jsons) ? manifest.jsons.slice() : [],
            pdfs: Array.isArray(manifest?.pdfs) ? manifest.pdfs.slice() : []
        },
        summary: deriveWelcomeSessionSummary(session?.data)
    };
}

function normalizeSessionIndexEntry(entry) {
    const id = normalizeSessionId(entry?.id);
    if (!id) return null;
    const manifest = (entry?.manifest && typeof entry.manifest === 'object') ? entry.manifest : {};
    return {
        id,
        timestamp: entry?.timestamp || null,
        sessionNumber: normalizeSessionNumber(entry?.sessionNumber) || null,
        customName: entry?.customName || null,
        folderId: entry?.folderId || null,
        explicit: entry?.explicit === true,
        activeSets: Array.isArray(entry?.activeSets) ? entry.activeSets : [],
        manifest: {
            jsons: Array.isArray(manifest?.jsons) ? manifest.jsons : [],
            pdfs: Array.isArray(manifest?.pdfs) ? manifest.pdfs : []
        },
        summary: (entry?.summary && typeof entry.summary === 'object') ? entry.summary : null
    };
}

function sortSessionIndexEntries(entries) {
    return (Array.isArray(entries) ? entries.slice() : [])
        .sort((a, b) => new Date(b?.timestamp || 0) - new Date(a?.timestamp || 0));
}

function persistSessionIndex(entries) {
    const payload = {
        version: SESSION_INDEX_VERSION,
        updatedAt: Date.now(),
        entries: sortSessionIndexEntries(entries)
    };
    sessionIndexPersistChain = sessionIndexPersistChain
        .catch(() => { })
        .then(() => IDB.put('settings', SESSION_INDEX_STORE_KEY, payload))
        .catch((error) => {
            sessionIndexValidated = false;
            console.warn('Could not persist the saved-session index:', error);
        });
    return sessionIndexPersistChain;
}

function upsertSessionIndexEntry(session) {
    const entry = buildSessionIndexEntry(session);
    if (!entry) return;
    const existing = Array.isArray(sessionIndexCache) ? sessionIndexCache : [];
    const next = existing.filter((item) => normalizeSessionId(item?.id) !== entry.id);
    next.push(entry);
    sessionIndexCache = sortSessionIndexEntries(next);
    void persistSessionIndex(sessionIndexCache);
}

function removeSessionIndexEntries(sessionIds) {
    if (!Array.isArray(sessionIndexCache)) return;
    const removed = new Set((Array.isArray(sessionIds) ? sessionIds : [sessionIds])
        .map(normalizeSessionId)
        .filter(Boolean));
    if (!removed.size) return;
    sessionIndexCache = sessionIndexCache.filter((entry) => !removed.has(normalizeSessionId(entry?.id)));
    void persistSessionIndex(sessionIndexCache);
}

function resetSessionIndexToEmpty() {
    sessionIndexCache = [];
    void persistSessionIndex([]);
}

// Key-only read. Returns null when the read fails or times out so a slow store
// falls back to the full read instead of looking like an empty database.
async function readSessionIdSetOrNull(timeoutMs = 2500) {
    const timeoutToken = Symbol('session-index-keys-timeout');
    const timeoutPromise = new Promise((resolve) => {
        setTimeout(() => resolve(timeoutToken), Math.max(500, Number(timeoutMs) || 500));
    });
    try {
        const keys = await Promise.race([IDB.getAllKeys('sessions'), timeoutPromise]);
        if (keys === timeoutToken || !Array.isArray(keys)) return null;
        return new Set(keys.map(normalizeSessionId).filter(Boolean));
    } catch (error) {
        console.warn('Could not read saved-session keys:', error);
        return null;
    }
}

async function readStoredSessionIndexEntries(timeoutMs = 2500) {
    if (Array.isArray(sessionIndexCache)) return sessionIndexCache;
    const record = await readSessionStoreValueWithTimeout('settings', SESSION_INDEX_STORE_KEY, timeoutMs);
    if (!record || Number(record.version) !== SESSION_INDEX_VERSION || !Array.isArray(record.entries)) return null;
    return record.entries.map(normalizeSessionIndexEntry).filter(Boolean);
}

// Lightweight session list for Welcome/Continue and the retention cap. Entries
// are session-shaped stubs: `manifest`, `activeSets`, `customName`, `timestamp`
// and `sessionNumber` behave exactly as they do on a full record, but `data` is
// absent. Read the full record with IDB.get when session state is required.
async function readSavedSessionSummaries(options = {}) {
    // Every write to `sessions` in this tab flows through the IDB write hooks, so
    // once the index has been validated against the store it stays authoritative
    // for the rest of the page load and repeat callers pay nothing.
    if (sessionIndexValidated && Array.isArray(sessionIndexCache)) return sortSessionIndexEntries(sessionIndexCache);
    const timeoutMs = Math.max(1000, Number(options?.timeoutMs) || 3000);
    const [indexEntries, idSet] = await Promise.all([
        readStoredSessionIndexEntries(Math.min(timeoutMs, 2500)).catch(() => null),
        readSessionIdSetOrNull(Math.min(timeoutMs, 2500))
    ]);

    if (idSet && Array.isArray(indexEntries)) {
        const indexedIds = new Set(indexEntries.map((entry) => entry.id));
        const inSync = indexedIds.size === indexEntries.length
            && indexedIds.size === idSet.size
            && Array.from(idSet).every((id) => indexedIds.has(id));
        if (inSync) {
            sessionIndexCache = indexEntries;
            sessionIndexValidated = true;
            return sortSessionIndexEntries(indexEntries);
        }
    }
    if (idSet && idSet.size === 0) {
        sessionIndexCache = [];
        sessionIndexValidated = true;
        void persistSessionIndex([]);
        return [];
    }

    const sessions = await readSessionStoreAllWithTimeout('sessions', timeoutMs);
    const rebuilt = (Array.isArray(sessions) ? sessions : []).map(buildSessionIndexEntry).filter(Boolean);
    sessionIndexCache = sortSessionIndexEntries(rebuilt);
    sessionIndexValidated = Array.isArray(sessions);
    void persistSessionIndex(sessionIndexCache);
    return sessionIndexCache;
}

function getWelcomeSessionSummaryForEntry(session) {
    const cached = session?.summary;
    if (cached && typeof cached === 'object'
        && Object.prototype.hasOwnProperty.call(cached, 'accuracyPct')
        && Object.prototype.hasOwnProperty.call(cached, 'remainingCount')) {
        return cached;
    }
    return deriveWelcomeSessionSummary(session?.data);
}

const SESSION_SAVE_FAILURE_NOTICE_ID = 'session-save-failure-notice';
const SESSION_SAVE_WITH_BANKS_TIMEOUT_MS = 25000;

// Question banks that are live in a session but not yet committed. The next save of
// that session writes them in the same transaction as its record, so a session never
// commits pointing at a bank that did not.
const pendingQuestionBankWrites = new Map();

function queueQuestionBankPersistence(sessionId, setKey, questionArray) {
    const id = normalizeSessionId(sessionId);
    const key = String(setKey || '').trim();
    if (!id || !key || !Array.isArray(questionArray)) return;
    pendingQuestionBankWrites.set(`${id}\u0000${key}`, { sessionId: id, setKey: key, questions: questionArray });
}

function getPendingQuestionBankWrites(sessionId) {
    const id = normalizeSessionId(sessionId);
    return Array.from(pendingQuestionBankWrites.values()).filter((entry) => entry.sessionId === id);
}

// Clears only the exact entries that committed; a bank queued again meanwhile stays.
function settlePendingQuestionBankWrites(entries) {
    (Array.isArray(entries) ? entries : []).forEach((entry) => {
        const mapKey = `${entry.sessionId}\u0000${entry.setKey}`;
        if (pendingQuestionBankWrites.get(mapKey) === entry) pendingQuestionBankWrites.delete(mapKey);
    });
}

function buildQuestionBankWrites(pendingBanks) {
    return (Array.isArray(pendingBanks) ? pendingBanks : [])
        .map((entry) => ({ storeName: 'questions', key: entry.setKey, value: entry.questions }));
}

function buildSessionSaveWrites(sessionRecord, pendingBanks) {
    return buildQuestionBankWrites(pendingBanks)
        .concat([{ storeName: 'sessions', key: null, value: sessionRecord }]);
}

/* --- CONCURRENT SESSION WRITES ---
   Two tabs can hold the same session. Each remembers the progress it last read or
   committed (its base) and, when it saves, sends only its own changes since that
   base, per progress family and question key. The save reads the stored record and
   writes the merge inside one transaction: a key the other writer left alone takes
   this tab's value (a cleared value deletes it), and a key both changed differently
   is a conflict that keeps the stored value until the learner chooses. Timestamps,
   revisions, and answer counts never pick a winner. A session this tab knows existed
   and finds missing was deleted elsewhere, and a late save does not recreate it. */
const SESSION_TAB_ID = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
const SESSION_WRITE_SIGNAL_KEY = 'memora.session_write_signal_v1';
const SESSION_WRITE_CHANNEL_NAME = 'memora-session-writes';
const SESSION_CONFLICT_NOTICE_ID = 'session-save-conflict-notice';

function createEmptySessionProgressBuckets() {
    return Object.fromEntries(SESSION_PROGRESS_FAMILIES.map((family) => [family, {}]));
}

let sessionPersistenceBase = { sessionId: '', exists: false, revision: 0, buckets: createEmptySessionProgressBuckets() };

// Progress for questions outside the open pool, by question key. Opening another bank
// rebuilds the pool from the active sets; the leaving pool's progress is parked here and
// returns when its bank is selected again, and saves carry it so a returning bank is
// never measured as cleared. knownKeys records every key this tab has had open, so a
// parked absence (progress the learner removed) is not refilled from storage.
let offPoolQuestionProgress = { sessionId: '', knownKeys: new Set(), buckets: createEmptySessionProgressBuckets() };

function getOffPoolQuestionProgress() {
    const sessionId = normalizeSessionId(currentSessionId);
    if (offPoolQuestionProgress.sessionId !== sessionId) resetOffPoolQuestionProgress();
    return offPoolQuestionProgress;
}

function resetOffPoolQuestionProgress() {
    offPoolQuestionProgress = {
        sessionId: normalizeSessionId(currentSessionId),
        knownKeys: new Set(),
        buckets: createEmptySessionProgressBuckets()
    };
}

function parkQuestionProgress(poolKeys, bucketsByFamily) {
    const parked = getOffPoolQuestionProgress();
    poolKeys.forEach((key) => {
        parked.knownKeys.add(key);
        SESSION_PROGRESS_FAMILIES.forEach((family) => {
            const values = bucketsByFamily?.[family]?.[key];
            if (Array.isArray(values) && values.length) parked.buckets[family][key] = values.slice();
            else delete parked.buckets[family][key];
        });
    });
}

// Keys entering the pool take parked progress, or, for questions this tab never had
// open, what the session's stored record holds.
function addOffPoolQuestionProgress(bucketsByFamily, enteringKeys) {
    const parked = getOffPoolQuestionProgress();
    const stored = parked.sessionId && sessionPersistenceBase.sessionId === parked.sessionId
        ? sessionPersistenceBase.buckets
        : null;
    enteringKeys.forEach((key) => {
        SESSION_PROGRESS_FAMILIES.forEach((family) => {
            const source = parked.knownKeys.has(key) ? parked.buckets[family] : stored?.[family];
            const values = source?.[key];
            if (Array.isArray(values) && values.length) bucketsByFamily[family][key] = values.slice();
        });
    });
}

// A save measures the open pool plus every parked key, so progress made in a bank and
// left before the debounced save still reaches storage.
function withParkedQuestionProgress(localBuckets, observedKeys) {
    const parked = getOffPoolQuestionProgress();
    const buckets = {};
    SESSION_PROGRESS_FAMILIES.forEach((family) => { buckets[family] = { ...(localBuckets?.[family] || {}) }; });
    const observed = new Set(observedKeys);
    parked.knownKeys.forEach((key) => {
        if (observed.has(key)) return;
        observed.add(key);
        SESSION_PROGRESS_FAMILIES.forEach((family) => {
            const values = parked.buckets[family][key];
            if (values) buckets[family][key] = values.slice();
        });
    });
    return { buckets, observed };
}

function getSessionProgressAccessor(family) {
    switch (family) {
        case 'userAnswers': return [() => userAnswers, (next) => { userAnswers = next; }];
        case 'knowledgeState': return [() => knowledgeState, (next) => { knowledgeState = next; }];
        case 'savedQuestions': return [() => savedQuestions, (next) => { savedQuestions = next; }];
        case 'srsData': return [() => srsData, (next) => { srsData = next; }];
        case 'statsData': return [() => statsData, (next) => { statsData = next; }];
        default: return [() => ({}), () => { }];
    }
}

function sameSessionProgressBucket(left, right) {
    return JSON.stringify(left ?? null) === JSON.stringify(right ?? null);
}

// Progress as a stored record holds it, by question key. Records written before
// keyed buckets existed carry index maps, read against the open pool.
function readStoredSessionProgressBuckets(data) {
    const source = (data && typeof data === 'object') ? data : {};
    const buckets = {};
    SESSION_PROGRESS_FAMILIES.forEach((family) => {
        const keyed = source[`${family}ByQuestionKey`];
        // A keyed map is authoritative even when empty. Positional mirrors belong
        // to the old pool and must not manufacture progress on a new question.
        buckets[family] = hasQuestionKeyStateBuckets(keyed)
            ? sanitizeQuestionKeyBucketObject(keyed)
            : buildIndexedStateBucketsByQuestionKey(source[family]);
    });
    return buckets;
}

function buildLocalSessionProgressBuckets() {
    return {
        userAnswers: buildIndexedStateBucketsByQuestionKey(getPersistableUserAnswers()),
        knowledgeState: buildIndexedStateBucketsByQuestionKey(knowledgeState),
        savedQuestions: buildIndexedStateBucketsByQuestionKey(savedQuestions),
        srsData: buildIndexedStateBucketsByQuestionKey(srsData),
        statsData: buildIndexedStateBucketsByQuestionKey(statsData)
    };
}

function listObservedQuestionKeys() {
    const keys = new Set();
    for (let idx = 0; idx < questions.length; idx++) {
        const key = buildQuestionPersistenceKeyByIndex(idx);
        if (key) keys.add(key);
    }
    return keys;
}

// This tab's changes since its base. Only questions in the open pool are observed,
// so a key missing from the pool (an inactive set) is not a deletion.
function computeSessionProgressChanges(baseBuckets, localBuckets, observedKeys) {
    const changes = {};
    let count = 0;
    SESSION_PROGRESS_FAMILIES.forEach((family) => {
        const base = baseBuckets?.[family] || {};
        const local = localBuckets?.[family] || {};
        const familyChanges = {};
        new Set([...Object.keys(base), ...Object.keys(local)]).forEach((key) => {
            if (!observedKeys.has(key)) return;
            const before = base[key] ?? null;
            const after = local[key] ?? null;
            if (sameSessionProgressBucket(before, after)) return;
            familyChanges[key] = { base: before, next: after };
            count += 1;
        });
        changes[family] = familyChanges;
    });
    return { changes, count };
}

function mergeSessionProgressChanges(remoteBuckets, changes) {
    const merged = {};
    const conflicts = [];
    SESSION_PROGRESS_FAMILIES.forEach((family) => {
        const remote = remoteBuckets?.[family] || {};
        const next = { ...remote };
        Object.entries(changes?.[family] || {}).forEach(([key, change]) => {
            const stored = remote[key] ?? null;
            if (sameSessionProgressBucket(stored, change.base)) {
                if (change.next === null) delete next[key];
                else next[key] = change.next;
            } else if (!sameSessionProgressBucket(stored, change.next)) {
                conflicts.push({ family, key, base: change.base, local: change.next, remote: stored });
            }
        });
        merged[family] = next;
    });
    return { merged, conflicts };
}

// Index maps are derived from the merged keys, so a key deleted here cannot return
// through the legacy index fallback on the next load.
function writeSessionProgressIntoData(data, buckets) {
    SESSION_PROGRESS_FAMILIES.forEach((family) => {
        data[`${family}ByQuestionKey`] = buckets[family] || {};
        data[family] = buildIndexStateFromQuestionKeyBuckets(buckets[family] || {});
    });
    return data;
}

// Source membership as this tab last saw it stored: the manifest lists and PDF links.
function snapshotSessionMembership(data) {
    if (!data || typeof data !== 'object' || !data.manifest || typeof data.manifest !== 'object') return null;
    const list = (value) => (Array.isArray(value) ? value.map((v) => String(v || '').trim()).filter(Boolean) : []);
    const links = {};
    const rawLinks = data.manualPdfLinks && typeof data.manualPdfLinks === 'object' ? data.manualPdfLinks : {};
    Object.keys(rawLinks).forEach((name) => {
        const pdfs = list(rawLinks[name]);
        if (pdfs.length) links[name] = pdfs;
    });
    return { jsons: list(data.manifest.jsons), pdfs: list(data.manifest.pdfs), links };
}

// Membership is merged like progress: this tab's additions and removals since its
// base apply to the stored lists, so two tabs removing different sources both stick
// and a source one tab removed is not written back by another tab's stale list.
function mergeSessionMembership(storedData, localData, baseMembership) {
    const stored = snapshotSessionMembership(storedData);
    const local = snapshotSessionMembership(localData);
    if (!stored || !local || !baseMembership) return null;
    const mergeList = (storedList, localList, baseList) => {
        const baseSet = new Set(baseList);
        const localSet = new Set(localList);
        const removed = new Set(baseList.filter((name) => !localSet.has(name)));
        const added = localList.filter((name) => !baseSet.has(name));
        const out = storedList.filter((name) => !removed.has(name));
        added.forEach((name) => { if (!out.includes(name)) out.push(name); });
        return out;
    };
    const jsons = mergeList(stored.jsons, local.jsons, baseMembership.jsons || []);
    const pdfs = mergeList(stored.pdfs, local.pdfs, baseMembership.pdfs || []);
    const links = {};
    const baseLinks = baseMembership.links || {};
    new Set([...Object.keys(stored.links), ...Object.keys(local.links), ...Object.keys(baseLinks)]).forEach((name) => {
        if (!jsons.includes(name)) return;
        const merged = mergeList(stored.links[name] || [], local.links[name] || [], baseLinks[name] || [])
            .filter((pdf) => pdfs.includes(pdf));
        if (merged.length) links[name] = merged;
    });
    return { jsons, pdfs, links };
}

function reconcileSessionRecordForWrite(stored, localRecord, base, changes) {
    if (!stored && base.exists) {
        const error = new Error('This session is missing from storage. Your changes haven’t been saved. Export a copy to keep them, or Retry to restore the session.');
        error.name = 'SessionDeletedError';
        return { error };
    }
    const remote = stored ? readStoredSessionProgressBuckets(stored.data) : createEmptySessionProgressBuckets();
    const { merged, conflicts } = mergeSessionProgressChanges(remote, changes);
    const data = writeSessionProgressIntoData({ ...localRecord.data }, merged);
    let activeSets = localRecord.activeSets;
    const membership = stored && base.exists ? mergeSessionMembership(stored.data, localRecord.data, base.membership) : null;
    if (membership) {
        data.manifest = { ...(data.manifest || {}), jsons: membership.jsons, pdfs: membership.pdfs };
        data.manualPdfLinks = membership.links;
        if (Array.isArray(activeSets)) activeSets = activeSets.filter((name) => membership.jsons.includes(name));
    }
    return {
        record: {
            ...localRecord,
            ...(Array.isArray(activeSets) ? { activeSets } : {}),
            revision: (Number(stored?.revision) || 0) + 1,
            data
        },
        merged,
        conflicts
    };
}

// A conflicted key keeps the base it was measured from, so the conflict stays put
// until the learner resolves it instead of being overwritten by the next save.
// `membership` is the source list this tab's memory reflects; omitted, the same
// session keeps the one it had and a newly based session takes the list in memory.
function setSessionPersistenceBase(sessionId, revision, buckets, conflicts = [], membership = undefined) {
    const nextBuckets = createEmptySessionProgressBuckets();
    SESSION_PROGRESS_FAMILIES.forEach((family) => { nextBuckets[family] = { ...(buckets?.[family] || {}) }; });
    (conflicts || []).forEach((conflict) => {
        if (conflict.base === null) delete nextBuckets[conflict.family][conflict.key];
        else nextBuckets[conflict.family][conflict.key] = conflict.base;
    });
    const normalizedId = normalizeSessionId(sessionId);
    let nextMembership = membership;
    if (nextMembership === undefined) {
        nextMembership = sessionPersistenceBase.sessionId === normalizedId && sessionPersistenceBase.membership
            ? sessionPersistenceBase.membership
            : snapshotSessionMembership({ manifest: sessionManifest, manualPdfLinks: pdfAssociations });
    }
    sessionPersistenceBase = {
        sessionId: normalizedId,
        exists: true,
        revision: Number(revision) || 0,
        buckets: nextBuckets,
        membership: nextMembership
    };
}

function resetSessionPersistenceBase(sessionId) {
    sessionPersistenceBase = {
        sessionId: normalizeSessionId(sessionId),
        exists: false,
        revision: 0,
        buckets: createEmptySessionProgressBuckets(),
        membership: null
    };
}

// Brings changes another writer committed into memory, for keys this tab did not
// also change; conflicted keys keep this tab's value.
function adoptStoredSessionProgress(mergedBuckets, conflicts, localBuckets) {
    const conflictKeys = new Set((conflicts || []).map((conflict) => `${conflict.family}\u0000${conflict.key}`));
    let adopted = 0;
    SESSION_PROGRESS_FAMILIES.forEach((family) => {
        const merged = mergedBuckets?.[family] || {};
        const local = localBuckets?.[family] || {};
        const changedKeys = new Set(
            Object.keys({ ...merged, ...local }).filter((key) => (
                !conflictKeys.has(`${family}\u0000${key}`) && !sameSessionProgressBucket(merged[key], local[key])
            ))
        );
        if (!changedKeys.size) return;
        // Parked banks adopt the stored value too, or the next save would write the
        // stale parked copy back over the other writer's change.
        const parked = getOffPoolQuestionProgress();
        changedKeys.forEach((key) => {
            if (!parked.knownKeys.has(key)) return;
            if (Array.isArray(merged[key]) && merged[key].length) parked.buckets[family][key] = merged[key].slice();
            else delete parked.buckets[family][key];
        });
        const rebuilt = buildIndexStateFromQuestionKeyBuckets(merged);
        const [read, write] = getSessionProgressAccessor(family);
        const next = { ...(read() || {}) };
        for (let idx = 0; idx < questions.length; idx++) {
            if (!changedKeys.has(buildQuestionPersistenceKeyByIndex(idx))) continue;
            if (rebuilt[idx] === undefined) delete next[idx];
            else next[idx] = rebuilt[idx];
            adopted += 1;
        }
        write(next);
    });
    return adopted;
}

const SessionConflictStatus = {
    bySession: new Map(),
    set(sessionId, conflicts) {
        const id = normalizeSessionId(sessionId);
        if (Array.isArray(conflicts) && conflicts.length) this.bySession.set(id, conflicts.slice());
        else this.bySession.delete(id);
        this.render();
    },
    get(sessionId) {
        return this.bySession.get(normalizeSessionId(sessionId)) || [];
    },
    // Keep mine: measure this tab's values against what is stored now, then save.
    keepMine(sessionId) {
        const id = normalizeSessionId(sessionId);
        if (normalizeSessionId(currentSessionId) !== id || sessionPersistenceBase.sessionId !== id) return Promise.resolve(false);
        this.get(id).forEach((conflict) => {
            const family = sessionPersistenceBase.buckets[conflict.family];
            if (conflict.remote === null) delete family[conflict.key];
            else family[conflict.key] = conflict.remote;
        });
        this.set(id, []);
        markSessionDirty();
        return SessionManager.save(false);
    },
    // Keep theirs: put the stored values back into memory; nothing is left to send.
    keepTheirs(sessionId) {
        const id = normalizeSessionId(sessionId);
        if (normalizeSessionId(currentSessionId) !== id || sessionPersistenceBase.sessionId !== id) return Promise.resolve(false);
        const byFamily = {};
        this.get(id).forEach((conflict) => {
            const family = sessionPersistenceBase.buckets[conflict.family];
            if (conflict.remote === null) delete family[conflict.key];
            else family[conflict.key] = conflict.remote;
            if (!byFamily[conflict.family]) byFamily[conflict.family] = {};
            byFamily[conflict.family][conflict.key] = conflict.remote;
        });
        Object.entries(byFamily).forEach(([familyName, remoteByKey]) => {
            const [read, write] = getSessionProgressAccessor(familyName);
            const rebuilt = buildIndexStateFromQuestionKeyBuckets(
                Object.fromEntries(Object.entries(remoteByKey).filter(([, values]) => values !== null))
            );
            const next = { ...(read() || {}) };
            for (let idx = 0; idx < questions.length; idx++) {
                if (!Object.prototype.hasOwnProperty.call(remoteByKey, buildQuestionPersistenceKeyByIndex(idx))) continue;
                if (rebuilt[idx] === undefined) delete next[idx];
                else next[idx] = rebuilt[idx];
            }
            write(next);
        });
        this.set(id, []);
        markSessionDirty();
        return SessionManager.save(false);
    },
    exportConflicts(sessionId) {
        const conflicts = this.get(sessionId);
        if (!conflicts.length) return false;
        const payload = {
            kind: 'memora-session-conflicts',
            version: 1,
            exportedAt: new Date().toISOString(),
            sessionId: normalizeSessionId(sessionId),
            conflicts
        };
        triggerBlobDownload(
            new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' }),
            `memora-session-conflicts-${normalizeSessionId(sessionId) || 'session'}.json`
        );
        return true;
    },
    render() {
        if (typeof document === 'undefined' || !document.body) return;
        const sessionId = normalizeSessionId(currentSessionId);
        const conflicts = this.get(sessionId);
        let notice = document.getElementById(SESSION_CONFLICT_NOTICE_ID);
        if (!conflicts.length) {
            notice?.remove();
            return;
        }
        if (!notice) {
            notice = document.createElement('div');
            notice.id = SESSION_CONFLICT_NOTICE_ID;
            notice.className = 'quiz-action-toast session-save-conflict-notice show';
            notice.setAttribute('role', 'alert');
            document.body.appendChild(notice);
        }
        const count = conflicts.length;
        const message = document.createElement('span');
        message.className = 'quiz-action-toast-message';
        message.textContent = `Another window changed ${count} of the same ${count === 1 ? 'answer' : 'answers'} as this one. Your ${count === 1 ? 'version is' : 'versions are'} not saved yet. Which should stay?`;
        const makeAction = (label, onClick) => {
            const button = document.createElement('button');
            button.type = 'button';
            button.className = 'quiz-action-toast-action';
            button.textContent = label;
            button.addEventListener('click', onClick);
            return button;
        };
        notice.replaceChildren(
            message,
            makeAction('Keep mine', () => { void this.keepMine(sessionId); }),
            makeAction('Keep theirs', () => { void this.keepTheirs(sessionId); }),
            makeAction('Export', () => { this.exportConflicts(sessionId); })
        );
    }
};

let sessionWriteChannel = null;

function getSessionWriteChannel() {
    if (sessionWriteChannel === null) {
        try {
            sessionWriteChannel = typeof BroadcastChannel === 'function' ? new BroadcastChannel(SESSION_WRITE_CHANNEL_NAME) : false;
            if (sessionWriteChannel) sessionWriteChannel.onmessage = (event) => handlePeerSessionWrite(event.data);
        } catch (_) {
            sessionWriteChannel = false;
        }
    }
    return sessionWriteChannel || null;
}

// Tells other tabs a session committed. The message names the session and revision
// only; no study content leaves this tab.
function announceSessionWrite(sessionId, revision) {
    const message = { sessionId: normalizeSessionId(sessionId), revision: Number(revision) || 0, writerId: SESSION_TAB_ID };
    const channel = getSessionWriteChannel();
    if (channel) {
        try {
            channel.postMessage(message);
            return;
        } catch (_) { }
    }
    try {
        localStorage.setItem(SESSION_WRITE_SIGNAL_KEY, JSON.stringify({ ...message, at: Date.now() }));
    } catch (_) { }
}

function handlePeerSessionWrite(message) {
    if (!message || message.writerId === SESSION_TAB_ID) return;
    if (message.sessionId === '') {
        sessionFoldersCache = null;
        window.dispatchEvent(new CustomEvent('memora:session-folders-change'));
        return;
    }
    if (normalizeSessionId(message.sessionId) !== normalizeSessionId(currentSessionId)) return;
    if ((Number(message.revision) || 0) <= sessionPersistenceBase.revision) return;
    void reconcileOpenSessionWithStore();
}

// Renames in one read-modify-write transaction so only customName changes: progress
// another tab or an autosave wrote after this tab's last read is kept, and the bumped
// revision tells other tabs to reconcile. Resolves to the stored record, or null when no
// such session is stored. A write that fails throws, so a rejected save is never mistaken
// for a session that simply is not saved yet.
async function renameStoredSessionRecord(sessionId, customName) {
    if (customName) await assertSessionNameAvailable(customName, sessionId);
    try {
        const outcome = await IDB.compareAndPut(sessionId, (stored) => stored
            ? { record: { ...stored, customName: customName || null, revision: (Number(stored.revision) || 0) + 1 } }
            : { error: Object.assign(new Error('Session not found.'), { name: 'NotFoundError' }) });
        announceSessionWrite(sessionId, outcome.record.revision);
        return outcome.record;
    } catch (error) {
        if (error?.name === 'NotFoundError') return null;
        console.warn('Session rename failed:', error);
        throw error;
    }
}

// Keeps saved sessions within the cap by moving the oldest overflow to Recently
// Deleted (restorable), never the session that was just saved. Summaries carry
// id and timestamp, so this does not deserialize every saved session.
async function enforceSavedSessionRetention(protectedSessionId) {
    const movedNames = [];
    try {
        const summaries = await readSavedSessionSummaries({ timeoutMs: SESSION_RESTORE_SESSION_TIMEOUT_MS });
        let overflow = summaries.length - SAVED_SESSION_RETENTION_LIMIT;
        const protectedId = normalizeSessionId(protectedSessionId);
        for (let index = summaries.length - 1; overflow > 0 && index >= 0; index -= 1) {
            const id = normalizeSessionId(summaries[index]?.id);
            if (!id || id === protectedId || id === normalizeSessionId(currentSessionId)) continue;
            if (await softDeleteSession(id)) {
                overflow -= 1;
                movedNames.push(typeof getDeletedSessionDisplayName === 'function'
                    ? getDeletedSessionDisplayName({ id, data: summaries[index] })
                    : (summaries[index]?.customName || 'An older session'));
            }
        }
        if (movedNames.length) showSavedSessionRetentionNotice(movedNames);
    } catch (error) {
        // Retention is housekeeping; it must not fail a save that already committed.
        console.warn('Saved-session retention skipped:', error);
    }
}

// Without persistence a browser may clear local study data under storage pressure. The
// request is made once, after the first committed save that holds content, and a refusal is
// a normal outcome. Persistence lowers that risk; it never makes data permanent.
const MEMORA_STORAGE_PERSIST_REQUESTED_KEY = 'memora.storage_persist_requested_v1';

async function requestMemoraPersistentStorageOnce() {
    const storage = typeof navigator !== 'undefined' ? navigator.storage : null;
    if (!storage || typeof storage.persist !== 'function') return;
    try {
        if (localStorage.getItem(MEMORA_STORAGE_PERSIST_REQUESTED_KEY)) return;
        if (typeof storage.persisted === 'function' && await storage.persisted()) return;
        localStorage.setItem(MEMORA_STORAGE_PERSIST_REQUESTED_KEY, String(Date.now()));
        await storage.persist();
    } catch (error) {
        console.info('Persistent storage was not requested:', error?.name || error);
    }
}

// Settings > Data status line. Never claims more than the browser reports.
async function describeMemoraStoragePersistence() {
    const storage = typeof navigator !== 'undefined' ? navigator.storage : null;
    const unknown = 'This browser does not say whether it protects saved data. Clearing site data removes it.';
    if (!storage || typeof storage.persisted !== 'function') return unknown;
    try {
        return (await storage.persisted())
            ? 'This browser will not clear Memoraful data on its own. Clearing site data yourself still removes it.'
            : 'This browser may clear saved data if the device runs low on space. Export anything you cannot recreate.';
    } catch (_) {
        return unknown;
    }
}
window.describeMemoraStoragePersistence = describeMemoraStoragePersistence;

// The cap moves sessions to Recently Deleted, where they stay restorable; naming them keeps
// that from looking like lost work.
// Its own toast, kept until dismissed: the shared popup is replaced by the next message.
function showSavedSessionRetentionNotice(names = []) {
    const label = names.length === 1 ? `"${names[0]}"` : `${names.length} older sessions`;
    const days = typeof DELETION_RETENTION_DAYS === 'number' ? DELETION_RETENTION_DAYS : 15;
    document.getElementById('session-retention-notice')?.remove();
    const notice = document.createElement('div');
    notice.id = 'session-retention-notice';
    notice.className = 'quiz-action-toast session-retention-notice show';
    notice.setAttribute('role', 'status');
    const message = document.createElement('span');
    message.className = 'quiz-action-toast-message';
    message.textContent = `${label} moved to Recently Deleted to stay under ${SAVED_SESSION_RETENTION_LIMIT} saved sessions. You can restore it there for ${days} days.`;
    notice.appendChild(message);
    const makeAction = (text, onClick) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'quiz-action-toast-action';
        button.textContent = text;
        button.addEventListener('click', onClick);
        notice.appendChild(button);
    };
    if (typeof showRecentlyDeleted === 'function') {
        makeAction('Recently Deleted', () => {
            notice.remove();
            void showRecentlyDeleted();
        });
    }
    makeAction('Dismiss', () => notice.remove());
    document.body.appendChild(notice);
}

// A signal only prompts this. Unsaved memory is never overwritten: a dirty tab saves,
// which merges; a clean tab reads the stored record and adopts it.
async function reconcileOpenSessionWithStore() {
    if (IS_EMBED_DEMO) return;
    const sessionId = normalizeSessionId(currentSessionId);
    if (!sessionId || sessionPersistenceBase.sessionId !== sessionId || !sessionPersistenceBase.exists) return;
    if (sessionDirty) {
        saveSessionDebounced();
        return;
    }
    const stored = await readSessionStoreValueWithTimeout('sessions', sessionId, SESSION_RESTORE_SESSION_TIMEOUT_MS);
    if (!stored || sessionDirty || normalizeSessionId(currentSessionId) !== sessionId) return;
    if ((Number(stored.revision) || 0) <= sessionPersistenceBase.revision) return;
    const remote = readStoredSessionProgressBuckets(stored.data);
    const conflicts = SessionConflictStatus.get(sessionId);
    adoptStoredSessionProgress(remote, conflicts, buildLocalSessionProgressBuckets());
    setSessionPersistenceBase(sessionId, stored.revision, remote, conflicts);
}

if (typeof window !== 'undefined' && typeof document !== 'undefined') {
    getSessionWriteChannel();
    window.addEventListener('storage', (event) => {
        if (event.key !== SESSION_WRITE_SIGNAL_KEY || !event.newValue) return;
        try { handlePeerSessionWrite(JSON.parse(event.newValue)); } catch (_) { }
    });
    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') void reconcileOpenSessionWithStore();
    });
}

// Outcome of session saves. A failed save keeps the record it tried to write and
// stays visible, with Retry and Export, until a newer save of that session
// commits. Attempts are ordered, so an older save finishing late can neither
// clear a newer failure nor raise one that a newer save already superseded.
const SessionSaveStatus = {
    sequence: 0,
    dismissedSequence: 0,
    bySession: new Map(),
    entry(sessionId) {
        const key = normalizeSessionId(sessionId);
        if (!this.bySession.has(key)) this.bySession.set(key, { lastSucceeded: 0, failure: null });
        return this.bySession.get(key);
    },
    begin(sessionId, revision) {
        this.sequence += 1;
        return { sessionId: normalizeSessionId(sessionId), revision, sequence: this.sequence };
    },
    succeed(attempt) {
        const entry = this.entry(attempt.sessionId);
        entry.lastSucceeded = Math.max(entry.lastSucceeded, attempt.sequence);
        if (entry.failure && entry.failure.sequence <= attempt.sequence) entry.failure = null;
        this.render();
    },
    fail(attempt, record, error, banks = [], reconcile = null) {
        console.warn('Session save failed; the unsaved state stays in memory:', attempt.sessionId, error);
        const entry = this.entry(attempt.sessionId);
        if (entry.lastSucceeded > attempt.sequence) return;
        if (entry.failure && entry.failure.sequence > attempt.sequence) return;
        // Keep the base and changes the record was measured against, so a retry
        // reconciles inside the same transaction as a normal save.
        const previousReconcile = entry.failure?.reconcile || null;
        entry.failure = {
            sequence: attempt.sequence,
            revision: attempt.revision,
            record,
            error,
            banks: Array.isArray(banks) ? banks : [],
            reconcile: reconcile || previousReconcile
        };
        this.render();
    },
    getFailure(sessionId) {
        return this.bySession.get(normalizeSessionId(sessionId))?.failure || null;
    },
    latestFailure() {
        let latest = null;
        this.bySession.forEach((entry) => {
            if (entry.failure && (!latest || entry.failure.sequence > latest.sequence)) latest = entry.failure;
        });
        return latest;
    },
    async retry(sessionId) {
        const failure = this.getFailure(sessionId);
        if (!failure) return true;
        // The open session retries from its latest in-memory revision. A session the
        // user has since left retries the exact record that failed.
        if (normalizeSessionId(currentSessionId) === normalizeSessionId(sessionId)) {
            // Retrying after a deletion elsewhere is the learner choosing to restore it.
            if (failure.error?.name === 'SessionDeletedError' && sessionPersistenceBase.sessionId === normalizeSessionId(sessionId)) {
                sessionPersistenceBase = { ...sessionPersistenceBase, exists: false };
            }
            return SessionManager.save(false);
        }
        const attempt = this.begin(sessionId, failure.revision);
        // The newer-copy check and the write happen in one transaction, so a peer
        // commit can never land between them and be overwritten by this old record.
        const restoringDeleted = failure.error?.name === 'SessionDeletedError';
        const reconcile = failure.reconcile;
        const base = reconcile?.base
            ? { ...reconcile.base, exists: restoringDeleted ? false : reconcile.base.exists }
            : null;
        try {
            await IDB.compareAndPut(failure.record.id, (stored) => {
                const peerCommitted = stored && (
                    Date.parse(stored.timestamp) > Date.parse(failure.record.timestamp)
                    || (base && (Number(stored.revision) || 0) > (Number(base.revision) || 0))
                );
                if (peerCommitted) {
                    const conflict = new Error('A newer copy of this session was saved since. Export this copy to keep it.');
                    conflict.name = 'SessionSaveConflictError';
                    return { error: conflict };
                }
                if (base) return reconcileSessionRecordForWrite(stored, failure.record, base, reconcile.changes || {});
                return { record: { ...failure.record, revision: (Number(stored?.revision) || 0) + 1 }, merged: null, conflicts: [] };
            }, buildQuestionBankWrites(failure.banks),
            failure.banks.length ? SESSION_SAVE_WITH_BANKS_TIMEOUT_MS : SESSION_RESTORE_SESSION_TIMEOUT_MS);
        } catch (error) {
            this.fail(attempt, failure.record, error, failure.banks);
            return false;
        }
        settlePendingQuestionBankWrites(failure.banks);
        this.succeed(attempt);
        return true;
    },
    exportFailure(sessionId) {
        const failure = this.getFailure(sessionId);
        if (!failure) return false;
        const payload = {
            kind: 'memora-unsaved-session',
            version: 1,
            exportedAt: new Date().toISOString(),
            session: failure.record,
            questionBanks: Object.fromEntries((failure.banks || []).map((entry) => [entry.setKey, entry.questions]))
        };
        triggerBlobDownload(
            new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' }),
            `memora-unsaved-session-${normalizeSessionId(failure.record?.id) || 'session'}.json`
        );
        return true;
    },
    describe(failure) {
        const errorName = failure?.error?.name || '';
        if (errorName === 'QuotaExceededError') return 'Browser storage is full, so this session was not saved.';
        if (errorName === 'SessionSaveConflictError' || errorName === 'SessionDeletedError') return failure.error.message;
        return 'This session could not be saved.';
    },
    render() {
        if (typeof document === 'undefined' || !document.body) return;
        const failure = this.latestFailure();
        let notice = document.getElementById(SESSION_SAVE_FAILURE_NOTICE_ID);
        if (!failure || failure.sequence <= this.dismissedSequence) {
            notice?.remove();
            return;
        }
        if (!notice) {
            notice = document.createElement('div');
            notice.id = SESSION_SAVE_FAILURE_NOTICE_ID;
            notice.className = 'quiz-action-toast session-save-failure-notice show';
            notice.setAttribute('role', 'alert');
            document.body.appendChild(notice);
        }
        const sessionId = normalizeSessionId(failure.record?.id);
        let sessionLabel = '';
        try { sessionLabel = formatWelcomeSessionDisplayName(failure.record); } catch (_) { }
        const message = document.createElement('span');
        message.className = 'quiz-action-toast-message';
        message.textContent = sessionLabel
            ? `${sessionLabel}: ${this.describe(failure)}`
            : this.describe(failure);
        const makeAction = (label, onClick) => {
            const button = document.createElement('button');
            button.type = 'button';
            button.className = 'quiz-action-toast-action';
            button.textContent = label;
            button.addEventListener('click', onClick);
            return button;
        };
        const retryButton = makeAction('Retry', async () => {
            retryButton.disabled = true;
            await this.retry(sessionId);
            retryButton.disabled = false;
        });
        const exportButton = makeAction('Export', () => { this.exportFailure(sessionId); });
        const dismissButton = makeAction('Dismiss', () => {
            this.dismissedSequence = failure.sequence;
            this.render();
        });
        notice.replaceChildren(message, retryButton, exportButton, dismissButton);
    }
};

const SessionManager = {
    async save(showNotification = false, { sourceRemoval = null } = {}) {
        if (sourceRemoval && sourceRemoval.sessionId !== currentSessionId) return false;
        if (IS_EMBED_DEMO) return;
        if (!hasPersistableSessionContent()) {
            await discardEmptyCurrentSessionPersistence();
            return;
        }
        if (!currentSessionId) currentSessionId = Date.now().toString();
        const saveRevision = sessionStateRevision;
        // Everything below targets this session, even if the learner switches mid-save.
        const saveSessionId = currentSessionId;

        const quizPersistenceState = getQuizPersistenceStateForSave();
        const pdfTutorStudyState = getTransientPdfTutorStudyPersistenceState();
        const toolState = {
            mix: isMixAnswers,
            hard: isHardMode,
            shuffle: isShuffled,
            as: autoSaveWrong,
            aa: Object.prototype.hasOwnProperty.call(quizPersistenceState, 'autoAdvance') ? !!quizPersistenceState.autoAdvance : autoAdvance,
            ae: Object.prototype.hasOwnProperty.call(quizPersistenceState, 'autoEvidence') ? !!quizPersistenceState.autoEvidence : autoEvidence,
            ax: false,
            zen: isZenMode,
            adaptive: Object.prototype.hasOwnProperty.call(quizPersistenceState, 'isAdaptiveReviewMode') ? !!quizPersistenceState.isAdaptiveReviewMode : isAdaptiveReviewMode,
            // Today's review is Spaced Review over seen cards only; a reload must not widen it.
            adaptiveSeenOnly: adaptiveReviewSeenOnly,
            flashcardAdaptive: pdfTutorStudyState
                ? false
                : (Object.prototype.hasOwnProperty.call(quizPersistenceState, 'isFlashcardAdaptiveReviewMode') ? !!quizPersistenceState.isFlashcardAdaptiveReviewMode : isFlashcardAdaptiveReviewMode)
        };

        const themeState = getCurrentThemeStateSnapshot();

        const normalizeSetKeyForSave = (key) => String(key || '')
            .trim()
            .toLowerCase()
            .replace(/\.(json|txt)$/i, '');
        const activeSetArray = Array.from(activeQuestionSetKeys || []).map(v => String(v || '').trim()).filter(Boolean);
        const learnBaseActiveSetArray = pdfTutorStudyState && Array.isArray(pdfTutorStudyState.mainActiveSetKeys)
            ? pdfTutorStudyState.mainActiveSetKeys.map(v => String(v || '').trim()).filter(Boolean)
            : activeSetArray;
        const activeSetLookup = new Set(learnBaseActiveSetArray);
        const activeSetNormalizedLookup = new Set(learnBaseActiveSetArray.map(normalizeSetKeyForSave));
        let activeFiles = Array.isArray(sessionManifest?.jsons)
            ? sessionManifest.jsons.filter((key) => {
                const normalized = normalizeSetKeyForSave(key);
                return activeSetLookup.has(key) || activeSetNormalizedLookup.has(normalized);
            })
            : [];
        if (!activeFiles.length) {
            activeFiles = learnBaseActiveSetArray;
        }
        activeFiles = Array.from(new Set(activeFiles.map(v => String(v || '').trim()).filter(Boolean)));

        const persistedUserAnswers = getPersistableUserAnswers();
        const persistedUserAnswersByQuestionKey = buildIndexedStateBucketsByQuestionKey(persistedUserAnswers);
        const persistedSavedQuestionsByQuestionKey = buildIndexedStateBucketsByQuestionKey(savedQuestions);
        const persistedKnowledgeStateByQuestionKey = buildIndexedStateBucketsByQuestionKey(knowledgeState);
        const persistedSrsDataByQuestionKey = buildIndexedStateBucketsByQuestionKey(srsData);
        const persistedStatsDataByQuestionKey = buildIndexedStateBucketsByQuestionKey(statsData);
        const persistedAiQuestionCacheByQuestionKey = buildIndexedStateBucketsByQuestionKey(aiQuestionCacheByIndex);
        const persistedAdaptiveReviewSnapshotByQuestionKey = buildIndexedStateBucketsByQuestionKey(adaptiveReviewSnapshot);
        const persistedFilteredQuestionKeys = buildFilteredQuestionKeysForPersistence(quizPersistenceState.filteredIndices);
        const persistedCondensedQuizState = normalizeCondensedQuizState(condensedQuizState);
        const pdfTutorQuizCheckpoint = getTransientPdfTutorQuizCheckpointPersistenceState();
        const resumeStateUpdatedAt = Date.now();
        const nextSessionData = {
            userAnswers: persistedUserAnswers,
            userAnswersByQuestionKey: persistedUserAnswersByQuestionKey,
            knowledgeState: { ...knowledgeState },
            knowledgeStateByQuestionKey: persistedKnowledgeStateByQuestionKey,
            savedQuestions: { ...savedQuestions },
            savedQuestionsByQuestionKey: persistedSavedQuestionsByQuestionKey,
            srsData: { ...srsData },
            srsDataByQuestionKey: persistedSrsDataByQuestionKey,
            statsData: { ...statsData },
            statsDataByQuestionKey: persistedStatsDataByQuestionKey,
            aiQuestionCacheByIndex: { ...aiQuestionCacheByIndex },
            aiQuestionCacheByQuestionKey: persistedAiQuestionCacheByQuestionKey,
            adaptiveReviewSnapshot: { ...adaptiveReviewSnapshot },
            adaptiveReviewSnapshotByQuestionKey: persistedAdaptiveReviewSnapshotByQuestionKey,
            adaptiveSnapshotRankFilters: Array.from(adaptiveSnapshotRankFilters),
            currentQIndex: quizPersistenceState.currentQIndex,
            filteredIndices: quizPersistenceState.filteredIndices,
            filteredQuestionKeys: persistedFilteredQuestionKeys,
            quizCompletionCohortState: getQuizCompletionCohortPersistenceState(),
            isFilterActive: quizPersistenceState.isFilterActive,
            resumeStateUpdatedAt,
            condensedQuizState: persistedCondensedQuizState,
            highYieldQuizState: normalizeHighYieldQuizState(highYieldQuizState),
            adaptivePracticeState: normalizeAdaptivePracticeState(adaptivePracticeState),
            startTime: startTime || Date.now(),
            toolState: toolState,
            themeState: themeState,
            studyDashboard: createDefaultStudyDashboardState(studyDashboardState),
            studySetup: createDefaultStudySetupState(studySetupState),
            studyPlan: createDefaultStudyPlanState(studyPlanState),
            pdfTutorStudyState,
            pdfTutorQuizCheckpoint,
            flashcardPassState: (recordFlashcardStraightPass(), { ...flashcardPassStateByDeck }),
            // Persisted field name stays `manualPdfLinks` for saved-session compatibility.
            manualPdfLinks: { ...pdfAssociations },
            manifest: {
                jsons: Array.isArray(sessionManifest?.jsons) ? [...sessionManifest.jsons] : [],
                pdfs: Array.isArray(sessionManifest?.pdfs) ? [...sessionManifest.pdfs] : []
            }
        };
        const localProgressBuckets = {
            userAnswers: persistedUserAnswersByQuestionKey,
            knowledgeState: persistedKnowledgeStateByQuestionKey,
            savedQuestions: persistedSavedQuestionsByQuestionKey,
            srsData: persistedSrsDataByQuestionKey,
            statsData: persistedStatsDataByQuestionKey
        };
        const observedQuestionKeys = listObservedQuestionKeys();
        // The base and parked progress belong to this session only while it is open;
        // capture them before the first await so a switch cannot rewrite the evidence
        // (that the session existed, what it held) this save is reconciled against.
        const originBase = sessionPersistenceBase.sessionId === normalizeSessionId(saveSessionId)
            ? sessionPersistenceBase
            : { sessionId: normalizeSessionId(saveSessionId), exists: false, revision: 0, buckets: createEmptySessionProgressBuckets(), membership: null };
        const withParked = withParkedQuestionProgress(localProgressBuckets, observedQuestionKeys);
        const sessionCustomNameAtSave = currentSessionCustomName;
        const sessionNumberAtSave = currentSessionNumber;
        const sessionExplicitAtSave = currentSessionExplicit === true;

        // Preserve existing customName if session already exists; otherwise use a name chosen before imports.
        const existingSession = await readSessionStoreValueWithTimeout('sessions', saveSessionId, SESSION_RESTORE_SESSION_TIMEOUT_MS);
        const customName = normalizeSessionNameInput(existingSession?.customName || sessionCustomNameAtSave) || null;
        const sessionNumber = sessionNumberAtSave
            || normalizeSessionNumber(existingSession?.sessionNumber)
            || null;
        if (normalizeSessionId(currentSessionId) === normalizeSessionId(saveSessionId)) {
            currentSessionNumber = sessionNumber;
            currentSessionCustomName = customName || '';
        }
        let activeSetsToPersist = activeFiles;
        const currentProgressMeta = getCurrentSessionProgressMeta({
            userAnswers: persistedUserAnswers,
            filteredIndices: quizPersistenceState.filteredIndices
        });
        const existingProgressMeta = existingSession
            ? buildSessionProgressMetaFromContainer(existingSession.data, existingSession.timestamp)
            : null;
        if (existingSession?.data && isSessionStateOlder(currentProgressMeta, existingProgressMeta)) {
            const protectedSessionData = mergeSessionStateForSafeSave(existingSession.data, nextSessionData);
            protectedSessionData.progressMeta = buildMergedSessionProgressMeta(existingProgressMeta, currentProgressMeta, protectedSessionData);
            Object.assign(nextSessionData, protectedSessionData);
            activeSetsToPersist = mergeStringList(existingSession.activeSets, activeFiles);
            console.warn('Protected session save from older in-memory state:', saveSessionId);
        } else {
            nextSessionData.progressMeta = currentProgressMeta;
        }
        // Remove membership from the detached record. Runtime sources remain intact
        // until the transaction commits, including on a failed or superseded save.
        if (sourceRemoval) {
            const { key, type } = sourceRemoval;
            const manifest = nextSessionData.manifest;
            const links = { ...nextSessionData.manualPdfLinks };
            if (type === 'json') {
                manifest.jsons = manifest.jsons.filter(name => name !== key);
                activeSetsToPersist = activeSetsToPersist.filter(name => name !== key);
                delete links[key];
            } else {
                manifest.pdfs = manifest.pdfs.filter(name => name !== key);
                Object.keys(links).forEach(name => {
                    if (!Array.isArray(links[name])) return;
                    links[name] = links[name].filter(pdf => pdf !== key);
                    if (!links[name].length) delete links[name];
                });
            }
            nextSessionData.manualPdfLinks = links;
        }
        const finalProgressMeta = buildSessionProgressMetaFromContainer(nextSessionData);
        sessionProgressRevision = finalProgressMeta.revision;
        sessionProgressUpdatedAt = finalProgressMeta.updatedAt || Date.now();

        const session = {
            id: saveSessionId,
            timestamp: new Date().toISOString(),
            sessionNumber,
            customName: customName, // Preserve the custom name
            // Survives the first real content arriving, so the flag never has to be recomputed.
            explicit: sessionExplicitAtSave || existingSession?.explicit === true,
            activeSets: activeSetsToPersist,
            data: nextSessionData
        };

        // A save that began before a newer in-memory mutation must not land
        // after the newer snapshot and restore transient Tutor/Learn state.
        if (sessionStateRevision !== saveRevision) {
            sessionDirty = true;
            return false;
        }

        // Resolve membership immediately before the write. Recently loaded sessions keep
        // their folder even if the protective IDB reread times out, and a concurrent move
        // wins over stale save state instead of being written back into Ungrouped.
        session.folderId = resolveSessionFolderMembershipForSave(saveSessionId, existingSession);
        const pendingBanks = getPendingQuestionBankWrites(session.id);
        // A newer base for the same session (another save committed meanwhile) is
        // used; once the session is no longer open its base is the one captured above.
        const latestBase = sessionPersistenceBase.sessionId === normalizeSessionId(session.id)
            ? sessionPersistenceBase
            : originBase;
        const base = originBase.exists && !latestBase.exists ? { ...latestBase, exists: true } : latestBase;
        const { changes } = computeSessionProgressChanges(base.buckets, withParked.buckets, withParked.observed);
        const saveAttempt = SessionSaveStatus.begin(session.id, saveRevision);
        let writeOutcome;
        try {
            writeOutcome = await IDB.compareAndPut(
                session.id,
                (stored) => reconcileSessionRecordForWrite(stored, session, base, changes),
                buildQuestionBankWrites(pendingBanks),
                pendingBanks.length ? SESSION_SAVE_WITH_BANKS_TIMEOUT_MS : SESSION_RESTORE_SESSION_TIMEOUT_MS
            );
        } catch (error) {
            // Nothing was committed, so the in-memory state and its quick recovery
            // snapshot remain the only copy: keep both and report the failure.
            sessionDirty = true;
            SessionSaveStatus.fail(saveAttempt, session, error, pendingBanks, { base, changes });
            return false;
        }
        settlePendingQuestionBankWrites(pendingBanks);
        SessionSaveStatus.succeed(saveAttempt);
        if (hasPersistableSavedSessionContent(session)) void requestMemoraPersistentStorageOnce();
        rememberSessionFolderMembership(session.id, session.folderId);
        const committedRecord = writeOutcome.record;
        if (normalizeSessionId(currentSessionId) === normalizeSessionId(session.id)) {
            // Membership base is what this tab's memory holds (its own list after this
            // save), not the merged stored list, so another tab's additions are never
            // mistaken for removals made here.
            setSessionPersistenceBase(session.id, committedRecord.revision, writeOutcome.merged, writeOutcome.conflicts, snapshotSessionMembership(session.data));
            // Another writer's changes enter memory only if nothing changed here meanwhile;
            // otherwise the next save merges them again.
            if (sessionStateRevision === saveRevision) {
                adoptStoredSessionProgress(writeOutcome.merged, writeOutcome.conflicts, localProgressBuckets);
            }
            SessionConflictStatus.set(session.id, writeOutcome.conflicts);
        }
        announceSessionWrite(session.id, committedRecord.revision);
        // Retention runs only after this save committed, against the post-save count.
        await enforceSavedSessionRetention(saveSessionId);

        // The session record carries these links and wins on load; this settings
        // mirror is secondary and cannot un-save the record that just committed.
        await persistPdfAssociations();
        // Everything after the write targets the session that was written, which
        // is no longer the open one if the user switched sessions mid-save.
        const savedSessionIsOpen = normalizeSessionId(currentSessionId) === normalizeSessionId(session.id);
        if (savedSessionIsOpen) setLastSessionId(session.id);
        // A newer mutation during the write lives only in memory and the quick
        // snapshot, so both the snapshot and the dirty flag stay until it commits.
        if (sessionStateRevision === saveRevision) {
            clearQuickSessionSnapshot(session.id, { onlyOwn: true });
            if (savedSessionIsOpen && !writeOutcome.conflicts.length) sessionDirty = false;
        }

        if (showNotification) {
            // Update button to show saved state
            const saveBtn = els.saveSessionBtn;
            if (saveBtn) {
                const span = saveBtn.querySelector('span');
                if (span) {
                    span.innerHTML = '&#10003; Saved';
                    span.style.color = '#4ade80';
                    span.style.background = 'none';
                    span.style.webkitBackgroundClip = 'unset';
                    span.style.backgroundClip = 'unset';
                }
                // Reset after 2 seconds
                setTimeout(() => {
                    if (span) {
                        span.innerHTML = '💾 Save Session';
                        span.classList.add('gradient-text');
                        span.style.color = '';
                        span.style.background = '';
                        span.style.webkitBackgroundClip = '';
                        span.style.backgroundClip = '';
                    }
                }, 2000);
            }
        }
        return true;
    },

    load(sessionId, options = {}) {
        // One load runs at a time. A pick made while it runs is queued, latest pick
        // wins, and runs when the current load finishes; every caller's promise
        // settles with the last load. Re-picking the running session drops the queue.
        if (sessionLoadInFlight) {
            sessionLoadQueuedRequest = normalizeSessionId(sessionId) === sessionLoadRunningId
                ? null
                : { sessionId, options };
            return sessionLoadInFlight;
        }
        setSessionHistoryLoadPending(true);
        sessionLoadInFlight = (async () => {
            let request = { sessionId, options };
            let result;
            try {
                while (request) {
                    sessionLoadRunningId = normalizeSessionId(request.sessionId);
                    sessionLoadQueuedRequest = null;
                    try {
                        result = await SessionManager.loadInternal(request.sessionId, request.options);
                    } catch (error) {
                        if (!sessionLoadQueuedRequest) throw error;
                        console.warn('Session load failed; loading the newer pick instead.', error);
                    }
                    request = sessionLoadQueuedRequest;
                }
                return result;
            } finally {
                sessionLoadInFlight = null;
                sessionLoadRunningId = '';
                sessionLoadQueuedRequest = null;
                setSessionHistoryLoadPending(false);
            }
        })();
        return sessionLoadInFlight;
    },

    async loadInternal(sessionId, options = {}) {
        if (IS_EMBED_DEMO) {
            demoUnavailableFeature('Session loading');
            return;
        }
        // 0. SAVE CURRENT SESSION FIRST (if we have one)
        const normalizedLoadSessionId = normalizeSessionId(sessionId);
        const welcomeLoadEpoch = Number(options?.welcomeSessionLoadEpoch) || 0;
        const isStaleWelcomeContinueLoad = () => !!options?.fromWelcomeContinue
            && welcomeLoadEpoch
            && !isCurrentWelcomeSessionLoad(welcomeLoadEpoch);
        if (isStaleWelcomeContinueLoad()) return false;
        const isWelcomeContinueReload = !!options?.fromWelcomeContinue
            && normalizedLoadSessionId
            && normalizeSessionId(currentSessionId) === normalizedLoadSessionId;
        if (currentSessionId && hasPersistableSessionContent() && !isWelcomeContinueReload) {
            console.log("Saving current session before loading new one...");
            if (await this.save(false) === false) return false; // Keep unsaved work open.
            if (isStaleWelcomeContinueLoad()) return false;
        }

        const session = await readSessionStoreValueWithTimeout('sessions', sessionId, SESSION_RESTORE_SESSION_TIMEOUT_MS);
        if (isStaleWelcomeContinueLoad()) return false;
        if (!session) {
            showPopupText('That session could not be found. It may have been deleted in another window.', null, true);
            return false;
        }
        rememberSessionFolderMembership(session.id, session.folderId);
        // This setting is independent of quiz-bank restoration. Start it now so
        // its IndexedDB latency overlaps the required session/question reads.
        const savedPdfLinksPromise = readSessionStoreValueWithTimeout('settings', 'manual_pdf_links', 3000);
        const storedSessionData = session.data;
        const quickSnapshotOutcome = {};
        const mergedQuickSnapshot = mergeQuickSnapshotIntoSession(session, quickSnapshotOutcome);
        if (mergedQuickSnapshot) {
            console.debug('Merged unsaved quick snapshot into session load:', session.id);
        }

        const candidateManifest = {
            jsons: getSessionManifestJsonNames(session),
            pdfs: getSessionManifestPdfNames(session)
        };
        if (!Array.isArray(candidateManifest.jsons)) candidateManifest.jsons = [];
        if (!Array.isArray(candidateManifest.pdfs)) candidateManifest.pdfs = [];
        candidateManifest.jsons = Array.from(new Set(candidateManifest.jsons.map(v => String(v || '').trim()).filter(Boolean)));
        candidateManifest.pdfs = Array.from(new Set(candidateManifest.pdfs.map(v => String(v || '').trim()).filter(Boolean)));

        const candidateQuestionSets = {};
        const candidateActiveSetKeys = new Set();

        const persistedPdfTutorStudyState = session?.data?.pdfTutorStudyState;
        const savedActiveSetKeys = persistedPdfTutorStudyState?.active === true
            && Array.isArray(persistedPdfTutorStudyState.mainActiveSetKeys)
            ? persistedPdfTutorStudyState.mainActiveSetKeys.map(v => String(v || '').trim()).filter(Boolean)
            : (Array.isArray(session.activeSets)
                ? session.activeSets.map(v => String(v || '').trim()).filter(Boolean)
                : []);
        const hasExplicitActiveSetSelection = savedActiveSetKeys.length > 0;
        const normalizeSetKey = (key) => String(key || '')
            .trim()
            .toLowerCase()
            .replace(/\.(json|txt)$/i, '');
        const learnOnlyDeckKey = normalizeSetKey(persistedPdfTutorStudyState?.deckKey || '');
        const savedActiveSetKeyLookup = new Set(savedActiveSetKeys);
        const savedActiveSetKeyNormalizedLookup = new Set(savedActiveSetKeys.map(normalizeSetKey));

        // 2. Load only quiz banks before opening. PDF blobs restore in the background
        // so large or stale stored PDFs cannot hold the Welcome Continue loader.
        const missingQuestionPayloadNames = [];
        const corruptQuestionPayloadNames = [];
        const deferredPdfNames = Array.isArray(candidateManifest.pdfs) ? [...candidateManifest.pdfs] : [];
        // A declared manifest is authoritative; display names are legacy lookup hints.
        const savedManifest = getSessionManifestFromSavedSession(session);
        const questionLookupNames = Array.isArray(savedManifest.jsons)
            ? getSessionManifestJsonNames(session)
            : getSessionQuestionLookupNames(session);
        if (Array.isArray(questionLookupNames) && questionLookupNames.length > 0) {
            // Banks are independent records. Restore them together instead of
            // making multi-source sessions wait for one full IndexedDB lookup per bank.
            const restoredCollections = await readStoredQuestionCollectionsForSessionKeys(questionLookupNames);
            const restoredQuestionSets = questionLookupNames.map((jsonName) => ({
                jsonName,
                ...(restoredCollections.get(jsonName) || { questions: null, usedKey: jsonName, reason: 'missing' })
            }));
            if (isStaleWelcomeContinueLoad()) return false;
            for (const { jsonName, questions: qData, usedKey, reason } of restoredQuestionSets) {
                const resolvedKey = usedKey || jsonName;

                if (Array.isArray(qData)) {
                    if (qData.length === 0) {
                        if (candidateManifest.jsons.includes(jsonName)) missingQuestionPayloadNames.push(jsonName);
                        continue;
                    }
                    if (qData.length > 0 && !candidateManifest.jsons.includes(jsonName)) candidateManifest.jsons.push(jsonName);
                    // Keep both canonical and fallback aliases in memory so legacy sessions resolve cleanly.
                    candidateQuestionSets[jsonName] = qData;
                    if (resolvedKey !== jsonName) {
                        candidateQuestionSets[resolvedKey] = qData;
                    }

                    const aliasKeys = (resolvedKey === jsonName) ? [jsonName] : [jsonName, resolvedKey];
                    const isLearnOnlySet = !!learnOnlyDeckKey && aliasKeys.some((alias) => (
                        normalizeSetKey(alias) === learnOnlyDeckKey
                    ));
                    const shouldBeActive = !isLearnOnlySet && (!hasExplicitActiveSetSelection || aliasKeys.some((alias) => (
                        savedActiveSetKeyLookup.has(alias) ||
                        savedActiveSetKeyNormalizedLookup.has(normalizeSetKey(alias))
                    )));

                    if (shouldBeActive && qData.length > 0) {
                        aliasKeys.forEach(alias => candidateActiveSetKeys.add(alias));
                    }
                } else {
                    if (reason === 'corrupt') corruptQuestionPayloadNames.push(jsonName);
                    else if (candidateManifest.jsons.includes(jsonName)) missingQuestionPayloadNames.push(jsonName);
                }
            }
        }

        if (!Object.values(candidateQuestionSets).some(set => Array.isArray(set) && set.length > 0)) {
            const recoveredQuestions = recoverEmbeddedSessionQuestions(session, formatWelcomeSessionDisplayName(session));
            if (recoveredQuestions.length > 0) {
                const recoveredKey = formatWelcomeSessionDisplayName(session) || normalizeSessionId(session.id) || 'Recovered session';
                if (!candidateManifest.jsons.includes(recoveredKey)) candidateManifest.jsons.push(recoveredKey);
                candidateQuestionSets[recoveredKey] = recoveredQuestions;
                candidateActiveSetKeys.add(recoveredKey);
                console.log(`Recovered ${recoveredQuestions.length} embedded legacy questions for session "${recoveredKey}".`);
            }
        }

        // Safety fallback for legacy sessions with mismatched active-set keys:
        // if no active sets resolved, activate whatever actually loaded.
        if (candidateActiveSetKeys.size === 0 && Object.keys(candidateQuestionSets).length > 0) {
            candidateManifest.jsons.forEach((key) => {
                if (learnOnlyDeckKey && normalizeSetKey(key) === learnOnlyDeckKey) return;
                if (Array.isArray(candidateQuestionSets[key]) && candidateQuestionSets[key].length > 0) candidateActiveSetKeys.add(key);
                const strippedKey = key.replace(/\.json$/i, '').replace(/\.txt$/i, '');
                if (learnOnlyDeckKey && normalizeSetKey(strippedKey) === learnOnlyDeckKey) return;
                if (Array.isArray(candidateQuestionSets[strippedKey]) && candidateQuestionSets[strippedKey].length > 0) candidateActiveSetKeys.add(strippedKey);
            });
        }

        const restoredQuestionPayloads = Object.values(candidateQuestionSets)
            .filter(set => Array.isArray(set) && set.length > 0)
            .flat();
        const restoreReadiness = assessSessionRestoreReadiness({
            questions: restoredQuestionPayloads,
            sessionManifest: candidateManifest,
            session,
            missingQuestionPayloadNames,
            corruptQuestionPayloadNames
        });
        if (!restoreReadiness.ok) {
            warnAndStayOnWelcomeForEmptySession(session, candidateManifest, restoreReadiness);
            return false;
        }

        const linksFromSettings = await savedPdfLinksPromise;
        if (isStaleWelcomeContinueLoad()) return false;
        // Validation has finished. Replace live resources and identity without yielding.
        clearTimeout(saveTimeout);
        saveTimeout = null;
        resetAIResponseUIState();

        pendingAdaptiveRating = null;
        adaptiveAnswerRevealedAt = 0;
        adaptiveClearedAnswersBackup = {};
        adaptiveResumeAnchorIdx = null;
        closeAdaptiveRatingModal();
        closeAdaptiveSnapshotModal();

        // 1. HYGIENIC LOAD: Clear current memory
        revokeLoadedPdfBlobUrls();
        clearAllPdfRuntimeCaches({ revokeBlobUrls: false, endSession: true });
        loadedPdfFiles = {};
        loadedQuestionSets = {};
        activeQuestionSetKeys = new Set();
        condensedQuizState = createDefaultCondensedQuizState();
        highYieldQuizState = normalizeHighYieldQuizState();
        adaptivePracticeState = normalizeAdaptivePracticeState();
        highYieldLastAppliedPool = null;
        questionDuplicateReportsBySetKey = {};
        questionDuplicateReportLookupStateBySetKey = {};
        savedDuplicateEntriesCache = [];
        isSavedDuplicateView = false;
        sessionManifest = candidateManifest;
        loadedQuestionSets = candidateQuestionSets;
        activeQuestionSetKeys = candidateActiveSetKeys;

        const sessionData = (session.data && typeof session.data === 'object') ? session.data : {};
        const savedSessionName = getSavedSessionDisplayNameValue(session);
        currentSessionId = session.id;
        sessionOpenEpoch += 1;
        currentSessionNumber = rememberSessionNumber(session.sessionNumber) || null;
        currentSessionCustomName = savedSessionName || session.customName || '';
        syncWorkingSessionTitle();
        currentSessionExplicit = session.explicit === true;
        setLastSessionId(currentSessionId, { refreshWelcome: false });
        applyLoadedSessionProgressMeta(sessionData, session.timestamp);
        userAnswers = (sessionData.userAnswers && typeof sessionData.userAnswers === 'object')
            ? sessionData.userAnswers
            : {};
        knowledgeState = (sessionData.knowledgeState && typeof sessionData.knowledgeState === 'object')
            ? sessionData.knowledgeState
            : {};
        savedQuestions = (sessionData.savedQuestions && typeof sessionData.savedQuestions === 'object')
            ? sessionData.savedQuestions
            : {};
        srsData = (sessionData.srsData && typeof sessionData.srsData === 'object')
            ? sessionData.srsData
            : {};
        statsData = (sessionData.statsData && typeof sessionData.statsData === 'object')
            ? sessionData.statsData
            : {};
        aiQuestionCacheByIndex = (sessionData.aiQuestionCacheByIndex && typeof sessionData.aiQuestionCacheByIndex === 'object')
            ? sessionData.aiQuestionCacheByIndex
            : {};
        adaptiveReviewSnapshot = (sessionData.adaptiveReviewSnapshot && typeof sessionData.adaptiveReviewSnapshot === 'object')
            ? sessionData.adaptiveReviewSnapshot
            : {};
        adaptiveSnapshotRankFilters = new Set(Array.isArray(sessionData.adaptiveSnapshotRankFilters)
            ? sessionData.adaptiveSnapshotRankFilters.map((v) => Number(v)).filter((v) => Number.isFinite(v) && v >= 1 && v <= 4)
            : []);
        studyDashboardState = createDefaultStudyDashboardState(sessionData.studyDashboard);
        studySetupState = createDefaultStudySetupState(sessionData.studySetup);
        studyPlanState = createDefaultStudyPlanState(sessionData.studyPlan);
        const savedCurrentQIndex = Number(sessionData.currentQIndex);
        currentQIndex = Number.isInteger(savedCurrentQIndex) && savedCurrentQIndex >= 0 ? savedCurrentQIndex : 0;
        quizCompletionCohortState = normalizeQuizCompletionCohortState(sessionData.quizCompletionCohortState);
        if (mergedQuickSnapshot) saveSessionDebounced();
        // Restore PDF links with session-state priority, then persist merged view.
        const sessionPdfLinks = (sessionData.manualPdfLinks && typeof sessionData.manualPdfLinks === 'object')
            ? sessionData.manualPdfLinks
            : {};
        let savedPdfLinks = {};
        if (linksFromSettings && typeof linksFromSettings === 'object') {
            savedPdfLinks = linksFromSettings;
        }
        pdfAssociations = { ...savedPdfLinks, ...sessionPdfLinks };
        void persistPdfAssociations();
        console.debug('Restored pdfAssociations on session load:', Object.keys(pdfAssociations).length, 'links');

        const t = (sessionData.toolState && typeof sessionData.toolState === 'object')
            ? sessionData.toolState
            : {};
        const savedPdfTutorStudyState = persistedPdfTutorStudyState;
        const restoringPdfTutorStudy = savedPdfTutorStudyState?.active === true
            && savedPdfTutorStudyState?.mode === 'flashcards';
        condensedQuizState = normalizeCondensedQuizState(sessionData.condensedQuizState);
        highYieldQuizState = normalizeHighYieldQuizState(sessionData.highYieldQuizState);
        adaptivePracticeState = normalizeAdaptivePracticeState(sessionData.adaptivePracticeState);
        highYieldLastAppliedPool = null;
        isMixAnswers = !!t.mix;
        isHardMode = !!t.hard;
        isShuffled = !!t.shuffle;
        autoSaveWrong = !!t.as;
        autoAdvance = !!t.aa;
        autoEvidence = !!t.ae;
        autoExplanation = false;
        isZenMode = false; // Zen mode was removed; a session saved with it on must not re-enter it.
        isAdaptiveReviewMode = !!t.adaptive;
        // Restored before the queue below is rebuilt, and reset for a session without it.
        adaptiveReviewSeenOnly = isAdaptiveReviewMode && !!t.adaptiveSeenOnly;
        isFlashcardAdaptiveReviewMode = restoringPdfTutorStudy ? false : !!t.flashcardAdaptive;
        if (restoringPdfTutorStudy) isFlashcardMode = false;

        if (isMixAnswers) els.mixAnswersBtn.classList.add('active'); else els.mixAnswersBtn.classList.remove('active');
        if (isHardMode) els.hardModeBtn.classList.add('active'); else els.hardModeBtn.classList.remove('active');
        if (isShuffled) els.shuffleBtn.classList.add('active'); else els.shuffleBtn.classList.remove('active');
        if (els.srsModeBtn) els.srsModeBtn.classList.toggle('active', isAdaptiveReviewMode);

        els.autoSaveBtn.classList.toggle('active', autoSaveWrong);
        syncAutoStudyToolButtons();

        if (isZenMode) document.body.classList.add('zen-active'); else document.body.classList.remove('zen-active');
        syncCondensedQuizToggleButton();

        const sessionTheme = (sessionData.themeState && typeof sessionData.themeState === 'object')
            ? sessionData.themeState
            : null;
        const localTheme = getStoredQuizTheme();
        const sessionThemeTs = Number(sessionTheme && sessionTheme.updatedAt) || 0;
        const localThemeTs = Number(localTheme && localTheme.updatedAt) || 0;
        const themeToApply = (localTheme && localThemeTs >= sessionThemeTs) ? localTheme : (sessionTheme || localTheme);
        if (themeToApply) {
            applyLoadedTheme(themeToApply);
            // If session theme wins over local, sync it immediately so refresh uses the same theme.
            if (themeToApply === sessionTheme && sessionThemeTs > localThemeTs) {
                saveThemeState();
            }
        }

        rebuildActiveQuestions(false);
        const rebuiltRestoreReadiness = assessSessionRestoreReadiness({
            questions: restoredQuestionPayloads,
            session,
            sessionManifest,
            missingQuestionPayloadNames,
            corruptQuestionPayloadNames
        });
        if (!rebuiltRestoreReadiness.ok) {
            warnAndStayOnWelcomeForEmptySession(session, sessionManifest, rebuiltRestoreReadiness);
            return false;
        }
        userAnswers = restoreIndexedStateFromLegacyAndQuestionKey(userAnswers, sessionData.userAnswersByQuestionKey, (rawValue) => {
            const parsed = Number(rawValue);
            return Number.isInteger(parsed) && parsed >= 0 ? parsed : undefined;
        });

        knowledgeState = restoreIndexedStateFromLegacyAndQuestionKey(knowledgeState, sessionData.knowledgeStateByQuestionKey, (rawValue) => {
            const value = normalizeKnowledgeStateValue(rawValue);
            return value || undefined;
        });

        savedQuestions = restoreIndexedStateFromLegacyAndQuestionKey(savedQuestions, sessionData.savedQuestionsByQuestionKey, (rawValue) => {
            const parsed = Number(rawValue);
            return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined;
        });

        srsData = restoreIndexedStateFromLegacyAndQuestionKey(srsData, sessionData.srsDataByQuestionKey, (rawValue) => (
            rawValue && typeof rawValue === 'object' ? rawValue : undefined
        ));
        statsData = restoreIndexedStateFromLegacyAndQuestionKey(statsData, sessionData.statsDataByQuestionKey, (rawValue) => (
            Array.isArray(rawValue) ? rawValue : undefined
        ));
        aiQuestionCacheByIndex = restoreIndexedStateFromLegacyAndQuestionKey(aiQuestionCacheByIndex, sessionData.aiQuestionCacheByQuestionKey, (rawValue) => (
            rawValue && typeof rawValue === 'object' ? rawValue : undefined
        ));
        adaptiveReviewSnapshot = restoreIndexedStateFromLegacyAndQuestionKey(adaptiveReviewSnapshot, sessionData.adaptiveReviewSnapshotByQuestionKey, (rawValue, idx) => {
            if (!rawValue || typeof rawValue !== 'object') return undefined;
            return { ...rawValue, idx };
        });
        setSessionPersistenceBase(
            session.id,
            session.revision,
            readStoredSessionProgressBuckets(storedSessionData),
            quickSnapshotOutcome.conflicts || [],
            snapshotSessionMembership({ manifest: sessionManifest, manualPdfLinks: storedSessionData?.manualPdfLinks })
        );
        // The rebuild above ran against the previous session's maps; nothing it parked belongs here.
        resetOffPoolQuestionProgress();
        flashcardPassStateByDeck = normalizeFlashcardPassStateByDeck(sessionData.flashcardPassState);
        flashcardPassDeckId = '';
        SessionConflictStatus.set(session.id, quickSnapshotOutcome.conflicts || []);

        const filteredFromKeys = buildFilteredIndicesFromQuestionKeyList(sessionData.filteredQuestionKeys);
        if (filteredFromKeys.length > 0) {
            filteredIndices = filteredFromKeys;
            isFilterActive = !!sessionData.isFilterActive;
            if (filteredIndices.length === 0) resetFilter();
        } else if (sessionData.filteredIndices && sessionData.filteredIndices.length > 0) {
            filteredIndices = sessionData.filteredIndices
                .map((idx) => Number(idx))
                .filter((idx) => Number.isInteger(idx) && idx >= 0 && idx < questions.length);
            isFilterActive = !!sessionData.isFilterActive;
            if (filteredIndices.length === 0) resetFilter();
        } else {
            resetFilter();
        }
        if (isShuffled && !isAdaptiveReviewMode && !isFlashcardAdaptiveReviewMode) {
            applySmartShuffle({
                showNotice: false,
                shuffleUnanswered: false,
                preserveCurrentQuestion: true,
                sortAnswered: false
            });
        }
        currentQIndex = Math.min(Math.max(currentQIndex, 0), Math.max(filteredIndices.length - 1, 0));
        if (!isAdaptiveReviewMode && !isFlashcardAdaptiveReviewMode && !isFlashcardMode) queueAnsweredQuestionsBehind();

        if (isAdaptiveReviewMode) {
            const adaptiveQueue = buildAdaptiveReviewQueue();
            if (adaptiveQueue.length > 0) {
                stashAndClearAdaptiveAnswers(adaptiveQueue);
                filteredIndices = adaptiveQueue;
                isFilterActive = true;
                currentQIndex = Math.min(currentQIndex, filteredIndices.length - 1);
            } else {
                isAdaptiveReviewMode = false;
                if (els.srsModeBtn) els.srsModeBtn.classList.remove('active');
            }
        }

        if (isFlashcardAdaptiveReviewMode) {
            const flashcardQueue = buildAdaptiveReviewQueue();
            if (flashcardQueue.length > 0) {
                filteredIndices = flashcardQueue;
                isFilterActive = true;
                currentQIndex = Math.min(currentQIndex, Math.max(filteredIndices.length - 1, 0));
                isFlashcardMode = true;
            } else {
                isFlashcardAdaptiveReviewMode = false;
                if (els.flashcardSrsModeBtn) els.flashcardSrsModeBtn.classList.remove('active');
                if (els.fcSrsModeBtn) els.fcSrsModeBtn.classList.remove('active');
            }
        }

        if (isAdaptiveReviewMode || isFlashcardAdaptiveReviewMode) {
            resetQuizCompletionCohort('adaptive-queue-restore');
        } else if (quizCompletionCohortState.activeQuestionKeys.length === 0) {
            resetQuizCompletionCohort('legacy-session-restore');
        } else {
            const visibleKeys = new Set(getQuizCompletionQuestionKeysForIndices(filteredIndices));
            quizCompletionCohortState = {
                ...quizCompletionCohortState,
                activeQuestionKeys: quizCompletionCohortState.activeQuestionKeys.filter((key) => visibleKeys.has(key)),
                pendingNewQuestionKeys: quizCompletionCohortState.pendingNewQuestionKeys
                    .filter((key) => !visibleKeys.has(key)),
                updatedAt: Date.now()
            };
            if (quizCompletionCohortState.activeQuestionKeys.length === 0) {
                resetQuizCompletionCohort('session-cohort-repair');
            }
        }

        updateSourceDropdown();
        updateImportButtonState();
        if (isStaleWelcomeContinueLoad()) return false;
        prepareSessionLandingSoloMode();
        enterQuizMode({ surface: 'dashboard', skipQuestionRender: !questions.length });
        triggerSessionLoadFade();
        // Published so a caller waiting on one source (Lecture Finder's cross-session Open) awaits the
        // real restore instead of polling for a fixed time.
        window.memoraSessionSourceRestore = {
            sessionId: currentSessionId,
            promise: restoreSessionPdfFilesInBackground(deferredPdfNames)
                .catch((error) => ({ restoredPdfFiles: [], missingPdfFiles: [], error }))
        };
        await restorePdfTutorQuizCheckpointAfterSessionLoad(sessionData);
        if (isStaleWelcomeContinueLoad()) return false;
        startTime = Date.now();
        return true;
    },
    showHistory(options = {}) {
        if (sessionHistoryLoadInFlight) return sessionHistoryLoadInFlight;
        setSessionHistoryLoadPending(true);
        sessionHistoryLoadInFlight = (async () => {
            try {
                return await SessionManager.showHistoryInternal(options);
            } finally {
                sessionHistoryLoadInFlight = null;
                setSessionHistoryLoadPending(false);
            }
        })();
        return sessionHistoryLoadInFlight;
    },

    async showHistoryInternal(options = {}) {
        if (IS_EMBED_DEMO) {
            demoUnavailableFeature('Session history');
            return;
        }
        const isRefresh = !!els.historyModal?.classList.contains('active');
        const keepScroll = isRefresh && options.preserveScroll !== false;
        const previousScroll = (keepScroll && els.historyList) ? els.historyList.scrollTop : 0;
        await loadSessionHistoryExpansion();
        const sessions = await readSessionStoreAllWithTimeout('sessions', 3000);
        const folders = await getSessionFolders(true);
        renderSessionHistoryTree(sessions, folders, { scrollTop: previousScroll });
        els.historyModal.classList.add('active');
        if (els.historyDeleteAllBtn) {
            const hasRows = !!els.historyList?.querySelector('.hist-item');
            els.historyDeleteAllBtn.disabled = !hasRows;
            els.historyDeleteAllBtn.setAttribute('aria-disabled', hasRows ? 'false' : 'true');
            els.historyDeleteAllBtn.title = hasRows ? 'Delete all saved sessions' : 'No saved sessions to delete';
        }
    },

    async rename(sessionId, newName) {
        const normalizedName = normalizeSessionNameInput(newName);
        // Throws when the write fails, before anything on screen changes.
        const session = await renameStoredSessionRecord(sessionId, normalizedName);
        const isCurrentSession = normalizeSessionId(sessionId) === normalizeSessionId(currentSessionId);
        if (isCurrentSession) {
            // A current session with no stored record yet (or one another tab deleted)
            // keeps its new name in memory and is saved under it.
            currentSessionCustomName = normalizedName;
            syncWorkingSessionTitle();
            renderStudyDashboard();
        }
        return !!(session || isCurrentSession);
    }
};

/* =====================================================================
   Session History Folders
   Folder registry and history-tree expansion state live in the generic IDB
   `settings` store (no schema bump). Membership rides on the session record
   itself (`folderId`) so it survives soft-delete and restore through Recently
   Deleted.
   ===================================================================== */

const SESSION_FOLDERS_STORE_KEY = 'sessionFolders';
const SESSION_HISTORY_FOLDER_EXPANSION_STORE_KEY = 'memora_session_history_folder_expansion_v1';
const SESSION_HISTORY_UNGROUPED_FOLDER_ID = '__ungrouped__';
const SESSION_HISTORY_SORT_KEY = 'memoraSessionHistorySort';
const SESSION_HISTORY_SORT_MODES = Object.freeze(['recent', 'name', 'answered']);
const SESSION_FOLDER_DEFAULT_COLOR = '#60a5fa';
const SESSION_FOLDER_PALETTE = Object.freeze([
    { hex: '#60a5fa', label: 'Blue' },
    { hex: '#a78bfa', label: 'Violet' },
    { hex: '#f472b6', label: 'Pink' },
    { hex: '#fbbf24', label: 'Amber' },
    { hex: '#34d399', label: 'Emerald' },
    { hex: '#22d3ee', label: 'Cyan' }
]);

let sessionFoldersCache = null;
const sessionHistoryExpandedFolders = new Set();
let sessionHistoryExpansionLoaded = false;
let sessionHistoryExpansionSaveChain = Promise.resolve();

function getSessionHistoryExpansionKey(folderId) {
    return String(folderId || SESSION_HISTORY_UNGROUPED_FOLDER_ID);
}

function isSessionFolderExpanded(folderId) {
    return sessionHistoryExpandedFolders.has(getSessionHistoryExpansionKey(folderId));
}

async function loadSessionHistoryExpansion(forceReload = false) {
    if (sessionHistoryExpansionLoaded && !forceReload) return;
    sessionHistoryExpansionLoaded = true;
    sessionHistoryExpandedFolders.clear();
    try {
        const stored = await IDB.get('settings', SESSION_HISTORY_FOLDER_EXPANSION_STORE_KEY);
        const expandedFolderIds = Array.isArray(stored)
            ? stored
            : (Array.isArray(stored?.expandedFolderIds) ? stored.expandedFolderIds : []);
        expandedFolderIds.forEach((folderId) => {
            const key = String(folderId || '').trim();
            if (key) sessionHistoryExpandedFolders.add(key);
        });
    } catch (e) {
        console.warn('Could not read session history folder expansion:', e);
    }
}

function persistSessionHistoryExpansion() {
    const payload = {
        version: 1,
        expandedFolderIds: Array.from(sessionHistoryExpandedFolders)
    };
    sessionHistoryExpansionSaveChain = sessionHistoryExpansionSaveChain
        .catch(() => {})
        .then(() => IDB.put('settings', SESSION_HISTORY_FOLDER_EXPANSION_STORE_KEY, payload))
        .catch((e) => console.warn('Could not save session history folder expansion:', e));
    return sessionHistoryExpansionSaveChain;
}

/* Stroke-only 24x24 shapes matching the dock icon idiom. */
const SESSION_FOLDER_ICON_SHAPES = Object.freeze({
    folder: '<path d="M3.6 6.4h5.6l2 2.1h9.2v8.8a2.3 2.3 0 0 1-2.3 2.3H5.9a2.3 2.3 0 0 1-2.3-2.3V6.4z"></path>',
    folderOpen: '<path d="M3.6 6.4h5.6l2 2.1h9.2v2.3H3.6V6.4z"></path><path d="M3.6 10.8h16.8l-1.6 6.5a2.3 2.3 0 0 1-2.2 1.8H5.9a2.3 2.3 0 0 1-2.3-2.3v-6z"></path>',
    folderPlus: '<path d="M3.6 6.4h5.6l2 2.1h9.2v8.8a2.3 2.3 0 0 1-2.3 2.3H5.9a2.3 2.3 0 0 1-2.3-2.3V6.4z"></path><path d="M12 11.7v4.8"></path><path d="M9.6 14.1h4.8"></path>',
    chevron: '<path d="M9.6 5.9 15.8 12l-6.2 6.1"></path>',
    inbox: '<path d="M3.4 13.3h4.3l1.4 2.4h5.8l1.4-2.4h4.3"></path><path d="M6.2 4.9h11.6l2.8 8.4v3.4a2.3 2.3 0 0 1-2.3 2.3H5.7a2.3 2.3 0 0 1-2.3-2.3v-3.4l2.8-8.4z"></path>',
    palette: '<path d="M12 3.4a8.6 8.6 0 0 0 0 17.2c1.3 0 2-.8 2-1.8 0-.5-.2-.9-.5-1.2-.3-.4-.5-.8-.5-1.2 0-1 .8-1.8 1.8-1.8h1.4a4.4 4.4 0 0 0 4.4-4.4c0-3.8-3.9-6.8-8.6-6.8z"></path><path d="M7.4 11.2h.01"></path><path d="M10.1 7.9h.01"></path><path d="M14.4 7.9h.01"></path>',
    pencil: '<path d="M15.1 4.7 19.3 8.9"></path><path d="M17.2 2.6a1.9 1.9 0 0 1 2.7 2.7L8.5 16.7l-3.6.9.9-3.6L17.2 2.6z"></path>',
    trash: '<path d="M4.6 6.7h14.8"></path><path d="M9.5 6.7V5a1.4 1.4 0 0 1 1.4-1.4h2.2A1.4 1.4 0 0 1 14.5 5v1.7"></path><path d="M6.7 6.7l.8 11.9a1.9 1.9 0 0 0 1.9 1.8h5.2a1.9 1.9 0 0 0 1.9-1.8l.8-11.9"></path>',
    move: '<path d="M3.6 6.4h5.6l2 2.1h9.2v8.8a2.3 2.3 0 0 1-2.3 2.3H5.9a2.3 2.3 0 0 1-2.3-2.3V6.4z"></path><path d="M12 16.6V10"></path><path d="M9.4 12.6 12 10l2.6 2.6"></path>',
    sort: '<path d="M6.6 5.2v13.6"></path><path d="M3.7 15.9 6.6 18.8 9.5 15.9"></path><path d="M12.7 7.3h7.7"></path><path d="M12.7 12h5.6"></path><path d="M12.7 16.7h3.5"></path>',
    check: '<path d="M5.2 12.5 9.7 17 18.8 6.9"></path>',
    close: '<path d="M6.3 6.3 17.7 17.7"></path><path d="M17.7 6.3 6.3 17.7"></path>'
});

function sessionFolderIcon(shapeName, options = {}) {
    const shape = SESSION_FOLDER_ICON_SHAPES[shapeName] || '';
    const extraClass = options.className ? ` ${options.className}` : '';
    const stroke = options.stroke || 'url(#hist-icon-grad)';
    const strokeWidth = options.strokeWidth || 2.1;
    return `<svg class="hist-svg${extraClass}" viewBox="0 0 24 24" aria-hidden="true" focusable="false">`
        + `<g fill="none" stroke="${stroke}" stroke-width="${strokeWidth}" stroke-linecap="round" stroke-linejoin="round">${shape}</g>`
        + '</svg>';
}

function makeSessionFolderId() {
    return 'fld_' + Date.now().toString(36) + Math.random().toString(36).slice(2, 7);
}

function normalizeSessionFolderColor(value) {
    const hex = String(value || '').trim().toLowerCase();
    const match = SESSION_FOLDER_PALETTE.find((entry) => entry.hex.toLowerCase() === hex);
    return match ? match.hex : SESSION_FOLDER_DEFAULT_COLOR;
}

function normalizeSessionFolderRecord(raw, index) {
    if (!raw || typeof raw !== 'object') return null;
    const id = String(raw.id || '').trim();
    if (!id) return null;
    const name = String(raw.name || '').trim() || 'Untitled Folder';
    return {
        id,
        name: name.slice(0, 60),
        color: normalizeSessionFolderColor(raw.color),
        order: Number.isFinite(Number(raw.order)) ? Number(raw.order) : index,
        createdAt: Number.isFinite(Number(raw.createdAt)) ? Number(raw.createdAt) : Date.now()
    };
}

async function getSessionFolders(forceReload = false) {
    if (!forceReload && Array.isArray(sessionFoldersCache)) return sessionFoldersCache;
    let stored = null;
    try {
        stored = await IDB.get('settings', SESSION_FOLDERS_STORE_KEY);
    } catch (e) {
        console.warn('Could not read session folders:', e);
    }
    const list = Array.isArray(stored) ? stored : [];
    sessionFoldersCache = list
        .map(normalizeSessionFolderRecord)
        .filter(Boolean)
        .sort((a, b) => a.order - b.order || a.createdAt - b.createdAt);
    return sessionFoldersCache;
}

async function saveSessionFolders(change) {
    const outcome = await IDB.compareAndPutSetting(SESSION_FOLDERS_STORE_KEY, (stored) => {
        const current = (Array.isArray(stored) ? stored : []).map(normalizeSessionFolderRecord).filter(Boolean);
        const next = typeof change === 'function' ? change(current) : change;
        const normalized = (Array.isArray(next) ? next : current).map(normalizeSessionFolderRecord)
            .filter(Boolean).map((folder, index) => ({ ...folder, order: index }));
        return { value: normalized };
    });
    sessionFoldersCache = outcome.value;
    window.dispatchEvent(new CustomEvent('memora:session-folders-change'));
    announceSessionWrite('', 0);
    return outcome.value;
}

// History-surface wrapper: reports a failed folder write and resolves false so the
// caller keeps its form open instead of running success handling.
async function runSessionFolderChange(change) {
    try {
        return await change();
    } catch (error) {
        if (isDuplicateNameError(error)) {
            showPopupText(error.message, null, true);
            return false;
        }
        console.error('Could not save session folders:', error);
        showPopup('Could not save folder changes. Please try again.', null, true);
        return false;
    }
}

async function createSessionFolder(name, color) {
    const folder = normalizeSessionFolderRecord({ id: makeSessionFolderId(), name, color, createdAt: Date.now() });
    if (!folder) return null;
    await saveSessionFolders((folders) => {
        const clash = findSessionFolderNameConflict(folders, name);
        if (clash) throw makeDuplicateNameError('folder', clash);
        return [...folders, { ...folder, order: folders.length }];
    });
    setSessionFolderExpanded(folder.id, true);
    return folder;
}

async function updateSessionFolder(folderId, patch) {
    let changed = false;
    await saveSessionFolders((folders) => {
        if (patch && Object.hasOwn(patch, 'name')) {
            const clash = findSessionFolderNameConflict(folders, patch.name, folderId);
            if (clash) throw makeDuplicateNameError('folder', clash);
        }
        return folders.map((folder) => {
            if (folder.id !== folderId) return folder;
            changed = true;
            return { ...folder, ...patch };
        });
    });
    return changed;
}

function setSessionFolderExpanded(folderId, expanded) {
    const key = getSessionHistoryExpansionKey(folderId);
    if (expanded) sessionHistoryExpandedFolders.add(key);
    else sessionHistoryExpandedFolders.delete(key);
    void persistSessionHistoryExpansion();
}

// Changes only folderId on the stored session, in one read-modify-write transaction, so
// answers and notes another tab or an autosave wrote since this tab last read are kept.
// `onlyIfFolderId` skips sessions that were moved elsewhere in the meantime (the write is
// refused with a FolderMismatchError and nothing changes). Resolves to the stored record.
async function writeSessionFolderId(sessionId, folderId, onlyIfFolderId = undefined) {
    const outcome = await IDB.compareAndPut(sessionId, (stored) => {
        if (!stored) return { error: Object.assign(new Error('Session not found.'), { name: 'NotFoundError' }) };
        if (onlyIfFolderId !== undefined && (stored.folderId || null) !== onlyIfFolderId) {
            return { error: Object.assign(new Error('Session moved to another folder.'), { name: 'FolderMismatchError' }) };
        }
        return { record: { ...stored, folderId, revision: (Number(stored.revision) || 0) + 1 } };
    });
    announceSessionWrite(sessionId, outcome.record.revision);
    return outcome.record;
}

async function deleteSessionFolder(folderId) {
    const folders = await getSessionFolders();
    if (!folders.some((folder) => folder.id === folderId)) return false;
    const sessions = await readSessionStoreAllWithTimeout('sessions', 3000);
    let unfileFailure = null;
    for (const session of sessions) {
        if (!session || session.folderId !== folderId) continue;
        const cacheKey = String(session.id ?? '').trim();
        const hadCached = sessionFolderMembershipCache.has(cacheKey);
        const priorCached = sessionFolderMembershipCache.get(cacheKey);
        rememberSessionFolderMembership(session.id, null);
        try {
            await writeSessionFolderId(session.id, null, folderId);
        } catch (e) {
            if (hadCached) sessionFolderMembershipCache.set(cacheKey, priorCached);
            else sessionFolderMembershipCache.delete(cacheKey);
            // Moved elsewhere or deleted by another tab: nothing left to unfile.
            if (e?.name === 'NotFoundError' || e?.name === 'FolderMismatchError') continue;
            console.warn('Could not unfile session during folder delete:', e);
            unfileFailure = unfileFailure || e;
        }
    }
    // A folder that still holds sessions must not disappear as if it had been emptied.
    if (unfileFailure) throw unfileFailure;
    await saveSessionFolders((current) => current.filter((folder) => folder.id !== folderId));
    setSessionFolderExpanded(folderId, false);
    return true;
}

async function assignSessionToFolder(sessionId, folderId) {
    const cacheKey = String(sessionId ?? '').trim();
    const hadCached = sessionFolderMembershipCache.has(cacheKey);
    const priorCached = sessionFolderMembershipCache.get(cacheKey);
    const targetFolderId = rememberSessionFolderMembership(sessionId, folderId);
    try {
        await writeSessionFolderId(sessionId, targetFolderId);
    } catch (e) {
        if (hadCached) sessionFolderMembershipCache.set(cacheKey, priorCached);
        else sessionFolderMembershipCache.delete(cacheKey);
        if (e?.name === 'NotFoundError') return false;
        console.error('Could not move session:', e);
        showPopup('Could not move that session.', null, true);
        return false;
    }
    return true;
}

function getSessionHistorySortMode() {
    try {
        const stored = localStorage.getItem(SESSION_HISTORY_SORT_KEY);
        if (SESSION_HISTORY_SORT_MODES.includes(stored)) return stored;
    } catch (e) { /* ignore */ }
    return 'recent';
}

function setSessionHistorySortMode(mode) {
    const next = SESSION_HISTORY_SORT_MODES.includes(mode) ? mode : 'recent';
    try { localStorage.setItem(SESSION_HISTORY_SORT_KEY, next); } catch (e) { /* ignore */ }
    return next;
}

function getSessionAnsweredCount(session) {
    return session?.data?.userAnswers ? Object.keys(session.data.userAnswers).length : 0;
}

function describeSessionFiles(session) {
    const jsons = (session?.data?.manifest && session.data.manifest.jsons) || [];
    const pdfs = (session?.data?.manifest && session.data.manifest.pdfs) || [];
    const parts = [];
    if (pdfs.length > 0) parts.push(`${pdfs.length} PDF${pdfs.length > 1 ? 's' : ''}`);
    if (jsons.length > 0) parts.push(`${jsons.length} Quiz${jsons.length > 1 ? 'zes' : ''}`);
    return parts.length > 0 ? parts.join(', ') : 'No files';
}

function sortSessionsForHistory(sessions, mode) {
    const list = [...sessions];
    if (mode === 'name') {
        list.sort((a, b) => formatWelcomeSessionDisplayName(a)
            .localeCompare(formatWelcomeSessionDisplayName(b), undefined, { sensitivity: 'base' }));
    } else if (mode === 'answered') {
        list.sort((a, b) => getSessionAnsweredCount(b) - getSessionAnsweredCount(a)
            || new Date(b.timestamp) - new Date(a.timestamp));
    } else {
        list.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
    }
    return list;
}

function isPhoneDeviceLayout() {
    return document.documentElement.classList.contains('phone-device');
}

function buildSessionHistoryItem(session, folders, groupSessionIds) {
    const date = new Date(session.timestamp);
    const fmtDate = date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    const answered = getSessionAnsweredCount(session);
    const displayName = formatWelcomeSessionDisplayName(session);
    const currentFolderId = session.folderId || null;

    const item = document.createElement('div');
    item.className = 'hist-item';
    item.dataset.sessionId = String(session.id);
    item.innerHTML = `
        <div class="hist-item-row">
            <div class="hist-meta">
                <span class="hist-date">${escapeHtml(fmtDate)}</span>
                <span class="hist-name">${escapeHtml(displayName)}</span>
                <span class="hist-detail">${escapeHtml(describeSessionFiles(session))} &bull; ${answered} Answered</span>
            </div>
            <div class="hist-item-actions">
                <button class="hist-icon-btn hist-move" type="button" title="Move to folder" aria-label="Move to folder">${sessionFolderIcon('move')}</button>
                <button class="hist-icon-btn hist-rename" type="button" title="Rename session" aria-label="Rename session">${sessionFolderIcon('pencil')}</button>
                <button class="hist-icon-btn hist-del" type="button" title="Delete session" aria-label="Delete session">${sessionFolderIcon('trash', { stroke: '#f87171' })}</button>
            </div>
        </div>
        <div class="hist-drawer"><div class="hist-drawer-inner"></div></div>`;

    const metaDiv = item.querySelector('.hist-meta');
    const drawer = item.querySelector('.hist-drawer');
    const drawerInner = item.querySelector('.hist-drawer-inner');

    const closeDrawer = () => {
        item.classList.remove('drawer-open');
        drawer.dataset.mode = '';
        drawerInner.innerHTML = '';
        // A draggable ancestor swallows text selection, so drag is only armed
        // while the inline editors are closed.
        if (!isPhoneDeviceLayout()) item.draggable = true;
    };

    metaDiv.onclick = () => {
        metaDiv.style.pointerEvents = 'none';
        void (async () => {
            try {
                const loaded = await SessionManager.load(session.id);
                if (loaded) {
                    closeHistory();
                } else {
                    metaDiv.style.pointerEvents = '';
                }
            } catch (error) {
                console.error('Failed to load session from history:', error);
                showPopup('Could not reopen that saved session. The session list is still available.', null, true);
                metaDiv.style.pointerEvents = '';
            }
        })();
    };

    /* --- Move: inline drawer instead of a popover so nothing is clipped --- */
    item.querySelector('.hist-move').onclick = (e) => {
        e.stopPropagation();
        if (item.classList.contains('drawer-open') && drawer.dataset.mode === 'move') {
            closeDrawer();
            return;
        }
        drawer.dataset.mode = 'move';
        const chips = [{ id: null, name: 'Ungrouped', color: '#94a3b8' }, ...folders].map((folder) => {
            const active = (folder.id || null) === currentFolderId;
            return `<button class="hist-chip${active ? ' active' : ''}" type="button" data-folder-id="${escapeHtml(String(folder.id || ''))}" style="--chip-accent:${escapeHtml(folder.color)}">`
                + sessionFolderIcon(folder.id ? 'folder' : 'inbox', { stroke: folder.color, strokeWidth: 2 })
                + `<span>${escapeHtml(folder.name)}</span>`
                + (active ? sessionFolderIcon('check', { className: 'hist-chip-check', stroke: folder.color, strokeWidth: 2.6 }) : '')
                + '</button>';
        }).join('');
        drawerInner.innerHTML = `<div class="hist-drawer-label">Move to folder</div><div class="hist-chip-row">${chips}</div>`;
        item.classList.add('drawer-open');
        item.draggable = false;
        drawerInner.querySelectorAll('.hist-chip').forEach((chip) => {
            chip.onclick = async (ce) => {
                ce.stopPropagation();
                const targetId = chip.dataset.folderId || null;
                if (targetId === currentFolderId) { closeDrawer(); return; }
                const moved = await assignSessionToFolder(session.id, targetId);
                if (!moved) return;
                if (targetId) setSessionFolderExpanded(targetId, true);
                await SessionManager.showHistory();
            };
        });
    };

    /* --- Rename: in-place, keeps the row visible --- */
    item.querySelector('.hist-rename').onclick = (e) => {
        e.stopPropagation();
        if (item.classList.contains('drawer-open') && drawer.dataset.mode === 'rename') {
            closeDrawer();
            return;
        }
        drawer.dataset.mode = 'rename';
        drawerInner.innerHTML = `
            <div class="hist-drawer-label">Rename session</div>
            <div class="hist-rename-edit">
                <input type="text" class="hist-rename-input" value="${escapeHtml(session.customName || '')}" placeholder="Enter session name..." maxlength="80" />
                <div class="hist-rename-actions">
                    <button class="hist-rename-save" type="button">Save</button>
                    <button class="hist-rename-cancel" type="button">Cancel</button>
                </div>
            </div>`;
        item.classList.add('drawer-open');
        item.draggable = false;
        const input = drawerInner.querySelector('.hist-rename-input');
        input.focus();
        input.select();
        requestAnimationFrame(() => {
            if (!item.isConnected || !item.classList.contains('drawer-open')) return;
            item.scrollIntoView({ block: 'nearest', inline: 'nearest' });
        });

        const saveRename = async () => {
            const newName = input.value.trim() || null;
            let renamed = null;
            try {
                renamed = await renameStoredSessionRecord(session.id, newName);
            } catch (error) {
                if (isDuplicateNameError(error)) {
                    showPopupText(error.message, null, true);
                    input.focus();
                    return;
                }
            }
            if (!renamed) {
                showPopup('Rename failed. Please try again.', null, true);
                return;
            }
            session.customName = newName;
            if (normalizeSessionId(session.id) === normalizeSessionId(currentSessionId)) {
                currentSessionCustomName = newName || '';
                syncWorkingSessionTitle();
                renderStudyDashboard();
            }
            await SessionManager.showHistory();
        };

        drawerInner.querySelector('.hist-rename-save').onclick = (se) => { se.stopPropagation(); void saveRename(); };
        drawerInner.querySelector('.hist-rename-cancel').onclick = (se) => { se.stopPropagation(); closeDrawer(); };
        input.onclick = (se) => se.stopPropagation();
        input.onkeydown = (ke) => {
            if (ke.key === 'Enter') { ke.preventDefault(); void saveRename(); }
            if (ke.key === 'Escape') { ke.preventDefault(); closeDrawer(); }
        };
    };

    item.querySelector('.hist-del').onclick = async (e) => {
        e.stopPropagation();
        const confirmed = await showThemeConfirm('Delete this session? It will be moved to Recently Deleted.', {
            title: 'Delete Session',
            okText: 'Delete',
            cancelText: 'Cancel',
            danger: true
        });
        if (!confirmed) return;

        const wasCurrentSession = normalizeSessionId(session.id) === normalizeSessionId(currentSessionId);
        const wasLastSession = normalizeSessionId(session.id) === normalizeSessionId(localStorage.getItem('lastSessionId'));
        const deleted = await softDeleteSession(session.id, session);
        if (!deleted) {
            showPopup('Delete failed. Please try again.', null, true);
            return;
        }
        await syncAfterSavedSessionDelete(session.id, { wasCurrentSession, wasLastSession });
        await SessionManager.showHistory();
        showPopup('Session moved to Recently Deleted', null, false);
    };

    /* --- Drag to a folder header (pointer devices only) --- */
    if (!isPhoneDeviceLayout()) {
        item.draggable = true;
        item.addEventListener('dragstart', (ev) => {
            item.classList.add('hist-dragging');
            els.historyList?.classList.add('hist-drag-active');
            try {
                ev.dataTransfer.effectAllowed = 'move';
                ev.dataTransfer.setData('text/plain', String(session.id));
            } catch (err) { /* ignore */ }
        });
        item.addEventListener('dragend', () => {
            item.classList.remove('hist-dragging');
            els.historyList?.classList.remove('hist-drag-active');
            els.historyList?.querySelectorAll('.hist-drop-active')
                .forEach((node) => node.classList.remove('hist-drop-active'));
        });
    }

    if (Array.isArray(groupSessionIds)) groupSessionIds.push(String(session.id));
    return item;
}

function attachSessionFolderDropTarget(node, folderId) {
    if (isPhoneDeviceLayout()) return;
    node.addEventListener('dragover', (ev) => {
        ev.preventDefault();
        try { ev.dataTransfer.dropEffect = 'move'; } catch (e) { /* ignore */ }
        node.classList.add('hist-drop-active');
    });
    node.addEventListener('dragleave', (ev) => {
        // Ignore leaves that only cross into a child of the drop target.
        if (ev.relatedTarget && node.contains(ev.relatedTarget)) return;
        node.classList.remove('hist-drop-active');
    });
    node.addEventListener('drop', async (ev) => {
        ev.preventDefault();
        node.classList.remove('hist-drop-active');
        let sessionId = '';
        try { sessionId = ev.dataTransfer.getData('text/plain'); } catch (e) { /* ignore */ }
        if (!sessionId) return;
        const moved = await assignSessionToFolder(sessionId, folderId);
        if (!moved) return;
        if (folderId) setSessionFolderExpanded(folderId, true);
        await SessionManager.showHistory();
    });
}

function buildSessionFolderGroup(config) {
    const { id, name, color, sessions, folders, collapsible, expanded, isUngrouped } = config;
    const group = document.createElement('div');
    group.className = `hist-folder${expanded ? ' open' : ''}${isUngrouped ? ' hist-folder-ungrouped' : ''}`;
    group.style.setProperty('--folder-accent', color);
    if (id) group.dataset.folderId = id;

    const latest = sessions.reduce((acc, s) => Math.max(acc, new Date(s.timestamp).getTime() || 0), 0);
    const latestLabel = latest ? new Date(latest).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : '';
    const countLabel = `${sessions.length} session${sessions.length === 1 ? '' : 's'}`;
    const summary = latestLabel ? `${countLabel} &bull; ${escapeHtml(latestLabel)}` : countLabel;

    group.innerHTML = `
        <div class="hist-folder-head" role="button" tabindex="0" aria-expanded="${expanded ? 'true' : 'false'}">
            <span class="hist-folder-chev">${sessionFolderIcon('chevron', { stroke: color, strokeWidth: 2.4 })}</span>
            <span class="hist-folder-icon">${sessionFolderIcon(isUngrouped ? 'inbox' : 'folder', { stroke: color })}</span>
            <span class="hist-folder-titles">
                <span class="hist-folder-name">${escapeHtml(name)}</span>
                <span class="hist-folder-count">${summary}</span>
            </span>
            ${collapsible && id ? `<span class="hist-folder-actions">
                <button class="hist-icon-btn hist-folder-act" type="button" data-act="color" title="Folder color" aria-label="Folder color">${sessionFolderIcon('palette', { stroke: color })}</button>
                <button class="hist-icon-btn hist-folder-act" type="button" data-act="rename" title="Rename folder" aria-label="Rename folder">${sessionFolderIcon('pencil')}</button>
                <button class="hist-icon-btn hist-folder-act" type="button" data-act="delete" title="Delete folder" aria-label="Delete folder">${sessionFolderIcon('trash', { stroke: '#f87171' })}</button>
            </span>` : ''}
        </div>
        <div class="hist-folder-edit"><div class="hist-folder-edit-inner"></div></div>
        <div class="hist-folder-body"><div class="hist-folder-body-inner"></div></div>`;

    const head = group.querySelector('.hist-folder-head');
    const bodyInner = group.querySelector('.hist-folder-body-inner');
    const editWrap = group.querySelector('.hist-folder-edit');
    const editInner = group.querySelector('.hist-folder-edit-inner');

    if (sessions.length === 0) {
        bodyInner.innerHTML = `<div class="hist-folder-empty">${isUngrouped ? 'Every session is filed away.' : 'Drop a session here or use the move icon.'}</div>`;
    } else {
        sessions.forEach((session, index) => {
            const item = buildSessionHistoryItem(session, folders, null);
            item.style.setProperty('--stagger', String(Math.min(index, 8)));
            bodyInner.appendChild(item);
        });
    }

    const toggle = () => {
        const nowOpen = !group.classList.contains('open');
        group.classList.toggle('open', nowOpen);
        head.setAttribute('aria-expanded', nowOpen ? 'true' : 'false');
        setSessionFolderExpanded(id, nowOpen);
    };

    head.addEventListener('click', (ev) => {
        if (ev.target.closest('.hist-folder-act')) return;
        toggle();
    });
    head.addEventListener('keydown', (ev) => {
        if (ev.key !== 'Enter' && ev.key !== ' ') return;
        ev.preventDefault();
        toggle();
    });

    // The whole folder is the drop zone, not just its title row - dropping onto the
    // open list of sessions is the obvious gesture and used to do nothing.
    attachSessionFolderDropTarget(group, id || null);

    const closeEdit = () => {
        group.classList.remove('edit-open');
        editInner.innerHTML = '';
    };

    group.querySelectorAll('.hist-folder-act').forEach((btn) => {
        btn.onclick = async (ev) => {
            ev.stopPropagation();
            const act = btn.dataset.act;

            if (act === 'delete') {
                const confirmed = await showThemeConfirm(
                    `Delete the folder "${name}"? Its ${sessions.length} session${sessions.length === 1 ? '' : 's'} will move to Ungrouped, not to Recently Deleted.`,
                    { title: 'Delete Folder', okText: 'Delete Folder', cancelText: 'Cancel', danger: true }
                );
                if (!confirmed) return;
                if (!await runSessionFolderChange(() => deleteSessionFolder(id))) return;
                await SessionManager.showHistory();
                return;
            }

            if (group.classList.contains('edit-open') && editWrap.dataset.mode === act) {
                closeEdit();
                return;
            }
            editWrap.dataset.mode = act;

            if (act === 'rename') {
                editInner.innerHTML = `
                    <div class="hist-rename-edit">
                        <input type="text" class="hist-rename-input" value="${escapeHtml(name)}" placeholder="Folder name..." maxlength="60" />
                        <div class="hist-rename-actions">
                            <button class="hist-rename-save" type="button">Save</button>
                            <button class="hist-rename-cancel" type="button">Cancel</button>
                        </div>
                    </div>`;
                group.classList.add('edit-open');
                const input = editInner.querySelector('.hist-rename-input');
                input.focus();
                input.select();
                requestAnimationFrame(() => {
                    if (!group.isConnected || !group.classList.contains('edit-open')) return;
                    group.scrollIntoView({ block: 'nearest', inline: 'nearest' });
                });
                const save = async () => {
                    const nextName = input.value.trim();
                    if (!nextName) { showPopup('Folder name cannot be empty.', null, true); return; }
                    if (!await runSessionFolderChange(() => updateSessionFolder(id, { name: nextName.slice(0, 60) }))) return;
                    await SessionManager.showHistory();
                };
                editInner.querySelector('.hist-rename-save').onclick = (se) => { se.stopPropagation(); void save(); };
                editInner.querySelector('.hist-rename-cancel').onclick = (se) => { se.stopPropagation(); closeEdit(); };
                input.onclick = (se) => se.stopPropagation();
                input.onkeydown = (ke) => {
                    if (ke.key === 'Enter') { ke.preventDefault(); void save(); }
                    if (ke.key === 'Escape') { ke.preventDefault(); closeEdit(); }
                };
                return;
            }

            if (act === 'color') {
                const swatches = SESSION_FOLDER_PALETTE.map((entry) => {
                    const active = entry.hex.toLowerCase() === String(color).toLowerCase();
                    return `<button class="hist-swatch${active ? ' active' : ''}" type="button" data-color="${entry.hex}" style="--swatch:${entry.hex}" title="${entry.label}" aria-label="${entry.label}"></button>`;
                }).join('');
                editInner.innerHTML = `<div class="hist-drawer-label">Folder color</div><div class="hist-swatch-row">${swatches}</div>`;
                group.classList.add('edit-open');
                editInner.querySelectorAll('.hist-swatch').forEach((swatch) => {
                    swatch.onclick = async (se) => {
                        se.stopPropagation();
                        if (!await runSessionFolderChange(() => updateSessionFolder(id, { color: normalizeSessionFolderColor(swatch.dataset.color) }))) return;
                        await SessionManager.showHistory();
                    };
                });
            }
        };
    });

    return group;
}

function closeSessionHistorySortMenu() {
    const wrap = els.historyList?.querySelector('#hist-sort');
    if (!wrap || !wrap.classList.contains('open')) return;
    wrap.classList.remove('open');
    wrap.querySelector('#hist-sort-btn')?.setAttribute('aria-expanded', 'false');
}

/* Bound once against the list element; the toolbar is rebuilt on every
   refresh, so the handler resolves the current menu at call time. */
function bindSessionHistorySortDismiss() {
    const list = els.historyList;
    if (!list || list.dataset.sortDismissBound === '1') return;
    list.dataset.sortDismissBound = '1';
    list.addEventListener('click', (ev) => {
        if (!ev.target.closest('#hist-sort')) closeSessionHistorySortMenu();
    });
    list.addEventListener('scroll', closeSessionHistorySortMenu, { passive: true });
}

const SESSION_HISTORY_SORT_LABELS = Object.freeze({
    recent: 'Most recent',
    name: 'Name (A-Z)',
    answered: 'Most answered'
});

function buildSessionHistoryToolbar(sortMode, hasSessions) {
    const toolbar = document.createElement('div');
    toolbar.className = 'hist-toolbar';
    const sortOptions = SESSION_HISTORY_SORT_MODES.map((mode) => {
        const active = mode === sortMode;
        return `<button class="hist-sort-opt${active ? ' active' : ''}" type="button" role="option" aria-selected="${active}" data-mode="${mode}">`
            + `<span class="hist-sort-opt-check">${active ? sessionFolderIcon('check', { strokeWidth: 2.6 }) : ''}</span>`
            + `<span>${escapeHtml(SESSION_HISTORY_SORT_LABELS[mode])}</span></button>`;
    }).join('');

    toolbar.innerHTML = `
        <button class="hist-toolbar-btn" id="hist-new-folder-btn" type="button">
            ${sessionFolderIcon('folderPlus')}<span class="gradient-text">New Folder</span>
        </button>
        <div class="hist-sort" id="hist-sort">
            <button class="hist-sort-btn" id="hist-sort-btn" type="button" title="Sort sessions"
                aria-haspopup="listbox" aria-expanded="false" ${hasSessions ? '' : 'disabled'}>
                <span class="hist-sort-value gradient-text">${escapeHtml(SESSION_HISTORY_SORT_LABELS[sortMode] || SESSION_HISTORY_SORT_LABELS.recent)}</span>
                ${sessionFolderIcon('chevron', { className: 'hist-sort-caret', strokeWidth: 2.4 })}
            </button>
            <div class="hist-sort-menu" role="listbox" aria-label="Sort sessions">${sortOptions}</div>
        </div>
        <div class="hist-new-folder"><div class="hist-new-folder-inner">
            <input type="text" class="hist-rename-input" id="hist-new-folder-input" placeholder="Folder name..." maxlength="60" />
            <div class="hist-rename-actions">
                <button class="hist-rename-save" id="hist-new-folder-save" type="button">Create</button>
                <button class="hist-rename-cancel" id="hist-new-folder-cancel" type="button">Cancel</button>
            </div>
        </div></div>`;

    /* Custom listbox: a native <select> renders an OS-chrome panel that cannot
       be themed, so the options are ordinary buttons styled like the app. */
    const sortWrap = toolbar.querySelector('#hist-sort');
    const sortBtn = toolbar.querySelector('#hist-sort-btn');
    const closeSortMenu = () => {
        sortWrap.classList.remove('open');
        sortBtn.setAttribute('aria-expanded', 'false');
    };
    sortBtn.onclick = (ev) => {
        ev.stopPropagation();
        const nowOpen = !sortWrap.classList.contains('open');
        sortWrap.classList.toggle('open', nowOpen);
        sortBtn.setAttribute('aria-expanded', nowOpen ? 'true' : 'false');
    };
    sortWrap.querySelectorAll('.hist-sort-opt').forEach((opt) => {
        opt.onclick = async (ev) => {
            ev.stopPropagation();
            closeSortMenu();
            if (opt.dataset.mode === sortMode) return;
            setSessionHistorySortMode(opt.dataset.mode);
            await SessionManager.showHistory();
        };
    });
    sortWrap.addEventListener('keydown', (ev) => {
        if (ev.key === 'Escape' && sortWrap.classList.contains('open')) {
            ev.stopPropagation();
            closeSortMenu();
            sortBtn.focus();
        }
    });
    bindSessionHistorySortDismiss();

    const newFolderBtn = toolbar.querySelector('#hist-new-folder-btn');
    const input = toolbar.querySelector('#hist-new-folder-input');
    const closeCreate = () => {
        toolbar.classList.remove('creating');
        input.value = '';
    };
    newFolderBtn.onclick = () => {
        if (toolbar.classList.contains('creating')) { closeCreate(); return; }
        toolbar.classList.add('creating');
        input.focus();
    };
    const create = async () => {
        const name = input.value.trim();
        if (!name) { showPopup('Give the folder a name first.', null, true); return; }
        if (!await runSessionFolderChange(() => createSessionFolder(name, SESSION_FOLDER_DEFAULT_COLOR))) return;
        await SessionManager.showHistory();
    };
    toolbar.querySelector('#hist-new-folder-save').onclick = () => { void create(); };
    toolbar.querySelector('#hist-new-folder-cancel').onclick = closeCreate;
    input.onkeydown = (ke) => {
        if (ke.key === 'Enter') { ke.preventDefault(); void create(); }
        if (ke.key === 'Escape') { ke.preventDefault(); closeCreate(); }
    };

    return toolbar;
}

function renderSessionHistoryTree(sessions, folders, options = {}) {
    const list = els.historyList;
    if (!list) return;

    const sortMode = getSessionHistorySortMode();
    const allSessions = Array.isArray(sessions) ? sessions.filter(Boolean) : [];
    const folderList = Array.isArray(folders) ? folders : [];
    const knownFolderIds = new Set(folderList.map((folder) => folder.id));

    list.innerHTML = '';
    list.classList.add('hist-tree', 'hist-priming');
    if (els.historyDeleteAllBtn) els.historyDeleteAllBtn.disabled = allSessions.length === 0;

    // One shared gradient definition for every stroke-only icon below.
    const defs = document.createElement('div');
    defs.className = 'hist-svg-defs';
    defs.innerHTML = `<svg width="0" height="0" aria-hidden="true" focusable="false"><defs>
        <linearGradient id="hist-icon-grad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stop-color="var(--g1, #60a5fa)" />
            <stop offset="100%" stop-color="var(--g2, #f472b6)" />
        </linearGradient>
    </defs></svg>`;
    list.appendChild(defs);

    list.appendChild(buildSessionHistoryToolbar(sortMode, allSessions.length > 0));

    if (allSessions.length === 0 && folderList.length === 0) {
        const empty = document.createElement('div');
        empty.className = 'hist-empty';
        empty.textContent = 'No saved sessions.';
        list.appendChild(empty);
    }

    folderList.forEach((folder) => {
        const members = sortSessionsForHistory(
            allSessions.filter((session) => session.folderId === folder.id),
            sortMode
        );
        list.appendChild(buildSessionFolderGroup({
            id: folder.id,
            name: folder.name,
            color: folder.color,
            sessions: members,
            folders: folderList,
            collapsible: true,
            expanded: isSessionFolderExpanded(folder.id),
            isUngrouped: false
        }));
    });

    const ungrouped = sortSessionsForHistory(
        allSessions.filter((session) => !session.folderId || !knownFolderIds.has(session.folderId)),
        sortMode
    );
    if (ungrouped.length > 0 || folderList.length > 0) {
        list.appendChild(buildSessionFolderGroup({
            id: null,
            name: 'Ungrouped',
            color: '#94a3b8',
            sessions: ungrouped,
            folders: folderList,
            collapsible: true,
            expanded: isSessionFolderExpanded(null),
            isUngrouped: true
        }));
    }

    list.scrollTop = Number(options.scrollTop) || 0;
    // Let the freshly painted open/closed state settle before enabling motion,
    // otherwise every refresh replays the reveal animation.
    requestAnimationFrame(() => requestAnimationFrame(() => list.classList.remove('hist-priming')));
}

async function softDeleteSavedSessionBatch(sessions) {
    let count = 0;
    for (const session of sessions) {
        if (!session || session.id === undefined || session.id === null) continue;
        const deleted = await softDeleteSession(session.id, session);
        if (deleted) count += 1;
    }
    return count;
}

function resetRuntimeAfterDeletingSavedSessions() {
    currentSessionId = null;
    sessionOpenEpoch += 1;
    currentSessionCustomName = '';
    currentSessionExplicit = false;
    currentSessionNumber = null;
    syncWorkingSessionTitle();
    clearQuickSessionSnapshot();
    userAnswers = {};
    savedQuestions = {};
    knowledgeState = {};
    srsData = {};
    statsData = {};
    aiQuestionCacheByIndex = {};
    adaptiveReviewSnapshot = {};
    adaptiveSnapshotRankFilters.clear();
    currentQIndex = 0;
    loadedQuestionSets = {};
    activeQuestionSetKeys = new Set();
    condensedQuizState = createDefaultCondensedQuizState();
    highYieldQuizState = normalizeHighYieldQuizState();
    adaptivePracticeState = normalizeAdaptivePracticeState();
    highYieldLastAppliedPool = null;
    syncCondensedQuizToggleButton();
    questionDuplicateReportsBySetKey = {};
    questionDuplicateReportLookupStateBySetKey = {};
    savedDuplicateEntriesCache = [];
    isSavedDuplicateView = false;
    revokeLoadedPdfBlobUrls();
    clearAllPdfRuntimeCaches({ revokeBlobUrls: false, endSession: true });
    loadedPdfFiles = {};
    questions = [];
    sessionManifest = { jsons: [], pdfs: [] };
    pdfAssociations = {};
    setLastSessionId(null);

    if (els.pdfFrame) els.pdfFrame.src = 'about:blank';
    if (typeof destroyAdvancedPdfDocument === 'function') destroyAdvancedPdfDocument();
    if (typeof setPdfPaneMode === 'function') setPdfPaneMode('pdf');
    if (els.pdfTitle) els.pdfTitle.innerText = 'Add a file to see it here';
    currentPdfBlobUrl = null;
    lastPdfState = { src: '', title: '', fileName: '', page: 1, searchTerm: '' };
    if (els.workArea) els.workArea.classList.remove('split-view', 'trio-view', 'split-view-ai');
    if (els.pdfPane) els.pdfPane.style.display = 'none';
    isViewerOpen = false;
    isTrioMode = false;
    const toggleViewerText = $('toggle-viewer-btn')?.querySelector('.quick-control-text');
    if (toggleViewerText) toggleViewerText.innerText = 'Source';

    rebuildActiveQuestions(false);
    updateSourceDropdown();
    syncContinueButton();
    syncContentDependentControls();
    // The title falls back to the loaded quiz names, so it is read again once they are gone.
    syncWorkingSessionTitle();
}

async function syncAfterSavedSessionDelete(sessionId, options = {}) {
    const normalizedSessionId = normalizeSessionId(sessionId);
    const wasCurrentSession = !!options.wasCurrentSession
        || (!!normalizedSessionId && normalizedSessionId === normalizeSessionId(currentSessionId));
    const wasLastSession = !!options.wasLastSession;

    if (wasCurrentSession) {
        resetRuntimeAfterDeletingSavedSessions();
        enterWelcomeMode();
        await resolveLastSessionId({ preferStored: false });
        await syncWelcomeScreenState();
        return;
    }

    if (wasLastSession) {
        await resolveLastSessionId({ preferStored: false });
    } else {
        syncContinueButton();
        await syncWelcomeScreenState();
    }
}

async function deleteAllSessionsFromHistory() {
    if (IS_EMBED_DEMO) {
        demoUnavailableFeature('Session history');
        return;
    }

    const sessions = await IDB.getAll('sessions');
    if (!sessions.length) {
        showPopup('No saved sessions to delete.', null, true);
        return;
    }

    const confirmed = await showThemeConfirm(
        `Move all ${sessions.length} saved session${sessions.length === 1 ? '' : 's'} to Recently Deleted?\n\nThey can be restored within 15 days.`,
        { title: 'Delete All Sessions', okText: 'Delete All', cancelText: 'Cancel', danger: true }
    );
    if (!confirmed) return;

    try {
        // Only the listed records are targets. The session open when deletion finishes
        // is reset only if it was one of them; live work that was never saved (or
        // opened while deleting) is not part of this operation.
        const targetIds = new Set(sessions.map((session) => normalizeSessionId(session?.id)).filter(Boolean));
        const count = await softDeleteSavedSessionBatch(sessions);
        const openSessionId = normalizeSessionId(currentSessionId);
        const openSessionTargeted = !!openSessionId && targetIds.has(openSessionId);
        if (count !== sessions.length) {
            if (openSessionTargeted) {
                const currentStillSaved = await IDB.get('sessions', openSessionId);
                if (!currentStillSaved && normalizeSessionId(currentSessionId) === openSessionId) {
                    await syncAfterSavedSessionDelete(openSessionId, { wasCurrentSession: true, wasLastSession: true });
                }
            }
            await SessionManager.showHistory();
            syncContinueButton();
            showPopup(`Moved ${count} of ${sessions.length} sessions to Recently Deleted.`, null, true);
            return;
        }
        if (openSessionTargeted) {
            resetRuntimeAfterDeletingSavedSessions();
            await SessionManager.showHistory();
            enterWelcomeMode();
        } else {
            await SessionManager.showHistory();
            await resolveLastSessionId({ preferStored: false });
            syncContinueButton();
        }
        showPopup(`${count} session${count === 1 ? '' : 's'} moved to Recently Deleted.`, null, false);
    } catch (e) {
        console.error('Delete all sessions failed:', e);
        showPopup('Delete all failed: ' + e.message, null, true);
    }
}
window.deleteAllSessionsFromHistory = deleteAllSessionsFromHistory;

els.welcomeHistoryBtn.onclick = () => {
    if (IS_EMBED_DEMO) {
        demoUnavailableFeature('Session history');
        return;
    }
    SessionManager.showHistory();
};
els.historyMenuBtn.onclick = () => {
    if (IS_EMBED_DEMO) {
        demoUnavailableFeature('Session history');
        return;
    }
    SessionManager.showHistory();
};
if (els.studyDashboardMenuBtn) {
    els.studyDashboardMenuBtn.onclick = () => {
        openStudyDashboardFromTools();
    };
}
if (els.studySetupMenuBtn) {
    els.studySetupMenuBtn.onclick = () => {
        openStudySetupFromTools();
    };
}
function closeHistory(e) { if (e && e.target !== els.historyModal && e.target.innerText !== 'Close') return; if (els.historyModal.classList.contains('active')) window.dispatchEvent(new CustomEvent('memora:history-closing')); els.historyModal.classList.remove('active'); }

function resetAIResponseUIState() {
    if (els.expl) {
        els.expl.style.display = 'none';
        els.expl.innerHTML = '';
        els.expl.classList.remove('ai-response-box', 'ai-response-loading', 'explanation-revealing', 'from-correct', 'from-incorrect');
        els.expl.removeAttribute('data-tone');
        els.expl.removeAttribute('aria-busy');
        els.expl.onclick = null;
        els.expl.title = '';
        els.expl.style.cursor = '';
    }

    closeAIResponsePopup();

    const aiPanel = document.getElementById('ai-response-panel');
    if (aiPanel) aiPanel.style.display = 'none';

    if (typeof aiResponseCache !== 'undefined') {
        aiResponseCache = { cacheKey: null, explanation: null, deepDive: null, hint: null };
    }

    if (typeof lastAIResponseData !== 'undefined') {
        lastAIResponseData = { text: '', label: '', model: '' };
    }

    if (typeof pendingAIRequests !== 'undefined' && pendingAIRequests && typeof pendingAIRequests.clear === 'function') {
        pendingAIRequests.clear();
        if (typeof updateButtonPulsation === 'function') updateButtonPulsation();
    }
}

function prepareSessionLandingSoloMode() {
    suppressAutoStudyOpenOnNextQuestionRender = true;
    suppressAutoStudyOpenUntilQuestionChange = true;
    suppressedAutoStudyQuestionRealIdx = null;
    clearAutoStudyTimers();

    const workArea = els?.workArea || $('work-area');
    if (workArea?.classList?.contains('pdf-tutor-view') && typeof closePdfTutorSession === 'function') {
        try {
            closePdfTutorSession({
                keepPdf: false,
                forceDisableAgent: true,
                skipGuidedPrepConfirm: true
            });
        } catch (error) {
            console.warn('Failed to close Tutor workspace during session landing reset:', error);
        }
    }

    if (typeof isPdfPaneExpanded !== 'undefined' && isPdfPaneExpanded && typeof togglePdfPaneExpanded === 'function') {
        togglePdfPaneExpanded(false);
    }
    if (typeof detachSmartLineListeners === 'function') detachSmartLineListeners();

    if (workArea) {
        workArea.classList.remove(
            'split-view',
            'split-view-ai',
            'trio-view',
            'pdf-tutor-view',
            'pdf-tutor-quiz-view',
            'pdf-tutor-quiz-duo',
            'pdf-tutor-quiz-trio',
            'pane-layout-target-single',
            'pane-layout-target-duo',
            'pane-layout-target-trio',
            'pane-open-transitioning',
            'pane-close-transitioning'
        );
        workArea.style.gridTemplateColumns = '';
    }
    if (els?.quizCard) {
        els.quizCard.style.removeProperty('--pane-layout-target-card-width');
        els.quizCard.style.removeProperty('--pane-layout-target-card-height');
    }
    document.body?.classList?.remove('pdf-tutor-mode', 'pdf-tutor-quiz-mode', 'pdf-tutor-transcript-open', 'pdf-tutor-silent-study', 'pdf-tutor-silent-expl-open');

    if (els?.pdfPane) els.pdfPane.style.display = '';
    if (els?.pdfTitle) els.pdfTitle.style.display = '';
    const aiFrame = $('ai-frame');
    if (aiFrame) aiFrame.src = '';

    isViewerOpen = false;
    isTrioMode = false;
    if (typeof setPdfPaneMode === 'function') setPdfPaneMode('pdf');
    if (typeof setViewerToggleLabel === 'function') setViewerToggleLabel('Show PDF');
    if (typeof setTrioToggleLabel === 'function') setTrioToggleLabel('\u2AF6 Trio');
    if (typeof syncAiPaneModeView === 'function') {
        const mode = typeof aiPaneMode !== 'undefined' ? aiPaneMode : null;
        void syncAiPaneModeView(mode);
    }
    if (typeof applyPanelOrderLayout === 'function') applyPanelOrderLayout();
    if (typeof resetQuizCardPosition === 'function') {
        resetQuizCardPosition({ layoutMode: 'single' });
    }
}

let quizEmptyStateTitleOverride = '';
let quizGenerationCardPendingShowsLanding = false;
let quizGenerationCardMessageQueue = [];
let quizGenerationCardMessageTimer = null;
let quizGenerationCardMessageRotationTimer = null;
let quizGenerationCardMessageRotationPhrases = [];
let quizGenerationCardMessageRotationIndex = 0;
const QUIZ_GENERATION_CARD_MESSAGE_ROTATION_MS = 4200;

function getQuizEmptyStateTitle() {
    if (quizEmptyStateTitleOverride) return quizEmptyStateTitleOverride;
    const hasPdfs = Array.isArray(sessionManifest?.pdfs)
        && sessionManifest.pdfs.some(pdfName => !!loadedPdfFiles?.[pdfName]);
    if (hasPdfs) {
        return '';
    }
    return 'Import files to get started';
}

function syncQuizEmptyStateMessage() {
    if (Array.isArray(questions) && questions.length > 0) return;
    const titleEl = els.qText?.querySelector?.('.quiz-empty-state-title');
    if (titleEl && !document.body.classList.contains('quiz-generation-card-pending')) {
        const title = getQuizEmptyStateTitle();
        if (titleEl.textContent !== title) {
            titleEl.textContent = title;
        }
    }
}
window.syncQuizEmptyStateMessage = syncQuizEmptyStateMessage;

function getQuizEmptyStateMarkup() {
    const title = getQuizEmptyStateTitle();
    let titleHtml;
    if (title.endsWith('...')) {
        const base = title.substring(0, title.length - 3);
        titleHtml = `${escapeHtml(base)}<span class="loading-dots"><span>.</span><span>.</span><span>.</span></span>`;
    } else {
        titleHtml = escapeHtml(title);
    }
    const isPending = !!document.body?.classList?.contains('quiz-generation-card-pending');
    // A pending generation may intentionally cover an already-created question
    // bank. Keep this status copy compact across any quiet landing re-render.
    const generationMessageClass = isPending && quizGenerationCardPendingShowsLanding
        ? ' quiz-generation-ready-message'
        : '';
    return `
        <div class="quiz-empty-state">
            <p class="quiz-empty-state-title${generationMessageClass}">${titleHtml}</p>
        </div>
    `;
}

function setContentDependentControlState(control, shouldDisable, disabledReason = '') {
    if (!control) return;
    if (!Object.prototype.hasOwnProperty.call(control.dataset, 'memoraContentControlTitle')) {
        control.dataset.memoraContentControlTitle = control.title || '';
    }
    const disabled = !!shouldDisable;
    control.disabled = disabled;
    if (disabled) {
        control.setAttribute('aria-disabled', 'true');
        if (disabledReason) control.title = disabledReason;
    } else {
        control.removeAttribute('aria-disabled');
        control.title = control.dataset.memoraContentControlTitle || '';
    }
}

function syncContentDependentControls() {
    const questionList = Array.isArray(questions) ? questions : [];
    const activeIndices = Array.isArray(filteredIndices)
        ? filteredIndices.filter((idx) => Number.isInteger(idx) && idx >= 0 && idx < questionList.length && questionList[idx])
        : [];
    const generationLanding = typeof isQuizGenerationCardLandingShowing === 'function'
        ? isQuizGenerationCardLandingShowing()
        : false;
    const usableQuestionCount = activeIndices.length;
    const currentRealIdx = Number.isInteger(filteredIndices?.[currentQIndex])
        ? filteredIndices[currentQIndex]
        : -1;
    const hasCurrentQuestion = !generationLanding
        && Number.isInteger(currentRealIdx)
        && currentRealIdx >= 0
        && currentRealIdx < questionList.length
        && !!questionList[currentRealIdx];
    const hasUsableQuestions = !generationLanding && usableQuestionCount > 0;
    const noQuestionsReason = 'No quiz questions are available yet.';
    const noCurrentReason = 'Open a quiz question before using this control.';

    setContentDependentControlState(els.prev, !hasCurrentQuestion || currentQIndex <= 0,
        hasCurrentQuestion ? 'Already at the first question.' : noCurrentReason);
    setContentDependentControlState(els.next, !hasCurrentQuestion, noCurrentReason);
    setContentDependentControlState(els.save, !hasCurrentQuestion, noCurrentReason);
    if (els.save) {
        if (hasCurrentQuestion && typeof updateSaveButton === 'function') updateSaveButton(currentRealIdx);
        else {
            els.save.classList.remove('saved');
            els.save.innerText = '☆ Save';
        }
    }
    setContentDependentControlState(els.jump, !hasUsableQuestions, noQuestionsReason);
    setContentDependentControlState(els.resetQBtn, !hasCurrentQuestion, noCurrentReason);
    setContentDependentControlState(els.refreshQuestionBtn, !hasCurrentQuestion, noCurrentReason);
    setContentDependentControlState(els.refreshQuizBtn, !hasUsableQuestions, noQuestionsReason);

    const validSavedIndices = Object.keys(savedQuestions || {})
        .map(Number)
        .filter((idx) => Number.isInteger(idx) && idx >= 0 && idx < questionList.length && !!questionList[idx]);
    const hasSavedQuestions = validSavedIndices.length > 0;
    setContentDependentControlState(els.quizSavedMainBtn, !hasSavedQuestions,
        'No saved questions are available yet.');
    setContentDependentControlState(els.btnQuizSavedModal, !hasSavedQuestions,
        'No saved questions are available yet.');
    const pdfExportPending = els.btnDownloadPdf?.dataset.memoraPdfExportPending === 'true';
    setContentDependentControlState(els.btnDownloadPdf, pdfExportPending || !hasSavedQuestions,
        pdfExportPending ? 'Preparing the saved-questions PDF.' : 'No saved questions are available yet.');
    setContentDependentControlState($('export-quiz-btn'), !hasUsableQuestions, noQuestionsReason);

    document.querySelectorAll('#export-modal .export-option-btn').forEach((button) => {
        setContentDependentControlState(button, !hasUsableQuestions, noQuestionsReason);
    });
    setContentDependentControlState($('export-saved-only'), !hasSavedQuestions,
        'No saved questions are available yet.');

    if (typeof syncQuizSourceButtonState === 'function') syncQuizSourceButtonState();
    return { usableQuestionCount, hasCurrentQuestion, hasSavedQuestions };
}
window.syncContentDependentControls = syncContentDependentControls;

function clearQuizGenerationCardMessageQueue() {
    quizGenerationCardMessageQueue = [];
    if (quizGenerationCardMessageTimer) {
        clearTimeout(quizGenerationCardMessageTimer);
        quizGenerationCardMessageTimer = null;
    }
    if (quizGenerationCardMessageRotationTimer) {
        clearInterval(quizGenerationCardMessageRotationTimer);
        quizGenerationCardMessageRotationTimer = null;
    }
    quizGenerationCardMessageRotationPhrases = [];
    quizGenerationCardMessageRotationIndex = 0;
    const titleEl = els?.qText?.querySelector?.('.quiz-empty-state-title');
    titleEl?.classList?.remove(
        'quiz-generation-ready-message',
        'quiz-generation-message-entering',
        'quiz-generation-message-leaving'
    );
}
window.clearQuizGenerationCardMessageQueue = clearQuizGenerationCardMessageQueue;

function isQuizGenerationCardMessageRenderAllowed() {
    return !!document.body?.classList?.contains('quiz-generation-card-pending')
        && quizGenerationCardPendingShowsLanding
        && !document.documentElement.classList.contains('phone-device');
}

function renderQuizGenerationCardReadyMessage(titleEl, message) {
    if (!titleEl) return;
    const text = String(message || '').trim().replace(/\.{3}\s*$/, '');
    titleEl.replaceChildren(document.createTextNode(text));
    const dots = document.createElement('span');
    dots.className = 'loading-dots';
    dots.setAttribute('aria-hidden', 'true');
    for (let index = 0; index < 3; index += 1) {
        const dot = document.createElement('span');
        dot.textContent = '.';
        dots.appendChild(dot);
    }
    titleEl.appendChild(dots);
}

function startQuizGenerationCardMessageRotation(phrases = []) {
    const normalizedPhrases = Array.from(new Set(
        (Array.isArray(phrases) ? phrases : [])
            .map((phrase) => String(phrase || '').trim())
            .filter(Boolean)
    ));

    clearQuizGenerationCardMessageQueue();
    if (normalizedPhrases.length < 2
        || document.documentElement.classList.contains('phone-device')) return false;

    quizGenerationCardMessageRotationPhrases = normalizedPhrases;
    quizGenerationCardMessageRotationIndex = 1;
    quizGenerationCardMessageRotationTimer = setInterval(() => {
        if (!isQuizGenerationCardMessageRenderAllowed()) {
            clearQuizGenerationCardMessageQueue();
            return;
        }

        const phrase = quizGenerationCardMessageRotationPhrases[
            quizGenerationCardMessageRotationIndex % quizGenerationCardMessageRotationPhrases.length
        ];
        quizGenerationCardMessageRotationIndex += 1;
        queueQuizGenerationCardMessage(phrase);
    }, QUIZ_GENERATION_CARD_MESSAGE_ROTATION_MS);
    return true;
}
window.startQuizGenerationCardMessageRotation = startQuizGenerationCardMessageRotation;

function showNextQuizGenerationCardMessage() {
    if (quizGenerationCardMessageTimer || !quizGenerationCardMessageQueue.length) return;
    if (!isQuizGenerationCardMessageRenderAllowed()) {
        clearQuizGenerationCardMessageQueue();
        return;
    }

    const titleEl = els?.qText?.querySelector?.('.quiz-empty-state-title');
    if (!titleEl) {
        clearQuizGenerationCardMessageQueue();
        return;
    }

    const message = quizGenerationCardMessageQueue.shift();
    titleEl.classList.remove('quiz-generation-message-entering');
    titleEl.classList.add('quiz-generation-message-leaving');
    quizGenerationCardMessageTimer = setTimeout(() => {
        quizGenerationCardMessageTimer = null;
        if (!isQuizGenerationCardMessageRenderAllowed()) {
            clearQuizGenerationCardMessageQueue();
            return;
        }

        const currentTitleEl = els?.qText?.querySelector?.('.quiz-empty-state-title');
        if (!currentTitleEl) {
            clearQuizGenerationCardMessageQueue();
            return;
        }
        quizEmptyStateTitleOverride = message;
        renderQuizGenerationCardReadyMessage(currentTitleEl, message);
        currentTitleEl.classList.add('quiz-generation-ready-message');
        currentTitleEl.classList.remove('quiz-generation-message-leaving');
        currentTitleEl.classList.add('quiz-generation-message-entering');
        quizGenerationCardMessageTimer = setTimeout(() => {
            quizGenerationCardMessageTimer = null;
            if (!isQuizGenerationCardMessageRenderAllowed()) {
                clearQuizGenerationCardMessageQueue();
                return;
            }
            currentTitleEl.classList.remove('quiz-generation-message-entering');
            showNextQuizGenerationCardMessage();
        }, 1250);
    }, 180);
}

function queueQuizGenerationCardMessage(message = '') {
    const text = String(message || '').trim();
    if (!text
        || !isQuizGenerationCardMessageRenderAllowed()) return false;
    quizGenerationCardMessageQueue.push(text);
    showNextQuizGenerationCardMessage();
    return true;
}
window.queueQuizGenerationCardMessage = queueQuizGenerationCardMessage;

function setQuizGenerationCardPending(pending, message = 'Generating quiz, just a moment...', options = {}) {
    const wasPending = !!document.body?.classList?.contains('quiz-generation-card-pending');
    const requestedShowLanding = pending
        ? options?.showLanding !== false
        : (options?.showLanding === true || quizGenerationCardPendingShowsLanding);
    const showLanding = requestedShowLanding;
    const allowDynamicMessageRotation = !document.documentElement.classList.contains('phone-device');
    const optionSources = Array.isArray(options?.sources)
        ? options.sources
        : (options?.sources ? [options.sources] : []);
    const optionPhrases = allowDynamicMessageRotation && Array.isArray(options?.phrases)
        ? options.phrases
        : (allowDynamicMessageRotation
            && optionSources.length > 0
            && typeof window.generateQuizGenStatusPhrases === 'function'
            ? window.generateQuizGenStatusPhrases(optionSources)
            : []);
    const firstPhrase = String(optionPhrases[0] || '').trim();
    const shouldShowFailureLanding = !pending
        && showLanding
        && (!questions.length || !filteredIndices.length)
        && !!String(options?.failureMessage || '').trim();
    quizGenerationCardPendingShowsLanding = !!pending && showLanding;
    if (!pending || !showLanding || !wasPending) {
        clearQuizGenerationCardMessageQueue();
    }
    quizEmptyStateTitleOverride = pending && requestedShowLanding
        ? firstPhrase || String(message || 'Generating quiz, just a moment...')
        : (shouldShowFailureLanding ? String(options.failureMessage).trim() : '');
    document.body?.classList?.toggle('quiz-generation-card-pending', !!pending);

    if (pending) {
        if (showLanding) {
            // Guided quizzes reuse this presentation inside the live Learn workspace. The
            // ordinary Create flow still owns the full workspace reset.
            if (options?.preserveWorkspace !== true) prepareSessionLandingSoloMode();
            showEmptySessionImportLanding();
            if (optionPhrases.length > 1 && (!wasPending || !quizGenerationCardMessageRotationTimer)) {
                startQuizGenerationCardMessageRotation(optionPhrases);
            }
            if (optionPhrases.length > 0) {
                const titleEl = els?.qText?.querySelector?.('.quiz-empty-state-title');
                titleEl?.classList?.add('quiz-generation-ready-message');
            }
            if ((options?.showGeneratingCounter === true || pending) && els.prog) {
                if (els.prog._counterInterval) {
                    clearInterval(els.prog._counterInterval);
                    els.prog._counterInterval = null;
                }
                els.prog.innerText = 'Generating...';
            }

            // Disable controls during background generation
            if (els.prev) els.prev.disabled = true;
            if (els.next) els.next.disabled = true;
            if (els.save) els.save.disabled = true;
        } else {
            // User returned to study the active bank; restore controls
            if (els.prev) els.prev.disabled = (currentQIndex === 0);
            if (els.next) els.next.disabled = false;
            if (els.save) els.save.disabled = false;
        }
    } else {
        if (wasPending && quizCompletionCohortState.pendingNewQuestionKeys.length > 0) {
            includePendingQuizCompletionQuestions({ reason: 'generation-complete' });
        }
        // Restore controls
        if (els.prev) els.prev.disabled = false;
        if (els.next) els.next.disabled = false;
        if (els.save) els.save.disabled = false;

        if (questions.length && filteredIndices.length) {
            if (isFlashcardMode) renderFlashcardGrid();
            else renderQuestion();
        } else if (!questions.length || !filteredIndices.length) {
            showEmptySessionImportLanding();
        }
    }
    syncContentDependentControls();
}
window.setQuizGenerationCardPending = setQuizGenerationCardPending;

function isQuizGenerationCardLandingShowing() {
    return !!document.body?.classList?.contains('quiz-generation-card-pending') && quizGenerationCardPendingShowsLanding;
}
window.isQuizGenerationCardLandingShowing = isQuizGenerationCardLandingShowing;

function updateDiscreteQuestionCounter(newTotal) {
    void newTotal;
    syncQuizProgressText();
    syncQuizProgressFill();
}

function showEmptySessionImportLanding() {
    document.body.classList.remove('welcome-mode', 'study-dashboard-open');
    setWelcomeSessionLoadPending(false);
    setWelcomeActionsPending(false);
    if (els.welcome) els.welcome.style.display = 'none';
    if (typeof hideStudyPlannerSurfaces === 'function') hideStudyPlannerSurfaces();
    if (els.fcContainer) els.fcContainer.style.display = 'none';
    if (els.quizContainer) els.quizContainer.style.display = 'flex';
    if (els.quizCard) els.quizCard.style.display = 'block';
    if (els.quizContent) els.quizContent.style.display = 'block';
    if (els.resultsView) els.resultsView.style.display = 'none';
    if (els.prog) {
        if (document.body?.classList?.contains('quiz-generation-card-pending') || (typeof isQuizGenerationCardLandingShowing === 'function' && isQuizGenerationCardLandingShowing())) {
            if (els.prog._counterInterval) {
                clearInterval(els.prog._counterInterval);
                els.prog._counterInterval = null;
            }
            els.prog.innerText = 'Generating...';
        } else {
            // A shipped "Question 0 of 0" reads as a broken counter rather
            // than an empty state, especially on the marketing embed where
            // it is the first number a visitor sees (MD-11).
            els.prog.innerText = '—';
        }
    }
    if (els.progressFill) els.progressFill.style.width = '0%';
    if (els.qText) {
        els.qText.innerHTML = getQuizEmptyStateMarkup();
    }
    if (els.choices) {
        els.choices.classList.remove('hard-mode');
        els.choices.innerHTML = `
            <div class="quiz-skeleton-choice"></div>
            <div class="quiz-skeleton-choice"></div>
            <div class="quiz-skeleton-choice"></div>
            <div class="quiz-skeleton-choice"></div>
        `;
        if (document.body.classList.contains('quiz-generation-card-pending')
            && !document.documentElement.classList.contains('phone-device')) {
            const skeletonChoices = els.choices.querySelectorAll('.quiz-skeleton-choice');
            const lineLengths = [58, 70, 82, 94].map((baseLength) => (
                baseLength + Math.floor(Math.random() * 7) - 3
            ));
            for (let index = lineLengths.length - 1; index > 0; index -= 1) {
                const swapIndex = Math.floor(Math.random() * (index + 1));
                [lineLengths[index], lineLengths[swapIndex]] = [lineLengths[swapIndex], lineLengths[index]];
            }
            skeletonChoices.forEach((choice, index) => {
                choice.style.setProperty('--quiz-generation-line-length', `${lineLengths[index % lineLengths.length]}%`);
            });
        }
    }
    if (els.expl) {
        els.expl.style.display = 'none';
        els.expl.innerHTML = '';
        els.expl.classList.remove('ai-response-box', 'ai-response-loading', 'explanation-revealing', 'from-correct', 'from-incorrect');
        els.expl.removeAttribute('data-tone');
        els.expl.removeAttribute('aria-busy');
        els.expl.onclick = null;
        els.expl.title = '';
        els.expl.style.cursor = '';
    }
    lastRenderedQuestionRealIdx = null;
    adaptiveLastRenderedIdx = null;
    syncNotesButtonIndicator(null);
    updateSourceDropdown();
    updateImportButtonState();
    syncQuizEmptyStateMessage();
    if (typeof applyQuizCardContentScale === 'function') applyQuizCardContentScale();
    requestAnimationFrame(() => {
        if (typeof window.memoraInstallQuizEmptyNeuralAtlas === 'function') {
            window.memoraInstallQuizEmptyNeuralAtlas();
        }
    });
    requestAnimationFrame(positionQuickActionsDock);
}

// RESET DB HANDLER - Now handled in Database Modal (confirmClearDatabase)

async function startNewSessionWithNamePrompt(wipe = true, options = {}) {
    invalidateWelcomeSessionLoad();
    const isPhone = document.documentElement.classList.contains('phone-device');
    const shouldPrompt = isPhone && !options?.skipNamePrompt;
    const shouldAutoName = !isPhone && !options?.defaultName;
    const sessionName = shouldPrompt
        ? await showSessionNamePrompt({ validate: (name) => findSessionNameConflict(name) })
        : normalizeSessionNameInput(options.defaultName || (isPhone ? 'Study Session' : ''));
    if (!sessionName && !shouldAutoName) return false;
    return new Promise((resolve) => {
        setTimeout(() => {
            try {
                startNewSession(wipe, {
                    ...(options || {}),
                    ...(sessionName ? { customName: sessionName } : {}),
                    pulseImport: options?.pulseImport !== false
                });
                resolve(true);
            } catch (error) {
                console.error('Failed to start new session:', error);
                setWelcomeSessionLoadPending(false);
                setWelcomeActionsPending(false);
                showPopup('Could not start a new session. Reload the page and try again.', null, true);
                resolve(false);
            }
        }, 0);
    });
}
window.startNewSessionWithNamePrompt = startNewSessionWithNamePrompt;

function startNewSession(wipe = true, options = {}) {
    window.calendarCancelAutoOpenForLaunch?.();
    window.closeCalendarSurface?.({ immediate: true, force: true });
    if (IS_EMBED_DEMO && !options?.demoLanding) {
        loadEmbeddedDemoSession(false, { openViewer: false });
        showPopup("Demo restarted.", null, false);
        return;
    }
    if (IS_EMBED_DEMO && options?.demoLanding) {
        quizEmptyStateTitleOverride = 'Import file to get started';
    }
    invalidateWelcomeSessionLoad();
    const startedFromWelcome = document.body.classList.contains('welcome-mode');
    setWelcomeSessionLoadPending(false);
    setWelcomeActionsPending(false);
    console.log("Starting new session...");
    const requestedName = normalizeSessionNameInput(options.customName);
    const isPhone = document.documentElement.classList.contains('phone-device');
    const sessionNumber = !requestedName && !isPhone ? getNextSessionNumber() : null;
    const customName = requestedName || (isPhone ? '' : getDefaultNewSessionName(sessionNumber));
    currentSessionId = Date.now().toString();
    sessionOpenEpoch += 1;
    currentSessionNumber = sessionNumber;
    currentSessionCustomName = customName;
    // A plain start makes an ordinary session; the rail marks one explicit once it is named.
    currentSessionExplicit = false;
    resetSessionPersistenceBase(currentSessionId);
    resetOffPoolQuestionProgress();
    flashcardPassStateByDeck = {};
    flashcardPassDeckId = '';
    syncWorkingSessionTitle();
    clearQuickSessionSnapshot();
    sessionProgressRevision = 0;
    sessionProgressUpdatedAt = Date.now();
    userAnswers = {}; savedQuestions = {}; knowledgeState = {}; currentQIndex = 0;
    quizCompletionCohortState = {
        version: QUIZ_COMPLETION_COHORT_VERSION,
        activeQuestionKeys: [],
        pendingNewQuestionKeys: [],
        updatedAt: Date.now()
    };
    srsData = {}; statsData = {};
    studyDashboardState = createDefaultStudyDashboardState();
    studySetupState = createDefaultStudySetupState();
    studyPlanState = createDefaultStudyPlanState();
    forcedChoiceOrderByQuestion = {};
    mixedChoiceOrderByQuestion = {};
    condensedQuizState = createDefaultCondensedQuizState();
    highYieldQuizState = normalizeHighYieldQuizState();
    adaptivePracticeState = normalizeAdaptivePracticeState();
    highYieldLastAppliedPool = null;
    isAdaptiveReviewMode = false;
    isFlashcardAdaptiveReviewMode = false;
    pendingAdaptiveRating = null;
    adaptiveAnswerRevealedAt = 0;
    if (adaptiveSnapshotRankFilters && typeof adaptiveSnapshotRankFilters.clear === 'function') adaptiveSnapshotRankFilters.clear();
    if (adaptiveSessionSeenIndices && typeof adaptiveSessionSeenIndices.clear === 'function') adaptiveSessionSeenIndices.clear();
    adaptiveLastRenderedIdx = null;
    adaptiveClearedAnswersBackup = {};
    adaptiveResumeAnchorIdx = null;
    aiQuestionCacheByIndex = {};
    currentStreak = 0; // Reset Streak
    autoEvidence = false;
    autoExplanation = false;
    autoAdvance = false;

    resetAIResponseUIState();
    closeAdaptiveRatingModal();
    closeAdaptiveSnapshotModal();

    if (wipe) {
        // Hygienic Wipe
        revokeLoadedPdfBlobUrls();
        clearAllPdfRuntimeCaches({ revokeBlobUrls: false, endSession: true });
        loadedPdfFiles = {};
        loadedQuestionSets = {};
        activeQuestionSetKeys = new Set();
        condensedQuizState = createDefaultCondensedQuizState();
        highYieldQuizState = normalizeHighYieldQuizState();
        adaptivePracticeState = normalizeAdaptivePracticeState();
        highYieldLastAppliedPool = null;
        questionDuplicateReportsBySetKey = {};
        questionDuplicateReportLookupStateBySetKey = {};
        savedDuplicateEntriesCache = [];
        isSavedDuplicateView = false;
        sessionManifest = { jsons: [], pdfs: [] };
        questions = [];
        pdfAssociations = {};

        // Clear PDF panel and reset layout
        if (els.pdfFrame) els.pdfFrame.src = 'about:blank';
        if (typeof destroyAdvancedPdfDocument === 'function') destroyAdvancedPdfDocument();
        if (typeof setPdfPaneMode === 'function') setPdfPaneMode('pdf');
        if (els.pdfTitle) els.pdfTitle.innerText = 'Add a file to see it here';
        currentPdfBlobUrl = null;
        lastPdfState = { src: '', title: '', fileName: '', page: 1, searchTerm: '' }; // Clear cached PDF state

        // Reset to single quiz view (no PDF, no AI panel)
        if (els.workArea) els.workArea.classList.remove('split-view', 'trio-view', 'split-view-ai');
        if (els.pdfPane) els.pdfPane.style.display = 'none'; // Explicitly hide PDF pane
        isViewerOpen = false;
        isTrioMode = false;
        const toggleViewerText = $('toggle-viewer-btn')?.querySelector('.quick-control-text');
        if (toggleViewerText) toggleViewerText.innerText = 'Source';
    }

    const hasQuestionBanks = Object.values(loadedQuestionSets || {}).some(set => {
        const normalizedSet = normalizeStoredQuestionCollection(set);
        return Array.isArray(normalizedSet) && normalizedSet.length > 0;
    });
    if (hasQuestionBanks) {
        rebuildActiveQuestions();
    } else {
        questions = [];
        filteredIndices = [];
        isFilterActive = false;
        if (els.filterBanner) els.filterBanner.style.display = 'none';
        syncCondensedQuizToggleButton();
    }
    const startedEmptySession = !questions.length;
    if (startedEmptySession) {
        showEmptySessionImportLanding();
        syncAutoStudyToolButtons();
        clearSessionLoadFade();
        clearMemoraSurfaceSettle();
    } else {
        enterQuizMode({ surface: 'dashboard', skipQuestionRender: false });
    }
    syncContentDependentControls();

    startTime = Date.now();

    // Only an explicit user-created session should draw attention to Import.
    if (options.pulseImport === true && startedEmptySession && !options.isTutorial) {
        updateImportButtonState();
    }

    if (startedFromWelcome && !startedEmptySession) triggerSessionLoadFade();
    if (customName && !startedEmptySession) {
        void SessionManager.save(false).catch((error) => {
            console.warn('Failed to save new named session shell:', error);
        });
    }
    if (!options.isTutorial && !options.quiet) {
        showPopup(customName ? `New session started: ${escapeHtml(customName)}` : "New Session Started", null, false);
    }
}

function enterQuizMode(options = {}) {
    window.closeCalendarSurface?.({ immediate: true, force: true });
    console.debug("Entering Quiz Mode...");

    const finishEnterQuizMode = () => {
        document.body.classList.remove('welcome-mode');
        if (els.welcome) els.welcome.style.display = 'none';
        setModeToggleButtonLabel(isFlashcardMode);
        const dashboardOnly = !!options?.skipQuestionRender
            && options?.surface === 'dashboard'
            && shouldOfferStudyDashboard();
        if (dashboardOnly) {
            if (els.quizContainer) els.quizContainer.style.display = 'none';
            if (els.fcContainer) els.fcContainer.style.display = 'none';
            hideStudyPlannerSurfaces();
            showStudyDashboard({ keepBackground: false });
        } else if (isFlashcardMode) showFlashcards(); else showQuiz();
        if (options?.surface === 'dashboard' && shouldAutoOpenStudyDashboard()) {
            showStudyDashboard({ keepBackground: false });
        }
        updateSourceDropdown();
        requestAnimationFrame(positionQuickActionsDock);

        // Ensure auto button UI matches state
        els.autoSaveBtn.classList.toggle('active', autoSaveWrong);
        syncAutoStudyToolButtons();
        syncAdaptiveModeUI();
        window.calendarMaybeAutoOpenAfterWorkspaceIdle?.();
    };

    // Always run synchronously — session-load paths rely on enterQuizMode completing
    // before triggerSessionLoadFade runs. The load-fade overlay handles the visual
    // transition, so a separate welcome fade-out would just cause ghost bleed-through.
    finishEnterQuizMode();
    if (options?.surface !== 'dashboard' && !isFlashcardMode) {
        void Promise.resolve(window.MemoraRuntime?.ensureFeature?.('quizgen')).then(() => window.offerInterruptedQuizGenForActiveBanks?.()).catch(error => console.warn('Quiz recovery failed:', error));
    }
}

function isMemoraPdfExplanationFeatureEnabledForCore() {
    return false;
}

function syncAutoStudyToolButtons() {
    if (els.autoSaveBtn) { els.autoSaveBtn.classList.toggle('active', autoSaveWrong); els.autoSaveBtn.setAttribute('aria-pressed', String(autoSaveWrong)); }
    if (els.autoAdvanceBtn) els.autoAdvanceBtn.setAttribute('aria-pressed', String(autoAdvance));
    if (els.autoEvidenceBtn) els.autoEvidenceBtn.setAttribute('aria-pressed', String(autoEvidence));
    const pdfExplanationEnabled = isMemoraPdfExplanationFeatureEnabledForCore();
    if (!pdfExplanationEnabled && autoExplanation) autoExplanation = false;
    if (els.autoAdvanceBtn) els.autoAdvanceBtn.classList.toggle('active', autoAdvance);
    if (els.autoEvidenceBtn) els.autoEvidenceBtn.classList.toggle('active', autoEvidence);
    if (els.autoExplanationBtn) {
        els.autoExplanationBtn.hidden = !pdfExplanationEnabled;
        els.autoExplanationBtn.classList.toggle('active', pdfExplanationEnabled && autoExplanation);
        els.autoExplanationBtn.setAttribute('aria-pressed', pdfExplanationEnabled && autoExplanation ? 'true' : 'false');
        els.autoExplanationBtn.title = pdfExplanationEnabled
            ? 'Auto-open PDF explanation at the current slide'
            : 'Turn on PDF Explanation under AI & Help to use this legacy option';
    }
}

function setAutoStudyToolPreferences(nextState = {}, options = {}) {
    if (Object.prototype.hasOwnProperty.call(nextState, 'autoAdvance')) {
        autoAdvance = !!nextState.autoAdvance;
    }
    if (Object.prototype.hasOwnProperty.call(nextState, 'autoEvidence')) {
        autoEvidence = !!nextState.autoEvidence;
    }
    if (Object.prototype.hasOwnProperty.call(nextState, 'autoExplanation')) {
        autoExplanation = isMemoraPdfExplanationFeatureEnabledForCore() && !!nextState.autoExplanation;
    }

    syncAutoStudyToolButtons();

    if (options?.saveSession !== false && typeof SessionManager !== 'undefined' && SessionManager && typeof SessionManager.save === 'function') {
        void SessionManager.save(false);
    }
}

function toggleAutoEvidenceMode({ openCurrent = false, notify = false } = {}) {
    const enabled = !autoEvidence;
    setAutoStudyToolPreferences({ autoEvidence: enabled });
    let openedCurrent = true;
    if (enabled && openCurrent) {
        const preferTrioLayout = !!autoExplanation && isMemoraPdfExplanationFeatureEnabledForCore();
        openedCurrent = triggerEvidence({
            includeSearch: false,
            smoothJump: true,
            forceFreshViewer: true,
            maintainPdfScale: true,
            preferDuoPdfPane: !preferTrioLayout,
            preferTrioLayout,
            preferQuizFocus: true,
            silentIfMissing: false
        });
    }
    if (notify && (!enabled || openedCurrent !== false)) {
        showPopup(`Auto-Sources ${enabled ? 'ON' : 'OFF'}`, null, false);
    }
    return enabled;
}

function toggleAutoExplanationMode(_options = {}) {
    setAutoStudyToolPreferences({ autoExplanation: false }, { saveSession: false });
    return false;
}

window.addEventListener('memora:pdf-explanation-feature-changed', (event) => {
    if (!event?.detail?.enabled && autoExplanation) {
        setAutoStudyToolPreferences({ autoExplanation: false });
        return;
    }
    syncAutoStudyToolButtons();
});

function resetWelcomeViewportScroll() {
    const quizPane = $('quiz-pane');
    const reset = () => {
        if (!document.body.classList.contains('welcome-mode')) return;
        if (quizPane?.scrollTop !== 0) quizPane.scrollTop = 0;
        if (window.scrollX !== 0 || window.scrollY !== 0) window.scrollTo(0, 0);
    };
    reset();
    requestAnimationFrame(() => {
        reset();
        requestAnimationFrame(reset);
    });
}

window.addEventListener('pageshow', () => {
    if (document.body.classList.contains('welcome-mode')) resetWelcomeViewportScroll();
});

function enterWelcomeMode() {
    document.body.classList.add('welcome-mode');
    resetWelcomeViewportScroll();
    // The calendar exit choreography parks welcome-screen-fade-in at `none` so it
    // cannot restart mid-close; a real entry into Welcome re-arms it.
    window.calendarResetWelcomeIntroState?.();
    setWelcomeActionsPending(true);
    if (els.welcome) els.welcome.style.removeProperty('display');
    if (els.quizContainer) els.quizContainer.style.display = 'none';
    if (els.fcContainer) els.fcContainer.style.display = 'none';
    hideStudyPlannerSurfaces();
    void syncWelcomeScreenState({ progressive: true });
    triggerMemoraSurfaceSettle('welcome', 640);
}

function handleReturnToMainMenuFromTitle() {
    if (document.body.classList.contains('welcome-mode')) return;

    closeMenuDropdown();
    closeQuickActionsDock();
    if (els.sourceDropdown) els.sourceDropdown.classList.remove('active');
    setSearchDropdownOpen(false);
    closeThemeModal();
    if (els.themeConfirmModal) els.themeConfirmModal.classList.remove('active');
    closeAdaptiveRatingModal();
    closeAdaptiveSnapshotModal();

    if (els.popup && els.popup.classList.contains('active')) {
        els.popup.classList.remove('active');
        currentPopupBtn = null;
    }

    if (els.workArea) {
        els.workArea.classList.remove('split-view', 'trio-view', 'split-view-ai');
    }
    if (els.pdfPane) els.pdfPane.style.display = 'none';
    isViewerOpen = false;
    isTrioMode = false;

    const viewerBtnText = $('toggle-viewer-btn')?.querySelector('.quick-control-text');
    if (viewerBtnText) viewerBtnText.innerText = 'Source';
    const trioBtnText = $('toggle-trio-btn')?.querySelector('.gradient-text');
    if (trioBtnText) trioBtnText.innerText = "\u{1F5C2}\uFE0F Trio";

    const hasSessionContent = hasPersistableSessionContent();
    if (!IS_EMBED_DEMO && currentSessionId && hasSessionContent) {
        writeQuickSessionSnapshot();
        setLastSessionId(currentSessionId);
        syncContinueButton(currentSessionId);
    } else {
        syncContinueButton();
    }

    enterWelcomeMode();

    if (!IS_EMBED_DEMO && currentSessionId) {
        void SessionManager.save(false).catch((err) => {
            console.warn('Failed to save session after returning to welcome screen:', err);
        });
    }
}
window.returnToMainMenuFromTitle = () => { void handleReturnToMainMenuFromTitle(); };

function getFilterBannerTextEl() {
    if (!els.filterBanner) return null;
    return $('filter-banner-text') || els.filterBanner.querySelector('span');
}

function setAdaptiveSnapshotButtonVisible(visible) {
    const btn = $('adaptive-snapshot-btn');
    if (!btn) return;
    btn.style.display = visible ? 'inline-flex' : 'none';
}

function setAdaptiveSkipUndoneButtonVisible(visible) {
    const btn = $('adaptive-skip-undone-btn');
    if (!btn) return;
    btn.style.display = visible ? 'inline-flex' : 'none';
}

function resetFilter() {
    highYieldQuizState.baseFilteredQuestionKeys = [];
    highYieldQuizState.baseIsFilterActive = false;
    highYieldLastAppliedPool = null;
    filteredIndices = getDefaultQuestionPoolIndices();
    if (isShuffled) applySmartShuffle({ showNotice: false });
    resetQuizCompletionCohort('eligible-pool-reset');
    isFilterActive = false;
    els.filterBanner.style.display = 'none';
    const bannerText = getFilterBannerTextEl();
    if (bannerText) bannerText.innerHTML = '<b>Study Mode Active:</b> Quizzing only review cards (Again/Hard).';
    setAdaptiveSnapshotButtonVisible(false);
    setAdaptiveSkipUndoneButtonVisible(false);
    if (els.exitFilter) els.exitFilter.textContent = 'Exit Study Mode';
    closeAdaptiveSnapshotModal();
    els.searchInput.value = ''; setSearchDropdownOpen(false);
}

/* --- ADAPTIVE REVIEW / SRS LOGIC --- */
const SRS_DAY_MS = 24 * 60 * 60 * 1000;
const ADAPTIVE_RATING_MINUTES = { again: 10, shaky: 180, good: 1440, easy: 4320 };
const ADAPTIVE_RATING_QUALITY = { again: 1, shaky: 3, good: 4, easy: 5 };

function normalizeKnowledgeStateValue(rawValue) {
    const value = String(rawValue || '').trim().toLowerCase();
    if (!value || value === 'neu') return 'neu';
    if (value === 'new' || value === 'again') return 'again';
    if (value === 'fam' || value === 'hard' || value === 'shaky') return 'shaky';
    if (value === 'good') return 'good';
    if (value === 'mas' || value === 'mastered' || value === 'easy') return 'easy';
    return undefined;
}

function isKnowledgeReviewFocusState(rawValue) {
    const value = normalizeKnowledgeStateValue(rawValue);
    return value === 'again' || value === 'shaky';
}

function isKnowledgeStrongState(rawValue) {
    const value = normalizeKnowledgeStateValue(rawValue);
    return value === 'good' || value === 'easy';
}

function getKnowledgeStateColor(rawValue) {
    const value = normalizeKnowledgeStateValue(rawValue);
    if (value === 'again') return 'var(--st-new)';
    if (value === 'shaky') return 'var(--st-fam)';
    if (value === 'good') return 'var(--st-mas)';
    if (value === 'easy') return 'var(--st-easy, #38bdf8)';
    return 'var(--st-neu)';
}

function normalizeAdaptiveQuestionKeyPart(value) {
    return String(value || '')
        .replace(/<[^>]*>/g, ' ')
        .replace(/&nbsp;/gi, ' ')
        .replace(/&amp;/gi, '&')
        .replace(/&lt;/gi, '<')
        .replace(/&gt;/gi, '>')
        .toLowerCase()
        .replace(/\s+/g, ' ')
        .trim();
}

function buildAdaptiveQuestionKeyDataFromQuestion(questionObj) {
    const q = questionObj;
    if (!q || typeof q !== 'object') return null;

    const stem = normalizeAdaptiveQuestionKeyPart(q.q || q.question || '');
    if (!stem) return null;

    const choices = Array.isArray(q.c) ? q.c : (Array.isArray(q.o) ? q.o : []);
    const ansIdx = Number.isInteger(q.a) ? q.a : -1;
    const answerText = (ansIdx >= 0 && ansIdx < choices.length) ? choices[ansIdx] : '';
    const source = normalizeAdaptiveQuestionKeyPart(q.sourceFile || q.setName || '');
    const answerNorm = normalizeAdaptiveQuestionKeyPart(answerText);

    return { source, stem, answerNorm };
}

function buildAdaptiveQuestionKeyData(idx) {
    return buildAdaptiveQuestionKeyDataFromQuestion(questions[idx]);
}

function getAdaptiveQuestionKeyVariantsFromQuestion(questionObj) {
    const data = buildAdaptiveQuestionKeyDataFromQuestion(questionObj);
    if (!data) return [];

    const variants = new Set();

    if (data.answerNorm) variants.add(`${data.stem}::${data.answerNorm}`);
    variants.add(data.stem);

    if (data.source) {
        if (data.answerNorm) variants.add(`${data.source}::${data.stem}::${data.answerNorm}`);
        variants.add(`${data.source}::${data.stem}`);
    }

    return Array.from(variants);
}

function getAdaptiveQuestionKeyVariantsByIndex(idx) {
    return getAdaptiveQuestionKeyVariantsFromQuestion(questions[idx]);
}

function getAdaptiveQuestionKeyByIndex(idx) {
    const variants = getAdaptiveQuestionKeyVariantsByIndex(idx);
    return variants[0] || '';
}

function saveAdaptiveConfidenceStoreDebounced() {
    if (adaptiveConfidenceSaveTimer) clearTimeout(adaptiveConfidenceSaveTimer);
    adaptiveConfidenceSaveTimer = setTimeout(() => {
        try {
            localStorage.setItem(ADAPTIVE_CONFIDENCE_STORE_KEY, JSON.stringify(adaptiveConfidenceByKey || {}));
        } catch (_) {
            // Ignore storage write errors (quota/private mode)
        }
    }, 150);
}

function getSessionConfidenceForIndex(idx) {
    const data = srsData[idx];
    const confRaw = typeof data?.lastConfidence === 'string' ? data.lastConfidence.toLowerCase() : '';
    if (confRaw === 'again' || confRaw === 'shaky' || confRaw === 'good' || confRaw === 'easy') return confRaw;

    const snap = adaptiveReviewSnapshot?.[idx];
    const snapConf = typeof snap?.confidence === 'string' ? snap.confidence.toLowerCase() : '';
    if (snapConf === 'again' || snapConf === 'shaky' || snapConf === 'good' || snapConf === 'easy') return snapConf;

    return '';
}

function rememberAdaptiveConfidenceForIndex(idx, confidence, reviewedAt = Date.now()) {
    const confRaw = typeof confidence === 'string' ? confidence.toLowerCase() : '';
    if (confRaw !== 'again' && confRaw !== 'shaky' && confRaw !== 'good' && confRaw !== 'easy') return;

    const keys = getAdaptiveQuestionKeyVariantsByIndex(idx);
    if (!keys.length) return;

    const safeReviewedAt = Number.isFinite(reviewedAt) ? reviewedAt : Date.now();
    keys.forEach((key) => {
        const existing = adaptiveConfidenceByKey[key] || {};
        adaptiveConfidenceByKey[key] = {
            confidence: confRaw,
            reviewedAt: Number(existing.reviewedAt) || safeReviewedAt
        };
    });
    saveAdaptiveConfidenceStoreDebounced();
}

function hasGlobalAdaptiveConfidenceForIndex(idx) {
    const keys = getAdaptiveQuestionKeyVariantsByIndex(idx);
    if (!keys.length) return false;

    return keys.some((key) => {
        const entry = adaptiveConfidenceByKey?.[key];
        const confRaw = typeof entry?.confidence === 'string' ? entry.confidence.toLowerCase() : '';
        return confRaw === 'again' || confRaw === 'shaky' || confRaw === 'good' || confRaw === 'easy';
    });
}

function backfillAdaptiveConfidenceStoreFromSessionData() {
    if (!Array.isArray(questions) || questions.length === 0) return;

    let changed = false;
    for (let idx = 0; idx < questions.length; idx++) {
        const conf = getSessionConfidenceForIndex(idx);
        if (!conf) continue;

        const keys = getAdaptiveQuestionKeyVariantsByIndex(idx);
        if (!keys.length) continue;

        const hasExisting = keys.some((key) => {
            const existingConf = typeof adaptiveConfidenceByKey?.[key]?.confidence === 'string'
                ? adaptiveConfidenceByKey[key].confidence.toLowerCase()
                : '';
            return existingConf === conf;
        });
        if (hasExisting) continue;

        const reviewedAt = Number(srsData[idx]?.lastReviewed) || Number(adaptiveReviewSnapshot?.[idx]?.lastRatedAt) || Date.now();
        rememberAdaptiveConfidenceForIndex(idx, conf, reviewedAt);
        changed = true;
    }

    if (changed) saveAdaptiveConfidenceStoreDebounced();
}
async function hydrateAdaptiveConfidenceStoreFromSessionHistory() {
    if (adaptiveConfidenceHydrated) return;
    if (adaptiveConfidenceHydrationInFlight) return adaptiveConfidenceHydrationInFlight;

    adaptiveConfidenceHydrationInFlight = (async () => {
        try {
            const sessions = await readSessionStoreAllWithTimeout('sessions', 3000);
            if (!Array.isArray(sessions) || sessions.length === 0) return;

            let changed = false;

            for (const session of sessions) {
                const data = session?.data;
                if (!data || typeof data !== 'object') continue;

                const sessionSrs = data.srsData;
                if (!sessionSrs || typeof sessionSrs !== 'object') continue;

                const manifestJsons = Array.isArray(data?.manifest?.jsons)
                    ? data.manifest.jsons
                    : (Array.isArray(session?.activeSets) ? session.activeSets : []);
                if (!manifestJsons.length) continue;

                let combined = [];
                for (const jsonName of manifestJsons) {
                    if (typeof jsonName !== 'string' || !jsonName.trim()) continue;
                    let qSet = await readSessionStoreValueWithTimeout('questions', jsonName, 3000);
                    if (!Array.isArray(qSet)) {
                        const stripped = jsonName.replace(/\.json$/i, '').replace(/\.txt$/i, '');
                        if (stripped !== jsonName) {
                            qSet = await readSessionStoreValueWithTimeout('questions', stripped, 3000);
                        }
                    }
                    if (Array.isArray(qSet) && qSet.length) combined = combined.concat(qSet);
                }
                if (!combined.length) continue;

                for (const [idxKey, rec] of Object.entries(sessionSrs)) {
                    const idx = Number(idxKey);
                    if (!Number.isInteger(idx) || idx < 0 || idx >= combined.length) continue;

                    let confRaw = typeof rec?.lastConfidence === 'string' ? rec.lastConfidence.toLowerCase() : '';
                    if (confRaw !== 'again' && confRaw !== 'shaky' && confRaw !== 'good' && confRaw !== 'easy') {
                        const q = Number(rec?.lastQuality);
                        if (Number.isFinite(q)) {
                            if (q <= 2) confRaw = 'again';
                            else if (q === 3) confRaw = 'shaky';
                            else if (q === 4) confRaw = 'good';
                            else if (q >= 5) confRaw = 'easy';
                        }
                    }
                    if (confRaw !== 'again' && confRaw !== 'shaky' && confRaw !== 'good' && confRaw !== 'easy') continue;

                    const keys = getAdaptiveQuestionKeyVariantsFromQuestion(combined[idx]);
                    if (!keys.length) continue;

                    const reviewedAt = Number(rec?.lastReviewed) || Number(rec?.nextReview) || Date.now();
                    let updated = false;
                    keys.forEach((key) => {
                        const existingConf = typeof adaptiveConfidenceByKey?.[key]?.confidence === 'string'
                            ? adaptiveConfidenceByKey[key].confidence.toLowerCase()
                            : '';
                        if (existingConf) return;
                        adaptiveConfidenceByKey[key] = { confidence: confRaw, reviewedAt };
                        updated = true;
                    });
                    if (updated) changed = true;
                }
            }

            if (changed) saveAdaptiveConfidenceStoreDebounced();
        } catch (err) {
            console.warn('Adaptive confidence history hydration failed:', err);
        } finally {
            adaptiveConfidenceHydrated = true;
            adaptiveConfidenceHydrationInFlight = null;
        }
    })();

    return adaptiveConfidenceHydrationInFlight;
}
function ensureSRSRecord(idx) {
    const existing = srsData[idx] || {};
    const normalized = {
        interval: Number.isFinite(existing.interval) ? existing.interval : 0,
        repetitions: Number.isFinite(existing.repetitions) ? existing.repetitions : 0,
        ef: Number.isFinite(existing.ef) ? existing.ef : 2.5,
        nextReview: Number.isFinite(existing.nextReview) ? existing.nextReview : 0,
        lapses: Number.isFinite(existing.lapses) ? existing.lapses : 0,
        lastQuality: Number.isFinite(existing.lastQuality) ? existing.lastQuality : null,
        lastConfidence: typeof existing.lastConfidence === 'string' ? existing.lastConfidence : null,
        lastReviewed: Number.isFinite(existing.lastReviewed) ? existing.lastReviewed : 0
    };

    if (normalized.ef < 1.3) normalized.ef = 1.3;
    if (normalized.ef > 2.8) normalized.ef = 2.8;
    if (normalized.interval < 0) normalized.interval = 0;
    if (normalized.repetitions < 0) normalized.repetitions = 0;
    srsData[idx] = normalized;
    return normalized;
}

function updateSRS(idx, isCorrect) {
    const now = Date.now();
    const data = ensureSRSRecord(idx);
    const q = isCorrect ? 5 : 2;

    if (q >= 3) {
        if (data.repetitions === 0) data.interval = 1;
        else if (data.repetitions === 1) data.interval = 6;
        else data.interval = Math.max(1, Math.round(data.interval * data.ef));
        data.repetitions += 1;
    } else {
        data.repetitions = 0;
        data.interval = 1;
        data.lapses += 1;
    }

    data.ef = Math.max(1.3, Math.min(2.8, data.ef + (0.1 - (5 - q) * (0.08 + (5 - q) * 0.02))));
    data.lastQuality = q;
    data.lastConfidence = isCorrect ? 'good' : 'again';
    data.lastReviewed = now;
    data.nextReview = clampReviewToUpcomingExam(now + (data.interval * SRS_DAY_MS), now);
    srsData[idx] = data;
}

// An interval that would carry a card past its session's next exam is shortened so the
// card is still seen before that exam (app.review-schedule.js owns the exam lookup).
function clampReviewToUpcomingExam(nextReview, now = Date.now()) {
    const clamped = window.MemoraReviewSchedule?.clampNextReview?.(nextReview, now, currentSessionId);
    return Number.isFinite(clamped) ? clamped : nextReview;
}

function updateAdaptiveSRS(idx, confidence, isCorrect) {
    const now = Date.now();
    const data = ensureSRSRecord(idx);
    let rating = ADAPTIVE_RATING_QUALITY[confidence] ? confidence : 'shaky';
    if (!isCorrect && (rating === 'good' || rating === 'easy')) rating = 'shaky';

    const quality = ADAPTIVE_RATING_QUALITY[rating];
    const minDelayMs = (ADAPTIVE_RATING_MINUTES[rating] || ADAPTIVE_RATING_MINUTES.shaky) * 60 * 1000;

    if (rating === 'again') {
        data.repetitions = 0;
        data.interval = ADAPTIVE_RATING_MINUTES.again / (24 * 60);
        data.ef = Math.max(1.3, data.ef - 0.2);
        data.lapses += 1;
    } else {
        if (data.repetitions === 0) {
            data.interval = rating === 'shaky' ? 0.5 : (rating === 'good' ? 1 : 3);
        } else if (data.repetitions === 1) {
            data.interval = rating === 'shaky' ? 1 : (rating === 'good' ? 3 : 6);
        } else {
            const confidenceBoost = rating === 'easy' ? 1.35 : (rating === 'good' ? 1.12 : 0.9);
            data.interval = Math.max(0.5, data.interval * data.ef * confidenceBoost);
        }
        data.repetitions += 1;
        data.ef = Math.max(1.3, Math.min(2.8, data.ef + (0.1 - (5 - quality) * (0.08 + (5 - quality) * 0.02))));
    }

    data.lastQuality = quality;
    data.lastConfidence = rating;
    data.lastReviewed = now;
    data.nextReview = clampReviewToUpcomingExam(now + Math.max(minDelayMs, data.interval * SRS_DAY_MS), now);
    srsData[idx] = data;
    rememberAdaptiveConfidenceForIndex(idx, rating, now);
}



function getAdaptiveRank(confidence) {
    return ADAPTIVE_CONFIDENCE_TO_RANK[confidence] || ADAPTIVE_CONFIDENCE_TO_RANK.shaky;
}

function hasAdaptiveConfidenceRating(idx) {
    if (hasGlobalAdaptiveConfidenceForIndex(idx)) return true;

    const conf = getSessionConfidenceForIndex(idx);
    if (conf) {
        const reviewedAt = Number(srsData[idx]?.lastReviewed) || Number(adaptiveReviewSnapshot?.[idx]?.lastRatedAt) || Date.now();
        rememberAdaptiveConfidenceForIndex(idx, conf, reviewedAt);
        return true;
    }

    const data = srsData[idx];
    if (!data || typeof data !== 'object') return false;

    // Legacy/back-compat: older sessions may not have lastConfidence but still indicate prior review history.
    const lastReviewed = Number(data.lastReviewed) || 0;
    const lastQuality = Number(data.lastQuality);
    const repetitions = Number(data.repetitions) || 0;
    const lapses = Number(data.lapses) || 0;
    const interval = Number(data.interval) || 0;
    const nextReview = Number(data.nextReview) || 0;

    return lastReviewed > 0 || Number.isFinite(lastQuality) || repetitions > 0 || lapses > 0 || interval > 0 || nextReview > 0;
}
function getAdaptiveConfidenceLabel(confidence) {
    const labels = {
        again: 'Again',
        shaky: 'Hard',
        good: 'Good',
        easy: 'Easy'
    };
    return labels[confidence] || 'Hard';
}



function normalizeAdaptiveSelectedIdx(rawIdx, choicesLength = null) {
    const parsed = Number(rawIdx);
    if (!Number.isInteger(parsed) || parsed < 0) return null;
    if (Number.isFinite(choicesLength) && parsed >= choicesLength) return null;
    return parsed;
}

function recordAdaptiveSnapshotEntry(realIdx, confidence, isCorrect, selectedIdxOverride = null) {
    if (!Number.isInteger(realIdx)) return;
    const now = Date.now();
    const rating = ADAPTIVE_RATING_QUALITY[confidence] ? confidence : 'shaky';
    const rank = getAdaptiveRank(rating);
    const q = questions[realIdx] || {};
    const existing = adaptiveReviewSnapshot[realIdx] || { attempts: 0, firstRatedAt: now };
    const rawSelectedIdx = selectedIdxOverride !== null ? selectedIdxOverride : userAnswers[realIdx];
    const choiceList = Array.isArray(q.c) ? q.c : (Array.isArray(q.o) ? q.o : []);
    const selectedIdx = normalizeAdaptiveSelectedIdx(rawSelectedIdx, choiceList.length);
    const correctIdx = normalizeAdaptiveSelectedIdx(q.a, choiceList.length);
    const selectedAnswer = selectedIdx !== null ? (choiceList[selectedIdx] || '') : '';
    const correctAnswer = correctIdx !== null ? (choiceList[correctIdx] || '') : '';
    const derivedCorrect = (selectedIdx !== null && correctIdx !== null) ? (selectedIdx === correctIdx) : !!isCorrect;

    adaptiveReviewSnapshot[realIdx] = {
        ...existing,
        idx: realIdx,
        rank,
        confidence: rating,
        isCorrect: derivedCorrect,
        attempts: Number(existing.attempts || 0) + 1,
        selectedIdx,
        correctIdx,
        selectedAnswer,
        correctAnswer,
        sourceFile: q.sourceFile || existing.sourceFile || 'Unknown',
        firstRatedAt: Number(existing.firstRatedAt) || now,
        lastRatedAt: now,
        snapshotVersion: 2
    };
}

function getAdaptiveSnapshotEntries() {
    const snapshotEntries = Object.values(adaptiveReviewSnapshot || {})
        .filter(entry => entry && Number.isInteger(Number(entry.idx)));

    const historicalEntries = Object.entries(srsData || {})
        .filter(([k, data]) => {
            if (!Number.isInteger(Number(k)) || !data || typeof data !== 'object') return false;
            const reviewedAt = Number(data.lastReviewed) || 0;
            const nextReviewAt = Number(data.nextReview) || 0;
            const reps = Math.max(0, Number(data.repetitions) || 0);
            const lapses = Math.max(0, Number(data.lapses) || 0);
            const qualityRaw = Number(data.lastQuality);
            const confRaw = typeof data.lastConfidence === 'string' ? data.lastConfidence.toLowerCase() : '';
            return reviewedAt > 0 || nextReviewAt > 0 || reps > 0 || lapses > 0 || Number.isFinite(qualityRaw) || !!confRaw;
        })
        .map(([k, data]) => {
            const idx = Number(k);
            const qualityRaw = Number(data.lastQuality);
            const quality = Number.isFinite(qualityRaw) ? qualityRaw : null;
            const confRaw = typeof data.lastConfidence === 'string' ? data.lastConfidence.toLowerCase() : '';
            const conf = ADAPTIVE_RATING_QUALITY[confRaw]
                ? confRaw
                : (quality !== null ? (quality >= 4 ? 'good' : (quality <= 2 ? 'again' : 'shaky')) : 'shaky');
            const rank = getAdaptiveRank(conf);
            const lastAt = Number(data.lastReviewed) || Number(data.nextReview) || 0;
            return {
                idx,
                rank,
                confidence: conf,
                isCorrect: quality !== null ? quality >= 4 : (conf === 'good' || conf === 'easy'),
                attempts: Math.max(1, Number(data.repetitions || 0) + Number(data.lapses || 0)),
                selectedIdx: null,
                sourceFile: (questions[idx] && questions[idx].sourceFile) || 'Unknown',
                firstRatedAt: lastAt,
                lastRatedAt: lastAt
            };
        });

    const sourceByIdx = new Map();
    snapshotEntries.forEach((entry) => sourceByIdx.set(Number(entry.idx), entry));
    historicalEntries.forEach((entry) => {
        if (!sourceByIdx.has(entry.idx)) sourceByIdx.set(entry.idx, entry);
    });

    let sourceEntries = Array.from(sourceByIdx.values());

    // Final fallback for very old sessions: derive from question stats.
    if (sourceEntries.length === 0) {
        sourceEntries = Object.entries(statsData || {})
            .filter(([k, attempts]) => Number.isInteger(Number(k)) && Array.isArray(attempts) && attempts.length > 0)
            .map(([k, attempts]) => {
                const idx = Number(k);
                const last = attempts[attempts.length - 1] || {};
                const isCorrect = !!last.correct;
                const confidence = isCorrect ? 'good' : 'again';
                const lastAt = Number(last.date) || 0;
                return {
                    idx,
                    rank: getAdaptiveRank(confidence),
                    confidence,
                    isCorrect,
                    attempts: attempts.length,
                    selectedIdx: null,
                    sourceFile: (questions[idx] && questions[idx].sourceFile) || 'Unknown',
                    firstRatedAt: Number(attempts[0]?.date) || lastAt,
                    lastRatedAt: lastAt
                };
            });
    }

    return sourceEntries
        .map((entry) => {
            const idx = Number(entry.idx);
            const q = questions[idx] || {};
            const confidence = ADAPTIVE_RATING_QUALITY[entry.confidence] ? entry.confidence : 'shaky';
            const fallbackRank = getAdaptiveRank(confidence);
            const rankVal = Number(entry.rank);
            const rank = Number.isFinite(rankVal) && rankVal >= 1 && rankVal <= 4 ? rankVal : fallbackRank;
            const choices = Array.isArray(q.c) ? q.c : (Array.isArray(q.o) ? q.o : []);
            const entryVersion = Number(entry.snapshotVersion) || 1;
            const selectedIdx = normalizeAdaptiveSelectedIdx(entryVersion >= 2 ? entry.selectedIdx : null, choices.length);
            const storedCorrectIdx = normalizeAdaptiveSelectedIdx(entryVersion >= 2 ? entry.correctIdx : null, choices.length);
            const fallbackCorrectIdx = normalizeAdaptiveSelectedIdx(q.a, choices.length);
            const resolvedCorrectIdx = storedCorrectIdx !== null ? storedCorrectIdx : fallbackCorrectIdx;
            const selectedAnswerFromIdx = selectedIdx !== null ? (choices[selectedIdx] || '') : '';
            const selectedAnswer = selectedAnswerFromIdx || (typeof entry.selectedAnswer === 'string' ? entry.selectedAnswer : '');
            const correctAnswerFromIdx = resolvedCorrectIdx !== null ? (choices[resolvedCorrectIdx] || '') : '';
            const correctAnswer = correctAnswerFromIdx || (typeof entry.correctAnswer === 'string' ? entry.correctAnswer : '');
            const hasReliableChoiceData = selectedIdx !== null && resolvedCorrectIdx !== null;
            const displayIsCorrect = hasReliableChoiceData ? (selectedIdx === resolvedCorrectIdx) : !!entry.isCorrect;
            return {
                idx,
                confidence,
                rank,
                isCorrect: displayIsCorrect,
                attempts: Math.max(1, Number(entry.attempts) || 1),
                sourceFile: entry.sourceFile || q.sourceFile || 'Unknown',
                questionText: q.q || '',
                selectedAnswer,
                correctAnswer,
                firstRatedAt: Number(entry.firstRatedAt) || 0,
                lastRatedAt: Number(entry.lastRatedAt) || 0
            };
        })
        .sort((a, b) => (b.lastRatedAt || 0) - (a.lastRatedAt || 0));
}

function updateAdaptiveRankBadge() {
    const badge = $('adaptive-rank-count-badge');
    if (!badge) return;
    badge.innerText = adaptiveSnapshotRankFilters.size === 0 ? '(All)' : `(${adaptiveSnapshotRankFilters.size})`;
}

function renderAdaptiveRankDropdown() {
    const dropdown = $('adaptive-rank-dropdown');
    if (!dropdown) return;

    dropdown.innerHTML = '';

    const allItem = document.createElement('div');
    allItem.className = 'source-filter-item' + (adaptiveSnapshotRankFilters.size === 0 ? ' selected' : '');
    allItem.innerHTML = `<div class="check">${adaptiveSnapshotRankFilters.size === 0 ? '&#10003;' : ''}</div><span>All Ranks</span>`;
    allItem.onclick = (e) => {
        e.stopPropagation();
        adaptiveSnapshotRankFilters.clear();
    renderAdaptiveRankDropdown();
        renderAdaptiveRankChips();
        renderAdaptiveSnapshotList();
    };
    dropdown.appendChild(allItem);

    [1, 2, 3, 4].forEach((rank) => {
        const selected = adaptiveSnapshotRankFilters.has(rank);
        const item = document.createElement('div');
        item.className = 'source-filter-item' + (selected ? ' selected' : '');
        item.innerHTML = `<div class="check">${selected ? '&#10003;' : ''}</div><span>${ADAPTIVE_RANK_LABELS[rank] || `Rank ${rank}`}</span>`;
        item.onclick = (e) => {
            e.stopPropagation();
            if (selected) adaptiveSnapshotRankFilters.delete(rank);
            else adaptiveSnapshotRankFilters.add(rank);
            if (adaptiveSnapshotRankFilters.size === 4) adaptiveSnapshotRankFilters.clear();
    renderAdaptiveRankDropdown();
            renderAdaptiveRankChips();
            renderAdaptiveSnapshotList();
        };
        dropdown.appendChild(item);
    });

    updateAdaptiveRankBadge();
}

function renderAdaptiveRankChips() {
    const container = $('adaptive-rank-chips');
    if (!container) return;

    container.innerHTML = '';
    if (adaptiveSnapshotRankFilters.size === 0) return;

    Array.from(adaptiveSnapshotRankFilters)
        .filter((rank) => Number.isFinite(rank))
        .sort((a, b) => a - b)
        .forEach((rank) => {
            const chip = document.createElement('div');
            chip.className = 'source-chip';
            chip.innerHTML = `${escapeHtml(ADAPTIVE_RANK_LABELS[rank] || `Rank ${rank}`)} <span class="remove" data-rank="${rank}">&times;</span>`;
            const removeBtn = chip.querySelector('.remove');
            if (removeBtn) {
                removeBtn.onclick = (e) => {
                    e.stopPropagation();
                    removeAdaptiveRankFilter(rank);
                };
            }
            container.appendChild(chip);
        });
}

function renderAdaptiveSnapshotList() {
    const listEl = $('adaptive-snapshot-list');
    if (!listEl) return;

    const entries = getAdaptiveSnapshotEntries();
    const activeRanks = adaptiveSnapshotRankFilters.size > 0 ? adaptiveSnapshotRankFilters : null;
    const filtered = activeRanks ? entries.filter(entry => activeRanks.has(entry.rank)) : entries;

    if (filtered.length === 0) {
        listEl.innerHTML = '<div style="color:#94a3b8; text-align:center; padding:20px;">No adaptive ratings found for this filter.</div>';
        updateAdaptiveRankBadge();
        return;
    }

    const groups = { 1: [], 2: [], 3: [], 4: [] };
    filtered.forEach((entry) => {
        if (!groups[entry.rank]) groups[entry.rank] = [];
        groups[entry.rank].push(entry);
    });

    listEl.innerHTML = '';
    [1, 2, 3, 4].forEach((rank) => {
        const rankEntries = groups[rank] || [];
        if (rankEntries.length === 0) return;

        const groupEl = document.createElement('div');
        groupEl.className = 'adaptive-rank-group';
        const isCollapsed = adaptiveSnapshotCollapsedRanks.has(rank);
        groupEl.classList.toggle('collapsed', isCollapsed);

        const heading = document.createElement('button');
        heading.type = 'button';
        heading.className = `adaptive-rank-heading rank-${rank}`;
        heading.setAttribute('aria-expanded', isCollapsed ? 'false' : 'true');
        heading.innerHTML = `
            <span class="adaptive-rank-title">
                <span class="adaptive-rank-chevron">&#9662;</span>
                <span>${escapeHtml(ADAPTIVE_RANK_LABELS[rank] || `Rank ${rank}`)}</span>
            </span>
            <span class="adaptive-rank-count">${rankEntries.length} card${rankEntries.length === 1 ? '' : 's'}</span>
        `;
        heading.onclick = () => {
            if (adaptiveSnapshotCollapsedRanks.has(rank)) adaptiveSnapshotCollapsedRanks.delete(rank);
            else adaptiveSnapshotCollapsedRanks.add(rank);
            renderAdaptiveSnapshotList();
        };
        groupEl.appendChild(heading);

        const body = document.createElement('div');
        body.className = 'adaptive-rank-body';
        groupEl.appendChild(body);

        rankEntries.forEach((entry) => {
            const entryEl = document.createElement('div');
            entryEl.className = 'saved-item adaptive-entry';
            const statusIcon = entry.isCorrect ? '&#10003;' : '&#10005;';
            const statusLabel = entry.isCorrect ? 'Correct' : 'Incorrect';
            const confidenceLabel = getAdaptiveConfidenceLabel(entry.confidence);
            const questionText = escapeHtml(entry.questionText || 'Question unavailable');
            const correctAnswer = escapeHtml(entry.correctAnswer || 'N/A');
            const selectedAnswer = escapeHtml(entry.selectedAnswer || 'No answer recorded');
            const sourceText = escapeHtml(entry.sourceFile || 'Unknown');
            const timeText = escapeHtml(entry.lastRatedAt ? new Date(entry.lastRatedAt).toLocaleString() : 'Unknown');

            entryEl.innerHTML = `
                <div class="saved-q">Q${entry.idx + 1}: ${questionText}</div>
                <div class="saved-a ${entry.isCorrect ? 'adaptive-correct' : 'adaptive-incorrect'}">${statusIcon} ${statusLabel} - ${escapeHtml(confidenceLabel)}</div>
                <div class="adaptive-entry-meta">
                    <span>Correct: ${correctAnswer}</span>
                    <span>Your pick: ${selectedAnswer}</span>
                    <span>Attempts: ${entry.attempts}</span>
                    <span>Source: ${sourceText}</span>
                    <span>Last rated: ${timeText}</span>
                </div>
                <span class="adaptive-rank-pill rank-${entry.rank}">R${entry.rank}</span>
            `;
            body.appendChild(entryEl);
        });

        listEl.appendChild(groupEl);
    });

    updateAdaptiveRankBadge();
}

function removeAdaptiveRankFilter(rank) {
    const value = Number(rank);
    if (!Number.isFinite(value)) return;
    adaptiveSnapshotRankFilters.delete(value);
    renderAdaptiveRankDropdown();
    renderAdaptiveRankChips();
    renderAdaptiveSnapshotList();
}

function toggleAdaptiveRankDropdown(event) {
    if (event && typeof event.stopPropagation === 'function') event.stopPropagation();
    const dropdown = $('adaptive-rank-dropdown');
    if (!dropdown) return;
    dropdown.classList.toggle('active');
}

function openAdaptiveSnapshotModal() {
    const modal = $('adaptive-snapshot-modal');
    if (!modal) return;
    renderAdaptiveRankDropdown();
    renderAdaptiveRankChips();
    renderAdaptiveSnapshotList();
    modal.classList.add('active');
}

function closeAdaptiveSnapshotModal(event) {
    const modal = $('adaptive-snapshot-modal');
    if (!modal) return;
    modal.classList.remove('active');
    const dropdown = $('adaptive-rank-dropdown');
    if (dropdown) dropdown.classList.remove('active');
}

window.openAdaptiveSnapshotModal = openAdaptiveSnapshotModal;
window.closeAdaptiveSnapshotModal = closeAdaptiveSnapshotModal;
window.toggleAdaptiveRankDropdown = toggleAdaptiveRankDropdown;
window.removeAdaptiveRankFilter = removeAdaptiveRankFilter;

function getAdaptivePriorityScore(idx, now = Date.now()) {
    const data = srsData[idx];
    if (!data) return Number.POSITIVE_INFINITY;

    const nextReview = Number.isFinite(data.nextReview) ? data.nextReview : 0;
    const overdueDays = (now - nextReview) / SRS_DAY_MS;
    const qualityPenalty = (5 - (Number.isFinite(data.lastQuality) ? data.lastQuality : 3)) * 0.4;
    const lapseWeight = (Number.isFinite(data.lapses) ? data.lapses : 0) * 0.25;
    const repetitionWeight = (Number.isFinite(data.repetitions) ? data.repetitions : 0) * 0.08;
    return overdueDays + qualityPenalty + lapseWeight - repetitionWeight;
}

// Today's review (app.review-schedule.js) opens Spaced Review over cards the learner has
// already seen; a pass started any other way also takes in unseen cards. The setting
// belongs to the running pass, because the queue is rebuilt after every rating.
let adaptiveReviewSeenOnly = false;

function hasAdaptiveReviewHistory(data) {
    if (!data || typeof data !== 'object') return false;
    return (Number(data.lastReviewed) || 0) > 0
        || (Number(data.repetitions) || 0) > 0
        || (Number(data.lapses) || 0) > 0;
}

function buildAdaptiveReviewQueue(now = Date.now(), { seenOnly = adaptiveReviewSeenOnly } = {}) {
    const schedule = window.MemoraReviewSchedule;
    const due = [];
    questions.forEach((_, idx) => {
        const data = srsData[idx];
        if (seenOnly && !hasAdaptiveReviewHistory(data)) return;
        if (!data || !Number.isFinite(data.nextReview) || data.nextReview <= now) {
            due.push(idx);
            return;
        }
        // In the last days before an exam, every shaky or once-missed card comes back.
        if (schedule?.isExamCrunchDue?.(data, now)) due.push(idx);
    });

    due.sort((a, b) => {
        const pa = getAdaptivePriorityScore(a, now);
        const pb = getAdaptivePriorityScore(b, now);
        if (pb !== pa) return pb - pa;
        return a - b;
    });
    return interleaveAdaptiveQueueBySource(due);
}

// Mixing lectures makes each card a choice between concepts rather than a run of one
// topic. Within that, the most urgent card still comes first: each step takes the
// highest-priority card whose lecture differs from the last one, while another exists.
function interleaveAdaptiveQueueBySource(sortedIndices) {
    const sourceOf = (idx) => String(questions[idx]?.sourceFile || questions[idx]?.setName || '');
    const lanes = new Map();
    sortedIndices.forEach((idx) => {
        const key = sourceOf(idx);
        if (!lanes.has(key)) lanes.set(key, []);
        lanes.get(key).push(idx);
    });
    if (lanes.size < 2) return sortedIndices;
    const rank = new Map(sortedIndices.map((idx, position) => [idx, position]));
    const queues = Array.from(lanes, ([key, list]) => ({ key, list, head: 0 }));
    const ordered = [];
    let lastKey = null;
    while (ordered.length < sortedIndices.length) {
        const open = queues.filter((queue) => queue.head < queue.list.length);
        const candidates = open.length > 1 ? open.filter((queue) => queue.key !== lastKey) : open;
        let pick = candidates[0];
        candidates.forEach((queue) => {
            if (rank.get(queue.list[queue.head]) < rank.get(pick.list[pick.head])) pick = queue;
        });
        ordered.push(pick.list[pick.head]);
        pick.head += 1;
        lastKey = pick.key;
    }
    return ordered;
}

function getSrsRatingContentEl() {
    if (!els.srsRatingModal) return null;
    return els.srsRatingModal.querySelector('.srs-rating-content');
}

function getSrsRatingModalBounds(content) {
    const inset = 8;
    const workspace = document.body.classList.contains('memora-live-left-rail-preview')
        ? els.workArea?.getBoundingClientRect() : null;
    // The rail overlays viewport space; that space is not an available popup gutter.
    const left = workspace?.width > 0 ? Math.max(inset, workspace.left + inset) : inset;
    const right = window.innerWidth - inset;
    const bottom = window.innerHeight - inset;
    const availableWidth = Math.max(0, right - left);
    const width = content?.offsetWidth || Math.min(560, availableWidth);
    const height = content?.offsetHeight || 0;
    return {
        left, right, top: inset, bottom, availableWidth, width, height,
        maxLeft: Math.max(left, right - width),
        maxTop: Math.max(inset, bottom - height)
    };
}

function isSoloQuizLayoutForSrsRating() {
    const workArea = els.workArea;
    if (!workArea) return false;
    if (document.body.classList.contains('welcome-mode')) return false;
    return !workArea.classList.contains('split-view')
        && !workArea.classList.contains('split-view-ai')
        && !workArea.classList.contains('trio-view');
}

function isDuoQuizLayoutForSrsRating() {
    const workArea = els.workArea;
    if (!workArea) return false;
    if (document.body.classList.contains('welcome-mode')) return false;
    return !workArea.classList.contains('trio-view')
        && (workArea.classList.contains('split-view') || workArea.classList.contains('split-view-ai'));
}

function applySrsRatingModalResponsiveWidth(content) {
    if (!content) return;
    if (content.classList.contains('srs-rating-docked')) return;
    const bounds = getSrsRatingModalBounds(content);
    const preferredWidth = isSoloQuizLayoutForSrsRating() ? 392 : 560;
    content.style.width = `${Math.min(preferredWidth, bounds.availableWidth)}px`;
}

function getSoloSrsRatingModalPlacement(content) {
    if (!content || !isSoloQuizLayoutForSrsRating()) return null;

    const quizCard = els.quizCard;
    if (!quizCard) return null;

    const rect = quizCard.getBoundingClientRect();
    const bounds = getSrsRatingModalBounds(content);
    const { width, maxLeft, maxTop } = bounds;
    const gap = 22;
    const alignedTop = Math.min(Math.max(Math.round(rect.top + 18), bounds.top), maxTop);
    const roomRight = bounds.right - rect.right;
    const roomLeft = rect.left - bounds.left;

    if (roomRight >= width + gap) {
        return {
            left: Math.min(Math.max(Math.round(rect.right + gap), bounds.left), maxLeft),
            top: alignedTop
        };
    }

    if (roomLeft >= width + gap) {
        return {
            left: Math.min(Math.max(Math.round(rect.left - width - gap), bounds.left), maxLeft),
            top: alignedTop
        };
    }

    return null;
}

// No gutter beside the card: dock a compact 1-4 bar under the choices, above Previous/Next,
// so the stem, the choices and the top-right badges all stay clear.
function getDockedSrsRatingModalPlacement(content) {
    if (!content || !(isSoloQuizLayoutForSrsRating() || isDuoQuizLayoutForSrsRating())) return null;
    const quizCard = els.quizCard;
    if (!quizCard) return null;
    const rect = quizCard.getBoundingClientRect();
    if (rect.width < 520 || rect.bottom <= 0 || rect.top >= window.innerHeight) return null;

    content.classList.add('srs-rating-docked');
    const inset = 24;
    content.style.width = `${Math.round(Math.min(760, rect.width - inset * 2))}px`;
    const bounds = getSrsRatingModalBounds(content);
    const { width, height, maxLeft, maxTop } = bounds;

    const visibleBottom = (el) => {
        const box = el?.offsetParent ? el.getBoundingClientRect() : null;
        return box && box.height > 0 ? box.bottom : 0;
    };
    const nextBtn = document.getElementById('next-btn');
    const nextBox = nextBtn?.offsetParent ? nextBtn.getBoundingClientRect() : null;
    const anchorBottom = nextBox && nextBox.top > rect.top && nextBox.top < window.innerHeight
        ? nextBox.top - 14
        : Math.min(rect.bottom - 16, bounds.bottom);
    const choices = [...quizCard.querySelectorAll('button.choice')];
    const floor = Math.max(...choices.map(visibleBottom), visibleBottom(document.getElementById('explanation')), 0) + 12;
    const top = Math.max(floor, anchorBottom - height);
    return {
        left: Math.min(Math.max(Math.round(rect.left + (rect.width - width) / 2), bounds.left), maxLeft),
        top: Math.min(Math.max(Math.round(top), bounds.top), maxTop)
    };
}

function getDuoSrsRatingModalPlacement(content) {
    if (!content || !isDuoQuizLayoutForSrsRating()) return null;

    const quizCard = els.quizCard;
    if (!quizCard) return null;

    const rect = quizCard.getBoundingClientRect();
    const bounds = getSrsRatingModalBounds(content);
    const { width, maxLeft, maxTop } = bounds;
    const gap = 28;
    const alignedTop = Math.min(Math.max(Math.round(rect.top + 18), bounds.top), maxTop);
    const roomRight = bounds.right - rect.right;

    if (roomRight >= width + gap) {
        const remainingSlack = Math.max(0, roomRight - width - gap);
        const extraShift = Math.min(220, Math.round(remainingSlack * 0.78));
        return {
            left: Math.min(Math.max(Math.round(rect.right + gap + extraShift), bounds.left), maxLeft),
            top: alignedTop
        };
    }

    return null;
}

function centerSrsRatingModal(force = false) {
    const content = getSrsRatingContentEl();
    if (!content) return;

    if (!force && content.dataset.userMoved === '1') return;

    content.classList.remove('srs-rating-docked');
    applySrsRatingModalResponsiveWidth(content);
    const preferredPlacement = getSoloSrsRatingModalPlacement(content) || getDuoSrsRatingModalPlacement(content)
        || getDockedSrsRatingModalPlacement(content);
    const bounds = getSrsRatingModalBounds(content);
    const { width, height } = bounds;
    const left = preferredPlacement?.left ?? Math.max(bounds.left, Math.min(bounds.maxLeft, Math.round((bounds.left + bounds.right - width) / 2)));
    const top = preferredPlacement?.top ?? Math.max(bounds.top, Math.min(bounds.maxTop, Math.round((bounds.top + bounds.bottom - height) / 2)));

    content.style.left = `${left}px`;
    content.style.top = `${top}px`;
    content.style.transform = 'none';
    content.style.margin = '0';
}

function clampSrsRatingModalToViewport() {
    const content = getSrsRatingContentEl();
    if (!content) return;

    applySrsRatingModalResponsiveWidth(content);
    const bounds = getSrsRatingModalBounds(content);
    const { maxLeft, maxTop } = bounds;

    const left = Number.parseFloat(content.style.left);
    const top = Number.parseFloat(content.style.top);
    const safeLeft = Number.isFinite(left) ? Math.min(Math.max(left, bounds.left), maxLeft) : bounds.left;
    const safeTop = Number.isFinite(top) ? Math.min(Math.max(top, bounds.top), maxTop) : bounds.top;

    content.style.left = `${safeLeft}px`;
    content.style.top = `${safeTop}px`;
    content.style.transform = 'none';
}

function setupSrsRatingShiftDrag() {
    if (!els.srsRatingModal || els.srsRatingModal.dataset.shiftDragReady === '1') return;

    const content = getSrsRatingContentEl();
    if (!content) return;

    let isDragging = false;
    let startX = 0;
    let startY = 0;
    let originLeft = 0;
    let originTop = 0;

    content.addEventListener('mousedown', (e) => {
        if (!e.shiftKey || e.button !== 0) return;
        if (e.target.closest('button, a, input, textarea, select, label')) return;

        e.preventDefault();
        isDragging = true;
        startX = e.clientX;
        startY = e.clientY;

        if (content.style.transform && content.style.transform !== 'none') {
            centerSrsRatingModal(true);
        }

        originLeft = Number.parseFloat(content.style.left);
        originTop = Number.parseFloat(content.style.top);
        if (!Number.isFinite(originLeft) || !Number.isFinite(originTop)) {
            centerSrsRatingModal(true);
            originLeft = Number.parseFloat(content.style.left) || 8;
            originTop = Number.parseFloat(content.style.top) || 8;
        }

        content.classList.add('dragging');
        content.dataset.userMoved = '1';
    });

    const srsDragMoveThrottled = createRafDragThrottle((e) => {
        if (!isDragging) return;

        const bounds = getSrsRatingModalBounds(content);
        const { maxLeft, maxTop } = bounds;

        const nextLeft = Math.min(Math.max(originLeft + (e.clientX - startX), bounds.left), maxLeft);
        const nextTop = Math.min(Math.max(originTop + (e.clientY - startY), bounds.top), maxTop);

        content.style.left = `${nextLeft}px`;
        content.style.top = `${nextTop}px`;
        content.style.transform = 'none';
    });

    document.addEventListener('mousemove', (e) => {
        if (!isDragging) return;
        srsDragMoveThrottled(e);
    });

    document.addEventListener('mouseup', () => {
        if (!isDragging) return;
        srsDragMoveThrottled.flush();
        isDragging = false;
        content.classList.remove('dragging');
    });

    const syncPlacement = () => {
        if (!els.srsRatingModal.classList.contains('active')) return;
        if (content.dataset.userMoved === '1') clampSrsRatingModalToViewport();
        else centerSrsRatingModal(true);
    };
    window.addEventListener('resize', syncPlacement);
    if (typeof ResizeObserver === 'function') {
        const observer = new ResizeObserver(syncPlacement);
        if (els.workArea) observer.observe(els.workArea);
        observer.observe(content);
    }

    els.srsRatingModal.dataset.shiftDragReady = '1';
}

function closeAdaptiveRatingModal() {
    if (els.srsRatingModal) els.srsRatingModal.classList.remove('active');
}

function openAdaptiveRatingModal(realIdx, isCorrect, selectedIdx = null) {
    if (!els.srsRatingModal) return;
    pendingAdaptiveRating = { realIdx, isCorrect, selectedIdx: normalizeAdaptiveSelectedIdx(selectedIdx) };
    adaptiveAnswerRevealedAt = Date.now();

    if (els.srsRatingTitle) {
        els.srsRatingTitle.textContent = isCorrect ? 'How Confident Were You?' : 'How Hard Was That One?';
    }
    if (els.srsRatingSubtitle) {
        els.srsRatingSubtitle.textContent = isCorrect
            ? 'Your confidence adjusts when this card comes back.'
            : 'Rating helps decide how soon this card returns.';
    }

    setupSrsRatingShiftDrag();
    const content = getSrsRatingContentEl();
    if (content) {
        content.classList.remove('dragging');
        content.dataset.userMoved = '0';
    }

    els.srsRatingModal.classList.add('active');
    requestAnimationFrame(() => {
        centerSrsRatingModal(true);
        clampSrsRatingModalToViewport();
    });
}

function ensurePendingAdaptiveRatingModalVisible(showReminder = false) {
    if (!pendingAdaptiveRating) return false;
    if (!els.srsRatingModal?.classList.contains('active')) {
        const { realIdx, isCorrect, selectedIdx } = pendingAdaptiveRating;
        openAdaptiveRatingModal(realIdx, isCorrect, selectedIdx);
    }
    if (showReminder) showPopup('Rate this answer to continue spaced review.', null, false);
    return true;
}

function updateAdaptiveFilterBanner() {
    if (!isAdaptiveReviewMode) return;
    const total = filteredIndices.length;
    const remaining = filteredIndices.filter(idx => userAnswers[idx] === undefined).length;
    els.filterBanner.style.display = 'flex';
    const bannerText = getFilterBannerTextEl();
    if (bannerText) bannerText.innerHTML = `<b>Quiz Spaced Review (Anki-style):</b> ${remaining} remaining of ${total} due cards`;
    setAdaptiveSnapshotButtonVisible(true);
    setAdaptiveSkipUndoneButtonVisible(true);
    if (els.exitFilter) els.exitFilter.textContent = 'Exit';
}
function jumpToNextAdaptiveUndone() {
    if (!isAdaptiveReviewMode) return;
    if (ensurePendingAdaptiveRatingModalVisible(true)) return;
    if (!Array.isArray(filteredIndices) || filteredIndices.length === 0) return;

    const total = filteredIndices.length;
    const start = Number.isInteger(currentQIndex) ? currentQIndex : 0;

    const findNext = (predicate) => {
        for (let step = 1; step <= total; step++) {
            const viewIdx = (start + step) % total;
            const realIdx = filteredIndices[viewIdx];
            if (predicate(realIdx, viewIdx)) return viewIdx;
        }
        return -1;
    };

    // "Undone" in adaptive mode means unanswered in the current due queue.
    const targetViewIndex = findNext((realIdx) => userAnswers[realIdx] === undefined);

    if (targetViewIndex === -1) {
        showPopup('No unanswered due cards left right now.', null, false);
        return;
    }

    currentQIndex = targetViewIndex;
    renderQuestion('anim-fade');
}
function stashAndClearAdaptiveAnswers(indices) {
    if (!Array.isArray(indices) || indices.length === 0) return;
    indices.forEach((idx) => {
        if (userAnswers[idx] !== undefined && adaptiveClearedAnswersBackup[idx] === undefined) {
            adaptiveClearedAnswersBackup[idx] = userAnswers[idx];
        }
        delete userAnswers[idx];
    });
}
function restoreAdaptiveClearedAnswers() {
    const entries = Object.entries(adaptiveClearedAnswersBackup || {});
    if (entries.length === 0) return;
    entries.forEach(([idx, ans]) => {
        if (userAnswers[idx] === undefined) userAnswers[idx] = ans;
    });
    adaptiveClearedAnswersBackup = {};
}
// Only real inline markup is stripped. Comparison text ("pH < 7.35 and PaCO2 > 45")
// is content and stays in the identity, so changed thresholds are different questions.
function normalizeQuestionPersistencePart(value) {
    return String(value || '')
        .replace(/<\/?(?:a|abbr|b|big|br|code|del|div|em|font|h[1-6]|hr|i|img|ins|li|mark|ol|p|pre|q|s|small|span|strike|strong|sub|sup|table|tbody|td|th|thead|tr|u|ul)(?:\s[^<>]*)?\/?>/gi, ' ')
        .replace(/&nbsp;/gi, ' ')
        .replace(/&amp;/gi, '&')
        .replace(/&lt;/gi, '<')
        .replace(/&gt;/gi, '>')
        .toLowerCase()
        .replace(/\s+/g, ' ')
        .trim();
}
function getQuestionChoicesForPersistence(questionObj) {
    if (!questionObj || typeof questionObj !== 'object') return [];
    if (Array.isArray(questionObj.c)) return questionObj.c;
    if (Array.isArray(questionObj.o)) return questionObj.o;
    if (Array.isArray(questionObj.choices)) return questionObj.choices;
    if (Array.isArray(questionObj.options)) return questionObj.options;
    return [];
}
function getQuestionAnswerIndexForPersistence(questionObj) {
    if (!questionObj || typeof questionObj !== 'object') return -1;
    const choices = getQuestionChoicesForPersistence(questionObj);
    const stableAnswerText = questionObj.answerText
        ?? questionObj.answer_text
        ?? questionObj.correctChoiceText
        ?? questionObj.correct_choice_text;
    if (stableAnswerText !== undefined && stableAnswerText !== null && String(stableAnswerText).trim()) {
        const stableIndex = findChoiceIdentityIndex(stableAnswerText, choices, Number.isInteger(questionObj.a) ? questionObj.a : -1);
        if (stableIndex >= 0) return stableIndex;
    }
    if (Number.isInteger(questionObj.a)) return questionObj.a;
    if (typeof questionObj.answer === 'number' && Number.isInteger(questionObj.answer)) return questionObj.answer;
    if (typeof questionObj.answer === 'string') {
        const letter = questionObj.answer.trim().toUpperCase();
        if (/^[A-H]$/.test(letter)) return letter.charCodeAt(0) - 65;
        const normalizedAnswer = normalizeQuestionForDedup(questionObj.answer);
        const answerTextIndex = choices.findIndex(choice => normalizeQuestionForDedup(choice) === normalizedAnswer);
        if (answerTextIndex >= 0) return answerTextIndex;
    }
    return -1;
}
// Choice text is escaped so "|" inside a choice cannot read as a separator; choices
// without "|" or "\" keep the same key as before. escapeChoices:false rebuilds the old key.
function buildQuestionPersistenceKeyFromQuestion(questionObj, normalizePart = normalizeQuestionPersistencePart, { escapeChoices = true } = {}) {
    if (!questionObj || typeof questionObj !== 'object') return '';
    const stem = normalizePart(questionObj.q || questionObj.question || '');
    if (!stem) return '';
    const source = normalizePart(questionObj.sourceFile || questionObj.setName || '');
    const choices = getQuestionChoicesForPersistence(questionObj)
        .map((choice) => normalizePart(choice));
    const answerIdx = getQuestionAnswerIndexForPersistence(questionObj);
    const answerText = (answerIdx >= 0 && answerIdx < choices.length) ? choices[answerIdx] : '';
    const answerPart = answerText || (answerIdx >= 0 ? `#${answerIdx}` : '#');
    const sourcePart = source || 'nosource';
    const choicesPart = escapeChoices
        ? choices.map((choice) => choice.replace(/[\\|]/g, (ch) => `\\${ch}`)).join('|')
        : choices.join('|');
    return `${sourcePart}::${stem}::${answerPart}::${choicesPart}`;
}

// The identity before comparison text was kept: everything from "<" to ">" was
// dropped. Stored progress and notes may still be keyed this way.
function normalizeLegacyQuestionPersistencePart(value) {
    return normalizeQuestionPersistencePart(String(value || '').replace(/<[^>]*>/g, ' '));
}

// Legacy key -> current key for loaded questions whose identity changed, limited to
// the given stored keys. A legacy key shared by two loaded questions is ambiguous and
// never transfers state.
function getLegacyQuestionKeyAliases(storedKeys) {
    const lists = [];
    if (Array.isArray(questions)) lists.push(questions);
    if (loadedQuestionSets && typeof loadedQuestionSets === 'object') {
        Object.values(loadedQuestionSets).forEach((list) => { if (Array.isArray(list) && !lists.includes(list)) lists.push(list); });
    }
    return getLegacyQuestionKeyAliasesForQuestions(storedKeys, lists);
}

// The same aliases over given question lists, so a closed session's banks resolve its
// legacy keys without depending on whichever session is open.
function getLegacyQuestionKeyAliasesForQuestions(storedKeys, lists) {
    const aliases = new Map();
    const wanted = new Set(storedKeys || []);
    if (!wanted.size) return aliases;
    const targets = new Map();
    const seen = new Set();
    (lists || []).forEach((list) => (Array.isArray(list) ? list : []).forEach((question) => {
        if (!question || typeof question !== 'object' || seen.has(question)) return;
        seen.add(question);
        const texts = [question.q, question.question, question.sourceFile, question.setName, ...getQuestionChoicesForPersistence(question)];
        const choiceTexts = getQuestionChoicesForPersistence(question);
        const legacyKeys = [];
        if (texts.some((text) => typeof text === 'string' && text.includes('<'))) {
            legacyKeys.push(buildQuestionPersistenceKeyFromQuestion(question, normalizeLegacyQuestionPersistencePart, { escapeChoices: false }));
        }
        if (choiceTexts.some((text) => /[\\|]/.test(String(text ?? '')))) {
            legacyKeys.push(buildQuestionPersistenceKeyFromQuestion(question, normalizeQuestionPersistencePart, { escapeChoices: false }));
        }
        const current = buildQuestionPersistenceKeyFromQuestion(question);
        legacyKeys.forEach((legacy) => {
            if (!wanted.has(legacy) || !current || current === legacy) return;
            if (!targets.has(legacy)) targets.set(legacy, new Set());
            targets.get(legacy).add(current);
        });
    }));
    if (!targets.size) return aliases;
    // A legacy key equal to some question's current key (one without comparison text
    // collided with it) belongs to that question as well, so it is ambiguous too.
    const currentKeys = new Set();
    seen.forEach((question) => currentKeys.add(buildQuestionPersistenceKeyFromQuestion(question)));
    targets.forEach((set, legacy) => {
        if (set.size === 1 && !currentKeys.has(legacy)) aliases.set(legacy, [...set][0]);
    });
    return aliases;
}

// Stored buckets keyed by a legacy identity move to the current key (when that key
// has nothing of its own yet), so existing progress survives the identity change.
function migrateLegacyQuestionKeyBuckets(buckets) {
    const keys = Object.keys(buckets || {});
    if (!keys.length || !keys.some((key) => key.includes('::'))) return buckets;
    const aliases = getLegacyQuestionKeyAliases(keys);
    if (!aliases.size) return buckets;
    const migrated = { ...buckets };
    aliases.forEach((current, legacy) => {
        if (!Array.isArray(migrated[legacy]) || Array.isArray(migrated[current])) return;
        migrated[current] = migrated[legacy];
        delete migrated[legacy];
    });
    return migrated;
}
// Notes, unlike progress, belong to one copy of a bank. A duplicated session's questions carry
// a noteScope, so a note edited in the copy leaves the original session's note alone.
function buildQuestionNoteKeyFromQuestion(questionObj) {
    const key = buildQuestionPersistenceKeyFromQuestion(questionObj);
    const scope = key ? normalizeQuestionPersistencePart(questionObj.noteScope || '') : '';
    return scope ? `${key}::note-scope=${scope}` : key;
}
function buildQuestionPersistenceKeyByIndex(idx) {
    if (!Number.isInteger(idx) || idx < 0 || idx >= questions.length) return '';
    return buildQuestionPersistenceKeyFromQuestion(questions[idx]);
}
function hasQuestionKeyStateBuckets(rawBuckets) {
    return !!rawBuckets && typeof rawBuckets === 'object' && !Array.isArray(rawBuckets);
}
function sanitizeQuestionKeyBucketObject(rawBuckets) {
    if (!rawBuckets || typeof rawBuckets !== 'object') return {};
    const clean = {};
    Object.entries(rawBuckets).forEach(([key, values]) => {
        const normalizedKey = String(key || '').trim();
        if (!normalizedKey || !Array.isArray(values) || values.length === 0) return;
        clean[normalizedKey] = values.slice();
    });
    return migrateLegacyQuestionKeyBuckets(clean);
}
function buildIndexedStateBucketsByQuestionKey(indexState) {
    const buckets = {};
    if (!indexState || typeof indexState !== 'object' || !Array.isArray(questions) || questions.length === 0) return buckets;
    Object.entries(indexState).forEach(([idxKey, value]) => {
        const idx = Number(idxKey);
        if (!Number.isInteger(idx) || idx < 0 || idx >= questions.length) return;
        const qKey = buildQuestionPersistenceKeyByIndex(idx);
        if (!qKey) return;
        if (!Array.isArray(buckets[qKey])) buckets[qKey] = [];
        buckets[qKey].push(value);
    });
    return buckets;
}
function buildIndexStateFromQuestionKeyBuckets(rawBuckets, transformValue = null) {
    const buckets = sanitizeQuestionKeyBucketObject(rawBuckets);
    const bucketCursor = {};
    const rebuilt = {};
    if (!Array.isArray(questions) || questions.length === 0) return rebuilt;
    for (let idx = 0; idx < questions.length; idx++) {
        const qKey = buildQuestionPersistenceKeyByIndex(idx);
        if (!qKey) continue;
        const values = buckets[qKey];
        if (!Array.isArray(values) || values.length === 0) continue;
        const cursor = Number(bucketCursor[qKey]) || 0;
        if (cursor >= values.length) continue;
        const rawValue = values[cursor];
        const value = (typeof transformValue === 'function')
            ? transformValue(rawValue, idx)
            : rawValue;
        bucketCursor[qKey] = cursor + 1;
        if (value !== undefined) rebuilt[idx] = value;
    }
    return rebuilt;
}
function buildFilteredQuestionKeysForPersistence(indices) {
    if (!Array.isArray(indices) || indices.length === 0) return [];
    const keys = [];
    indices.forEach((rawIdx) => {
        const idx = Number(rawIdx);
        if (!Number.isInteger(idx) || idx < 0 || idx >= questions.length) return;
        const qKey = buildQuestionPersistenceKeyByIndex(idx);
        if (qKey) keys.push(qKey);
    });
    return keys;
}
function buildFilteredIndicesFromQuestionKeyList(questionKeys) {
    if (!Array.isArray(questionKeys) || questionKeys.length === 0 || !Array.isArray(questions) || questions.length === 0) return [];
    const indexBuckets = {};
    for (let idx = 0; idx < questions.length; idx++) {
        const qKey = buildQuestionPersistenceKeyByIndex(idx);
        if (!qKey) continue;
        if (!Array.isArray(indexBuckets[qKey])) indexBuckets[qKey] = [];
        indexBuckets[qKey].push(idx);
    }
    const bucketCursor = {};
    const rebuilt = [];
    questionKeys.forEach((key) => {
        const normalizedKey = String(key || '').trim();
        if (!normalizedKey) return;
        const bucket = indexBuckets[normalizedKey];
        if (!Array.isArray(bucket) || bucket.length === 0) return;
        const cursor = Number(bucketCursor[normalizedKey]) || 0;
        if (cursor >= bucket.length) return;
        rebuilt.push(bucket[cursor]);
        bucketCursor[normalizedKey] = cursor + 1;
    });
    return rebuilt;
}
function appendMissingQuestionKeyOccurrences(existingKeys, candidateKeys) {
    const baseKeys = Array.isArray(existingKeys)
        ? existingKeys.map((key) => String(key || '').trim()).filter(Boolean)
        : [];
    const remainingMatches = {};
    baseKeys.forEach((key) => {
        remainingMatches[key] = (remainingMatches[key] || 0) + 1;
    });

    const merged = baseKeys.slice();
    (Array.isArray(candidateKeys) ? candidateKeys : []).forEach((rawKey) => {
        const key = String(rawKey || '').trim();
        if (!key) return;
        if ((remainingMatches[key] || 0) > 0) {
            remainingMatches[key] -= 1;
            return;
        }
        merged.push(key);
    });
    return merged;
}

function normalizeQuizCompletionQuestionKeys(rawKeys) {
    if (!Array.isArray(rawKeys)) return [];
    const seen = new Set();
    const normalized = [];
    rawKeys.forEach((rawKey) => {
        const key = String(rawKey || '').trim();
        if (!key || seen.has(key)) return;
        seen.add(key);
        normalized.push(key);
    });
    return normalized;
}

function calculateQuizCompletionProgress({
    currentPosition = 0,
    filteredQuestionKeys = [],
    activeCohortKeys = [],
    pendingQuestionKeys = [],
    answeredQuestionKeys = []
} = {}) {
    const visibleKeys = normalizeQuizCompletionQuestionKeys(filteredQuestionKeys);
    const visibleKeySet = new Set(visibleKeys);
    const requestedCohortKeys = normalizeQuizCompletionQuestionKeys(activeCohortKeys);
    const cohortKeys = (requestedCohortKeys.length > 0
        ? requestedCohortKeys.filter((key) => visibleKeySet.has(key))
        : visibleKeys.slice());
    const cohortKeySet = new Set(cohortKeys);
    const pendingKeys = normalizeQuizCompletionQuestionKeys(pendingQuestionKeys)
        .filter((key) => !cohortKeySet.has(key));
    const answeredKeys = new Set(normalizeQuizCompletionQuestionKeys(answeredQuestionKeys));
    const answeredInCohort = cohortKeys.reduce(
        (count, key) => count + (answeredKeys.has(key) ? 1 : 0),
        0
    );
    const safePosition = visibleKeys.length > 0
        ? Math.min(visibleKeys.length, Math.max(1, Number(currentPosition) || 1))
        : 0;
    const cohortTotal = cohortKeys.length;
    const completionPercent = cohortTotal > 0
        ? Math.min(100, Math.max(0, Math.round((answeredInCohort / cohortTotal) * 100)))
        : 0;
    return {
        currentPosition: safePosition,
        cohortTotal,
        answeredInCohort,
        pendingNewQuestions: pendingKeys.length,
        completionPercent
    };
}

function normalizeQuizCompletionCohortState(rawState) {
    const source = rawState && typeof rawState === 'object' ? rawState : {};
    const activeQuestionKeys = normalizeQuizCompletionQuestionKeys(source.activeQuestionKeys);
    const activeKeySet = new Set(activeQuestionKeys);
    const pendingNewQuestionKeys = normalizeQuizCompletionQuestionKeys(source.pendingNewQuestionKeys)
        .filter((key) => !activeKeySet.has(key));
    return {
        version: QUIZ_COMPLETION_COHORT_VERSION,
        activeQuestionKeys,
        pendingNewQuestionKeys,
        updatedAt: Number(source.updatedAt) || 0
    };
}

function getQuizCompletionCohortPersistenceState() {
    return normalizeQuizCompletionCohortState({
        ...quizCompletionCohortState,
        updatedAt: Date.now()
    });
}

function getQuizCompletionQuestionKeysForIndices(indices = filteredIndices) {
    return buildFilteredQuestionKeysForPersistence(indices);
}

function getQuizCompletionProgressState() {
    const visibleQuestionKeys = getQuizCompletionQuestionKeysForIndices(filteredIndices);
    const answeredQuestionKeys = (Array.isArray(filteredIndices) ? filteredIndices : [])
        .filter((idx) => userAnswers[idx] !== undefined)
        .map((idx) => buildQuestionPersistenceKeyByIndex(idx))
        .filter(Boolean);
    return calculateQuizCompletionProgress({
        currentPosition: currentQIndex + 1,
        filteredQuestionKeys: visibleQuestionKeys,
        activeCohortKeys: quizCompletionCohortState.activeQuestionKeys,
        pendingQuestionKeys: quizCompletionCohortState.pendingNewQuestionKeys,
        answeredQuestionKeys
    });
}

function resetQuizCompletionCohort(reason = 'reset') {
    const activeQuestionKeys = normalizeQuizCompletionQuestionKeys(
        getQuizCompletionQuestionKeysForIndices(filteredIndices)
    );
    quizCompletionCohortState = {
        version: QUIZ_COMPLETION_COHORT_VERSION,
        activeQuestionKeys,
        pendingNewQuestionKeys: [],
        updatedAt: Date.now(),
        reason: String(reason || 'reset')
    };
    return quizCompletionCohortState;
}

function getQuestionIndicesForCompletionKeys(keys) {
    return buildFilteredIndicesFromQuestionKeyList(normalizeQuizCompletionQuestionKeys(keys));
}

function includePendingQuizCompletionQuestions(options = {}) {
    const previousCurrentKey = buildQuestionPersistenceKeyByIndex(filteredIndices[currentQIndex]);
    const availableKeys = normalizeQuizCompletionQuestionKeys(
        getQuizCompletionQuestionKeysForIndices(Array.from({ length: questions.length }, (_, idx) => idx))
    );
    const availableKeySet = new Set(availableKeys);
    const activeKeys = normalizeQuizCompletionQuestionKeys(quizCompletionCohortState.activeQuestionKeys)
        .filter((key) => availableKeySet.has(key));
    const pendingKeys = normalizeQuizCompletionQuestionKeys(quizCompletionCohortState.pendingNewQuestionKeys);
    const newlyIncludedKeys = pendingKeys.filter((key) => availableKeySet.has(key));
    const stillPendingKeys = pendingKeys.filter((key) => !availableKeySet.has(key));
    const nextActiveKeys = normalizeQuizCompletionQuestionKeys(activeKeys.concat(newlyIncludedKeys));
    const nextIndices = getQuestionIndicesForCompletionKeys(nextActiveKeys);
    if (nextIndices.length > 0) filteredIndices = nextIndices;
    quizCompletionCohortState = {
        version: QUIZ_COMPLETION_COHORT_VERSION,
        activeQuestionKeys: nextActiveKeys,
        pendingNewQuestionKeys: stillPendingKeys,
        updatedAt: Date.now(),
        reason: String(options.reason || 'include-pending')
    };
    if (previousCurrentKey) {
        const currentRealIdx = filteredIndices.findIndex((idx) => (
            buildQuestionPersistenceKeyByIndex(idx) === previousCurrentKey
        ));
        if (currentRealIdx >= 0) currentQIndex = currentRealIdx;
    }
    syncQuizJumpSelectOptions();
    if (typeof syncQuizProgressText === 'function') syncQuizProgressText();
    if (typeof syncQuizProgressFill === 'function') syncQuizProgressFill();
    return getQuizCompletionProgressState();
}
window.calculateQuizCompletionProgress = calculateQuizCompletionProgress;
window.getQuizCompletionProgressState = getQuizCompletionProgressState;
window.includePendingQuizCompletionQuestions = includePendingQuizCompletionQuestions;

function reconcileCondensedQuizStateWithCurrentQuestionBank(options = {}) {
    const {
        refreshView = false,
        preserveCurrentQuestion = true,
        save = false
    } = options || {};

    const hasBaseSnapshot = Array.isArray(condensedQuizState?.baseFilteredQuestionKeys)
        && condensedQuizState.baseFilteredQuestionKeys.length > 0;
    if (!hasCondensedQuizSelection() && !hasBaseSnapshot) {
        syncCondensedQuizToggleButton();
        return false;
    }

    const currentPoolQuestionKeys = buildFilteredQuestionKeysForPersistence(
        Array.from({ length: questions.length }, (_, idx) => idx)
    );
    if (!currentPoolQuestionKeys.length) {
        syncCondensedQuizToggleButton();
        return false;
    }

    const nextState = normalizeCondensedQuizState(condensedQuizState);
    const mergedBaseQuestionKeys = appendMissingQuestionKeyOccurrences(nextState.baseFilteredQuestionKeys, currentPoolQuestionKeys);
    let changed = false;

    if (mergedBaseQuestionKeys.length !== nextState.baseFilteredQuestionKeys.length) {
        nextState.baseFilteredQuestionKeys = mergedBaseQuestionKeys;
        changed = true;
    }

    const nextSourceQuestionCount = Math.max(
        Number(nextState.sourceQuestionCount) || 0,
        currentPoolQuestionKeys.length
    );
    if (nextSourceQuestionCount !== nextState.sourceQuestionCount) {
        nextState.sourceQuestionCount = nextSourceQuestionCount;
        changed = true;
    }

    if (nextState.enabled && nextState.questionKeys.length > 0 && buildFilteredIndicesFromQuestionKeyList(nextState.questionKeys).length === 0) {
        nextState.enabled = false;
        changed = true;
    }

    if (!changed) {
        syncCondensedQuizToggleButton();
        return false;
    }

    nextState.updatedAt = Date.now();
    condensedQuizState = normalizeCondensedQuizState(nextState);
    syncCondensedQuizToggleButton();

    if (refreshView) {
        refreshQuizViewForCurrentQuestionPool({ preserveCurrentQuestion });
    }

    if (save) {
        markSessionStateMutation();
        saveSessionDebounced();
    }

    return true;
}
function createDefaultCondensedQuizState() {
    return {
        version: CONDENSED_QUIZ_STATE_VERSION,
        questionKeys: [],
        selectedSetKeys: [],
        baseFilteredQuestionKeys: [],
        baseCurrentQuestionKey: '',
        baseCurrentQuestionViewIndex: 0,
        baseIsFilterActive: false,
        targetCount: 0,
        sourceQuestionCount: 0,
        promptRequest: '',
        enabled: false,
        updatedAt: 0
    };
}
function normalizeCondensedQuizState(rawState = null) {
    const fallback = createDefaultCondensedQuizState();
    if (!rawState || typeof rawState !== 'object') return fallback;

    const questionKeys = Array.isArray(rawState.questionKeys)
        ? rawState.questionKeys.map((key) => String(key || '').trim()).filter(Boolean)
        : [];
    const selectedSetKeys = Array.isArray(rawState.selectedSetKeys)
        ? rawState.selectedSetKeys.map((key) => String(key || '').trim()).filter(Boolean)
        : [];
    const baseFilteredQuestionKeys = Array.isArray(rawState.baseFilteredQuestionKeys)
        ? rawState.baseFilteredQuestionKeys.map((key) => String(key || '').trim()).filter(Boolean)
        : [];
    const normalized = {
        version: CONDENSED_QUIZ_STATE_VERSION,
        questionKeys,
        selectedSetKeys,
        baseFilteredQuestionKeys,
        baseCurrentQuestionKey: String(rawState.baseCurrentQuestionKey || '').trim(),
        baseCurrentQuestionViewIndex: Math.max(0, Number(rawState.baseCurrentQuestionViewIndex) || 0),
        baseIsFilterActive: !!rawState.baseIsFilterActive,
        targetCount: Math.max(0, Number(rawState.targetCount) || 0),
        sourceQuestionCount: Math.max(0, Number(rawState.sourceQuestionCount) || 0),
        promptRequest: typeof rawState.promptRequest === 'string' ? rawState.promptRequest : '',
        enabled: !!rawState.enabled && questionKeys.length > 0,
        updatedAt: Math.max(0, Number(rawState.updatedAt) || 0)
    };
    if (normalized.targetCount <= 0 && normalized.questionKeys.length > 0) {
        normalized.targetCount = normalized.questionKeys.length;
    }
    if (normalized.sourceQuestionCount < normalized.questionKeys.length) {
        normalized.sourceQuestionCount = normalized.questionKeys.length;
    }
    return normalized;
}
function hasCondensedQuizSelection() {
    return Array.isArray(condensedQuizState?.questionKeys) && condensedQuizState.questionKeys.length > 0;
}
function getCondensedQuestionIndices() {
    if (!hasCondensedQuizSelection()) return [];
    return buildFilteredIndicesFromQuestionKeyList(condensedQuizState.questionKeys);
}
function getCondensedVisibleQuestionCount() {
    const visibleCount = getCondensedQuestionIndices().length;
    if (visibleCount > 0 || (Array.isArray(questions) && questions.length > 0)) {
        return visibleCount;
    }
    return condensedQuizState.questionKeys.length;
}
// Adaptive Practice chooses within the existing pool; SRS still owns due dates.
const ADAPTIVE_PRACTICE_POLICY = Object.freeze({
    initial: Object.freeze([0.30, 0.55, 0.15]),
    blend: 0.10,
    evidenceWindow: 24,
    coverageDecay: 0.03,
    success: Object.freeze([
        Object.freeze([0.25, 0.55, 0.20]),
        Object.freeze([0.15, 0.50, 0.35]),
        Object.freeze([0.20, 0.55, 0.25])
    ]),
    miss: Object.freeze([
        Object.freeze([0.60, 0.35, 0.05]),
        Object.freeze([0.45, 0.50, 0.05]),
        Object.freeze([0.35, 0.55, 0.10])
    ])
});
function normalizeAdaptivePracticeState(raw = {}) {
    return { historyKeys: normalizeQuizCompletionQuestionKeys(raw?.historyKeys) };
}
function getAdaptivePracticeLevel(question) {
    const level = Number(question?.lvl);
    return [1, 2, 3].includes(level) ? level : null;
}
function hasAdaptivePracticeAnswerCue(question) {
    if (question?.answerUnverified) return false;
    const choices = getQuestionChoicesForPersistence(question);
    const answer = getQuestionAnswerIndexForPersistence(question);
    if (choices.length < 2 || answer < 0 || answer >= choices.length) return false;
    const lengths = choices.map(choice => (String(choice || '').replace(/<[^>]*>/g, ' ').match(/\S+/g) || []).length);
    if (lengths.some(length => !length)) return false;
    return lengths.every((length, index) => index === answer || lengths[answer] > length);
}
function getAdaptivePracticeSource(question) {
    // src is the uploaded lecture; sourceFile can instead name the generated JSON bank.
    return normalizeQuestionPersistencePart(question?.src || question?.sourceFile || question?.setName || '');
}
function getAdaptivePracticeContext() {
    return { questions, statsData, userAnswers, currentIndex: filteredIndices[currentQIndex], historyKeys: adaptivePracticeState.historyKeys };
}
function getAdaptivePracticeEvidence(source = null, context = getAdaptivePracticeContext()) {
    // The earliest recorded attempt per stable question counts once, including after Refresh.
    const evidence = new Map();
    context.questions.forEach((question, index) => {
        const level = getAdaptivePracticeLevel(question);
        const attempt = context.statsData[index]?.filter(entry => typeof entry?.correct === 'boolean' && Number.isFinite(entry.date))
            .reduce((first, entry) => !first || entry.date < first.date ? entry : first, null);
        const key = buildQuestionPersistenceKeyFromQuestion(question);
        if (!attempt || !key || question.flashcardOnly || question.answerUnverified
            || (source !== null && getAdaptivePracticeSource(question) !== source)) return;
        if (attempt.correct && hasAdaptivePracticeAnswerCue(question)) return;
        const previous = evidence.get(key);
        if (!previous || attempt.date < previous.date) evidence.set(key, { ...attempt, level });
    });
    return [...evidence.values()].sort((a, b) => a.date - b.date).slice(-ADAPTIVE_PRACTICE_POLICY.evidenceWindow);
}
function getAdaptivePracticeProbabilities(source = null, context = getAdaptivePracticeContext()) {
    const probabilities = [...ADAPTIVE_PRACTICE_POLICY.initial];
    getAdaptivePracticeEvidence(source, context).forEach(entry => {
        if (!entry.level) return;
        const target = (entry.correct ? ADAPTIVE_PRACTICE_POLICY.success : ADAPTIVE_PRACTICE_POLICY.miss)[entry.level - 1];
        target.forEach((value, index) => {
            probabilities[index] += (value - probabilities[index]) * ADAPTIVE_PRACTICE_POLICY.blend;
        });
    });
    return probabilities;
}
function getAdaptivePracticeFileWeight(source, context = getAdaptivePracticeContext()) {
    const evidence = getAdaptivePracticeEvidence(source, context);
    const misses = evidence.filter(entry => !entry.correct).length;
    let weight = 1 + (misses + 1) / (evidence.length + 2);
    const current = context.questions[context.currentIndex];
    if (current && getAdaptivePracticeSource(current) === source) {
        const answer = context.userAnswers[context.currentIndex];
        if (answer !== undefined) weight += answer === getQuestionAnswerIndexForPersistence(current) ? 0.15 : 0.35;
    }
    const history = new Set(context.historyKeys);
    const shown = context.questions.filter(question => getAdaptivePracticeSource(question) === source
        && history.has(buildQuestionPersistenceKeyFromQuestion(question))).length;
    return weight / (1 + shown * ADAPTIVE_PRACTICE_POLICY.coverageDecay);
}
function isAdaptivePracticeActive() {
    return !isAdaptiveReviewMode && !isFlashcardMode
        && !document.querySelector('.memora-exam-mode') && !getTransientQuizPersistenceOverride()
        && !isPublicTourQuizDemoActive();
}
function sampleAdaptivePracticeCandidate(indices, random = Math.random, context = getAdaptivePracticeContext()) {
    if (!indices.length) return null;
    const unflagged = indices.filter(index => !hasAdaptivePracticeAnswerCue(context.questions[index]));
    const pool = unflagged.length ? unflagged : indices;
    const pick = (items, weights) => {
        let remaining = Math.min(1 - Number.EPSILON, Math.max(0, random())) * weights.reduce((sum, weight) => sum + weight, 0);
        for (let index = 0; index < items.length; index++) {
            remaining -= weights[index];
            if (remaining < 0) return items[index];
        }
        return items[items.length - 1];
    };
    const files = new Map();
    pool.forEach(index => {
        const source = getAdaptivePracticeSource(context.questions[index]);
        if (!files.has(source)) files.set(source, []);
        files.get(source).push(index);
    });
    const sources = [...files.keys()];
    const source = pick(sources, sources.map(value => getAdaptivePracticeFileWeight(value, context)));
    const probabilities = getAdaptivePracticeProbabilities(source, context);
    const buckets = [[], [], []];
    files.get(source).forEach(index => buckets[(getAdaptivePracticeLevel(context.questions[index]) || 2) - 1].push(index));
    const availableLevels = [0, 1, 2].filter(level => buckets[level].length);
    const level = pick(availableLevels, availableLevels.map(index => probabilities[index]));
    const current = context.questions[context.currentIndex];
    const missed = current && context.userAnswers[context.currentIndex] !== undefined
        && context.userAnswers[context.currentIndex] !== getQuestionAnswerIndexForPersistence(current);
    return pick(buckets[level], buckets[level].map(index => {
        const question = context.questions[index];
        const file = getAdaptivePracticeSource(question);
        const related = missed && file && file === getAdaptivePracticeSource(current)
            && question.add && question.add === current.add;
        return related ? 1.25 : 1;
    }));
}
function applyAdaptivePracticeOrder() {
    if (!isAdaptivePracticeActive() || !filteredIndices.length) return;
    const current = filteredIndices[currentQIndex];
    const key = buildQuestionPersistenceKeyByIndex(current);
    if (!key) return;
    if (!adaptivePracticeState.historyKeys.length) {
        // Older sessions retain their already-presented prefix when automatic pacing starts.
        adaptivePracticeState.historyKeys = buildFilteredQuestionKeysForPersistence(filteredIndices.slice(0, currentQIndex + 1));
    } else if (!adaptivePracticeState.historyKeys.includes(key)) adaptivePracticeState.historyKeys.push(key);
    const ranks = new Map(adaptivePracticeState.historyKeys.map((value, index) => [value, index]));
    // In-place ordering keeps the existing high-yield pool identity and preserves every eligible item.
    filteredIndices.sort((a, b) => (ranks.get(buildQuestionPersistenceKeyByIndex(a)) ?? Infinity)
        - (ranks.get(buildQuestionPersistenceKeyByIndex(b)) ?? Infinity));
    currentQIndex = filteredIndices.indexOf(current);
}
function prepareAdaptivePracticeNextQuestion(random = Math.random) {
    if (!isAdaptivePracticeActive()) return;
    const history = new Set(adaptivePracticeState.historyKeys);
    const nextPosition = currentQIndex + 1;
    // Back/Next traverses presented history without resampling it.
    if (history.has(buildQuestionPersistenceKeyByIndex(filteredIndices[nextPosition]))) return;
    let candidates = filteredIndices.slice(nextPosition).filter(index => !history.has(buildQuestionPersistenceKeyByIndex(index)));
    if (isHighYieldQuizEnabled() && getSelectedQuizTierLevel() === 1) {
        const coreCandidates = candidates.filter(index => getQuizQuestionViewTier(questions[index]) === 1);
        if (coreCandidates.length) candidates = coreCandidates;
    }
    {
        const frontCandidates = candidates.filter(index => !isQuizQuestionShownLast(questions[index]));
        if (frontCandidates.length) candidates = frontCandidates;
    }
    const picked = sampleAdaptivePracticeCandidate(candidates, random);
    if (picked === null) return;
    const position = filteredIndices.indexOf(picked, nextPosition);
    filteredIndices.splice(position, 1);
    filteredIndices.splice(nextPosition, 0, picked);
    syncQuizJumpSelectOptions();
}
function normalizeHighYieldQuizState(rawState = {}) {
    return {
        enabled: typeof rawState?.enabled === 'boolean' ? rawState.enabled : null,
        level: [1, 2, 3].includes(Number(rawState?.level)) ? Number(rawState.level) : 1,
        baseFilteredQuestionKeys: normalizeQuizCompletionQuestionKeys(rawState?.baseFilteredQuestionKeys),
        baseIsFilterActive: !!rawState?.baseIsFilterActive,
        streamedFallbackQuestionKeys: normalizeQuizCompletionQuestionKeys(rawState?.streamedFallbackQuestionKeys),
        pinnedQuestionKey: String(rawState?.pinnedQuestionKey || '')
    };
}
// Auto quizzes rank each question 1 Core or 2 Extended; older banks carry tier 3 (detail) or
// only hy (true = core). The quiz studies two cumulative levels: Core, and Extended (Core plus
// every other question). Tier 3 and unlabeled questions study as Extended, so none is unreachable.
const QUIZ_TIER_LEVELS = Object.freeze([
    Object.freeze({ level: 1, label: 'Core', hint: 'must-know only' }),
    Object.freeze({ level: 2, label: 'Extended', hint: 'Core + other important facts' })
]);
function getQuizAutoQuestionTier(question) {
    const tier = Number(question?.tier);
    if (tier === 1 || tier === 2 || tier === 3) return tier;
    return typeof question?.hy === 'boolean' ? (question.hy ? 1 : 2) : null;
}
// The answer check tags a question that turns on an incidental number or detail (13 vs 13.5 g/dL)
// to show last. It stays behind every other question in the pool whatever shuffles or reorders it.
function isQuizQuestionShownLast(question) {
    return question?.quizShowLast === true;
}
function moveShownLastQuestionsBehind(indices) {
    if (!Array.isArray(indices) || !indices.some(index => isQuizQuestionShownLast(questions[index]))) return indices;
    const ordered = indices.filter(index => !isQuizQuestionShownLast(questions[index]))
        .concat(indices.filter(index => isQuizQuestionShownLast(questions[index])));
    indices.splice(0, indices.length, ...ordered);
    return indices;
}
function getQuizQuestionViewTier(question) {
    return Math.min(2, getQuizAutoQuestionTier(question) || 2);
}
function getSelectedQuizTierLevel() {
    // A level saved as All (3) before the picker had two levels studies as Extended.
    const level = Number(highYieldQuizState?.level);
    return level === 2 || level === 3 ? 2 : 1;
}
function countQuizQuestionsUpToLevel(level, indices = null) {
    const pool = Array.isArray(indices) ? indices.map(index => questions[index]) : questions;
    return pool.filter(question => question && getQuizQuestionViewTier(question) <= level).length;
}
function hasHighYieldQuizLabels() {
    return questions.some(question => getQuizAutoQuestionTier(question) !== null);
}
function isHighYieldQuizEnabled() {
    if (condensedQuizState.enabled || !hasHighYieldQuizLabels()) return false;
    return highYieldQuizState.enabled === null
        ? questions.some(question => question.quizAuto === true)
        : highYieldQuizState.enabled;
}
function filterHighYieldQuestionIndices(indices) {
    if (!isHighYieldQuizEnabled()) return indices;
    const level = getSelectedQuizTierLevel();
    const streamingCore = level === 1 && !isFlashcardMode && isAnyQuizGenerationActive() && isOpenQuizStillGenerating();
    const fallbackHistory = new Set(!isFlashcardMode ? highYieldQuizState.streamedFallbackQuestionKeys : []);
    // Core first, then standard, then detail; the stable sort keeps slide or shuffle order within a tier.
    return indices.filter(index => questions[index] && (streamingCore || getQuizQuestionViewTier(questions[index]) <= level
        || fallbackHistory.has(buildQuestionPersistenceKeyByIndex(index))))
        .sort((a, b) => (getQuizQuestionViewTier(questions[a]) - getQuizQuestionViewTier(questions[b]))
            || (isQuizQuestionShownLast(questions[a]) - isQuizQuestionShownLast(questions[b])));
}
function getHighYieldBaseQuestionIndices() {
    // Learn All-cards review owns the whole selected deck; its pass filter is not a quiz subset.
    // A base captured at first arrival must not hide cards that stream in later.
    if (typeof isFlashcardMode !== 'undefined' && isFlashcardMode && !isFlashcardAdaptiveReviewMode
        && flashcardReviewScope === 'all' && questions.length && questions.every(question => question.flashcardOnly === true)) {
        // Review the missed is a chosen subset; the tier filter works inside it.
        if (typeof flashcardPassSubsetKeys !== 'undefined' && Array.isArray(flashcardPassSubsetKeys)) return buildFilteredIndicesFromQuestionKeyList(flashcardPassSubsetKeys);
        return questions.map((_, index) => index);
    }
    return highYieldQuizState.baseIsFilterActive
        ? buildFilteredIndicesFromQuestionKeyList(highYieldQuizState.baseFilteredQuestionKeys)
        : questions.map((_, index) => index);
}
function applyHighYieldQuizPool({ preserveCurrentQuestion = true, captureBase = false, currentRealIdx = filteredIndices[currentQIndex] } = {}) {
    if (!isHighYieldQuizEnabled()) {
        highYieldLastAppliedPool = null;
        syncHighYieldQuizControls();
        return;
    }
    if (captureBase) {
        highYieldQuizState.baseFilteredQuestionKeys = buildFilteredQuestionKeysForPersistence(filteredIndices);
        highYieldQuizState.baseIsFilterActive = !!isFilterActive;
    }
    const base = getHighYieldBaseQuestionIndices();
    if (preserveCurrentQuestion && getSelectedQuizTierLevel() === 1 && !isFlashcardMode
        && isAnyQuizGenerationActive() && isOpenQuizStillGenerating() && base.includes(currentRealIdx)
        && getQuizQuestionViewTier(questions[currentRealIdx]) > 1) {
        highYieldQuizState.streamedFallbackQuestionKeys = normalizeQuizCompletionQuestionKeys([
            ...(highYieldQuizState.streamedFallbackQuestionKeys || []), buildQuestionPersistenceKeyByIndex(currentRealIdx)
        ]);
    }
    const target = filterHighYieldQuestionIndices(base);
    const next = buildPreservedQuestionPoolIndices(target, filteredIndices);
    highYieldQuizState.pinnedQuestionKey = '';
    if (preserveCurrentQuestion && Number.isInteger(currentRealIdx) && questions[currentRealIdx] && !next.includes(currentRealIdx)) {
        const position = isShuffled ? Math.min(currentQIndex, next.length) : next.findIndex(index => index > currentRealIdx);
        next.splice(position < 0 ? next.length : position, 0, currentRealIdx);
        highYieldQuizState.pinnedQuestionKey = buildQuestionPersistenceKeyByIndex(currentRealIdx);
    }
    const changed = next.length !== filteredIndices.length || next.some((index, position) => index !== filteredIndices[position]);
    filteredIndices = next;
    highYieldLastAppliedPool = filteredIndices;
    currentQIndex = preserveCurrentQuestion && next.includes(currentRealIdx)
        ? next.indexOf(currentRealIdx) : Math.min(Math.max(currentQIndex, 0), Math.max(0, next.length - 1));
    if (changed) resetQuizCompletionCohort('high-yield-pool');
    syncHighYieldQuizControls();
}
// The level picker beside the progress counter: "· Core ▾" opens Core, Extended and All with
// their question counts. Levels only choose what to study; no question is ever removed.
function syncHighYieldQuizControls() {
    syncFlashcardTierControl();
    const header = $('quiz-high-yield-count');
    if (!header) return;
    const available = hasHighYieldQuizLabels();
    header.hidden = !available;
    header.classList.remove('memora-selected-generation-shimmer');
    if (!available) return;
    if (!header.querySelector('.quiz-tier-trigger')) buildQuizTierControl(header);
    const selected = isHighYieldQuizEnabled() ? getSelectedQuizTierLevel() : 2;
    renderQuizTierMenu(header, selected, QUIZ_TIER_LEVELS.map(({ level }) => countQuizQuestionsUpToLevel(level)));
}
// Flashcards study the quiz's level (renderFlashcardGrid applies the same pool), so the review
// header carries the same picker.
function syncFlashcardTierControl() {
    const header = $('fc-tier-control');
    if (!header) return;
    const available = hasHighYieldQuizLabels();
    header.hidden = !available;
    if (!available) return;
    if (!header.querySelector('.quiz-tier-trigger')) buildQuizTierControl(header, 'Cards to study');
    renderQuizTierMenu(header, isHighYieldQuizEnabled() ? getSelectedQuizTierLevel() : 2, QUIZ_TIER_LEVELS.map(({ level }) => countQuizQuestionsUpToLevel(level)));
}
// Shared by the quiz header and the flashcard review header.
function renderQuizTierMenu(header, selected, counts) {
    const signature = [selected, ...counts].join('|');
    if (header.dataset.tierSignature === signature) return;
    header.dataset.tierSignature = signature;
    const current = QUIZ_TIER_LEVELS[selected - 1];
    const trigger = header.querySelector('.quiz-tier-trigger');
    trigger.querySelector('.quiz-tier-label').textContent = current.label;
    trigger.setAttribute('aria-label', `Studying ${current.label}: ${current.hint}. Change level.`);
    // A level that would add no questions (a quiz with Core questions only) is left out.
    header.querySelector('.quiz-tier-menu').replaceChildren(...QUIZ_TIER_LEVELS.filter(({ level }) => level === 1 || level === selected || counts[level - 1] > counts[level - 2]).map(({ level, label, hint }) => {
        const item = document.createElement('button');
        item.type = 'button';
        item.className = 'quiz-tier-item';
        item.dataset.level = String(level);
        item.setAttribute('role', 'option');
        item.setAttribute('aria-selected', String(level === selected));
        item.classList.toggle('is-current', level === selected);
        item.disabled = counts[level - 1] === 0;
        const name = document.createElement('span');
        name.className = 'quiz-tier-name';
        name.textContent = label;
        const count = document.createElement('span');
        count.className = 'quiz-tier-count';
        count.textContent = String(counts[level - 1]);
        const note = document.createElement('span');
        note.className = 'quiz-tier-hint';
        note.textContent = hint;
        item.append(name, count, note);
        return item;
    }));
}
function buildQuizTierControl(header, menuLabel = 'Questions to study') {
    header.replaceChildren();
    delete header.dataset.tierSignature;
    const dot = document.createElement('span');
    dot.className = 'quiz-tier-dot';
    dot.setAttribute('aria-hidden', 'true');
    dot.textContent = '·';
    const trigger = document.createElement('button');
    trigger.type = 'button';
    trigger.className = 'quiz-tier-trigger';
    trigger.setAttribute('aria-haspopup', 'listbox');
    trigger.setAttribute('aria-expanded', 'false');
    const label = document.createElement('span');
    label.className = 'quiz-tier-label';
    const chevron = document.createElement('span');
    chevron.className = 'quiz-tier-chevron';
    chevron.setAttribute('aria-hidden', 'true');
    chevron.textContent = '▾';
    trigger.append(label, chevron);
    const menu = document.createElement('div');
    menu.className = 'quiz-tier-menu';
    menu.setAttribute('role', 'listbox');
    menu.setAttribute('aria-label', menuLabel);
    menu.hidden = true;
    header.append(dot, trigger, menu);
    const close = () => {
        menu.hidden = true;
        trigger.setAttribute('aria-expanded', 'false');
    };
    trigger.addEventListener('click', () => {
        menu.hidden = !menu.hidden;
        trigger.setAttribute('aria-expanded', String(!menu.hidden));
        if (!menu.hidden) (menu.querySelector('.is-current') || menu.querySelector('.quiz-tier-item:not(:disabled)'))?.focus();
    });
    menu.addEventListener('click', (event) => {
        const item = event.target.closest('.quiz-tier-item');
        if (!item || item.disabled) return;
        close();
        setQuizTierView(Number(item.dataset.level));
        trigger.focus({ preventScroll: true });
    });
    header.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && !menu.hidden) {
            event.preventDefault();
            close();
            trigger.focus({ preventScroll: true });
        } else if (!menu.hidden && (event.key === 'ArrowDown' || event.key === 'ArrowUp')) {
            event.preventDefault();
            const items = [...menu.querySelectorAll('.quiz-tier-item:not(:disabled)')];
            const index = items.indexOf(document.activeElement);
            items[Math.max(0, Math.min(items.length - 1, index + (event.key === 'ArrowDown' ? 1 : -1)))]?.focus();
        }
    });
    document.addEventListener('pointerdown', (event) => {
        if (!header.contains(event.target)) close();
    });
}
function toggleHighYieldQuizView(forceEnabled = null, { save = true, refreshView = true } = {}) {
    if (!hasHighYieldQuizLabels()) return false;
    const currentRealIdx = filteredIndices[currentQIndex];
    const nextEnabled = forceEnabled === null ? !isHighYieldQuizEnabled() : !!forceEnabled;
    if (nextEnabled) {
        if (condensedQuizState.enabled) {
            restoreCondensedBaseQuestionPoolSnapshot();
            condensedQuizState.enabled = false;
            syncCondensedQuizToggleButton();
        }
        highYieldQuizState = normalizeHighYieldQuizState({
            enabled: true,
            level: highYieldQuizState.level,
            baseFilteredQuestionKeys: buildFilteredQuestionKeysForPersistence(filteredIndices),
            baseIsFilterActive: isFilterActive,
            streamedFallbackQuestionKeys: highYieldQuizState.streamedFallbackQuestionKeys
        });
        highYieldLastAppliedPool = null;
        applyHighYieldQuizPool({ currentRealIdx });
    } else {
        const base = getHighYieldBaseQuestionIndices();
        highYieldQuizState.enabled = false;
        highYieldQuizState.pinnedQuestionKey = '';
        filteredIndices = isShuffled ? buildPreservedQuestionPoolIndices(base, filteredIndices) : base;
        currentQIndex = filteredIndices.includes(currentRealIdx) ? filteredIndices.indexOf(currentRealIdx) : 0;
        isFilterActive = highYieldQuizState.baseIsFilterActive;
        highYieldLastAppliedPool = null;
    }
    if (isShuffled) applySmartShuffle({ showNotice: false, shuffleUnanswered: false, preserveCurrentQuestion: true, sortAnswered: false });
    if (isHighYieldQuizEnabled()) highYieldLastAppliedPool = filteredIndices;
    syncHighYieldQuizControls();
    if (refreshView) {
        if (isFlashcardMode) renderFlashcardGrid();
        else renderQuestion();
    }
    if (save) {
        markSessionStateMutation();
        writeQuickSessionSnapshot();
        saveSessionDebounced();
    }
    return true;
}
// Changing level keeps every question already in the pool where it is and appends the newly
// included ones, so moving up never reshuffles what the student has seen.
function setQuizTierView(level, { continueFromNew = false, save = true, refreshView = true } = {}) {
    if (!hasHighYieldQuizLabels()) return false;
    const previousCount = filteredIndices.length;
    const currentRealIdx = filteredIndices[currentQIndex];
    highYieldQuizState.level = Number(level) >= 2 ? 2 : 1;
    if (!isHighYieldQuizEnabled()) return toggleHighYieldQuizView(true, { save, refreshView });
    applyHighYieldQuizPool({ preserveCurrentQuestion: false });
    // Continuing from the results screen starts at the first newly added question; otherwise the
    // current question stays put while it is still included.
    currentQIndex = continueFromNew
        ? Math.min(previousCount, Math.max(0, filteredIndices.length - 1))
        : Math.max(0, filteredIndices.indexOf(currentRealIdx));
    highYieldLastAppliedPool = filteredIndices;
    syncHighYieldQuizControls();
    if (refreshView) {
        if (isFlashcardMode) renderFlashcardGrid();
        else renderQuestion();
    }
    if (save) {
        markSessionStateMutation();
        writeQuickSessionSnapshot();
        saveSessionDebounced();
    }
    return true;
}
function navigatePastHighYieldPinnedQuestion(direction) {
    if (!isHighYieldQuizEnabled() || !highYieldQuizState.pinnedQuestionKey
        || buildQuestionPersistenceKeyByIndex(filteredIndices[currentQIndex]) !== highYieldQuizState.pinnedQuestionKey) return false;
    const position = currentQIndex;
    if (direction > 0 && position === filteredIndices.length - 1 && isAnyQuizGenerationActive() && isOpenQuizStillGenerating()) {
        window.handleQuizGenerationStudyBoundary?.();
        return true;
    }
    filteredIndices.splice(position, 1);
    highYieldQuizState.pinnedQuestionKey = '';
    highYieldLastAppliedPool = filteredIndices;
    resetQuizCompletionCohort('high-yield-navigation');
    const next = direction > 0 ? position : position - 1;
    if (next < 0 || next >= filteredIndices.length) {
        currentQIndex = Math.max(0, filteredIndices.length - 1);
        if (direction > 0) {
            if (advanceFromCoreIntoExtendedTier()) return true;
            if (isQuizTierFinishGated()) { renderQuestion('anim-fade'); showQuizUnansweredFinishNotice(); return true; }
            showResults();
            return true;
        }
        return false;
    }
    handleQuestionNavigationFromExpandedQuizCard();
    currentQIndex = next;
    writeQuickSessionSnapshot();
    renderQuestion('anim-fade');
    void window.requestGeminiLiveQuestionReadoutIfArmed?.({ includeChoices: true, reason: direction > 0 ? 'next-question' : 'prev-question' });
    return true;
}
// A level is finished only once nearly every question in it is answered: Next skips freely, and
// Core still rolls into Extended, but finishing with questions open asks first. The allowance
// (5%, at least one) covers a question skipped by accident.
function isQuizTierFinishGated() {
    if (!hasHighYieldQuizLabels() || !isHighYieldQuizEnabled() || isAdaptiveReviewMode) return false;
    const pool = filteredIndices.filter(index => questions[index]);
    const unanswered = pool.filter(index => userAnswers[index] === undefined).length;
    return unanswered > Math.max(1, Math.round(pool.length * 0.05));
}

// The quiet notice names the first three open questions by position and offers Do it anyway,
// which opens the results without answering them.
function showQuizUnansweredFinishNotice() {
    const open = [];
    filteredIndices.forEach((index, position) => {
        if (questions[index] && userAnswers[index] === undefined) open.push(`Q${position + 1}`);
    });
    const listed = open.slice(0, 3).join(', ') + (open.length > 3 ? ` · plus ${open.length - 3} more` : '');
    showPopup(`You haven't answered all the questions: ${escapeHtml(listed)}`, null, false,
        { kind: 'warning', duration: 9000, dismissible: true, popupClass: 'quiz-unanswered-finish-notice' });
    if (!els.popup) return;
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'popup-inline-action';
    button.textContent = 'Do it anyway';
    button.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        const close = els.popup.querySelector('.popup-inline-close');
        if (close) close.click();
        else els.popup.classList.remove('active');
        showResults();
    });
    els.popup.insertBefore(button, els.popup.querySelector('.popup-inline-close') || null);
}

// Moving past the last Core question rolls straight into Extended instead of the results screen,
// with a short notice that says how many Extended questions follow and offers Generate more.
function advanceFromCoreIntoExtendedTier() {
    if (!isHighYieldQuizEnabled() || getSelectedQuizTierLevel() !== 1) return false;
    if (isAnyQuizGenerationActive() && isOpenQuizStillGenerating()) return false;
    const base = getHighYieldBaseQuestionIndices();
    const coreCount = countQuizQuestionsUpToLevel(1, base);
    const nextLevel = [2].find(level => countQuizQuestionsUpToLevel(level, base) > coreCount);
    if (!nextLevel) return false;
    const corePool = new Set(filteredIndices);
    const coreSkipped = isQuizTierFinishGated();
    // Pacing re-sorts the pool by what it has presented; Extended questions seen in an earlier pass
    // would sort in among the Core run, so the history restarts as exactly that run.
    if (isAdaptivePracticeActive()) adaptivePracticeState.historyKeys = buildFilteredQuestionKeysForPersistence(filteredIndices);
    handleQuestionNavigationFromExpandedQuizCard();
    resetQuizCompletionCohort('high-yield-auto-extended');
    if (!setQuizTierView(nextLevel, { continueFromNew: true, save: false, refreshView: false })) return false;
    // Land on the first question Core did not hold, so question 12 is followed by 13, never by 1.
    const firstNew = filteredIndices.findIndex(index => !corePool.has(index));
    if (firstNew >= 0) currentQIndex = firstNew;
    markSessionStateMutation();
    writeQuickSessionSnapshot();
    saveSessionDebounced();
    if (firstNew >= 0) {
        renderQuestion('anim-fade');
        const extendedCount = countQuizQuestionsUpToLevel(nextLevel, base) - coreCount;
        const canGenerateMore = !!window.getOpenAutoQuizName?.() && !window.isAutoQuizMoreGenerating?.();
        showPopup(coreCount
            ? `${coreSkipped ? 'Moving on' : 'Core done'}. ${extendedCount} Extended question${extendedCount === 1 ? '' : 's'} next.`
            : `No Core questions yet. ${extendedCount} Extended question${extendedCount === 1 ? '' : 's'} next.`, null, false,
            { kind: 'success', duration: canGenerateMore ? 9000 : 4000, dismissible: canGenerateMore, popupClass: 'quiz-core-done-notice' });
        if (canGenerateMore) appendAutoQuizMoreNoticeButton();
        void window.requestGeminiLiveQuestionReadoutIfArmed?.({ includeChoices: true, reason: 'next-question' });
    } else showResults();
    return true;
}

/* A flashcard pass at Core rolls into Extended the way the quiz does, with the same short notice.
   It happens once every Core card is rated, or when the last card is rated with about 5% (at least
   one) left unrated by accident; the arrows alone never end Core. */
function advanceFlashcardsFromCoreIntoExtended(ratedPosition) {
    if (!isHighYieldQuizEnabled() || getSelectedQuizTierLevel() !== 1 || flashcardReviewScope !== 'all' || isFlashcardAdaptiveReviewMode) return false;
    if (isOpenFlashcardDeckGenerating() || window.isAutoFlashcardMoreGenerating?.()) return false;
    const unrated = filteredIndices.filter(index => !flashcardPassRatings.has(index)).length;
    if (unrated && !(ratedPosition === filteredIndices.length - 1 && unrated <= Math.max(1, Math.round(filteredIndices.length * 0.05)))) return false;
    const base = getHighYieldBaseQuestionIndices();
    const coreCount = countQuizQuestionsUpToLevel(1, base);
    const extendedCount = countQuizQuestionsUpToLevel(2, base) - coreCount;
    if (extendedCount <= 0) return false;
    const corePool = new Set(filteredIndices);
    if (!setQuizTierView(2, { save: false, refreshView: false })) return false;
    flashcardPassComplete = false;
    currentQIndex = Math.max(0, filteredIndices.findIndex(index => !corePool.has(index)));
    flashcardDisplayAnchor = getFlashcardDisplayKey(filteredIndices[currentQIndex]);
    markSessionStateMutation();
    saveSessionDebounced();
    renderFlashcardGrid();
    const canGenerateMore = !!window.getOpenAutoFlashcardDeckKey?.();
    showPopup(`Core done. ${extendedCount} Extended card${extendedCount === 1 ? '' : 's'} next.`, null, false,
        { kind: 'success', duration: canGenerateMore ? 9000 : 4000, dismissible: canGenerateMore, popupClass: 'quiz-core-done-notice' });
    if (canGenerateMore) appendAutoQuizMoreNoticeButton(startAutoFlashcardsMoreInBackground);
    return true;
}

function startAutoFlashcardsMoreInBackground() {
    if (!window.getOpenAutoFlashcardDeckKey?.() || window.isAutoFlashcardMoreGenerating?.()) return false;
    void window.generateMoreAutoFlashcards().catch(error => console.warn('[Flashcards] Generate more failed:', error));
    syncFlashcardGenerationShimmer();
    return true;
}

// The Core-done notice carries Generate more; the button becomes a short status once pressed.
function appendAutoQuizMoreNoticeButton(start = startAutoQuizMoreInBackground) {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'popup-inline-action';
    button.textContent = 'Generate more';
    button.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        if (!start()) return;
        button.replaceWith(Object.assign(document.createElement('span'), { className: 'popup-inline-status', textContent: 'Writing more…' }));
    });
    const close = els.popup.querySelector('.popup-inline-close');
    els.popup.insertBefore(button, close || null);
}

// Starts the next auto set; questions stream into the open quiz.
function startAutoQuizMoreInBackground() {
    if (typeof window.generateMoreAutoQuizQuestions !== 'function' || !window.getOpenAutoQuizName?.()) return false;
    if (window.isAutoQuizMoreGenerating?.() || isAnyQuizGenerationActive()) return false;
    void window.generateMoreAutoQuizQuestions().catch(error => console.warn('[Quiz] Generate more failed:', error));
    return true;
}
window.startAutoQuizMoreInBackground = startAutoQuizMoreInBackground;

// An auto quiz whose levels are all studied offers its next set.
function appendAutoQuizMoreAction() {
    if (!window.getOpenAutoQuizName?.()) return;
    const actions = els.resultsView.querySelector('.ss-next-actions') || els.resultsView;
    if (window.isAutoQuizMoreGenerating?.() || isAnyQuizGenerationActive()) {
        const note = document.createElement('p');
        note.id = 'quiz-auto-more-status';
        note.className = 'ss-next-note';
        note.textContent = 'More questions are being written. They join this quiz as they arrive.';
        actions.appendChild(note);
        return;
    }
    const button = document.createElement('button');
    button.type = 'button';
    button.id = 'quiz-auto-more';
    button.className = 'ss-btn';
    button.textContent = 'Generate more questions';
    button.addEventListener('click', () => {
        if (!startAutoQuizMoreInBackground()) return;
        button.replaceWith(Object.assign(document.createElement('p'), { id: 'quiz-auto-more-status', className: 'ss-next-note', textContent: 'More questions are being written. They join this quiz as they arrive.' }));
    });
    actions.appendChild(button);
}

function appendHighYieldContinueAction() {
    if (!isHighYieldQuizEnabled()) { appendAutoQuizMoreAction(); return; }
    const level = getSelectedQuizTierLevel();
    const base = getHighYieldBaseQuestionIndices();
    const studied = countQuizQuestionsUpToLevel(level, base);
    const nextLevel = [2].find(candidate => candidate > level && countQuizQuestionsUpToLevel(candidate, base) > studied);
    if (!nextLevel) { appendAutoQuizMoreAction(); return; }
    const more = countQuizQuestionsUpToLevel(nextLevel, base) - studied;
    const button = document.createElement('button');
    button.type = 'button';
    button.id = 'quiz-high-yield-continue';
    button.className = 'ss-btn';
    button.textContent = `Continue with ${QUIZ_TIER_LEVELS[nextLevel - 1].label} (${more} more)`;
    button.addEventListener('click', () => {
        resetQuizCompletionCohort('high-yield-continue');
        setQuizTierView(nextLevel, { continueFromNew: true });
    });
    const actions = els.resultsView.querySelector('.ss-next-actions') || els.resultsView;
    actions.appendChild(button);
}
window.toggleHighYieldQuizView = toggleHighYieldQuizView;
window.syncHighYieldQuizControls = syncHighYieldQuizControls;
window.setQuizTierView = setQuizTierView;

function getDefaultQuestionPoolIndices() {
    if (condensedQuizState.enabled && hasCondensedQuizSelection()) {
        const condensedIndices = getCondensedQuestionIndices();
        if (condensedIndices.length > 0) return condensedIndices;
    }
    return moveShownLastQuestionsBehind(filterHighYieldQuestionIndices(questions.map((_, i) => i)));
}
function captureCurrentQuestionPoolSnapshot() {
    const currentRealIdx = Number.isInteger(filteredIndices?.[currentQIndex]) ? filteredIndices[currentQIndex] : -1;
    return {
        baseFilteredQuestionKeys: buildFilteredQuestionKeysForPersistence(filteredIndices),
        baseCurrentQuestionKey: Number.isInteger(currentRealIdx) && currentRealIdx >= 0
            ? (buildQuestionPersistenceKeyByIndex(currentRealIdx) || '')
            : '',
        baseCurrentQuestionViewIndex: Number.isInteger(currentQIndex) ? currentQIndex : 0,
        baseIsFilterActive: !!isFilterActive
    };
}
function buildPreservedQuestionPoolIndices(targetIndices, existingIndices = filteredIndices) {
    const target = Array.isArray(targetIndices)
        ? targetIndices.map((idx) => Number(idx)).filter((idx) => Number.isInteger(idx) && idx >= 0 && idx < questions.length)
        : [];
    if (target.length === 0) return [];

    const targetLookup = new Set(target);
    const preserved = [];
    const seen = new Set();

    (Array.isArray(existingIndices) ? existingIndices : []).forEach((rawIdx) => {
        const idx = Number(rawIdx);
        if (!Number.isInteger(idx) || !targetLookup.has(idx) || seen.has(idx)) return;
        seen.add(idx);
        preserved.push(idx);
    });

    target.forEach((idx) => {
        if (seen.has(idx)) return;
        seen.add(idx);
        preserved.push(idx);
    });

    return preserved;
}
function restoreCondensedBaseQuestionPoolSnapshot() {
    const baseQuestionKeys = Array.isArray(condensedQuizState?.baseFilteredQuestionKeys)
        ? condensedQuizState.baseFilteredQuestionKeys
        : [];
    const restoredIndices = buildFilteredIndicesFromQuestionKeyList(baseQuestionKeys);
    if (restoredIndices.length === 0) return false;

    filteredIndices = restoredIndices;
    resetQuizCompletionCohort('condensed-pool-restore');
    isFilterActive = !!condensedQuizState?.baseIsFilterActive;

    const currentQuestionKey = String(condensedQuizState?.baseCurrentQuestionKey || '').trim();
    if (currentQuestionKey) {
        const nextIndex = filteredIndices.findIndex((realIdx) => buildQuestionPersistenceKeyByIndex(realIdx) === currentQuestionKey);
        if (nextIndex >= 0) {
            currentQIndex = nextIndex;
            return true;
        }
    }

    const fallbackIndex = Math.max(0, Math.min(
        Number(condensedQuizState?.baseCurrentQuestionViewIndex) || 0,
        Math.max(filteredIndices.length - 1, 0)
    ));
    currentQIndex = fallbackIndex;
    return true;
}
function ensureCondensedQuizToggleButton() {
    if (!els.menuDropdown) return null;
    let btn = $(CONDENSED_QUIZ_TOGGLE_BTN_ID);
    if (!hasCondensedQuizSelection()) {
        if (btn) {
            const wrapped = btn.closest('.tool-help-item-wrap');
            if (wrapped) wrapped.remove();
            else btn.remove();
        }
        return null;
    }

    if (!btn) {
        btn = document.createElement('button');
        btn.type = 'button';
        btn.id = CONDENSED_QUIZ_TOGGLE_BTN_ID;
        btn.className = 'glass-btn';
        btn.title = 'Toggle condensed question view';
        btn.addEventListener('click', (event) => {
            event.preventDefault();
            event.stopPropagation();
            toggleCondensedQuizView();
            requestAnimationFrame(closeMenuDropdown);
        });
    }
    const dropdown = els.menuDropdown;
    const buttonContainer = ensureToolsMenuButtonWrapped(btn);
    const anchor = els.shuffleBtn?.closest('.tool-help-item-wrap')
        || els.shuffleBtn
        || els.mixAnswersBtn?.closest('.tool-help-item-wrap')
        || els.mixAnswersBtn
        || els.zenModeBtn?.closest('.tool-help-item-wrap')
        || els.zenModeBtn
        || dropdown.lastElementChild;
    if (buttonContainer.parentElement !== dropdown) {
        if (anchor && anchor.parentElement === dropdown) {
            anchor.insertAdjacentElement('afterend', buttonContainer);
        } else {
            dropdown.appendChild(buttonContainer);
        }
    } else if (anchor && anchor.parentElement === dropdown && buttonContainer.previousElementSibling !== anchor) {
        anchor.insertAdjacentElement('afterend', buttonContainer);
    }
    return btn;
}
function syncCondensedQuizToggleButton() {
    const btn = ensureCondensedQuizToggleButton();
    if (!btn) return;

    const visibleCount = getCondensedVisibleQuestionCount();
    const sourceCount = Math.max(Number(condensedQuizState.sourceQuestionCount) || 0, visibleCount);
    const label = condensedQuizState.enabled ? 'Condensed View On' : 'Condensed View Off';
    btn.classList.toggle('active', !!condensedQuizState.enabled);
    btn.title = condensedQuizState.enabled
        ? `Disable condensed question view (${visibleCount}/${sourceCount})`
        : `Enable condensed question view (${visibleCount}/${sourceCount})`;
    btn.innerHTML = `
        <span class="gradient-text">${label}</span>
        ${visibleCount ? `<span style="margin-left:auto; color:#94a3b8; font-size:0.82rem; white-space:nowrap;">${visibleCount}/${sourceCount}</span>` : ''}
    `;
}
function refreshQuizViewForCurrentQuestionPool({ preserveCurrentQuestion = false } = {}) {
    const currentRealIdx = Number.isInteger(filteredIndices?.[currentQIndex]) ? filteredIndices[currentQIndex] : -1;
    if (isHighYieldQuizEnabled()) {
        applyHighYieldQuizPool({ preserveCurrentQuestion });
    } else if (condensedQuizState.enabled && hasCondensedQuizSelection()) {
        filteredIndices = buildPreservedQuestionPoolIndices(getCondensedQuestionIndices(), filteredIndices);
        if (filteredIndices.length === 0) {
            filteredIndices = getCondensedQuestionIndices();
        }
    } else if (!restoreCondensedBaseQuestionPoolSnapshot()) {
        const defaultIndices = questions.map((_, i) => i);
        filteredIndices = buildPreservedQuestionPoolIndices(defaultIndices, filteredIndices);
        if (filteredIndices.length === 0) {
            filteredIndices = defaultIndices;
        }
        isFilterActive = false;
    }

    if (isShuffled) {
        applySmartShuffle({
            showNotice: false,
            shuffleUnanswered: false,
            preserveCurrentQuestion,
            sortAnswered: false
        });
    } else if (filteredIndices.length === 0) {
        currentQIndex = 0;
    } else if (preserveCurrentQuestion && currentRealIdx >= 0) {
        const nextIndex = filteredIndices.indexOf(currentRealIdx);
        currentQIndex = nextIndex >= 0 ? nextIndex : Math.min(currentQIndex, filteredIndices.length - 1);
    } else {
        currentQIndex = Math.min(Math.max(currentQIndex, 0), filteredIndices.length - 1);
    }

    if (isHighYieldQuizEnabled()) highYieldLastAppliedPool = filteredIndices;
    if (isFlashcardMode) renderFlashcardGrid();
    else renderQuestion();
}
function setCondensedQuizState(rawState, options = {}) {
    const {
        enableView = null,
        refreshView = false,
        preserveCurrentQuestion = false,
        save = true,
        silent = true
    } = options || {};
    const normalized = normalizeCondensedQuizState(rawState);
    if ((enableView === null ? normalized.enabled : enableView) && normalized.questionKeys.length > 0 && isHighYieldQuizEnabled()) {
        toggleHighYieldQuizView(false, { save: false, refreshView: false });
    }
    if (normalized.questionKeys.length > 0) {
        const baseSnapshot = (condensedQuizState.enabled && Array.isArray(condensedQuizState.baseFilteredQuestionKeys) && condensedQuizState.baseFilteredQuestionKeys.length > 0)
            ? {
                baseFilteredQuestionKeys: [...condensedQuizState.baseFilteredQuestionKeys],
                baseCurrentQuestionKey: String(condensedQuizState.baseCurrentQuestionKey || '').trim(),
                baseCurrentQuestionViewIndex: Math.max(0, Number(condensedQuizState.baseCurrentQuestionViewIndex) || 0),
                baseIsFilterActive: !!condensedQuizState.baseIsFilterActive
            }
            : captureCurrentQuestionPoolSnapshot();
        Object.assign(normalized, baseSnapshot);
    }
    normalized.enabled = enableView === null ? normalized.enabled : (!!enableView && normalized.questionKeys.length > 0);
    normalized.updatedAt = Math.max(normalized.updatedAt, Date.now());
    condensedQuizState = normalized;
    syncCondensedQuizToggleButton();

    if (refreshView) {
        refreshQuizViewForCurrentQuestionPool({ preserveCurrentQuestion });
    }

    if (save) {
        markSessionStateMutation();
        saveSessionDebounced();
    }

    if (!silent && hasCondensedQuizSelection()) {
        const visibleCount = getCondensedQuestionIndices().length || condensedQuizState.questionKeys.length;
        showPopup(
            condensedQuizState.enabled
                ? `Condensed view ready: ${visibleCount} high-yield question${visibleCount === 1 ? '' : 's'}.`
                : 'Condensed bank updated.',
            null,
            false
        );
    }
    return { ...condensedQuizState };
}
function clearCondensedQuizState(options = {}) {
    return setCondensedQuizState(createDefaultCondensedQuizState(), {
        ...options,
        enableView: false
    });
}
function toggleCondensedQuizView(forceEnabled = null, options = {}) {
    const nextEnabled = forceEnabled === null ? !condensedQuizState.enabled : !!forceEnabled;
    if (nextEnabled && !hasCondensedQuizSelection()) {
        if (!options?.silent) showPopup('No condensed quiz exists for this session yet.', null, true);
        return false;
    }
    if (nextEnabled) {
        const visibleCount = getCondensedQuestionIndices().length;
        if (visibleCount === 0) {
            if (!options?.silent) showPopup('The condensed quiz cannot be shown because its source questions are not active.', null, true);
            return false;
        }
    }

    if (nextEnabled && isHighYieldQuizEnabled()) toggleHighYieldQuizView(false, { save: false, refreshView: false });
    const snapshotWhenEnabling = nextEnabled ? captureCurrentQuestionPoolSnapshot() : null;
    condensedQuizState = normalizeCondensedQuizState({
        ...condensedQuizState,
        ...(snapshotWhenEnabling || {}),
        enabled: nextEnabled,
        updatedAt: Date.now()
    });
    syncCondensedQuizToggleButton();

    if (options?.refreshView !== false) {
        refreshQuizViewForCurrentQuestionPool({ preserveCurrentQuestion: true });
    }

    if (options?.save !== false) {
        markSessionStateMutation();
        saveSessionDebounced();
    }

    if (!options?.silent) {
        const count = getCondensedQuestionIndices().length || condensedQuizState.questionKeys.length;
        showPopup(
            nextEnabled
                ? `Condensed view enabled: ${count} high-yield question${count === 1 ? '' : 's'}.`
                : 'Returned to the full question bank.',
            null,
            false
        );
    }
    return true;
}
window.getCondensedQuizState = () => ({ ...normalizeCondensedQuizState(condensedQuizState) });
window.setCondensedQuizState = setCondensedQuizState;
window.clearCondensedQuizState = clearCondensedQuizState;
window.toggleCondensedQuizView = toggleCondensedQuizView;
function sanitizeIndexedStateObject(rawState, transformValue = null) {
    const clean = {};
    if (!rawState || typeof rawState !== 'object') return clean;
    Object.entries(rawState).forEach(([idxKey, rawValue]) => {
        const idx = Number(idxKey);
        if (!Number.isInteger(idx) || idx < 0 || idx >= questions.length) return;
        const value = (typeof transformValue === 'function')
            ? transformValue(rawValue, idx)
            : rawValue;
        if (value !== undefined) clean[idx] = value;
    });
    return clean;
}
function restoreIndexedStateFromLegacyAndQuestionKey(indexStateRaw, keyedBucketsRaw, transformValue = null) {
    // Only records without a keyed map use the legacy positional fallback.
    // Missing keys in a modern record mean unanswered/unset, not missing data.
    return hasQuestionKeyStateBuckets(keyedBucketsRaw)
        ? buildIndexStateFromQuestionKeyBuckets(keyedBucketsRaw, transformValue)
        : sanitizeIndexedStateObject(indexStateRaw, transformValue);
}

function countStateEntries(value) {
    return (value && typeof value === 'object') ? Object.keys(value).length : 0;
}

function mergeStateObjectMaps(existingState, incomingState) {
    const existing = (existingState && typeof existingState === 'object') ? existingState : {};
    const incoming = (incomingState && typeof incomingState === 'object') ? incomingState : {};
    return { ...existing, ...incoming };
}

function mergeStringList(existingList, incomingList) {
    const merged = [];
    const seen = new Set();
    [existingList, incomingList].forEach((list) => {
        if (!Array.isArray(list)) return;
        list.forEach((entry) => {
            const value = String(entry || '').trim();
            if (!value || seen.has(value)) return;
            seen.add(value);
            merged.push(value);
        });
    });
    return merged;
}

function mergeSessionManifestState(existingManifest, incomingManifest) {
    return {
        jsons: mergeStringList(existingManifest?.jsons, incomingManifest?.jsons),
        pdfs: mergeStringList(existingManifest?.pdfs, incomingManifest?.pdfs)
    };
}

function chooseLatestThemeState(existingTheme, incomingTheme) {
    const existingTs = Number(existingTheme?.updatedAt) || 0;
    const incomingTs = Number(incomingTheme?.updatedAt) || 0;
    if (incomingTs >= existingTs) return incomingTheme || existingTheme || null;
    return existingTheme || incomingTheme || null;
}

function buildSessionProgressMetaFromContainer(container, fallbackTimestamp = null, overrides = {}) {
    const state = (container && typeof container === 'object') ? container : {};
    const fallbackUpdatedAt = Number(new Date(fallbackTimestamp || 0)) || 0;
    const rawMeta = (state.progressMeta && typeof state.progressMeta === 'object') ? state.progressMeta : {};
    const revisionValue = Number(overrides.revision ?? rawMeta.revision);
    const updatedAtValue = Number(overrides.updatedAt ?? rawMeta.updatedAt ?? fallbackUpdatedAt);
    return {
        version: SESSION_PROGRESS_META_VERSION,
        revision: Number.isInteger(revisionValue) && revisionValue >= 0 ? revisionValue : 0,
        updatedAt: updatedAtValue > 0 ? updatedAtValue : fallbackUpdatedAt,
        answeredCount: countStateEntries(state.userAnswers),
        savedCount: countStateEntries(state.savedQuestions),
        knowledgeCount: countStateEntries(state.knowledgeState),
        srsCount: countStateEntries(state.srsData),
        statsCount: countStateEntries(state.statsData),
        aiCacheCount: countStateEntries(state.aiQuestionCacheByIndex),
        adaptiveCount: countStateEntries(state.adaptiveReviewSnapshot),
        filteredCount: Array.isArray(state.filteredIndices) ? state.filteredIndices.length : 0
    };
}

function getCurrentSessionProgressMeta(overrides = {}) {
    const userAnswersState = Object.prototype.hasOwnProperty.call(overrides, 'userAnswers')
        ? overrides.userAnswers
        : getPersistableUserAnswers();
    return buildSessionProgressMetaFromContainer({
        userAnswers: userAnswersState,
        savedQuestions: Object.prototype.hasOwnProperty.call(overrides, 'savedQuestions') ? overrides.savedQuestions : savedQuestions,
        knowledgeState: Object.prototype.hasOwnProperty.call(overrides, 'knowledgeState') ? overrides.knowledgeState : knowledgeState,
        srsData: Object.prototype.hasOwnProperty.call(overrides, 'srsData') ? overrides.srsData : srsData,
        statsData: Object.prototype.hasOwnProperty.call(overrides, 'statsData') ? overrides.statsData : statsData,
        aiQuestionCacheByIndex: Object.prototype.hasOwnProperty.call(overrides, 'aiQuestionCacheByIndex') ? overrides.aiQuestionCacheByIndex : aiQuestionCacheByIndex,
        adaptiveReviewSnapshot: Object.prototype.hasOwnProperty.call(overrides, 'adaptiveReviewSnapshot') ? overrides.adaptiveReviewSnapshot : adaptiveReviewSnapshot,
        filteredIndices: Object.prototype.hasOwnProperty.call(overrides, 'filteredIndices') ? overrides.filteredIndices : filteredIndices
    }, null, {
        revision: Object.prototype.hasOwnProperty.call(overrides, 'revision') ? overrides.revision : sessionProgressRevision,
        updatedAt: Object.prototype.hasOwnProperty.call(overrides, 'updatedAt') ? overrides.updatedAt : sessionProgressUpdatedAt
    });
}

function applyLoadedSessionProgressMeta(container, fallbackTimestamp = null) {
    const meta = buildSessionProgressMetaFromContainer(container, fallbackTimestamp);
    sessionProgressRevision = meta.revision;
    sessionProgressUpdatedAt = meta.updatedAt || Number(new Date(fallbackTimestamp || 0)) || Date.now();
    return meta;
}

function markSessionStateMutation() {
    const baseRevision = Number.isInteger(sessionProgressRevision) && sessionProgressRevision >= 0 ? sessionProgressRevision : 0;
    sessionProgressRevision = baseRevision + 1;
    sessionProgressUpdatedAt = Date.now();
    return { revision: sessionProgressRevision, updatedAt: sessionProgressUpdatedAt };
}

function getSessionProgressRichnessScore(meta) {
    const safeMeta = (meta && typeof meta === 'object') ? meta : {};
    return (
        (Number(safeMeta.answeredCount) || 0) * 1000000000000 +
        (Number(safeMeta.knowledgeCount) || 0) * 100000000 +
        (Number(safeMeta.savedCount) || 0) * 1000000 +
        (Number(safeMeta.srsCount) || 0) * 10000 +
        (Number(safeMeta.statsCount) || 0) * 100 +
        (Number(safeMeta.adaptiveCount) || 0) * 10 +
        (Number(safeMeta.aiCacheCount) || 0)
    );
}

function isSessionStateOlder(candidateMeta, baselineMeta) {
    if (!candidateMeta || !baselineMeta) return false;
    const candidateRevision = Number(candidateMeta.revision);
    const baselineRevision = Number(baselineMeta.revision);
    const candidateHasRevision = Number.isInteger(candidateRevision) && candidateRevision > 0;
    const baselineHasRevision = Number.isInteger(baselineRevision) && baselineRevision > 0;

    if (candidateHasRevision && baselineHasRevision && candidateRevision !== baselineRevision) {
        return candidateRevision < baselineRevision;
    }
    if (candidateHasRevision && !baselineHasRevision) return false;
    if (!candidateHasRevision && baselineHasRevision) return true;

    const answeredGap = (Number(baselineMeta.answeredCount) || 0) - (Number(candidateMeta.answeredCount) || 0);
    if (answeredGap > 0) return true;

    const candidateScore = getSessionProgressRichnessScore(candidateMeta);
    const baselineScore = getSessionProgressRichnessScore(baselineMeta);
    if (candidateScore >= baselineScore) return false;

    const candidateUpdatedAt = Number(candidateMeta.updatedAt) || 0;
    const baselineUpdatedAt = Number(baselineMeta.updatedAt) || 0;
    return !candidateUpdatedAt || !baselineUpdatedAt || candidateUpdatedAt <= baselineUpdatedAt;
}

function buildMergedSessionProgressMeta(existingMeta, incomingMeta, mergedState) {
    const revision = Math.max(Number(existingMeta?.revision) || 0, Number(incomingMeta?.revision) || 0);
    const updatedAt = Math.max(Number(existingMeta?.updatedAt) || 0, Number(incomingMeta?.updatedAt) || 0, Date.now());
    return buildSessionProgressMetaFromContainer(mergedState, null, { revision, updatedAt });
}

function mergeSessionStateForSafeSave(existingStateRaw, incomingStateRaw) {
    const existingState = (existingStateRaw && typeof existingStateRaw === 'object') ? existingStateRaw : {};
    const incomingState = (incomingStateRaw && typeof incomingStateRaw === 'object') ? incomingStateRaw : {};
    const mergedState = { ...existingState, ...incomingState };
    [
        'userAnswers',
        'userAnswersByQuestionKey',
        'knowledgeState',
        'knowledgeStateByQuestionKey',
        'savedQuestions',
        'savedQuestionsByQuestionKey',
        'srsData',
        'srsDataByQuestionKey',
        'statsData',
        'statsDataByQuestionKey',
        'aiQuestionCacheByIndex',
        'aiQuestionCacheByQuestionKey',
        'adaptiveReviewSnapshot',
        'adaptiveReviewSnapshotByQuestionKey',
        'manualPdfLinks'
    ].forEach((field) => {
        if (existingState[field] === undefined && incomingState[field] === undefined) return;
        mergedState[field] = mergeStateObjectMaps(existingState[field], incomingState[field]);
    });

    if (existingState.manifest !== undefined || incomingState.manifest !== undefined) {
        mergedState.manifest = mergeSessionManifestState(existingState.manifest, incomingState.manifest);
    }
    if (existingState.activeSets !== undefined || incomingState.activeSets !== undefined) {
        mergedState.activeSets = mergeStringList(existingState.activeSets, incomingState.activeSets);
    }

    const existingStartTime = Number(existingState.startTime) || 0;
    const incomingStartTime = Number(incomingState.startTime) || 0;
    mergedState.startTime = existingStartTime && incomingStartTime
        ? Math.min(existingStartTime, incomingStartTime)
        : (incomingStartTime || existingStartTime || Date.now());

    const themeState = chooseLatestThemeState(existingState.themeState, incomingState.themeState);
    if (themeState) mergedState.themeState = themeState;
    if (existingState.studyDashboard !== undefined || incomingState.studyDashboard !== undefined) {
        mergedState.studyDashboard = mergeStudyDashboardState(existingState.studyDashboard, incomingState.studyDashboard);
    }
    if (existingState.studySetup !== undefined || incomingState.studySetup !== undefined) {
        mergedState.studySetup = mergeStudySetupState(existingState.studySetup, incomingState.studySetup);
    }
    if (existingState.studyPlan !== undefined || incomingState.studyPlan !== undefined) {
        mergedState.studyPlan = mergeStudyPlanState(existingState.studyPlan, incomingState.studyPlan);
    }
    return mergedState;
}

function getPersistableUserAnswers() {
    const merged = { ...userAnswers };
    Object.entries(adaptiveClearedAnswersBackup || {}).forEach(([idx, ans]) => {
        if (merged[idx] === undefined) merged[idx] = ans;
    });
    return merged;
}
function getQuickSessionSnapshot() {
    try {
        const raw = localStorage.getItem(SESSION_QUICK_SNAPSHOT_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        return (parsed && typeof parsed === 'object') ? parsed : null;
    } catch (_) {
        return null;
    }
}

function sanitizeQuestionIndexListForPersistence(indices) {
    return Array.isArray(indices)
        ? indices.map((idx) => Number(idx)).filter((idx) => Number.isInteger(idx) && idx >= 0)
        : [];
}

function getTransientQuizPersistenceOverride() {
    try {
        const getter = typeof window.getPdfTutorQuizPersistenceState === 'function'
            ? window.getPdfTutorQuizPersistenceState
            : null;
        if (!getter) return null;
        const override = getter();
        if (!override || typeof override !== 'object') return null;
        const overrideIndices = sanitizeQuestionIndexListForPersistence(override.filteredIndices);
        if (!overrideIndices.length) return null;
        const overrideCurrentQIndex = Number(override.currentQIndex);
        return {
            ...override,
            filteredIndices: overrideIndices,
            currentQIndex: Number.isInteger(overrideCurrentQIndex)
                ? Math.min(Math.max(overrideCurrentQIndex, 0), Math.max(overrideIndices.length - 1, 0))
                : 0,
            isFilterActive: !!override.isFilterActive
        };
    } catch (_) {
        return null;
    }
}

function getTransientPdfTutorQuizCheckpointPersistenceState() {
    try {
        const getter = typeof window.getPdfTutorQuizCheckpointPersistenceState === 'function'
            ? window.getPdfTutorQuizCheckpointPersistenceState
            : null;
        if (!getter) return null;
        const checkpoint = getter();
        if (!checkpoint || typeof checkpoint !== 'object' || !checkpoint.active) return null;
        const checkpointIndices = sanitizeQuestionIndexListForPersistence(checkpoint.indices);
        if (!checkpointIndices.length) return null;
        const checkpointCurrentQIndex = Number(checkpoint.currentQIndex);
        return {
            ...checkpoint,
            active: true,
            indices: checkpointIndices,
            currentQIndex: Number.isInteger(checkpointCurrentQIndex)
                ? Math.min(Math.max(checkpointCurrentQIndex, 0), Math.max(checkpointIndices.length - 1, 0))
                : 0
        };
    } catch (_) {
        return null;
    }
}

function persistNormalizedQuestionCollectionIfChanged(sourceKey, rawValue, normalizedValue) {
    const key = String(sourceKey || '').trim();
    if (!key || !Array.isArray(normalizedValue)) return;

    let changed = true;
    try {
        changed = JSON.stringify(rawValue) !== JSON.stringify(normalizedValue);
    } catch (_) { }
    if (!changed || typeof IDB === 'undefined' || typeof IDB.put !== 'function') return;

    try {
        const write = IDB.put('questions', key, normalizedValue);
        if (write && typeof write.catch === 'function') {
            write.catch((error) => console.warn(`Failed to persist normalized question set ${key}:`, error));
        }
    } catch (error) {
        console.warn(`Failed to persist normalized question set ${key}:`, error);
    }
}

function getTransientPdfTutorStudyPersistenceState() {
    try {
        const getter = typeof window.getPdfTutorStudyPersistenceState === 'function'
            ? window.getPdfTutorStudyPersistenceState
            : null;
        if (!getter) return null;
        const state = getter();
        if (!state || typeof state !== 'object' || state.active !== true) return null;
        return {
            ...state,
            active: true,
            mode: String(state.mode || '').trim() || 'flashcards',
            pdfName: String(state.pdfName || '').trim(),
            deckKey: String(state.deckKey || '').trim(),
            page: Math.max(1, Number(state.page) || 1),
            spacedReview: state.spacedReview === true
        };
    } catch (_) {
        return null;
    }
}

async function restorePdfTutorQuizCheckpointAfterSessionLoad(sessionData) {
    const checkpoint = sessionData?.pdfTutorQuizCheckpoint;
    if (!checkpoint || typeof checkpoint !== 'object' || !checkpoint.active) return false;
    try {
        const restore = typeof window.restorePdfTutorQuizCheckpointFromPersistence === 'function'
            ? window.restorePdfTutorQuizCheckpointFromPersistence
            : null;
        if (!restore) return false;

        const timeoutToken = Symbol('pdf-tutor-checkpoint-timeout');
        const timeoutPromise = new Promise((resolve) => {
            setTimeout(() => resolve(timeoutToken), SESSION_RESTORE_TUTOR_CHECKPOINT_TIMEOUT_MS);
        });
        const result = await Promise.race([Promise.resolve().then(() => restore(checkpoint)), timeoutPromise]);
        if (result === timeoutToken) {
            console.warn('Timed out restoring Tutor quiz checkpoint during session load.');
            return false;
        }
        return !!result;
    } catch (error) {
        console.warn('Failed to restore Tutor quiz checkpoint:', error);
        return false;
    }
}

function getQuizPersistenceStateForSave() {
    const override = getTransientQuizPersistenceOverride();
    if (override) return { ...override, quizCompletionCohortState: getQuizCompletionCohortPersistenceState() };
    const persistedIndices = sanitizeQuestionIndexListForPersistence(filteredIndices);
    const safeCurrentQIndex = Number.isInteger(currentQIndex)
        ? Math.min(Math.max(currentQIndex, 0), Math.max(persistedIndices.length - 1, 0))
        : 0;
    return {
        filteredIndices: persistedIndices,
        currentQIndex: safeCurrentQIndex,
        isFilterActive: !!isFilterActive,
        quizCompletionCohortState: getQuizCompletionCohortPersistenceState()
    };
}

// The snapshot records what this tab changed since its base, so a restore applies
// those changes to whatever is stored then instead of replaying a whole older state.
function buildQuickSnapshotProgressChanges(userAnswerBuckets, knowledgeBuckets, savedBuckets) {
    const sessionId = normalizeSessionId(currentSessionId);
    if (!sessionId || sessionPersistenceBase.sessionId !== sessionId) return {};
    const localBuckets = {
        userAnswers: userAnswerBuckets,
        knowledgeState: knowledgeBuckets,
        savedQuestions: savedBuckets,
        srsData: buildIndexedStateBucketsByQuestionKey(srsData),
        statsData: buildIndexedStateBucketsByQuestionKey(statsData)
    };
    const withParked = withParkedQuestionProgress(localBuckets, listObservedQuestionKeys());
    const { changes } = computeSessionProgressChanges(sessionPersistenceBase.buckets, withParked.buckets, withParked.observed);
    return { baseRevision: sessionPersistenceBase.revision, progressChanges: changes };
}

function writeQuickSessionSnapshot() {
    if (IS_EMBED_DEMO) return;
    if (!hasPersistableSessionContent()) return;
    if (!currentSessionId) currentSessionId = Date.now().toString();
    markSessionDirty();
    try {
        const quizPersistenceState = getQuizPersistenceStateForSave();
        const pdfTutorStudyState = getTransientPdfTutorStudyPersistenceState();
        const pdfTutorQuizCheckpoint = getTransientPdfTutorQuizCheckpointPersistenceState();
        const persistedUserAnswers = getPersistableUserAnswers();
        const persistedUserAnswersByQuestionKey = buildIndexedStateBucketsByQuestionKey(persistedUserAnswers);
        const persistedSavedQuestionsByQuestionKey = buildIndexedStateBucketsByQuestionKey(savedQuestions);
        const persistedKnowledgeStateByQuestionKey = buildIndexedStateBucketsByQuestionKey(knowledgeState);
        const persistedFilteredQuestionKeys = buildFilteredQuestionKeysForPersistence(quizPersistenceState.filteredIndices);
        const snapshotTimestamp = Date.now();
        let snapshot = {
            v: 1,
            sessionId: String(currentSessionId),
            ts: snapshotTimestamp,
            resumeStateUpdatedAt: snapshotTimestamp,
            userAnswers: persistedUserAnswers,
            userAnswersByQuestionKey: persistedUserAnswersByQuestionKey,
            savedQuestions: { ...savedQuestions },
            savedQuestionsByQuestionKey: persistedSavedQuestionsByQuestionKey,
            knowledgeState: { ...knowledgeState },
            knowledgeStateByQuestionKey: persistedKnowledgeStateByQuestionKey,
            currentQIndex: quizPersistenceState.currentQIndex,
            filteredIndices: quizPersistenceState.filteredIndices,
            filteredQuestionKeys: persistedFilteredQuestionKeys,
            quizCompletionCohortState: quizPersistenceState.quizCompletionCohortState,
            highYieldQuizState: normalizeHighYieldQuizState(highYieldQuizState),
            adaptivePracticeState: normalizeAdaptivePracticeState(adaptivePracticeState),
            isFilterActive: quizPersistenceState.isFilterActive,
            studyDashboard: createDefaultStudyDashboardState(studyDashboardState),
            studySetup: createDefaultStudySetupState(studySetupState),
            studyPlan: createDefaultStudyPlanState(studyPlanState),
            pdfTutorStudyState,
            progressMeta: getCurrentSessionProgressMeta({
                userAnswers: persistedUserAnswers,
                savedQuestions: { ...savedQuestions },
                knowledgeState: { ...knowledgeState },
                filteredIndices: quizPersistenceState.filteredIndices,
                updatedAt: sessionProgressUpdatedAt || snapshotTimestamp
            }),
            pdfTutorQuizCheckpoint,
            writerId: SESSION_TAB_ID,
            ...buildQuickSnapshotProgressChanges(persistedUserAnswersByQuestionKey, persistedKnowledgeStateByQuestionKey, persistedSavedQuestionsByQuestionKey)
        };
        const existingSnapshot = getQuickSessionSnapshot();
        if (existingSnapshot && String(existingSnapshot.sessionId) === String(snapshot.sessionId)) {
            const existingMeta = buildSessionProgressMetaFromContainer(existingSnapshot, existingSnapshot.ts);
            const incomingMeta = buildSessionProgressMetaFromContainer(snapshot, snapshot.ts);
            if (isSessionStateOlder(incomingMeta, existingMeta)) {
                const mergedSnapshot = mergeSessionStateForSafeSave(existingSnapshot, snapshot);
                snapshot = {
                    ...mergedSnapshot,
                    v: 1,
                    sessionId: String(currentSessionId),
                    ts: Math.max(Number(existingSnapshot.ts) || 0, Number(snapshot.ts) || 0),
                    progressMeta: buildMergedSessionProgressMeta(existingMeta, incomingMeta, mergedSnapshot)
                };
            }
        }
        localStorage.setItem(SESSION_QUICK_SNAPSHOT_KEY, JSON.stringify(snapshot));
    } catch (_) {
        // Ignore storage quota/private-mode failures.
    }
}
// options.onlyOwn: a save clears only a snapshot this tab wrote, never another
// tab's newer recovery copy of the same session.
function clearQuickSessionSnapshot(sessionId = null, options = {}) {
    try {
        if (!sessionId) {
            localStorage.removeItem(SESSION_QUICK_SNAPSHOT_KEY);
            return;
        }
        const snapshot = getQuickSessionSnapshot();
        if (options?.onlyOwn && snapshot?.writerId && snapshot.writerId !== SESSION_TAB_ID) return;
        if (snapshot && String(snapshot.sessionId) === String(sessionId)) {
            localStorage.removeItem(SESSION_QUICK_SNAPSHOT_KEY);
        }
    } catch (_) {
        // Ignore storage access failures.
    }
}
function mergeQuickSnapshotIntoSession(session, outcome = {}) {
    if (!session || !session.data) return false;
    const snapshot = getQuickSessionSnapshot();
    if (!snapshot || String(snapshot.sessionId) !== String(session.id)) return false;

    // A snapshot that carries its own changes applies them to the stored progress
    // like a save would: untouched keys take the snapshot's value, and a key the
    // stored record changed differently becomes a conflict for the learner.
    const storedHasKeyedProgress = SESSION_PROGRESS_FAMILIES.every((family) => (
        session.data[`${family}ByQuestionKey`] && typeof session.data[`${family}ByQuestionKey`] === 'object'
    ));
    if (snapshot.progressChanges && typeof snapshot.progressChanges === 'object' && storedHasKeyedProgress) {
        const remote = {};
        SESSION_PROGRESS_FAMILIES.forEach((family) => {
            remote[family] = sanitizeQuestionKeyBucketObject(session.data[`${family}ByQuestionKey`]);
        });
        const { merged, conflicts } = mergeSessionProgressChanges(remote, snapshot.progressChanges);
        conflicts.forEach((conflict) => {
            if (conflict.local === null) delete merged[conflict.family][conflict.key];
            else merged[conflict.family][conflict.key] = conflict.local;
        });
        const viewState = { ...snapshot };
        SESSION_PROGRESS_FAMILIES.forEach((family) => {
            delete viewState[family];
            delete viewState[`${family}ByQuestionKey`];
        });
        delete viewState.progressChanges;
        const mergedData = mergeSessionStateForSafeSave(session.data, viewState);
        SESSION_PROGRESS_FAMILIES.forEach((family) => {
            mergedData[`${family}ByQuestionKey`] = merged[family];
            mergedData[family] = {};
        });
        session.data = mergedData;
        outcome.conflicts = conflicts;
        return true;
    }

    const sessionMeta = buildSessionProgressMetaFromContainer(session.data, session.timestamp);
    const snapshotMeta = buildSessionProgressMetaFromContainer(snapshot, snapshot.ts);
    const sessionResumeUpdatedAt = Number(session.data.resumeStateUpdatedAt) || Number(new Date(session.timestamp || 0)) || 0;
    const snapshotResumeUpdatedAt = Number(snapshot.resumeStateUpdatedAt) || Number(snapshot.ts) || 0;
    if (isSessionStateOlder(snapshotMeta, sessionMeta) && snapshotResumeUpdatedAt <= sessionResumeUpdatedAt) return false;

    const mergedSessionData = mergeSessionStateForSafeSave(session.data, snapshot);
    mergedSessionData.progressMeta = buildMergedSessionProgressMeta(sessionMeta, snapshotMeta, mergedSessionData);
    session.data = mergedSessionData;
    return true;
}
function syncAdaptiveModeUI() {
    if (els.srsModeBtn) els.srsModeBtn.classList.toggle('active', isAdaptiveReviewMode);
    if (els.flashcardSrsModeBtn) els.flashcardSrsModeBtn.classList.toggle('active', isFlashcardAdaptiveReviewMode);
    if (els.fcSrsModeBtn) {
        els.fcSrsModeBtn.classList.toggle('active', isFlashcardAdaptiveReviewMode);
        els.fcSrsModeBtn.textContent = isFlashcardAdaptiveReviewMode ? 'Exit Flashcard Spaced Review (Anki-style)' : 'Flashcard Spaced Review (Anki-style)';
    }
    if (!isAdaptiveReviewMode) {
        pendingAdaptiveRating = null;
        adaptiveAnswerRevealedAt = 0;
        adaptiveSessionSeenIndices.clear();
        adaptiveLastRenderedIdx = null;
        adaptiveResumeAnchorIdx = null;
        closeAdaptiveRatingModal();
        closeAdaptiveSnapshotModal();
        setAdaptiveSnapshotButtonVisible(false);
        setAdaptiveSkipUndoneButtonVisible(false);
        if (els.jump) els.jump.classList.remove('adaptive-repeat-pulse');
    } else {
        setAdaptiveSkipUndoneButtonVisible(true);
    }
}
function activateAdaptiveReviewMode(showNotice = true, { seenOnly = false } = {}) {
    const dueIndices = buildAdaptiveReviewQueue(Date.now(), { seenOnly });
    if (dueIndices.length === 0) {
        showPopup('No cards are due right now. You are caught up.', null, false);
        return false;
    }
    adaptiveReviewSeenOnly = !!seenOnly;

    if (isFlashcardAdaptiveReviewMode) {
        deactivateFlashcardAdaptiveReviewMode(false);
    }

    const currentRealIdx = (Array.isArray(filteredIndices) && Number.isInteger(filteredIndices[currentQIndex]))
        ? filteredIndices[currentQIndex]
        : null;

    isAdaptiveReviewMode = true;
    adaptiveResumeAnchorIdx = Number.isInteger(currentRealIdx) ? currentRealIdx : null;
    adaptiveClearedAnswersBackup = {};
    stashAndClearAdaptiveAnswers(dueIndices);
    filteredIndices = dueIndices;
    resetQuizCompletionCohort('adaptive-pool-activate');
    isFilterActive = true;
    currentQIndex = 0;
    pendingAdaptiveRating = null;
    adaptiveAnswerRevealedAt = 0;
    adaptiveSnapshotRankFilters.clear();
    adaptiveSessionSeenIndices.clear();
    adaptiveLastRenderedIdx = null;
    isFlashcardMode = false;
    els.modeBtn.querySelector('.gradient-text').innerText = "\uD83C\uDFB4 Switch to Flashcards";
    updateAdaptiveFilterBanner();
    showQuiz();
    syncAdaptiveModeUI();

    if (showNotice) showPopup(`Quiz Spaced Review (Anki-style) started: ${dueIndices.length} cards due.`, null, false);
    saveThemeState();
    markSessionStateMutation();
    saveSessionDebounced();
    setStudyDashboardLastMode(STUDY_ACTION_KEYS.quizSpacedReview);
    return true;
}
function deactivateAdaptiveReviewMode(showNotice = true) {
    const resumeAnchorIdx = Number.isInteger(adaptiveResumeAnchorIdx) ? adaptiveResumeAnchorIdx : null;
    isAdaptiveReviewMode = false;
    adaptiveReviewSeenOnly = false;
    pendingAdaptiveRating = null;
    adaptiveAnswerRevealedAt = 0;
    closeAdaptiveRatingModal();
    closeAdaptiveSnapshotModal();
    restoreAdaptiveClearedAnswers();

    resetFilter();
    if (!isShuffled) {
        let firstUnans = -1;
        if (Number.isInteger(resumeAnchorIdx)) {
            const start = Math.min(Math.max(resumeAnchorIdx, 0), Math.max(filteredIndices.length - 1, 0));
            for (let i = start; i < filteredIndices.length; i++) {
                const idx = filteredIndices[i];
                if (userAnswers[idx] === undefined) {
                    firstUnans = i;
                    break;
                }
            }
        }
        if (firstUnans === -1) firstUnans = filteredIndices.findIndex(idx => userAnswers[idx] === undefined);
        currentQIndex = firstUnans >= 0
            ? firstUnans
            : Math.min(Math.max(currentQIndex, 0), Math.max(filteredIndices.length - 1, 0));
    }
    adaptiveResumeAnchorIdx = null;

    syncAdaptiveModeUI();
    if (isFlashcardMode) renderFlashcardGrid(); else renderQuestion();
    if (showNotice) showPopup('Quiz Spaced Review (Anki-style) turned off.', null, false);
    saveThemeState();
    markSessionStateMutation();
    saveSessionDebounced();
    flushPendingSessionSave();
}
function refreshAdaptiveQueueAfterRating() {
    const queue = buildAdaptiveReviewQueue();
    if (queue.length === 0) {
        const finishedSeenOnly = adaptiveReviewSeenOnly;
        deactivateAdaptiveReviewMode(false);
        showPopup('Quiz Spaced Review (Anki-style) complete. No cards due right now.', null, false);
        // Today's review moves on to the next session that has cards due.
        window.dispatchEvent(new CustomEvent('memora:spaced-review-complete', {
            detail: { sessionId: currentSessionId, seenOnly: finishedSeenOnly }
        }));
        return false;
    }

    filteredIndices = queue;
    resetQuizCompletionCohort('adaptive-pool-refresh');
    isFilterActive = true;
    currentQIndex = 0;
    updateAdaptiveFilterBanner();
    return true;
}

function toggleAdaptiveReviewMode() {
    if (!questions.length) {
        showPopup('Load questions first to use Quiz Spaced Review (Anki-style).', null, true);
        return;
    }
    if (isAdaptiveReviewMode) {
        deactivateAdaptiveReviewMode(true);
        return;
    }
    activateAdaptiveReviewMode(true);
}

function activateFlashcardAdaptiveReviewMode(showNotice = true) {
    flashcardReviewScope = 'due';
    flashcardPassComplete = false;
    flashcardUndoStack = [];
    const dueIndices = buildAdaptiveReviewQueue();
    if (isAdaptiveReviewMode) {
        deactivateAdaptiveReviewMode(false);
    }

    isFlashcardAdaptiveReviewMode = true;
    filteredIndices = dueIndices;
    resetQuizCompletionCohort('flashcard-adaptive-pool-activate');
    isFilterActive = true;
    currentQIndex = 0;
    fcPage = 0;
    isFlashcardMode = true;
    admitFlashcardDueQueue(dueIndices);
    const admittedCount = filteredIndices.length;
    els.modeBtn.querySelector('.gradient-text').innerText = '🎴 Switch to Quiz';
    closeAdaptiveSnapshotModal();
    syncAdaptiveModeUI();
    showFlashcards();

    if (admittedCount === 0) {
        showPopup('You are caught up. No flashcards are due right now.', null, false);
    } else if (showNotice) {
        showPopup(`Quiz Review Cards started: ${admittedCount} card${admittedCount === 1 ? '' : 's'} due.`, null, false);
    }
    markSessionStateMutation();
    saveSessionDebounced();
    setStudyDashboardLastMode(STUDY_ACTION_KEYS.quizReviewCards);
    return true;
}

function deactivateFlashcardAdaptiveReviewMode(showNotice = true) {
    isFlashcardAdaptiveReviewMode = false;
    resetFilter();
    syncAdaptiveModeUI();
    if (isFlashcardMode) renderFlashcardGrid(); else renderQuestion();
    if (showNotice) showPopup('Flashcard Spaced Review (Anki-style) turned off.', null, false);
    markSessionStateMutation();
    saveSessionDebounced();
}

// Newly admitted due cards are intersected with the selected level (Core/Extended) before a
// current card is chosen. The tier pool must not treat the first due card as the card on
// screen, or it would pin an Extended card under Core.
function admitFlashcardDueQueue(queue = buildAdaptiveReviewQueue()) {
    filteredIndices = queue;
    currentQIndex = 0;
    if (isHighYieldQuizEnabled()) applyHighYieldQuizPool({ captureBase: true, preserveCurrentQuestion: false });
    return filteredIndices;
}

function refreshFlashcardAdaptiveQueueAfterRating() {
    const lookingAt = currentQIndex;
    const queue = admitFlashcardDueQueue();
    currentQIndex = lookingAt;
    resetQuizCompletionCohort('flashcard-adaptive-pool-refresh');
    isFilterActive = true;
    if (queue.length === 0) {
        currentQIndex = 0;
        showPopup('Quiz Review Cards complete. No cards are due right now.', null, false);
        return false;
    }

    currentQIndex = Math.min(currentQIndex, Math.max(filteredIndices.length - 1, 0));
    return true;
}

function toggleFlashcardAdaptiveReviewMode() {
    if (!questions.length) {
        showPopup('Load questions first to use Quiz Review Cards.', null, true);
        return;
    }
    activateFlashcardAdaptiveReviewMode(true);
}

window.submitSRSConfidence = function (confidence) {
    if (!pendingAdaptiveRating) {
        closeAdaptiveRatingModal();
        return;
    }
    const { realIdx, isCorrect, selectedIdx } = pendingAdaptiveRating;
    const rating = ADAPTIVE_RATING_QUALITY[confidence] ? confidence : 'shaky';
    recordAdaptiveSnapshotEntry(realIdx, rating, isCorrect, selectedIdx);

    pendingAdaptiveRating = null;
    adaptiveAnswerRevealedAt = 0;
    closeAdaptiveRatingModal();

    knowledgeState[realIdx] = rating;
    updateAdaptiveSRS(realIdx, rating, isCorrect);
    if (typeof saveAICache === 'function') {
        void saveAICache();
    }
    if (isAdaptiveReviewMode) {
        const stillHasDue = refreshAdaptiveQueueAfterRating();
        if (stillHasDue) renderQuestion();
    }
    if ($('adaptive-snapshot-modal')?.classList.contains('active')) {
        renderAdaptiveSnapshotList();
    }
    markSessionStateMutation();
    saveSessionDebounced();
    flushPendingSessionSave();
};
/* --- STATS LOGIC --- */
function updateStats(idx, isCorrect, duration) {
    if (!statsData[idx]) statsData[idx] = [];
    statsData[idx].push({
        date: Date.now(),
        duration: duration,
        correct: isCorrect
    });
    window.dispatchEvent(new CustomEvent('memora:stats-updated', {
        detail: { questionIndex: idx }
    }));
}

els.statsMenuBtn.onclick = openStatsModal;
els.srsModeBtn.onclick = () => {
    toggleAdaptiveReviewMode();
    if (isAdaptiveReviewMode) setStudyDashboardLastMode(STUDY_ACTION_KEYS.quizSpacedReview);
};
if (els.flashcardSrsModeBtn) {
    els.flashcardSrsModeBtn.onclick = () => {
        toggleFlashcardAdaptiveReviewMode();
        if (isFlashcardAdaptiveReviewMode) setStudyDashboardLastMode(STUDY_ACTION_KEYS.flashcardSpacedReview);
    };
}
if (els.fcSrsModeBtn) {
    els.fcSrsModeBtn.onclick = () => {
        toggleFlashcardAdaptiveReviewMode();
        if (isFlashcardAdaptiveReviewMode) setStudyDashboardLastMode(STUDY_ACTION_KEYS.flashcardSpacedReview);
    };
}
function populateStatsInsights() {
    renderStatsActivityHeatmap();

    const qStats = buildHardestQuestionStats(20);
    const focusBtn = $('hardest-focus-btn');
    if (focusBtn) focusBtn.disabled = qStats.length === 0;
    renderStatsAttentionList(qStats.slice(0, 3));
    renderStatsHardestList(qStats);
}

function getStatsLocalDayKey(value) {
    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

function buildStatsDailyActivity() {
    const activity = {};
    Object.values(statsData || {}).forEach((attempts) => {
        if (!Array.isArray(attempts)) return;
        attempts.forEach((attempt) => {
            const key = attempt?.date ? getStatsLocalDayKey(attempt.date) : '';
            if (!key) return;
            const day = activity[key] || (activity[key] = { count: 0, correct: 0, duration: 0 });
            day.count++;
            if (attempt.correct) day.correct++;
            day.duration += Number(attempt.duration) || 0;
        });
    });
    return activity;
}

// Consecutive study days ending today; a streak survives until the end of the day after it.
function countStatsDayStreak(activity) {
    const today = new Date();
    let offset = activity[getStatsLocalDayKey(today)] ? 0 : 1;
    let streak = 0;
    for (;;) {
        const day = new Date(today.getFullYear(), today.getMonth(), today.getDate() - offset);
        if (!activity[getStatsLocalDayKey(day)]) return streak;
        streak++;
        offset++;
    }
}

function formatStatsMonthRange(first, last) {
    const month = date => date.toLocaleDateString(undefined, { month: 'short' });
    if (first.getFullYear() !== last.getFullYear()) {
        return `${month(first)} ${first.getFullYear()} – ${month(last)} ${last.getFullYear()}`;
    }
    if (first.getMonth() !== last.getMonth()) return `${month(first)} – ${month(last)} ${last.getFullYear()}`;
    return `${month(last)} ${last.getFullYear()}`;
}

function renderStatsActivityHeatmap() {
    const container = $('heatmap-container');
    if (!container) return;

    const dayCount = Number($('stats-activity-range')?.value) === 90 ? 90 : 30;
    const activity = buildStatsDailyActivity();
    const today = new Date();
    const first = new Date(today.getFullYear(), today.getMonth(), today.getDate() - (dayCount - 1));
    const cells = [];
    for (let offset = dayCount - 1; offset >= 0; offset--) {
        const date = new Date(today.getFullYear(), today.getMonth(), today.getDate() - offset);
        const day = activity[getStatsLocalDayKey(date)];
        const count = day?.count || 0;
        const level = count > 30 ? 4 : count > 15 ? 3 : count > 5 ? 2 : count > 0 ? 1 : 0;
        const label = date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
        const minutes = day ? Math.round(day.duration / 60000) : 0;
        const detail = count > 0
            ? `${label} · ${count} question${count === 1 ? '' : 's'} · ${day.correct} correct${minutes > 0 ? ` · ${minutes} min` : ''}`
            : `${label} · No study activity`;
        cells.push(`<div class="mstat-day${level ? ` l${level}` : ''}${offset === 0 ? ' today' : ''}" title="${escapeHtml(detail)}"></div>`);
    }
    container.innerHTML = cells.join('');
    container.classList.toggle('is-long', dayCount > 30);

    const monthsEl = $('stats-activity-months');
    if (monthsEl) monthsEl.textContent = formatStatsMonthRange(first, today);
    const copyEl = $('stats-activity-copy');
    if (copyEl) copyEl.textContent = `Last ${dayCount} days of quizzes and review.`;
}

function renderStatsAttentionList(items) {
    const list = $('stats-attention-list');
    if (!list) return;
    if (!items.length) {
        list.innerHTML = '<div class="mstat-empty">Nothing missed yet. Questions you get wrong will surface here.</div>';
        return;
    }
    list.innerHTML = items.map((item, i) => {
        const source = formatResultsReviewSourceTitle(item.sourceFile || 'Unknown');
        const misses = `${item.wrongCount} missed attempt${item.wrongCount === 1 ? '' : 's'}`;
        return `
            <button type="button" class="mstat-row mstat-attention-row" data-stats-question="${item.idx}">
                <span class="mstat-rank">${String(i + 1).padStart(2, '0')}</span>
                <span class="mstat-row-main">
                    <strong>Q${item.idx + 1} · ${escapeHtml(item.q)}</strong>
                    <span>${escapeHtml(source)} · ${misses}</span>
                </span>
                <span class="mstat-miss">${Math.round(item.ratio * 100)}%</span>
            </button>
        `;
    }).join('');
}

function renderStatsHardestList(items) {
    const list = $('hardest-questions-list');
    if (!list) return;
    if (!items.length) {
        list.innerHTML = '<div class="mstat-empty">No missed-question data yet.</div>';
        return;
    }
    list.innerHTML = items.map((item, i) => {
        const source = formatResultsReviewSourceTitle(item.sourceFile || 'Unknown');
        return `
            <button type="button" class="mstat-row mstat-hard-row" data-stats-question="${item.idx}">
                <span class="mstat-rank">${String(i + 1).padStart(2, '0')}</span>
                <span class="mstat-row-main">
                    <strong>${escapeHtml(item.q)}</strong>
                    <span>Q${item.idx + 1} · ${escapeHtml(source)}</span>
                </span>
                <span class="mstat-hard-meta">${item.wrongCount} of ${item.total} missed</span>
                <span class="mstat-miss">${Math.round(item.ratio * 100)}%</span>
            </button>
        `;
    }).join('');
}

function closeStatsModalLegacy(e) {
    if (e && e.target !== els.statsModal && e.target.innerText !== 'Close') return;
    els.statsModal.classList.remove('active');
}

/* --- THEME LOGIC --- */
function stopToolsMenuPositionTracking() {
    if (toolsMenuPositionFrame) {
        cancelAnimationFrame(toolsMenuPositionFrame);
        toolsMenuPositionFrame = 0;
    }
}

function updateToolsMenuDropdownPosition() {
    const dropdown = els.menuDropdown;
    const trigger = els.menuTrigger;
    if (!dropdown || !trigger || !dropdown.classList.contains('active')) return false;

    const btnRect = trigger.getBoundingClientRect();
    if (!Number.isFinite(btnRect.width) || btnRect.width <= 0 || !Number.isFinite(btnRect.height) || btnRect.height <= 0) {
        return false;
    }

    const viewportPad = 12;
    const gap = 10;
    const maxViewportHeight = Math.max(220, window.innerHeight - (viewportPad * 2));
    const availableBelow = Math.max(0, window.innerHeight - btnRect.bottom - gap - viewportPad);
    const availableAbove = Math.max(0, btnRect.top - gap - viewportPad);
    const preferredBelow = availableBelow >= Math.min(260, maxViewportHeight);
    const openAbove = !preferredBelow && availableAbove > availableBelow;
    const availableHeight = Math.max(220, openAbove ? availableAbove : availableBelow);

    dropdown.style.right = 'auto';
    dropdown.style.bottom = 'auto';
    dropdown.style.maxHeight = `${Math.min(maxViewportHeight, availableHeight)}px`;

    const maxDropdownWidth = Math.max(180, window.innerWidth - (viewportPad * 2));
    const dropdownWidth = Math.min(
        Math.max(180, Math.round(dropdown.getBoundingClientRect().width || dropdown.offsetWidth || 280)),
        maxDropdownWidth
    );
    const dropdownHeight = Math.min(
        Math.max(120, Math.round(dropdown.scrollHeight || dropdown.offsetHeight || 0)),
        Math.min(maxViewportHeight, availableHeight)
    );

    let left = btnRect.left + (btnRect.width / 2) - (dropdownWidth / 2);
    left = clampNum(left, viewportPad, Math.max(viewportPad, window.innerWidth - dropdownWidth - viewportPad));

    let top = openAbove
        ? (btnRect.top - dropdownHeight - gap)
        : (btnRect.bottom + gap);
    top = clampNum(top, viewportPad, Math.max(viewportPad, window.innerHeight - dropdownHeight - viewportPad));

    dropdown.style.left = `${Math.round(left)}px`;
    dropdown.style.top = `${Math.round(top)}px`;
    return true;
}

function startToolsMenuPositionTracking() {
    stopToolsMenuPositionTracking();

    const tick = () => {
        if (!els.menuDropdown || !els.menuDropdown.classList.contains('active')) {
            stopToolsMenuPositionTracking();
            return;
        }
        updateToolsMenuDropdownPosition();
        toolsMenuPositionFrame = requestAnimationFrame(tick);
    };

    updateToolsMenuDropdownPosition();
    toolsMenuPositionFrame = requestAnimationFrame(tick);
}

document.addEventListener('click', (e) => {
    const clickInsideThemeConfirm = !!(els.themeConfirmModal && els.themeConfirmModal.contains(e.target));
    if (els.themeTrigger && !els.themeTrigger.contains(e.target) && els.themeModal && !els.themeModal.contains(e.target)) { /* Modal handles its own close */ }
    if (els.menuTrigger && !els.menuTrigger.contains(e.target) && els.menuDropdown && !els.menuDropdown.contains(e.target)) closeMenuDropdown();
    if (els.searchInput && !els.searchInput.contains(e.target) && els.searchDropdown && !els.searchDropdown.contains(e.target)) setSearchDropdownOpen(false);
    if (els.sourceTrigger && !els.sourceTrigger.contains(e.target) && els.sourceDropdown && !els.sourceDropdown.contains(e.target) && !clickInsideThemeConfirm) els.sourceDropdown.classList.remove('active');
    // Close auto-options dropdown on click outside
    const autoTrigger = $('auto-options-trigger');
    const autoDropdown = $('auto-options-dropdown');
    if (autoTrigger && !autoTrigger.contains(e.target) && autoDropdown && !autoDropdown.contains(e.target)) { autoDropdown.classList.remove('active'); autoTrigger.setAttribute('aria-expanded', 'false'); }
    if (els.quickActionsDock && !els.quickActionsDock.contains(e.target)) closeQuickActionsDock();
    if (els.resetQBtn && !els.resetQBtn.contains(e.target) && els.refreshOptionsDropdown && !els.refreshOptionsDropdown.contains(e.target)) {
        els.refreshOptionsDropdown.classList.remove('active');
        els.resetQBtn.classList.remove('active');
    }
    syncFloatingQuizLayering();
});
function toggleDropdown(type) {
    const d = { menu: els.menuDropdown, source: els.sourceDropdown };
    Object.keys(d).forEach(k => {
        if (k === type || !d[k]) return;
        if (k === 'menu') {
            closeMenuDropdown();
            return;
        }
        d[k].classList.remove('active');
    });
    if (type === 'source') ensureSourceDropdownPortal();
    if (d[type]) {
        d[type].classList.toggle('active');
        if (type === 'menu') {
            if (d[type].classList.contains('active')) {
                syncToolsMenuGroups();
                startToolsMenuPositionTracking();
            } else {
                stopToolsMenuPositionTracking();
            }
        }
        // Draw connection lines immediately when source dropdown becomes visible
        if (type === 'source' && d[type].classList.contains('active')) {
            flushPendingSourceDropdownRender();
            requestAnimationFrame(() => drawConnectionLines());
        }
    }
    syncFloatingQuizLayering();
}
function openToolsMenuDropdown() {
    if (!els.menuDropdown) return;
    if (!els.menuDropdown.classList.contains('active')) {
        toggleDropdown('menu');
        return;
    }
    syncToolsMenuGroups();
    startToolsMenuPositionTracking();
    syncFloatingQuizLayering();
}
function closeMenuDropdown() {
    if (!els.menuDropdown) return;
    els.menuDropdown.classList.remove('active');
    stopToolsMenuPositionTracking();
    syncFloatingQuizLayering();
}

const TOOLS_HELP_VISIBILITY_KEY = 'tools_help_markers_visible';
let openToolsMenuGroups = new Set();

function syncToolsMenuGroups() {
    const dropdown = els.menuDropdown;
    if (!dropdown) return;
    const groups = Array.from(dropdown.querySelectorAll('.menu-group'));
    if (!groups.length) return;

    groups.forEach((group) => {
        const groupId = group.dataset.menuGroup || '';
        const isOpen = openToolsMenuGroups.has(groupId);
        group.classList.toggle('is-open', isOpen);
        const trigger = group.querySelector('[data-menu-group-trigger]');
        if (trigger) {
            trigger.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
            trigger.classList.toggle('active', isOpen);
        }
        const panel = group.querySelector('.menu-group-panel');
        if (!panel) return;

        const nextState = isOpen ? '1' : '0';
        const previousState = panel.dataset.menuPanelOpen || '';
        const currentHeight = panel.hidden ? 0 : panel.getBoundingClientRect().height;
        const currentOpacity = panel.hidden ? 0 : Number.parseFloat(getComputedStyle(panel).opacity || '1');
        panel.getAnimations?.().forEach((animation) => animation.cancel());
        panel.dataset.menuPanelOpen = nextState;

        const reduceMotion = getMemoraMotionMode() !== 'standard';
        const shouldAnimate = previousState !== ''
            && previousState !== nextState
            && typeof panel.animate === 'function'
            && !reduceMotion;

        if (!shouldAnimate) {
            panel.hidden = !isOpen;
            panel.style.removeProperty('height');
            panel.style.removeProperty('opacity');
            return;
        }

        panel.hidden = false;
        const expandedHeight = panel.scrollHeight;
        const fromHeight = Math.max(0, currentHeight);
        const toHeight = isOpen ? expandedHeight : 0;
        const fromOpacity = Number.isFinite(currentOpacity) ? currentOpacity : (isOpen ? 0 : 1);
        const animation = panel.animate([
            { height: `${fromHeight}px`, opacity: fromOpacity },
            { height: `${toHeight}px`, opacity: isOpen ? 1 : 0 }
        ], {
            duration: 240,
            easing: 'cubic-bezier(0.22, 1, 0.36, 1)',
            fill: 'both'
        });

        animation.onfinish = () => {
            if (panel.dataset.menuPanelOpen !== nextState) return;
            panel.hidden = !isOpen;
            panel.style.removeProperty('height');
            panel.style.removeProperty('opacity');
            if (isOpen) revealToolsMenuGroup(group);
        };
    });
}

function setupToolsMenuGroups() {
    const dropdown = els.menuDropdown;
    if (!dropdown || dropdown.dataset.groupSetup === '1') return;
    dropdown.dataset.groupSetup = '1';
    const initialOpenGroups = Array.from(dropdown.querySelectorAll('.menu-group.is-open'))
        .map((group) => group.dataset.menuGroup || '')
        .filter(Boolean);
    openToolsMenuGroups = new Set(initialOpenGroups);
    syncToolsMenuGroups();
}

function getOpenToolsMenuGroups() {
    return Array.from(openToolsMenuGroups);
}

function setOpenToolsMenuGroups(groupIds) {
    const nextGroups = Array.isArray(groupIds)
        ? groupIds.map((id) => String(id || '').trim()).filter(Boolean)
        : [];
    openToolsMenuGroups = new Set(nextGroups);
    syncToolsMenuGroups();
    if (els.menuDropdown) els.menuDropdown.scrollTop = 0;
}

function collapseAllToolsMenuGroups() {
    setOpenToolsMenuGroups([]);
}

function revealToolsMenuGroup(group) {
    if (!group || !els.menuDropdown) return;
    const dropdown = els.menuDropdown;

    // Let the panel render first, then nudge the scroll so the expanded content stays in view.
    window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => {
            const dropdownRect = dropdown.getBoundingClientRect();
            const groupRect = group.getBoundingClientRect();
            const padding = 18;

            if (groupRect.bottom > (dropdownRect.bottom - padding)) {
                dropdown.scrollBy({
                    top: groupRect.bottom - dropdownRect.bottom + padding,
                    behavior: 'smooth'
                });
            } else if (groupRect.top < (dropdownRect.top + padding)) {
                dropdown.scrollBy({
                    top: groupRect.top - dropdownRect.top - padding,
                    behavior: 'smooth'
                });
            }
        });
    });
}

function getToolsMenuHelpText(btn) {
    const byId = {
        'ai-config-btn': 'Set or update your Gemini API key used by Hint and Explain.',
        'pdf-explanation-feature-toggle': 'Toggle the legacy PDF Explanation generator and AI pane mode.',
        'ai-model-choice-toggle': 'Toggle manual AI model selection in the quiz generator.',
        'ai-fast-model-mode-toggle': 'Prefer Gemini 3.5 Flash Lite for supported AI generation across the app.',
        'quiz-gen-prompt-customization-toggle': 'Show or hide optional prompt instructions in the quiz generator.',
        'ai-batch-performance-toggle': 'Toggle batch performance controls for choosing how many PDFs generate at once.',
        'ask-memora-menu-btn': 'Open Ask Memoraful to ask about your quiz, PDFs, or how the app works.',
        'theme-menu-btn': 'Customize colors, gradients, fonts, and ambience.',
        'history-menu-btn': 'Load a prior session and continue where you left off.',
        'study-dashboard-menu-btn': 'Open the current session dashboard with counts, review status, and launch shortcuts.',
        'study-setup-menu-btn': 'Answer a session-only study setup questionnaire and regenerate the current plan.',
        'stats-menu-btn': 'Open performance analytics and hardest-question trends.',
        'pdf-tutor-tools-silent-study-btn': 'Toggle PDF Tutor Silent Study on or off from Tools.',
        'shortcuts-menu-btn': 'Open the keyboard shortcuts panel and customization controls.',
        'accessibility-high-contrast-btn': 'Boost contrast for text, borders, and menu surfaces.',
        'accessibility-reduce-motion-btn': 'Reduce animations and transitions throughout the app.',
        'accessibility-voice-agent-btn': 'Show or hide Memoraful voice controls and the voice reader card.',
        'accessibility-manual-import-btn': 'Show or hide the manual PDF import chooser and import card.',
        'srs-mode-btn': 'Toggle Quiz Spaced Review (Anki-style) with confidence-based scheduling.',
        'flashcard-srs-mode-btn': 'Toggle Flashcard Spaced Review (Anki-style) for due cards.',
        'mode-toggle': 'Switch between quiz mode and flashcard mode.',
        'shuffle-btn': 'Reorder questions; unanswered are prioritized.',
        [CONDENSED_QUIZ_TOGGLE_BTN_ID]: 'Toggle the condensed high-yield question subset on or off.',
        'hard-mode-toggle': 'Hide choices until you reveal them.',
        'mix-answers-toggle': 'Shuffle the answer order for each question.',
        'game-mode-toggle': 'Toggle XP, levels, and achievements.',
        'quiz-saved-main-btn': 'Study only questions you have saved/bookmarked.',
        'export-quiz-btn': 'Export quiz data to JSON or CSV.',
        'database-btn': 'Inspect imported files and local database entries.'
    };
    if (btn.id && byId[btn.id]) return byId[btn.id];

    const label = (btn.textContent || '').replace(/\s+/g, ' ').trim().toLowerCase();
    if (label.includes('chatgpt')) return 'Open ChatGPT for external reasoning support.';
    if (label.includes('gemini')) return 'Open Google Gemini for external assistance.';
    if (label.includes('notebooklm')) return 'Open NotebookLM in a new tab for source-grounded notes.';
    if (label.includes('api key')) return 'Set or update your Gemini API key used by Hint and Explain.';

    return btn.title || 'Open this tool action.';
}

function syncToolsMenuHelpWrapVisibility(btn) {
    if (!btn) return;
    const wrap = btn.closest('.tool-help-item-wrap');
    if (!wrap) return;
    const isHidden = btn.hidden || btn.style.display === 'none';
    wrap.hidden = isHidden;
}

function ensureToolsMenuButtonWrapped(btn) {
    if (!btn) return null;
    const existingWrap = btn.closest('.tool-help-item-wrap');
    if (existingWrap) {
        syncToolsMenuHelpWrapVisibility(btn);
        return existingWrap;
    }

    const wrap = document.createElement('div');
    wrap.className = 'tool-help-item-wrap';
    btn.parentElement?.insertBefore(wrap, btn);
    wrap.appendChild(btn);

    const marker = document.createElement('button');
    marker.type = 'button';
    marker.className = 'tool-help-marker';
    marker.textContent = '?';
    marker.dataset.tip = getToolsMenuHelpText(btn);
    marker.setAttribute('aria-label', `Help: ${marker.dataset.tip}`);
    marker.addEventListener('click', (e) => { e.preventDefault(); e.stopPropagation(); });
    marker.addEventListener('mousedown', (e) => { e.preventDefault(); e.stopPropagation(); });
    wrap.appendChild(marker);
    syncToolsMenuHelpWrapVisibility(btn);
    return wrap;
}

function setupToolsMenuHelpMarkers() {
    const dropdown = els.menuDropdown;
    if (!dropdown || dropdown.dataset.helpSetup === '1') return;
    dropdown.dataset.helpSetup = '1';
    dropdown.querySelector('.tools-help-toggle-row')?.remove();

    const menuButtons = Array.from(dropdown.querySelectorAll('button.glass-btn[data-menu-leaf="true"]'));
    menuButtons.forEach((btn) => {
        ensureToolsMenuButtonWrapped(btn);
    });

    const setMarkersVisible = (isVisible) => {
        dropdown.classList.toggle('help-markers-visible', isVisible);
        localStorage.setItem(TOOLS_HELP_VISIBILITY_KEY, isVisible ? '1' : '0');
    };
    setMarkersVisible(false);
}

// Theme Trigger now opens Modal (Moved to new ID)
let themeModalOpener = null;

function getThemeModalFocusables() {
    return Array.from(els.themeModal?.querySelectorAll('button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), summary, [href], [tabindex]:not([tabindex="-1"])') || [])
        .filter((node) => !node.closest('[hidden], [inert]')
            && node.getClientRects().length > 0
            && getComputedStyle(node).visibility !== 'hidden');
}

function openThemeModal(opener = document.activeElement) {
    if (window.MemoraSettings) return window.MemoraSettings.open({ section: 'appearance', opener });
    if (!els.themeModal) return false;
    const wasOpen = els.themeModal.classList.contains('active');
    els.themeModal.classList.add('active');
    closeMenuDropdown();
    if (wasOpen) return true;
    themeModalOpener = opener instanceof HTMLElement && opener !== document.body ? opener : null;
    // Move focus into the dialog so keyboard and screen-reader users start inside it.
    // Not MemoraModalCoordinator: its document-wide focus guard would pull focus back
    // out of dialogs Settings launches itself, such as API Key.
    requestAnimationFrame(() => {
        if (!els.themeModal.classList.contains('active') || els.themeModal.contains(document.activeElement)) return;
        const target = els.themeModal.querySelector('[role="tab"][aria-selected="true"]')
            || els.themeModal.querySelector('.theme-modal-content[role="dialog"]');
        target?.focus({ preventScroll: true });
    });
    return true;
}

function closeThemeModal() {
    if (!els.themeModal) return;
    const focusWasInside = els.themeModal.contains(document.activeElement);
    els.themeModal.classList.remove('active');
    const opener = themeModalOpener;
    themeModalOpener = null;
    // A superseding navigation has already moved focus; only return it from inside.
    if (focusWasInside && opener?.isConnected && !opener.disabled) {
        opener.focus({ preventScroll: true });
    }
}

const themeBtn = $('theme-menu-btn');
if (themeBtn) themeBtn.onclick = () => openThemeModal();
if (els.themeModal) {
    // Tab wraps at the dialog's edges instead of walking into the suspended page.
    els.themeModal.addEventListener('keydown', (event) => {
        if (event.key !== 'Tab' || !els.themeModal.classList.contains('active')) return;
        const focusables = getThemeModalFocusables();
        if (!focusables.length) return;
        const active = document.activeElement;
        const index = focusables.indexOf(active);
        if (event.shiftKey && index <= 0) {
            event.preventDefault();
            focusables[focusables.length - 1].focus();
        } else if (!event.shiftKey && (index === -1 || index === focusables.length - 1)) {
            event.preventDefault();
            focusables[0].focus();
        }
    });
}

els.menuTrigger.onclick = (e) => { e.stopPropagation(); toggleDropdown('menu'); };

// Close tools menu when clicking any item or the popup backdrop.
// Capture phase keeps this reliable even if a menu button stops propagation.
els.menuDropdown.addEventListener('click', (e) => {
    const groupTrigger = e.target.closest('[data-menu-group-trigger]');
    if (groupTrigger) {
        const group = groupTrigger.closest('.menu-group');
        const nextGroup = group?.dataset.menuGroup || '';
        if (!nextGroup) return;

        if (openToolsMenuGroups.has(nextGroup)) openToolsMenuGroups.delete(nextGroup);
        else openToolsMenuGroups.add(nextGroup);
        syncToolsMenuGroups();
        if (openToolsMenuGroups.has(nextGroup)) revealToolsMenuGroup(group);
        return;
    }

    const clickedItem = e.target.closest('button.glass-btn[data-menu-leaf="true"]');
    if (clickedItem) {
        requestAnimationFrame(closeMenuDropdown);
        return;
    }

    if (e.target === els.menuDropdown) {
        closeMenuDropdown();
    }
}, true);
setupToolsMenuGroups();
setupToolsMenuHelpMarkers();
window.getOpenToolsMenuGroups = getOpenToolsMenuGroups;
window.setOpenToolsMenuGroups = setOpenToolsMenuGroups;
window.collapseAllToolsMenuGroups = collapseAllToolsMenuGroups;
window.openToolsMenuDropdown = openToolsMenuDropdown;
window.closeToolsMenuDropdown = closeMenuDropdown;
els.sourceTrigger.onclick = (e) => { e.stopPropagation(); toggleDropdown('source'); };
if (els.aiTrigger) els.aiTrigger.onclick = (e) => { e.stopPropagation(); toggleDropdown('ai'); };

// Auto-options dropdown click toggle
const autoOptsTrigger = $('auto-options-trigger');
if (autoOptsTrigger) autoOptsTrigger.onclick = (e) => { e.stopPropagation(); const dd = $('auto-options-dropdown'); if (dd) { dd.classList.toggle('active'); autoOptsTrigger.setAttribute('aria-expanded', String(dd.classList.contains('active'))); } };
// Close auto-options on mouseleave with grace period for gap crossing
let autoOptsLeaveTimer = null;
const autoOptsWrapper = document.querySelector('.auto-options-wrapper');
if (autoOptsWrapper) {
    autoOptsWrapper.addEventListener('mouseleave', () => {
        autoOptsLeaveTimer = setTimeout(() => { const dd = $('auto-options-dropdown'); if (dd) dd.classList.remove('active'); autoOptsTrigger?.setAttribute('aria-expanded', 'false'); }, 300);
    });
    autoOptsWrapper.addEventListener('mouseenter', () => {
        if (autoOptsLeaveTimer) { clearTimeout(autoOptsLeaveTimer); autoOptsLeaveTimer = null; }
    });
}

if (els.resetQBtn) {
    els.resetQBtn.onclick = (e) => {
        e.stopPropagation();
        if (els.refreshOptionsDropdown) {
            const willOpen = !els.refreshOptionsDropdown.classList.contains('active');
            els.refreshOptionsDropdown.classList.toggle('active', willOpen);
            els.resetQBtn.classList.toggle('active', willOpen);
        }
    };
}
if (els.refreshQuestionBtn) {
    els.refreshQuestionBtn.onclick = (e) => {
        e.stopPropagation();
        refreshCurrentQuestionAndShuffle();
        if (els.refreshOptionsDropdown) els.refreshOptionsDropdown.classList.remove('active');
        if (els.resetQBtn) els.resetQBtn.classList.remove('active');
    };
}
if (els.refreshQuizBtn) {
    els.refreshQuizBtn.onclick = async (e) => {
        e.stopPropagation();
        if (els.refreshOptionsDropdown) els.refreshOptionsDropdown.classList.remove('active');
        if (els.resetQBtn) els.resetQBtn.classList.remove('active');
        // Refresh clears every answer in the open quiz and has no undo, so answered work asks first.
        const answeredCount = Object.keys(userAnswers || {}).length;
        if (questions.length && answeredCount > 0) {
            const confirmed = await showThemeConfirm(
                `This clears your ${answeredCount} ${answeredCount === 1 ? 'answer' : 'answers'} in this quiz and reshuffles it. Saved questions stay saved. This can't be undone.`,
                { title: 'Refresh quiz?', okText: 'Refresh', cancelText: 'Cancel', danger: true }
            );
            if (!confirmed) return;
        }
        refreshEntireQuizAndShuffle();
    };
}

/* --- CUSTOM EVIDENCE RESIZE LOGIC --- */
function setupSourceResize() {
    addResizeHandle();
}


/* --- AI TOOLS (now merged into Tools menu) --- */

/* --- SOURCE / EVIDENCE LOGIC (ROBUST) --- */

// --- UPDATED LOGIC: Multi-Source Support ---
function findBestPdfForQuestion(q) {
    if (!q) return null;

    // Use loadedPdfFiles if available, otherwise fall back to sessionManifest.pdfs
    let pdfKeys = Object.keys(loadedPdfFiles || {});
    if (pdfKeys.length === 0 && sessionManifest && Array.isArray(sessionManifest.pdfs)) {
        pdfKeys = sessionManifest.pdfs.filter(Boolean);
    }

    // Direct question PDF properties
    const directPdf = String(q.pdfName || q.sourcePdf || q.pdf || '').trim();
    if (directPdf) {
        if (pdfKeys.length > 0) {
            const cleanDirect = directPdf.toLowerCase().replace(/\.pdf$/i, '');
            const exact = pdfKeys.find(k => k.toLowerCase() === directPdf.toLowerCase() || k.toLowerCase().replace(/\.pdf$/i, '') === cleanDirect);
            if (exact) return exact;
        } else {
            return directPdf.toLowerCase().endsWith('.pdf') ? directPdf : `${directPdf}.pdf`;
        }
    }

    // Get the source file for this question (the JSON/quiz it came from)
    const questionSourceFile = String(q.sourceFile || q.src || q.source || q.file || '').trim();

    // Try to find the quiz JSON name from the sourceFile
    let possibleQuizKeys = [questionSourceFile].filter(Boolean);
    if (questionSourceFile) {
        if (questionSourceFile.toLowerCase().endsWith('.pdf')) {
            const baseName = questionSourceFile.replace(/\.pdf$/i, '');
            possibleQuizKeys.push(baseName + '_quiz.json');
            possibleQuizKeys.push(baseName + '.json');
            possibleQuizKeys.push(baseName);
        }
        if (questionSourceFile.toLowerCase().endsWith('_quiz.json')) {
            const baseName = questionSourceFile.replace(/_quiz\.json$/i, '');
            possibleQuizKeys.push(baseName + '.pdf');
            possibleQuizKeys.push(baseName);
        }
        if (questionSourceFile.toLowerCase().endsWith('.json')) {
            const baseName = questionSourceFile.replace(/\.json$/i, '');
            possibleQuizKeys.push(baseName + '.pdf');
            possibleQuizKeys.push(baseName);
        }
    }

    // 1. CHECK MANUAL LINKS FIRST (Highest Priority - User's explicit choice)
    for (const quizKey of possibleQuizKeys) {
        if (quizKey && pdfAssociations && pdfAssociations[quizKey]) {
            const linkedPool = pdfAssociations[quizKey];
            if (Array.isArray(linkedPool) && linkedPool.length > 0) {
                const specificSource = q.src || q.source || q.file || q.sourceFile;
                if (specificSource) {
                    const cleanSrc = specificSource.toLowerCase().trim().replace(/\.pdf$/i, '');
                    const exactMatch = linkedPool.find(k => k.toLowerCase().replace(/\.pdf$/i, '') === cleanSrc);
                    if (exactMatch) return exactMatch;

                    const poolMatch = linkedPool.find(k => {
                        const cleanK = k.toLowerCase().replace(/\.pdf$/i, '');
                        return cleanK.includes(cleanSrc) || cleanSrc.includes(cleanK);
                    });
                    if (poolMatch) return poolMatch;
                }
                return linkedPool[0];
            }
        }
    }

    // 2. CHECK SPECIFIC QUESTION TAG (q.src, q.source, q.file)
    const specificSource = String(q.src || q.source || q.file || '').trim();
    if (specificSource && pdfKeys.length > 0) {
        const cleanSrc = specificSource.toLowerCase().replace(/\.pdf$/i, '');

        let exactMatch = pdfKeys.find(k => k.toLowerCase().replace(/\.pdf$/i, '') === cleanSrc || k.toLowerCase() === specificSource.toLowerCase());
        if (exactMatch) return exactMatch;

        let containsMatch = pdfKeys.find(k => {
            const cleanPdf = k.toLowerCase().replace(/\.pdf$/i, '');
            return cleanPdf.includes(cleanSrc) || cleanSrc.includes(cleanPdf);
        });
        if (containsMatch) return containsMatch;
    }

    // 3. FALLBACK: Match on the JSON filename itself
    if (questionSourceFile && pdfKeys.length > 0) {
        const jsonName = questionSourceFile.toLowerCase().replace(/\.json$/, '').replace(/\.pdf$/, '').replace(/\.txt$/, '').replace('_quiz', '');
        let match = pdfKeys.find(k => {
            const cleanPdf = k.toLowerCase().replace(/\.pdf$/i, '');
            return jsonName === cleanPdf || cleanPdf === jsonName || cleanPdf.includes(jsonName) || jsonName.includes(cleanPdf);
        });
        if (match) return match;
    }

    // 4. If only one PDF is loaded in session, match it
    if (pdfKeys.length === 1 && pdfKeys[0]) {
        return pdfKeys[0];
    }

    // 5. If question itself names a PDF directly, use that even if not currently loaded into active viewer
    for (const key of possibleQuizKeys) {
        if (/\.pdf$/i.test(key)) return key;
    }
    if (specificSource && !/\.json$/i.test(specificSource)) {
        return specificSource.toLowerCase().endsWith('.pdf') ? specificSource : `${specificSource}.pdf`;
    }
    if (questionSourceFile && !/\.json$/i.test(questionSourceFile)) {
        return questionSourceFile.toLowerCase().endsWith('.pdf') ? questionSourceFile : `${questionSourceFile}.pdf`;
    }

    return null;
}

// Helper: Get vibrant color for connection lines
const linkColors = [
    '#f472b6', // pink
    '#60a5fa', // blue
    '#4ade80', // green
    '#fbbf24', // amber
    '#a78bfa', // purple
    '#fb923c', // orange
    '#22d3d8', // cyan
    '#f87171', // red
    '#a3e635', // lime
    '#e879f9', // fuchsia
];
function stringToColor(str, idx) {
    // If index provided, use it directly
    if (typeof idx === 'number') {
        return linkColors[idx % linkColors.length];
    }
    // Fallback: hash string to get consistent color
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
        hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }
    return linkColors[Math.abs(hash) % linkColors.length];
}

/* ============================================================================
   PDF ASSOCIATIONS
   A quiz bank is associated with the lecture PDF it came from. The association
   is always automatic: quiz generation records it directly, and filename
   matching fills in banks that arrive without one (legacy sessions, plain JSON
   imports). There is no manual picker.
   Storage stays on the legacy `manual_pdf_links` settings key and the legacy
   `manualPdfLinks` saved-session field so older sessions keep working.
   ============================================================================ */

const PDF_ASSOCIATION_MATCH_MIN_SCORE = 0.5;
const PDF_ASSOCIATION_WORD_CACHE_LIMIT = 500;
const pdfAssociationWordCache = new Map();

function normalizePdfAssociationWord(word) {
    // "ies" before the plain plural, so "arteries" meets "artery".
    return String(word || '')
        .toLowerCase()
        .replace(/ies$/, 'y')
        .replace(/s$/, '');
}

function getPdfAssociationBaseName(filename) {
    return String(filename || '').trim().toLowerCase().replace(/\.(json|txt|pdf|pptx|ppt)$/i, '').trim();
}

// Canonical filename tokenizer for automatic association matching. Cached because
// the Sources view re-resolves the same filenames many times per session.
function getPdfAssociationWords(filename) {
    const cacheKey = String(filename || '');
    const cached = pdfAssociationWordCache.get(cacheKey);
    if (cached) return cached;
    const words = cacheKey
        .toLowerCase()
        .replace(/\.(json|txt|pdf|pptx|ppt)$/i, '')
        .replace(/[-_&]/g, ' ')
        // Whole words only: "test" inside "Testis" is the lecture's name.
        .replace(/\b(?:quiz(?:zes)?|slides?|questions?|answers?|review|tests?|exams?)\b/gi, '')
        .trim()
        .split(/\s+/)
        // Numbers are kept, so "Lecture 1" never matches "Lecture 2.pdf".
        .filter(word => word.length > 2 || /^\d+$/.test(word))
        .map(normalizePdfAssociationWord);
    if (pdfAssociationWordCache.size >= PDF_ASSOCIATION_WORD_CACHE_LIMIT) pdfAssociationWordCache.clear();
    pdfAssociationWordCache.set(cacheKey, words);
    return words;
}

function scorePdfAssociationCandidate(quizWords, pdfWords) {
    if (!quizWords.length || !pdfWords.length) return { score: 0, strictMatch: false };
    const quizSet = new Set(quizWords);
    const pdfSet = new Set(pdfWords);
    let matches = 0;
    quizSet.forEach((word) => { if (pdfSet.has(word)) matches += 1; });
    const union = new Set([...quizWords, ...pdfWords]);
    const jaccardScore = matches / union.size;
    const strictMatch = matches === Math.min(quizSet.size, pdfSet.size);
    return { score: strictMatch ? jaccardScore + 0.5 : jaccardScore, strictMatch };
}

function findBestPdfAssociationForQuizKey(quizKey, pdfKeys) {
    const quizBase = getPdfAssociationBaseName(quizKey);
    const exact = quizBase ? (Array.isArray(pdfKeys) ? pdfKeys : []).find(pdfKey => getPdfAssociationBaseName(pdfKey) === quizBase) : null;
    if (exact) return { pdfKey: exact, score: 2 };
    const quizWords = getPdfAssociationWords(quizKey);
    let bestMatch = null;
    let bestScore = 0;
    (Array.isArray(pdfKeys) ? pdfKeys : []).forEach((pdfKey) => {
        const similarity = scorePdfAssociationCandidate(quizWords, getPdfAssociationWords(pdfKey));
        if (!similarity.strictMatch || similarity.score <= PDF_ASSOCIATION_MATCH_MIN_SCORE) return;
        if (similarity.score <= bestScore) return;
        bestScore = similarity.score;
        bestMatch = pdfKey;
    });
    return bestMatch ? { pdfKey: bestMatch, score: bestScore } : null;
}

function hasPdfAssociation(quizKey) {
    const associated = pdfAssociations?.[quizKey];
    return Array.isArray(associated) && associated.length > 0;
}

// Fill in associations for quiz banks that do not have one yet. Incremental by
// design: a PDF imported after the first pass still gets matched, and an
// existing association (from generation or a restored session) is never
// overwritten. Returns the number of associations added.
function ensureAutomaticPdfAssociations(quizKeys, pdfKeys) {
    const unassociated = (Array.isArray(quizKeys) ? quizKeys : []).filter(key => key && !hasPdfAssociation(key));
    if (!unassociated.length || !Array.isArray(pdfKeys) || !pdfKeys.length) return 0;
    let added = 0;
    unassociated.forEach((quizKey) => {
        const match = findBestPdfAssociationForQuizKey(quizKey, pdfKeys);
        if (!match) return;
        pdfAssociations[quizKey] = [match.pdfKey];
        added += 1;
    });
    return added;
}

function persistPdfAssociations() {
    const normalized = {};
    Object.entries(pdfAssociations || {}).forEach(([quizKey, linkedPdfs]) => {
        if (typeof quizKey !== 'string' || !Array.isArray(linkedPdfs)) return;
        const cleanPdfs = [...new Set(linkedPdfs.filter(pdf => typeof pdf === 'string' && pdf.trim()))];
        if (cleanPdfs.length > 0) normalized[quizKey] = cleanPdfs;
    });
    pdfAssociations = normalized;

    const snapshot = {};
    Object.entries(normalized).forEach(([quizKey, linkedPdfs]) => {
        snapshot[quizKey] = [...linkedPdfs];
    });

    pdfAssociationsPersistChain = pdfAssociationsPersistChain
        .catch(() => { })
        .then(() => IDB.put('settings', 'manual_pdf_links', snapshot, 5000))
        .catch((e) => {
            console.warn('Failed to persist pdfAssociations:', e);
        });

    return pdfAssociationsPersistChain;
}

let sourceDropdownRenderPending = false;

/* The Sources panel is a full two-column rebuild: every quiz bank and PDF row,
   the duplicate-report preload for each bank, and the animated SVG connector
   layer. Session restore, quiz generation and every source toggle call it, so
   it used to be rebuilt many times per restore while nobody was looking at it.
   updateSourceDropdown() now renders only when the panel is on screen and
   otherwise records that it is stale; opening the panel flushes it. */
function isSourceDropdownVisible() {
    const dropdown = els.sourceDropdown || document.getElementById('source-dropdown');
    return !!(dropdown && dropdown.classList.contains('active'));
}

// Side effects other surfaces depend on, independent of the panel's own DOM.
function syncSourceStateWithoutRender() {
    const automaticAssociationsAdded = ensureAutomaticPdfAssociations(sessionManifest.jsons, sessionManifest.pdfs);
    if (automaticAssociationsAdded > 0) {
        console.log(`PDF associations: +${automaticAssociationsAdded} automatic, ${Object.keys(pdfAssociations).length} total.`);
        void persistPdfAssociations();
    }
    if (els.sourceWrapper) els.sourceWrapper.style.display = 'inline-block';
    updateImportButtonState();
    if (document.body?.classList?.contains('quiz-generation-pane-open')
        && typeof syncQuizGenerationSourceMenuItems === 'function') {
        syncQuizGenerationSourceMenuItems();
    }
}

function updateSourceDropdown(options = {}) {
    if (!options?.force && !isSourceDropdownVisible()) {
        sourceDropdownRenderPending = true;
        syncSourceStateWithoutRender();
        return;
    }
    sourceDropdownRenderPending = false;
    renderSourceDropdown();
}

function flushPendingSourceDropdownRender() {
    if (!sourceDropdownRenderPending) return;
    sourceDropdownRenderPending = false;
    renderSourceDropdown();
}

function renderSourceDropdown() {
    ensureSourceDropdownPortal();
    els.sourceDropdown.innerHTML = '';

    const qKeys = sessionManifest.jsons;
    const pdfKeys = sessionManifest.pdfs;
    const getSelectionKeyVariants = (key) => {
        const rawKey = String(key || '').trim();
        if (!rawKey) return [];
        const strippedKey = rawKey.replace(/\.(json|txt)$/i, '');
        return Array.from(new Set([rawKey, strippedKey].filter(Boolean)));
    };
    const isQuizSelectionActive = (key) => getSelectionKeyVariants(key)
        .some((variant) => activeQuestionSetKeys.has(variant));
    const setQuizSelectionActive = (key, active) => {
        getSelectionKeyVariants(key).forEach((variant) => {
            if (active) activeQuestionSetKeys.add(variant);
            else activeQuestionSetKeys.delete(variant);
        });
    };
    const getCachedDuplicateReportForSet = (setKey) => {
        const variants = getQuestionSetKeyVariantsForDedup(setKey);
        for (const variant of variants) {
            if (questionDuplicateReportsBySetKey[variant]) return questionDuplicateReportsBySetKey[variant];
        }
        return null;
    };
    const getDuplicateCountForSet = (setKey) => {
        const report = getCachedDuplicateReportForSet(setKey);
        if (Number.isFinite(report?.stats?.removed)) return Math.max(0, Number(report.stats.removed) || 0);
        return Array.isArray(report?.entries) ? report.entries.length : 0;
    };
    const hasResolvedDuplicateLookupForSet = (setKey) => {
        const variants = getQuestionSetKeyVariantsForDedup(setKey);
        return variants.some(variant => questionDuplicateReportLookupStateBySetKey[variant]);
    };
    const getAggregateDuplicateSummaryForSetKeys = (setKeys = []) => {
        const normalizedSetKeys = Array.from(new Set((Array.isArray(setKeys) ? setKeys : []).map(key => String(key || '').trim()).filter(Boolean)));
        const seen = new Set();
        let pendingCount = 0;

        normalizedSetKeys.forEach(setKey => {
            const report = getCachedDuplicateReportForSet(setKey);
            if (!report || !Array.isArray(report.entries)) {
                if (!hasResolvedDuplicateLookupForSet(setKey)) pendingCount += 1;
                return;
            }
            report.entries.forEach(rawEntry => {
                const entry = createSavedDuplicateViewEntry(rawEntry, setKey);
                if (!entry || seen.has(entry.id)) return;
                seen.add(entry.id);
            });
        });

        return {
            ready: pendingCount === 0,
            pendingCount,
            count: seen.size
        };
    };
    const missingDuplicateReports = qKeys.filter(key => !getCachedDuplicateReportForSet(key) && !hasResolvedDuplicateLookupForSet(key));
    if (missingDuplicateReports.length) {
        const renderToken = Date.now() + Math.random();
        renderSourceDropdown._duplicateRenderToken = renderToken;
        void Promise.all(missingDuplicateReports.map(key => loadQuestionDuplicateReport(key)))
            .then(() => {
                if (renderSourceDropdown._duplicateRenderToken !== renderToken) return;
                const dropdownVisible = !!(els.sourceDropdown && els.sourceDropdown.classList.contains('active'));
                const evidenceMounted = !!document.getElementById('evidence-grid');
                if (dropdownVisible || evidenceMounted) updateSourceDropdown();
            })
            .catch(err => console.warn('Failed to preload evidence duplicate reports:', err));
    }

    // Associations are automatic. Fill in any bank that does not have one yet
    // without disturbing pairs that generation or a restored session already set.
    const automaticAssociationsAdded = ensureAutomaticPdfAssociations(qKeys, pdfKeys);
    if (automaticAssociationsAdded > 0) {
        console.log(`PDF associations: +${automaticAssociationsAdded} automatic, ${Object.keys(pdfAssociations).length} total.`);
        void persistPdfAssociations();
    }

    // Empty state
    if (qKeys.length === 0 && pdfKeys.length === 0) {
        els.sourceDropdown.innerHTML = '<div style="padding:30px; text-align:center; color:#64748b; font-size:0.9rem;">No files loaded.<br>Import JSONs or PDFs using the buttons above.</div>';
        addResizeHandle();
        els.sourceWrapper.style.display = 'inline-block';
        return;
    }

    // Create horizontal grid container
    const grid = document.createElement('div');
    grid.className = 'evidence-grid';
    grid.id = 'evidence-grid';

    // SVG for connection lines
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.id = 'evidence-svg';
    grid.appendChild(svg);

    // Left column: JSONs
    const leftCol = document.createElement('div');
    leftCol.className = 'evidence-column';
    leftCol.id = 'evidence-json-col';

    const leftHeader = document.createElement('div');
    leftHeader.className = 'evidence-column-header';
    const aggregateDuplicateSummary = getAggregateDuplicateSummaryForSetKeys(qKeys);
    const aggregateDuplicateCountLabel = String(aggregateDuplicateSummary.count);
    const aggregateDuplicateDisabledClass = qKeys.length === 0 ? ' disabled' : '';
    const aggregateDuplicateTitle = aggregateDuplicateSummary.pendingCount > 0
        ? 'Checking duplicate questions for the loaded question banks'
        : (aggregateDuplicateSummary.count > 0
            ? 'Open duplicate questions for the loaded question banks'
            : 'No duplicate questions found for the loaded question banks');
    leftHeader.classList.add('has-action');
    leftHeader.innerHTML = `
        <div class="evidence-column-header-main">
            <span class="evidence-column-title">📚 Question Banks</span>
            <span class="evidence-column-count" title="${qKeys.length} question bank(s)">${qKeys.length}</span>
        </div>
        <button type="button" class="evidence-column-action${aggregateDuplicateDisabledClass}" title="${aggregateDuplicateTitle}">
            Duplicates Q: ${aggregateDuplicateCountLabel}
        </button>
    `;
    leftHeader.querySelector('.evidence-column-action')?.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (!qKeys.length) return;
        void window.openSavedDuplicateViewForQuestionSets(qKeys, e);
    });
    leftCol.appendChild(leftHeader);

    // Right column: PDFs
    const rightCol = document.createElement('div');
    rightCol.className = 'evidence-column';
    rightCol.id = 'evidence-pdf-col';

    const rightHeader = document.createElement('div');
    rightHeader.className = 'evidence-column-header';
    rightHeader.classList.add('has-action');
    rightHeader.innerHTML = `
        <div class="evidence-column-header-main">
            <span class="evidence-column-title">📄 Lecture PDFs</span>
            <span class="evidence-column-count" title="${pdfKeys.length} lecture PDF(s)">${pdfKeys.length}</span>
        </div>
        <button type="button" class="evidence-column-action" title="Add lecture materials">
            + Add
        </button>
    `;
    rightHeader.querySelector('.evidence-column-action')?.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (typeof window.openPdfTutorSourcePdfPicker === 'function') {
            window.openPdfTutorSourcePdfPicker();
            return;
        }
        showPopup('PDF import tools are still loading. Try again in a moment.', null, true);
    });
    rightCol.appendChild(rightHeader);

    // Build paired layout - reorder PDFs to match JSON links for horizontal lines
    // Strategy: Create ordered PDF list where linked PDFs appear in same position as their JSON
    const orderedPdfs = [];
    const usedPdfs = new Set();

    // First pass: for each JSON, add its linked PDFs in order
    qKeys.forEach(jsonKey => {
        const linkedPdfs = pdfAssociations[jsonKey] || [];
        linkedPdfs.forEach(pdfKey => {
            if (!usedPdfs.has(pdfKey) && pdfKeys.includes(pdfKey)) {
                orderedPdfs.push({ pdfKey, jsonKey });
                usedPdfs.add(pdfKey);
            }
        });
        // If no links, add a placeholder to maintain row alignment
        if (linkedPdfs.length === 0) {
            orderedPdfs.push({ pdfKey: null, jsonKey });
        }
    });

    // Second pass: add remaining unlinked PDFs at the end
    pdfKeys.forEach(pdfKey => {
        if (!usedPdfs.has(pdfKey)) {
            orderedPdfs.push({ pdfKey, jsonKey: null });
        }
    });

    // Now render JSONs
    qKeys.forEach((jsonKey, idx) => {
        const linkedPdfs = pdfAssociations[jsonKey] || [];
        const displayName = jsonKey.replace(/\.json$/i, '').replace(/\.txt$/i, '');
        const count = loadedQuestionSets[jsonKey] ? loadedQuestionSets[jsonKey].length : 0;

        const jsonItem = document.createElement('div');
        jsonItem.className = 'evidence-item';
        jsonItem.id = `json-item-${idx}`;
        jsonItem.dataset.key = jsonKey;
        if (activeQuestionSetKeys.has(jsonKey)) jsonItem.classList.add('active');
        else jsonItem.classList.add('inactive'); // Dim toggled-off quizzes
        // An unlinked bank is still a valid active quiz. Reserve the dimmed unlinked
        // treatment for banks that are also toggled off.
        if (linkedPdfs.length === 0 && !activeQuestionSetKeys.has(jsonKey)) jsonItem.classList.add('unlinked');

        jsonItem.innerHTML = `
                    <span class="evidence-item-name">${displayName}</span>
                    <div class="evidence-item-meta-group">
                        <span class="evidence-item-meta">${count} Qs</span>
                    </div>
                    <div class="evidence-item-actions">
                        <button class="evidence-action-btn del-btn" title="Remove">&times;</button>
                    </div>
                `;

        jsonItem.querySelector('.evidence-item-name').onclick = (e) => {
            e.stopPropagation();
            // Save scroll positions before toggle
            const jsonCol = $('evidence-json-col');
            const pdfCol = $('evidence-pdf-col');
            const savedJsonScroll = jsonCol ? jsonCol.scrollTop : 0;
            const savedPdfScroll = pdfCol ? pdfCol.scrollTop : 0;

            if (activeQuestionSetKeys.has(jsonKey)) activeQuestionSetKeys.delete(jsonKey);
            else activeQuestionSetKeys.add(jsonKey);
            updateSourceDropdown();
            rebuildActiveQuestions();
            markSessionStateMutation();
            saveSessionDebounced();

            // Restore scroll positions after DOM update
            requestAnimationFrame(() => {
                const newJsonCol = $('evidence-json-col');
                const newPdfCol = $('evidence-pdf-col');
                if (newJsonCol) newJsonCol.scrollTop = savedJsonScroll;
                if (newPdfCol) newPdfCol.scrollTop = savedPdfScroll;
            });
        };
        jsonItem.querySelector('.del-btn').onclick = (e) => { e.stopPropagation(); removeSource(jsonKey, 'json'); };

        leftCol.appendChild(jsonItem);
    });

    // Render PDFs in reordered sequence
    orderedPdfs.forEach((entry, idx) => {
        if (entry.pdfKey) {
            const pdfItem = createPdfItem(entry.pdfKey, idx, entry.jsonKey);
            rightCol.appendChild(pdfItem);
        }
    });

    grid.appendChild(leftCol);
    grid.appendChild(rightCol);

    // Sync scroll between columns + redraw lines.
    // Use proportional sync so columns stay aligned even when their scroll ranges differ.
    let evidenceScrollSyncing = false;
    let evidenceDrawQueued = false;
    const queueEvidenceLinesDraw = () => {
        if (evidenceDrawQueued) return;
        evidenceDrawQueued = true;
        requestAnimationFrame(() => {
            evidenceDrawQueued = false;
            drawConnectionLines();
        });
    };
    const syncScroll = (source, target) => {
        if (evidenceScrollSyncing) return;
        evidenceScrollSyncing = true;

        const sourceMax = Math.max(0, source.scrollHeight - source.clientHeight);
        const targetMax = Math.max(0, target.scrollHeight - target.clientHeight);

        // If either side cannot scroll, keep native scrolling on the active column.
        // This avoids clamping the PDF column to 0 when the quiz column is empty.
        if (sourceMax <= 0 || targetMax <= 0) {
            evidenceScrollSyncing = false;
            queueEvidenceLinesDraw();
            return;
        }

        const ratio = sourceMax > 0 ? (source.scrollTop / sourceMax) : 0;

        target.scrollTop = ratio * targetMax;

        // Clamp source to the effective synced ratio to prevent diagonal snap at limits.
        const appliedRatio = targetMax > 0 ? (target.scrollTop / targetMax) : 0;
        const correctedSource = appliedRatio * sourceMax;
        if (Math.abs(source.scrollTop - correctedSource) > 1) {
            source.scrollTop = correctedSource;
        }

        evidenceScrollSyncing = false;
        queueEvidenceLinesDraw();
    };
    leftCol.addEventListener('scroll', () => syncScroll(leftCol, rightCol));
    rightCol.addEventListener('scroll', () => syncScroll(rightCol, leftCol));

    // Keep gap-area wheel scrolling in sync with the same proportional logic.
    grid.addEventListener('wheel', (e) => {
        if (e.target.closest('#evidence-json-col, #evidence-pdf-col')) return;
        const leftMax = Math.max(0, leftCol.scrollHeight - leftCol.clientHeight);
        const rightMax = Math.max(0, rightCol.scrollHeight - rightCol.clientHeight);
        const primaryCol = (rightMax > 0 && leftMax <= 0) ? rightCol : leftCol;
        const secondaryCol = primaryCol === leftCol ? rightCol : leftCol;
        primaryCol.scrollTop += e.deltaY;
        syncScroll(primaryCol, secondaryCol);
    }, { passive: true });

    els.sourceDropdown.appendChild(grid);

    // Session management buttons
    if (qKeys.length > 0 || pdfKeys.length > 0) {
        const footer = document.createElement('div');
        footer.className = 'evidence-footer';

        const allJsonQuizzesActive = qKeys.length > 0 && qKeys.every(isQuizSelectionActive);
        const toggleQuizSelectionBtn = document.createElement('button');
        toggleQuizSelectionBtn.type = 'button';
        toggleQuizSelectionBtn.className = 'glass-btn';
        toggleQuizSelectionBtn.style.cssText = 'flex:1; justify-content:center; font-size:0.75rem; padding:6px;';
        toggleQuizSelectionBtn.innerText = allJsonQuizzesActive ? 'Deselect All Quizzes' : 'Select All Quizzes';
        toggleQuizSelectionBtn.onclick = (e) => {
            e.stopPropagation();
            // Re-read state at click time so the control always toggles the
            // current set of JSON banks, even if another path changed selection
            // after this menu was rendered.
            const shouldActivateAll = !(qKeys.length > 0 && qKeys.every(isQuizSelectionActive));
            qKeys.forEach((key) => setQuizSelectionActive(key, shouldActivateAll));
            rebuildActiveQuestions();
            updateSourceDropdown();
            markSessionStateMutation();
            saveSessionDebounced();
        };

        const clearBtn = document.createElement('button');
        clearBtn.className = 'glass-btn evidence-danger';
        clearBtn.style.cssText = 'flex:1; justify-content:center; font-size:0.75rem; padding:6px;';
        clearBtn.innerText = 'Clear All';
        clearBtn.onclick = (e) => { e.stopPropagation(); clearCurrentSessionFiles(); };

        if (qKeys.length > 0) {
            footer.appendChild(toggleQuizSelectionBtn);
        }
        footer.appendChild(clearBtn);
        els.sourceDropdown.appendChild(footer);
    }

    addResizeHandle();
    els.sourceWrapper.style.display = 'inline-block';

    if (document.body?.classList?.contains('quiz-generation-pane-open')
        && typeof syncQuizGenerationSourceMenuItems === 'function') {
        syncQuizGenerationSourceMenuItems();
    }

    // Draw connection lines after DOM renders
    queueEvidenceLinesDraw();

    // Update import button state based on loaded PDFs
    updateImportButtonState();
}

// Helper function to create PDF item
function createPdfItem(pdfKey, rowIdx, linkedJsonKey) {
    const item = document.createElement('div');
    item.className = 'evidence-item';
    item.id = `pdf-item-${rowIdx}`;
    item.dataset.key = pdfKey;
    item.dataset.row = rowIdx;

    // Dim a PDF that no quiz bank is associated with.
    const isLinked = Object.values(pdfAssociations).some(arr => arr && arr.includes(pdfKey));
    if (!isLinked) item.classList.add('unlinked');

    // Strip .pdf extension for cleaner display
    const displayName = pdfKey.replace(/\.pdf$/i, '');

    item.innerHTML = `
                <span class="evidence-item-name">${displayName}</span>
                <div class="evidence-item-actions">
                    <button class="evidence-action-btn del-btn" title="Remove">&times;</button>
                </div>
            `;

    item.querySelector('.evidence-item-name').onclick = (e) => {
        e.stopPropagation();
        if (loadedPdfFiles[pdfKey]) {
            openPdf(pdfKey);
            if (els.sourceDropdown) els.sourceDropdown.classList.remove('active');
            syncFloatingQuizLayering();
        }
    };

    item.querySelector('.del-btn').onclick = (e) => {
        e.stopPropagation();
        removeSource(pdfKey, 'pdf');
    };

    return item;
}

// Draw polished connector curves between linked JSON/PDF pairs
function drawConnectionLines() {
    const svg = document.getElementById('evidence-svg');
    const grid = document.getElementById('evidence-grid');
    if (!svg || !grid) return;

    const gridRect = grid.getBoundingClientRect();
    const phaseNowMs = performance.now();
    // Match the api-key-modal Memora→Gemini connectivity animation: 1.8s loop, 3 staggered pulses.
    const globalSyncDuration = '1.8s';
    const globalSyncDurationMs = 1800;
    const basePhaseSec = (phaseNowMs % globalSyncDurationMs) / 1000;
    const globalSyncPhase = '-' + basePhaseSec.toFixed(3) + 's';
    const pulseStaggerSec = 0.6;
    const pulseCount = 3;

    const jsonCol = document.getElementById('evidence-json-col');
    const pdfCol = document.getElementById('evidence-pdf-col');
    const leftColRect = jsonCol ? jsonCol.getBoundingClientRect() : null;
    const rightColRect = pdfCol ? pdfCol.getBoundingClientRect() : null;

    const getElementOpacity = (rect, containerRect) => {
        if (!containerRect) return 1;
        if (rect.bottom <= containerRect.top || rect.top >= containerRect.bottom) {
            return 0;
        }
        const midY = rect.top + rect.height / 2;
        const fadeDistance = 28;
        if (midY < containerRect.top + fadeDistance) {
            return Math.max(0, (midY - containerRect.top) / fadeDistance);
        }
        if (midY > containerRect.bottom - fadeDistance) {
            return Math.max(0, (containerRect.bottom - midY) / fadeDistance);
        }
        return 1;
    };

    const jsonItems = Array.from(document.querySelectorAll('#evidence-json-col .evidence-item:not(.evidence-spacer):not([hidden])'));
    const pdfItems = Array.from(document.querySelectorAll('#evidence-pdf-col .evidence-item:not(.evidence-spacer):not([hidden])'));
    const pdfByKey = new Map(pdfItems.map((el) => [el.dataset.key, el]));

    const buildPath = (startX, startY, endX, endY) => {
        const spanX = Math.max(24, endX - startX);
        const bend = Math.max(28, Math.min(120, spanX * 0.34));
        const deltaY = endY - startY;
        const guide = Math.abs(deltaY) < 6
            ? 0
            : Math.sign(deltaY) * Math.min(42, Math.max(10, Math.abs(deltaY) * 0.30));

        const c1x = startX + bend;
        const c2x = endX - bend;
        const c1y = startY + guide;
        const c2y = endY - guide;

        return `M ${startX.toFixed(1)} ${startY.toFixed(1)} C ${c1x.toFixed(1)} ${c1y.toFixed(1)}, ${c2x.toFixed(1)} ${c2y.toFixed(1)}, ${endX.toFixed(1)} ${endY.toFixed(1)}`;
    };

    const activeConnectionKeys = new Set();

    jsonItems.forEach((jsonEl, idx) => {
        const jsonKey = jsonEl.dataset.key;
        const linkedPdfs = pdfAssociations[jsonKey] || [];
        if (linkedPdfs.length === 0) return;

        const jsonRect = jsonEl.getBoundingClientRect();
        const startX = jsonRect.right - gridRect.left + 2;
        const startY = jsonRect.top + jsonRect.height / 2 - gridRect.top;
        const color = stringToColor(jsonKey, idx);
        const syncClass = ' syncing';

        const jsonOpacity = getElementOpacity(jsonRect, leftColRect);

        linkedPdfs.forEach((pdfKey) => {
            const pdfEl = pdfByKey.get(pdfKey);
            if (!pdfEl) return;

            const connectionKey = `${jsonKey}::${pdfKey}`;
            activeConnectionKeys.add(connectionKey);

            const pdfRect = pdfEl.getBoundingClientRect();
            const endX = pdfRect.left - gridRect.left - 2;
            const endY = pdfRect.top + pdfRect.height / 2 - gridRect.top;
            const pathData = buildPath(startX, startY, endX, endY);

            const pdfOpacity = getElementOpacity(pdfRect, rightColRect);
            const connectionOpacity = Math.min(jsonOpacity, pdfOpacity);
            const batchState = typeof quizGenBatchProgressState !== 'undefined'
                ? quizGenBatchProgressState
                : null;
            const batchEntry = batchState?.entries?.[pdfKey] || null;
            const isGenerationTracked = !!batchEntry
                && document.body?.classList?.contains('quiz-generation-pane-open');
            const generationProgress = isGenerationTracked
                && typeof computeQuizGenBatchEntryProgress === 'function'
                ? Math.max(0, Math.min(1, computeQuizGenBatchEntryProgress(batchEntry)))
                : 1;

            let group = svg.querySelector(`g[data-connection-key="${connectionKey}"]`);
            if (!group) {
                group = document.createElementNS('http://www.w3.org/2000/svg', 'g');
                group.setAttribute('data-connection-key', connectionKey);

                const mainPath = document.createElementNS('http://www.w3.org/2000/svg', 'path');
                mainPath.setAttribute('class', 'connection-line' + syncClass);
                mainPath.setAttribute('stroke-linecap', 'round');
                mainPath.style.setProperty('--sync-delay', globalSyncPhase);
                mainPath.setAttribute('stroke', color);
                group.appendChild(mainPath);

                const progressPath = document.createElementNS('http://www.w3.org/2000/svg', 'path');
                progressPath.setAttribute('class', 'connection-progress-line');
                progressPath.setAttribute('pathLength', '1');
                progressPath.setAttribute('stroke-linecap', 'round');
                progressPath.setAttribute('stroke', color);
                group.appendChild(progressPath);

                for (let pulseIdx = 0; pulseIdx < pulseCount; pulseIdx++) {
                    const pulseBegin = '-' + ((basePhaseSec + pulseIdx * pulseStaggerSec) % (globalSyncDurationMs / 1000)).toFixed(3) + 's';

                    const flowDot = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
                    flowDot.setAttribute('r', '3.4');
                    flowDot.setAttribute('fill', color);
                    flowDot.setAttribute('opacity', '0');
                    flowDot.setAttribute('class', 'connection-flow-dot' + syncClass);

                    const animateMotion = document.createElementNS('http://www.w3.org/2000/svg', 'animateMotion');
                    animateMotion.setAttribute('dur', globalSyncDuration);
                    animateMotion.setAttribute('begin', pulseBegin);
                    animateMotion.setAttribute('repeatCount', 'indefinite');
                    animateMotion.setAttribute('calcMode', 'spline');
                    animateMotion.setAttribute('keyTimes', '0;1');
                    animateMotion.setAttribute('keySplines', '0.42 0 0.58 1');
                    flowDot.appendChild(animateMotion);

                    // Fade in/out along the travel, same envelope as api-key-connectivity-travel.
                    const animateFade = document.createElementNS('http://www.w3.org/2000/svg', 'animate');
                    animateFade.setAttribute('attributeName', 'opacity');
                    animateFade.setAttribute('values', '0;0.95;0.95;0');
                    animateFade.setAttribute('keyTimes', '0;0.16;0.82;1');
                    animateFade.setAttribute('dur', globalSyncDuration);
                    animateFade.setAttribute('begin', pulseBegin);
                    animateFade.setAttribute('repeatCount', 'indefinite');
                    flowDot.appendChild(animateFade);

                    group.appendChild(flowDot);
                }

                const startDot = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
                startDot.setAttribute('r', '2.4');
                startDot.setAttribute('fill', color);
                startDot.setAttribute('class', 'connection-endpoint' + syncClass);
                startDot.style.setProperty('--sync-delay', globalSyncPhase);
                group.appendChild(startDot);

                const endDot = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
                endDot.setAttribute('r', '2.4');
                endDot.setAttribute('fill', color);
                endDot.setAttribute('class', 'connection-endpoint' + syncClass);
                endDot.style.setProperty('--sync-delay', globalSyncPhase);
                group.appendChild(endDot);

                svg.appendChild(group);
            }

            // Update attributes dynamically in place
            group.style.setProperty('--connector-color', color);
            group.style.opacity = connectionOpacity;
            group.style.display = connectionOpacity === 0 ? 'none' : '';

            // Children: [0]=mainPath, [1]=progressPath, [2..pulseCount+1]=flow dots, [last-1]=startDot, [last]=endDot
            const mainPath = group.childNodes[0];
            const progressPath = group.childNodes[1];
            const startDot = group.childNodes[2 + pulseCount];
            const endDot = group.childNodes[3 + pulseCount];

            if (mainPath.getAttribute('d') !== pathData) mainPath.setAttribute('d', pathData);
            if (progressPath.getAttribute('d') !== pathData) progressPath.setAttribute('d', pathData);
            progressPath.setAttribute('pathLength', '1');
            progressPath.style.strokeDasharray = '1';
            progressPath.style.strokeDashoffset = String(1 - generationProgress);
            progressPath.style.opacity = isGenerationTracked ? '0.94' : '0';
            for (let pulseIdx = 0; pulseIdx < pulseCount; pulseIdx++) {
                const flowDot = group.childNodes[2 + pulseIdx];
                const animateMotion = flowDot ? flowDot.firstChild : null;
                if (!animateMotion) continue;
                if (animateMotion.getAttribute('path') !== pathData) animateMotion.setAttribute('path', pathData);
                if (isGenerationTracked) {
                    animateMotion.setAttribute('keyPoints', `0;${Math.max(0.02, generationProgress).toFixed(3)}`);
                } else {
                    animateMotion.removeAttribute('keyPoints');
                }
            }

            const startXStr = startX.toFixed(1);
            const startYStr = startY.toFixed(1);
            if (startDot.getAttribute('cx') !== startXStr) startDot.setAttribute('cx', startXStr);
            if (startDot.getAttribute('cy') !== startYStr) startDot.setAttribute('cy', startYStr);

            const endXStr = endX.toFixed(1);
            const endYStr = endY.toFixed(1);
            if (endDot.getAttribute('cx') !== endXStr) endDot.setAttribute('cx', endXStr);
            if (endDot.getAttribute('cy') !== endYStr) endDot.setAttribute('cy', endYStr);
            endDot.style.opacity = isGenerationTracked && generationProgress < 0.995 ? '0.22' : '';
        });
    });

    // Remove obsolete groups
    const existingGroups = Array.from(svg.querySelectorAll('g[data-connection-key]'));
    existingGroups.forEach((groupEl) => {
        const key = groupEl.getAttribute('data-connection-key');
        if (!activeConnectionKeys.has(key)) {
            groupEl.remove();
        }
    });
}

/* The Sources panel is authored inside .source-wrapper (z-index: 100) within #controls-bar
   (z-index: 2000). Both are positioned with a z-index, so both open a stacking context and the
   panel's own z-index can never lift it past 2000 in the root context. The docked Ask Memora
   surface is a <body> child in the 2147483450 band, so it always painted over the panel and its
   content bled through. Reparenting to <body> puts the panel in the root stacking context where
   its z-index actually counts. Idempotent; els.sourceDropdown keeps pointing at the same node. */
function ensureSourceDropdownPortal() {
    const dropdown = els.sourceDropdown || $('source-dropdown');
    if (!dropdown || !document.body) return dropdown || null;
    // Generation reuses the real Sources menu inside its right-side dock.
    // Keep it mounted there while the dock is open so updateSourceDropdown()
    // can refresh the same two-column grid and animated SVG connections.
    if (document.body.classList.contains('quiz-generation-pane-open')
        && document.getElementById('quiz-gen-pane-source-menu')) {
        return dropdown;
    }
    if (dropdown.parentElement !== document.body) {
        document.body.appendChild(dropdown);
    }
    return dropdown;
}

function ensureSourceResizeHandle() {
    const dropdown = els.sourceDropdown || $('source-dropdown');
    if (!dropdown) return null;

    let resizeHandle = document.getElementById('source-resize-handle');
    if (!resizeHandle) {
        resizeHandle = document.createElement('div');
        resizeHandle.id = 'source-resize-handle';
        resizeHandle.innerHTML = '&#8990;';
    }

    if (resizeHandle.parentElement !== dropdown) {
        dropdown.appendChild(resizeHandle);
    }

    return resizeHandle;
}

function addResizeHandle() {
    const dropdown = els.sourceDropdown || $('source-dropdown');
    if (!dropdown) return;

    // Enable shift+drag FIRST - before any early returns
    enableShiftDrag(dropdown);

    const resizeHandle = ensureSourceResizeHandle();
    if (!resizeHandle) return;

    // Remove existing logic if any (this function might be called multiple times)
    resizeHandle.onmousedown = null;

    // Attach resize event listener
    let startX, startY, startW, startH;
    resizeHandle.onmousedown = function (e) {
        e.preventDefault();
        e.stopPropagation();
        startX = e.clientX;
        startY = e.clientY;
        startW = dropdown.offsetWidth;
        startH = dropdown.offsetHeight;

        // Switch from right-anchored to left-anchored positioning.
        // The panel is position: fixed, so `left` is viewport-relative — do NOT subtract the
        // parent's rect (this used to, which skewed the anchor by the parent's offset).
        // Matches how enableShiftDrag() below anchors the same element.
        const rect = dropdown.getBoundingClientRect();
        const leftPos = rect.left;

        dropdown.style.left = leftPos + 'px';
        dropdown.style.right = 'auto';

        document.onmousemove = function (e) {
            requestAnimationFrame(() => {
                const deltaX = e.clientX - startX;
                const deltaY = e.clientY - startY;

                let newW = startW + deltaX;
                // Max width constraint (viewport)
                if (rect.left + newW > window.innerWidth - 20) {
                    newW = window.innerWidth - 20 - rect.left;
                }

                dropdown.style.width = newW + 'px';
                dropdown.style.height = (startH + deltaY) + 'px';

                drawConnectionLines(); // Dynamic Redraw
            });
        };

        document.onmouseup = function () {
            document.onmousemove = null;
            document.onmouseup = null;
        };
    };
}

function enableShiftDrag(el) {
    if (el.dataset.hasShiftDrag) return;
    el.dataset.hasShiftDrag = "true";

    let isDragging = false;
    let startX, startY, initialLeft, initialTop;

    // Add cursor feedback when holding shift over the panel
    el.addEventListener('mousemove', (e) => {
        if (e.shiftKey && !isDragging) {
            el.style.cursor = 'grab';
        } else if (!isDragging) {
            el.style.cursor = '';
        }
    });

    el.addEventListener('mouseleave', () => {
        if (!isDragging) {
            el.style.cursor = '';
        }
    });

    el.addEventListener('mousedown', (e) => {
        // Only exclude buttons and resize handle - shift+drag should work from anywhere else
        if (!e.shiftKey || e.target.closest('button, #source-resize-handle')) return;

        e.preventDefault();
        e.stopPropagation();
        isDragging = true;
        startX = e.clientX;
        startY = e.clientY;

        const rect = el.getBoundingClientRect();
        initialLeft = rect.left;
        initialTop = rect.top;

        // Switch to calculated pixels for smooth dragging
        el.style.left = initialLeft + 'px';
        el.style.top = initialTop + 'px';
        el.style.right = 'auto';
        el.style.cursor = 'grabbing';
    }, true); // Capture phase

    const shiftDragMoveThrottled = createRafDragThrottle((e) => {
        if (!isDragging) return;
        const deltaX = e.clientX - startX;
        const deltaY = e.clientY - startY;

        el.style.left = (initialLeft + deltaX) + 'px';
        el.style.top = (initialTop + deltaY) + 'px';
    });

    document.addEventListener('mousemove', (e) => {
        if (!isDragging) return;
        shiftDragMoveThrottled(e);
    });

    document.addEventListener('mouseup', () => {
        if (isDragging) {
            shiftDragMoveThrottled.flush();
            isDragging = false;
            el.style.cursor = '';
        }
    });
}
function resolveThemeConfirm(result) {
    if (els.themeConfirmModal) {
        els.themeConfirmModal.classList.remove('active');
    }
    // Hand focus back to whatever opened the confirmation, such as a Remove button in a dialog.
    window.MemoraModalCoordinator?.markClosed?.('theme-confirm-modal', { restore: true });
    const resolver = themeConfirmResolver;
    themeConfirmResolver = null;
    if (typeof resolver === 'function') resolver(result);
}

function closeThemeConfirmModal(e) {
    if (!els.themeConfirmModal) return;
    if (e && e.target !== els.themeConfirmModal) return;
    if (els.themeConfirmModal.dataset.navigationDismissOnly === 'true') return;
    resolveThemeConfirm(false);
}
window.closeThemeConfirmModal = closeThemeConfirmModal;

function showThemeConfirm(message, options = {}) {
    const modal = els.themeConfirmModal;
    if (!modal || !els.themeConfirmMessage || !els.themeConfirmOk || !els.themeConfirmCancel) {
        return Promise.resolve(confirm(message));
    }

    if (themeConfirmResolver) resolveThemeConfirm(false);

    const title = options.title || 'Confirm Action';
    const okText = options.okText || 'OK';
    const cancelText = options.cancelText || 'Cancel';
    const isDanger = !!options.danger;

    if (els.themeConfirmTitle) els.themeConfirmTitle.innerText = title;
    els.themeConfirmMessage.innerText = message;
    els.themeConfirmOk.innerText = okText;
    els.themeConfirmCancel.innerText = cancelText;
    els.themeConfirmCancel.hidden = !!options.hideCancel;
    modal.dataset.navigationDismissOnly = String(!!options.navigationDismissOnly);
    els.themeConfirmOk.classList.toggle('danger', isDanger);
    els.themeConfirmOk.classList.toggle('theme-confirm-inline-ok', !!options.linkOk);
    modal.classList.toggle('theme-confirm-pop-in', !!options.popIn);
    // An inline action is a link-style button at the end of the message that stands in for
    // the bottom OK. Every confirmation resets this, so ordinary ones keep both buttons.
    const inlineOkText = options.inlineOk ? String(options.inlineOk) : '';
    let inlineOkButton = null;
    els.themeConfirmOk.hidden = !!inlineOkText;
    if (inlineOkText) {
        inlineOkButton = document.createElement('button');
        inlineOkButton.type = 'button';
        inlineOkButton.className = 'theme-confirm-inline-ok';
        inlineOkButton.textContent = inlineOkText;
        els.themeConfirmMessage.append(' ', inlineOkButton);
    }
    let closeButton = modal.querySelector('.theme-confirm-close');
    if (options.showClose && !closeButton) {
        closeButton = document.createElement('button');
        closeButton.type = 'button';
        closeButton.className = 'theme-confirm-close';
        closeButton.setAttribute('aria-label', 'Dismiss notice');
        closeButton.textContent = '×';
        modal.querySelector('.theme-confirm-content').append(closeButton);
    }
    if (closeButton) {
        closeButton.hidden = !options.showClose;
        closeButton.onclick = (event) => {
            event.preventDefault();
            event.stopPropagation();
            resolveThemeConfirm(false);
        };
    }
    const focusTarget = inlineOkButton || els.themeConfirmOk;

    modal.classList.add('active');
    // Its own top layer: a trapped dialog beneath it (API key over Settings, Ask) would
    // otherwise pull focus straight back out of the confirmation.
    window.MemoraModalCoordinator?.open({
        id: 'theme-confirm-modal',
        element: modal.querySelector('.theme-confirm-content') || modal,
        opener: document.activeElement,
        initialFocus: focusTarget,
        isOpen: () => modal.classList.contains('active'),
        close: () => {
            if (options.navigationDismissOnly) return false;
            resolveThemeConfirm(false);
            return true;
        }
    });

    return new Promise(resolve => {
        themeConfirmResolver = resolve;
        els.themeConfirmOk.onclick = (evt) => {
            evt.preventDefault();
            evt.stopPropagation();
            resolveThemeConfirm(true);
        };
        if (inlineOkButton) inlineOkButton.onclick = els.themeConfirmOk.onclick;
        els.themeConfirmCancel.onclick = (evt) => {
            evt.preventDefault();
            evt.stopPropagation();
            resolveThemeConfirm(false);
        };
        requestAnimationFrame(() => {
            try { focusTarget.focus(); } catch (e) { /* ignore */ }
        });
    });
}

function resolveSessionNamePrompt(value) {
    if (els.sessionNameModal) {
        els.sessionNameModal.classList.remove('active');
    }
    const resolver = sessionNamePromptResolver;
    sessionNamePromptResolver = null;
    if (typeof resolver === 'function') resolver(value);
}

function closeSessionNamePrompt(e) {
    if (!els.sessionNameModal) return;
    if (e && e.target !== els.sessionNameModal) return;
    resolveSessionNamePrompt(null);
}
window.closeSessionNamePrompt = closeSessionNamePrompt;

function showSessionNamePrompt(options = {}) {
    const modal = els.sessionNameModal;
    if (!modal || !els.sessionNameInput || !els.sessionNameCancel || !els.sessionNameForm) {
        const fallbackName = normalizeSessionNameInput(prompt(options.title || 'Name this session', options.defaultName || ''));
        return Promise.resolve(fallbackName || null);
    }

    if (sessionNamePromptResolver) resolveSessionNamePrompt(null);

    const title = $('session-name-title');
    const message = $('session-name-message');
    if (title) title.textContent = options.title || 'Name This Session';
    if (message) message.textContent = options.message || 'Give this study run a name before starting.';
    if (els.sessionNameOk) els.sessionNameOk.textContent = options.okText || 'Start';
    els.sessionNameInput.value = normalizeSessionNameInput(options.defaultName || '');
    if (els.sessionNameError) els.sessionNameError.textContent = '';
    modal.classList.add('active');

    return new Promise(resolve => {
        sessionNamePromptResolver = resolve;

        const submitName = async (evt) => {
            if (evt) {
                evt.preventDefault();
                evt.stopPropagation();
            }
            const name = normalizeSessionNameInput(els.sessionNameInput.value);
            if (!name) {
                if (els.sessionNameError) els.sessionNameError.textContent = options.errorMessage || 'Enter a session name to start.';
                try { els.sessionNameInput.focus(); } catch (e) { /* ignore */ }
                return;
            }
            // `validate` resolves to the clashing name; the prompt stays open until the name is free.
            const clash = typeof options.validate === 'function' ? await options.validate(name) : '';
            if (sessionNamePromptResolver !== resolve) return;
            if (clash) {
                showPopupText(duplicateNameMessage('session', clash), null, true);
                try { els.sessionNameInput.focus(); els.sessionNameInput.select(); } catch (e) { /* ignore */ }
                return;
            }
            resolveSessionNamePrompt(name);
        };

        const cancelPrompt = (evt) => {
            if (evt) {
                evt.preventDefault();
                evt.stopPropagation();
            }
            resolveSessionNamePrompt(null);
        };

        els.sessionNameForm.onsubmit = submitName;
        if (els.sessionNameOk) els.sessionNameOk.onclick = submitName;
        els.sessionNameCancel.onclick = cancelPrompt;
        els.sessionNameInput.oninput = () => {
            if (els.sessionNameError) els.sessionNameError.textContent = '';
        };
        els.sessionNameInput.onkeydown = (evt) => {
            if (evt.key === 'Escape') cancelPrompt(evt);
        };

        requestAnimationFrame(() => {
            try {
                els.sessionNameInput.focus();
                els.sessionNameInput.select();
            } catch (e) { /* ignore */ }
        });
    });
}

async function renameCurrentSessionFromWorkspace() {
    if (document.documentElement.classList.contains('phone-device')) return false;
    const sessionId = normalizeSessionId(currentSessionId);
    if (!sessionId) return false;
    const currentName = getCurrentSessionDisplayLabel();
    const nextName = await showSessionNamePrompt({
        title: 'Rename Session',
        message: 'Choose a title that will be easy to recognize later.',
        okText: 'Save',
        errorMessage: 'Enter a session name to save.',
        defaultName: currentName,
        validate: (name) => findSessionNameConflict(name, sessionId)
    });
    if (!nextName || nextName === currentName) return false;
    try {
        await SessionManager.rename(sessionId, nextName);
        showPopupText(`Session renamed to “${nextName}”.`, null, false);
        return true;
    } catch (error) {
        if (isDuplicateNameError(error)) {
            showPopupText(error.message, null, true);
            return false;
        }
        console.warn('Failed to rename the current session:', error);
        showPopup('Could not rename this session. Try again.', null, true);
        return false;
    }
}
window.renameCurrentSessionFromWorkspace = renameCurrentSessionFromWorkspace;

async function clearCurrentSessionFiles() {
    if (IS_EMBED_DEMO) {
        demoUnavailableFeature('Clearing files');
        return;
    }
    const confirmed = await showThemeConfirm(
        "Remove all files from this session? (Files remain in database for other sessions)",
        { title: 'Clear Current Session', okText: 'Clear All', cancelText: 'Cancel' }
    );
    if (!confirmed) return;
    startNewSession(true);
    await SessionManager.save(false);
}

/* Resolves { removed, cancelled, error } so a caller can report the truth. The session that
   asked keeps authority across the confirmation: a switch while it is open removes nothing. */
let sourceRemovalInFlight = null;

async function removeSource(key, type) {
    if (IS_EMBED_DEMO) {
        demoUnavailableFeature('Removing demo sources');
        return { removed: false, cancelled: true };
    }
    const askedInSession = currentSessionId;
    const sourceLabel = type === 'pdf' ? 'PDF' : 'Quiz File';
    const confirmed = await showThemeConfirm(
        `Remove "${key}" from this session?`,
        { title: `Remove ${sourceLabel}`, okText: 'Remove', cancelText: 'Cancel' }
    );
    if (!confirmed || askedInSession !== currentSessionId) return { removed: false, cancelled: true };

    // Serialize confirmed removals so each record starts from the membership
    // left by the previous commit, instead of resurrecting another removed source.
    while (sourceRemovalInFlight) {
        await sourceRemovalInFlight;
        if (currentSessionId !== askedInSession) return { removed: false, cancelled: true };
    }
    const removal = (async () => {
        const sessionId = currentSessionId;
        let saveError = null;
        try {
            const saved = await SessionManager.save(false, { sourceRemoval: { sessionId, key, type } });
            if (saved !== true) {
                saveError = SessionSaveStatus.getFailure(sessionId)?.error
                    || new Error('The source could not be removed because the session changed while saving. Please try again.');
            }
        } catch (error) {
            saveError = error;
        }
        if (saveError) {
            console.warn(`Removing ${key} could not be saved:`, saveError);
            return { removed: false, cancelled: false, error: saveError?.message || 'The change could not be saved.' };
        }
        // A completed write belongs to the session that requested it. Never detach
        // sources or close viewers in a different session opened during the save.
        if (currentSessionId !== sessionId) return { removed: true, cancelled: false };
    
        if (type === 'json') {
            sessionManifest.jsons = sessionManifest.jsons.filter(name => name !== key);
            delete loadedQuestionSets[key];
            activeQuestionSetKeys.delete(key);
            delete pdfAssociations[key];
            rebuildActiveQuestions();
        } else if (type === 'pdf') {
            sessionManifest.pdfs = sessionManifest.pdfs.filter(name => name !== key);
            Object.keys(pdfAssociations).forEach(name => {
                if (!Array.isArray(pdfAssociations[name])) return;
                pdfAssociations[name] = pdfAssociations[name].filter(pdf => pdf !== key);
                if (!pdfAssociations[name].length) delete pdfAssociations[name];
            });
        }
        markSessionStateMutation();
        saveSessionDebounced();
        await persistPdfAssociations();
        if (currentSessionId !== sessionId) return { removed: true, cancelled: false };
        updateSourceDropdown();
    
        if (type === 'json') {
            await clearQuestionDuplicateReport(key, { clearArchive: true });
            if (currentSessionId !== sessionId) return { removed: true, cancelled: false };
            if (questions.length === 0 && sessionManifest.jsons.length === 0 && sessionManifest.pdfs.length === 0) {
                enterWelcomeMode();
            }
        } else if (type === 'pdf') {
            if (loadedPdfFiles[key]) URL.revokeObjectURL(loadedPdfFiles[key]);
            delete loadedPdfFiles[key];
            forgetMemoraPptxSource(key);
            delete pdfTextIndex[key];
            if (pdfTextCache[key]) delete pdfTextCache[key];
            if (currentPdfTextKey === key) {
                currentPdfText = null;
                currentPdfTextKey = null;
            }
    
            if (typeof window.deletePdfExplanationRecord === 'function') {
                try {
                    await window.deletePdfExplanationRecord(key, sessionId || null, { refreshAiPane: false });
                } catch (err) {
                    console.warn(`Failed to remove PDF explanation cache for ${key}:`, err);
                }
            }
    
            if (currentSessionId !== sessionId) return { removed: true, cancelled: false };
            if ((lastPdfState?.fileName === key)
                || memoraPptxViewerFileName === key
                || normalizePdfStateTitleToName(els.pdfTitle?.innerText) === key) {
                if (els.pdfFrame) els.pdfFrame.src = '';
                if (pdfViewerFileName === key) destroyAdvancedPdfDocument();
                if (memoraPptxViewerFileName === key) destroyMemoraPptxViewer();
                toggleViewer(false);
                els.pdfTitle.innerText = "Add a file to see it here";
                currentPdfBlobUrl = null;
            }
            if (typeof window.refreshAiPanePdfExplanationIfVisible === 'function') {
                window.refreshAiPanePdfExplanationIfVisible(getCurrentPdfNameForExplanationPane());
            }
        }
        return { removed: true, cancelled: false };
    })();
    sourceRemovalInFlight = removal;
    try {
        return await removal;
    } finally {
        if (sourceRemovalInFlight === removal) sourceRemovalInFlight = null;
    }
}

function toggleQuestionSet(key) {
    if (activeQuestionSetKeys.has(key)) { if (activeQuestionSetKeys.size > 1) activeQuestionSetKeys.delete(key); else { showPopup("At least one question set must be active.", null, true); return; } } else activeQuestionSetKeys.add(key);
    rebuildActiveQuestions(); updateSourceDropdown();
}

function syncQuizJumpSelectOptions() {
    if (!els.jump) return;
    els.jump.replaceChildren(...filteredIndices.map((ridx, vidx) => {
        const option = document.createElement('option');
        option.value = vidx;
        option.textContent = `Q ${ridx + 1}` + (isKnowledgeStrongState(knowledgeState[ridx]) ? ' \u2713' : '');
        return option;
    }));
    els.jump.value = currentQIndex;
    if (typeof syncQuizQuestionJumpMenu === 'function') syncQuizQuestionJumpMenu();
}

// A rebuild keeps a restricted pool restricted. Due review recomputes membership from the
// schedule; a whole-deck Flashcard pass and the Condensed/High-Yield owners take in new
// cards; every other filter (Saved, missed, subsets, ranges) keeps only the cards it had.
function rebuildRestrictedPoolIndices(preservedIndices, missingIndices, currentRealIdx = -1) {
    if (isAdaptiveReviewMode || isFlashcardAdaptiveReviewMode) {
        const queue = buildAdaptiveReviewQueue();
        const due = new Set(queue);
        // The card on screen stays until its own rating moves the queue on.
        const kept = preservedIndices.filter(idx => due.has(idx) || idx === currentRealIdx);
        const keptSet = new Set(kept);
        return kept.concat(queue.filter(idx => !keptSet.has(idx)));
    }
    const wholeDeckFlashcardPass = isFlashcardMode && flashcardReviewScope === 'all' && !flashcardPassSubsetKeys;
    if (!isFilterActive || wholeDeckFlashcardPass || condensedQuizState?.enabled || isHighYieldQuizEnabled()) {
        return preservedIndices.concat(missingIndices);
    }
    return preservedIndices.slice();
}

function rebuildActiveQuestions(resetIndex = true) {
    // The open deck's pass is captured under its own deck id before the pool changes.
    recordFlashcardStraightPass();
    const renderedQuestionKeyBeforeRebuild = !resetIndex
        ? String(els.quizContent?._memoraRenderedQuestionKey || '')
        : '';
    const renderedQuestionIndexBeforeRebuild = !resetIndex
        ? Number(els.quizContent?._memoraRenderedQuestionRealIdx)
        : -1;
    // Generated banks are added at the front of the manifest. Preserve the current
    // question pool by its stable keys before that reorders the backing array.
    const previousFilteredQuestionKeys = resetIndex
        ? []
        : buildFilteredQuestionKeysForPersistence(filteredIndices);
    // Back up index-based state objects into stable key buckets before altering the array
    const answersBuckets = buildIndexedStateBucketsByQuestionKey(userAnswers);
    const savedBuckets = buildIndexedStateBucketsByQuestionKey(savedQuestions);
    const knowledgeBuckets = buildIndexedStateBucketsByQuestionKey(knowledgeState);
    const srsBuckets = buildIndexedStateBucketsByQuestionKey(srsData);
    const statsBuckets = buildIndexedStateBucketsByQuestionKey(statsData);
    const progressBuckets = {
        userAnswers: answersBuckets,
        savedQuestions: savedBuckets,
        knowledgeState: knowledgeBuckets,
        srsData: srsBuckets,
        statsData: statsBuckets
    };
    // The leaving pool's progress is parked before the pool changes.
    const previousPoolKeys = listObservedQuestionKeys();
    parkQuestionProgress(previousPoolKeys, progressBuckets);

    let combined = [];
    // Use manifest order (new generated quizzes are unshifted to the front of manifest.jsons, placing them first)
    sessionManifest.jsons.forEach(key => {
        const strippedKey = key.replace(/\.(json|txt)$/i, '');
        const aliasKeys = Array.from(new Set([key, strippedKey].filter(Boolean)));
        if (!aliasKeys.some(alias => activeQuestionSetKeys.has(alias))) return;

        const sourceKey = aliasKeys.find(alias => loadedQuestionSets[alias] !== undefined);
        if (!sourceKey) return;

        const rawQuestionSet = loadedQuestionSets[sourceKey];
        const normalizedSet = normalizeStoredQuestionCollection(rawQuestionSet, sourceKey);
        if (Array.isArray(normalizedSet)) {
            persistNormalizedQuestionCollectionIfChanged(sourceKey, rawQuestionSet, normalizedSet);
            loadedQuestionSets[sourceKey] = normalizedSet;
            aliasKeys.forEach((alias) => {
                if (loadedQuestionSets[alias] !== undefined) loadedQuestionSets[alias] = normalizedSet;
            });
            combined = combined.concat(normalizedSet);
        } else {
            console.warn(`loadedQuestionSets[${sourceKey}] could not be normalized:`, loadedQuestionSets[sourceKey]);
        }
    });

    // Normalize once more to protect sessions imported before the shared legacy parser existed.
    questions = combined
        .map(q => normalizeLegacyQuizQuestion(q, q?.sourceFile || q?.src || ''))
        .filter(Boolean);

    // A bank returning to the pool brings back its parked or stored progress.
    const enteringKeys = Array.from(listObservedQuestionKeys()).filter((key) => !previousPoolKeys.has(key));
    addOffPoolQuestionProgress(progressBuckets, enteringKeys);

    // Restore index-based states using the new indices of the questions array
    const rebuiltUserAnswers = buildIndexStateFromQuestionKeyBuckets(answersBuckets);
    userAnswers = rebuiltUserAnswers;
    savedQuestions = buildIndexStateFromQuestionKeyBuckets(savedBuckets);
    knowledgeState = buildIndexStateFromQuestionKeyBuckets(knowledgeBuckets);
    srsData = buildIndexStateFromQuestionKeyBuckets(srsBuckets);
    statsData = buildIndexStateFromQuestionKeyBuckets(statsBuckets);

    if (!resetIndex) {
        let targetRealIdx = renderedQuestionIndexBeforeRebuild;
        if (renderedQuestionKeyBeforeRebuild) {
            const foundIdx = questions.findIndex(q => buildQuestionPersistenceKeyFromQuestion(q) === renderedQuestionKeyBeforeRebuild);
            if (foundIdx !== -1) targetRealIdx = foundIdx;
        }

        const preservedIndices = buildFilteredIndicesFromQuestionKeyList(previousFilteredQuestionKeys);
        const existingIndices = new Set(preservedIndices);
        const missingIndices = [];
        for (let i = 0; i < questions.length; i++) {
            if (!existingIndices.has(i)) missingIndices.push(i);
        }
        const eligiblePoolIsIntentionallyChanging = isAdaptiveReviewMode
            || isFilterActive
            || condensedQuizState?.enabled
            || isHighYieldQuizEnabled();
        if (eligiblePoolIsIntentionallyChanging) {
            // Filters and adaptive queues define a new eligible pool. They must
            // explicitly start a fresh completion cohort rather than treating
            // newly eligible cards as streamed additions to the old run.
            filteredIndices = rebuildRestrictedPoolIndices(preservedIndices, missingIndices, targetRealIdx);
            resetQuizCompletionCohort('eligible-pool-rebuild');
        } else {
            const previousActiveKeys = normalizeQuizCompletionQuestionKeys(
                quizCompletionCohortState.activeQuestionKeys.length > 0
                    ? quizCompletionCohortState.activeQuestionKeys
                    : previousFilteredQuestionKeys
            );
            const candidateKeys = getQuizCompletionQuestionKeysForIndices(
                Array.from({ length: questions.length }, (_, idx) => idx)
            );
            const candidateKeySet = new Set(candidateKeys);
            const activeKeys = previousActiveKeys.filter((key) => candidateKeySet.has(key));
            const pendingKeys = normalizeQuizCompletionQuestionKeys(quizCompletionCohortState.pendingNewQuestionKeys)
                .filter((key) => candidateKeySet.has(key) && !activeKeys.includes(key));
            const knownKeys = new Set(activeKeys.concat(pendingKeys));
            const newlyArrivedKeys = candidateKeys.filter((key) => !knownKeys.has(key));
            const nextPendingKeys = normalizeQuizCompletionQuestionKeys(pendingKeys.concat(newlyArrivedKeys));

            if (activeKeys.length === 0) {
                // A first load or a legacy session has no cohort metadata yet.
                // Start it from the currently available queue.
                filteredIndices = isShuffled ? missingIndices.slice() : missingIndices.slice();
                quizCompletionCohortState = {
                    version: QUIZ_COMPLETION_COHORT_VERSION,
                    activeQuestionKeys: normalizeQuizCompletionQuestionKeys(
                        getQuizCompletionQuestionKeysForIndices(filteredIndices)
                    ),
                    pendingNewQuestionKeys: [],
                    updatedAt: Date.now(),
                    reason: 'cohort-initialized'
                };
            } else {
                // The active cohort remains the visible queue. Newly generated
                // questions are tracked separately until generation completes or
                // the user explicitly rolls the cohort forward.
                filteredIndices = getQuestionIndicesForCompletionKeys(activeKeys);
                quizCompletionCohortState = {
                    version: QUIZ_COMPLETION_COHORT_VERSION,
                    activeQuestionKeys: activeKeys,
                    pendingNewQuestionKeys: nextPendingKeys,
                    updatedAt: Date.now(),
                    reason: 'incremental-rebuild'
                };
            }
        }
        if (targetRealIdx !== -1 && filteredIndices.length) {
            const newPos = filteredIndices.indexOf(targetRealIdx);
            currentQIndex = newPos !== -1 ? newPos : Math.min(Math.max(currentQIndex, 0), filteredIndices.length - 1);
        } else {
            currentQIndex = filteredIndices.length
                ? Math.min(Math.max(currentQIndex, 0), filteredIndices.length - 1)
                : 0;
        }
    }

    markQuestionNotesNeedsMigration();
    forcedChoiceOrderByQuestion = {};
    mixedChoiceOrderByQuestion = {};
    const condensedStateChanged = reconcileCondensedQuizStateWithCurrentQuestionBank({
        refreshView: !resetIndex && !!condensedQuizState?.enabled,
        preserveCurrentQuestion: true,
        save: false
    });
    if (!condensedStateChanged) syncCondensedQuizToggleButton();
    if (!resetIndex) applyHighYieldQuizPool({ preserveCurrentQuestion: true });
    else highYieldLastAppliedPool = null;
    backfillAdaptiveConfidenceStoreFromSessionData();
    console.debug(`Rebuilt questions: ${questions.length} total.`);
    // A reopened quiz finishes any answer explanations its generation run left unwritten. Quiz
    // generation loads on intent, so it is fetched only when a question here lacks feedback.
    if (typeof scheduleQuizExplanationResumeForActiveSets === 'function') {
        scheduleQuizExplanationResumeForActiveSets();
    } else if (questions.some((q) => q && !q.answerDispute && q.sourceFile && Array.isArray(q.c) && q.c.length > 1
        && (!String(q.e || '').trim() || q.c.some((_c, i) => i !== Number(q.a) && !String(q.w?.[i] || '').trim())))) {
        Promise.resolve(window.MemoraRuntime?.ensureFeature?.('quizgen'))
            .then(() => { if (typeof scheduleQuizExplanationResumeForActiveSets === 'function') scheduleQuizExplanationResumeForActiveSets(); })
            .catch(() => {});
    }

    // Only reset filter and render if resetIndex is true
    // When loading a session, we handle filter/render separately
    if (resetIndex) {
        resetFilter();
        if (!isShuffled) currentQIndex = 0;
        if (isFlashcardMode) renderFlashcardGrid(); else renderQuestion();
    } else {
        const nextRenderedRealIdx = Number(filteredIndices[currentQIndex]);
        const nextRenderedQuestionKey = Number.isInteger(nextRenderedRealIdx)
            ? buildQuestionPersistenceKeyFromQuestion(questions[nextRenderedRealIdx])
            : '';
        const renderedCardIsStale = !renderedQuestionKeyBeforeRebuild
            ? (questions.length > 0 && filteredIndices.length > 0)
            : (renderedQuestionKeyBeforeRebuild !== nextRenderedQuestionKey);
        if (renderedCardIsStale && !isFlashcardMode) {
            renderQuestion();
        } else {
            if (Number.isInteger(nextRenderedRealIdx) && nextRenderedRealIdx >= 0) {
                if (els.quizContent) {
                    els.quizContent._memoraRenderedQuestionRealIdx = nextRenderedRealIdx;
                    els.quizContent._memoraRenderedQuestionKey = nextRenderedQuestionKey;
                }
                lastRenderedQuestionRealIdx = nextRenderedRealIdx;
                syncNotesButtonIndicator(nextRenderedRealIdx);
                if (questions[nextRenderedRealIdx]) {
                    const sourceLabel = questions[nextRenderedRealIdx].sourceFile || 'Unknown';
                    const modelLabel = getQuestionGenerationModel(questions[nextRenderedRealIdx]) || 'Not tracked';
                    if (els.jump) els.jump.title = `Source: ${sourceLabel}\nAI Model: ${modelLabel}`;
                }
            }
            syncQuizJumpSelectOptions();
            // Animate the Question X of Y counter
            updateDiscreteQuestionCounter(filteredIndices.length);
        }
    }

    if (els.studyDashboardSurface && els.studyDashboardSurface.style.display !== 'none') {
        renderStudyDashboard();
    }
    syncContentDependentControls();
}

function openPdf(filename) {
    if (!loadedPdfFiles[filename]) return;
    loadPdfInPane(filename, { page: 1, searchTerm: '', smooth: true, ensureViewer: true, maintainScale: false });
}

/* --- THEME & PERSISTENCE --- */
const THEME_GRADIENT_COLORS = [
    ['#60a5fa', '#a78bfa', '#f472b6'], // Default
    ['#f59e0b', '#f97316', '#dc2626'], // Amber
    ['#c0265b', '#7f1d5a', '#5c2f1a'], // Rosewood
    ['#84cc16', '#10b981', '#0891b2'], // Lime Teal
    ['#b45309', '#d97706', '#65a30d'], // Earthy
    ['#06b6d4', '#4f46e5', '#9333ea'], // Midnight Aurora
    ['#65a30d', '#059669', '#0ea5e9'], // Sage Bloom
    ['#f43f5e', '#f97316', '#eab308'], // Coral Tide
    ['#6366f1', '#c026d3', '#f472b6'], // Lavender Dusk
    ['#0891b2', '#2563eb', '#334155'], // Ocean Glass
    ['#22c55e', '#047857', '#4d7c0f'], // Forest Moss
    ['#facc15', '#ea580c', '#b91c1c'], // Solar Flare
    ['#7e22ce', '#be185d', '#e11d48'], // Berry Nebula
    ['#22d3ee', '#34d399', '#a3e635'], // Arctic Mint
    ['#f97316', '#db2777', '#6d28d9']  // Ember Violet
];
const THEME_GRADIENT_STOPS = ['40%', '40%', '40%', '40%', '40%', '42%', '42%', '42%', '44%', '44%', '44%', '42%', '42%', '44%', '42%'];
const gradients = THEME_GRADIENT_COLORS.map((colors, idx) => buildGradientString(colors, THEME_GRADIENT_STOPS[idx]));
const DEFAULT_THEME_COLORS = THEME_GRADIENT_COLORS[0];
const DEFAULT_THEME_GRADIENT = gradients[0];
const LEGACY_SATURATED_DEFAULT_COLORS = ['#2f80ed', '#7c3aed', '#db2777'];
const GRADIENT_INTENSITY_DEFAULT = 100;
const GRADIENT_INTENSITY_MIN = 50;
const GRADIENT_INTENSITY_MAX = 150;

const AI_BOLD_DEFAULT = '#bfdbfe';
const QUIZ_CARD_BG_TRANSPARENT = 'transparent';
const QUIZ_CARD_BG_DEFAULT_COLOR = '#0f172a';
const QUIZ_CARD_BG_DEFAULT = QUIZ_CARD_BG_TRANSPARENT;
const QUIZ_CARD_BG_TRANSPARENT_PREF_KEY = 'memora.quizCardBg.transparent.explicit';
const QUIZ_BG_GAMMA_DEFAULT = 100;
const QUIZ_BG_DIM_DEFAULT = 45;
const QUIZ_BG_ACCENT_DEFAULT = 100;
let customGradientMode = '2';
let gradientIntensity = GRADIENT_INTENSITY_DEFAULT;
let lastQuizCardBgColor = QUIZ_CARD_BG_DEFAULT_COLOR;

function clampThemeSliderValue(rawValue, min, max, fallback) {
    const next = Number(rawValue);
    if (!Number.isFinite(next)) return fallback;
    return Math.min(max, Math.max(min, Math.round(next)));
}

function buildGradientString(colors, midStop = '40%') {
    const stops = Array.isArray(colors) ? colors.filter(Boolean) : [];
    if (stops.length >= 3) return `linear-gradient(135deg, ${stops[0]}, ${stops[1]} ${midStop}, ${stops[2]})`;
    if (stops.length === 2) return `linear-gradient(135deg, ${stops[0]}, ${stops[1]})`;
    const only = stops[0] || DEFAULT_THEME_COLORS[0];
    return `linear-gradient(135deg, ${only}, ${only})`;
}

function isLegacySaturatedDefaultColors(colors) {
    if (!Array.isArray(colors) || colors.length < 3) return false;
    return LEGACY_SATURATED_DEFAULT_COLORS.every((color, idx) =>
        String(colors[idx] || '').trim().toLowerCase() === color);
}

function normalizeLegacyDefaultGradientString(gradient) {
    const stops = String(gradient || '').match(/#[0-9a-fA-F]{3,8}|rgba?\([^)]+\)|hsla?\([^)]+\)/g) || [];
    if (isLegacySaturatedDefaultColors([stops[0], stops[1], stops[stops.length - 1]])) {
        return DEFAULT_THEME_GRADIENT;
    }
    return gradient;
}

function getGradientIntensityRatio() {
    return clampThemeSliderValue(gradientIntensity, GRADIENT_INTENSITY_MIN, GRADIENT_INTENSITY_MAX, GRADIENT_INTENSITY_DEFAULT) / 100;
}

function applyThemeColorIntensity(color) {
    const normalized = normalizeHexColorForCanvas(color, '');
    if (!normalized) return color;
    const { h, s, l } = hexToHsl(normalized);
    return hslToHex(h, clampNum(s * getGradientIntensityRatio(), 0, 1), l);
}

function getIntensityAdjustedColors(colors) {
    return (Array.isArray(colors) ? colors : DEFAULT_THEME_COLORS).map(applyThemeColorIntensity);
}

function syncGradientIntensityControls() {
    gradientIntensity = clampThemeSliderValue(gradientIntensity, GRADIENT_INTENSITY_MIN, GRADIENT_INTENSITY_MAX, GRADIENT_INTENSITY_DEFAULT);
    if (els.gradientIntensity) els.gradientIntensity.value = String(gradientIntensity);
    if (els.gradientIntensityValue) els.gradientIntensityValue.textContent = `${gradientIntensity}%`;
    document.documentElement.style.setProperty('--theme-gradient-saturation', getGradientIntensityRatio().toFixed(2));
}

function applyGradientIntensity(options = {}) {
    gradientIntensity = clampThemeSliderValue(els.gradientIntensity?.value, GRADIENT_INTENSITY_MIN, GRADIENT_INTENSITY_MAX, GRADIENT_INTENSITY_DEFAULT);
    syncGradientIntensityControls();
    const activeSwatch = document.querySelector('.grad-swatch.active');
    if (Number.isInteger(currentThemeIndex) && currentThemeIndex >= 0 && activeSwatch) {
        applyGradientPreset(currentThemeIndex, activeSwatch, options);
    } else {
        updateCustomGradient(options);
    }
}

function updateBackgroundControlsSummary() {
    if (els.bgGammaValue && els.bgGamma) els.bgGammaValue.textContent = `${els.bgGamma.value}%`;
    if (els.bgDimValue && els.bgDim) els.bgDimValue.textContent = `${els.bgDim.value}%`;
    if (els.bgAccentValue && els.bgAccent) els.bgAccentValue.textContent = `${els.bgAccent.value}%`;
    if (els.bgControlsStatus && els.bgGamma) els.bgControlsStatus.textContent = `Gamma ${els.bgGamma.value}%`;
}

function applyQuizBackgroundControls({ gamma, dim, accent } = {}, options = {}) {
    const nextGamma = clampThemeSliderValue(gamma ?? els.bgGamma?.value, 70, 130, QUIZ_BG_GAMMA_DEFAULT);
    const nextDim = clampThemeSliderValue(dim ?? els.bgDim?.value, 0, 45, QUIZ_BG_DIM_DEFAULT);
    const nextAccent = clampThemeSliderValue(accent ?? els.bgAccent?.value, 60, 140, QUIZ_BG_ACCENT_DEFAULT);

    if (els.bgGamma) els.bgGamma.value = String(nextGamma);
    if (els.bgDim) els.bgDim.value = String(nextDim);
    if (els.bgAccent) els.bgAccent.value = String(nextAccent);

    document.documentElement.style.setProperty('--quiz-pane-bg-brightness', (nextGamma / 100).toFixed(2));
    document.documentElement.style.setProperty('--quiz-pane-bg-dim-opacity', (nextDim / 100).toFixed(2));
    document.documentElement.style.setProperty('--quiz-pane-bg-saturation', (nextAccent / 100).toFixed(2));

    updateBackgroundControlsSummary();
    if (options.persist !== false) saveThemeState();
}

function resetQuizBackgroundControls(options = {}) {
    applyQuizBackgroundControls({
        gamma: QUIZ_BG_GAMMA_DEFAULT,
        dim: QUIZ_BG_DIM_DEFAULT,
        accent: QUIZ_BG_ACCENT_DEFAULT
    }, options);
}

function syncQuizCardBgState(value) {
    const isTransparent = String(value || '').trim().toLowerCase() === QUIZ_CARD_BG_TRANSPARENT;
    document.body.classList.toggle('quiz-card-bg-transparent', isTransparent);
    if (els.colCardBgTransparentBtn) {
        els.colCardBgTransparentBtn.classList.toggle('active', isTransparent);
        els.colCardBgTransparentBtn.setAttribute('aria-pressed', isTransparent ? 'true' : 'false');
    }
    if (!isTransparent && value) {
        lastQuizCardBgColor = value;
        if (els.colCardBg) {
            els.colCardBg.dataset.lastColor = value;
        }
    }
}

function shouldPreserveTransparentQuizCardBg(themeState) {
    return !!themeState?.cBgTransparentExplicit ||
        localStorage.getItem(QUIZ_CARD_BG_TRANSPARENT_PREF_KEY) === '1';
}

function normalizeLoadedQuizCardBg(value, themeState) {
    const normalized = String(value || '').trim().toLowerCase();
    if (!normalized) return QUIZ_CARD_BG_DEFAULT;
    if (normalized === QUIZ_CARD_BG_TRANSPARENT && !shouldPreserveTransparentQuizCardBg(themeState)) {
        return QUIZ_CARD_BG_DEFAULT;
    }
    return value;
}

function toggleQuizCardBgTransparency() {
    const isTransparent = document.body.classList.contains('quiz-card-bg-transparent');
    if (isTransparent) {
        localStorage.removeItem(QUIZ_CARD_BG_TRANSPARENT_PREF_KEY);
        const fallback = (els.colCardBg && (els.colCardBg.dataset.lastColor || els.colCardBg.value)) || lastQuizCardBgColor || QUIZ_CARD_BG_DEFAULT_COLOR;
        updateThemeProp('--quiz-card-bg', fallback);
        syncPickerValue(els.colCardBg, fallback);
        return;
    }

    const current = (els.colCardBg && els.colCardBg.value) || lastQuizCardBgColor || QUIZ_CARD_BG_DEFAULT_COLOR;
    if (els.colCardBg) {
        els.colCardBg.dataset.lastColor = current;
    }
    lastQuizCardBgColor = current;
    localStorage.setItem(QUIZ_CARD_BG_TRANSPARENT_PREF_KEY, '1');
    updateThemeProp('--quiz-card-bg', QUIZ_CARD_BG_TRANSPARENT);
}

function setGradient(idx, el) {
    applyGradientPreset(idx, el);
}

// --- Style Overhauls (css/theme-overhauls.css) ---
const THEME_OVERHAUL_STORAGE_KEY = 'memora_theme_overhaul_v1';
const THEME_OVERHAUL_NAMES = [
    'default', 'glassmorphism', 'cozy-earth', 'royal-obsidian', 'sakura',
    'abyssal-deep', 'indigo-nebula', 'nord-frost', 'steampunk',
    'aurora-slate', 'ember-noir', 'verdant-ink', 'carbon-night',
    'editorial-night', 'ultraviolet'
];
let currentThemeOverhaul = 'editorial-night';

function isThemeOverhaulDesktopEligible() {
    const support = window.__MEMORA_DEVICE_SUPPORT__ || {};
    const isPhoneLike = !!support.isPhoneLike
        || document.documentElement.classList.contains('phone-device');
    const isIpadLike = document.documentElement.classList.contains('ipad-like-device');
    return !isPhoneLike && (!support.isTabletLike || isIpadLike);
}

// Styled themes own their accent palette and are already dark (theme-overhauls.css), so
// gradients and Dark Mode only change the Default theme. Say so beside those controls
// instead of leaving them silently inert.
function syncThemeScopedControls(appliedName = 'default') {
    const styledTheme = appliedName !== 'default';
    const gradientControls = $('theme-gradient-controls');
    if (gradientControls) gradientControls.inert = styledTheme;
    const gradientNote = $('theme-gradient-scope-note');
    if (gradientNote) gradientNote.hidden = !styledTheme;
    const darkModeBtn = $('dark-mode-toggle');
    if (darkModeBtn) darkModeBtn.disabled = styledTheme;
    const darkModeNote = $('dark-mode-scope-note');
    if (darkModeNote) darkModeNote.hidden = !styledTheme;
}

function setThemeOverhaul(themeName, options = {}) {
    const { persist = true, allowLandingEditorial = false } = options;
    const requestedName = THEME_OVERHAUL_NAMES.includes(themeName) ? themeName : 'editorial-night';
    const name = requestedName === 'default' ? 'editorial-night' : requestedName;
    const desktopEligible = isThemeOverhaulDesktopEligible();
    const support = window.__MEMORA_DEVICE_SUPPORT__ || {};
    const isPhoneLike = !!support.isPhoneLike
        || document.documentElement.classList.contains('phone-device');
    const landingEditorialEligible = allowLandingEditorial
        && name === 'editorial-night'
        && !isPhoneLike;
    const appliedName = desktopEligible || landingEditorialEligible ? name : 'default';
    currentThemeOverhaul = name;
    // Attribute lives on <html> so variable overrides recompute the derived
    // :root vars (--app-surface*, --btn-*), and on <body> for element rules.
    // Phones and non-iPad tablets retain the original Default experience.
    if (appliedName === 'default') {
        document.documentElement.removeAttribute('data-theme-overhaul');
        if (document.body) document.body.removeAttribute('data-theme-overhaul');
    } else {
        document.documentElement.setAttribute('data-theme-overhaul', appliedName);
        if (document.body) document.body.setAttribute('data-theme-overhaul', appliedName);
    }
    const overhaulContainer = document.getElementById('theme-overhaul-container');
    if (overhaulContainer) overhaulContainer.hidden = !desktopEligible;
    document.querySelectorAll('#theme-overhaul-container .overhaul-btn').forEach(btn => {
        const checked = btn.dataset.overhaul === name;
        btn.classList.toggle('active', checked);
        btn.setAttribute('aria-checked', checked ? 'true' : 'false');
        // One Tab stop for the group; arrow keys move between themes.
        btn.tabIndex = checked ? 0 : -1;
    });
    syncThemeScopedControls(appliedName);
    syncGradientControlsToActiveTheme();
    if (persist) {
        const savedAt = String(Date.now());
        let persistedLocally = false;
        try {
            localStorage.setItem(THEME_OVERHAUL_STORAGE_KEY, name);
            persistedLocally = localStorage.getItem(THEME_OVERHAUL_STORAGE_KEY) === name;
            if (persistedLocally) {
                localStorage.setItem(`${THEME_OVERHAUL_STORAGE_KEY}_updated_at`, savedAt);
            }
        } catch (error) {
            console.warn('Theme preference could not be saved to local storage:', error);
        }
        try {
            sessionStorage.setItem(THEME_OVERHAUL_STORAGE_KEY, name);
            if (sessionStorage.getItem(THEME_OVERHAUL_STORAGE_KEY) === name) {
                sessionStorage.setItem(`${THEME_OVERHAUL_STORAGE_KEY}_updated_at`, savedAt);
            }
        } catch (error) {
            console.warn('Theme preference could not be mirrored to session storage:', error);
        }
        if (!persistedLocally) {
            console.warn('Theme preference is available for this tab only.');
        }
        try {
            saveThemeState();
        } catch (error) {
            console.warn('Theme preference could not be mirrored to the unified theme state:', error);
        }
    }
    window.dispatchEvent(new CustomEvent('memora:theme-overhaul-changed', {
        detail: { theme: appliedName, requestedTheme: name, desktopEligible }
    }));
    // Refresh browser chrome tint + canvas color derived from --card-bg.
    if (typeof syncMetaThemeColor === 'function') {
        try { syncMetaThemeColor(); } catch (e) { /* non-fatal */ }
    }
}

function loadThemeOverhaulState() {
    let localSaved = null;
    let sessionSaved = null;
    let localUpdatedAt = 0;
    let sessionUpdatedAt = 0;
    try {
        localSaved = localStorage.getItem(THEME_OVERHAUL_STORAGE_KEY);
        localUpdatedAt = Number(localStorage.getItem(`${THEME_OVERHAUL_STORAGE_KEY}_updated_at`)) || 0;
    } catch (error) {
        console.warn('Theme preference could not be read from local storage:', error);
    }
    try {
        sessionSaved = sessionStorage.getItem(THEME_OVERHAUL_STORAGE_KEY);
        sessionUpdatedAt = Number(sessionStorage.getItem(`${THEME_OVERHAUL_STORAGE_KEY}_updated_at`)) || 0;
    } catch (error) {
        console.warn('Theme preference could not be read from session storage:', error);
    }
    const localValid = THEME_OVERHAUL_NAMES.includes(localSaved);
    const sessionValid = THEME_OVERHAUL_NAMES.includes(sessionSaved);
    let saved = localValid && sessionValid
        ? (sessionUpdatedAt > localUpdatedAt ? sessionSaved : localSaved)
        : (sessionValid ? sessionSaved : (localValid ? localSaved : null));
    if (!THEME_OVERHAUL_NAMES.includes(saved)) {
        try {
            const storedTheme = getStoredQuizTheme();
            const storedOverhaul = storedTheme?.overhaul;
            saved = THEME_OVERHAUL_NAMES.includes(storedOverhaul)
                ? storedOverhaul
                : document.documentElement.getAttribute('data-theme-overhaul');
        } catch (error) {
            console.warn('Theme overhaul fallback could not be read:', error);
        }
    }
    const queryTheme = (DEMO_QUERY.get('theme') || '').trim();
    if (THEME_OVERHAUL_NAMES.includes(queryTheme)) {
        saved = queryTheme;
    }
    setThemeOverhaul(saved === 'default' ? 'editorial-night' : (saved || 'editorial-night'), { persist: false });
}

function syncMetaThemeColor() {
    const meta = document.getElementById('meta-theme-color');
    const styles = getComputedStyle(document.documentElement);
    const surfaceMuted = styles.getPropertyValue('--app-surface-muted').trim();
    if (!surfaceMuted) {
        if (meta) meta.setAttribute('content', '#030711');
        return;
    }
    // Match the topmost stop of the html/body background gradient (base.css)
    // so browser chrome tinting blends into the page instead of showing a
    // hard black band above/below the app.
    const probe = document.createElement('span');
    probe.style.color = 'color-mix(in srgb, var(--app-surface-muted) 82%, #151922 18%)';
    probe.style.position = 'fixed';
    probe.style.visibility = 'hidden';
    (document.body || document.documentElement).appendChild(probe);
    const resolved = getComputedStyle(probe).color;
    probe.remove();
    // Theme surfaces derive from a semi-transparent --card-bg, so the resolved
    // color can carry alpha. Flatten it over the page base (#030711) because
    // browsers ignore/clamp alpha when tinting chrome. Browsers serialize
    // color-mix() results either as rgb()/rgba() or as color(srgb ...),
    // so parse both.
    const parsed = parseCssColorToRgba(resolved);
    if (!parsed) {
        if (meta) meta.setAttribute('content', resolved || '#030711');
        return;
    }
    const base = [3, 7, 17]; // #030711
    const flat = [parsed.r, parsed.g, parsed.b].map((channel, i) =>
        Math.round(channel * parsed.a + base[i] * (1 - parsed.a)));
    const themedSolid = `rgb(${flat[0]}, ${flat[1]}, ${flat[2]})`;
    // Keep the browser's chrome tint and the page canvas on the same theme solid.
    if (meta) meta.setAttribute('content', themedSolid);
    applyThemedCanvasColor(themedSolid);
}

// Safari (and other browsers) tint their chrome and safe-area/overscroll
// regions from the page's background-COLOR, not its background-image
// gradients. base.css hard-codes near-black fallbacks (#030711 / #0a0f15)
// under the themed gradients, which is why the browser bars rendered as a
// stark black band. Mirror the themed color onto html/body so the chrome
// blends with the theme.
function parseCssColorToRgba(value) {
    if (!value) return null;
    let m = /rgba?\(\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)(?:\s*,\s*([\d.]+))?\s*\)/.exec(value);
    if (m) {
        return { r: parseFloat(m[1]), g: parseFloat(m[2]), b: parseFloat(m[3]), a: m[4] === undefined ? 1 : parseFloat(m[4]) };
    }
    m = /color\(srgb\s+([\d.]+)\s+([\d.]+)\s+([\d.]+)(?:\s*\/\s*([\d.]+%?|none))?\s*\)/.exec(value);
    if (m) {
        let a = 1;
        if (m[4] !== undefined && m[4] !== 'none') {
            a = m[4].endsWith('%') ? parseFloat(m[4]) / 100 : parseFloat(m[4]);
        }
        return { r: parseFloat(m[1]) * 255, g: parseFloat(m[2]) * 255, b: parseFloat(m[3]) * 255, a };
    }
    return null;
}

function applyThemedCanvasColor(themedSolid) {
    if (!themedSolid) return;
    document.documentElement.style.backgroundColor = themedSolid;
    document.documentElement.style.setProperty('--chrome-merge-color', themedSolid);
    if (document.body) document.body.style.backgroundColor = themedSolid;
}

const THEME_GRADIENT_PROPS = ['--g', '--g1', '--g2', '--g3', '--amb-c1', '--amb-c2'];

function setThemeGradientProperty(prop, value) {
    const priority = document.documentElement.hasAttribute('data-theme-overhaul') ? 'important' : '';
    document.documentElement.style.setProperty(prop, value, priority);
}

// A custom gradient exists only as inline vars; without them the theme's own palette
// shows. Styled themes lock their accent palette on <body> (theme-overhauls.css), so a
// custom gradient is only visible while the Default theme is selected.
function hasCustomThemeGradient() {
    return !!document.documentElement.style.getPropertyValue('--g');
}

function clearThemeGradientOverrides() {
    THEME_GRADIENT_PROPS.forEach((prop) => document.documentElement.style.removeProperty(prop));
}

function isValidGradientPresetIndex(index) {
    return Number.isInteger(index) && index >= 0 && index < THEME_GRADIENT_COLORS.length;
}

function buildGradientPresetString(index) {
    return buildGradientString(getIntensityAdjustedColors(THEME_GRADIENT_COLORS[index]), THEME_GRADIENT_STOPS[index] || '40%');
}

// Theme state names its preset; a record from before that field is matched against each
// preset at its saved intensity, so a hand-built gradient only resembling one stays custom.
function resolveStoredGradientPreset(t) {
    const saved = String(t?.gradient || '').trim().toLowerCase();
    if (isValidGradientPresetIndex(t?.gradientPreset)) {
        return !saved || buildGradientPresetString(t.gradientPreset).toLowerCase() === saved ? t.gradientPreset : -1;
    }
    if (!saved || t?.gradientPreset !== undefined) return -1;
    return THEME_GRADIENT_COLORS.findIndex((_colors, index) => buildGradientPresetString(index).toLowerCase() === saved);
}

function findGradientSwatch(index) {
    return Array.from(document.querySelectorAll('.grad-swatch'))
        .find((swatch) => (swatch.getAttribute('onclick') || '').includes(`setGradient(${index},`)) || null;
}

function setActiveGradientSwatch(swatch) {
    document.querySelectorAll('.grad-swatch').forEach((node) => {
        const active = node === swatch;
        node.classList.toggle('active', active);
        node.setAttribute('aria-pressed', active ? 'true' : 'false');
    });
}

// With no custom gradient the builder mirrors the palette on screen, so editing a color
// or the intensity starts from the active theme instead of stale picker values.
function syncGradientControlsToActiveTheme() {
    if (hasCustomThemeGradient()) return;
    const nativeGradient = getComputedStyle(document.documentElement).getPropertyValue('--g').trim();
    const stops = nativeGradient.match(/#[0-9a-fA-F]{3,8}|rgba?\([^)]+\)|hsla?\([^)]+\)/g) || [];
    if (!stops.length) return;
    syncPickerValue(els.gradStart, stops[0]);
    syncPickerValue(els.gradMid, stops.length >= 3 ? stops[1] : stops[0]);
    syncPickerValue(els.gradEnd, stops[stops.length - 1]);
    const distinctStops = new Set(stops.map((stop) => stop.toLowerCase())).size;
    setCustomGradientMode(distinctStops === 1 ? '1' : (stops.length >= 3 ? '3' : '2'), { persist: false, updateGradient: false });
    currentThemeIndex = gradients.indexOf(nativeGradient);
    setActiveGradientSwatch(currentThemeIndex >= 0 ? findGradientSwatch(currentThemeIndex) : null);
}

function applyGradientPreset(idx, el, options = {}) {
    const index = Number.isInteger(Number(idx)) ? Number(idx) : 0;
    const colors = THEME_GRADIENT_COLORS[index] || THEME_GRADIENT_COLORS[0];
    const adjustedColors = getIntensityAdjustedColors(colors);
    const gradient = buildGradientString(adjustedColors, THEME_GRADIENT_STOPS[index] || '40%');
    const start = adjustedColors[0];
    const mid = adjustedColors[1] || start;
    const end = adjustedColors[2] || mid;

    currentThemeIndex = index;
    beatColorT = 0;
    setThemeGradientProperty('--g', gradient);
    setThemeGradientProperty('--g1', start);
    setThemeGradientProperty('--g2', end);
    setThemeGradientProperty('--g3', mid);
    setThemeGradientProperty('--amb-c1', start + '66');
    setThemeGradientProperty('--amb-c2', end + '99');

    if (els.gradStart) els.gradStart.value = colors[0] || DEFAULT_THEME_COLORS[0];
    if (els.gradMid) els.gradMid.value = colors[1] || colors[0] || DEFAULT_THEME_COLORS[1];
    if (els.gradEnd) els.gradEnd.value = colors[2] || colors[1] || colors[0] || DEFAULT_THEME_COLORS[2];
    setCustomGradientMode('3', { persist: false, updateGradient: false });

    // Swatches are not in preset-index order, so match the swatch by its preset.
    setActiveGradientSwatch(el || findGradientSwatch(index));
    if (options.persist !== false) saveThemeState();
    syncMetaThemeColor();
}

function applyTheme(gradString) {
    const normalizedGradient = normalizeLegacyDefaultGradientString(gradString);
    setThemeGradientProperty('--g', normalizedGradient);
    localStorage.setItem('lecom-theme-gradient', normalizedGradient);

    // Extract individual color stops for border/pulsation theming
    const colorMatch = normalizedGradient.match(/#[0-9a-fA-F]{3,8}|rgba?\([^)]+\)|hsla?\([^)]+\)/g);
    if (colorMatch && colorMatch.length >= 2) {
        setThemeGradientProperty('--g1', colorMatch[0]);
        setThemeGradientProperty('--g2', colorMatch[colorMatch.length - 1]);
        setThemeGradientProperty('--g3', colorMatch.length >= 3 ? colorMatch[1] : '');
        setThemeGradientProperty('--amb-c1', colorMatch[0] + '66');
        setThemeGradientProperty('--amb-c2', colorMatch[colorMatch.length - 1] + '99');
    } else if (colorMatch && colorMatch.length === 1) {
        setThemeGradientProperty('--g1', colorMatch[0]);
        setThemeGradientProperty('--g2', colorMatch[0]);
        setThemeGradientProperty('--g3', '');
        setThemeGradientProperty('--amb-c1', colorMatch[0] + '66');
        setThemeGradientProperty('--amb-c2', colorMatch[0] + '99');
    }

    // Also need to handle custom start/end if manually changed
    if (normalizedGradient.startsWith('linear-gradient')) {
        // Apply 'active' class to buttons if it matches a preset
        const idx = gradients.indexOf(normalizedGradient);
        if (idx !== -1) {
            currentThemeIndex = idx;
            setActiveGradientSwatch(findGradientSwatch(idx));
        } else {
            currentThemeIndex = -1;
            // It's a custom one, clear active swatches
            setActiveGradientSwatch(null);
        }
    }
    syncMetaThemeColor();
}

// Custom Gradient Inputs
const gradStartInput = document.getElementById('grad-start');
const gradMidInput = document.getElementById('grad-mid');
const gradEndInput = document.getElementById('grad-end');

if (gradStartInput && gradEndInput) {
    gradStartInput.addEventListener('input', updateCustomGradient);
    if (gradMidInput) gradMidInput.addEventListener('input', updateCustomGradient);
    gradEndInput.addEventListener('input', updateCustomGradient);
}

// Color Inputs Handling
const colorInputs = [
    { id: 'col-card-bg', var: '--quiz-card-bg', default: QUIZ_CARD_BG_DEFAULT_COLOR }, // Matches #quiz-card css only
    { id: 'col-text-q', var: '--text-q', default: '#f8fafc' },
    { id: 'col-text-a', var: '--text-a', default: '#e2e8f0' },
    { id: 'col-opt-bg', var: '--choice-bg', default: 'rgba(255, 255, 255, 0.03)' }, // Matches .choice css
    { id: 'col-ai-bold', var: '--ai-bold', default: AI_BOLD_DEFAULT }
];

colorInputs.forEach(item => {
    const input = document.getElementById(item.id);
    if (input) {
        if (item.id === 'col-card-bg') {
            input.addEventListener('input', (e) => {
                const value = e.target.value;
                lastQuizCardBgColor = value;
                input.dataset.lastColor = value;
                updateThemeProp('--quiz-card-bg', value);
            });
            return;
        }
        input.addEventListener('input', (e) => {
            const val = e.target.value;
            document.documentElement.style.setProperty(item.var, val);
            if (['--quiz-card-bg', '--text-q', '--text-a', '--choice-bg'].includes(item.var)) {
                document.documentElement.style.setProperty(item.var.replace('--', '--custom-'), val);
            }
            localStorage.setItem('lecom-theme-' + item.id, val);
        });
    }
});

function isDefaultThemeColor(prop, value, themeState) {
    if (!value) return true;
    const val = String(value).trim().toLowerCase();
    if (prop === '--quiz-card-bg') {
        if (val === 'transparent' || val === 'rgba(0, 0, 0, 0)') {
            return !shouldPreserveTransparentQuizCardBg(themeState);
        }
        return val === '#0f172a' || val === 'rgba(15, 23, 42, 1)' || val === '#020617';
    }
    if (prop === '--text-q') {
        return val === '#f8fafc' || val === '#ffffff' || val === 'rgb(248, 250, 252)';
    }
    if (prop === '--text-a') {
        return val === '#e2e8f0' || val === 'rgb(226, 232, 240)';
    }
    if (prop === '--choice-bg') {
        return val === 'rgba(255, 255, 255, 0.03)' || val === '#1e293b' || val === 'rgba(125, 211, 252, 0.04)';
    }
    return false;
}

// Load persisted colors on init
function loadPersistedColors() {
    // Prefer unified QuizTheme if present to avoid stale legacy key overrides.
    if (getStoredQuizTheme()) return;

    const savedGrad = localStorage.getItem('lecom-theme-gradient');
    if (savedGrad) applyTheme(savedGrad);

    colorInputs.forEach(item => {
        const saved = localStorage.getItem('lecom-theme-' + item.id);
        if (saved) {
            if (!isDefaultThemeColor(item.var, saved, null)) {
                document.documentElement.style.setProperty(item.var, saved);
                if (['--quiz-card-bg', '--text-q', '--text-a', '--choice-bg'].includes(item.var)) {
                    document.documentElement.style.setProperty(item.var.replace('--', '--custom-'), saved);
                }
            }
            const input = document.getElementById(item.id);
            if (item.id === 'col-card-bg') {
                const cardBg = normalizeLoadedQuizCardBg(saved);
                if (!isDefaultThemeColor(item.var, cardBg, null)) {
                    document.documentElement.style.setProperty(item.var, cardBg);
                    document.documentElement.style.setProperty('--custom-quiz-card-bg', cardBg);
                }
                syncQuizCardBgState(cardBg);
                if (cardBg !== QUIZ_CARD_BG_TRANSPARENT) {
                    syncPickerValue(input, cardBg);
                }
                return;
            }
            syncPickerValue(input, saved);
        }
    });

    // Backward-compat: older builds stored three AI bold colors; use the first.
    if (!localStorage.getItem('lecom-theme-col-ai-bold')) {
        const legacyAIBold = localStorage.getItem('lecom-theme-col-ai-bold-1');
        if (legacyAIBold) {
            document.documentElement.style.setProperty('--ai-bold', legacyAIBold);
            syncPickerValue(document.getElementById('col-ai-bold'), legacyAIBold);
        }
    }
}

// Call load immediately
loadPersistedColors();
syncQuizCardBgState(getThemeVar(getComputedStyle(document.documentElement), '--quiz-card-bg', QUIZ_CARD_BG_DEFAULT));
resetQuizBackgroundControls({ persist: false });

// Global theme helpers (needed by applyLoadedTheme)
function updateThemeProp(prop, val) {
    document.documentElement.style.setProperty(prop, val);
    if (['--quiz-card-bg', '--text-q', '--text-a', '--choice-bg'].includes(prop)) {
        document.documentElement.style.setProperty(prop.replace('--', '--custom-'), val);
    }
    if (prop === '--quiz-card-bg') {
        syncQuizCardBgState(val);
    }
    saveThemeState();
}

function normalizeGradientMode(value) {
    const mode = String(value || '').trim();
    return mode === '1' || mode === '3' ? mode : '2';
}

function setCustomGradientMode(mode, options = {}) {
    customGradientMode = normalizeGradientMode(mode);
    if (els.gradModeBtns) {
        els.gradModeBtns.forEach((btn) => {
            const isActive = btn.dataset.gradientMode === customGradientMode;
            btn.classList.toggle('active', isActive);
            btn.setAttribute('aria-pressed', isActive ? 'true' : 'false');
        });
    }

    const middleField = document.querySelector('[data-gradient-color="middle"]');
    const endField = document.querySelector('[data-gradient-color="end"]');
    if (middleField) middleField.hidden = customGradientMode !== '3';
    if (endField) {
        endField.hidden = customGradientMode === '1';
        const label = endField.querySelector('span');
        if (label) label.textContent = customGradientMode === '3' ? 'Color 3' : 'Color 2';
        const input = endField.querySelector('input');
        if (input) input.title = customGradientMode === '3' ? 'Color 3' : 'Color 2';
    }

    if (options.updateGradient !== false) updateCustomGradient(options);
}

function updateCustomGradient(options = {}) {
    const mode = normalizeGradientMode(customGradientMode);
    const baseStart = els.gradStart?.value || DEFAULT_THEME_COLORS[0];
    const baseMid = els.gradMid?.value || DEFAULT_THEME_COLORS[1];
    const baseEnd = els.gradEnd?.value || DEFAULT_THEME_COLORS[2];
    const s = applyThemeColorIntensity(baseStart);
    const m = applyThemeColorIntensity(baseMid);
    const e = applyThemeColorIntensity(baseEnd);
    const gradient = mode === '1'
        ? `linear-gradient(135deg, ${s}, ${s})`
        : mode === '3'
            ? `linear-gradient(135deg, ${s}, ${m} 45%, ${e})`
            : `linear-gradient(135deg, ${s}, ${e})`;

    currentThemeIndex = -1;
    setThemeGradientProperty('--g', gradient);
    setThemeGradientProperty('--g1', s);
    setThemeGradientProperty('--g2', mode === '1' ? s : e);
    setThemeGradientProperty('--g3', mode === '3' ? m : '');
    setThemeGradientProperty('--amb-c1', s + '66');
    setThemeGradientProperty('--amb-c2', (mode === '1' ? s : e) + '99');
    setActiveGradientSwatch(null);
    if (options.persist !== false) saveThemeState();
    syncMetaThemeColor();
}

function applyBorderMode() {
    els.borderModeStatus.innerText = isBorderMode ? "ON" : "OFF";
    els.borderModeStatus.style.color = isBorderMode ? "#4ade80" : "#94a3b8";
    els.borderModeBtn?.setAttribute('aria-pressed', isBorderMode ? 'true' : 'false');
    if (isBorderMode) els.workArea.classList.add('bordered-mode');
    else els.workArea.classList.remove('bordered-mode');
}

function applyDarkMode(options = {}) {
    document.body.classList.toggle('dark-mode-active', !!isDarkMode);
    if (els.darkModeStatus) {
        els.darkModeStatus.innerText = isDarkMode ? "ON" : "OFF";
        els.darkModeStatus.style.color = isDarkMode ? "#4ade80" : "#94a3b8";
    }
    if (els.darkModeBtn) {
        els.darkModeBtn.classList.toggle('active', !!isDarkMode);
        els.darkModeBtn.setAttribute('aria-pressed', isDarkMode ? 'true' : 'false');
    }
    syncMetaThemeColor();
    if (options.persist !== false) saveThemeState();
}

function toggleDarkMode() {
    isDarkMode = !isDarkMode;
    applyDarkMode();
    if (isDarkMode) showPopup("Dark Mode Active.", null, false);
}

function applyAmbientMode() {
    if (!els.quizCard) return;
    syncAmbientVisualizerBodyFlags();
    const suppressCardGlowForVisualizer = !!isSysSyncActive;

    els.ambientBtn?.setAttribute('aria-pressed', isAmbientMode ? 'true' : 'false');
    if (els.ambientModeStatus) {
        els.ambientModeStatus.innerText = isAmbientMode ? "ON" : "OFF";
        els.ambientModeStatus.style.color = isAmbientMode ? "#4ade80" : "#94a3b8";
    } else if (els.ambientBtn) {
        els.ambientBtn.innerText = isAmbientMode ? "Ambient Mode: ON" : "Ambient Mode: OFF";
    }

    if (isAmbientMode) {
        els.quizCard.classList.add('ambient-active');
        if (els.ambientControls) els.ambientControls.style.display = 'flex';

        let min = '20px', max = '60px';
        if (ambientSize === 'small') { min = '10px'; max = '30px'; }
        if (ambientSize === 'large') { min = '40px'; max = '100px'; }
        document.documentElement.style.setProperty('--amb-min', min);
        document.documentElement.style.setProperty('--amb-max', max);
        const styles = getComputedStyle(document.documentElement);
        const ambC1 = styles.getPropertyValue('--amb-c1').trim() || '#60a5fa66';
        const ambC2 = styles.getPropertyValue('--amb-c2').trim() || '#f472b699';

        if (ambientType === 'static') {
            els.quizCard.classList.add('is-static');
            els.quizCard.style.animation = 'none';
            if (!suppressCardGlowForVisualizer) {
                els.quizCard.style.boxShadow = `0 0 var(--amb-max) ${ambC2}, 0 20px 60px rgba(0,0,0,0.6)`;
            } else {
                els.quizCard.style.boxShadow = '';
            }
        } else {
            els.quizCard.classList.remove('is-static');
            els.quizCard.style.animation = suppressCardGlowForVisualizer
                ? 'none'
                : 'shadow-pulse 4s ease-in-out infinite alternate';
            if (!suppressCardGlowForVisualizer) {
                els.quizCard.style.boxShadow = `0 0 var(--amb-min) ${ambC1}, 0 20px 60px rgba(0,0,0,0.6)`;
            } else {
                els.quizCard.style.boxShadow = '';
            }
        }
        return;
    }

    els.quizCard.classList.remove('ambient-active');
    if (els.ambientControls) els.ambientControls.style.display = 'none';
    els.quizCard.style.boxShadow = '';
    els.quizCard.classList.remove('is-static');
    els.quizCard.style.animation = '';
    if (!isSysSyncActive) {
        const canvas = $('audio-visualizer');
        const ctx = canvas?.getContext('2d');
        if (ctx) ctx.clearRect(0, 0, canvas.width, canvas.height);
    }
}

function toggleZenMode() {
    isZenMode = !isZenMode;
    if (isZenMode) {
        document.body.classList.add('zen-active');
        closeMenuDropdown();
        syncFloatingQuizLayering();
        showPopup("Zen Mode Active. Press ESC to exit.", null, false);
    } else {
        document.body.classList.remove('zen-active');
    }
    if (typeof scheduleQuizCardPositionReset === 'function') {
        scheduleQuizCardPositionReset();
    } else if (typeof resetQuizCardPosition === 'function') {
        requestAnimationFrame(() => resetQuizCardPosition());
    }
    saveThemeState();
}

/* Inter and Outfit ship with the page because the built-in themes use them. The
   other Font-picker families are only ever needed once a user chooses one, so
   their webfont request is made at that moment instead of on every page load. */
const ON_DEMAND_WEBFONT_STYLESHEETS = Object.freeze({
    'noto sans': '/css/font-notosans.css',
    'plus jakarta sans': '/css/font-plusjakartasans.css'
});

function ensureSelectedWebFontLoaded(fontValue) {
    const declaration = String(fontValue || '').toLowerCase();
    Object.entries(ON_DEMAND_WEBFONT_STYLESHEETS).forEach(([family, href]) => {
        if (!declaration.includes(family)) return;
        void window.MemoraRuntime.ensureStyleLoaded({ href, key: `webfont:${family}` })
            .catch((error) => console.warn(`Could not load the ${family} webfont:`, error));
    });
}

// Browsers normalize quotes in font-family, so "Inter, sans-serif" must still match
// the "'Inter', sans-serif" option after a reload.
function normalizeThemeFontValue(value) {
    return String(value || '').replace(/["']/g, '').replace(/\s*,\s*/g, ',').trim().toLowerCase();
}

function findThemeFontOption(value) {
    const normalized = normalizeThemeFontValue(value);
    if (!normalized || !els.fontSelect) return null;
    return Array.from(els.fontSelect.options)
        .find((option) => option.value && normalizeThemeFontValue(option.value) === normalized) || null;
}

// An empty value means the theme's own font; anything else is a user override that
// wins across themes through --memora-user-font.
function applyThemeFontChoice(value) {
    const option = findThemeFontOption(value);
    const font = option ? option.value : '';
    const bodyStyle = document.body.style;
    if (font) {
        ensureSelectedWebFontLoaded(font);
        bodyStyle.fontFamily = font;
        ['--memora-user-font', '--font-main', '--font-heading'].forEach((prop) => bodyStyle.setProperty(prop, font));
    } else {
        bodyStyle.removeProperty('font-family');
        ['--memora-user-font', '--font-main', '--font-heading'].forEach((prop) => bodyStyle.removeProperty(prop));
    }
    if (els.fontSelect) els.fontSelect.value = font;
}

function setupThemeHandlers() {
    window.setGradient = (idx, el) => {
        applyGradientPreset(idx, el);
    };

    els.gradStart.oninput = updateCustomGradient;
    if (els.gradMid) els.gradMid.oninput = updateCustomGradient;
    els.gradEnd.oninput = updateCustomGradient;
    if (els.gradModeBtns) {
        els.gradModeBtns.forEach((btn) => {
            btn.onclick = () => setCustomGradientMode(btn.dataset.gradientMode);
        });
        setCustomGradientMode(customGradientMode, { persist: false, updateGradient: false });
    }
    if (els.gradientIntensity) {
        syncGradientIntensityControls();
        els.gradientIntensity.oninput = () => applyGradientIntensity();
    }
    els.colCardBg.oninput = (e) => {
        const value = e.target.value;
        lastQuizCardBgColor = value;
        els.colCardBg.dataset.lastColor = value;
        updateThemeProp('--quiz-card-bg', value);
    };
    if (els.colCardBgTransparentBtn) {
        els.colCardBgTransparentBtn.onclick = toggleQuizCardBgTransparency;
    }
    els.colTextQ.oninput = (e) => updateThemeProp('--text-q', e.target.value);
    els.colTextA.oninput = (e) => updateThemeProp('--text-a', e.target.value);
    els.colOptBg.oninput = (e) => updateThemeProp('--choice-bg', e.target.value);
    if (els.colAIBold) els.colAIBold.oninput = (e) => updateThemeProp('--ai-bold', e.target.value);
    els.fontSelect.onchange = (e) => {
        applyThemeFontChoice(e.target.value);
        saveThemeState();
    };
    if (els.ambientBtn) {
        els.ambientBtn.onclick = () => { isAmbientMode = !isAmbientMode; applyAmbientMode(); saveThemeState(); };
    }
    if (els.ambSize) {
        els.ambSize.onchange = () => { ambientSize = els.ambSize.value; applyAmbientMode(); saveThemeState(); };
    }
    if (els.ambStyle) {
        els.ambStyle.onchange = () => { ambientType = els.ambStyle.value; applyAmbientMode(); saveThemeState(); };
    }
    els.borderModeBtn.onclick = () => { isBorderMode = !isBorderMode; applyBorderMode(); saveThemeState(); };
    if (els.darkModeBtn) {
        els.darkModeBtn.onclick = toggleDarkMode;
    }
    if (els.bgControlsBtn && els.bgControlsPanel) {
        els.bgControlsBtn.onclick = () => {
            const willOpen = els.bgControlsPanel.hidden;
            els.bgControlsPanel.hidden = !willOpen;
            els.bgControlsBtn.classList.toggle('active', willOpen);
        };
    }
    [els.bgGamma, els.bgDim, els.bgAccent].forEach((input) => {
        if (!input) return;
        input.addEventListener('input', () => {
            applyQuizBackgroundControls();
        });
    });
    if (els.bgControlsResetBtn) {
        els.bgControlsResetBtn.onclick = () => resetQuizBackgroundControls();
    }
    const overhaulGroup = document.querySelector('#theme-overhaul-container .theme-overhaul-toggle');
    if (overhaulGroup) {
        // Radio group keys: arrows and Home/End move the choice and focus together.
        overhaulGroup.addEventListener('keydown', (event) => {
            if (!['ArrowRight', 'ArrowDown', 'ArrowLeft', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
            const buttons = Array.from(overhaulGroup.querySelectorAll('.overhaul-btn'));
            const index = buttons.indexOf(document.activeElement);
            if (index === -1) return;
            event.preventDefault();
            const step = event.key === 'ArrowRight' || event.key === 'ArrowDown' ? 1 : -1;
            const next = event.key === 'Home' ? 0
                : event.key === 'End' ? buttons.length - 1
                    : (index + step + buttons.length) % buttons.length;
            buttons[next].focus();
            buttons[next].click();
        });
    }
    els.resetThemeBtn.onclick = () => {
        gradientIntensity = GRADIENT_INTENSITY_DEFAULT;
        syncGradientIntensityControls();
        // Back to the selected theme's own palette rather than pinning Default's gradient.
        clearThemeGradientOverrides();
        localStorage.removeItem(QUIZ_CARD_BG_TRANSPARENT_PREF_KEY);
        
        // Remove custom color styles from inline styles so responsive CSS default values apply
        document.documentElement.style.removeProperty('--quiz-card-bg');
        document.documentElement.style.removeProperty('--custom-quiz-card-bg');
        document.documentElement.style.removeProperty('--text-q');
        document.documentElement.style.removeProperty('--custom-text-q');
        document.documentElement.style.removeProperty('--text-a');
        document.documentElement.style.removeProperty('--custom-text-a');
        document.documentElement.style.removeProperty('--choice-bg');
        document.documentElement.style.removeProperty('--custom-choice-bg');

        document.documentElement.style.setProperty('--ai-bold', AI_BOLD_DEFAULT);
        syncMetaThemeColor();
        // The theme's own font, not Times New Roman forced over every theme.
        applyThemeFontChoice('');
        
        // Sync color picker UI values to match new default dark mode state
        els.colCardBg.value = '#0f172a';
        els.colTextQ.value = '#f8fafc';
        els.colTextA.value = '#e2e8f0';
        // Color inputs only accept hex; the markup default stands in for the translucent CSS value.
        els.colOptBg.value = '#1e293b';
        
        if (els.colAIBold) els.colAIBold.value = AI_BOLD_DEFAULT;
        isAmbientMode = false; ambientSize = 'medium'; ambientType = 'dynamic'; isBorderMode = false; isZenMode = false; isDarkMode = false;
        document.body.classList.remove('zen-active'); els.ambSize.value = 'medium'; els.ambStyle.value = 'dynamic';
        // Beat Color Shift is part of the look being reset; a checked box left behind kept
        // its transient palette alive.
        if (beatColorToggleEl) beatColorToggleEl.checked = false;
        clearBeatColorPresentation();
        if (els.colCardBg) {
            els.colCardBg.dataset.lastColor = '#0f172a';
        }
        lastQuizCardBgColor = QUIZ_CARD_BG_DEFAULT_COLOR;
        syncQuizCardBgState(QUIZ_CARD_BG_DEFAULT);
        resetQuizBackgroundControls({ persist: false });
        applyAmbientMode(); applyBorderMode(); applyDarkMode({ persist: false });
        syncGradientControlsToActiveTheme();
        saveThemeState();
    };
}

const MEMORA_AGENT_THEME_PRESETS = Object.freeze([
    { key: 'default', label: 'Default', index: 0 },
    { key: 'amber', label: 'Amber', index: 1 },
    { key: 'rose', label: 'Rosewood', index: 2 },
    { key: 'lime', label: 'Lime Teal', index: 3 },
    { key: 'earthy', label: 'Earthy', index: 4 },
    { key: 'midnight_aurora', label: 'Midnight Aurora', index: 5 },
    { key: 'sage_bloom', label: 'Sage Bloom', index: 6 },
    { key: 'coral_tide', label: 'Coral Tide', index: 7 },
    { key: 'lavender_dusk', label: 'Lavender Dusk', index: 8 },
    { key: 'ocean_glass', label: 'Ocean Glass', index: 9 },
    { key: 'forest_moss', label: 'Forest Moss', index: 10 },
    { key: 'solar_flare', label: 'Solar Flare', index: 11 },
    { key: 'berry_nebula', label: 'Berry Nebula', index: 12 },
    { key: 'arctic_mint', label: 'Arctic Mint', index: 13 },
    { key: 'ember_violet', label: 'Ember Violet', index: 14 }
]);

const MEMORA_AGENT_ACTIONS = Object.freeze([
    { action: 'open_current_pdf', label: 'Open the current card PDF' },
    { action: 'open_pdf', label: 'Open a loaded PDF by exact or partial name' },
    { action: 'open_pdf_explanation', label: 'Open the PDF explanation for the current card' },
    { action: 'open_theme', label: 'Open Theme settings' },
    { action: 'set_theme_preset', label: 'Apply a built-in theme preset' },
    { action: 'set_custom_gradient', label: 'Apply a one-, two-, or three-color custom theme gradient' },
    { action: 'reset_theme', label: 'Reset theme settings' },
    { action: 'set_ambient_mode', label: 'Turn Ambient mode on or off' },
    { action: 'set_border_mode', label: 'Turn bordered mode on or off' },
    { action: 'set_quiz_card_transparency', label: 'Make the quiz card background transparent or solid' },
    { action: 'open_database', label: 'Open the local Database modal' },
    { action: 'open_saved', label: 'Open Saved Questions' },
    { action: 'list_quizzes', label: 'List available quizzes and saved progress' },
    { action: 'open_quiz', label: 'Open a named or latest quiz on its question card' },
    { action: 'resume_quiz', label: 'Resume the active, named or latest quiz with saved answers' },
    { action: 'list_sessions', label: 'List saved study sessions' },
    { action: 'open_session', label: 'Open a saved study session by ID or name' },
    { action: 'navigate', label: 'Open Dashboard, Sessions, Saved Content, Sources, Learn, Settings, Calendar or Ask' },
    { action: 'open_learn', label: 'Open or resume saved content in any Learn module' },
    { action: 'list_learn', label: 'List existing content for a Learn module' },
    { action: 'create_learn', label: 'Create using the native Learn setup and admission guards' },
    { action: 'learn_control', label: 'Operate the current native Learn surface' },
    { action: 'app_control', label: 'Discover, activate or edit visible native app controls' },
    { action: 'open_source_slide', label: 'Pull up the current cited slide or a named file/page in the file viewer' },
    { action: 'search_library', label: 'Search lecture slides through the native Lecture Finder' },
    { action: 'quiz_control', label: 'Move between quiz questions, choose an answer or save the current question' },
    { action: 'get_status', label: 'Read background jobs and estimated API quota' },
    { action: 'get_study_stats', label: 'Read performance and weak topics' },
    { action: 'save_note', label: 'Save the last reply or dictated text to notes' },
    { action: 'add_calendar_event', label: 'Add a calendar item' },
    { action: 'remove_calendar_event', label: 'Remove a calendar item' },
    { action: 'review_missed', label: 'Review missed questions' },
    { action: 'flashcard_control', label: 'Read, reveal and rate flashcards' },
    { action: 'compare_slides', label: 'Compare two slides in the source viewer' },
    { action: 'undo_last', label: 'Undo the last Voice Agent screen action or created item' },
    { action: 'describe_capabilities', label: 'List supported Ask Memoraful actions' }
]);

function getMemoraAgentCapabilities() {
    const pdfExplanationEnabled = isMemoraPdfExplanationFeatureEnabledForCore();
    return MEMORA_AGENT_ACTIONS
        .filter((item) => pdfExplanationEnabled || item.action !== 'open_pdf_explanation')
        .map((item) => ({ ...item }));
}

function createMemoraAgentResult(ok, message, extra = {}) {
    return {
        ...(extra || {}),
        ok: !!ok,
        message: String(message || '').trim()
    };
}


// Voice tools borrow native data/writers; they introduce no durable store.
function getMemoraAgentStudyStats(payload = {}) {
    const query = normalizeMemoraAgentMatchText(payload.lecture || '');
    const limit = Math.min(10, Math.max(1, Number(payload.limit) || 5));
    const entries = buildStatsLectureData().filter(entry => !query || normalizeMemoraAgentMatchText(entry.rawName).includes(query));
    if (query && !entries.length) return createMemoraAgentResult(false, 'That lecture is not in this session.');
    const indices = new Set(entries.flatMap(entry => entry.questionIndices));
    const lectures = entries.map(entry => ({ lecture: entry.rawName, attempts: entry.attempts,
        accuracy: entry.attempts ? Math.round(100 * entry.correctAttempts / entry.attempts) : null }));
    const hardest = buildHardestQuestionStats(questions.length).filter(item => !query || indices.has(item.idx)).slice(0, limit)
        .map(item => ({ stem: getPlainStudyText(item.q).slice(0, 120), misses: item.wrongCount, lecture: item.sourceFile }));
    const topics = new Map();
    questions.forEach((q, idx) => {
        if (query && !indices.has(idx)) return;
        const topic = String(q.topic || q.category || q.sourceFile || q.src || q.setName || 'Uncategorized');
        const data = topics.get(topic) || { topic, attempts: 0, correct: 0 };
        for (const attempt of statsData[idx] || []) { data.attempts++; if (attempt.correct) data.correct++; }
        topics.set(topic, data);
    });
    const activity = buildStatsDailyActivity();
    return createMemoraAgentResult(true, 'Study performance checked.', { lectures, hardest,
        weakestTopics: [...topics.values()].filter(t => t.attempts).map(t => ({ topic: t.topic, attempts: t.attempts,
            accuracy: Math.round(100 * t.correct / t.attempts) })).sort((a,b) => a.accuracy-b.accuracy).slice(0, limit),
        streak: countStatsDayStreak(activity), recentActivity: Object.entries(activity).sort().slice(-7).map(([date,data]) => ({ date,...data })) });
}

function getMemoraAgentStudyQuestionIndices(payload = {}) {
    const scope = payload.scope || 'session';
    const lecture = normalizeMemoraAgentMatchText(payload.lecture || '');
    if (!['session', 'saved', 'misses', 'last_session', 'lecture', 'all'].includes(scope)) return [];
    return questions.map((q,idx) => idx).filter(idx => {
        const q = questions[idx];
        if (!q || !Array.isArray(q.c) || !Number.isInteger(q.a) || !q.c[q.a]) return false;
        if ((scope === 'lecture' || lecture) && (!lecture || ![q.sourceFile,q.src,q.setName].some(v => normalizeMemoraAgentMatchText(v).includes(lecture)))) return false;
        if (scope === 'saved') return !!savedQuestions[idx];
        if (payload.missedOnly || ['misses','last_session','all'].includes(scope)) {
            if (scope === 'last_session') return userAnswers[idx] !== undefined && userAnswers[idx] !== q.a;
            return wrongAnswerHistory.has(idx) || wrongAnswerHistory.has(buildQuestionPersistenceKeyFromQuestion(q));
        }
        return true;
    });
}

function getMemoraAgentOralQuestion(idx, id) {
    const q = questions[idx];
    if (!q) return null;
    return { question_id: id, stem: getPlainStudyText(q.q || q.question || ''),
        choices: q.c.map((text,index) => ({ letter: String.fromCharCode(65+index), text: getPlainStudyText(text) })) };
}

function runMemoraAgentFlashcardControl(payload = {}) {
    if (!isFlashcardReviewSurfaceVisible()) return createMemoraAgentResult(false, 'Open your flashcards first.');
    const operation = payload.operation || 'read';
    const idx = filteredIndices[currentQIndex], q = questions[idx];
    const remaining = isFlashcardAdaptiveReviewMode ? filteredIndices.length
        : flashcardReviewScope === 'all' ? filteredIndices.filter(i => !flashcardPassRatings.has(i)).length : filteredIndices.length-currentQIndex;
    if (operation === 'status') return createMemoraAgentResult(true, 'Flashcard session checked.', { remaining });
    if (!q || flashcardPassComplete) return createMemoraAgentResult(true, 'This flashcard session is complete.', { remaining: 0 });
    const key = getFlashcardDisplayKey(idx);
    if (operation === 'read') return createMemoraAgentResult(true, getPlainStudyText(q.q || q.question || ''), { front: getPlainStudyText(q.q || q.question || ''), remaining, cardId: key });
    if (((operation === 'rate' || operation === 'reveal') && !payload.cardId) || (payload.cardId && payload.cardId !== key)) return createMemoraAgentResult(false, 'The flashcard changed. Read the current card again.');
    if (operation === 'reveal') {
        flashcardRevealedKeys.add(key); renderFlashcardGrid();
        const back = getPlainStudyText((q.c?.[q.a] || q.answer || '') + ' ' + (q.e || q.explanation || ''));
        return createMemoraAgentResult(true, back, { back, cardId: key });
    }
    if (operation !== 'rate' || !['again','hard','good','easy'].includes(payload.rating)) return createMemoraAgentResult(false, 'Choose Again, Hard, Good or Easy.');
    if (!flashcardRevealedKeys.has(key)) return createMemoraAgentResult(false, 'Reveal the answer after the student responds, then rate it.');
    if (!rateCurrentFlashcard(payload.rating === 'hard' ? 'shaky' : payload.rating)) return createMemoraAgentResult(false, 'The card could not be rated.');
    if (!isFlashcardAdaptiveReviewMode && !flashcardPassComplete && filteredIndices[currentQIndex] === idx) {
        const next = filteredIndices.findIndex((i,pos) => pos > currentQIndex && !flashcardPassRatings.has(i));
        if (next >= 0) { currentQIndex = next; renderFlashcardGrid(); }
    }
    return { ...runMemoraAgentFlashcardControl({ operation: 'read' }), rating: payload.rating };
}

function getMemoraAgentNativeControlLabel(element) {
    return String(element.getAttribute('aria-label') || [...(element.labels || [])].map(item => item.textContent).join(' ')
        || element.getAttribute('title') || element.placeholder || element.textContent || element.name || element.id || '').trim().slice(0, 160);
}

function getMemoraAgentNativeControlRecord(id) {
    const control = memoraAgentNativeControls?.controls.find(c => c.id === String(id));
    if (!control || !['INPUT','SELECT','TEXTAREA'].includes(control.element.tagName)) return null;
    // Choosing a radio clears the member its group had before, and Undo has to choose that one again.
    const element = control.element;
    const radioPrevious = element.type === 'radio' && element.name
        ? [...(element.form || document).querySelectorAll('input[type="radio"]')]
            .find(item => item !== element && item.checked && item.name === element.name && item.form === element.form) || null
        : null;
    return { ...control, elementId: element.id, originalValue: control.value, originalChecked: control.checked, radioPrevious };
}

async function restoreMemoraAgentNativeField(field) {
    const element = field.element;
    if (!element?.isConnected || element.value !== field.afterValue || element.checked !== field.afterChecked) return false;
    const target = field.radioPrevious || element;
    if (field.radioPrevious && (!target.isConnected || target.checked)) return false;
    const label = field.radioPrevious ? getMemoraAgentNativeControlLabel(target) : field.label;
    const result = executeMemoraAgentNativeControl({ operation: 'discover', query: label });
    const item = result.controls?.find(c => c.label === label && memoraAgentNativeControls.controls.find(r => r.id === c.control)?.element === target);
    return !!item && executeMemoraAgentNativeControl({ operation: 'set', control: item.control,
        value: field.radioPrevious ? target.value : field.originalValue, enabled: field.radioPrevious ? true : !!field.originalChecked }).ok;
}

function captureMemoraAgentStudyScreen() {
    return { route: window.MemoraLiveLeftRailRouting?.currentRoute?.() || null,
        viewer: window.MemoraSourceViewer?.current?.() || null, pdf: { ...lastPdfState },
        theme: getCurrentThemeStateSnapshot(), indices: filteredIndices.slice(), index: currentQIndex,
        // The questions themselves, so Undo can find them again in a reordered or replaced bank.
        questionRefs: filteredIndices.map(idx => questions[idx]), currentQuestionRef: questions[filteredIndices[currentQIndex]] || null,
        filter: isFilterActive, flashcards: isFlashcardMode, revealed: [...flashcardRevealedKeys] };
}

/* Old numeric positions only mean something in the bank they were taken from. The snapshot's
 * questions are found again by identity; when one is gone (bank replaced or regenerated) the
 * Undo is refused rather than applying the old indices to unrelated questions. */
function resolveMemoraAgentStudySnapshotQuestions(snapshot) {
    if (!Array.isArray(snapshot?.questionRefs)) return { indices: (snapshot?.indices || []).filter(idx => !!questions[idx]), index: snapshot?.index || 0 };
    const indices = snapshot.questionRefs.map(ref => questions.indexOf(ref));
    if (indices.some(idx => idx < 0)) return null;
    // An unfiltered view also shows questions added since, after the ones it already had.
    if (!snapshot.filter) questions.forEach((_question, idx) => { if (!indices.includes(idx)) indices.push(idx); });
    const index = snapshot.currentQuestionRef ? indices.indexOf(questions.indexOf(snapshot.currentQuestionRef)) : 0;
    return index < 0 ? null : { indices, index };
}

async function restoreMemoraAgentStudyScreen(snapshot, appearance = false, isCurrent = () => true) {
    if (!isCurrent()) return false;
    if (appearance) { applyLoadedTheme(snapshot.theme); saveThemeState(); return true; }
    const restoresQuiz = snapshot.route?.name === 'learn-quizzes' || !!snapshot.flashcards;
    if (restoresQuiz && !resolveMemoraAgentStudySnapshotQuestions(snapshot)) return false;
    window.MemoraSourceViewer?.close?.('voice-undo');
    const routing = window.MemoraLiveLeftRailRouting;
    if (snapshot.route && routing && !await routing.restoreRoute(snapshot.route)) return false;
    if (!isCurrent()) return false;
    if (snapshot.viewer) {
        const viewer = window.MemoraSourceViewer;
        const receipt = snapshot.viewer.comparison
            ? await viewer?.openComparison?.({ left: { file: snapshot.viewer.name, page: snapshot.viewer.page },
                right: { file: snapshot.viewer.comparison.name, page: snapshot.viewer.comparison.page }, isCurrent })
            : viewer?.open?.({ source: { name: snapshot.viewer.name, page: snapshot.viewer.page } });
        return !!receipt && await receipt.ready && isCurrent();
    }
    if (snapshot.pdf?.fileName && snapshot.pdf.page) loadPdfInPane(snapshot.pdf.fileName, { page: snapshot.pdf.page, ensureViewer: true });
    if (snapshot.route?.name === 'learn-quizzes' || (snapshot.flashcards && isFlashcardReviewSurfaceVisible())) {
        const target = resolveMemoraAgentStudySnapshotQuestions(snapshot);
        if (!target) return false;
        filteredIndices = target.indices; currentQIndex = target.index;
        isFilterActive = snapshot.filter; isFlashcardMode = snapshot.flashcards;
        if (snapshot.revealed) {
            flashcardRevealedKeys.clear();
            snapshot.revealed.forEach(key => flashcardRevealedKeys.add(key));
        }
        if (snapshot.flashcards) {
            flashcardDisplayAnchor = getFlashcardDisplayKey(filteredIndices[currentQIndex]);
            showFlashcards();
        }
        else showQuiz();
    }
    return true;
}

async function removeMemoraAgentCreatedNote(record) {
    const latest = tutorNotesByKey[record.key];
    if (!latest) return true;
    if (latest.updatedAt !== record.updatedAt || latest.note !== record.note) return false;
    delete tutorNotesByKey[record.key];
    const saved = await persistTutorNotesStore();
    if (!saved && !tutorNotesByKey[record.key]) tutorNotesByKey[record.key] = latest;
    window.syncPdfTutorNoteIndicators?.();
    return saved;
}

/* Which question, in which session and choice order, is on screen. A visible ordinal repeats
 * across decks and filters, so an answer is checked against this rather than its number alone. */
function getMemoraAgentQuizIdentity() {
    const realIdx = Array.isArray(filteredIndices) ? filteredIndices[currentQIndex] : undefined;
    const question = Number.isInteger(realIdx) ? questions?.[realIdx] : null;
    if (!question) return '';
    const order = [...($('choices')?.querySelectorAll?.('.choice') || [])].map(button => button.dataset.idx).join(',');
    return [String(currentSessionId || ''), buildQuestionPersistenceKeyFromQuestion(question) || realIdx, order].join('|');
}

function getMemoraAgentCurrentQuestionContext() {
    const realIdx = Array.isArray(filteredIndices) && Number.isInteger(filteredIndices[currentQIndex])
        ? filteredIndices[currentQIndex]
        : currentQIndex;
    const question = Array.isArray(questions) && Number.isInteger(realIdx) ? questions[realIdx] : null;
    const citation = question ? getQuestionCitationInfo(question) : { citationText: '', page: null };
    const linkedPdf = question ? findBestPdfForQuestion(question) : '';

    return {
        question,
        realIdx,
        linkedPdf: linkedPdf || '',
        citationPage: citation.page || null,
        citationText: citation.citationText || '',
        sourceFile: String(question?.sourceFile || question?.src || question?.setName || '').trim()
    };
}

function getMemoraAgentLoadedPdfNames() {
    return Object.keys(loadedPdfFiles || {});
}

function normalizeMemoraAgentMatchText(value) {
    return String(value || '')
        .toLowerCase()
        .replace(/\.pdf\b/g, '')
        .replace(/[_-]+/g, ' ')
        .replace(/[^a-z0-9]+/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
}

const MEMORA_AGENT_ORDINAL_KINDS = {
    lecture: 'lecture', lect: 'lecture', lec: 'lecture', l: 'lecture',
    chapter: 'chapter', chap: 'chapter', ch: 'chapter',
    week: 'week', wk: 'week', w: 'week',
    session: 'session', class: 'session', day: 'session',
    part: 'part', pt: 'part', unit: 'unit', module: 'module', mod: 'module', topic: 'topic'
};
const MEMORA_AGENT_ORDINAL_NUMBER_WORDS = ['zero', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten',
    'eleven', 'twelve', 'thirteen', 'fourteen', 'fifteen', 'sixteen', 'seventeen', 'eighteen', 'nineteen'];
const MEMORA_AGENT_ORDINAL_TENS_WORDS = ['', '', 'twenty', 'thirty', 'forty', 'fifty', 'sixty', 'seventy', 'eighty', 'ninety'];

/* Numbered materials: "Lecture 8" has to find L8_Hemodynamics, Lec08 and Lecture_08 alike, and
 * must never settle for Lecture 18. Each entry is a number and the kind word in front of it. */
function getMemoraAgentOrdinals(value) {
    const tokens = normalizeMemoraAgentMatchText(value)
        .replace(/([a-z])(\d)/g, '$1 $2')
        .replace(/(\d)([a-z])/g, '$1 $2')
        .split(' ')
        .filter(Boolean);
    const ordinals = [];
    for (let index = 0; index < tokens.length; index++) {
        const token = tokens[index];
        const wordValue = MEMORA_AGENT_ORDINAL_NUMBER_WORDS.indexOf(token);
        const tensValue = MEMORA_AGENT_ORDINAL_TENS_WORDS.indexOf(token) * 10;
        let number = /^\d{1,3}$/.test(token) ? Number(token) : (wordValue > 0 ? wordValue : tensValue > 0 ? tensValue : NaN);
        if (!Number.isFinite(number)) continue;
        // "twenty one" is 21, not a 20 followed by a 1.
        const unitValue = MEMORA_AGENT_ORDINAL_NUMBER_WORDS.indexOf(tokens[index + 1]);
        const kind = MEMORA_AGENT_ORDINAL_KINDS[tokens[index - 1]] || '';
        if (tensValue > 0 && unitValue > 0 && unitValue < 10) { number += unitValue; index++; }
        ordinals.push({ number, kind });
    }
    return ordinals;
}

function scoreMemoraAgentPdfCandidate(query, candidate) {
    const normalizedQuery = normalizeMemoraAgentMatchText(query);
    const normalizedCandidate = normalizeMemoraAgentMatchText(candidate);
    if (!normalizedQuery || !normalizedCandidate) return 0;
    if (normalizedQuery === normalizedCandidate) return 1;

    const queryOrdinal = getMemoraAgentOrdinals(query).find((ordinal) => ordinal.kind);
    if (queryOrdinal) {
        const candidateOrdinals = getMemoraAgentOrdinals(candidate);
        const sameKind = candidateOrdinals.filter((ordinal) => ordinal.kind === queryOrdinal.kind);
        if (sameKind.some((ordinal) => ordinal.number === queryOrdinal.number)) return 0.94;
        if (sameKind.length) return 0;
        if (!candidateOrdinals.some((ordinal) => ordinal.kind)
            && candidateOrdinals.some((ordinal) => ordinal.number === queryOrdinal.number)) return 0.86;
        if (candidateOrdinals.length) return 0;
    }

    if (normalizedCandidate.startsWith(normalizedQuery) || normalizedQuery.startsWith(normalizedCandidate)) return 0.92;
    if (normalizedCandidate.includes(normalizedQuery) || normalizedQuery.includes(normalizedCandidate)) return 0.84;

    const queryTokens = normalizedQuery.split(' ').filter(Boolean);
    const candidateTokens = new Set(normalizedCandidate.split(' ').filter(Boolean));
    if (!queryTokens.length || !candidateTokens.size) return 0;
    const matches = queryTokens.filter((token) => candidateTokens.has(token)).length;
    return matches / Math.max(queryTokens.length, candidateTokens.size);
}

/* Whether the lecture is the surface the student is looking at. app.ai.js owns that rule for the
 * Agent, so it is asked first and this only mirrors its facts. The Tutor modal's `active` class is
 * not one of them: the lecture drops it once it is mounted into the work area, and a parked lecture
 * is still mounted while the quiz is the thing on screen. */
function isMemoraAgentTutorForeground() {
    try {
        if (typeof getMemoraAgentForegroundSurface === 'function') {
            return getMemoraAgentForegroundSurface().tutorForeground === true;
        }
    } catch (_error) { }
    const parked = typeof document !== 'undefined'
        && !!document.body?.classList?.contains('memora-live-learn-parked');
    const work = (typeof els !== 'undefined' ? els?.workArea : null) || $('work-area');
    return !parked && !!work?.classList?.contains('pdf-tutor-view');
}

function resolveMemoraAgentPdfTarget(target = '', { preferCurrent = true } = {}) {
    const loadedPdfNames = getMemoraAgentLoadedPdfNames();
    const current = getMemoraAgentCurrentQuestionContext();
    const targetText = String(target || '').trim();
    const targetLooksCurrent = !targetText || /\b(current|linked|this|that|it|source|card|question|evidence)\b/i.test(targetText);
    const viewer = typeof window !== 'undefined' ? window.MemoraSourceViewer?.current?.() : null;
    if (preferCurrent && targetLooksCurrent && viewer?.kind !== 'image' && loadedPdfNames.includes(viewer?.name)) {
        return { ok: true, name: viewer.name, source: 'active-source', score: 1, page: viewer.page };
    }

    // "This PDF" means the one on screen. The Tutor lecture is the foreground source whenever it is
    // open, so it wins over the PDF linked to whichever question was last looked at.
    const tutorName = typeof pdfTutorState !== 'undefined' ? pdfTutorState.pdfName : '';
    const tutorIsForeground = !!tutorName
        && loadedPdfNames.includes(tutorName)
        && isMemoraAgentTutorForeground();
    if (preferCurrent && targetLooksCurrent && tutorIsForeground) {
        return {
            ok: true,
            name: tutorName,
            source: 'active-source',
            score: 1,
            page: Math.max(1, Number(pdfTutorState.currentPage) || 1)
        };
    }

    if (preferCurrent && targetLooksCurrent && current.linkedPdf) {
        return {
            ok: true,
            name: current.linkedPdf,
            source: 'current-question',
            score: 1,
            page: current.citationPage || 1
        };
    }

    if (!loadedPdfNames.length) {
        return { ok: false, retryable: false, message: 'No PDFs are loaded in this session yet.' };
    }

    if (preferCurrent && targetLooksCurrent) {
        const visibleName = lastPdfState?.fileName || normalizePdfStateTitleToName(lastPdfState?.title || '');
        const name = (loadedPdfNames.includes(visibleName) ? visibleName : '')
            || (loadedPdfNames.length === 1 ? loadedPdfNames[0] : '');
        if (name) return { ok: true, name, source: 'active-source', score: 1, page: name === visibleName ? (lastPdfState?.page || 1) : 1 };
    }
    const ranked = loadedPdfNames
        .map((name) => ({
            name,
            score: scoreMemoraAgentPdfCandidate(targetText, name)
        }))
        .sort((a, b) => b.score - a.score);

    const best = ranked[0];
    const second = ranked[1];
    const loadedList = loadedPdfNames.slice(0, 12).map((name) => `"${name}"`).join(', ')
        + (loadedPdfNames.length > 12 ? `, and ${loadedPdfNames.length - 12} more` : '');
    if (!best || best.score < 0.45) {
        return {
            ok: false,
            status: 'choose-file',
            needsClarification: true,
            retryable: false,
            choices: loadedPdfNames.slice(0, 12),
            message: targetText
                ? `No loaded file matches "${targetText}". The loaded files are ${loadedList}. Ask the student which one they mean, then call again with that file name as target.`
                : `I could not tell which file they mean. The loaded files are ${loadedList}. Ask the student which one.`
        };
    }
    // Two equally good names (L8_Hemodynamics and Lecture 8 - Cell Injury) are a question, not a coin toss.
    if (second && ((best.score < 0.82 && Math.abs(best.score - second.score) < 0.12) || (best.score < 1 && second.score === best.score))) {
        return {
            ok: false,
            ambiguous: true,
            status: 'choose-file',
            needsClarification: true,
            retryable: false,
            choices: [best.name, second.name],
            message: `More than one file could match: "${best.name}" and "${second.name}". Ask the student which one, then call again with that file name as target.`
        };
    }

    return {
        ok: true,
        name: best.name,
        source: 'matched-name',
        score: best.score,
        page: current.linkedPdf === best.name ? (current.citationPage || 1) : 1
    };
}

function getMemoraAgentQuestionSearchTerm(question) {
    const choices = Array.isArray(question?.c) && question.c.length
        ? question.c
        : (Array.isArray(question?.o) ? question.o : []);
    const answerText = Number.isInteger(question?.a) ? String(choices[question.a] || '') : '';
    const words = answerText.split(/\s+/).filter((word) => word.length > 3).slice(0, 3);
    return words.length ? words.join(' ') : '';
}

function revealMemoraAgentAskDockForSource() {
    const reveal = typeof revealAskMemoraForSourceEvidence === 'function'
        ? revealAskMemoraForSourceEvidence
        : (typeof window !== 'undefined' && typeof window.revealAskMemoraForSourceEvidence === 'function'
            ? window.revealAskMemoraForSourceEvidence
            : null);
    if (!reveal) return;
    const getRect = typeof getAskMemoraVisiblePanelRect === 'function'
        ? getAskMemoraVisiblePanelRect
        : (typeof window !== 'undefined' && typeof window.getAskMemoraVisiblePanelRect === 'function'
            ? window.getAskMemoraVisiblePanelRect
            : null);
    const pdfRect = getRect ? getRect('pdf-pane') : null;
    if (pdfRect) {
        reveal();
    } else if (typeof requestAnimationFrame === 'function') {
        requestAnimationFrame(() => reveal());
    } else {
        reveal();
    }
}

function formatMemoraAgentSourceSlideAnswer(pdfName, page, sourceOrigin = '') {
    if (sourceOrigin === 'prior_answer') {
        return `That answer cites **${pdfName}**, **Slide ${page}** (opened in the viewer).`;
    }
    return `This question is from **${pdfName}**, **Slide ${page}** (opened in the viewer).`;
}

function getMemoraAgentSourceSlideFallbackMessage(resolved = {}, current = {}) {
    if (resolved?.ambiguous && resolved.message) {
        return resolved.message;
    }
    const citation = String(current?.citationText || '').trim();
    if (citation) {
        return `This card cites "${citation}", but no matching slide could be opened.`;
    }
    const linked = String(current?.sourceFile || current?.linkedPdf || '').trim();
    if (linked) {
        return `"${linked}" is linked to this card, but is not loaded in this session yet.`;
    }
    if (resolved?.message) {
        return `I could not open a source slide for this card: ${resolved.message}`;
    }
    return 'I could not open a source slide for this card because there is no linked lecture file loaded.';
}

function openMemoraAgentPdfTarget(target = '', options = {}) {
    const isSourceSlide = options.queryType === 'source_slide';
    const resolved = resolveMemoraAgentPdfTarget(target, {
        preferCurrent: options.preferCurrent !== false
    });
    if (!resolved.ok) {
        if (isSourceSlide) {
            const current = getMemoraAgentCurrentQuestionContext();
            return createMemoraAgentResult(false, getMemoraAgentSourceSlideFallbackMessage(resolved, current), {
                ...resolved,
                queryType: 'source_slide'
            });
        }
        return createMemoraAgentResult(false, resolved.message, resolved);
    }
    if (!loadedPdfFiles[resolved.name]) {
        return createMemoraAgentResult(false, `"${resolved.name}" is linked, but the PDF file is not loaded in this browser session yet.`, {
            ...resolved,
            queryType: isSourceSlide ? 'source_slide' : (options.queryType || '')
        });
    }

    const current = getMemoraAgentCurrentQuestionContext();
    const page = Math.max(1, Number(options.page || resolved.page || current.citationPage || 1) || 1);
    const searchTerm = current.question && current.linkedPdf === resolved.name
        ? getMemoraAgentQuestionSearchTerm(current.question)
        : '';
    // A cited slide shows in the file viewer, leaving Ask where it is; the PDF pane is the fallback.
    const shownInFileViewer = isSourceSlide
        && typeof openAskMemoraCitationInFileViewer === 'function'
        && openAskMemoraCitationInFileViewer(page, resolved.name);
    if (!shownInFileViewer) {
        jumpToPdfPage(page, resolved.name, searchTerm, {
            smooth: true,
            maintainScale: true,
            forceReload: false
        });
    }

    if (isSourceSlide) {
        if (!shownInFileViewer) {
            revealMemoraAgentAskDockForSource();
            if (typeof flashAskMemoraOpenedPdfSource === 'function') {
                flashAskMemoraOpenedPdfSource(page);
            }
        }
        return createMemoraAgentResult(true, formatMemoraAgentSourceSlideAnswer(resolved.name, page, options.sourceOrigin), {
            pdfName: resolved.name,
            page,
            source: resolved.source,
            score: resolved.score,
            queryType: 'source_slide'
        });
    }

    showPopupText(`Opened ${resolved.name} to page ${page}.`, null, false);
    return createMemoraAgentResult(true, `Opened "${resolved.name}" to page ${page} in the PDF pane.`, {
        pdfName: resolved.name,
        page,
        source: resolved.source,
        score: resolved.score,
        queryType: options.queryType || ''
    });
}

function resolveMemoraAgentThemePreset(payload = {}) {
    const raw = String(payload.preset || payload.theme || payload.name || payload.key || '').trim().toLowerCase();
    const index = Number(payload.index);
    if (Number.isInteger(index) && gradients[index]) return MEMORA_AGENT_THEME_PRESETS[index] || null;
    if (!raw) return null;

    const aliasMap = {
        default: ['default', 'blue', 'pink', 'purple', 'blue pink', 'original', 'normal'],
        amber: ['amber', 'orange', 'red', 'warm', 'sunset', 'fire'],
        rose: ['rose', 'maroon', 'wine', 'brown rose'],
        lime: ['lime', 'green', 'teal', 'mint'],
        earthy: ['earthy', 'earth', 'olive', 'brown', 'forest']
    };

    const matchedKey = Object.entries(aliasMap).find(([key, aliases]) => key === raw || aliases.includes(raw))?.[0];
    return MEMORA_AGENT_THEME_PRESETS.find((preset) => preset.key === matchedKey) || null;
}

function applyMemoraAgentThemePreset(payload = {}) {
    const preset = resolveMemoraAgentThemePreset(payload);
    if (!preset || !gradients[preset.index]) {
        return createMemoraAgentResult(false, 'I could not match that to a built-in Memoraful theme.');
    }
    const swatch = document.querySelectorAll('.grad-swatch')[preset.index] || null;
    if (typeof window.setGradient === 'function') {
        window.setGradient(preset.index, swatch);
    } else {
        setGradient(preset.index, swatch);
    }
    showPopup(`Theme changed to ${preset.label}.`, null, false);
    return createMemoraAgentResult(true, `Changed the theme to ${preset.label}.`, { preset: preset.key });
}

function normalizeMemoraAgentHexColor(value) {
    const raw = String(value || '').trim();
    const match = raw.match(/^#([0-9a-f]{3}|[0-9a-f]{6})$/i);
    if (!match) return '';
    const hex = match[1].toLowerCase();
    if (hex.length === 3) {
        return `#${hex.split('').map((char) => char + char).join('')}`;
    }
    return `#${hex}`;
}

function applyMemoraAgentCustomGradient(payload = {}) {
    const colors = Array.isArray(payload.colors) ? payload.colors : [];
    // Three slots, in the order a gradient reads: start, middle, end. Two colors arrive as
    // (start, end) or as (color1, color2), so a filled second slot with an empty third is the end.
    const declared = [
        payload.start || payload.color || payload.color1 || colors[0],
        payload.mid || payload.middle || payload.color2 || colors[1],
        payload.end || payload.color3 || colors[2]
    ].map((value) => normalizeMemoraAgentHexColor(value));
    const declaredCount = declared.filter(Boolean).length;
    const start = declared[0];
    const mid = declared[2] ? declared[1] : '';
    const end = declared[2] || declared[1];
    const requestedMode = payload.mode || payload.count || payload.colorsCount || colors.length;
    // Without a stated count, the colors themselves say how many were asked for.
    const mode = normalizeGradientMode(requestedMode || String(declaredCount || 1));
    if (!start || (mode === '2' && !end) || (mode === '3' && (!mid || !end))) {
        return createMemoraAgentResult(false, 'I need one, two, or three valid hex colors to apply a custom theme.');
    }
    if (els.gradStart) els.gradStart.value = start;
    if (els.gradMid && mid) els.gradMid.value = mid;
    if (els.gradEnd && end) els.gradEnd.value = end;
    setCustomGradientMode(mode);
    showPopup('Custom theme gradient applied.', null, false);
    return createMemoraAgentResult(true, `Changed the theme gradient using ${mode} color${mode === '1' ? '' : 's'}.`, { start, mid, end, mode });
}

function resolveMemoraAgentBoolean(payload = {}, currentValue = false) {
    if (typeof payload.enabled === 'boolean') return payload.enabled;
    const raw = String(payload.state || payload.mode || '').trim().toLowerCase();
    if (['on', 'true', 'enable', 'enabled', 'active'].includes(raw)) return true;
    if (['off', 'false', 'disable', 'disabled', 'inactive'].includes(raw)) return false;
    return !currentValue;
}

function setMemoraAgentAmbientMode(payload = {}) {
    const next = resolveMemoraAgentBoolean(payload, isAmbientMode);
    if (next !== isAmbientMode) {
        isAmbientMode = next;
        applyAmbientMode();
        saveThemeState();
    }
    return createMemoraAgentResult(true, `Ambient mode is ${isAmbientMode ? 'on' : 'off'}.`);
}

function setMemoraAgentBorderMode(payload = {}) {
    const next = resolveMemoraAgentBoolean(payload, isBorderMode);
    if (next !== isBorderMode) {
        isBorderMode = next;
        applyBorderMode();
        saveThemeState();
    }
    return createMemoraAgentResult(true, `Bordered mode is ${isBorderMode ? 'on' : 'off'}.`);
}

function setMemoraAgentQuizCardTransparency(payload = {}) {
    const current = document.body.classList.contains('quiz-card-bg-transparent');
    const next = resolveMemoraAgentBoolean(payload, current);
    if (next !== current) toggleQuizCardBgTransparency();
    const isTransparent = document.body.classList.contains('quiz-card-bg-transparent');
    return createMemoraAgentResult(true, `Quiz card background is now ${isTransparent ? 'transparent' : 'solid'}.`);
}

function resolveMemoraAgentNamedItem(items, target = '', { resume = false } = {}) {
    const query = normalizeMemoraAgentMatchText(target);
    if (!items.length) return createMemoraAgentResult(false, 'No matching saved content is available.');
    if (!query || /^(latest|last|most recent|current)$/.test(query)) {
        const item = (resume && (!query || query === 'current') && items.find(entry => entry.active)) || items.at(-1);
        return { ok: true, item };
    }
    const exact = items.filter(item => [item.id, item.name, ...(item.aliases || [])].some(value => normalizeMemoraAgentMatchText(value) === query));
    const matches = exact.length ? exact : items.filter(item => [item.name, ...(item.aliases || [])].some(value => normalizeMemoraAgentMatchText(value).includes(query)));
    return matches.length === 1 ? { ok: true, item: matches[0] }
        : createMemoraAgentResult(false, matches.length ? 'Which one do you mean?' : 'That saved content could not be found.', { choices: (matches.length ? matches : items).map(({ id, name }) => ({ id, name })) });
}

async function getMemoraAgentQuizCatalog() {
    const materials = await window.MemoraLiveLeftRailContext?.getSessionMaterials?.({ fresh: true });
    // Generation unshifts new banks into the manifest. Match its extension aliases once,
    // and rank a material by its newest usable bank rather than its source import date.
    const normalizeKey = key => String(key || '').replace(/\.(json|txt)$/i, '');
    const order = [...(sessionManifest?.jsons || []), ...Object.keys(loadedQuestionSets || {})].map(normalizeKey);
    return (materials || []).filter(material => material.modules?.['learn-quizzes']?.exists)
        .map(material => {
            const quiz = material.modules['learn-quizzes'];
            return { name: material.name, aliases: quiz.refs || [], count: quiz.count, answered: quiz.answered, active: !!quiz.active,
                order: Math.min(Infinity, ...(quiz.refs || []).map(key => order.indexOf(normalizeKey(key))).filter(index => index >= 0)) };
        }).sort((a, b) => b.order - a.order);
}

let memoraAgentNativeControls = null;
let memoraAgentNativeControlSequence = 0;

function getMemoraAgentNativeControlRoot() {
    const visible = element => !!element && !element.hidden && !element.closest('[inert],[aria-hidden="true"]')
        && element.getClientRects().length && getComputedStyle(element).visibility !== 'hidden';
    const menu = [...document.querySelectorAll('.memora-select-menu,[role="menu"]')].filter(visible).at(-1);
    if (menu) return menu;
    const overlays = [...document.querySelectorAll('.modal.active,[id$="-modal"].active,#memora-source-viewer')]
        .filter(element => element.id !== 'voice-agent-modal' && visible(element));
    if (overlays.length) return overlays.sort((a, b) => (Number(getComputedStyle(a).zIndex) || 0) - (Number(getComputedStyle(b).zIndex) || 0)).at(-1);
    return ['calendar-surface', 'memora-rail-context', 'work-area'].map(id => $(id)).find(visible) || null;
}

function executeMemoraAgentNativeControl(payload = {}) {
    const root = getMemoraAgentNativeControlRoot();
    const navigation = ['work-area', 'memora-rail-context', 'calendar-surface'].includes(root?.id) ? $('controls-bar') : null;
    const route = window.MemoraLiveLeftRailRouting?.currentRoute?.();
    const eligible = element => !!element && (root?.contains(element) || navigation?.contains(element)) && element.isConnected && !element.disabled && !element.readOnly
        && !element.closest('[inert],[hidden],[aria-hidden="true"],#voice-agent-modal')
        && element.getAttribute('aria-disabled') !== 'true' && element.getClientRects().length
        && getComputedStyle(element).visibility !== 'hidden';
    const label = getMemoraAgentNativeControlLabel;
    const privateField = element => element.type === 'password' || /api.?key|secret|token|credential|password/i.test(`${element.id} ${element.name} ${element.autocomplete}`);
    if (payload.isCurrent?.() === false || !root) return createMemoraAgentResult(false, 'No current native surface is available.');
    if (payload.operation === 'discover') {
        const query = normalizeMemoraAgentMatchText(payload.query || '');
        const selectors = 'button,summary,input,select,textarea,[role="button"],[role="tab"]';
        const elements = [...new Set([...(root.querySelectorAll(selectors) || []), ...(navigation?.querySelectorAll(selectors) || [])])]
            .filter(element => eligible(element) && element.type !== 'hidden' && !privateField(element) && label(element)
                && (!query || normalizeMemoraAgentMatchText(label(element)).includes(query)));
        const offset = Math.max(0, Number.isInteger(payload.offset) ? payload.offset : 0);
        const sequence = ++memoraAgentNativeControlSequence;
        const controls = elements.slice(offset, offset + 60).map((element, index) => ({ id: `${sequence}:${index}`, element, label: label(element), value: element.value, checked: element.checked }));
        memoraAgentNativeControls = { root, route, session: String(currentSessionId || ''), controls };
        return createMemoraAgentResult(true, 'These are the current native controls. Activate a button or set a field, then inspect state; asynchronous work may still be running.', {
            surface: root.id || root.getAttribute('role') || 'native-controls', total: elements.length, nextOffset: offset + controls.length < elements.length ? offset + controls.length : null,
            controls: controls.map(({id, element, label: name}) => ({ control: id, label: name,
                kind: element.type === 'file' ? 'file-picker' : ['INPUT','TEXTAREA','SELECT'].includes(element.tagName) ? 'field' : 'button',
                type: element.type || element.tagName.toLowerCase(),
                ...(['INPUT','TEXTAREA','SELECT'].includes(element.tagName) && element.type !== 'file' ? {
                    value: ['checkbox','radio'].includes(element.type) ? element.checked : String(element.value || '').slice(0, 500),
                    ...(String(element.value || '').length > 500 ? { valueTruncated: true } : {}),
                    ...(element.tagName === 'SELECT' ? { options: [...element.options].filter(option => !option.disabled).map(option => ({ value: option.value, label: option.textContent.trim() })) } : {}),
                    ...(element.min ? { min: element.min } : {}), ...(element.max ? { max: element.max } : {})
                } : {})
            }))
        });
    }
    const snapshot = memoraAgentNativeControls;
    const control = snapshot?.controls.find(item => item.id === String(payload.control));
    const element = control?.element;
    if (!snapshot || snapshot.root !== root || snapshot.session !== String(currentSessionId || '') || !eligible(element)
        || control.label !== label(element) || window.MemoraLiveLeftRailRouting?.routesEqual?.(snapshot.route, route) === false) {
        return createMemoraAgentResult(false, 'That native control changed. Discover controls again.');
    }
    if (['INPUT','TEXTAREA','SELECT'].includes(element.tagName) && (element.value !== control.value || element.checked !== control.checked)) {
        return createMemoraAgentResult(false, 'That field was edited after discovery. Discover its current state again.');
    }
    if (element.type === 'file') return createMemoraAgentResult(false, 'Choose the file in the native file picker; the browser requires your interaction.', { status: 'user-interaction-required' });
    if (payload.operation === 'set') {
        if (!['INPUT','TEXTAREA','SELECT'].includes(element.tagName) || privateField(element)) return createMemoraAgentResult(false, 'That control is not an editable field.');
        if (['checkbox','radio'].includes(element.type)) {
            if (typeof payload.enabled !== 'boolean') return createMemoraAgentResult(false, 'Provide an explicit enabled value.');
            if (element.checked !== payload.enabled) element.click();
            if (element.checked !== payload.enabled) return createMemoraAgentResult(false, 'That option cannot be cleared; choose another option in its group.');
        } else {
            const value = String(payload.value ?? '');
            if (element.tagName === 'SELECT' && ![...element.options].some(option => option.value === value && !option.disabled)) return createMemoraAgentResult(false, 'Choose one of the listed option values.');
            const previous = element.value;
            element.value = value;
            if ((element.validity && !element.validity.valid) || element.value !== value) { element.value = previous; return createMemoraAgentResult(false, 'That value is invalid for this native field.'); }
            element.dispatchEvent(new Event('input', { bubbles: true }));
            element.dispatchEvent(new Event('change', { bubbles: true }));
        }
    } else if (payload.operation === 'activate' && !['INPUT','TEXTAREA','SELECT'].includes(element.tagName)) element.click();
    else return createMemoraAgentResult(false, 'Discover controls, then activate a button or set a field.');
    memoraAgentNativeControls = null;
    return createMemoraAgentResult(true, 'Updated the native control. Inspect the current screen before claiming asynchronous work completed.', { status: 'activated', label: control.label });
}

function normalizeMemoraAgentModule(value) {
    const key = normalizeMemoraAgentMatchText(value);
    const aliases = { quizzes: 'learn-quizzes', quiz: 'learn-quizzes', 'study guide': 'learn-guide', guide: 'learn-guide',
        flashcards: 'learn-flashcards', 'flash cards': 'learn-flashcards', 'closed occlusion': 'learn-occlusion', occlusion: 'learn-occlusion',
        'guided tutor': 'learn-guided', tutor: 'learn-guided', guided: 'learn-guided', clinic: 'learn-clinic' };
    return aliases[key] || Object.values(aliases).find(mode => normalizeMemoraAgentMatchText(mode) === key) || '';
}

async function executeMemoraAgentStudyAction(action, payload = {}) {
    const routing = window.MemoraLiveLeftRailRouting;
    const context = window.MemoraLiveLeftRailContext;
    let originSession = String(currentSessionId || '');
    const originRoute = routing?.currentRoute?.();
    const current = () => payload.isCurrent?.() !== false && String(currentSessionId || '') === originSession
        && (!originRoute || routing?.routesEqual?.(originRoute, routing.currentRoute()) !== false);
    if (action === 'list_sessions' || action === 'open_session') {
        const sessions = (await readSavedSessionSummaries()).map(entry => ({ id: entry.id,
            name: entry.customName || `Study Session ${entry.sessionNumber || entry.id}`, timestamp: entry.timestamp,
            aliases: entry.manifest?.jsons || [], active: entry.id === currentSessionId }));
        if (!current()) return createMemoraAgentResult(false, 'Action cancelled: the session or page changed.');
        if (action === 'list_sessions') return { ok: true, sessions };
        const resolved = resolveMemoraAgentNamedItem([...sessions].reverse(), payload.sessionId || payload.target, { resume: true });
        if (!resolved.ok) return resolved;
        if (resolved.item.id !== currentSessionId && !await SessionManager.load(resolved.item.id)) return createMemoraAgentResult(false, 'That study session could not be loaded.');
        if (String(currentSessionId) !== String(resolved.item.id) || payload.isCurrent?.() === false) return createMemoraAgentResult(false, 'A newer session selection replaced that request.');
        await routing?.restoreRoute?.({ name: 'dashboard', sessionId: currentSessionId });
        return createMemoraAgentResult(true, 'Opened the study session.', { sessionId: resolved.item.id, name: resolved.item.name });
    }
    if (action === 'list_quizzes' || action === 'open_quiz' || action === 'resume_quiz') {
        const quizzes = await getMemoraAgentQuizCatalog();
        if (!current()) return createMemoraAgentResult(false, 'Action cancelled: the session or page changed.');
        if (action === 'list_quizzes') return { ok: true, sessionId: originSession, quizzes };
        const resolved = resolveMemoraAgentNamedItem(quizzes, payload.target, { resume: action === 'resume_quiz' });
        if (!resolved.ok) return resolved;
        const previousQuestion = resolved.item.active ? questions?.[filteredIndices[currentQIndex]] : null;
        const previousKey = previousQuestion ? buildQuestionPersistenceKeyFromQuestion(previousQuestion) : '';
        const opened = await context?.enterMaterial?.('learn-quizzes', resolved.item.name, { isCurrent: current });
        if (!opened || payload.isCurrent?.() === false || String(currentSessionId || '') !== originSession) return createMemoraAgentResult(false, 'The quiz could not be opened or another navigation replaced it.');
        // Native Open restores answer buckets but starts its queue at zero. Resume the retained
        // card by its stable key; otherwise continue at the first unanswered visible question.
        const position = previousKey ? filteredIndices.findIndex(index => buildQuestionPersistenceKeyFromQuestion(questions[index]) === previousKey) : -1;
        const pending = filteredIndices.findIndex(index => userAnswers[index] === undefined);
        if (position >= 0 || pending >= 0) { currentQIndex = position >= 0 ? position : pending; renderQuestion(); }
        const quiz = getMemoraAgentCurrentQuestionContext();
        return createMemoraAgentResult(true, 'Opened the quiz at its saved position.', {
            name: resolved.item.name, count: resolved.item.count, answered: resolved.item.answered,
            questionNumber: Number(currentQIndex) + 1, question: quiz.question?.q || quiz.question?.question || '', choices: getAskMemoraCurrentQuestionInfo()?.context?.choices || ''
        });
    }
    if (['open_learn', 'list_learn', 'create_learn', 'learn_control'].includes(action)) {
        const mode = normalizeMemoraAgentModule(payload.module || routing?.currentRoute?.().name);
        if (!['learn-quizzes', 'learn-guide', 'learn-flashcards', 'learn-occlusion', 'learn-guided', 'learn-clinic'].includes(mode)) return createMemoraAgentResult(false, 'Choose a supported Learn module.');
        if (window.requireMemoraLearnMode?.(mode) === false) return createMemoraAgentResult(false, 'That module is unavailable for this profile.');
        if (action === 'learn_control') return context?.voiceControl?.(mode, payload) || createMemoraAgentResult(false, 'That native control is unavailable.');
        const materials = await context?.getSessionMaterials?.({ fresh: true });
        if (!current()) return createMemoraAgentResult(false, 'Action cancelled: the session or page changed.');
        const candidates = (materials || []).filter(item => action === 'create_learn' ? item.isSource : item.modules?.[mode]?.exists);
        if (action === 'list_learn') return { ok: true, module: mode, materials: candidates.map(({ name }) => ({ name })) };
        const prior = context?.openedMaterials?.(mode) || [];
        const resolved = resolveMemoraAgentNamedItem(candidates.map(item => ({ ...item, active: prior.includes(item.name) })), payload.target, { resume: true });
        if (!resolved.ok) return resolved;
        if (action === 'create_learn') return await context?.voiceCreate?.(mode, resolved.item.name, payload) || createMemoraAgentResult(false, 'Native creation is unavailable.');
        const opened = await context?.enterMaterial?.(mode, resolved.item.name, { isCurrent: current });
        const foreground = routing?.currentRoute?.();
        const ok = !!opened && payload.isCurrent?.() !== false && String(currentSessionId || '') === originSession && foreground?.name === mode;
        return createMemoraAgentResult(ok, ok ? 'Opened saved Learn content on screen.' : 'That content could not be opened or a newer request replaced it.', { module: mode, name: resolved.item.name });
    }
    if (action === 'open_source_slide') {
        if (payload.sessionId && String(payload.sessionId) !== originSession) {
            const indexed = pdfTutorSlideFinderState.otherSessionSources?.some(source => source.sessionId === payload.sessionId && source.pdfName === payload.target);
            if (!indexed) return createMemoraAgentResult(false, 'That source was not returned by the saved-session search.');
            if (!await SessionManager.load(payload.sessionId) || String(currentSessionId) !== String(payload.sessionId)) return createMemoraAgentResult(false, 'That source session could not be loaded.');
            originSession = String(currentSessionId || '');
            if (payload.isCurrent?.() === false) return createMemoraAgentResult(false, 'The slide request was cancelled.');
        }
        const viewerSource = window.MemoraSourceViewer?.current?.();
        // A page-only request names the lecture the voice last cited, not the quiz's own lecture.
        const citedDeck = !payload.target && !viewerSource && payload.page !== undefined
            && typeof getGeminiLiveRecentCitedDeck === 'function' ? getGeminiLiveRecentCitedDeck(payload.page) : '';
        let resolved = !payload.target && viewerSource
            ? { ok: true, name: viewerSource.name, page: viewerSource.page }
            : resolveMemoraAgentPdfTarget(payload.target || citedDeck, { preferCurrent: !(payload.target || citedDeck) });
        if (!resolved.ok && citedDeck) resolved = resolveMemoraAgentPdfTarget('', { preferCurrent: true });
        if (!resolved.ok) return resolved;
        // A question with no citation must not silently show page one as its evidence.
        if (!payload.target && payload.page === undefined && !viewerSource && resolved.source === 'current-question' && !getMemoraAgentCurrentQuestionContext().citationPage) {
            return createMemoraAgentResult(false, 'This question has no cited slide. Use search_library to find a relevant slide.');
        }
        const page = payload.page ?? resolved.page;
        if (!Number.isInteger(page) || page < 1) return createMemoraAgentResult(false, 'No cited slide is available. Choose a file and page, or search_library for a topic.');
        const sourceRef = loadedPdfFiles[resolved.name];
        const viewer = window.MemoraSourceViewer?.open?.({ source: { name: resolved.name, page }, origin: { surface: 'voice-agent' } });
        if (!viewer || !await viewer.ready) return createMemoraAgentResult(false, 'That slide could not be displayed.');
        const visible = window.MemoraSourceViewer.current();
        if (payload.isCurrent?.() === false || String(currentSessionId || '') !== originSession || sourceRef !== loadedPdfFiles[resolved.name] || visible?.id !== viewer.id || visible?.page !== page) return createMemoraAgentResult(false, 'The slide request was replaced or its page is unavailable.');
        return createMemoraAgentResult(true, 'Opened the slide in the file viewer.', { name: resolved.name, page });
    }
    if (action === 'search_library') {
        const query = String(payload.query || payload.target || '').trim();
        if (!query) return createMemoraAgentResult(false, 'Give a slide topic to search for.');
        // With a source enlarged in the file viewer the student is reading slides, so a slide lookup
        // moves the viewer to the best match instead of opening Lecture Finder underneath it.
        if (window.MemoraSourceViewer?.current?.() && typeof runGeminiLiveSlideSearchTool === 'function') {
            const outcome = await runGeminiLiveSlideSearchTool(query, { backgroundWork: false }).catch(() => null);
            if (payload.isCurrent?.() === false || String(currentSessionId || '') !== originSession) return createMemoraAgentResult(false, 'The library search was replaced by a newer request.');
            const top = outcome?.found ? outcome.results?.[0] : null;
            if (top?.pdfName && top.page) {
                const page = Math.max(1, Math.round(Number(top.page) || 1));
                const opened = await executeMemoraAgentStudyAction('open_source_slide', { ...payload, target: top.pdfName, page, sessionId: undefined });
                if (opened?.ok) return { ...opened, message: `Opened the best matching slide, page ${page}, in the file viewer.`, slides: (outcome.slides || []).slice(0, 6) };
            }
        }
        if (payload.isCurrent?.() === false || String(currentSessionId || '') !== originSession) return createMemoraAgentResult(false, 'The library search was replaced by a newer request.');
        // A requested scope is passed through, not pre-set: a fresh opening would replace it with its default.
        const scope = ['session', 'sessions', 'current'].includes(payload.scope) ? payload.scope : undefined;
        const result = await window.MemoraLectureFinder?.openFromMainSearch?.(query, {
            searchOverlay: true, fromVoice: true, allowEmbeddingBuild: false,
            allowQueryEmbeddingBuild: true, backgroundWork: false, scope,
            isCurrent: () => payload.isCurrent?.() !== false && String(currentSessionId || '') === originSession
        });
        if (result?.ok && (payload.isCurrent?.() === false || String(currentSessionId || '') !== originSession)) return createMemoraAgentResult(false, 'The library search was replaced by a newer request.');
        return result?.ok ? { ...result, slides: (pdfTutorSlideFinderState.activeResults || []).slice(0, 6).map(slide => ({
            name: slide.pdfName, page: slide.page, confidence: slide.confidence,
            sessionId: pdfTutorSlideFinderState.otherSessionSources?.find(source => source.pdfName === slide.pdfName)?.sessionId || originSession,
            snippet: String(slide.snippet || '').slice(0, 300)
        })) } : result || createMemoraAgentResult(false, 'Lecture Finder is unavailable.');
    }
    if (action === 'navigate') {
        const destination = normalizeMemoraAgentMatchText(payload.destination).replace(/ /g, '-');
        const module = normalizeMemoraAgentModule(payload.destination);
        // The enlarged transcript sits above every destination; its layer settles before the next one opens.
        window.yieldVoiceAgentModalToDestination?.();
        await Promise.resolve();
        let opened = false;
        if (module) { await context?.open?.(module); opened = $('memora-rail-context')?.hidden === false; }
        else if (destination === 'stats') { openStatsModal(); opened = $('stats-modal')?.classList.contains('active'); }
        else if (destination === 'notes') { openNotesModal({ type: 'browse' }); opened = $('notes-modal')?.classList.contains('active'); }
        else if (['dashboard', 'sessions', 'sources'].includes(destination)) opened = await routing?.restoreRoute?.({ name: destination, sessionId: currentSessionId });
        else if (destination === 'saved-content') { await routing?.openSavedContent?.(payload.category || 'all'); opened = routing?.currentRoute?.().name === 'saved-content'; }
        else if (destination === 'learn' || destination === 'quizzes') { await context?.open?.(destination === 'quizzes' ? 'learn-quizzes' : ''); opened = $('memora-rail-context')?.hidden === false; }
        else if (destination === 'settings') { window.MemoraSettings?.open?.({ section: payload.section || 'appearance' }); opened = $('settings-modal')?.classList.contains('active'); }
        else if (destination === 'calendar') {
            await routing?.restoreRoute?.({ name: 'calendar', sessionId: currentSessionId });
            opened = routing?.currentRoute?.().name === 'calendar' && $('calendar-surface')?.getClientRects().length > 0;
        }
        else if (destination === 'ask') { openAskAIModalFromSearch(); opened = $('ask-ai-modal')?.classList.contains('active'); }
        // Settings and Notes are built to sit over the file viewer. Anything else replaces it,
        // closing only once the destination is really there so a failed open keeps the student's slide.
        if (opened && destination !== 'settings' && destination !== 'notes') window.MemoraSourceViewer?.close?.('navigation');
        return createMemoraAgentResult(!!opened, opened ? `Opened ${destination}.` : 'That destination could not be opened.');
    }
    if (action === 'quiz_control') {
        if (!isGeminiLiveQuizCardInForeground() || !filteredIndices?.length) return createMemoraAgentResult(false, 'Open a quiz on screen first.');
        const operation = String(payload.operation || '');
        // Live reads the card it is asked about. The tool lists status beside the move operations, and
        // rejecting it left the model saying it could not see the question on screen.
        if (operation === 'status' || operation === 'read') {
            const info = typeof getAskMemoraCurrentQuestionInfo === 'function' ? getAskMemoraCurrentQuestionInfo() : null;
            const context = info?.context || null;
            if (!context?.question) return createMemoraAgentResult(false, 'The question on screen could not be read.');
            const state = typeof getAskMemoraCurrentQuestionAnswerState === 'function' ? getAskMemoraCurrentQuestionAnswerState(info) : { answered: false };
            // The key stays out until the student has answered, as in Ask.
            const reveal = state.answered ? { correctLetter: context.correctLetter, correctAnswer: context.correctAnswer, explanation: context.explanation } : {};
            return createMemoraAgentResult(true, state.answered
                ? 'This is the question on screen and the student has answered it. Explain it.'
                : 'This is the question on screen. The student has not answered it yet: explain what it asks without giving away the answer.',
                { questionNumber: currentQIndex + 1, totalQuestions: filteredIndices.length, question: context.question, choices: context.choices, answered: !!state.answered, studentChoice: state.selectedLetter || '', ...reveal });
        }
        if (operation === 'go_to_question') {
            const index = Number(payload.questionNumber) - 1;
            if (!Number.isInteger(index) || index < 0 || index >= filteredIndices.length) return createMemoraAgentResult(false, 'Choose a visible question number in this quiz.');
            currentQIndex = index;
            renderQuestion();
        } else if (operation === 'save_question') {
            if (typeof toggleSave !== 'function') return createMemoraAgentResult(false, 'Saving a question is unavailable.');
            if (!savedQuestions[filteredIndices[currentQIndex]]) toggleSave();
        } else {
            if (!['next_question', 'prev_question', 'select_choice'].includes(operation)) return createMemoraAgentResult(false, 'That quiz control is unavailable.');
            // An answer names the question it answers. A delayed select after a manual Next used to land on
            // whichever question was current by then.
            // The question the student was looking at when they spoke is the authority: when it is still on
            // screen, a stale or missing number from the model is its own slip, not a changed question.
            const spokenOnThisQuestion = typeof payload.quizIdentity === 'string' && !!payload.quizIdentity && payload.quizIdentity === getMemoraAgentQuizIdentity();
            if (operation === 'select_choice' && !spokenOnThisQuestion && Number(payload.questionNumber) !== currentQIndex + 1) return createMemoraAgentResult(false, `The question on screen changed. Question ${currentQIndex + 1} is showing now; include questionNumber to answer it.`, { questionNumber: currentQIndex + 1 });
            // The same number in another deck, filter or choice order is a different question.
            if (operation === 'select_choice' && typeof payload.quizIdentity === 'string' && payload.quizIdentity !== getMemoraAgentQuizIdentity()) return createMemoraAgentResult(false, `The question on screen changed. Read question ${currentQIndex + 1} to the student before answering it.`, { questionNumber: currentQIndex + 1 });
            if (operation === 'select_choice' && (!Number.isInteger(payload.choiceIndex) || !getGeminiLiveVisibleChoiceSnapshot(payload.choiceIndex)?.button || userAnswers?.[filteredIndices[currentQIndex]] !== undefined)) return createMemoraAgentResult(false, 'That choice is unavailable or the question is already answered.');
            const outcome = executeGeminiLiveVoiceQuizAction({ type: operation, displayIndex: payload.choiceIndex });
            return createMemoraAgentResult(!!outcome, outcome?.localReply || 'Quiz control could not run.', outcome?.verdict || {});
        }
        return createMemoraAgentResult(true, 'Updated the quiz.', { questionNumber: currentQIndex + 1 });
    }
    return createMemoraAgentResult(false, 'That study action is unavailable.');
}

async function executeMemoraAgentAction(action, payload = {}) {
    const safeAction = String(action || '').trim();
    if (['get_status','get_study_stats','save_note','add_calendar_event','remove_calendar_event','review_missed','flashcard_control','compare_slides','undo_last'].includes(safeAction)) {
        if (typeof executeGeminiLiveStudyTool !== 'function') return createMemoraAgentResult(false, 'Voice study tools are unavailable.');
        return executeGeminiLiveStudyTool(safeAction, payload);
    }
    if (safeAction === 'app_control') {
        await new Promise(resolve => requestAnimationFrame(resolve));
        return executeMemoraAgentNativeControl(payload);
    }
    if (['list_quizzes', 'open_quiz', 'resume_quiz', 'list_sessions', 'open_session', 'navigate', 'open_learn', 'list_learn', 'create_learn', 'learn_control', 'quiz_control', 'open_source_slide', 'search_library'].includes(safeAction)) {
        return executeMemoraAgentStudyAction(safeAction, payload);
    }
    switch (safeAction) {
        case 'open_current_pdf':
            return openMemoraAgentPdfTarget('', { ...payload, preferCurrent: true });
        case 'open_pdf':
            return openMemoraAgentPdfTarget(payload.target || payload.pdfName || payload.name || '', payload);
        case 'open_pdf_explanation': {
            if (!isMemoraPdfExplanationFeatureEnabledForCore()) {
                return createMemoraAgentResult(false, 'PDF Explanation is off. Turn it on under AI & Help to use that legacy pane.');
            }
            if (typeof openPdfExplanationForCurrentQuestion !== 'function') {
                return createMemoraAgentResult(false, 'PDF explanation is not available right now.');
            }
            const opened = await openPdfExplanationForCurrentQuestion({ silent: false, smooth: true, ensureVisibleAiPane: true });
            return opened
                ? createMemoraAgentResult(true, 'Opened the PDF explanation for the current card.')
                : createMemoraAgentResult(false, 'I could not open a PDF explanation for the current card.');
        }
        case 'open_theme':
            // The same handoff as navigate: the enlarged transcript gives way before the next layer opens.
            window.yieldVoiceAgentModalToDestination?.();
            await Promise.resolve();
            openThemeModal();
            return createMemoraAgentResult(true, 'Opened Theme settings.');
        case 'set_theme_preset':
            return applyMemoraAgentThemePreset(payload);
        case 'set_custom_gradient':
            return applyMemoraAgentCustomGradient(payload);
        case 'reset_theme':
            els.resetThemeBtn?.click();
            return createMemoraAgentResult(true, 'Reset the theme to Memoraful defaults.');
        case 'set_ambient_mode':
            return setMemoraAgentAmbientMode(payload);
        case 'set_border_mode':
            return setMemoraAgentBorderMode(payload);
        case 'set_quiz_card_transparency':
            return setMemoraAgentQuizCardTransparency(payload);
        case 'open_database':
            window.yieldVoiceAgentModalToDestination?.();
            await Promise.resolve();
            await openDatabaseModal();
            // Like the destinations navigate opens, the Database replaces the file viewer once it is up.
            if ($('database-modal')?.classList.contains('active')) window.MemoraSourceViewer?.close?.('navigation');
            return createMemoraAgentResult(true, 'Opened the Database.');
        case 'open_saved':
            window.yieldVoiceAgentModalToDestination?.();
            await Promise.resolve();
            await openSavedModal();
            if (window.MemoraLiveLeftRailRouting?.currentRoute?.().name === 'saved-content') window.MemoraSourceViewer?.close?.('navigation');
            return createMemoraAgentResult(true, 'Opened Saved Questions.');
        case 'describe_capabilities':
            return createMemoraAgentResult(true, `I can run these Memoraful actions: ${getMemoraAgentCapabilities().map((item) => item.label).join('; ')}.`);
        default:
            return createMemoraAgentResult(false, 'That Memoraful action is not available.');
    }
}

function getMemoraAgentStateSnapshot() {
    const current = getMemoraAgentCurrentQuestionContext();
    return {
        sessionId: String(currentSessionId || ''),
        route: window.MemoraLiveLeftRailRouting?.currentRoute?.() || null,
        sourceViewer: window.MemoraSourceViewer?.current?.() || null,
        loadedPdfNames: getMemoraAgentLoadedPdfNames(),
        currentLinkedPdf: current.linkedPdf,
        currentCitationPage: current.citationPage,
        currentSourceFile: current.sourceFile,
        visiblePdfName: lastPdfState?.fileName || normalizePdfStateTitleToName(lastPdfState?.title || '') || '',
        themePresets: MEMORA_AGENT_THEME_PRESETS.map(({ key, label }) => ({ key, label })),
        zenMode: !!isZenMode,
        ambientMode: !!isAmbientMode,
        borderMode: !!isBorderMode,
        quizCardTransparent: document.body.classList.contains('quiz-card-bg-transparent')
    };
}

window.MemoraAgentActions = {
    getCapabilities: getMemoraAgentCapabilities,
    getStateSnapshot: getMemoraAgentStateSnapshot,
    execute: executeMemoraAgentAction
};

/* --- VIEW MODES --- */
els.prev.onclick = prevQuestion; els.next.onclick = nextQuestion; els.save.onclick = toggleSave;
els.modeBtn.onclick = () => {
    if (isAdaptiveReviewMode && !isFlashcardMode) {
        showPopup('Turn off Quiz Spaced Review (Anki-style) before switching to Flashcards.', null, false);
        return;
    }
    if (isFlashcardAdaptiveReviewMode && isFlashcardMode) {
        deactivateFlashcardAdaptiveReviewMode(false);
    }

    isFlashcardMode = !isFlashcardMode;
    if (!isFlashcardMode) flashcardReviewScope = 'due';
    setModeToggleButtonLabel(isFlashcardMode);
    els.modeBtn.querySelector('.gradient-text').innerText = isFlashcardMode ? "🎴 Switch to Quiz" : "🎴 Switch to Flashcards";
    if (els.popup.classList.contains('active')) {
        els.popup.classList.remove('active');
        currentPopupBtn = null;
    }
    if (els.menuDropdown.classList.contains('active')) {
        closeMenuDropdown();
    }
    isFlashcardMode ? showFlashcards() : showQuiz();
    setStudyDashboardLastMode(isFlashcardMode ? STUDY_ACTION_KEYS.flashcards : STUDY_ACTION_KEYS.quiz);
    syncQuickActionsMenuState();
    syncFloatingQuizLayering();
};
els.shuffleBtn.onclick = () => {
    // Explanations are written in display order while a run is active, so the order holds until it ends.
    if (window.syncShuffleGenerationLock?.() === true) return;
    isShuffled = !isShuffled;
    if (isShuffled) { els.shuffleBtn.classList.add('active'); applySmartShuffle(); } else { els.shuffleBtn.classList.remove('active'); filteredIndices.sort((a, b) => a - b); queueAnsweredQuestionsBehind(); showPopup("Order reset.", null, false); }
    syncQuickActionsMenuState();
    if (!isFlashcardMode) renderQuestion();
};
function partitionQuestionQueueByCompletion(indices, options = {}) {
    const {
        shuffleUnanswered = false,
        sortAnswered = false
    } = options || {};
    const answered = [];
    const unanswered = [];
    (Array.isArray(indices) ? indices : []).forEach((rawIdx) => {
        const idx = Number(rawIdx);
        if (!Number.isInteger(idx) || idx < 0 || idx >= questions.length) return;
        (userAnswers[idx] !== undefined ? answered : unanswered).push(idx);
    });
    if (shuffleUnanswered) {
        // Quick-start questions keep their place at the front: they arrive explained, and a shuffle
        // would otherwise put a question that is still waiting for its explanation second.
        const quickStart = unanswered.filter((idx) => questions[idx]?.autoStarter === true);
        const rest = unanswered.filter((idx) => questions[idx]?.autoStarter !== true);
        shuffleInPlace(rest);
        unanswered.splice(0, unanswered.length, ...quickStart, ...rest);
    }
    moveShownLastQuestionsBehind(unanswered);
    if (sortAnswered) answered.sort((a, b) => a - b);
    return {
        ordered: [...answered, ...unanswered],
        unansweredCount: unanswered.length,
        answeredCount: answered.length
    };
}
// Answered cards go behind the current position and the view lands on the first unanswered card
// (the current one, when it is still unanswered), so Next walks only new cards and Back reviews.
function queueAnsweredQuestionsBehind() {
    const currentRealIdx = filteredIndices[currentQIndex];
    const { ordered, answeredCount, unansweredCount } = partitionQuestionQueueByCompletion(filteredIndices);
    if (!unansweredCount) return;
    if (Number.isInteger(currentRealIdx) && userAnswers[currentRealIdx] === undefined) {
        ordered.splice(ordered.indexOf(currentRealIdx), 1);
        ordered.splice(answeredCount, 0, currentRealIdx);
    }
    filteredIndices = ordered;
    applySmartShuffle({ showNotice: false, shuffleUnanswered: false, sortAnswered: false });
}
function applySmartShuffle({
    showNotice = true,
    shuffleUnanswered = true,
    preserveCurrentQuestion = false,
    sortAnswered = true
} = {}) {
    const currentRealIdx = Number.isInteger(filteredIndices?.[currentQIndex]) ? filteredIndices[currentQIndex] : -1;
    const { ordered, unansweredCount, answeredCount } = partitionQuestionQueueByCompletion(filteredIndices, {
        shuffleUnanswered,
        sortAnswered
    });
    filteredIndices = ordered;
    if (filteredIndices.length === 0) {
        currentQIndex = 0;
    } else if (
        preserveCurrentQuestion
        && currentRealIdx >= 0
    ) {
        const nextIndex = filteredIndices.indexOf(currentRealIdx);
        currentQIndex = nextIndex >= 0 ? nextIndex : 0;
    } else {
        currentQIndex = unansweredCount > 0
            ? answeredCount
            : Math.min(Math.max(currentQIndex, 0), filteredIndices.length - 1);
    }
    if (!preserveCurrentQuestion && isAdaptivePracticeActive()) {
        // Explicit ordering replaces the old seen-card prefix so the next render cannot undo it.
        adaptivePracticeState.historyKeys = buildFilteredQuestionKeysForPersistence(filteredIndices.slice(0, currentQIndex + 1));
    }
    if (showNotice) showPopup(`Smart Shuffle: ${unansweredCount} unanswered queued.`, null, false);
    return { unansweredCount, answeredCount };
}
els.hardModeBtn.onclick = () => { isHardMode = !isHardMode; els.hardModeBtn.classList.toggle('active'); syncQuickActionsMenuState(); if (!isFlashcardMode) renderQuestion(); };
els.mixAnswersBtn.onclick = () => {
    isMixAnswers = !isMixAnswers;
    mixedChoiceOrderByQuestion = {};
    els.mixAnswersBtn.classList.toggle('active');
    syncQuickActionsMenuState();
    if (!isFlashcardMode) renderQuestion();
};

/* --- SEARCH --- */
let mainSearchFinderLastQuery = '';

function setMainSearchFinderReopenVisible(visible, query = '') {
    const btn = els?.searchFinderReopenBtn || $('search-finder-reopen-btn');
    if (!btn) return;
    const cleanQuery = String(query || mainSearchFinderLastQuery || '').trim();
    if (visible && cleanQuery) {
        mainSearchFinderLastQuery = cleanQuery;
        btn.hidden = false;
        btn.dataset.finderQuery = cleanQuery;
        btn.title = `Reopen Lecture Finder for "${cleanQuery}"`;
        btn.setAttribute('aria-label', `Reopen Lecture Finder for ${cleanQuery}`);
        return;
    }
    btn.hidden = true;
    delete btn.dataset.finderQuery;
    btn.title = 'Reopen Lecture Finder';
    btn.setAttribute('aria-label', 'Reopen Lecture Finder');
}

window.setMainSearchFinderReopenVisible = setMainSearchFinderReopenVisible;

function canRunMainSearchLectureFinder(term = '') {
    const query = String(term || '').trim();
    return query.length >= 3
        && !!window.MemoraLectureFinder
        && typeof window.MemoraLectureFinder.openFromMainSearch === 'function'
        && (!window.MemoraLectureFinder.canSearch || window.MemoraLectureFinder.canSearch());
}

async function runMainSearchLectureFinder(term = '') {
    const query = String(term || '').trim();
    if (!canRunMainSearchLectureFinder(query)) return false;
    const fromSearchOverlay = document.body?.classList.contains('memora-live-left-rail-search-open') === true;
    setSearchDropdownOpen(false);
    if (!fromSearchOverlay && els?.searchInput) els.searchInput.value = '';
    setMainSearchFinderReopenVisible(true, query);
    if (!fromSearchOverlay) showPopup('Opening Lecture Finder...', null, false, 30000);
    try {
        const result = await window.MemoraLectureFinder.openFromMainSearch(query, { searchOverlay: fromSearchOverlay });
        if (!result?.ok) {
            showPopupText(result?.message || 'No matching lecture slide was found.', null, true);
            return false;
        }
        if (!fromSearchOverlay) showPopupText(result?.message || 'Lecture Finder results are open.', null, false);
        return true;
    } catch (error) {
        console.error('Lecture search failed:', error);
        showPopupText(error.message || 'Lecture search failed.', null, true);
        return false;
    }
}

els.searchFinderReopenBtn?.addEventListener('click', (event) => {
    event.preventDefault();
    event.stopPropagation();
    const query = String(event.currentTarget?.dataset?.finderQuery || mainSearchFinderLastQuery || '').trim();
    if (!query || !window.MemoraLectureFinder?.reopenLatest) {
        showPopup('Run a lecture search first.', null, true);
        return;
    }
    setSearchDropdownOpen(false);
    void window.MemoraLectureFinder.reopenLatest(query);
});

// Lower-cased searchable text per question object, rebuilt only when its text changes,
// so typing does not re-normalize the whole bank on every keystroke.
const mainSearchQuestionHaystackCache = new WeakMap();
let mainSearchInputTimer = 0;
let mainSearchRunToken = 0;
const MAIN_SEARCH_MAX_QUESTION_RESULTS = 10;
const MAIN_SEARCH_MAX_SESSION_RESULTS = 5;

function getMainSearchQuestionHaystack(q) {
    if (!q || typeof q !== 'object') return '';
    const choices = Array.isArray(q.c) ? q.c : (Array.isArray(q.o) ? q.o : []);
    const signature = [q.q, q.question, q.e, q.explanation, q.sourceFile, q.src, q.setName, choices.length, choices[0]];
    const cached = mainSearchQuestionHaystackCache.get(q);
    if (cached && cached.signature.every((value, index) => value === signature[index])) return cached.text;
    const text = [
        q.q || q.question || '',
        q.e || q.explanation || '',
        choices.map((choice) => String(choice || '')).join(' '),
        q.sourceFile || q.src || q.setName || ''
    ].map((part) => String(part).toLowerCase()).join(' ').trim();
    mainSearchQuestionHaystackCache.set(q, { signature, text });
    return text;
}

function getMainSearchResultItems() {
    return Array.from(els.searchDropdown?.querySelectorAll('.search-item') || []);
}

// One active result drives both arrow keys and Enter; the input keeps focus and points
// at it through aria-activedescendant.
function setMainSearchActiveResult(index) {
    const items = getMainSearchResultItems();
    const next = items.length ? Math.max(-1, Math.min(items.length - 1, index)) : -1;
    items.forEach((item, itemIndex) => {
        const active = itemIndex === next;
        item.classList.toggle('is-active', active);
        item.setAttribute('aria-selected', active ? 'true' : 'false');
    });
    if (next >= 0) {
        els.searchInput?.setAttribute('aria-activedescendant', items[next].id);
        items[next].scrollIntoView?.({ block: 'nearest' });
    } else {
        els.searchInput?.removeAttribute('aria-activedescendant');
    }
    return next;
}

function appendMainSearchResult(className, label, text, onActivate) {
    const div = document.createElement('div');
    div.className = className;
    div.id = `main-search-result-${els.searchDropdown.children.length}`;
    div.setAttribute('role', 'option');
    div.setAttribute('aria-selected', 'false');
    div.innerHTML = `<div class="s-idx">${label}</div><div class="s-txt">${text}</div>`;
    div.onclick = onActivate;
    els.searchDropdown.appendChild(div);
    return div;
}

async function runMainSearchQuery(rawTerm) {
    const token = ++mainSearchRunToken;
    const term = String(rawTerm || '').trim().toLowerCase();
    if (term.length < 2) { setSearchDropdownOpen(false); return; }

    // 1. Search questions, stopping once the displayed limit is reached.
    const qMatches = [];
    const questionSearchIndices = condensedQuizState.enabled && hasCondensedQuizSelection()
        ? getCondensedQuestionIndices()
        : questions.map((_, idx) => idx);
    for (const idx of questionSearchIndices) {
        if (getMainSearchQuestionHaystack(questions[idx]).includes(term)) qMatches.push(idx);
        if (qMatches.length >= MAIN_SEARCH_MAX_QUESTION_RESULTS) break;
    }

    // 2. Search PDFs
    const pdfMatches = [];
    for (const [filename, pages] of Object.entries(pdfTextIndex || {})) {
        for (const { page, text } of (Array.isArray(pages) ? pages : [])) {
            const pdfText = String(text || '').toLowerCase();
            if (pdfText.includes(term)) {
                // Get context snippet around match
                const pos = pdfText.indexOf(term);
                const start = Math.max(0, pos - 30);
                const end = Math.min(pdfText.length, pos + term.length + 40);
                const snippet = (start > 0 ? '...' : '') + pdfText.substring(start, end) + (end < pdfText.length ? '...' : '');
                pdfMatches.push({ filename, page, snippet });
                if (pdfMatches.length >= 5) break; // Limit PDF matches
            }
        }
        if (pdfMatches.length >= 5) break;
    }

    // 3. Search saved sessions by name.
    let sessionMatches = [];
    try {
        const summaries = await readSavedSessionSummaries({ timeoutMs: 3000 }) || [];
        if (token !== mainSearchRunToken) return;
        sessionMatches = summaries
            .map((entry) => ({ id: normalizeSessionId(entry?.id), name: getSavedSessionDisplayNameValue(entry) || (entry?.sessionNumber ? `Session ${entry.sessionNumber}` : '') }))
            .filter((entry) => entry.id && entry.name && entry.name.toLowerCase().includes(term))
            .slice(0, MAIN_SEARCH_MAX_SESSION_RESULTS);
    } catch (error) {
        console.warn('Session search skipped:', error);
    }
    if (token !== mainSearchRunToken) return;

    const lectureSearchAvailable = canRunMainSearchLectureFinder(term);
    if (qMatches.length === 0 && pdfMatches.length === 0 && sessionMatches.length === 0 && !lectureSearchAvailable) {
        setSearchDropdownOpen(false);
        return;
    }

    els.searchDropdown.innerHTML = '';
    els.searchDropdown.setAttribute('role', 'listbox');
    els.searchDropdown.setAttribute('aria-label', 'Search results');

    if (lectureSearchAvailable && !document.body?.classList.contains('memora-live-left-rail-search-open')) {
        appendMainSearchResult('search-item search-item-pdf', 'Open Lecture Finder', `Search loaded lecture slides for "${escapeHtml(term)}"`, () => {
            void runMainSearchLectureFinder(term);
        });
    }

    // Render question matches
    const questionSetKeysByIndex = qMatches.length ? buildResultsQuestionSetKeyByIndexMap() : {};
    qMatches.forEach(idx => {
        const matchedQuestion = questions[idx];
        const matchedSetKey = String(questionSetKeysByIndex[idx] || '');
        const matchedText = String(matchedQuestion?.q || matchedQuestion?.question || '');
        const div = appendMainSearchResult('search-item', `Question ${idx + 1}`, `${escapeHtml(matchedText.slice(0, 60))}...`, null);
        div.onclick = async () => {
            window.MemoraLiveLeftRailPreview?.closeSearch?.();
            els.searchInput.value = '';
            setSearchDropdownOpen(false);
            const routed = await window.MemoraLiveLeftRailRouting?.returnToQuizzes?.();
            if (routed === false) return;
            const nextIdx = questions.indexOf(matchedQuestion);
            const currentSetKeysByIndex = buildResultsQuestionSetKeyByIndexMap();
            const resolvedIdx = nextIdx >= 0 ? nextIdx : questions.findIndex((candidate, candidateIdx) =>
                String(currentSetKeysByIndex[candidateIdx] || '') === matchedSetKey
                && String(candidate?.q || candidate?.question || '') === matchedText);
            if (resolvedIdx < 0) {
                showPopup('That question is no longer available in the selected quiz files.', null, true);
                return;
            }
            if (condensedQuizState.enabled && hasCondensedQuizSelection() && !getCondensedQuestionIndices().includes(resolvedIdx)) {
                showPopup('That question is outside Condensed View. Turn it off to open the full bank.', null, true);
                return;
            }
            jumpToQuestionByRealIndex(resolvedIdx);
        };
    });

    // Render session matches
    sessionMatches.forEach(({ id, name }) => {
        appendMainSearchResult('search-item search-item-session', 'Session', escapeHtml(name), async () => {
            window.MemoraLiveLeftRailPreview?.closeSearch?.();
            els.searchInput.value = '';
            setSearchDropdownOpen(false);
            try {
                if (!await SessionManager.load(id)) showPopup('Could not open that session.', null, true);
            } catch (error) {
                console.error('Failed to open session from search:', error);
                showPopup('Could not open that session.', null, true);
            }
        });
    });

    // Render PDF matches
    pdfMatches.forEach(({ filename, page, snippet }) => {
        appendMainSearchResult('search-item search-item-pdf', `${escapeHtml(filename.substring(0, 20))} - Page ${page}`, escapeHtml(snippet), () => {
            jumpToPdfPage(page, filename, term, { smooth: true, maintainScale: false }); // Keep smooth jump for search results
            els.searchInput.value = ''; setSearchDropdownOpen(false);
        });
    });

    setMainSearchActiveResult(-1);
    setSearchDropdownOpen(true);
}

els.searchInput.setAttribute('aria-controls', 'search-dropdown');
els.searchInput.addEventListener('input', (e) => {
    const value = String(e?.target?.value || '');
    if (mainSearchInputTimer) clearTimeout(mainSearchInputTimer);
    mainSearchInputTimer = setTimeout(() => {
        mainSearchInputTimer = 0;
        void runMainSearchQuery(value);
    }, 120);
});
els.searchInput.addEventListener('keydown', (e) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        const items = getMainSearchResultItems();
        if (!items.length || !els.searchDropdown?.classList.contains('active')) return;
        e.preventDefault();
        const current = items.findIndex((item) => item.classList.contains('is-active'));
        const next = e.key === 'ArrowDown'
            ? (current + 1) % items.length
            : (current <= 0 ? items.length - 1 : current - 1);
        setMainSearchActiveResult(next);
        return;
    }
    if (e.key !== 'Enter') return;
    const active = els.searchDropdown?.classList.contains('active')
        ? els.searchDropdown.querySelector('.search-item.is-active')
        : null;
    if (active) {
        e.preventDefault();
        active.click();
        return;
    }
    const term = String(e.currentTarget?.value || '').trim();
    const shouldRunLectureSearch = canRunMainSearchLectureFinder(term);
    if (!shouldRunLectureSearch) return;
    e.preventDefault();
    void runMainSearchLectureFinder(term);
});
if (els.autoSaveBtn) {
    els.autoSaveBtn.onclick = () => {
        autoSaveWrong = !autoSaveWrong;
        syncAutoStudyToolButtons();
        markSessionStateMutation();
        saveSessionDebounced();
        saveThemeState();
    };
}
if (els.autoAdvanceBtn) els.autoAdvanceBtn.onclick = () => { setAutoStudyToolPreferences({ autoAdvance: !autoAdvance }); };
if (els.autoEvidenceBtn) els.autoEvidenceBtn.onclick = () => { toggleAutoEvidenceMode({ openCurrent: true, notify: true }); };
window.setAutoStudyToolPreferences = setAutoStudyToolPreferences;

function toggleShortcuts(event = null) {
    if (event) {
        if (typeof event.preventDefault === 'function') event.preventDefault();
        if (typeof event.stopPropagation === 'function') event.stopPropagation();
    }
    const panel = $('shortcuts-panel');
    if (!panel) return;
    const shouldOpen = panel.style.display !== 'block';
    panel.style.display = shouldOpen ? 'block' : 'none';
    if (shouldOpen) {
        window.MemoraModalCoordinator?.open({
            id: 'shortcuts-panel',
            element: panel,
            opener: document.activeElement,
            trapFocus: true,
            isOpen: () => panel.style.display === 'block',
            close: () => {
                panel.style.display = 'none';
                return true;
            }
        });
    } else {
        window.MemoraModalCoordinator?.markClosed('shortcuts-panel');
    }
}

// Click away to close shortcuts panel
document.addEventListener('click', (e) => {
    const panel = $('shortcuts-panel');
    const trigger = e.target instanceof Element
        ? e.target.closest('#shortcuts-btn, #shortcuts-menu-btn')
        : null;
    if (panel && panel.style.display === 'block' && !panel.contains(e.target) && !trigger) {
        panel.style.display = 'none';
        window.MemoraModalCoordinator?.markClosed('shortcuts-panel');
    }
});

// --- SHORTCUT REMAPPING SYSTEM ---
let shortcutEditMode = false;
let editingKeyTag = null;

// Default shortcuts mapping
const defaultShortcuts = {
    selectA: 'a', selectB: 'b', selectC: 'c', selectD: 'd',
    next: 'ArrowRight', prev: 'ArrowLeft',
    save: 's', evidence: 'e', openPdfExplanation: 'p', reset: 'u', copy: 'c',
    toggleAutoEvidence: 'e', toggleAutoExplanation: 'p',
    pauseReader: 'p', openVoiceReader: 'a', toggleAgent: 'b', regenerateQuestion: 'r' // copy/auto toggles use ctrl/cmd; reader/toggle agent use shift
};
const SHORTCUT_STORAGE_KEY = 'customShortcuts';
const SHORTCUT_MODIFIER_BY_ACTION = Object.freeze({
    copy: 'mod',
    toggleAutoEvidence: 'mod',
    toggleAutoExplanation: 'mod',
    pauseReader: 'shift',
    openVoiceReader: 'shift',
    toggleAgent: 'shift',
    regenerateQuestion: 'shift'
});
let customShortcuts = loadCustomShortcutsFromStorage();

function getShortcutModifier(action) {
    return SHORTCUT_MODIFIER_BY_ACTION[action] || 'none';
}

function loadCustomShortcutsFromStorage() {
    try {
        const raw = JSON.parse(localStorage.getItem(SHORTCUT_STORAGE_KEY) || '{}');
        if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {};
        const normalizeKey = (rawKey) => {
            const key = String(rawKey || '').trim();
            if (!key) return '';
            const lower = key.toLowerCase();
            const aliases = {
                right: 'arrowright',
                left: 'arrowleft',
                up: 'arrowup',
                down: 'arrowdown',
                esc: 'escape',
                space: ' '
            };
            return aliases[lower] || lower;
        };

        // Resolve legacy duplicates on load so only one non-modified action owns each key.
        // Latest entry from storage wins and conflicting previous action becomes unbound.
        const resolved = {};
        let changed = false;
        Object.entries(raw).forEach(([action, rawValue]) => {
            if (!Object.prototype.hasOwnProperty.call(defaultShortcuts, action)) return;
            const value = typeof rawValue === 'string' ? rawValue : '';
            const normalizedTarget = normalizeKey(value);
            if (!normalizedTarget) {
                resolved[action] = '';
                if (value !== '') changed = true;
                return;
            }

            Object.keys(defaultShortcuts).forEach((otherAction) => {
                if (otherAction === action) return;
                if (getShortcutModifier(otherAction) !== getShortcutModifier(action)) return;
                const otherValue = Object.prototype.hasOwnProperty.call(resolved, otherAction)
                    ? resolved[otherAction]
                    : defaultShortcuts[otherAction];
                if (normalizeKey(otherValue) !== normalizedTarget) return;
                if (resolved[otherAction] !== '') changed = true;
                resolved[otherAction] = '';
            });

            if (resolved[action] !== value) changed = true;
            resolved[action] = value;
        });

        if (changed) {
            localStorage.setItem(SHORTCUT_STORAGE_KEY, JSON.stringify(resolved));
        }
        return resolved;
    } catch (_err) {
        return {};
    }
}

function persistCustomShortcuts() {
    localStorage.setItem(SHORTCUT_STORAGE_KEY, JSON.stringify(customShortcuts));
}

// Get display name for a key
function getKeyDisplayName(key) {
    const names = {
        'ArrowRight': '→', 'ArrowLeft': '←', 'ArrowUp': '↑', 'ArrowDown': '↓',
        'Enter': '↵', 'Escape': 'Esc', ' ': 'Space', 'Backspace': '⌫'
    };
    return names[key] || key.toUpperCase();
}

// Get the active shortcut for an action
function getShortcut(action) {
    if (Object.prototype.hasOwnProperty.call(customShortcuts, action)) {
        return typeof customShortcuts[action] === 'string' ? customShortcuts[action] : '';
    }
    return defaultShortcuts[action];
}

function normalizeShortcutKeyName(rawKey) {
    // The Space key's value is ' ', which trimming would erase.
    if (rawKey === ' ') return ' ';
    const key = String(rawKey || '').trim();
    if (!key) return '';
    const lower = key.toLowerCase();
    // A modifier alone is never a binding: Shift+Shift fired on every Shift press.
    if (['shift', 'control', 'alt', 'altgraph', 'meta', 'os', 'capslock'].includes(lower)) return '';
    const aliases = {
        right: 'arrowright',
        left: 'arrowleft',
        up: 'arrowup',
        down: 'arrowdown',
        esc: 'escape',
        space: ' '
    };
    if (aliases[lower]) return aliases[lower];
    return key.length === 1 ? lower : lower;
}

function matchesShortcutKeyEvent(event, configuredKey) {
    const expected = normalizeShortcutKeyName(configuredKey);
    if (!expected) return false;
    const actual = normalizeShortcutKeyName(event?.key);
    return actual === expected;
}

function setShortcutForAction(action, key) {
    if (!action || !Object.prototype.hasOwnProperty.call(defaultShortcuts, action)) return;
    const normalizedTarget = normalizeShortcutKeyName(key);
    if (!normalizedTarget) return;

    // Latest assignment wins: unbind any previous action using the same key.
    Object.keys(defaultShortcuts).forEach((otherAction) => {
        if (otherAction === action) return;
        if (getShortcutModifier(otherAction) !== getShortcutModifier(action)) return;
        if (normalizeShortcutKeyName(getShortcut(otherAction)) !== normalizedTarget) return;
        customShortcuts[otherAction] = '';
    });

    customShortcuts[action] = key;
}

// Toggle edit mode for shortcuts
window.toggleShortcutEditMode = function () {
    shortcutEditMode = !shortcutEditMode;
    const btn = $('modify-shortcuts-btn');
    const hint = $('shortcut-edit-hint');
    const keyTags = document.querySelectorAll('#shortcuts-panel .key-tag[data-action]');
    const label = btn?.querySelector('.gradient-text');

    if (shortcutEditMode) {
        btn.querySelector('.gradient-text').innerText = '&#10003; Done';
        hint.style.display = 'block';
        keyTags.forEach(tag => {
            tag.style.cursor = 'pointer';
            tag.classList.add('editable');
            // Keyboard users reach each binding with Tab and pick it with Enter/Space.
            tag.setAttribute('tabindex', '0');
            tag.setAttribute('role', 'button');
            const rowLabel = tag.closest('.key-row')?.querySelector('span')?.textContent?.trim() || tag.dataset.action;
            tag.setAttribute('aria-label', `Change shortcut for ${rowLabel}, currently ${tag.innerText.trim()}`);
        });
    } else {
        btn.querySelector('.gradient-text').innerText = '✏️ Modify';
        hint.style.display = 'none';
        keyTags.forEach(tag => {
            tag.style.cursor = '';
            tag.classList.remove('editable', 'editing');
            tag.removeAttribute('tabindex');
            tag.removeAttribute('role');
            tag.removeAttribute('aria-label');
        });
        editingKeyTag = null;
        persistCustomShortcuts();
    }
    if (label) label.textContent = shortcutEditMode ? 'Done' : 'Modify';
};

// Handle key tag clicks in edit mode
document.addEventListener('click', (e) => {
    const keyTag = e.target.closest('.key-tag[data-action]');
    if (keyTag && shortcutEditMode) {
        e.stopPropagation();
        // Remove editing class from previous
        if (editingKeyTag) editingKeyTag.classList.remove('editing');
        // Highlight this one
        keyTag.classList.add('editing');
        editingKeyTag = keyTag;
    }
});

// Handle key press to assign new shortcut
document.addEventListener('keydown', (e) => {
    if (typeof window.shouldSuppressMemoraGlobalShortcuts === 'function' && window.shouldSuppressMemoraGlobalShortcuts(e)) {
        return;
    }
    if (shortcutEditMode && !editingKeyTag && (e.key === 'Enter' || e.key === ' ')) {
        const focusedTag = e.target?.closest?.('#shortcuts-panel .key-tag[data-action].editable');
        if (focusedTag) {
            e.preventDefault();
            e.stopImmediatePropagation();
            focusedTag.classList.add('editing');
            editingKeyTag = focusedTag;
            return;
        }
    }
    if (editingKeyTag && shortcutEditMode) {
        // Tab still moves focus and Escape cancels; neither becomes a binding.
        if (e.key === 'Tab' || e.key === 'Escape') {
            if (e.key === 'Escape') {
                e.preventDefault();
                e.stopImmediatePropagation();
            }
            editingKeyTag.classList.remove('editing');
            editingKeyTag = null;
            return;
        }
        e.preventDefault();
        e.stopImmediatePropagation();
        const action = editingKeyTag.dataset.action;
        const key = e.key;
        // Shift on the way to Shift+V keeps the editor waiting for the real key.
        if (!normalizeShortcutKeyName(key)) return;

        setShortcutForAction(action, key);
        updateShortcutDisplays();
        persistCustomShortcuts();
        editingKeyTag.classList.remove('editing');
        editingKeyTag = null;
    }
});

// Update key-tag displays on load with custom shortcuts
function updateShortcutDisplays() {
    document.querySelectorAll('#shortcuts-panel .key-tag[data-action]').forEach(tag => {
        const action = tag.dataset.action;
        // A bare modifier saved by an older editor is not a usable binding.
        const key = normalizeShortcutKeyName(getShortcut(action)) ? getShortcut(action) : '';
        const modifier = getShortcutModifier(action);
        if (modifier === 'mod') {
            tag.innerText = key ? `Ctrl+${getKeyDisplayName(key)}` : '-';
            return;
        }
        if (modifier === 'shift') {
            tag.innerText = key ? `Shift+${getKeyDisplayName(key)}` : '-';
            return;
        }
        tag.innerText = key ? getKeyDisplayName(key) : '-';
    });
    window.syncQuizRegenerateButtonState?.();
}

function matchesShortcutActionEvent(event, action) {
    if (!event || !action || !Object.prototype.hasOwnProperty.call(defaultShortcuts, action)) return false;
    const modifier = getShortcutModifier(action);
    if (modifier === 'mod') {
        if (!(event.ctrlKey || event.metaKey) || event.altKey || event.shiftKey) return false;
    } else if (modifier === 'shift') {
        if (!event.shiftKey || event.ctrlKey || event.metaKey || event.altKey) return false;
    } else if (event.ctrlKey || event.metaKey || event.altKey || event.shiftKey) {
        return false;
    }
    return matchesShortcutKeyEvent(event, getShortcut(action));
}

window.getShortcutForAction = getShortcut;
window.matchesShortcutActionEvent = matchesShortcutActionEvent;
// Call on page load
setTimeout(updateShortcutDisplays, 100);

function openVoiceReaderCardShortcutAction() {
    if (typeof window.isAccessibilityVoiceAgentEnabled === 'function' && !window.isAccessibilityVoiceAgentEnabled()) {
        return false;
    }
    if (typeof window.openVoiceAgentModal === 'function') {
        window.openVoiceAgentModal({
            focusInput: false,
            anchorToBrand: true
        });
        return true;
    }
    const modal = $('voice-agent-modal');
    if (!modal) return false;
    modal.classList.add('active');
    return true;
}

function showQuiz() {
    if ((!Array.isArray(questions) || questions.length === 0 || !Array.isArray(filteredIndices) || filteredIndices.length === 0)
        && shouldOfferStudyDashboard()
        && !isStudyDashboardVisible()) {
        if (els.fcContainer) els.fcContainer.style.display = 'none';
        if (els.quizContainer) els.quizContainer.style.display = 'none';
        showStudyDashboard({ keepBackground: false });
        return;
    }

    const hasMultiPaneLayout = els.workArea.classList.contains('split-view')
        || els.workArea.classList.contains('split-view-ai')
        || els.workArea.classList.contains('trio-view');
    const quizCardPosition = getComputedStyle(els.quizCard).position;
    const hasDetachedGeometry = !els.quizCard.classList.contains('is-floating')
        && (quizCardPosition === 'fixed' || quizCardPosition === 'absolute');
    if (!hasMultiPaneLayout && !isQuizCardExpanded && (els.quizCard.classList.contains('is-floating') || hasDetachedGeometry)) {
        resetQuizCardPosition();
    }
    hideStudyPlannerSurfaces();
    els.fcContainer.style.display = 'none';
    els.quizCard.style.display = 'block';
    els.quizContainer.style.display = 'flex';
    els.quizContent.style.display = 'block';
    els.resultsView.style.display = 'none';
    if (!isQuizCardExpanded && typeof restoreQuizCardGeometryForCurrentLayout === 'function') {
        restoreQuizCardGeometryForCurrentLayout();
        requestAnimationFrame(() => restoreQuizCardGeometryForCurrentLayout());
    }
    renderQuestion();
}
function syncQuizReviewCardsControlVisibility() {
    const button = els.quizUnknownBtn;
    if (!button) return false;
    const shouldHide = isDedicatedLearnFlashcardPool();
    button.hidden = shouldHide;
    // Shared button styles specify display, overriding the browser's hidden rule.
    if (shouldHide) button.style.setProperty('display', 'none', 'important');
    else button.style.removeProperty('display');
    button.setAttribute('aria-hidden', shouldHide ? 'true' : 'false');
    return true;
}

function showFlashcards() {
    if (els.popup.classList.contains('active')) {
        els.popup.classList.remove('active');
        currentPopupBtn = null;
    }
    if (!isFlashcardAdaptiveReviewMode && flashcardReviewScope !== 'all') {
        isFlashcardAdaptiveReviewMode = true;
        isFlashcardMode = true;
        admitFlashcardDueQueue();
        isFilterActive = true;
        currentQIndex = 0;
        fcPage = 0;
        syncAdaptiveModeUI();
    }
    isFlashcardMode = true;
    setModeToggleButtonLabel(true);
    hideStudyPlannerSurfaces();
    els.quizContainer.style.display = 'none';
    els.fcContainer.style.display = 'block';
    renderFlashcardGrid();
}

// Image Lightbox for enlarged view
function openImageLightbox(imgSrc, options = {}) {
    // Enlarged images open in the shared source viewer (app.source-viewer.js), with zoom and
    // Ask about a highlighted area. The plain lightbox below is only its fallback.
    if (imgSrc && window.MemoraSourceViewer?.open) {
        const handle = window.MemoraSourceViewer.open({
            source: { kind: 'image', url: imgSrc, name: options.title || 'Image', alt: options.alt || '', attribution: options.attribution || null },
            origin: { surface: options.surface || 'image', opener: options.opener || (document.activeElement instanceof HTMLElement ? document.activeElement : null) }
        });
        if (handle) return;
    }
    let lightbox = $('image-lightbox');
    if (!lightbox) {
        lightbox = document.createElement('div');
        lightbox.id = 'image-lightbox';
        lightbox.className = 'image-lightbox';
        lightbox.innerHTML = '<img src="" alt="Enlarged view">';
        lightbox.onclick = closeImageLightbox;
        document.body.appendChild(lightbox);
    }
    lightbox.querySelector('img').src = imgSrc;
    // Trigger reflow for animation
    void lightbox.offsetWidth;
    lightbox.classList.add('active');
    // Prevent body scroll
    document.body.style.overflow = 'hidden';
}

function closeImageLightbox() {
    const lightbox = $('image-lightbox');
    if (lightbox) {
        lightbox.classList.remove('active');
        document.body.style.overflow = '';
    }
}

// Close lightbox on Escape key
document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') closeImageLightbox();
});

/* --- FLASHCARD LOGIC --- */
const FLASHCARD_UNDO_LIMIT = 20;
let flashcardUndoStack = [];
// Disposable display state; the native question/SRS stores remain authoritative.
let flashcardDisplayMode = 'single';
let flashcardDisplayContext = '';
let flashcardDisplayAnchor = '';
const flashcardRevealedKeys = new Set();

function getFlashcardDisplayKey(realIdx) {
    return buildQuestionPersistenceKeyByIndex(realIdx);
}

function selectFlashcardDisplayCard(realIdx) {
    const position = filteredIndices.indexOf(realIdx);
    if (position < 0) return false;
    currentQIndex = position;
    flashcardDisplayAnchor = getFlashcardDisplayKey(realIdx);
    const segments = $('fc-segments');
    if (segments?.children) Array.from(segments.children).forEach((segment, bucket) => {
        const first = Math.floor(bucket * filteredIndices.length / segments.children.length);
        const last = Math.floor((bucket + 1) * filteredIndices.length / segments.children.length);
        segment.classList.toggle('is-current', position >= first && position < last);
    });
    return true;
}

/* The app moves focus to the card face after a flip or an arrow step. That focus is not the
   learner tabbing, so it draws no ring; the ring returns once focus leaves and comes back. */
function focusFlashcardFace(node) {
    if (!node) return;
    node.dataset.fcQuietFocus = "true";
    node.addEventListener("blur", () => delete node.dataset.fcQuietFocus, { once: true });
    node.focus({ preventScroll: true, focusVisible: false });
}

function toggleFlashcardReveal(wrapper) {
    const realIdx = Number(wrapper.dataset.fcIndex);
    if (!selectFlashcardDisplayCard(realIdx)) return;
    const revealed = !wrapper.classList.contains('flipped');
    wrapper.classList.toggle('flipped', revealed);
    if (revealed) flashcardRevealedKeys.add(flashcardDisplayAnchor);
    else flashcardRevealedKeys.delete(flashcardDisplayAnchor);
    wrapper.querySelectorAll('.fc-reveal').forEach(node => node.setAttribute('aria-expanded', String(revealed)));
    const reveal = wrapper.querySelector(revealed ? '.flashcard-back .fc-reveal' : '.flashcard-front .fc-reveal');
    focusFlashcardFace(reveal);
}

/* A Learn flashcard deck grows batch by batch while its generation job runs; its counter shimmers
   like the quiz counter does for a streaming quiz. */
function isOpenFlashcardDeckGenerating() {
    const jobs = window.MemoraGenerationQueue?.jobs;
    if (!jobs?.list || !questions.length) return false;
    // A running job names only its lecture; its deck key is recorded when it settles.
    const deckSources = new Set(questions.filter((q) => q?.flashcardOnly).map((q) => String(q.src || '')).filter(Boolean));
    if (!deckSources.size) return false;
    return jobs.list().some((job) => job?.mode === 'learn-flashcards' && !jobs.isTerminal?.(job.status)
        && (!job.sessionId || String(job.sessionId) === String(currentSessionId || ''))
        && (job.sources || []).some((source) => deckSources.has(String(source?.name || source || ''))));
}

function syncFlashcardGenerationShimmer() {
    if (!els.fcInfo) return;
    const generating = questions.length > 0
        && ((isAnyQuizGenerationActive() && isOpenQuizStillGenerating()) || isOpenFlashcardDeckGenerating() || !!window.isAutoFlashcardMoreGenerating?.());
    els.fcInfo.classList.toggle('memora-selected-generation-shimmer', generating);
}
window.addEventListener('memora:generation-job-change', (event) => {
    if (event.detail?.job?.mode === 'learn-flashcards' && isFlashcardMode) syncFlashcardGenerationShimmer();
});
window.addEventListener('memora:auto-flashcards-more', () => {
    if (!isFlashcardMode) return;
    syncFlashcardGenerationShimmer();
    // The writing note on a finished pass becomes Generate more again once the set lands.
    if (flashcardPassComplete && els.fcGrid.querySelector('.fc-pass-actions')) renderFlashcardGrid();
});

/* The Source chip previews the cited slide as a small thumbnail on hover or focus, and a click
   opens that page in the shared source viewer, leaving the review and the workspace untouched. */
let flashcardSourcePeek = null;

function getFlashcardSourceTarget(realIdx) {
    const q = questions[realIdx];
    const page = getQuestionCitationInfo(q).page;
    const pdfName = page ? findBestPdfForQuestion(q) : null;
    return page && pdfName && loadedPdfFiles?.[pdfName] ? { pdfName, page } : null;
}

function hideFlashcardSourcePeek() {
    if (!flashcardSourcePeek) return;
    flashcardSourcePeek.token += 1;
    flashcardSourcePeek.node.classList.remove('is-visible');
}

function showFlashcardSourcePeek(button, target) {
    if (!target || !button?.isConnected) return;
    if (!flashcardSourcePeek) {
        const node = document.createElement('div');
        node.className = 'fc-source-peek';
        node.setAttribute('aria-hidden', 'true');
        document.body.appendChild(node);
        flashcardSourcePeek = { node, token: 0 };
    }
    const peek = flashcardSourcePeek;
    const token = ++peek.token;
    const canRender = typeof renderPdfTutorPageImage === 'function';
    peek.node.innerHTML = `<span class="fc-source-peek-frame">${canRender ? '<span class="memora-thumb-standin"></span>' : ''}</span><span class="fc-source-peek-caption">Slide ${target.page} · ${escapeHtml(target.pdfName)}</span>`;
    peek.node.classList.toggle('is-caption-only', !canRender);
    const place = () => {
        const rect = button.getBoundingClientRect();
        const width = peek.node.offsetWidth || 260;
        const height = peek.node.offsetHeight || 190;
        const top = rect.top - height - 10;
        peek.node.style.left = `${Math.max(8, Math.min(window.innerWidth - width - 8, rect.left))}px`;
        peek.node.style.top = `${top >= 8 ? top : rect.bottom + 10}px`;
    };
    place();
    peek.node.classList.add('is-visible');
    if (!canRender) return;
    Promise.resolve(renderPdfTutorPageImage(target.pdfName, target.page, { maxWidth: 360, quality: 0.6, cache: true }))
        .catch(() => '')
        .then((image) => {
            if (token !== peek.token) return;
            const frame = peek.node.querySelector('.fc-source-peek-frame');
            if (frame) frame.innerHTML = image ? `<img src="${image}" alt="">` : '<span class="fc-source-peek-empty">Preview unavailable</span>';
            place();
        });
}

function openFlashcardSource(button, target) {
    hideFlashcardSourcePeek();
    if (target && window.MemoraSourceViewer?.open) {
        const handle = window.MemoraSourceViewer.open({
            source: { name: target.pdfName, page: target.page, title: target.pdfName },
            origin: { surface: 'flashcards', opener: button }
        });
        if (handle) return true;
    }
    return triggerEvidence({ preferDuoPdfPane: true, minimizeDockedAsk: true });
}

function bindFlashcardSourceButtons(wrapper, realIdx, key) {
    wrapper.querySelectorAll('[data-fc-source]').forEach((button) => {
        const target = () => getFlashcardSourceTarget(realIdx);
        button.addEventListener('pointerenter', () => showFlashcardSourcePeek(button, target()));
        button.addEventListener('focus', () => { if (button.matches(':focus-visible')) showFlashcardSourcePeek(button, target()); });
        button.addEventListener('pointerleave', hideFlashcardSourcePeek);
        button.addEventListener('blur', hideFlashcardSourcePeek);
        button.addEventListener('click', (event) => {
            event.stopPropagation();
            if (getFlashcardDisplayKey(realIdx) !== key || !selectFlashcardDisplayCard(realIdx)) return;
            openFlashcardSource(button, target());
        });
    });
}

/* Card editing and deletion change the stored deck, not a display copy. Card state is keyed by
   card content, so an edit keeps the card's index (and its rating), while a deletion rebuilds the
   deck and carries pass ratings and the undo stack across by key. Deleted cards wait in a
   per-session trash that the Learn Actions menu lists with Restore; Z undoes the last deletion. */
const FLASHCARD_EDIT_ICON = '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M4 20h4L18.6 9.4a2.1 2.1 0 0 0-3-3L5 17v3"/><path d="m14 7.9 3 3"/></svg>';
const FLASHCARD_DELETE_ICON = '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M4.5 7h15"/><path d="M9.5 7V4.8c0-.4.4-.8.8-.8h3.4c.4 0 .8.4.8.8V7"/><path d="m6.5 7 .9 12.1c.1 1 .9 1.9 2 1.9h5.2c1.1 0 1.9-.9 2-1.9L17.5 7"/><path d="M10.2 11v6M13.8 11v6"/></svg>';
const FLASHCARD_TRASH_PREFIX = 'flashcard_trash_v1::';
const FLASHCARD_TRASH_LIMIT = 60;
let flashcardTrash = { sessionId: null, items: [], loading: null };
// The Learn surface re-renders the deck on background ticks; an open editor is a draft that
// survives them, with its text, focus and caret.
let flashcardEditDraft = null;
/* Escape in the editor cancels the edit. Claimed at window capture, which core registers first,
   because the Tutor's own Escape handling would otherwise close Flashcards around the editor. */
window.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape' || !flashcardEditDraft || !event.target?.closest?.('.fc-editor')) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    flashcardEditDraft = null;
    renderFlashcardGrid();
}, true);

function getFlashcardAnswerText(q) {
    return (Array.isArray(q?.c) && Number.isInteger(q?.a) ? (q.c[q.a] || '') : '')
        || (typeof q?.answer === 'string' ? q.answer : '');
}

function loadFlashcardTrash() {
    const sessionId = String(currentSessionId || '');
    if (flashcardTrash.sessionId === sessionId) return Promise.resolve(flashcardTrash.loading || flashcardTrash.items);
    const trash = { sessionId, items: [], loading: null };
    flashcardTrash = trash;
    if (!sessionId || typeof IDB === 'undefined') return Promise.resolve(trash.items);
    trash.loading = Promise.resolve()
        .then(() => IDB.get('settings', `${FLASHCARD_TRASH_PREFIX}${sessionId}`))
        .catch(() => null)
        .then((stored) => {
            // A deletion made while the stored trash was loading is kept ahead of it.
            if (Array.isArray(stored)) trash.items = trash.items.concat(stored.filter((item) => item?.id && item.card && item.setKey));
            trash.loading = null;
            if (flashcardTrash === trash) syncFlashcardTrashMenu();
            return trash.items;
        });
    return trash.loading;
}

function persistFlashcardTrash() {
    const { sessionId, items } = flashcardTrash;
    syncFlashcardTrashMenu();
    if (!sessionId || typeof IDB === 'undefined') return;
    Promise.resolve()
        .then(() => IDB.put('settings', `${FLASHCARD_TRASH_PREFIX}${sessionId}`, items))
        .catch((error) => console.warn('Deleted flashcards could not be saved:', error));
}

// The Learn header's Actions menu is built from #pdf-tutor-flashcards-menu, so the trash lives there
// as hidden Restore buttons the menu lists under Deleted cards.
function syncFlashcardTrashMenu() {
    const menu = document.getElementById('pdf-tutor-flashcards-menu');
    if (!menu) return;
    let tray = menu.querySelector(':scope > .pdf-tutor-flashcards-trash');
    if (!tray) {
        tray = document.createElement('div');
        tray.className = 'pdf-tutor-flashcards-trash';
        tray.hidden = true;
        menu.appendChild(tray);
    }
    const items = flashcardTrash.sessionId === String(currentSessionId || '')
        ? flashcardTrash.items.filter((item) => Array.isArray(loadedQuestionSets?.[item.setKey]))
        : [];
    const markup = items.map((item) => `<button type="button" id="fc-trash-restore-${escapeHtml(item.id)}" data-fc-trash-restore="${escapeHtml(item.id)}" data-fc-trash-front="${escapeHtml(item.front || 'Untitled card')}">Restore</button>`).join('');
    if (tray._fcMarkup === markup) return;
    tray._fcMarkup = markup;
    tray.innerHTML = markup;
    tray.querySelectorAll('[data-fc-trash-restore]').forEach((button) => {
        button.onclick = () => restoreDeletedFlashcard(button.dataset.fcTrashRestore);
    });
}

// The stored card behind questions[realIdx]: its set, that set's array and the card's place in it.
function findFlashcardSourceEntry(realIdx) {
    const key = buildQuestionPersistenceKeyByIndex(realIdx);
    if (!key) return null;
    for (const manifestKey of sessionManifest?.jsons || []) {
        const stripped = String(manifestKey).replace(/\.(json|txt)$/i, '');
        const aliases = Array.from(new Set([manifestKey, stripped].filter(Boolean)));
        if (!aliases.some((alias) => activeQuestionSetKeys.has(alias))) continue;
        const setKey = aliases.find((alias) => Array.isArray(loadedQuestionSets[alias]));
        if (!setKey) continue;
        const set = loadedQuestionSets[setKey];
        const index = set.findIndex((entry) => entry === questions[realIdx]
            || buildQuestionPersistenceKeyFromQuestion(normalizeLegacyQuizQuestion(entry, entry?.sourceFile || entry?.src || '')) === key);
        if (index >= 0) return { setKey, set, index, key };
    }
    return null;
}

function persistFlashcardSourceSet(setKey, set) {
    loadedQuestionSets[setKey] = set;
    if (typeof syncLoadedQuestionSetAliases === 'function') syncLoadedQuestionSetAliases(setKey, set);
    if (typeof IDB === 'undefined') return;
    Promise.resolve()
        .then(() => IDB.put('questions', setKey, set))
        .catch((error) => console.warn(`Flashcard deck ${setKey} could not be saved:`, error));
}

// Rebuilds the open deck after a card leaves or returns, re-pointing index-keyed review state.
function rebuildFlashcardDeckByKey() {
    const keyOf = (idx) => buildQuestionPersistenceKeyByIndex(idx);
    const passByKey = [...flashcardPassRatings].map(([idx, rating]) => [keyOf(idx), rating]);
    const undoByKey = flashcardUndoStack.map((entry) => ({
        entry,
        key: Number.isInteger(entry.qIdx) ? keyOf(entry.qIdx) : '',
        filtered: (entry.filteredIndices || []).map(keyOf)
    }));
    rebuildActiveQuestions(false);
    const indexByKey = new Map();
    questions.forEach((q, idx) => {
        const key = buildQuestionPersistenceKeyFromQuestion(q);
        if (key && !indexByKey.has(key)) indexByKey.set(key, idx);
    });
    flashcardPassRatings = new Map(passByKey.filter(([key]) => indexByKey.has(key)).map(([key, rating]) => [indexByKey.get(key), rating]));
    undoByKey.forEach(({ entry, key, filtered }) => {
        if (entry.type !== 'delete') entry.qIdx = indexByKey.has(key) ? indexByKey.get(key) : -1;
        entry.filteredIndices = filtered.filter((item) => indexByKey.has(item)).map((item) => indexByKey.get(item));
    });
    flashcardUndoStack = flashcardUndoStack.filter((entry) => entry.type === 'delete' || entry.qIdx >= 0);
    return indexByKey;
}

async function deleteFlashcard(realIdx, key) {
    await loadFlashcardTrash();
    if (getFlashcardDisplayKey(realIdx) !== key) return false;
    const source = findFlashcardSourceEntry(realIdx);
    if (!source) {
        showPopup('This card could not be found in its deck.', null, true);
        return false;
    }
    const card = source.set[source.index];
    const position = filteredIndices.indexOf(realIdx);
    const item = {
        id: `fc${Date.now().toString(36)}${Math.random().toString(36).slice(2, 7)}`,
        setKey: source.setKey,
        index: source.index,
        card,
        front: String(card?.q || card?.question || '').replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 160),
        deletedAt: Date.now()
    };
    const undoEntry = {
        type: 'delete',
        trashId: item.id,
        knowledge: knowledgeState[realIdx],
        srs: srsData[realIdx] ? JSON.parse(JSON.stringify(srsData[realIdx])) : null,
        passRating: flashcardPassRatings.get(realIdx),
        position
    };
    source.set.splice(source.index, 1);
    persistFlashcardSourceSet(source.setKey, source.set);
    flashcardRevealedKeys.delete(key);
    flashcardTrash.items = [item, ...flashcardTrash.items].slice(0, FLASHCARD_TRASH_LIMIT);
    persistFlashcardTrash();
    rebuildFlashcardDeckByKey();
    currentQIndex = Math.max(0, Math.min(position >= 0 ? position : currentQIndex, filteredIndices.length - 1));
    flashcardDisplayAnchor = Number.isInteger(filteredIndices[currentQIndex]) ? getFlashcardDisplayKey(filteredIndices[currentQIndex]) : '';
    flashcardUndoStack.push(undoEntry);
    if (flashcardUndoStack.length > FLASHCARD_UNDO_LIMIT) flashcardUndoStack.shift();
    markSessionStateMutation();
    saveSessionDebounced();
    renderFlashcardGrid();
    showPopup('Card deleted. Press Z to undo.', null, false);
    return true;
}

// Restore from the Actions menu brings a card back unrated; undo puts back the state it had.
function restoreDeletedFlashcard(trashId, undoEntry = null) {
    const item = flashcardTrash.items.find((entry) => entry.id === trashId);
    const set = item ? loadedQuestionSets[item.setKey] : null;
    if (!item || !Array.isArray(set)) {
        showPopup('That card is not in a deck open in this session.', null, true);
        return false;
    }
    set.splice(Math.min(Math.max(0, Number(item.index) || 0), set.length), 0, item.card);
    persistFlashcardSourceSet(item.setKey, set);
    flashcardTrash.items = flashcardTrash.items.filter((entry) => entry !== item);
    persistFlashcardTrash();
    if (!undoEntry) flashcardUndoStack = flashcardUndoStack.filter((entry) => entry.trashId !== trashId);
    const lookingAt = currentQIndex;
    const indexByKey = rebuildFlashcardDeckByKey();
    const realIdx = indexByKey.get(buildQuestionPersistenceKeyFromQuestion(normalizeLegacyQuizQuestion(item.card, item.card?.sourceFile || item.card?.src || '')));
    if (Number.isInteger(realIdx)) {
        if (undoEntry && undoEntry.knowledge !== undefined) knowledgeState[realIdx] = undoEntry.knowledge;
        else delete knowledgeState[realIdx];
        if (undoEntry?.srs) srsData[realIdx] = undoEntry.srs;
        else delete srsData[realIdx];
        if (undoEntry && undoEntry.passRating !== undefined) flashcardPassRatings.set(realIdx, undoEntry.passRating);
        else flashcardPassRatings.delete(realIdx);
        if (!undoEntry) delete userAnswers[realIdx];
        // The card returns where the student is looking, so the restore is seen.
        const at = Math.min(filteredIndices.filter((idx) => idx !== realIdx).length,
            Math.max(0, undoEntry && undoEntry.position >= 0 ? undoEntry.position : lookingAt));
        filteredIndices = filteredIndices.filter((idx) => idx !== realIdx);
        filteredIndices.splice(at, 0, realIdx);
        currentQIndex = at;
        flashcardPassComplete = false;
        flashcardDisplayAnchor = getFlashcardDisplayKey(realIdx);
    }
    markSessionStateMutation();
    saveSessionDebounced();
    renderFlashcardGrid();
    showPopup(undoEntry ? 'Card restored.' : 'Card restored as new.', null, false);
    return true;
}

function saveFlashcardEdit(realIdx, key, frontText, backText, imageEdit = null) {
    const front = tidyFlashcardText(frontText, { front: true });
    const back = tidyFlashcardText(backText);
    if (!front || !back) {
        showPopup('A card needs both a front and a back.', null, true);
        return false;
    }
    if (getFlashcardDisplayKey(realIdx) !== key) return false;
    const source = findFlashcardSourceEntry(realIdx);
    const apply = (card) => {
        if (!card || typeof card !== 'object') return;
        if (!card.q && typeof card.question === 'string') card.question = front;
        else card.q = front;
        if (card.flashcardFront !== undefined) card.flashcardFront = front;
        if (Array.isArray(card.c) && Number.isInteger(card.a) && card.a >= 0 && card.a < card.c.length) card.c[card.a] = back;
        else card.answer = back;
        if (card.flashcardBack !== undefined) card.flashcardBack = back;
        if (imageEdit?.changed) {
            delete card.flashcardImageRegion;
            if (imageEdit.image) {
                card.flashcardImage = imageEdit.image;
                card.flashcardImageAlt = imageEdit.alt || 'Image added to this card';
                card.flashcardImageSource = 'user';
            } else {
                delete card.flashcardImage;
                delete card.flashcardImageAlt;
                delete card.flashcardImageSource;
            }
        }
    };
    if (source) {
        apply(source.set[source.index]);
        persistFlashcardSourceSet(source.setKey, source.set);
    }
    apply(questions[realIdx]);
    const nextKey = getFlashcardDisplayKey(realIdx);
    if (flashcardRevealedKeys.delete(key)) flashcardRevealedKeys.add(nextKey);
    if (flashcardDisplayAnchor === key) flashcardDisplayAnchor = nextKey;
    markSessionStateMutation();
    saveSessionDebounced();
    flashcardEditDraft = null;
    renderFlashcardGrid();
    return true;
}

function openFlashcardEditor(wrapper, realIdx, key, { focus = true } = {}) {
    const q = questions[realIdx];
    const face = wrapper.querySelector('.flashcard-front');
    if (!q || !face) return;
    if (flashcardEditDraft?.key !== key) {
        const image = !q.flashcardImageRegion || q.flashcardImageSource === 'user' ? String(q.flashcardImage || '') : '';
        flashcardEditDraft = { key, front: String(q.q || q.question || ''), back: getFlashcardAnswerText(q), image, imageAlt: q.flashcardImageAlt || '', imageChanged: false, imagePending: false, field: 'front', start: null, end: null };
    }
    const draft = flashcardEditDraft;
    wrapper.classList.remove('flipped');
    wrapper.classList.add('is-editing');
    face.innerHTML = `<form class="fc-editor" novalidate aria-label="Edit card">
        <div class="fc-card-meta"><span class="fc-card-index">Edit card</span></div>
        <label class="fc-editor-field"><span>Front</span><textarea name="front" rows="3" maxlength="600"></textarea></label>
        <label class="fc-editor-field"><span>Back</span><textarea name="back" rows="5" maxlength="1600"></textarea></label>
        <div class="fc-editor-image-picker"><span>Image (optional)</span><button type="button" class="fc-chip" data-fc-image-add>Add image</button><input type="file" name="image" accept="image/png,image/jpeg,image/webp" hidden></div>
        <div class="fc-editor-image"><img alt="Card image preview"><button type="button" class="fc-chip" data-fc-image-remove>Remove image</button></div>
        <div class="fc-editor-actions"><span class="fc-editor-hint">Ctrl+Enter to save · Esc to cancel</span><button type="button" class="fc-chip" data-fc-edit-cancel>Cancel</button><button type="submit" class="fc-chip is-primary">Save</button></div>
    </form>`;
    const form = face.querySelector('form');
    form.elements.front.value = draft.front;
    form.elements.back.value = draft.back;
    const imagePreview = form.querySelector('.fc-editor-image');
    const refreshImage = () => {
        imagePreview.hidden = !draft.image;
        const preview = imagePreview.querySelector('img');
        if (draft.image) preview.src = draft.image;
        else preview.removeAttribute('src');
        form.querySelector('[type="submit"]').disabled = !!draft.imagePending;
        const addImage = form.querySelector('[data-fc-image-add]');
        addImage.textContent = draft.image ? 'Change image' : 'Add image';
        addImage.disabled = !!draft.imagePending;
    };
    refreshImage();
    form.querySelector('[data-fc-image-add]').onclick = () => form.elements.image.click();
    form.querySelector('[data-fc-image-remove]').onclick = () => {
        draft.imageVersion = (draft.imageVersion || 0) + 1;
        draft.image = '';
        draft.imageAlt = '';
        draft.imageChanged = true;
        draft.imagePending = false;
        refreshImage();
        form.querySelector('[data-fc-image-add]').focus();
    };
    form.elements.image.onchange = async () => {
        const file = form.elements.image.files?.[0];
        if (!file) return;
        if (!/^image\/(?:png|jpeg|webp)$/.test(file.type)) {
            showPopup('Choose a PNG, JPEG, or WebP image.', null, true);
            return;
        }
        const version = (draft.imageVersion || 0) + 1;
        draft.imageVersion = version;
        draft.imagePending = true;
        refreshImage();
        try {
            const data = await new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = () => resolve(reader.result);
                reader.onerror = () => reject(reader.error || new Error('Image could not be read.'));
                reader.readAsDataURL(file);
            });
            const image = new Image();
            image.src = data;
            await image.decode();
            if (flashcardEditDraft !== draft || draft.imageVersion !== version) return;
            const scale = Math.min(1, 1600 / Math.max(image.naturalWidth, image.naturalHeight));
            const canvas = document.createElement('canvas');
            canvas.width = Math.max(1, Math.round(image.naturalWidth * scale));
            canvas.height = Math.max(1, Math.round(image.naturalHeight * scale));
            canvas.getContext('2d').drawImage(image, 0, 0, canvas.width, canvas.height);
            draft.image = canvas.toDataURL('image/webp', 0.9);
            draft.imageAlt = file.name;
            draft.imageChanged = true;
        } catch (error) {
            if (flashcardEditDraft === draft && draft.imageVersion === version) showPopup('That image could not be opened. Try a different file.', null, true);
        } finally {
            if (flashcardEditDraft === draft && draft.imageVersion === version) {
                draft.imagePending = false;
                renderFlashcardGrid();
            }
        }
    };
    const cancel = () => {
        flashcardEditDraft = null;
        renderFlashcardGrid();
    };
    form.addEventListener('input', () => {
        draft.front = form.elements.front.value;
        draft.back = form.elements.back.value;
    });
    form.onsubmit = (event) => {
        event.preventDefault();
        if (draft.imagePending) return;
        saveFlashcardEdit(realIdx, key, form.elements.front.value, form.elements.back.value, { changed: draft.imageChanged, image: draft.image, alt: draft.imageAlt });
    };
    form.querySelector('[data-fc-edit-cancel]').onclick = cancel;
    form.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
            event.preventDefault();
            event.stopPropagation();
            cancel();
        } else if (event.key === 'Enter' && (event.ctrlKey || event.metaKey)) {
            event.preventDefault();
            form.requestSubmit();
        }
    });
    if (!focus) return;
    const field = form.elements[draft.field === 'back' ? 'back' : 'front'];
    field.focus({ preventScroll: true });
    const end = field.value.length;
    field.setSelectionRange(Number.isInteger(draft.start) ? Math.min(draft.start, end) : end, Number.isInteger(draft.end) ? Math.min(draft.end, end) : end);
}

function bindFlashcardCardTools(wrapper, realIdx, key) {
    wrapper.querySelectorAll('[data-fc-edit]').forEach((button) => {
        button.addEventListener('click', (event) => {
            event.stopPropagation();
            if (getFlashcardDisplayKey(realIdx) !== key || !selectFlashcardDisplayCard(realIdx)) return;
            openFlashcardEditor(wrapper, realIdx, key);
        });
    });
    wrapper.querySelectorAll('[data-fc-delete]').forEach((button) => {
        button.addEventListener('click', (event) => {
            event.stopPropagation();
            void deleteFlashcard(realIdx, key);
        });
    });
}

function syncFlashcardDisplayControls() {
    void loadFlashcardTrash();
    syncFlashcardTrashMenu();
    const compact = flashcardDisplayMode === 'compact';
    els.fcContainer.classList.toggle('fc-compact', compact);
    // One control: 1 is the single-card review; 5, 10 and 15 lay that many cards out at once.
    const perView = compact ? fcLimit : 1;
    els.fcContainer.querySelectorAll('[data-fc-per-view]').forEach((button) => {
        const value = Number(button.dataset.fcPerView);
        button.setAttribute('aria-pressed', String(value === perView));
        button.onclick = () => {
            if (value === perView) return;
            flashcardDisplayMode = value > 1 ? 'compact' : 'single';
            if (value > 1) fcLimit = value;
            renderFlashcardGrid();
        };
    });
    if (els.fcLimit) {
        els.fcLimit.value = String(fcLimit);
        els.fcLimit.onchange = () => {
            fcLimit = [5, 10, 15].includes(Number(els.fcLimit.value)) ? Number(els.fcLimit.value) : 10;
            renderFlashcardGrid();
        };
    }
    const step = compact ? fcLimit : 1;
    const start = compact ? Math.floor(currentQIndex / fcLimit) * fcLimit : currentQIndex;
    if (els.fcPrev) {
        els.fcPrev.disabled = !filteredIndices.length || start === 0 || flashcardPassComplete;
        els.fcPrev.onclick = () => moveFlashcard(compact ? -fcLimit - currentQIndex % fcLimit : -1);
    }
    if (els.fcNext) {
        els.fcNext.disabled = !filteredIndices.length || start + step >= filteredIndices.length || flashcardPassComplete;
        els.fcNext.onclick = () => moveFlashcard(compact ? fcLimit - currentQIndex % fcLimit : 1);
    }
    const undo = $('fc-undo');
    if (undo) { undo.disabled = !flashcardUndoStack.length; undo.onclick = undoLastFlashcardRating; }
    const title = $('fc-review-title');
    // With levels, the picker beside the title names what is shown instead of "All cards".
    const levels = hasHighYieldQuizLabels();
    if (title) title.textContent = flashcardReviewScope === 'all' ? (levels ? 'Flashcard review' : 'Flashcard review · All cards')
        : (levels ? 'Spaced repetition' : 'Spaced repetition · Due cards');
    syncFlashcardTierControl();
}


function isFlashcardReviewSurfaceVisible() {
    const container = els?.fcContainer;
    return !!(isFlashcardMode && container && container.style.display !== 'none' && container.getClientRects().length);
}

function pushFlashcardUndo(qIdx) {
    flashcardUndoStack.push({
        qIdx,
        srs: srsData[qIdx] ? JSON.parse(JSON.stringify(srsData[qIdx])) : null,
        knowledge: knowledgeState[qIdx],
        filteredIndices: Array.isArray(filteredIndices) ? filteredIndices.slice() : [],
        currentQIndex,
        passComplete: flashcardPassComplete,
        passRating: flashcardPassRatings.get(qIdx),
        // The rating that finishes Core moves the pass into Extended; undo moves it back.
        tierLevel: highYieldQuizState.level
    });
    if (flashcardUndoStack.length > FLASHCARD_UNDO_LIMIT) flashcardUndoStack.shift();
}

// Restores the card's schedule and queue position. The global confidence store written by
// rememberAdaptiveConfidenceForIndex is not rolled back; it only marks that a rating happened.
function undoLastFlashcardRating() {
    const entry = flashcardUndoStack.pop();
    if (!entry) {
        showPopup('Nothing to undo.', null, false);
        return false;
    }
    if (entry.type === 'delete') return restoreDeletedFlashcard(entry.trashId, entry);
    if (entry.srs) srsData[entry.qIdx] = entry.srs; else delete srsData[entry.qIdx];
    if (entry.knowledge === undefined) delete knowledgeState[entry.qIdx]; else knowledgeState[entry.qIdx] = entry.knowledge;
    if (entry.tierLevel !== undefined && entry.tierLevel !== highYieldQuizState.level) highYieldQuizState.level = entry.tierLevel;
    filteredIndices = entry.filteredIndices;
    // The restored queue is the applied pool, not a new base for the tier filter to capture.
    highYieldLastAppliedPool = filteredIndices;
    currentQIndex = entry.currentQIndex;
    flashcardPassComplete = entry.passComplete;
    flashcardDisplayAnchor = getFlashcardDisplayKey(entry.qIdx);
    if (entry.passRating === undefined) flashcardPassRatings.delete(entry.qIdx); else flashcardPassRatings.set(entry.qIdx, entry.passRating);
    recordFlashcardStraightPass();
    markSessionStateMutation();
    saveSessionDebounced();
    renderFlashcardGrid();
    showPopup('Last rating undone.', null, false);
    return true;
}

function rateCurrentFlashcard(rating) {
    const realIdx = filteredIndices[currentQIndex];
    if (!Number.isInteger(realIdx)) return false;
    window.setKnowledge(realIdx, rating, null);
    return true;
}

function moveFlashcard(delta) {
    if (!filteredIndices.length) return false;
    currentQIndex = Math.min(filteredIndices.length - 1, Math.max(0, currentQIndex + delta));
    flashcardDisplayAnchor = getFlashcardDisplayKey(filteredIndices[currentQIndex]);
    renderFlashcardGrid();
    return true;
}

/* Space flips, and on the back Space means Good (Anki's convention). 1-4 rate once the back is
   showing, Z undoes, and the arrows move through a straight pass. A focused control keeps Space
   and Enter for itself. */
function handleFlashcardReviewKeydown(e) {
    if (!isFlashcardReviewSurfaceVisible() || e.altKey || e.ctrlKey || e.metaKey) return false;
    const target = e.target instanceof Element ? e.target : null;
    if (target?.closest('input, textarea, select, [contenteditable="true"], [role="menu"], [role="dialog"]')) return false;
    const compact = flashcardDisplayMode === 'compact';
    const focused = target?.closest('.flashcard-wrapper');
    const wrapper = compact ? focused : els.fcGrid?.querySelector('.flashcard-wrapper');
    const key = e.key;
    const claim = () => { e.preventDefault(); e.stopImmediatePropagation(); };
    if (e.repeat && [' ', 'Enter', '1', '2', '3', '4', 'z', 'Z', 'ArrowRight', 'ArrowLeft'].includes(key)) {
        if (!wrapper || target?.closest('button')) return false;
        claim();
        return true;
    }
    if ((e.ctrlKey || e.metaKey) && !e.shiftKey && !e.altKey && (key === 'z' || key === 'Z')) {
        claim();
        undoLastFlashcardRating();
        return true;
    }
    if (target?.closest('button')) return false;
    if (key === ' ' || key === 'Enter') {
        if (!wrapper || (compact && !target?.closest('.fc-reveal'))) return false;
        claim();
        if (compact || !wrapper.classList.contains('flipped')) toggleFlashcardReveal(wrapper);
        else rateCurrentFlashcard('good');
        return true;
    }
    if (['1', '2', '3', '4'].includes(key)) {
        if (!wrapper?.classList.contains('flipped')) return false;
        claim();
        if (compact) selectFlashcardDisplayCard(Number(wrapper.dataset.fcIndex));
        rateCurrentFlashcard(['again', 'shaky', 'good', 'easy'][Number(key) - 1]);
        return true;
    }
    if (key === 'z' || key === 'Z') {
        if (compact && !focused && flashcardUndoStack.at(-1)?.type !== 'delete') return false;
        claim();
        undoLastFlashcardRating();
        return true;
    }
    if ((key === 'ArrowRight' || key === 'ArrowLeft') && flashcardReviewScope === 'all' && (!compact || focused)) {
        claim();
        moveFlashcard(key === 'ArrowRight' ? 1 : -1);
        return true;
    }
    return false;
}

let flashcardLearningTimer = 0;

function getNextFlashcardLearningDueAt(now = Date.now(), horizonMs = 30 * 60 * 1000) {
    let soonest = Infinity;
    for (let idx = 0; idx < questions.length; idx += 1) {
        const due = Number(srsData?.[idx]?.nextReview) || 0;
        if (due > now && due - now <= horizonMs) soonest = Math.min(soonest, due);
    }
    return Number.isFinite(soonest) ? soonest : 0;
}

function renderFlashcardEmptyState() {
    clearTimeout(flashcardLearningTimer);
    const nextAt = isFlashcardAdaptiveReviewMode ? getNextFlashcardLearningDueAt() : 0;
    if (nextAt) {
        const minutes = Math.max(1, Math.ceil((nextAt - Date.now()) / 60000));
        if (els.fcInfo) els.fcInfo.innerText = 'Cards coming back soon';
        els.fcGrid.innerHTML = `<div class="flashcard-empty-state"><strong>Next card in ${minutes} min</strong>Cards you marked Again come back here on their own.</div>`;
        flashcardLearningTimer = setTimeout(() => {
            if (!isFlashcardAdaptiveReviewMode || !isFlashcardReviewSurfaceVisible()) return;
            admitFlashcardDueQueue();
            renderFlashcardGrid();
        }, Math.max(1000, nextAt - Date.now() + 250));
        return;
    }
    if (els.fcInfo) els.fcInfo.innerText = 'No cards due';
    els.fcGrid.innerHTML = '<div class="flashcard-empty-state"><strong>You are caught up</strong>No flashcards are due right now. Memoraful will bring them back when it is time to review.<div class="fc-pass-actions"><button type="button" class="glass-btn" data-fc-pass="all">Study all cards anyway</button></div></div>';
    els.fcGrid.querySelector('[data-fc-pass="all"]')?.addEventListener('click', () => launchBaseFlashcardMode(false));
}

function renderFlashcardGrid({ preserveExisting = false } = {}) {
    applyHighYieldQuizPool({ captureBase: !!highYieldLastAppliedPool && filteredIndices !== highYieldLastAppliedPool });
    syncQuizReviewCardsControlVisibility();
    const focusedCard = document.activeElement?.closest('.flashcard-wrapper');
    const restoreCardFocus = !!focusedCard;
    const editorField = document.activeElement?.closest?.('.fc-editor') ? document.activeElement : null;
    if (flashcardEditDraft && editorField?.name) {
        Object.assign(flashcardEditDraft, { field: editorField.name, start: editorField.selectionStart, end: editorField.selectionEnd });
    }
    const scrollParent = els.fcContainer.closest('#quiz-pane') || els.fcContainer.parentElement;
    const scrollTop = scrollParent?.scrollTop || 0;
    const context = JSON.stringify([currentSessionId, Array.from(activeQuestionSetKeys || []).sort()]);
    if (flashcardDisplayContext !== context) {
        flashcardDisplayContext = context;
        flashcardDisplayAnchor = '';
        flashcardRevealedKeys.clear();
        flashcardUndoStack = [];
    }
    const anchorPos = filteredIndices.findIndex(index => getFlashcardDisplayKey(index) === flashcardDisplayAnchor);
    if (anchorPos >= 0) currentQIndex = anchorPos;
    currentQIndex = Math.min(Math.max(currentQIndex, 0), Math.max(0, filteredIndices.length - 1));
    if (flashcardReviewScope === 'all' && flashcardPassComplete && filteredIndices.some(index => !flashcardPassRatings.has(index))) flashcardPassComplete = false;
    syncFlashcardDisplayControls();
    const retainedCards = preserveExisting ? new Map(Array.from(els.fcGrid.children).map(node => [node.dataset.fcKey, node])) : null;
    if (!preserveExisting) els.fcGrid.replaceChildren();
    const segments = $('fc-segments');
    if (segments) {
        segments.replaceChildren();
        const count = Math.min(20, filteredIndices.length);
        for (let bucket = 0; bucket < count; bucket += 1) {
            const first = Math.floor(bucket * filteredIndices.length / count);
            const last = Math.floor((bucket + 1) * filteredIndices.length / count);
            const segment = document.createElement('span');
            segment.className = 'fc-segment';
            segment.classList.toggle('is-current', currentQIndex >= first && currentQIndex < last);
            segment.classList.toggle('is-rated', flashcardReviewScope === 'all' && filteredIndices.slice(first, last).every(index => flashcardPassRatings.has(index)));
            segments.appendChild(segment);
        }
    }
    const progress = $('fc-progress');
    if (progress) {
        progress.max = Math.max(1, filteredIndices.length);
        progress.value = flashcardReviewScope === 'all' ? flashcardPassRatings.size : 0;
        progress.setAttribute('aria-label', flashcardReviewScope === 'all' ? 'Cards rated in this pass' : 'Due review');
    }
    if (flashcardReviewScope === 'all' && flashcardPassComplete) { renderFlashcardPassSummary(); return; }
    if (!filteredIndices.length) { renderFlashcardEmptyState(); return; }
    const compact = flashcardDisplayMode === 'compact';
    const start = compact ? Math.floor(currentQIndex / fcLimit) * fcLimit : currentQIndex;
    const end = Math.min(filteredIndices.length, start + (compact ? fcLimit : 1));
    fcPage = compact ? Math.floor(start / fcLimit) : currentQIndex;
    flashcardDisplayAnchor = getFlashcardDisplayKey(filteredIndices[currentQIndex]);
    if (els.fcInfo) {
        syncFlashcardGenerationShimmer();
        els.fcInfo.innerText = (compact ? `Cards ${start + 1}-${end} of ${filteredIndices.length}` : `Card ${currentQIndex + 1} of ${filteredIndices.length}`)
            + (flashcardReviewScope === 'due' ? ' · Due now' : ` · ${flashcardPassRatings.size} rated`);
    }
    for (let position = start; position < end; position += 1) {
        const realIdx = filteredIndices[position];
        const q = questions[realIdx];
        if (!q) continue;
        const key = getFlashcardDisplayKey(realIdx);
        const status = normalizeKnowledgeStateValue(knowledgeState[realIdx]) || 'neu';
        const renderKey = JSON.stringify([q, status]);
        const retained = retainedCards?.get(key);
        const reuse = retained?.dataset.fcIndex === String(realIdx) && retained.dataset.fcRender === renderKey;
        const wrapper = reuse ? retained : document.createElement('article');
        if (!reuse) {
            wrapper.className = 'flashcard-wrapper';
            wrapper.dataset.fcIndex = String(realIdx);
            wrapper.dataset.fcKey = key;
            wrapper.classList.toggle('flipped', flashcardRevealedKeys.has(key));
            wrapper.dataset.fcRender = renderKey;
            wrapper.innerHTML = generateCardHTML(realIdx, q, status);
            wrapper.querySelectorAll('.fc-reveal').forEach(node => {
            node.setAttribute('aria-expanded', String(flashcardRevealedKeys.has(key)));
            node.onclick = (event) => {
                if (event.target.closest('a, button, input, select, textarea')) return;
                if (getFlashcardDisplayKey(realIdx) === key) toggleFlashcardReveal(wrapper);
            };
            });
            wrapper.onfocusin = () => {
            if (getFlashcardDisplayKey(realIdx) === key) selectFlashcardDisplayCard(realIdx);
            };
            wrapper.querySelectorAll('[data-fc-rating]').forEach(button => {
            button.onclick = () => {
                if (getFlashcardDisplayKey(realIdx) !== key || !selectFlashcardDisplayCard(realIdx)) return;
                window.setKnowledge(realIdx, button.dataset.fcRating, button);
            };
            });
            bindFlashcardSourceButtons(wrapper, realIdx, key);
            bindFlashcardCardTools(wrapper, realIdx, key);
        }
        const slot = els.fcGrid.children[position - start] || null;
        if (slot !== wrapper) els.fcGrid.insertBefore(wrapper, slot);
        if (!reuse && flashcardEditDraft?.key === key) openFlashcardEditor(wrapper, realIdx, key, { focus: !!editorField });
    }
    while (els.fcGrid.children.length > end - start) els.fcGrid.lastElementChild.remove();
    if (restoreCardFocus) {
        const selected = Array.from(els.fcGrid.children).find(node => node.dataset.fcKey === flashcardDisplayAnchor);
        focusFlashcardFace(selected?.querySelector(selected.classList.contains('flipped') ? '.flashcard-back .fc-reveal' : '.flashcard-front .fc-reveal'));
    }
    if (scrollParent) scrollParent.scrollTop = scrollTop;
}
function renderFlashcardPassSummary() {
    const missed = filteredIndices.filter((idx) => ['again', 'shaky'].includes(flashcardPassRatings.get(idx)));
    if (els.fcInfo) els.fcInfo.innerText = 'Pass complete';
    els.fcGrid.innerHTML = `<div class="flashcard-empty-state"><strong>Pass complete</strong>${filteredIndices.length} card${filteredIndices.length === 1 ? '' : 's'} · ${missed.length} marked Again or Hard<div class="fc-pass-actions">${missed.length ? `<button type="button" class="glass-btn" data-fc-pass="missed">Review the ${missed.length} missed</button>` : ''}<button type="button" class="glass-btn" data-fc-pass="restart">Start over</button></div></div>`;
    els.fcGrid.querySelector('[data-fc-pass="missed"]')?.addEventListener('click', () => beginFlashcardStraightPass(missed, { subset: true }));
    els.fcGrid.querySelector('[data-fc-pass="restart"]')?.addEventListener('click', () => beginFlashcardStraightPass(questions.map((_, idx) => idx)));
    // An Auto deck offers its next set; new cards join this pass as they arrive.
    const actions = els.fcGrid.querySelector('.fc-pass-actions');
    if (!actions || !window.getOpenAutoFlashcardDeckKey?.()) return;
    const writingNote = () => Object.assign(document.createElement('p'), { className: 'fc-pass-note', textContent: 'More cards are being written. They join this deck as they arrive.' });
    if (window.isAutoFlashcardMoreGenerating?.()) { actions.after(writingNote()); return; }
    const more = Object.assign(document.createElement('button'), { type: 'button', className: 'glass-btn', textContent: 'Generate more cards' });
    more.dataset.fcPass = 'more';
    more.addEventListener('click', () => { if (startAutoFlashcardsMoreInBackground()) { more.remove(); actions.after(writingNote()); } });
    actions.appendChild(more);
}
/* Cards arrive from models and older decks in lower case and without punctuation. Only the
   sentence start and a missing question mark are fixed; the wording is never rewritten. */
function tidyFlashcardText(text, { front = false } = {}) {
    let value = String(text ?? '').replace(/\s+/g, ' ').trim();
    if (!value || /<[a-z][^>]*>/i.test(value)) return value;
    // A leading lower-case letter is capitalised unless the word is mixed-case on purpose (mRNA, pH).
    value = value.replace(/^([("'\u201c]*)([a-z])(?![A-Z])/, (_match, lead, letter) => lead + letter.toUpperCase());
    if (front && !/[?.!:;)\]"'\u201d]$/.test(value)
        && /^(?:what|which|how|why|when|where|who|whom|whose)\b/i.test(value)) value += '?';
    return value;
}

function generateCardHTML(i, q, status) {
    const sColor = getKnowledgeStateColor(status);
    const questionText = formatQuestionStemHtml(tidyFlashcardText(q.q || q.question || 'Question unavailable', { front: true }));
    const answer = (Array.isArray(q.c) && Number.isInteger(q.a) ? (q.c[q.a] || '') : '')
        || (typeof q.answer === 'string' ? q.answer : '');
    const explanation = q.e || q.explanation || '';
    // An image card shows the slide crop it was made from; only an inline image is ever rendered.
    const imageSrc = (!q.flashcardImageRegion || q.flashcardImageSource === 'user')
        && /^data:image\/(?:jpeg|png|webp);base64,[A-Za-z0-9+/=]+$/.test(String(q.flashcardImage || '')) ? q.flashcardImage : '';
    const image = (side) => imageSrc ? `<img class="fc-card-image${side === 'back' ? ' is-answer' : ''}" src="${imageSrc}" alt="${escapeHtml(q.flashcardImageAlt || 'Slide figure for this card')}" draggable="false">` : '';
    const label = { neu: srsData[i] ? 'Review' : 'New', again: 'Again', shaky: 'Hard', good: 'Good', easy: 'Easy' }[status] || 'New';
    const citation = getQuestionCitationInfo(q);
    const source = citation.page ? `<button type="button" class="fc-chip fc-source" data-fc-source aria-label="Open source, slide ${citation.page}">Slide ${citation.page}</button>` : '';
    const tools = `<button type="button" class="fc-chip fc-icon-chip" data-fc-edit aria-label="Edit card ${i + 1}" title="Edit card">${FLASHCARD_EDIT_ICON}</button><button type="button" class="fc-chip fc-icon-chip is-danger" data-fc-delete aria-label="Delete card ${i + 1}" title="Delete card">${FLASHCARD_DELETE_ICON}</button>`;
    return `<div class="flashcard-inner"><div class="flashcard-front"><div class="fc-card-meta"><span class="fc-card-index">Card ${i + 1}</span><span class="fc-card-chips">${source}<span class="fc-chip fc-status-chip"><span class="fc-status-dot dot-q-${i}" style="background:${sColor}"></span>${label}</span>${tools}</span></div><div class="fc-reveal" tabindex="0" role="button" aria-label="Reveal answer for card ${i + 1}">${image('front')}<div class="fc-q-text">${questionText}</div><div class="fc-flip-hint">Click or press Space to reveal</div></div></div><div class="flashcard-back"><div class="fc-card-meta"><span class="fc-card-index">Card ${i + 1} · Answer</span><span class="fc-card-chips">${source}<span class="fc-chip fc-status-chip"><span class="fc-status-dot" style="background:${sColor}"></span>${label}</span>${tools}</span></div><div class="fc-reveal" tabindex="0" role="button" aria-label="Show question for card ${i + 1}">${image('back')}<div class="fc-a-text"><span class="fc-a-correct">${escapeHtml(tidyFlashcardText(answer))}</span>${sanitizeQuizRichTextHtml(explanation)}</div></div><div class="fc-actions"><button type="button" class="fc-btn btn-again" data-fc-rating="again">Again</button><button type="button" class="fc-btn btn-hard" data-fc-rating="shaky">Hard</button><button type="button" class="fc-btn btn-good" data-fc-rating="good">Good</button><button type="button" class="fc-btn btn-easy" data-fc-rating="easy">Easy</button></div><div class="fc-key-hint">1 Again · 2 Hard · 3 Good · 4 Easy · Z undo</div></div></div>`;
}
window.setKnowledge = function (qIdx, status, btn) {
    const normalizedStatus = normalizeKnowledgeStateValue(status);
    if (!normalizedStatus || normalizedStatus === 'neu') return;

    const priorFilteredPos = Array.isArray(filteredIndices) ? filteredIndices.indexOf(qIdx) : -1;

    if (priorFilteredPos >= 0) currentQIndex = priorFilteredPos;
    pushFlashcardUndo(qIdx);
    knowledgeState[qIdx] = normalizedStatus;
    const cardDue = srsCardDueState(qIdx), recalled = normalizedStatus === 'good' || normalizedStatus === 'easy';
    if (cardDue.due) void window.memoraGameStudy?.credit('flashcards', window.memoraGameStudy.identity(questions[qIdx]), 'study', recalled)?.then(credited => {
        const combo = noteDueRecall(cardDue.dueReview, recalled);
        if (!credited || !cardDue.dueReview || !recalled) return;
        if (combo > 1) awardXP(Math.round(3 * (combo - 1)), 'Combo', { quiet: true });
        maybeDueCardFind();
    });
    updateAdaptiveSRS(qIdx, normalizedStatus, true);
    markSessionStateMutation();
    saveSessionDebounced();
    flashcardRevealedKeys.delete(getFlashcardDisplayKey(qIdx));
    const c = getKnowledgeStateColor(normalizedStatus);
    document.querySelectorAll(`.dot-q-${qIdx}`).forEach(d => d.style.background = c);
    if (isFlashcardAdaptiveReviewMode) {
        const stillHasDue = refreshFlashcardAdaptiveQueueAfterRating();
        if (stillHasDue) {
            currentQIndex = Math.min(Math.max(priorFilteredPos, 0), Math.max(filteredIndices.length - 1, 0));
        }
        flashcardDisplayAnchor = getFlashcardDisplayKey(filteredIndices[currentQIndex]);
        renderFlashcardGrid();
        flushPendingSessionSave();
        return;
    }
    if (flashcardReviewScope === 'all') {
        flashcardPassRatings.set(qIdx, normalizedStatus);
        flashcardPassComplete = filteredIndices.every(index => flashcardPassRatings.has(index));
        if (advanceFlashcardsFromCoreIntoExtended(priorFilteredPos)) {
            recordFlashcardStraightPass();
            flushPendingSessionSave();
            return;
        }
        if (flashcardDisplayMode === 'single' && !flashcardPassComplete) {
            const nextUnrated = filteredIndices.findIndex((index, position) => position > priorFilteredPos && !flashcardPassRatings.has(index));
            currentQIndex = nextUnrated >= 0 ? nextUnrated : filteredIndices.findIndex(index => !flashcardPassRatings.has(index));
        }
        recordFlashcardStraightPass();
        flashcardDisplayAnchor = getFlashcardDisplayKey(filteredIndices[currentQIndex]);
        renderFlashcardGrid();
        flushPendingSessionSave();
        return;
    }
    renderFlashcardGrid();
};
els.quizUnknownBtn.onclick = () => {
    launchQuizReviewCards({ showNotice: false });
};

/* --- SAVED QUESTIONS MODAL LOGIC --- */
let isSavedCompact = true;
let savedSourceFilters = new Set(); // Empty = all sources
let isSavedDuplicateView = false;
let savedDuplicateEntriesCache = [];
let savedDuplicateSetKeysOverride = null;
let savedDuplicateIncludeCurrentOverride = false;
let isSavedDuplicateDetectionRunning = false;

function clearSavedDuplicateEntriesOverride() {
    savedDuplicateSetKeysOverride = null;
    savedDuplicateIncludeCurrentOverride = false;
}

function closeSavedExportMenu() {
    const menu = $('saved-export-menu');
    if (menu) menu.open = false;
}

function closeSavedSourceDropdown() {
    const dropdown = $('saved-source-dropdown');
    if (dropdown) dropdown.classList.remove('active');
    $('saved-source-btn')?.setAttribute('aria-expanded', 'false');
}

function syncSavedViewToggleState() {
    const compactBtn = $('saved-compact-btn');
    const expandedBtn = $('saved-expanded-btn');
    if (compactBtn) {
        compactBtn.classList.toggle('active', isSavedCompact);
        compactBtn.setAttribute('aria-pressed', isSavedCompact ? 'true' : 'false');
    }
    if (expandedBtn) {
        expandedBtn.classList.toggle('active', !isSavedCompact);
        expandedBtn.setAttribute('aria-pressed', isSavedCompact ? 'false' : 'true');
    }
}

function updateSavedCountLabel(count, total = count) {
    const label = $('saved-count-label');
    if (!label) return;
    const visibleCount = Math.max(0, Number(count) || 0);
    const totalCount = Math.max(visibleCount, Number(total) || 0);
    const suffix = totalCount === 1 ? 'question' : 'questions';
    label.textContent = visibleCount === totalCount
        ? `${visibleCount} ${suffix}`
        : `${visibleCount} of ${totalCount} ${suffix}`;
}

async function openSavedModal(options = {}) {
    if (!options.embedded && !isSavedDuplicateView && document.body.classList.contains('memora-live-left-rail-preview') && window.MemoraLiveLeftRailRouting?.openSavedContent) return window.MemoraLiveLeftRailRouting.openSavedContent('questions');
    const opener = document.activeElement;
    if (!isSavedDuplicateView) clearSavedDuplicateEntriesOverride();
    const savedIndices = Object.keys(savedQuestions).map(Number).filter(i => !isNaN(i));
    const validSavedIndices = savedIndices.filter(idx => idx < questions.length);
    if (!options.embedded && !isSavedDuplicateView && validSavedIndices.length === 0) {
        syncContentDependentControls();
        showPopupText('No saved questions are available yet.', null, true, 5000);
        return false;
    }
    const duplicateEntries = isSavedDuplicateView
        ? await collectDuplicateEntriesForQuestionSetKeys(
            Array.isArray(savedDuplicateSetKeysOverride) ? savedDuplicateSetKeysOverride : Array.from(activeQuestionSetKeys || []).map(key => String(key || '').trim()).filter(Boolean),
            {
                includeCurrentQuestions: Array.isArray(savedDuplicateSetKeysOverride) && savedDuplicateSetKeysOverride.length
                    ? savedDuplicateIncludeCurrentOverride
                    : true,
                updateCache: true
            }
        )
        : await collectSavedDuplicateEntries();

    if (options.isCurrent && !options.isCurrent()) return false;
    let sources;
    if (isSavedDuplicateView) {
        sources = Array.from(new Set(duplicateEntries.map(entry => entry.sourceFile || 'Unknown')));
    } else {
        sources = Array.from(new Set(validSavedIndices.map(idx => getSavedQuestionSourceLabel(questions[idx]))));
    }

    const availableSources = new Set(sources);
    savedSourceFilters = new Set(Array.from(savedSourceFilters).filter(src => availableSources.has(src)));

    renderSavedSourceDropdown(sources);
    if (isSavedDuplicateView) renderSavedDuplicateQuestions(duplicateEntries);
    else renderSavedQuestions(validSavedIndices);

    renderSavedSourceChips();
    updateSavedDuplicateButtons(duplicateEntries.length);
    syncSavedViewToggleState();
    updateSavedDetectDuplicatesButtonState();
    els.savedModal.classList.add('active');
    syncContentDependentControls();
    const dialog = els.savedModal.querySelector('.modal-content');
    window.MemoraModalCoordinator?.open({
        id: 'saved-modal',
        element: dialog || els.savedModal,
        opener,
        isOpen: () => els.savedModal.classList.contains('active'),
        close: () => {
            window.exitSavedFullscreenAndClose?.({ fromCoordinator: true });
            return true;
        }
    });
    return true;
}

function getSavedQuestionSourceLabel(question, fallback = 'Unknown') {
    return String(question?.sourceFile || question?.src || fallback || 'Unknown').trim() || 'Unknown';
}

function getDuplicateEntryUniqueKey(entry) {
    const duplicateStem = normalizeQuestionForDedup(entry?.duplicateQuestion?.q || entry?.duplicateQuestion?.question || '');
    const canonicalStem = normalizeQuestionForDedup(entry?.canonicalQuestion?.q || entry?.canonicalQuestion?.question || '');
    return [
        entry?.duplicateSetKey || '',
        entry?.canonicalSetKey || '',
        entry?.duplicateType || '',
        duplicateStem,
        canonicalStem
    ].join('||');
}

function resolveCurrentQuestionIndexForSnapshot(questionSnapshot) {
    if (!questionSnapshot || !Array.isArray(questions) || !questions.length) return -1;
    const targetSignature = buildQuestionSignatureForDedup(questionSnapshot);
    const targetStem = normalizeQuestionForDedup(questionSnapshot.q || questionSnapshot.question || '');
    let stemFallback = -1;

    for (let i = 0; i < questions.length; i++) {
        const current = questions[i];
        if (!current) continue;
        if (targetSignature && buildQuestionSignatureForDedup(current) === targetSignature) return i;
        if (targetStem && stemFallback === -1 && normalizeQuestionForDedup(current.q || current.question || '') === targetStem) {
            stemFallback = i;
        }
    }

    return stemFallback;
}

function createSavedDuplicateViewEntry(rawEntry, fallbackSetKey = '') {
    const duplicateQuestion = cloneQuestionForDuplicateReport(rawEntry?.duplicateQuestion);
    const canonicalQuestion = cloneQuestionForDuplicateReport(rawEntry?.canonicalQuestion);
    if (!duplicateQuestion || !canonicalQuestion) return null;

    const canonicalCurrentIndex = resolveCurrentQuestionIndexForSnapshot(canonicalQuestion);
    const canonicalOriginalIndex = Number.isInteger(rawEntry?.canonicalOriginalIndex) ? rawEntry.canonicalOriginalIndex : -1;
    const duplicateOriginalIndex = Number.isInteger(rawEntry?.duplicateOriginalIndex) ? rawEntry.duplicateOriginalIndex : -1;
    const sourceFile = getSavedQuestionSourceLabel(duplicateQuestion, fallbackSetKey);
    const duplicateType = rawEntry?.duplicateType === 'near' ? 'near' : 'exact';
    const similarity = Number(rawEntry?.similarity);
    const referenceLabel = canonicalCurrentIndex >= 0
        ? `Q${canonicalCurrentIndex + 1}`
        : (canonicalOriginalIndex >= 0
            ? `${getSavedQuestionSourceLabel(canonicalQuestion, fallbackSetKey)} Q${canonicalOriginalIndex + 1}`
            : getSavedQuestionSourceLabel(canonicalQuestion, fallbackSetKey));

    return {
        id: getDuplicateEntryUniqueKey({
            duplicateSetKey: rawEntry?.duplicateSetKey || fallbackSetKey,
            canonicalSetKey: rawEntry?.canonicalSetKey || fallbackSetKey,
            duplicateType,
            duplicateQuestion,
            canonicalQuestion
        }),
        duplicateType,
        similarity: Number.isFinite(similarity) ? similarity : null,
        duplicateSetKey: rawEntry?.duplicateSetKey || fallbackSetKey,
        canonicalSetKey: rawEntry?.canonicalSetKey || fallbackSetKey,
        duplicateOriginalIndex,
        canonicalOriginalIndex,
        duplicateQuestion,
        canonicalQuestion,
        sourceFile,
        canonicalCurrentIndex,
        referenceLabel,
        noteLabel: `${duplicateType === 'exact' ? 'Exact duplicate' : 'Near duplicate'} of ${referenceLabel}`,
        matchSummary: duplicateType === 'near'
            ? (
                rawEntry?.answerConceptMatch
                    ? `Concept match${Number.isFinite(rawEntry?.conceptOverlap) ? ` • overlap ${(Number(rawEntry.conceptOverlap) * 100).toFixed(0)}%` : ''}`
                    : (Number.isFinite(similarity)
                        ? `Similarity ${(similarity * 100).toFixed(0)}%`
                        : 'Near duplicate')
            )
            : 'Exact stem/choice match'
    };
}

async function collectSavedDuplicateEntries() {
    return collectDuplicateEntriesForQuestionSetKeys(
        Array.from(activeQuestionSetKeys || []).map(key => String(key || '').trim()).filter(Boolean),
        { includeCurrentQuestions: true, updateCache: true }
    );
}

async function collectDuplicateEntriesForQuestionSetKeys(setKeys = [], { includeCurrentQuestions = false, updateCache = false } = {}) {
    const seen = new Set();
    const entries = [];
    const normalizedSetKeys = Array.from(new Set((Array.isArray(setKeys) ? setKeys : []).map(key => String(key || '').trim()).filter(Boolean)));
    await ensureQuestionDuplicateReportsLoaded(normalizedSetKeys);
    await ensureQuestionDuplicateArchivesLoaded(normalizedSetKeys);

    normalizedSetKeys.forEach(setKey => {
        const report = questionDuplicateReportsBySetKey[setKey];
        if (report && Array.isArray(report.entries)) {
            report.entries.forEach(rawEntry => {
                const entry = createSavedDuplicateViewEntry(rawEntry, setKey);
                if (!entry || seen.has(entry.id)) return;
                seen.add(entry.id);
                entries.push(entry);
            });
        }

        const archive = questionDuplicateArchivesBySetKey[setKey];
        if (!archive || !Array.isArray(archive.entries)) return;
        archive.entries.forEach(rawEntry => {
            const entry = createSavedDuplicateViewEntry(rawEntry, setKey);
            if (!entry || seen.has(entry.id)) return;
            seen.add(entry.id);
            entries.push(entry);
        });
    });

    if (includeCurrentQuestions) {
        analyzeCurrentQuestionDuplicates(questions).forEach(rawEntry => {
            const entry = createSavedDuplicateViewEntry(rawEntry, rawEntry?.duplicateSetKey || '__active__');
            if (!entry || seen.has(entry.id)) return;
            seen.add(entry.id);
            entries.push(entry);
        });
    }

    entries.sort((left, right) => {
        const leftIdx = left.canonicalCurrentIndex >= 0 ? left.canonicalCurrentIndex : Number.MAX_SAFE_INTEGER;
        const rightIdx = right.canonicalCurrentIndex >= 0 ? right.canonicalCurrentIndex : Number.MAX_SAFE_INTEGER;
        if (leftIdx !== rightIdx) return leftIdx - rightIdx;
        if (left.sourceFile !== right.sourceFile) return left.sourceFile.localeCompare(right.sourceFile);
        return left.noteLabel.localeCompare(right.noteLabel);
    });

    if (updateCache) savedDuplicateEntriesCache = entries;
    return entries;
}

function updateSavedDuplicateButtons(duplicateCount) {
    const duplicateBtn = $('saved-duplicates-btn');
    const duplicateExportBtn = $('saved-duplicate-export-btn');
    const duplicateBadge = $('saved-duplicate-count-badge');
    const count = Math.max(0, Number(duplicateCount) || 0);
    if (duplicateBtn) {
        const selected = isSavedDuplicateView;
        const unavailable = count === 0 && !selected;
        duplicateBtn.classList.toggle('active', selected);
        duplicateBtn.setAttribute('aria-pressed', selected ? 'true' : 'false');
        duplicateBtn.disabled = unavailable;
        duplicateBtn.setAttribute('aria-disabled', unavailable ? 'true' : 'false');
        duplicateBtn.title = selected
            ? 'Show saved questions'
            : unavailable
                ? 'No duplicate questions are available.'
                : 'Review duplicate questions';
    }
    if (duplicateExportBtn) {
        const unavailable = count === 0;
        duplicateExportBtn.disabled = unavailable;
        duplicateExportBtn.setAttribute('aria-disabled', unavailable ? 'true' : 'false');
        duplicateExportBtn.title = unavailable
            ? 'No duplicate questions are available.'
            : 'Download duplicate questions';
    }
    if (duplicateBadge) duplicateBadge.innerText = String(count);
}

function getSavedSvgIcon(name, extraClass = '') {
    const paths = {
        check: '<path d="m5 12 4 4L19 6"></path>',
        book: '<path d="M4 5.5A2.5 2.5 0 0 1 6.5 3H11v16H6.5A2.5 2.5 0 0 0 4 21.5zM20 5.5A2.5 2.5 0 0 0 17.5 3H13v16h4.5a2.5 2.5 0 0 1 2.5 2.5z"></path>',
        close: '<path d="M6 6l12 12M18 6 6 18"></path>'
    };
    const body = paths[name] || '';
    const classes = `saved-svg-icon${extraClass ? ` ${extraClass}` : ''}`;
    return `<svg class="${classes}" viewBox="0 0 24 24" aria-hidden="true" focusable="false">${body}</svg>`;
}

function updateSavedDetectDuplicatesButtonState() {
    const detectBtn = $('saved-detect-duplicates-btn');
    const detectLabel = $('saved-detect-duplicates-label');
    if (!detectBtn || !detectLabel) return;
    detectBtn.disabled = isSavedDuplicateDetectionRunning;
    detectBtn.classList.toggle('detecting', isSavedDuplicateDetectionRunning);
    detectLabel.textContent = isSavedDuplicateDetectionRunning ? 'Detecting...' : 'Detect Duplicates';
}

function buildSavedDuplicatePreviewCardHtml(question, labelText) {
    if (!question) {
        return `
            <div class="saved-duplicate-preview-card">
                <div class="saved-duplicate-preview-title">${escapeHtml(labelText || 'Linked Question')}</div>
                <div class="saved-duplicate-preview-empty">Referenced question unavailable.</div>
            </div>
        `;
    }

    const choices = Array.isArray(question.c) ? question.c : (Array.isArray(question.o) ? question.o : []);
    const answerIdx = getQuestionAnswerIndexForDedup(question);
    const answerText = (answerIdx >= 0 && answerIdx < choices.length) ? choices[answerIdx] : '';

    return `
        <div class="saved-duplicate-preview-card">
            <div class="saved-duplicate-preview-title">${escapeHtml(labelText || 'Linked Question')}</div>
            <div class="saved-duplicate-preview-question">${escapeHtml(question.q || question.question || 'Question unavailable')}</div>
            ${answerText ? `<div class="saved-duplicate-preview-answer">${getSavedSvgIcon('check', 'saved-answer-icon')}${escapeHtml(answerText)}</div>` : ''}
            <div class="saved-duplicate-preview-source">${getSavedSvgIcon('book', 'saved-source-icon')}${escapeHtml(getSavedQuestionSourceLabel(question))}</div>
        </div>
    `;
}

function buildSavedDuplicateAnchorHtml(entry) {
    if (!entry) return '';
    const label = `${entry.duplicateType === 'exact' ? 'Dup' : 'Near Dup'} of ${entry.referenceLabel}`;
    return `
        <div class="saved-duplicate-anchor" onclick="event.stopPropagation();">
            <button type="button" class="saved-duplicate-chip ${entry.duplicateType}">
                ${escapeHtml(label)}
            </button>
            <div class="saved-duplicate-preview">
                ${buildSavedDuplicatePreviewCardHtml(entry.canonicalQuestion, entry.referenceLabel)}
            </div>
        </div>
    `;
}

function buildSavedQuestionItemHtml(question, {
    compact = true,
    displayLabel = 'Question',
    userAnswer = undefined,
    sideHtml = '',
    footerHtml = '',
    metaHtml = ''
} = {}) {
    const imgSrc = question?.imgUrl || question?.img;
    const imgHTML = typeof imgSrc === 'string' && imgSrc.trim()
        ? `<img src="${escapeHtml(imgSrc)}" data-saved-lightbox-src="${escapeHtml(imgSrc)}" class="saved-q-image${compact ? ' compact' : ''}">`
        : '';
    const safeQuestionText = escapeHtml(question?.q || question?.question || 'Question unavailable');
    const choices = Array.isArray(question?.c) ? question.c : (Array.isArray(question?.o) ? question.o : []);
    const answerIdx = getQuestionAnswerIndexForDedup(question);
    const answerText = (answerIdx >= 0 && answerIdx < choices.length) ? choices[answerIdx] : '';

    if (compact) {
        return `
            ${imgHTML}
            <div class="saved-q-row">
                <div class="saved-q-main">
                    <div class="saved-q">${escapeHtml(displayLabel)}: ${safeQuestionText}</div>
                    <div class="saved-a">${getSavedSvgIcon('check', 'saved-answer-icon')}${escapeHtml(answerText || 'Answer unavailable')}</div>
                    ${metaHtml}
                </div>
                ${sideHtml}
            </div>
            ${footerHtml}
        `;
    }

    let choicesHTML = '<div class="saved-choices">';
    choices.forEach((choice, cIdx) => {
        let classes = 'saved-choice';
        if (cIdx === answerIdx) classes += ' correct';
        else if (userAnswer !== undefined && cIdx === userAnswer && userAnswer !== answerIdx) classes += ' wrong user-picked';
        choicesHTML += `<div class="${classes}">
            <span class="choice-label">${['A', 'B', 'C', 'D', 'E', 'F'][cIdx] || `${cIdx + 1}` }.</span>
            <span>${escapeHtml(choice)}</span>
        </div>`;
    });
    choicesHTML += '</div>';

    return `
        ${imgHTML}
        <div class="saved-q-row">
            <div class="saved-q-main">
                <div class="saved-q">${escapeHtml(displayLabel)}: ${safeQuestionText}</div>
                ${metaHtml}
            </div>
            ${sideHtml}
        </div>
        ${choicesHTML}
        ${footerHtml}
    `;
}

function jumpToQuestionByRealIndex(idx) {
    if (window.MemoraLiveLeftRailRouting?.currentRoute?.().name === 'saved-content') return window.MemoraLiveLeftRailRouting.returnToQuizzes().then(opened => { if (opened !== false) jumpToQuestionByRealIndex(idx); });
    if (condensedQuizState.enabled && hasCondensedQuizSelection() && !getCondensedQuestionIndices().includes(idx)) {
        showPopup('That saved question is outside Condensed View. Turn it off to open the full bank.', null, true);
        return;
    }
    closeSavedModal();
    currentQIndex = filteredIndices.indexOf(idx);
    if (currentQIndex === -1) {
        resetFilter();
        currentQIndex = filteredIndices.indexOf(idx);
        if (currentQIndex === -1) currentQIndex = 0;
    }
    isFlashcardMode = false;
    els.modeBtn.querySelector('.gradient-text').innerText = "\u{1F3B4} Switch to Flashcards";
    showQuiz();
}

function bindSavedQuestionItemActions(container) {
    container?.querySelectorAll?.('[data-saved-lightbox-src]').forEach((image) => {
        image.addEventListener('click', (event) => {
            event.stopPropagation();
            openImageLightbox(image.dataset.savedLightboxSrc || '');
        });
    });
}

function renderSavedSourceDropdown(sources) {
    const dropdown = $('saved-source-dropdown');
    dropdown.innerHTML = '';
    const sourceButton = $('saved-source-btn');
    if (sourceButton) sourceButton.setAttribute('aria-expanded', dropdown.classList.contains('active') ? 'true' : 'false');

    // "All" option
    const allItem = document.createElement('div');
    allItem.className = 'source-filter-item' + (savedSourceFilters.size === 0 ? ' selected' : '');
    allItem.setAttribute('role', 'option');
    allItem.setAttribute('aria-selected', savedSourceFilters.size === 0 ? 'true' : 'false');
    allItem.innerHTML = `<div class="check">${savedSourceFilters.size === 0 ? getSavedSvgIcon('check', 'saved-check-icon') : ''}</div><span>All Sources</span>`;
    allItem.onclick = (e) => {
        e.stopPropagation();
        savedSourceFilters.clear();
        closeSavedSourceDropdown();
        void openSavedModal({ embedded: $('saved-modal')?.dataset.savedEmbedded === 'true' }); // Re-render
    };
    dropdown.appendChild(allItem);

    // Individual sources
    sources.forEach(src => {
        const item = document.createElement('div');
        const isSelected = savedSourceFilters.has(src);
        item.className = 'source-filter-item' + (isSelected ? ' selected' : '');
        item.setAttribute('role', 'option');
        item.setAttribute('aria-selected', isSelected ? 'true' : 'false');
        item.innerHTML = `<div class="check">${isSelected ? getSavedSvgIcon('check', 'saved-check-icon') : ''}</div>`;
        const label = document.createElement('span');
        label.textContent = src;
        item.appendChild(label);
        item.onclick = (e) => {
            e.stopPropagation();
            if (isSelected) savedSourceFilters.delete(src);
            else savedSourceFilters.add(src);
            closeSavedSourceDropdown();
            void openSavedModal({ embedded: $('saved-modal')?.dataset.savedEmbedded === 'true' }); // Re-render
        };
        dropdown.appendChild(item);
    });

    // Update badge
    const badge = $('source-count-badge');
    badge.innerText = savedSourceFilters.size === 0 ? '(All)' : `(${savedSourceFilters.size})`;
}

function renderSavedSourceChips() {
    const container = $('saved-source-chips');
    container.innerHTML = '';

    if (savedSourceFilters.size === 0) return;

    savedSourceFilters.forEach(src => {
        const chip = document.createElement('div');
        chip.className = 'source-chip';
        chip.appendChild(document.createTextNode(`${src.length > 20 ? src.substring(0, 20) + '...' : src} `));
        const remove = document.createElement('span');
        remove.className = 'remove';
        remove.innerHTML = getSavedSvgIcon('close', 'saved-chip-remove-icon');
        remove.onclick = () => removeSavedSourceFilter(src);
        chip.appendChild(remove);
        container.appendChild(chip);
    });
}

window.removeSavedSourceFilter = function (src) {
    savedSourceFilters.delete(src);
    closeSavedSourceDropdown();
    void openSavedModal({ embedded: $('saved-modal')?.dataset.savedEmbedded === 'true' });
};

function renderSavedQuestions(validSavedIndices) {
    els.savedList.innerHTML = '';

    // Filter by source if active
    let filteredSavedIndices = validSavedIndices;
    if (savedSourceFilters.size > 0) {
        filteredSavedIndices = validSavedIndices.filter(idx => {
            const src = questions[idx].sourceFile || 'Unknown';
            return savedSourceFilters.has(src);
        });
    }

    updateSavedCountLabel(filteredSavedIndices.length, validSavedIndices.length);
    if (filteredSavedIndices.length === 0) {
        const message = validSavedIndices.length > 0
            ? 'No saved questions match the selected sources.'
            : 'No saved questions are available yet.';
        els.savedList.innerHTML = `<div class="saved-empty-state">${message}</div>`;
        return;
    }

    filteredSavedIndices.forEach(idx => {
        const q = questions[idx];
        const userAnswer = userAnswers[idx];
        const div = document.createElement('div');
        div.className = 'saved-item' + (isSavedCompact ? '' : ' expanded');
        const footerHtml = !isSavedCompact
            ? `<div class="saved-source-tag">${getSavedSvgIcon('book', 'saved-source-icon')}Source: ${escapeHtml(getSavedQuestionSourceLabel(q))}</div>`
            : '';
        div.innerHTML = buildSavedQuestionItemHtml(q, {
            compact: isSavedCompact,
            displayLabel: `Q${idx + 1}`,
            userAnswer,
            footerHtml
        });
        bindSavedQuestionItemActions(div);
        div.onclick = () => jumpToQuestionByRealIndex(idx);
        els.savedList.appendChild(div);
    });
}

function renderSavedDuplicateQuestions(duplicateEntries) {
    els.savedList.innerHTML = '';

    let filteredEntries = Array.isArray(duplicateEntries) ? duplicateEntries : [];
    if (savedSourceFilters.size > 0) {
        filteredEntries = filteredEntries.filter(entry => savedSourceFilters.has(entry.sourceFile || 'Unknown'));
    }

    if (filteredEntries.length === 0) {
        updateSavedCountLabel(0, duplicateEntries.length);
        els.savedList.innerHTML = '<div class="saved-duplicate-empty">No duplicate questions found for the current quiz selection.</div>';
        return;
    }

    updateSavedCountLabel(filteredEntries.length, duplicateEntries.length);

    filteredEntries.forEach(entry => {
        const div = document.createElement('div');
        div.className = `saved-item duplicate-entry ${entry.duplicateType}${isSavedCompact ? '' : ' expanded'}`;
        if (entry.canonicalCurrentIndex < 0) div.classList.add('no-nav');

        const metaHtml = `<div class="saved-duplicate-inline-note">${escapeHtml(entry.noteLabel)}${entry.matchSummary ? ` • ${escapeHtml(entry.matchSummary)}` : ''}</div>`;
        const footerHtml = `
            <div class="saved-source-tag">
                ${getSavedSvgIcon('book', 'saved-source-icon')}Duplicate source: ${escapeHtml(entry.sourceFile)}
                <span class="saved-duplicate-source-note">${escapeHtml(entry.matchSummary)}</span>
            </div>
        `;

        div.innerHTML = buildSavedQuestionItemHtml(entry.duplicateQuestion, {
            compact: isSavedCompact,
            displayLabel: entry.duplicateOriginalIndex >= 0 ? `Removed Q${entry.duplicateOriginalIndex + 1}` : 'Removed Duplicate',
            sideHtml: buildSavedDuplicateAnchorHtml(entry),
            footerHtml,
            metaHtml
        });
        bindSavedQuestionItemActions(div);

        if (entry.canonicalCurrentIndex >= 0) {
            div.onclick = () => jumpToQuestionByRealIndex(entry.canonicalCurrentIndex);
        }

        els.savedList.appendChild(div);
    });
}

window.toggleSavedCompact = function () {
    isSavedCompact = true;
    closeSavedExportMenu();
    closeSavedSourceDropdown();
    syncSavedViewToggleState();
    void openSavedModal({ embedded: $('saved-modal')?.dataset.savedEmbedded === 'true' });
};

window.toggleSavedExpanded = function () {
    isSavedCompact = false;
    closeSavedExportMenu();
    closeSavedSourceDropdown();
    syncSavedViewToggleState();
    void openSavedModal({ embedded: $('saved-modal')?.dataset.savedEmbedded === 'true' });
};

function getDuplicateExportFilename(setKeys = [], preferredStem = '') {
    const normalizedPreferredStem = String(preferredStem || '').trim().replace(/[<>:"/\\|?*]+/g, '_');
    if (normalizedPreferredStem) {
        return `${normalizedPreferredStem}_duplicates.json`;
    }

    const normalizedSetKeys = Array.from(new Set((Array.isArray(setKeys) ? setKeys : []).map(key => String(key || '').trim()).filter(Boolean)));
    if (normalizedSetKeys.length === 1) {
        return `${normalizedSetKeys[0].replace(/\.(json|txt)$/i, '')}_duplicates.json`;
    }
    if (normalizedSetKeys.length > 1) {
        return `${normalizedSetKeys[0].replace(/\.(json|txt)$/i, '')}_combined_duplicates.json`;
    }
    return 'quiz_duplicates.json';
}

function buildDuplicateExportPayload(duplicateEntries, activeQuestionSets = [], extraMeta = {}) {
    return {
        generatedAt: new Date().toISOString(),
        duplicateCount: duplicateEntries.length,
        activeQuestionSets: Array.isArray(activeQuestionSets) ? activeQuestionSets : [],
        ...extraMeta,
        duplicates: duplicateEntries.map(entry => ({
            duplicateType: entry.duplicateType,
            similarity: entry.similarity,
            sourceFile: entry.sourceFile,
            duplicateOriginalIndex: entry.duplicateOriginalIndex,
            canonicalCurrentQuestion: entry.canonicalCurrentIndex >= 0 ? `Q${entry.canonicalCurrentIndex + 1}` : null,
            canonicalOriginalIndex: entry.canonicalOriginalIndex,
            referenceLabel: entry.referenceLabel,
            duplicateQuestion: entry.duplicateQuestion,
            canonicalQuestion: entry.canonicalQuestion
        }))
    };
}

async function saveDuplicateExportPayload(payload, suggestedName, successMessage) {
    const jsonString = JSON.stringify(payload, null, 2);

    if ('showSaveFilePicker' in window && window.isSecureContext) {
        try {
            const handle = await window.showSaveFilePicker({
                suggestedName,
                types: [{ description: 'JSON Duplicate Report', accept: { 'application/json': ['.json'] } }]
            });
            const writable = await handle.createWritable();
            await writable.write(jsonString);
            await writable.close();
            showPopupText(successMessage || `Saved duplicate report: ${handle.name}`, null, false);
            return true;
        } catch (err) {
            if (err?.name !== 'AbortError') console.warn('Duplicate report save failed:', err);
        }
    }

    triggerBlobDownload(new Blob([jsonString], { type: 'application/json' }), suggestedName);
    showPopupText(successMessage || 'Downloaded duplicate report', null, false);
    return true;
}

function getSessionQuestionSetKeys(session) {
    const sessionJsons = Array.isArray(session?.activeSets)
        ? session.activeSets.filter(f => String(f || '').trim().endsWith('.json'))
        : [];
    const manifestJsons = Array.isArray(session?.data?.manifest?.jsons)
        ? session.data.manifest.jsons
        : [];
    return Array.from(new Set([...sessionJsons, ...manifestJsons].map(key => String(key || '').trim()).filter(Boolean)));
}

window.toggleSavedDuplicateView = function () {
    closeSavedExportMenu();
    closeSavedSourceDropdown();
    isSavedDuplicateView = !isSavedDuplicateView;
    clearSavedDuplicateEntriesOverride();
    savedSourceFilters = new Set();
    void openSavedModal({ embedded: $('saved-modal')?.dataset.savedEmbedded === 'true' });
};

window.openSavedDuplicateViewForQuestionSets = async function (setKeys, event = null) {
    if (event) {
        event.preventDefault();
        event.stopPropagation();
    }

    const normalizedSetKeys = Array.from(new Set((Array.isArray(setKeys) ? setKeys : [setKeys])
        .map(key => String(key || '').trim())
        .filter(Boolean)));
    if (!normalizedSetKeys.length) {
        showPopup('Question banks not found for duplicate view.', null, true);
        return;
    }

    const duplicateEntries = await collectDuplicateEntriesForQuestionSetKeys(normalizedSetKeys, {
        includeCurrentQuestions: false,
        updateCache: true
    });
    if (!duplicateEntries.length) {
        showPopup(
            normalizedSetKeys.length === 1
                ? 'No duplicate questions found for this question bank.'
                : 'No duplicate questions found for the loaded question banks.',
            null,
            false
        );
        return;
    }

    if (els.sourceDropdown) els.sourceDropdown.classList.remove('active');

    savedSourceFilters = new Set();
    savedDuplicateSetKeysOverride = normalizedSetKeys;
    savedDuplicateIncludeCurrentOverride = false;
    isSavedDuplicateView = true;
    await openSavedModal();
};

window.openSavedDuplicateViewForQuestionSet = async function (setKey, event = null) {
    const normalizedSetKey = String(setKey || '').trim();
    if (!normalizedSetKey) {
        showPopup('Question bank not found for duplicate view.', null, true);
        return;
    }
    await window.openSavedDuplicateViewForQuestionSets([normalizedSetKey], event);
};

window.openSavedDuplicateExpandedViewForQuestionSets = async function (setKeys, event = null) {
    isSavedCompact = false;
    if ($('saved-compact-btn')) $('saved-compact-btn').classList.remove('active');
    if ($('saved-expanded-btn')) $('saved-expanded-btn').classList.add('active');
    await window.openSavedDuplicateViewForQuestionSets(setKeys, event);
};

window.openSavedDuplicateExpandedViewForQuestionSet = async function (setKey, event = null) {
    const normalizedSetKey = String(setKey || '').trim();
    if (!normalizedSetKey) {
        showPopup('Question bank not found for duplicate view.', null, true);
        return;
    }
    await window.openSavedDuplicateExpandedViewForQuestionSets([normalizedSetKey], event);
};

window.exportSavedDuplicateQuestions = async function (event) {
    closeSavedExportMenu();
    if (event) {
        event.preventDefault();
        event.stopPropagation();
    }

    const activeKeys = Array.from(activeQuestionSetKeys || []).map(key => String(key || '').trim()).filter(Boolean);
    const duplicateEntries = await collectDuplicateEntriesForQuestionSetKeys(activeKeys, { includeCurrentQuestions: true });
    if (!duplicateEntries.length) {
        showPopup('No duplicate questions found in the current quiz.', null, false);
        return;
    }

    const payload = buildDuplicateExportPayload(duplicateEntries, activeKeys);
    const suggestedName = getDuplicateExportFilename(activeKeys);
    await saveDuplicateExportPayload(
        payload,
        suggestedName,
        `Downloaded duplicate report (${duplicateEntries.length} question${duplicateEntries.length === 1 ? '' : 's'})`
    );
};

window.exportDuplicateQuestionsForSession = async function (sessionId, event = null) {
    if (event) {
        event.preventDefault();
        event.stopPropagation();
    }

    const targetSessionId = normalizeSessionId(sessionId);
    if (!targetSessionId) {
        showPopup('Session not found for duplicate export.', null, true);
        return;
    }

    const session = await IDB.get('sessions', targetSessionId);
    if (!session) {
        showPopup('Session not found for duplicate export.', null, true);
        return;
    }

    const setKeys = getSessionQuestionSetKeys(session);
    const includeCurrentQuestions = normalizeSessionId(currentSessionId) === targetSessionId;
    const duplicateEntries = await collectDuplicateEntriesForQuestionSetKeys(setKeys, { includeCurrentQuestions });
    if (!duplicateEntries.length) {
        showPopup('No duplicate questions found for this session.', null, false);
        return;
    }

    const displayName = session.customName
        || (setKeys[0] ? setKeys[0].replace(/\.(json|txt)$/i, '') : 'session');
    const payload = buildDuplicateExportPayload(duplicateEntries, setKeys, {
        sessionId: targetSessionId,
        sessionName: displayName
    });
    const suggestedName = getDuplicateExportFilename(setKeys, displayName);
    await saveDuplicateExportPayload(
        payload,
        suggestedName,
        `Downloaded duplicate report (${duplicateEntries.length} question${duplicateEntries.length === 1 ? '' : 's'})`
    );
};

window.runActiveQuizDuplicateDetection = runActiveQuizDuplicateDetection;

window.detectSavedDuplicates = async function (event = null) {
    if (event) {
        event.preventDefault();
        event.stopPropagation();
    }
    await runActiveQuizDuplicateDetection({
        silent: false,
        showNoDuplicatesPopup: true,
        openDuplicateView: true,
        refreshSavedModalIfOpen: true
    });
};

window.toggleSavedSourceDropdown = function (e) {
    e.stopPropagation();
    closeSavedExportMenu();
    const dropdown = $('saved-source-dropdown');
    dropdown.classList.toggle('active');
    $('saved-source-btn')?.setAttribute('aria-expanded', dropdown.classList.contains('active') ? 'true' : 'false');
};

window.toggleSavedFullscreen = function () {
    const modalContent = $('saved-modal').querySelector('.modal-content');

    if (!document.fullscreenElement) {
        // Enter true fullscreen
        document.body.classList.add('saved-fullscreen-active');
        if (modalContent.requestFullscreen) {
            modalContent.requestFullscreen();
        } else if (modalContent.webkitRequestFullscreen) {
            modalContent.webkitRequestFullscreen();
        } else if (modalContent.msRequestFullscreen) {
            modalContent.msRequestFullscreen();
        }
    } else {
        // Exit fullscreen
        document.body.classList.remove('saved-fullscreen-active');
        if (document.exitFullscreen) {
            document.exitFullscreen();
        } else if (document.webkitExitFullscreen) {
            document.webkitExitFullscreen();
        } else if (document.msExitFullscreen) {
            document.msExitFullscreen();
        }
    }
};

// Exit fullscreen and close modal (X button behavior)
window.exitSavedFullscreenAndClose = function (options = {}) {
    if (!options?.fromCoordinator && window.MemoraModalCoordinator?.isOpen('saved-modal')) {
        return window.MemoraModalCoordinator.close('saved-modal', 'button');
    }
    const finishClose = () => closeSavedModal(null, { fromCoordinator: true });
    if (document.fullscreenElement) {
        document.body.classList.remove('saved-fullscreen-active');
        if (document.exitFullscreen) {
            document.exitFullscreen().then(() => {
                finishClose();
            });
        } else {
            finishClose();
        }
    } else {
        finishClose();
    }
    return true;
};

const ENTER_FULLSCREEN_ICON_PATH = "M9 4H4v5M4 4l5 5M15 4h5v5M20 4l-5 5M9 20H4v-5M4 20l5-5M15 20h5v-5M20 20l-5-5";
const EXIT_FULLSCREEN_ICON_PATH = "M4 9V4h5M4 4l5 5M20 9V4h-5M20 4l-5 5M4 15v5h5M4 20l5-5M20 15v5h-5M20 20l-5-5";
function setFullscreenIcon(targetEl, isExit, sizeRem = 1.0, gradientId = '') {
    if (!targetEl) return;
    const iconPath = isExit ? EXIT_FULLSCREEN_ICON_PATH : ENTER_FULLSCREEN_ICON_PATH;
    const size = `${sizeRem}rem`;
    const stroke = gradientId ? `url(#${gradientId})` : 'currentColor';
    const gradientDefs = gradientId === 'global-fullscreen-gradient' ? `
            <defs>
                <linearGradient id="global-fullscreen-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stop-color="var(--g1, #60a5fa)"></stop>
                    <stop offset="100%" stop-color="var(--g2, #f472b6)"></stop>
                </linearGradient>
            </defs>` : '';
    targetEl.innerHTML = `
        <svg viewBox="0 0 24 24" width="${size}" height="${size}" aria-hidden="true" focusable="false" style="display:block;">
            ${gradientDefs}
            <path d="${iconPath}" fill="none" stroke="${stroke}" stroke-width="2.1" stroke-linecap="round" stroke-linejoin="round"></path>
        </svg>
    `;
}

let fullscreenHeaderOverlapRaf = 0;

function applyFullscreenHeaderClearance() {
    const headers = [$('pdf-header'), $('ai-header')].filter(Boolean);
    headers.forEach((header) => header.style.setProperty('--fullscreen-clearance-left', '0px'));

    const rootStyle = document.documentElement ? document.documentElement.style : null;
    const dockButtons = [$('global-fullscreen-btn'), $('global-home-btn')].filter(Boolean);
    const guardRects = dockButtons
        .map((button) => button.getBoundingClientRect())
        .filter((rect) => rect.width && rect.height);
    if (!guardRects.length) {
        if (rootStyle) {
            rootStyle.removeProperty('--fullscreen-dock-top');
            rootStyle.removeProperty('--fullscreen-dock-left');
        }
        return;
    }

    const dockRight = Math.max(...guardRects.map((rect) => rect.right));
    if (rootStyle) {
        rootStyle.setProperty('--fullscreen-dock-top', `${Math.round(Math.min(...guardRects.map((rect) => rect.top)))}px`);
        rootStyle.setProperty('--fullscreen-dock-left', `${Math.round(dockRight + 8)}px`);
    }

    const askLauncher = $('public-ask-launcher');
    if (document.body?.classList.contains('public-floating-hidden') && askLauncher) {
        const askRect = askLauncher.getBoundingClientRect();
        if (askRect.width && askRect.height) guardRects.push(askRect);
    }

    const guardRight = Math.max(...guardRects.map((rect) => rect.right)) + 10;
    const guardTop = Math.min(...guardRects.map((rect) => rect.top)) - 4;
    const guardBottom = Math.max(...guardRects.map((rect) => rect.bottom)) + 4;

    headers.forEach((header) => {
        const rect = header.getBoundingClientRect();
        if (!rect.width || !rect.height) return;
        const hasVerticalOverlap = rect.bottom > guardTop && rect.top < guardBottom;
        if (!hasVerticalOverlap) return;
        const overlap = Math.ceil(guardRight - rect.left);
        if (overlap <= 0) return;
        header.style.setProperty('--fullscreen-clearance-left', `${Math.min(220, overlap)}px`);
    });
}

function scheduleFullscreenHeaderClearance() {
    if (fullscreenHeaderOverlapRaf) cancelAnimationFrame(fullscreenHeaderOverlapRaf);
    fullscreenHeaderOverlapRaf = requestAnimationFrame(() => {
        fullscreenHeaderOverlapRaf = 0;
        applyFullscreenHeaderClearance();
    });
}

function syncFullscreenButtons() {
    const globalBtn = $('global-fullscreen-btn');
    const isFullscreen = !!document.fullscreenElement;

    if (globalBtn) {
        const iconHost = $('global-fullscreen-icon') || globalBtn;
        setFullscreenIcon(iconHost, isFullscreen, 0.79, 'global-fullscreen-gradient');
        globalBtn.title = isFullscreen ? 'Exit Fullscreen (ESC or Ctrl+O)' : 'Toggle Fullscreen (Ctrl+O)';
    }

    const headerBtn = $('saved-fullscreen-btn');
    if (headerBtn) {
        setFullscreenIcon(headerBtn, isFullscreen, 1.02);
        headerBtn.title = isFullscreen ? 'Exit Fullscreen (ESC)' : 'Toggle Fullscreen';
    }

    const tutorMenuBtn = $('pdf-tutor-fullscreen-menu-btn');
    if (tutorMenuBtn) {
        const iconHost = tutorMenuBtn.querySelector('.pdf-more-menu-key');
        const label = tutorMenuBtn.querySelector('.pdf-more-menu-label');
        setFullscreenIcon(iconHost, isFullscreen, 0.82);
        if (label) label.textContent = isFullscreen ? 'Windowed' : 'Fullscreen';
        tutorMenuBtn.title = isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen';
        tutorMenuBtn.setAttribute('aria-label', tutorMenuBtn.title);
    }

    scheduleFullscreenHeaderClearance();
}

// Update button icons when fullscreen state changes
document.addEventListener('fullscreenchange', syncFullscreenButtons);
window.addEventListener('resize', scheduleFullscreenHeaderClearance);
setTimeout(syncFullscreenButtons, 0);

// Global Fullscreen toggle (entire page)
window.toggleGlobalFullscreen = function () {
    if (!document.fullscreenElement) {
        if (document.documentElement.requestFullscreen) {
            document.documentElement.requestFullscreen();
        } else if (document.documentElement.webkitRequestFullscreen) {
            document.documentElement.webkitRequestFullscreen();
        } else if (document.documentElement.msRequestFullscreen) {
            document.documentElement.msRequestFullscreen();
        }
    } else {
        if (document.exitFullscreen) {
            document.exitFullscreen();
        } else if (document.webkitExitFullscreen) {
            document.webkitExitFullscreen();
        } else if (document.msExitFullscreen) {
            document.msExitFullscreen();
        }
    }
};

// Ctrl+O shortcut for global fullscreen
document.addEventListener('keydown', (e) => {
    if (e.ctrlKey && e.key === 'o') {
        e.preventDefault();
        toggleGlobalFullscreen();
    }
    // Ctrl+H for history
    if (e.ctrlKey && e.key === 'h') {
        e.preventDefault();
        SessionManager.showHistory();
    }
});

// Close dropdowns on outside click
document.addEventListener('click', (e) => {
    const target = e.target instanceof Element ? e.target : null;

    const savedDropdown = $('saved-source-dropdown');
    if (savedDropdown && (!target || !target.closest('#saved-modal .source-filter-wrapper'))) {
        closeSavedSourceDropdown();
    }

    const savedExportMenu = $('saved-export-menu');
    if (savedExportMenu && (!target || !target.closest('#saved-modal .saved-export-disclosure'))) {
        savedExportMenu.open = false;
    }

    const adaptiveDropdown = $('adaptive-rank-dropdown');
    if (adaptiveDropdown && (!target || !target.closest('#adaptive-snapshot-modal .source-filter-wrapper'))) {
        adaptiveDropdown.classList.remove('active');
    }
});

function closeSavedModal(e = null, options = {}) {
    closeSavedExportMenu();
    els.savedModal.classList.remove('active');
    closeSavedSourceDropdown();
    clearSavedDuplicateEntriesOverride();
    if (!options?.fromCoordinator) {
        window.MemoraModalCoordinator?.markClosed('saved-modal');
    }
    return true;
}

function startSavedQuizMode() {
    if (window.MemoraLiveLeftRailRouting?.currentRoute?.().name === 'saved-content') return window.MemoraLiveLeftRailRouting.returnToQuizzes().then(opened => { if (opened !== false) startSavedQuizMode(); });
    closeSavedExportMenu();
    const savedIndices = Object.keys(savedQuestions).map(Number).filter(i => !isNaN(i)); const validSavedIndices = savedIndices.filter(idx => idx < questions.length);
    if (validSavedIndices.length === 0) {
        syncContentDependentControls();
        showPopupText('No saved questions are available yet.', null, true, 5000);
        return false;
    }
    validSavedIndices.forEach(idx => { delete userAnswers[idx]; });
    isAdaptiveReviewMode = false;
    syncAdaptiveModeUI();
    filteredIndices = moveShownLastQuestionsBehind(validSavedIndices.sort((a, b) => a - b));
    if (isShuffled) applySmartShuffle({ showNotice: false });
    isFilterActive = true;
    currentQIndex = 0;
    isFlashcardMode = false;
    els.modeBtn.querySelector('.gradient-text').innerText = "🎴 Switch to Flashcards";
    els.filterBanner.style.display = 'flex';
    const bannerText = getFilterBannerTextEl();
    if (bannerText) bannerText.innerText = `Study Mode: ${validSavedIndices.length} Saved Questions`;
    setAdaptiveSnapshotButtonVisible(false);
    showQuiz();
    els.savedModal.classList.remove('active'); // Close modal if open
    window.MemoraModalCoordinator?.markClosed('saved-modal');
    syncContentDependentControls();
    return true;
}

/* Pictures (photos of notes, screenshots, scanned handouts) are study sources too. Each becomes
 * a one-page PDF at import, so every PDF owner (the viewer and its highlights, Ask, the Tutor,
 * quiz generation) takes it exactly as it takes a lecture. The page is the picture's own size,
 * capped so a phone photo stays a reasonable upload, and keeps its EXIF orientation. */
const MEMORA_IMAGE_SOURCE_EXTENSION_RE = /\.(?:png|jpe?g|webp|gif|bmp)$/i;
const MEMORA_IMAGE_SOURCE_MAX_SIDE_PX = 2400;

function isMemoraImageSourceFile(file) {
    const name = String(file?.name || '');
    return MEMORA_IMAGE_SOURCE_EXTENSION_RE.test(name)
        || (/^image\/(?:png|jpeg|webp|gif|bmp)$/i.test(String(file?.type || '')) && !/\.[a-z0-9]+$/i.test(name));
}

function getMemoraImageSourcePdfName(file, takenNames = new Set()) {
    const name = String(file?.name || 'Image').trim() || 'Image';
    const base = name.replace(/\.[a-z0-9]+$/i, '') || 'Image';
    const extension = (name.match(/\.([a-z0-9]+)$/i)?.[1] || 'image').toLowerCase();
    const isTaken = (candidate) => takenNames.has(candidate)
        || (typeof isMemoraPdfSourceCurrentlyLoaded === 'function' && isMemoraPdfSourceCurrentlyLoaded(candidate));
    let candidate = `${base}.pdf`;
    if (isTaken(candidate)) candidate = `${base} (${extension}).pdf`;
    for (let index = 2; isTaken(candidate) && index < 100; index += 1) candidate = `${base} (${extension} ${index}).pdf`;
    return candidate;
}

async function convertMemoraImageFileToPdf(file, pdfName) {
    const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
    try {
        const scale = Math.min(1, MEMORA_IMAGE_SOURCE_MAX_SIDE_PX / Math.max(bitmap.width, bitmap.height, 1));
        const width = Math.max(1, Math.round(bitmap.width * scale));
        const height = Math.max(1, Math.round(bitmap.height * scale));
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const context = canvas.getContext('2d');
        // A transparent PNG would come out black as a JPEG page; paper is white.
        context.fillStyle = '#ffffff';
        context.fillRect(0, 0, width, height);
        context.drawImage(bitmap, 0, 0, width, height);
        const dataUrl = canvas.toDataURL('image/jpeg', 0.9);
        canvas.width = 0;
        canvas.height = 0;
        const jsPDF = await getMemoraJsPdfConstructor();
        const doc = new jsPDF({
            orientation: width >= height ? 'landscape' : 'portrait',
            unit: 'px',
            format: [width, height],
            hotfixes: ['px_scaling'],
            compress: true
        });
        doc.addImage(dataUrl, 'JPEG', 0, 0, width, height);
        return new File([doc.output('blob')], pdfName, { type: 'application/pdf', lastModified: file.lastModified || Date.now() });
    } finally {
        bitmap.close?.();
    }
}

// Returns the files with every picture replaced by its one-page PDF; anything else passes through.
async function convertMemoraImageSourceFiles(files = []) {
    // Word documents become a quiz file or a text PDF first (app.docx-import.js).
    const list = await (window.MemoraDocxImport?.convertFiles?.(files) ?? Array.from(files || []));
    if (!list.some(isMemoraImageSourceFile)) return list;
    const takenNames = new Set(list.filter((file) => !isMemoraImageSourceFile(file)).map((file) => String(file?.name || '')));
    const converted = [];
    for (const file of list) {
        if (!isMemoraImageSourceFile(file)) {
            converted.push(file);
            continue;
        }
        const pdfName = getMemoraImageSourcePdfName(file, takenNames);
        takenNames.add(pdfName);
        try {
            converted.push(await convertMemoraImageFileToPdf(file, pdfName));
        } catch (error) {
            console.warn(`Image source ${file.name} could not be converted:`, error);
            if (typeof showPopupText === 'function') showPopupText(`${file.name} could not be read as an image.`, null, true);
        }
    }
    return converted;
}

window.isMemoraImageSourceFile = isMemoraImageSourceFile;
window.convertMemoraImageSourceFiles = convertMemoraImageSourceFiles;

async function getMemoraJsPdfConstructor() {
    if (!window.MemoraPdfCompressor && typeof window.MemoraRuntime?.ensureFeature === 'function') {
        await window.MemoraRuntime.ensureFeature('pdfCompressor');
    }
    if (typeof window.ensureMemoraJsPdfConstructor === 'function') {
        const jsPDF = await window.ensureMemoraJsPdfConstructor();
        if (typeof jsPDF === 'function') return jsPDF;
    }
    const jspdfModule = typeof window.ensureMemoraJsPdfLoaded === 'function'
        ? await window.ensureMemoraJsPdfLoaded()
        : window.jspdf;
    const { jsPDF } = jspdfModule || {};
    if (typeof jsPDF !== 'function') throw new Error('PDF library not loaded.');
    return jsPDF;
}

function setSavedPdfExportPending(isPending) {
    const button = els.btnDownloadPdf;
    if (!button) return;
    const label = button.querySelector('.gradient-text');
    if (isPending) {
        if (!Object.prototype.hasOwnProperty.call(button.dataset, 'memoraPdfIdleLabel')) {
            button.dataset.memoraPdfIdleLabel = label?.textContent || button.textContent || 'Download PDF';
            button.dataset.memoraPdfIdleTitle = button.title || '';
        }
        button.dataset.memoraPdfExportPending = 'true';
        button.disabled = true;
        button.setAttribute('aria-disabled', 'true');
        button.setAttribute('aria-busy', 'true');
        button.title = 'Preparing the saved-questions PDF.';
        if (label) label.textContent = 'Preparing PDF...';
    } else {
        delete button.dataset.memoraPdfExportPending;
        button.disabled = false;
        button.removeAttribute('aria-disabled');
        button.removeAttribute('aria-busy');
        button.title = button.dataset.memoraPdfIdleTitle || '';
        if (label) label.textContent = button.dataset.memoraPdfIdleLabel || 'Download PDF';
        delete button.dataset.memoraPdfIdleLabel;
        delete button.dataset.memoraPdfIdleTitle;
        syncContentDependentControls();
    }
}

// PDF Download Logic
async function downloadSavedAsPDF() {
    closeSavedExportMenu();
    const savedIndices = Object.keys(savedQuestions).map(Number).filter(i => !isNaN(i));
    const validSavedIndices = savedIndices.filter(idx => idx < questions.length);

    if (validSavedIndices.length === 0) {
        syncContentDependentControls();
        showPopupText('No saved questions are available yet.', null, true, 5000);
        return false;
    }

    setSavedPdfExportPending(true);
    try {
        if (typeof window.openMemoraHtmlPdfPreview === 'function') {
            const html = validSavedIndices.map((idx) => {
                const q = questions[idx];
                const imgSrc = q.imgUrl || q.img;
                return `<h3>Q${idx + 1}</h3><p>${escapeHtml(q.q)}</p>${imgSrc ? `<figure><img src="${escapeHtml(imgSrc)}" alt=""></figure>` : ''}<p><strong>Answer:</strong> ${escapeHtml(q.c?.[q.a] ?? '')}</p>`;
            }).join('');
            if (window.openMemoraHtmlPdfPreview({ html, title: 'Saved Questions Review' })) return true;
        }
        const jsPDF = await getMemoraJsPdfConstructor();
        if (typeof jsPDF !== 'function') throw new Error('PDF library not loaded.');

        const doc = new jsPDF();
    let y = 20;
    const pageHeight = doc.internal.pageSize.height;
    const pageWidth = doc.internal.pageSize.width;
    const margin = 15;
    const maxWidth = 180;
    const maxImgWidth = 100;
    const maxImgHeight = 60;

    doc.setFontSize(16);
    doc.text("Saved Questions Review", margin, y);
    y += 15;

    doc.setFontSize(11);

    for (let i = 0; i < validSavedIndices.length; i++) {
        const idx = validSavedIndices[i];
        const q = questions[idx];
        const imgSrc = q.imgUrl || q.img;

        // Calculate space needed
        let spaceNeeded = 20; // Base for question + answer
        if (imgSrc) spaceNeeded += maxImgHeight + 10;

        // Check page break before adding content
        if (y + spaceNeeded > pageHeight - 20) {
            doc.addPage();
            y = 20;
        }

        // Add image if present
        if (imgSrc) {
            try {
                // Load image and add to PDF
                const img = await loadImageAsDataURL(imgSrc);
                if (img) {
                    // Calculate scaled dimensions
                    const imgProps = doc.getImageProperties(img);
                    let imgWidth = imgProps.width;
                    let imgHeight = imgProps.height;

                    // Scale to fit
                    if (imgWidth > maxImgWidth) {
                        imgHeight = (imgHeight * maxImgWidth) / imgWidth;
                        imgWidth = maxImgWidth;
                    }
                    if (imgHeight > maxImgHeight) {
                        imgWidth = (imgWidth * maxImgHeight) / imgHeight;
                        imgHeight = maxImgHeight;
                    }

                    // Center the image
                    const imgX = margin;
                    doc.addImage(img, 'JPEG', imgX, y, imgWidth, imgHeight);
                    y += imgHeight + 5;
                }
            } catch (e) {
                console.warn('Could not add image to PDF:', e);
            }
        }

        const qText = `Q${idx + 1}: ${q.q}`;
        const aText = `Answer: ${q.c[q.a]}`;

        // Split text to fit width
        const splitQ = doc.splitTextToSize(qText, maxWidth);
        const splitA = doc.splitTextToSize(aText, maxWidth);

        doc.setTextColor(0, 0, 0);
        doc.text(splitQ, margin, y);
        y += (splitQ.length * 6) + 2;

        doc.setTextColor(0, 128, 0); // Green for answer
        doc.text(splitA, margin, y);
        y += (splitA.length * 6) + 10; // Spacing between items
    }

        doc.save("Saved_Questions.pdf");
        return true;
    } catch (error) {
        console.error('Saved-question PDF export failed:', error);
        showPopupText('PDF export could not be prepared. Try again.', null, true, 7000);
        return false;
    } finally {
        setSavedPdfExportPending(false);
    }
}

// Helper function to load image as data URL for PDF embedding
async function loadImageAsDataURL(url) {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => {
            const canvas = document.createElement('canvas');
            canvas.width = img.width;
            canvas.height = img.height;
            const ctx = canvas.getContext('2d');
            ctx.drawImage(img, 0, 0);
            try {
                resolve(canvas.toDataURL('image/jpeg', 0.8));
            } catch (e) {
                reject(e);
            }
        };
        img.onerror = () => reject(new Error('Failed to load image'));
        img.src = url;
    });
}

// Update Buttons to use new Modal
els.quizSavedMainBtn.onclick = openSavedModal;
if (els.quizSavedBtn) {
    els.quizSavedBtn.onclick = () => {
        if (isFlashcardAdaptiveReviewMode) deactivateFlashcardAdaptiveReviewMode(false);
        isFlashcardMode = false;
        els.modeBtn.querySelector('.gradient-text').innerText = "🎴 Switch to Flashcards";
        showQuiz();
    };
}
els.btnQuizSavedModal.onclick = startSavedQuizMode;
els.btnDownloadPdf.onclick = downloadSavedAsPDF;

// Export Quiz Modal
function openExportModal() {
    if (els.popup.classList.contains('active')) { els.popup.classList.remove('active'); currentPopupBtn = null; }
    const controlState = syncContentDependentControls();
    if (!controlState.hasCurrentQuestion && controlState.usableQuestionCount === 0) {
        showPopupText('No quiz questions are available yet.', null, true, 5000);
        return false;
    }
    $('export-modal').classList.add('active');
    return true;
}
window.openExportModal = openExportModal;

function closeExportModal(event) {
    if (event.target === $('export-modal') || event.target.closest('.welcome-btn')) {
        $('export-modal').classList.remove('active');
    }
}
window.closeExportModal = closeExportModal;

// --- Database Modal ---
const DB_FILE_LIMIT = 500;
const DB_FILE_TYPE_PDF_EXPLANATION = 'pdf_explanation';
const DB_FILE_TYPE_GUIDE = 'guide';
const MEMORA_SAVED_GUIDES_STORAGE_KEY = 'memora_saved_guides_v1';
const MEMORA_SAVED_GUIDES_LIMIT = 80;

async function getSessionPdfExplanationIndex(sessionId = null) {
    if (typeof window.getPdfExplanationIndex !== 'function') return {};
    try {
        const index = await window.getPdfExplanationIndex(sessionId || null);
        return (index && typeof index === 'object') ? index : {};
    } catch (err) {
        console.warn(`Failed to load PDF explanation index for session ${sessionId || 'current'}:`, err);
        return {};
    }
}

function getPdfExplanationDownloadFilename(pdfName) {
    const base = String(pdfName || '').replace(/\.pdf$/i, '').trim() || 'lecture';
    return `${base}_explanation.pdf`;
}

function slugifyMemoraSavedGuideName(seed = '', fallback = 'study-guide') {
    return String(seed || fallback)
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
        .slice(0, 64) || fallback;
}

function getMemoraSavedGuideDownloadFilename(record = {}, format = 'pdf') {
    const extension = String(format || '').trim().toLowerCase() === 'html' ? 'html' : 'pdf';
    const seed = record.filenameSeed || record.sourceLabel || record.title || 'study-guide';
    const base = slugifyMemoraSavedGuideName(seed, 'study-guide');
    return base.endsWith('study-guide')
        ? `${base}.${extension}`
        : `${base}-study-guide.${extension}`;
}

function normalizeMemoraSavedGuideRecord(raw = {}) {
    const markdown = String(raw.markdown || raw.text || '').trim();
    if (!markdown) return null;
    const generatedAt = Number(raw.generatedAt || raw.createdAt || Date.now()) || Date.now();
    const sessionId = normalizeSessionId(raw.sessionId || (typeof currentSessionId !== 'undefined' ? currentSessionId : ''));
    const id = String(raw.id || [
        'guide',
        sessionId || 'global',
        generatedAt,
        Math.random().toString(36).slice(2, 8)
    ].join('-')).trim();
    return {
        id,
        version: 1,
        guideType: String(raw.guideType || raw.type || 'study-guide').trim() || 'study-guide',
        sessionId,
        title: String(raw.title || 'Study Guide').trim() || 'Study Guide',
        sourceLabel: String(raw.sourceLabel || raw.sources || '').trim(),
        filenameSeed: String(raw.filenameSeed || raw.sourceLabel || raw.title || 'study-guide').trim(),
        markdown,
        generatedAt,
        modelLabel: String(raw.modelLabel || raw.model || '').trim()
    };
}

async function readMemoraSavedGuides() {
    try {
        const records = await IDB.getKey(MEMORA_SAVED_GUIDES_STORAGE_KEY);
        return (Array.isArray(records) ? records : [])
            .map(normalizeMemoraSavedGuideRecord)
            .filter(Boolean)
            .sort((a, b) => (b.generatedAt || 0) - (a.generatedAt || 0));
    } catch (error) {
        console.warn('Failed to read saved guides:', error);
        return [];
    }
}

async function writeMemoraSavedGuides(records = []) {
    const normalized = (Array.isArray(records) ? records : [])
        .map(normalizeMemoraSavedGuideRecord)
        .filter(Boolean)
        .sort((a, b) => (b.generatedAt || 0) - (a.generatedAt || 0))
        .slice(0, MEMORA_SAVED_GUIDES_LIMIT);
    await IDB.set(MEMORA_SAVED_GUIDES_STORAGE_KEY, normalized);
    return normalized;
}

async function saveMemoraGuideToDatabase(record = {}) {
    const normalized = normalizeMemoraSavedGuideRecord(record);
    if (!normalized) return null;
    try {
        const existing = await readMemoraSavedGuides();
        const withoutDuplicate = existing.filter((entry) => entry.id !== normalized.id);
        await writeMemoraSavedGuides([normalized, ...withoutDuplicate]);
        return normalized;
    } catch (error) {
        console.warn('Failed to save guide to database:', error);
        return null;
    }
}
window.saveMemoraGuideToDatabase = saveMemoraGuideToDatabase;

function getMemoraSavedGuidesForSession(records = [], sessionId = '') {
    const normalizedSessionId = normalizeSessionId(sessionId);
    if (!normalizedSessionId) return [];
    return (Array.isArray(records) ? records : [])
        .filter((record) => normalizeSessionId(record?.sessionId) === normalizedSessionId)
        .sort((a, b) => (b.generatedAt || 0) - (a.generatedAt || 0));
}

async function downloadMemoraSavedGuide(record = null, format = 'pdf') {
    const normalized = normalizeMemoraSavedGuideRecord(record);
    if (!normalized) throw new Error('Guide not found.');
    const downloadFormat = String(format || '').trim().toLowerCase() === 'html' ? 'html' : 'pdf';
    if (normalized.guideType === 'results-review' && downloadFormat === 'pdf') {
        if (window.openMemoraGuidePdfPreview?.({ markdown: normalized.markdown, title: 'Study report', sourceLabel: normalized.sourceLabel })) return true;
        const blob = await buildResultsAiReviewPdfBlob({
            title: normalized.title,
            text: normalized.markdown,
            generatedAt: normalized.generatedAt,
            modelLabel: normalized.modelLabel
        });
        triggerBlobDownload(blob, getMemoraSavedGuideDownloadFilename(normalized, downloadFormat));
        return true;
    }
    if (typeof window.downloadMemoraGuideExport === 'function') {
        return window.downloadMemoraGuideExport({
            markdown: normalized.markdown,
            title: normalized.title,
            sourceLabel: normalized.sourceLabel,
            filename: getMemoraSavedGuideDownloadFilename(normalized, downloadFormat),
            format: downloadFormat
        });
    }
    const markdownName = getMemoraSavedGuideDownloadFilename(normalized, 'md').replace(/\.pdf$/i, '.md');
    triggerBlobDownload(new Blob([normalized.markdown], { type: 'text/markdown;charset=utf-8' }), markdownName);
    return true;
}

function triggerBlobDownload(blob, filename) {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    try {
        window.dispatchEvent(new CustomEvent('memora:study-set-exported', {
            detail: { format, questionCount: dataToExport.length }
        }));
    } catch (_error) { }
    URL.revokeObjectURL(url);
}

async function buildPdfExplanationPdfBlob(pdfName, record) {
    const jsPDF = await getMemoraJsPdfConstructor();

    const doc = new jsPDF({ unit: 'pt', format: 'letter' });
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const marginLeft = 42;
    const marginRight = 42;
    const marginTop = 50;
    const marginBottom = 42;
    const maxWidth = pageWidth - marginLeft - marginRight;
    let y = marginTop;

    const writeBlock = (text, {
        fontSize = 11,
        fontStyle = 'normal',
        lineGap = 4,
        gapAfter = 8
    } = {}) => {
        const content = String(text || '').trim();
        if (!content) return;

        doc.setFont('helvetica', fontStyle);
        doc.setFontSize(fontSize);
        const lineHeight = fontSize + lineGap;
        const lines = doc.splitTextToSize(content, maxWidth);

        lines.forEach(line => {
            if (y + lineHeight > pageHeight - marginBottom) {
                doc.addPage();
                y = marginTop;
            }
            doc.text(line, marginLeft, y);
            y += lineHeight;
        });

        y += gapAfter;
    };

    const titleBase = String(pdfName || '').replace(/\.pdf$/i, '').trim() || 'Lecture';
    const generatedAt = record?.generatedAt ? new Date(record.generatedAt).toLocaleString() : '';

    writeBlock(`${titleBase} - PDF Explanation`, {
        fontSize: 15,
        fontStyle: 'bold',
        lineGap: 5,
        gapAfter: 12
    });
    writeBlock(`Source PDF: ${pdfName || 'Unknown'}`, { fontSize: 10, lineGap: 3, gapAfter: 4 });
    if (generatedAt) writeBlock(`Generated: ${generatedAt}`, { fontSize: 10, lineGap: 3, gapAfter: 4 });
    if (record?.scopeLabel) writeBlock(`Scope: ${record.scopeLabel}`, { fontSize: 10, lineGap: 3, gapAfter: 4 });
    if (record?.model) writeBlock(`Model: ${record.model}`, { fontSize: 10, lineGap: 3, gapAfter: 8 });
    if (record?.wasTruncated) {
        writeBlock('Note: This explanation may be truncated due to model output limits.', {
            fontSize: 9,
            lineGap: 3,
            gapAfter: 8
        });
    }

    writeBlock(record?.text || '', {
        fontSize: 11,
        lineGap: 4,
        gapAfter: 0
    });

    return doc.output('blob');
}

async function openDatabaseModal() {
    if (IS_EMBED_DEMO) {
        demoUnavailableFeature('Database manager');
        return;
    }
    if (els.popup.classList.contains('active')) { els.popup.classList.remove('active'); currentPopupBtn = null; }

    // Count items using existing sessionManifest
    const pdfCount = sessionManifest.pdfs.length;
    const jsonCount = sessionManifest.jsons.length;
    let explanationCount = 0;
    if (typeof currentSessionId === 'string' && currentSessionId.trim()) {
        const currentExplanationIndex = await getSessionPdfExplanationIndex(currentSessionId.trim());
        explanationCount = Object.keys(currentExplanationIndex).length;
    }
    const savedGuideRecords = await readMemoraSavedGuides();
    const totalGuideCount = savedGuideRecords.length;
    const totalFiles = pdfCount + jsonCount + explanationCount + totalGuideCount;
    let duplicateCount = 0;
    try {
        const duplicateEntries = await collectSavedDuplicateEntries();
        duplicateCount = duplicateEntries.length;
    } catch (err) {
        console.warn('Failed to collect duplicate entries for database modal:', err);
    }

    // Get saved sessions from IDB (same as history panel)
    let sessions = [];
    try {
        sessions = await IDB.getAll('sessions') || [];
        // Sort by timestamp (newest first)
        sessions.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
    } catch (e) { sessions = []; }

    const explanationIndexesBySession = {};
    const normalizedCurrentDbSessionId = normalizeSessionId(currentSessionId);
    const duplicateCountBySessionId = {};
    if (sessions.length > 0 && typeof window.getPdfExplanationIndex === 'function') {
        await Promise.all(sessions.map(async (s) => {
            const sid = typeof s?.id === 'string' ? s.id : '';
            if (!sid) return;
            explanationIndexesBySession[sid] = await getSessionPdfExplanationIndex(sid);
        }));
    }
    if (sessions.length > 0) {
        for (const session of sessions) {
            const sid = normalizeSessionId(session?.id);
            if (!sid) continue;
            try {
                const duplicateEntries = await collectDuplicateEntriesForQuestionSetKeys(
                    getSessionQuestionSetKeys(session),
                    { includeCurrentQuestions: sid === normalizedCurrentDbSessionId }
                );
                duplicateCountBySessionId[sid] = duplicateEntries.length;
            } catch (err) {
                duplicateCountBySessionId[sid] = 0;
                console.warn(`Failed to collect duplicate entries for session ${sid}:`, err);
            }
        }
    }

    // Update header info
    $('db-session-count').textContent = `${sessions.length} Session${sessions.length !== 1 ? 's' : ''}`;
    $('db-storage-info').textContent = `${totalFiles} / ${DB_FILE_LIMIT} files`;

    // Build content list HTML - sessions as main items
    let html = '';

    if (sessions.length === 0) {
        html = '<div style="text-align:center; color:#64748b; padding:40px;">No sessions saved yet</div>';
    } else {
        sessions.forEach((s, idx) => {
            // Format session info
            const date = new Date(s.timestamp);
            const fmtDate = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
            const displayName = s.customName || (s.activeSets && s.activeSets.length > 0 ? s.activeSets[0].replace('.json', '') : 'Session');
            const qCount = s.data && s.data.userAnswers ? Object.keys(s.data.userAnswers).length : 0;

            // Get PDFs and Quizzes for this session
            const sessionPdfs = s.activeSets ? s.activeSets.filter(f => f.endsWith('.pdf')) : [];
            const sessionJsons = s.activeSets ? s.activeSets.filter(f => f.endsWith('.json')) : [];

            // Also check manifest in session data if available (saved as 'manifest')
            const manifestPdfs = s.data?.manifest?.pdfs || [];
            const manifestJsons = s.data?.manifest?.jsons || s.activeSets || [];
            const allPdfs = [...new Set([...sessionPdfs, ...manifestPdfs])];
            const allJsons = [...new Set([...sessionJsons, ...manifestJsons])];
            const explanationIndex = explanationIndexesBySession[s.id] || {};
            const allExplanationPdfs = Object.keys(explanationIndex || {}).sort((a, b) => a.localeCompare(b));
            const normalizedSessionId = normalizeSessionId(s.id);
            const sessionGuides = getMemoraSavedGuidesForSession(savedGuideRecords, normalizedSessionId);
            const isCurrentSessionCard = Boolean(normalizedCurrentDbSessionId) && normalizedSessionId === normalizedCurrentDbSessionId;
            const shouldStartExpanded = idx === 0 || isCurrentSessionCard;
            const duplicateSessionCount = normalizedSessionId ? (duplicateCountBySessionId[normalizedSessionId] || 0) : 0;
            const duplicateActionTitle = duplicateSessionCount
                ? `Download duplicate questions (${duplicateSessionCount})`
                : 'No duplicate questions found in this session';
            html += `<div class="db-session-card${shouldStartExpanded ? '' : ' collapsed'}" id="db-session-${idx}">
                <div class="db-session-header" data-db-section-id="db-session-${idx}">
                    <div class="db-session-info">
                        <span class="db-session-name">${escapeHtml(displayName)}</span>
                        <span class="db-session-meta">${fmtDate} &bull; ${qCount} answered</span>
                    </div>
                    <div class="db-session-actions">
                        <button type="button" class="db-session-dup-btn${duplicateSessionCount ? '' : ' disabled'}" aria-disabled="${duplicateSessionCount ? 'false' : 'true'}" data-db-action="export-duplicates" data-session-id="${escapeHtml(s.id)}" title="${escapeHtml(duplicateActionTitle)}">
                            <span class="db-session-action-label">Duplicate Questions</span>
                            <span class="db-session-action-icon" aria-hidden="true">&#x2B07;</span>
                        </button>
                        <button type="button" class="db-download-all-btn" data-db-action="download-session" data-session-id="${escapeHtml(s.id)}" title="Download All Files">
                            <span class="db-session-action-label">All</span>
                            <span class="db-session-action-icon" aria-hidden="true">&#x2B07;</span>
                        </button>
                        <button type="button" class="db-item-del" data-db-action="delete-session" data-session-id="${escapeHtml(s.id)}" title="Delete Session">&times;</button>
                        <span class="toggle-icon">&#9660;</span>
                    </div>
                </div>
                <div class="db-session-content">
                    <div class="db-session-cols">
                        <div class="db-col">
                            <div class="db-col-header">Lectures (${allPdfs.length})</div>
                            <div class="db-col-items">
                                ${allPdfs.length ? allPdfs.map(p => `<div class="db-col-item"><span class="db-item-name">${escapeHtml(p.replace('.pdf', ''))}</span><button class="db-item-dl" data-db-action="download-file" data-db-filename="${escapeHtml(p)}" data-db-file-type="pdf" title="Download">⬇</button></div>`).join('') : '<div class="db-col-empty">No PDFs</div>'}
                            </div>
                        </div>
                        <div class="db-col">
                            <div class="db-col-header">Quizzes (${allJsons.length})</div>
                            <div class="db-col-items">
                                ${allJsons.length ? allJsons.map(j => `<div class="db-col-item"><span class="db-item-name">${escapeHtml(j.replace('.json', ''))}</span><button class="db-item-dl" data-db-action="download-file" data-db-filename="${escapeHtml(j)}" data-db-file-type="json" title="Download">⬇</button></div>`).join('') : '<div class="db-col-empty">No quizzes</div>'}
                            </div>
                        </div>
                        <div class="db-col">
                            <div class="db-col-header">Explanations (${allExplanationPdfs.length})</div>
                            <div class="db-col-items">
                                ${allExplanationPdfs.length ? allExplanationPdfs.map(p => {
                    const meta = explanationIndex[p] || {};
                    const titleBits = [];
                    if (meta.scopeLabel) titleBits.push(`Scope: ${meta.scopeLabel}`);
                    if (meta.model) titleBits.push(`Model: ${meta.model}`);
                    if (meta.updatedAt) {
                        const dateText = new Date(meta.updatedAt).toLocaleString();
                        if (dateText && dateText !== 'Invalid Date') titleBits.push(`Updated: ${dateText}`);
                    }
                    const itemTitle = titleBits.join(' | ');
                    return `<div class="db-col-item"><span class="db-item-name" title="${escapeHtml(itemTitle)}">${escapeHtml(p.replace(/\.pdf$/i, ''))} (Explanation)</span><button class="db-item-dl" data-db-action="download-file" data-db-filename="${escapeHtml(p)}" data-db-file-type="${DB_FILE_TYPE_PDF_EXPLANATION}" data-session-id="${escapeHtml(s.id)}" title="Download PDF">⬇</button></div>`;
                }).join('') : '<div class="db-col-empty">No explanations</div>'}
                            </div>
                        </div>
                        <div class="db-col">
                            <div class="db-col-header">Guides (${sessionGuides.length})</div>
                            <div class="db-col-items">
                                ${sessionGuides.length ? sessionGuides.map((guide) => {
                    const guideDate = guide.generatedAt ? new Date(guide.generatedAt).toLocaleString() : '';
                    const titleBits = [];
                    if (guide.sourceLabel) titleBits.push(`Source: ${guide.sourceLabel}`);
                    if (guide.modelLabel) titleBits.push(`Model: ${guide.modelLabel}`);
                    if (guideDate && guideDate !== 'Invalid Date') titleBits.push(`Generated: ${guideDate}`);
                    const itemTitle = titleBits.join(' | ');
                    return `<div class="db-col-item"><span class="db-item-name" title="${escapeHtml(itemTitle)}">${escapeHtml(guide.title || 'Study Guide')}</span><button class="db-item-dl" data-db-action="download-file" data-db-filename="${escapeHtml(guide.id)}" data-db-file-type="${DB_FILE_TYPE_GUIDE}" data-session-id="${escapeHtml(s.id)}" title="Download PDF">⬇</button></div>`;
                }).join('') : '<div class="db-col-empty">No guides</div>'}
                            </div>
                        </div>
                    </div>
                </div>
            </div>`;
        });
    }

    $('db-content-list').innerHTML = html;
    bindDatabaseContentActions($('db-content-list'));
    updateSavedDuplicateButtons(duplicateCount);
    document.querySelectorAll('#database-modal .db-danger-btn').forEach((button) => {
        const hasStoredData = totalFiles > 0 || sessions.length > 0;
        setContentDependentControlState(button, !hasStoredData, 'No saved data is available to clear.');
    });
    $('database-modal').classList.add('active');
}
window.openDatabaseModal = openDatabaseModal;

function bindDatabaseContentActions(container) {
    if (!container) return;
    container.onclick = (event) => {
        const actionTarget = event.target.closest('[data-db-action]');
        if (actionTarget) {
            event.stopPropagation();
            const sessionId = actionTarget.dataset.sessionId || '';
            const action = actionTarget.dataset.dbAction;
            if (action === 'export-duplicates') {
                if (actionTarget.getAttribute('aria-disabled') !== 'true') {
                    void exportDuplicateQuestionsForSession(sessionId, event);
                }
            } else if (action === 'download-session') {
                void downloadSessionFiles(sessionId);
            } else if (action === 'delete-session') {
                void deleteDatabaseItem('session', sessionId);
            } else if (action === 'download-file') {
                void downloadDbFile(actionTarget.dataset.dbFilename || '', actionTarget.dataset.dbFileType || '', sessionId || null);
            }
            return;
        }

        const header = event.target.closest('[data-db-section-id]');
        if (header) toggleDbSection(header.dataset.dbSectionId || '');
    };
}

// Toggle database section collapse
function toggleDbSection(sectionId) {
    const section = document.getElementById(sectionId);
    if (section) section.classList.toggle('collapsed');
}
window.toggleDbSection = toggleDbSection;

function closeDatabaseModal(event) {
    if (event.target === $('database-modal') || event.target.closest('.welcome-btn')) {
        $('database-modal').classList.remove('active');
    }
}
window.closeDatabaseModal = closeDatabaseModal;

function showStorageInfo() {
    const info = `Database: IndexedDB
Name: ${IDB.dbName}
Origin: ${window.location.origin}

IndexedDB is browser internal storage and cannot be opened in File Explorer.

To view raw data:
1. Press F12 to open DevTools
2. Go to Application tab
3. Expand IndexedDB in left sidebar
4. Click on "${IDB.dbName}"`;

    showPopup(escapeHtml(info).replace(/\n/g, '<br>'), null, false, { sticky: true, dismissible: true });
}
window.showStorageInfo = showStorageInfo;

async function deleteDatabaseItem(type, name) {
    const labelByType = {
        pdf: 'PDF',
        json: 'Quiz File',
        session: 'Session'
    };
    const targetLabel = labelByType[type] || 'Item';
    const message = type === 'session'
        ? `Delete "${name}"? It will be moved to Recently Deleted.`
        : `Delete "${name}"?`;
    const confirmed = await showThemeConfirm(message, {
        title: `Delete ${targetLabel}`,
        okText: 'Delete',
        cancelText: 'Cancel',
        danger: true
    });
    if (!confirmed) return;

    const normalizedTargetSessionId = type === 'session' ? normalizeSessionId(name) : '';
    const wasCurrentSession = !!normalizedTargetSessionId && normalizedTargetSessionId === normalizeSessionId(currentSessionId);
    const wasLastSession = !!normalizedTargetSessionId && normalizedTargetSessionId === normalizeSessionId(localStorage.getItem('lastSessionId'));

    try {
        if (type === 'pdf') {
            await IDB.del('files', name);
            forgetMemoraSourceRevision(name);
            sessionManifest.pdfs = sessionManifest.pdfs.filter(p => p !== name);
            if (loadedPdfFiles[name]) { URL.revokeObjectURL(loadedPdfFiles[name]); delete loadedPdfFiles[name]; }
        } else if (type === 'json') {
            await IDB.del('questions', name);
            sessionManifest.jsons = sessionManifest.jsons.filter(j => j !== name);
            delete loadedQuestionSets[name];
            // Also remove from active sets if loaded
            activeQuestionSetKeys.delete(name);
            await clearQuestionDuplicateReport(name, { clearArchive: true });
        } else if (type === 'session') {
            // Soft delete - move to recently deleted instead of permanent delete
            const deleted = await softDeleteSession(name);
            if (!deleted) throw new Error('Session could not be moved to Recently Deleted.');
            await syncAfterSavedSessionDelete(name, { wasCurrentSession, wasLastSession });
        }

        // Sync all views
        updateSourceDropdown(); // Evidence panel

        // Refresh history panel if open
        if (els.historyModal && els.historyModal.classList.contains('active')) {
            SessionManager.showHistory();
        }

        // Refresh database modal
        openDatabaseModal();

        showPopup('Deleted successfully', null, false);
    } catch (e) {
        console.error('Delete failed:', e);
        showPopup('Delete failed: ' + e.message, null, false);
    }
}
window.deleteDatabaseItem = deleteDatabaseItem;

// Download a single file from the database
async function downloadDbFile(filename, type, sessionId = null) {
    try {
        if (type === 'pdf') {
            const data = await IDB.get('files', filename);
            if (!data) {
                showPopup('PDF not found in database', null, true);
                return;
            }
            // Convert ArrayBuffer to Blob
            const blob = data instanceof Blob ? data : new Blob([data], { type: 'application/pdf' });
            triggerBlobDownload(blob, filename);
            showPopupText(`Downloaded ${filename}`, null, false);
        } else if (type === 'json') {
            const data = await IDB.get('questions', filename);
            if (!data) {
                showPopup('Quiz not found in database', null, true);
                return;
            }
            const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
            triggerBlobDownload(blob, filename.endsWith('.json') ? filename : filename + '.json');
            showPopupText(`Downloaded ${filename}`, null, false);
        } else if (type === DB_FILE_TYPE_PDF_EXPLANATION) {
            if (typeof window.getPdfExplanationRecord !== 'function') {
                showPopup('PDF explanation service is not available', null, true);
                return;
            }
            const record = await window.getPdfExplanationRecord(filename, sessionId || null);
            if (!record || !record.text) {
                showPopup('PDF explanation not found in database', null, true);
                return;
            }
            const blob = await buildPdfExplanationPdfBlob(filename, record);
            const downloadName = getPdfExplanationDownloadFilename(filename);
            triggerBlobDownload(blob, downloadName);
            showPopupText(`Downloaded ${downloadName}`, null, false);
        } else if (type === DB_FILE_TYPE_GUIDE) {
            const guides = await readMemoraSavedGuides();
            const record = guides.find((guide) => guide.id === filename && (!sessionId || normalizeSessionId(guide.sessionId) === normalizeSessionId(sessionId)));
            if (!record) {
                showPopup('Guide not found in database', null, true);
                return;
            }
            await downloadMemoraSavedGuide(record, 'pdf');
            showPopupText(`Downloaded ${getMemoraSavedGuideDownloadFilename(record, 'pdf')}`, null, false);
        }
    } catch (e) {
        console.error('Download failed:', e);
        showPopup('Download failed: ' + e.message, null, true);
    }
}
window.downloadDbFile = downloadDbFile;

// Download all files from a session
async function downloadSessionFiles(sessionId) {
    try {
        const session = await IDB.get('sessions', sessionId);
        if (!session) {
            showPopup('Session not found', null, true);
            return;
        }

        // Get PDFs and JSONs from session manifest
        const manifestPdfs = session.data?.manifest?.pdfs || [];
        const manifestJsons = session.data?.manifest?.jsons || session.activeSets || [];
        const explanationIndex = await getSessionPdfExplanationIndex(sessionId);
        const explanationPdfNames = Object.keys(explanationIndex || {});
        const guideRecords = getMemoraSavedGuidesForSession(await readMemoraSavedGuides(), sessionId);

        let downloadCount = 0;
        const totalFiles = manifestPdfs.length + manifestJsons.length + explanationPdfNames.length + guideRecords.length;

        if (totalFiles === 0) {
            showPopup('No files to download', null, true);
            return;
        }

        showPopup(`Downloading ${totalFiles} files...`, null, false);

        // Download PDFs
        for (const pdfName of manifestPdfs) {
            try {
                const data = await IDB.get('files', pdfName);
                if (data) {
                    const blob = data instanceof Blob ? data : new Blob([data], { type: 'application/pdf' });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = pdfName;
                    a.click();
                    URL.revokeObjectURL(url);
                    downloadCount++;
                    // Small delay between downloads
                    await new Promise(r => setTimeout(r, 300));
                }
            } catch (e) {
                console.warn(`Failed to download ${pdfName}:`, e);
            }
        }

        // Download JSONs
        for (const jsonName of manifestJsons) {
            try {
                const data = await IDB.get('questions', jsonName);
                if (data) {
                    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
                    triggerBlobDownload(blob, jsonName.endsWith('.json') ? jsonName : jsonName + '.json');
                    downloadCount++;
                    // Small delay between downloads
                    await new Promise(r => setTimeout(r, 300));
                }
            } catch (e) {
                console.warn(`Failed to download ${jsonName}:`, e);
            }
        }

        // Download generated PDF explanations
        for (const pdfName of explanationPdfNames) {
            try {
                if (typeof window.getPdfExplanationRecord !== 'function') continue;
                const record = await window.getPdfExplanationRecord(pdfName, sessionId);
                if (record && record.text) {
                    const blob = await buildPdfExplanationPdfBlob(pdfName, record);
                    triggerBlobDownload(blob, getPdfExplanationDownloadFilename(pdfName));
                    downloadCount++;
                    // Small delay between downloads
                    await new Promise(r => setTimeout(r, 300));
                }
            } catch (e) {
                console.warn(`Failed to download explanation for ${pdfName}:`, e);
            }
        }

        // Download generated study guides
        for (const guide of guideRecords) {
            try {
                await downloadMemoraSavedGuide(guide, 'pdf');
                downloadCount++;
                await new Promise(r => setTimeout(r, 300));
            } catch (e) {
                console.warn(`Failed to download guide ${guide?.title || guide?.id || ''}:`, e);
            }
        }

        showPopup(`Downloaded ${downloadCount} of ${totalFiles} files`, null, false);
    } catch (e) {
        console.error('Batch download failed:', e);
        showPopup('Download failed: ' + e.message, null, true);
    }
}
window.downloadSessionFiles = downloadSessionFiles;

// Generate quiz from a session's PDFs
async function generateQuizFromSession(sessionId) {
    try {
        const session = await IDB.get('sessions', sessionId);
        if (!session) {
            showPopup('Session not found', null, true);
            return;
        }

        const manifestPdfs = session.data?.manifest?.pdfs || [];
        if (manifestPdfs.length === 0) {
            showPopup('No PDFs in this session', null, true);
            return;
        }

        // Load PDFs from IndexedDB as File objects for the generation modal
        const pdfFiles = [];
        for (const pdfName of manifestPdfs) {
            const data = await IDB.get('files', pdfName);
            if (data) {
                const blob = data instanceof Blob ? data : new Blob([data], { type: 'application/pdf' });
                const file = new File([blob], pdfName, { type: 'application/pdf' });
                pdfFiles.push(file);
            }
        }

        if (pdfFiles.length === 0) {
            showPopup('Could not load PDFs from database', null, true);
            return;
        }

        // Close database modal
        $('database-modal').classList.remove('active');

        // Open quiz generation modal with these PDFs
        showQuizGenerationModal(pdfFiles);
        showPopup(`Loaded ${pdfFiles.length} PDFs for generation`, null, false);
    } catch (e) {
        console.error('Generate from session failed:', e);
        showPopup('Failed to load session: ' + e.message, null, true);
    }
}
window.generateQuizFromSession = generateQuizFromSession;

async function confirmClearDatabase() {
    const allSessions = await IDB.getAll('sessions');
    if (!allSessions.length) {
        showPopup('No saved sessions to delete.', null, true);
        return;
    }

    const confirmed = await showThemeConfirm(
        `This will move all ${allSessions.length} saved session${allSessions.length === 1 ? '' : 's'} to Recently Deleted.\n\nThey can be restored within 15 days.`,
        { title: 'Delete All Sessions', okText: 'Delete All', cancelText: 'Cancel', danger: true }
    );
    if (!confirmed) return;

    try {
        const currentSessionIdBeforeDelete = normalizeSessionId(currentSessionId);
        const deletingCurrentSession = allSessions.some((session) => normalizeSessionId(session?.id) === currentSessionIdBeforeDelete);
        const count = await softDeleteSavedSessionBatch(allSessions);
        if (count !== allSessions.length) {
            if (deletingCurrentSession && currentSessionIdBeforeDelete) {
                const currentStillSaved = await IDB.get('sessions', currentSessionIdBeforeDelete);
                if (!currentStillSaved) {
                    await syncAfterSavedSessionDelete(currentSessionIdBeforeDelete, { wasCurrentSession: true, wasLastSession: true });
                }
            }
            showPopup(`Moved ${count} of ${allSessions.length} sessions to Recently Deleted.`, null, true);
            openDatabaseModal();
            return;
        }
        resetRuntimeAfterDeletingSavedSessions();
        showPopup(`${count} session${count === 1 ? '' : 's'} moved to Recently Deleted. Reloading...`, null, false);

        // Reload the page to return to main menu
        setTimeout(() => location.reload(), 1000);
    } catch (e) {
        console.error('Reset failed:', e);
        showPopup('Reset failed: ' + e.message, null, true);
    }
}
window.confirmClearDatabase = confirmClearDatabase;

// --- Question Notes ---
let questionNotes = {}; // Legacy index-keyed notes (fallback only)
let questionNotesByQuestionKey = {};
let notesModalQuestionIndex = null;
let notesModalInitialValue = '';
// Where focus returns when the editor closes; docked question notes bypass the router's own return.
let notesModalReturnFocus = null;
const QUESTION_NOTES_STORAGE_KEY = 'questionNotes';
const QUESTION_NOTES_STORE_VERSION = 2;
const TUTOR_NOTES_STORAGE_KEY = 'pdfTutorSegmentNotes';
// A note save the user is waiting on cannot hang on a blocked database, so the
// write is bounded and a timeout counts as a failure rather than a silent stall.
const NOTES_PERSIST_TIMEOUT_MS = 5000;
// Additive recovery records. They hold what the reader typed but that storage
// has not yet confirmed, so a failed write or a reload does not lose the text.
const NOTES_RECOVERY_DRAFT_STORAGE_KEY = 'notesEditorRecoveryDrafts';
const NOTES_RECOVERY_DRAFT_STORE_VERSION = 1;
const NOTES_PERSIST_FAILURE_MESSAGE = 'Could not save this note to your device. Your text is still here — try saving again.';
const TUTOR_NOTES_STORE_VERSION = 2;
let tutorNotesByKey = {};
let tutorNotesStoreHydrated = false;
let notesModalContext = null;
let notesSurfaceAnchorElement = null;
let notesSurfaceAnchorRect = null;
let notesBrowserEntryContexts = new Map();
const NOTES_ALL_SESSIONS_SCOPE = 'sessions:all';
let notesBrowserSessionScope = 'current';
let notesBrowserPrioritySourceTokens = new Set();
let notesBrowserRenderToken = 0;
let questionNotesLegacyMigrated = false;
const NOTES_SURFACE_MARGIN = 16;
const NOTES_SURFACE_DOCKED_TOP = 24;
const NOTES_SURFACE_MIN_WIDTH = 380;
const NOTES_SURFACE_MAX_WIDTH = 560;
const QUIZ_SIDE_SURFACE_TOOLS_CLEARANCE = 32;
const NOTES_SURFACE_DEFAULT_WIDTH = 520;
const NOTES_SURFACE_MIN_HEIGHT = 340;
const NOTES_SURFACE_DEFAULT_HEIGHT = 520;
let notesSurfaceFloating = false;
let notesSurfaceDragging = false;

function triggerNotesSurfaceEnterAnimation() {
    const modalContent = $('notes-modal-content');
    if (!modalContent) return;
    if (modalContent.__notesSurfaceEnterTimer) {
        clearTimeout(modalContent.__notesSurfaceEnterTimer);
    }
    modalContent.classList.remove('surface-entering');
    modalContent.classList.remove('surface-preparing');
    void modalContent.offsetWidth;
    modalContent.classList.add('surface-entering');
    modalContent.__notesSurfaceEnterTimer = window.setTimeout(() => {
        modalContent.classList.remove('surface-entering');
        modalContent.__notesSurfaceEnterTimer = 0;
    }, 460);
}

function sanitizeQuestionNoteText(rawNote) {
    if (typeof rawNote !== 'string') return '';
    return rawNote.trim();
}

function getQuestionNoteKeyByIndex(questionIndex) {
    if (!Number.isInteger(questionIndex) || questionIndex < 0 || questionIndex >= questions.length) return '';
    return buildQuestionNoteKeyFromQuestion(questions[questionIndex]);
}

// Notes another writer (a session duplicate) just committed are taken into memory and into the
// sync baseline together, so this tab shows them and its next save does not count them as edits.
function adoptStoredQuestionNotes(entries = {}) {
    const sync = notesStoreSyncState.question;
    Object.entries(entries || {}).forEach(([key, rawNote]) => {
        const note = sanitizeQuestionNoteText(rawNote);
        const normalizedKey = String(key || '').trim();
        if (!normalizedKey || !note) return;
        questionNotesByQuestionKey[normalizedKey] = note;
        setOrDeleteNotesSyncEntry(sync.memory, `k:${normalizedKey}`, notesStoreJson(note));
        setOrDeleteNotesSyncEntry(sync.stored, `k:${normalizedKey}`, notesStoreJson(note));
    });
}

function markQuestionNotesNeedsMigration() {
    questionNotesLegacyMigrated = false;
}

function migrateLegacyQuestionNotesToQuestionKeys({ persist = true } = {}) {
    if (questionNotesLegacyMigrated) return;
    if (!questionNotes || typeof questionNotes !== 'object') {
        questionNotesLegacyMigrated = true;
        return;
    }
    if (!Array.isArray(questions) || questions.length === 0) return;

    let changed = false;
    Object.entries({ ...questionNotes }).forEach(([idxKey, rawNote]) => {
        const idx = Number(idxKey);
        if (!Number.isInteger(idx) || idx < 0 || idx >= questions.length) return;
        const note = sanitizeQuestionNoteText(rawNote);
        if (!note) {
            delete questionNotes[idx];
            changed = true;
            return;
        }
        const qKey = getQuestionNoteKeyByIndex(idx);
        if (!qKey) return;
        const existingNote = sanitizeQuestionNoteText(questionNotesByQuestionKey[qKey]);
        if (!existingNote) {
            questionNotesByQuestionKey[qKey] = note;
        } else if (existingNote !== note) {
            // Two different texts claim the same question. Dropping the legacy
            // one loses writing the student cannot get back, so it stays put and
            // surfaces as an unlinked older note instead of being overwritten.
            return;
        }
        delete questionNotes[idx];
        changed = true;
    });

    questionNotesLegacyMigrated = true;
    if (changed && persist) void persistQuestionNotesStore();
}

function ensureQuestionNotesLegacyMigration() {
    if (!questionNotesLegacyMigrated) {
        migrateLegacyQuestionNotesToQuestionKeys({ persist: true });
    }
}

// A note saved under the question's legacy identity (before comparison text was kept
// in keys), when that identity is unambiguous among the loaded questions.
function getLegacyQuestionNoteKeyByIndex(questionIndex) {
    const question = Number.isInteger(questionIndex) ? questions?.[questionIndex] : null;
    const noteKey = getQuestionNoteKeyByIndex(questionIndex);
    if (!question || !noteKey) return '';
    const legacyKey = buildQuestionPersistenceKeyFromQuestion(question, normalizeLegacyQuestionPersistencePart);
    const currentKey = buildQuestionPersistenceKeyFromQuestion(question);
    if (!legacyKey || legacyKey === currentKey) return '';
    if (getLegacyQuestionKeyAliases([legacyKey]).get(legacyKey) !== currentKey) return '';
    return legacyKey + noteKey.slice(currentKey.length);
}

function getQuestionNoteForIndex(questionIndex) {
    ensureQuestionNotesLegacyMigration();
    const noteKey = getQuestionNoteKeyByIndex(questionIndex);
    if (noteKey) {
        const keyedNote = questionNotesByQuestionKey ? questionNotesByQuestionKey[noteKey] : '';
        const normalizedKeyedNote = sanitizeQuestionNoteText(keyedNote);
        if (normalizedKeyedNote) return normalizedKeyedNote;
        const legacyNoteKey = getLegacyQuestionNoteKeyByIndex(questionIndex);
        const legacyKeyedNote = legacyNoteKey ? sanitizeQuestionNoteText(questionNotesByQuestionKey?.[legacyNoteKey]) : '';
        if (legacyKeyedNote) return legacyKeyedNote;
    }
    const legacyNote = questionNotes ? questionNotes[questionIndex] : '';
    return sanitizeQuestionNoteText(legacyNote);
}

function setQuestionNoteForIndex(questionIndex, noteText) {
    ensureQuestionNotesLegacyMigration();
    if (!Number.isInteger(questionIndex) || questionIndex < 0) return;
    const note = sanitizeQuestionNoteText(noteText);
    const noteKey = getQuestionNoteKeyByIndex(questionIndex);
    // Writing moves a legacy-keyed note to the current key.
    const legacyNoteKey = noteKey ? getLegacyQuestionNoteKeyByIndex(questionIndex) : '';
    if (legacyNoteKey) delete questionNotesByQuestionKey[legacyNoteKey];
    if (note) {
        if (noteKey) {
            questionNotesByQuestionKey[noteKey] = note;
            delete questionNotes[questionIndex];
        } else {
            questionNotes[questionIndex] = note;
        }
        return;
    }
    if (noteKey) delete questionNotesByQuestionKey[noteKey];
    delete questionNotes[questionIndex];
}

function serializeQuestionNotesStore() {
    const payload = {
        v: QUESTION_NOTES_STORE_VERSION,
        byQuestionKey: { ...(questionNotesByQuestionKey || {}) }
    };
    // Legacy index-keyed notes can only be migrated once their bank is loaded.
    // Until then they have to survive every unrelated save: emitting only
    // byQuestionKey means one note written while the quiz is unloaded destroys
    // every unmigrated legacy note permanently.
    const legacyByIndex = {};
    Object.entries(questionNotes || {}).forEach(([indexKey, rawNote]) => {
        const index = Number(indexKey);
        const note = sanitizeQuestionNoteText(rawNote);
        if (!Number.isInteger(index) || index < 0 || !note) return;
        legacyByIndex[index] = note;
    });
    if (Object.keys(legacyByIndex).length) payload.legacyByIndex = legacyByIndex;
    return payload;
}

// Notes are written as changes against the stored record, never as this tab's
// whole snapshot: a snapshot written by a tab that loaded earlier erases every
// note another tab saved since. Each store remembers, per note, the in-memory
// and stored value as of the last load or commit. A save sends only the notes
// that changed since then, and the transaction applies them to whatever is
// stored now. Notes this tab never touched are left as stored, so a deletion
// made elsewhere cannot be undone by a stale tab, and a note that another tab
// changed since this tab last saw it is a conflict rather than an overwrite.
const notesStoreSyncState = {
    question: { memory: new Map(), stored: new Map(), chain: Promise.resolve(), lastFailure: '' },
    tutor: { memory: new Map(), stored: new Map(), chain: Promise.resolve(), lastFailure: '' }
};
const NOTES_PERSIST_CONFLICT_MESSAGE = 'This note was changed in another tab. Your text is still here — save again to keep your version.';

function notesStoreJson(value) {
    return value === undefined ? undefined : JSON.stringify(value);
}

function setOrDeleteNotesSyncEntry(map, key, json) {
    if (json === undefined) map.delete(key);
    else map.set(key, json);
}

// Question notes flatten to `k:<question key>` and `i:<legacy index>` so the
// keyed store and the unmigrated legacy notes are diffed as one set.
function readNotesMemoryFlat(kind) {
    const flat = new Map();
    if (kind === 'tutor') {
        Object.entries(tutorNotesByKey || {}).forEach(([key, record]) => {
            if (record && typeof record === 'object') flat.set(key, record);
        });
        return flat;
    }
    Object.entries(questionNotesByQuestionKey || {}).forEach(([key, note]) => {
        const clean = sanitizeQuestionNoteText(note);
        if (key && clean) flat.set(`k:${key}`, clean);
    });
    Object.entries(questionNotes || {}).forEach(([indexKey, note]) => {
        const index = Number(indexKey);
        const clean = sanitizeQuestionNoteText(note);
        if (Number.isInteger(index) && index >= 0 && clean) flat.set(`i:${index}`, clean);
    });
    return flat;
}

// One entry of readNotesMemoryFlat, read directly: a save diffs every key it touches, and
// rebuilding the whole flat map per key made large note stores quadratic.
function readNotesMemoryValue(kind, flatKey) {
    const key = String(flatKey || '');
    if (kind === 'tutor') {
        const record = tutorNotesByKey && Object.hasOwn(tutorNotesByKey, key) ? tutorNotesByKey[key] : undefined;
        return record && typeof record === 'object' ? record : undefined;
    }
    const id = key.slice(2);
    if (key.startsWith('k:')) {
        if (!id || !questionNotesByQuestionKey || !Object.hasOwn(questionNotesByQuestionKey, id)) return undefined;
        return sanitizeQuestionNoteText(questionNotesByQuestionKey[id]) || undefined;
    }
    if (!key.startsWith('i:') || !questionNotes) return undefined;
    const index = Number(id);
    if (!Number.isInteger(index) || index < 0 || String(index) !== id) return undefined;
    // The unmigrated legacy map is small; scanning it keeps the flat map's rule exactly (the last
    // spelling of an index with non-empty text wins).
    let value;
    Object.entries(questionNotes).forEach(([indexKey, note]) => {
        if (Number(indexKey) !== index) return;
        const clean = sanitizeQuestionNoteText(note);
        if (clean) value = clean;
    });
    return value;
}

// Stored values are kept raw, so notes this tab does not change are written
// back exactly as another tab left them.
function readNotesStoredFlat(kind, raw) {
    const flat = new Map();
    if (!raw || typeof raw !== 'object') return flat;
    if (kind === 'tutor') {
        const source = raw.byKey && typeof raw.byKey === 'object' && !Array.isArray(raw.byKey) ? raw.byKey : raw;
        Object.entries(source).forEach(([key, record]) => {
            if (key && record && typeof record === 'object') flat.set(key, record);
        });
        return flat;
    }
    const keyed = raw.byQuestionKey;
    if (keyed && typeof keyed === 'object' && !Array.isArray(keyed)) {
        Object.entries(keyed).forEach(([key, note]) => {
            if (key && note !== undefined && note !== null) flat.set(`k:${key}`, note);
        });
        const legacy = raw.legacyByIndex;
        if (legacy && typeof legacy === 'object' && !Array.isArray(legacy)) {
            Object.entries(legacy).forEach(([index, note]) => {
                if (note !== undefined && note !== null) flat.set(`i:${index}`, note);
            });
        }
        return flat;
    }
    Object.entries(raw).forEach(([index, note]) => {
        if (Number.isInteger(Number(index)) && note !== undefined && note !== null) flat.set(`i:${index}`, note);
    });
    return flat;
}

function buildNotesStoreRecord(kind, flat, raw) {
    if (kind === 'tutor') {
        const keepsEnvelope = raw && typeof raw === 'object' && raw.byKey && typeof raw.byKey === 'object';
        const byKey = {};
        flat.forEach((record, key) => { byKey[key] = record; });
        return { ...(keepsEnvelope ? raw : {}), v: TUTOR_NOTES_STORE_VERSION, byKey };
    }
    const keepsEnvelope = raw && typeof raw === 'object' && raw.byQuestionKey && typeof raw.byQuestionKey === 'object';
    const payload = { ...(keepsEnvelope ? raw : {}), v: QUESTION_NOTES_STORE_VERSION, byQuestionKey: {} };
    const legacyByIndex = {};
    flat.forEach((note, flatKey) => {
        if (flatKey.startsWith('k:')) payload.byQuestionKey[flatKey.slice(2)] = note;
        else if (flatKey.startsWith('i:')) legacyByIndex[flatKey.slice(2)] = note;
    });
    if (Object.keys(legacyByIndex).length) payload.legacyByIndex = legacyByIndex;
    else delete payload.legacyByIndex;
    return payload;
}

// Brings one note this tab has not changed up to date with what storage holds.
function applyStoredNoteToMemory(kind, flatKey, rawValue) {
    if (kind === 'tutor') {
        const record = rawValue === undefined ? null : normalizeTutorNoteRecord(rawValue, flatKey);
        if (record) tutorNotesByKey[flatKey] = record;
        else delete tutorNotesByKey[flatKey];
        return;
    }
    const note = rawValue === undefined ? '' : sanitizeQuestionNoteText(rawValue);
    if (flatKey.startsWith('k:')) {
        const key = flatKey.slice(2);
        if (note) questionNotesByQuestionKey[key] = note;
        else delete questionNotesByQuestionKey[key];
    } else if (flatKey.startsWith('i:')) {
        const index = Number(flatKey.slice(2));
        if (note) questionNotes[index] = note;
        else delete questionNotes[index];
    }
}

// Called once the stored record has been read into memory, before anything
// edits it, so later saves can tell this tab's changes from everyone else's.
function captureNotesStoreBaseline(kind, raw) {
    const sync = notesStoreSyncState[kind];
    sync.memory = new Map();
    sync.stored = new Map();
    readNotesMemoryFlat(kind).forEach((value, key) => sync.memory.set(key, notesStoreJson(value)));
    readNotesStoredFlat(kind, raw).forEach((value, key) => sync.stored.set(key, notesStoreJson(value)));
}

function getNotesPersistFailureMessage(kind) {
    return notesStoreSyncState[kind]?.lastFailure === 'conflict'
        ? NOTES_PERSIST_CONFLICT_MESSAGE
        : NOTES_PERSIST_FAILURE_MESSAGE;
}

async function commitNotesStoreChanges(kind) {
    const sync = notesStoreSyncState[kind];
    const storageKey = kind === 'tutor' ? TUTOR_NOTES_STORAGE_KEY : QUESTION_NOTES_STORAGE_KEY;
    const current = readNotesMemoryFlat(kind);
    const changes = new Map();
    new Set([...current.keys(), ...sync.memory.keys()]).forEach((key) => {
        const value = current.get(key);
        if (notesStoreJson(value) !== sync.memory.get(key)) changes.set(key, value);
    });
    sync.lastFailure = '';
    if (!changes.size) return true;

    const outcome = await withMemoraStorageTimeout(
        () => IDB.compareAndPutSetting(storageKey, (raw) => {
            const storedFlat = readNotesStoredFlat(kind, raw);
            const conflicts = [];
            changes.forEach((value, key) => {
                const now = notesStoreJson(storedFlat.get(key));
                if (now !== sync.stored.get(key) && now !== notesStoreJson(value)) {
                    conflicts.push({ key, stored: storedFlat.get(key) });
                }
            });
            if (conflicts.length) return { skip: true, conflicts };
            changes.forEach((value, key) => {
                if (value === undefined) storedFlat.delete(key);
                else storedFlat.set(key, value);
            });
            return { value: buildNotesStoreRecord(kind, storedFlat, raw), storedFlat };
        }, NOTES_PERSIST_TIMEOUT_MS),
        NOTES_PERSIST_TIMEOUT_MS,
        kind === 'tutor' ? 'Tutor notes save' : 'Question notes save'
    );

    if (outcome?.conflicts?.length) {
        outcome.conflicts.forEach(({ key, stored }) => {
            // A removal loses to an edit made elsewhere: that note stays. Text
            // this tab wrote is kept in memory and acknowledged against the
            // newer stored copy, so saving again is a deliberate replacement.
            if (changes.get(key) === undefined) {
                applyStoredNoteToMemory(kind, key, stored);
                setOrDeleteNotesSyncEntry(sync.memory, key, notesStoreJson(readNotesMemoryValue(kind, key)));
            }
            setOrDeleteNotesSyncEntry(sync.stored, key, notesStoreJson(stored));
        });
        sync.lastFailure = 'conflict';
        console.warn(`${kind === 'tutor' ? 'Tutor' : 'Question'} note save refused: changed in another tab.`, outcome.conflicts.map(c => c.key));
        return false;
    }

    const committed = outcome?.storedFlat instanceof Map ? outcome.storedFlat : new Map();
    const latest = readNotesMemoryFlat(kind);
    new Set([...committed.keys(), ...sync.stored.keys(), ...sync.memory.keys(), ...changes.keys()]).forEach((key) => {
        const expected = changes.has(key) ? notesStoreJson(changes.get(key)) : sync.memory.get(key);
        if (notesStoreJson(latest.get(key)) !== expected) {
            // Edited again while this write was in flight: the next save owns it.
            if (changes.has(key)) {
                setOrDeleteNotesSyncEntry(sync.stored, key, notesStoreJson(committed.get(key)));
                setOrDeleteNotesSyncEntry(sync.memory, key, expected);
            }
            return;
        }
        if (!changes.has(key)) applyStoredNoteToMemory(kind, key, committed.get(key));
        setOrDeleteNotesSyncEntry(sync.stored, key, notesStoreJson(committed.get(key)));
        setOrDeleteNotesSyncEntry(sync.memory, key, notesStoreJson(readNotesMemoryValue(kind, key)));
    });
    return true;
}

// Saves are serialized per store so each one diffs against the baseline the
// previous one left behind.
function persistNotesStoreChanges(kind) {
    const sync = notesStoreSyncState[kind];
    const run = sync.chain.then(() => commitNotesStoreChanges(kind));
    sync.chain = run.then(() => {}, () => {});
    return run;
}

// Resolves true only once the write has actually landed. It never rejects, so
// the fire-and-forget callers are unaffected, but a caller that tells the user
// "saved" can wait for the real answer instead of assuming one.
function persistQuestionNotesStore() {
    return persistNotesStoreChanges('question')
        .then((saved) => saved === true)
        .catch((err) => {
            notesStoreSyncState.question.lastFailure = 'failed';
            console.warn('Failed to persist question notes:', err);
            return false;
        });
}

function hydrateQuestionNotesStore(rawStore) {
    questionNotes = {};
    questionNotesByQuestionKey = {};
    markQuestionNotesNeedsMigration();
    if (!rawStore || typeof rawStore !== 'object') return;

    // Preferred stable format.
    const byQuestionKeyRaw = rawStore.byQuestionKey;
    if (byQuestionKeyRaw && typeof byQuestionKeyRaw === 'object' && !Array.isArray(byQuestionKeyRaw)) {
        Object.entries(byQuestionKeyRaw).forEach(([key, value]) => {
            const normalizedKey = String(key || '').trim();
            const note = sanitizeQuestionNoteText(value);
            if (!normalizedKey || !note) return;
            questionNotesByQuestionKey[normalizedKey] = note;
        });
        // Legacy notes that never found a question ride along in the versioned
        // record. They are read back here so they stay recoverable instead of
        // being dropped the first time the keyed store is written.
        const legacyByIndexRaw = rawStore.legacyByIndex;
        if (legacyByIndexRaw && typeof legacyByIndexRaw === 'object' && !Array.isArray(legacyByIndexRaw)) {
            Object.entries(legacyByIndexRaw).forEach(([indexKey, value]) => {
                const index = Number(indexKey);
                const note = sanitizeQuestionNoteText(value);
                if (!Number.isInteger(index) || index < 0 || !note) return;
                questionNotes[index] = note;
            });
        }
        return;
    }

    // Legacy index-based store fallback; it will be migrated after questions load.
    Object.entries(rawStore).forEach(([idxKey, value]) => {
        const idx = Number(idxKey);
        const note = sanitizeQuestionNoteText(value);
        if (!Number.isInteger(idx) || idx < 0 || !note) return;
        questionNotes[idx] = note;
    });
}

function normalizeTutorNoteKey(rawKey) {
    return String(rawKey || '').trim();
}

function normalizeTutorNoteImageDataUrl(rawValue) {
    const value = String(rawValue || '').trim();
    if (!value || value.length > 6000000) return '';
    return /^data:image\/(?:png|jpe?g|webp);base64,[a-z0-9+/=\s]+$/i.test(value) ? value : '';
}

function normalizeTutorNoteType(rawValue, imageDataUrl = '') {
    return String(rawValue || '').trim().toLowerCase() === 'image' || imageDataUrl ? 'image' : 'text';
}

function normalizeTutorNoteRegion(region) {
    if (!region || typeof region !== 'object') return null;
    const keys = ['x', 'y', 'width', 'height'];
    const normalized = {};
    keys.forEach((key) => {
        const value = Number(region[key]);
        if (Number.isFinite(value)) normalized[key] = Math.max(0, Math.min(1, value));
    });
    if (!Number.isFinite(normalized.width) || !Number.isFinite(normalized.height)) return null;
    ['objectId', 'id', 'label', 'targetText', 'targetType', 'targetKind', 'source', 'sourceSurface'].forEach((key) => {
        const value = String(region[key] || '').trim();
        if (value) normalized[key] = value.slice(0, 180);
    });
    return normalized;
}

function normalizeTutorNoteContext(rawContext = {}) {
    const key = normalizeTutorNoteKey(rawContext.key || rawContext.noteKey);
    if (!key) return null;
    const pdfName = String(rawContext.pdfName || rawContext.sourceTitle || '').trim();
    const pageNumber = Math.max(1, Math.round(Number(rawContext.pageNumber || rawContext.page || 1) || 1));
    const segmentIndex = Math.max(0, Math.round(Number(rawContext.segmentIndex) || 0));
    const regionIndex = Math.max(0, Math.round(Number(rawContext.regionIndex) || 0));
    const title = String(rawContext.title || `Slide ${pageNumber} segment ${segmentIndex + 1}`).trim();
    const preview = String(rawContext.preview || rawContext.targetText || rawContext.label || '').trim();
    const imageDataUrl = normalizeTutorNoteImageDataUrl(rawContext.imageDataUrl);
    const inferredTargetType = key.startsWith('study-guide::') || /^Study Guide highlight$/i.test(title)
        ? 'study-guide'
        : (segmentIndex >= 9999 ? 'source' : 'guided');
    const targetType = ['study-guide', 'guided', 'source'].includes(String(rawContext.targetType || '').trim())
        ? String(rawContext.targetType).trim()
        : inferredTargetType;
    return {
        type: 'tutor',
        key,
        sessionId: normalizeSessionId(rawContext.sessionId),
        pdfName,
        sourceTitle: String(rawContext.sourceTitle || pdfName || 'Tutor Notes').trim(),
        pageNumber,
        segmentIndex,
        regionIndex,
        title: title.slice(0, 140),
        preview: preview.slice(0, 420),
        noteType: normalizeTutorNoteType(rawContext.noteType, imageDataUrl),
        snippetNote: rawContext.snippetNote === true || String(rawContext.noteMode || '').trim().toLowerCase() === 'snippet',
        imageDataUrl,
        imageAlt: String(rawContext.imageAlt || '').trim().slice(0, 220),
        targetType,
        targetId: String(rawContext.targetId || '').trim().slice(0, 180),
        studyGuideDetailMode: String(rawContext.studyGuideDetailMode || '').trim().slice(0, 40),
        fromNotesBrowser: rawContext.fromNotesBrowser === true,
        returnTo: normalizeNotesReturnDescriptor(rawContext),
        region: normalizeTutorNoteRegion(rawContext.region),
        anchorEl: rawContext.anchorEl instanceof Element ? rawContext.anchorEl : null,
        anchorRect: rawContext.anchorRect && typeof rawContext.anchorRect === 'object' ? rawContext.anchorRect : null
    };
}

function normalizeTutorNoteRecord(rawRecord = {}, fallbackKey = '') {
    const key = normalizeTutorNoteKey(rawRecord.key || fallbackKey);
    const note = sanitizeQuestionNoteText(rawRecord.note || rawRecord.text || '');
    const imageDataUrl = normalizeTutorNoteImageDataUrl(rawRecord.imageDataUrl);
    const preview = String(rawRecord.preview || rawRecord.targetText || rawRecord.label || '').trim().slice(0, 420);
    const snippetNote = rawRecord.snippetNote === true || String(rawRecord.noteMode || '').trim().toLowerCase() === 'snippet';
    if (!key || (!note && !imageDataUrl && !(snippetNote && preview))) return null;
    const context = normalizeTutorNoteContext({
        ...rawRecord,
        imageDataUrl,
        key
    }) || { type: 'tutor', key };
    return {
        v: TUTOR_NOTES_STORE_VERSION,
        key,
        note,
        sessionId: normalizeSessionId(context.sessionId || rawRecord.sessionId),
        pdfName: String(context.pdfName || rawRecord.pdfName || '').trim(),
        sourceTitle: String(context.sourceTitle || rawRecord.sourceTitle || rawRecord.pdfName || 'Tutor Notes').trim(),
        pageNumber: Math.max(1, Math.round(Number(context.pageNumber || rawRecord.pageNumber || 1) || 1)),
        segmentIndex: Math.max(0, Math.round(Number(context.segmentIndex || rawRecord.segmentIndex) || 0)),
        regionIndex: Math.max(0, Math.round(Number(context.regionIndex || rawRecord.regionIndex) || 0)),
        title: String(context.title || rawRecord.title || 'Tutor note').trim().slice(0, 140),
        preview: String(context.preview || preview).trim().slice(0, 420),
        noteType: normalizeTutorNoteType(context.noteType || rawRecord.noteType, context.imageDataUrl || imageDataUrl),
        snippetNote: context.snippetNote === true || snippetNote,
        imageDataUrl: context.imageDataUrl || imageDataUrl,
        imageAlt: String(context.imageAlt || rawRecord.imageAlt || '').trim().slice(0, 220),
        targetType: context.targetType,
        targetId: String(context.targetId || rawRecord.targetId || '').trim().slice(0, 180),
        studyGuideDetailMode: String(context.studyGuideDetailMode || rawRecord.studyGuideDetailMode || '').trim().slice(0, 40),
        region: normalizeTutorNoteRegion(context.region || rawRecord.region),
        createdAt: Number(rawRecord.createdAt) || Number(rawRecord.updatedAt) || Date.now(),
        updatedAt: Number(rawRecord.updatedAt) || Date.now()
    };
}

function serializeTutorNotesStore() {
    return {
        v: TUTOR_NOTES_STORE_VERSION,
        byKey: { ...(tutorNotesByKey || {}) }
    };
}

function hydrateTutorNotesStore(rawStore) {
    tutorNotesByKey = {};
    // Anything that prunes by "does this key have a note?" has to know the answer is
    // real and not just an empty map that has not loaded yet.
    tutorNotesStoreHydrated = true;
    if (!rawStore || typeof rawStore !== 'object') return;
    const source = rawStore.byKey && typeof rawStore.byKey === 'object' && !Array.isArray(rawStore.byKey)
        ? rawStore.byKey
        : rawStore;
    Object.entries(source).forEach(([key, value]) => {
        const record = normalizeTutorNoteRecord(value, key);
        if (record) tutorNotesByKey[record.key] = record;
    });
}

// Same contract as persistQuestionNotesStore: true means the write landed.
function persistTutorNotesStore() {
    return persistNotesStoreChanges('tutor')
        .then((saved) => saved === true)
        .catch((err) => {
            notesStoreSyncState.tutor.lastFailure = 'failed';
            console.warn('Failed to persist Tutor notes:', err);
            return false;
        });
}

function getTutorNoteForKey(key) {
    const normalizedKey = normalizeTutorNoteKey(key);
    if (!normalizedKey) return '';
    return sanitizeQuestionNoteText(tutorNotesByKey?.[normalizedKey]?.note || '');
}

function hasTutorNoteForKey(key) {
    const normalizedKey = normalizeTutorNoteKey(key);
    return !!(normalizedKey && normalizeTutorNoteRecord(tutorNotesByKey?.[normalizedKey], normalizedKey));
}

function setTutorNoteForContext(context, noteText) {
    const normalizedContext = normalizeTutorNoteContext(context);
    if (!normalizedContext) return null;
    const note = sanitizeQuestionNoteText(noteText);
    const previous = tutorNotesByKey[normalizedContext.key] || {};
    const imageDataUrl = normalizeTutorNoteImageDataUrl(normalizedContext.imageDataUrl || previous.imageDataUrl);
    const snippetNote = normalizedContext.snippetNote === true || previous.snippetNote === true;
    const preview = String(normalizedContext.preview || previous.preview || '').trim().slice(0, 420);
    if (!note && !imageDataUrl && !(snippetNote && preview)) {
        delete tutorNotesByKey[normalizedContext.key];
        return null;
    }
    const record = normalizeTutorNoteRecord({
        ...previous,
        ...normalizedContext,
        note,
        preview,
        snippetNote,
        imageDataUrl,
        noteType: normalizeTutorNoteType(normalizedContext.noteType || previous.noteType, imageDataUrl),
        createdAt: previous.createdAt || Date.now(),
        updatedAt: Date.now()
    }, normalizedContext.key);
    if (record) tutorNotesByKey[record.key] = record;
    return record;
}

function getQuestionNoteEntries() {
    ensureQuestionNotesLegacyMigration();
    const entries = [];
    const questionKeyIndex = new Map();
    if (Array.isArray(questions)) {
        questions.forEach((question, idx) => {
            const key = buildQuestionNoteKeyFromQuestion(question);
            if (key && !questionKeyIndex.has(key)) questionKeyIndex.set(key, idx);
        });
    }
    Object.entries(questionNotesByQuestionKey || {}).forEach(([key, rawNote]) => {
        const note = sanitizeQuestionNoteText(rawNote);
        if (!key || !note) return;
        const idx = questionKeyIndex.has(key) ? questionKeyIndex.get(key) : -1;
        const question = idx >= 0 ? questions[idx] : null;
        const sourceTitle = String(question?.sourceFile || question?.src || question?.setName || key.split('::')[0] || 'Question Notes').trim() || 'Question Notes';
        const title = idx >= 0 ? `Question ${idx + 1}` : 'Question note';
        const preview = question ? getPlainStudyText(question.q || question.question || '') : 'Question is not currently loaded.';
        entries.push({
            kind: 'question',
            key,
            note,
            sourceTitle,
            title,
            preview,
            questionIndex: idx,
            updatedAt: 0,
            context: {
                type: 'question',
                questionIndex: idx,
                questionKey: key
            }
        });
    });
    Object.entries(questionNotes || {}).forEach(([idxKey, rawNote]) => {
        const idx = Number(idxKey);
        const note = sanitizeQuestionNoteText(rawNote);
        if (!Number.isInteger(idx) || idx < 0 || !note) return;
        const question = questions?.[idx] || null;
        const sourceTitle = String(question?.sourceFile || question?.src || question?.setName || 'Question Notes').trim() || 'Question Notes';
        entries.push({
            kind: 'question',
            key: `legacy:${idx}`,
            note,
            sourceTitle,
            title: `Question ${idx + 1}`,
            preview: question ? getPlainStudyText(question.q || question.question || '') : 'Question is not currently loaded.',
            questionIndex: idx,
            updatedAt: 0,
            context: {
                type: 'question',
                questionIndex: idx
            }
        });
    });
    return entries;
}

function getTutorNoteEntries() {
    const getStoredTutorNotePreview = (record = {}) => {
        const preview = String(record.preview || `Slide ${record.pageNumber || 1}`).trim();
        if (!String(record.key || '').startsWith('evidence-pdf::')) return preview;
        const cleaned = preview
            .replace(/\bstudent[- ]selected source(?: PDF)? (?:area|region|text)\b/gi, ' ')
            .replace(/\ba custom (?:text |visual )?area selected by the student in the source PDF panel\.?/gi, ' ')
            .replace(/\s+/g, ' ')
            .trim();
        if (!cleaned) return `Selected source area on slide ${record.pageNumber || 1}`;
        const words = cleaned.split(' ');
        for (const repetitions of [3, 2]) {
            if (words.length % repetitions !== 0) continue;
            const chunkLength = words.length / repetitions;
            const firstChunk = words.slice(0, chunkLength).join(' ');
            const repeated = Array.from({ length: repetitions }, (_, index) => (
                words.slice(index * chunkLength, (index + 1) * chunkLength).join(' ')
            ));
            if (repeated.every((chunk) => chunk === firstChunk)) return firstChunk;
        }
        return cleaned;
    };
    return Object.values(tutorNotesByKey || {})
        .map((record) => normalizeTutorNoteRecord(record, record?.key))
        .filter(Boolean)
        .map((record) => {
            const preview = getStoredTutorNotePreview(record);
            return {
                kind: 'tutor',
                key: record.key,
                note: record.note,
                sourceTitle: record.sourceTitle || record.pdfName || 'Tutor Notes',
                title: record.title || `Slide ${record.pageNumber} segment ${record.segmentIndex + 1}`,
                preview,
                pdfName: record.pdfName,
                pageNumber: record.pageNumber,
                segmentIndex: record.segmentIndex,
                regionIndex: record.regionIndex,
                noteType: record.noteType,
                snippetNote: record.snippetNote === true,
                imageDataUrl: record.imageDataUrl,
                imageAlt: record.imageAlt,
                sessionId: record.sessionId,
                updatedAt: record.updatedAt || 0,
                context: {
                    type: 'tutor',
                    ...record,
                    preview
                }
            };
        });
}

function getAllNotesEntries() {
    const priorityTokens = notesBrowserPrioritySourceTokens instanceof Set ? notesBrowserPrioritySourceTokens : new Set();
    const currentSourceTokens = collectNotesSourceTokens(getCurrentNotesSessionSourceValues());
    const getSourceScore = (entry) => {
        const entryTokens = getNotesEntrySourceTokens(entry);
        if (priorityTokens.size) {
            for (const token of entryTokens) {
                if (priorityTokens.has(token)) return 0;
            }
        }
        if (currentSourceTokens.size) {
            for (const token of entryTokens) {
                if (currentSourceTokens.has(token)) return 1;
            }
        }
        return 2;
    };
    return [...getQuestionNoteEntries(), ...getTutorNoteEntries()]
        .sort((a, b) => {
            const scoreCompare = getSourceScore(a) - getSourceScore(b);
            if (scoreCompare) return scoreCompare;
            const sourceCompare = String(a.sourceTitle || '').localeCompare(String(b.sourceTitle || ''));
            if (sourceCompare) return sourceCompare;
            const pageCompare = (Number(a.pageNumber) || 0) - (Number(b.pageNumber) || 0);
            if (pageCompare) return pageCompare;
            return (Number(b.updatedAt) || 0) - (Number(a.updatedAt) || 0);
        });
}

function normalizeNotesBrowserSessionScope(value = 'current') {
    const raw = String(value || '').trim();
    if (raw === NOTES_ALL_SESSIONS_SCOPE || raw === 'all-sessions') return NOTES_ALL_SESSIONS_SCOPE;
    if (raw === 'all') return 'all';
    if (raw === 'current') return 'current';
    if (raw.startsWith('session:')) {
        const sessionId = normalizeSessionId(raw.slice('session:'.length));
        if (sessionId) return `session:${sessionId}`;
    }
    return 'current';
}

function normalizeNotesSourceToken(value = '') {
    return String(value || '')
        .replace(/\.(json|txt|pdf)$/i, '')
        .replace(/\bstudy\s+guide\b/gi, '')
        .replace(/[_-]+/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()
        .toLowerCase();
}

function collectNotesSourceTokens(values = []) {
    const tokens = new Set();
    (Array.isArray(values) ? values : [values]).forEach((value) => {
        const raw = String(value || '').trim();
        if (!raw) return;
        [
            raw,
            raw.replace(/\.(json|txt|pdf)$/i, ''),
            raw.replace(/\bstudy\s+guide\b/gi, ''),
            raw.replace(/\bstudy\s+guide\b/gi, '').replace(/\.(json|txt|pdf)$/i, '')
        ].forEach((variant) => {
            const token = normalizeNotesSourceToken(variant);
            if (token) tokens.add(token);
        });
    });
    return tokens;
}

function getNotesEntrySourceTokens(entry = {}) {
    const values = [
        entry.sourceTitle,
        entry.pdfName,
        entry.context?.sourceTitle,
        entry.context?.pdfName
    ];
    if (entry.kind === 'question' && entry.key) {
        values.push(String(entry.key).split('::')[0]);
    }
    return collectNotesSourceTokens(values);
}

function getNotesBrowserDisplayNote(entry = {}) {
    const note = sanitizeQuestionNoteText(entry.note);
    if (!note) return '';
    const prefixMatch = note.match(/^(Study Guide highlight|Ask Memora highlight):\s*/i);
    const displayNote = prefixMatch ? note.slice(prefixMatch[0].length).trim() : note;
    const preview = sanitizeQuestionNoteText(entry.preview);
    const normalizeForCompare = (value = '') => String(value || '')
        .replace(/\s+/g, ' ')
        .trim()
        .toLowerCase();
    if (preview && normalizeForCompare(displayNote) === normalizeForCompare(preview)) return '';
    return displayNote;
}

function getCurrentNotesSessionSourceValues() {
    const values = [];
    if (Array.isArray(sessionManifest?.jsons)) values.push(...sessionManifest.jsons);
    if (Array.isArray(sessionManifest?.pdfs)) values.push(...sessionManifest.pdfs);
    values.push(...Object.keys(loadedQuestionSets || {}));
    values.push(...Object.keys(loadedPdfFiles || {}));
    if (Array.isArray(questions)) {
        questions.forEach((question) => {
            values.push(question?.sourceFile, question?.src, question?.setName);
        });
    }
    return values.filter(Boolean);
}

function getCurrentNotesActiveSourceValues() {
    const values = [];
    const currentQuestionIndex = Array.isArray(filteredIndices) && Number.isInteger(currentQIndex)
        ? filteredIndices[currentQIndex]
        : -1;
    const question = Number.isInteger(currentQuestionIndex) && currentQuestionIndex >= 0
        ? questions?.[currentQuestionIndex]
        : null;
    if (question) {
        values.push(question.sourceFile, question.src, question.setName);
    }
    values.push(
        lastPdfState?.fileName,
        lastPdfState?.title,
        normalizePdfStateTitleToName?.(els?.pdfTitle?.innerText || '')
    );
    return values.filter(Boolean);
}

function getNotesSessionSourceValues(session = null) {
    const manifest = (session?.data && typeof session.data === 'object' && session.data.manifest && typeof session.data.manifest === 'object')
        ? session.data.manifest
        : ((session?.manifest && typeof session.manifest === 'object') ? session.manifest : {});
    return [
        ...(Array.isArray(session?.activeSets) ? session.activeSets : []),
        ...(Array.isArray(manifest?.jsons) ? manifest.jsons : []),
        ...(Array.isArray(manifest?.pdfs) ? manifest.pdfs : [])
    ].filter(Boolean);
}

function createNotesSessionDescriptor({ id = '', label = '', sourceValues = [], timestamp = '', current = false } = {}) {
    return {
        id: normalizeSessionId(id) || (current ? 'current' : ''),
        label: String(label || '').trim() || (current ? getCurrentSessionDisplayLabel() : 'Session'),
        timestamp,
        current: !!current,
        sourceTokens: collectNotesSourceTokens(sourceValues)
    };
}

function createCurrentNotesSourceDescriptor(currentSessionDescriptor = null) {
    const priorityTokens = notesBrowserPrioritySourceTokens instanceof Set ? notesBrowserPrioritySourceTokens : new Set();
    const sourceTokens = priorityTokens.size
        ? new Set(priorityTokens)
        : collectNotesSourceTokens(getCurrentNotesActiveSourceValues());
    if (!sourceTokens.size) return currentSessionDescriptor;
    return {
        id: normalizeSessionId(currentSessionDescriptor?.id) || normalizeSessionId(currentSessionId) || 'current',
        label: 'Current source',
        timestamp: currentSessionDescriptor?.timestamp || '',
        current: true,
        sourceOnly: true,
        sourceTokens
    };
}

function noteEntryMatchesSourceTokens(entry = {}, sourceTokens = new Set()) {
    if (!sourceTokens?.size) return false;
    const entryTokens = getNotesEntrySourceTokens(entry);
    for (const token of entryTokens) {
        if (sourceTokens.has(token)) return true;
    }
    return false;
}

function noteEntryMatchesSession(entry = {}, descriptor = null) {
    const entrySessionId = normalizeSessionId(entry.sessionId || entry.context?.sessionId);
    const descriptorSessionId = normalizeSessionId(descriptor?.id);
    const requiresSourceMatch = descriptor?.sourceOnly === true;
    if (entrySessionId) {
        if (!descriptorSessionId || entrySessionId !== descriptorSessionId) return false;
        return requiresSourceMatch ? noteEntryMatchesSourceTokens(entry, descriptor.sourceTokens) : true;
    }
    if (!descriptor?.sourceTokens?.size) return false;
    return noteEntryMatchesSourceTokens(entry, descriptor.sourceTokens);
}

function countNotesForSession(entries = [], descriptor = null) {
    return entries.reduce((count, entry) => count + (noteEntryMatchesSession(entry, descriptor) ? 1 : 0), 0);
}

async function getSavedNotesSessionDescriptors() {
    if (typeof IDB === 'undefined' || typeof IDB.getAll !== 'function') return [];
    try {
        const sessions = await IDB.getAll('sessions') || [];
        return sessions
            .filter((session) => hasPersistableSavedSessionContent(session))
            .map((session) => createNotesSessionDescriptor({
                id: session?.id,
                label: formatWelcomeSessionDisplayName(session),
                sourceValues: getNotesSessionSourceValues(session),
                timestamp: session?.timestamp || ''
            }))
            .filter((descriptor) => descriptor.id && descriptor.sourceTokens.size > 0)
            .sort((a, b) => (Date.parse(b.timestamp || '') || 0) - (Date.parse(a.timestamp || '') || 0));
    } catch (error) {
        console.warn('Failed to load sessions for My Notes filter:', error);
        return [];
    }
}

async function getNotesBrowserSessionOptions(entries = []) {
    const currentSessionDescriptor = createNotesSessionDescriptor({
        id: currentSessionId,
        label: getCurrentSessionDisplayLabel(),
        sourceValues: getCurrentNotesSessionSourceValues(),
        current: true
    });
    const currentSourceDescriptor = createCurrentNotesSourceDescriptor(currentSessionDescriptor);
    const savedDescriptors = await getSavedNotesSessionDescriptors();
    const options = [{
        value: 'current',
        label: 'Current',
        sessionName: 'Current source',
        count: countNotesForSession(entries, currentSourceDescriptor),
        descriptor: currentSourceDescriptor
    }];
    const currentId = normalizeSessionId(currentSessionDescriptor.id);
    const seenSessionIds = new Set(currentId ? [currentId] : []);
    savedDescriptors.forEach((descriptor) => {
        const descriptorId = normalizeSessionId(descriptor.id);
        if (!descriptorId || seenSessionIds.has(descriptorId)) return;
        const count = countNotesForSession(entries, descriptor);
        if (count <= 0) return;
        seenSessionIds.add(descriptorId);
        options.push({
            value: `session:${descriptorId}`,
            label: descriptor.label,
            sessionName: descriptor.label,
            count,
            descriptor
        });
    });
    options.push({
        value: 'all',
        label: 'All',
        sessionName: 'Current session',
        count: countNotesForSession(entries, currentSessionDescriptor),
        descriptor: currentSessionDescriptor
    });
    options.push({
        value: NOTES_ALL_SESSIONS_SCOPE,
        label: 'All',
        sessionName: 'All sessions',
        count: entries.length,
        descriptor: null
    });
    return options;
}

function getEffectiveNotesBrowserSessionScope(options = []) {
    const normalized = normalizeNotesBrowserSessionScope(notesBrowserSessionScope);
    if (options.some((option) => option.value === normalized)) return normalized;
    return normalized === 'all' ? 'all' : 'current';
}

function getNotesBrowserScopedEntries(entries = [], options = [], scope = 'current') {
    const normalizedScope = normalizeNotesBrowserSessionScope(scope);
    if (normalizedScope === NOTES_ALL_SESSIONS_SCOPE) return entries;
    const option = options.find((item) => item.value === normalizedScope);
    if (!option?.descriptor) return [];
    return entries.filter((entry) => noteEntryMatchesSession(entry, option.descriptor));
}

function getNotesBrowserSessionFilterHtml(options = [], activeScope = 'current', visibleCount = 0, totalCount = 0) {
    const currentOption = options.find((option) => option.value === 'current') || { count: 0, sessionName: 'Current session' };
    const allOption = options.find((option) => option.value === 'all') || {
        value: 'all',
        sessionName: 'Current session',
        count: totalCount
    };
    const allSessionsOption = options.find((option) => option.value === NOTES_ALL_SESSIONS_SCOPE) || null;
    const sessionOptions = [
        allOption,
        allSessionsOption,
        ...options.filter((option) => String(option.value || '').startsWith('session:'))
    ].filter(Boolean);
    const externalSessionScope = activeScope === NOTES_ALL_SESSIONS_SCOPE || activeScope.startsWith('session:');
    const selectValue = externalSessionScope ? activeScope : 'all';
    const allBadgeCount = (activeScope === NOTES_ALL_SESSIONS_SCOPE || activeScope.startsWith('session:'))
        ? totalCount
        : (allOption.count || 0);
    const allButtonScope = activeScope === 'current' ? 'all' : activeScope;
    const selectHtml = sessionOptions.length
        ? `
            <label class="notes-session-select-label">
                <span>Session</span>
                <select class="notes-session-select" data-notes-session-select aria-label="Choose notes session">
                    ${sessionOptions.map((option) => {
                        const label = `${option.sessionName || option.label || 'Session'} (${option.count})`;
                        return `<option value="${escapeHtml(option.value)}" ${option.value === selectValue ? 'selected' : ''}>${escapeHtml(label)}</option>`;
                    }).join('')}
                </select>
            </label>
        `
        : '';
    return `
        <div class="notes-session-filter" aria-label="Notes session filter">
            <div class="notes-session-toggle-group${externalSessionScope ? ' is-current-hidden' : ''}" role="group" aria-label="Notes scope">
                <button type="button" class="notes-session-toggle notes-session-toggle--current ${activeScope === 'current' ? 'active' : ''}${externalSessionScope ? ' is-scope-unavailable' : ''}" data-notes-session-scope="current" aria-pressed="${activeScope === 'current' ? 'true' : 'false'}" aria-hidden="${externalSessionScope ? 'true' : 'false'}" tabindex="${externalSessionScope ? '-1' : '0'}">
                    <span>Current</span>
                    <small>${currentOption.count || 0}</small>
                </button>
                <button type="button" class="notes-session-toggle ${activeScope !== 'current' ? 'active' : ''}" data-notes-session-scope="${escapeHtml(allButtonScope)}" aria-pressed="${activeScope !== 'current' ? 'true' : 'false'}">
                    <span>All</span>
                    <small>${allBadgeCount}</small>
                </button>
            </div>
            ${selectHtml}
            <small class="notes-session-count">${visibleCount} of ${totalCount}</small>
            <div class="notes-action-row">
                <button type="button" class="welcome-btn primary notes-gen-guide-btn" onclick="openGuideSourceSelection(event)">Generate Guide</button>
            </div>
        </div>
    `;
}

async function renderNotesBrowser({ animateScopeChange = false } = {}) {
    const browser = $('notes-browser');
    if (!browser) return;
    const renderToken = ++notesBrowserRenderToken;
    const shouldAnimateScopeChange = !!animateScopeChange
        && getMemoraMotionMode() !== 'reduced';
    if (shouldAnimateScopeChange && browser.childElementCount > 0) {
        browser.classList.remove('is-scope-entering');
        browser.classList.add('is-scope-leaving');
        await new Promise((resolve) => setTimeout(resolve, 170));
        if (renderToken !== notesBrowserRenderToken) return;
    }
    const commitBrowserHtml = (html = '') => {
        browser.innerHTML = html;
        browser.classList.remove('is-scope-leaving', 'is-scope-entering');
        if (!shouldAnimateScopeChange) return;
        browser.classList.add('is-scope-entering');
        requestAnimationFrame(() => requestAnimationFrame(() => {
            if (renderToken === notesBrowserRenderToken) browser.classList.remove('is-scope-entering');
        }));
    };
    notesBrowserEntryContexts = new Map();
    const entries = getAllNotesEntries();
    if (!entries.length) {
        commitBrowserHtml('<div class="notes-empty-state">No notes saved yet.</div>');
        return;
    }

    const sessionOptions = await getNotesBrowserSessionOptions(entries);
    if (renderToken !== notesBrowserRenderToken) return;
    const activeScope = getEffectiveNotesBrowserSessionScope(sessionOptions);
    notesBrowserSessionScope = activeScope;
    const scopedEntries = getNotesBrowserScopedEntries(entries, sessionOptions, activeScope);
    const currentOption = sessionOptions.find((option) => option.value === 'current') || null;
    const allOption = sessionOptions.find((option) => option.value === 'all') || null;
    const allSessionsOption = sessionOptions.find((option) => option.value === NOTES_ALL_SESSIONS_SCOPE) || null;
    const activeOption = sessionOptions.find((option) => option.value === activeScope) || null;
    const selectedOption = activeScope === 'current'
        ? currentOption
        : (activeScope === 'all' ? allOption : (activeScope === NOTES_ALL_SESSIONS_SCOPE ? null : activeOption));
    const totalCount = activeScope === NOTES_ALL_SESSIONS_SCOPE
        ? (allSessionsOption?.count ?? entries.length)
        : (activeScope === 'current'
            ? (allOption?.count ?? scopedEntries.length)
            : (selectedOption?.count ?? scopedEntries.length));
    const filterHtml = getNotesBrowserSessionFilterHtml(sessionOptions, activeScope, scopedEntries.length, totalCount);
    if (!scopedEntries.length) {
        const emptyText = activeScope === 'current'
            ? 'No notes saved for the current source yet.'
            : (activeScope === 'all'
                ? 'No notes saved for the current session yet.'
                : (activeScope === NOTES_ALL_SESSIONS_SCOPE
                    ? 'No notes saved in any session yet.'
                    : 'No notes saved for this session yet.'));
        commitBrowserHtml(`${filterHtml}<div class="notes-empty-state">${emptyText}</div>`);
        return;
    }

    const groups = new Map();
    scopedEntries.forEach((entry) => {
        const groupKey = `${entry.kind}:${entry.noteType || 'text'}:${entry.sourceTitle || 'Notes'}`;
        if (!groups.has(groupKey)) {
            groups.set(groupKey, {
                title: entry.sourceTitle || 'Notes',
                kind: entry.kind,
                noteType: entry.noteType || 'text',
                entries: []
            });
        }
        groups.get(groupKey).entries.push(entry);
    });

    const groupHtml = Array.from(groups.values()).map((group, groupIndex) => {
        const rows = group.entries.map((entry, entryIndex) => {
            const entryId = `notes-entry-${groupIndex}-${entryIndex}`;
            const ownerOption = selectedOption
                || (currentOption?.descriptor && noteEntryMatchesSession(entry, currentOption.descriptor)
                    ? currentOption
                    : sessionOptions.find((option) => option.value.startsWith('session:') && noteEntryMatchesSession(entry, option.descriptor)));
            const ownerSessionId = ownerOption?.descriptor?.current
                ? normalizeSessionId(currentSessionId)
                : normalizeSessionId(ownerOption?.descriptor?.id);
            notesBrowserEntryContexts.set(entryId, {
                ...entry.context,
                targetSessionId: ownerSessionId,
                targetSessionName: String(ownerOption?.sessionName || ownerOption?.label || '').trim()
            });
            const suppressPreview = entry.kind === 'tutor' && (
                !!entry.imageDataUrl
                || entry.noteType === 'image'
                || /^Source image\b/i.test(String(entry.title || ''))
            );
            const preview = !suppressPreview && entry.preview ? `<div class="notes-entry-preview">${escapeHtml(entry.preview)}</div>` : '';
            const image = entry.imageDataUrl
                ? `<figure class="notes-entry-image-preview"><img src="${entry.imageDataUrl}" alt="${escapeHtml(entry.imageAlt || entry.title || 'Saved source highlight')}"></figure>`
                : '';
            const displayNote = getNotesBrowserDisplayNote(entry);
            const note = displayNote ? `<div class="notes-entry-note">${escapeHtml(displayNote).replace(/\n/g, '<br>')}</div>` : '';
            const canOpen = !!ownerOption && ((entry.kind === 'question' && (
                (Number.isInteger(entry.questionIndex) && entry.questionIndex >= 0)
                || !!entry.context?.questionKey
            )) || (entry.kind === 'tutor' && entry.pdfName && entry.pdfName !== 'Ask Memora'));
            return `
                <article class="notes-entry notes-entry-${entry.kind}${entry.imageDataUrl || entry.noteType === 'image' ? ' notes-entry-image' : ''}" data-note-entry-id="${escapeHtml(entryId)}" tabindex="0">
                    <div class="notes-entry-main">
                        <div class="notes-entry-title">${escapeHtml(entry.title || 'Note')}</div>
                        ${preview}
                        ${image}
                        ${note}
                    </div>
                    <div class="notes-entry-actions">
                        ${canOpen ? `<button type="button" class="glass-btn notes-entry-action" data-note-action="open" data-note-entry-id="${escapeHtml(entryId)}">Open</button>` : ''}
                        <button type="button" class="glass-btn notes-entry-action" data-note-action="edit" data-note-entry-id="${escapeHtml(entryId)}">Edit</button>
                        <button type="button" class="glass-btn notes-entry-action" data-note-action="delete" data-note-entry-id="${escapeHtml(entryId)}">Delete</button>
                    </div>
                </article>
            `;
        }).join('');
        const cleanedTitle = String(group.title || '')
            .replace(/\.[^.]+$/, '')
            .replace(/[_-]+/g, ' ')
            .replace(/\s+/g, ' ')
            .trim();
        const iconSvg = `<svg class="notes-group-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" style="width:13px; height:13px; opacity:0.85; flex-shrink:0; transform:translateY(1px);"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline></svg>`;
        return `
            <section class="notes-browser-group">
                <div class="notes-browser-heading">
                    <span class="notes-group-title-wrap">
                        ${iconSvg}
                        <span class="notes-group-title-text">${escapeHtml(cleanedTitle)}</span>
                    </span>
                    <small class="notes-group-tag tag-${group.noteType === 'image' ? 'image' : group.kind}">${group.noteType === 'image' ? 'Source' : (group.kind === 'tutor' ? 'Tutor' : 'Quiz')} Notes</small>
                </div>
                <div class="notes-browser-list">${rows}</div>
            </section>
        `;
    }).join('');
    commitBrowserHtml(`${filterHtml}${groupHtml}`);
}

function findQuestionIndexForNoteContext(context = {}) {
    const directIndex = Number(context?.questionIndex);
    if (Number.isInteger(directIndex) && directIndex >= 0 && directIndex < questions.length) {
        const requestedKey = String(context?.questionKey || '').trim();
        if (!requestedKey || buildQuestionPersistenceKeyByIndex(directIndex) === requestedKey) return directIndex;
    }
    const questionKey = String(context?.questionKey || '').trim();
    if (!questionKey) return -1;
    return questions.findIndex((question) => buildQuestionPersistenceKeyFromQuestion(question) === questionKey);
}

// A note can belong to a quiz this session still holds while the open pool leaves it out.
function findInactiveQuestionBankForNoteContext(context = {}) {
    const questionKey = String(context?.questionKey || '').trim();
    if (!questionKey) return '';
    return (sessionManifest?.jsons || []).find((key) => {
        const aliasKeys = [key, String(key || '').replace(/\.(json|txt)$/i, '')];
        if (aliasKeys.some((alias) => activeQuestionSetKeys.has(alias))) return false;
        const sourceKey = aliasKeys.find((alias) => loadedQuestionSets[alias] !== undefined);
        const set = sourceKey ? normalizeStoredQuestionCollection(loadedQuestionSets[sourceKey], sourceKey) : null;
        return Array.isArray(set) && set.some((question) => buildQuestionPersistenceKeyFromQuestion(
            normalizeLegacyQuizQuestion(question, question?.sourceFile || question?.src || '')) === questionKey);
    }) || '';
}

function jumpToQuestionNoteContext(context) {
    let idx = findQuestionIndexForNoteContext(context);
    const inactiveBank = idx < 0 ? findInactiveQuestionBankForNoteContext(context) : '';
    if (idx < 0 && !inactiveBank) {
        showPopup('That quiz question is not available in this session.', null, true);
        return false;
    }
    // Open lands on the visible quiz card, not on a question changed behind Saved Content.
    const routing = window.MemoraLiveLeftRailRouting;
    if (routing?.currentRoute?.().name === 'saved-content') {
        return Promise.resolve(routing.returnToQuizzes()).then((opened) => opened !== false && jumpToQuestionNoteContext(context));
    }
    if (inactiveBank) {
        // The bank joins the pool; parked progress returns with it (rebuildActiveQuestions).
        activeQuestionSetKeys.add(inactiveBank);
        rebuildActiveQuestions(false);
        updateSourceDropdown();
        idx = findQuestionIndexForNoteContext(context);
        if (idx < 0) {
            showPopup('That quiz question is not available in this session.', null, true);
            return false;
        }
    }
    closeNotesModal({ target: $('notes-modal') });
    if (typeof closeSavedModal === 'function') closeSavedModal();
    currentQIndex = filteredIndices.indexOf(idx);
    if (currentQIndex === -1) {
        resetFilter();
        currentQIndex = filteredIndices.indexOf(idx);
        if (currentQIndex === -1) currentQIndex = 0;
    }
    isFlashcardMode = false;
    if (typeof setModeToggleButtonLabel === 'function') setModeToggleButtonLabel(false);
    showQuiz();
    return true;
}

async function confirmNotesSessionSwitch(context = {}) {
    const targetSessionId = normalizeSessionId(context.targetSessionId);
    const activeSessionId = normalizeSessionId(currentSessionId);
    if (!targetSessionId || targetSessionId === activeSessionId) return true;
    const sessionName = String(context.targetSessionName || 'that session').trim();
    const confirmed = await showThemeConfirm(
        `This note belongs to a different session (${sessionName}). Would you like to go to it?`,
        {
            title: 'Open Different Session?',
            okText: 'Go to Session',
            cancelText: 'Stay Here'
        }
    );
    if (!confirmed) return false;
    closeNotesModal({ target: $('notes-modal') });
    await SessionManager.load(targetSessionId);
    return normalizeSessionId(currentSessionId) === targetSessionId;
}

async function openNotesBrowserEntry(context, action = 'edit') {
    if (!context || typeof context !== 'object') return false;
    if (action === 'delete') return deleteNotesBrowserEntry(context);
    if (action === 'open') {
        const sessionReady = await confirmNotesSessionSwitch(context);
        if (!sessionReady) return false;
        if (context.type === 'question') return jumpToQuestionNoteContext(context);
        if (context.type === 'tutor' && typeof window.openPdfTutorNoteTarget === 'function') {
            closeNotesModal({ target: $('notes-modal') });
            return window.openPdfTutorNoteTarget(context);
        }
        showPopup('That Tutor note source is not currently available.', null, true);
        return false;
    }
    if (context.type === 'question') {
        openNotesModal({
            type: 'question',
            questionIndex: Number(context.questionIndex),
            questionKey: context.questionKey,
            // Question rows used to carry no return context at all, so editing
            // one from the collection dropped the reader back into the quiz.
            returnTo: {
                fromNotesBrowser: true,
                sessionScope: notesBrowserSessionScope,
                selectedEntryId: context.entryId || context.questionKey || ''
            }
        });
        return true;
    }
    if (context.type === 'tutor') {
        openNotesModal({
            ...context,
            // Every Tutor note came from the collection, not only image and
            // snippet ones, so every one of them returns to it.
            fromNotesBrowser: true,
            returnTo: {
                fromNotesBrowser: true,
                sessionScope: notesBrowserSessionScope,
                selectedEntryId: context.entryId || context.key || ''
            }
        });
        return true;
    }
    return false;
}

async function deleteNotesBrowserEntry(context = {}) {
    if (!context || typeof context !== 'object') return false;
    const noteLabel = context.type === 'tutor' ? 'Tutor note' : 'Question note';
    const sessionName = String(context.targetSessionName || '').trim();
    const confirmed = await showThemeConfirm(
        sessionName
            ? `Delete this ${noteLabel.toLowerCase()} from ${sessionName}? This cannot be undone.`
            : `Delete this ${noteLabel.toLowerCase()}? This cannot be undone.`,
        {
            title: 'Delete Note?',
            okText: 'Delete',
            cancelText: 'Cancel',
            danger: true
        }
    );
    if (!confirmed) return false;

    let deleted = false;
    let persisted = true;
    // A removal that never reaches storage comes back on the next load, so the
    // in-memory stores are rolled back rather than left disagreeing with disk.
    const restoreRemovedNotes = [];
    if (context.type === 'question') {
        const qIdx = Number(context.questionIndex);
        const questionKey = String(context.questionKey || '').trim();
        if (questionKey && Object.prototype.hasOwnProperty.call(questionNotesByQuestionKey || {}, questionKey)) {
            const previous = questionNotesByQuestionKey[questionKey];
            restoreRemovedNotes.push(() => { if (!Object.prototype.hasOwnProperty.call(questionNotesByQuestionKey, questionKey)) questionNotesByQuestionKey[questionKey] = previous; });
            delete questionNotesByQuestionKey[questionKey];
            deleted = true;
        }
        const indexIsUsable = Number.isInteger(qIdx) && qIdx >= 0;
        const indexKey = indexIsUsable ? getQuestionNoteKeyByIndex(qIdx) : '';
        // Only the record this row actually owns may be removed. The key derived
        // from the live bank belongs to whatever question currently sits at this
        // index, which is a different note as soon as the context has gone
        // stale, so it is touched only when it is provably the same record.
        const indexOwnsThisRecord = indexIsUsable && (!questionKey || (!!indexKey && indexKey === questionKey));
        if (indexOwnsThisRecord) {
            if (!questionKey && indexKey && Object.prototype.hasOwnProperty.call(questionNotesByQuestionKey || {}, indexKey)) {
                const previous = questionNotesByQuestionKey[indexKey];
                restoreRemovedNotes.push(() => { if (!Object.prototype.hasOwnProperty.call(questionNotesByQuestionKey, indexKey)) questionNotesByQuestionKey[indexKey] = previous; });
                delete questionNotesByQuestionKey[indexKey];
                deleted = true;
            }
            if (Object.prototype.hasOwnProperty.call(questionNotes || {}, qIdx)) {
                const previous = questionNotes[qIdx];
                restoreRemovedNotes.push(() => { if (!Object.prototype.hasOwnProperty.call(questionNotes, qIdx)) questionNotes[qIdx] = previous; });
                delete questionNotes[qIdx];
                deleted = true;
            }
        }
        if (deleted) persisted = await persistQuestionNotesStore();
        if (!persisted) restoreRemovedNotes.forEach(restore => restore());
        syncNotesButtonIndicator(Number.isInteger(qIdx) ? qIdx : null);
    } else if (context.type === 'tutor') {
        const tutorKey = normalizeTutorNoteKey(context.key);
        if (tutorKey && Object.prototype.hasOwnProperty.call(tutorNotesByKey || {}, tutorKey)) {
            const previous = tutorNotesByKey[tutorKey];
            restoreRemovedNotes.push(() => { if (!Object.prototype.hasOwnProperty.call(tutorNotesByKey, tutorKey)) tutorNotesByKey[tutorKey] = previous; });
            delete tutorNotesByKey[tutorKey];
            deleted = true;
        }
        if (deleted) persisted = await persistTutorNotesStore();
        if (!persisted) restoreRemovedNotes.forEach(restore => restore());
        if (typeof window.syncPdfTutorNoteIndicators === 'function') window.syncPdfTutorNoteIndicators();
    }

    if (!deleted) {
        showPopup('That note was already removed.', null, true);
        void renderNotesBrowser();
        return false;
    }

    if (!persisted) {
        const noteKind = context.type === 'tutor' ? 'tutor' : 'question';
        showPopup(notesStoreSyncState[noteKind].lastFailure === 'conflict'
            ? 'This note was changed in another tab, so it was not deleted. Its newer text is shown here.'
            : 'Could not delete this note from your device. It is still here — try again.', null, true);
        await renderNotesBrowser();
        return false;
    }

    showPopup('Note deleted.', null, false);
    await renderNotesBrowser();
    return true;
}

function handleNotesBrowserInteraction(event) {
    const target = event.target instanceof Element ? event.target : null;
    const scopeButton = target?.closest?.('[data-notes-session-scope]');
    if (scopeButton) {
        event.preventDefault();
        event.stopPropagation();
        notesBrowserSessionScope = normalizeNotesBrowserSessionScope(scopeButton.getAttribute('data-notes-session-scope') || 'current');
        void renderNotesBrowser({ animateScopeChange: true });
        return;
    }
    const actionButton = target?.closest?.('[data-note-action]');
    const entry = target?.closest?.('[data-note-entry-id]');
    if (!entry) return;
    const entryId = entry.getAttribute('data-note-entry-id');
    const context = notesBrowserEntryContexts.get(entryId);
    if (!context) return;
    const action = actionButton?.getAttribute?.('data-note-action') || 'edit';
    event.preventDefault();
    event.stopPropagation();
    void openNotesBrowserEntry(context, action);
}

function syncNotesButtonIndicator(questionIndex = null) {
    const notesBtn = els.notesBtn || $('notes-btn-inline');
    if (!notesBtn) return;

    if (!Number.isInteger(questionIndex)) {
        if (!Array.isArray(filteredIndices) || filteredIndices.length === 0) {
            notesBtn.classList.remove('has-note');
            return;
        }
        if (!Number.isInteger(currentQIndex) || currentQIndex < 0 || currentQIndex >= filteredIndices.length) {
            notesBtn.classList.remove('has-note');
            return;
        }
        questionIndex = filteredIndices[currentQIndex];
    }

    const rawNote = getQuestionNoteForIndex(questionIndex);
    const hasNote = rawNote.length > 0;
    notesBtn.classList.toggle('has-note', hasNote);
}

function restoreNotesDockedQuizCardLayout() {
    const quizCard = els?.quizCard || $('quiz-card');
    if (!quizCard) return;
    if (document.body.classList.contains('quiz-side-surface-switching')) return;
    const hadDockedLayout = 'notesPrevWidth' in quizCard.dataset;

    const restoreOrClear = (dataKey, styleProp, cssProp = styleProp) => {
        if (!(dataKey in quizCard.dataset)) return;
        const previous = quizCard.dataset[dataKey];
        if (previous) quizCard.style.setProperty(cssProp, previous);
        else quizCard.style.removeProperty(cssProp);
        delete quizCard.dataset[dataKey];
    };

    restoreOrClear('notesPrevWidth', 'width');
    restoreOrClear('notesPrevMaxWidth', 'maxWidth', 'max-width');
    restoreOrClear('notesPrevTransform', 'transform');
    restoreOrClear('notesPrevAlignSelf', 'alignSelf', 'align-self');
    restoreOrClear('notesPrevMargin', 'margin');
    if (hadDockedLayout && typeof getQuizCardLayoutMode === 'function'
        && getQuizCardLayoutMode() === 'single'
        && typeof resetQuizCardToLayoutDefault === 'function') {
        resetQuizCardToLayoutDefault('single');
    }
    if (typeof scheduleQuizCardContentScaleSync === 'function') scheduleQuizCardContentScaleSync();
    if (typeof scheduleQuickActionsDockPosition === 'function') scheduleQuickActionsDockPosition();
    if (typeof trackQuickActionsDockPosition === 'function') trackQuickActionsDockPosition();
}

function beginQuizSideSurfaceSwap(from, to) {
    const card = els?.quizCard || $('quiz-card');
    if (!card) return;
    for (const suffix of ['PrevWidth', 'PrevMaxWidth', 'PrevTransform', 'PrevAlignSelf', 'PrevMargin']) {
        const source = `${from}${suffix}`;
        const destination = `${to}${suffix}`;
        if (source in card.dataset) {
            card.dataset[destination] = card.dataset[source];
            delete card.dataset[source];
        }
    }
    document.body.classList.add('quiz-side-surface-switching');
    beginQuizSideSurfaceSwap.destination = to;
    window.clearTimeout(beginQuizSideSurfaceSwap.timer);
    beginQuizSideSurfaceSwap.timer = window.setTimeout(() => {
        document.body.classList.remove('quiz-side-surface-switching');
        beginQuizSideSurfaceSwap.destination = '';
    }, 700);
}

function endQuizSideSurfaceSwapOnClose(surface) {
    if (beginQuizSideSurfaceSwap.destination !== surface) return;
    window.clearTimeout(beginQuizSideSurfaceSwap.timer);
    beginQuizSideSurfaceSwap.destination = '';
    document.body.classList.remove('quiz-side-surface-switching');
}

function restoreSoloQuizCardAfterSideSurface() {
    const card = els?.quizCard || $('quiz-card');
    if (!card || typeof getQuizCardLayoutMode !== 'function'
        || getQuizCardLayoutMode() !== 'single'
        || card.classList.contains('is-floating') || card.classList.contains('quiz-card-expanded')) return;
    card.style.removeProperty('max-width');
    card.style.removeProperty('transform');
    card.style.removeProperty('align-self');
    card.style.removeProperty('margin');
    if (typeof resetQuizCardToLayoutDefault === 'function') resetQuizCardToLayoutDefault('single');
}

function syncNotesDockedQuizCardLayout() {
    const quizCard = els?.quizCard || $('quiz-card');
    const quizPane = $('quiz-pane');
    if (!quizCard || !quizPane) return;
    if (quizCard.classList.contains('is-floating') || quizCard.classList.contains('quiz-card-expanded')) return;
    if (document.body.classList.contains('quiz-side-surface-switching')) return;

    if (!('notesPrevWidth' in quizCard.dataset)) quizCard.dataset.notesPrevWidth = quizCard.style.width || '';
    if (!('notesPrevMaxWidth' in quizCard.dataset)) quizCard.dataset.notesPrevMaxWidth = quizCard.style.maxWidth || '';
    if (!('notesPrevTransform' in quizCard.dataset)) quizCard.dataset.notesPrevTransform = quizCard.style.transform || '';
    if (!('notesPrevAlignSelf' in quizCard.dataset)) quizCard.dataset.notesPrevAlignSelf = quizCard.style.alignSelf || '';
    if (!('notesPrevMargin' in quizCard.dataset)) quizCard.dataset.notesPrevMargin = quizCard.style.margin || '';

    const paneStyle = window.getComputedStyle(quizPane);
    const paneRect = quizPane.getBoundingClientRect();
    const paneContentLeft = Math.round((paneRect.left || 0) + (parseFloat(paneStyle.paddingLeft) || 0));
    const dockedPanelWidth = Math.max(
        0,
        Math.round(
            parseFloat(document.body.style.getPropertyValue('--notes-panel-width'))
            || ($('notes-modal')?.classList.contains('active')
                ? Math.min(NOTES_SURFACE_MAX_WIDTH, Math.max(NOTES_SURFACE_MIN_WIDTH, Math.round(window.innerWidth * 0.4)))
                : 0)
        )
    );
    // The pane already reserves the fixed panel with right padding. Measure
    // from its content edge to the original panel edge, as Ask does. Notes
    // itself gives up width for Tools, while the quiz keeps Ask's card width.
    const panelLeft = window.innerWidth - dockedPanelWidth
        - QUIZ_SIDE_SURFACE_TOOLS_CLEARANCE - NOTES_SURFACE_MARGIN;
    const availableWidth = Math.max(320, Math.min(840,
        Math.round(panelLeft - paneContentLeft - NOTES_SURFACE_MARGIN - 12)));

    const wasAlreadyDocked = quizCard.style.alignSelf === 'flex-start';
    const prefersReducedMotion = !memoraMotionAllowsSpatial('structural');
    const firstLeft = wasAlreadyDocked ? 0 : quizCard.getBoundingClientRect().left;

    quizCard.style.setProperty('width', `${availableWidth}px`, 'important');
    quizCard.style.setProperty('max-width', `${availableWidth}px`, 'important');
    quizCard.style.setProperty('transform', 'none');
    quizCard.style.alignSelf = 'flex-start';
    quizCard.style.margin = '0 0 2rem 0';

    // align-self cannot transition, so animate its horizontal displacement
    // with the same FLIP motion and easing used by Ask Memora.
    if (!wasAlreadyDocked && !prefersReducedMotion && typeof quizCard.animate === 'function') {
        const lastLeft = quizCard.getBoundingClientRect().left;
        const dx = firstLeft - lastLeft;
        if (Math.abs(dx) > 1) {
            quizCard.animate(
                [{ transform: `translateX(${dx}px)` }, { transform: 'translateX(0)' }],
                { duration: getMemoraMotionDuration('structural', 'in'), easing: getMemoraMotionEasing('soft'), fill: 'none' }
            );
        }
    }

    if (typeof scheduleQuizCardContentScaleSync === 'function') scheduleQuizCardContentScaleSync();
    if (typeof scheduleQuickActionsDockPosition === 'function') scheduleQuickActionsDockPosition();
    if (typeof trackQuickActionsDockPosition === 'function') trackQuickActionsDockPosition();
}

function resetNotesSurfaceState() {
    notesSurfaceFloating = false;
    notesSurfaceDragging = false;
    notesSurfaceAnchorElement = null;
    notesSurfaceAnchorRect = null;
    document.body.classList.remove('notes-docked-open');
    document.body.style.removeProperty('--notes-panel-width');
    restoreNotesDockedQuizCardLayout();

    const modal = $('notes-modal');
    const modalContent = $('notes-modal-content');
    if (modal) {
        delete modal.dataset.layoutMode;
    }
    if (!modalContent) return;
    modalContent.classList.remove('notes-surface-floating', 'surface-preparing', 'surface-entering', 'surface-switch-out');
    ['left', 'top', 'right', 'bottom', 'width', 'height', 'max-width', 'max-height', 'cursor'].forEach((prop) => {
        modalContent.style.removeProperty(prop);
    });
}

function isElementVisibleForNotesAnchor(el) {
    if (!el || !document.body.contains(el)) return false;
    const style = window.getComputedStyle(el);
    return style.display !== 'none'
        && style.visibility !== 'hidden'
        && Number(style.opacity || 1) > 0.01
        && el.getBoundingClientRect().width > 0
        && el.getBoundingClientRect().height > 0;
}

function isSoloNotesLayoutActive() {
    const workArea = els?.workArea || $('work-area');
    if (!workArea || document.body.classList.contains('welcome-mode')) return false;
    if ($('ask-ai-modal')?.classList.contains('active')) return false;
    return !workArea.classList.contains('split-view')
        && !workArea.classList.contains('split-view-ai')
        && !workArea.classList.contains('trio-view')
        && !workArea.classList.contains('pdf-tutor-view');
}

function getExplicitNotesAnchorRect() {
    const sourceRect = notesSurfaceAnchorElement?.isConnected
        ? notesSurfaceAnchorElement.getBoundingClientRect()
        : notesSurfaceAnchorRect;
    if (!sourceRect) return null;
    const width = Number(sourceRect.width) || 0;
    const height = Number(sourceRect.height) || 0;
    if (width <= 0 || height <= 0) return null;
    return {
        left: Number(sourceRect.left) || 0,
        top: Number(sourceRect.top) || 0,
        right: Number(sourceRect.right) || ((Number(sourceRect.left) || 0) + width),
        bottom: Number(sourceRect.bottom) || ((Number(sourceRect.top) || 0) + height),
        width,
        height
    };
}

function applyNotesSurfaceNearAnchor(anchor) {
    if (!anchor) return false;
    const width = Math.min(NOTES_SURFACE_MAX_WIDTH, Math.max(NOTES_SURFACE_MIN_WIDTH, Math.min(NOTES_SURFACE_DEFAULT_WIDTH, window.innerWidth - (NOTES_SURFACE_MARGIN * 2))));
    const height = Math.min(NOTES_SURFACE_DEFAULT_HEIGHT, Math.max(NOTES_SURFACE_MIN_HEIGHT, window.innerHeight - (NOTES_SURFACE_MARGIN * 2)));
    const gap = 12;
    let left = anchor.right + gap;
    if (left + width > window.innerWidth - NOTES_SURFACE_MARGIN) {
        left = anchor.left - width - gap;
    }
    if (left < NOTES_SURFACE_MARGIN) {
        left = Math.min(Math.max(NOTES_SURFACE_MARGIN, anchor.left), window.innerWidth - width - NOTES_SURFACE_MARGIN);
    }
    let top = Math.min(Math.max(NOTES_SURFACE_MARGIN, anchor.top), window.innerHeight - height - NOTES_SURFACE_MARGIN);
    if (!Number.isFinite(top)) top = NOTES_SURFACE_MARGIN;
    applyNotesSurfaceRect({ left, top, width, height });
    return true;
}

function getNotesAnchorRect() {
    const workArea = els?.workArea || $('work-area');
    if (!workArea) return null;

    const aiPane = $('ai-pane');
    const pdfPane = $('pdf-pane');
    if ((workArea.classList.contains('trio-view') || workArea.classList.contains('split-view-ai')) && isElementVisibleForNotesAnchor(aiPane)) {
        return aiPane.getBoundingClientRect();
    }
    if (workArea.classList.contains('split-view') && isElementVisibleForNotesAnchor(pdfPane)) {
        return pdfPane.getBoundingClientRect();
    }
    if (isElementVisibleForNotesAnchor(aiPane)) return aiPane.getBoundingClientRect();
    if (isElementVisibleForNotesAnchor(pdfPane)) return pdfPane.getBoundingClientRect();
    return null;
}

function clampNotesSurfaceRect(left, top, width, height) {
    const safeWidth = Math.min(Math.max(Number(width) || NOTES_SURFACE_DEFAULT_WIDTH, NOTES_SURFACE_MIN_WIDTH), window.innerWidth - (NOTES_SURFACE_MARGIN * 2));
    const safeHeight = Math.min(Math.max(Number(height) || NOTES_SURFACE_MIN_HEIGHT, NOTES_SURFACE_MIN_HEIGHT), window.innerHeight - (NOTES_SURFACE_MARGIN * 2));
    const maxLeft = Math.max(NOTES_SURFACE_MARGIN, window.innerWidth - safeWidth - NOTES_SURFACE_MARGIN);
    const maxTop = Math.max(NOTES_SURFACE_MARGIN, window.innerHeight - safeHeight - NOTES_SURFACE_MARGIN);
    return {
        left: clampNum(Number(left) || NOTES_SURFACE_MARGIN, NOTES_SURFACE_MARGIN, maxLeft),
        top: clampNum(Number(top) || NOTES_SURFACE_MARGIN, NOTES_SURFACE_MARGIN, maxTop),
        width: safeWidth,
        height: safeHeight
    };
}

function applyNotesSurfaceRect({ left, top, width, height }) {
    const modalContent = $('notes-modal-content');
    if (!modalContent) return;
    const rect = clampNotesSurfaceRect(left, top, width, height);
    modalContent.style.left = `${rect.left}px`;
    modalContent.style.top = `${rect.top}px`;
    modalContent.style.right = 'auto';
    modalContent.style.bottom = 'auto';
    modalContent.style.width = `${rect.width}px`;
    modalContent.style.height = `${rect.height}px`;
    modalContent.style.maxWidth = `${window.innerWidth - (NOTES_SURFACE_MARGIN * 2)}px`;
    modalContent.style.maxHeight = `${window.innerHeight - (NOTES_SURFACE_MARGIN * 2)}px`;
}

function getNotesDockedPanelTop() {
    const quizCard = els?.quizCard || $('quiz-card');
    if (!quizCard || window.getComputedStyle(quizCard).display === 'none') return NOTES_SURFACE_DOCKED_TOP;
    const rect = quizCard.getBoundingClientRect();
    if (!Number.isFinite(rect.top) || rect.height <= 0) return NOTES_SURFACE_DOCKED_TOP;
    return Math.max(0, Math.round(rect.top));
}

function syncNotesSurfaceLayout() {
    const modal = $('notes-modal');
    const modalContent = $('notes-modal-content');
    if (!modal || !modalContent || !modal.classList.contains('active')) return;
    if (modal.dataset.savedEmbedded === 'true') {
        modal.dataset.layoutMode = 'embedded';
        document.body.classList.remove('notes-docked-open', 'notes-surface-front');
        document.body.style.removeProperty('--notes-panel-width');
        restoreNotesDockedQuizCardLayout();
        return;
    }

    const paneRect = window.getQuizSideSurfacePaneRect?.();
    if (paneRect) {
        // Shared Slide/Ask/Notes box: sit over the slide pane and leave the duo quiz card alone.
        modal.dataset.layoutMode = 'pane';
        document.body.classList.remove('notes-docked-open');
        document.body.style.removeProperty('--notes-panel-width');
        modalContent.classList.remove('notes-surface-floating');
        window.applyQuizSideSurfacePaneRect?.(modalContent, paneRect);
        return;
    }

    if (notesSurfaceFloating) {
        applyNotesSurfaceRect({
            left: parseFloat(modalContent.style.left) || modalContent.getBoundingClientRect().left,
            top: parseFloat(modalContent.style.top) || modalContent.getBoundingClientRect().top,
            width: parseFloat(modalContent.style.width) || modalContent.getBoundingClientRect().width,
            height: parseFloat(modalContent.style.height) || modalContent.getBoundingClientRect().height
        });
        return;
    }

    const isQuizCompanion = notesModalContext?.type === 'question' && isSoloNotesLayoutActive();
    const isDocked = isQuizCompanion && window.matchMedia('(min-width: 1160px)').matches;
    modal.dataset.layoutMode = isDocked ? 'docked' : (isQuizCompanion ? 'compact' : 'overlay');
    document.body.classList.toggle('notes-docked-open', isDocked);
    modalContent.classList.remove('notes-surface-floating');
    // Publish the docked panel width BEFORE sizing the quiz card so the card
    // can reserve the panel's footprint instead of stretching full width.
    if (isDocked) {
        const dockWidth = Math.max(NOTES_SURFACE_MIN_WIDTH,
            Math.min(NOTES_SURFACE_MAX_WIDTH, Math.round(window.innerWidth * 0.4)) - QUIZ_SIDE_SURFACE_TOOLS_CLEARANCE);
        document.body.style.setProperty('--notes-panel-width', `${dockWidth}px`);
        syncNotesDockedQuizCardLayout();
    } else {
        restoreNotesDockedQuizCardLayout();
    }

    if (isDocked) {
        const width = Math.max(NOTES_SURFACE_MIN_WIDTH,
            Math.min(NOTES_SURFACE_MAX_WIDTH, Math.round(window.innerWidth * 0.4)) - QUIZ_SIDE_SURFACE_TOOLS_CLEARANCE);
        document.body.style.setProperty('--notes-panel-width', `${width}px`);
        const quizCard = els?.quizCard || $('quiz-card');
        const quizCardRect = quizCard && window.getComputedStyle(quizCard).display !== 'none'
            ? quizCard.getBoundingClientRect() : null;
        let top = quizCardRect?.height > 0 ? Math.round(quizCardRect.top) : getNotesDockedPanelTop();
        const height = Math.min(
            quizCardRect?.height > 0 ? Math.round(quizCardRect.height) : NOTES_SURFACE_DEFAULT_HEIGHT,
            Math.max(NOTES_SURFACE_MIN_HEIGHT, window.innerHeight - top - NOTES_SURFACE_MARGIN)
        );
        top = Math.min(top, Math.max(0, window.innerHeight - height - NOTES_SURFACE_MARGIN));
        applyNotesSurfaceRect({
            left: window.innerWidth - width - NOTES_SURFACE_MARGIN,
            top,
            width,
            height
        });
        return;
    }

    document.body.style.removeProperty('--notes-panel-width');

    if (isQuizCompanion) {
        const paneRect = $('quiz-pane')?.getBoundingClientRect();
        const width = Math.min(NOTES_SURFACE_DEFAULT_WIDTH,
            Math.max(NOTES_SURFACE_MIN_WIDTH, (paneRect?.width || window.innerWidth) - (NOTES_SURFACE_MARGIN * 2)));
        const height = Math.min(NOTES_SURFACE_DEFAULT_HEIGHT, window.innerHeight - (NOTES_SURFACE_MARGIN * 2));
        applyNotesSurfaceRect({
            left: (paneRect?.left || 0) + ((paneRect?.width || window.innerWidth) - width) / 2,
            top: (window.innerHeight - height) / 2,
            width,
            height
        });
        return;
    }

    const explicitAnchor = getExplicitNotesAnchorRect();
    if (explicitAnchor && applyNotesSurfaceNearAnchor(explicitAnchor)) {
        return;
    }

    const anchor = getNotesAnchorRect();
    if (anchor) {
        const width = Math.min(NOTES_SURFACE_MAX_WIDTH, Math.max(NOTES_SURFACE_MIN_WIDTH, Math.min(NOTES_SURFACE_DEFAULT_WIDTH, anchor.width - 24)));
        const height = Math.min(NOTES_SURFACE_DEFAULT_HEIGHT, Math.max(NOTES_SURFACE_MIN_HEIGHT, anchor.height - 24));
        applyNotesSurfaceRect({
            left: anchor.left + 12,
            top: anchor.top + 12,
            width,
            height
        });
        return;
    }

    const fallbackWidth = Math.min(NOTES_SURFACE_DEFAULT_WIDTH, window.innerWidth - (NOTES_SURFACE_MARGIN * 2));
    const fallbackHeight = Math.min(NOTES_SURFACE_DEFAULT_HEIGHT, window.innerHeight - (NOTES_SURFACE_MARGIN * 2));
    applyNotesSurfaceRect({
        left: (window.innerWidth - fallbackWidth) / 2,
        top: (window.innerHeight - fallbackHeight) / 2,
        width: fallbackWidth,
        height: fallbackHeight
    });
}

// The editor has to be able to return exactly where it was launched from. This
// resolver rebuilds its context object from scratch, so anything the return trip
// needs has to survive normalization instead of being passed ad hoc at one call
// site and silently dropped at the next.
function normalizeNotesReturnDescriptor(rawContext = {}) {
    const source = (rawContext && typeof rawContext === 'object') ? rawContext : {};
    const raw = (source.returnTo && typeof source.returnTo === 'object') ? source.returnTo : source;
    if (raw.fromNotesBrowser !== true) return null;
    return {
        fromNotesBrowser: true,
        sessionScope: normalizeNotesBrowserSessionScope(
            raw.sessionScope === undefined ? notesBrowserSessionScope : raw.sessionScope
        ),
        selectedEntryId: String(raw.selectedEntryId || '').trim()
    };
}

function resolveQuestionNotesContext(rawContext = {}) {
    const returnTo = normalizeNotesReturnDescriptor(rawContext);
    const requestedKey = String(rawContext.questionKey || '').trim();
    const requestedIndex = Number(rawContext.questionIndex);
    const indexIsLive = Number.isInteger(requestedIndex)
        && requestedIndex >= 0
        && requestedIndex < questions.length;

    // The key is the identity and the index is only a locator. Letting the index
    // win re-derives the key from the live bank, so a caller holding a stale
    // index (a browser row, a reordered bank) would silently be pointed at
    // whatever question now occupies that slot.
    if (requestedKey) {
        const indexMatchesKey = indexIsLive && getQuestionNoteKeyByIndex(requestedIndex) === requestedKey;
        return {
            type: 'question',
            questionIndex: indexMatchesKey ? requestedIndex : -1,
            questionKey: requestedKey,
            returnTo
        };
    }
    if (indexIsLive) {
        return {
            type: 'question',
            questionIndex: requestedIndex,
            questionKey: getQuestionNoteKeyByIndex(requestedIndex),
            returnTo
        };
    }
    if (!Array.isArray(filteredIndices) || filteredIndices.length === 0) return null;
    if (!Number.isInteger(currentQIndex) || currentQIndex < 0 || currentQIndex >= filteredIndices.length) return null;
    const qIdx = filteredIndices[currentQIndex];
    return {
        type: 'question',
        questionIndex: qIdx,
        questionKey: getQuestionNoteKeyByIndex(qIdx),
        returnTo
    };
}

function getExistingNoteForContext(context) {
    if (!context) return '';
    if (context.type === 'tutor') return getTutorNoteForKey(context.key);
    if (context.type === 'question') {
        // Key first, for the same reason the resolver prefers it. The index read
        // stays as the fallback because unmigrated legacy notes exist only under
        // their original index.
        const noteKey = String(context.questionKey || '').trim();
        if (noteKey) {
            const keyedNote = sanitizeQuestionNoteText(questionNotesByQuestionKey[noteKey]);
            if (keyedNote) return keyedNote;
        }
        if (Number.isInteger(context.questionIndex) && context.questionIndex >= 0) {
            return getQuestionNoteForIndex(context.questionIndex);
        }
        return '';
    }
    return '';
}

function configureNotesModalForContext(context) {
    const title = $('notes-modal-title');
    const copy = $('notes-modal-copy');
    const textarea = $('notes-input');
    const buttons = $('notes-buttons');
    const browser = $('notes-browser');
    const guideSelector = $('notes-guide-selector');
    const guidePreview = $('notes-guide-preview');
    const imagePreview = $('notes-image-preview');
    const image = $('notes-image-preview-img');
    const imageCaption = $('notes-image-preview-caption');
    const snippetPreview = $('notes-snippet-preview');
    const snippetText = $('notes-snippet-preview-text');
    const saveButton = $('notes-save-btn') || buttons?.querySelector?.('.primary');
    const secondaryButton = $('notes-secondary-btn');
    const modalContent = $('notes-modal-content');
    const closeBtn = modalContent?.querySelector?.('.notes-close-btn');
    const isBrowse = context?.type === 'browse';
    const isImageNote = context?.type === 'tutor' && (context?.noteType === 'image' || !!context?.imageDataUrl);
    const isSnippetNote = context?.type === 'tutor' && !isImageNote && context?.snippetNote === true && !!context?.preview;
    const isSourceNote = isImageNote || isSnippetNote;
    const isBrowserSourceEdit = isSourceNote && context?.fromNotesBrowser === true;
    if (title) {
        title.textContent = isBrowse
            ? 'My Notes'
            : (isBrowserSourceEdit
                ? `Edit ${isSnippetNote ? 'Snippet' : 'Source'} Note`
                : (isSnippetNote
                    ? 'Add to Notes'
                    : (isImageNote ? 'Save Source to Notes' : (context?.type === 'tutor' ? 'Tutor Note' : 'Question Notes'))));
    }
    if (copy) {
        copy.textContent = isBrowse
            ? 'Review saved quiz, Tutor, snippet, and source image notes. Notes are saved locally on this device.'
            : (isBrowserSourceEdit
                ? `Update the optional comment attached to this saved ${isSnippetNote ? 'snippet' : 'source image'}.`
                : (isSnippetNote
                ? 'The selected snippet will be saved to My Notes. Add an optional comment below.'
                : (isImageNote
                ? 'The highlighted source area will be saved as an image. Add an optional comment below.'
                : (context?.type === 'tutor'
                ? 'Add a personal note for this Tutor segment. Notes are saved locally and stay available even if the segment overlay is removed.'
                : 'Add personal notes for this question. Notes are saved locally.'))));
    }
    if (closeBtn) closeBtn.setAttribute('aria-label', isBrowse ? 'Close My Notes' : 'Close notes');
    modalContent?.classList.toggle('notes-image-mode', isImageNote);
    modalContent?.classList.toggle('notes-snippet-mode', isSnippetNote);
    if (textarea) {
        textarea.hidden = isBrowse;
        textarea.placeholder = isSourceNote ? 'Add a comment (optional)...' : 'Your notes here...';
        textarea.setAttribute('aria-label', isSourceNote ? `Optional comment for saved ${isSnippetNote ? 'snippet' : 'source image'}` : 'Note text');
    }
    if (buttons) buttons.hidden = isBrowse;
    if (saveButton) saveButton.textContent = isBrowserSourceEdit ? 'Save Changes' : (isSourceNote ? 'Save to Notes' : 'Save Note');
    if (secondaryButton) secondaryButton.textContent = isBrowserSourceEdit ? 'Back' : 'Cancel';
    if (imagePreview) imagePreview.hidden = !isImageNote;
    if (image) {
        if (isImageNote) {
            image.src = context.imageDataUrl;
            image.alt = context.imageAlt || context.title || 'Highlighted source image';
        } else {
            image.removeAttribute('src');
            image.alt = '';
        }
    }
    if (imageCaption) imageCaption.textContent = isImageNote
        ? (context.imageAlt || `Source highlight from slide ${context.pageNumber || 1}`)
        : '';
    if (snippetPreview) snippetPreview.hidden = !isSnippetNote;
    if (snippetText) snippetText.textContent = isSnippetNote ? context.preview : '';
    if (guideSelector) guideSelector.hidden = true;
    if (guidePreview) guidePreview.hidden = true;
    if (browser) {
        browser.hidden = !isBrowse;
        if (isBrowse) void renderNotesBrowser();
    }
}

function syncNotesModalSaveState() {
    const context = notesModalContext;
    const saveButton = $('notes-save-btn');
    const textarea = $('notes-input');
    if (!saveButton || !textarea) return;
    const isBrowserSourceEdit = context?.type === 'tutor'
        && (context?.noteType === 'image' || context?.snippetNote === true)
        && context?.fromNotesBrowser === true;
    saveButton.disabled = isBrowserSourceEdit && textarea.value === notesModalInitialValue;
}

function openNotesModal(rawContext = {}) {
    const requestedType = String(rawContext?.type || rawContext?.mode || '').trim().toLowerCase();
    let context = null;
    if (requestedType === 'browse' || requestedType === 'my-notes') {
        notesBrowserSessionScope = normalizeNotesBrowserSessionScope(rawContext?.sessionScope || 'current');
        if (rawContext?.preservePriority !== true) {
            notesBrowserPrioritySourceTokens = collectNotesSourceTokens([
                rawContext?.pdfName,
                rawContext?.sourceTitle,
                ...(Array.isArray(rawContext?.sourceValues) ? rawContext.sourceValues : [])
            ]);
        }
        context = { type: 'browse' };
    } else if (requestedType === 'tutor') {
        context = normalizeTutorNoteContext(rawContext);
    } else {
        context = resolveQuestionNotesContext(rawContext);
    }
    if (!context) return;
    if ($('ask-ai-modal')?.classList.contains('active')
        && !$('ask-ai-modal').classList.contains('is-closing')) {
        // A handoff already fading Ask out opens Notes itself; a second click must not stack another.
        if (getAskAIModalContentEl()?.classList.contains('surface-switch-out')) return;
        beginQuizSideSurfaceSwap('askAi', 'notes');
        getAskAIModalContentEl()?.classList.add('surface-switch-out');
        window.setTimeout(() => {
            closeAskAIModalForDuoHandoff();
            openNotesModal(rawContext);
        }, 170);
        return;
    }

    // Retargeting the editor invalidates any write still in flight for the
    // previous target, so it can no longer repaint indicators or clear drafts
    // that now belong to this one.
    bumpNotesEditorGeneration();
    notesModalContext = context;
    notesModalQuestionIndex = Number.isInteger(context.questionIndex) ? context.questionIndex : null;
    const existingNote = getExistingNoteForContext(context);
    const textarea = $('notes-input');
    const modal = $('notes-modal');
    resetNotesSurfaceState();
    if (!textarea || !modal) return;
    notesSurfaceAnchorElement = context.anchorEl || null;
    notesSurfaceAnchorRect = context.anchorRect || null;
    configureNotesModalForContext(context);
    // A draft only survives when its write never landed, so it is newer than
    // what storage holds and the editor reopens on it. The baseline stays the
    // stored note, which leaves the editor correctly dirty until it commits.
    const pendingDraft = getNotesRecoveryDraft(context);
    textarea.value = (pendingDraft && pendingDraft.text !== existingNote)
        ? pendingDraft.text
        : existingNote;
    notesModalInitialValue = existingNote;
    syncNotesModalSaveState();
    const modalContent = $('notes-modal-content');
    modalContent?.classList.remove('surface-entering');
    modalContent?.classList.add('surface-preparing');
    document.body.classList.add('notes-surface-front');
    document.body.classList.remove('ask-ai-surface-front');
    if (typeof scheduleFloatingQuizCardReanchorForManagedLayout === 'function') {
        scheduleFloatingQuizCardReanchorForManagedLayout();
    }
    // The rail's Routing suspends every overlay it did not admit, so a Notes surface opened
    // from the quiz card or Tools stayed inert and invisible over a hidden quiz. Admit it
    // before exposing it; the opener is still focused because nothing has moved focus yet.
    const notesOpener = document.activeElement instanceof HTMLElement && !modal.contains(document.activeElement)
        ? document.activeElement : (context.anchorEl || null);
    notesModalReturnFocus = notesOpener;
    // Docked question notes share the quiz stage. Taking ownership of an overlay
    // stage here suspends the quiz container and makes the card disappear.
    if (context.type !== 'question' || (!isSoloNotesLayoutActive() && !window.getQuizSideSurfacePaneRect?.())) {
        window.MemoraLiveLeftRailRouting?.admitNativeTransient?.('notes', notesOpener);
    }
    modal.classList.add('active');
    requestAnimationFrame(() => {
        syncNotesSurfaceLayout();
        triggerNotesSurfaceEnterAnimation();
        if (context.type === 'browse') focusNotesSurfaceWhenReady($('notes-browser'));
        else focusNotesSurfaceWhenReady(textarea, { caretToEnd: true });
        // The surface-front layout shift resizes the quiz card's content box.
        // Re-run the scale sync so the card does not stay stretched while the
        // notes modal is open.
        if (typeof scheduleQuizCardContentScaleSync === 'function') {
            scheduleQuizCardContentScaleSync();
        }
    });
}
window.openNotesModal = openNotesModal;

/* Question Notes docked beside the quiz can still be held by the rail's stage (inert and
   hidden) in the frame that opens them, and the entrance can keep them hidden a little longer.
   Focus the editor as soon as it is reachable, unless the learner has already moved focus. */
function focusNotesSurfaceWhenReady(target, { caretToEnd = false } = {}) {
    const modal = $('notes-modal');
    if (!target?.focus || !modal) return;
    const apply = () => {
        target.focus({ preventScroll: true });
        if (caretToEnd && typeof target.setSelectionRange === 'function') target.setSelectionRange(target.value.length, target.value.length);
    };
    const openedFrom = document.activeElement;
    apply();
    if (document.activeElement === target) return;
    // Retry each frame while the surface settles; stop once focused, closed, or moved on.
    const deadline = performance.now() + 1500;
    const retry = () => {
        if (!modal.classList.contains('active') || document.activeElement !== openedFrom || performance.now() > deadline) return;
        apply();
        if (document.activeElement !== target) requestAnimationFrame(retry);
    };
    requestAnimationFrame(retry);
}

function openMyNotesModal(options = {}) {
    if (!options.embedded && document.body.classList.contains('memora-live-left-rail-preview') && window.MemoraLiveLeftRailRouting?.openSavedContent) return window.MemoraLiveLeftRailRouting.openSavedContent('notes');
    const requestedOptions = options && typeof options === 'object' ? options : {};
    notesBrowserSessionScope = normalizeNotesBrowserSessionScope(requestedOptions.sessionScope || 'current');
    openNotesModal({ ...requestedOptions, type: 'browse', sessionScope: notesBrowserSessionScope });
}
window.openMyNotesModal = openMyNotesModal;

function handleNotesSecondaryAction(event = null) {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    // A save already in flight owns the outcome; Cancel must not race it.
    if (notesEditorPendingTransition) return;
    // Cancel discards: the editor returns to the stored note, so the exit below
    // finds nothing dirty and commits nothing. Only the explicit Save button and
    // the other exits (close, Escape, click-away, switching) save a draft.
    discardNotesEditorDraft();
    if (notesModalContext?.fromNotesBrowser === true) {
        openNotesModal({
            type: 'browse',
            sessionScope: notesBrowserSessionScope,
            preservePriority: true
        });
        return;
    }
    closeNotesModal({ target: $('notes-modal') });
}
window.handleNotesSecondaryAction = handleNotesSecondaryAction;

const notesModalContent = $('notes-modal-content');
if (notesModalContent && notesModalContent.dataset.dragBound !== '1') {
    notesModalContent.dataset.dragBound = '1';
    let dragStartX = 0;
    let dragStartY = 0;
    let dragLeft = 0;
    let dragTop = 0;

    // Segment-note anchors live inside transient PDF highlight overlays. Keep
    // editor interactions away from document-level PDF/Tutor click-away logic
    // so focusing the optional comment box cannot dismiss the note surface.
    ['pointerdown', 'mousedown', 'click'].forEach((eventName) => {
        notesModalContent.addEventListener(eventName, (event) => event.stopPropagation());
    });

    notesModalContent.addEventListener('mousedown', (event) => {
        if (event.button !== 0 || !event.shiftKey) return;
        if (event.target.closest('button, a, textarea, input, select')) return;
        event.preventDefault();
        const rect = notesModalContent.getBoundingClientRect();
        notesSurfaceFloating = true;
        notesSurfaceDragging = true;
        notesModalContent.classList.add('notes-surface-floating');
        document.body.classList.remove('notes-docked-open');
        dragStartX = event.clientX;
        dragStartY = event.clientY;
        dragLeft = rect.left;
        dragTop = rect.top;
        applyNotesSurfaceRect({
            left: rect.left,
            top: rect.top,
            width: rect.width,
            height: rect.height
        });
        notesModalContent.style.cursor = 'grabbing';
    });

    const notesDragMoveThrottled = createRafDragThrottle((event) => {
        if (!notesSurfaceDragging) return;
        applyNotesSurfaceRect({
            left: dragLeft + event.clientX - dragStartX,
            top: dragTop + event.clientY - dragStartY,
            width: parseFloat(notesModalContent.style.width) || notesModalContent.getBoundingClientRect().width,
            height: parseFloat(notesModalContent.style.height) || notesModalContent.getBoundingClientRect().height
        });
    });

    document.addEventListener('mousemove', (event) => {
        if (!notesSurfaceDragging) return;
        notesDragMoveThrottled(event);
    });

    document.addEventListener('mouseup', () => {
        if (!notesSurfaceDragging) return;
        notesDragMoveThrottled.flush();
        notesSurfaceDragging = false;
        notesModalContent.style.cursor = 'default';
    });
}

function closeNotesModal(event) {
    if (notesSurfaceDragging) return;
    const target = event?.target instanceof Element ? event.target : null;
    if (target === $('notes-modal') || target?.closest?.('.welcome-btn') || target?.closest?.('.notes-close-btn')) {
        // Closing flushes uncommitted work first and stays open if that write
        // fails; teardown reverses the surface-front layout shift, so the quiz
        // card content scale re-syncs there.
        void requestNotesEditorTransition(teardownNotesEditorSurface);
    }
}
window.closeNotesModal = closeNotesModal;

// ---------------------------------------------------------------------------
// Editor identity, draft recovery, and guarded transitions
// ---------------------------------------------------------------------------

// Bumped whenever the editor is opened or retargeted. A write that completes
// after the editor has moved on must not touch the new target or clear the new
// draft, so every path that awaits re-checks the generation it started with.
let notesEditorGeneration = 0;
let notesEditorDraftRevision = 0;
let notesEditorPendingTransition = null;
let notesEditorWriteChain = Promise.resolve();
let notesRecoveryDrafts = {};

function bumpNotesEditorGeneration() {
    notesEditorGeneration += 1;
    return notesEditorGeneration;
}

function isNotesEditorInEditingMode(context = notesModalContext) {
    const type = context?.type;
    return type === 'question' || type === 'tutor';
}

function getNotesEditorDraftText() {
    const textarea = $('notes-input');
    return typeof textarea?.value === 'string' ? textarea.value : '';
}

// Whitespace counts as an edit, so this compares the raw values rather than the
// sanitized ones. Editor mode decides whether a comparison is meaningful at all:
// a `.hidden` check would miss the CSS-hidden editor states.
function isNotesEditorDirty() {
    if (!isNotesEditorInEditingMode()) return false;
    return getNotesEditorDraftText() !== notesModalInitialValue;
}

function buildNotesDraftId(context) {
    if (!context) return '';
    if (context.type === 'tutor') {
        const key = normalizeTutorNoteKey(context.key);
        return key ? `tutor::${key}` : '';
    }
    if (context.type !== 'question') return '';
    const questionKey = String(context.questionKey || '').trim();
    if (questionKey) return `question::${questionKey}`;
    const index = Number(context.questionIndex);
    return Number.isInteger(index) && index >= 0 ? `question-legacy::${index}` : '';
}

// Writes are serialized so an older completion can never land after a newer one
// and leave storage disagreeing with what the reader last typed.
function enqueueNotesEditorWrite(task) {
    const run = notesEditorWriteChain.then(task, task);
    notesEditorWriteChain = run.then(() => {}, () => {});
    return run;
}

// Stored drafts are keyed by note and writing tab, so two tabs holding drafts for the
// same note keep both, and one tab's save never rewrites another tab's entries.
// `supersedes` lists the stored entries this tab's editor reopened on: committing the
// note resolves those too.
function getNotesRecoveryDraftWriterId() {
    return typeof SESSION_TAB_ID === 'string' ? SESSION_TAB_ID : '';
}

function getNotesRecoveryDraftStorageKey(record) {
    return record?.writer ? `${record.draftId}\u0001${record.writer}` : String(record?.draftId || '');
}

function hydrateNotesRecoveryDrafts(rawStore) {
    notesRecoveryDrafts = {};
    const source = rawStore?.byDraftId;
    if (!source || typeof source !== 'object' || Array.isArray(source)) return;
    const keysByDraftId = {};
    Object.entries(source).forEach(([storageKey, record]) => {
        if (!record || typeof record !== 'object') return;
        if (typeof record.text !== 'string') return;
        const id = String(record.draftId || storageKey || '').trim();
        if (!id) return;
        (keysByDraftId[id] = keysByDraftId[id] || []).push(storageKey);
        const candidate = {
            draftId: id,
            text: record.text,
            revision: Number(record.revision) || 0,
            updatedAt: Number(record.updatedAt) || 0,
            writer: typeof record.writer === 'string' ? record.writer : ''
        };
        const current = notesRecoveryDrafts[id];
        if (!current || candidate.updatedAt >= current.updatedAt) notesRecoveryDrafts[id] = candidate;
    });
    Object.values(notesRecoveryDrafts).forEach((record) => {
        const ownKey = getNotesRecoveryDraftStorageKey(record);
        record.supersedes = keysByDraftId[record.draftId].filter((key) => key !== ownKey);
    });
}

// `change(byDraftId)` edits the latest stored entries in place inside one transaction,
// leaving every entry it does not name as stored; it returns false to write nothing.
function persistNotesRecoveryDrafts(change) {
    return withMemoraStorageTimeout(
        () => IDB.compareAndPutSetting(NOTES_RECOVERY_DRAFT_STORAGE_KEY, (raw) => {
            const stored = raw?.byDraftId;
            const byDraftId = stored && typeof stored === 'object' && !Array.isArray(stored) ? { ...stored } : {};
            if (change(byDraftId) === false) return { skip: true };
            return { value: { v: NOTES_RECOVERY_DRAFT_STORE_VERSION, byDraftId } };
        }),
        NOTES_PERSIST_TIMEOUT_MS,
        'Note draft recovery save'
    )
        .then(() => true)
        .catch((err) => {
            console.warn('Failed to persist note recovery drafts:', err);
            return false;
        });
}

function getNotesRecoveryDraft(context) {
    const draftId = buildNotesDraftId(context);
    return draftId ? (notesRecoveryDrafts[draftId] || null) : null;
}

function recordNotesRecoveryDraft(context, text) {
    const draftId = buildNotesDraftId(context);
    if (!draftId) return null;
    notesEditorDraftRevision += 1;
    const previous = notesRecoveryDrafts[draftId];
    const snapshot = {
        draftId,
        text,
        revision: notesEditorDraftRevision,
        updatedAt: Date.now(),
        writer: getNotesRecoveryDraftWriterId()
    };
    const ownKey = getNotesRecoveryDraftStorageKey(snapshot);
    const previousKey = previous ? getNotesRecoveryDraftStorageKey(previous) : '';
    snapshot.supersedes = Array.from(new Set([
        ...(previous?.supersedes || []),
        ...(previousKey && previousKey !== ownKey ? [previousKey] : [])
    ]));
    notesRecoveryDrafts[draftId] = snapshot;
    const { supersedes: _supersedes, ...stored } = snapshot;
    void persistNotesRecoveryDrafts((byDraftId) => { byDraftId[ownKey] = stored; });
    return snapshot;
}

// Only the exact revision that was committed is cleared. Anything the reader
// typed after the write started stays dirty and keeps its recovery record.
function clearNotesRecoveryDraft(draftId, revision) {
    if (!draftId) return;
    const existing = notesRecoveryDrafts[draftId];
    if (!existing) return;
    if (Number(revision) && existing.revision > Number(revision)) return;
    delete notesRecoveryDrafts[draftId];
    const ownKey = getNotesRecoveryDraftStorageKey(existing);
    const resolvedKeys = existing.supersedes || [];
    void persistNotesRecoveryDrafts((byDraftId) => {
        let changed = false;
        const own = byDraftId[ownKey];
        if (own && !(Number(revision) && Number(own.revision) > Number(revision))) {
            delete byDraftId[ownKey];
            changed = true;
        }
        resolvedKeys.forEach((key) => {
            if (key in byDraftId) {
                delete byDraftId[key];
                changed = true;
            }
        });
        return changed;
    });
}

// Cancel's discard: puts the stored note back in the editor and drops the
// recovery record the editor reopened on, at that record's own revision, so a
// discarded draft neither commits on exit nor comes back on the next open.
function discardNotesEditorDraft() {
    if (!isNotesEditorInEditingMode()) return;
    const draft = getNotesRecoveryDraft(notesModalContext);
    if (draft) clearNotesRecoveryDraft(draft.draftId, draft.revision);
    const textarea = $('notes-input');
    if (textarea) textarea.value = notesModalInitialValue;
}

// The single identity-safe write for question notes, shared by the explicit
// Save button and by the guarded transition flush so the two can never drift.
function applyQuestionNoteForContext(context, note) {
    let qIdx = Number(context.questionIndex);
    const capturedKey = String(context.questionKey || '').trim();
    const indexIsLive = Number.isInteger(qIdx) && qIdx >= 0 && qIdx < questions.length;
    // Writing by index re-derives the question key from the live bank. The bank
    // can be regenerated or reordered while the editor is open, so a stale index
    // would attach the note to whatever question now sits at that position. The
    // key captured when the editor opened identifies the question the reader was
    // actually writing about.
    const indexStillTargetsSameQuestion = indexIsLive
        && (!capturedKey || getQuestionNoteKeyByIndex(qIdx) === capturedKey);
    if (indexStillTargetsSameQuestion) {
        setQuestionNoteForIndex(qIdx, note);
        return { applied: true, indicatorIndex: qIdx };
    }
    if (capturedKey) {
        ensureQuestionNotesLegacyMigration();
        const cleanNote = sanitizeQuestionNoteText(note);
        if (cleanNote) questionNotesByQuestionKey[capturedKey] = cleanNote;
        else delete questionNotesByQuestionKey[capturedKey];
        // The note went to a question that is no longer at this index, so the
        // on-card indicator has to be resolved from the live question instead.
        return { applied: true, indicatorIndex: null };
    }
    return { applied: false, indicatorIndex: indexIsLive ? qIdx : null };
}

// Commits the editor's current text through the owning note helper. Resolves
// false when the write did not land, so callers can refuse to move on.
async function commitNotesEditorDraft(context, text, generation) {
    if (!isNotesEditorInEditingMode(context)) return true;
    const snapshot = recordNotesRecoveryDraft(context, text);
    return enqueueNotesEditorWrite(async () => {
        if (context.type === 'tutor') {
            setTutorNoteForContext(context, text);
            const persisted = await persistTutorNotesStore();
            if (typeof window.syncPdfTutorNoteIndicators === 'function') window.syncPdfTutorNoteIndicators();
            if (persisted) clearNotesRecoveryDraft(snapshot?.draftId, snapshot?.revision);
            return persisted;
        }
        const { applied, indicatorIndex } = applyQuestionNoteForContext(context, text);
        if (!applied) return false;
        const persisted = await persistQuestionNotesStore();
        // A write that finished after the editor moved on must not repaint the
        // indicator for a question the reader is no longer looking at.
        if (generation === undefined || generation === notesEditorGeneration) {
            syncNotesButtonIndicator(indicatorIndex);
        }
        if (persisted) clearNotesRecoveryDraft(snapshot?.draftId, snapshot?.revision);
        return persisted;
    });
}

// Unconditional internal teardown. The public close handler needs a matching
// event target, which makes it useless for Escape, session switches, and every
// other internal transition, so those all come through here instead.
function teardownNotesEditorSurface() {
    const wasDocked = $('notes-modal')?.dataset.layoutMode === 'docked';
    endQuizSideSurfaceSwapOnClose('notes');
    bumpNotesEditorGeneration();
    notesModalQuestionIndex = null;
    notesModalContext = null;
    notesModalInitialValue = '';
    const notesModalEl = $('notes-modal');
    const focusWasInside = !!notesModalEl?.contains(document.activeElement);
    notesModalEl?.classList.remove('active');
    document.body.classList.remove('notes-surface-front');
    // Closing must not drop keyboard focus onto the page: return it to what opened Notes.
    const returnFocus = notesModalReturnFocus;
    notesModalReturnFocus = null;
    if ((focusWasInside || document.activeElement === document.body) && returnFocus?.isConnected && !returnFocus.disabled) {
        requestAnimationFrame(() => {
            if (document.activeElement && document.activeElement !== document.body && !notesModalEl?.contains(document.activeElement)) return;
            returnFocus.focus?.();
        });
    }
    if ($('ask-ai-modal')?.classList.contains('active')) {
        document.body.classList.add('ask-ai-surface-front');
    }
    resetNotesSurfaceState();
    if (wasDocked && !$('ask-ai-modal')?.classList.contains('active')
        && !document.body.classList.contains('quiz-side-surface-switching')) {
        restoreSoloQuizCardAfterSideSurface();
    }
    if (typeof scheduleQuizCardContentScaleSync === 'function') {
        scheduleQuizCardContentScaleSync();
    }
    window.releaseQuizSideSurfacePane?.();
}

// The one gate every editor exit goes through: close, Escape, click-away,
// switching to another note, browsing, and session switches. Repeated events
// collapse onto the single pending transition instead of stacking dialogs.
function requestNotesEditorTransition(proceed) {
    if (notesSurfaceDragging) return Promise.resolve(false);
    if (notesEditorPendingTransition) return notesEditorPendingTransition;
    if (!isNotesEditorDirty()) {
        proceed?.();
        return Promise.resolve(true);
    }
    const context = notesModalContext;
    const generation = notesEditorGeneration;
    let draftText = getNotesEditorDraftText();
    notesEditorPendingTransition = (async () => {
        for (;;) {
            const committed = await commitNotesEditorDraft(context, draftText, generation);
            if (!committed) {
                // The write never landed. Keep the editor and its exact text rather
                // than closing over work that was never stored anywhere. A failure
                // for a note the editor has already left keeps its recovery draft
                // but reports nothing over the note now open.
                if (generation === notesEditorGeneration) {
                    showPopup(getNotesPersistFailureMessage(context?.type === 'tutor' ? 'tutor' : 'question'), null, true);
                }
                return false;
            }
            if (generation !== notesEditorGeneration) return false;
            notesModalInitialValue = draftText;
            // Typing accepted while that write was in flight is saved before leaving.
            const latest = getNotesEditorDraftText();
            if (latest === draftText) break;
            draftText = latest;
        }
        proceed?.();
        return true;
    })();
    const settle = () => { notesEditorPendingTransition = null; };
    notesEditorPendingTransition.then(settle, settle);
    return notesEditorPendingTransition;
}

// Returns true when the editor was launched from the collection and the reader
// has been sent back to it, in which case the caller must not also run the
// ordinary editor teardown.
function resumeNotesBrowserAfterEdit(context) {
    const returnTo = context?.returnTo?.fromNotesBrowser === true
        ? context.returnTo
        : (context?.fromNotesBrowser === true
            ? { fromNotesBrowser: true, sessionScope: notesBrowserSessionScope }
            : null);
    if (!returnTo) return false;
    openNotesModal({
        type: 'browse',
        sessionScope: returnTo.sessionScope || notesBrowserSessionScope,
        preservePriority: true
    });
    return true;
}

// After Save commits `savedText`, text typed during the write is newer than what was
// stored. The editor stays open on it, unsaved and kept for recovery, instead of closing.
function keepNotesEditorForTypingDuringSave(context, savedText) {
    notesModalInitialValue = savedText;
    const latest = getNotesEditorDraftText();
    if (latest === savedText) return false;
    recordNotesRecoveryDraft(context, latest);
    syncNotesModalSaveState();
    showPopup('Saved. Your newer changes are not saved yet; save again to keep them.', null, false);
    return true;
}

async function saveQuestionNote(event = null) {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    const context = notesModalContext || resolveQuestionNotesContext({ questionIndex: notesModalQuestionIndex });
    const note = $('notes-input').value;
    if (!context) return;
    // Captured before either branch awaits. If the editor is retargeted or
    // closed while this write is in flight, the completion belongs to the old
    // note only: it may clear that note's own draft revision, but it must not
    // report status for, close, or navigate the editor that replaced it.
    const generation = notesEditorGeneration;
    if (context.type === 'tutor') {
        const snapshot = recordNotesRecoveryDraft(context, note);
        const record = setTutorNoteForContext(context, note);
        const persisted = await persistTutorNotesStore();
        if (typeof window.syncPdfTutorNoteIndicators === 'function') window.syncPdfTutorNoteIndicators();
        if (persisted) clearNotesRecoveryDraft(snapshot?.draftId, snapshot?.revision);
        if (generation !== notesEditorGeneration) return;
        if (!persisted) {
            // The editor stays open with the text intact; closing it here would
            // throw away the only copy of a note that never reached storage.
            showPopup(getNotesPersistFailureMessage('tutor'), null, true);
            return;
        }
        if (keepNotesEditorForTypingDuringSave(context, note)) return;
        showPopup(
            record
                ? (context.fromNotesBrowser === true
                    ? 'Note changes saved!'
                    : (record.noteType === 'image'
                        ? 'Source image saved to My Notes!'
                        : (record.snippetNote ? 'Snippet saved to My Notes!' : 'Tutor note saved!')))
                : 'Tutor note removed.',
            null,
            false
        );
        if (resumeNotesBrowserAfterEdit(context)) return;
    } else {
        const { applied, indicatorIndex } = applyQuestionNoteForContext(context, note);
        if (!applied) return;
        // Persist to IndexedDB (stable key-based format)
        const snapshot = recordNotesRecoveryDraft(context, note);
        const persisted = await persistQuestionNotesStore();
        if (persisted) clearNotesRecoveryDraft(snapshot?.draftId, snapshot?.revision);
        if (generation !== notesEditorGeneration) return;
        syncNotesButtonIndicator(indicatorIndex);
        if (!persisted) {
            showPopup(getNotesPersistFailureMessage('question'), null, true);
            return;
        }
        if (keepNotesEditorForTypingDuringSave(context, note)) return;
        showPopup('Note saved!', null, false);
        if (resumeNotesBrowserAfterEdit(context)) return;
    }
    teardownNotesEditorSurface();
}
window.saveQuestionNote = saveQuestionNote;

$('notes-input')?.addEventListener('input', syncNotesModalSaveState);

async function saveTutorNoteDirectly(rawContext = {}, noteText = '', options = {}) {
    const context = normalizeTutorNoteContext(rawContext);
    const note = sanitizeQuestionNoteText(noteText);
    if (!context || (!note && !context.imageDataUrl && !(context.snippetNote && context.preview))) return null;
    const record = setTutorNoteForContext(context, note);
    const persisted = await persistTutorNotesStore();
    if (typeof window.syncPdfTutorNoteIndicators === 'function') window.syncPdfTutorNoteIndicators();
    if (!persisted) {
        // Callers decorate their highlight as saved on a truthy record, so a
        // failed write has to report failure rather than a saved-looking record.
        if (options.showNotification !== false) {
            showPopup(getNotesPersistFailureMessage('tutor'), null, true);
        }
        return null;
    }
    if (options.showNotification !== false) {
        showPopup(options.notificationText || 'Tutor note saved!', null, false);
    }
    return record;
}
window.saveTutorNoteDirectly = saveTutorNoteDirectly;

document.addEventListener('mousedown', (event) => {
    const modal = $('notes-modal');
    const modalContent = $('notes-modal-content');
    if (!modal || !modalContent || !modal.classList.contains('active')) return;
    if ((modal.dataset.layoutMode || '') === 'docked') return;
    // Ask and Slide take over the shared pane with their own crossfade. Closing Notes here, on
    // mousedown before their click, dropped the duo to the solo card for a frame first.
    if (event.target.closest('#notes-modal-content, #notes-btn-inline, #ask-memora-inline-btn, #toggle-viewer-btn, #my-notes-menu-btn, #pdf-tutor-my-notes-btn, #pdf-tutor-guided-my-notes-btn, .pdf-tutor-focus-note-btn')) return;
    // Clicking away is a dismissal like any other: it must not discard writing
    // that has not reached storage.
    void requestNotesEditorTransition(teardownNotesEditorSurface);
});

window.addEventListener('resize', syncNotesSurfaceLayout, { passive: true });
$('notes-browser')?.addEventListener('click', handleNotesBrowserInteraction);
$('notes-browser')?.addEventListener('keydown', (event) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    handleNotesBrowserInteraction(event);
});
$('notes-browser')?.addEventListener('change', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const select = target?.closest?.('[data-notes-session-select]');
    if (!select) return;
    notesBrowserSessionScope = normalizeNotesBrowserSessionScope(select.value || 'current');
    void renderNotesBrowser({ animateScopeChange: true });
});

window.getTutorNoteForKey = getTutorNoteForKey;
window.hasTutorNoteForKey = hasTutorNoteForKey;
window.areTutorNotesHydrated = () => tutorNotesStoreHydrated;
window.getAllMemoraNotesEntries = getAllNotesEntries;

// --- NotebookLM Integration ---
function openNotebookLM() {
    window.open('https://notebooklm.google.com', '_blank');
}
window.openNotebookLM = openNotebookLM;

// ============================================================
// CANVAS LMS INTEGRATION
// ============================================================
// Canvas credentials are stored in IndexedDB for security.
// Never stored on disk or in code.

let canvasCredentials = null;
let currentCanvasCourse = null;

// These settings are needed early, but must wait for init() to own the one DB
// open. Reading them together avoids three competing opens and transactions.
async function hydrateStartupSettingsAfterDatabaseOpen() {
    try {
        const [questionNotes, tutorNotes, recoveryDrafts, savedCanvasCredentials] = await IDB.getMany('settings', [
            QUESTION_NOTES_STORAGE_KEY,
            TUTOR_NOTES_STORAGE_KEY,
            NOTES_RECOVERY_DRAFT_STORAGE_KEY,
            'canvasCredentials'
        ]);
        hydrateQuestionNotesStore(questionNotes);
        hydrateTutorNotesStore(tutorNotes);
        captureNotesStoreBaseline('question', questionNotes);
        captureNotesStoreBaseline('tutor', tutorNotes);
        // Text the reader typed but storage never confirmed comes back with the
        // editor rather than being lost to the reload.
        hydrateNotesRecoveryDrafts(recoveryDrafts);
        if (savedCanvasCredentials) canvasCredentials = savedCanvasCredentials;
    } catch (_error) {
        hydrateQuestionNotesStore(undefined);
        hydrateNotesRecoveryDrafts(undefined);
        tutorNotesByKey = {};
        // An unread store has no baseline, so later saves add only this tab's
        // notes to whatever is stored instead of replacing it.
        captureNotesStoreBaseline('question', undefined);
        captureNotesStoreBaseline('tutor', undefined);
        // A failed read is still a settled one - "no notes" is the honest answer.
        tutorNotesStoreHydrated = true;
    }

    syncNotesButtonIndicator();
    if (typeof window.syncPdfTutorNoteIndicators === 'function') window.syncPdfTutorNoteIndicators();
}

function openCanvasSettingsModal() {
    // Pre-fill with existing values
    if (canvasCredentials) {
        $('canvas-url-input').value = canvasCredentials.url || '';
        $('canvas-token-input').value = canvasCredentials.token || '';
    }
    $('canvas-settings-modal').classList.add('active');
    $('canvas-settings-status').style.display = 'none';
}
window.openCanvasSettingsModal = openCanvasSettingsModal;

function closeCanvasSettingsModal(event) {
    if (event.target === $('canvas-settings-modal') || event.target.closest('.welcome-btn')) {
        $('canvas-settings-modal').classList.remove('active');
    }
}
window.closeCanvasSettingsModal = closeCanvasSettingsModal;

async function saveCanvasCredentials() {
    const url = $('canvas-url-input').value.trim();
    const token = $('canvas-token-input').value.trim();

    if (!url || !token) {
        showCanvasStatus('Please enter both Canvas URL and API token.', 'error');
        return;
    }

    // Test the connection
    showCanvasStatus('Testing connection...', 'info');

    try {
        const response = await fetch('http://localhost:5555/canvas/courses', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ canvasUrl: url, canvasToken: token })
        });

        const data = await response.json();

        if (data.error) {
            showCanvasStatus(data.error, 'error');
            return;
        }

        // Success - save credentials
        canvasCredentials = { url, token };
        await IDB.set('canvasCredentials', canvasCredentials);

        showCanvasStatus('Connected successfully!', 'success');

        // Close settings and open browser after a moment
        setTimeout(() => {
            $('canvas-settings-modal').classList.remove('active');
            openCanvasBrowserModal();
        }, 800);

    } catch (e) {
        showCanvasStatus('Failed to connect. Is the server running?', 'error');
    }
}
window.saveCanvasCredentials = saveCanvasCredentials;

function showCanvasStatus(msg, type) {
    const el = $('canvas-settings-status');
    el.textContent = msg;
    el.style.display = 'block';
    el.style.color = type === 'error' ? '#ef4444' : type === 'success' ? '#22c55e' : '#60a5fa';
}

function showCanvasTokenHelp() {
    $('canvas-help-modal').classList.add('active');
}
window.showCanvasTokenHelp = showCanvasTokenHelp;

async function openCanvasBrowserModal() {
    // Canvas import requires server
    const serverAvailable = await checkServerAvailable();
    if (!serverAvailable) {
        showServerRequiredPopup('Canvas import');
        return;
    }

    if (!canvasCredentials) {
        openCanvasSettingsModal();
        return;
    }
    $('canvas-browser-modal').classList.add('active');
    loadCanvasCourses();
}
window.openCanvasBrowserModal = openCanvasBrowserModal;

function closeCanvasBrowserModal(event) {
    $('canvas-browser-modal').classList.remove('active');
}
window.closeCanvasBrowserModal = closeCanvasBrowserModal;

// Navigate back in Canvas browser (breadcrumb) or return to import modal if at root
function canvasBrowserGoBack() {
    const breadcrumb = $('canvas-breadcrumb');
    const crumbs = breadcrumb.querySelectorAll('.canvas-crumb');

    if (crumbs.length <= 1) {
        // At root level, close Canvas modal and reopen import modal
        $('canvas-browser-modal').classList.remove('active');
        $('import-modal').classList.add('active');
        window.registerImportModalLayer?.();
    } else {
        // Go back one level - click the previous crumb
        crumbs[crumbs.length - 2].click();
    }
}
window.canvasBrowserGoBack = canvasBrowserGoBack;

async function loadCanvasCourses() {
    currentCanvasCourse = null;
    updateCanvasBreadcrumb(['Courses']);
    $('canvas-content').innerHTML = '<div class="canvas-loading">Loading courses...</div>';

    try {
        const response = await fetch('http://localhost:5555/canvas/courses', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ canvasUrl: canvasCredentials.url, canvasToken: canvasCredentials.token })
        });

        const data = await response.json();

        if (data.error) {
            $('canvas-content').innerHTML = `<div class="canvas-error">${escapeHtml(data.error)}</div>`;
            return;
        }

        if (!data.courses || data.courses.length === 0) {
            $('canvas-content').innerHTML = '<div class="canvas-empty">No courses found.</div>';
            return;
        }

        renderCanvasCourses(data.courses);

    } catch (e) {
        $('canvas-content').innerHTML = '<div class="canvas-error">Failed to load courses. Is the server running?</div>';
    }
}
window.loadCanvasCourses = loadCanvasCourses;

function renderCanvasCourses(courses) {
    const html = courses.map(c => `
        <div class="canvas-item" data-canvas-action="open-course" data-canvas-id="${escapeHtml(c.id)}" data-canvas-name="${escapeHtml(c.name)}">
            <span class="canvas-item-icon">📚</span>
            <div class="canvas-item-info">
                <div class="canvas-item-name">${escapeHtml(c.name)}</div>
                <div class="canvas-item-meta">${escapeHtml(c.code || '')}</div>
            </div>
            <span style="color:#64748b;">→</span>
        </div>
    `).join('');

    $('canvas-content').innerHTML = html;
    bindCanvasContentActions();
}

async function loadCanvasFiles(courseId, courseName) {
    currentCanvasCourse = { id: courseId, name: courseName };
    updateCanvasBreadcrumb(['Courses', courseName]);
    $('canvas-content').innerHTML = '<div class="canvas-loading">Loading files...</div>';

    try {
        const response = await fetch(`http://localhost:5555/canvas/files/${courseId}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ canvasUrl: canvasCredentials.url, canvasToken: canvasCredentials.token })
        });

        const data = await response.json();

        if (data.error) {
            $('canvas-content').innerHTML = `<div class="canvas-error">${escapeHtml(data.error)}</div>`;
            return;
        }

        if (!data.files || data.files.length === 0) {
            $('canvas-content').innerHTML = '<div class="canvas-empty">No compatible files found (PDF, PPTX, DOCX).</div>';
            return;
        }

        renderCanvasFiles(data.files);

    } catch (e) {
        $('canvas-content').innerHTML = '<div class="canvas-error">Failed to load files.</div>';
    }
}
window.loadCanvasFiles = loadCanvasFiles;

function renderCanvasFiles(files) {
    const html = files.map(f => {
        const size = f.size ? `${(f.size / 1024).toFixed(1)} KB` : '';
        const isPdf = f.name.toLowerCase().endsWith('.pdf');
        return `
            <div class="canvas-item" data-canvas-action="download-file" data-canvas-id="${escapeHtml(f.id)}" data-canvas-name="${escapeHtml(f.name)}">
                <span class="canvas-item-icon">${isPdf ? '📄' : '📎'}</span>
                <div class="canvas-item-info">
                    <div class="canvas-item-name">${escapeHtml(f.name)}</div>
                    <div class="canvas-item-meta">${size}</div>
                </div>
                <div style="display:flex; gap:6px;">
                    <button class="canvas-item-action" data-canvas-action="download-file" data-canvas-id="${escapeHtml(f.id)}" data-canvas-name="${escapeHtml(f.name)}" title="Import file to Sources">
                        Import
                    </button>
                    <button class="canvas-item-action" data-canvas-action="download-file-and-quiz" data-canvas-id="${escapeHtml(f.id)}" data-canvas-name="${escapeHtml(f.name)}" style="background:linear-gradient(135deg, #6ee7b7, #3b82f6);" title="Import and generate quiz">
                        Import + Quiz
                    </button>
                </div>
            </div>
        `;
    }).join('');

    $('canvas-content').innerHTML = html;
    bindCanvasContentActions();
}

function bindCanvasContentActions() {
    const content = $('canvas-content');
    if (!content) return;
    content.onclick = (event) => {
        const actionTarget = event.target.closest('[data-canvas-action]');
        if (!actionTarget) return;
        event.stopPropagation();
        const id = actionTarget.dataset.canvasId || '';
        const name = actionTarget.dataset.canvasName || '';
        if (actionTarget.dataset.canvasAction === 'open-course') {
            void loadCanvasFiles(id, name);
        } else if (actionTarget.dataset.canvasAction === 'download-file-and-quiz') {
            void downloadCanvasFileAndQuiz(id, name);
        } else if (actionTarget.dataset.canvasAction === 'download-file') {
            void downloadCanvasFile(id, name);
        }
    };
}

async function downloadCanvasFile(fileId, filename) {
    // Close canvas browser modal
    $('canvas-browser-modal').classList.remove('active');

    const lowerFilename = filename.toLowerCase();
    const needsConversion = lowerFilename.endsWith('.pptx') || lowerFilename.endsWith('.docx') ||
        lowerFilename.endsWith('.ppt') || lowerFilename.endsWith('.doc');

    // Show progress modal for conversion files
    if (needsConversion) {
        showConversionProgress(filename);
    } else {
        showPopup('Downloading from Canvas...', null, false);
    }

    try {
        // Step 1: Download from Canvas
        if (needsConversion) updateConversionProgress(20, 'Downloading from Canvas...');

        const response = await fetch('http://localhost:5555/canvas/download', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                canvasUrl: canvasCredentials.url,
                canvasToken: canvasCredentials.token,
                fileId: fileId
            })
        });

        const data = await response.json();

        if (data.error) {
            hideConversionProgress();
            showPopupText('Download failed: ' + data.error, null, false);
            return;
        }

        const actualLowerFilename = data.filename.toLowerCase();

        // Handle PDFs directly
        if (actualLowerFilename.endsWith('.pdf')) {
            const added = await addPDFToStorage(data.filename, data.content);
            if (!added) {
                hideConversionProgress();
                return;
            }
            hideConversionProgress();
            showPopupText(`Imported: ${data.filename}`, null, false);
            showEvidencePanelWithFlash(data.filename);
            return;
        }

        // For PPTX/DOCX, convert to PDF
        if (actualLowerFilename.endsWith('.pptx') || actualLowerFilename.endsWith('.docx') ||
            actualLowerFilename.endsWith('.ppt') || actualLowerFilename.endsWith('.doc')) {

            updateConversionProgress(40, 'Converting to PDF using Office...');

            try {
                const convertResponse = await fetch('http://localhost:5555/convert-to-pdf', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        content: data.content,
                        filename: data.filename,
                        contentType: data.contentType
                    })
                });

                updateConversionProgress(70, 'Processing converted file...');

                const convertData = await convertResponse.json();

                if (convertData.success && convertData.filename.endsWith('.pdf')) {
                    updateConversionProgress(90, 'Saving to library...');
                    const added = await addPDFToStorage(convertData.filename, convertData.content);
                    if (!added) {
                        hideConversionProgress();
                        return;
                    }

                    updateConversionProgress(100, 'Complete!');
                    setTimeout(() => {
                        hideConversionProgress();
                        showPopupText(`Converted & imported: ${convertData.filename}`, null, false);
                        showEvidencePanelWithFlash(convertData.filename);
                    }, 500);
                } else {
                    hideConversionProgress();
                    showPopupText('Conversion failed: ' + (convertData.error || 'Office not available'), null, false);
                }
            } catch (e) {
                hideConversionProgress();
                showPopupText('Conversion failed: ' + e.message, null, false);
            }
            return;
        }

        // Other file types
        hideConversionProgress();
        showPopup('File type not supported for import.', null, false);

    } catch (e) {
        hideConversionProgress();
        showPopupText('Download failed: ' + e.message, null, false);
    }
}
window.downloadCanvasFile = downloadCanvasFile;

// Download from Canvas and immediately open quiz generation modal
async function downloadCanvasFileAndQuiz(fileId, filename) {
    // Close canvas browser modal
    $('canvas-browser-modal').classList.remove('active');

    const lowerFilename = filename.toLowerCase();
    const needsConversion = lowerFilename.endsWith('.pptx') || lowerFilename.endsWith('.docx') ||
        lowerFilename.endsWith('.ppt') || lowerFilename.endsWith('.doc');

    // Show progress modal for conversion files
    if (needsConversion) {
        showConversionProgress(filename);
    } else {
        showPopup('Downloading from Canvas...', null, false);
    }

    try {
        // Step 1: Download from Canvas
        if (needsConversion) updateConversionProgress(20, 'Downloading from Canvas...');

        const response = await fetch('http://localhost:5555/canvas/download', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                canvasUrl: canvasCredentials.url,
                canvasToken: canvasCredentials.token,
                fileId: fileId
            })
        });

        const data = await response.json();

        if (data.error) {
            hideConversionProgress();
            showPopupText('Download failed: ' + data.error, null, false);
            return;
        }

        const actualLowerFilename = data.filename.toLowerCase();
        let finalFilename = data.filename;

        // Handle PDFs directly
        if (actualLowerFilename.endsWith('.pdf')) {
            const added = await addPDFToStorage(data.filename, data.content);
            if (!added) {
                hideConversionProgress();
                return;
            }
            hideConversionProgress();
            showPopupText(`Imported: ${data.filename}`, null, false);
            // Open quiz generation modal instead of evidence panel
            setTimeout(() => {
                const pdfFilesArray = Object.keys(loadedPdfFiles).map(name => ({ name, blob: loadedPdfFiles[name] }));
                showQuizGenerationModal(pdfFilesArray);
            }, 300);
            return;
        }

        // For PPTX/DOCX, convert to PDF
        if (actualLowerFilename.endsWith('.pptx') || actualLowerFilename.endsWith('.docx') ||
            actualLowerFilename.endsWith('.ppt') || actualLowerFilename.endsWith('.doc')) {

            updateConversionProgress(40, 'Converting to PDF using Office...');

            try {
                const convertResponse = await fetch('http://localhost:5555/convert-to-pdf', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        content: data.content,
                        filename: data.filename,
                        contentType: data.contentType
                    })
                });

                updateConversionProgress(70, 'Processing converted file...');

                const convertData = await convertResponse.json();

                if (convertData.success && convertData.filename.endsWith('.pdf')) {
                    updateConversionProgress(90, 'Saving to library...');
                    const added = await addPDFToStorage(convertData.filename, convertData.content);
                    if (!added) {
                        hideConversionProgress();
                        return;
                    }

                    updateConversionProgress(100, 'Complete!');
                    setTimeout(() => {
                        hideConversionProgress();
                        showPopupText(`Converted & imported: ${convertData.filename}`, null, false);
                        // Open quiz generation modal instead of evidence panel
                        setTimeout(() => {
                            const pdfFilesArray = Object.keys(loadedPdfFiles).map(name => ({ name, blob: loadedPdfFiles[name] }));
                            showQuizGenerationModal(pdfFilesArray);
                        }, 300);
                    }, 500);
                } else {
                    hideConversionProgress();
                    showPopupText('Conversion failed: ' + (convertData.error || 'Office not available'), null, false);
                }
            } catch (e) {
                hideConversionProgress();
                showPopupText('Conversion failed: ' + e.message, null, false);
            }
            return;
        }

        // Other file types
        hideConversionProgress();
        showPopup('File type not supported for import.', null, false);

    } catch (e) {
        hideConversionProgress();
        showPopupText('Download failed: ' + e.message, null, false);
    }
}
window.downloadCanvasFileAndQuiz = downloadCanvasFileAndQuiz;

// Add PDF to storage from base64 content
async function addPDFToStorage(filename, base64Content) {
    try {
        if (isMemoraPdfSourceCurrentlyLoaded(filename)) {
            showMemoraDuplicatePdfSourceWarning(filename);
            return false;
        }
        // Convert base64 to Blob
        const byteCharacters = atob(base64Content);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: 'application/pdf' });

        // Create File object
        const { file, renamedFrom } = await claimMemoraSourceFileName(new File([blob], filename, { type: 'application/pdf' }));
        if (renamedFrom) showMemoraSourceRenamedNotice(renamedFrom, file.name);
        filename = file.name;

        if (typeof window.startQuizGenPdfUploadPreparation === 'function') {
            void window.startQuizGenPdfUploadPreparation(file, filename);
        }

        // Create URL for viewer (same as regular import)
        recordMemoraSourceRevision(filename, { byteLength: file.size });
        if (loadedPdfFiles[filename]) URL.revokeObjectURL(loadedPdfFiles[filename]);
        loadedPdfFiles[filename] = URL.createObjectURL(file);

        // Store in the background so conversion output is usable immediately.
        void putMemoraSourceFileRecord(filename, file)
            .catch((dbErr) => {
                console.warn("DB Save Failed for PDF, using memory only", dbErr);
                showMemoraSourceSaveFailure(file);
            });

        // Extract text for smart search (same as regular import)
        // PDF text indexing disabled.

        // Add to session manifest (same as regular import)
        if (!sessionManifest.pdfs.includes(filename)) {
            sessionManifest.pdfs.push(filename);
        }

        // Refresh evidence panel list
        if (typeof updateSourceDropdown === 'function') {
            updateSourceDropdown();
        }

        // Open the PDF in the viewer (same as regular import)
        if (typeof openPdf === 'function') {
            openPdf(filename);
        }

        if (!currentSessionId) currentSessionId = Date.now().toString();
        try {
            await SessionManager.save(false);
        } catch (saveErr) {
            console.warn('Session save failed after PDF storage import:', saveErr);
        }

        console.log(`PDF imported from Canvas: ${filename}`);
        return true;
    } catch (e) {
        console.error('Failed to add PDF to storage:', e);
        throw e;
    }
}


// Conversion progress modal helpers
function showConversionProgress(filename) {
    const ext = filename.split('.').pop().toUpperCase();
    const appName = (ext === 'PPTX' || ext === 'PPT') ? 'PowerPoint' : 'Word';

    $('conversion-icon').textContent = '';  // No emoji
    $('conversion-title').textContent = `Converting ${appName} to PDF`;
    $('conversion-subtitle').style.display = 'none';  // Hide subtitle
    $('conversion-status').textContent = 'Preparing file...';
    $('conversion-progress-fill').style.width = '10%';
    $('conversion-progress-modal').classList.add('active');
}

function updateConversionProgress(percent, status) {
    $('conversion-progress-fill').style.width = percent + '%';
    $('conversion-status').textContent = status;
}

function hideConversionProgress() {
    $('conversion-progress-modal').classList.remove('active');
}

// Show evidence panel and flash the newly imported file
function showEvidencePanelWithFlash(filename) {
    console.log('showEvidencePanelWithFlash called with:', filename);

    // Open the PDF in the viewer first
    if (typeof openPdf === 'function' && loadedPdfFiles[filename]) {
        openPdf(filename);
    }

    // Force update the PDF title AFTER openPdf (in case it wasn't set)
    setTimeout(() => {
        const pdfTitle = document.getElementById('pdf-title');
        if (pdfTitle) {
            pdfTitle.innerText = filename;
            console.log('Set PDF title to:', filename);
        }
        if (els.pdfTitle) {
            els.pdfTitle.innerText = filename;
        }
    }, 100);

    // Ensure the viewer panel is open
    if (typeof toggleViewer === 'function') {
        toggleViewer(true);
    }

    // Refresh the evidence panel content. This path opens the panel and then reads
    // its rows, so the rebuild cannot be deferred.
    if (typeof updateSourceDropdown === 'function') {
        updateSourceDropdown({ force: true });
    }

    // Show the source wrapper and trigger the dropdown
    const sourceWrapper = document.getElementById('source-wrapper');
    const sourceTrigger = document.getElementById('source-trigger-btn');
    const sourceDropdown = document.getElementById('source-dropdown');

    if (sourceWrapper) {
        sourceWrapper.style.display = 'inline-block';
    }

    // Make the dropdown visible directly
    if (sourceDropdown) {
        sourceDropdown.classList.add('active');
        // Don't set inline display:block - let CSS handle it via .active class
        syncFloatingQuizLayering();
    }

    // Flash the newly added PDF item in the evidence panel
    setTimeout(() => {
        // Find items in the evidence panel - get the PDF column on the right
        const pdfCol = document.getElementById('evidence-pdf-col');
        const cleanName = filename.replace('.pdf', '').substring(0, 10).toLowerCase();

        console.log('Looking for PDF with name containing:', cleanName);

        // Look for evidence items in the PDF column
        if (pdfCol) {
            const items = pdfCol.querySelectorAll('.evidence-item');
            console.log('Found', items.length, 'items in PDF column');

            items.forEach(item => {
                const text = item.textContent.toLowerCase();
                if (text.includes(cleanName)) {
                    console.log('MATCH! Flashing item:', text.substring(0, 30));
                    item.classList.add('new-import-flash');
                    setTimeout(() => item.classList.remove('new-import-flash'), 5000);
                }
            });
        } else {
            // Fallback: Look for any evidence items
            console.log('PDF column not found, searching all evidence items');
            const allItems = document.querySelectorAll('.evidence-item');
            allItems.forEach(item => {
                const text = item.textContent.toLowerCase();
                if (text.includes(cleanName)) {
                    console.log('MATCH (fallback)! Flashing item');
                    item.classList.add('new-import-flash');
                    setTimeout(() => item.classList.remove('new-import-flash'), 5000);
                }
            });
        }

        // Also flash the PDF pane header
        const pdfPane = document.getElementById('pdf-pane');
        if (pdfPane) {
            pdfPane.classList.add('evidence-flash');
            setTimeout(() => pdfPane.classList.remove('evidence-flash'), 2000);
        }
    }, 800);
}


function updateCanvasBreadcrumb(parts) {
    const html = parts.map((p, i) => {
        if (i === 0) {
            return `<span class="canvas-crumb" onclick="loadCanvasCourses()">${escapeHtml(p)}</span>`;
        }
        return `<span class="canvas-crumb-sep">›</span><span class="canvas-crumb">${escapeHtml(p)}</span>`;
    }).join('');

    $('canvas-breadcrumb').innerHTML = html;
}

function escapeHtml(str) {
    const value = String(str ?? '');
    if (!value) return '';
    return value
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
}

function sanitizeQuizRichTextHtml(value) {
    const template = document.createElement('template');
    template.innerHTML = String(value ?? '');
    const allowedTags = new Set(['B', 'BR', 'CODE', 'EM', 'I', 'LI', 'OL', 'P', 'PRE', 'S', 'STRONG', 'SUB', 'SUP', 'U', 'UL']);
    const blockedTags = new Set(['EMBED', 'IFRAME', 'MATH', 'OBJECT', 'SCRIPT', 'STYLE', 'SVG', 'TEMPLATE']);

    const sanitizeChildren = (parent) => {
        Array.from(parent.childNodes).forEach((node) => {
            if (node.nodeType === 8) {
                node.remove();
                return;
            }
            if (node.nodeType !== 1) return;
            if (blockedTags.has(node.tagName)) {
                node.remove();
                return;
            }
            sanitizeChildren(node);
            if (!allowedTags.has(node.tagName)) {
                node.replaceWith(...Array.from(node.childNodes));
                return;
            }
            Array.from(node.attributes).forEach((attribute) => node.removeAttribute(attribute.name));
        });
    };

    sanitizeChildren(template.content);
    return template.innerHTML;
}

function serializeAnkiField(value) {
    return String(value ?? '')
        .replace(/\r\n?/g, '\n')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/\n/g, '<br>')
        .replace(/\t/g, ' ');
}


function exportQuiz(format) {
    const savedOnly = $('export-saved-only')?.checked;
    const savedIndices = Object.keys(savedQuestions).map(Number).filter(i => !isNaN(i) && i < questions.length);
    const exportOrder = Array.isArray(filteredIndices) && filteredIndices.length > 0
        ? filteredIndices.filter((idx) => Number.isInteger(idx) && idx >= 0 && idx < questions.length)
        : questions.map((_, idx) => idx);
    const questionSetKeyByIndex = buildResultsQuestionSetKeyByIndexMap();

    const orderedSavedIndices = exportOrder.filter((idx) => savedIndices.includes(idx));
    const fallbackSavedIndices = savedIndices.filter((idx) => !orderedSavedIndices.includes(idx));
    const exportIndices = savedOnly
        ? [...orderedSavedIndices, ...fallbackSavedIndices]
        : exportOrder;
    const dataToExport = exportIndices.map((i) => questions[i]).filter(Boolean);

    if (dataToExport.length === 0) {
        syncContentDependentControls();
        showPopupText('No quiz questions are available yet.', null, true, 5000);
        return false;
    }

    let content, filename, mimeType;

    switch (format) {
        case 'anki':
            // Anki format: Front\tBack (tab-separated)
            content = dataToExport.map(q => {
                const front = serializeAnkiField(q.q);
                const answer = serializeAnkiField(q.c?.[q.a]);
                const explanation = serializeAnkiField(q.e);
                const back = explanation ? `${answer}<br><br>${explanation}` : answer;
                return `${front}\t${back}`;
            }).join('\n');
            filename = 'quiz_anki.txt';
            mimeType = 'text/plain';
            break;

        case 'quizlet':
            // Quizlet format: Term; Definition (semicolon-separated)
            content = dataToExport.map(q => {
                const term = q.q.replace(/;/g, ',').replace(/\n/g, ' ');
                const def = `${q.c[q.a]}`.replace(/;/g, ',').replace(/\n/g, ' ');
                return `${term}; ${def}`;
            }).join('\n');
            filename = 'quiz_quizlet.txt';
            mimeType = 'text/plain';
            break;

        case 'csv': {
            // CSV with headers: one column per choice position, as many as the longest question has.
            const csvField = (value) => `"${String(value ?? '').replace(/"/g, '""')}"`;
            const choiceCount = Math.max(4, ...dataToExport.map(q => (Array.isArray(q.c) ? q.c.length : 0)));
            const choiceHeaders = Array.from({ length: choiceCount }, (_, i) => `Choice ${String.fromCharCode(65 + i)}`);
            const headers = ['Question', ...choiceHeaders, 'Correct Answer', 'Explanation', 'Source'].join(',');
            const rows = dataToExport.map(q => {
                const choices = Array.isArray(q.c) ? q.c : [];
                return [
                    csvField(q.q),
                    ...choiceHeaders.map((_, i) => csvField(choices[i])),
                    csvField(choices[q.a]),
                    csvField(q.e),
                    csvField(q.src || q.sourceFile)
                ].join(',');
            });
            content = [headers, ...rows].join('\n');
            filename = 'quiz_export.csv';
            mimeType = 'text/csv';
            break;
        }

        case 'json':
            content = JSON.stringify(exportIndices.map((idx) => {
                const question = questions[idx];
                if (!question || typeof question !== 'object') return question;
                return {
                    ...question,
                    quizSetKey: String(
                        question.quizSetKey
                        || question.setKey
                        || question.bankKey
                        || questionSetKeyByIndex[idx]
                        || ''
                    ).trim()
                };
            }), null, 2);
            filename = 'quiz_export.json';
            mimeType = 'application/json';
            break;
    }

    // Download the file
    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);

    // Close modal and show confirmation
    $('export-modal').classList.remove('active');
    showPopup(`Exported ${dataToExport.length} questions as ${format.toUpperCase()}`, null, false);
    syncContentDependentControls();
    return true;
}
window.exportQuiz = exportQuiz;

if (els.adaptiveSkipUndoneBtn) {
    els.adaptiveSkipUndoneBtn.onclick = jumpToNextAdaptiveUndone;
}

els.exitFilter.onclick = () => {
    if (isAdaptiveReviewMode) {
        deactivateAdaptiveReviewMode(true);
        return;
    }
    resetFilter();
    // If not shuffled, go to first unanswered question.
    if (!isShuffled) {
        const firstUnans = filteredIndices.findIndex(idx => userAnswers[idx] === undefined);
        currentQIndex = firstUnans >= 0 ? firstUnans : 0;
    }
    isFlashcardMode ? renderFlashcardGrid() : renderQuestion();
};

/* --- QUIZ LOGIC --- */
const pageRegex = /(?:Page|Pg|Slide|p\.?|Sl\.?)\s*(?:#|no\.?|num\.?|:|-)?\s*(\d+)|^\s*(\d+)/i;

function getQuestionCitationInfo(question) {
    if (!question || typeof question !== 'object') {
        return { citationText: '', page: null };
    }
    const citationText = String(
        question.add ||
        question.citation ||
        question.page ||
        question.slide ||
        question.slideNumber ||
        question.citationPage ||
        ''
    ).trim();
    const match = citationText ? citationText.match(pageRegex) : null;
    let parsed = match ? parseInt(match[1] || match[2], 10) : NaN;
    if (!Number.isFinite(parsed) || parsed <= 0) {
        const directNum = Number(question.slideNumber || question.slide || question.citationPage || question.page);
        if (Number.isFinite(directNum) && directNum > 0) {
            parsed = directNum;
        }
    }
    return {
        citationText,
        page: Number.isFinite(parsed) && parsed > 0 ? Math.floor(parsed) : null
    };
}

function syncQuizSourceButtonState() {
    const btn = $('toggle-viewer-btn') || document.getElementById('toggle-viewer-btn');
    if (!btn) return;
    const textSpan = btn.querySelector('.quick-control-text');

    if (!Array.isArray(questions) || !questions.length || !Array.isArray(filteredIndices) || !filteredIndices.length) {
        if (textSpan) textSpan.textContent = 'Source';
        btn.disabled = true;
        btn.classList.add('is-disabled');
        btn.setAttribute('aria-disabled', 'true');
        btn.title = 'No source slide linked to this question';
        btn.setAttribute('aria-label', 'No source slide linked to this question');
        return;
    }

    const realIdx = filteredIndices[currentQIndex];
    const q = Number.isInteger(realIdx) ? questions[realIdx] : null;
    if (!q || typeof q !== 'object') {
        if (textSpan) textSpan.textContent = 'Source';
        btn.disabled = true;
        btn.classList.add('is-disabled');
        btn.setAttribute('aria-disabled', 'true');
        btn.title = 'No source slide linked to this question';
        btn.setAttribute('aria-label', 'No source slide linked to this question');
        return;
    }

    const citation = typeof getQuestionCitationInfo === 'function'
        ? getQuestionCitationInfo(q)
        : null;
    let slideNumber = Number.isInteger(citation?.page) && citation.page > 0 ? citation.page : null;
    if (!slideNumber) {
        const rawSlide = Number(q.citationPage || q.slide || q.page || q.slideNumber || 0);
        if (Number.isInteger(rawSlide) && rawSlide > 0) {
            slideNumber = rawSlide;
        }
    }

    const targetPdf = typeof findBestPdfForQuestion === 'function'
        ? String(findBestPdfForQuestion(q) || '').trim()
        : '';

    const hasSlide = Number.isInteger(slideNumber) && slideNumber > 0;
    const hasSource = hasSlide || !!targetPdf;
    // A linked name is only a source once that file is loaded; a JSON-only bank's derived
    // "<bank>.pdf" is offered as something to link, never opened as an empty viewer.
    const sourceLoaded = !!targetPdf && !!loadedPdfFiles?.[targetPdf];
    btn.toggleAttribute('data-source-missing', hasSource && !sourceLoaded);

    if (hasSource && !sourceLoaded) {
        if (textSpan) textSpan.textContent = 'Link source';
        btn.disabled = false;
        btn.classList.remove('is-disabled');
        btn.removeAttribute('aria-disabled');
        btn.title = 'Add the lecture file for this question';
        btn.setAttribute('aria-label', 'Link a source file for this question');
    } else if (hasSlide) {
        const labelText = `Slide ${slideNumber}`;
        if (textSpan) textSpan.textContent = labelText;
        btn.disabled = false;
        btn.classList.remove('is-disabled');
        btn.removeAttribute('aria-disabled');
        const titleText = targetPdf
            ? `Open ${targetPdf} at Slide ${slideNumber}`
            : `Open source slide (Slide ${slideNumber})`;
        btn.title = titleText;
        btn.setAttribute('aria-label', titleText);
    } else if (hasSource) {
        if (textSpan) textSpan.textContent = 'Source';
        btn.disabled = false;
        btn.classList.remove('is-disabled');
        btn.removeAttribute('aria-disabled');
        btn.title = `Open ${targetPdf}`;
        btn.setAttribute('aria-label', `Open ${targetPdf}`);
    } else {
        if (textSpan) textSpan.textContent = 'Source';
        btn.disabled = true;
        btn.classList.add('is-disabled');
        btn.setAttribute('aria-disabled', 'true');
        btn.title = 'No source slide linked to this question';
        btn.setAttribute('aria-label', 'No source slide linked to this question');
    }
}
window.syncQuizSourceButtonState = syncQuizSourceButtonState;

function focusQuizQuestionPanel({ preserveScroll = true } = {}) {
    const target = els?.quizCard || $('quiz-card');
    if (!target) return;
    if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
    try {
        target.focus({ preventScroll: !!preserveScroll });
    } catch (_err) {
        try { target.focus(); } catch (_err2) { }
    }
}

function ensureAutoStudyTrioLayout() {
    const workArea = els?.workArea || $('work-area');
    if (!workArea) return false;
    if (workArea.classList.contains('trio-view') || isTrioMode) {
        isTrioMode = true;
        isViewerOpen = true;
        return true;
    }

    if (typeof toggleTrio === 'function') {
        toggleTrio(true, { skipPdfRestore: true, preserveExpandedLock: true });
        return true;
    }

    window.markPaneOpenTransitioning?.({ targetLayout: 'trio' });
    workArea.classList.remove('split-view', 'split-view-ai');
    workArea.classList.add('trio-view');
    if (els.pdfPane) els.pdfPane.style.display = '';
    if (els.pdfTitle) els.pdfTitle.style.display = '';
    isTrioMode = true;
    isViewerOpen = true;
    if (typeof setPdfPaneMode === 'function') setPdfPaneMode('pdf');
    if (typeof resetPanelResizeColumnsForModeSwitch === 'function') resetPanelResizeColumnsForModeSwitch();
    if (typeof setViewerToggleLabel === 'function') setViewerToggleLabel('Hide PDF');
    if (typeof setTrioToggleLabel === 'function') setTrioToggleLabel('\u2AF6 Exit Trio');
    if (typeof applyPanelOrderLayout === 'function') applyPanelOrderLayout();
    return true;
}

function ensurePdfEvidenceDuoLayout() {
    const workArea = els?.workArea || $('work-area');
    if (!workArea) return false;
    if (window.isAskMemoraDocked?.()) window.revealAskMemoraForSourceEvidence?.();
    const isPdfDuo = workArea.classList.contains('split-view')
        && !workArea.classList.contains('split-view-ai')
        && !workArea.classList.contains('trio-view')
        && !isTrioMode;
    if (isPdfDuo) {
        isViewerOpen = true;
        return true;
    }

    if (isPdfPaneExpanded && typeof togglePdfPaneExpanded === 'function') {
        togglePdfPaneExpanded(false);
    }
    if (typeof AI_PANE_MODE_PDF_EXPLANATION !== 'undefined'
        && aiPaneMode === AI_PANE_MODE_PDF_EXPLANATION
        && typeof preserveAiExplanationReaderForResume === 'function') {
        preserveAiExplanationReaderForResume('Reader paused. Reopen PDF Explanation to resume.');
    }

    const aiFrame = $('ai-frame');
    if (aiFrame) aiFrame.src = '';
    if (workArea.classList.contains('split-view-ai') || workArea.classList.contains('trio-view') || isTrioMode) {
        window.markPaneCloseTransitioning?.('ai');
    }
    window.markPaneOpenTransitioning?.({ targetLayout: 'duo' });
    workArea.classList.remove('split-view-ai', 'trio-view');
    workArea.classList.add('split-view');
    if (els.pdfPane) els.pdfPane.style.display = '';
    if (els.pdfTitle) els.pdfTitle.style.display = '';
    isTrioMode = false;
    isViewerOpen = true;

    if (typeof setPdfPaneMode === 'function') setPdfPaneMode('pdf');
    if (typeof resetPanelResizeColumnsForModeSwitch === 'function') resetPanelResizeColumnsForModeSwitch();
    if (typeof setViewerToggleLabel === 'function') {
        setViewerToggleLabel('Hide PDF');
    } else {
        const label = $('toggle-viewer-btn')?.querySelector('.quick-control-text');
        if (label) label.innerText = 'Source';
    }
    if (typeof setTrioToggleLabel === 'function') setTrioToggleLabel('\u2AF6 Trio');
    if (typeof applyPanelOrderLayout === 'function') applyPanelOrderLayout();
    if (typeof scheduleQuizCardPositionReset === 'function') {
        scheduleQuizCardPositionReset({ forceLayoutDefault: true, layoutMode: 'duo', preserveExpandedLock: true });
    }
    void syncPdfScaleForCurrentLayoutModeSwitch();
    return true;
}

function triggerEvidence({
    syncExplanation = false,
    smoothJump = false,
    includeSearch = false,
    forceFreshViewer = false,
    preferQuizFocus = false,
    maintainPdfScale = false,
    silentIfMissing = false,
    preferDuoPdfPane = false,
    preferTrioLayout = false,
    minimizeDockedAsk = false
} = {}) {
    const q = questions[filteredIndices[currentQIndex]];
    if (!q) return false;

    // 1. Try to parse page number from citation, default to 1 if missing
    const citation = getQuestionCitationInfo(q);
    const p = citation.page || 1;

    // 2. Find the PDF
    const targetPdf = findBestPdfForQuestion(q);

    // 3. Feedback if missing
    if (!targetPdf || !loadedPdfFiles[targetPdf]) {
        if (!silentIfMissing) {
            showPopup("No linked PDF found for this question.", null, true);
        }
        return false;
    }

    /* The Source button asks for this. It runs only once a real source has resolved, so a question
     * with no linked PDF never moves Ask, and before the layout and jump below, whose own viewer
     * hooks then find Ask already minimized rather than docked and add nothing further. */
    if (minimizeDockedAsk && window.isAskMemoraDocked?.()) {
        window.revealAskMemoraForSourceEvidence?.();
    }

    // Track evidence views for achievement
    evidenceViews++;
    checkAchievements();
    saveGamificationData();

    // 4. Extract key term for highlighting (use correct answer text)
    const answerText = q.c && q.c[q.a] ? q.c[q.a] : null;
    // Take first 3-4 significant words from the answer for search
    let searchTerm = null;
    if (includeSearch && answerText) {
        const words = answerText.split(/\s+/).filter(w => w.length > 3).slice(0, 3);
        if (words.length > 0) searchTerm = words.join(' ');
    }

    if (preferTrioLayout) {
        ensureAutoStudyTrioLayout();
    } else if (preferDuoPdfPane) {
        ensurePdfEvidenceDuoLayout();
    }

    // 5. Execute with highlighting
    jumpToPdfPage(p, targetPdf, searchTerm, {
        smooth: !!smoothJump,
        maintainScale: !!maintainPdfScale,
        forceReload: !!forceFreshViewer
    });

    if (preferQuizFocus) {
        requestAnimationFrame(() => focusQuizQuestionPanel({ preserveScroll: true }));
    }

    if (syncExplanation && isMemoraPdfExplanationFeatureEnabledForCore() && (isTrioMode || els.workArea.classList.contains('split-view-ai'))) {
        void openPdfExplanationForCurrentQuestion({
            silent: true,
            smooth: true,
            ensureVisibleAiPane: false
        }).finally(() => {
            if (preferQuizFocus) focusQuizQuestionPanel({ preserveScroll: true });
        });
    }
    return true;
}

/* --- ADVANCED PDF.JS VIEWER (IN-PANE, NO IFRAME FOR PDF MODE) --- */
let pdfPaneContentMode = 'pdf'; // 'pdf' | 'ai'
let pdfViewerEventBus = null;
let pdfViewerLinkService = null;
let pdfViewerFindController = null;
let pdfViewerInstance = null;
let pdfViewerDoc = null;
let pdfViewerLoadingTask = null;
let pdfViewerFileName = '';
let pdfViewerPagesReady = false;
let pdfViewerScaleMode = 'page-fit';
let pdfViewerOpenToken = 0;
let pdfViewerResizeObserver = null;
let memoraPptxResizeObserver = null;
let pdfZoomSettleToken = 0;
let pdfResponsiveScaleRefreshFrame = 0;
let pdfVisibleRenderRecoveryFrame = 0;
let pdfVisibleRenderRecoveryToken = 0;
let pdfForceIframeMode = false;
let isPdfPaneExpanded = false;
let pdfMiniToolbarBound = false;
let pdfMoreMenuBound = false;
let pdfViewerContainerHandlersBound = false;
let pdfScaleModeRestoreOnCollapse = null;
let pdfEvidenceTargetFileName = '';
let pdfEvidenceTargetPage = null;
/* The page the app last asked the viewer to show, kept until the student scrolls or taps the
 * viewer. While a pane opens, PDF.js briefly reports page 1 (its container starts at zero
 * width), so re-reading currentPageNumber mid-transition sent the viewer to page 1 and back. */
let pdfViewerRequestedPage = null;

function getAdvancedPdfPageToKeep() {
    if (pdfViewerRequestedPage && pdfViewerRequestedPage.fileName === pdfViewerFileName) {
        return pdfViewerRequestedPage.page;
    }
    return Math.max(1, Number(pdfViewerInstance?.currentPageNumber) || 1);
}
let memoraPptxViewerInstance = null;
let memoraPptxViewerFileName = '';
let memoraPptxViewerSlideCount = 0;
let memoraPptxViewerCurrentSlide = 1;
let memoraPptxViewerZoomPercent = 100;
let memoraPptxViewerSearchHighlight = null;
const memoraPptxDocumentCache = new Map();
const memoraPptxDocumentLoads = new Map();
const MEMORA_SOURCE_MIN_FIT_ZOOM = 0.9;
const PDF_MIN_SCALE = 0.27;
const PDF_MAX_SCALE = 5;
const PDF_PAGE_SCROLL_TOP_OFFSET = 14;
let pdfScaleClampInProgress = false;
// The editorial embed can be opened directly from disk, where PDF.js cannot
// fetch a file:// URL. Keep the real PDF source, but let the browser's native
// PDF frame take over if the in-pane renderer cannot open that source.
const PDF_ENABLE_IFRAME_RUNTIME_FALLBACK = IS_EMBED_DEMO;
const PDF_RENDERER_PREF_KEY = 'pdf_renderer_mode';
let pdfRendererMode = 'pdfjs'; // 'pdfjs' | 'iframe'
const PDF_WHEEL_ZOOM_IN_STEP = 1.09;
const PDF_WHEEL_ZOOM_OUT_STEP = 0.91;
const PDF_KEYBTN_ZOOM_IN_STEP = 1.11;
const PDF_KEYBTN_ZOOM_OUT_STEP = 0.9;
let pdfJsSafetyPatchesApplied = false;
const PDF_WHEEL_ZOOM_DEADZONE_PX = 1.25;
const PDF_WHEEL_ZOOM_MAX_DELTA_PX = 96;
const PDF_WHEEL_ZOOM_SENSITIVITY = 0.020392010496;

function isMemoraPptxSourceName(filename = '') {
    return String(filename || '').trim().toLowerCase().endsWith('.pptx');
}

function isMemoraLegacyPptSourceName(filename = '') {
    return String(filename || '').trim().toLowerCase().endsWith('.ppt');
}

function isMemoraPowerPointSourceName(filename = '') {
    return isMemoraPptxSourceName(filename) || isMemoraLegacyPptSourceName(filename);
}

// Source bytes are stored under their filename, so a same-named import replaces what every session
// reads. Each import records one new revision here and consumers compare revisions instead of
// hashing bytes. A file stored before revisions existed adopts one on first use; anything prepared
// before that adoption cannot be verified against it.
const MEMORA_SOURCE_REVISION_STORAGE_PREFIX = 'memora_source_revision_v1::';
const memoraSourceRevisionCache = new Map();
const memoraSourceRevisionReads = new Map();

function getMemoraSourceRevisionStorageKey(name = '') {
    return `${MEMORA_SOURCE_REVISION_STORAGE_PREFIX}${encodeURIComponent(String(name || '').trim())}`;
}

function createMemoraSourceRevisionToken() {
    return typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
        ? crypto.randomUUID()
        : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

function getMemoraSourceRevision(name = '') {
    return memoraSourceRevisionCache.get(String(name || '').trim()) || null;
}

function publishMemoraSourceRevision(descriptor, { replaced = false } = {}) {
    memoraSourceRevisionCache.set(descriptor.name, descriptor);
    if (replaced && typeof invalidatePdfTutorSourceRevision === 'function') {
        try {
            invalidatePdfTutorSourceRevision(descriptor.name);
        } catch (error) {
            console.warn('Source revision invalidation failed:', error);
        }
    }
    return descriptor;
}

function recordMemoraSourceRevision(name = '', { byteLength = 0 } = {}) {
    const sourceName = String(name || '').trim();
    if (!sourceName) return null;
    const descriptor = Object.freeze({
        name: sourceName,
        revision: createMemoraSourceRevisionToken(),
        byteLength: Math.max(0, Number(byteLength) || 0),
        recordedAt: Date.now(),
        adopted: false
    });
    memoraSourceRevisionReads.delete(sourceName);
    publishMemoraSourceRevision(descriptor, { replaced: true });
    void IDB.put('settings', getMemoraSourceRevisionStorageKey(sourceName), descriptor)
        .catch((error) => console.warn(`Source revision for ${sourceName} could not be saved:`, error));
    return descriptor;
}

async function ensureMemoraSourceRevision(name = '', { byteLength = 0 } = {}) {
    const sourceName = String(name || '').trim();
    if (!sourceName) return null;
    const size = Math.max(0, Number(byteLength) || 0);
    const matchesSize = (descriptor) => !size || !descriptor?.byteLength || descriptor.byteLength === size;
    const cached = memoraSourceRevisionCache.get(sourceName);
    if (cached && matchesSize(cached)) return cached;
    if (!cached && memoraSourceRevisionReads.has(sourceName)) return memoraSourceRevisionReads.get(sourceName);
    const read = (async () => {
        let stored = null;
        if (!cached) {
            try {
                stored = await IDB.get('settings', getMemoraSourceRevisionStorageKey(sourceName));
            } catch (_error) { }
        }
        // An import that finished while storage was read owns the revision.
        const current = memoraSourceRevisionCache.get(sourceName);
        if (current !== cached) return current;
        if (stored?.name === sourceName && typeof stored.revision === 'string' && stored.revision && matchesSize(stored)) {
            return publishMemoraSourceRevision(Object.freeze({ ...stored }));
        }
        const adopted = Object.freeze({
            name: sourceName,
            revision: `adopted-${createMemoraSourceRevisionToken()}`,
            byteLength: size,
            recordedAt: Date.now(),
            adopted: true
        });
        publishMemoraSourceRevision(adopted, { replaced: !!(cached || stored) });
        try {
            await IDB.put('settings', getMemoraSourceRevisionStorageKey(sourceName), adopted);
        } catch (error) {
            console.warn(`Source revision for ${sourceName} could not be saved:`, error);
        }
        return adopted;
    })();
    memoraSourceRevisionReads.set(sourceName, read);
    try {
        return await read;
    } finally {
        if (memoraSourceRevisionReads.get(sourceName) === read) memoraSourceRevisionReads.delete(sourceName);
    }
}

function forgetMemoraSourceRevision(name = '') {
    const sourceName = String(name || '').trim();
    if (!sourceName) return;
    memoraSourceRevisionCache.delete(sourceName);
    memoraSourceRevisionReads.delete(sourceName);
    if (typeof invalidatePdfTutorSourceRevision === 'function') {
        try {
            invalidatePdfTutorSourceRevision(sourceName);
        } catch (error) {
            console.warn('Source revision invalidation failed:', error);
        }
    }
    void IDB.del('settings', getMemoraSourceRevisionStorageKey(sourceName))
        .catch((error) => console.warn(`Source revision for ${sourceName} could not be removed:`, error));
    void IDB.del('settings', getMemoraSourceThumbnailStorageKey(sourceName))
        .catch((error) => console.warn(`Source thumbnail for ${sourceName} could not be removed:`, error));
    // Slide vectors are derived from the same bytes and leave with them.
    if (typeof forgetMemoraSlideIndexFile === 'function') forgetMemoraSlideIndexFile(sourceName);
}

/* Source cards (Your sources, the Learn picker) draw slide 1 of the whole deck. Their in-memory
 * cache is keyed by blob URL, which changes on every page load, so each visit streamed every
 * deck into a PDF.js worker again to draw a 160 px image. The image is kept with the source
 * revision it was drawn from; any import or replacement changes the revision. */
function getMemoraSourceThumbnailStorageKey(name = '') {
    return `memora_source_thumbnail_v2::${encodeURIComponent(String(name || '').trim())}`;
}

async function readMemoraSourceThumbnail(name = '') {
    const sourceName = String(name || '').trim();
    if (!sourceName || typeof IDB === 'undefined') return '';
    try {
        const revision = await ensureMemoraSourceRevision(sourceName);
        if (!revision?.revision) return '';
        const stored = await IDB.get('settings', getMemoraSourceThumbnailStorageKey(sourceName));
        return stored?.revision === revision.revision && /^data:image\//.test(String(stored?.image || ''))
            ? stored.image
            : '';
    } catch (_error) {
        return '';
    }
}

async function writeMemoraSourceThumbnail(name = '', image = '') {
    const sourceName = String(name || '').trim();
    if (!sourceName || typeof IDB === 'undefined' || !/^data:image\//.test(String(image || ''))) return false;
    try {
        const revision = await ensureMemoraSourceRevision(sourceName);
        if (!revision?.revision) return false;
        await IDB.put('settings', getMemoraSourceThumbnailStorageKey(sourceName), {
            name: sourceName,
            revision: revision.revision,
            image,
            savedAt: Date.now()
        });
        return true;
    } catch (error) {
        console.warn(`Source thumbnail for ${sourceName} could not be saved:`, error);
        return false;
    }
}

function isMemoraPdfSourceCurrentlyLoaded(filename = '', sourceMap = null) {
    const normalizedName = String(filename || '').trim();
    const sources = sourceMap || (typeof loadedPdfFiles !== 'undefined' ? loadedPdfFiles : null);
    return !!normalizedName
        && !!sources
        && Object.prototype.hasOwnProperty.call(sources, normalizedName);
}

function showMemoraDuplicatePdfSourceWarning(filenameOrNames = '') {
    const names = Array.isArray(filenameOrNames) ? filenameOrNames : [filenameOrNames];
    const normalizedNames = Array.from(new Set(names
        .map((name) => String(name || '').trim())
        .filter(Boolean)));
    if (!normalizedNames.length) return;
    const label = normalizedNames.length === 1
        ? normalizedNames[0]
        : `${normalizedNames.length} source files`;
    showPopupText(
        `Skipped ${label}: a source with this name is already open. Rename the new file before importing it.`,
        null,
        true,
        7000
    );
}

// The files store is shared by every session and keyed by file name. A source whose name
// is stored but not part of this session belongs to another session (or Recently Deleted),
// so it is kept as a separate copy under a free name ("Lecture (2).pdf") instead of
// overwriting that file. Callers handle a name already open in this session first.
// Every caller attaches the claimed name synchronously after the await, so a claim
// holds its name until the next task; an overlapping import of another file with the
// same name then gets its own name instead of overwriting the first one.
const pendingMemoraSourceFileClaims = new Set();
function reserveMemoraSourceFileClaim(name) {
    pendingMemoraSourceFileClaims.add(name);
    setTimeout(() => pendingMemoraSourceFileClaims.delete(name), 0);
}
// A claim stores the file under its free name in the same transaction that read the
// names, so another tab cannot take that name in between; the caller's own save of the
// same file then has nothing left to write. A file whose name could not be checked is
// saved only where no record exists yet: an unknown store is never an empty one.
const memoraSourceFilesStoredByClaim = new WeakSet();
const memoraSourceFilesSavedCreateOnly = new WeakSet();
function pickFreeMemoraSourceFileName(name, taken) {
    const extensionMatch = name.match(/^(.*?)(\.[^.]+)$/);
    let candidate = name;
    for (let suffix = 2; taken.has(candidate); suffix += 1) {
        candidate = extensionMatch ? `${extensionMatch[1]} (${suffix})${extensionMatch[2]}` : `${name} (${suffix})`;
    }
    return candidate;
}
function renameMemoraSourceFile(file, candidate) {
    if (candidate === file.name) return file;
    return new File([file], candidate, {
        type: file.type || getMemoraSourceMimeType(candidate),
        lastModified: file.lastModified
    });
}
async function claimMemoraSourceFileName(file) {
    const name = String(file?.name || '').trim();
    const sessionNames = Array.isArray(sessionManifest?.pdfs) ? sessionManifest.pdfs.map(String) : [];
    if (!name || (sessionNames.includes(name) && !pendingMemoraSourceFileClaims.has(name))) return { file, renamedFrom: '' };
    // Read when the name is chosen: an overlapping claim in this tab may have reserved one meanwhile.
    const localNames = () => new Set([...sessionNames, ...Object.keys(loadedPdfFiles || {}), ...pendingMemoraSourceFileClaims]);
    const claimed = (candidate, stored) => {
        const claimedFile = renameMemoraSourceFile(file, candidate);
        (stored ? memoraSourceFilesStoredByClaim : memoraSourceFilesSavedCreateOnly).add(claimedFile);
        reserveMemoraSourceFileClaim(candidate);
        return { file: claimedFile, renamedFrom: candidate === name ? '' : name };
    };
    if (typeof IDB.runWriteTransaction !== 'function') {
        let storedNames = null;
        try {
            storedNames = new Set((await IDB.getAllKeys('files')).map((key) => String(key)));
        } catch (error) {
            console.warn('Stored source names could not be read; the source is saved only under a new name.', error);
        }
        return claimed(pickFreeMemoraSourceFileName(name, new Set([...(storedNames || []), ...localNames()])), false);
    }
    for (let attempt = 0; attempt < 2; attempt += 1) {
        try {
            const stored = await IDB.runWriteTransaction(['files'], (tx, write, abortWith, setResult) => {
                const store = tx.objectStore('files');
                const read = store.getAllKeys();
                read.onerror = () => abortWith(read.error);
                read.onsuccess = () => {
                    const taken = new Set([...read.result.map((key) => String(key)), ...localNames()]);
                    const candidate = pickFreeMemoraSourceFileName(name, taken);
                    const request = store.add(renameMemoraSourceFile(file, candidate), candidate);
                    request.onerror = () => abortWith(request.error);
                    setResult(candidate);
                };
            }, memoraSourceSaveTimeoutMs(file));
            return claimed(stored, true);
        } catch (error) {
            console.warn('The source name could not be claimed in storage.', error);
        }
    }
    // Live use goes on; the later save refuses to replace any record under this name.
    return claimed(pickFreeMemoraSourceFileName(name, localNames()), false);
}

/* A source the browser could not store lasts only until reload, so the notice stays up with
   Retry instead of a timed toast. */
function showMemoraSourceSaveFailure(file) {
    const name = String(file?.name || '').trim();
    if (!name || typeof document === 'undefined') return;
    const noticeId = 'memora-source-save-failure-notice';
    document.getElementById(noticeId)?.remove();
    const notice = document.createElement('div');
    notice.id = noticeId;
    notice.className = 'quiz-action-toast session-save-failure-notice source-save-failure-notice show';
    notice.setAttribute('role', 'alert');
    const message = document.createElement('span');
    message.className = 'quiz-action-toast-message';
    message.textContent = `${name} is open, but this browser could not save it. It will be missing after a reload unless saving succeeds.`;
    notice.appendChild(message);
    const makeAction = (label, onClick) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'quiz-action-toast-action';
        button.textContent = label;
        button.addEventListener('click', onClick);
        notice.appendChild(button);
        return button;
    };
    const retry = makeAction('Retry', async () => {
        retry.disabled = true;
        try {
            await putMemoraSourceFileRecord(name, file);
            notice.remove();
            showPopupText(`${name} is saved.`, null, false);
        } catch (error) {
            console.warn('Source save retry failed:', error);
            message.textContent = `${name} still could not be saved. Free some space on this device, then try again.`;
            retry.disabled = false;
        }
    });
    makeAction('Dismiss', () => notice.remove());
    document.body.appendChild(notice);
}

function showMemoraSourceRenamedNotice(originalName = '', savedName = '') {
    showPopupText(
        `Saved as ${savedName}: another session already has a file named ${originalName}.`,
        null,
        false,
        9000
    );
}

function getMemoraSourceMimeType(filename = '', fallback = '') {
    if (isMemoraPptxSourceName(filename)) {
        return 'application/vnd.openxmlformats-officedocument.presentationml.presentation';
    }
    if (isMemoraLegacyPptSourceName(filename)) {
        return 'application/vnd.ms-powerpoint';
    }
    return String(fallback || '').trim() || 'application/pdf';
}

async function resolveMemoraPptxBlob(source, filename = '') {
    if (source instanceof Blob) return source;
    const clonedBuffer = source instanceof ArrayBuffer
        ? source.slice(0)
        : (ArrayBuffer.isView(source) ? source.buffer.slice(source.byteOffset, source.byteOffset + source.byteLength) : null);
    if (clonedBuffer) return new Blob([clonedBuffer], { type: getMemoraSourceMimeType(filename) });
    const sourceUrl = String(source?.url || loadedPdfFiles?.[filename] || '').trim();
    if (sourceUrl) {
        const response = await fetch(sourceUrl);
        if (!response.ok) throw new Error(`Could not load ${filename || 'the PowerPoint file'}.`);
        return await response.blob();
    }
    const stored = await IDB.get('files', filename);
    if (!stored) throw new Error(`PowerPoint file not found: ${filename}`);
    return stored instanceof Blob
        ? stored
        : new Blob([stored], { type: getMemoraSourceMimeType(filename) });
}

async function getMemoraPptxDocumentEntry(source, filename = '') {
    const name = String(filename || source?.name || '').trim();
    if (!isMemoraPptxSourceName(name)) throw new Error('A .pptx PowerPoint file is required.');
    // One parsed presentation per unchanged source. The signature is name, byte size and
    // modification time only: an object URL changes between reads of the same stored File.
    const knownSize = source instanceof Blob
        ? Number(source.size) || 0
        : ((source instanceof ArrayBuffer || ArrayBuffer.isView(source)) ? source.byteLength : 0);
    const knownModified = Number(source?.lastModified) || 0;
    const matches = (entry) => !!entry
        && (!knownSize || !entry.size || entry.size === knownSize)
        && (!knownModified || !entry.lastModified || entry.lastModified === knownModified);
    const cached = memoraPptxDocumentCache.get(name);
    if (cached?.presentation && matches(cached)) {
        memoraPptxDocumentCache.delete(name);
        memoraPptxDocumentCache.set(name, cached);
        return cached;
    }
    // Simultaneous readers share one parse instead of each building its own presentation.
    const pending = memoraPptxDocumentLoads.get(name);
    if (pending && matches(pending)) {
        const shared = await pending.promise;
        if (matches(shared)) return shared;
    }
    const load = { size: knownSize, lastModified: knownModified, promise: null };
    load.promise = (async () => {
        const blob = await resolveMemoraPptxBlob(source, name);
        window.MemoraRuntime?.validateStudySourceSize?.(blob.size, name);
        const size = Number(blob.size) || 0;
        const lastModified = knownModified || Number(blob.lastModified) || 0;
        const runtime = await window.MemoraRuntime.ensurePptxRuntimeLoaded();
        const arrayBuffer = await blob.arrayBuffer();
        const files = await runtime.parseZipLazyMedia(arrayBuffer, runtime.RECOMMENDED_ZIP_LIMITS);
        const presentation = runtime.buildPresentation(files, { lazySlides: true });
        const entry = { signature: `${name}::${size}::${lastModified}`, size, lastModified, blob, presentation, runtime };
        // A source forgotten mid-parse (replaced or deleted) must not repopulate the cache.
        // Forgetting removes only that name's load, so another deck's parse still commits.
        if (memoraPptxDocumentLoads.get(name) === load) {
            memoraPptxDocumentCache.delete(name);
            memoraPptxDocumentCache.set(name, entry);
            while (memoraPptxDocumentCache.size > 2) {
                const oldestName = memoraPptxDocumentCache.keys().next().value;
                if (!oldestName) break;
                memoraPptxDocumentCache.delete(oldestName);
            }
        }
        return entry;
    })();
    memoraPptxDocumentLoads.set(name, load);
    try {
        return await load.promise;
    } finally {
        if (memoraPptxDocumentLoads.get(name) === load) memoraPptxDocumentLoads.delete(name);
    }
}

async function getMemoraPptxTextPages(filename = '', source = null) {
    const name = String(filename || source?.name || '').trim();
    const entry = await getMemoraPptxDocumentEntry(source, name);
    entry.runtime.materializeAllSlideNodes(entry.presentation);
    // Shapes hidden for a slide's animation end state still carry lecture text.
    const textIndex = entry.runtime.buildTextIndex({
        ...entry.presentation,
        slides: entry.presentation.slides.map((slide) => (slide.memoraAllNodes ? { ...slide, nodes: slide.memoraAllNodes } : slide))
    });
    const textBySlide = Array.from({ length: entry.presentation.slides.length }, () => []);
    const seenBySlide = textBySlide.map(() => new Set());
    textIndex.forEach((item) => {
        const slideIndex = Math.max(0, Math.floor(Number(item?.slideIndex) || 0));
        const text = String(item?.text || '').replace(/\s+/g, ' ').trim();
        if (!text || !textBySlide[slideIndex] || seenBySlide[slideIndex].has(text)) return;
        seenBySlide[slideIndex].add(text);
        textBySlide[slideIndex].push(text);
    });
    return textBySlide.map((parts, index) => ({
        id: `${name}::${index + 1}`,
        page: index + 1,
        text: parts.join(' ').trim() || '[No selectable text was detected on this slide.]'
    }));
}

async function getMemoraPptxSlideCount(source, filename = '') {
    const name = String(filename || source?.name || '').trim();
    const entry = await getMemoraPptxDocumentEntry(source, name);
    return Math.max(1, Number(entry.presentation?.slides?.length) || 1);
}

async function renderMemoraPptxSlidePreview(filename = '', pageNumber = 1) {
    const name = String(filename || '').trim();
    if (!isMemoraPptxSourceName(name) || typeof document === 'undefined') return null;
    const page = Math.max(1, Number(pageNumber) || 1);
    const entry = await getMemoraPptxDocumentEntry(null, name);
    const slide = entry.presentation?.slides?.[page - 1];
    if (!slide) return null;
    // No mediaUrlCache: the renderer revokes its media blob URLs on dispose only when it owns
    // the cache. A caller-supplied map left every slide image in the blob store for the session.
    const handle = entry.runtime.renderSlide(entry.presentation, slide, {
        pdfjs: false
    });
    await handle.ready;
    return {
        element: handle.element,
        width: Math.max(1, Number(entry.presentation?.width) || 16),
        height: Math.max(1, Number(entry.presentation?.height) || 9),
        dispose: () => handle.dispose?.()
    };
}

function forgetMemoraPptxSource(filename = '') {
    const name = String(filename || '').trim();
    if (!name) return;
    memoraPptxDocumentCache.delete(name);
    // A parse still in flight for the old file must not land in the cache.
    memoraPptxDocumentLoads.delete(name);
}

window.isMemoraPptxSourceName = isMemoraPptxSourceName;
window.isMemoraLegacyPptSourceName = isMemoraLegacyPptSourceName;
window.isMemoraPowerPointSourceName = isMemoraPowerPointSourceName;
window.isMemoraPdfSourceCurrentlyLoaded = isMemoraPdfSourceCurrentlyLoaded;
window.showMemoraDuplicatePdfSourceWarning = showMemoraDuplicatePdfSourceWarning;
window.claimMemoraSourceFileName = claimMemoraSourceFileName;
window.showMemoraSourceRenamedNotice = showMemoraSourceRenamedNotice;
window.showMemoraSourceSaveFailure = showMemoraSourceSaveFailure;
window.getMemoraSourceMimeType = getMemoraSourceMimeType;
window.getMemoraPptxTextPages = getMemoraPptxTextPages;
window.getMemoraPptxSlideCount = getMemoraPptxSlideCount;
window.renderMemoraPptxSlidePreview = renderMemoraPptxSlidePreview;
window.renderMemoraPptxSlideImage = renderMemoraPptxSlideImage;
window.openMemoraPptxSharedSlideRasters = openMemoraPptxSharedSlideRasters;
window.rasterizeMemoraPptxSlideElement = rasterizeMemoraPptxSlideElement;
window.forgetMemoraPptxSource = forgetMemoraPptxSource;
window.setMemoraPptxSlide = setMemoraPptxSlide;

function readMemoraPptxBlobAsDataUrl(blob, abortSignal = null) {
    return new Promise((resolve, reject) => {
        if (abortSignal?.aborted) {
            reject(new DOMException('Rasterization aborted.', 'AbortError'));
            return;
        }
        const reader = new FileReader();
        const cleanup = () => abortSignal?.removeEventListener?.('abort', onAbort);
        const onAbort = () => {
            try { reader.abort(); } catch (_) { }
            cleanup();
            reject(new DOMException('Rasterization aborted.', 'AbortError'));
        };
        reader.onload = () => {
            cleanup();
            resolve(String(reader.result || ''));
        };
        reader.onerror = () => {
            cleanup();
            reject(reader.error || new Error('Could not inline slide image.'));
        };
        reader.onabort = () => {
            cleanup();
            reject(new DOMException('Rasterization aborted.', 'AbortError'));
        };
        abortSignal?.addEventListener?.('abort', onAbort, { once: true });
        reader.readAsDataURL(blob);
    });
}

/* A slide picture inlined into the rasterizing SVG is decoded at its full size even when the
   output is a 360px thumbnail, and Chrome keeps that decode after the render: a 36-slide deck
   rendered twice held ~190 MiB more than the same renders with the SVG step bypassed. Inlining a
   copy no larger than the raster needs keeps that cost proportional to the output. */
async function downscaleMemoraPptxEmbeddedBlob(blob, maxDimension = 0, abortSignal = null) {
    const limit = Math.floor(Number(maxDimension) || 0);
    if (!limit || !(blob instanceof Blob) || typeof createImageBitmap !== 'function' || typeof document === 'undefined') return blob;
    let bitmap = null;
    try {
        // SVG and other formats createImageBitmap cannot decode keep their original bytes.
        bitmap = await createImageBitmap(blob);
        if (abortSignal?.aborted) throw new DOMException('Rasterization aborted.', 'AbortError');
        const scale = limit / Math.max(1, bitmap.width, bitmap.height);
        if (!(scale < 1)) return blob;
        const canvas = document.createElement('canvas');
        canvas.width = Math.max(1, Math.round(bitmap.width * scale));
        canvas.height = Math.max(1, Math.round(bitmap.height * scale));
        const context = canvas.getContext('2d');
        if (!context) return blob;
        context.imageSmoothingQuality = 'high';
        context.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
        // WebP keeps transparency, which a JPEG re-encode would flatten.
        const smaller = await new Promise((resolve) => canvas.toBlob(resolve, 'image/webp', 0.9));
        canvas.width = 1;
        canvas.height = 1;
        return smaller || blob;
    } catch (error) {
        if (error?.name === 'AbortError' || abortSignal?.aborted) throw error;
        return blob;
    } finally {
        try { bitmap?.close?.(); } catch (_error) { }
    }
}

async function getMemoraPptxEmbeddedUrl(url, abortSignal = null, cache = null, maxDimension = 0) {
    const normalizedUrl = String(url || '').trim();
    if (!normalizedUrl || normalizedUrl.startsWith('data:')) return normalizedUrl;
    if (cache?.has(normalizedUrl)) return cache.get(normalizedUrl);

    const preparation = (async () => {
        if (abortSignal?.aborted) throw new DOMException('Rasterization aborted.', 'AbortError');
        const response = await fetch(normalizedUrl, { signal: abortSignal || undefined });
        if (!response.ok) throw new Error(`Could not inline slide media (${response.status}).`);
        const blob = await downscaleMemoraPptxEmbeddedBlob(await response.blob(), maxDimension, abortSignal);
        return await readMemoraPptxBlobAsDataUrl(blob, abortSignal);
    })();
    cache?.set(normalizedUrl, preparation);
    return preparation;
}

async function prepareMemoraPptxSlideClone(slideElement, abortSignal = null, {
    omitRasterResources = false,
    maxImageDimension = 0
} = {}) {
    const clone = slideElement.cloneNode(true);
    clone.setAttribute('xmlns', 'http://www.w3.org/1999/xhtml');
    const embeddedUrlCache = new Map();
    const sourceElements = [slideElement, ...slideElement.querySelectorAll('*')];
    const clonedElements = [clone, ...clone.querySelectorAll('*')];
    const isSafeEmbeddedUrl = (url) => {
        const normalizedUrl = String(url || '').trim().toLowerCase();
        return !normalizedUrl
            || normalizedUrl.startsWith('data:')
            || normalizedUrl.startsWith('#')
            || normalizedUrl === 'about:blank';
    };
    const containsUnsafeCssUrl = (value) => Array.from(String(value || '').matchAll(/url\((['"]?)([^'")]+)\1\)/gi))
        .some((match) => !isSafeEmbeddedUrl(match[2]));

    const sanitizeCloneResources = () => {
        const elements = [clone, ...clone.querySelectorAll('*')];
        elements.forEach((element) => {
            const tagName = String(element?.tagName || '').toLowerCase();
            if (['audio', 'embed', 'iframe', 'object', 'source', 'video'].includes(tagName)) {
                element.remove();
                return;
            }
            if (omitRasterResources && ['canvas', 'feimage', 'image', 'img'].includes(tagName)) {
                element.remove();
                return;
            }
            if (tagName === 'img') {
                const sourceUrl = element.getAttribute('src') || '';
                if (!isSafeEmbeddedUrl(sourceUrl)) {
                    element.remove();
                    return;
                }
                element.removeAttribute('srcset');
            }
            if (['feimage', 'image', 'use'].includes(tagName)) {
                const sourceUrl = element.getAttribute('href') || element.getAttribute('xlink:href') || '';
                if (!isSafeEmbeddedUrl(sourceUrl)) {
                    if (tagName === 'use') {
                        element.removeAttribute('href');
                        element.removeAttribute('xlink:href');
                    } else {
                        element.remove();
                        return;
                    }
                }
            }
            const style = element?.style;
            if (!style) return;
            for (let index = style.length - 1; index >= 0; index -= 1) {
                const propertyName = style.item(index);
                const propertyValue = style.getPropertyValue(propertyName);
                if ((omitRasterResources && propertyValue.includes('url(')) || containsUnsafeCssUrl(propertyValue)) {
                    style.removeProperty(propertyName);
                }
            }
        });
    };

    if (omitRasterResources) {
        sanitizeCloneResources();
        if (abortSignal?.aborted) throw new DOMException('Rasterization aborted.', 'AbortError');
        return clone;
    }

    sourceElements.forEach((sourceElement, index) => {
        if (sourceElement.tagName !== 'CANVAS') return;
        const sourceCanvas = sourceElement;
        const clonedCanvas = clonedElements[index];
        if (!clonedCanvas) return;
        try {
            const image = document.createElement('img');
            Array.from(clonedCanvas.attributes || []).forEach((attribute) => {
                if (!['width', 'height'].includes(attribute.name)) image.setAttribute(attribute.name, attribute.value);
            });
            image.width = Math.max(1, Number(sourceCanvas.width) || sourceCanvas.clientWidth || 1);
            image.height = Math.max(1, Number(sourceCanvas.height) || sourceCanvas.clientHeight || 1);
            image.src = sourceCanvas.toDataURL('image/png');
            clonedCanvas.replaceWith(image);
        } catch (_) {
            clonedCanvas.remove();
        }
    });

    await Promise.all(sourceElements.map(async (sourceElement, index) => {
        if (sourceElement.tagName !== 'IMG') return;
        const sourceImage = sourceElement;
        const clonedImage = clonedElements[index];
        const sourceUrl = sourceImage.currentSrc || sourceImage.src || '';
        if (!clonedImage || !sourceUrl || sourceUrl.startsWith('data:')) return;
        try {
            clonedImage.src = await getMemoraPptxEmbeddedUrl(sourceUrl, abortSignal, embeddedUrlCache, maxImageDimension);
        } catch (error) {
            if (abortSignal?.aborted) throw error;
            clonedImage.remove();
        }
    }));

    await Promise.all(sourceElements.map(async (sourceElement, index) => {
        if (String(sourceElement.tagName || '').toLowerCase() !== 'image') return;
        const sourceImage = sourceElement;
        const clonedImage = clonedElements[index];
        const sourceUrl = sourceImage.getAttribute('href') || sourceImage.getAttribute('xlink:href') || '';
        if (!clonedImage || !sourceUrl || sourceUrl.startsWith('data:')) return;
        try {
            const embeddedUrl = await getMemoraPptxEmbeddedUrl(sourceUrl, abortSignal, embeddedUrlCache, maxImageDimension);
            clonedImage.setAttribute('href', embeddedUrl);
            clonedImage.removeAttribute('xlink:href');
        } catch (error) {
            if (abortSignal?.aborted) throw error;
            clonedImage.remove();
        }
    }));

    await Promise.all(sourceElements.map(async (sourceElement, index) => {
        const clonedElement = clonedElements[index];
        const backgroundImage = String(sourceElement?.style?.backgroundImage || '');
        if (!clonedElement || !backgroundImage || !backgroundImage.includes('url(')) return;
        const matches = Array.from(backgroundImage.matchAll(/url\((['"]?)([^'")]+)\1\)/g));
        let embeddedBackground = backgroundImage;
        let couldEmbedEveryBackground = true;
        for (const match of matches) {
            const sourceUrl = String(match[2] || '').trim();
            if (!sourceUrl || sourceUrl.startsWith('data:')) continue;
            try {
                const embeddedUrl = await getMemoraPptxEmbeddedUrl(sourceUrl, abortSignal, embeddedUrlCache, maxImageDimension);
                embeddedBackground = embeddedBackground.replace(match[0], `url("${embeddedUrl}")`);
            } catch (error) {
                if (abortSignal?.aborted) throw error;
                couldEmbedEveryBackground = false;
                break;
            }
        }
        clonedElement.style.backgroundImage = couldEmbedEveryBackground ? embeddedBackground : 'none';
    }));

    sanitizeCloneResources();
    if (abortSignal?.aborted) throw new DOMException('Rasterization aborted.', 'AbortError');
    return clone;
}

async function rasterizeMemoraPptxSlideElement(slideElement, {
    sourceWidth,
    sourceHeight,
    targetWidth,
    targetHeight,
    abortSignal = null,
    omitRasterResources = false
} = {}) {
    const sWidth = Math.max(1, Math.floor(Number(sourceWidth) || 960));
    const sHeight = Math.max(1, Math.floor(Number(sourceHeight) || 540));
    const tWidth = Math.max(1, Math.floor(Number(targetWidth) || sWidth));
    const tHeight = Math.max(1, Math.floor(Number(targetHeight) || sHeight));

    const clonedSlide = await prepareMemoraPptxSlideClone(slideElement, abortSignal, {
        omitRasterResources,
        // Headroom for cropped or zoomed pictures, which show part of the image enlarged.
        maxImageDimension: Math.ceil(Math.max(tWidth, tHeight) * 1.5)
    });
    clonedSlide.style.margin = '0';
    clonedSlide.style.width = `${sWidth}px`;
    clonedSlide.style.height = `${sHeight}px`;
    // An SVG image gets none of the page's CSS, so text that overflows its box would paint scrollbars.
    const scrollbarGuard = document.createElementNS('http://www.w3.org/1999/xhtml', 'style');
    scrollbarGuard.textContent = '*{scrollbar-width:none!important}*::-webkit-scrollbar{display:none!important}';
    clonedSlide.insertBefore(scrollbarGuard, clonedSlide.firstChild);
    const serializedSlide = new XMLSerializer().serializeToString(clonedSlide);
    const svgMarkup = `<svg xmlns="http://www.w3.org/2000/svg" width="${tWidth}" height="${tHeight}" viewBox="0 0 ${sWidth} ${sHeight}"><foreignObject x="0" y="0" width="${sWidth}" height="${sHeight}">${serializedSlide}</foreignObject></svg>`;
    const svgUrl = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svgMarkup)}`;
    const image = new Image();

    try {
        await new Promise((resolve, reject) => {
            const cleanup = () => abortSignal?.removeEventListener?.('abort', onAbort);
            const onAbort = () => {
                image.src = '';
                cleanup();
                reject(new DOMException('Rasterization aborted.', 'AbortError'));
            };
            image.onload = () => {
                cleanup();
                resolve();
            };
            image.onerror = () => {
                cleanup();
                reject(new Error('Could not rasterize a rendered PowerPoint slide.'));
            };
            abortSignal?.addEventListener?.('abort', onAbort, { once: true });
            image.src = svgUrl;
        });
        if (abortSignal?.aborted) throw new DOMException('Rasterization aborted.', 'AbortError');

        const canvas = document.createElement('canvas');
        canvas.width = tWidth;
        canvas.height = tHeight;
        const context = canvas.getContext('2d', { willReadFrequently: true });
        if (!context) throw new Error('Cannot rasterize PowerPoint because canvas context is unavailable.');
        context.fillStyle = '#ffffff';
        context.fillRect(0, 0, tWidth, tHeight);
        context.drawImage(image, 0, 0, tWidth, tHeight);
        return canvas;
    } finally {
        image.src = '';
    }
}

// A slide rasterized through the SVG foreignObject path leaves native memory behind for the life
// of the tab, so each slide is rasterized once per parsed deck, at one width that serves the
// viewer page, its thumbnail strip, the Tutor, quick start, the picker and the PDF converter.
// Smaller requests are scaled down from that JPEG, an ordinary image decode the renderer does
// release. The rasters live on the document entry, so a replaced or forgotten deck drops them
// with its parse. The base scale matches the converter, so an upload reuses the viewer slides.
const MEMORA_PPTX_SLIDE_RASTER_SCALE = 1.2;
const MEMORA_PPTX_SLIDE_RASTER_QUALITY = 0.86;
const MEMORA_PPTX_SLIDE_RASTER_CACHE_CHARS = 48 * 1024 * 1024;

function getMemoraPptxSlideRaster(entry, page, requestedWidth) {
    if (!entry.slideRasters) entry.slideRasters = { pages: new Map(), chars: 0 };
    const cache = entry.slideRasters;
    const cached = cache.pages.get(page);
    if (cached && cached.width >= requestedWidth) {
        cache.pages.delete(page);
        cache.pages.set(page, cached);
        return cached.promise;
    }
    const sourceWidth = Math.max(1, Number(entry.presentation?.width) || 960);
    const sourceHeight = Math.max(1, Number(entry.presentation?.height) || 540);
    const width = Math.min(Math.round(sourceWidth * 2.5), Math.max(requestedWidth, Math.round(sourceWidth * MEMORA_PPTX_SLIDE_RASTER_SCALE)));
    const height = Math.max(1, Math.round(sourceHeight * (width / sourceWidth)));
    const record = { width, chars: 0, promise: null };
    const forget = () => {
        if (cache.pages.get(page) !== record) return;
        cache.pages.delete(page);
        cache.chars -= record.chars;
    };
    // No abort signal: a raster a closed view no longer wants still finishes and is kept, since
    // abandoning it would not return the memory it already cost.
    record.promise = (async () => {
        let handle = null;
        try {
            const slide = entry.presentation?.slides?.[page - 1];
            if (!slide) return null;
            // No mediaUrlCache: the renderer revokes its media blob URLs on dispose only when it owns
            // the cache. A caller-supplied map left every slide image in the blob store for the session.
            handle = entry.runtime.renderSlide(entry.presentation, slide, {
                pdfjs: false
            });
            await handle.ready;
            // Autofit re-measures after fonts load and on the next frames; let that finish so the
            // image shows fitted text. The timeout covers hidden tabs, where frames never fire.
            await Promise.race([
                Promise.resolve(document.fonts?.ready).then(() => new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)))),
                new Promise((resolve) => setTimeout(resolve, 250))
            ]);
            const canvas = await rasterizeMemoraPptxSlideElement(handle.element, {
                sourceWidth,
                sourceHeight,
                targetWidth: width,
                targetHeight: height
            });
            if (!canvas) return null;
            const url = canvas.toDataURL('image/jpeg', MEMORA_PPTX_SLIDE_RASTER_QUALITY);
            canvas.width = 1;
            canvas.height = 1;
            return { url, width, height };
        } catch (error) {
            console.warn(`PowerPoint slide ${page} rasterization failed:`, error);
            return null;
        } finally {
            if (handle && typeof handle.dispose === 'function') {
                try { handle.dispose(); } catch (_) { }
            }
        }
    })();
    // A smaller raster of this slide is replaced; callers already awaiting it still get it.
    if (cached) {
        cache.pages.delete(page);
        cache.chars -= cached.chars;
    }
    cache.pages.set(page, record);
    record.promise.then((raster) => {
        if (!raster) {
            forget();
            return;
        }
        if (cache.pages.get(page) !== record) return;
        record.chars = raster.url.length;
        cache.chars += record.chars;
        for (const [oldestPage, oldest] of cache.pages) {
            if (cache.chars <= MEMORA_PPTX_SLIDE_RASTER_CACHE_CHARS || oldest === record) break;
            cache.pages.delete(oldestPage);
            cache.chars -= oldest.chars;
        }
    });
    return record.promise;
}

async function scaleMemoraPptxSlideRaster(raster, width, height, format, quality) {
    if (raster.width === width && raster.height === height && format === 'jpeg'
        && Math.abs(quality - MEMORA_PPTX_SLIDE_RASTER_QUALITY) <= 0.03) {
        return raster.url;
    }
    const image = new Image();
    image.decoding = 'async';
    await new Promise((resolve, reject) => {
        image.onload = resolve;
        image.onerror = () => reject(new Error('Could not decode a cached PowerPoint slide.'));
        image.src = raster.url;
    });
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    try {
        const context = canvas.getContext('2d');
        if (!context) return '';
        context.imageSmoothingQuality = 'high';
        context.drawImage(image, 0, 0, width, height);
        return format === 'png'
            ? canvas.toDataURL('image/png')
            : canvas.toDataURL('image/jpeg', quality);
    } finally {
        canvas.width = 1;
        canvas.height = 1;
        image.src = '';
    }
}

// The PowerPoint-to-PDF converter takes its slides from here, so a slide the viewer, Tutor or a
// quiz already rasterized is not rasterized again for an upload, and the other way round.
async function openMemoraPptxSharedSlideRasters(source, filename = '') {
    const entry = await getMemoraPptxDocumentEntry(source, filename);
    return {
        totalSlides: Math.max(0, Number(entry.presentation?.slides?.length) || 0),
        width: Number(entry.presentation?.width) || 0,
        height: Number(entry.presentation?.height) || 0,
        raster: (page, minWidth) => getMemoraPptxSlideRaster(entry, page, minWidth),
        scale: scaleMemoraPptxSlideRaster
    };
}

async function renderMemoraPptxSlideImage(filename = '', pageNumber = 1, options = {}) {
    const name = String(filename || '').trim();
    if (!isMemoraPptxSourceName(name) || typeof document === 'undefined') return '';
    const page = Math.max(1, Number(pageNumber) || 1);
    const maxWidth = Math.max(160, Math.min(3200, Number(options.maxWidth) || 1400));
    const quality = Math.max(0.5, Math.min(0.95, Number(options.quality) || 0.74));
    const format = String(options.format || '').toLowerCase() === 'png' ? 'png' : 'jpeg';
    const abortSignal = options.abortSignal || null;

    try {
        const entry = await getMemoraPptxDocumentEntry(null, name);
        if (!entry.presentation?.slides?.[page - 1]) return '';
        if (abortSignal?.aborted) return '';

        const sourceWidth = Math.max(1, Number(entry.presentation?.width) || 960);
        const sourceHeight = Math.max(1, Number(entry.presentation?.height) || 540);
        // A slide wider than maxWidth is scaled down to it; only narrower slides are scaled up.
        const scale = Math.min(2.5, Math.max(0.05, maxWidth / sourceWidth));
        const targetWidth = Math.max(1, Math.round(sourceWidth * scale));
        const targetHeight = Math.max(1, Math.round(sourceHeight * scale));

        const raster = await getMemoraPptxSlideRaster(entry, page, targetWidth);
        if (!raster || abortSignal?.aborted) return '';
        return await scaleMemoraPptxSlideRaster(raster, targetWidth, targetHeight, format, quality);
    } catch (error) {
        console.warn(`PowerPoint slide ${page} rasterization failed:`, error);
        return '';
    }
}

function getPdfWheelZoomFactor(deltaY = 0, deltaMode = 0) {
    let delta = Number(deltaY) || 0;
    if (deltaMode === 1) delta *= 16; // line-based wheel units
    else if (deltaMode === 2) delta *= 120; // page-based wheel units
    if (!Number.isFinite(delta) || Math.abs(delta) < PDF_WHEEL_ZOOM_DEADZONE_PX) return 1;

    delta = Math.max(-PDF_WHEEL_ZOOM_MAX_DELTA_PX, Math.min(PDF_WHEEL_ZOOM_MAX_DELTA_PX, delta));
    const factor = Math.exp(-delta * PDF_WHEEL_ZOOM_SENSITIVITY);
    // Keep per-event zoom bounded to avoid runaway feel on high-frequency pinch streams.
    return Math.max(0.94, Math.min(1.06, factor));
}

function isPdfTutorPdfViewActive() {
    const workArea = els?.workArea || $('work-area');
    return !!(
        workArea?.classList.contains('pdf-tutor-view')
        && !workArea.classList.contains('pdf-tutor-study-guide-duo')
    );
}

function hasOpenMemoraPdfFocusAskCardForZoom() {
    if (typeof window.hasOpenMemoraPdfFocusAskCard !== 'function') return false;
    try {
        return !!window.hasOpenMemoraPdfFocusAskCard();
    } catch (_err) {
        return false;
    }
}

function notifyTutorPdfZoomIfActive(options = {}) {
    if (!isPdfTutorPdfViewActive() && !hasOpenMemoraPdfFocusAskCardForZoom()) return false;
    if (typeof window.notifyPdfTutorUserPdfZoom !== 'function') return false;
    try {
        window.notifyPdfTutorUserPdfZoom(options);
        return true;
    } catch (_err) {
        return false;
    }
}

async function applyPdfEvidenceDefaultScale(pageNumber = null) {
    return setAdvancedPdfScaleMode('page-fit', { preservePageNumber: pageNumber });
}

async function syncPdfScaleForCurrentLayoutModeSwitch() {
    if (isPdfPaneExpanded || isPdfPaneInAiMode()) return false;

    const workArea = els?.workArea || $('work-area');
    const isDuoOrTrioPdfLayout = !!(
        (
            workArea?.classList?.contains('split-view')
            && !workArea?.classList?.contains('split-view-ai')
        )
        || workArea?.classList?.contains('trio-view')
    );
    if (!isDuoOrTrioPdfLayout) return false;

    await waitForPdfPaneLayoutReady(1, 12);
    if (memoraPptxViewerInstance) {
        return fitMemoraPptxViewerToPage();
    }
    if (!pdfViewerInstance || !pdfViewerDoc || !pdfViewerFileName) return false;

    const targetPage = getAdvancedPdfPageToKeep();
    await applyPdfEvidenceDefaultScale(targetPage, {
        filename: pdfViewerFileName,
        docOverride: pdfViewerDoc
    });

    setAdvancedPdfPage(targetPage, false);
    updatePdfMiniToolbarState();
    return true;
}

function normalizePdfStateTitleToName(titleText) {
    return String(titleText || '')
        .replace(/\s*\(Page\s+\d+\)\s*$/i, '')
        .trim();
}

function canUseAdvancedPdfViewer() {
    return typeof pdfjsLib !== 'undefined'
        && typeof pdfjsViewer !== 'undefined'
        && typeof pdfjsViewer.PDFViewer === 'function';
}

// Text layers measure every glyph run with canvas measureText, which held the main thread for
// 350-550 ms per page while the slide pane slid in or out, freezing the pane and rail motion.
// Each text-layer pass now waits for the pane layout to settle and runs one at a time with a
// frame between, so motion keeps its frames. Canvases still paint at once; only the selectable
// text arrives a moment later.
const PDF_TEXT_LAYER_MAX_DEFER_MS = 2000;
const PDF_TEXT_LAYER_TURN_CAP_MS = 1500;
let pdfJsTextLayerRenderChain = Promise.resolve();

function waitForPdfTextLayerTurn() {
    const startedAt = performance.now();
    return new Promise((resolve) => {
        const attempt = () => {
            const waited = performance.now() - startedAt;
            if (waited < PDF_TEXT_LAYER_MAX_DEFER_MS
                && typeof window.whenPaneLayoutSettled === 'function'
                && window.isPaneLayoutTransitioning?.()) {
                window.whenPaneLayoutSettled(attempt);
                return;
            }
            // rAF stalls in hidden tabs, so a timer backs it up.
            let done = false;
            const finish = () => {
                if (done) return;
                done = true;
                setTimeout(resolve, 0);
            };
            requestAnimationFrame(finish);
            setTimeout(finish, 100);
        };
        attempt();
    });
}

function patchPdfJsTextLayerDeferral() {
    const builderProto = pdfjsViewer?.TextLayerBuilder?.prototype;
    if (!builderProto || typeof builderProto.render !== 'function' || typeof builderProto.cancel !== 'function') return;
    if (builderProto.__memoraDeferredRenderWrapped) return;

    const originalRender = builderProto.render;
    const originalCancel = builderProto.cancel;
    const cancelEpochs = new WeakMap();
    builderProto.cancel = function (...args) {
        cancelEpochs.set(this, (cancelEpochs.get(this) || 0) + 1);
        return originalCancel.apply(this, args);
    };
    builderProto.render = function (...args) {
        const builder = this;
        const epoch = cancelEpochs.get(builder) || 0;
        const turn = pdfJsTextLayerRenderChain.then(waitForPdfTextLayerTurn).then(() => {
            // A page reset or zoom cancelled this pass while it waited; its replacement renders instead.
            if ((cancelEpochs.get(builder) || 0) !== epoch) return undefined;
            return originalRender.apply(builder, args);
        });
        // A stuck text stream must not hold every later page's text layer hostage.
        pdfJsTextLayerRenderChain = Promise.race([
            turn,
            new Promise((resolve) => setTimeout(resolve, PDF_TEXT_LAYER_TURN_CAP_MS))
        ]).catch(() => { });
        return turn;
    };
    Object.defineProperty(builderProto, '__memoraDeferredRenderWrapped', {
        value: true,
        configurable: false,
        enumerable: false,
        writable: false
    });
}

function applyPdfJsSafetyPatches() {
    if (pdfJsSafetyPatchesApplied) return;
    pdfJsSafetyPatchesApplied = true;

    try {
        patchPdfJsViewerCanvasReadbackContexts();
    } catch (_err) { }

    try {
        patchPdfJsTextLayerDeferral();
    } catch (_err) { }

    try {
        const managerProto = pdfjsViewer?.AnnotationEditorUIManager?.prototype;
        if (!managerProto || typeof managerProto.destroy !== 'function') return;
        if (managerProto.__quizSafeDestroyWrapped) return;

        const originalDestroy = managerProto.destroy;
        managerProto.destroy = function (...args) {
            try {
                return originalDestroy.apply(this, args);
            } catch (err) {
                console.warn('PDF.js guard: AnnotationEditorUIManager.destroy failed, suppressing.', err);
                return undefined;
            }
        };
        Object.defineProperty(managerProto, '__quizSafeDestroyWrapped', {
            value: true,
            configurable: false,
            enumerable: false,
            writable: false
        });
        console.info('PDF.js safety patch active: guarded AnnotationEditorUIManager.destroy');
    } catch (_err) { }
}

function loadPdfRendererPreference() {
    try {
        const raw = String(localStorage.getItem(PDF_RENDERER_PREF_KEY) || '').trim().toLowerCase();
        if (raw === 'iframe' || raw === 'pdfjs') {
            pdfRendererMode = raw;
        }
    } catch (_err) { }
}

function savePdfRendererPreference() {
    try {
        localStorage.setItem(PDF_RENDERER_PREF_KEY, pdfRendererMode);
    } catch (_err) { }
}

function updatePdfRendererToggleButton() {
    if (!els?.pdfRendererToggleBtn) return;
    const usingIframe = pdfRendererMode === 'iframe';
    const canUsePdfJs = canUseAdvancedPdfViewer();
    const rendererKey = els.pdfRendererToggleBtn.querySelector('.pdf-more-menu-key');
    if (rendererKey) rendererKey.textContent = usingIframe ? 'IF' : 'JS';
    else els.pdfRendererToggleBtn.innerText = usingIframe ? 'IF' : 'JS';
    els.pdfRendererToggleBtn.classList.toggle('active', usingIframe);
    els.pdfRendererToggleBtn.title = usingIframe
        ? 'Switch to PDF.js renderer'
        : 'Switch to iframe renderer';
    els.pdfRendererToggleBtn.setAttribute('aria-label', els.pdfRendererToggleBtn.title);
    els.pdfRendererToggleBtn.disabled = !canUsePdfJs;
}

function shouldUseIframeRenderer() {
    if (pdfRendererMode === 'iframe') return true;
    if (PDF_ENABLE_IFRAME_RUNTIME_FALLBACK && pdfForceIframeMode) return true;
    return false;
}

function setPdfRendererMode(mode = 'pdfjs', { reopenCurrent = true, smooth = false } = {}) {
    const nextMode = String(mode || '').toLowerCase() === 'iframe' ? 'iframe' : 'pdfjs';
    if (pdfRendererMode === nextMode && !reopenCurrent) {
        updatePdfRendererToggleButton();
        return false;
    }

    pdfRendererMode = nextMode;
    pdfViewerOpenToken += 1; // Invalidate in-flight open requests when renderer is switched.
    savePdfRendererPreference();
    updatePdfRendererToggleButton();

    if (isPdfPaneExpanded) togglePdfPaneExpanded(false);
    setPdfPaneMode('pdf');

    if (!reopenCurrent) return true;
    const restorePdf = getPreferredPdfForRestore();
    if (!restorePdf || !loadedPdfFiles[restorePdf]) return true;

    return loadPdfInPane(restorePdf, {
        page: Math.max(1, Number(lastPdfState?.page) || 1),
        searchTerm: String(lastPdfState?.searchTerm || '').trim(),
        smooth,
        ensureViewer: true,
        maintainScale: true
    });
}

function syncPdfPaneHeaderHeight() {
    const pane = els?.pdfPane || $('pdf-pane');
    const header = $('pdf-header');
    if (!pane || !header) return;
    const h = Math.max(26, Math.round(header.getBoundingClientRect().height || header.offsetHeight || 0));
    // Unchanged heights are not rewritten, so a repeat sync does not invalidate the pane's layout.
    if (pane.style.getPropertyValue('--pdf-header-height') !== `${h}px`) pane.style.setProperty('--pdf-header-height', `${h}px`);
}

// A pane-mode switch measures the header on the next frame. Measuring at once forced a full
// workspace layout in the middle of the click that opens the pane (~40ms per Ask/Notes/Slide open).
let pdfPaneHeaderHeightFrame = 0;
function schedulePdfPaneHeaderHeightSync() {
    if (pdfPaneHeaderHeightFrame) return;
    pdfPaneHeaderHeightFrame = requestAnimationFrame(() => {
        pdfPaneHeaderHeightFrame = 0;
        syncPdfPaneHeaderHeight();
    });
}

function setPdfMoreMenuOpen(force) {
    const menu = els?.pdfMoreMenu || $('pdf-more-menu');
    const btn = els?.pdfMoreBtn || $('pdf-more-btn');
    const wrap = els?.pdfMoreWrap || $('pdf-more-wrap');
    if (!menu) return false;
    const next = force === undefined ? !menu.classList.contains('active') : !!force;
    menu.classList.toggle('active', next);
    if (wrap) wrap.classList.toggle('menu-open', next);
    if (btn) btn.classList.toggle('active', next);
    if (btn) btn.setAttribute('aria-expanded', next ? 'true' : 'false');
    menu.setAttribute('aria-hidden', next ? 'false' : 'true');
    return next;
}

function setPdfEvidenceTarget(filename = '', pageNumber = null) {
    const normalizedFile = String(filename || '').trim();
    const normalizedPage = Number.isFinite(Number(pageNumber)) && Number(pageNumber) > 0
        ? Math.round(Number(pageNumber))
        : null;
    pdfEvidenceTargetFileName = normalizedFile;
    pdfEvidenceTargetPage = normalizedPage;
    updatePdfEvidencePageHighlight();
}

function getAdvancedPdfPageDiv(pageNumber) {
    const target = Math.max(1, Number(pageNumber) || 1);
    if (memoraPptxViewerInstance && memoraPptxViewerFileName) {
        const pptxRoot = els?.pptxViewer || $('pptx-viewer');
        return pptxRoot?.querySelector?.(`:scope > [data-slide-index="${target - 1}"]`) || null;
    }
    const pagesRoot = els?.pdfJsPages || $('pdfjs-pages');
    if (!pagesRoot) return null;

    const byAttr = pagesRoot.querySelector(`.page[data-page-number="${target}"]`);
    if (byAttr) return byAttr;

    const view = typeof pdfViewerInstance?.getPageView === 'function'
        ? pdfViewerInstance.getPageView(target - 1)
        : null;
    return view?.div || null;
}

function updatePdfEvidencePageHighlight() {
    const pagesRoot = els?.pdfJsPages || $('pdfjs-pages');
    const pptxRoot = els?.pptxViewer || $('pptx-viewer');
    // Only pages whose state changes are touched: stripping and re-adding the class on the page
    // that keeps it restyled that page's whole text layer on every refresh during an open.
    const clearHighlights = (keep = null) => {
        [pagesRoot, pptxRoot].filter(Boolean).forEach((root) => root.querySelectorAll('.evidence-target-page').forEach((node) => {
            if (node !== keep) node.classList.remove('evidence-target-page');
        }));
    };

    const hasPptxDoc = !!memoraPptxViewerInstance && !!memoraPptxViewerFileName;
    const hasPdfDoc = !!pdfViewerDoc && !!pdfViewerInstance && !!pdfViewerFileName;
    const sourceFileName = hasPptxDoc ? memoraPptxViewerFileName : pdfViewerFileName;
    if ((!hasPptxDoc && !hasPdfDoc)
        || !pdfEvidenceTargetFileName
        || pdfEvidenceTargetFileName !== sourceFileName
        || !Number.isFinite(pdfEvidenceTargetPage)
        || pdfEvidenceTargetPage < 1) {
        clearHighlights();
        return;
    }

    const targetPage = Math.min(
        hasPptxDoc
            ? Math.max(1, Number(memoraPptxViewerSlideCount) || 1)
            : Math.max(1, Number(pdfViewerDoc.numPages) || 1),
        Math.round(pdfEvidenceTargetPage)
    );

    const apply = (attempt = 0) => {
        const currentFileName = memoraPptxViewerInstance && memoraPptxViewerFileName
            ? memoraPptxViewerFileName
            : pdfViewerFileName;
        if (currentFileName !== pdfEvidenceTargetFileName) return;
        const pageDiv = getAdvancedPdfPageDiv(targetPage);
        if (pageDiv) {
            clearHighlights(pageDiv);
            if (!pageDiv.classList.contains('evidence-target-page')) pageDiv.classList.add('evidence-target-page');
            return;
        }
        if (attempt === 0) clearHighlights();
        if (attempt < 14) requestAnimationFrame(() => apply(attempt + 1));
    };

    apply(0);
}

// While a pane opens or closes, the predicted width costs a forced workspace layout inside the click
// and is only a guess until the panes land. Keep the last bound until then; the settle pass
// re-clamps the scale and refreshes the toolbar with the landed width.
let advancedPdfMinimumZoomScale = 0;
function getAdvancedPdfMinimumZoomScale() {
    if (advancedPdfMinimumZoomScale > 0 && window.isPaneLayoutTransitioning?.()) {
        window.whenPaneLayoutSettled?.(runPdfViewerLayoutSettlePass);
        return advancedPdfMinimumZoomScale;
    }
    const fittedScale = computePredictedPdfScale('page-fit');
    advancedPdfMinimumZoomScale = Math.max(PDF_MIN_SCALE, (fittedScale || PDF_MIN_SCALE) * MEMORA_SOURCE_MIN_FIT_ZOOM);
    return advancedPdfMinimumZoomScale;
}

function clampAdvancedPdfScaleToBounds() {
    if (!pdfViewerInstance || pdfScaleClampInProgress) return false;
    const current = Number(pdfViewerInstance.currentScale);
    if (!Number.isFinite(current) || current <= 0) return false;
    if ((pdfViewerScaleMode === 'page-fit' || pdfViewerScaleMode === 'page-width') && current <= PDF_MAX_SCALE) {
        return false;
    }
    const bounded = clampNum(current, getAdvancedPdfMinimumZoomScale(), PDF_MAX_SCALE);
    if (Math.abs(bounded - current) < 0.0001) return false;

    pdfScaleClampInProgress = true;
    try {
        pdfViewerInstance.currentScaleValue = String(bounded);
        pdfViewerScaleMode = bounded;
    } catch (_err) {
        pdfScaleClampInProgress = false;
        return false;
    }
    requestAnimationFrame(() => {
        pdfScaleClampInProgress = false;
        updatePdfMiniToolbarState();
    });
    return true;
}

function setPdfPaneMode(mode = 'pdf') {
    const pane = $('pdf-pane');
    const pdfHost = $('pdfjs-host');
    const frame = $('pdf-frame');
    const prevMode = pdfPaneContentMode;
    pdfPaneContentMode = mode === 'ai' ? 'ai' : 'pdf';

    if (!pane) return;
    schedulePdfPaneHeaderHeightSync();
    updatePdfRendererToggleButton();
    setPdfMoreMenuOpen(false);

    if (pdfPaneContentMode === 'ai') {
        if (isPdfPaneExpanded) togglePdfPaneExpanded(false);
        pane.classList.add('ai-mode');
        if (pdfHost) pdfHost.style.display = 'none';
        if (frame) frame.style.display = 'block';
        updatePdfMiniToolbarState();
        return;
    }

    pane.classList.remove('ai-mode');
    if (prevMode === 'ai' && frame && frame.src && frame.src !== 'about:blank') {
        // Stop embedded AI sessions when returning to PDF mode.
        frame.src = 'about:blank';
    }
    const useAdvancedPdf = canUseAdvancedPdfViewer() && !shouldUseIframeRenderer();
    if (useAdvancedPdf) {
        if (pdfHost) pdfHost.style.display = 'block';
        if (frame) frame.style.display = 'none';
        void waitForPdfPaneLayoutReady(1, 8).then((ready) => {
            if (!ready) return;
            refreshAdvancedPdfViewerLayout();
        });
    } else if (shouldUseIframeRenderer() || hasPdfFrameDocument(frame)) {
        if (pdfHost) pdfHost.style.display = 'none';
        if (frame) frame.style.display = 'block';
    } else if (frame) {
        // PDF.js is not loaded yet, or the source is a PowerPoint deck: an empty frame would
        // only paint a white sheet over the pane and behind the transparent Study Guide.
        frame.style.display = 'none';
    }
    updatePdfMiniToolbarState();
}

function hasPdfFrameDocument(frame) {
    const src = String(frame?.getAttribute?.('src') || '').trim();
    return !!src && src !== 'about:blank';
}

function isPdfPaneLayoutReady() {
    const pane = els?.pdfPane || $('pdf-pane');
    const host = els?.pdfJsHost || $('pdfjs-host');
    if (!pane || !host) return false;
    if (pane.offsetParent === null || host.offsetParent === null) return false;

    const paneRect = pane.getBoundingClientRect();
    const hostRect = host.getBoundingClientRect();
    const paneWidth = Math.max(pane.clientWidth || 0, paneRect.width || 0);
    const paneHeight = Math.max(pane.clientHeight || 0, paneRect.height || 0);
    const hostWidth = Math.max(host.clientWidth || 0, hostRect.width || 0);
    const hostHeight = Math.max(host.clientHeight || 0, hostRect.height || 0);

    return paneWidth > 120 && paneHeight > 120 && hostWidth > 80 && hostHeight > 80;
}

function waitForPdfPaneLayoutReady(minFrames = 2, maxFrames = 18) {
    return new Promise((resolve) => {
        let frameCount = 0;
        const tick = () => {
            frameCount += 1;
            syncPdfPaneHeaderHeight();
            const ready = isPdfPaneLayoutReady();
            if (ready && frameCount >= minFrames) {
                resolve(true);
                return;
            }
            if (frameCount >= maxFrames) {
                resolve(ready);
                return;
            }
            requestAnimationFrame(tick);
        };
        /* A pane still animating open cannot be ready before its transition settles, and polling
         * layout on each of those frames forced a reflow mid-animation (the opener asks for 40+
         * frames). Count the frames without reading layout until the pane reports it has
         * settled, then continue with the same readiness checks and frame limits. */
        if (minFrames > 1
            && window.isPaneLayoutTransitioning?.()
            && typeof window.whenPaneLayoutSettled === 'function') {
            let settled = false;
            const countFrame = () => {
                if (settled) return;
                frameCount += 1;
                requestAnimationFrame(countFrame);
            };
            requestAnimationFrame(countFrame);
            window.whenPaneLayoutSettled(() => {
                if (settled) return;
                settled = true;
                if (frameCount >= maxFrames) {
                    syncPdfPaneHeaderHeight();
                    resolve(isPdfPaneLayoutReady());
                    return;
                }
                requestAnimationFrame(tick);
            });
            return;
        }
        requestAnimationFrame(tick);
    });
}

function computePredictedPdfScale(mode = pdfViewerScaleMode) {
    if (!pdfViewerInstance || !pdfViewerDoc) return null;
    if (typeof window.getPredictedPaneWidth !== 'function') return null;
    const predictedWidth = window.getPredictedPaneWidth('pdf-pane');
    if (!predictedWidth) return null;
    const pageView = pdfViewerInstance.getPageView?.(Math.max(0, (pdfViewerInstance.currentPageNumber || 1) - 1));
    const currentScale = Number(pdfViewerInstance.currentScale) || 0;
    if (!pageView?.width || !pageView?.height || !(currentScale > 0)) return null;
    // Mirror PDF.js's own page-fit / page-width math (40px horizontal and 5px vertical padding,
    // page size per unit of currentScale). The pane border takes ~2px of the predicted width.
    // A width-only guess lands page-fit at the wrong scale and snaps when the pane settles.
    const unscaledWidth = pageView.width / currentScale;
    const unscaledHeight = pageView.height / currentScale;
    const widthScale = Math.max(100, predictedWidth - 2 - 40) / unscaledWidth;
    const hostHeight = Number((els?.pdfJsHost || $('pdfjs-host'))?.clientHeight) || 0;
    const rawScale = mode === 'page-fit' && hostHeight > 120
        ? Math.min(widthScale, (hostHeight - 5) / unscaledHeight)
        : widthScale;
    return clampNum(rawScale, PDF_MIN_SCALE, PDF_MAX_SCALE);
}

function runPdfViewerLayoutSettlePass() {
    if (!pdfViewerInstance || !pdfViewerPagesReady) return;
    syncPdfPaneHeaderHeight();
    if (scheduleAdvancedPdfResponsiveScaleRefresh({ preservePage: true })) {
        return;
    }
    clampAdvancedPdfScaleToBounds();
    updatePdfMiniToolbarState();
    scheduleAdvancedPdfVisiblePageRenderRecovery();
}

function scheduleAdvancedPdfVisiblePageRenderRecovery({ preservePage = true, maxFrames = 10 } = {}) {
    if (!pdfViewerInstance || !pdfViewerPagesReady) return false;
    // A pane open/close animation owns the work area for its full duration. Re-rasterising the
    // visible pages on every frame of it spends the same budget the compositor needs for the
    // expansion, so hand the crisp re-render to the single settle pass instead. The pane still
    // shows a correctly scaled page meanwhile: refreshAdvancedPdfViewerLayout already renders
    // once at the predicted scale when the transition starts.
    if (window.isPaneLayoutTransitioning?.()) {
        window.whenPaneLayoutSettled?.(runPdfViewerLayoutSettlePass);
        return false;
    }
    if (pdfVisibleRenderRecoveryFrame) cancelAnimationFrame(pdfVisibleRenderRecoveryFrame);
    const recoveryToken = ++pdfVisibleRenderRecoveryToken;
    const openTokenAtSchedule = pdfViewerOpenToken;
    const fileNameAtSchedule = pdfViewerFileName;
    const pageToKeep = getAdvancedPdfPageToKeep();
    let frameCount = 0;
    const recover = () => {
        pdfVisibleRenderRecoveryFrame = 0;
        if (recoveryToken !== pdfVisibleRenderRecoveryToken
            || openTokenAtSchedule !== pdfViewerOpenToken
            || fileNameAtSchedule !== pdfViewerFileName
            || !pdfViewerInstance
            || !pdfViewerPagesReady
        ) return;

        try {
            if (preservePage && Number(pdfViewerInstance.currentPageNumber) !== pageToKeep) {
                pdfViewerInstance.currentPageNumber = pageToKeep;
            }
            const visiblePages = typeof pdfViewerInstance._getVisiblePages === 'function'
                ? pdfViewerInstance._getVisiblePages()
                : undefined;
            if (typeof pdfViewerInstance.update === 'function') pdfViewerInstance.update();
            if (typeof pdfViewerInstance.forceRendering === 'function') {
                pdfViewerInstance.forceRendering(visiblePages);
            }
        } catch (_err) { }

        frameCount += 1;
        if (frameCount < Math.max(1, Number(maxFrames) || 1)) {
            pdfVisibleRenderRecoveryFrame = requestAnimationFrame(recover);
        }
    };
    pdfVisibleRenderRecoveryFrame = requestAnimationFrame(recover);
    return true;
}

function refreshAdvancedPdfViewerLayout() {
    syncPdfPaneHeaderHeight();
    if (!pdfViewerInstance) {
        updatePdfMiniToolbarState();
        return false;
    }

    try {
        if (window.isPaneLayoutTransitioning?.() && (pdfViewerScaleMode === 'page-fit' || pdfViewerScaleMode === 'page-width')) {
            const predictedScale = computePredictedPdfScale(pdfViewerScaleMode);
            if (Number.isFinite(predictedScale) && predictedScale > 0) {
                pdfViewerInstance.currentScale = predictedScale;
            }
        } else if (pdfViewerScaleMode === 'page-fit' || pdfViewerScaleMode === 'page-width') {
            pdfViewerInstance.currentScaleValue = pdfViewerScaleMode;
        } else {
            const numericMode = Number(pdfViewerScaleMode);
            if (Number.isFinite(numericMode) && numericMode > 0) {
                pdfViewerInstance.currentScaleValue = String(clampNum(numericMode, PDF_MIN_SCALE, PDF_MAX_SCALE));
            }
        }
    } catch (_err) { }

    try {
        if (typeof pdfViewerInstance.update === 'function') pdfViewerInstance.update();
        if (typeof pdfViewerInstance.forceRendering === 'function') pdfViewerInstance.forceRendering();
    } catch (_err) { }

    clampAdvancedPdfScaleToBounds();
    updatePdfEvidencePageHighlight();
    updatePdfMiniToolbarState();
    return true;
}

function scheduleAdvancedPdfResponsiveScaleRefresh({ preservePage = true, preservePageNumber = null } = {}) {
    if (!pdfViewerInstance || !pdfViewerPagesReady) return false;
    const mode = pdfViewerScaleMode;
    if (mode !== 'page-fit' && mode !== 'page-width') return false;

    const pageToKeep = Math.max(1, Number(preservePageNumber) || getAdvancedPdfPageToKeep());
    const openTokenAtSchedule = pdfViewerOpenToken;
    const fileNameAtSchedule = pdfViewerFileName;
    if (pdfResponsiveScaleRefreshFrame) {
        cancelAnimationFrame(pdfResponsiveScaleRefreshFrame);
    }

    pdfResponsiveScaleRefreshFrame = requestAnimationFrame(() => {
        pdfResponsiveScaleRefreshFrame = 0;
        if (!pdfViewerInstance || !pdfViewerPagesReady || pdfViewerScaleMode !== mode) return;
        if (pdfViewerOpenToken !== openTokenAtSchedule || pdfViewerFileName !== fileNameAtSchedule) return;
        if (!isPdfPaneLayoutReady()) return;
        refreshAdvancedPdfViewerLayout();
        if (!preservePage) return;
        requestAnimationFrame(() => {
            if (!pdfViewerInstance || !pdfViewerDoc || pdfViewerScaleMode !== mode) return;
            if (pdfViewerOpenToken !== openTokenAtSchedule || pdfViewerFileName !== fileNameAtSchedule) return;
            if (!isPdfPaneLayoutReady()) return;
            setAdvancedPdfPage(pageToKeep, false);
            scheduleAdvancedPdfVisiblePageRenderRecovery({ preservePage: true });
        });
    });
    return true;
}

function isPdfPaneInAiMode() {
    const pane = $('pdf-pane');
    return pdfPaneContentMode === 'ai' || !!pane?.classList.contains('ai-mode');
}

function togglePdfPaneExpanded(force, focusPageNumber = null) {
    const pane = els?.pdfPane || $('pdf-pane');
    if (!pane || isPdfPaneInAiMode()) return false;
    const wasExpanded = !!isPdfPaneExpanded;
    const next = force === undefined ? !isPdfPaneExpanded : !!force;
    if (next && isQuizCardExpanded) toggleQuizCardExpanded(false);
    if (next) closeActiveAnchoredPopup();
    const pageToKeep = Number.isFinite(Number(focusPageNumber)) && Number(focusPageNumber) > 0
        ? Math.round(Number(focusPageNumber))
        : getAdvancedPdfPageToKeep();
    // Expansion borrows the shared file viewer; the Duo pane keeps its page and zoom.
    const sourceViewer = window.MemoraSourceViewer;
    if (!next && sourceViewer?.current?.()?.originSurface === 'quiz-pane') {
        sourceViewer.close();
        return true;
    }
    if (next) {
        const sourceName = memoraPptxViewerFileName || pdfViewerFileName || lastPdfState?.fileName;
        if (!sourceName || !sourceViewer?.open) return false;
        const openToken = pdfViewerOpenToken;
        if (typeof closeQuickActionsDock === 'function') closeQuickActionsDock();
        return !!sourceViewer.open({
            source: { name: sourceName, page: pageToKeep },
            origin: { surface: 'quiz-pane', opener: document.activeElement },
            onClose: () => {
                if (pdfViewerOpenToken !== openToken) return;
                // New/deleted viewer highlights also appear beside the quiz on return.
                pane.querySelectorAll('.page[data-page-number], #pptx-viewer > [data-slide-index]').forEach((pageDiv) => {
                    const page = pageDiv.matches('.page')
                        ? Number(pageDiv.dataset.pageNumber)
                        : Number(pageDiv.dataset.slideIndex) + 1;
                    void window.renderPdfEvidenceStoredSelectionsForPage?.(pageDiv, page, sourceName);
                });
            }
        });
    }
    isPdfPaneExpanded = next;
    pane.classList.toggle('pdf-pane-expanded', next);
    document.body.classList.toggle('pdf-expanded-mode', next);
    if (typeof closeQuickActionsDock === 'function') closeQuickActionsDock();
    if (typeof scheduleQuickActionsDockPosition === 'function') scheduleQuickActionsDockPosition();
    syncPdfPaneHeaderHeight();
    if (next && els?.pdfJsHost) els.pdfJsHost.focus();
    if (next && pdfViewerInstance && pdfViewerDoc) {
        requestAnimationFrame(() => {
            if (!pdfViewerInstance || !pdfViewerDoc) return;
            setAdvancedPdfPage(pageToKeep, false);
        });
    }
    if (!next && wasExpanded && pdfScaleModeRestoreOnCollapse !== null && pdfViewerInstance) {
        const restore = pdfScaleModeRestoreOnCollapse;
        pdfScaleModeRestoreOnCollapse = null;
        requestAnimationFrame(() => {
            if (!pdfViewerInstance) return;
            if (restore === 'page-fit' || restore === 'page-width') {
                setAdvancedPdfScaleMode(restore);
                return;
            }
            const numericRestore = Number(restore);
            if (!Number.isFinite(numericRestore) || numericRestore <= 0) return;
            const bounded = clampNum(numericRestore, PDF_MIN_SCALE, PDF_MAX_SCALE);
            try {
                pdfViewerInstance.currentScaleValue = String(bounded);
                pdfViewerScaleMode = bounded;
                updatePdfMiniToolbarState();
            } catch (_err) { }
        });
    }
    if (next === false && !wasExpanded) {
        pdfScaleModeRestoreOnCollapse = null;
    }
    updatePdfMiniToolbarState();
    return true;
}

function setAdvancedPdfScaleMode(mode = 'page-width', { preservePageNumber = null, userInitiated = false } = {}) {
    if (!pdfViewerInstance) return false;
    const targetMode = mode === 'page-fit' ? 'page-fit' : 'page-width';
    const pageToKeep = Math.max(1, Number(preservePageNumber) || getAdvancedPdfPageToKeep());
    const openTokenAtSet = pdfViewerOpenToken;
    const fileNameAtSet = pdfViewerFileName;
    try {
        notifyTutorPdfZoomIfActive({ force: !!userInitiated });
        pdfViewerInstance.currentScaleValue = targetMode;
        pdfViewerScaleMode = targetMode;
        requestAnimationFrame(() => {
            clampAdvancedPdfScaleToBounds();
            if (pdfViewerOpenToken !== openTokenAtSet || pdfViewerFileName !== fileNameAtSet) return;
            if (pdfViewerInstance && pdfViewerDoc) setAdvancedPdfPage(pageToKeep, false);
        });
        updatePdfMiniToolbarState();
        return true;
    } catch (_err) {
        return false;
    }
}

// Owned by setAdvancedPdfPage. Its alignment retry chain outlives the call that
// scheduled it, so identity alone cannot reject a stale chain: a newer request
// often reuses the same viewer instance and the same file. This sequence makes
// each call the sole owner of its own retries.
let pdfAlignToPageRequestSeq = 0;

function setAdvancedPdfPage(pageNumber, smooth = true) {
    if (!pdfViewerInstance || !pdfViewerDoc) return false;
    const total = Math.max(1, Number(pdfViewerDoc.numPages) || 1);
    const target = Math.min(total, Math.max(1, Number(pageNumber) || 1));
    pdfViewerRequestedPage = { fileName: pdfViewerFileName, page: target };
    // A load or teardown can have a document before its page views exist.
    if (typeof pdfViewerInstance.getPageView === 'function' && !pdfViewerInstance.getPageView(target - 1)) return false;
    pdfViewerInstance.currentPageNumber = target;

    // Capture-at-schedule / compare-at-fire, the same idiom the responsive scale
    // refresh uses. Reads the live values, so user-driven toolbar and page clicks
    // match at fire time; only a genuinely newer request supersedes them.
    const alignRequestId = ++pdfAlignToPageRequestSeq;
    const viewerAtSchedule = pdfViewerInstance;
    const openTokenAtSchedule = pdfViewerOpenToken;
    const fileNameAtSchedule = pdfViewerFileName;
    let didAlignToPage = false;
    let didAlignToRenderedCanvas = false;
    const alignToTargetPage = (attempt = 0) => {
        if (alignRequestId !== pdfAlignToPageRequestSeq
            || pdfViewerInstance !== viewerAtSchedule
            || pdfViewerOpenToken !== openTokenAtSchedule
            || pdfViewerFileName !== fileNameAtSchedule
        ) return;
        const pageView = pdfViewerInstance?.getPageView?.(target - 1);
        const pageDiv = pageView?.div;
        const container = els?.pdfJsHost || $('pdfjs-host');
        const hasCanvas = !!pageDiv?.querySelector?.('canvas');
        const hasVisibleViewer = !!pageDiv?.isConnected && !!container?.isConnected
            && pageDiv.offsetParent !== null && container.offsetParent !== null;
        const shouldAlignWithPage = hasVisibleViewer && (
            !didAlignToPage
            || (hasCanvas && !didAlignToRenderedCanvas)
        );
        if (shouldAlignWithPage) {
            const containerRect = container.getBoundingClientRect();
            const pageRect = pageDiv.getBoundingClientRect();
            const hasVisibleLayout = containerRect.height > 1 && pageRect.height > 1;
            let targetTop;
            if (hasVisibleLayout) {
                const availableCenterSpace = Math.max(0, (containerRect.height - pageRect.height) / 2);
                targetTop = container.scrollTop + (pageRect.top - containerRect.top)
                    - (availableCenterSpace || PDF_PAGE_SCROLL_TOP_OFFSET);
            } else {
                try { pdfViewerInstance.scrollPageIntoView({ pageNumber: target }); } catch (_err) { }
                const pageOffsetTop = Number(pageDiv.offsetTop);
                targetTop = Number.isFinite(pageOffsetTop) && (target === 1 || pageOffsetTop > 0)
                    ? pageOffsetTop - PDF_PAGE_SCROLL_TOP_OFFSET
                    : container.scrollTop;
            }
            targetTop = Math.max(0, targetTop);
            try {
                container.scrollTo({
                    top: targetTop,
                    behavior: smooth ? 'smooth' : 'auto'
                });
            } catch (_err) {
                container.scrollTop = targetTop;
            }
            didAlignToPage = true;
            if (hasCanvas) didAlignToRenderedCanvas = true;
        } else if (!didAlignToPage && attempt === 0 && hasVisibleViewer
            && typeof pdfViewerInstance?.scrollPageIntoView === 'function') {
            try {
                pdfViewerInstance.scrollPageIntoView({ pageNumber: target });
            } catch (_err) { }
        }

        if (shouldAlignWithPage && container && (pdfViewerScaleMode === 'page-fit' || pdfViewerScaleMode === 'page-width')) {
            container.scrollLeft = 0;
        }

        if ((!hasCanvas || !didAlignToRenderedCanvas) && attempt < 8) {
            requestAnimationFrame(() => alignToTargetPage(attempt + 1));
        }
    };
    alignToTargetPage(0);

    updatePdfEvidencePageHighlight();
    updatePdfMiniToolbarState();
    scheduleAdvancedPdfVisiblePageRenderRecovery({ preservePage: true, maxFrames: 6 });
    return true;
}

function pruneAdvancedPdfViewerPageCanvases(centerPage = null, { radius = 1, keepVisible = true } = {}) {
    if (!pdfViewerInstance || !pdfViewerDoc) return 0;
    const total = Math.max(1, Number(pdfViewerDoc.numPages) || 1);
    const target = Math.min(total, Math.max(1, Number(centerPage) || Number(pdfViewerInstance.currentPageNumber) || 1));
    const keepRadius = Math.max(0, Math.min(3, Number(radius) || 0));
    const keepPages = new Set();
    for (let page = Math.max(1, target - keepRadius); page <= Math.min(total, target + keepRadius); page += 1) {
        keepPages.add(page);
    }

    const container = els?.pdfJsHost || $('pdfjs-host');
    if (keepVisible && typeof pdfViewerInstance._getVisiblePages === 'function') {
        try {
            const visiblePages = pdfViewerInstance._getVisiblePages();
            visiblePages?.views?.forEach((entry) => {
                const visiblePage = Number(entry?.id || entry?.view?.id);
                if (!Number.isFinite(visiblePage) || visiblePage < 1) return;
                for (let page = Math.max(1, visiblePage - 1); page <= Math.min(total, visiblePage + 1); page += 1) {
                    keepPages.add(page);
                }
            });
        } catch (_err) { }
    }
    const containerRect = keepVisible && container ? container.getBoundingClientRect() : null;
    if (containerRect?.height && containerRect?.width) {
        const verticalPad = Math.max(180, containerRect.height * 0.65);
        for (let page = 1; page <= total; page += 1) {
            const pageDiv = getAdvancedPdfPageDiv(page);
            if (!pageDiv) continue;
            const rect = pageDiv.getBoundingClientRect();
            if (rect.bottom >= containerRect.top - verticalPad && rect.top <= containerRect.bottom + verticalPad) {
                keepPages.add(page);
            }
        }
    }

    let pruned = 0;
    for (let page = 1; page <= total; page += 1) {
        if (keepPages.has(page)) continue;
        const view = typeof pdfViewerInstance.getPageView === 'function'
            ? pdfViewerInstance.getPageView(page - 1)
            : null;
        const div = view?.div || getAdvancedPdfPageDiv(page);
        if (!div || !div.querySelector?.('canvas')) continue;
        try {
            if (typeof view?.cancelRendering === 'function') view.cancelRendering();
        } catch (_err) { }
        try {
            if (typeof view?.reset === 'function') {
                view.reset();
                pruned += 1;
                continue;
            }
        } catch (_err) { }
        div.querySelectorAll('canvas').forEach((canvas) => {
            try {
                canvas.width = 1;
                canvas.height = 1;
                canvas.remove();
                pruned += 1;
            } catch (_err) { }
        });
    }

    if (pruned && typeof pdfViewerDoc.cleanup === 'function') {
        try { pdfViewerDoc.cleanup(); } catch (_err) { }
    }
    return pruned;
}

window.pruneAdvancedPdfViewerPageCanvases = pruneAdvancedPdfViewerPageCanvases;

function getPdfMiniToolbarStudyGuideZoomState() {
    if (typeof window.getMemoraPdfTutorStudyGuideContentZoomState !== 'function') return null;
    try {
        return window.getMemoraPdfTutorStudyGuideContentZoomState();
    } catch (_err) {
        return null;
    }
}

function applyPdfMiniToolbarStudyGuideZoom(factor) {
    if (typeof window.adjustMemoraPdfTutorStudyGuideContentZoomByFactor !== 'function') return false;
    try {
        return !!window.adjustMemoraPdfTutorStudyGuideContentZoomByFactor(factor);
    } catch (_err) {
        return false;
    }
}

function updatePdfMiniToolbarState() {
    const hasPdfDoc = !!pdfViewerDoc && !!pdfViewerInstance && !isPdfPaneInAiMode();
    const hasPptxDoc = !!memoraPptxViewerInstance && !!memoraPptxViewerFileName && !isPdfPaneInAiMode();
    const hasDoc = hasPdfDoc || hasPptxDoc;
    const studyGuideZoomState = getPdfMiniToolbarStudyGuideZoomState();
    const useStudyGuideZoom = !!studyGuideZoomState?.active;
    const totalPages = hasPptxDoc
        ? Math.max(1, Number(memoraPptxViewerSlideCount) || 1)
        : (hasPdfDoc ? Math.max(1, Number(pdfViewerDoc.numPages) || 1) : 0);
    const currentPage = hasPptxDoc
        ? Math.min(totalPages, Math.max(1, Number(memoraPptxViewerCurrentSlide) || 1))
        : (hasPdfDoc
            ? Math.min(totalPages, Math.max(1, Number(pdfViewerInstance.currentPageNumber) || 1))
            : 0);
    const currentScale = hasPptxDoc
        ? Math.max(0.1, Number(memoraPptxViewerZoomPercent) / 100 || 1)
        : (hasPdfDoc ? (Number(pdfViewerInstance.currentScale) || 0) : 0);
    if (hasPdfDoc && currentScale < (getAdvancedPdfMinimumZoomScale() - 0.0001)) {
        clampAdvancedPdfScaleToBounds();
    }
    const atMinZoom = useStudyGuideZoom
        ? !!studyGuideZoomState.atMin
        : hasDoc && currentScale <= ((hasPptxDoc ? getMemoraPptxMinimumZoomPercent() / 100 : getAdvancedPdfMinimumZoomScale()) + 0.001);
    const atMaxZoom = useStudyGuideZoom
        ? !!studyGuideZoomState.atMax
        : hasDoc && currentScale >= ((hasPptxDoc ? 4 : PDF_MAX_SCALE) - 0.001);
    const isFitPage = hasPptxDoc
        ? memoraPptxViewerInstance?.fitMode === 'contain'
        : hasPdfDoc && pdfViewerScaleMode === 'page-fit';
    const isFitWidth = hasPdfDoc && pdfViewerScaleMode === 'page-width';
    const sourceFileName = hasPptxDoc
        ? memoraPptxViewerFileName
        : (hasPdfDoc ? String(pdfViewerFileName || '') : '');

    if (els.pdfPageIndicator) {
        els.pdfPageIndicator.title = hasDoc
            ? `${hasPptxDoc ? 'Slide' : 'Page'} ${currentPage} of ${totalPages}`
            : 'No document open';
    }
    if (els.pdfPageUnit) els.pdfPageUnit.textContent = hasPptxDoc ? 'Sl' : 'Pg';
    if (els.pdfPageInput) {
        els.pdfPageInput.disabled = !hasDoc;
        els.pdfPageInput.min = '1';
        els.pdfPageInput.max = hasDoc ? String(totalPages) : '1';
        if (document.activeElement !== els.pdfPageInput) {
            els.pdfPageInput.value = hasDoc ? String(currentPage) : '';
        }
    }
    if (els.pdfPageTotal) els.pdfPageTotal.textContent = hasDoc ? String(totalPages) : '-';
    if (els.pdfZoomLabel) {
        if (useStudyGuideZoom) {
            const zoom = Number(studyGuideZoomState.zoom) || 1;
            els.pdfZoomLabel.innerText = `${Math.round(zoom * 100)}%`;
            els.pdfZoomLabel.title = 'Study Guide content zoom';
        } else if (hasDoc) {
            els.pdfZoomLabel.innerText = `${Math.round(currentScale * 100)}%`;
            els.pdfZoomLabel.title = hasPptxDoc ? 'PowerPoint zoom' : 'PDF zoom';
        } else {
            els.pdfZoomLabel.innerText = '--%';
            els.pdfZoomLabel.title = '';
        }
    }

    const disableZoom = useStudyGuideZoom ? !studyGuideZoomState.available : !hasDoc;
    updatePdfRendererToggleButton();
    if (!hasDoc) setPdfMoreMenuOpen(false);
    if (els.pdfMoreBtn) els.pdfMoreBtn.disabled = !hasDoc;
    if (els.pdfPrevPageBtn) els.pdfPrevPageBtn.disabled = !hasDoc || currentPage <= 1;
    if (els.pdfNextPageBtn) els.pdfNextPageBtn.disabled = !hasDoc || currentPage >= totalPages;
    if (els.pdfZoomOutBtn) {
        els.pdfZoomOutBtn.disabled = disableZoom || atMinZoom;
        els.pdfZoomOutBtn.title = useStudyGuideZoom ? 'Zoom Study Guide out' : 'Zoom out';
    }
    if (els.pdfZoomInBtn) {
        els.pdfZoomInBtn.disabled = disableZoom || atMaxZoom;
        els.pdfZoomInBtn.title = useStudyGuideZoom ? 'Zoom Study Guide in' : 'Zoom in';
    }
    if (els.pdfFitPageBtn) {
        els.pdfFitPageBtn.disabled = disableZoom;
        els.pdfFitPageBtn.classList.toggle('active', isFitPage);
    }
    if (els.pdfFitWidthBtn) {
        els.pdfFitWidthBtn.disabled = disableZoom || hasPptxDoc;
        els.pdfFitWidthBtn.classList.toggle('active', isFitWidth);
    }
    if (els.pdfExpandBtn) {
        els.pdfExpandBtn.disabled = !hasDoc;
        els.pdfExpandBtn.classList.toggle('active', !!isPdfPaneExpanded);
        els.pdfExpandBtn.title = isPdfPaneExpanded ? 'Restore document view' : 'Expand document view';
        els.pdfExpandBtn.setAttribute('aria-label', els.pdfExpandBtn.title);
        const menuLabel = els.pdfExpandBtn.querySelector('.pdf-more-menu-label');
        if (menuLabel) menuLabel.textContent = isPdfPaneExpanded ? 'Restore' : 'Expand';
    }
    if (els.pdfExpandHeaderBtn) {
        els.pdfExpandHeaderBtn.disabled = !hasDoc;
        els.pdfExpandHeaderBtn.classList.toggle('active', !!isPdfPaneExpanded);
        els.pdfExpandHeaderBtn.title = isPdfPaneExpanded ? 'Restore document view' : 'Expand document view';
        els.pdfExpandHeaderBtn.setAttribute('aria-label', els.pdfExpandHeaderBtn.title);
    }
    [els.pdfExpandBtn, els.pdfExpandHeaderBtn].forEach((button) => {
        const path = button?.querySelector('.pdf-header-icon-svg path');
        if (path) path.setAttribute('d', isPdfPaneExpanded
            ? 'M5 12h14'
            : 'M9 5H5v4M15 5h4v4M9 19H5v-4M15 19h4v-4');
    });
    if (els.pdfRendererToggleBtn && hasPptxDoc) els.pdfRendererToggleBtn.disabled = true;
    if (typeof window.updateMemoraPdfQuizUpToControl === 'function') {
        try {
            window.updateMemoraPdfQuizUpToControl({ hasDoc, fileName: sourceFileName, currentPage, totalPages });
        } catch (_err) { }
    }
    if (typeof window.updateMemoraPdfEvidenceAskControl === 'function') {
        try {
            window.updateMemoraPdfEvidenceAskControl({ hasDoc, fileName: sourceFileName, currentPage, totalPages });
        } catch (_err) { }
    }
}

window.getMemoraVisiblePdfInfo = function getMemoraVisiblePdfInfo() {
    const hasPptxDoc = !!memoraPptxViewerInstance && !!memoraPptxViewerFileName && !isPdfPaneInAiMode();
    const hasPdfDoc = !!pdfViewerDoc && !!pdfViewerInstance && !isPdfPaneInAiMode();
    const hasDoc = hasPptxDoc || hasPdfDoc;
    const totalPages = hasPptxDoc
        ? Math.max(1, Number(memoraPptxViewerSlideCount) || 1)
        : (hasPdfDoc ? Math.max(1, Number(pdfViewerDoc.numPages) || 1) : 0);
    const currentPage = hasPptxDoc
        ? Math.min(totalPages, Math.max(1, Number(memoraPptxViewerCurrentSlide) || 1))
        : (hasPdfDoc ? Math.min(totalPages, Math.max(1, Number(pdfViewerInstance.currentPageNumber) || 1)) : 0);
    return {
        hasDoc,
        fileName: hasPptxDoc ? memoraPptxViewerFileName : (hasPdfDoc ? String(pdfViewerFileName || '') : ''),
        currentPage,
        totalPages,
        sourceType: hasPptxDoc ? 'pptx' : (hasPdfDoc ? 'pdf' : '')
    };
};

function setupPdfMiniToolbar() {
    bindMemoraSourceViewerContainerHandlers(els?.pdfJsHost || $('pdfjs-host'));
    if (pdfMiniToolbarBound) return;
    pdfMiniToolbarBound = true;

    if (!pdfMoreMenuBound) {
        pdfMoreMenuBound = true;
        if (els.pdfMoreBtn) {
            els.pdfMoreBtn.setAttribute('aria-expanded', 'false');
            els.pdfMoreBtn.onclick = (e) => {
                e.stopPropagation();
                setPdfMoreMenuOpen();
            };
            els.pdfMoreBtn.onmousedown = (e) => {
                e.stopPropagation();
            };
        }
        if (els.pdfMoreMenu) {
            els.pdfMoreMenu.setAttribute('aria-hidden', 'true');
            els.pdfMoreMenu.onclick = (e) => {
                e.stopPropagation();
            };
            els.pdfMoreMenu.onmousedown = (e) => {
                e.stopPropagation();
            };
        }
        document.addEventListener('click', (e) => {
            const wrap = els?.pdfMoreWrap || $('pdf-more-wrap');
            if (!wrap || !e.target || !(e.target instanceof Node)) {
                setPdfMoreMenuOpen(false);
                return;
            }
            if (!wrap.contains(e.target)) setPdfMoreMenuOpen(false);
        });
        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape') setPdfMoreMenuOpen(false);
        });
    }

    if (els.pdfPrevPageBtn) {
        els.pdfPrevPageBtn.onclick = () => {
            if (memoraPptxViewerInstance) {
                void setMemoraPptxSlide(memoraPptxViewerCurrentSlide - 1, true);
                return;
            }
            setAdvancedPdfPage((Number(pdfViewerInstance?.currentPageNumber) || 1) - 1, true);
        };
    }
    if (els.pdfNextPageBtn) {
        els.pdfNextPageBtn.onclick = () => {
            if (memoraPptxViewerInstance) {
                void setMemoraPptxSlide(memoraPptxViewerCurrentSlide + 1, true);
                return;
            }
            setAdvancedPdfPage((Number(pdfViewerInstance?.currentPageNumber) || 1) + 1, true);
        };
    }
    if (els.pdfTutorFullscreenMenuBtn) {
        els.pdfTutorFullscreenMenuBtn.onclick = () => {
            setPdfMoreMenuOpen(false);
            window.toggleGlobalFullscreen?.();
        };
    }
    if (els.pdfPageIndicator && els.pdfPageInput) {
        const applyPageJump = () => {
            const totalPages = memoraPptxViewerInstance
                ? Math.max(1, Number(memoraPptxViewerSlideCount) || 1)
                : Math.max(1, Number(pdfViewerDoc?.numPages) || 1);
            const requestedPage = Math.round(Number(els.pdfPageInput.value) || 0);
            if ((!memoraPptxViewerInstance && !pdfViewerDoc) || requestedPage < 1) {
                updatePdfMiniToolbarState();
                return false;
            }
            const targetPage = Math.min(totalPages, requestedPage);
            els.pdfPageInput.value = String(targetPage);
            if (memoraPptxViewerInstance) {
                void setMemoraPptxSlide(targetPage, true);
                return true;
            }
            return setAdvancedPdfPage(targetPage, true);
        };
        els.pdfPageIndicator.onsubmit = (event) => {
            event.preventDefault();
            event.stopPropagation();
            applyPageJump();
            els.pdfPageInput.blur();
        };
        els.pdfPageInput.onchange = () => applyPageJump();
        els.pdfPageInput.onfocus = () => els.pdfPageInput.select();
        els.pdfPageInput.onclick = (event) => event.stopPropagation();
        els.pdfPageInput.onkeydown = (event) => {
            event.stopPropagation();
            if (event.key !== 'Escape') return;
            event.preventDefault();
            updatePdfMiniToolbarState();
            els.pdfPageInput.blur();
        };
    }
    if (els.pdfZoomOutBtn) {
        els.pdfZoomOutBtn.onclick = () => {
            if (applyPdfMiniToolbarStudyGuideZoom(PDF_KEYBTN_ZOOM_OUT_STEP)) {
                updatePdfMiniToolbarState();
                return;
            }
            if (memoraPptxViewerInstance) {
                void zoomMemoraPptxByFactor(PDF_KEYBTN_ZOOM_OUT_STEP);
                return;
            }
            zoomAdvancedPdfByFactor(PDF_KEYBTN_ZOOM_OUT_STEP);
        };
    }
    if (els.pdfZoomInBtn) {
        els.pdfZoomInBtn.onclick = () => {
            if (applyPdfMiniToolbarStudyGuideZoom(PDF_KEYBTN_ZOOM_IN_STEP)) {
                updatePdfMiniToolbarState();
                return;
            }
            if (memoraPptxViewerInstance) {
                void zoomMemoraPptxByFactor(PDF_KEYBTN_ZOOM_IN_STEP);
                return;
            }
            zoomAdvancedPdfByFactor(PDF_KEYBTN_ZOOM_IN_STEP);
        };
    }
    if (els.pdfRendererToggleBtn) {
        els.pdfRendererToggleBtn.onclick = () => {
            setPdfMoreMenuOpen(false);
            if (memoraPptxViewerInstance) return;
            const nextMode = pdfRendererMode === 'iframe' ? 'pdfjs' : 'iframe';
            setPdfRendererMode(nextMode, { reopenCurrent: true, smooth: false });
        };
    }
    if (els.pdfFitPageBtn) {
        els.pdfFitPageBtn.onclick = () => {
            setPdfMoreMenuOpen(false);
            if (memoraPptxViewerInstance) {
                void setMemoraPptxZoomPercent(100);
                return;
            }
            void setAdvancedPdfScaleMode('page-fit', { userInitiated: true });
        };
    }
    if (els.pdfFitWidthBtn) {
        els.pdfFitWidthBtn.onclick = () => {
            setPdfMoreMenuOpen(false);
            void setAdvancedPdfScaleMode('page-width', { userInitiated: true });
        };
    }
    [els.pdfExpandBtn, els.pdfExpandHeaderBtn].forEach((button) => {
        if (!button) return;
        button.onclick = () => {
            setPdfMoreMenuOpen(false);
            togglePdfPaneExpanded(true, getMemoraCurrentSourcePageNumber());
        };
    });
    syncPdfPaneHeaderHeight();
    updatePdfMiniToolbarState();
}

function destroyAdvancedPdfDocument() {
    setAdvancedPdfPaneLoadingState('clear');
    if (isPdfPaneExpanded) togglePdfPaneExpanded(false);
    pdfScaleModeRestoreOnCollapse = null;
    const pagesRoot = els?.pdfJsPages || $('pdfjs-pages');
    if (pagesRoot) {
        pagesRoot.querySelectorAll('.evidence-target-page').forEach((node) => {
            node.classList.remove('evidence-target-page');
        });
    }
    // Cancel page views before dropping their DOM. PDF.js owns text-layer listeners and
    // render callbacks too; merely emptying the root leaves detached page trees alive.
    try { pdfViewerInstance?.setDocument(null); } catch (_err) { }
    if (pagesRoot) {
        pagesRoot.innerHTML = '';
    }
    try {
        if (pdfViewerDoc && !(pdfViewerLoadingTask?.__memoraImageUsers > 0) && typeof pdfViewerDoc.cleanup === 'function') {
            pdfViewerDoc.cleanup();
        }
    } catch (_err) { }
    // The loading task owns borrowed render leases; retire it before clearing the viewer.
    if (pdfViewerLoadingTask) {
        try { void pdfViewerLoadingTask.destroy(); } catch (_err) { }
    } else if (pdfViewerDoc && typeof pdfViewerDoc.destroy === 'function') {
        try { void pdfViewerDoc.destroy(); } catch (_err) { }
    }
    pdfViewerDoc = null;
    pdfViewerFileName = '';
    pdfViewerRequestedPage = null;
    pdfViewerPagesReady = false;
    updatePdfMiniToolbarState();
}

function runMemoraPptxLayoutSettlePass() {
    if (!memoraPptxViewerInstance) return;
    syncMemoraPptxSourcePages();
    updatePdfMiniToolbarState();
}

function destroyMemoraPptxViewer() {
    if (memoraPptxResizeObserver) {
        try { memoraPptxResizeObserver.disconnect(); } catch (_error) { }
    }
    memoraPptxResizeObserver = null;
    if (memoraPptxViewerSearchHighlight?.dispose) {
        try { memoraPptxViewerSearchHighlight.dispose(); } catch (_error) { }
    }
    memoraPptxViewerSearchHighlight = null;
    if (memoraPptxViewerInstance?.destroy) {
        try { memoraPptxViewerInstance.destroy(); } catch (_error) { }
    }
    memoraPptxViewerInstance = null;
    memoraPptxViewerFileName = '';
    memoraPptxViewerSlideCount = 0;
    memoraPptxViewerCurrentSlide = 1;
    memoraPptxViewerZoomPercent = 100;
    const container = els?.pptxViewer || $('pptx-viewer');
    if (container) {
        container.classList.remove('pptx-slides-entering');
        container.innerHTML = '';
        container.style.display = 'none';
    }
    updatePdfMiniToolbarState();
}

function getMemoraCurrentSourcePageNumber() {
    if (memoraPptxViewerInstance && memoraPptxViewerFileName) {
        return Math.max(1, Number(memoraPptxViewerCurrentSlide) || 1);
    }
    return Math.max(1, Number(pdfViewerInstance?.currentPageNumber) || 1);
}

// Snapshot of the app shell's scroll position, taken immediately before a slide
// jump that must not move the app. Only the initial PowerPoint landing uses it.
function captureMemoraAppViewportGuard() {
    const quizPane = $('quiz-pane');
    return {
        quizPane,
        quizPaneScrollTop: quizPane ? Math.max(0, Number(quizPane.scrollTop) || 0) : 0,
        windowScrollX: Math.max(0, Number(window.scrollX) || 0),
        windowScrollY: Math.max(0, Number(window.scrollY) || 0)
    };
}

function restoreMemoraAppViewportGuard(guard) {
    if (!guard) return;
    const quizPane = guard.quizPane;
    if (quizPane && quizPane.scrollTop !== guard.quizPaneScrollTop) {
        quizPane.scrollTop = guard.quizPaneScrollTop;
    }
    if (window.scrollX !== guard.windowScrollX || window.scrollY !== guard.windowScrollY) {
        window.scrollTo(guard.windowScrollX, guard.windowScrollY);
    }
}

async function setMemoraPptxSlide(pageNumber, smooth = true, { preserveAppViewport = false } = {}) {
    if (!memoraPptxViewerInstance || !memoraPptxViewerSlideCount) return false;
    const page = Math.min(
        memoraPptxViewerSlideCount,
        Math.max(1, Math.round(Number(pageNumber) || 1))
    );
    // goToSlide() ends in Element.scrollIntoView(), which scrolls every scrollable
    // ancestor of the slide - including the app shell's #quiz-pane. Only the initial
    // PowerPoint landing asks for protection. Pin that one jump to an instant scroll
    // (a smooth animation keeps scrolling after this await resolves, which is why an
    // immediate restore could appear to work and then fail) and snap the app viewport
    // back around it. Later slide navigation and user scrolling are untouched.
    const viewportGuard = preserveAppViewport ? captureMemoraAppViewportGuard() : null;
    await memoraPptxViewerInstance.goToSlide(page - 1, {
        behavior: (smooth && !preserveAppViewport) ? 'smooth' : 'instant',
        block: 'start'
    });
    if (viewportGuard) {
        restoreMemoraAppViewportGuard(viewportGuard);
        if (typeof restorePdfTutorEntryQuizPaneViewport === 'function') {
            restorePdfTutorEntryQuizPaneViewport();
        }
        requestAnimationFrame(() => {
            restoreMemoraAppViewportGuard(viewportGuard);
        });
    }
    memoraPptxViewerCurrentSlide = page;
    if (lastPdfState && memoraPptxViewerFileName) lastPdfState.page = page;
    updatePdfMiniToolbarState();
    return true;
}

function syncMemoraPptxSourcePageElement(slideItem, metrics = null) {
    if (!slideItem || !memoraPptxViewerInstance) return false;
    const slideIndex = Math.max(0, Number.parseInt(slideItem.dataset?.slideIndex || '0', 10) || 0);
    const display = metrics || memoraPptxViewerInstance.getDisplayMetrics?.();
    const displayWidth = Math.max(1, Number(display?.displayWidth) || 1);
    const displayHeight = Math.max(1, Number(display?.displayHeight) || 1);
    const scale = Math.max(0.0001, Number(display?.scale) || 1);
    slideItem.dataset.pageNumber = String(slideIndex + 1);
    slideItem.classList.add('memora-source-page');

    const wrapper = slideItem.firstElementChild;
    if (!wrapper) return true;
    wrapper.style.width = `${displayWidth}px`;
    wrapper.style.height = `${displayHeight}px`;
    wrapper.style.maxWidth = 'none';
    const slideElement = wrapper.firstElementChild;
    if (slideElement) {
        slideElement.style.transform = `scale(${scale})`;
        slideElement.style.transformOrigin = 'top left';
    }
    return true;
}

function syncMemoraPptxSourcePages() {
    const container = els?.pptxViewer || $('pptx-viewer');
    if (!container || !memoraPptxViewerInstance) return false;
    const metrics = memoraPptxViewerInstance.getDisplayMetrics?.() || null;
    container.querySelectorAll(':scope > [data-slide-index]').forEach((slideItem) => {
        syncMemoraPptxSourcePageElement(slideItem, metrics);
    });
    return true;
}

function getMemoraPptxFitZoomFactor() {
    const viewer = memoraPptxViewerInstance;
    const host = els?.pdfJsHost || $('pdfjs-host');
    const container = els?.pptxViewer || $('pptx-viewer');
    const presentation = viewer?.presentationData || viewer?.presentation;
    if (!viewer || !host || !container || !presentation) return false;

    // The renderer's contain mode measures the full viewer client width. Account
    // for both containers' padding so a fitted slide cannot spill under the Ask
    // Memora surface or beyond the visible duo pane.
    const hostStyle = window.getComputedStyle ? window.getComputedStyle(host) : null;
    const containerStyle = window.getComputedStyle ? window.getComputedStyle(container) : null;
    const hostWidth = Math.max(1, (host.clientWidth || host.getBoundingClientRect().width || 0)
        - (parseFloat(hostStyle?.paddingLeft) || 0)
        - (parseFloat(hostStyle?.paddingRight) || 0));
    const hostHeight = Math.max(1, (host.clientHeight || host.getBoundingClientRect().height || 0)
        - (parseFloat(hostStyle?.paddingTop) || 0)
        - (parseFloat(hostStyle?.paddingBottom) || 0));
    const containerWidth = Math.max(1, container.clientWidth || container.getBoundingClientRect().width || hostWidth);
    const containerHeight = Math.max(1, container.clientHeight || container.getBoundingClientRect().height || hostHeight);
    const contentWidth = Math.max(1, containerWidth
        - (parseFloat(containerStyle?.paddingLeft) || 0)
        - (parseFloat(containerStyle?.paddingRight) || 0));
    const contentHeight = Math.max(1, Math.min(
        hostHeight,
        containerHeight
            - (parseFloat(containerStyle?.paddingTop) || 0)
            - (parseFloat(containerStyle?.paddingBottom) || 0)
    ));
    const slideWidth = Math.max(1, Number(presentation.width) || 1);
    const slideHeight = Math.max(1, Number(presentation.height) || 1);
    // PptxViewer's contain scale is based on container.clientWidth (including
    // its padding), so derive the correction from that same measured width.
    const baseScale = containerWidth / slideWidth;
    const availableWidth = Math.min(contentWidth, hostWidth);
    const fittedFactor = clampNum(
        Math.min(1, availableWidth / (slideWidth * baseScale), contentHeight / (slideHeight * baseScale)),
        0.1,
        1
    );

    return fittedFactor;
}

function getMemoraPptxMinimumZoomPercent() {
    return Math.max(10, Math.ceil((getMemoraPptxFitZoomFactor() || 0.1) * MEMORA_SOURCE_MIN_FIT_ZOOM * 100));
}

function fitMemoraPptxViewerToPage() {
    const viewer = memoraPptxViewerInstance;
    if (!viewer) return false;
    viewer._memoraSourceZoomPreview = null;
    if (viewer.fitMode !== 'contain' && typeof viewer.setFitMode === 'function') {
        void viewer.setFitMode('contain');
    }
    const fittedFactor = getMemoraPptxFitZoomFactor();
    if (!fittedFactor) return false;
    viewer.zoomFactor = fittedFactor;
    memoraPptxViewerZoomPercent = Math.round(fittedFactor * 100);
    syncMemoraPptxSourcePages();
    updatePdfMiniToolbarState();
    return true;
}

async function setMemoraPptxZoomPercent(percent, {
    anchorClientX = null,
    anchorClientY = null,
    anchorTarget = null
} = {}) {
    const viewer = memoraPptxViewerInstance;
    const host = els?.pdfJsHost || $('pdfjs-host');
    const pptxRoot = els?.pptxViewer || $('pptx-viewer');
    if (!viewer || !host || !pptxRoot) return false;
    // Preserve sub-percent trackpad input; the toolbar alone rounds its displayed percentage.
    const nextPercent = Math.min(400, Math.max(getMemoraPptxMinimumZoomPercent(), Number(percent) || 100));
    const currentPercent = Math.min(400, Math.max(10, Number(memoraPptxViewerZoomPercent) || 100));
    if (Math.abs(nextPercent - currentPercent) < 0.0001) return false;
    const hostRect = host.getBoundingClientRect();
    const x = Number.isFinite(anchorClientX) ? anchorClientX : hostRect.left + host.clientWidth / 2;
    const y = Number.isFinite(anchorClientY) ? anchorClientY : hostRect.top + host.clientHeight / 2;
    let slideNode = (anchorTarget instanceof Element ? anchorTarget : null)?.closest?.('#pptx-viewer > [data-slide-index]') || null;
    if (!slideNode && typeof document.elementsFromPoint === 'function') {
        slideNode = document.elementsFromPoint(x, y)
            .map((node) => node instanceof Element ? node.closest('#pptx-viewer > [data-slide-index]') : null)
            .find(Boolean) || null;
    }
    const preview = viewer._memoraSourceZoomPreview || (viewer._memoraSourceZoomPreview = { container: host });
    preview.anchorX = x; preview.anchorY = y;
    preview.anchorTarget = anchorTarget;
    preview.pageNumber = slideNode ? Number(slideNode.dataset.slideIndex) + 1 : memoraPptxViewerCurrentSlide;
    // Keep mounted slides and Tutor controls in place, and restore the pointer anchor before paint.
    keepAdvancedPdfZoomAnchor(preview, () => {
        viewer.zoomFactor = nextPercent / 100;
        memoraPptxViewerZoomPercent = nextPercent;
        syncMemoraPptxSourcePages();
    });
    notifyTutorPdfZoomIfActive({ force: true });
    updatePdfMiniToolbarState();
    const zoomSettleToken = ++pdfZoomSettleToken;
    requestAnimationFrame(() => {
        if (viewer !== memoraPptxViewerInstance || zoomSettleToken !== pdfZoomSettleToken) return;
        keepAdvancedPdfZoomAnchor(preview, () => {});
    });
    return true;
}

function zoomMemoraPptxByFactor(factor = 1, anchorClientX = null, anchorClientY = null, anchorTarget = null) {
    if (!memoraPptxViewerInstance || !Number.isFinite(factor) || factor <= 0) return false;
    return setMemoraPptxZoomPercent(memoraPptxViewerZoomPercent * factor, {
        anchorClientX,
        anchorClientY,
        anchorTarget
    });
}

async function openMemoraPptxWithBrowserViewer(
    filename,
    { page = 1, searchTerm = '', smooth = true, source = null } = {}
) {
    if (!filename || !loadedPdfFiles[filename]) return false;
    const token = ++pdfViewerOpenToken;
    const host = els?.pdfJsHost || $('pdfjs-host');
    const container = els?.pptxViewer || $('pptx-viewer');
    const pages = els?.pdfJsPages || $('pdfjs-pages');
    const emptyState = els?.pdfEmptyState || $('pdf-empty-state');
    const frame = els?.pdfFrame || $('pdf-frame');
    if (!host || !container) return false;

    // Match the PDF path: let the source pane reach a usable size before the
    // PowerPoint renderer measures, mounts, and scrolls to the cited slide.
    // On a fresh open, wait through the full pane transition so its resize
    // rebuild cannot replace the requested slide with slide 1. Consecutive
    // evidence jumps in an already-open pane only wait one frame.
    const workArea = els?.workArea || $('work-area');
    const paneOpening = !!workArea?.classList?.contains('pane-open-transitioning')
        || !!workArea?.classList?.contains('pane-open-expanding');
    await waitForPdfPaneLayoutReady(paneOpening ? 40 : 1, paneOpening ? 48 : 12);
    if (token !== pdfViewerOpenToken) return false;

    hardResetAdvancedPdfViewer();
    destroyMemoraPptxViewer();
    if (token !== pdfViewerOpenToken) return false;
    if (frame) {
        frame.src = 'about:blank';
        frame.style.display = 'none';
    }
    if (emptyState) emptyState.style.display = 'none';
    if (pages) pages.style.display = 'none';
    host.style.display = 'block';
    container.style.display = 'block';
    container.innerHTML = '';
    const requestedPage = Math.max(1, Math.round(Number(page) || 1));
    markPdfPaneLoading(requestedPage, 'slide');

    try {
        const entry = await getMemoraPptxDocumentEntry(source, filename);
        if (token !== pdfViewerOpenToken) return false;
        const viewer = new entry.runtime.PptxViewer(container, {
            fitMode: 'contain',
            zoomPercent: 100,
            scrollContainer: host,
            pdfjs: false,
            onSlideChange: (slideIndex) => {
                if (token !== pdfViewerOpenToken || memoraPptxViewerInstance !== viewer) return;
                memoraPptxViewerCurrentSlide = Math.max(1, Number(slideIndex) + 1);
                if (lastPdfState) lastPdfState.page = memoraPptxViewerCurrentSlide;
                updatePdfEvidencePageHighlight();
                updatePdfMiniToolbarState();
                if (typeof maybeAutoAdvanceGuidedStudyFromPdfPage === 'function') {
                    maybeAutoAdvanceGuidedStudyFromPdfPage(memoraPptxViewerCurrentSlide, filename);
                }
            },
            onSlideRendered: (slideIndex, slideElement) => {
                if (token !== pdfViewerOpenToken || memoraPptxViewerInstance !== viewer) return;
                const slideItem = slideElement?.closest?.('[data-slide-index]') || null;
                if (slideItem) {
                    syncMemoraPptxSourcePageElement(slideItem);
                    if (typeof window.renderPdfEvidenceStoredSelectionsForPage === 'function') {
                        try {
                            void window.renderPdfEvidenceStoredSelectionsForPage(
                                slideItem,
                                Math.max(1, Number(slideIndex) + 1),
                                filename
                            );
                        } catch (_err) { }
                    }
                }
                if (typeof window.notifyPdfTutorPageRendered === 'function') {
                    try { window.notifyPdfTutorPageRendered(Math.max(1, Number(slideIndex) + 1)); } catch (_err) { }
                }
            },
            onSlideUnmounted: (slideIndex) => {
                if (token !== pdfViewerOpenToken || memoraPptxViewerInstance !== viewer) return;
                // Windowed rendering resets the spacer to its initial render height on
                // unmount. Preserve today's zoom so unloading an off-screen slide cannot
                // move the slide under the reader's cursor after zoom settles.
                const slideItem = container.querySelector(`:scope > [data-slide-index="${slideIndex}"]`);
                syncMemoraPptxSourcePageElement(slideItem);
            },
            onSlideError: (slideIndex, error) => {
                console.warn(`PowerPoint slide ${Number(slideIndex) + 1} could not render:`, error);
            },
            onRenderComplete: () => {
                if (token !== pdfViewerOpenToken || memoraPptxViewerInstance !== viewer) return;
                syncMemoraPptxSourcePages();
                updatePdfEvidencePageHighlight();
            }
        });
        memoraPptxViewerInstance = viewer;
        // A width change (pane glide, scrollbar, window resize) made the renderer drop every
        // slide and rebuild the list a few slides per frame, leaving an empty end of list the
        // reader could not scroll past. Rescale the slides already in the list instead, keeping
        // the slide under the middle of the pane where it was.
        let resizeFrame = 0;
        viewer.handleContainerResize = () => {
            const width = container.clientWidth || 0;
            if (!width || width === viewer.lastMeasuredContainerWidth) return;
            viewer.lastMeasuredContainerWidth = width;
            if (resizeFrame) cancelAnimationFrame(resizeFrame);
            resizeFrame = requestAnimationFrame(() => {
                resizeFrame = 0;
                if (token !== pdfViewerOpenToken || memoraPptxViewerInstance !== viewer) return;
                keepAdvancedPdfZoomAnchor({ container: host }, () => syncMemoraPptxSourcePages());
            });
        };
        // The renderer mounts slides 1-4 up front. Hold every mount but the cited slide until
        // the landing below has mounted it and scrolled to it; the list's lazy observer
        // then brings in its neighbours and the rest as the reader scrolls.
        const mountListSlide = viewer.mountListSlide;
        let landingSlideOnly = true;
        viewer.mountListSlide = function (slideIndex, ...rest) {
            if (landingSlideOnly && slideIndex !== requestedSlide - 1) return;
            return mountListSlide.call(this, slideIndex, ...rest);
        };
        memoraPptxViewerFileName = filename;
        memoraPptxViewerSlideCount = Math.max(1, Number(entry.presentation?.slides?.length) || 1);
        const requestedSlide = Math.min(memoraPptxViewerSlideCount, requestedPage);
        memoraPptxViewerCurrentSlide = requestedSlide;
        memoraPptxViewerZoomPercent = 100;
        viewer.load(entry.presentation);
        // Set the fitted zoom before the renderer mounts its first slide so the
        // initial paint is already page-fitted instead of being corrected later.
        fitMemoraPptxViewerToPage();
        await viewer.renderList({
            windowed: true,
            initialSlides: 4,
            batchSize: 4,
            overscanViewport: 1.5,
            showSlideLabels: false
        });
        if (token !== pdfViewerOpenToken || memoraPptxViewerInstance !== viewer) {
            viewer.destroy();
            return false;
        }
        syncMemoraPptxSourcePages();
        // renderList reports its initial visible slide through onSlideChange. Keep the
        // requested target stable so that callback cannot reset a search result to slide 1.
        // The initial landing is always an instant jump so no queued smooth-scroll
        // animation can drag #quiz-pane after this call resolves.
        try {
            await setMemoraPptxSlide(requestedSlide, false, { preserveAppViewport: true });
        } finally {
            // The cited slide is mounted and in view; its neighbours can come in now.
            landingSlideOnly = false;
        }
        const query = String(searchTerm || '').trim();
        if (query) {
            const targetSlideIndex = memoraPptxViewerCurrentSlide - 1;
            const match = viewer.searchText(query)?.find((result) => (
                Number(result?.slideIndex) === targetSlideIndex
            )) || null;
            if (match) {
                memoraPptxViewerSearchHighlight = await viewer.highlightSearchResult(match, {
                    scrollIntoView: false,
                    borderColor: '#60a5fa',
                    backgroundColor: 'rgba(96, 165, 250, 0.18)',
                    boxShadow: '0 0 0 2px rgba(96, 165, 250, 0.22)',
                    padding: 2
                });
            }
        }
        currentPdfBlobUrl = loadedPdfFiles[filename];
        setAdvancedPdfPaneLoadingState('loaded');
        if (window.ResizeObserver && container) {
            if (memoraPptxResizeObserver) {
                try { memoraPptxResizeObserver.disconnect(); } catch (_e) { }
            }
            memoraPptxResizeObserver = new ResizeObserver(() => {
                if (!memoraPptxViewerInstance) return;
                if (window.isPaneLayoutTransitioning?.()) {
                    window.whenPaneLayoutSettled?.(runMemoraPptxLayoutSettlePass);
                    return;
                }
                syncMemoraPptxSourcePages();
                updatePdfMiniToolbarState();
            });
            memoraPptxResizeObserver.observe(container);
        }
        updatePdfMiniToolbarState();
        try {
            window.dispatchEvent(new CustomEvent('memora:pptx-viewer-ready', {
                detail: {
                    fileName: filename,
                    page: memoraPptxViewerCurrentSlide
                }
            }));
        } catch (_error) { }
        return true;
    } catch (error) {
        if (token !== pdfViewerOpenToken) return false;
        console.error('PowerPoint viewer failed:', error);
        destroyMemoraPptxViewer();
        setAdvancedPdfPaneLoadingState('error', 'This PowerPoint could not be displayed. Try again.');
        if (emptyState) {
            emptyState.style.display = 'flex';
            const message = emptyState.querySelector('.pdf-empty-state-title');
            if (message) message.textContent = 'This PowerPoint could not be displayed';
        }
        showPopupText(`Could not display ${filename}: ${error?.message || error}`, null, true);
        return false;
    }
}

function showEmptyPdfPaneState(title = 'Add a file to see it here') {
    const pane = els?.pdfPane || $('pdf-pane');
    const host = els?.pdfJsHost || $('pdfjs-host');
    const frame = els?.pdfFrame || $('pdf-frame');
    const titleEl = els?.pdfTitle || $('pdf-title');

    destroyAdvancedPdfDocument();
    destroyMemoraPptxViewer();

    if (pane) pane.style.display = '';
    if (frame) {
        if (frame.src && frame.src !== 'about:blank') frame.src = 'about:blank';
        frame.style.display = 'none';
    }

    setPdfPaneMode('pdf');

    if (host) {
        host.style.display = 'block';
        host.scrollTop = 0;
    }
    if (frame) frame.style.display = 'none';
    if (titleEl) {
        titleEl.style.display = '';
        titleEl.innerText = title;
    }

    const emptyState = els?.pdfEmptyState || $('pdf-empty-state');
    if (emptyState) {
        emptyState.style.display = 'flex';
        const message = emptyState.querySelector('.pdf-empty-state-title');
        if (message) message.textContent = title;
    }
    const pages = els?.pdfJsPages || $('pdfjs-pages');
    if (pages) pages.style.display = 'none';
    const pptxPages = els?.pptxViewer || $('pptx-viewer');
    if (pptxPages) pptxPages.style.display = 'none';

    currentPdfBlobUrl = null;
    lastPdfState = { src: '', title: '', fileName: '', page: 1, searchTerm: '' };
    syncPdfPaneHeaderHeight();
    updatePdfMiniToolbarState();

}

function resetAdvancedPdfZoomToPageWidth({ userInitiated = false } = {}) {
    if (!pdfViewerInstance) return false;
    if (isPdfTutorPdfViewActive() && typeof window.clearPdfTutorUnifiedZoomPreview === 'function') {
        window.clearPdfTutorUnifiedZoomPreview();
    }
    try {
        notifyTutorPdfZoomIfActive({ force: !!userInitiated });
        pdfViewerInstance.currentScaleValue = 'page-fit';
        pdfViewerScaleMode = 'page-fit';
        scheduleAdvancedPdfResponsiveScaleRefresh({ preservePage: true });
        updatePdfMiniToolbarState();
        return true;
    } catch (_err) {
        return false;
    }
}

// Wheel, pinch and button zoom glide on a CSS zoom of the page stack, and PDF.js renders the
// final scale once input stops. Re-rendering on every wheel tick made zoom land in steps.
let memoraPdfZoomPreview = null;

function getAdvancedPdfZoomPreviewAnchor(preview) {
    const hostRect = preview.container.getBoundingClientRect();
    const x = Number.isFinite(preview.anchorX) ? preview.anchorX : hostRect.left + hostRect.width / 2;
    const y = Number.isFinite(preview.anchorY) ? preview.anchorY : hostRect.top + hostRect.height / 2;
    // Intro card and transcript are real zoom targets above source page 1.
    const candidates = [preview.anchorTarget];
    if (typeof document !== 'undefined' && typeof document.elementsFromPoint === 'function') {
        candidates.push(...document.elementsFromPoint(x, y));
    }
    let intro = null;
    let sourcePage = null;
    for (const node of candidates) {
        if (node?.isConnected === false) continue;
        sourcePage ||= node?.closest?.('.page, #pptx-viewer > [data-slide-index]');
        if (!node?.closest?.('.pdf-tutor-guided-debrief.is-embedded')) continue;
        intro = node.closest('.pdf-tutor-guided-debrief-slide, .pdf-tutor-guided-debrief-bar')
            || node.closest('.pdf-tutor-guided-debrief.is-embedded');
        break;
    }
    if (!intro && !sourcePage && typeof getPdfTutorEmbeddedIntroSlide === 'function') {
        // A toolbar/blank-space fallback may use Intro only while that card is in view.
        intro = getPdfTutorEmbeddedIntroSlide();
    }
    const page = intro || sourcePage || getAdvancedPdfPageDiv(preview.pageNumber) || getAdvancedPdfPageDiv(Number(pdfViewerInstance?.currentPageNumber) || 1);
    return { x, y, page };
}

function keepAdvancedPdfZoomAnchor(preview, applyScale) {
    const { x, y, page } = getAdvancedPdfZoomPreviewAnchor(preview);
    const before = page?.getBoundingClientRect();
    const previous = preview.sourceAnchor;
    // Reuse the file viewer's stable source-point rule across pinch rounding and Fit crossings.
    const keepAnchor = !!previous && previous.page === page
        && previous.scrollLeft === preview.container.scrollLeft && previous.scrollTop === preview.container.scrollTop
        && previous.stageWidth === preview.container.clientWidth && previous.stageHeight === preview.container.clientHeight
        && Math.abs(previous.x - x) < 1 && Math.abs(previous.y - y) < 1;
    const ratioX = keepAnchor ? previous.ratioX : clampNum((x - (before?.left || 0)) / Math.max(1, before?.width || 0), 0, 1);
    const ratioY = keepAnchor ? previous.ratioY : clampNum((y - (before?.top || 0)) / Math.max(1, before?.height || 0), 0, 1);
    applyScale();
    if (typeof syncPdfTutorGuidedIntroGeometry === 'function') syncPdfTutorGuidedIntroGeometry();
    if (!before?.width || !before?.height) return;
    const after = page.getBoundingClientRect();
    preview.container.scrollLeft += after.left + ratioX * after.width - x;
    preview.container.scrollTop += after.top + ratioY * after.height - y;
    preview.sourceAnchor = { page, x, y, ratioX, ratioY,
        scrollLeft: preview.container.scrollLeft, scrollTop: preview.container.scrollTop,
        stageWidth: preview.container.clientWidth, stageHeight: preview.container.clientHeight };
}

function cancelAdvancedPdfZoomPreview() {
    const preview = memoraPdfZoomPreview;
    if (!preview) return;
    memoraPdfZoomPreview = null;
    if (preview.frame) cancelAnimationFrame(preview.frame);
    if (preview.timer) clearTimeout(preview.timer);
    preview.pagesRoot.style.zoom = preview.originalZoom || '';
}

function settleAdvancedPdfZoomPreview() {
    const preview = memoraPdfZoomPreview;
    if (!preview) return false;
    if (preview.viewer !== pdfViewerInstance || preview.fileName !== pdfViewerFileName || !preview.pagesRoot.isConnected) {
        cancelAdvancedPdfZoomPreview();
        return false;
    }
    const active = document.activeElement;
    const selection = window.getSelection?.();
    const ranges = Array.from({ length: selection?.rangeCount || 0 }, (_, index) => selection.getRangeAt(index).cloneRange());
    const inputSelection = active && typeof active.selectionStart === 'number'
        ? [active.selectionStart, active.selectionEnd, active.selectionDirection] : null;
    memoraPdfZoomPreview = null;
    if (preview.frame) cancelAnimationFrame(preview.frame);
    if (preview.timer) clearTimeout(preview.timer);
    // PDF.js resets page children when it rescales. Keep the app's own layers (highlights and an
    // open Ask card) attached across that reset instead of letting them vanish until re-render.
    const retained = Array.from(preview.pagesRoot.querySelectorAll('.page')).map((pageDiv) => ({
        pageDiv,
        nodes: Array.from(pageDiv.children).filter((node) => Array.from(node.classList).some((name) => name.startsWith('pdf-tutor-')))
    }));
    keepAdvancedPdfZoomAnchor(preview, () => {
        retained.forEach(({ nodes }) => nodes.forEach((node) => node.remove()));
        try {
            preview.pagesRoot.style.zoom = preview.originalZoom || '';
            notifyTutorPdfZoomIfActive({ force: true });
            pdfViewerInstance.currentScaleValue = String(preview.target);
            pdfViewerScaleMode = preview.target;
        } finally {
            retained.forEach(({ pageDiv, nodes }) => nodes.forEach((node) => pageDiv.appendChild(node)));
        }
    });
    if (active?.isConnected && active !== document.activeElement) active.focus({ preventScroll: true });
    if (inputSelection && active?.isConnected) active.setSelectionRange(...inputSelection);
    if (!inputSelection && ranges.length && selection) {
        selection.removeAllRanges(); ranges.forEach((range) => selection.addRange(range));
    }
    updatePdfMiniToolbarState();
    return true;
}

function previewAdvancedPdfZoom(factor, anchorClientX = null, anchorClientY = null, anchorTarget = null) {
    const container = els?.pdfJsHost || $('pdfjs-host');
    const pagesRoot = pdfViewerInstance?.viewer;
    if (!container || !pagesRoot || !pdfViewerInstance.pagesCount) return false;
    let preview = memoraPdfZoomPreview;
    if (preview && (preview.viewer !== pdfViewerInstance || preview.fileName !== pdfViewerFileName)) {
        cancelAdvancedPdfZoomPreview();
        preview = null;
    }
    if (!preview) {
        const base = Number(pdfViewerInstance.currentScale) || 1;
        preview = {
            viewer: pdfViewerInstance, fileName: pdfViewerFileName, container, pagesRoot,
            base, target: base, shown: base, frameAt: 0, frame: 0, timer: 0, settleDue: false,
            originalZoom: pagesRoot.style.zoom, anchorX: null, anchorY: null, pageNumber: 0
        };
        memoraPdfZoomPreview = preview;
    }
    preview.target = Math.min(PDF_MAX_SCALE, Math.max(getAdvancedPdfMinimumZoomScale(), preview.target * factor));
    preview.anchorX = anchorClientX;
    preview.anchorY = anchorClientY;
    preview.anchorTarget = anchorTarget;
    const anchorPage = (anchorTarget instanceof Element ? anchorTarget : null)?.closest?.('.page')
        || (Number.isFinite(anchorClientX) && Number.isFinite(anchorClientY)
            ? document.elementsFromPoint(anchorClientX, anchorClientY)
                .map((node) => node instanceof Element ? node.closest('.page') : null)
                .find(Boolean)
            : null);
    const anchorPageNumber = Number.parseInt(anchorPage?.dataset?.pageNumber || '', 10);
    preview.pageNumber = Number.isFinite(anchorPageNumber) ? anchorPageNumber : (Number(pdfViewerInstance.currentPageNumber) || 1);
    preview.settleDue = false;
    if (preview.timer) clearTimeout(preview.timer);
    // Each frame eases the shown scale toward the accumulated target in log space, so separate
    // wheel notches read as one continuous zoom. Reduced motion shows the target at once.
    const step = (timestamp) => {
        preview.frame = 0;
        if (memoraPdfZoomPreview !== preview) return;
        const now = Number.isFinite(timestamp) ? timestamp : performance.now();
        const elapsed = preview.frameAt ? Math.min(64, Math.max(0, now - preview.frameAt)) : 16;
        preview.frameAt = now;
        const reduceMotion = getMemoraMotionMode() === 'reduced';
        const gap = Math.log(preview.target / preview.shown);
        preview.shown = reduceMotion || Math.abs(gap) < 0.002
            ? preview.target
            : preview.shown * Math.exp(gap * (1 - Math.exp(-elapsed / 60)));
        keepAdvancedPdfZoomAnchor(preview, () => {
            preview.pagesRoot.style.zoom = String(preview.shown / preview.base);
        });
        if (preview.shown !== preview.target) preview.frame = requestAnimationFrame(step);
        else if (preview.settleDue) settleAdvancedPdfZoomPreview();
    };
    if (!preview.frame) preview.frame = requestAnimationFrame(step);
    // The sharp render waits until input stops and the glide has reached the target.
    preview.timer = setTimeout(() => {
        preview.timer = 0;
        preview.settleDue = true;
        if (!preview.frame && memoraPdfZoomPreview === preview) settleAdvancedPdfZoomPreview();
    }, 180);
    return true;
}

function zoomAdvancedPdfByFactor(factor = 1.0, anchorClientX = null, anchorClientY = null, anchorTarget = null) {
    const container = els?.pdfJsHost || $('pdfjs-host');
    if (!pdfViewerInstance || !container || !Number.isFinite(factor) || factor <= 0) return;
    const isPdfTutorZoom = isPdfTutorPdfViewActive();
    if (
        isPdfTutorZoom
        && !window.__pdfTutorCommittingUnifiedZoom
        && typeof window.handlePdfTutorUnifiedZoomPreview === 'function'
        && window.handlePdfTutorUnifiedZoomPreview(factor, anchorClientX, anchorClientY, anchorTarget)
    ) {
        return;
    }
    if (!isPdfTutorZoom && previewAdvancedPdfZoom(factor, anchorClientX, anchorClientY, anchorTarget)) return;
    const current = Number(pdfViewerInstance.currentScale) || 1;
    const next = Math.min(PDF_MAX_SCALE, Math.max(getAdvancedPdfMinimumZoomScale(), current * factor));
    if (!Number.isFinite(next) || Math.abs(next - current) < 0.0001) return;

    const rect = container.getBoundingClientRect();
    const hostStyle = window.getComputedStyle ? window.getComputedStyle(container) : null;
    const padLeft = hostStyle ? (parseFloat(hostStyle.paddingLeft) || 0) : 0;
    const padRight = hostStyle ? (parseFloat(hostStyle.paddingRight) || 0) : 0;
    const padTop = hostStyle ? (parseFloat(hostStyle.paddingTop) || 0) : 0;
    const padBottom = hostStyle ? (parseFloat(hostStyle.paddingBottom) || 0) : 0;
    const viewportWidth = Math.max(
        0,
        (container.clientWidth || rect.width || 0) - padLeft - padRight
    );
    const viewportHeight = Math.max(
        0,
        (container.clientHeight || rect.height || 0) - padTop - padBottom
    );

    // Anchor against the true content viewport (exclude host padding) so zoom target remains stable.
    const relX = Number.isFinite(anchorClientX)
        ? clampNum(anchorClientX - rect.left - padLeft, 0, viewportWidth)
        : (viewportWidth / 2);
    const relY = Number.isFinite(anchorClientY)
        ? clampNum(anchorClientY - rect.top - padTop, 0, viewportHeight)
        : (viewportHeight / 2);

    const anchorViewportX = rect.left + padLeft + relX;
    const anchorViewportY = rect.top + padTop + relY;
    const preScrollWidth = Math.max(1, container.scrollWidth || 1);
    const preScrollHeight = Math.max(1, container.scrollHeight || 1);
    const preAnchorX = container.scrollLeft + relX;
    const preAnchorY = container.scrollTop + relY;

    // Prefer page-local anchoring (stable across page jumps and non-scaled inter-page spacing).
    const targetEl = anchorTarget instanceof Element
        ? anchorTarget
        : (Number.isFinite(anchorClientX) && Number.isFinite(anchorClientY)
            ? document.elementFromPoint(anchorClientX, anchorClientY)
            : null);
    let pageNode = targetEl?.closest?.('.page') || null;
    if (isPdfTutorZoom && Number.isFinite(anchorClientX) && Number.isFinite(anchorClientY)) {
        const stackedPage = document.elementsFromPoint(anchorClientX, anchorClientY)
            .map((node) => node instanceof Element ? node.closest('.page') : null)
            .find(Boolean);
        if (stackedPage) pageNode = stackedPage;
    }
    pageNode = pageNode || getAdvancedPdfPageDiv(Number(pdfViewerInstance.currentPageNumber) || 1);
    const pageNumber = Number.parseInt(
        pageNode?.getAttribute?.('data-page-number') || pageNode?.dataset?.pageNumber || '',
        10
    );
    let pageAnchorRatioX = 0.5;
    let pageAnchorRatioY = 0.5;
    if (pageNode) {
        const pageRect = pageNode.getBoundingClientRect();
        if (pageRect.width > 0 && pageRect.height > 0) {
            const localX = Number.isFinite(anchorClientX) ? (anchorClientX - pageRect.left) : (pageRect.width / 2);
            const localY = Number.isFinite(anchorClientY) ? (anchorClientY - pageRect.top) : (pageRect.height / 2);
            pageAnchorRatioX = clampNum(localX / pageRect.width, 0, 1);
            pageAnchorRatioY = clampNum(localY / pageRect.height, 0, 1);
        }
    }

    notifyTutorPdfZoomIfActive({ force: true });
    pdfViewerInstance.currentScaleValue = String(next);
    pdfViewerScaleMode = next;

    // Re-apply anchor across a few frames while PDF.js finalizes layout.
    const zoomSettleToken = ++pdfZoomSettleToken;
    let settleFrames = 0;
    const settleAnchor = (sync = false) => {
        if (!pdfViewerInstance || zoomSettleToken !== pdfZoomSettleToken) return;
        const maxLeft = Math.max(0, container.scrollWidth - container.clientWidth);
        const maxTop = Math.max(0, container.scrollHeight - container.clientHeight);

        const anchorPageDiv = Number.isFinite(pageNumber) && pageNumber > 0
            ? getAdvancedPdfPageDiv(pageNumber)
            : null;
        if (anchorPageDiv) {
            const pageRectNow = anchorPageDiv.getBoundingClientRect();
            const targetClientX = pageRectNow.left + (pageAnchorRatioX * pageRectNow.width);
            const targetClientY = pageRectNow.top + (pageAnchorRatioY * pageRectNow.height);
            const deltaX = targetClientX - anchorViewportX;
            const deltaY = targetClientY - anchorViewportY;
            container.scrollLeft = clampNum(container.scrollLeft + deltaX, 0, maxLeft);
            container.scrollTop = clampNum(container.scrollTop + deltaY, 0, maxTop);
        } else {
            // Fallback: keep previous content point under the anchor.
            const widthRatio = (container.scrollWidth || preScrollWidth) / preScrollWidth;
            const heightRatio = (container.scrollHeight || preScrollHeight) / preScrollHeight;
            const targetLeft = (preAnchorX * widthRatio) - relX;
            const targetTop = (preAnchorY * heightRatio) - relY;
            container.scrollLeft = clampNum(targetLeft, 0, maxLeft);
            container.scrollTop = clampNum(targetTop, 0, maxTop);
        }
        if (isPdfTutorZoom) notifyTutorPdfZoomIfActive({ force: true });
        if (sync) return;
        settleFrames += 1;
        if (settleFrames < 4) {
            requestAnimationFrame(() => settleAnchor());
            return;
        }
        updatePdfMiniToolbarState();
    };
    // PDF.js scrolls to its last recorded location while it rescales. Correct in the same task,
    // or that jump paints for a frame before the anchor pulls the page back.
    settleAnchor(true);
    requestAnimationFrame(() => settleAnchor());
}

// Owned by applyAdvancedPdfSearch. The delayed findagain below belongs to the
// search that scheduled it. A newer search usually keeps the same find controller,
// so controller identity alone cannot reject the stale one.
let pdfAdvancedSearchRequestSeq = 0;

function applyAdvancedPdfSearch(searchTerm = '') {
    if (!pdfViewerFindController) return;
    const query = String(searchTerm || '').trim();
    const searchRequestId = ++pdfAdvancedSearchRequestSeq;
    const controllerAtSchedule = pdfViewerFindController;
    const openTokenAtSchedule = pdfViewerOpenToken;
    const fileNameAtSchedule = pdfViewerFileName;
    try {
        pdfViewerFindController.executeCommand('find', {
            query,
            phraseSearch: true,
            caseSensitive: false,
            entireWord: false,
            highlightAll: query.length > 0,
            findPrevious: false,
            matchDiacritics: false
        });
        if (query) {
            // Move to first match near current page.
            setTimeout(() => {
                if (searchRequestId !== pdfAdvancedSearchRequestSeq
                    || pdfViewerFindController !== controllerAtSchedule
                    || pdfViewerOpenToken !== openTokenAtSchedule
                    || pdfViewerFileName !== fileNameAtSchedule
                ) return;
                try {
                    controllerAtSchedule.executeCommand('findagain', {
                        query,
                        phraseSearch: true,
                        caseSensitive: false,
                        entireWord: false,
                        highlightAll: true,
                        findPrevious: false,
                        matchDiacritics: false
                    });
                } catch (_err) { }
            }, 70);
        }
    } catch (_err) { }
}

function waitForAdvancedPdfPagesReady(token) {
    return new Promise((resolve) => {
        const started = Date.now();
        const tick = () => {
            if (token !== pdfViewerOpenToken) return resolve(false);
            if (pdfViewerPagesReady) return resolve(true);

            const instance = pdfViewerInstance;
            const doc = pdfViewerDoc;
            if (instance && doc) {
                const pagesCount = Math.max(
                    0,
                    Number(instance.pagesCount) || Number(instance._pages?.length) || 0
                );
                if (pagesCount > 0) return resolve(true);

                const firstView = typeof instance.getPageView === 'function' ? instance.getPageView(0) : null;
                if (firstView?.div) return resolve(true);
            }

            if ((Date.now() - started) > 15000) return resolve(false);
            requestAnimationFrame(tick);
        };
        tick();
    });
}

function waitForAdvancedPdfPageCanvas(pageNumber, token, timeoutMs = 3500) {
    return new Promise((resolve) => {
        const started = Date.now();
        const target = Math.max(1, Number(pageNumber) || 1);
        const tick = () => {
            if (token !== pdfViewerOpenToken) return resolve(false);
            if (!pdfViewerInstance) return resolve(false);

            const pageView = typeof pdfViewerInstance.getPageView === 'function'
                ? pdfViewerInstance.getPageView(target - 1)
                : null;
            const canvas = pageView?.div?.querySelector?.('canvas');
            const renderingState = Number(pageView?.renderingState);
            const renderReady = !!canvas && (!Number.isFinite(renderingState) || renderingState === 3);
            if (renderReady) return resolve(true);

            if ((Date.now() - started) > timeoutMs) return resolve(false);
            requestAnimationFrame(tick);
        };
        tick();
    });
}

function markPdfPaneLoading(pageNumber = null, unit = 'page') {
    const host = els?.pdfJsHost || $('pdfjs-host');
    const pagesRoot = els?.pdfJsPages || $('pdfjs-pages');
    if (host) {
        const safeTarget = Number.isFinite(Number(pageNumber)) && Number(pageNumber) > 0
            ? Math.max(1, Math.round(Number(pageNumber)))
            : null;
        const safeUnit = String(unit || 'page').toLowerCase() === 'slide' ? 'slide' : 'page';
        const loadingLabel = safeTarget
            ? `Opening ${safeUnit} ${safeTarget}\u2026`
            : 'Opening source\u2026';
        setAdvancedPdfPaneLoadingState('loading', loadingLabel);
        host.scrollTop = 0;
    }
    if (pagesRoot) {
        // PDF.js emits `pagerendered` again as pages are lazily painted while
        // scrolling. Only the first render after an explicit open should
        // replay the page-stack entrance animation.
        pagesRoot.dataset.pdfPagesEnteringPending = '1';
        pagesRoot.classList.remove('pdf-pages-entering');
        if (pagesRoot.dataset.pdfPagesEnteringTimer) {
            clearTimeout(Number(pagesRoot.dataset.pdfPagesEnteringTimer));
            delete pagesRoot.dataset.pdfPagesEnteringTimer;
        }
    }
}
function markPdfPagesLoadedAnimation() {
    const host = els?.pdfJsHost || $('pdfjs-host');
    const pagesRoot = els?.pdfJsPages || $('pdfjs-pages');
    const shouldAnimate = pagesRoot?.dataset.pdfPagesEnteringPending === '1';
    if (pagesRoot) delete pagesRoot.dataset.pdfPagesEnteringPending;
    setAdvancedPdfPaneLoadingState('loaded');
    if (!shouldAnimate || !pagesRoot) return;
    pagesRoot.classList.remove('pdf-pages-entering');
    void pagesRoot.offsetWidth;
    pagesRoot.classList.add('pdf-pages-entering');
    if (pagesRoot.dataset.pdfPagesEnteringTimer) {
        clearTimeout(Number(pagesRoot.dataset.pdfPagesEnteringTimer));
    }
    const timer = setTimeout(() => {
        pagesRoot.classList.remove('pdf-pages-entering');
        delete pagesRoot.dataset.pdfPagesEnteringTimer;
    }, 420);
    pagesRoot.dataset.pdfPagesEnteringTimer = String(timer);
}

function setAdvancedPdfPaneLoadingState(state = 'clear', label = '') {
    const host = els?.pdfJsHost || $('pdfjs-host');
    if (!host) return;
    const status = $('pdfjs-loading-status');
    const nextState = ['loading', 'loaded', 'error'].includes(state) ? state : 'clear';
    if (nextState === 'clear') {
        host.classList.remove('pdf-pane-loading');
        host.removeAttribute('aria-busy');
        delete host.dataset.loadingLabel;
        if (status) {
            status.hidden = true;
            status.textContent = '';
            delete status.dataset.state;
        }
        return;
    }

    host.classList.toggle('pdf-pane-loading', nextState === 'loading');
    host.setAttribute('aria-busy', nextState === 'loading' ? 'true' : 'false');
    if (nextState === 'loading') host.dataset.loadingLabel = String(label || 'Opening source\u2026');
    else delete host.dataset.loadingLabel;
    if (status) {
        status.hidden = nextState === 'loaded';
        status.dataset.state = nextState;
        if (nextState !== 'loaded') status.textContent = String(label || 'Opening source\u2026');
    }
}

function hardResetAdvancedPdfViewer() {
    setAdvancedPdfPaneLoadingState('clear');
    (els?.pdfJsPages || $('pdfjs-pages'))?.classList.remove('pdf-pages-entering');
    try {
        if (pdfViewerResizeObserver && typeof pdfViewerResizeObserver.disconnect === 'function') {
            pdfViewerResizeObserver.disconnect();
        }
    } catch (_err) { }
    pdfViewerResizeObserver = null;

    try {
        destroyAdvancedPdfDocument();
    } catch (_err) { }

    pdfViewerEventBus = null;
    pdfViewerLinkService = null;
    pdfViewerFindController = null;
    pdfViewerInstance = null;
    pdfViewerLoadingTask = null;
    pdfViewerPagesReady = false;
}

function bindMemoraSourceViewerContainerHandlers(container) {
    if (!container || pdfViewerContainerHandlersBound) return false;
    pdfViewerContainerHandlersBound = true;

    container.addEventListener('pointerdown', () => container.focus());
    // The student is driving the viewer now, so their position wins over the last request.
    const releaseRequestedPage = () => { pdfViewerRequestedPage = null; };
    ['wheel', 'touchstart', 'pointerdown', 'keydown'].forEach((type) => {
        container.addEventListener(type, releaseRequestedPage, { passive: true });
    });
    container.addEventListener('wheel', (e) => {
        if (!e.ctrlKey && !e.metaKey) return;
        e.preventDefault();
        const wheelFactor = getPdfWheelZoomFactor(e.deltaY, e.deltaMode);
        if (!Number.isFinite(wheelFactor) || Math.abs(wheelFactor - 1) < 0.0005) return;
        if (memoraPptxViewerInstance) {
            void zoomMemoraPptxByFactor(wheelFactor, e.clientX, e.clientY, e.target);
            return;
        }
        zoomAdvancedPdfByFactor(
            wheelFactor,
            e.clientX,
            e.clientY,
            e.target
        );
    }, { passive: false });

    container.addEventListener('contextmenu', (e) => {
        if (isPdfPaneInAiMode()) return;
        if (els.srsRatingModal?.classList.contains('active')) return;
        if (!(e.target instanceof Element)) return;
        const pageNode = e.target.closest('.page, #pptx-viewer > [data-slide-index]');
        if (!pageNode) return;
        // Keep annotation controls interactive.
        if (e.target.closest('.annotationLayer a, .annotationLayer button, .annotationLayer input, .annotationLayer textarea, .annotationLayer select')) return;

        // Prevent browser context menu so right-click is dedicated to expanding/collapsing the source.
        e.preventDefault();

        const selected = window.getSelection ? window.getSelection().toString().trim() : '';
        if (selected) return;
        const isPptxPage = pageNode.matches('#pptx-viewer > [data-slide-index]');
        const parsedPage = isPptxPage
            ? (parseInt(pageNode.dataset?.slideIndex || '', 10) + 1)
            : parseInt(pageNode.getAttribute('data-page-number') || pageNode.dataset?.pageNumber || '', 10);
        const focusPage = Number.isFinite(parsedPage) && parsedPage > 0
            ? parsedPage
            : getMemoraCurrentSourcePageNumber();

        if ((els?.workArea || $('work-area'))?.classList.contains('pdf-tutor-view')) return;

        togglePdfPaneExpanded(true, focusPage);
    });

    container.addEventListener('keydown', (e) => {
        const k = String(e.key || '').toLowerCase();
        if (!k) return;

        if ((e.ctrlKey || e.metaKey) && (k === '=' || k === '+')) {
            e.preventDefault();
            if (memoraPptxViewerInstance) {
                void zoomMemoraPptxByFactor(PDF_KEYBTN_ZOOM_IN_STEP);
                return;
            }
            zoomAdvancedPdfByFactor(PDF_KEYBTN_ZOOM_IN_STEP);
            return;
        }
        if ((e.ctrlKey || e.metaKey) && k === '-') {
            e.preventDefault();
            if (memoraPptxViewerInstance) {
                void zoomMemoraPptxByFactor(PDF_KEYBTN_ZOOM_OUT_STEP);
                return;
            }
            zoomAdvancedPdfByFactor(PDF_KEYBTN_ZOOM_OUT_STEP);
            return;
        }
        if ((e.ctrlKey || e.metaKey) && k === '0') {
            e.preventDefault();
            if (memoraPptxViewerInstance) {
                void setMemoraPptxZoomPercent(100);
                return;
            }
            resetAdvancedPdfZoomToPageWidth({ userInitiated: true });
            return;
        }

        if (k === 'e') {
            e.preventDefault();
            e.stopPropagation();
            triggerEvidence();
            return;
        }
        if ((els?.workArea || $('work-area'))?.classList.contains('pdf-tutor-view')) {
            if (k === 'arrowright' || k === 'arrowleft') e.preventDefault();
            return;
        }
        if (k === 'arrowright') {
            e.preventDefault();
            nextQuestion();
            return;
        }
        if (k === 'arrowleft') {
            e.preventDefault();
            prevQuestion();
        }
    });
    return true;
}

function ensureAdvancedPdfViewer() {
    if (!canUseAdvancedPdfViewer()) return false;
    applyPdfJsSafetyPatches();
    if (pdfViewerInstance) return true;

    const container = $('pdfjs-host');
    const viewer = $('pdfjs-pages');
    if (!container || !viewer) return false;

    try {
        pdfViewerEventBus = new pdfjsViewer.EventBus();
        pdfViewerLinkService = new pdfjsViewer.PDFLinkService({ eventBus: pdfViewerEventBus });
        pdfViewerFindController = new pdfjsViewer.PDFFindController({
            eventBus: pdfViewerEventBus,
            linkService: pdfViewerLinkService
        });
        pdfViewerInstance = new pdfjsViewer.PDFViewer({
            container,
            viewer,
            eventBus: pdfViewerEventBus,
            linkService: pdfViewerLinkService,
            findController: pdfViewerFindController,
            textLayerMode: 2,
            annotationMode: 0,
            annotationEditorMode: -1,
            removePageBorders: false
        });
        pdfViewerLinkService.setViewer(pdfViewerInstance);
        pdfForceIframeMode = false;

        pdfViewerEventBus.on('pagesinit', () => {
            pdfViewerPagesReady = true;
            if (pdfViewerInstance && (pdfViewerScaleMode === 'page-fit' || pdfViewerScaleMode === 'page-width')) {
                pdfViewerInstance.currentScaleValue = pdfViewerScaleMode;
            }
            if (pdfViewerFileName === pdfEvidenceTargetFileName && Number.isFinite(pdfEvidenceTargetPage)) {
                setAdvancedPdfPage(pdfEvidenceTargetPage, false);
            }
            requestAnimationFrame(() => { clampAdvancedPdfScaleToBounds(); });
            updatePdfEvidencePageHighlight();
            updatePdfMiniToolbarState();
        });

        pdfViewerEventBus.on('pagesloaded', () => {
            pdfViewerPagesReady = true;
            updatePdfEvidencePageHighlight();
            updatePdfMiniToolbarState();
        });

        const notifyTutorPageRendered = (evt) => {
            const pageNumber = Math.max(1, Number(evt?.pageNumber) || 1);
            if (typeof window.notifyPdfTutorPageRendered === 'function') {
                try { window.notifyPdfTutorPageRendered(pageNumber); } catch (_err) { }
            }
        };
        const markPdfLoadedAfterPageRendered = (evt) => {
            notifyTutorPageRendered(evt);
            const pageNumber = Math.max(1, Number(evt?.pageNumber) || 1);
            const pageView = pdfViewerInstance?.getPageView?.(pageNumber - 1);
            if (pageView?.div && typeof window.renderPdfEvidenceStoredSelectionsForPage === 'function') {
                try { void window.renderPdfEvidenceStoredSelectionsForPage(pageView.div, pageNumber, pdfViewerFileName); } catch (_err) { }
            }
            const canvas = pageView?.div?.querySelector?.('canvas');
            const renderingState = Number(pageView?.renderingState);
            const pendingRequest = pdfPaneLatestRequest;
            const requestMatches = !pendingRequest
                || pendingRequest.status !== 'pending'
                || (pendingRequest.filename === pdfViewerFileName && Number(pendingRequest.page) === pageNumber);
            if (els?.pdfJsHost?.classList?.contains('pdf-pane-loading')
                && requestMatches
                && canvas
                && (!Number.isFinite(renderingState) || renderingState === 3)) {
                markPdfPagesLoadedAnimation();
            }
        };
        pdfViewerEventBus.on('pagerendered', markPdfLoadedAfterPageRendered);
        pdfViewerEventBus.on('textlayerrendered', notifyTutorPageRendered);

        pdfViewerEventBus.on('scalechanging', () => {
            if (pdfViewerInstance) {
                const scaleValue = String(pdfViewerInstance.currentScaleValue || '').toLowerCase();
                if (scaleValue === 'page-fit' || scaleValue === 'page-width') {
                    pdfViewerScaleMode = scaleValue;
                } else {
                    const numericScale = Number(pdfViewerInstance.currentScale);
                    if (Number.isFinite(numericScale) && numericScale > 0) {
                        pdfViewerScaleMode = numericScale;
                    }
                }
            }
            notifyTutorPdfZoomIfActive();
            if (clampAdvancedPdfScaleToBounds()) return;
            updatePdfMiniToolbarState();
        });

        pdfViewerEventBus.on('pagechanging', (evt) => {
            if (!pdfViewerFileName || !evt) return;
            const nextPage = Math.max(1, Number(evt.pageNumber) || 1);
            const title = pdfViewerFileName;
            if (els?.pdfTitle) els.pdfTitle.innerText = title;
            if (typeof lastPdfState === 'object' && lastPdfState) {
                lastPdfState = {
                    ...lastPdfState,
                    src: loadedPdfFiles[pdfViewerFileName] || '',
                    title,
                    fileName: pdfViewerFileName,
                    page: nextPage
                };
            }
            updatePdfEvidencePageHighlight();
            updatePdfMiniToolbarState();
            if (typeof maybeAutoAdvanceGuidedStudyFromPdfPage === 'function') {
                maybeAutoAdvanceGuidedStudyFromPdfPage(nextPage, pdfViewerFileName);
            }
        });

        bindMemoraSourceViewerContainerHandlers(container);

        if (window.ResizeObserver) {
            pdfViewerResizeObserver = new ResizeObserver(() => {
                if (!pdfViewerInstance || !pdfViewerPagesReady) return;
                if (window.isPaneLayoutTransitioning?.()) {
                    window.whenPaneLayoutSettled?.(runPdfViewerLayoutSettlePass);
                    return;
                }
                syncPdfPaneHeaderHeight();
                if (scheduleAdvancedPdfResponsiveScaleRefresh({ preservePage: true })) {
                    return;
                }
                clampAdvancedPdfScaleToBounds();
                updatePdfMiniToolbarState();
                scheduleAdvancedPdfVisiblePageRenderRecovery();
            });
            pdfViewerResizeObserver.observe(container);
            const pane = els?.pdfPane || $('pdf-pane');
            const header = $('pdf-header');
            if (pane) pdfViewerResizeObserver.observe(pane);
            if (header) pdfViewerResizeObserver.observe(header);
        }

        setupPdfMiniToolbar();
        return true;
    } catch (err) {
        console.error('Failed to initialize advanced PDF viewer:', err);
        pdfViewerEventBus = null;
        pdfViewerLinkService = null;
        pdfViewerFindController = null;
        pdfViewerInstance = null;
        pdfForceIframeMode = PDF_ENABLE_IFRAME_RUNTIME_FALLBACK;
        return false;
    }
}

function openPdfInIframeFallback(filename, { page = 1, searchTerm = '', smooth = true, request = null } = {}) {
    if (!filename || !loadedPdfFiles[filename] || !els?.pdfFrame) {
        settlePdfPaneRequest(request, 'failed');
        return false;
    }
    const blobUrl = loadedPdfFiles[filename];
    const safePage = Math.max(1, Number(page) || 1);
    const q = String(searchTerm || '').trim();
    let fragment = `#page=${safePage}&view=Fit`;
    if (q) fragment += `&search=${encodeURIComponent(q)}&phrase=true`;
    const targetSrc = `${blobUrl}${fragment}`;

    setPdfPaneMode('pdf');
    const host = els?.pdfJsHost || $('pdfjs-host');
    const frame = els.pdfFrame;
    if (host) {
        setAdvancedPdfPaneLoadingState('clear');
        host.style.display = 'none';
    }
    frame.style.display = 'block';
    frame.removeAttribute('src');
    // Observability contract for the fallback. src is assigned inside the timeout below,
    // and it always carries a #page/&search fragment, so a bare blob URL can never equal
    // it - and equality would not prove the frame finished loading anyway. This attribute
    // appears only on load, and carries the URL that actually loaded.
    frame.removeAttribute('data-memora-pdf-loaded');
    setTimeout(() => {
        frame.src = targetSrc;
        frame.onload = (event) => {
            try { frame.setAttribute('data-memora-pdf-loaded', targetSrc); } catch (_err) { }
            settlePdfPaneRequest(request, 'completed');
            injectIframeHotkeys(event);
        };
    }, smooth ? 100 : 0);
    currentPdfBlobUrl = blobUrl;
    updatePdfMiniToolbarState();
    return true;
}

async function openPdfWithAdvancedViewer(
    filename,
    { page = 1, searchTerm = '', smooth = true, maintainScale = false, forceReload = false, recoveryAttempt = 0 } = {}
) {
    if (!filename || !loadedPdfFiles[filename]) return false;
    const safePage = Math.max(1, Number(page) || 1);
    markPdfPaneLoading(safePage, 'page');
    try {
        await window.MemoraRuntime.ensurePdfRuntimeLoaded();
    } catch (error) {
        console.warn('PDF viewer runtime could not be loaded:', error);
        setAdvancedPdfPaneLoadingState('error', 'The document viewer could not load. Try again.');
        return false;
    }
    if (!ensureAdvancedPdfViewer()) {
        setAdvancedPdfPaneLoadingState('error', 'The document viewer could not start. Try again.');
        return false;
    }

    // Source highlighting is an interaction on the PDF.js surface, not an
    // Ask-only intent. Load the lazy AI interaction owner as soon as the
    // viewer is ready so a user can drag immediately after the page appears.
    if (typeof window.ensureMemoraFeature === 'function') {
        void window.ensureMemoraFeature('ai');
    }

    const blobUrl = loadedPdfFiles[filename];
    const query = String(searchTerm || '').trim();
    const token = ++pdfViewerOpenToken;

    try {
        setPdfPaneMode('pdf');

        const emptyState = els?.pdfEmptyState || $('pdf-empty-state');
        if (emptyState) emptyState.style.display = 'none';
        const pagesEl = els?.pdfJsPages || $('pdfjs-pages');
        if (pagesEl) pagesEl.style.display = 'block';

        await waitForPdfPaneLayoutReady(1, 12);
        if (token !== pdfViewerOpenToken) return false;
        refreshAdvancedPdfViewerLayout();
        currentPdfBlobUrl = blobUrl;

        const needsLoad = !!forceReload || !pdfViewerDoc || pdfViewerFileName !== filename;
        const workArea = els?.workArea || $('work-area');
        const shouldForceFitPageForDuoEvidence = !!(
            !isPdfPaneExpanded
            && workArea?.classList?.contains('split-view')
            && !workArea?.classList?.contains('trio-view')
            && !workArea?.classList?.contains('split-view-ai')
        );
        const shouldForceFitPageForOpen = !!(
            !isPdfPaneExpanded
            && (
                workArea?.classList?.contains('trio-view')
                || workArea?.classList?.contains('split-view-ai')
            )
        );
        if (needsLoad) {
            pdfViewerPagesReady = false;
            if (pdfViewerLoadingTask && typeof pdfViewerLoadingTask.destroy === 'function') {
                try { await pdfViewerLoadingTask.destroy(); } catch (_err) { }
                if (token !== pdfViewerOpenToken) return false;
            }
            // The incoming task is not owned by the old viewer being reset below.
            const incomingTask = getPdfJsDocumentWithReadbackCanvas(blobUrl);
            const doc = await incomingTask.promise;
            if (token !== pdfViewerOpenToken) {
                try { await doc.destroy(); } catch (_err) { }
                return false;
            }
            // Recreate the viewer shell on every new document to avoid PDF.js doc-swap
            // teardown crashes (AnnotationEditorUIManager.destroy null access).
            hardResetAdvancedPdfViewer();
            if (token !== pdfViewerOpenToken) {
                try { await doc.destroy(); } catch (_err) { }
                return false;
            }
            markPdfPaneLoading(safePage, 'page');
            if (!ensureAdvancedPdfViewer()) {
                try { await doc.destroy(); } catch (_err) { }
                setAdvancedPdfPaneLoadingState('error', 'The document viewer could not start. Try again.');
                return false;
            }
            pdfViewerPagesReady = false;
            pdfViewerLoadingTask = incomingTask;
            pdfViewerDoc = doc;
            pdfViewerFileName = filename;
            if (shouldForceFitPageForDuoEvidence || shouldForceFitPageForOpen || !maintainScale) {
                // PDF.js reads this mode while it builds the first page viewport.
                // Set it before setDocument so the initial paint is page-fitted.
                pdfViewerScaleMode = 'page-fit';
                pdfViewerInstance.currentScaleValue = 'page-fit';
            }
            pdfViewerInstance.setDocument(doc);
            pdfViewerLinkService.setDocument(doc, null);
        }

        const pagesReady = await waitForAdvancedPdfPagesReady(token);
        if (token !== pdfViewerOpenToken || !pdfViewerInstance) return false;
        if (!pagesReady) {
            console.warn('Advanced PDF pages readiness timed out; continuing with best-effort render.');
        }
        refreshAdvancedPdfViewerLayout();

        const maxPage = Math.max(1, Number(pdfViewerDoc?.numPages) || safePage);
        const targetPage = Math.min(maxPage, safePage);
        const shouldResetScaleForOpen = shouldForceFitPageForDuoEvidence || shouldForceFitPageForOpen || !maintainScale;
        if (shouldResetScaleForOpen) {
            if (shouldForceFitPageForDuoEvidence) {
                await applyPdfEvidenceDefaultScale(targetPage, {
                    filename,
                    docOverride: pdfViewerDoc
                });
                if (token !== pdfViewerOpenToken || !pdfViewerInstance) return false;
            } else if (shouldForceFitPageForOpen) {
                setAdvancedPdfScaleMode('page-fit', { preservePageNumber: targetPage });
            } else {
                pdfViewerInstance.currentScaleValue = 'page-fit';
                pdfViewerScaleMode = 'page-fit';
            }
        }
        await new Promise((resolve) => requestAnimationFrame(resolve));
        if (token !== pdfViewerOpenToken || !pdfViewerInstance) return false;
        clampAdvancedPdfScaleToBounds();
        setAdvancedPdfPage(targetPage, smooth);
        updatePdfEvidencePageHighlight();
        const canvasReady = await waitForAdvancedPdfPageCanvas(targetPage, token, 3800);
        if (token !== pdfViewerOpenToken || !pdfViewerInstance) return false;
        if (!canvasReady) {
            console.warn(`PDF page ${targetPage} did not finish rendering.`);
            setAdvancedPdfPaneLoadingState('error', 'This page could not finish rendering. Try again.');
            return false;
        }
        markPdfPagesLoadedAnimation();
        refreshAdvancedPdfViewerLayout();
        if (token === pdfViewerOpenToken && pdfViewerInstance && pdfViewerDoc) {
            setAdvancedPdfPage(targetPage, false);
        }
        scheduleAdvancedPdfResponsiveScaleRefresh({ preservePage: true, preservePageNumber: targetPage });

        if (query) applyAdvancedPdfSearch(query);
        else applyAdvancedPdfSearch('');

        updatePdfMiniToolbarState();
        return true;
    } catch (err) {
        if (token !== pdfViewerOpenToken) return false;
        console.error('Advanced PDF open failed:', err);

        if (recoveryAttempt < 2) {
            hardResetAdvancedPdfViewer();
            return openPdfWithAdvancedViewer(filename, {
                page: safePage,
                searchTerm: query,
                smooth,
                maintainScale,
                forceReload,
                recoveryAttempt: recoveryAttempt + 1
            });
        }

        hardResetAdvancedPdfViewer();
        if (PDF_ENABLE_IFRAME_RUNTIME_FALLBACK) {
            return openPdfInIframeFallback(filename, { page: safePage, searchTerm: query, smooth });
        }
        setPdfPaneMode('pdf');
        setAdvancedPdfPaneLoadingState('error', 'This document could not be displayed. Try again.');
        return false;
    }
}

function getPreferredPdfForRestore() {
    if (lastPdfState && typeof lastPdfState.fileName === 'string' && loadedPdfFiles[lastPdfState.fileName]) {
        return lastPdfState.fileName;
    }
    const fromTitle = normalizePdfStateTitleToName(lastPdfState?.title || '');
    if (fromTitle && loadedPdfFiles[fromTitle]) return fromTitle;
    if (currentPdfBlobUrl) {
        const byBlob = Object.keys(loadedPdfFiles).find((name) => loadedPdfFiles[name] === currentPdfBlobUrl);
        if (byBlob) return byBlob;
    }
    const loaded = Object.keys(loadedPdfFiles || {});
    return loaded.length > 0 ? loaded[0] : '';
}

function loadMemoraPptxInPane(
    filename,
    { page = 1, searchTerm = '', smooth = true, ensureViewer = true } = {}
) {
    if (!filename || !loadedPdfFiles[filename] || !isMemoraPptxSourceName(filename)) return false;
    const safePage = Math.max(1, Number(page) || 1);
    const query = String(searchTerm || '').trim();
    const blobUrl = loadedPdfFiles[filename];
    if (els?.pdfTitle) els.pdfTitle.innerText = filename;
    currentPdfBlobUrl = blobUrl;
    const popoutBtn = $('popout-pdf-btn');
    if (popoutBtn) {
        popoutBtn.onclick = () => {
            setPdfMoreMenuOpen(false);
            return currentPdfBlobUrl ? window.open(currentPdfBlobUrl, '_blank') : showPopupText('No PowerPoint is open.', null, true);
        };
    }
    if (lastPdfState) {
        lastPdfState = {
            ...lastPdfState,
            src: blobUrl,
            title: filename,
            fileName: filename,
            page: safePage,
            searchTerm: query
        };
    }
    const openingViewerForRequest = !!(ensureViewer && !isViewerOpen);
    markPdfPaneLoading(safePage, 'slide');
    if (openingViewerForRequest) toggleViewer(true, { skipRestoreLoad: true });
    setPdfPaneMode('pdf');

    const reusableViewer = memoraPptxViewerInstance;
    const canReuseViewer = !!reusableViewer
        && memoraPptxViewerFileName === filename
        && !query;
    if (canReuseViewer) {
        const host = els?.pdfJsHost || $('pdfjs-host');
        const container = els?.pptxViewer || $('pptx-viewer');
        const pages = els?.pdfJsPages || $('pdfjs-pages');
        const frame = els?.pdfFrame || $('pdf-frame');
        if (frame) frame.style.display = 'none';
        if (pages) pages.style.display = 'none';
        if (host) host.style.display = 'block';
        if (container) container.style.display = 'block';

        const navigationRequestId = Math.max(
            0,
            Number(reusableViewer._memoraSourceNavigationRequestId) || 0
        ) + 1;
        reusableViewer._memoraSourceNavigationRequestId = navigationRequestId;
        const workArea = els?.workArea || $('work-area');
        const isDuoPdfLayout = !!(
            workArea?.classList?.contains('split-view')
            && !workArea?.classList?.contains('trio-view')
            && !workArea?.classList?.contains('split-view-ai')
        );
        if (isDuoPdfLayout) fitMemoraPptxViewerToPage();
        const applyRequestedSlide = async () => {
            if (memoraPptxViewerInstance !== reusableViewer
                || memoraPptxViewerFileName !== filename
                || reusableViewer._memoraSourceNavigationRequestId !== navigationRequestId) return false;
            const moved = await setMemoraPptxSlide(safePage, false);
            if (memoraPptxViewerInstance !== reusableViewer
                || reusableViewer._memoraSourceNavigationRequestId !== navigationRequestId) return false;
            if (moved) {
                markPdfPagesLoadedAnimation();
            } else {
                setAdvancedPdfPaneLoadingState('error', 'This page could not finish rendering. Try again.');
            }
            updatePdfEvidencePageHighlight();
            updatePdfMiniToolbarState();
            return moved;
        };

        // Move immediately so a quick E press on the next question cannot keep
        // showing the previous citation. If reopening the pane, reassert the
        // same target after its animation so a resize cannot undo the jump.
        void applyRequestedSlide();
        if (openingViewerForRequest) {
            void waitForPdfPaneLayoutReady(40, 48).then(() => {
                if (isDuoPdfLayout) fitMemoraPptxViewerToPage();
                return applyRequestedSlide();
            });
        }
        return true;
    }

    void openMemoraPptxWithBrowserViewer(filename, {
        page: safePage,
        searchTerm: query,
        smooth
    });
    return true;
}

/* --- OWNED PDF PANE REQUEST COMPLETION CHANNEL ---
   loadPdfInPane is, and stays, a synchronous dispatcher: its boolean says the request
   was accepted, never that the viewer finished showing it. Observers previously had to
   infer completion by sniffing shared viewer bindings (pdfViewerFileName / pdfViewerDoc)
   and the iframe src, all of which are written long before the page, scale and search
   work finishes. This channel is additive - no caller signature changes - and gives one
   dispatched request its own settled result that an observer can await. */
let pdfPaneRequestSeq = 0;
let pdfPaneLatestRequest = null;
const pdfPaneRequestsById = new Map();
const PDF_PANE_REQUEST_HISTORY = 8;

function snapshotPdfPaneRequest(record) {
    if (!record) return null;
    return Object.freeze({
        id: record.id,
        filename: record.filename,
        page: record.page,
        searchTerm: record.searchTerm,
        status: record.status
    });
}

function settlePdfPaneRequest(record, status) {
    if (!record || record.status !== 'pending') return;
    // A result that arrives for a request the pane no longer owns is never success.
    record.status = pdfPaneLatestRequest === record ? status : 'superseded';
    const resolveCompletion = record.resolveCompletion;
    record.resolveCompletion = null;
    if (resolveCompletion) resolveCompletion(snapshotPdfPaneRequest(record));
}

function beginPdfPaneRequest(filename, { page = 1, searchTerm = '' } = {}) {
    const previous = pdfPaneLatestRequest;
    const record = {
        id: ++pdfPaneRequestSeq,
        filename: String(filename || ''),
        page: Math.max(1, Number(page) || 1),
        searchTerm: String(searchTerm || '').trim(),
        status: 'pending',
        resolveCompletion: null
    };
    record.completion = new Promise((resolve) => { record.resolveCompletion = resolve; });
    pdfPaneLatestRequest = record;
    pdfPaneRequestsById.set(record.id, record);
    while (pdfPaneRequestsById.size > PDF_PANE_REQUEST_HISTORY) {
        const oldest = pdfPaneRequestsById.keys().next().value;
        if (oldest === undefined) break;
        pdfPaneRequestsById.delete(oldest);
    }
    // Settle whatever the previous dispatch was still waiting on, so its observer gets a
    // definite answer instead of hanging until its own timeout policy fires.
    if (previous) settlePdfPaneRequest(previous, 'superseded');
    return record;
}

window.MemoraPdfPaneRequests = Object.freeze({
    version: 1,
    latest: () => snapshotPdfPaneRequest(pdfPaneLatestRequest),
    get: (requestId) => snapshotPdfPaneRequest(pdfPaneRequestsById.get(Number(requestId)) || null),
    awaitCompletion(requestId) {
        const record = pdfPaneRequestsById.get(Number(requestId));
        if (!record) return Promise.resolve(null);
        return record.completion;
    }
});

function loadPdfInPane(
    filename,
    {
        page = 1,
        searchTerm = '',
        smooth = true,
        ensureViewer = true,
        maintainScale = false,
        forceReload = false
    } = {}
) {
    if (!filename || !loadedPdfFiles[filename]) return false;
    if ((ensureViewer || isViewerOpen) && window.isAskMemoraDocked?.()) {
        window.revealAskMemoraForSourceEvidence?.();
    }
    if (isMemoraPptxSourceName(filename)) {
        return loadMemoraPptxInPane(filename, {
            page,
            searchTerm,
            smooth,
            ensureViewer
        });
    }
    destroyMemoraPptxViewer();
    const safePage = Math.max(1, Number(page) || 1);
    const query = String(searchTerm || '').trim();
    const blobUrl = loadedPdfFiles[filename];
    const paneRequest = beginPdfPaneRequest(filename, { page: safePage, searchTerm: query });

    // PDF.js cannot XHR a file:// URL. In the standalone Editorial prototype,
    // use the browser's native PDF frame for that launch mode so the real
    // lecture still appears when the page is opened directly from disk.
    if (IS_EMBED_DEMO && /^file:/i.test(String(blobUrl || ''))) {
        pdfForceIframeMode = true;
    }

    const title = filename;
    if (els?.pdfTitle) els.pdfTitle.innerText = title;
    currentPdfBlobUrl = blobUrl;
    const popoutBtn = $('popout-pdf-btn');
    if (popoutBtn) {
        popoutBtn.onclick = () => {
            setPdfMoreMenuOpen(false);
            return currentPdfBlobUrl ? window.open(currentPdfBlobUrl, '_blank') : showPopupText('No PDF is open.', null, true);
        };
    }
    if (typeof lastPdfState === 'object' && lastPdfState) {
        lastPdfState = {
            ...lastPdfState,
            src: blobUrl,
            title,
            fileName: filename,
            page: safePage,
            searchTerm: query
        };
    }

    const openedViewerForRequest = !!(ensureViewer && !isViewerOpen);
    if (openedViewerForRequest && pdfViewerInstance && pdfViewerDoc && pdfViewerFileName === filename) {
        const targetPage = Math.min(Math.max(1, Number(pdfViewerDoc.numPages) || 1), safePage);
        setAdvancedPdfPage(targetPage, false);
    }

    markPdfPaneLoading(safePage, 'page');

    if (openedViewerForRequest) {
        toggleViewer(true, { skipRestoreLoad: true });
    }

    setPdfPaneMode('pdf');

    if (shouldUseIframeRenderer()) {
        return openPdfInIframeFallback(filename, { page: safePage, searchTerm: query, smooth, request: paneRequest });
    }

    const reusablePdfViewer = pdfViewerInstance;
    const canReusePdfViewer = !!reusablePdfViewer
        && !!pdfViewerDoc
        && pdfViewerFileName === filename
        && !forceReload
        && !query;
    if (canReusePdfViewer) {
        const targetPage = Math.min(
            Math.max(1, Number(pdfViewerDoc.numPages) || 1),
            safePage
        );
        const workArea = els?.workArea || $('work-area');
        const isDuoPdfLayout = !!(
            workArea?.classList?.contains('split-view')
            && !workArea?.classList?.contains('trio-view')
            && !workArea?.classList?.contains('split-view-ai')
        );
        const navigationOpenToken = ++pdfViewerOpenToken;
        reusablePdfViewer._memoraSourceNavigationRequestId = navigationOpenToken;

        const requestStillCurrent = () => (
            pdfViewerOpenToken === navigationOpenToken
            && pdfViewerInstance === reusablePdfViewer
            && pdfViewerFileName === filename
            && reusablePdfViewer._memoraSourceNavigationRequestId === navigationOpenToken
        );
        const revealRequestedPage = async () => {
            if (!requestStillCurrent()) return 'superseded';
            if (isDuoPdfLayout) {
                await syncPdfScaleForCurrentLayoutModeSwitch();
                if (!requestStillCurrent()) return 'superseded';
            }
            setAdvancedPdfPage(targetPage, false);
            const canvasReady = await waitForAdvancedPdfPageCanvas(targetPage, navigationOpenToken, 3800);
            if (!requestStillCurrent()) return 'superseded';
            setAdvancedPdfPage(targetPage, false);
            if (!canvasReady) {
                console.warn(`PDF page ${targetPage} did not finish rendering during pane reuse.`);
                setAdvancedPdfPaneLoadingState('error', 'This page could not finish rendering. Try again.');
                return 'failed';
            }
            markPdfPagesLoadedAnimation();
            updatePdfEvidencePageHighlight();
            updatePdfMiniToolbarState();
            scheduleAdvancedPdfResponsiveScaleRefresh({
                preservePage: true,
                preservePageNumber: targetPage
            });
            return 'completed';
        };

        // Supersede any prior page-open pipeline immediately. When this click
        // reopens the pane, reassert the same page after its layout animation.
        setAdvancedPdfPage(targetPage, false);
        void revealRequestedPage().then((status) => {
            settlePdfPaneRequest(paneRequest, status);
        }, (error) => {
            console.warn('PDF pane reuse could not reveal the requested page:', error);
            settlePdfPaneRequest(paneRequest, 'failed');
        });
        if (openedViewerForRequest) {
            void waitForPdfPaneLayoutReady(40, 48).then(async () => {
                if (!requestStillCurrent()) return;
                if (isDuoPdfLayout) {
                    await syncPdfScaleForCurrentLayoutModeSwitch();
                    if (!requestStillCurrent()) return;
                }
                setAdvancedPdfPage(targetPage, false);
                scheduleAdvancedPdfResponsiveScaleRefresh({
                    preservePage: true,
                    preservePageNumber: targetPage
                });
            });
        }
        return true;
    }

    const openAdvancedViewer = () => {
        if (ensureAdvancedPdfViewer()) {
            void openPdfWithAdvancedViewer(filename, {
                page: safePage,
                searchTerm: query,
                smooth,
                maintainScale,
                forceReload: !!forceReload
            }).then((shown) => {
                settlePdfPaneRequest(paneRequest, shown ? 'completed' : 'failed');
            }, (error) => {
                console.warn('Advanced PDF open rejected:', error);
                settlePdfPaneRequest(paneRequest, 'failed');
            });
            return true;
        }
        if (PDF_ENABLE_IFRAME_RUNTIME_FALLBACK) {
            return openPdfInIframeFallback(filename, { page: safePage, searchTerm: query, smooth, request: paneRequest });
        }
        setAdvancedPdfPaneLoadingState('error', 'The document viewer could not start. Try again.');
        return false;
    };

    if (canUseAdvancedPdfViewer()) {
        // Terminal here: nothing downstream can still serve this request.
        const opened = openAdvancedViewer();
        if (!opened) settlePdfPaneRequest(paneRequest, 'failed');
        return opened;
    }

    const deferredOpenToken = ++pdfViewerOpenToken;
    void window.MemoraRuntime.ensurePdfRuntimeLoaded()
        .then(() => {
            if (deferredOpenToken !== pdfViewerOpenToken) return settlePdfPaneRequest(paneRequest, 'superseded');
            if (!loadedPdfFiles[filename] || loadedPdfFiles[filename] !== blobUrl) return settlePdfPaneRequest(paneRequest, 'failed');
            setPdfPaneMode('pdf');
            if (!openAdvancedViewer()) {
                openPdfInIframeFallback(filename, { page: safePage, searchTerm: query, smooth, request: paneRequest });
            }
        })
        .catch((error) => {
            if (deferredOpenToken !== pdfViewerOpenToken) return settlePdfPaneRequest(paneRequest, 'superseded');
            console.warn('PDF viewer runtime could not be loaded; using browser PDF fallback.', error);
            openPdfInIframeFallback(filename, { page: safePage, searchTerm: query, smooth, request: paneRequest });
        });
    return true;
}

function extractSlideNumberFromExplanationHeader(text) {
    const match = String(text || '').match(/\bslide\s*(\d+)\b/i);
    if (!match) return null;
    const parsed = parseInt(match[1], 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

let aiExplanationSlideHighlightTimer = null;

function getAiExplanationStickyHeaderOffset() {
    const shell = $('ai-expl-reader-shell');
    let offset = 10;
    if (!(shell instanceof HTMLElement) || !shell.closest('#ai-expl-content')) return offset;

    const shellRect = shell.getBoundingClientRect();
    const shellStyles = window.getComputedStyle ? getComputedStyle(shell) : null;
    const shellMarginBottom = shellStyles ? parseFloat(shellStyles.marginBottom || '0') : 0;
    offset += Math.max(0, shellRect.height);
    offset += Number.isFinite(shellMarginBottom) ? Math.max(0, shellMarginBottom) : 0;
    return Math.round(offset);
}

function scrollAiExplanationToSlide(slideNumber, smooth = true) {
    const content = $('ai-expl-content');
    const view = $('ai-expl-view');
    if (!content || !view || !Number.isFinite(slideNumber) || slideNumber < 1) {
        return false;
    }

    const targets = content.querySelectorAll('.dd-section-header, h1, h2, h3, h4, h5');
    let target = null;
    targets.forEach((node) => {
        if (target) return;
        const found = extractSlideNumberFromExplanationHeader(node.textContent);
        if (found === slideNumber) target = node;
    });

    if (!target) return false;

    content.querySelectorAll('.ai-expl-slide-target').forEach((node) => node.classList.remove('ai-expl-slide-target'));
    const highlightNode = target.closest('.ai-expl-slide-card') || target;
    highlightNode.classList.add('ai-expl-slide-target');
    const viewRect = view.getBoundingClientRect();
    const highlightRect = highlightNode.getBoundingClientRect();
    const stickyOffset = getAiExplanationStickyHeaderOffset();
    const maxScrollTop = Math.max(0, view.scrollHeight - view.clientHeight);
    const nextScrollTop = clampNum(
        view.scrollTop + (highlightRect.top - viewRect.top) - stickyOffset,
        0,
        maxScrollTop
    );
    view.scrollTo({ top: nextScrollTop, behavior: smooth ? 'smooth' : 'auto' });

    if (aiExplanationSlideHighlightTimer) {
        clearTimeout(aiExplanationSlideHighlightTimer);
        aiExplanationSlideHighlightTimer = null;
    }

    return true;
}
window.getAiExplanationStickyHeaderOffset = getAiExplanationStickyHeaderOffset;

function ensurePdfExplanationDuoLayout() {
    const workArea = els?.workArea || $('work-area');
    if (!workArea) return false;
    if (workArea.classList.contains('split-view-ai')) return true;

    if (workArea.classList.contains('trio-view') || isTrioMode) {
        if (typeof closePdfInTrio === 'function') {
            closePdfInTrio();
            return true;
        }
        window.markPaneCloseTransitioning?.('pdf');
        workArea.classList.remove('trio-view');
    }

    if (workArea.classList.contains('split-view')) {
        window.markPaneCloseTransitioning?.('pdf');
    }
    window.markPaneOpenTransitioning?.({ targetLayout: 'duo' });
    workArea.classList.remove('split-view', 'trio-view');
    workArea.classList.add('split-view-ai');
    isTrioMode = false;
    isViewerOpen = false;
    if (typeof resetPanelResizeColumnsForModeSwitch === 'function') resetPanelResizeColumnsForModeSwitch();
    if (typeof setViewerToggleLabel === 'function') setViewerToggleLabel('Show PDF');
    if (typeof setTrioToggleLabel === 'function') setTrioToggleLabel('\u2AF6 Trio');
    if (typeof applyPanelOrderLayout === 'function') applyPanelOrderLayout();
    if (typeof scheduleQuizCardPositionReset === 'function') {
        scheduleQuizCardPositionReset({ forceLayoutDefault: true, layoutMode: 'duo' });
    }
    return true;
}

async function openPdfExplanationForCurrentQuestion({ silent = false, smooth = true, ensureVisibleAiPane = true, preferDuoAiPane = false, preferTrioLayout = false } = {}) {
    if (!isMemoraPdfExplanationFeatureEnabledForCore()) return false;
    const q = questions[filteredIndices[currentQIndex]];
    if (!q) return false;
    const expectedRealIdx = filteredIndices[currentQIndex];

    const targetPdf = findBestPdfForQuestion(q);
    if (!targetPdf || (!loadedPdfFiles[targetPdf] && !(sessionManifest?.pdfs || []).includes(targetPdf))) {
        if (!silent) showPopup('No linked PDF found for this question.', null, true);
        return false;
    }

    const citation = getQuestionCitationInfo(q);
    const targetSlide = citation.page || 1;

    if (ensureVisibleAiPane && preferTrioLayout) {
        ensureAutoStudyTrioLayout();
        await waitForPdfPaneLayoutReady();
        if (filteredIndices[currentQIndex] !== expectedRealIdx) return false;
    }

    let inSplitAiLayout = els.workArea.classList.contains('split-view-ai');
    let inTrioLayout = isTrioMode || els.workArea.classList.contains('trio-view');
    if (ensureVisibleAiPane && preferDuoAiPane && !inSplitAiLayout && !inTrioLayout) {
        ensurePdfExplanationDuoLayout();
        await waitForPdfPaneLayoutReady();
        if (filteredIndices[currentQIndex] !== expectedRealIdx) return false;
    }

    inSplitAiLayout = els.workArea.classList.contains('split-view-ai');
    inTrioLayout = isTrioMode || els.workArea.classList.contains('trio-view');
    if (!inTrioLayout && !inSplitAiLayout && ensureVisibleAiPane) {
        ensurePdfExplanationDuoLayout();
        await waitForPdfPaneLayoutReady();
        if (filteredIndices[currentQIndex] !== expectedRealIdx) return false;
    }

    inSplitAiLayout = els.workArea.classList.contains('split-view-ai');
    inTrioLayout = isTrioMode || els.workArea.classList.contains('trio-view');
    if (!inTrioLayout && !inSplitAiLayout && !ensureVisibleAiPane) {
        return false;
    }

    if (filteredIndices[currentQIndex] !== expectedRealIdx) return false;
    await syncAiPaneModeView(AI_PANE_MODE_PDF_EXPLANATION, targetPdf);
    if (filteredIndices[currentQIndex] !== expectedRealIdx) return false;
    await new Promise((resolve) => requestAnimationFrame(resolve));

    const moved = scrollAiExplanationToSlide(targetSlide, smooth);
    if (!moved && !silent) {
        showPopupText(`Opened PDF explanation for ${targetPdf}. Slide ${targetSlide} heading not found in this explanation.`, null, false);
    }
    return moved;
}
window.openPdfExplanationForCurrentQuestion = openPdfExplanationForCurrentQuestion;

function updateLiveStats() {
    // 1. Completion
    const completion = getQuizCompletionProgressState();
    const compPct = completion.completionPercent;
    $('comp-val').innerText = `${compPct}%`;

    // 2. Accuracy
    let correctCount = 0;
    let answeredCount = 0;

    // Calculate based on ALL answered questions in the current set
    filteredIndices.forEach(idx => {
        if (userAnswers[idx] !== undefined && isQuestionAnswerKeyVerified(questions[idx])) {
            answeredCount++;
            if (userAnswers[idx] === questions[idx].a) {
                correctCount++;
            }
        }
    });

    // A 100% default reads as "perfect score" rather than "nothing answered
    // yet" -- especially on the marketing embed, where it is the first
    // number a visitor reads (MD-10). Show an em dash until there is a real
    // answer to measure.
    $('acc-val').innerText = answeredCount > 0
        ? `${Math.round((correctCount / answeredCount) * 100)}%`
        : '—';

    // Color code accuracy
    const accEl = $('acc-val');
    // Note: We are now using gradient text for the value, so we don't override color here
    // unless we want to break the theme. Let's keep the theme gradient for consistency.

    // 3. Streak Display
    $('streak-val').innerText = currentStreak;
}

function toggleStatsBar(hide) {
    if (hide) {
        $('live-stats-bar').classList.add('is-hidden');
        $('stats-collapsed-btn').style.display = 'flex';
    } else {
        $('live-stats-bar').classList.remove('is-hidden');
        $('stats-collapsed-btn').style.display = 'none';
    }
    document.body.classList.toggle('stats-bar-open', !hide);
}

/* ============================================================================
   SECTION 8: QUIZ RENDERING & ANSWER HANDLING
   renderQuestion, handleAnswer, navigation, results
   ============================================================================ */
function getQuestionGenerationModel(q) {
    if (!q || typeof q !== 'object') return '';

    const directModel = [
        q.genModel,
        q.generationModel,
        q.generatedModel,
        q.aiModel,
        q.model,
        q.modelName,
        q.quizModel
    ].find((v) => typeof v === 'string' && v.trim().length > 0);

    if (directModel) return directModel.trim();

    const src = (q.sourceFile || '').trim();
    if (!src) return '';

    const srcBase = src.replace(/\.pdf$/i, '').replace(/\.json$/i, '').replace(/\.txt$/i, '');
    const candidateKeys = [src, `${srcBase}_quiz.json`, `${srcBase}_quiz`, srcBase];

    for (const key of candidateKeys) {
        const set = loadedQuestionSets[key];
        if (!Array.isArray(set) || set.length === 0) continue;
        const sample = set.find((item) => item && typeof item === 'object') || set[0];
        const setModel = [
            sample.genModel,
            sample.generationModel,
            sample.generatedModel,
            sample.aiModel,
            sample.model,
            sample.modelName,
            sample.quizModel
        ].find((v) => typeof v === 'string' && v.trim().length > 0);
        if (setModel) return setModel.trim();
    }

    return '';
}

function autoScrollQuizPaneToChoices() {
    const workArea = els?.workArea || $('work-area');
    const quizPane = $('quiz-pane');
    if (!workArea || !quizPane || isFlashcardMode) return;

    const inDuoOrTrioLayout = workArea.classList.contains('split-view')
        || workArea.classList.contains('split-view-ai')
        || workArea.classList.contains('trio-view');
    if (!inDuoOrTrioLayout) return;

    const choiceButtons = els?.choices
        ? Array.from(els.choices.querySelectorAll('button.choice'))
        : [];
    if (!choiceButtons.length) return;

    // Start each rendered question from the top of the quiz pane.
    quizPane.scrollTop = 0;

    const realIdx = Array.isArray(filteredIndices) && Number.isInteger(filteredIndices[currentQIndex])
        ? filteredIndices[currentQIndex]
        : -1;

    let hasPreexistingAiHintOrExplanation = false;
    if (Number.isInteger(realIdx) && realIdx >= 0 && typeof aiQuestionCacheByIndex === 'object' && aiQuestionCacheByIndex) {
        const cached = aiQuestionCacheByIndex[String(realIdx)];
        const hasExplanation = typeof cached?.explanation === 'string' && cached.explanation.trim().length > 0;
        const hasHint = typeof cached?.hint === 'string' && cached.hint.trim().length > 0;
        const hasDeepDive = typeof cached?.deepDive === 'string' && cached.deepDive.trim().length > 0;
        hasPreexistingAiHintOrExplanation = hasExplanation || hasHint || hasDeepDive;
    }

    if (!hasPreexistingAiHintOrExplanation && typeof getAICacheKey === 'function' && typeof aiResponseCache === 'object' && aiResponseCache) {
        const currentCacheKey = getAICacheKey(currentQIndex);
        const cacheMatchesCurrentQuestion = !!currentCacheKey && aiResponseCache.cacheKey === currentCacheKey;
        if (cacheMatchesCurrentQuestion) {
            const hasExplanation = typeof aiResponseCache.explanation === 'string' && aiResponseCache.explanation.trim().length > 0;
            const hasHint = typeof aiResponseCache.hint === 'string' && aiResponseCache.hint.trim().length > 0;
            const hasDeepDive = typeof aiResponseCache.deepDive === 'string' && aiResponseCache.deepDive.trim().length > 0;
            hasPreexistingAiHintOrExplanation = hasExplanation || hasHint || hasDeepDive;
        }
    }

    // Only auto-scroll to bottom when no preexisting AI hint/explanation exists for this question.
    if (hasPreexistingAiHintOrExplanation) return;

    requestAnimationFrame(() => {
        const targetBottom = Math.max(0, quizPane.scrollHeight - quizPane.clientHeight);
        if (targetBottom <= 1) return;
        quizPane.scrollTo({
            top: targetBottom,
            behavior: 'smooth'
        });
    });
}

const RESULTS_AI_REVIEW_MODEL = GEMINI_38_FLASH_MODEL;
const RESULTS_AI_REVIEW_FALLBACK_MODEL = GEMINI_37_FLASH_MODEL;
const RESULTS_AI_REVIEW_SECONDARY_FALLBACK_MODEL = GEMINI_36_FLASH_MODEL;
const RESULTS_AI_REVIEW_TERTIARY_FALLBACK_MODEL = GEMINI_35_FLASH_MODEL;
const RESULTS_AI_REVIEW_CHUNK_SIZE = 30;
const RESULTS_AI_REVIEW_REQUIRED_SECTIONS = Object.freeze([
    'Focus Areas',
    'Recurring Themes',
    'Why These Misses Happened',
    'Study Guide',
    'What To Review First',
    'Self-Check'
]);
const RESULTS_AI_REVIEW_CONDENSED_SECTIONS = Object.freeze([
    'Where You Stand',
    'Focus Areas',
    'Key Fixes',
    'Review First',
    'Self-Check'
]);
// A condensed report goes straight from the misses to the writer below this many misses.
const RESULTS_AI_REVIEW_CONDENSED_DIRECT_LIMIT = 60;
let latestResultsReviewContext = null;
let latestResultsAiReviewOutput = null;
let resultsAiReviewBusyMode = '';
let resultsWrongExportMode = 'question_only';

function stripResultsReviewText(value) {
    return String(value || '')
        .replace(/<[^>]*>/g, ' ')
        .replace(/&nbsp;/gi, ' ')
        .replace(/&amp;/gi, '&')
        .replace(/&lt;/gi, '<')
        .replace(/&gt;/gi, '>')
        .replace(/\s+/g, ' ')
        .trim();
}

function truncateResultsReviewText(value, maxLen = 220) {
    const clean = stripResultsReviewText(value);
    if (clean.length <= maxLen) return clean;
    return `${clean.slice(0, Math.max(0, maxLen - 1)).trimEnd()}...`;
}

function formatResultsReviewSourceTitle(rawTitle) {
    const original = String(rawTitle || '').trim();
    if (!original) return 'Unknown';
    const withoutExt = original.replace(/\.(json|txt|pdf)$/i, '');
    const withoutQuizSuffix = withoutExt.replace(/_quiz$/i, '').replace(/\bquiz$/i, '');
    const spaced = withoutQuizSuffix.replace(/[_]+/g, ' ').replace(/\s+/g, ' ').trim();
    return spaced || withoutQuizSuffix || original;
}

function buildResultsQuestionSetKeyByIndexMap() {
    const indexMap = {};
    let cursor = 0;
    const orderedSetKeys = Array.isArray(sessionManifest?.jsons) ? sessionManifest.jsons : [];
    orderedSetKeys.forEach((rawSetKey) => {
        const setKey = String(rawSetKey || '').trim();
        const setQuestions = loadedQuestionSets?.[setKey];
        if (!setKey || !activeQuestionSetKeys.has(setKey) || !Array.isArray(setQuestions)) return;
        for (let localIdx = 0; localIdx < setQuestions.length; localIdx++) {
            indexMap[cursor + localIdx] = setKey;
        }
        cursor += setQuestions.length;
    });
    return indexMap;
}

function buildResultsAiReviewContext({
    sessionQuestionIndices = [],
    wrongIndices = [],
    answered = 0,
    correct = 0,
    incorrect = 0,
    unanswered = 0,
    pct = 0,
    sourceRows = [],
    answers = null
} = {}) {
    // A reopened exam reviews its submitted answers, not later practice answers.
    const answerSource = answers && typeof answers === 'object' ? answers : userAnswers;
    const questionIndices = Array.isArray(sessionQuestionIndices)
        ? sessionQuestionIndices.map((idx) => Number(idx)).filter((idx) => Number.isInteger(idx) && idx >= 0 && idx < questions.length)
        : [];
    const missedIndices = Array.isArray(wrongIndices)
        ? wrongIndices.map((idx) => Number(idx)).filter((idx) => Number.isInteger(idx) && idx >= 0 && idx < questions.length)
        : [];
    const indexToSetKey = buildResultsQuestionSetKeyByIndexMap();
    const activeQuizKeys = Array.isArray(sessionManifest?.jsons)
        ? sessionManifest.jsons
            .map((key) => String(key || '').trim())
            .filter((key) => key && activeQuestionSetKeys.has(key) && Array.isArray(loadedQuestionSets?.[key]))
        : [];
    const quizTitles = [];
    const seenQuizTitles = new Set();
    activeQuizKeys.forEach((setKey) => {
        const title = formatResultsReviewSourceTitle(setKey);
        if (!title || seenQuizTitles.has(title)) return;
        seenQuizTitles.add(title);
        quizTitles.push(title);
    });

    const pdfTitleSet = new Set();
    activeQuizKeys.forEach((setKey) => {
        const linkedPdfs = pdfAssociations?.[setKey];
        if (!Array.isArray(linkedPdfs)) return;
        linkedPdfs.forEach((pdfName) => {
            const title = formatResultsReviewSourceTitle(pdfName);
            if (title) pdfTitleSet.add(title);
        });
    });
    (Array.isArray(sessionManifest?.pdfs) ? sessionManifest.pdfs : []).forEach((pdfName) => {
        const title = formatResultsReviewSourceTitle(pdfName);
        if (title) pdfTitleSet.add(title);
    });

    const sourceTitleSet = new Set();
    questionIndices.forEach((idx) => {
        const q = questions[idx];
        const sourceTitle = formatResultsReviewSourceTitle(q?.sourceFile || indexToSetKey[idx] || '');
        if (sourceTitle) sourceTitleSet.add(sourceTitle);
        const rawPdfSource = String(q?.sourceFile || '').trim();
        if (/\.pdf$/i.test(rawPdfSource)) {
            const pdfTitle = formatResultsReviewSourceTitle(rawPdfSource);
            if (pdfTitle) pdfTitleSet.add(pdfTitle);
        }
    });

    const missedQuestions = missedIndices.map((idx, order) => {
        const q = questions[idx];
        if (!q || typeof q !== 'object') return null;
        const choices = Array.isArray(q.c) ? q.c : [];
        const selectedIdx = Number.isInteger(answerSource[idx]) ? answerSource[idx] : -1;
        const correctIdx = Number.isInteger(q.a) ? q.a : -1;
        const quizKey = String(indexToSetKey[idx] || '').trim();
        const sourceFile = String(q.sourceFile || '').trim();
        const correctLetter = correctIdx >= 0 ? String.fromCharCode(65 + correctIdx) : '?';
        return {
            order: order + 1,
            displayNumber: idx + 1,
            realIndex: idx,
            quizTitle: formatResultsReviewSourceTitle(quizKey || sourceFile || 'Unknown'),
            sourceTitle: formatResultsReviewSourceTitle(sourceFile || quizKey || 'Unknown'),
            stem: truncateResultsReviewText(q.q || q.question || '', 240),
            selectedIndex: selectedIdx,
            selectedAnswer: truncateResultsReviewText(
                selectedIdx >= 0 && selectedIdx < choices.length ? choices[selectedIdx] : 'No answer recorded',
                140
            ),
            correctAnswer: truncateResultsReviewText(
                correctIdx >= 0 && correctIdx < choices.length ? choices[correctIdx] : 'Answer unavailable',
                140
            ),
            correctLetter,
            explanation: truncateResultsReviewText(q.e || q.explanation || '', 240),
            choices: choices.map((choice, choiceIdx) => ({
                letter: String.fromCharCode(65 + choiceIdx),
                text: stripResultsReviewText(choice)
            }))
        };
    }).filter(Boolean);

    const sourceSummaries = (Array.isArray(sourceRows) ? sourceRows : []).map((entry) => ({
        title: formatResultsReviewSourceTitle(entry?.src || entry?.shortName || 'Unknown'),
        correct: Math.max(0, Number(entry?.correct) || 0),
        wrong: Math.max(0, Number(entry?.wrong) || 0),
        total: Math.max(0, Number(entry?.total) || 0),
        pct: Math.max(0, Math.min(100, Number(entry?.pct) || 0))
    }));

    return {
        answered: Math.max(0, Number(answered) || 0),
        correct: Math.max(0, Number(correct) || 0),
        incorrect: Math.max(0, Number(incorrect) || 0),
        unanswered: Math.max(0, Number(unanswered) || 0),
        pct: Math.max(0, Math.min(100, Number(pct) || 0)),
        totalQuestions: questionIndices.length,
        missedCount: missedQuestions.length,
        sessionQuestionIndices: questionIndices.slice(),
        wrongIndices: missedIndices.slice(),
        quizTitles,
        pdfTitles: Array.from(pdfTitleSet),
        sourceTitles: Array.from(sourceTitleSet),
        sourceSummaries,
        missedQuestions
    };
}

function chunkResultsAiReviewItems(items = [], chunkSize = RESULTS_AI_REVIEW_CHUNK_SIZE) {
    const normalizedItems = Array.isArray(items) ? items.slice() : [];
    const safeChunkSize = Math.max(1, Number(chunkSize) || RESULTS_AI_REVIEW_CHUNK_SIZE);
    const chunks = [];
    for (let idx = 0; idx < normalizedItems.length; idx += safeChunkSize) {
        chunks.push(normalizedItems.slice(idx, idx + safeChunkSize));
    }
    return chunks;
}

function buildResultsAiStudyGuideDirectPrompt(context = null) {
    const reviewContext = context && typeof context === 'object' ? context : latestResultsReviewContext;
    if (!reviewContext || !Array.isArray(reviewContext.missedQuestions) || reviewContext.missedQuestions.length === 0) return '';

    const quizTitles = Array.isArray(reviewContext.quizTitles) && reviewContext.quizTitles.length
        ? reviewContext.quizTitles.join(', ')
        : 'No quiz titles available';
    const pdfTitles = Array.isArray(reviewContext.pdfTitles) && reviewContext.pdfTitles.length
        ? reviewContext.pdfTitles.join(', ')
        : 'No PDF titles available';
    const sourceBreakdown = Array.isArray(reviewContext.sourceSummaries) && reviewContext.sourceSummaries.length
        ? reviewContext.sourceSummaries
            .map((entry, index) => `${index + 1}. ${entry.title} | Correct ${entry.correct}/${entry.total} | Wrong ${entry.wrong} | Score ${entry.pct}%`)
            .join('\n')
        : 'No source breakdown available';
    const missedQuestionLines = reviewContext.missedQuestions
        .map((item) => {
            const choicesText = Array.isArray(item.choices) && item.choices.length
                ? item.choices
                    .map((choice) => `${choice.letter}) ${truncateResultsReviewText(choice.text, 180)}`)
                    .join(' / ')
                : 'Choices unavailable';
            const explanationPart = item.explanation ? ` | Explanation clue: ${item.explanation}` : '';
            return `${item.order}. Quiz bank: ${item.quizTitle} | Source: ${item.sourceTitle} | Q${item.displayNumber}: ${item.stem} | Choices: ${choicesText} | Chosen: ${item.selectedAnswer} | Correct: ${item.correctLetter}. ${item.correctAnswer}${explanationPart}`;
        })
        .join('\n');

    return `You are an expert exam-prep tutor writing one final Study Guide after a completed quiz session.

Use the full missed-question list below directly when completing or repairing the final merged guide.

Write Markdown only. Do not include code fences, JSON, batch labels, or process notes.

Use these exact section headers:
${RESULTS_AI_REVIEW_REQUIRED_SECTIONS.map((section) => `## ${section}`).join('\n')}

Rules:
- Focus on what the student needs to learn next from the missed questions.
- Do not stop after identifying weak sources. Source performance is only context; the deliverable is a teaching guide.
- Make "## Study Guide" the longest section. It must contain detailed concept teaching, not just a to-do list or source ranking.
- Under "## Study Guide", create substantial concept blocks. Each important block should include: core rule, why the chosen answer was wrong, why the correct answer is right, common trap, exam-style cues, and relevant missed question numbers.
- When a miss reflects a selected-vs-correct contrast, explicitly compare those two answers so the student can see the reasoning error.
- Use the missed questions and explanations to infer the student's misunderstanding. Explain the content enough that the student can answer a similar question correctly next time.
- Treat any inferred misunderstanding as a hypothesis from the selected-versus-correct contrast (for example, "You may have confused..."); do not state a learner belief as known fact.
- Include missed question numbers throughout the guide so the student can trace each concept back to specific misses.
- Merge duplicate ideas instead of repeating the same explanation for every question.
- Call out weak source titles when the source breakdown supports it.
- Keep the guide high-yield and source-grounded. Do not invent unsupported lecture facts.
- Preserve exact qualifiers, exceptions, negation, numbers, units, populations, stages, and time frames from the supplied question records. A quiz explanation is supporting context, not independent proof of a new lecture claim.
- Prefer clear explanatory paragraphs plus bullets/tables where they make contrasts easier to study.
- Finish the response completely; do not end mid-bullet, mid-table, or mid-sentence.

Session summary:
- Total questions in this run: ${reviewContext.totalQuestions}
- Answered: ${reviewContext.answered}
- Correct: ${reviewContext.correct}
- Incorrect: ${reviewContext.incorrect}
- Unanswered: ${reviewContext.unanswered}
- Accuracy: ${reviewContext.pct}%
- Total missed questions: ${reviewContext.missedCount}

Quiz titles:
${quizTitles}

PDF titles:
${pdfTitles}

Performance by source:
${sourceBreakdown}

Missed questions:
${missedQuestionLines}`;
}

function buildResultsAiStudyGuideBatchPrompt(context = null, batchItems = [], batchIndex = 0, totalBatches = 1) {
    const reviewContext = context && typeof context === 'object' ? context : latestResultsReviewContext;
    const items = Array.isArray(batchItems) ? batchItems : [];
    if (!reviewContext || !items.length) return '';

    const sourceBreakdown = Array.isArray(reviewContext.sourceSummaries) && reviewContext.sourceSummaries.length
        ? reviewContext.sourceSummaries
            .map((entry, index) => `${index + 1}. ${entry.title} | Correct ${entry.correct}/${entry.total} | Wrong ${entry.wrong} | Score ${entry.pct}%`)
            .join('\n')
        : 'No source breakdown available';
    const missedQuestionLines = items
        .map((item) => {
            const choicesText = Array.isArray(item.choices) && item.choices.length
                ? item.choices
                    .map((choice) => `${choice.letter}) ${truncateResultsReviewText(choice.text, 220)}`)
                    .join(' / ')
                : 'Choices unavailable';
            const explanationPart = item.explanation ? ` | Explanation clue: ${item.explanation}` : '';
            return `${item.order}. Source: ${item.sourceTitle} | Q${item.displayNumber}: ${item.stem} | Choices: ${choicesText} | Chosen: ${item.selectedAnswer} | Correct: ${item.correctLetter}. ${item.correctAnswer}${explanationPart}`;
        })
        .join('\n');

    return `You are an expert exam-prep tutor writing one in-depth internal Study Guide walkthrough section.

This internal section covers up to ${RESULTS_AI_REVIEW_CHUNK_SIZE} missed questions. Your job is to teach the student the concepts behind this group, not just list weak sources.

Write Markdown only. Do not include code fences, JSON, or process notes.

Use these exact section headers:
## Focus
## Concept Walkthrough
## Why The Misses Happened
## Question-by-Question Teaching Notes
## High-Yield Self-Check

Rules:
- Be in-depth. The "Concept Walkthrough" and "Question-by-Question Teaching Notes" sections should be the longest sections.
- For each major concept, explain the core rule, why the selected answer was wrong, why the correct answer was right, the common trap, and exam-style cues.
- Include every question in this batch at least once by displayed question number.
- Merge duplicate concepts, but do not skip any missed question.
- Use concise tables when comparing similar answer choices or concepts would help.
- Stay grounded in the missed question text, answer choices, and explanation clues. Do not invent unsupported lecture facts.
- Finish completely; do not end mid-sentence or mid-bullet.

Session summary:
- Total questions in this run: ${reviewContext.totalQuestions}
- Accuracy: ${reviewContext.pct}%
- Total missed questions: ${reviewContext.missedCount}
- This group missed questions: ${items.length}

Performance by source:
${sourceBreakdown}

Missed questions in this group:
${missedQuestionLines}`;
}

function buildResultsAiStudyGuideBatchRepairPrompt(context = null, batchItems = [], batchIndex = 0, totalBatches = 1, draft = '') {
    return `${buildResultsAiStudyGuideBatchPrompt(context, batchItems, batchIndex, totalBatches)}

The previous batch walkthrough was too short or incomplete and must be replaced.

Previous draft:
${String(draft || '').trim() || '[empty]'}

Return a complete replacement for this walkthrough section. It must teach the content in depth and include every missed question in this group by question number.`;
}

function formatResultsAiReviewSessionBlock(reviewContext) {
    const sourceBreakdown = Array.isArray(reviewContext.sourceSummaries) && reviewContext.sourceSummaries.length
        ? reviewContext.sourceSummaries
            .map((entry, index) => `${index + 1}. ${entry.title} | Correct ${entry.correct}/${entry.total} | Wrong ${entry.wrong} | Score ${entry.pct}%`)
            .join('\n')
        : 'No source breakdown available';
    return `Session summary:
- Total questions: ${reviewContext.totalQuestions}
- Answered: ${reviewContext.answered} | Correct: ${reviewContext.correct} | Incorrect: ${reviewContext.incorrect} | Unanswered: ${reviewContext.unanswered}
- Accuracy: ${reviewContext.pct}%
- Missed questions: ${reviewContext.missedCount}

Quiz titles: ${(reviewContext.quizTitles || []).join(', ') || 'unknown'}

Performance by source:
${sourceBreakdown}`;
}

function formatResultsAiCondensedMissLines(items = []) {
    return items.map((item) => {
        const choicesText = Array.isArray(item.choices) && item.choices.length
            ? item.choices.map((choice) => `${choice.letter}) ${truncateResultsReviewText(choice.text, 140)}`).join(' / ')
            : 'Choices unavailable';
        const explanationPart = item.explanation ? ` | Explanation clue: ${item.explanation}` : '';
        return `Q${item.displayNumber} (${item.sourceTitle}): ${item.stem} | Choices: ${choicesText} | Chosen: ${item.selectedAnswer} | Correct: ${item.correctLetter}. ${item.correctAnswer}${explanationPart}`;
    }).join('\n');
}

// One short report the student can read in a few minutes; the Questions report already lists every miss.
function buildResultsAiCondensedReportPrompt(context = null, { groupNotes = '' } = {}) {
    const reviewContext = context && typeof context === 'object' ? context : latestResultsReviewContext;
    const missed = Array.isArray(reviewContext?.missedQuestions) ? reviewContext.missedQuestions : [];
    if (!reviewContext || (!missed.length && !groupNotes)) return '';
    const evidence = groupNotes
        ? `Concept notes already distilled from all ${reviewContext.missedCount} missed questions:\n${groupNotes}`
        : `Missed questions:\n${formatResultsAiCondensedMissLines(missed)}`;

    return `You are an expert exam-prep tutor writing a short study report after a completed quiz session.

Goal: a condensed, high-quality report the student can read in a few minutes - about 1-2 printed pages (roughly 450-850 words). Depth comes from precise rules, not length.

Write Markdown only. No code fences, JSON, batch labels, or process notes.

Use these exact section headers, in this order:
${RESULTS_AI_REVIEW_CONDENSED_SECTIONS.map((section) => `## ${section}`).join('\n')}

Section rules:
- Where You Stand: 2-3 sentences on the score, the weakest sources when the breakdown supports it, and the overall pattern in the misses.
- Focus Areas: 3-5 bullets, highest-yield first. Each names a concept and the missed question numbers it covers (for example "Q4, Q17").
- Key Fixes: one compact block per concept, merging questions that test the same idea, at most 8 blocks. Each block starts with the concept in bold plus its question numbers, then the rule to remember in one or two sentences, then the trap: what made the chosen answer tempting and the cue that points to the correct one. Use a small table only to contrast three or more look-alike items.
- Review First: 3 numbered actions in priority order.
- Self-Check: 3-5 short questions with the answer in parentheses, testing the rules from Key Fixes.

Rules:
- Teach what the student needs to fix next. Do not restate every question stem or walk through misses one by one.
- Treat any inferred misunderstanding as a hypothesis ("You may have confused..."), not a fact about the student.
- Stay grounded in the supplied questions, choices, and explanation clues. Preserve exact qualifiers, exceptions, negation, numbers, units, populations, stages, and time frames. Do not invent lecture facts.
- Finish completely; never stop mid-sentence, mid-bullet, or mid-table.

${formatResultsAiReviewSessionBlock(reviewContext)}

${evidence}`;
}

function buildResultsAiCondensedNotesPrompt(context = null, batchItems = []) {
    const items = Array.isArray(batchItems) ? batchItems : [];
    if (!items.length) return '';
    return `You are an expert exam-prep tutor preparing internal notes for a short study report. Group the missed questions below by the concept they test.

For each concept write one line: **Concept** - Q numbers - the rule to remember - the trap that made the chosen answer tempting.
Keep it under about 250 words. Plain Markdown bullets only, no headings or commentary. Stay grounded in the supplied questions and explanation clues; preserve exact qualifiers, negation, and numbers.

Missed questions:
${formatResultsAiCondensedMissLines(items)}`;
}

function evaluateResultsAiCondensedReportQuality(markdown = '', context = null) {
    const text = String(markdown || '').trim();
    const wordCount = countResultsAiGuideWords(text);
    const missedCount = Math.max(1, Number(context?.missedCount) || 1);
    const maxWordCount = Math.min(1200, 650 + missedCount * 6);
    const missingSections = RESULTS_AI_REVIEW_CONDENSED_SECTIONS
        .filter((section) => !getResultsAiGuideHeadingPattern(section).test(text));
    return {
        ok: !missingSections.length && wordCount >= 220 && wordCount <= maxWordCount,
        wordCount,
        maxWordCount,
        missingSections
    };
}

function buildResultsAiCondensedRepairPrompt(context = null, draft = '', quality = {}, groupNotes = '') {
    const missing = quality?.missingSections?.length ? quality.missingSections.join(', ') : 'none';
    const issue = quality?.wordCount > quality?.maxWordCount
        ? `It ran ${quality.wordCount} words; keep the replacement under about 850 words by merging concepts and cutting repetition, not by dropping the highest-yield fixes.`
        : `It ran ${quality?.wordCount || 0} words and is missing: ${missing}. Fill every section within about 450-850 words.`;
    return `${buildResultsAiCondensedReportPrompt(context, { groupNotes })}

The previous draft did not fit the format and must be replaced. ${issue}

Previous draft:
${String(draft || '').trim() || '[empty]'}

Return a complete replacement, not a continuation, with every required section header.`;
}

function getResultsAiGuideHeadingPattern(section = '') {
    return new RegExp(`^##\\s+${String(section).replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\s*$`, 'im');
}

function countResultsAiGuideWords(text = '') {
    return String(text || '').trim().split(/\s+/).filter(Boolean).length;
}

function getResultsAiGuideSectionText(markdown = '', sectionName = '') {
    const target = String(sectionName || '').trim().toLowerCase();
    if (!target) return '';
    const text = String(markdown || '').replace(/\r\n?/g, '\n');
    const headingPattern = /^##\s+(.+?)\s*$/gim;
    const headings = [];
    let match = null;
    while ((match = headingPattern.exec(text)) !== null) {
        headings.push({
            name: String(match[1] || '').trim().toLowerCase(),
            start: match.index,
            end: headingPattern.lastIndex
        });
    }
    const currentIndex = headings.findIndex((heading) => heading.name === target);
    if (currentIndex < 0) return '';
    const nextHeading = headings[currentIndex + 1];
    return text.slice(headings[currentIndex].end, nextHeading ? nextHeading.start : text.length).trim();
}

function evaluateResultsAiStudyGuideQuality(markdown = '', context = null) {
    const text = String(markdown || '').trim();
    const wordCount = countResultsAiGuideWords(text);
    const missedCount = Math.max(1, Number(context?.missedCount) || 1);
    const minWordCount = Math.min(5200, Math.max(1400, missedCount * 35));
    const missingSections = RESULTS_AI_REVIEW_REQUIRED_SECTIONS
        .filter((section) => !new RegExp(`^##\\s+${section.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\s*$`, 'im').test(text));
    const studyGuideText = getResultsAiGuideSectionText(text, 'Study Guide');
    const studyGuideWords = countResultsAiGuideWords(studyGuideText);
    const hasReasoningLanguage = /\b(?:chosen|selected|wrong|incorrect|correct answer|trap|cue|because|reasoning|confus(?:e|ion)|distinguish)\b/i.test(text);
    const ok = !missingSections.length
        && wordCount >= minWordCount
        && studyGuideWords >= Math.max(450, Math.floor(minWordCount * 0.35))
        && hasReasoningLanguage;
    return {
        ok,
        wordCount,
        minWordCount,
        missingSections,
        studyGuideWords,
        hasReasoningLanguage
    };
}

function evaluateResultsAiStudyGuideBatchQuality(markdown = '', batchItems = []) {
    const text = String(markdown || '').trim();
    const wordCount = countResultsAiGuideWords(text);
    const itemCount = Math.max(1, Array.isArray(batchItems) ? batchItems.length : 1);
    const minWordCount = Math.min(2400, Math.max(700, itemCount * 35));
    const hasReasoningLanguage = /\b(?:chosen|selected|wrong|incorrect|correct answer|trap|cue|because|reasoning|confus(?:e|ion)|distinguish)\b/i.test(text);
    return {
        ok: wordCount >= minWordCount && hasReasoningLanguage,
        wordCount,
        minWordCount,
        hasReasoningLanguage
    };
}

async function callResultsAiStudyGuideModelWithRetries(prompt = '', requestOptions = {}, {
    emptyMessage = 'Study Guide request returned an empty response.'
} = {}) {
    const requestedLadder = Array.isArray(requestOptions?.modelLadder)
        ? requestOptions.modelLadder.map((model) => String(model || '').trim()).filter(Boolean)
        : [];
    const modelLadder = Array.from(new Set([
        ...(requestedLadder.length ? requestedLadder : [GEMINI_38_FLASH_MODEL]),
        RESULTS_AI_REVIEW_FALLBACK_MODEL,
        RESULTS_AI_REVIEW_SECONDARY_FALLBACK_MODEL,
        RESULTS_AI_REVIEW_TERTIARY_FALLBACK_MODEL,
        GEMINI_3_FLASH_MODEL
    ]));
    const response = String(await callGeminiFlash(prompt, {
        ...requestOptions,
        modelLadder
    }) || '').trim();
    if (response) return response;
    throw new Error(emptyMessage);
}

function buildResultsAiStudyGuideRepairPrompt(context = null, draft = '', quality = {}) {
    const basePrompt = buildResultsAiStudyGuideDirectPrompt(context);
    const missing = Array.isArray(quality?.missingSections) && quality.missingSections.length
        ? quality.missingSections.join(', ')
        : 'none';
    return `${basePrompt}

The previous draft was too short or incomplete and must be replaced.

Previous draft:
${String(draft || '').trim() || '[empty]'}

Quality issues:
- Word count: ${quality?.wordCount || 0}; expected at least about ${quality?.minWordCount || 1000}.
- Missing required sections: ${missing}.
- Study Guide section words: ${quality?.studyGuideWords || 0}.

Return a complete replacement, not a continuation. The replacement must include every required section header. Do not merely rank weak sources. The "## Study Guide" section must teach the tested concepts in depth and explicitly explain why the user's chosen answers were wrong compared with the correct answers.`;
}

function buildResultsAiStudyGuideCoverageAppendix(context = null) {
    const reviewContext = context && typeof context === 'object' ? context : latestResultsReviewContext;
    const missed = Array.isArray(reviewContext?.missedQuestions) ? reviewContext.missedQuestions : [];
    if (!missed.length) return '';
    const grouped = new Map();
    missed.forEach((item) => {
        const source = item.sourceTitle || item.quizTitle || 'Unknown source';
        if (!grouped.has(source)) grouped.set(source, []);
        grouped.get(source).push(item);
    });
    const sections = [];
    grouped.forEach((items, source) => {
        const lines = items.map((item) => {
            const explanation = item.explanation
                ? ` Review clue: ${item.explanation}`
                : ' Rebuild the rule that separates the selected answer from the correct answer.';
            return `- **Q${item.displayNumber}:** ${item.stem} You chose **${item.selectedAnswer}**; correct answer: **${item.correctLetter}. ${item.correctAnswer}**.${explanation}`;
        });
        sections.push(`### ${source}\n${lines.join('\n')}`);
    });
    return `## Missed Question Reasoning Map

Use this map to connect the guide back to each miss. For every item, restudy the rule that makes the correct answer more defensible than the selected answer.

${sections.join('\n\n')}`;
}

function buildResultsAiStudyGuideFallbackFromBatches(batchGuides = []) {
    const body = (Array.isArray(batchGuides) ? batchGuides : [])
        .map((batch) => String(batch?.text || '').trim())
        .filter(Boolean)
        .join('\n\n');
    if (!body) return '';
    return `## Focus Areas

- Review the highest-yield missed-question themes in the walkthrough below.

## Recurring Themes

- The concepts below appeared across the missed questions and should be reviewed before the next attempt.

## Why These Misses Happened

- The missed questions point to selected-vs-correct answer traps. Use the walkthrough to compare what was chosen with what made the correct answer stronger.

## Study Guide

${body}

## What To Review First

- Start with the concepts that appear in multiple missed questions.
- Rework each listed question after reviewing the corresponding explanation.

## Self-Check

- For each missed question, explain why the selected answer is less defensible than the correct answer.
- Name the clue in the stem that should point you toward the correct answer.`;
}

async function generateResultsAiCondensedReport(reviewContext, requestOptions, retryState) {
    const missed = Array.isArray(reviewContext?.missedQuestions) ? reviewContext.missedQuestions : [];
    let groupNotes = '';
    if (missed.length > RESULTS_AI_REVIEW_CONDENSED_DIRECT_LIMIT) {
        const chunks = chunkResultsAiReviewItems(missed, RESULTS_AI_REVIEW_CHUNK_SIZE);
        const notes = [];
        for (let batchIndex = 0; batchIndex < chunks.length; batchIndex += 1) {
            showPopup('Building your study report...', null, false, 90000);
            notes.push(await callResultsAiStudyGuideModelWithRetries(buildResultsAiCondensedNotesPrompt(reviewContext, chunks[batchIndex]), {
                ...requestOptions,
                usageLabel: `Results study report notes ${batchIndex + 1}/${chunks.length}`
            }, { emptyMessage: 'Study report notes returned an empty response.', retryState }));
        }
        groupNotes = notes.join('\n');
    }
    showPopup('Building your study report...', null, false, 90000);
    let report = await callResultsAiStudyGuideModelWithRetries(buildResultsAiCondensedReportPrompt(reviewContext, { groupNotes }), {
        ...requestOptions,
        usageLabel: 'Results study report'
    }, { emptyMessage: 'Study report returned an empty response.', retryState });
    let quality = evaluateResultsAiCondensedReportQuality(report, reviewContext);
    if (!quality.ok) {
        showPopup('Tightening your study report...', null, false, 90000);
        const repaired = String(await callResultsAiStudyGuideModelWithRetries(
            buildResultsAiCondensedRepairPrompt(reviewContext, report, quality, groupNotes),
            { ...requestOptions, usageLabel: 'Repair results study report' },
            { emptyMessage: 'Study report repair returned an empty response.', retryState }
        ) || '').trim();
        const repairedQuality = evaluateResultsAiCondensedReportQuality(repaired, reviewContext);
        if (repaired && (repairedQuality.ok || !repairedQuality.missingSections.length)) {
            report = repaired;
            quality = repairedQuality;
        }
        if (!quality.ok) console.warn('Results study report missed its format target:', quality);
    }
    return { text: report, batchCount: 1 };
}

async function generateResultsAiStudyGuideWithQualityCheck(reviewContext = null, { length = 'detailed' } = {}) {
    const retryState = { primaryFailures: 0 };
    const requestOptions = {
        usageSurface: 'Results Study Guide',
        usageLabel: 'Results study guide',
        modelLadder: [RESULTS_AI_REVIEW_MODEL],
        anonymousFreeTier: true,
        generationConfig: {
            temperature: 0.35
        }
    };
    if (length === 'condensed') return generateResultsAiCondensedReport(reviewContext, requestOptions, retryState);
    const chunks = chunkResultsAiReviewItems(reviewContext?.missedQuestions || [], RESULTS_AI_REVIEW_CHUNK_SIZE);
    if (!chunks.length) throw new Error('Could not build the study guide batches.');

    const batchGuides = [];
    for (let batchIndex = 0; batchIndex < chunks.length; batchIndex += 1) {
        const batchItems = chunks[batchIndex];
        showPopup('Building Study Guide...', null, false, 90000);
        const batchPrompt = buildResultsAiStudyGuideBatchPrompt(reviewContext, batchItems, batchIndex, chunks.length);
        if (!batchPrompt) throw new Error('Could not build a Study Guide request.');
        let batchText = await callResultsAiStudyGuideModelWithRetries(batchPrompt, {
            ...requestOptions,
            usageLabel: `Results study guide batch ${batchIndex + 1}/${chunks.length}`
        }, {
            emptyMessage: 'Study Guide batch returned an empty response.',
            retryState
        });
        let batchQuality = evaluateResultsAiStudyGuideBatchQuality(batchText, batchItems);
        if (!batchQuality.ok) {
            showPopup('Building Study Guide...', null, false, 90000);
            const repairedBatch = await callResultsAiStudyGuideModelWithRetries(
                buildResultsAiStudyGuideBatchRepairPrompt(reviewContext, batchItems, batchIndex, chunks.length, batchText),
                {
                    ...requestOptions,
                    usageLabel: `Repair results study guide batch ${batchIndex + 1}/${chunks.length}`
                },
                {
                    emptyMessage: 'Study Guide batch repair returned an empty response.',
                    retryState
                }
            );
            const repairedQuality = evaluateResultsAiStudyGuideBatchQuality(repairedBatch, batchItems);
            if (repairedBatch && (repairedQuality.ok || repairedQuality.wordCount > batchQuality.wordCount || !batchText)) {
                batchText = repairedBatch;
                batchQuality = repairedQuality;
            }
        }
        if (!batchText) throw new Error('Study Guide generation returned an empty response.');
        batchGuides.push({
            index: batchIndex,
            text: batchText,
            quality: batchQuality
        });
    }

    showPopup('Finalizing Study Guide...', null, false, 90000);
    const synthesisPrompt = buildResultsAiStudyGuideSynthesisPrompt(reviewContext, batchGuides);
    let guide = String(synthesisPrompt ? await callResultsAiStudyGuideModelWithRetries(synthesisPrompt, {
        ...requestOptions,
        usageLabel: `Combine ${chunks.length} results study guide batches`
    }, {
        emptyMessage: 'Final Study Guide synthesis returned an empty response.',
        retryState
    }) : '').trim();
    if (!guide) {
        guide = buildResultsAiStudyGuideFallbackFromBatches(batchGuides);
    }
    let quality = evaluateResultsAiStudyGuideQuality(guide, reviewContext);
    if (quality.ok) return { text: guide, batchCount: chunks.length };

    showPopup('Finalizing Study Guide...', null, false, 90000);
    const repairPrompt = buildResultsAiStudyGuideRepairPrompt(reviewContext, guide, quality);
    const repairedGuide = String(await callResultsAiStudyGuideModelWithRetries(repairPrompt, {
        ...requestOptions,
        usageLabel: 'Repair short results study guide'
    }, {
        emptyMessage: 'Final Study Guide repair returned an empty response.',
        retryState
    }) || '').trim();
    const repairedQuality = evaluateResultsAiStudyGuideQuality(repairedGuide, reviewContext);
    if (repairedGuide && (repairedQuality.ok || repairedQuality.wordCount > quality.wordCount || !guide)) {
        guide = repairedGuide;
        quality = repairedQuality;
    }

    if (!quality.ok) {
        console.warn('Results Study Guide remained below quality target:', quality);
        const appendix = buildResultsAiStudyGuideCoverageAppendix(reviewContext);
        if (appendix && !/\bMissed Question Reasoning Map\b/i.test(guide)) {
            guide = `${guide}\n\n${appendix}`.trim();
        }
    }
    return { text: guide, batchCount: chunks.length };
}

function getResultsAiReviewModelLabel(batchCount = 1) {
    const rawModel = (typeof lastUsedModel !== 'undefined' && lastUsedModel)
        ? lastUsedModel
        : RESULTS_AI_REVIEW_MODEL;
    const formattedModel = typeof formatAskMemoraModelLabel === 'function'
        ? formatAskMemoraModelLabel(rawModel)
        : (rawModel === RESULTS_AI_REVIEW_MODEL ? 'Gemini 3.8 Flash' : rawModel);
    return formattedModel || '';
}

function normalizeResultsAiTopicKey(value = '') {
    return String(value || '')
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
}

function normalizeResultsAiStringArray(value, maxItems = 8) {
    const seen = new Set();
    return (Array.isArray(value) ? value : [])
        .map((entry) => truncateResultsReviewText(entry, 220))
        .filter((entry) => {
            const key = normalizeResultsAiTopicKey(entry);
            if (!entry || !key || seen.has(key)) return false;
            seen.add(key);
            return true;
        })
        .slice(0, Math.max(1, Number(maxItems) || 8));
}

function normalizeResultsAiNumberArray(value, maxItems = 16) {
    const seen = new Set();
    return (Array.isArray(value) ? value : [])
        .map((entry) => Number(entry))
        .filter((entry) => Number.isInteger(entry) && entry > 0 && !seen.has(entry) && seen.add(entry))
        .slice(0, Math.max(1, Number(maxItems) || 16))
        .sort((a, b) => a - b);
}

function extractResultsAiJsonPayload(rawText = '') {
    const raw = String(rawText || '').trim();
    if (!raw) return null;

    const candidates = [];
    const strippedFence = raw
        .replace(/^```json\s*/i, '')
        .replace(/^```\s*/i, '')
        .replace(/\s*```$/i, '')
        .trim();

    candidates.push(raw);
    if (strippedFence && strippedFence !== raw) candidates.push(strippedFence);

    const firstBrace = strippedFence.indexOf('{');
    const lastBrace = strippedFence.lastIndexOf('}');
    if (firstBrace >= 0 && lastBrace > firstBrace) {
        candidates.push(strippedFence.slice(firstBrace, lastBrace + 1).trim());
    }

    const seen = new Set();
    for (const candidate of candidates) {
        if (!candidate || seen.has(candidate)) continue;
        seen.add(candidate);
        try {
            return JSON.parse(candidate);
        } catch (_err) {
            // Try the next candidate.
        }
    }
    return null;
}

function buildResultsAiReviewChunkPrompt(context = null, chunkItems = [], chunkIndex = 0, totalChunks = 1) {
    const reviewContext = context && typeof context === 'object' ? context : latestResultsReviewContext;
    if (!reviewContext || !Array.isArray(chunkItems) || chunkItems.length === 0) return '';

    const quizTitles = reviewContext.quizTitles.length ? reviewContext.quizTitles.join(', ') : 'No quiz titles available';
    const pdfTitles = reviewContext.pdfTitles.length ? reviewContext.pdfTitles.join(', ') : 'No PDF titles available';
    const sourceBreakdown = reviewContext.sourceSummaries.length
        ? reviewContext.sourceSummaries
            .map((entry, index) => `${index + 1}. ${entry.title} | Correct ${entry.correct}/${entry.total} | Wrong ${entry.wrong} | Score ${entry.pct}%`)
            .join('\n')
        : 'No source breakdown available';
    const missedQuestionLines = chunkItems
        .map((item) => {
            const explanationPart = item.explanation ? ` | Explanation clue: ${item.explanation}` : '';
            return `${item.order}. Quiz bank: ${item.quizTitle} | Source: ${item.sourceTitle} | Q${item.displayNumber}: ${item.stem} | Chosen: ${item.selectedAnswer} | Correct: ${item.correctLetter}. ${item.correctAnswer}${explanationPart}`;
        })
        .join('\n');

    return `You are an expert dental board-review tutor analyzing one chunk of missed questions from a completed quiz session.

This is chunk ${chunkIndex + 1} of ${Math.max(1, totalChunks)}.

Return valid JSON only. Do not include markdown fences, comments, or extra text.

Use this exact JSON shape:
{
  "chunk_summary": "1-3 sentence overview of what this batch says about the student's weak areas.",
  "focus_areas": [
    {
      "topic": "short topic name",
      "why_it_matters": "why this topic needs attention",
      "miss_patterns": ["specific confusion or pattern"],
      "source_titles": ["source title"],
      "question_numbers": [12, 18]
    }
  ],
  "recurring_themes": ["theme"],
  "concept_traps": ["specific contrast or misconception the student is mixing up"],
  "study_actions": ["specific next study step"],
  "self_checks": ["short self-check question"]
}

Rules:
- Ground everything in the missed questions and source performance below.
- Focus on the underlying concepts, not generic encouragement.
- Combine related misses into themes instead of repeating every question stem.
- Keep "focus_areas" to 2-6 strong priorities.
- Keep "recurring_themes", "concept_traps", "study_actions", and "self_checks" concise and specific.
- "question_numbers" must use the displayed question numbers from the list below.

Session summary:
- Total questions in this run: ${reviewContext.totalQuestions}
- Answered: ${reviewContext.answered}
- Correct: ${reviewContext.correct}
- Incorrect: ${reviewContext.incorrect}
- Unanswered: ${reviewContext.unanswered}
- Accuracy: ${reviewContext.pct}%
- Total missed questions: ${reviewContext.missedCount}
- Missed questions in this chunk: ${chunkItems.length}

Quiz titles:
${quizTitles}

PDF titles:
${pdfTitles}

Performance by source:
${sourceBreakdown}

Missed questions in this chunk:
${missedQuestionLines}`;
}

function normalizeResultsAiChunkReviewPayload(payload, chunkItems = [], chunkIndex = 0, rawText = '') {
    const sourcePayload = payload && typeof payload === 'object' ? payload : {};
    const focusAreasRaw = Array.isArray(sourcePayload.focus_areas)
        ? sourcePayload.focus_areas
        : (Array.isArray(sourcePayload.focusAreas) ? sourcePayload.focusAreas : []);

    const focusAreas = focusAreasRaw
        .map((entry) => {
            const topic = truncateResultsReviewText(entry?.topic || entry?.name || '', 140);
            const whyItMatters = truncateResultsReviewText(entry?.why_it_matters || entry?.whyItMatters || entry?.why || '', 260);
            const missPatterns = normalizeResultsAiStringArray(entry?.miss_patterns || entry?.missPatterns || entry?.patterns || [], 6);
            const sourceTitles = normalizeResultsAiStringArray(entry?.source_titles || entry?.sourceTitles || entry?.sources || [], 6);
            const questionNumbers = normalizeResultsAiNumberArray(entry?.question_numbers || entry?.questionNumbers || entry?.questions || [], 16);
            if (!topic && !whyItMatters && missPatterns.length === 0) return null;
            return { topic, whyItMatters, missPatterns, sourceTitles, questionNumbers };
        })
        .filter(Boolean)
        .slice(0, 6);

    const fallbackSummary = chunkItems.length > 0
        ? `Chunk ${chunkIndex + 1} covers ${chunkItems.length} missed question${chunkItems.length === 1 ? '' : 's'} from this session.`
        : `Chunk ${chunkIndex + 1} review.`;

    return {
        chunkIndex: chunkIndex + 1,
        chunkSummary: truncateResultsReviewText(sourcePayload.chunk_summary || sourcePayload.chunkSummary || sourcePayload.summary || rawText || fallbackSummary, 360) || fallbackSummary,
        focusAreas,
        recurringThemes: normalizeResultsAiStringArray(sourcePayload.recurring_themes || sourcePayload.recurringThemes || sourcePayload.themes || [], 8),
        conceptTraps: normalizeResultsAiStringArray(sourcePayload.concept_traps || sourcePayload.conceptTraps || sourcePayload.traps || [], 8),
        studyActions: normalizeResultsAiStringArray(sourcePayload.study_actions || sourcePayload.studyActions || sourcePayload.actions || [], 8),
        selfChecks: normalizeResultsAiStringArray(sourcePayload.self_checks || sourcePayload.selfChecks || sourcePayload.questions || [], 8),
        rawText: String(rawText || '').trim()
    };
}

function buildResultsAiAggregatedStudyData(context = null, chunkFindings = []) {
    const reviewContext = context && typeof context === 'object' ? context : latestResultsReviewContext;
    const findings = Array.isArray(chunkFindings) ? chunkFindings.filter(Boolean) : [];
    if (!reviewContext || findings.length === 0) return null;

    const focusAreaMap = new Map();
    const recurringThemeMap = new Map();
    const conceptTrapMap = new Map();
    const studyActionMap = new Map();
    const selfCheckMap = new Map();

    const addRankedText = (map, text) => {
        const clean = truncateResultsReviewText(text, 240);
        const key = normalizeResultsAiTopicKey(clean);
        if (!clean || !key) return;
        const existing = map.get(key) || { text: clean, count: 0 };
        existing.count += 1;
        map.set(key, existing);
    };

    findings.forEach((finding) => {
        (Array.isArray(finding.focusAreas) ? finding.focusAreas : []).forEach((area) => {
            const topic = truncateResultsReviewText(area?.topic || area?.whyItMatters || 'Focus area', 140);
            const key = normalizeResultsAiTopicKey(topic);
            if (!key) return;
            const existing = focusAreaMap.get(key) || {
                topic,
                whyParts: new Set(),
                missPatterns: new Set(),
                sourceTitles: new Set(),
                questionNumbers: new Set(),
                count: 0
            };
            existing.count += 1;
            if (area?.whyItMatters) existing.whyParts.add(truncateResultsReviewText(area.whyItMatters, 220));
            (Array.isArray(area?.missPatterns) ? area.missPatterns : []).forEach((entry) => existing.missPatterns.add(truncateResultsReviewText(entry, 180)));
            (Array.isArray(area?.sourceTitles) ? area.sourceTitles : []).forEach((entry) => existing.sourceTitles.add(truncateResultsReviewText(entry, 120)));
            (Array.isArray(area?.questionNumbers) ? area.questionNumbers : []).forEach((entry) => {
                const num = Number(entry);
                if (Number.isInteger(num) && num > 0) existing.questionNumbers.add(num);
            });
            focusAreaMap.set(key, existing);
        });

        (Array.isArray(finding.recurringThemes) ? finding.recurringThemes : []).forEach((entry) => addRankedText(recurringThemeMap, entry));
        (Array.isArray(finding.conceptTraps) ? finding.conceptTraps : []).forEach((entry) => addRankedText(conceptTrapMap, entry));
        (Array.isArray(finding.studyActions) ? finding.studyActions : []).forEach((entry) => addRankedText(studyActionMap, entry));
        (Array.isArray(finding.selfChecks) ? finding.selfChecks : []).forEach((entry) => addRankedText(selfCheckMap, entry));
    });

    const focusAreas = Array.from(focusAreaMap.values())
        .map((entry) => ({
            topic: entry.topic,
            whyItMatters: Array.from(entry.whyParts).slice(0, 2).join(' '),
            missPatterns: Array.from(entry.missPatterns).slice(0, 4),
            sourceTitles: Array.from(entry.sourceTitles).slice(0, 5),
            questionNumbers: Array.from(entry.questionNumbers).sort((a, b) => a - b).slice(0, 24),
            count: entry.count
        }))
        .sort((a, b) => (b.count - a.count) || (b.questionNumbers.length - a.questionNumbers.length))
        .slice(0, 8);

    const rankMapEntries = (map, maxItems = 8) => Array.from(map.values())
        .sort((a, b) => (b.count - a.count) || a.text.localeCompare(b.text))
        .slice(0, maxItems)
        .map((entry) => entry.text);

    return {
        totalChunks: findings.length,
        missedCount: reviewContext.missedCount,
        pct: reviewContext.pct,
        quizTitles: reviewContext.quizTitles.slice(),
        pdfTitles: reviewContext.pdfTitles.slice(),
        sourceSummaries: reviewContext.sourceSummaries.slice(),
        chunkSummaries: findings.map((finding) => finding.chunkSummary).filter(Boolean),
        focusAreas,
        recurringThemes: rankMapEntries(recurringThemeMap, 8),
        conceptTraps: rankMapEntries(conceptTrapMap, 8),
        studyActions: rankMapEntries(studyActionMap, 8),
        selfChecks: rankMapEntries(selfCheckMap, 8)
    };
}

function buildResultsAiStudyGuideSynthesisPrompt(context = null, batchGuides = []) {
    const reviewContext = context && typeof context === 'object' ? context : latestResultsReviewContext;
    const walkthroughs = (Array.isArray(batchGuides) ? batchGuides : [])
        .map((batch, index) => String(batch?.text || '').trim() && `### Group ${index + 1}\n${String(batch.text).trim()}`)
        .filter(Boolean)
        .join('\n\n');
    if (!reviewContext || !walkthroughs) return '';

    return `You are an expert exam-prep tutor writing one final detailed study guide after a completed quiz session.

Merge the internal group walkthroughs below, which already cover every missed question, into one clean guide.

Write Markdown only. No code fences, JSON, group labels, or process notes.

Use these exact section headers:
${RESULTS_AI_REVIEW_REQUIRED_SECTIONS.map((section) => `## ${section}`).join('\n')}

Rules:
- Make "## Study Guide" the longest section: concept blocks with the core rule, why the chosen answer was wrong, why the correct answer is right, the common trap, exam-style cues, and the missed question numbers.
- Merge duplicate concepts across groups instead of repeating them, but keep every missed question number somewhere in the guide.
- Call out weak source titles when the source breakdown supports it.
- Keep facts exactly as the walkthroughs state them; do not invent lecture facts.
- Finish completely; do not end mid-sentence, mid-bullet, or mid-table.

${formatResultsAiReviewSessionBlock(reviewContext)}

Group walkthroughs:
${walkthroughs}`;
}

function buildResultsAiStudyGuideFallback(context = null, aggregate = null) {
    const reviewContext = context && typeof context === 'object' ? context : latestResultsReviewContext;
    const studyData = aggregate && typeof aggregate === 'object' ? aggregate : null;
    if (!reviewContext || !studyData) return '';

    const weakSources = reviewContext.sourceSummaries
        .slice()
        .sort((a, b) => (a.pct - b.pct) || (b.wrong - a.wrong))
        .slice(0, 4);

    const focusAreaLines = studyData.focusAreas.length
        ? studyData.focusAreas.map((area) => {
            const patternText = area.missPatterns.length ? ` Patterns: ${area.missPatterns.join('; ')}.` : '';
            const sourceText = area.sourceTitles.length ? ` Sources: ${area.sourceTitles.join(', ')}.` : '';
            const questionText = area.questionNumbers.length ? ` Missed questions: ${area.questionNumbers.join(', ')}.` : '';
            return `- **${area.topic}**: ${area.whyItMatters || 'This theme showed up repeatedly in the missed questions.'}${patternText}${sourceText}${questionText}`;
        }).join('\n')
        : '- No clear focus areas were extracted from the chunked review.';

    const recurringThemeLines = studyData.recurringThemes.length
        ? studyData.recurringThemes.map((entry) => `- ${entry}`).join('\n')
        : '- The misses do not cluster into one narrow theme, so review the focus areas above first.';

    const conceptTrapLines = studyData.conceptTraps.length
        ? studyData.conceptTraps.map((entry) => `- ${entry}`).join('\n')
        : '- Recheck the distinctions between the answer you chose and the answer that was actually correct.';

    const studyGuideLines = studyData.studyActions.length
        ? studyData.studyActions.map((entry) => `- ${entry}`).join('\n')
        : '- Revisit the linked source pages for the missed questions and rebuild the core definitions before doing another pass.';

    const priorityLines = weakSources.length
        ? weakSources.map((entry) => `- **${entry.title}**: ${entry.pct}% accuracy (${entry.wrong} wrong). Review this source first.`).join('\n')
        : '- Start with the largest focus area above and rework those questions before widening the review.';

    const selfCheckLines = studyData.selfChecks.length
        ? studyData.selfChecks.map((entry) => `- ${entry}`).join('\n')
        : '- Explain the correct reasoning for each missed question out loud without looking at the choices.';

    return `## Focus Areas
${focusAreaLines}

## Recurring Themes
${recurringThemeLines}

## Why These Misses Happened
${conceptTrapLines}

## Study Guide
${studyGuideLines}

## What To Review First
${priorityLines}

## Self-Check
${selfCheckLines}`;
}

function syncResultsExpandButtonState() {
    const btn = $('results-expand-btn');
    if (!btn) return;
    const isExpanded = typeof isQuizCardExpanded !== 'undefined' && !!isQuizCardExpanded;
    btn.textContent = isExpanded ? 'Compact' : 'Expand';
    btn.closest('.ss-shell')?.classList.toggle('is-expanded', isExpanded);
    btn.setAttribute('aria-pressed', isExpanded ? 'true' : 'false');
}

function toggleResultsFullscreen() {
    if (typeof toggleQuizCardExpanded !== 'function') return false;
    const nextExpanded = !(typeof isQuizCardExpanded !== 'undefined' && !!isQuizCardExpanded);
    // The summary glides open like Lecture Finder and takes the rail's width while it is expanded.
    const changed = toggleQuizCardExpanded(nextExpanded, { animate: true, holdRail: nextExpanded });
    requestAnimationFrame(syncResultsExpandButtonState);
    return changed;
}

function exitResultsFullscreenIfOpen() {
    const resultsVisible = !!els?.resultsView && getComputedStyle(els.resultsView).display !== 'none';
    if (!resultsVisible) return;
    if (typeof isQuizCardExpanded === 'undefined' || !isQuizCardExpanded) return;
    if (typeof toggleQuizCardExpanded === 'function') {
        toggleQuizCardExpanded(false);
    }
}

function getResultsAiReviewDownloadFilename(record = null) {
    const source = record && typeof record === 'object' ? record : latestResultsAiReviewOutput;
    const stamp = new Date(source?.generatedAt || Date.now()).toISOString().slice(0, 19).replace(/[T:]/g, '-');
    return `Memora_Study_Guide_${stamp}.pdf`;
}

function buildResultsAiReviewPlainText(value = '') {
    return String(value || '')
        .replace(/\r\n?/g, '\n')
        .replace(/^##\s+/gm, '')
        .replace(/^[-*]\s+/gm, '• ')
        .replace(/\*\*(.*?)\*\*/g, '$1')
        .replace(/`([^`]+)`/g, '$1')
        .replace(/<\/?[^>]+>/g, ' ')
        .replace(/\n{3,}/g, '\n\n')
        .trim();
}

async function buildResultsAiReviewPdfBlob(record = null) {
    const source = record && typeof record === 'object' ? record : latestResultsAiReviewOutput;
    if (!source?.text) throw new Error('No study guide is available to export.');
    const jsPDF = await getMemoraJsPdfConstructor();

    const doc = new jsPDF({ unit: 'pt', format: 'letter' });
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const marginLeft = 42;
    const marginRight = 42;
    const marginTop = 50;
    const marginBottom = 42;
    const maxWidth = pageWidth - marginLeft - marginRight;
    let y = marginTop;

    const writeBlock = (text, {
        fontSize = 11,
        fontStyle = 'normal',
        lineGap = 4,
        gapAfter = 8
    } = {}) => {
        const content = String(text || '').trim();
        if (!content) return;

        doc.setFont('helvetica', fontStyle);
        doc.setFontSize(fontSize);
        const lineHeight = fontSize + lineGap;
        const lines = doc.splitTextToSize(content, maxWidth);

        lines.forEach((line) => {
            if (y + lineHeight > pageHeight - marginBottom) {
                doc.addPage();
                y = marginTop;
            }
            doc.text(line, marginLeft, y);
            y += lineHeight;
        });

        y += gapAfter;
    };

    writeBlock(source.title || 'Study Guide', {
        fontSize: 16,
        fontStyle: 'bold',
        lineGap: 5,
        gapAfter: 12
    });
    if (source.generatedAt) {
        writeBlock(`Generated: ${new Date(source.generatedAt).toLocaleString()}`, {
            fontSize: 10,
            lineGap: 3,
            gapAfter: 4
        });
    }
    if (source.modelLabel) {
        writeBlock(`Build: ${source.modelLabel}`, {
            fontSize: 10,
            lineGap: 3,
            gapAfter: 10
        });
    }
    writeBlock(buildResultsAiReviewPlainText(source.text), {
        fontSize: 11,
        lineGap: 4,
        gapAfter: 0
    });

    return doc.output('blob');
}

async function downloadResultsAiReviewPdf() {
    if (!latestResultsAiReviewOutput?.text) {
        showPopup('Generate a study guide before exporting it as a PDF.', null, true);
        return false;
    }
    if (window.openMemoraGuidePdfPreview?.({ markdown: latestResultsAiReviewOutput.text, title: 'Study report', sourceLabel: latestResultsReviewContext?.quizTitles?.join(', ') || '' })) return true;
    try {
        const blob = await buildResultsAiReviewPdfBlob(latestResultsAiReviewOutput);
        triggerBlobDownload(blob, getResultsAiReviewDownloadFilename(latestResultsAiReviewOutput));
        return true;
    } catch (error) {
        showPopupText(`Could not export the study guide PDF: ${error?.message || 'Unknown error'}`, null, true);
        return false;
    }
}

function closeResultsAiReviewModal() {
    const modal = $('results-ai-review-modal');
    if (modal) modal.classList.remove('active');
}

function openResultsAiReviewModal({ title = 'Final Review', text = '', modelLabel = '' } = {}) {
    if (!text) return;
    let modal = $('results-ai-review-modal');
    if (!modal) {
        modal = document.createElement('div');
        modal.id = 'results-ai-review-modal';
        modal.className = 'ai-popup-modal';
        modal.innerHTML = `
            <div class="ai-popup-content results-ai-review-modal-card">
                <div id="results-ai-review-inner" class="results-ai-review-modal-shell"></div>
            </div>
        `;
        document.body.appendChild(modal);
    }

    const inner = $('results-ai-review-inner');
    if (!inner) return;
    const renderedContent = (typeof formatAIResponse === 'function')
        ? formatAIResponse(text)
        : `<pre style="white-space:pre-wrap; margin:0;">${escapeHtml(text)}</pre>`;
    const modelLabelMarkup = modelLabel
        ? `<span class="results-ai-review-model-chip">${escapeHtml(modelLabel)}</span>`
        : '';
    inner.innerHTML = `
        <div class="results-ai-review-modal-header">
            <span class="results-ai-review-modal-heading">
                <strong class="gradient-text results-ai-review-modal-title">${escapeHtml(title)}</strong>
                ${modelLabelMarkup}
            </span>
            <span class="results-ai-review-modal-header-actions">
                <button class="glass-btn results-ai-review-download-btn" type="button" onclick="downloadResultsAiReviewPdf()">Download PDF</button>
                <button class="theme-close-btn" onclick="closeResultsAiReviewModal()">&times;</button>
            </span>
        </div>
        <div class="results-ai-review-modal-body ai-response-content hide-scrollbar">${renderedContent}</div>
    `;
    modal.classList.add('active');
}

function openLatestResultsAiReview() {
    if (!latestResultsAiReviewOutput?.text) {
        showPopup('No study guide has been generated for this session yet.', null, true);
        return false;
    }
    openResultsAiReviewModal(latestResultsAiReviewOutput);
    return true;
}

function renderResultsAiReviewInline() {
    const host = $('results-ai-review-output');
    if (!host) return;

    const record = latestResultsAiReviewOutput;
    if (!record?.text) {
        host.hidden = true;
        host.innerHTML = '';
        return;
    }

    const renderedContent = (typeof formatAIResponse === 'function')
        ? formatAIResponse(record.text)
        : `<pre style="white-space:pre-wrap; margin:0;">${escapeHtml(record.text)}</pre>`;
    const generatedLabel = record.generatedAt
        ? new Date(record.generatedAt).toLocaleString()
        : '';
    const metaParts = [
        generatedLabel ? `Generated ${generatedLabel}` : '',
        record.modelLabel || ''
    ].filter(Boolean);

    host.hidden = false;
    host.innerHTML = `
        <div class="results-ai-review-output-header">
            <div class="results-ai-review-output-meta">${escapeHtml(metaParts.join(' | ') || 'Study guide ready')}</div>
            <div class="results-ai-review-output-actions">
                <button class="glass-btn results-ai-review-inline-btn" type="button" onclick="openLatestResultsAiReview()">Open Popup</button>
                <button class="glass-btn results-ai-review-inline-btn" type="button" onclick="downloadResultsAiReviewPdf()">Download PDF</button>
            </div>
        </div>
        <div class="results-ai-review-output-body hide-scrollbar">${renderedContent}</div>
    `;
}

function setResultsAiReviewButtonsState() {
    const studyGuideBtn = $('results-ai-review-btn');
    const exportBtn = $('results-export-wrong-btn');
    const hasMisses = !!(latestResultsReviewContext && latestResultsReviewContext.missedCount > 0);
    const isBusy = !!resultsAiReviewBusyMode;
    const isUnavailable = !hasMisses || isBusy;

    if (studyGuideBtn) {
        studyGuideBtn.disabled = false;
        studyGuideBtn.setAttribute('aria-disabled', isUnavailable ? 'true' : 'false');
        studyGuideBtn.classList.toggle('is-disabled', isUnavailable);
        studyGuideBtn.textContent = resultsAiReviewBusyMode
            ? 'Building Study Guide...'
            : (latestResultsAiReviewOutput?.text ? 'Regenerate Study Guide' : 'Generate Study Guide');
    }
    if (exportBtn) {
        exportBtn.disabled = false;
        exportBtn.setAttribute('aria-disabled', isUnavailable ? 'true' : 'false');
        exportBtn.classList.toggle('is-disabled', isUnavailable);
    }
}

function setResultsWrongExportMode(mode = 'question_only') {
    resultsWrongExportMode = mode === 'question_with_answers' ? 'question_with_answers' : 'question_only';
    const questionOnlyBtn = $('results-export-mode-question-only');
    const withAnswersBtn = $('results-export-mode-with-answers');
    if (questionOnlyBtn) questionOnlyBtn.classList.toggle('active', resultsWrongExportMode === 'question_only');
    if (withAnswersBtn) withAnswersBtn.classList.toggle('active', resultsWrongExportMode === 'question_with_answers');
}

async function generateResultsAiReview({ preview = false, length = 'detailed' } = {}) {
    // A Study Guide built from a finished quiz is still a Study Guide; free profiles get the key offer.
    if (typeof requireMemoraProductFeature === 'function' && !requireMemoraProductFeature('study-guide')) return;
    if (resultsAiReviewBusyMode) {
        showPopup('An AI study guide is already running. Wait for it to finish before starting another one.', null, true);
        return;
    }
    if (!latestResultsReviewContext || latestResultsReviewContext.missedCount <= 0) {
        showPopup('No missed questions are available for the study guide from this run.', null, true);
        return;
    }
    if (typeof callGeminiFlash !== 'function') {
        showPopup('AI study guide is unavailable in this build.', null, true);
        return;
    }

    const reviewContext = latestResultsReviewContext;
    const missedItems = Array.isArray(reviewContext?.missedQuestions) ? reviewContext.missedQuestions : [];
    if (missedItems.length === 0) {
        showPopup('Could not build the AI study guide request.', null, true);
        return;
    }

    resultsAiReviewBusyMode = 'study_guide';
    setResultsAiReviewButtonsState();

    try {
        const condensed = length === 'condensed';
        showPopup(condensed ? 'Building your study report...' : 'Building Study Guide...', null, false, 90000);
        const studyGuideBuild = await generateResultsAiStudyGuideWithQualityCheck(reviewContext, { length });
        const finalStudyGuide = String(studyGuideBuild?.text || '').trim();

        if (!finalStudyGuide) {
            throw new Error('The AI study guide returned an empty response.');
        }

        latestResultsAiReviewOutput = {
            title: condensed ? 'Study report' : 'Study Guide',
            length,
            text: finalStudyGuide,
            modelLabel: getResultsAiReviewModelLabel(),
            generatedAt: Date.now()
        };
        const savedGuide = await saveMemoraGuideToDatabase({
            guideType: 'results-review',
            sessionId: currentSessionId,
            title: condensed ? 'Results Study Report' : 'Results Study Guide',
            sourceLabel: reviewContext.quizTitles.join(', ') || reviewContext.sourceTitles.join(', ') || 'Quiz results',
            filenameSeed: 'results-study-guide',
            markdown: finalStudyGuide,
            generatedAt: latestResultsAiReviewOutput.generatedAt,
            modelLabel: latestResultsAiReviewOutput.modelLabel
        });
        if (savedGuide?.id) latestResultsAiReviewOutput.savedGuideId = savedGuide.id;
        renderResultsAiReviewInline();
        setResultsAiReviewButtonsState();
        if (typeof dismissPopup === 'function') dismissPopup();
        if (preview) await downloadResultsAiReviewPdf();
        else openResultsAiReviewModal(latestResultsAiReviewOutput);
    } catch (error) {
        if (typeof dismissPopup === 'function') dismissPopup();
        showPopupText(`Failed to build the study guide: ${error?.message || 'Unknown error'}`, null, true);
    } finally {
        resultsAiReviewBusyMode = '';
        setResultsAiReviewButtonsState();
    }
}

function exportResultsWrongAnswers() {
    if (resultsAiReviewBusyMode) {
        showPopup('Wait for the current AI study guide request to finish before exporting wrong answers.', null, true);
        return;
    }
    const context = latestResultsReviewContext;
    if (!context || !Array.isArray(context.missedQuestions) || context.missedQuestions.length === 0) {
        showPopup('No missed questions are available to export from this run.', null, true);
        return;
    }

    const lines = [
        'Memoraful Wrong Answers Export',
        `Generated: ${new Date().toLocaleString()}`,
        `Accuracy: ${context.pct}%`,
        `Missed Questions: ${context.missedCount}`,
        `Quiz Titles: ${context.quizTitles.join(', ') || 'None listed'}`,
        `PDF Titles: ${context.pdfTitles.join(', ') || 'None listed'}`,
        ''
    ];

    context.missedQuestions.forEach((item) => {
        const questionRecord = questions[item.realIndex] || null;
        const fullStem = stripResultsReviewText(questionRecord?.q || questionRecord?.question || item.stem);
        const fullChoices = Array.isArray(questionRecord?.c)
            ? questionRecord.c.map((choice, choiceIdx) => ({
                letter: String.fromCharCode(65 + choiceIdx),
                text: stripResultsReviewText(choice)
            }))
            : item.choices;
        // The answer captured with this result, not a later practice answer.
        const selectedIdx = Number.isInteger(item.selectedIndex) ? item.selectedIndex : -1;
        const selectedLetter = selectedIdx >= 0 ? String.fromCharCode(65 + selectedIdx) : '';
        const selectedChoiceText = selectedIdx >= 0 && selectedIdx < fullChoices.length ? fullChoices[selectedIdx].text : '';
        const correctIdx = Number.isInteger(questionRecord?.a) ? questionRecord.a : -1;
        const correctLetter = correctIdx >= 0 ? String.fromCharCode(65 + correctIdx) : item.correctLetter;
        const correctChoiceText = correctIdx >= 0 && correctIdx < fullChoices.length
            ? fullChoices[correctIdx].text
            : item.correctAnswer;
        lines.push(`${item.order}. ${fullStem}`);
        lines.push(`Source: ${item.sourceTitle}`);
        if (resultsWrongExportMode === 'question_with_answers') {
            lines.push('Choices:');
            fullChoices.forEach((choice) => {
                lines.push(`- ${choice.letter}. ${choice.text}`);
            });
            if (selectedLetter && selectedChoiceText) {
                lines.push(`Your answer: ${selectedLetter}. ${selectedChoiceText}`);
            }
            lines.push(`Correct answer: ${correctLetter}. ${correctChoiceText}`);
        }
        lines.push('');
    });

    const content = lines.join('\n');
    const modeLabel = resultsWrongExportMode === 'question_with_answers' ? 'wrong_answers_with_answers' : 'wrong_questions_only';
    const filename = `memora_${modeLabel}_${new Date().toISOString().slice(0, 10)}.txt`;
    triggerBlobDownload(new Blob([content], { type: 'text/plain;charset=utf-8' }), filename);
    showPopup(`Exported ${context.missedCount} wrong question${context.missedCount === 1 ? '' : 's'}.`, null, false);
}

function openResultsReviewQuestion(realIdx) {
    const questionIndex = Number(realIdx);
    if (!Number.isInteger(questionIndex) || questionIndex < 0 || questionIndex >= questions.length) return false;

    exitResultsFullscreenIfOpen();
    if (!Array.isArray(filteredIndices) || !filteredIndices.includes(questionIndex)) {
        resetFilter();
    }

    const nextIndex = filteredIndices.indexOf(questionIndex);
    currentQIndex = nextIndex >= 0 ? nextIndex : 0;
    els.quizContent.style.display = 'block';
    els.resultsView.style.display = 'none';
    renderQuestion();
    return true;
}

function quizResultsSourceQuestions(sourceKey = '') {
    const normalizedSourceKey = String(sourceKey || '').trim();
    const context = latestResultsReviewContext;
    if (!context || !Array.isArray(context.sessionQuestionIndices) || !normalizedSourceKey) {
        showPopup('This source is unavailable for source-specific review.', null, true);
        return false;
    }

    const sourceIndices = context.sessionQuestionIndices.filter((idx) => String(questions[idx]?.sourceFile || 'Unknown').trim() === normalizedSourceKey);
    if (sourceIndices.length === 0) {
        showPopup('No questions from that source are available in this session.', null, true);
        return false;
    }

    isAdaptiveReviewMode = false;
    syncAdaptiveModeUI();
    filteredIndices = sourceIndices.slice();
    currentQIndex = 0;
    exitResultsFullscreenIfOpen();
    els.filterBanner.style.display = 'flex';
    const bannerText = getFilterBannerTextEl();
    const sourceLabel = formatResultsReviewSourceTitle(normalizedSourceKey);
    if (bannerText) {
        bannerText.innerHTML = `<b>Source Quiz:</b> ${escapeHtml(sourceLabel)} (${sourceIndices.length} question${sourceIndices.length === 1 ? '' : 's'}).`;
    }
    setAdaptiveSnapshotButtonVisible(false);
    setAdaptiveSkipUndoneButtonVisible(false);
    els.quizContent.style.display = 'block';
    els.resultsView.style.display = 'none';
    renderQuestion();
    return true;
}

let latestSessionReportData = null;
let sessionSummaryPrimaryAction = null;
let sessionReportReturnFocus = null;

function getSessionSummaryAnswerRecord(realIdx) {
    const q = questions[realIdx] || {};
    const choices = Array.isArray(q.c) ? q.c.map((choice) => stripResultsReviewText(choice)) : [];
    const selected = Number.isInteger(userAnswers[realIdx]) ? userAnswers[realIdx] : -1;
    const correct = Number.isInteger(q.a) ? q.a : -1;
    return {
        stem: stripResultsReviewText(q.q || q.question || ''),
        choices,
        selected,
        correct,
        isCorrect: selected >= 0 && selected === correct
    };
}

function getSessionSummaryQuestionMeta(realIdx, { includeSource = false } = {}) {
    const q = questions[realIdx] || {};
    const sessionOrder = latestSessionReportData?.sessionIndices || filteredIndices || [];
    const position = sessionOrder.indexOf(realIdx);
    const parts = [`Question ${position >= 0 ? position + 1 : realIdx + 1}`];
    if (includeSource) parts.push(formatResultsReviewSourceTitle(String(q.sourceFile || 'Unknown').trim()));
    const citation = stripResultsReviewText(q.add || '');
    if (citation && citation.length <= 48) parts.push(citation);
    return parts.join(' · ');
}

function openFirstUnansweredSessionQuestion() {
    const sessionOrder = latestSessionReportData?.sessionIndices || [];
    const firstUnanswered = sessionOrder.find((idx) => userAnswers[idx] === undefined);
    if (Number.isInteger(firstUnanswered)) return openResultsReviewQuestion(firstUnanswered);
    return returnToQuizFromResults();
}

// Coverage comes before mastery: a set is not "clear" while most of it is unanswered.
function getSessionSummaryNextMove({ answered, incorrect, unanswered = 0, sourceRows }) {
    if (!answered) {
        return {
            title: 'No answers recorded yet.',
            note: 'Answer a few questions to see where to focus next.',
            primaryLabel: 'Back to quiz',
            run: () => returnToQuizFromResults()
        };
    }
    if (unanswered > 0) {
        return {
            title: `Finish the ${unanswered} unanswered question${unanswered === 1 ? '' : 's'}.`,
            note: incorrect
                ? `You answered ${answered} of ${answered + unanswered} and missed ${incorrect}. Finish the set, then review the misses.`
                : `You answered ${answered} of ${answered + unanswered}, all correctly. Complete the set before moving on.`,
            primaryLabel: `Answer the remaining ${unanswered}`,
            run: () => openFirstUnansweredSessionQuestion()
        };
    }
    if (!incorrect) {
        const source = sourceRows[0] || null;
        return {
            title: 'You’re clear on this set.',
            note: sourceRows.length > 1
                ? 'No weak source was detected. Re-quiz a source or move on.'
                : 'No weak source was detected. Re-quiz the lecture or move on.',
            primaryLabel: 'Quiz source again',
            run: () => (source ? quizResultsSourceQuestions(source.src) : returnToQuizFromResults())
        };
    }
    const weakest = sourceRows.slice().sort((a, b) => (b.wrong - a.wrong) || (a.pct - b.pct))[0];
    const weakestTitle = weakest ? (weakest.displayTitle || weakest.shortName) : '';
    return {
        title: 'Review the missed source before another pass.',
        note: weakest && sourceRows.length > 1
            ? `${weakestTitle} accounted for most misses in this session.`
            : `${incorrect} miss${incorrect === 1 ? '' : 'es'} in this session are worth revisiting.`,
        primaryLabel: 'Review missed questions',
        run: () => resetAndReview()
    };
}

function runSessionSummaryPrimaryAction() {
    if (typeof sessionSummaryPrimaryAction === 'function') sessionSummaryPrimaryAction();
}

function reviewSessionSummaryAnswers() {
    const sessionOrder = latestSessionReportData?.sessionIndices || [];
    const firstAnswered = sessionOrder.find((idx) => userAnswers[idx] !== undefined);
    if (Number.isInteger(firstAnswered)) return openResultsReviewQuestion(firstAnswered);
    return returnToQuizFromResults();
}

function buildSessionSummaryMarkup({ exam = false, pct, grade, summaryMessage, answered, correct, incorrect, unanswered, sourceRows, wrong, resultsExpanded, extraMarkup = '' }) {
    latestSessionReportData = {
        generatedAt: Date.now(),
        pct,
        answered,
        correct,
        incorrect,
        unanswered,
        sourceRows: sourceRows.slice(),
        sessionIndices: filteredIndices.slice(),
        wrong: wrong.slice(),
        quizTitles: Array.isArray(latestResultsReviewContext?.quizTitles) ? latestResultsReviewContext.quizTitles.slice() : []
    };
    const next = getSessionSummaryNextMove({ answered, incorrect, unanswered, sourceRows });
    sessionSummaryPrimaryAction = next.run;
    const tone = !answered ? '' : pct >= 80 ? 'good' : 'mixed';
    const gradeTone = !answered ? 'none' : pct >= 80 ? '' : 'mixed';
    const sourceCount = sourceRows.length;
    const sourceHint = sourceCount
        ? `${sourceCount} source${sourceCount === 1 ? '' : 's'} used · ${incorrect ? 'attention recommended' : 'no weak source detected'}`
        : 'No answered sources yet';

    return `
        <div class="ss-shell${resultsExpanded ? ' is-expanded' : ''}">
            <header class="ss-topbar">
                <div class="ss-topbar-copy">
                    <span class="ss-kicker">${exam ? 'Exam submitted' : 'Quiz complete'}</span>
                    <h2 class="ss-title">${exam ? 'Exam results' : 'Session summary'}</h2>
                </div>
                <div class="ss-topbar-actions">
                    <button class="ss-btn" id="results-expand-btn" type="button" onclick="toggleResultsFullscreen()" aria-pressed="${resultsExpanded ? 'true' : 'false'}">${resultsExpanded ? 'Compact' : 'Expand'}</button>
                    <div class="ss-report-picker">
                        <button class="ss-btn primary" id="results-export-report-btn" type="button" aria-haspopup="menu" aria-expanded="false" aria-controls="results-report-menu" onclick="toggleResultsReportMenu()">
                            Export report
                            <span class="ss-pdf-chip">PDF</span>
                        </button>
                        <div class="ss-report-menu" id="results-report-menu" role="menu" aria-label="Export report" hidden>
                            <button type="button" role="menuitem" data-report-kind="study" data-report-length="condensed" onclick="chooseResultsReport('study', 'condensed')"><strong>Study report · Condensed</strong><span>1-2 pages: what to fix and focus on</span></button>
                            <button type="button" role="menuitem" data-report-kind="study" data-report-length="detailed" onclick="chooseResultsReport('study', 'detailed')"><strong>Study report · Detailed</strong><span>Full concept walkthrough of every miss</span></button>
                            <button type="button" role="menuitem" data-report-kind="questions" onclick="chooseResultsReport('questions')"><strong>Questions report</strong><span>Every question with your answer</span></button>
                        </div>
                    </div>
                </div>
            </header>

            <div class="ss-body">
                <section class="ss-hero">
                    <div class="ss-panel ss-score-panel">
                        <div class="ss-score-number ${tone}">${exam || answered ? `${pct}%` : '—'}</div>
                        <div class="ss-score-meta">
                            <span class="ss-grade ${gradeTone}">${exam ? 'Exam score' : escapeHtml(grade.label)}</span>
                            <div class="ss-score-sub">${escapeHtml(summaryMessage)}</div>
                        </div>
                    </div>
                    <div class="ss-panel ss-next-panel">
                        <div class="ss-next-copy">
                            <span class="ss-label">Next best move</span>
                            <span class="ss-next-title">${escapeHtml(next.title)}</span>
                            <span class="ss-next-note">${escapeHtml(next.note)}</span>
                        </div>
                        <div class="ss-next-actions">
                            ${answered ? '<button class="ss-btn" type="button" onclick="reviewSessionSummaryAnswers()">Review answers</button>' : ''}
                            <button class="ss-btn primary" type="button" onclick="runSessionSummaryPrimaryAction()">${escapeHtml(next.primaryLabel)}</button>
                        </div>
                    </div>
                </section>

                <section class="ss-metrics">
                    <div class="ss-metric"><span class="ss-metric-label">Answered</span><span class="ss-metric-value">${answered}</span></div>
                    <div class="ss-metric"><span class="ss-metric-label">Correct</span><span class="ss-metric-value good">${correct}</span></div>
                    <div class="ss-metric"><span class="ss-metric-label">Incorrect</span><span class="ss-metric-value bad">${incorrect}</span></div>
                    <div class="ss-metric"><span class="ss-metric-label">Unanswered</span><span class="ss-metric-value">${unanswered}</span></div>
                </section>

                <section class="ss-panel ss-review-panel">
                    <div class="ss-review-head">
                        <div class="ss-review-head-copy">
                            <strong>Sources</strong>
                            <span>${escapeHtml(sourceHint)}</span>
                        </div>
                        <button class="ss-btn compact" id="results-sources-toggle" type="button" onclick="toggleSessionSummarySources()">Expand all</button>
                    </div>
                    <div class="ss-source-list" id="results-source-groups"></div>
                </section>

                ${extraMarkup}

                <footer class="ss-footer">
                    <button class="ss-btn" type="button" onclick="returnToQuizFromResults()">Back to quiz</button>
                </footer>
            </div>
        </div>
    `;
}

function syncSessionSummarySourcesToggle() {
    const btn = $('results-sources-toggle');
    if (!btn) return;
    const groups = Array.from(document.querySelectorAll('#results-source-groups .ss-source'));
    btn.hidden = groups.length === 0;
    btn.textContent = groups.length > 0 && groups.every((group) => group.open) ? 'Collapse all' : 'Expand all';
}

function toggleSessionSummarySources() {
    const groups = Array.from(document.querySelectorAll('#results-source-groups .ss-source'));
    const shouldOpen = groups.some((group) => !group.open);
    groups.forEach((group) => { group.open = shouldOpen; });
    syncSessionSummarySourcesToggle();
}

function renderResultsSourceWorkspace(sourceRows = []) {
    const host = $('results-source-groups');
    if (!host) return;
    host.innerHTML = '';

    if (!Array.isArray(sourceRows) || sourceRows.length === 0) {
        host.innerHTML = '<div class="ss-empty">No source breakdown is available for this run.</div>';
        syncSessionSummarySourcesToggle();
        return;
    }

    sourceRows.forEach((entry) => {
        const details = document.createElement('details');
        details.className = 'ss-source';
        const scoreTone = entry.pct >= 80 ? '' : entry.pct >= 50 ? 'mixed' : 'bad';

        const summary = document.createElement('summary');
        summary.className = 'ss-source-summary';
        summary.innerHTML = `
            <span class="ss-source-main">
                <span class="ss-source-name">${escapeHtml(entry.displayTitle || entry.shortName || 'Unknown')}</span>
                <span class="ss-source-meta">${entry.correct} correct · ${entry.wrong} wrong · ${entry.total} total</span>
            </span>
            <span class="ss-source-score ${scoreTone}">${entry.pct}%</span>
            <span class="ss-source-chevron" aria-hidden="true"></span>
        `;
        details.appendChild(summary);

        const content = document.createElement('div');
        content.className = 'ss-source-content';
        const answeredIndices = (entry.questionIndices || []).filter((idx) => userAnswers[idx] !== undefined);
        if (answeredIndices.length === 0) {
            content.innerHTML = '<div class="ss-empty">No answered questions under this source.</div>';
        }
        answeredIndices.forEach((realIdx) => {
            const record = getSessionSummaryAnswerRecord(realIdx);
            const stem = record.stem.length > 160 ? `${record.stem.slice(0, 160)}…` : record.stem;
            const row = document.createElement('button');
            row.type = 'button';
            row.className = 'ss-question-row';
            row.innerHTML = `
                <span class="ss-question-copy">
                    <strong>${escapeHtml(stem || `Question ${realIdx + 1}`)}</strong>
                    <span>${escapeHtml(getSessionSummaryQuestionMeta(realIdx))}</span>
                </span>
                <span class="ss-question-status ${record.isCorrect ? '' : 'bad'}">${record.isCorrect ? 'Correct' : 'Missed'}</span>
            `;
            row.addEventListener('click', () => openResultsReviewQuestion(realIdx));
            content.appendChild(row);
        });
        details.appendChild(content);
        details.addEventListener('toggle', syncSessionSummarySourcesToggle);
        host.appendChild(details);
    });
    syncSessionSummarySourcesToggle();
}

function buildSessionReportQuestionMarkup(realIdx) {
    const record = getSessionSummaryAnswerRecord(realIdx);
    const letter = (idx) => (idx >= 0 ? String.fromCharCode(65 + idx) : '—');
    const choicesMarkup = record.choices.map((choice, idx) => {
        const isSelected = idx === record.selected;
        const isCorrect = idx === record.correct;
        const classes = ['sr-choice'];
        if (isSelected) classes.push('selected');
        if (isCorrect) classes.push('correct');
        if (isSelected && !isCorrect) classes.push('wrong');
        const state = isSelected && isCorrect ? 'Chosen · Correct' : isSelected ? 'Chosen' : isCorrect ? 'Correct' : '';
        return `
            <div class="${classes.join(' ')}">
                <span class="sr-choice-letter">${letter(idx)}</span>
                <span class="sr-choice-copy">${escapeHtml(choice)}</span>
                ${state ? `<span class="sr-choice-state">${state}</span>` : ''}
            </div>
        `;
    }).join('');
    return `
        <article class="sr-question">
            <div class="sr-q-top">
                <div class="sr-q-title">${escapeHtml(record.stem || `Question ${realIdx + 1}`)}</div>
                <span class="sr-q-tag ${record.isCorrect ? '' : 'bad'}">${record.isCorrect ? 'Correct' : 'Missed'}</span>
            </div>
            <div class="sr-q-meta">${escapeHtml(getSessionSummaryQuestionMeta(realIdx, { includeSource: true }))}</div>
            ${choicesMarkup ? `<div class="sr-choice-list">${choicesMarkup}</div>` : ''}
            <div class="sr-answer-summary">
                <span><strong>Chosen answer:</strong> ${letter(record.selected)}</span>
                <span><strong>Correct answer:</strong> ${letter(record.correct)}</span>
            </div>
        </article>
    `;
}

function getSessionReportTitle(data) {
    const quizTitle = (data.quizTitles || []).map((title) => String(title || '').trim()).find(Boolean);
    return quizTitle ? `${quizTitle} Report` : 'Quiz Session Report';
}

function buildSessionReportMarkup(data) {
    const generated = new Date(data.generatedAt);
    const dateLabel = generated.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
    const sourceNames = data.sourceRows.map((row) => row.displayTitle || row.shortName).filter(Boolean);
    const hasMisses = data.wrong.length > 0;
    // The Questions report promises every question, so correct answers are listed beside the misses.
    const questionIndices = data.sessionIndices.filter((idx) => userAnswers[idx] !== undefined);

    let interpretation = 'No questions were answered in this session.';
    if (data.answered && !hasMisses) {
        interpretation = 'No weak source was detected in this session. Performance was complete on all answered items.';
    } else if (hasMisses) {
        const weakest = data.sourceRows.slice().sort((a, b) => (b.wrong - a.wrong) || (a.pct - b.pct))[0];
        interpretation = data.sourceRows.length > 1 && weakest
            ? `Most misses came from ${weakest.displayTitle || weakest.shortName} (${weakest.wrong} of ${data.incorrect}). Review the missed questions and their cited source pages before repeating this set.`
            : `${data.incorrect} of ${data.answered} answered question${data.answered === 1 ? '' : 's'} were missed. Review the missed questions and their cited source pages before repeating this set.`;
    }

    const sourceTableRows = data.sourceRows.map((row) => `
        <tr>
            <td><span class="sr-source-name">${escapeHtml(row.displayTitle || row.shortName || 'Unknown')}</span></td>
            <td class="num">${row.correct}</td>
            <td class="num">${row.wrong}</td>
            <td class="num">${row.pct}%</td>
        </tr>
    `).join('');

    return `
        <article class="sr-page" id="session-report-page">
            <div class="sr-brandline">
                <div class="sr-brand">
                    <strong>Memoraful</strong>
                    <span>Quiz Session Report</span>
                </div>
                <div class="sr-date">Session report<br><span>${escapeHtml(dateLabel)}</span></div>
            </div>

            <section class="sr-titleblock">
                <span class="sr-eyebrow">Session complete</span>
                <h1 class="sr-title">${escapeHtml(getSessionReportTitle(data))}</h1>
                <div class="sr-subtitle">${escapeHtml(sourceNames.join(' · ') || 'No source recorded')}</div>
            </section>

            <section class="sr-score-grid">
                <div class="sr-score-card primary">
                    <span class="sr-score-label">Score</span>
                    <span class="sr-score-value ${data.answered && data.pct >= 80 ? 'good' : ''}">${data.answered ? `${data.pct}%` : '—'}</span>
                </div>
                <div class="sr-score-card"><span class="sr-score-label">Correct</span><span class="sr-score-value good">${data.correct}</span></div>
                <div class="sr-score-card"><span class="sr-score-label">Incorrect</span><span class="sr-score-value bad">${data.incorrect}</span></div>
                <div class="sr-score-card"><span class="sr-score-label">Unanswered</span><span class="sr-score-value">${data.unanswered}</span></div>
            </section>

            <section class="sr-section">
                <div class="sr-section-head">
                    <h3>Performance by source</h3>
                    <span>${data.sourceRows.length} source${data.sourceRows.length === 1 ? '' : 's'}</span>
                </div>
                ${sourceTableRows ? `
                <table class="sr-table">
                    <thead><tr><th>Source</th><th class="num">Correct</th><th class="num">Wrong</th><th class="num">Score</th></tr></thead>
                    <tbody>${sourceTableRows}</tbody>
                </table>` : '<div class="sr-empty">No source breakdown is available for this run.</div>'}
            </section>

            <div class="sr-callout">
                <div class="sr-callout-label">Session interpretation</div>
                <div class="sr-callout-copy">${escapeHtml(interpretation)}</div>
            </div>

            <section class="sr-section">
                <div class="sr-section-head">
                    <h3>Every question</h3>
                    <span>${questionIndices.length} item${questionIndices.length === 1 ? '' : 's'}${hasMisses ? ` · ${data.wrong.length} missed` : ''}</span>
                </div>
                <div class="sr-question-list">
                    ${questionIndices.map((idx) => buildSessionReportQuestionMarkup(idx)).join('') || '<div class="sr-empty">No answered questions to list.</div>'}
                </div>
            </section>

            <footer class="sr-footer">
                <span><strong>Memoraful</strong> · Session report generated from recorded quiz results</span>
                <span>${escapeHtml(generated.toLocaleString())}</span>
            </footer>
        </article>
    `;
}

function ensureSessionReportPreview() {
    let backdrop = $('session-report-backdrop');
    if (backdrop) return backdrop;
    backdrop = document.createElement('div');
    backdrop.id = 'session-report-backdrop';
    backdrop.className = 'session-report-backdrop';
    backdrop.innerHTML = `
        <section class="session-report-modal" role="dialog" aria-modal="true" aria-labelledby="session-report-heading">
            <header class="session-report-header">
                <div class="session-report-header-copy">
                    <strong id="session-report-heading">Report preview</strong>
                    <span>A4 · PDF · generated from this session’s recorded results</span>
                </div>
                <div class="session-report-header-actions">
                    <button class="session-report-close" id="session-report-close" type="button">Close</button>
                    <button class="session-report-save" id="session-report-save" type="button">Save PDF</button>
                </div>
            </header>
            <div class="session-report-workspace" id="session-report-workspace"></div>
        </section>
    `;
    document.body.appendChild(backdrop);
    backdrop.addEventListener('click', (event) => {
        if (event.target === backdrop) closeSessionReportPreview();
    });
    $('session-report-close').addEventListener('click', closeSessionReportPreview);
    $('session-report-save').addEventListener('click', saveSessionReportPdf);
    document.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape' || !backdrop.classList.contains('open')) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        closeSessionReportPreview();
    }, true);
    return backdrop;
}

let sessionReportRailRelease = null;

function closeResultsReportMenu() {
    const menu = $('results-report-menu');
    if (menu) menu.hidden = true;
    $('results-export-report-btn')?.setAttribute('aria-expanded', 'false');
    document.removeEventListener('pointerdown', onResultsReportMenuPointer, true);
    document.removeEventListener('keydown', onResultsReportMenuKey, true);
}

function onResultsReportMenuPointer(event) {
    if (!event.target?.closest?.('.ss-report-picker')) closeResultsReportMenu();
}

function onResultsReportMenuKey(event) {
    const items = Array.from(document.querySelectorAll('#results-report-menu [role="menuitem"]:not(:disabled)'));
    if (event.key === 'Escape') {
        event.preventDefault();
        event.stopImmediatePropagation();
        closeResultsReportMenu();
        $('results-export-report-btn')?.focus();
    } else if ((event.key === 'ArrowDown' || event.key === 'ArrowUp') && items.length) {
        event.preventDefault();
        const step = event.key === 'ArrowDown' ? 1 : -1;
        items[(items.indexOf(document.activeElement) + step + items.length) % items.length].focus();
    }
}

// Study report = AI review of the misses (shared guide generator); Questions report = every answer.
function toggleResultsReportMenu() {
    const menu = $('results-report-menu');
    if (!menu) return openSessionReportPreview();
    if (!menu.hidden) return closeResultsReportMenu();
    const noMisses = !latestResultsReviewContext || latestResultsReviewContext.missedCount <= 0;
    menu.querySelectorAll('[data-report-kind="study"]').forEach((study) => {
        const condensed = study.dataset.reportLength === 'condensed';
        study.disabled = noMisses || Boolean(resultsAiReviewBusyMode);
        study.querySelector('span').textContent = noMisses ? 'No misses to review this run'
            : resultsAiReviewBusyMode ? 'Building your study report…'
            : condensed ? '1-2 pages: what to fix and focus on' : 'Full concept walkthrough of every miss';
    });
    menu.hidden = false;
    $('results-export-report-btn')?.setAttribute('aria-expanded', 'true');
    document.addEventListener('pointerdown', onResultsReportMenuPointer, true);
    document.addEventListener('keydown', onResultsReportMenuKey, true);
    menu.querySelector('[role="menuitem"]:not(:disabled)')?.focus();
}

async function chooseResultsReport(kind, length = 'condensed') {
    closeResultsReportMenu();
    if (kind !== 'study') return openSessionReportPreview();
    // Reuse a report already built at this length; older reports without a length are detailed.
    if (latestResultsAiReviewOutput?.text && (latestResultsAiReviewOutput.length || 'detailed') === length) return downloadResultsAiReviewPdf();
    return generateResultsAiReview({ preview: true, length });
}

function openSessionReportPreview() {
    if (!latestSessionReportData) {
        showPopup('Finish a quiz session to export its report.', null, true);
        return false;
    }
    const backdrop = ensureSessionReportPreview();
    const workspace = $('session-report-workspace');
    if (workspace) {
        workspace.innerHTML = buildSessionReportMarkup(latestSessionReportData);
        workspace.scrollTop = 0;
    }
    sessionReportReturnFocus = document.activeElement;
    // The report takes the rail's width while it is open, like the expanded summary.
    if (!sessionReportRailRelease) sessionReportRailRelease = window.MemoraLiveLeftRailPreview?.holdCompact?.() || null;
    backdrop.classList.add('open');
    $('session-report-save')?.focus();
    return true;
}

function closeSessionReportPreview() {
    const backdrop = $('session-report-backdrop');
    if (!backdrop || !backdrop.classList.contains('open')) return false;
    backdrop.classList.remove('open');
    const releaseRail = sessionReportRailRelease;
    sessionReportRailRelease = null;
    releaseRail?.();
    if (sessionReportReturnFocus && typeof sessionReportReturnFocus.focus === 'function' && sessionReportReturnFocus.isConnected) {
        sessionReportReturnFocus.focus();
    }
    sessionReportReturnFocus = null;
    return true;
}

function saveSessionReportPdf() {
    const page = $('session-report-page');
    if (!page || !latestSessionReportData) return false;
    document.querySelectorAll('.session-report-print-frame').forEach((frame) => frame.remove());
    const stamp = new Date(latestSessionReportData.generatedAt).toISOString().slice(0, 10);
    const docTitle = `${getSessionReportTitle(latestSessionReportData)} ${stamp}`;
    const cssHref = new URL('css/session-summary.css', document.baseURI).href;
    const frame = document.createElement('iframe');
    frame.className = 'session-report-print-frame';
    frame.setAttribute('aria-hidden', 'true');
    frame.tabIndex = -1;
    frame.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;opacity:0;pointer-events:none;';
    frame.srcdoc = `<!doctype html><html><head><meta charset="utf-8"><title>${escapeHtml(docTitle)}</title><link rel="stylesheet" href="${escapeHtml(cssHref)}"></head><body class="session-report-print">${page.outerHTML}</body></html>`;
    frame.addEventListener('load', () => {
        const win = frame.contentWindow;
        if (!win) return;
        win.addEventListener('afterprint', () => setTimeout(() => frame.remove(), 0));
        win.focus();
        win.print();
        $('session-report-save')?.focus();
    }, { once: true });
    document.body.appendChild(frame);
    return true;
}

window.openSessionReportPreview = openSessionReportPreview;
window.toggleResultsReportMenu = toggleResultsReportMenu;
window.chooseResultsReport = chooseResultsReport;
window.closeSessionReportPreview = closeSessionReportPreview;
window.toggleSessionSummarySources = toggleSessionSummarySources;
window.runSessionSummaryPrimaryAction = runSessionSummaryPrimaryAction;
window.reviewSessionSummaryAnswers = reviewSessionSummaryAnswers;

window.closeResultsAiReviewModal = closeResultsAiReviewModal;
window.generateResultsAiReview = generateResultsAiReview;
window.setResultsWrongExportMode = setResultsWrongExportMode;
window.exportResultsWrongAnswers = exportResultsWrongAnswers;
window.downloadResultsAiReviewPdf = downloadResultsAiReviewPdf;
window.openLatestResultsAiReview = openLatestResultsAiReview;
window.toggleResultsFullscreen = toggleResultsFullscreen;
window.quizResultsSourceQuestions = quizResultsSourceQuestions;

function hasDismissedAnswerChoiceExplanationNotice() {
    try {
        return localStorage.getItem(ANSWER_CHOICE_EXPLANATION_NOTICE_DISMISSED_KEY) === '1';
    } catch (_err) {
        return false;
    }
}

function showAnswerChoiceExplanationNoticeIfNeeded(realIdx) {
    const answeredCount = Array.isArray(filteredIndices)
        ? filteredIndices.reduce((count, questionIdx) => (
            userAnswers?.[questionIdx] !== undefined ? count + 1 : count
        ), 0)
        : 0;
    if (answeredCount < ANSWER_CHOICE_EXPLANATION_NOTICE_ANSWER_THRESHOLD
        || userAnswers[realIdx] !== undefined
        || hasDismissedAnswerChoiceExplanationNotice()) return;

    const persistDismissal = (dismissed) => {
        try {
            if (dismissed) localStorage.setItem(ANSWER_CHOICE_EXPLANATION_NOTICE_DISMISSED_KEY, '1');
            else localStorage.removeItem(ANSWER_CHOICE_EXPLANATION_NOTICE_DISMISSED_KEY);
        } catch (_err) { }
    };

    showPopup('Tip: After answering a question, click any answer choice to see why it is right or wrong.', null, false, {
        sticky: true,
        dismissible: true,
        dismissLabel: 'Dismiss answer explanation tip',
        popupClass: 'answer-choice-explanation-notice'
    });
        const dismissPref = document.createElement('label');
    dismissPref.className = 'auto-study-notice-dismiss';
    dismissPref.innerHTML = `
        <input type="checkbox" aria-label="Do not show the answer explanation tip again">
        <span>Don't show again</span>
    `;
    const dismissPrefInput = dismissPref.querySelector('input');
    if (dismissPrefInput) {
        dismissPrefInput.checked = hasDismissedAnswerChoiceExplanationNotice();
        dismissPrefInput.addEventListener('change', () => {
            persistDismissal(!!dismissPrefInput.checked);
        });
    }
    els.popup?.appendChild(dismissPref);

    const closeButton = els.popup?.querySelector?.('.popup-inline-close');
    closeButton?.addEventListener('click', () => {
        persistDismissal(true);
    }, { once: true });
}

function getQuizNavigationProgressState() {
    const navigationIndices = isHighYieldQuizEnabled() && highYieldQuizState.pinnedQuestionKey
        ? filteredIndices.filter(index => buildQuestionPersistenceKeyByIndex(index) !== highYieldQuizState.pinnedQuestionKey)
        : filteredIndices;
    const availableQuestionCount = Array.isArray(navigationIndices) ? navigationIndices.length : 0;
    if (availableQuestionCount <= 0) {
        return { current: 0, total: 0, percent: 0, hasQuestion: false };
    }
    const requestedTarget = typeof window.getQuizGenNavigationProgressTarget === 'function'
        ? Number(window.getQuizGenNavigationProgressTarget())
        : 0;
    const isStreaming = isAnyQuizGenerationActive() && isOpenQuizStillGenerating();
    const total = isStreaming && Number.isFinite(requestedTarget) && requestedTarget > availableQuestionCount
        ? Math.floor(requestedTarget)
        : availableQuestionCount;
    const current = Math.min(availableQuestionCount, Math.max(1, navigationIndices.indexOf(filteredIndices[currentQIndex]) + 1));
    return {
        current,
        total,
        percent: Math.min(100, Math.max(0, Math.round((current / total) * 100))),
        hasQuestion: true
    };
}

function syncQuizProgressFill() {
    const progress = getQuizNavigationProgressState();
    if (els.progressFill) {
        els.progressFill.style.transform = `scaleX(${progress.percent / 100})`;
    }
    if (els.progressTrack) {
        els.progressTrack.setAttribute('aria-valuemin', '0');
        els.progressTrack.setAttribute('aria-valuemax', String(progress.total));
        els.progressTrack.setAttribute('aria-valuenow', String(progress.current));
        els.progressTrack.setAttribute('aria-valuetext', `Question ${progress.current} of ${progress.total}`);
    }
}

function isAnyQuizGenerationActive() {
    if (typeof isQuizGenActive === 'function' && isQuizGenActive !== isAnyQuizGenerationActive) {
        try { if (isQuizGenActive()) return true; } catch (_) { }
    }
    if (typeof window !== 'undefined' && typeof window.isQuizGenActive === 'function' && window.isQuizGenActive !== isAnyQuizGenerationActive && window.isQuizGenActive !== (window.isQuizGenerationActive)) {
        try { if (window.isQuizGenActive()) return true; } catch (_) { }
    }
    if (typeof isQuizGenerationCardLandingShowing === 'function') {
        try { if (isQuizGenerationCardLandingShowing()) return true; } catch (_) { }
    }
    if (typeof window !== 'undefined' && typeof window.isQuizGenerationCardLandingShowing === 'function') {
        try { if (window.isQuizGenerationCardLandingShowing()) return true; } catch (_) { }
    }
    if (typeof document !== 'undefined' && document.body?.classList?.contains('quiz-generation-card-pending')) {
        return true;
    }
    return false;
}

/* The card speaks for the quiz on screen. Before the first question lands there is nothing else
   to count; after that, only the quiz a run is streaming into keeps the generation tally. Another
   file generating in the background reports in the Quizzes header menu instead. */
function isOpenQuizStillGenerating() {
    if (!questions.length) return true;
    const streaming = String(window.getQuizGenStreamingQuizName?.() || '').replace(/\.(json|txt)$/i, '');
    if (!streaming || typeof activeQuestionSetKeys === 'undefined') return false;
    return [...activeQuestionSetKeys].some((key) => String(key).replace(/\.(json|txt)$/i, '') === streaming);
}

function syncQuizProgressText() {
    syncHighYieldQuizControls();
    // Streaming arrivals and run completion refresh navigation without re-rendering the answer.
    if (els.next) {
        const canFinish = !isAdaptiveReviewMode && filteredIndices.length > 0
            && currentQIndex === filteredIndices.length - 1
            && !(isAnyQuizGenerationActive() && isOpenQuizStillGenerating())
            && getQuizCompletionProgressState().pendingNewQuestions === 0;
        els.next.innerText = canFinish ? 'Finish' : 'Next';
        // Next stays available on the last question; finishing an unfinished level asks first.
        els.next.style.visibility = '';
        els.next.removeAttribute('aria-hidden');
    }
    if (!els.prog) return;
    if (els.prog._counterInterval) {
        clearInterval(els.prog._counterInterval);
        els.prog._counterInterval = null;
    }
    const selectedQuizGenerating = isAnyQuizGenerationActive() && isOpenQuizStillGenerating();
    const wasGenerating = els.prog.classList.contains('memora-selected-generation-shimmer');
    els.prog.classList.toggle('memora-selected-generation-shimmer', questions.length > 0 && selectedQuizGenerating);
    els.fcInfo?.classList.toggle('memora-selected-generation-shimmer', questions.length > 0
        && (selectedQuizGenerating || (typeof isOpenFlashcardDeckGenerating === 'function' && isOpenFlashcardDeckGenerating())));
    if (selectedQuizGenerating) {
        // The live status rotates per-run preparation copy; the level picker carries counts.
        const messages = window.MemoraGenerationQueue?.jobs?.message;
        const jobId = typeof quizGenActiveJobId === 'undefined' ? '' : quizGenActiveJobId;
        const refreshMessage = () => { els.prog.innerText = messages?.(jobId) || 'Generating...'; };
        refreshMessage();
        if (messages && jobId) els.prog._counterInterval = setInterval(() => {
            if (!isAnyQuizGenerationActive() || !isOpenQuizStillGenerating()) { syncQuizProgressText(); return; }
            refreshMessage();
        }, 1000);
        return;
    }
    if (wasGenerating && hasHighYieldQuizLabels()) {
        applyHighYieldQuizPool({ captureBase: !!highYieldLastAppliedPool && filteredIndices !== highYieldLastAppliedPool });
        memoraMotionFadeIn(els.prog.closest('.quiz-progress-summary') || els.prog);
    }
    const progress = getQuizNavigationProgressState();
    if (!progress.hasQuestion || !questions.length) {
        els.prog.innerText = 'Question 0 of 0';
        return;
    }
    els.prog.innerText = `Question ${progress.current} of ${progress.total}`;
}

window.isAnyQuizGenerationActive = isAnyQuizGenerationActive;
window.getQuizNavigationProgressState = getQuizNavigationProgressState;
window.syncQuizProgressFill = syncQuizProgressFill;
window.syncQuizProgressText = syncQuizProgressText;

function renderQuestion(animClass) {
    applyHighYieldQuizPool({ captureBase: !!highYieldLastAppliedPool && filteredIndices !== highYieldLastAppliedPool });
    applyAdaptivePracticeOrder();
    if (els.prog?._counterInterval) {
        clearInterval(els.prog._counterInterval);
        els.prog._counterInterval = null;
    }
    closeAIResponsePopup(); // Collapse expanded hint/explanation/deep-dive popup on question change
    els.quizContent.style.display = 'block'; els.resultsView.style.display = 'none'; window.focus(); if (els.popup.classList.contains('active')) { els.popup.classList.remove('active'); currentPopupBtn = null; }
    if (els.quizContent) els.quizContent.scrollTop = 0;
    if (els.quizCard) els.quizCard.scrollTop = 0;
    if (!questions.length || !filteredIndices.length) {
        console.debug("Render called with no questions; showing the empty state.");
        // Show empty state
        els.qText.innerHTML = isHighYieldQuizEnabled()
            ? '<div class="quiz-empty-state"><p>No questions in this level yet. Choose Extended or All above to see the others.</p></div>'
            : getQuizEmptyStateMarkup();
        els.choices.innerHTML = `
            <div class="quiz-skeleton-choice"></div>
            <div class="quiz-skeleton-choice"></div>
            <div class="quiz-skeleton-choice"></div>
            <div class="quiz-skeleton-choice"></div>`;
        if (els.prog) {
            if (document.body?.classList?.contains('quiz-generation-card-pending') || (typeof isQuizGenerationCardLandingShowing === 'function' && isQuizGenerationCardLandingShowing())) {
                if (els.prog._counterInterval) {
                    clearInterval(els.prog._counterInterval);
                    els.prog._counterInterval = null;
                }
                els.prog.innerText = 'Generating...';
            } else {
                els.prog.innerText = 'Question 0 of 0';
            }
        }
        syncQuizProgressFill();
        if (els.jump) els.jump.classList.remove('adaptive-repeat-pulse');
        if (els.jump) els.jump.title = '';
        adaptiveLastRenderedIdx = null;
        if (els.expl) {
            els.expl.style.display = 'none';
            els.expl.innerHTML = '';
            els.expl.classList.remove('ai-response-box', 'ai-response-loading', 'explanation-revealing', 'from-correct', 'from-incorrect');
            els.expl.removeAttribute('data-tone');
            els.expl.removeAttribute('aria-busy');
            els.expl.onclick = null;
            els.expl.title = '';
            els.expl.style.cursor = '';
        }
        lastRenderedQuestionRealIdx = null;
        if (els.quizContent) {
            els.quizContent._memoraRenderedQuestionKey = '';
            els.quizContent._memoraRenderedQuestionRealIdx = null;
        }
        syncNotesButtonIndicator(null);
        syncQuizSourceButtonState();
        if (typeof syncQuizRegenerateButtonState === 'function') syncQuizRegenerateButtonState();
        syncContentDependentControls();
        applyQuizCardContentScale();
        requestAnimationFrame(positionQuickActionsDock);
        return;
    }

    // Bounds check for currentQIndex
    if (currentQIndex >= filteredIndices.length) currentQIndex = 0;

    // Animation Trigger
    if (animClass) {
        els.quizContent.classList.remove('anim-fade');
        void els.quizContent.offsetWidth; // Trigger reflow
        els.quizContent.classList.add(animClass);
    }

    const realIdx = filteredIndices[currentQIndex], q = questions[realIdx];
    if (isAdaptiveReviewMode) adaptiveSessionSeenIndices.add(realIdx);
    if (isAdaptiveReviewMode) adaptiveLastRenderedIdx = realIdx;
    else adaptiveLastRenderedIdx = null;

    // Pulse for cards with adaptive confidence history (rated cards), not just viewed cards.
    const hasAdaptiveConfidence = isAdaptiveReviewMode && hasAdaptiveConfidenceRating(realIdx);
    if (els.jump) els.jump.classList.toggle('adaptive-repeat-pulse', hasAdaptiveConfidence);

    // Safety check for undefined question
    if (!q) {
        console.error("Question not found at index", realIdx);
        syncNotesButtonIndicator(null);
        syncContentDependentControls();
        return;
    }
    const shouldAnimateQuizGenTranscription = consumeQuizGenQuestionTranscription();
    const renderedQuestionKey = buildQuestionPersistenceKeyFromQuestion(q);
    if (els.quizContent) {
        els.quizContent._memoraRenderedQuestionKey = renderedQuestionKey;
        els.quizContent._memoraRenderedQuestionRealIdx = realIdx;
    }
    if (lastRenderedQuestionRealIdx !== null && lastRenderedQuestionRealIdx !== realIdx) {
        if (!isQuizCardExpanded) resetAdvancedPdfZoomToPageWidth();
        window.closeTransientQuestionPanesForNavigation?.({
            closePdf: !autoEvidence,
            closePdfExplanation: !(autoExplanation && isMemoraPdfExplanationFeatureEnabledForCore()),
            preserveExpandedLock: !!isQuizCardExpanded
        });
        window.resetAskMemoraSourceMinimizedForQuestionChange?.({
            autoEvidence: !!autoEvidence,
            autoExplanation: !!autoExplanation && isMemoraPdfExplanationFeatureEnabledForCore()
        });
    }
    lastRenderedQuestionRealIdx = realIdx;
    syncNotesButtonIndicator(realIdx);
    // Feedback still queued is written for the question on screen and the next ones in order first.
    window.requestUrgentQuizExplanation?.(q);

    // Show source/model metadata only on the question-number pill.
    const sourceLabel = q.sourceFile || 'Unknown';
    const modelLabel = getQuestionGenerationModel(q) || 'Not tracked';
    if (els.jump) {
        els.jump.title = `Source: ${sourceLabel}\nAI Model: ${modelLabel}`;
    }

    // Auto-Evidence / Auto-Explanation tied to this render only (prevents stale jumps).
    const renderToken = ++questionRenderToken;
    clearAutoStudyTimers();

    let skipAutoStudyOpenThisRender = suppressAutoStudyOpenOnNextQuestionRender;
    suppressAutoStudyOpenOnNextQuestionRender = false;
    if (suppressAutoStudyOpenUntilQuestionChange) {
        if (suppressedAutoStudyQuestionRealIdx === null) suppressedAutoStudyQuestionRealIdx = realIdx;
        if (suppressedAutoStudyQuestionRealIdx === realIdx) {
            skipAutoStudyOpenThisRender = true;
        } else {
            suppressAutoStudyOpenUntilQuestionChange = false;
            suppressedAutoStudyQuestionRealIdx = null;
        }
    }
    const effectiveAutoExplanation = !!autoExplanation && isMemoraPdfExplanationFeatureEnabledForCore();
    const preferAutoTrioLayout = !!(autoEvidence && effectiveAutoExplanation);
    if (!skipAutoStudyOpenThisRender && autoEvidence) {
        autoEvidenceTimer = setTimeout(() => {
            autoEvidenceTimer = null;
            if (renderToken !== questionRenderToken) return;
            if (filteredIndices[currentQIndex] !== realIdx) return;
            triggerEvidence({
                includeSearch: false,
                smoothJump: false,
                forceFreshViewer: true,
                maintainPdfScale: true,
                preferDuoPdfPane: !preferAutoTrioLayout,
                preferTrioLayout: preferAutoTrioLayout,
                silentIfMissing: true
            });
        }, 150);
    }
    if (!skipAutoStudyOpenThisRender && effectiveAutoExplanation) {
        autoExplanationTimer = setTimeout(() => {
            autoExplanationTimer = null;
            if (renderToken !== questionRenderToken) return;
            if (filteredIndices[currentQIndex] !== realIdx) return;
            void openPdfExplanationForCurrentQuestion({
                silent: true,
                smooth: true,
                preferDuoAiPane: !preferAutoTrioLayout,
                preferTrioLayout: preferAutoTrioLayout
            });
        }, autoEvidence ? 260 : 140);
    }

    // Update Dropdown Highlight
    updateSourceDropdown();

    // Display question image (from imgUrl field only - q.img is just a boolean flag)
    // Handle both regular URLs and idb:// URLs (stored in IndexedDB)
    const rawImgUrl = q.imgUrl || null;
    if (rawImgUrl) {
        els.img.style.display = 'block';
        els.img.src = '';  // Clear while loading
        els.img.onload = () => {
            autoScrollQuizPaneToChoices();
            scheduleQuizCardContentScaleSync();
        };
        // Async load: resolve idb:// URLs to blob URLs. The card may have moved on
        // by the time the lookup settles; only the question that asked may paint.
        els.img.dataset.memoraImageRequest = rawImgUrl;
        getImageBlobUrl(rawImgUrl).then(blobUrl => {
            if (els.img.dataset.memoraImageRequest !== rawImgUrl) return;
            if (blobUrl) {
                els.img.src = blobUrl;
                els.img.onclick = () => openImageLightbox(blobUrl);
                scheduleQuizCardContentScaleSync();
            } else {
                els.img.style.display = 'none';
                els.img.onload = null;
                scheduleQuizCardContentScaleSync();
            }
        });
    } else {
        delete els.img.dataset.memoraImageRequest;
        els.img.style.display = 'none';
        els.img.src = ''; // Clear any previous image
        els.img.onload = null;
    }
    const questionStem = q.q || q.question || 'Question unavailable';
    els.qText.innerHTML = shouldAnimateQuizGenTranscription
        ? formatQuizGenStemTranscriptionHtml(questionStem)
        : formatQuestionStemHtml(questionStem);
    const stemWordCount = shouldAnimateQuizGenTranscription
        ? getQuizGenStemWordCount(questionStem)
        : 0;
    syncQuizProgressText();
    syncQuizProgressFill();
    syncQuizJumpSelectOptions();
    els.choices.innerHTML = ''; if (isHardMode) { els.choices.classList.add('hard-mode'); if (!$('hard-mode-overlay')) { const ov = document.createElement('div'); ov.id = 'hard-mode-overlay'; ov.innerHTML = '<button type="button" id="hard-mode-btn">Click to Reveal Options</button>'; ov.onclick = () => { revealHardModeOverlayChoices({ focusFirst: ov.contains(document.activeElement) }); }; els.choices.appendChild(ov); } } else { els.choices.classList.remove('hard-mode'); if ($('hard-mode-overlay')) $('hard-mode-overlay').remove(); }
    const choices = Array.isArray(q.c) ? q.c : (Array.isArray(q.choices) ? q.choices : (Array.isArray(q.options) ? q.options : (Array.isArray(q.o) ? q.o : [])));
    if (!Array.isArray(q.c) && choices.length) q.c = [...choices];
    let cIndices = choices.map((_, i) => i);
    const displayChoiceTexts = standardizeQuizChoiceSetDisplay(choices.map((choice) => sanitizeQuestionDisplayText(String(choice ?? '')).replace(/^[A-F]\.\s*/i, '')));
    const forcedChoiceOrder = forcedChoiceOrderByQuestion[realIdx];
    if (Array.isArray(forcedChoiceOrder) && forcedChoiceOrder.length === cIndices.length) {
        cIndices = [...forcedChoiceOrder];
    } else if (isMixAnswers) {
        cIndices = getStableMixedChoiceOrder(realIdx);
    } else {
        delete mixedChoiceOrderByQuestion[realIdx];
    }
    cIndices.forEach((oIdx, vIdx) => {
        const btn = document.createElement('button');
        btn.className = 'choice';
        // Strip existing letter labels (A., B., etc.) from choice text to avoid duplicates
        const choiceText = displayChoiceTexts[oIdx] ?? '';
        // displayChoiceTexts already dropped leading "A. ", "B. " labels and evened out punctuation.
        btn.innerHTML = `<span class="label">${String.fromCharCode(65 + vIdx)}.</span> ${escapeHtml(choiceText)}`;
        btn.dataset.idx = oIdx;
        // Concealed Hard Mode choices stay out of the Tab order until Reveal Options.
        if (els.choices.classList.contains('hard-mode')) btn.inert = true;
        btn.onclick = () => {
            if (shouldAnimateQuizGenTranscription) cancelQuizGenQuestionTranscription();
            handleChoice(realIdx, oIdx, btn, renderedQuestionKey);
        };
        btn.oncontextmenu = (e) => { e.preventDefault(); btn.classList.toggle('eliminated'); };

        // EYEBALL TOGGLE
        const eye = document.createElement('div');
        eye.className = 'eye-toggle';
        eye.innerHTML = `<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/><line class="eye-strike" x1="3" y1="3" x2="21" y2="21" style="display:none; stroke: #ef4444;"/></svg>`;
        eye.onclick = (e) => {
            e.stopPropagation();
            btn.classList.toggle('eliminated');
            eye.querySelector('.eye-strike').style.display = btn.classList.contains('eliminated') ? 'block' : 'none';
        };
        btn.appendChild(eye);

        els.choices.appendChild(btn);
        if (shouldAnimateQuizGenTranscription) {
            btn.classList.add('choice-transcribe-enter');
            btn.style.animationDelay = `${(stemWordCount * QUIZ_GEN_STEM_TRANSCRIPTION_WORD_DELAY_MS) + 120 + (vIdx * QUIZ_GEN_CHOICE_TRANSCRIPTION_STEP_DELAY_MS)}ms`;
            btn.addEventListener('animationend', () => {
                btn.classList.remove('choice-transcribe-enter');
                btn.style.removeProperty('animation-delay');
            }, { once: true });
        }
    });
    // Staggered pop-in animation for answer choices on animated transitions
    if (animClass && !shouldAnimateQuizGenTranscription) {
        const choiceBtns = els.choices.querySelectorAll('button.choice');
        choiceBtns.forEach(btn => {
            btn.classList.add('choice-enter');
            btn.addEventListener('animationend', () => btn.classList.remove('choice-enter'), { once: true });
        });
    }
    if (userAnswers[realIdx] !== undefined) {
        if (shouldAnimateQuizGenTranscription) cancelQuizGenQuestionTranscription();
        revealAnswer(realIdx, userAnswers[realIdx], true);
    } else els.expl.style.display = 'none';
    updateSaveButton(realIdx);
    els.prev.disabled = currentQIndex === 0;
    if (isAdaptiveReviewMode) updateAdaptiveFilterBanner();

    updateLiveStats();
    autoScrollQuizPaneToChoices();

    // Restore AI content (cached deep dive/explanation/hint) and update button pulsation
    restoreAIContent();

    // Start Timer for Stats
    currentQuestionStartTime = Date.now();
    syncQuizSourceButtonState();
    if (typeof syncQuizRegenerateButtonState === 'function') syncQuizRegenerateButtonState();
    syncContentDependentControls();
    scheduleQuizCardContentScaleSync();
    scheduleQuizCardHeightNormalization();
    requestAnimationFrame(positionQuickActionsDock);
    if (!isPublicTourQuizDemoActive() && !IS_EMBED_DEMO) {
        showAnswerChoiceExplanationNoticeIfNeeded(realIdx);
    }
}

// A vignette stem is setup plus the actual question. Returning them apart lets the quiz put the
// question on its own line; short stems and stems without a closing question stay whole.
function splitQuestionStemAsk(raw = '') {
    const text = String(raw || '').trim();
    if (!/\?\s*$/.test(text)) return null;
    const boundary = /[.!?]["'”’)\]]?\s+(?=["'“‘(]?[A-Z])/g;
    let cut = -1;
    let match;
    while ((match = boundary.exec(text))) {
        const end = match.index + match[0].length;
        if (end >= text.length - 1) break;
        if (/\b(?:Dr|Mr|Mrs|Ms|vs|etc|Fig|No|St|approx|e\.g|i\.e)\.$/i.test(text.slice(0, match.index + 1))) continue;
        cut = end;
    }
    if (cut < 0) return null;
    const lead = text.slice(0, cut).trim();
    const ask = text.slice(cut).trim();
    if (lead.length < 40 || ask.split(/\s+/).length < 3) return null;
    return { lead, ask };
}

// Stems in labelled parts (Statements 1-3, Assertion/Reason) show each part on its own line, and
// drop a trailing "Which is correct?" since the answer choices already ask it.
const QUESTION_STEM_LABEL_PAIRS = [
    { labels: ['Statement 1', 'Statement 2', 'Statement 3'], first: /\bStatement\s*1\s*:\s*/i, second: /\s*\bStatement\s*2\s*:\s*/i, third: /\s*\bStatement\s*3\s*:\s*/i },
    { labels: ['Statement 1', 'Statement 2'], first: /\bStatement\s*1\s*:\s*/i, second: /\s*\bStatement\s*2\s*:\s*/i },
    { labels: ['Assertion', 'Reason'], first: /\bAssertion\s*:\s*/i, second: /\s*\bReason\s*:\s*/i }
];
const QUESTION_STEM_PAIR_TRAILING_ASK_RE = /\s*(?:which\s+(?:of\s+the\s+following\s+)?(?:option\s+|statement\s+)?(?:is|are)\s+(?:correct|true)|choose\s+the\s+correct\s+(?:option|answer))\s*[?.]?\s*$/i;

function findQuestionStemLabelPair(raw) {
    return QUESTION_STEM_LABEL_PAIRS.find((pair) => {
        const firstAt = raw.search(pair.first);
        return firstAt >= 0 && raw.slice(firstAt + 1).search(pair.second) >= 0 && (!pair.third || raw.search(pair.third) >= 0);
    }) || null;
}

function formatQuestionStemHtml(value) {
    const raw = sanitizeQuestionDisplayText(String(value ?? '').trim());
    if (!raw) return 'Question unavailable';
    const pair = findQuestionStemLabelPair(raw);
    if (!pair) {
        const parts = splitQuestionStemAsk(raw);
        return parts
            ? `<span class="question-stem-lead">${escapeHtml(parts.lead)}</span><span class="question-stem-ask">${escapeHtml(parts.ask)}</span>`
            : escapeHtml(raw);
    }
    const html = escapeHtml(raw.replace(QUESTION_STEM_PAIR_TRAILING_ASK_RE, ''))
        .replace(pair.first, `<strong class="question-statement-label">${pair.labels[0]}:</strong> `)
        .replace(pair.second, `</span><span class="question-statement-part"><strong class="question-statement-label">${pair.labels[1]}:</strong> `);
    return '<span class="question-statement-part">' + (pair.third ? html.replace(pair.third, `</span><span class="question-statement-part"><strong class="question-statement-label">${pair.labels[2]}:</strong> `) : html) + '</span>';
}

function getQuizGenStemWordCount(value) {
    const raw = sanitizeQuestionDisplayText(String(value ?? '').trim());
    if (!raw || findQuestionStemLabelPair(raw)) return 0;
    return raw.split(/\s+/).filter(Boolean).length;
}

function formatQuizGenStemTranscriptionHtml(value) {
    const raw = sanitizeQuestionDisplayText(String(value ?? '').trim());
    if (!raw) return 'Question unavailable';
    if (findQuestionStemLabelPair(raw)) {
        return formatQuestionStemHtml(raw);
    }
    const words = (text, offset = 0) => text
        .split(/\s+/)
        .filter(Boolean)
        .map((word, index) => `<span class="stem-word-transcribe" style="animation-delay: ${(offset + index) * QUIZ_GEN_STEM_TRANSCRIPTION_WORD_DELAY_MS}ms;">${escapeHtml(word)}</span>`)
        .join(' ');
    const parts = splitQuestionStemAsk(raw);
    if (!parts) return words(raw);
    const leadWords = parts.lead.split(/\s+/).filter(Boolean).length;
    return `<span class="question-stem-lead">${words(parts.lead)}</span><span class="question-stem-ask">${words(parts.ask, leadWords)}</span>`;
}

function getWrongChoiceFeedbackText(question, selectedIdx) {
    if (!question) return '';

    const normalize = (text) => String(text || '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, ' ')
        .trim();

    const correctExplanation = typeof question.e === 'string' ? question.e.trim() : '';
    const eNorm = normalize(correctExplanation);
    const wrongMap = (question.w && typeof question.w === 'object' && !Array.isArray(question.w)) ? question.w : null;

    let wrongText = '';
    if (wrongMap) {
        wrongText = wrongMap[selectedIdx] ?? wrongMap[String(selectedIdx)] ?? '';
    }
    wrongText = typeof wrongText === 'string' ? wrongText.trim() : '';

    if (wrongText) {
        const wNorm = normalize(wrongText);
        const duplicatesCorrectExplanation = !!(eNorm && (wNorm === eNorm || wNorm.includes(eNorm) || eNorm.includes(wNorm)));
        const isRejectedFallback = wNorm.includes('does not best match the stem')
            || wNorm.includes('best supported answer')
            || wNorm === 'that choice is incorrect'
            // Saved banks hold "the source does not mention X" notes; they teach nothing, so Explain shows instead.
            || (/\b(source|lecture|slides?|pages?|text|material)\b[^.;]{0,40}\b(does not|doesn't|do not|don't|never)\s+(mention|list|include|prioriti[sz]e|emphasi[sz]e|discuss|address|cover|highlight)|\bnot (mentioned|listed|included|discussed|emphasi[sz]ed|prioriti[sz]ed|covered)\b[^.;]{0,30}\b(in|by) the (source|lecture|slides?|pages?)/i.test(wrongText)
                && !/;|\.\s+\S|\b(because|instead|rather|whereas)\b/i.test(wrongText.replace(/\.\s*$/, '')));
        if (!duplicatesCorrectExplanation && !isRejectedFallback) {
            return wrongText;
        }
    }

    return '';
}

// Model output sometimes ends one choice with a period or starts it lowercase while the others
// do not, which marks the answer. Display only: stored text stays exact for answer identity.
function standardizeQuizChoiceSetDisplay(texts) {
    const list = (Array.isArray(texts) ? texts : []).map((text) => String(text || '').trim());
    const endsWithPeriod = (text) => /[^.]\.$/.test(text) && !/\b(etc|vs|spp|e\.g|i\.e|dr|st)\.$/i.test(text);
    const periodCount = list.filter(endsWithPeriod).length;
    const lowerStart = (text) => /^[a-z]+(\s|$)/.test(text);
    const upperStart = (text) => /^[A-Z]/.test(text);
    const capitalize = list.some(upperStart) && list.some(lowerStart);
    return list.map((text) => {
        let out = text;
        // A mixed set drops the stray periods; a set of full sentences keeps them all.
        if (periodCount > 0 && periodCount < list.length && endsWithPeriod(out)) out = out.slice(0, -1);
        if (capitalize && lowerStart(out)) out = out.charAt(0).toUpperCase() + out.slice(1);
        return out;
    });
}

function getPlainStudyText(value) {
    const raw = String(value ?? '').trim();
    if (!raw) return '';
    const template = document.createElement('template');
    template.innerHTML = sanitizeQuizRichTextHtml(raw);
    return (template.content.textContent || raw).replace(/\s+/g, ' ').trim();
}

function getChoiceDisplayLetter(choiceIdx, fallbackButton = null) {
    const fromButton = fallbackButton?.querySelector?.('.label')?.textContent || '';
    const cleanedFromButton = fromButton.replace(/[^A-Z]/gi, '').trim().toUpperCase();
    if (cleanedFromButton) return cleanedFromButton;

    const choiceButton = els?.choices?.querySelector?.(`.choice[data-idx="${choiceIdx}"]`);
    const fromChoice = choiceButton?.querySelector?.('.label')?.textContent || '';
    const cleanedFromChoice = fromChoice.replace(/[^A-Z]/gi, '').trim().toUpperCase();
    return cleanedFromChoice || String.fromCharCode(65 + Number(choiceIdx || 0));
}

function buildAnswerChoiceSayMoreContext(realIdx, selectedIdx, feedbackText, selectedButton = null) {
    const question = questions?.[realIdx] || {};
    const choices = Array.isArray(question.c) ? question.c : (Array.isArray(question.o) ? question.o : []);
    const correctIdx = Number(question.a);
    const selectedChoiceLetter = getChoiceDisplayLetter(selectedIdx, selectedButton);

    return {
        questionIndex: realIdx,
        viewQuestionNumber: Number.isInteger(currentQIndex) ? currentQIndex + 1 : null,
        totalQuestions: Array.isArray(filteredIndices) ? filteredIndices.length : null,
        questionText: getPlainStudyText(question.q || question.question || ''),
        selectedChoiceLetter,
        selectedChoiceText: getPlainStudyText(choices[selectedIdx] || ''),
        selectedChoiceIndex: selectedIdx,
        selectedChoiceIsCorrect: selectedIdx === correctIdx,
        feedbackText: getPlainStudyText(feedbackText),
        wrongExplanation: selectedIdx === correctIdx ? '' : getPlainStudyText(feedbackText),
        correctChoiceLetter: getChoiceDisplayLetter(correctIdx),
        correctChoiceText: getPlainStudyText(choices[correctIdx] || ''),
        correctChoiceIndex: correctIdx,
        correctExplanation: getPlainStudyText(question.e || question.explanation || ''),
        citation: getPlainStudyText(question.citation || question.source || question.add || question.page || '')
    };
}

// Practice, live accuracy and results agree: a question whose answer key could not be
// confirmed is never scored, rewarded, or shown with a "correct" choice.
function isQuestionAnswerKeyVerified(question) {
    return !!question && !question.answerUnverified;
}

// The open answer popup while its feedback is still being written: which question and choice,
// and the choice button it is anchored to.
let pendingAnswerChoiceFeedbackPopup = null;

// Called when explanations land or stop. Fills a still-open "Preparing" popup in place, on the
// same question and choice, without touching the answer, progress or scroll.
function refreshOpenAnswerChoiceFeedback() {
    const open = pendingAnswerChoiceFeedbackPopup;
    if (!open) return false;
    if (!els?.popup?.classList?.contains('active') || currentPopupBtn !== open.button || !open.button?.isConnected) {
        pendingAnswerChoiceFeedbackPopup = null;
        return false;
    }
    const realIdx = Number(filteredIndices?.[currentQIndex]);
    const question = Number.isInteger(realIdx) ? questions?.[realIdx] : null;
    if (!question || buildQuestionPersistenceKeyFromQuestion(question) !== open.key) {
        pendingAnswerChoiceFeedbackPopup = null;
        return false;
    }
    const ready = open.selectedIdx === Number(question.a)
        ? !!String(question.e || question.explanation || '').trim()
        : !!getWrongChoiceFeedbackText(question, open.selectedIdx);
    if (!ready && window.isQuizAnswerFeedbackPending?.(question) === true) return false;
    showAnswerChoiceFeedbackPopup(realIdx, open.selectedIdx, open.button);
    return true;
}
window.refreshOpenAnswerChoiceFeedback = refreshOpenAnswerChoiceFeedback;

function showAnswerChoiceFeedbackPopup(realIdx, selectedIdx, selectedButton) {
    const isCorrectChoice = selectedIdx === Number(questions?.[realIdx]?.a);
    const correctExplanation = String(questions?.[realIdx]?.e || questions?.[realIdx]?.explanation || '').trim();
    const feedbackText = isCorrectChoice ? correctExplanation : getWrongChoiceFeedbackText(questions[realIdx], selectedIdx);
    const hasFeedbackText = !!String(feedbackText || '').trim();
    const feedbackHtml = hasFeedbackText ? escapeHtml(feedbackText).replace(/\n/g, '<br>') : '';
    const actionLabel = hasFeedbackText ? 'Say more' : 'Explain';
    // Feedback that is still being written says so, and fills in when it arrives.
    const feedbackPending = !hasFeedbackText && typeof window.isQuizAnswerFeedbackPending === 'function'
        && window.isQuizAnswerFeedbackPending(questions?.[realIdx]) === true;
    const popupHtml = `
        ${hasFeedbackText ? `<div class="wrong-popup-body">${feedbackHtml}</div>` : ''}
        ${feedbackPending ? '<div class="wrong-popup-body wrong-popup-pending" role="status">Preparing this explanation…</div>' : ''}
        <div class="wrong-popup-say-more-row">
            <button type="button" class="wrong-popup-say-more" data-wrong-say-more="true">${actionLabel}</button>
        </div>
    `;

    let popupClasses = `answer-choice-callout ${isCorrectChoice ? 'correct-answer-callout' : 'wrong-answer-callout'}`;
    if (typeof window.isAskMemoraInterfaceVisible === 'function' && window.isAskMemoraInterfaceVisible()) {
        popupClasses += ' popup-surface-front';
    }

    showPopup(popupHtml, selectedButton, !isCorrectChoice, {
        duration: 10000,
        dismissible: true,
        popupClass: popupClasses
    });
    if (feedbackPending) window.requestUrgentQuizExplanation?.(questions?.[realIdx]);
    pendingAnswerChoiceFeedbackPopup = feedbackPending
        ? { key: buildQuestionPersistenceKeyFromQuestion(questions?.[realIdx]), selectedIdx, button: selectedButton }
        : null;

    const sayMoreButton = els?.popup?.querySelector?.('[data-wrong-say-more="true"]');
    if (!sayMoreButton) return;

    const context = buildAnswerChoiceSayMoreContext(realIdx, selectedIdx, feedbackText, selectedButton);
    sayMoreButton.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        closeActiveAnchoredPopup();
        if (typeof window.askMemoraAnswerChoiceSayMore === 'function') {
            window.askMemoraAnswerChoiceSayMore(context);
        } else if (typeof window.askMemoraWrongAnswerSayMore === 'function') {
            window.askMemoraWrongAnswerSayMore(context);
        } else {
            showPopup('Ask Memoraful is still loading. Try again in a moment.', null, true);
        }
    });
}

function revealHardModeOverlayChoices({ focusFirst = false } = {}) {
    els.choices.classList.remove('hard-mode');
    $('hard-mode-overlay')?.remove();
    els.choices.querySelectorAll('button.choice').forEach((choice) => { choice.inert = false; });
    if (focusFirst) els.choices.querySelector('button.choice')?.focus({ preventScroll: true });
}

function handleChoice(realIdx, cIdx, btn, renderedQuestionKey = '') {
    // Shortcuts and programmatic clicks must not score an answer the user cannot see.
    if (els.choices?.classList.contains('hard-mode')) return;
    const activeRealIdx = Number(filteredIndices[currentQIndex]);
    const activeQuestion = Number.isInteger(activeRealIdx) ? questions[activeRealIdx] : null;
    const activeQuestionKey = buildQuestionPersistenceKeyFromQuestion(activeQuestion);
    if (!activeQuestion
        || (!renderedQuestionKey && activeRealIdx !== realIdx)
        || (!!renderedQuestionKey && renderedQuestionKey !== activeQuestionKey)) {
        renderQuestion();
        showPopup('This question updated while the quiz was still loading. Please choose your answer again.', null, false);
        return;
    }
    // Streamed banks can move the same card to a new array index.
    realIdx = activeRealIdx;
    try { window.stopAiExplanationReaderPlayback?.({ keepStatus: false }); } catch (_err) { }
    try { window.stopGeminiLiveQuestionReadout?.({ keepStatus: false, onlyReadout: true }); } catch (_err) { }

    // Pop Animation
    btn.classList.add('anim-pop');
    setTimeout(() => btn.classList.remove('anim-pop'), 200);

    if (!isQuestionAnswerKeyVerified(questions[realIdx])) {
        // The pick is kept so the learner can move on, but nothing is scored against a
        // placeholder key: no stats, SRS, streak, XP or auto-saved "wrong" bookmark.
        if (userAnswers[realIdx] === undefined) {
            userAnswers[realIdx] = cIdx;
            syncQuizProgressFill();
            syncQuizProgressText();
        }
        revealAnswer(realIdx, userAnswers[realIdx]);
        showPopup('This question\u2019s answer key could not be confirmed, so it is not scored.', btn, false, {
            duration: 8000,
            dismissible: true,
            popupClass: 'answer-choice-callout'
        });
        updateLiveStats();
        if (!isPublicTourQuizDemoActive()) {
            markSessionStateMutation();
            saveSessionDebounced();
        }
        return;
    }

    // Calculate Duration
    const duration = Date.now() - currentQuestionStartTime;
    const isCorrect = (cIdx === questions[realIdx].a);
    const isFirstAttempt = (userAnswers[realIdx] === undefined);
    const isPublicTourDemo = isPublicTourQuizDemoActive();

    if (!isFirstAttempt) {
        showAnswerChoiceFeedbackPopup(realIdx, cIdx, btn);
        return;
    }

    const cardDue = srsCardDueState(realIdx);

    // Update Algorithms
    if (!isPublicTourDemo) {
        updateStats(realIdx, isCorrect, duration);
        if (!isAdaptiveReviewMode) updateSRS(realIdx, isCorrect);
    }

    // Refresh changes presentation, not reward credit. A later study day and a first
    // correct answer after a miss remain eligible; scoring/history above are unchanged.
    const rewardsEligible = isGameModeRewardEligible();
    const answerRewardDay = new Date().toDateString();
    const answerRewardIdentity = buildGameModeAnswerRewardIdentity(activeQuestion);
    const answerRewardKey = answerRewardIdentity?.key;
    const previousAnswerReward = xpData.answerRewardDays?.[answerRewardKey]
        || Object.entries(xpData.answerRewardDays || {}).find(([key, reward]) => answerRewardIdentity && key.startsWith(answerRewardIdentity.legacyPrefix)
            && reward?.day === answerRewardDay && reward.correct === true)?.[1];
    const growthRetry = isCorrect && previousAnswerReward?.day === answerRewardDay && previousAnswerReward.correct === false;
    const rewardAttemptEligible = rewardsEligible && !!answerRewardIdentity && (cardDue.due || growthRetry) && (previousAnswerReward?.day !== answerRewardDay
        || (isCorrect && previousAnswerReward.correct !== true));
    // Reward progress follows accepted durable credit, including competing windows.
    if (isFirstAttempt && !isPublicTourDemo && rewardAttemptEligible) {
        const rewardMode = typeof pdfTutorState !== 'undefined' && pdfTutorState.quizCheckpoint?.active && pdfTutorState.quizCheckpoint?.guidedStudy ? 'guided' : 'quizzes';
        const combo = noteDueRecall(cardDue.dueReview, isCorrect);
        const answerReward = { ...answerRewardIdentity, day: answerRewardDay, correct: isCorrect, mode: rewardMode, combo };
        xpData.answerRewardDays = Object.fromEntries(Object.entries(xpData.answerRewardDays || {}).filter(([, reward]) => reward?.day === answerRewardDay));
        xpData.answerRewardDays[answerRewardKey] = { ...previousAnswerReward, day: answerRewardDay, correct: isCorrect };
        const acceptProgress = (record, previousLevel, receipt) => {
            totalQuestionsAnswered++;
            currentStreak = receipt.streak;
            updateDailyStreak();
            if (isCorrect) {
                flawlessStreak++;
                quickAnswerStreak = duration < 10000 ? quickAnswerStreak + 1 : 0;
                if (wrongStreak >= 3) {
                    recoveryCorrect++;
                    if (recoveryCorrect >= 5) { resilienceCount++; recoveryCorrect = 0; wrongStreak = 0; }
                }
                wrongAnswerHistory.delete(answerRewardKey);
                const sVal = $('streak-val');
                sVal.classList.remove('streak-pop'); void sVal.offsetWidth; sVal.classList.add('streak-pop');
            } else {
                wrongStreak++; recoveryCorrect = 0;
                wrongAnswerHistory.add(answerRewardKey);
                if (receipt.shieldConsumed) showPowerUpNotification('Streak kept', receipt.powerUps.streakShields === 1 ? '1 shield left' : receipt.powerUps.streakShields + ' shields left');
                else { flawlessStreak = 0; quickAnswerStreak = 0; }
            }
            updatePowerUpIndicator(); saveGamificationData();
        };
        if (isCorrect) {
            const isGrowth = previousAnswerReward?.day === answerRewardDay && previousAnswerReward.correct === false;
            let reason = isGrowth ? 'Growth' : 'Correct';
            if (activePowerUps.xpMultiplierQuestions > 0) reason += ' (' + activePowerUps.xpMultiplier + 'x)';
            const estimate = calculateGameModeAnswerXP(true, isGrowth, currentStreak + 1, activePowerUps, calculateLevel(xpData.totalXP));
            const gameStep = window.memoraGameStudy ? { mode: rewardMode, key: answerReward.key, points: 10, mastery: true } : null;
            awardXP(Math.round(estimate.amount * combo), reason, { quiet: true, answerReward, gameStep, onAccepted: (...args) => {
                acceptProgress(...args); if (cardDue.dueReview) maybeDueCardFind();
            } });
        } else {
            commitXPRecordChange({ answerReward, onAccepted: (record, previousLevel, receipt) => {
                acceptProgress(record, previousLevel, receipt); checkAchievements();
            } });
        }
    }
    // --- END XP LOGIC ---

    userAnswers[realIdx] = cIdx;
    syncQuizProgressFill();
    syncQuizProgressText();

    if (cIdx !== questions[realIdx].a) {
        btn.classList.add('incorrect');
        showAnswerChoiceFeedbackPopup(realIdx, cIdx, btn);
        if (!isPublicTourDemo && (autoSaveWrong || isAdaptiveReviewMode) && !savedQuestions[realIdx]) {
            savedQuestions[realIdx] = Date.now();
            updateSaveButton(realIdx);
        }
    } else {
        showAnswerChoiceFeedbackPopup(realIdx, cIdx, btn);
    }

    if (!isPublicTourDemo && isCorrect && autoAdvance && !isAdaptiveReviewMode) {
        // Advance only from the card that was answered. A manual Next inside the
        // delay already moved on, and a second advance would skip a question.
        const answeredViewIndex = currentQIndex;
        const answeredSessionId = currentSessionId;
        const answeredRenderToken = questionRenderToken;
        setTimeout(() => {
            if (!autoAdvance || isAdaptiveReviewMode
                || currentSessionId !== answeredSessionId
                || questionRenderToken !== answeredRenderToken
                || currentQIndex !== answeredViewIndex
                || Number(filteredIndices[currentQIndex]) !== realIdx
                || buildQuestionPersistenceKeyFromQuestion(questions[realIdx]) !== activeQuestionKey) return;
            nextQuestion();
        }, 500);
    }

    revealAnswer(realIdx, cIdx);
    if (!isPublicTourDemo && typeof recordMemoraStudyAnswer === 'function') recordMemoraStudyAnswer(questions[realIdx], { chosenIndex: cIdx, correct: isCorrect, eventId: `${currentSessionId || ''}:${activeQuestionKey}:${Date.now()}` });
    if (!isPublicTourDemo && isAdaptiveReviewMode) openAdaptiveRatingModal(realIdx, isCorrect, cIdx);
    if (!isPublicTourDemo && isAdaptiveReviewMode && typeof saveAICache === 'function') {
        void saveAICache();
    }
    updateLiveStats(); // Update stats immediately
    if (!isPublicTourDemo) {
        markSessionStateMutation();
        saveSessionDebounced();
        if (isAdaptiveReviewMode) flushPendingSessionSave();
    }
}

function revealAnswer(idx, uIdx, isRestoring = false) {
    const q = questions[idx];
    const btns = Array.from(els.choices.querySelectorAll('.choice'));
    const answerKeyVerified = isQuestionAnswerKeyVerified(q);

    btns.forEach(b => {
        const bIdx = parseInt(b.dataset.idx);
        b.classList.add('locked');
        if (!answerKeyVerified) {
            b.classList.toggle('selected', bIdx === uIdx);
            return;
        }

        if (bIdx === q.a) {
            b.classList.add('correct');
            // Trigger Smart Evidence Connection only on fresh answers (not restore renders)
            if (!isRestoring && bIdx === uIdx && !isPublicTourQuizDemoActive()) {
                triggerSmartConnection(q);
            }
        } else if (bIdx === uIdx && uIdx !== q.a) {
            b.classList.add('incorrect');
        }
    });

    // Choice popups own static feedback. Keep the inline surface clear for AI responses.
    els.expl.innerHTML = '';
    els.expl.style.display = 'none';
    els.expl.classList.remove('ai-response-box', 'ai-response-loading', 'explanation-revealing', 'from-correct', 'from-incorrect');
    els.expl.removeAttribute('data-tone');
    els.expl.removeAttribute('aria-busy');
    els.expl.style.removeProperty('--explanation-origin-x');
    els.expl.onclick = null;
    els.expl.title = '';
    els.expl.style.cursor = '';
    els.choices.classList.remove('hard-mode');

    if ($('hard-mode-overlay')) {
        $('hard-mode-overlay').remove();
    }

    scheduleQuizCardContentScaleSync();
    if (typeof scheduleQuizCardHeightNormalization === 'function') {
        scheduleQuizCardHeightNormalization();
    }
}

/* --- REFRESH QUESTION/QUIZ LOGIC --- */
function shuffleInPlace(arr) {
    for (let i = arr.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    return arr;
}

function buildShuffledChoiceOrder(realIdx, previousOrder = null) {
    const q = questions[realIdx];
    const choices = Array.isArray(q?.c) ? q.c : (Array.isArray(q?.o) ? q.o : []);
    const order = choices.map((_, i) => i);
    if (order.length <= 1) return order;

    const previous = (Array.isArray(previousOrder) && previousOrder.length === order.length)
        ? previousOrder
        : forcedChoiceOrderByQuestion[realIdx];
    shuffleInPlace(order);
    if (Array.isArray(previous) && previous.length === order.length && order.every((v, i) => v === previous[i])) {
        [order[0], order[1]] = [order[1], order[0]];
    }
    return order;
}

function getStableMixedChoiceOrder(realIdx) {
    const q = questions[realIdx];
    const choices = Array.isArray(q?.c) ? q.c : (Array.isArray(q?.o) ? q.o : []);
    const existing = mixedChoiceOrderByQuestion[realIdx];
    if (Array.isArray(existing) && existing.length === choices.length) {
        return [...existing];
    }

    const nextOrder = buildShuffledChoiceOrder(realIdx, existing);
    mixedChoiceOrderByQuestion[realIdx] = [...nextOrder];
    return [...nextOrder];
}

function refreshCurrentQuestionAndShuffle(showNotification = true) {
    if (!questions.length || !filteredIndices.length) return;
    const realIdx = filteredIndices[currentQIndex];
    if (!Number.isInteger(realIdx)) return;

    if (els.refreshOptionsDropdown) els.refreshOptionsDropdown.classList.remove('active');
    if (els.resetQBtn) els.resetQBtn.classList.remove('active');

    // A bookmark is the learner's own pick; only the save/unsave control removes it.
    delete userAnswers[realIdx];
    if (typeof clearMemoraStudyMisses === 'function') clearMemoraStudyMisses(questions[realIdx]);
    if (pendingAdaptiveRating && pendingAdaptiveRating.realIdx === realIdx) {
        pendingAdaptiveRating = null;
        closeAdaptiveRatingModal();
    }

    const previousChoiceOrder = Array.isArray(forcedChoiceOrderByQuestion[realIdx])
        ? forcedChoiceOrderByQuestion[realIdx]
        : mixedChoiceOrderByQuestion[realIdx];
    delete mixedChoiceOrderByQuestion[realIdx];
    forcedChoiceOrderByQuestion[realIdx] = buildShuffledChoiceOrder(realIdx, previousChoiceOrder);
    renderQuestion();
    if (showNotification) showPopup('Question refreshed and choices shuffled.', null, false);
    markSessionStateMutation();
    saveSessionDebounced();
}

function refreshEntireQuizAndShuffle(showNotification = true) {
    if (!questions.length) return;

    if (els.refreshOptionsDropdown) els.refreshOptionsDropdown.classList.remove('active');
    if (els.resetQBtn) els.resetQBtn.classList.remove('active');

    // Saved (bookmarked) questions are the learner's own picks and survive a refresh.
    // The completion bonus is not reset here: a set pays once per day, so retakes can't farm it.
    userAnswers = {};
    if (typeof clearMemoraStudyMisses === 'function') clearMemoraStudyMisses(questions);
    pendingAdaptiveRating = null;
    closeAdaptiveRatingModal();
    forcedChoiceOrderByQuestion = {};
    mixedChoiceOrderByQuestion = {};
    condensedQuizState = createDefaultCondensedQuizState();
    highYieldQuizState = normalizeHighYieldQuizState({ enabled: highYieldQuizState.enabled });
    adaptivePracticeState = normalizeAdaptivePracticeState();
    highYieldLastAppliedPool = null;

    if (isAdaptiveReviewMode) {
        const adaptiveQueue = buildAdaptiveReviewQueue();
        if (adaptiveQueue.length === 0) {
            deactivateAdaptiveReviewMode(false);
            if (showNotification) showPopup('No cards are due right now.', null, false);
            return;
        }
        filteredIndices = adaptiveQueue;
        shuffleInPlace(filteredIndices);
        moveShownLastQuestionsBehind(filteredIndices);
        isFilterActive = true;
        currentQIndex = 0;
        updateAdaptiveFilterBanner();
    } else {
        resetFilter();
        shuffleInPlace(filteredIndices);
        moveShownLastQuestionsBehind(filteredIndices);
        currentQIndex = 0;
    }

    isFlashcardMode = false;
    els.modeBtn.querySelector('.gradient-text').innerText = "🎴 Switch to Flashcards";
    renderQuestion();
    if (showNotification) showPopup('Quiz refreshed and shuffled.', null, false);
    markSessionStateMutation();
    saveSessionDebounced();
}

function closeActiveAnchoredPopup() {
    if (!els.popup || (!currentPopupBtn && !els.popup.classList.contains('answer-choice-callout'))) return false;
    if (els.popup.timer) {
        clearTimeout(els.popup.timer);
        els.popup.timer = null;
    }
    els.popup.remainingMs = 0;
    els.popup.dismissAt = 0;
    els.popup.pauseOnHover = false;
    els.popup.onmouseenter = null;
    els.popup.onmouseleave = null;
    els.popup.classList.remove('active', 'popup-side-right', 'popup-side-left', 'popup-side-below', 'popup-side-above');
    currentPopupBtn = null;
    return true;
}

function getAnchoredPopupBoundary() {
    const viewportPadding = 10;
    const visualViewport = window.visualViewport;
    const viewportLeft = Number.isFinite(visualViewport?.offsetLeft) ? visualViewport.offsetLeft : 0;
    const viewportTop = Number.isFinite(visualViewport?.offsetTop) ? visualViewport.offsetTop : 0;
    const viewportWidth = Number.isFinite(visualViewport?.width) ? visualViewport.width : window.innerWidth;
    const viewportHeight = Number.isFinite(visualViewport?.height) ? visualViewport.height : window.innerHeight;
    const boundary = {
        left: viewportLeft + viewportPadding,
        top: viewportTop + viewportPadding,
        right: Math.max(viewportLeft + viewportPadding, viewportLeft + viewportWidth - viewportPadding),
        bottom: Math.max(viewportTop + viewportPadding, viewportTop + viewportHeight - viewportPadding)
    };

    const workArea = els?.workArea || $('work-area');
    const hasMultiPaneLayout = !!(
        workArea
        && (workArea.classList.contains('split-view')
            || workArea.classList.contains('split-view-ai')
            || workArea.classList.contains('trio-view')
            || workArea.classList.contains('pdf-tutor-quiz-duo')
            || workArea.classList.contains('pdf-tutor-quiz-trio'))
    );
    const constrainAnswerChoiceToQuizPane = !!(
        hasMultiPaneLayout
        && els?.popup?.classList?.contains('answer-choice-callout')
        && !document.body?.classList?.contains('quiz-card-expanded-mode')
    );
    const quizPane = constrainAnswerChoiceToQuizPane ? $('quiz-pane') : null;
    if (!quizPane) return boundary;

    const quizRect = quizPane.getBoundingClientRect();
    boundary.left = Math.max(boundary.left, quizRect.left + viewportPadding);
    boundary.top = Math.max(boundary.top, quizRect.top + viewportPadding);
    boundary.right = Math.min(boundary.right, quizRect.right - viewportPadding);
    boundary.bottom = Math.min(boundary.bottom, quizRect.bottom - viewportPadding);
    return boundary;
}

// Intersection of the window viewport with every overflow-clipping ancestor,
// so an anchor scrolled behind the edge of the quiz pane (whose rect may still
// sit inside the window) is correctly treated as clipped.
function getAnchoredPopupClipRect(anchor) {
    const clip = getAnchoredPopupBoundary();
    const clipsOverflow = /(auto|scroll|hidden|clip)/;
    let node = anchor.parentElement;
    while (node && node !== document.body && node !== document.documentElement) {
        const style = getComputedStyle(node);
        if (clipsOverflow.test(style.overflowY) || clipsOverflow.test(style.overflowX)) {
            const r = node.getBoundingClientRect();
            clip.left = Math.max(clip.left, r.left);
            clip.top = Math.max(clip.top, r.top);
            clip.right = Math.min(clip.right, r.right);
            clip.bottom = Math.min(clip.bottom, r.bottom);
        }
        node = node.parentElement;
    }
    return clip;
}

function isAnchoredPopupAnchorVisible(anchor) {
    if (!anchor || !anchor.isConnected) return false;
    if (document.body?.classList?.contains('pdf-expanded-mode')) return false;

    const rect = anchor.getBoundingClientRect();
    const boundary = getAnchoredPopupBoundary();
    if (!(rect.width > 0) || !(rect.height > 0)) return false;
    if (rect.right <= boundary.left || rect.left >= boundary.right || rect.bottom <= boundary.top || rect.top >= boundary.bottom) return false;

    // Close the popup as soon as the anchor is mostly clipped by its scroll
    // container (not only when it fully leaves the window), so the explanation
    // fades out early enough while scrolling instead of lingering at the edge.
    const clip = getAnchoredPopupClipRect(anchor);
    const visibleW = Math.min(rect.right, clip.right) - Math.max(rect.left, clip.left);
    const visibleH = Math.min(rect.bottom, clip.bottom) - Math.max(rect.top, clip.top);
    if (visibleW <= 0 || visibleH <= 0) return false;
    const visibleFraction = (visibleW * visibleH) / (rect.width * rect.height);
    if (visibleFraction < 0.5) return false;

    const style = getComputedStyle(anchor);
    if (style.display === 'none' || style.visibility === 'hidden' || Number(style.opacity) === 0) return false;
    return true;
}

/* --- UPDATED POPUP POSITIONING --- */
function updatePopupPosition(force = false) {
    if (!currentPopupBtn || (!force && !els.popup.classList.contains('active'))) return;
    if (!isAnchoredPopupAnchorVisible(currentPopupBtn)) {
        closeActiveAnchoredPopup();
        return;
    }

    const btnRect = currentPopupBtn.getBoundingClientRect();
    const popupRect = els.popup.getBoundingClientRect();
    const gap = 12;
    const boundary = getAnchoredPopupBoundary();

    let sideClass = 'popup-side-right';
    let left = btnRect.right + gap;
    let top = btnRect.top + (btnRect.height / 2) - (popupRect.height / 2);
    const keepRightInExpandedQuiz = document.body?.classList?.contains('quiz-card-expanded-mode');
    const keepAnswerChoiceCalloutRight = els.popup.classList.contains('answer-choice-callout');
    const visualViewport = window.visualViewport;
    const viewportLeft = Number.isFinite(visualViewport?.offsetLeft) ? visualViewport.offsetLeft : 0;
    const viewportWidth = Number.isFinite(visualViewport?.width) ? visualViewport.width : window.innerWidth;
    const paneConstrainedAnswerChoice = keepAnswerChoiceCalloutRight
        && (boundary.left > viewportLeft + 10 || boundary.right < viewportLeft + viewportWidth - 10);

    if (paneConstrainedAnswerChoice) {
        const rightLeft = btnRect.right + gap;
        const leftLeft = btnRect.left - popupRect.width - gap;
        if (rightLeft + popupRect.width <= boundary.right) {
            left = rightLeft;
        } else if (leftLeft >= boundary.left) {
            left = leftLeft;
            sideClass = 'popup-side-left';
        } else {
            left = btnRect.left + (btnRect.width / 2) - (popupRect.width / 2);
            const belowTop = btnRect.bottom + gap;
            const aboveTop = btnRect.top - popupRect.height - gap;
            if (belowTop + popupRect.height <= boundary.bottom || aboveTop < boundary.top) {
                top = belowTop;
                sideClass = 'popup-side-below';
            } else {
                top = aboveTop;
                sideClass = 'popup-side-above';
            }
        }
    } else if (left + popupRect.width > boundary.right && (keepRightInExpandedQuiz || keepAnswerChoiceCalloutRight)) {
        left = Math.max(boundary.left, boundary.right - popupRect.width);
    } else if (left + popupRect.width > boundary.right) {
        left = btnRect.left - popupRect.width - gap;
        sideClass = 'popup-side-left';
    }

    if (left < boundary.left) {
        left = boundary.left;
    }

    if (left + popupRect.width > boundary.right) {
        left = Math.max(boundary.left, boundary.right - popupRect.width);
    }

    if (top < boundary.top) top = boundary.top;
    if (top + popupRect.height > boundary.bottom) {
        top = Math.max(boundary.top, boundary.bottom - popupRect.height);
    }

    // Update only positioning/tone classes. Replacing the full className here
    // restarts the active fade on every re-anchor; WebKit can then hold the
    // callout at opacity 0 while pane/card geometry is still settling.
    const basePopupClass = els.popup.classList.contains('popup-error') ? 'popup-error' : 'popup-info';
    els.popup.classList.remove(
        'popup-side-right',
        'popup-side-left',
        'popup-side-below',
        'popup-side-above',
        'popup-error',
        'popup-info'
    );
    els.popup.classList.add(basePopupClass, sideClass);
    if (sideClass === 'popup-side-below' || sideClass === 'popup-side-above') {
        const arrowX = Math.min(
            Math.max((btnRect.left + (btnRect.width / 2)) - left, 12),
            Math.max(12, popupRect.width - 12)
        );
        els.popup.style.setProperty('--popup-arrow-x', `${arrowX}px`);
    } else {
        els.popup.style.removeProperty('--popup-arrow-x');
    }
    els.popup.style.top = top + 'px';
    els.popup.style.left = left + 'px';
    els.popup.style.right = '';
    els.popup.style.bottom = '';
    els.popup.style.transform = '';
}

const MEMORA_POPUP_KIND_ICONS = Object.freeze({
    info: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="9"/><path d="M12 10.5v5.5M12 7.5h.01"/></svg>',
    success: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="9"/><path d="m8 12 2.6 2.6L16 9"/></svg>',
    warning: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M10.4 4.4a1.8 1.8 0 0 1 3.2 0l8 14.1A1.8 1.8 0 0 1 20 21H4a1.8 1.8 0 0 1-1.6-2.5z"/><path d="M12 9v5M12 17.5h.01"/></svg>',
    error: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="9"/><path d="m9 9 6 6m0-6-6 6"/></svg>'
});

function showPopupText(text, btn, isError = true, duration = null) {
    return showPopup(escapeHtml(text), btn, isError, duration);
}

function showPopup(txt, btn, isError = true, duration = null) {
    const popupOptions = (duration && typeof duration === 'object' && !Array.isArray(duration))
        ? duration
        : null;
    const customDuration = popupOptions ? popupOptions.duration : duration;
    const popupClassName = popupOptions && typeof popupOptions.popupClass === 'string'
        ? popupOptions.popupClass.trim()
        : '';
    const popupClassList = popupClassName ? popupClassName.split(/\s+/).filter(Boolean) : [];
    const dismissible = !!popupOptions?.dismissible;
    const dismissLabel = popupOptions?.dismissLabel || 'Dismiss notification';
    const sticky = !!popupOptions?.sticky;
    const requestedKind = String(popupOptions?.kind || '').toLowerCase();
    const kind = Object.hasOwn(MEMORA_POPUP_KIND_ICONS, requestedKind)
        ? requestedKind : (isError ? 'error' : 'info');

    const closePopup = () => {
        if (els.popup.timer) {
            clearTimeout(els.popup.timer);
            els.popup.timer = null;
        }
        els.popup.remainingMs = 0;
        els.popup.dismissAt = 0;
        els.popup.pauseOnHover = false;
        els.popup.classList.remove('active');
        currentPopupBtn = null;
    };

    const armPopupTimer = (ms) => {
        const timeoutMs = Math.max(0, Number(ms) || 0);
        if (els.popup.timer) clearTimeout(els.popup.timer);
        if (timeoutMs === 0) {
            closePopup();
            return;
        }
        els.popup.remainingMs = timeoutMs;
        els.popup.dismissAt = Date.now() + timeoutMs;
        els.popup.timer = setTimeout(() => closePopup(), timeoutMs);
    };

    // Reset pause/resume handlers for each new popup render.
    els.popup.onmouseenter = null;
    els.popup.onmouseleave = null;
    els.popup.pauseOnHover = false;
    if (els.popup.timer) {
        clearTimeout(els.popup.timer);
        els.popup.timer = null;
    }

    els.popup.className = (btn ? isError : kind === 'error') ? 'popup-error' : 'popup-info';
    if (popupClassList.length) {
        els.popup.classList.add(...popupClassList);
    }
    if (!btn) {
        els.popup.classList.add(`popup-kind-${kind}`);
        els.popup.dataset.notificationKind = kind;
        els.popup.setAttribute('role', kind === 'error' ? 'alert' : 'status');
    } else {
        delete els.popup.dataset.notificationKind;
        els.popup.removeAttribute('role');
    }
    // Explicitly make sure active is removed first to reset the transition state
    els.popup.classList.remove('active');
    els.popup.innerHTML = (!btn
        ? `<span class="popup-kind-icon" aria-hidden="true">${MEMORA_POPUP_KIND_ICONS[kind]}</span>`
        : '') + (btn || typeof txt !== 'string'
        ? txt
        : txt.replace(/^\s*(?:\p{Extended_Pictographic}[\uFE0F\u200D]*)+\s*/u, ''));

    if (dismissible) {
        const closeBtn = document.createElement('button');
        closeBtn.type = 'button';
        closeBtn.className = 'popup-inline-close';
        closeBtn.setAttribute('aria-label', dismissLabel);
        closeBtn.innerHTML = '&times;';
        closeBtn.addEventListener('click', (event) => {
            event.preventDefault();
            event.stopPropagation();
            closePopup();
        });
        els.popup.appendChild(closeBtn);
    }

    if (!btn) {
        // Center popup at bottom (for general messages)
        els.popup.classList.add('popup-centered');
        els.popup.style.top = '';
        els.popup.style.left = '';
        els.popup.style.right = '';
        els.popup.style.bottom = '20px';
        els.popup.style.transform = 'translateX(-50%)';
        els.popup.style.left = '50%';
        void els.popup.offsetWidth; // Force reflow to restart transition/animation
        els.popup.classList.add('active');
    } else {
        // Position next to button
        els.popup.classList.remove('popup-centered');
        currentPopupBtn = btn;
        els.popup.style.bottom = '';
        els.popup.style.right = '';
        els.popup.style.transform = '';
        
        // Position first while inactive (opacity 0)
        updatePopupPosition(true);
        
        // Force reflow to guarantee the browser registers the inactive state bounds
        void els.popup.offsetWidth;
        
        // Transition in cleanly
        els.popup.classList.add('active');
    }

    const timeout = customDuration || (btn ? 10000 : 3000);
    if (btn) {
        // Wrong-answer callouts are anchored to choices; pause their timer on hover.
        els.popup.pauseOnHover = true;
        els.popup.onmouseenter = () => {
            if (!els.popup.pauseOnHover || !els.popup.classList.contains('active')) return;
            if (els.popup.timer) {
                clearTimeout(els.popup.timer);
                els.popup.timer = null;
            }
            els.popup.remainingMs = Math.max(0, (Number(els.popup.dismissAt) || Date.now()) - Date.now());
        };
        els.popup.onmouseleave = () => {
            if (!els.popup.pauseOnHover || !els.popup.classList.contains('active')) return;
            armPopupTimer(els.popup.remainingMs);
        };
    }

    if (!sticky) armPopupTimer(timeout);
}


// Statement 1/Statement 2 cards read alike, so Next never lands on one within three cards of
// another: the nearest later unanswered card trades places with it. Only unseen cards move.
const STATEMENT_QUESTION_MIN_GAP = 3;
function isStatementPairQuestion(question) {
    return !!question && (question.quizFormat === 'two_statement' || /^\s*Statement\s*1\s*:/i.test(String(question.q || '')));
}
function spaceStatementQuestionAtCurrent() {
    const realIdx = filteredIndices[currentQIndex];
    if (isFlashcardMode || userAnswers[realIdx] !== undefined || !isStatementPairQuestion(questions[realIdx])) return;
    const recent = filteredIndices.slice(Math.max(0, currentQIndex - STATEMENT_QUESTION_MIN_GAP), currentQIndex);
    if (!recent.some(idx => isStatementPairQuestion(questions[idx]))) return;
    for (let position = currentQIndex + 1; position < filteredIndices.length; position++) {
        const candidate = filteredIndices[position];
        if (userAnswers[candidate] !== undefined || isStatementPairQuestion(questions[candidate])) continue;
        filteredIndices[currentQIndex] = candidate;
        filteredIndices[position] = realIdx;
        return;
    }
}
// Next past the last question while this quiz is still generating: a gentle notice, then the next
// question opens by itself once it arrives, unless the student moves on or leaves the quiz first.
let quizNextArrivalWaiter = null;
function getCurrentQuizQuestionKey() {
    const realIdx = filteredIndices[currentQIndex];
    return Number.isInteger(realIdx) && questions[realIdx] ? buildQuestionPersistenceKeyFromQuestion(questions[realIdx]) : '';
}
function stopWaitingForNextGeneratedQuestion() {
    if (!quizNextArrivalWaiter) return;
    clearInterval(quizNextArrivalWaiter.timer);
    quizNextArrivalWaiter = null;
    if (els.popup?.classList.contains('quiz-next-arrival-notice')) {
        if (els.popup.timer) clearTimeout(els.popup.timer);
        els.popup.timer = null;
        els.popup.classList.remove('active');
    }
}
function waitForNextGeneratedQuestion() {
    const key = getCurrentQuizQuestionKey();
    if (!key) return;
    showPopup('More questions are still generating. The next one opens as soon as it arrives.', null, false,
        { kind: 'info', sticky: true, dismissible: true, popupClass: 'quiz-core-done-notice quiz-next-arrival-notice' });
    if (quizNextArrivalWaiter?.key === key) return;
    if (quizNextArrivalWaiter) clearInterval(quizNextArrivalWaiter.timer);
    quizNextArrivalWaiter = { key, timer: setInterval(checkNextGeneratedQuestionArrival, 400) };
}
function checkNextGeneratedQuestionArrival() {
    const waiter = quizNextArrivalWaiter;
    if (!waiter) return;
    const resultsShowing = !!els.resultsView && !!els.resultsView.style.display && els.resultsView.style.display !== 'none';
    const quizInView = !isFlashcardMode && !isAdaptiveReviewMode && !resultsShowing
        && !document.body.classList.contains('welcome-mode')
        && !!els.quizCard?.isConnected && els.quizCard.getClientRects().length > 0;
    // Moving to another question or leaving the quiz cancels the wait, and its notice with it.
    if (!quizInView || getCurrentQuizQuestionKey() !== waiter.key) { stopWaitingForNextGeneratedQuestion(); return; }
    if (currentQIndex >= filteredIndices.length - 1 && getQuizCompletionProgressState().pendingNewQuestions > 0) {
        includePendingQuizCompletionQuestions({ reason: 'next-question-arrived' });
    }
    if (currentQIndex < filteredIndices.length - 1) {
        stopWaitingForNextGeneratedQuestion();
        nextQuestion();
        return;
    }
    // Generation ended with nothing more for this view: Next now reads Finish.
    if (!(isAnyQuizGenerationActive() && isOpenQuizStillGenerating())) {
        stopWaitingForNextGeneratedQuestion();
        syncQuizProgressText();
    }
}
function nextQuestion() {
    // A generation landing can cover an existing bank. Only its explicit
    // Return to Quiz action may reveal that bank; keyboard shortcuts call this
    // function directly and therefore cannot rely on disabled button chrome.
    if (isQuizGenerationCardLandingShowing()) return;
    if (els.searchDropdown?.classList.contains('active')) {
        setSearchDropdownOpen(false);
        if (els.searchInput) els.searchInput.value = '';
    }
    window.MemoraLectureFinder?.close?.();
    if (isHighYieldQuizEnabled()) applyHighYieldQuizPool();
    if (navigatePastHighYieldPinnedQuestion(1)) return;
    if (isAdaptiveReviewMode) {
        if (ensurePendingAdaptiveRatingModalVisible(true)) return;
        if (!filteredIndices.length) return;
        handleQuestionNavigationFromExpandedQuizCard();
        currentQIndex = (currentQIndex + 1) % filteredIndices.length;
        writeQuickSessionSnapshot();
        renderQuestion('anim-fade');
        void window.requestGeminiLiveQuestionReadoutIfArmed?.({
            includeChoices: true,
            reason: 'next-question'
        });
        return;
    }
    prepareAdaptivePracticeNextQuestion();
    if (currentQIndex < filteredIndices.length - 1) {
        handleQuestionNavigationFromExpandedQuizCard();
        currentQIndex++;
        spaceStatementQuestionAtCurrent();
        writeQuickSessionSnapshot();
        renderQuestion('anim-fade');
        void window.requestGeminiLiveQuestionReadoutIfArmed?.({
            includeChoices: true,
            reason: 'next-question'
        });
    } else {
        const completion = getQuizCompletionProgressState();
        if (completion.pendingNewQuestions > 0 && completion.completionPercent >= 100) {
            includePendingQuizCompletionQuestions({ reason: 'finish-current-cohort' });
            prepareAdaptivePracticeNextQuestion();
            if (currentQIndex < filteredIndices.length - 1) {
                handleQuestionNavigationFromExpandedQuizCard();
                currentQIndex++;
                spaceStatementQuestionAtCurrent();
                writeQuickSessionSnapshot();
                renderQuestion('anim-fade');
                void window.requestGeminiLiveQuestionReadoutIfArmed?.({
                    includeChoices: true,
                    reason: 'next-question'
                });
                return;
            }
        }
        if (typeof window.handleQuizGenerationStudyBoundary === 'function'
            && window.handleQuizGenerationStudyBoundary()) {
            waitForNextGeneratedQuestion();
            return;
        }
        if (isAnyQuizGenerationActive() && isOpenQuizStillGenerating()) {
            waitForNextGeneratedQuestion();
            return;
        }
        if (advanceFromCoreIntoExtendedTier()) return;
        if (isQuizTierFinishGated()) { showQuizUnansweredFinishNotice(); return; }
        showResults();
    }
}
function prevQuestion() {
    if (isQuizGenerationCardLandingShowing()) return;
    if (navigatePastHighYieldPinnedQuestion(-1)) return;
    if (currentQIndex > 0) {
        handleQuestionNavigationFromExpandedQuizCard();
        currentQIndex--;
        writeQuickSessionSnapshot();
        renderQuestion('anim-fade');
        void window.requestGeminiLiveQuestionReadoutIfArmed?.({
            includeChoices: true,
            reason: 'prev-question'
        });
    }
}
els.jump.onchange = () => {
    currentQIndex = parseInt(els.jump.value);
    writeQuickSessionSnapshot();
    renderQuestion();
    void window.requestGeminiLiveQuestionReadoutIfArmed?.({
        includeChoices: true,
        reason: 'jump-question'
    });
};

const RESULTS_COMPLETION_AWARD_LIMIT = 200;

// Identifies one question set on one day for the completion bonus. Quiz and exam runs of the
// same set share the key, and a refresh or retake keeps it, so short sets can't be farmed.
function buildResultsCompletionAwardKey() {
    const parts = [...new Set(filteredIndices.map(idx => buildGameModeAnswerRewardIdentity(questions[idx])?.key).filter(Boolean))].sort();
    const text = parts.join('\n');
    let hash = 5381;
    for (let i = 0; i < text.length; i++) hash = ((hash * 33) ^ text.charCodeAt(i)) >>> 0;
    return `${new Date().toDateString()}|set|${parts.length}-${hash.toString(36)}`;
}

// Session bonus per answered question, so it grows with the work done rather than the
// number of finishes. Capped so one enormous set can't dwarf a week of normal study.
const SESSION_BONUS_PER_ANSWER = 2;
const SESSION_BONUS_ANSWER_CAP = 100;
function buildSessionCompletionBonus(answered, pct) {
    const counted = Math.min(SESSION_BONUS_ANSWER_CAP, Math.max(0, answered));
    const rows = [{ label: `${counted} answered`, amount: counted * SESSION_BONUS_PER_ANSWER }];
    let reason = 'Session complete';
    const accuracyRate = pct >= 90 ? 3 : pct >= 80 ? 2 : pct >= 70 ? 1 : 0;
    if (accuracyRate) {
        rows.push({ label: `Accuracy ${pct}%`, amount: counted * accuracyRate });
        reason = pct >= 90 ? 'Excellent session' : pct >= 80 ? 'Great session' : 'Good session';
    }
    if (pct === 100) {
        rows.push({ label: 'Perfect score', amount: counted * 2 });
        reason = 'Perfect session';
    }
    return { rows, reason, total: rows.reduce((sum, row) => sum + row.amount, 0) };
}

function hasResultsCompletionAward(key) {
    return Array.isArray(xpData?.completionAwardKeys) && xpData.completionAwardKeys.includes(key);
}

function recordResultsCompletionAward(key, persist = true) {
    const record = (list) => {
        const keys = list.filter(existing => existing !== key);
        keys.push(key);
        return keys.slice(-RESULTS_COMPLETION_AWARD_LIMIT);
    };
    xpData.completionAwardKeys = record(Array.isArray(xpData.completionAwardKeys) ? xpData.completionAwardKeys : []);
    if (persist) commitXPRecordChange({ mutateKeys: record });
}

function clearResultsCompletionAwards(prefix) {
    if (!Array.isArray(xpData?.completionAwardKeys)) return;
    const clear = (list) => list.filter(key => !String(key).startsWith(prefix));
    xpData.completionAwardKeys = clear(xpData.completionAwardKeys);
    commitXPRecordChange({ mutateKeys: clear });
}

// Saved mastery progress is separate from XP; reopening results never counts twice.
function buildMasteryQuizProgress(raw, key, day) {
    const count = Math.max(0, Math.floor(Number(raw?.count) || 0));
    const keys = raw?.day === day && Array.isArray(raw.keys) ? [...new Set(raw.keys.filter(value => typeof value === 'string'))] : [];
    if (keys.includes(key)) return { count, day, keys };
    return { count: count + 1, day, keys: [...keys, key] };
}
async function recordMasteryQuizCompletion(key, correct, total, answered, fullBank) {
    if (!isGameModeRewardEligible() || !fullBank || total < 5 || answered !== total || correct * 100 < total * 80 || !key) return false;
    try {
        const day = new Date().toDateString();
        const record = await IDB.runWriteTransaction(['files'], (tx, write, abort, setResult) => {
            const request = tx.objectStore('files').get('xpData');
            request.onerror = () => abort(request.error || new Error('Could not save quiz mastery.'));
            request.onsuccess = () => {
                if (!isGameModeRewardEligible()) { setResult(null); return; }
                const stored = request.result || {};
                const next = { ...stored, masteryQuizzes: buildMasteryQuizProgress(stored.masteryQuizzes, key, day) };
                write({ storeName: 'files', key: 'xpData', value: next });
                setResult(next);
            };
        }, { timeoutMs: 10000 });
        if (!record) return false;
        applyCommittedXPRecord(record);
        try { getXPSyncChannel()?.postMessage({ record }); } catch (_) { }
        checkAchievements();
        return true;
    } catch (error) {
        console.warn('Quiz mastery could not be saved; badges were not advanced.', error);
        return false;
    }
}

function showResults({ exam = false, answers = null, attemptId = '' } = {}) {
    const hasRenderableQuestions = Array.isArray(questions) && questions.length > 0
        && Array.isArray(filteredIndices) && filteredIndices.length > 0;
    if (!hasRenderableQuestions) {
        els.quizContent.style.display = 'block';
        els.resultsView.style.display = 'none';
        renderQuestion();
        return;
    }

    const getSessionGradeDetails = (scorePct, answeredCount) => {
        if (!answeredCount) {
            return { grade: 'N/A', label: 'No answers yet', tier: 'tier-none' };
        }
        if (scorePct >= 97) return { grade: 'S', label: 'Outstanding accuracy', tier: 'tier-elite' };
        if (scorePct >= 90) return { grade: 'A', label: 'Very strong performance', tier: 'tier-strong' };
        if (scorePct >= 80) return { grade: 'B', label: 'Solid performance', tier: 'tier-solid' };
        if (scorePct >= 70) return { grade: 'C', label: 'Developing consistency', tier: 'tier-developing' };
        return { grade: 'D', label: 'Needs reinforcement', tier: 'tier-foundation' };
    };

    const getSessionSummaryMessage = (scorePct, answeredCount, unansweredCount = 0) => {
        if (!answeredCount) return 'Answer a few items to generate full session insights.';
        if (scorePct === 100 && unansweredCount > 0) return `Correct on every answered item; ${unansweredCount} still unanswered.`;
        if (scorePct === 100) return 'Complete mastery on answered items.';
        if (scorePct >= 90) return 'Strong command of the material in this run.';
        if (scorePct >= 80) return 'Good control overall, with a few gaps to close.';
        if (scorePct >= 70) return 'Progress is clear; focus on error patterns.';
        return 'Review fundamentals and rerun targeted items.';
    };

    questionRenderToken++;
    clearAutoStudyTimers();

    els.quizContent.style.display = 'none';
    els.resultsView.style.display = 'flex';
    applyQuizCardContentScale({ forceReset: true });

    let correct = 0;
    let answered = 0;
    let wrong = [];
    let longestCorrectRun = 0;
    let currentCorrectRun = 0;
    const sourceStats = {}; // { sourceFile: { correct, wrong, total } }
    // A question whose answer key could not be confirmed has nothing to score against, so it
    // stays out of every count, denominator and source breakdown instead of passing as correct.
    let unscored = 0;

    filteredIndices.forEach((i) => {
        // A reopened exam scores its own submitted attempt, not later practice answers.
        const selected = (answers || userAnswers)[i];
        if (questions[i]?.answerUnverified) {
            unscored++;
            return;
        }
        if (selected === undefined) {
            currentCorrectRun = 0;
            return;
        }

        answered++;
        const q = questions[i];
        const src = q.sourceFile || 'Unknown';
        if (!sourceStats[src]) sourceStats[src] = { correct: 0, wrong: 0, total: 0 };
        sourceStats[src].total++;

        if (selected === q.a) {
            correct++;
            sourceStats[src].correct++;
            currentCorrectRun++;
            longestCorrectRun = Math.max(longestCorrectRun, currentCorrectRun);
        } else {
            wrong.push(i);
            sourceStats[src].wrong++;
            currentCorrectRun = 0;
        }
    });

    const totalQuestions = filteredIndices.length - unscored;
    const incorrect = answered - correct;
    const unanswered = Math.max(0, totalQuestions - answered);
    const answeredAccuracy = answered === 0 ? 0 : Math.round((correct / answered) * 100);
    const pct = exam ? (totalQuestions ? Math.round(correct / totalQuestions * 100) : 0) : answeredAccuracy;
    const grade = getSessionGradeDetails(pct, exam ? totalQuestions : answered);
    const unscoredNote = unscored ? ` · ${unscored} not scored (answer key unverified)` : '';
    const summaryMessage = (exam
        ? correct + '/' + totalQuestions + ' correct · ' + answered + '/' + totalQuestions + ' attempted · ' + answeredAccuracy + '% accuracy on answered questions'
        : getSessionSummaryMessage(pct, answered, unanswered)) + unscoredNote;

    if (pct === 100 && answered >= 5) {
        unlockAchievement('perfect-session');
    }
    if (answered >= 50) {
        unlockAchievement('marathon');
    }
    if (answered >= 100) {
        unlockAchievement('ultra-marathon');
    }

    let completionBonus = 0;
    let completionReason = '';
    const completionBonusRows = [];
    // One completion bonus per question set per day: Finish, Return, Finish, a refresh, or a
    // retake shows the result again without paying for it again.
    const completionAwardKey = buildResultsCompletionAwardKey();
    const fullMasteryBank = unscored === 0 && filteredIndices.length === questions.length && new Set(filteredIndices).size === questions.length
        && new Set(filteredIndices.map(idx => buildGameModeAnswerRewardIdentity(questions[idx])?.key).filter(Boolean)).size === questions.length;
    void recordMasteryQuizCompletion(completionAwardKey, correct, totalQuestions, answered, fullMasteryBank);
    const completionAlreadyAwarded = hasResultsCompletionAward(completionAwardKey);
    const completionAnswers = new Map();
    for (const index of filteredIndices) {
        const selected = (answers || userAnswers)[index], question = questions[index];
        const key = selected !== undefined && !question?.answerUnverified ? buildGameModeAnswerRewardIdentity(question)?.key : null;
        if (key && xpData.answerRewardDays?.[key]?.completionPaid !== true && !completionAnswers.has(key)) completionAnswers.set(key, selected === question.a);
    }
    if (isGameModeRewardEligible() && completionAnswers.size >= 5 && !completionAlreadyAwarded) {
        const distinctAccuracy = Math.round([...completionAnswers.values()].filter(Boolean).length / completionAnswers.size * 100);
        const bonus = buildSessionCompletionBonus(completionAnswers.size, distinctAccuracy);
        completionBonusRows.push(...bonus.rows);
        recordResultsCompletionAward(completionAwardKey, false);
        awardXP(bonus.total, bonus.reason, { quiet: true, completionAwardKey, completionRewardKeys: [...completionAnswers.keys()] });
        completionBonus = bonus.total;
        completionReason = bonus.reason;
    }

    const sourceRows = Object.entries(sourceStats).map(([src, stats]) => {
        const total = stats.total || (stats.correct + stats.wrong);
        const srcPct = total > 0 ? Math.round((stats.correct / total) * 100) : 0;
        const displayTitle = formatResultsReviewSourceTitle(src);
        const sourceQuestionIndices = filteredIndices.filter((idx) => String(questions[idx]?.sourceFile || 'Unknown').trim() === src);
        const sourceWrongIndices = wrong.filter((idx) => String(questions[idx]?.sourceFile || 'Unknown').trim() === src);
        return {
            src,
            displayTitle,
            shortName: displayTitle.length > 30 ? `${displayTitle.substring(0, 30)}...` : displayTitle,
            total,
            correct: stats.correct,
            wrong: stats.wrong,
            pct: srcPct,
            questionIndices: sourceQuestionIndices,
            wrongIndices: sourceWrongIndices
        };
    }).sort((a, b) => (b.pct - a.pct) || (b.total - a.total));

    const levelFloorXp = getXPForLevel(xpData.level);
    const levelCeilXp = getXPForLevel(xpData.level + 1);
    const levelSpan = Math.max(1, levelCeilXp - levelFloorXp);
    const levelProgressPct = Math.max(0, Math.min(100, Math.round(((xpData.totalXP - levelFloorXp) / levelSpan) * 100)));

    const completionBonusSummary = isGameMode
        ? (answered >= 5
            ? (completionAlreadyAwarded
                ? 'Completion bonus already earned for this set today'
                : `${completionReason} +${completionBonus}`)
            : 'Answer at least 5 to earn the completion bonus')
        : 'Game mode is off for this run';

    const completionBonusMarkup = completionBonusRows.length > 0 ? `
            <details class="results-game-breakdown">
                <summary>Completion bonus <span>+${completionBonus} XP</span></summary>
                <div class="results-game-breakdown-rows">
                    ${completionBonusRows.map((row) => `<span>${escapeHtml(row.label)}</span><span>+${row.amount}</span>`).join('')}
                </div>
            </details>
    ` : '';

    latestResultsReviewContext = buildResultsAiReviewContext({
        sessionQuestionIndices: filteredIndices.slice(),
        wrongIndices: wrong.slice(),
        answered,
        correct,
        incorrect,
        unanswered,
        pct,
        sourceRows,
        answers
    });
    latestResultsAiReviewOutput = null;
    const totalAchievements = Object.keys(ACHIEVEMENTS).length;
    const unlockedAchievementCount = Object.keys(ACHIEVEMENTS).filter((id) => !!achievementsData[id]).length;
    const newAchievementCount = sessionAchievements.length;
    const achievementsOpenAttr = newAchievementCount > 0 ? ' open' : '';
    const achievementsNewBadge = newAchievementCount > 0
        ? `<span class="results-achievements-new">+${newAchievementCount} new</span>`
        : '';
    const resultsExpanded = typeof isQuizCardExpanded !== 'undefined' && !!isQuizCardExpanded;
    const achievementsMarkup = isGameMode ? `
            <details class="achievements-section results-achievements-dropdown"${achievementsOpenAttr}>
                <summary class="results-achievements-summary">
                    <span class="achievements-title">Achievements</span>
                    <span class="results-achievements-meta">
                        <span class="results-achievements-count">${unlockedAchievementCount}/${totalAchievements} unlocked</span>
                        ${achievementsNewBadge}
                        <span class="results-achievements-chevron" aria-hidden="true"></span>
                    </span>
                </summary>
                <div class="results-achievements-content">
                    <div class="achievement-grid" id="results-achievement-grid"></div>
                </div>
            </details>
    ` : '';
    // One quiet strip: what this session earned and where the level bar stands. Mementos count
    // the wallet's XP share plus anything granted directly (daily bonus, chests, milestones).
    const sessionMementoTotal = Math.floor(sessionXP * 0.1) + sessionMementos;
    const sessionEarned = [`+${sessionXP} XP`, sessionMementoTotal > 0 ? `+${sessionMementoTotal} Mementos` : ''].filter(Boolean).join(' · ');
    const gameModeProgressMarkup = isGameMode ? `
            <section class="results-game-strip" aria-label="Game progress">
                <div class="results-game-head">
                    <span class="results-game-level">Level ${xpData.level}</span>
                    <span class="results-game-earned">${sessionEarned}</span>
                </div>
                <div class="results-game-bar" role="progressbar" aria-label="Progress to level ${xpData.level + 1}" aria-valuemin="0" aria-valuemax="100" aria-valuenow="${levelProgressPct}"><span style="width: ${levelProgressPct}%"></span></div>
                <div class="results-game-note">
                    <span>${Math.max(0, levelCeilXp - xpData.totalXP)} XP to level ${xpData.level + 1}</span>
                    ${completionBonusRows.length ? '' : `<span>${escapeHtml(completionBonusSummary)}</span>`}
                </div>
                ${completionBonusMarkup}
            </section>
    ` : '';
    els.resultsView.innerHTML = buildSessionSummaryMarkup({
        exam,
        pct,
        grade,
        summaryMessage,
        answered,
        correct,
        incorrect,
        unanswered,
        sourceRows,
        wrong,
        resultsExpanded,
        extraMarkup: `${gameModeProgressMarkup}${achievementsMarkup}`
    });
    appendHighYieldContinueAction();
    setResultsWrongExportMode(resultsWrongExportMode);
    setResultsAiReviewButtonsState();
    syncResultsExpandButtonState();
    renderResultsAiReviewInline();
    renderResultsSourceWorkspace(sourceRows);

    // Results show what this session unlocked plus the three closest goals, not the whole archive.
    const achGrid = $('results-achievement-grid');
    if (achGrid) {
        const toEntry = (id) => {
            const ach = ACHIEVEMENTS[id];
            const rarity = ACHIEVEMENT_RARITY_ORDER[ach.rarity] !== undefined ? ach.rarity : 'common';
            return { id, ach, rarity, isUnlocked: !!achievementsData[id], unlockTs: getAchievementUnlockTimestamp(id) };
        };
        const fresh = sessionAchievements.filter((id) => ACHIEVEMENTS[id]).map(toEntry);
        const closest = Object.keys(ACHIEVEMENTS)
            .filter((id) => !achievementsData[id] && typeof ACHIEVEMENTS[id].condition === 'function')
            .map((id) => {
                const progress = getAchievementProgress(id);
                return { id, ratio: progress ? progress.current / progress.target : 0 };
            })
            .filter((item) => item.ratio > 0)
            .sort((a, b) => b.ratio - a.ratio)
            .slice(0, 3)
            .map((item) => toEntry(item.id));
        achGrid.className = 'gm-ach-grid';
        achGrid.innerHTML = [
            ...fresh.map((entry) => renderAchievementCard(entry, { isNew: true })),
            closest.length ? `<p class="gm-ach-subhead">Closest next</p>` : '',
            ...closest.map((entry) => renderAchievementCard(entry))
        ].join('');
    }

    sessionXP = 0;
    sessionMementos = 0;
    sessionAchievements = [];
}

window.returnToQuizFromResults = function () {
    if (!Array.isArray(filteredIndices) || filteredIndices.length === 0) {
        location.reload();
        return;
    }
    if (!Number.isInteger(currentQIndex) || currentQIndex < 0 || currentQIndex >= filteredIndices.length) {
        currentQIndex = 0;
    }
    exitResultsFullscreenIfOpen();
    if (els.resultsView) {
        els.resultsView.classList.add('anim-fade-out');
        setTimeout(() => {
            els.resultsView.classList.remove('anim-fade-out');
            els.resultsView.style.display = 'none';
            els.quizContent.style.display = 'block';
            renderQuestion('anim-fade');
        }, 250);
    } else {
        els.quizContent.style.display = 'block';
        renderQuestion('anim-fade');
    }
}
window.resetAndReview = function () {
    const w = filteredIndices.filter(i => userAnswers[i] !== undefined && !questions[i].answerUnverified && userAnswers[i] !== questions[i].a);
    if (w.length === 0) {
        currentQIndex = 0;
        exitResultsFullscreenIfOpen();
        renderQuestion();
        return;
    }
    // Each missed concept comes back as a fresh question from the same slide when the
    // regenerate feature can write one (app.quiz-regenerate.js); without a key, a source
    // or a usable answer the pass opens over the original questions.
    // Rewriting takes a while and the learner can leave meanwhile, so the pass belongs to
    // this session as it is open now (a reopen is a new opening) and to these cards by
    // identity. Every way the pass opens - rewritten, cancelled, failed - checks both.
    const passSessionId = currentSessionId;
    const passOpening = sessionOpenEpoch;
    const passKeys = w.map((idx) => buildQuestionPersistenceKeyFromQuestion(questions[idx]));
    let passOpened = false;
    const isPassCurrent = () => currentSessionId === passSessionId && sessionOpenEpoch === passOpening;
    const startPass = ({ freshCount = 0, replacedKeys = null } = {}) => {
        if (passOpened || !isPassCurrent()) return false;
        const keyAt = (idx) => buildQuestionPersistenceKeyFromQuestion(questions[idx]);
        const indices = [];
        passKeys.forEach((key, position) => {
            const wanted = new Set([key, replacedKeys?.get?.(key)].filter(Boolean));
            const original = w[position];
            if (Number.isInteger(original) && wanted.has(keyAt(original))) {
                indices.push(original);
                return;
            }
            const moved = questions.findIndex((_, idx) => wanted.has(keyAt(idx)));
            if (moved >= 0) indices.push(moved);
        });
        if (!indices.length) return false;
        passOpened = true;
        isAdaptiveReviewMode = false;
        syncAdaptiveModeUI();
        // A retest needs an open card: clear this attempt's answer on every card in the pass,
        // original or rewritten. Review schedule, stats and the missed marker are history and stay.
        indices.forEach((idx) => { delete userAnswers[idx]; });
        filteredIndices = indices;
        resetQuizCompletionCohort('missed-review');
        currentQIndex = 0;
        exitResultsFullscreenIfOpen();
        els.filterBanner.style.display = 'flex';
        const bannerText = getFilterBannerTextEl();
        if (bannerText) {
            bannerText.innerHTML = freshCount > 0
                ? `<b>Missed concepts, new questions:</b> ${freshCount === indices.length ? `all ${indices.length}` : `${freshCount} of ${indices.length}`} rewritten from the same slides, so you test the idea rather than the answer you remember.`
                : `<b>Wrong Question Quiz:</b> ${indices.length} question${indices.length === 1 ? '' : 's'}.`;
        }
        setAdaptiveSnapshotButtonVisible(false);
        setAdaptiveSkipUndoneButtonVisible(false);
        renderQuestion();
        markSessionStateMutation();
        saveSessionDebounced();
        return true;
    };
    const regenerateReady = window.MemoraRuntime?.ensureFeature?.('regenerate');
    if (!regenerateReady || typeof regenerateReady.then !== 'function') {
        startPass();
        return;
    }
    regenerateReady
        .then(() => (typeof window.startFreshMissedQuestionReview === 'function'
            ? window.startFreshMissedQuestionReview(w, startPass, { isCurrent: isPassCurrent })
            : startPass()))
        .catch(() => startPass());
}
let saveOutlineFlashTimer = null;

function flashSaveOutline() {
    if (!els.quizCard) return;
    els.quizCard.classList.remove('save-outline-flash');
    void els.quizCard.offsetWidth;
    els.quizCard.classList.add('save-outline-flash');
    if (saveOutlineFlashTimer) clearTimeout(saveOutlineFlashTimer);
    saveOutlineFlashTimer = setTimeout(() => {
        if (els.quizCard) els.quizCard.classList.remove('save-outline-flash');
        saveOutlineFlashTimer = null;
    }, 1150);
}

function toggleSave() {
    const i = filteredIndices[currentQIndex];
    if (!Number.isInteger(i) || i < 0 || i >= questions.length || !questions[i]) {
        syncContentDependentControls();
        return false;
    }
    const wasSaved = !!savedQuestions[i];
    if (wasSaved) delete savedQuestions[i];
    else savedQuestions[i] = Date.now();
    updateSaveButton(i);
    if (!wasSaved) flashSaveOutline();
    markSessionStateMutation();
    saveSessionDebounced();
    syncContentDependentControls();
    return true;
}

function updateSaveButton(i) {
    if (savedQuestions[i]) {
        els.save.classList.add('saved');
        els.save.innerText = '★ Saved';
    } else {
        els.save.classList.remove('saved');
        els.save.innerText = '☆ Save';
    }
}
$('popout-pdf-btn').onclick = () => {
    setPdfMoreMenuOpen(false);
    return currentPdfBlobUrl ? window.open(currentPdfBlobUrl, '_blank') : showPopupText('No PDF is open.', null, true);
};

// Track last PDF state for restoration
let lastPdfState = { src: '', title: '', fileName: '', page: 1, searchTerm: '' };

function normalizePanelOrder(order) {
    const input = Array.isArray(order) ? order.map((id) => String(id || '').trim().toLowerCase()) : [];
    const normalized = [];
    input.forEach((id) => {
        if (PANEL_ORDER_DEFAULT.includes(id) && !normalized.includes(id)) normalized.push(id);
    });
    PANEL_ORDER_DEFAULT.forEach((id) => {
        if (!normalized.includes(id)) normalized.push(id);
    });
    return normalized;
}

function getActivePanelIdsForCurrentLayout() {
    if (!els?.workArea) return ['quiz'];
    if (els.workArea.classList.contains('pdf-tutor-quiz-trio')) return ['quiz', 'pdf', 'ai'];
    if (els.workArea.classList.contains('pdf-tutor-quiz-duo')) return ['quiz', 'pdf'];
    if (els.workArea.classList.contains('pdf-tutor-view')) return ['pdf', 'ai'];
    if (els.workArea.classList.contains('trio-view')) return ['quiz', 'pdf', 'ai'];
    if (els.workArea.classList.contains('split-view-ai')) return ['quiz', 'ai'];
    if (els.workArea.classList.contains('split-view')) return ['quiz', 'pdf'];
    return ['quiz'];
}

function getOrderedActivePanels() {
    const active = getActivePanelIdsForCurrentLayout();
    panelOrder = normalizePanelOrder(panelOrder);
    return panelOrder.filter((id) => active.includes(id));
}

function getPanelElementById(panelId) {
    if (panelId === 'quiz') return $('quiz-pane');
    if (panelId === 'pdf') return $('pdf-pane');
    if (panelId === 'ai') return $('ai-pane');
    return null;
}

function applyPanelOrderLayout({ persist = false } = {}) {
    const workArea = els?.workArea || $('work-area');
    if (!workArea) return;
    panelOrder = normalizePanelOrder(panelOrder);

    const quizPane = $('quiz-pane');
    const pdfPane = $('pdf-pane');
    const aiPane = $('ai-pane');
    const handleQuizPdf = $('resize-quiz-pdf');
    const handlePdfAi = $('resize-pdf-ai');

    [quizPane, pdfPane, aiPane, handleQuizPdf, handlePdfAi].forEach((node) => {
        if (!node) return;
        node.style.gridColumn = '';
        node.style.gridRow = '';
    });

    // Phone layouts are deliberately stacked. Leaving desktop reorder coordinates
    // on a pane creates implicit off-screen grid columns, so only preserve the
    // normalized order and shared post-layout work at this breakpoint.
    if (typeof window.matchMedia === 'function' && window.matchMedia('(max-width: 768px)').matches) {
        if (persist) saveThemeState();
        refreshPanelReorderReadyClass();
        scheduleFullscreenHeaderClearance();
        scheduleQuickActionsDockPosition();
        return;
    }

    const paneById = { quiz: quizPane, pdf: pdfPane, ai: aiPane };
    const orderedActive = getOrderedActivePanels();

    if (workArea.classList.contains('pdf-tutor-quiz-trio') && orderedActive.length === 3) {
        orderedActive.forEach((id, idx) => {
            const node = paneById[id];
            if (node) {
                node.style.gridColumn = String(1 + idx * 2);
                node.style.gridRow = '1';
            }
        });
        if (handleQuizPdf) {
            handleQuizPdf.style.gridColumn = '2';
            handleQuizPdf.style.gridRow = '1';
        }
        if (handlePdfAi) {
            handlePdfAi.style.gridColumn = '4';
            handlePdfAi.style.gridRow = '1';
        }
    } else if (workArea.classList.contains('pdf-tutor-quiz-duo') && orderedActive.length === 2) {
        const left = paneById[orderedActive[0]];
        const right = paneById[orderedActive[1]];
        if (left) {
            left.style.gridColumn = '1';
            left.style.gridRow = '1';
        }
        if (right) {
            right.style.gridColumn = '3';
            right.style.gridRow = '1';
        }
        if (handleQuizPdf) {
            handleQuizPdf.style.gridColumn = '2';
            handleQuizPdf.style.gridRow = '1';
        }
    } else if (workArea.classList.contains('pdf-tutor-view')) {
        if (pdfPane) {
            pdfPane.style.gridColumn = '1';
            pdfPane.style.gridRow = '1';
        }
        if (handlePdfAi) {
            handlePdfAi.style.gridColumn = '2';
            handlePdfAi.style.gridRow = '1';
        }
        if (aiPane) {
            aiPane.style.gridColumn = '3';
            aiPane.style.gridRow = '1';
        }
    } else if (workArea.classList.contains('trio-view') && orderedActive.length === 3) {
        orderedActive.forEach((id, idx) => {
            const node = paneById[id];
            if (node) {
                node.style.gridColumn = String(1 + idx * 2);
                node.style.gridRow = '1';
            }
        });
        if (handleQuizPdf) {
            handleQuizPdf.style.gridColumn = '2';
            handleQuizPdf.style.gridRow = '1';
        }
        if (handlePdfAi) {
            handlePdfAi.style.gridColumn = '4';
            handlePdfAi.style.gridRow = '1';
        }
    } else if (workArea.classList.contains('split-view') && orderedActive.length === 2) {
        const left = paneById[orderedActive[0]];
        const right = paneById[orderedActive[1]];
        if (left) {
            left.style.gridColumn = '1';
            left.style.gridRow = '1';
        }
        if (right) {
            right.style.gridColumn = '3';
            right.style.gridRow = '1';
        }
        if (handleQuizPdf) {
            handleQuizPdf.style.gridColumn = '2';
            handleQuizPdf.style.gridRow = '1';
        }
    } else if (workArea.classList.contains('split-view-ai') && orderedActive.length === 2) {
        const left = paneById[orderedActive[0]];
        const right = paneById[orderedActive[1]];
        if (left) {
            left.style.gridColumn = '1';
            left.style.gridRow = '1';
        }
        if (right) {
            right.style.gridColumn = '3';
            right.style.gridRow = '1';
        }
        if (handlePdfAi) {
            handlePdfAi.style.gridColumn = '2';
            handlePdfAi.style.gridRow = '1';
        }
    }

    if (persist) saveThemeState();
    refreshPanelReorderReadyClass();
    scheduleFullscreenHeaderClearance();
    scheduleQuickActionsDockPosition();
}

(() => {
    if (typeof window.matchMedia !== 'function') return;
    const phoneStackingMedia = window.matchMedia('(max-width: 768px)');
    const syncPanelOrderForPhoneBreakpoint = () => applyPanelOrderLayout();
    if (typeof phoneStackingMedia.addEventListener === 'function') {
        phoneStackingMedia.addEventListener('change', syncPanelOrderForPhoneBreakpoint);
    } else if (typeof phoneStackingMedia.addListener === 'function') {
        phoneStackingMedia.addListener(syncPanelOrderForPhoneBreakpoint);
    }
})();

function swapPanelOrderPanels(panelA, panelB) {
    panelOrder = normalizePanelOrder(panelOrder);
    const idxA = panelOrder.indexOf(panelA);
    const idxB = panelOrder.indexOf(panelB);
    if (idxA < 0 || idxB < 0 || idxA === idxB) return false;
    [panelOrder[idxA], panelOrder[idxB]] = [panelOrder[idxB], panelOrder[idxA]];
    return true;
}

let panelReorderDragState = null;
let panelReorderGhostEl = null;
let isPanelReorderModifierDown = false;

function getPanelLabel(panelId) {
    if (panelId === 'quiz') return 'Quiz';
    if (panelId === 'pdf') return 'PDF';
    if (panelId === 'ai') return 'AI';
    return 'Panel';
}

function canPanelReorderBeUsed() {
    if (!els?.workArea) return false;
    const hasReorderLayout = els.workArea.classList.contains('split-view')
        || els.workArea.classList.contains('split-view-ai')
        || els.workArea.classList.contains('trio-view');
    return hasReorderLayout && getActivePanelIdsForCurrentLayout().length >= 2;
}

function refreshPanelReorderReadyClass() {
    const activeEl = document.activeElement;
    const tag = String(activeEl?.tagName || '').toLowerCase();
    const editingFieldFocused = tag === 'input'
        || tag === 'textarea'
        || tag === 'select'
        || !!activeEl?.isContentEditable;
    const isReady = !panelReorderDragState
        && isPanelReorderModifierDown
        && !editingFieldFocused
        && canPanelReorderBeUsed();
    document.body.classList.toggle('panel-reorder-ready', isReady);
}

function sanitizeGhostNode(node) {
    if (!node) return;
    if (node.id) node.removeAttribute('id');
    node.querySelectorAll?.('[id]').forEach((el) => el.removeAttribute('id'));
    node.querySelectorAll?.('button, input, select, textarea, a, iframe').forEach((el) => el.remove());
}

function removePanelReorderGhost() {
    if (!panelReorderGhostEl) return;
    panelReorderGhostEl.remove();
    panelReorderGhostEl = null;
}

function createPanelReorderGhost(sourceId) {
    removePanelReorderGhost();

    const sourcePane = getPanelElementById(sourceId);
    if (!sourcePane) return null;
    const rect = sourcePane.getBoundingClientRect();
    const styles = getComputedStyle(sourcePane);

    const ghost = document.createElement('div');
    ghost.className = 'panel-reorder-panel-ghost';
    ghost.style.width = `${Math.round(rect.width)}px`;
    ghost.style.height = `${Math.round(rect.height)}px`;
    ghost.style.borderRadius = styles.borderRadius || '';
    ghost.style.background = styles.background || '';
    ghost.style.border = styles.border || '';
    ghost.style.boxShadow = styles.boxShadow || '';

    let headerSource = null;
    if (sourceId === 'pdf') headerSource = sourcePane.querySelector('#pdf-header');
    else if (sourceId === 'ai') headerSource = sourcePane.querySelector('#ai-header');
    else headerSource = sourcePane.querySelector('h1');

    if (headerSource) {
        const headerClone = headerSource.cloneNode(true);
        sanitizeGhostNode(headerClone);
        headerClone.classList.add('panel-reorder-ghost-header');
        ghost.appendChild(headerClone);
    } else {
        const title = document.createElement('div');
        title.className = 'panel-reorder-ghost-label';
        title.innerText = getPanelLabel(sourceId);
        ghost.appendChild(title);
    }

    const body = document.createElement('div');
    body.className = 'panel-reorder-ghost-body';
    ghost.appendChild(body);

    document.body.appendChild(ghost);
    panelReorderGhostEl = ghost;
    return rect;
}

function positionPanelReorderGhost(clientX, clientY) {
    if (!panelReorderGhostEl || !panelReorderDragState) return;
    const offsetX = Number(panelReorderDragState.dragOffsetX) || 48;
    const offsetY = Number(panelReorderDragState.dragOffsetY) || 22;
    const x = clientX - offsetX;
    const y = clientY - offsetY;
    panelReorderGhostEl.style.transform = `translate(${Math.round(x)}px, ${Math.round(y)}px)`;
}

function clearPanelReorderDragVisuals({ keepGhost = false } = {}) {
    document.body.classList.remove('panel-reorder-dragging');
    ['quiz', 'pdf', 'ai'].forEach((id) => {
        const pane = getPanelElementById(id);
        if (pane) pane.classList.remove('panel-reorder-source', 'panel-reorder-target');
    });
    if (!keepGhost) removePanelReorderGhost();
}

function syncPanelReorderDragVisuals() {
    clearPanelReorderDragVisuals({ keepGhost: true });
    if (!panelReorderDragState) return;
    document.body.classList.add('panel-reorder-dragging');
    const srcPane = getPanelElementById(panelReorderDragState.sourceId);
    const targetPane = getPanelElementById(panelReorderDragState.targetId);
    if (srcPane) srcPane.classList.add('panel-reorder-source');
    if (targetPane) targetPane.classList.add('panel-reorder-target');
}

function resolvePanelReorderSourceFromTarget(target) {
    if (!target || !target.closest) return null;
    if (target.closest('#pdf-header')) return 'pdf';
    if (target.closest('#ai-header')) return 'ai';
    if (target.closest('#controls-bar') || target.closest('#quiz-pane h1')) return 'quiz';
    return null;
}

function findPanelUnderPointer(clientX, clientY, activePanelIds = [], excludePanelId = '') {
    for (const panelId of activePanelIds) {
        if (panelId === excludePanelId) continue;
        const pane = getPanelElementById(panelId);
        if (!pane) continue;
        const rect = pane.getBoundingClientRect();
        const within = clientX >= rect.left && clientX <= rect.right && clientY >= rect.top && clientY <= rect.bottom;
        if (within) return panelId;
    }
    return null;
}

function startPanelReorderDrag(e) {
    if (e.button !== 0 || !e.shiftKey) return;
    if (!els?.workArea) return;
    if (!els.workArea.classList.contains('split-view') && !els.workArea.classList.contains('split-view-ai') && !els.workArea.classList.contains('trio-view')) return;
    if (e.target.closest('button, a, input, select, textarea, .panel-resize-handle')) return;

    const sourceId = resolvePanelReorderSourceFromTarget(e.target);
    if (!sourceId) return;

    const activePanelIds = getActivePanelIdsForCurrentLayout();
    if (activePanelIds.length < 2 || !activePanelIds.includes(sourceId)) return;

    panelReorderDragState = {
        sourceId,
        targetId: sourceId,
        activePanelIds,
        dragOffsetX: 52,
        dragOffsetY: 24
    };

    const sourceRect = createPanelReorderGhost(sourceId);
    if (sourceRect) {
        const maxOffsetX = Math.max(28, sourceRect.width - 28);
        const maxOffsetY = Math.max(20, sourceRect.height - 20);
        panelReorderDragState.dragOffsetX = Math.max(28, Math.min(maxOffsetX, e.clientX - sourceRect.left));
        panelReorderDragState.dragOffsetY = Math.max(20, Math.min(maxOffsetY, e.clientY - sourceRect.top));
    }

    syncPanelReorderDragVisuals();
    positionPanelReorderGhost(e.clientX, e.clientY);
    scheduleQuickActionsDockPosition();
    document.body.style.userSelect = 'none';
    refreshPanelReorderReadyClass();
    e.preventDefault();
}

function updatePanelReorderDrag(e) {
    if (!panelReorderDragState) return;
    positionPanelReorderGhost(e.clientX, e.clientY);
    scheduleQuickActionsDockPosition();
    const hovered = findPanelUnderPointer(
        e.clientX,
        e.clientY,
        panelReorderDragState.activePanelIds,
        panelReorderDragState.sourceId
    );
    const nextTarget = hovered || panelReorderDragState.sourceId;
    if (nextTarget !== panelReorderDragState.targetId) {
        panelReorderDragState.targetId = nextTarget;
        syncPanelReorderDragVisuals();
    }
}

function stopPanelReorderDrag({ cancel = false } = {}) {
    if (!panelReorderDragState) return;
    if (cancel) panelReorderDragMoveThrottled.cancel();
    else panelReorderDragMoveThrottled.flush();
    const { sourceId, targetId } = panelReorderDragState;
    panelReorderDragState = null;
    document.body.style.userSelect = '';
    clearPanelReorderDragVisuals();
    scheduleQuickActionsDockPosition();
    refreshPanelReorderReadyClass();

    if (cancel || !targetId || targetId === sourceId) return;
    const changed = swapPanelOrderPanels(sourceId, targetId);
    if (!changed) return;
    applyPanelOrderLayout({ persist: true });
    if (typeof saveSessionDebounced === 'function') {
        markSessionStateMutation();
        saveSessionDebounced();
    }
}

const panelReorderDragMoveThrottled = createRafDragThrottle(updatePanelReorderDrag);

function setupPanelReorderDrag() {
    document.addEventListener('mousedown', startPanelReorderDrag);
    document.addEventListener('mousemove', (e) => {
        if (!panelReorderDragState) return;
        panelReorderDragMoveThrottled(e);
    });
    document.addEventListener('mouseup', () => stopPanelReorderDrag());
    document.addEventListener('keydown', (e) => {
        if (e.key === 'Shift') {
            isPanelReorderModifierDown = true;
            refreshPanelReorderReadyClass();
        }
        if (e.key === 'Escape') stopPanelReorderDrag({ cancel: true });
    });
    document.addEventListener('keyup', (e) => {
        if (e.key === 'Shift') {
            isPanelReorderModifierDown = false;
            refreshPanelReorderReadyClass();
        }
    });
    window.addEventListener('blur', () => {
        isPanelReorderModifierDown = false;
        stopPanelReorderDrag({ cancel: true });
        refreshPanelReorderReadyClass();
    });
    refreshPanelReorderReadyClass();
}

/* moved to app.panes.js
        $('toggle-viewer-btn').querySelector('.quick-control-text').innerText = "Source";
        $('toggle-trio-btn').querySelector('.gradient-text').innerText = "⫶ Trio";
        isTrioMode = false;
        isViewerOpen = false;
        detachSmartLineListeners();
        void syncAiPaneModeView(aiPaneMode);
        applyPanelOrderLayout();
        return;
    }

    // If we're in split-view-ai (Quiz + AI) and trying to show PDF, restore to trio mode
    if (els.workArea.classList.contains('split-view-ai') && (f === true || (f === undefined && !isViewerOpen))) {
        els.workArea.classList.remove('split-view-ai');
        els.workArea.classList.add('trio-view');
        els.pdfPane.style.display = '';
        if (els.pdfTitle) els.pdfTitle.style.display = '';
        resetQuizCardPosition();

        // Update button states
        $('toggle-viewer-btn').querySelector('.quick-control-text').innerText = "Source";
        $('toggle-trio-btn').querySelector('.gradient-text').innerText = "⫶ Exit Trio";
        isTrioMode = true;
        isViewerOpen = true;
        setPdfPaneMode('pdf');
        const restorePdf = getPreferredPdfForRestore();
        if (restorePdf) {
            const restorePage = Math.max(1, Number(lastPdfState?.page) || 1);
            const restoreSearch = String(lastPdfState?.searchTerm || '').trim();
            loadPdfInPane(restorePdf, {
                page: restorePage,
                searchTerm: restoreSearch,
                smooth: false,
                ensureViewer: false,
                maintainScale: true
            });
        } else {
            showEmptyPdfPaneState('Add a file to see it here');
        }
        void syncAiPaneModeView(aiPaneMode);
        applyPanelOrderLayout();
        return;
    }

    isViewerOpen = f !== undefined ? f : !isViewerOpen;
    $('toggle-viewer-btn').querySelector('.quick-control-text').innerText = "Source";

    if (isViewerOpen) {
        // Check if any PDFs are loaded
        const restorePdf = getPreferredPdfForRestore();
        if (!restorePdf) {
            showEmptyPdfPaneState('Add a file to see it here');
        }

        els.workArea.classList.add('split-view');
        els.pdfPane.style.display = ''; // Clear any inline display:none
        setPdfPaneMode('pdf');
        $('pdf-title').style.display = ''; // Clear any inline display:none from AI mode
        if (!skipRestoreLoad) {
            const restorePage = Math.max(1, Number(lastPdfState?.page) || 1);
            const restoreSearch = String(lastPdfState?.searchTerm || '').trim();
            loadPdfInPane(restorePdf, {
                page: restorePage,
                searchTerm: restoreSearch,
                smooth: false,
                ensureViewer: false,
                maintainScale: true
            });
        }

        // Reset quiz card position
        resetQuizCardPosition();
    } else {
        if (isPdfPaneExpanded) togglePdfPaneExpanded(false);
        detachSmartLineListeners();

        // Remove split-view and split-view-ai to go back to single column
        els.workArea.classList.remove('split-view');
        els.workArea.classList.remove('split-view-ai');

        // Stop AI background content when pane is being hidden.
        if (isPdfPaneInAiMode()) {
            els.pdfFrame.src = '';
        }

        setPdfPaneMode('pdf');
        // Clear inline display style
        els.pdfPane.style.display = '';

        // Reset Quiz Card to centered state
        resetQuizCardPosition();
    }

    applyPanelOrderLayout();
}
// Helper to reset quiz card position on view change
function resetQuizCardPosition() {
    if (isQuizCardExpanded) toggleQuizCardExpanded(false);
    els.quizCard.classList.remove('is-floating');
    els.quizCard.style.position = '';
    els.quizCard.style.left = '';
    els.quizCard.style.top = '';
    els.quizCard.style.width = '';
    els.quizCard.style.height = '';
    els.quizCard.style.margin = '';
    els.quizCard.style.zIndex = '';
    els.quizCard.style.display = 'block';
    els.quizCard.style.maxWidth = '';
    els.quizCard.style.maxHeight = '';
    els.quizCard.style.transform = '';
    syncQuizCardCustomGeometryState();
    if (!restoreQuizCardGeometryForCurrentLayout()) {
        applyQuizCardContentScale({ forceReset: true });
        return;
    }
    scheduleQuizCardContentScaleSync();
}

$('toggle-viewer-btn').onclick = () => {
    toggleViewer();
};

// --- TRIO VIEW LOGIC ---
let isTrioMode = false;
setupAiPaneModeControls();
void syncAiPaneModeView(aiPaneMode);

function toggleTrio(f, { skipPdfRestore = false, preserveExpandedLock = false } = {}) {
    isTrioMode = f !== undefined ? f : !isTrioMode;
    const trioBtn = $('toggle-trio-btn');
    const aiFrame = $('ai-frame');
    const aiPane = $('ai-pane');
    const aiTitle = $('ai-title');

    if (isTrioMode) {
        // Enter Trio Mode
        // Remove ALL other view modes to prevent CSS conflicts
        els.workArea.classList.remove('split-view');
        els.workArea.classList.remove('split-view-ai');
        els.workArea.classList.add('trio-view');
        els.pdfPane.style.display = '';
        if (els.pdfTitle) els.pdfTitle.style.display = '';

        // Show PDF pane content mode and restore last opened PDF state.
        setPdfPaneMode('pdf');
        const restorePdf = getPreferredPdfForRestore();
        if (restorePdf && !skipPdfRestore) {
            loadPdfInPane(restorePdf, {
                page: Math.max(1, Number(lastPdfState?.page) || 1),
                searchTerm: String(lastPdfState?.searchTerm || '').trim(),
                smooth: false,
                ensureViewer: false,
                maintainScale: true
            });
        } else {
            showEmptyPdfPaneState('Add a file to see it here');
        }

        // Load Gemini in AI pane (default) - use Tauri native webview if available
        if (isTauriApp && window.__TAURI__.core) {
            // In Tauri, open AI as a native child webview embedded in the window
            window.__TAURI__.core.invoke('open_ai_panel', {
                url: lastOpenedAiUrl || 'https://gemini.google.com/app',
                title: lastOpenedAiTitle || 'Gemini'
            }).then(() => {
                console.log('Opened native Gemini panel');
            }).catch(err => console.error('Tauri AI panel error:', err));

            // Update the AI pane to show it's using native view
            aiFrame.src = 'about:blank';
            aiTitle.innerText = `${lastOpenedAiTitle || 'Gemini'} (Native)`;
        } else {
            aiFrame.src = lastOpenedAiUrl || 'https://gemini.google.com/app';
            aiTitle.innerText = lastOpenedAiTitle || 'Gemini';

            // Show extension link in header (theme-synced, inviting)
            const warningId = 'trio-iframe-warning';
            if (!document.getElementById(warningId)) {
                const warning = document.createElement('a');
                warning.id = warningId;
                warning.href = 'https://chromewebstore.google.com/detail/ignore-x-frame-headers/gleekbfjekiniecknbkamfmkohkpodhe';
                warning.target = '_blank';
                warning.className = 'gradient-text';
                warning.style.cssText = 'font-size:11px; margin-left:12px; text-decoration:none; opacity:0.85;';
                warning.title = 'AI sites block embedding in iframes. This extension bypasses that restriction.';
                warning.textContent = '🔗 Get extension';
                aiTitle.parentElement.insertBefore(warning, aiTitle.nextSibling);
            }
        }

        // Reset quiz card for proper layout
        resetQuizCardPosition(preserveExpandedLock ? { preserveExpandedLock: true } : {});

        isViewerOpen = true;
        $('toggle-viewer-btn').querySelector('.quick-control-text').innerText = "Source";
        trioBtn.querySelector('.gradient-text').innerText = "⫶ Exit Trio";
        void syncAiPaneModeView(aiPaneMode);
    } else {
        // Exit Trio Mode
        els.workArea.classList.remove('trio-view');

        // Clear AI iframe to stop it running in background
        aiFrame.src = '';

        // Reset to single view
        isViewerOpen = false;
        $('toggle-viewer-btn').querySelector('.quick-control-text').innerText = "Source";
        trioBtn.querySelector('.gradient-text').innerText = "⫶ Trio";

        // Reset Quiz Card to centered state
        resetQuizCardPosition();
        void syncAiPaneModeView(aiPaneMode);
    }
    applyPanelOrderLayout();
}

$('toggle-trio-btn').onclick = () => {
    toggleTrio();
};

// Close PDF in trio mode -> switch to Quiz + AI (split-view with AI)
function closePdfInTrio() {
    if (isPdfPaneExpanded) togglePdfPaneExpanded(false);
    if (!isTrioMode) {
        // Not in trio mode, just close PDF viewer
        toggleViewer(false);
        return;
    }

    // Switch from trio to split-view-ai (Quiz + AI)
    els.workArea.classList.remove('trio-view');
    els.workArea.classList.remove('split-view');
    els.workArea.classList.add('split-view-ai');

    // Reset card position for new layout
    resetQuizCardPosition();

    // Update button states
    $('toggle-viewer-btn').querySelector('.quick-control-text').innerText = "Source";
    $('toggle-trio-btn').querySelector('.gradient-text').innerText = "⫶ Trio";
    isTrioMode = false;
    isViewerOpen = false; // PDF is now hidden
    void syncAiPaneModeView(aiPaneMode);
    applyPanelOrderLayout();
}

// Close AI in trio mode -> switch to Quiz + PDF (normal split-view)
function closeAiInTrio() {
    // Works in both trio mode and split-view-ai mode
    const aiPane = $('ai-pane');
    const wasTrioMode = isTrioMode;
    const wasSplitViewAi = els.workArea.classList.contains('split-view-ai');

    // Clear AI iframe
    $('ai-frame').src = '';

    // Remove all view modes before rebuilding the target layout.
    els.workArea.classList.remove('trio-view');
    els.workArea.classList.remove('split-view-ai');
    els.workArea.classList.remove('split-view');

    if (wasTrioMode) {
        // Switch from trio to normal split-view (Quiz + PDF)
        els.workArea.classList.add('split-view');
        isTrioMode = false;
    } else if (wasSplitViewAi) {
        // Was in split-view-ai, go back to just Quiz (no panels)
        // since PDF wasn't open before
        isTrioMode = false;
    } else {
        // Was in some other mode, check if PDF is open
        if (isViewerOpen) {
            els.workArea.classList.add('split-view');
        }
    }

    // Reset card position once the destination layout classes are in place.
    resetQuizCardPosition();

    if (els.workArea.classList.contains('split-view')) {
        setPdfPaneMode('pdf');
    }

    // Update button states
    $('toggle-trio-btn').querySelector('.gradient-text').innerText = "⫶ Trio";
    void syncAiPaneModeView(aiPaneMode);
    applyPanelOrderLayout();
}

$('close-viewer-btn').onclick = () => {
    closePdfInTrio();
};
$('close-ai-btn').onclick = closeAiInTrio;
$('popout-ai-btn').onclick = () => {
    if (aiPaneMode === AI_PANE_MODE_PDF_EXPLANATION) {
        window.open(lastOpenedAiUrl || 'https://gemini.google.com/app', '_blank');
        return;
    }
    const aiFrame = $('ai-frame');
    const src = aiFrame.src;
    if (src && src !== 'about:blank') {
        window.open(src, '_blank');
    } else {
        window.open(lastOpenedAiUrl || 'https://gemini.google.com/app', '_blank');
    }
};
*/

// --- FIXED JUMP LOGIC (UNIVERSAL RESET) ---
function jumpToPdfPage(
    p,
    targetPdfName,
    searchTerm = null,
    { smooth = false, maintainScale = false, forceReload = false } = {}
) {
    if (!targetPdfName || !loadedPdfFiles[targetPdfName]) return;
    const pageNumber = Math.max(1, Number(p) || 1);
    const query = String(searchTerm || '').trim();
    setPdfEvidenceTarget(targetPdfName, pageNumber);
    loadPdfInPane(targetPdfName, {
        page: pageNumber,
        searchTerm: query,
        smooth: !!smooth,
        ensureViewer: true,
        maintainScale: !!maintainScale,
        forceReload: !!forceReload
    });
}

let saveTimeout;
function flushPendingSessionSave() {
    const hadPendingSave = !!saveTimeout;
    if (saveTimeout) {
        clearTimeout(saveTimeout);
        saveTimeout = null;
    }
    if (!hadPendingSave && !sessionDirty) return;
    writeQuickSessionSnapshot();
    if (!IS_EMBED_DEMO && hasPersistableSessionContent()) {
        void SessionManager.save(false);
    }
}
function saveSessionDebounced() {
    writeQuickSessionSnapshot();
    clearTimeout(saveTimeout);
    const scheduledSessionId = currentSessionId;
    const scheduledManifest = sessionManifest;
    saveTimeout = setTimeout(() => {
        saveTimeout = null;
        if (currentSessionId !== scheduledSessionId || sessionManifest !== scheduledManifest) return;
        void SessionManager.save(false);
    }, 2000);
}

if (!IS_EMBED_DEMO) {
    window.addEventListener('pagehide', flushPendingSessionSave);
    window.addEventListener('beforeunload', flushPendingSessionSave);
    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') {
            flushPendingSessionSave();
        }
    });
}

/* --- UPDATED THEME PERSISTENCE (HELPER) --- */
function parseThemeState(raw) {
    if (!raw) return null;
    try {
        const parsed = JSON.parse(raw);
        return (parsed && typeof parsed === 'object') ? parsed : null;
    } catch (e) {
        return null;
    }
}

function getStoredQuizTheme() {
    let scopedLocal = null;
    let scopedSession = null;
    try {
        scopedLocal = parseThemeState(localStorage.getItem(THEME_STORAGE_KEY));
    } catch (error) {
        console.warn('Theme state could not be read from local storage:', error);
    }
    try {
        scopedSession = parseThemeState(sessionStorage.getItem(THEME_STORAGE_KEY));
    } catch (error) {
        console.warn('Theme state could not be read from session storage:', error);
    }

    // Scoped keys always win once present (prevents older tabs from clobbering theme via legacy keys).
    const scopedTheme = (() => {
        if (scopedLocal && scopedSession) {
            const localTs = Number(scopedLocal.updatedAt) || 0;
            const sessionTs = Number(scopedSession.updatedAt) || 0;
            return localTs >= sessionTs ? scopedLocal : scopedSession;
        }
        return scopedLocal || scopedSession || null;
    })();
    if (scopedTheme) return scopedTheme;

    // One-time fallback/migration from legacy key.
    let legacy = null;
    try {
        legacy = parseThemeState(localStorage.getItem(LEGACY_THEME_STORAGE_KEY));
    } catch (error) {
        console.warn('Legacy theme state could not be read:', error);
    }
    if (legacy) {
        const serializedLegacy = JSON.stringify(legacy);
        try { localStorage.setItem(THEME_STORAGE_KEY, serializedLegacy); } catch (e) { /* ignore */ }
        try { sessionStorage.setItem(THEME_STORAGE_KEY, serializedLegacy); } catch (e) { /* ignore */ }
        return legacy;
    }
    return null;
}

function getThemeVar(rootStyles, prop, fallback = '') {
    return (document.documentElement.style.getPropertyValue(prop) || rootStyles.getPropertyValue(prop) || fallback || '').trim();
}

function normalizeColorForPicker(colorValue) {
    if (!colorValue || typeof colorValue !== 'string') return '';
    const value = colorValue.trim();
    if (/^#[0-9a-fA-F]{6}$/.test(value)) return value;
    if (/^#[0-9a-fA-F]{3}$/.test(value)) {
        return '#' + value.slice(1).split('').map(c => c + c).join('');
    }
    const probe = document.createElement('span');
    probe.style.color = value;
    if (!probe.style.color) return '';
    (document.body || document.documentElement).appendChild(probe);
    const computed = getComputedStyle(probe).color;
    probe.remove();
    const match = computed.match(/rgba?\(\s*(\d+),\s*(\d+),\s*(\d+)/i);
    if (!match) return '';
    return '#' + [match[1], match[2], match[3]]
        .map(n => Number(n).toString(16).padStart(2, '0'))
        .join('');
}

function syncPickerValue(inputEl, colorValue) {
    if (!inputEl) return;
    const normalized = normalizeColorForPicker(colorValue);
    if (normalized) inputEl.value = normalized;
}

function getCurrentThemeStateSnapshot() {
    const rootStyles = getComputedStyle(document.documentElement);
    const bodyStyles = getComputedStyle(document.body);
    const activeGradient = getThemeVar(rootStyles, '--g', DEFAULT_THEME_GRADIENT);
    panelOrder = normalizePanelOrder(panelOrder);
    return {
        overhaul: currentThemeOverhaul,
        gradient: activeGradient,
        gradientMode: customGradientMode,
        gradientIntensity,
        g1: (els.gradStart && els.gradStart.value) || getThemeVar(rootStyles, '--g1', DEFAULT_THEME_COLORS[0]),
        g2: (els.gradEnd && els.gradEnd.value) || getThemeVar(rootStyles, '--g2', DEFAULT_THEME_COLORS[2]),
        g3: (els.gradMid && els.gradMid.value) || getThemeVar(rootStyles, '--g3', DEFAULT_THEME_COLORS[1]),
        amb: isAmbientMode,
        ambSize: ambientSize,
        ambType: ambientType,
        borderMode: isBorderMode,
        darkMode: isDarkMode,
        cBg: getThemeVar(rootStyles, '--custom-quiz-card-bg', ''),
        cBgTransparentExplicit: document.body.classList.contains('quiz-card-bg-transparent') &&
            localStorage.getItem(QUIZ_CARD_BG_TRANSPARENT_PREF_KEY) === '1',
        tQ: getThemeVar(rootStyles, '--custom-text-q', ''),
        tA: getThemeVar(rootStyles, '--custom-text-a', ''),
        oBg: getThemeVar(rootStyles, '--custom-choice-bg', ''),
        aiBold: getThemeVar(rootStyles, '--ai-bold', AI_BOLD_DEFAULT),
        bgGamma: clampThemeSliderValue(els.bgGamma?.value, 70, 130, QUIZ_BG_GAMMA_DEFAULT),
        bgDim: clampThemeSliderValue(els.bgDim?.value, 0, 45, QUIZ_BG_DIM_DEFAULT),
        bgAccent: clampThemeSliderValue(els.bgAccent?.value, 60, 140, QUIZ_BG_ACCENT_DEFAULT),
        panelOrder: [...panelOrder],
        font: (document.body.style.fontFamily || bodyStyles.fontFamily || '').trim(),
        // Only inline overrides are user choices; computed values belong to the theme,
        // and restoring those pinned one theme's palette and font onto every other.
        gradientCustom: hasCustomThemeGradient(),
        gradientPreset: hasCustomThemeGradient() && isValidGradientPresetIndex(currentThemeIndex) ? currentThemeIndex : null,
        fontCustom: !!document.body.style.fontFamily,
        cBgSolid: normalizeColorForPicker(lastQuizCardBgColor) || '',
        updatedAt: Date.now()
    };
}

function isSameThemeState(a, b) {
    if (!a || !b) return false;
    const aTools = a.tools || {};
    const bTools = b.tools || {};
    const aOrder = normalizePanelOrder(a.panelOrder);
    const bOrder = normalizePanelOrder(b.panelOrder);
    return a.gradient === b.gradient &&
        (a.overhaul || 'default') === (b.overhaul || 'default') &&
        a.g1 === b.g1 &&
        a.g2 === b.g2 &&
        (a.g3 || '') === (b.g3 || '') &&
        normalizeGradientMode(a.gradientMode) === normalizeGradientMode(b.gradientMode) &&
        Number(a.gradientIntensity || GRADIENT_INTENSITY_DEFAULT) === Number(b.gradientIntensity || GRADIENT_INTENSITY_DEFAULT) &&
        !!a.amb === !!b.amb &&
        (a.ambSize || '') === (b.ambSize || '') &&
        (a.ambType || '') === (b.ambType || '') &&
        !!a.borderMode === !!b.borderMode &&
        !!a.darkMode === !!b.darkMode &&
        (a.cBg || '') === (b.cBg || '') &&
        (a.tQ || '') === (b.tQ || '') &&
        (a.tA || '') === (b.tA || '') &&
        (a.oBg || '') === (b.oBg || '') &&
        (a.aiBold || '') === (b.aiBold || '') &&
        Number(a.bgGamma || QUIZ_BG_GAMMA_DEFAULT) === Number(b.bgGamma || QUIZ_BG_GAMMA_DEFAULT) &&
        Number(a.bgDim || QUIZ_BG_DIM_DEFAULT) === Number(b.bgDim || QUIZ_BG_DIM_DEFAULT) &&
        Number(a.bgAccent || QUIZ_BG_ACCENT_DEFAULT) === Number(b.bgAccent || QUIZ_BG_ACCENT_DEFAULT) &&
        aOrder.join('|') === bOrder.join('|') &&
        (a.font || '') === (b.font || '') &&
        !!a.gradientCustom === !!b.gradientCustom &&
        (isValidGradientPresetIndex(a.gradientPreset) ? a.gradientPreset : -1) === (isValidGradientPresetIndex(b.gradientPreset) ? b.gradientPreset : -1) &&
        !!a.fontCustom === !!b.fontCustom &&
        (a.cBgSolid || '') === (b.cBgSolid || '') &&
        !!aTools.mix === !!bTools.mix &&
        !!aTools.hard === !!bTools.hard &&
        !!aTools.shuffle === !!bTools.shuffle &&
        !!aTools.as === !!bTools.as &&
        !!aTools.aa === !!bTools.aa &&
        !!aTools.ae === !!bTools.ae;
}

// States saved before gradientCustom/fontCustom existed recorded the computed palette and
// font even when the theme supplied them. Restore only what reads as a deliberate choice:
// untouched builder defaults and a captured theme palette (whose first stop is not the
// picker color) are not, and a font counts only when it is one of the offered fonts.
function isStoredThemeGradientCustom(t) {
    if (typeof t?.gradientCustom === 'boolean') return t.gradientCustom;
    const gradient = String(t?.gradient || '').trim().toLowerCase();
    if (!gradient) return false;
    const [start, middle, end] = DEFAULT_THEME_COLORS;
    const builderDefaults = [
        `linear-gradient(135deg, ${start}, ${end})`,
        `linear-gradient(135deg, ${start}, ${middle} 45%, ${end})`
    ];
    if (builderDefaults.includes(gradient)) return false;
    if (Number(t.gradientIntensity ?? GRADIENT_INTENSITY_DEFAULT) !== GRADIENT_INTENSITY_DEFAULT) return true;
    const firstStop = (gradient.match(/#[0-9a-f]{3,8}/) || [''])[0];
    return !!firstStop && firstStop === String(t.g1 || '').trim().toLowerCase();
}

function isStoredThemeFontCustom(t) {
    if (typeof t?.fontCustom === 'boolean') return t.fontCustom;
    return !!findThemeFontOption(t?.font);
}

function applyLoadedTheme(t) {
    if (!t) return;
    panelOrder = normalizePanelOrder(t.panelOrder);
    gradientIntensity = clampThemeSliderValue(t.gradientIntensity, GRADIENT_INTENSITY_MIN, GRADIENT_INTENSITY_MAX, GRADIENT_INTENSITY_DEFAULT);
    syncGradientIntensityControls();

    const loadedQuizCardBg = t.cBg ? normalizeLoadedQuizCardBg(t.cBg, t) : '';
    if (loadedQuizCardBg && !isDefaultThemeColor('--quiz-card-bg', loadedQuizCardBg, t)) {
        if (loadedQuizCardBg === QUIZ_CARD_BG_TRANSPARENT) {
            localStorage.setItem(QUIZ_CARD_BG_TRANSPARENT_PREF_KEY, '1');
        } else {
            localStorage.removeItem(QUIZ_CARD_BG_TRANSPARENT_PREF_KEY);
        }
        document.documentElement.style.setProperty('--quiz-card-bg', loadedQuizCardBg);
        document.documentElement.style.setProperty('--custom-quiz-card-bg', loadedQuizCardBg);
        syncQuizCardBgState(loadedQuizCardBg);
        if (loadedQuizCardBg !== QUIZ_CARD_BG_TRANSPARENT) {
            syncPickerValue(els.colCardBg, loadedQuizCardBg);
        }
    } else {
        document.documentElement.style.removeProperty('--quiz-card-bg');
        document.documentElement.style.removeProperty('--custom-quiz-card-bg');
        const isTransparentExplicit = loadedQuizCardBg === QUIZ_CARD_BG_TRANSPARENT && shouldPreserveTransparentQuizCardBg(t);
        syncQuizCardBgState(isTransparentExplicit ? QUIZ_CARD_BG_TRANSPARENT : QUIZ_CARD_BG_DEFAULT);
        syncPickerValue(els.colCardBg, QUIZ_CARD_BG_DEFAULT_COLOR);
    }
    // A transparent card turns back into the solid color chosen before it, even after a reload.
    const solidCardBg = normalizeColorForPicker(t.cBgSolid);
    if (solidCardBg && document.body.classList.contains('quiz-card-bg-transparent')) {
        lastQuizCardBgColor = solidCardBg;
        if (els.colCardBg) {
            els.colCardBg.dataset.lastColor = solidCardBg;
            els.colCardBg.value = solidCardBg;
        }
    }
    if (t.tQ && !isDefaultThemeColor('--text-q', t.tQ, t)) {
        document.documentElement.style.setProperty('--text-q', t.tQ);
        document.documentElement.style.setProperty('--custom-text-q', t.tQ);
        syncPickerValue(els.colTextQ, t.tQ);
    } else {
        document.documentElement.style.removeProperty('--text-q');
        document.documentElement.style.removeProperty('--custom-text-q');
        syncPickerValue(els.colTextQ, '#f8fafc');
    }
    if (t.tA && !isDefaultThemeColor('--text-a', t.tA, t)) {
        document.documentElement.style.setProperty('--text-a', t.tA);
        document.documentElement.style.setProperty('--custom-text-a', t.tA);
        syncPickerValue(els.colTextA, t.tA);
    } else {
        document.documentElement.style.removeProperty('--text-a');
        document.documentElement.style.removeProperty('--custom-text-a');
        syncPickerValue(els.colTextA, '#e2e8f0');
    }
    if (t.oBg && !isDefaultThemeColor('--choice-bg', t.oBg, t)) {
        document.documentElement.style.setProperty('--choice-bg', t.oBg);
        document.documentElement.style.setProperty('--custom-choice-bg', t.oBg);
        syncPickerValue(els.colOptBg, t.oBg);
    } else {
        document.documentElement.style.removeProperty('--choice-bg');
        document.documentElement.style.removeProperty('--custom-choice-bg');
        // The translucent CSS default reads as white in a picker; show the markup default.
        syncPickerValue(els.colOptBg, '#1e293b');
    }
    const aiBold = t.aiBold || t.aiB1 || t.aiB2 || t.aiB3 || AI_BOLD_DEFAULT;
    document.documentElement.style.setProperty('--ai-bold', aiBold);
    syncPickerValue(els.colAIBold, aiBold);
    const loadedBgDim = Number(t.bgDim);
    applyQuizBackgroundControls({
        gamma: t.bgGamma ?? QUIZ_BG_GAMMA_DEFAULT,
        dim: Number.isFinite(loadedBgDim) && loadedBgDim !== 21 ? loadedBgDim : QUIZ_BG_DIM_DEFAULT,
        accent: t.bgAccent ?? QUIZ_BG_ACCENT_DEFAULT
    }, { persist: false });
    // Only a chosen font is an override; otherwise the theme's own font shows.
    applyThemeFontChoice(isStoredThemeFontCustom(t) ? t.font : '');

    const savedGradient = t.gradient;
    const gradientCustom = isStoredThemeGradientCustom(t);
    // Presets re-apply from the preset table. Rebuilding one through the custom builder
    // moved its middle stop to 45% and cleared the selected swatch.
    const savedPreset = gradientCustom ? resolveStoredGradientPreset(t) : -1;
    if (!gradientCustom) {
        // No custom gradient: drop stale overrides so the theme's own palette shows, and
        // mirror that palette in the builder.
        clearThemeGradientOverrides();
        syncGradientControlsToActiveTheme();
    } else if (savedPreset >= 0) {
        applyGradientPreset(savedPreset, null, { persist: false });
    } else if (savedGradient) {
        const normalizedGradient = normalizeLegacyDefaultGradientString(savedGradient);
        const colorStops = normalizedGradient.match(/#[0-9a-fA-F]{3,8}|rgba?\([^)]+\)|hsla?\([^)]+\)/g) || [];
        let gradientStart = t.g1 || colorStops[0] || '';
        let gradientMid = t.g3 || colorStops[1] || '';
        let gradientEnd = t.g2 || colorStops[colorStops.length - 1] || gradientStart;
        if (t.gradientIntensity === undefined && isLegacySaturatedDefaultColors([gradientStart, gradientMid, gradientEnd])) {
            [gradientStart, gradientMid, gradientEnd] = DEFAULT_THEME_COLORS;
        }
        setCustomGradientMode(colorStops.length >= 3 ? '3' : (t.gradientMode || '2'), { persist: false, updateGradient: false });
        if (gradientStart) {
            document.documentElement.style.setProperty('--g1', gradientStart);
            document.documentElement.style.setProperty('--amb-c1', gradientStart + '66');
            if (els.gradStart) syncPickerValue(els.gradStart, gradientStart);
        }
        if (gradientMid) {
            document.documentElement.style.setProperty('--g3', gradientMid);
            if (els.gradMid) syncPickerValue(els.gradMid, gradientMid);
        }
        if (gradientEnd) {
            if (els.gradEnd) syncPickerValue(els.gradEnd, gradientEnd);
        }
        updateCustomGradient({ persist: false });
    } else if (t.g1 && t.g2) {
        if (els.gradStart) syncPickerValue(els.gradStart, t.g1);
        if (els.gradMid && t.g3) syncPickerValue(els.gradMid, t.g3);
        if (els.gradEnd) syncPickerValue(els.gradEnd, t.g2);
        setCustomGradientMode(t.gradientMode || (t.g3 ? '3' : '2'), { persist: false, updateGradient: false });
        updateCustomGradient({ persist: false });
    }

    isAmbientMode = !!t.amb;
    ambientSize = t.ambSize || 'medium';
    ambientType = t.ambType || 'dynamic';
    if (els.ambSize) els.ambSize.value = ambientSize;
    if (els.ambStyle) els.ambStyle.value = ambientType;

    isBorderMode = !!t.borderMode;
    applyBorderMode();
    isDarkMode = !!t.darkMode;
    applyDarkMode({ persist: false });
    applyAmbientMode();
    applyPanelOrderLayout();
}

function saveThemeState() {
    const t = {
        ...getCurrentThemeStateSnapshot(),
        // Save Tools to LocalStorage as well for default behavior
        tools: {
            mix: isMixAnswers,
            hard: isHardMode,
            shuffle: isShuffled,
            as: autoSaveWrong,
            aa: autoAdvance,
            ae: autoEvidence,
            ax: false
        }
    };
    const prev = getStoredQuizTheme();
    if (isSameThemeState(prev, t)) {
        const prevUpdatedAt = Number(prev.updatedAt) || 0;
        if (prevUpdatedAt > 0) t.updatedAt = prevUpdatedAt;
    }
    const serialized = JSON.stringify(t);
    try {
        localStorage.setItem(THEME_STORAGE_KEY, serialized);
    } catch (error) {
        console.warn('Theme state could not be saved to local storage:', error);
    }
    try {
        sessionStorage.setItem(THEME_STORAGE_KEY, serialized);
    } catch (error) {
        console.warn('Theme state could not be saved to session storage:', error);
    }

    // Keep legacy color keys in sync only as cold-start fallback for older states.
    try {
        localStorage.setItem('lecom-theme-gradient', t.gradient);
        localStorage.setItem('lecom-theme-col-card-bg', t.cBg);
        localStorage.setItem('lecom-theme-col-text-q', t.tQ);
        localStorage.setItem('lecom-theme-col-text-a', t.tA);
        localStorage.setItem('lecom-theme-col-opt-bg', t.oBg);
        localStorage.setItem('lecom-theme-col-ai-bold', t.aiBold || AI_BOLD_DEFAULT);
    } catch (error) {
        console.warn('Legacy theme fallbacks could not be updated:', error);
    }
}

function loadThemeState() {
    try {
        const t = getStoredQuizTheme();
        if (t) {
            let migrated = false;
            if (!t.font || 
                t.font === 'Arial, Helvetica, sans-serif' || 
                t.font === "'Arial, Helvetica, sans-serif'" || 
                t.font === 'Arial' || 
                t.font === 'Inter, Arial, sans-serif' || 
                t.font === "'Inter', sans-serif" || 
                t.font === "'Inter', Arial, sans-serif") {
                // Only states saved before fontCustom existed carry these old default fonts;
                // in a newer state Arial or Inter is the user's own choice.
                if (typeof t.fontCustom !== 'boolean') {
                    t.font = "'Times New Roman', serif";
                    migrated = true;
                }
            }

            applyLoadedTheme(t);

            if (migrated) {
                saveThemeState();
            }

            // Load Tools from LocalStorage if available
            if (t.tools) {
                isMixAnswers = t.tools.mix;
                isHardMode = t.tools.hard;
                isShuffled = t.tools.shuffle;
                autoSaveWrong = t.tools.as;
                autoAdvance = !!t.tools.aa;
                autoEvidence = !!t.tools.ae;
                autoExplanation = false;

                // Update UI
                if (isMixAnswers) els.mixAnswersBtn.classList.add('active');
                if (isHardMode) els.hardModeBtn.classList.add('active');
                if (isShuffled) els.shuffleBtn.classList.add('active');

                els.autoSaveBtn.classList.toggle('active', autoSaveWrong);
                syncAutoStudyToolButtons();
            }
        } else {
            // Default to light mode for new users/fresh sessions.
            isDarkMode = false;
            applyDarkMode({ persist: false });
        }
    } catch (e) { console.error("Theme Load Error", e); }
    loadThemeOverhaulState();
}

function snapBackToMainScreen() {
    let didChange = false;

    const resetPdfZoomIfPossible = () => {
        if (pdfViewerInstance && pdfViewerFileName) {
            return resetAdvancedPdfZoomToPageWidth();
        }
        const iframe = els?.pdfFrame;
        if (!iframe || !iframe.contentWindow) return false;
        try {
            const app = iframe.contentWindow.PDFViewerApplication;
            if (!app) return false;

            const currentPage = app.pdfViewer?.currentPageNumber || app.page || null;

            if (app.pdfViewer && 'currentScaleValue' in app.pdfViewer) {
                app.pdfViewer.currentScaleValue = 'page-fit';
            } else if ('currentScaleValue' in app) {
                app.currentScaleValue = 'page-fit';
            } else {
                return false;
            }

            if (currentPage && app.pdfViewer?.scrollPageIntoView) {
                app.pdfViewer.scrollPageIntoView({ pageNumber: currentPage });
            }
            return true;
        } catch (err) {
            return false;
        }
    };

    const lightbox = $('image-lightbox');
    if (lightbox && lightbox.classList.contains('active')) {
        closeImageLightbox();
        didChange = true;
    }

    const sectionEditor = $('section-editor-modal');
    if (sectionEditor && sectionEditor.classList.contains('active') && typeof closeSectionEditor === 'function') {
        closeSectionEditor();
        didChange = true;
    }

    if (document.fullscreenElement && typeof toggleGlobalFullscreen === 'function') {
        toggleGlobalFullscreen();
        didChange = true;
    }

    // Intentionally keep current layout mode (single/duo/trio/zen) unchanged.
    // This shortcut is for "zoom out / close temporary overlays" only.
    if (resetPdfZoomIfPossible()) {
        didChange = true;
    }

    // Always restore quiz card geometry (size/position) as part of "zoom out".
    if (typeof resetQuizCardPosition === 'function') {
        resetQuizCardPosition();
        didChange = true;
    }

    // If some browser zoom handlers use CSS zoom, normalize it.
    if (document.documentElement.style.zoom) {
        document.documentElement.style.zoom = '';
        didChange = true;
    }
    if (document.body.style.zoom) {
        document.body.style.zoom = '';
        didChange = true;
    }
}

document.addEventListener('keydown', (e) => {
    if (typeof window.shouldSuppressMemoraGlobalShortcuts === 'function' && window.shouldSuppressMemoraGlobalShortcuts(e)) {
        return;
    }
    const k = e.key.toLowerCase();
    const isModKey = e.ctrlKey || e.metaKey;
    const selectedText = window.getSelection ? window.getSelection().toString() : '';
    const targetTag = String(e.target?.tagName || '').toUpperCase();
    const isEditableTarget = ['INPUT', 'TEXTAREA', 'SELECT'].includes(targetTag) || !!e.target?.isContentEditable;
    const hasVisibleBackdropSurface = Array.from(document.querySelectorAll(GLOBAL_DIALOG_BACKDROP_SELECTORS))
        .some(isBackdropCandidateVisible);
    const floatingModalSurfaceSelectors = [
        '#pdf-tutor-modal.active',
        '#voice-agent-modal.active',
        '.ai-popup-modal.active',
        '.ai-expl-slide-modal.active'
    ].join(',');
    // An enlarged study visual sits above Voice Chat and handles its own keys, Tab included.
    const floatingModalHostSelectors = [
        '#pdf-tutor-modal',
        '#voice-agent-modal',
        '.ai-popup-modal',
        '.ai-expl-slide-modal',
        '.memora-artifact-preview'
    ].join(',');
    const hasVisibleFloatingModalSurface = Array.from(document.querySelectorAll(floatingModalSurfaceSelectors))
        .some(isBackdropCandidateVisible);
    const insideStudyDashboard = e.target instanceof Element && !!e.target.closest('#study-dashboard-surface');

    if (isStudyDashboardVisible() && !(e.target instanceof Element && e.target.closest('#api-key-modal.active'))
        && !matchesShortcutActionEvent(e, 'toggleAgent')) {
        if (k === 'escape') {
            e.preventDefault();
            e.stopImmediatePropagation();
            hideStudyPlannerSurfaces();
            return;
        }
        if (!insideStudyDashboard) {
            e.preventDefault();
            e.stopImmediatePropagation();
        }
        return;
    }

    if (isEditableTarget && k !== 'escape') return;

    if (els.themeConfirmModal && els.themeConfirmModal.classList.contains('active')) {
        if (k === 'escape') {
            e.preventDefault();
            e.stopImmediatePropagation();
            closeThemeConfirmModal({ target: els.themeConfirmModal });
        }
        return;
    }

    if (els.srsRatingModal && els.srsRatingModal.classList.contains('active')) {
        if (k === '1') { e.preventDefault(); window.submitSRSConfidence('again'); return; }
        if (k === '2') { e.preventDefault(); window.submitSRSConfidence('shaky'); return; }
        if (k === '3') { e.preventDefault(); window.submitSRSConfidence('good'); return; }
        if (k === '4') { e.preventDefault(); window.submitSRSConfidence('easy'); return; }
        if (k === 'escape') { e.preventDefault(); return; }
    }

    if (shortcutEditMode) return;

    const notesModal = $('notes-modal');
    if (notesModal && notesModal.classList.contains('active')) {
        if (k === 'escape') {
            e.preventDefault();
            // Repeated Escape presses collapse onto the one pending transition
            // rather than racing each other through teardown.
            void requestNotesEditorTransition(teardownNotesEditorSurface);
        }
        return;
    }

    if (isModKey && !e.altKey && !e.shiftKey && !selectedText && !hasVisibleBackdropSurface && (k === 'arrowup' || k === 'arrowdown')) {
        if (nudgeQuizCardContentScale(k === 'arrowup' ? 1 : -1)) {
            e.preventDefault();
            return;
        }
    }

    // --- Copy Question + Choices to Clipboard (Ctrl/Cmd + mapped key) ---
    if (isModKey && matchesShortcutKeyEvent(e, getShortcut('copy')) && !isFlashcardMode && !selectedText) {
        e.preventDefault();
        const realIdx = filteredIndices[currentQIndex];
        const q = questions[realIdx];
        if (q) {
            let text = "Please explain this question succinctly. Tell me why the correct answer is correct, and briefly explain why each wrong answer is incorrect.\n\n";
            text += `Question ${realIdx + 1}:\n${q.q}\n\n`;
            text += "Answer Choices:\n";
            q.c.forEach((choice, i) => {
                text += `${String.fromCharCode(65 + i)}. ${choice}\n`;
            });
            navigator.clipboard.writeText(text).then(() => {
                showPopup("Question copied to clipboard!", null, false);
            }).catch(err => {
                console.error("Copy failed:", err);
                showPopup("Copy failed - check permissions", null, true);
            });
        }
        return;
    }

    if (matchesShortcutActionEvent(e, 'openVoiceReader')) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        openVoiceReaderCardShortcutAction();
        return;
    }

    if (matchesShortcutActionEvent(e, 'toggleAgent')) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        void window.MemoraRuntime.ensureFeature('agent').then(() => window.toggleVoiceAgentShortcutAction()).catch((error) => {
            console.error('Toggle agent shortcut failed:', error);
        });
        return;
    }

    if (isMemoraPdfExplanationFeatureEnabledForCore() && matchesShortcutActionEvent(e, 'toggleAutoExplanation')) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        toggleAutoExplanationMode({ openCurrent: true, notify: true, silentOpen: false });
        return;
    }

    if (matchesShortcutActionEvent(e, 'toggleAutoEvidence')) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        toggleAutoEvidenceMode({ openCurrent: true, notify: true });
        return;
    }

    // Never treat Ctrl/Cmd combinations as quiz answer shortcuts.
    if (isModKey) return;

    if (k === '`' || k === '~') {
        e.preventDefault();
        snapBackToMainScreen();
        return;
    }

    if (k === '?') toggleShortcuts();

    if (k === 'escape') {
        // API Key, Usage, Export and Database can open above Settings; Escape closes the top
        // dialog first and hands keyboard focus back to the Settings control that opened it.
        const settingsChildDialog = $('api-key-modal')?.classList.contains('active')
            ? { close: () => window.closeAPIKeyModal?.({ target: $('api-key-modal') }), openerId: 'ai-config-btn' }
            : $('memora-api-usage-panel')
                ? { close: () => window.closeMemoraApiUsagePanel?.(), openerId: 'memora-api-usage-btn' }
                : $('export-modal')?.classList.contains('active')
                    ? { close: () => $('export-modal').classList.remove('active'), openerId: 'export-quiz-btn' }
                    : $('database-modal')?.classList.contains('active')
                        ? { close: () => $('database-modal').classList.remove('active'), openerId: 'database-btn' }
                        : null;
        if (settingsChildDialog) {
            e.preventDefault();
            e.stopImmediatePropagation();
            settingsChildDialog.close();
            if (els.themeModal?.classList.contains('active') && !els.themeModal.contains(document.activeElement)) {
                const opener = $(settingsChildDialog.openerId);
                if (opener && els.themeModal.contains(opener) && opener.getClientRects().length) opener.focus({ preventScroll: true });
            }
            return;
        }
        if ($('calendar-import-review-modal')?.classList.contains('active')) {
            e.preventDefault();
            e.stopImmediatePropagation();
            window.discardCalendarImportStaging?.();
            return;
        }
        if ($('clinic-modal')?.classList.contains('active')) {
            e.preventDefault();
            e.stopImmediatePropagation();
            window.closeClinicModal?.('button');
            return;
        }
        if (els.pdfMoreMenu && els.pdfMoreMenu.classList.contains('active')) {
            e.preventDefault();
            setPdfMoreMenuOpen(false);
            return;
        }
        if (isQuizCardExpanded) {
            e.preventDefault();
            toggleQuizCardExpanded(false);
            return;
        }
        if (isPdfPaneExpanded) {
            e.preventDefault();
            togglePdfPaneExpanded(false);
            return;
        }
        const savedExportMenu = $('saved-export-menu');
        if (savedExportMenu?.open) {
            e.preventDefault();
            closeSavedExportMenu();
            return;
        }
        if ($('history-modal').classList.contains('active')) els.historyModal.classList.remove('active');
        else if ($('saved-modal').classList.contains('active')) {
            e.preventDefault();
            window.MemoraModalCoordinator?.close('saved-modal', 'escape') || closeSavedModal();
            return;
        }
        else if ($('adaptive-snapshot-modal').classList.contains('active')) closeAdaptiveSnapshotModal();
        else if ($('stats-modal').classList.contains('active')) els.statsModal.classList.remove('active');
        else if ($('settings-modal')?.classList.contains('active')) window.MemoraSettings?.close('escape');
        else if ($('theme-modal').classList.contains('active')) closeThemeModal();
        else if ($('voice-agent-modal')?.classList.contains('active')) window.closeVoiceAgentModal?.('button');
        else if ($('ask-ai-modal')?.classList.contains('active')) {
            e.preventDefault();
            window.closeAskAIModal?.('button');
        }
        else if (hasVisibleBackdropSurface) {
            e.preventDefault();
            return;
        }
        else if ($('work-area')?.classList.contains('calendar-surface-open')) {
            e.preventDefault();
            window.closeCalendarSurface?.();
            return;
        }
        else if ((els.workArea?.classList.contains('split-view-ai')) || (els.workArea?.classList.contains('trio-view') && !isTrioMode)) {
            e.preventDefault();
            closeAiInTrio();
            return;
        }
        else if (isTrioMode || els.workArea?.classList.contains('trio-view')) {
            e.preventDefault();
            toggleTrio(false);
            return;
        }
        else if (isViewerOpen || els.workArea?.classList.contains('split-view')) {
            e.preventDefault();
            toggleViewer(false);
            return;
        }
        else if (isZenMode) toggleZenMode();
        return;
    }

    if (handleFlashcardReviewKeydown(e)) return;

    if (hasVisibleBackdropSurface || hasVisibleFloatingModalSurface) {
        const activeSurfaceSelectors = [
            GLOBAL_DIALOG_BACKDROP_SELECTORS,
            floatingModalHostSelectors
        ].join(',');
        if (e.target instanceof Element && e.target.closest(activeSurfaceSelectors)) {
            return;
        }
        e.preventDefault();
        e.stopImmediatePropagation();
        return;
    }

    if (!isFlashcardMode && isMemoraPdfExplanationFeatureEnabledForCore() && matchesShortcutActionEvent(e, 'openPdfExplanation')) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        void openPdfExplanationForCurrentQuestion({ silent: false, smooth: true, preferDuoAiPane: true });
        return;
    }

    if (matchesShortcutKeyEvent(e, getShortcut('next'))) {
        e.preventDefault();
        if (!isFlashcardMode) nextQuestion();
        return;
    } else if (matchesShortcutKeyEvent(e, getShortcut('prev'))) {
        e.preventDefault();
        if (!isFlashcardMode) prevQuestion();
        return;
    }

    // --- UPDATED E SHORTCUT ---
    if (matchesShortcutKeyEvent(e, getShortcut('evidence'))) {
        e.preventDefault();
        // Visual feedback that key was pressed
        const btn = $('toggle-viewer-btn');
        if (btn) {
            btn.classList.add('active');
            setTimeout(() => btn.classList.remove('active'), 200);
        }
        // Ask or Notes beside the card: E swaps the slide in, exactly as the Slide button does.
        if (typeof isQuizSideSurfaceInPane === 'function' && isQuizSideSurfaceInPane()
            && typeof switchQuizSideSurface === 'function') {
            void switchQuizSideSurface('slide');
            return;
        }
        triggerEvidence();
        return;
    }
    // --------------------------

    // --- REFRESH SHORTCUT (CHANGED TO U) ---
    if (matchesShortcutKeyEvent(e, getShortcut('reset'))) {
        e.preventDefault();
        refreshCurrentQuestionAndShuffle(false);
        return;
    }

    if (!isFlashcardMode) {
        if (matchesShortcutKeyEvent(e, getShortcut('save'))) {
            e.preventDefault();
            toggleSave();
            return;
        }
        const answerShortcutMap = {
            0: getShortcut('selectA'),
            1: getShortcut('selectB'),
            2: getShortcut('selectC'),
            3: getShortcut('selectD')
        };
        let choiceIdx = -1;
        Object.entries(answerShortcutMap).some(([idx, key]) => {
            if (!matchesShortcutKeyEvent(e, key)) return false;
            choiceIdx = Number(idx);
            return true;
        });
        if (choiceIdx >= 0) {
            const b = els.choices.querySelectorAll('button.choice')[choiceIdx];
            if (b) {
                e.preventDefault();
                b.click();
            }
            return;
        }
    }
});

// Double-click navigation removed per user request
function setupGestures() { /* Disabled */ }

// --- PANEL RESIZE SYSTEM ---
(function () {
    let isResizing = false;
    let currentHandle = null;
    let startX = 0;
    let startWidthsPx = [];
    let activePanelsAtResize = [];
    let activePointerId = null;
    let lastResizeOrientation = '';

    const workArea = $('work-area');
    const quizPane = $('quiz-pane');
    const pdfPane = $('pdf-pane');
    const aiPane = $('ai-pane');
    const resizeQuizPdf = $('resize-quiz-pdf');
    const resizePdfAi = $('resize-pdf-ai');
    const paneById = { quiz: quizPane, pdf: pdfPane, ai: aiPane };
    const resizeStorageKey = 'memora.panel_resize_ratios.v1';
    const clamp = (value, min, max) => Math.max(min, Math.min(max, value));

    function isPhoneResizeLocked() {
        return document.documentElement.classList.contains('phone-device');
    }

    function getResizeOrientation() {
        return window.innerWidth >= window.innerHeight ? 'landscape' : 'portrait';
    }

    function getResizeDeviceClass() {
        if (document.documentElement.classList.contains('ipad-like-device')) return 'ipad';
        return 'desktop';
    }

    function getResizeMode() {
        if (workArea.classList.contains('trio-view')) return 'trio';
        if (workArea.classList.contains('split-view-ai')) return 'duo-ai';
        if (workArea.classList.contains('split-view')) return 'duo-pdf';
        if (workArea.classList.contains('pdf-tutor-view')) return 'pdf-tutor';
        return '';
    }

    function getResizePersistenceKey() {
        const mode = getResizeMode();
        return mode ? `${mode}:${getResizeDeviceClass()}:${getResizeOrientation()}` : '';
    }

    function readResizeStore() {
        try {
            const parsed = JSON.parse(localStorage.getItem(resizeStorageKey) || '{}');
            return parsed && typeof parsed === 'object' ? parsed : {};
        } catch (_) {
            return {};
        }
    }

    function writeResizeStore(store) {
        try {
            localStorage.setItem(resizeStorageKey, JSON.stringify(store || {}));
        } catch (_) { }
    }

    function getActiveResizePanels() {
        return getOrderedActivePanels().filter((id) => paneById[id]);
    }

    function getMeasuredMinimumPanelWidth(id, pane) {
        if (!pane) return 220;
        const cssMinWidth = Number.parseFloat(getComputedStyle(pane).minWidth) || 0;
        const overflowingContentWidth = pane.scrollWidth > pane.clientWidth + 4
            ? Math.min(360, pane.scrollWidth)
            : 0;
        const usableFallback = id === 'ai' ? 300 : 240;
        return Math.max(cssMinWidth, overflowingContentWidth, usableFallback);
    }

    function getPanelWidthSnapshot(panelIds = activePanelsAtResize) {
        const widths = panelIds.map((id) => paneById[id]?.getBoundingClientRect?.().width || 0);
        const total = widths.reduce((sum, width) => sum + width, 0);
        return { widths, total };
    }

    function getMinimumWidthPercentages(panelIds, totalWidth) {
        const safeTotal = Math.max(1, totalWidth);
        return panelIds.map((id) => (
            (getMeasuredMinimumPanelWidth(id, paneById[id]) / safeTotal) * 100
        ));
    }

    function setGridWidths(widths) {
        if (!Array.isArray(widths) || widths.length < 2) return;
        const columns = [];
        widths.forEach((width, index) => {
            if (index > 0) columns.push('auto');
            columns.push(`${Number(width).toFixed(4)}%`);
        });
        workArea.style.gridTemplateColumns = columns.join(' ');
    }

    function updateResizeHandleAria(handle, widthsPx = null, panelIds = activePanelsAtResize) {
        if (!handle || panelIds.length < 2) return;
        const snapshot = widthsPx
            ? { widths: widthsPx, total: widthsPx.reduce((sum, width) => sum + width, 0) }
            : getPanelWidthSnapshot(panelIds);
        if (!(snapshot.total > 0)) return;
        const mins = getMinimumWidthPercentages(panelIds, snapshot.total);
        const pairStartIdx = panelIds.length === 2
            ? 0
            : (handle.id === 'resize-quiz-pdf' ? 0 : (handle.id === 'resize-pdf-ai' ? 1 : -1));
        if (pairStartIdx < 0 || pairStartIdx + 1 >= panelIds.length) return;
        const pairTotal = panelIds.length === 2
            ? 100
            : ((snapshot.widths[pairStartIdx] + snapshot.widths[pairStartIdx + 1]) / snapshot.total) * 100;
        const min = mins[pairStartIdx];
        const max = pairTotal - mins[pairStartIdx + 1];
        const current = (snapshot.widths[pairStartIdx] / snapshot.total) * 100;
        handle.setAttribute('aria-valuemin', String(Math.round(min * 10) / 10));
        handle.setAttribute('aria-valuemax', String(Math.round(max * 10) / 10));
        handle.setAttribute('aria-valuenow', String(Math.round(current * 10) / 10));
    }

    function saveResizeRatio() {
        if (isPhoneResizeLocked()) return;
        const panelIds = getActiveResizePanels();
        const { widths, total } = getPanelWidthSnapshot(panelIds);
        const key = getResizePersistenceKey();
        if (!key || panelIds.length < 2 || !(total > 0)) return;
        const store = readResizeStore();
        store[key] = {
            panelIds,
            widths: widths.map((width) => width / total * 100),
            orientation: getResizeOrientation(),
            deviceClass: getResizeDeviceClass(),
            viewportWidth: window.innerWidth,
            viewportHeight: window.innerHeight,
            updatedAt: Date.now()
        };
        writeResizeStore(store);
    }

    function applyClampedPersistedWidths(rawWidths, panelIds) {
        const numericWidths = panelIds.map((_, index) => Number(rawWidths?.[index])).map((width) => (
            Number.isFinite(width) && width > 0 ? width : 0
        ));
        if (numericWidths.some((width) => width <= 0)) return false;
        const total = numericWidths.reduce((sum, width) => sum + width, 0);
        if (!(total > 0)) return false;
        const normalized = numericWidths.map((width) => width / total * 100);
        const current = getPanelWidthSnapshot(panelIds);
        if (!(current.total > 0)) return false;
        const mins = getMinimumWidthPercentages(panelIds, current.total);
        const clamped = normalized.map((width, index) => Math.max(mins[index], width));
        const clampedTotal = clamped.reduce((sum, width) => sum + width, 0);
        if (clampedTotal > 100) {
            const scale = 100 / clampedTotal;
            for (let index = 0; index < clamped.length; index += 1) clamped[index] *= scale;
        } else if (clampedTotal < 100) {
            const surplus = 100 - clampedTotal;
            const originalTotal = normalized.reduce((sum, width) => sum + width, 0) || 100;
            for (let index = 0; index < clamped.length; index += 1) {
                clamped[index] += surplus * (normalized[index] / originalTotal);
            }
        }
        setGridWidths(clamped);
        const clampedWidthsPx = clamped.map((width) => width / 100 * current.total);
        updateResizeHandleAria(resizeQuizPdf, clampedWidthsPx, panelIds);
        updateResizeHandleAria(resizePdfAi, clampedWidthsPx, panelIds);
        return true;
    }

    function restoreResizeRatio() {
        if (isPhoneResizeLocked()) return false;
        const panelIds = getActiveResizePanels();
        const key = getResizePersistenceKey();
        if (!key || panelIds.length < 2) return false;
        const saved = readResizeStore()[key];
        if (!saved || JSON.stringify(saved.panelIds) !== JSON.stringify(panelIds)) return false;
        if (saved.orientation !== getResizeOrientation() || saved.deviceClass !== getResizeDeviceClass()) return false;
        return applyClampedPersistedWidths(saved.widths, panelIds);
    }

    // Pane opens restore the same canonical ratio before measuring their landing tracks.
    window.restorePanelResizeRatio = restoreResizeRatio;

    function clearInvalidResizeRatios(previousOrientation = '') {
        const currentOrientation = getResizeOrientation();
        const store = readResizeStore();
        let changed = false;
        Object.entries(store).forEach(([key, value]) => {
            if (!value || !Array.isArray(value.panelIds) || !Array.isArray(value.widths)
                || value.panelIds.length < 2 || value.panelIds.length !== value.widths.length
                || value.widths.some((width) => !Number.isFinite(Number(width)) || Number(width) <= 0)
                || (previousOrientation && previousOrientation !== currentOrientation
                    && value.deviceClass === getResizeDeviceClass()
                    && value.orientation === previousOrientation)) {
                delete store[key];
                changed = true;
            }
        });
        if (changed) writeResizeStore(store);
        lastResizeOrientation = currentOrientation;
    }

    function syncQuizCardForPanelResize() {
        const card = els?.quizCard || $('quiz-card');
        if (!card || isQuizCardExpanded || card.classList.contains('is-floating')) {
            scheduleQuizCardContentScaleSync();
            scheduleQuickActionsDockPosition();
            return;
        }

        restoreQuizCardGeometryForCurrentLayout();
        scheduleQuizCardHeightNormalization();
        scheduleQuizCardContentScaleSync();
        scheduleQuickActionsDockPosition();
    }

    function syncPdfPaneForPanelResize() {
        if (typeof scheduleAdvancedPdfResponsiveScaleRefresh === 'function') {
            scheduleAdvancedPdfResponsiveScaleRefresh({ preservePage: true });
        }
    }

    function startResize(e, handle) {
        if (isPhoneResizeLocked() || (e.button !== undefined && e.button !== 0)) return;
        const isTrioView = workArea.classList.contains('trio-view');
        const isSplitView = workArea.classList.contains('split-view');
        const isSplitViewAi = workArea.classList.contains('split-view-ai');
        const isPdfTutorView = workArea.classList.contains('pdf-tutor-view');
        if (isPdfTutorView && document.body.classList.contains('pdf-tutor-readout-active')) return;
        if (handle.id === 'resize-quiz-pdf' && !isTrioView && !isSplitView) return;
        if (handle.id === 'resize-pdf-ai' && !isTrioView && !isSplitViewAi && !isPdfTutorView) return;

        activePanelsAtResize = getActiveResizePanels();
        if (activePanelsAtResize.length < 2) return;

        const snapshot = getPanelWidthSnapshot(activePanelsAtResize);
        if (!Number.isFinite(snapshot.total) || snapshot.total <= 0) return;

        startWidthsPx = snapshot.widths;
        isResizing = true;
        currentHandle = handle;
        startX = e.clientX;
        activePointerId = Number.isFinite(e.pointerId) ? e.pointerId : null;

        handle.classList.add('dragging');
        document.body.classList.add('resizing-panels');
        document.body.style.cursor = 'col-resize';
        document.body.style.userSelect = 'none';
        updateResizeHandleAria(handle, startWidthsPx, activePanelsAtResize);
        try { handle.setPointerCapture?.(activePointerId); } catch (_) { }

        e.preventDefault();
    }

    function doResize(e) {
        if (!isResizing || !currentHandle) return;
        if (!Array.isArray(activePanelsAtResize) || activePanelsAtResize.length < 2) return;

        const deltaX = e.clientX - startX;
        const totalWidthPx = startWidthsPx.reduce((sum, width) => sum + width, 0);
        if (!(totalWidthPx > 0)) return;

        if (activePanelsAtResize.length === 2) {
            const mins = getMinimumWidthPercentages(activePanelsAtResize, totalWidthPx);
            const leftWidth = clamp(
                ((startWidthsPx[0] + deltaX) / totalWidthPx) * 100,
                mins[0],
                100 - mins[1]
            );
            setGridWidths([leftWidth, 100 - leftWidth]);
            const leftWidthPx = leftWidth / 100 * totalWidthPx;
            updateResizeHandleAria(currentHandle, [leftWidthPx, totalWidthPx - leftWidthPx], activePanelsAtResize);
            syncQuizCardForPanelResize();
            syncPdfPaneForPanelResize();
            return;
        }

        if (activePanelsAtResize.length === 3) {
            const pairStartIdx = currentHandle.id === 'resize-quiz-pdf' ? 0 : (currentHandle.id === 'resize-pdf-ai' ? 1 : -1);
            if (pairStartIdx < 0) return;

            const mins = getMinimumWidthPercentages(activePanelsAtResize, totalWidthPx);
            const pairTotalPx = startWidthsPx[pairStartIdx] + startWidthsPx[pairStartIdx + 1];
            const pairTotalPercent = pairTotalPx / totalWidthPx * 100;
            const leftWidth = clamp(
                (startWidthsPx[pairStartIdx] + deltaX) / totalWidthPx * 100,
                mins[pairStartIdx],
                pairTotalPercent - mins[pairStartIdx + 1]
            );
            const nextWidths = startWidthsPx.map((width) => width / totalWidthPx * 100);
            nextWidths[pairStartIdx] = leftWidth;
            nextWidths[pairStartIdx + 1] = pairTotalPercent - leftWidth;

            setGridWidths(nextWidths);
            updateResizeHandleAria(currentHandle, [
                nextWidths[0] / 100 * totalWidthPx,
                nextWidths[1] / 100 * totalWidthPx,
                nextWidths[2] / 100 * totalWidthPx
            ], activePanelsAtResize);
            syncQuizCardForPanelResize();
            syncPdfPaneForPanelResize();
        }
    }

    function stopResize() {
        if (!isResizing) return;

        doResizeThrottled.flush();
        saveResizeRatio();
        isResizing = false;
        if (currentHandle) {
            currentHandle.classList.remove('dragging');
            try { currentHandle.releasePointerCapture?.(activePointerId); } catch (_) { }
        }
        currentHandle = null;
        activePointerId = null;
        startWidthsPx = [];
        activePanelsAtResize = [];
        document.body.classList.remove('resizing-panels');
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
        syncQuizCardForPanelResize();
        syncPdfPaneForPanelResize();
    }

    function resizeFromKeyboard(event, handle) {
        if (isPhoneResizeLocked()) return;
        const isTrioView = workArea.classList.contains('trio-view');
        const isSplitView = workArea.classList.contains('split-view');
        const isSplitViewAi = workArea.classList.contains('split-view-ai');
        const isPdfTutorView = workArea.classList.contains('pdf-tutor-view');
        if (isPdfTutorView && document.body.classList.contains('pdf-tutor-readout-active')) return;
        if (handle.id === 'resize-quiz-pdf' && !isTrioView && !isSplitView) return;
        if (handle.id === 'resize-pdf-ai' && !isTrioView && !isSplitViewAi && !isPdfTutorView) return;
        const panelIds = getActiveResizePanels();
        const snapshot = getPanelWidthSnapshot(panelIds);
        if (panelIds.length < 2 || !(snapshot.total > 0)) return;
        const isArrow = event.key === 'ArrowLeft' || event.key === 'ArrowRight';
        const isBoundary = event.key === 'Home' || event.key === 'End';
        if (!isArrow && !isBoundary) return;
        event.preventDefault();
        const mins = getMinimumWidthPercentages(panelIds, snapshot.total);
        const pairStartIdx = panelIds.length === 2
            ? 0
            : (handle.id === 'resize-quiz-pdf' ? 0 : (handle.id === 'resize-pdf-ai' ? 1 : -1));
        if (pairStartIdx < 0) return;
        const pairTotal = panelIds.length === 2
            ? 100
            : (snapshot.widths[pairStartIdx] + snapshot.widths[pairStartIdx + 1]) / snapshot.total * 100;
        const min = mins[pairStartIdx];
        const max = pairTotal - mins[pairStartIdx + 1];
        const current = snapshot.widths[pairStartIdx] / snapshot.total * 100;
        const step = event.shiftKey ? 8 : 3;
        const next = event.key === 'Home'
            ? min
            : (event.key === 'End'
                ? max
                : clamp(current + (event.key === 'ArrowRight' ? step : -step), min, max));
        const widths = snapshot.widths.map((width) => width / snapshot.total * 100);
        widths[pairStartIdx] = next;
        widths[pairStartIdx + 1] = pairTotal - next;
        setGridWidths(widths);
        updateResizeHandleAria(handle, widths.map((width) => width / 100 * snapshot.total), panelIds);
        syncQuizCardForPanelResize();
        syncPdfPaneForPanelResize();
        saveResizeRatio();
    }

    // Attach event listeners
    [resizeQuizPdf, resizePdfAi].filter(Boolean).forEach((handle) => {
        handle.addEventListener('pointerdown', (e) => startResize(e, handle));
        handle.addEventListener('keydown', (e) => resizeFromKeyboard(e, handle));
    });

    const doResizeThrottled = createRafDragThrottle(doResize);
    document.addEventListener('pointermove', (e) => {
        if (!isResizing) return;
        doResizeThrottled(e);
    });
    document.addEventListener('pointerup', stopResize);
    document.addEventListener('pointercancel', stopResize);

    lastResizeOrientation = getResizeOrientation();
    window.addEventListener('resize', () => {
        clearInvalidResizeRatios(lastResizeOrientation);
        if (!isResizing) {
            const panels = getActiveResizePanels();
            updateResizeHandleAria(resizeQuizPdf, null, panels);
            updateResizeHandleAria(resizePdfAi, null, panels);
        }
    });
    window.addEventListener('orientationchange', () => {
        clearInvalidResizeRatios(lastResizeOrientation);
    });

    // Transition cleanup changes classes without changing the layout or its saved divider.
    let observedResizeLayout = `${getResizePersistenceKey()}:${getActiveResizePanels().join(",")}`;
    // Reset widths when view mode changes
    const observer = new MutationObserver((records) => {
        // Re-adding a class #work-area already has still queues a record. Treating that as a
        // view change cleared the grid tracks and re-ran the whole layout on every such write.
        if (!records.some((record) => record.oldValue !== workArea.getAttribute('class'))) return;
        const nextResizeLayout = `${getResizePersistenceKey()}:${getActiveResizePanels().join(",")}`;
        if (nextResizeLayout === observedResizeLayout) return;
        observedResizeLayout = nextResizeLayout;
        // A pane transition drives #work-area's classes and inline grid tracks itself. Clearing
        // gridTemplateColumns from here mid-flight cancels the tracks the animation is currently
        // interpolating; completePaneOpenTransition/commitAfterPaneClosePreview restore the
        // canonical layout when it settles.
        if (isResizing || window.isPaneLayoutTransitioning?.()) return;
        workArea.style.gridTemplateColumns = '';
        applyPanelOrderLayout();
        requestAnimationFrame(() => {
            if (window.isPaneLayoutTransitioning?.()) return;
            restoreResizeRatio();
            const panels = getActiveResizePanels();
            updateResizeHandleAria(resizeQuizPdf, null, panels);
            updateResizeHandleAria(resizePdfAi, null, panels);
        });
    });
    observer.observe(workArea, { attributes: true, attributeOldValue: true, attributeFilter: ['class'] });
    requestAnimationFrame(() => {
        clearInvalidResizeRatios();
        restoreResizeRatio();
    });
})();

/* ==========================================================================
   MYSTERY CARD REWARD SYSTEM
   ========================================================================== */

// Expanded mystery reward pool with rarity tiers.
// Rarity: common (gray), rare (blue), epic (violet), legendary (gold)
const MYSTERY_REWARDS = [
    // Stable IDs preserve reserved rewards. Values are improved for the ten-answer path.
    { id: 'mementos_20', name: 'Memento pouch', description: '+40 Mementos', icon: 'M', tier: 'low', rarity: 'common', profiles: ['all'], effect: { type: 'mementos', amount: 40 } },
    { id: 'mementos_40', name: 'Memento purse', description: '+80 Mementos', icon: 'M', tier: 'mid', rarity: 'rare', profiles: ['all'], effect: { type: 'mementos', amount: 80 } },
    { id: 'mult_15x_10', name: 'XP boost', description: '1.5x base XP for 10 correct answers', icon: 'XP', tier: 'mid', rarity: 'rare', profiles: ['all'], effect: { type: 'multiplier', value: 1.5, questions: 10 } },
    { id: 'shield_1', name: 'Streak Shield', description: 'A miss won’t break your streak', icon: 'SH', tier: 'mid', rarity: 'rare', profiles: ['all'], effect: { type: 'shield', charges: 1 } },
    { id: 'growth_boost', name: 'Growth Bonus', description: '+15 XP on the next 5 questions you’d missed before', icon: 'GR', tier: 'mid', rarity: 'rare', profiles: ['all'], effect: { type: 'growth_boost', amount: 15, questions: 5 } },
    { id: 'mementos_70', name: 'Memento chest', description: '+160 Mementos', icon: 'M', tier: 'high', rarity: 'legendary', profiles: ['all'], effect: { type: 'mementos', amount: 160 } },
    { id: 'mementos_120', name: 'Memento hoard', description: '+250 Mementos', icon: 'M', tier: 'high', rarity: 'legendary', profiles: ['all'], effect: { type: 'mementos', amount: 250 } },
    { id: 'mementos_epic', name: 'Celestial treasury', description: '+1,500 Mementos', icon: 'M', tier: 'high', rarity: 'epic', profiles: ['all'], effect: { type: 'mementos', amount: 1500 } }
].map(reward => ({ ...reward, modes: ['shield', 'growth_boost'].includes(reward.effect?.type) ? ['quizzes', 'exam'] : ['all'] }));

// Active power-ups from mystery cards
let activePowerUps = {
    xpMultiplier: 1,
    xpMultiplierQuestions: 0,
    streakShields: 0,
    growthBoost: 0,
    growthBoostQuestions: 0
};

function getMysteryCardGlyph(reward) {
    const effect = reward && reward.effect ? reward.effect : { type: 'xp' };
    switch (effect.type) {
        case 'multiplier':
            return `${effect.value || 1}x`;
        case 'shield':
            return `S${effect.charges || 1}`;
        case 'growth_boost':
            return 'GR';
        case 'mementos':
            return 'M';
        case 'xp_confetti':
            return 'XP+';
        case 'xp':
        default:
            return 'XP';
    }
}

function shouldUseLiteMysteryFX() {
    try {
        return getMemoraMotionMode() === 'reduced';
    } catch (err) {
        return false;
    }
}

function triggerMysteryCard() {
    if (!isGameMode) return;

    const profileKey = getCurrentMysteryStreakProfile();
    const profileRewards = MYSTERY_REWARDS.filter((reward) => {
        if (!Array.isArray(reward.profiles) || reward.profiles.length === 0) return true;
        return reward.profiles.includes('all') || reward.profiles.includes(profileKey);
    });

    const rewardPool = profileRewards.length >= 3 ? profileRewards : MYSTERY_REWARDS;
    const shuffled = [...rewardPool].sort(() => Math.random() - 0.5);
    const cardRewards = shuffled.slice(0, 3);

    // Create the mystery card overlay
    createMysteryCardOverlay(cardRewards, profileKey);
}

function createMysteryCardOverlay(cardRewards, profileKey) {
    // Remove existing overlay if any
    const existing = document.querySelector('.mystery-card-overlay');
    if (existing) existing.remove();

    const streakProfile = MYSTERY_STREAK_PROFILES[profileKey] || MYSTERY_STREAK_PROFILES.core;
    const isLiteFX = shouldUseLiteMysteryFX();
    const overlay = document.createElement('div');
    overlay.className = `mystery-card-overlay${isLiteFX ? ' perf-lite' : ''}`;
    overlay.innerHTML = `
        <div class="mystery-title">Reward Selection</div>
        <div class="mystery-subtitle">${streakProfile.label}: ${mysteryCardThreshold} correct answers reached. Crack open a chest!</div>
        <div class="mystery-chests-container">
            ${cardRewards.map((reward, idx) => `
                <div class="mystery-chest" data-idx="${idx}" data-reward-id="${reward.id}" data-tier="${reward.tier}" data-clicks="0" data-limit="${Math.floor(Math.random() * 4) + 3}">
                    <div class="chest-container">
                        <div class="chest-piece lid"></div>
                        <div class="chest-piece body-left"></div>
                        <div class="chest-piece body-right"></div>
                        <div class="chest-piece lock"></div>
                        <div class="chest-glow"></div>
                    </div>
                    <div class="chest-shadow"></div>
                    <div class="chest-reward-reveal tier-${reward.tier} rarity-${reward.rarity}">
                        <div class="rarity-badge">${reward.rarity.toUpperCase()}</div>
                        <div class="xp-reveal-icon type-${reward.effect?.type || 'xp'}">${getMysteryCardGlyph(reward)}</div>
                        <div class="xp-reveal-name">${reward.name}</div>
                        <div class="xp-reveal-amount">${reward.description}</div>
                    </div>
                </div>
            `).join('')}
        <button class="mystery-continue-btn" style="display: none;">Continue Session</button>
    `;

    document.body.appendChild(overlay);

    // Activate with slight delay for animation
    setTimeout(() => overlay.classList.add('active'), isLiteFX ? 20 : 50);

    // Add click handlers to chests
    const chests = overlay.querySelectorAll('.mystery-chest');
    chests.forEach(chest => {
        chest.addEventListener('click', () => handleMysteryChestClick(chest, chests, overlay));
    });

    // Continue button
    const continueBtn = overlay.querySelector('.mystery-continue-btn');
    continueBtn.addEventListener('click', () => closeMysteryCardOverlay(overlay));
}

function handleMysteryChestClick(selectedChest, allChests, overlay) {
    if (selectedChest.classList.contains('broken') || selectedChest.classList.contains('disabled')) return;
    const isLiteFX = overlay.classList.contains('perf-lite');

    // Disable clicking on all other chests
    allChests.forEach(chest => {
        if (chest !== selectedChest) {
            chest.classList.add('disabled');
        }
    });

    // Increment click count
    const clicks = parseInt(selectedChest.dataset.clicks) + 1;
    selectedChest.dataset.clicks = clicks;
    const limit = parseInt(selectedChest.dataset.limit);

    // Trigger bounce/shake animation with variable intensity
    const container = selectedChest.querySelector('.chest-container');
    if (container) {
        container.style.setProperty('--shake-intensity', clicks * 2.5);
        container.classList.remove('shake-hit');
        void container.offsetWidth; // force reflow
        container.classList.add('shake-hit');
    }

    // Spawn splinters/sparks
    if (!isLiteFX) {
        createWoodSplinters(selectedChest, clicks);
    }

    if (clicks >= limit) {
        // Break/Shatter the chest!
        selectedChest.classList.add('broken', 'selected');

        // Disable further clicking
        selectedChest.classList.add('disabled');

        // Get the reward by ID
        const rewardId = selectedChest.dataset.rewardId;
        const reward = MYSTERY_REWARDS.find(r => r.id === rewardId);
        const tier = selectedChest.dataset.tier;

        // Apply the reward effect after shatter animation
        setTimeout(() => {
            if (reward) {
                applyMysteryReward(reward);
            }

            // Show confetti for high tier or confetti effects
            if (tier === 'high' || reward?.effect?.type === 'xp_confetti') {
                createJackpotConfetti(selectedChest);
            }

            // Reveal ALL other chests so user can see what they missed
            let revealDelay = isLiteFX ? 140 : 300;
            allChests.forEach((chest) => {
                if (chest !== selectedChest) {
                    setTimeout(() => {
                        chest.classList.remove('disabled');
                        chest.classList.add('broken', 'revealed');
                        chest.style.opacity = '0.6';
                    }, revealDelay);
                    revealDelay += isLiteFX ? 100 : 200;
                }
            });

            // Show continue button after all reveals
            setTimeout(() => {
                const continueBtn = overlay.querySelector('.mystery-continue-btn');
                if (continueBtn) {
                    continueBtn.style.display = 'block';
                    setTimeout(() => continueBtn.classList.add('visible'), isLiteFX ? 20 : 50);
                }
            }, isLiteFX ? 380 : 600);

        }, isLiteFX ? 350 : 550);

        // Reset threshold for next mystery card
        mysteryCardThreshold = getRandomMysteryThreshold();
    }
}

function createWoodSplinters(chest, clicks) {
    const container = chest.querySelector('.chest-container');
    if (!container) return;

    const count = 3 + clicks * 2;
    const fragment = document.createDocumentFragment();

    for (let i = 0; i < count; i++) {
        const particle = document.createElement('div');
        particle.className = 'chest-particle';

        const colors = [
            '#5c3a21', // Dark wood
            '#7c4828', // Medium wood
            '#aa7c11', // Dark gold
            '#ffd700', // Bright gold
            '#e5c158'  // Light gold
        ];
        particle.style.backgroundColor = colors[Math.floor(Math.random() * colors.length)];

        const size = Math.floor(Math.random() * 5) + 3;
        particle.style.width = size + 'px';
        particle.style.height = (size * (Math.random() > 0.4 ? 0.4 : 1)) + 'px';

        const angle = Math.random() * Math.PI * 2;
        const distance = 30 + Math.random() * 55;
        const tx = Math.cos(angle) * distance;
        const ty = Math.sin(angle) * distance + (15 + Math.random() * 15);

        particle.style.setProperty('--tx', tx + 'px');
        particle.style.setProperty('--ty', ty + 'px');
        particle.style.setProperty('--rot', ((Math.random() - 0.5) * 360) + 'deg');

        fragment.appendChild(particle);
    }

    container.appendChild(fragment);

    setTimeout(() => {
        const particles = container.querySelectorAll('.chest-particle');
        particles.forEach(p => p.remove());
    }, 760);
}

// Apply the mystery card reward effect
function applyMysteryReward(reward) {
    const effect = reward.effect;

    switch (effect.type) {
        // The chest already showed what was won, so nothing here pops up again.
        case 'mementos':
            grantGameReward({ type: 'grant', amount: effect.amount, reason: reward.name });
            break;

        case 'xp':
        case 'xp_confetti':
            awardXP(effect.amount, reward.name, { quiet: true });
            break;

        case 'multiplier':
            activePowerUps.xpMultiplier = effect.value;
            activePowerUps.xpMultiplierQuestions = effect.questions;
            break;

        case 'shield':
            activePowerUps.streakShields = Math.min(MAX_STREAK_SHIELDS, activePowerUps.streakShields + effect.charges);
            break;

        case 'growth_boost':
            activePowerUps.growthBoost = effect.amount;
            activePowerUps.growthBoostQuestions = effect.questions;
            break;

        default:
            console.warn('Unknown reward type:', effect.type);
    }
    updatePowerUpIndicator();
    if (['multiplier', 'shield', 'growth_boost'].includes(effect.type)) {
        return IDB.put('files', 'activePowerUps', { ...activePowerUps })
            .catch((error) => console.warn('Could not save chest power-up:', error));
    }
}

// Show power-up notification
function showPowerUpNotification(title, description) {
    const isLiteFX = shouldUseLiteMysteryFX();
    const notification = document.createElement('div');
    notification.className = `power-up-notification${isLiteFX ? ' lite' : ''}`;
    notification.innerHTML = `
        <div class="power-up-title">${title}</div>
        <div class="power-up-desc">${description}</div>
    `;
    document.body.appendChild(notification);

    setTimeout(() => notification.classList.add('visible'), isLiteFX ? 20 : 50);
    setTimeout(() => {
        notification.classList.remove('visible');
        setTimeout(() => notification.remove(), isLiteFX ? 180 : 300);
    }, isLiteFX ? 1900 : 3000);

    // Update the indicator
    updatePowerUpIndicator();
}

// Update active power-up indicator (non-distracting corner display)
function updatePowerUpIndicator() {
    let indicator = document.querySelector('.power-up-indicator');

    // Check if any power-ups are active
    const hasShields = activePowerUps.streakShields > 0;
    const hasMultiplier = activePowerUps.xpMultiplierQuestions > 0;
    const hasGrowth = activePowerUps.growthBoostQuestions > 0;

    const hasAnyPowerUp = hasShields || hasMultiplier || hasGrowth;

    // Remove indicator if no power-ups
    if (!hasAnyPowerUp) {
        if (indicator) indicator.remove();
        return;
    }

    // Create indicator if doesn't exist
    if (!indicator) {
        indicator = document.createElement('div');
        indicator.className = 'power-up-indicator';
        document.body.appendChild(indicator);
    }

    // Build badges
    let html = '';

    if (hasShields) {
        html += `<div class="power-up-badge shield">
            <span class="icon">SH</span>
            <span class="count">${activePowerUps.streakShields}</span>
        </div>`;
    }

    if (hasMultiplier) {
        html += `<div class="power-up-badge multiplier">
            <span class="icon">XP</span>
            <span class="label">${activePowerUps.xpMultiplier}x</span>
            <span class="count">(${activePowerUps.xpMultiplierQuestions})</span>
        </div>`;
    }

    if (hasGrowth) {
        html += `<div class="power-up-badge">
            <span class="icon">GR</span>
            <span class="count">(${activePowerUps.growthBoostQuestions})</span>
        </div>`;
    }

    indicator.innerHTML = html;
}

// Update streak display helper
function updateStreakDisplay() {
    const sVal = document.querySelector('#streak-val, .streak-value');
    if (sVal) {
        sVal.textContent = currentStreak;
        sVal.classList.remove('streak-pop');
        void sVal.offsetWidth;
        sVal.classList.add('streak-pop');
    }
}

function createJackpotConfetti(card) {
    if (shouldUseLiteMysteryFX()) return;

    const container = document.createElement('div');
    container.className = 'jackpot-confetti-container';
    card.appendChild(container);

    const colors = ['#fbbf24', '#f97316', '#ef4444', '#a855f7', '#60a5fa', '#4ade80'];
    const confettiCount = 18;
    const fragment = document.createDocumentFragment();

    for (let i = 0; i < confettiCount; i++) {
        const confetti = document.createElement('div');
        confetti.className = 'jackpot-confetti active';
        confetti.style.background = colors[Math.floor(Math.random() * colors.length)];
        confetti.style.setProperty('--tx', `${(Math.random() - 0.5) * 300}px`);
        confetti.style.setProperty('--ty', `${(Math.random() - 0.5) * 300}px`);
        confetti.style.width = `${Math.random() * 8 + 5}px`;
        confetti.style.height = confetti.style.width;
        confetti.style.borderRadius = Math.random() > 0.5 ? '50%' : '2px';
        confetti.style.animationDelay = `${i * 16}ms`;
        fragment.appendChild(confetti);
    }

    container.appendChild(fragment);
    setTimeout(() => container.remove(), 1500);
}

function closeMysteryCardOverlay(overlay) {
    overlay.classList.remove('active');
    setTimeout(() => overlay.remove(), 400);
}

function showMysteryStreakNotification() {
    // Remove existing notification
    const existing = document.querySelector('.streak-mystery-notification');
    if (existing) existing.remove();

    const profileKey = getCurrentMysteryStreakProfile();
    const profile = MYSTERY_STREAK_PROFILES[profileKey] || MYSTERY_STREAK_PROFILES.core;
    const isLiteFX = shouldUseLiteMysteryFX();
    const notification = document.createElement('div');
    notification.className = `streak-mystery-notification streak-type-${profileKey}${isLiteFX ? ' lite' : ''}`;
    notification.innerHTML = `
        <div class="streak-label">${profile.label}</div>
        <div class="streak-text">${mysteryCardThreshold} correct answers in a row.</div>
        <div class="streak-note">${profile.detail} reward set unlocked.</div>
    `;

    document.body.appendChild(notification);

    // Activate
    setTimeout(() => notification.classList.add('active'), isLiteFX ? 20 : 50);

    // Auto-trigger mystery card after showing notification
    setTimeout(() => {
        notification.classList.remove('active');
        setTimeout(() => {
            notification.remove();
            triggerMysteryCard();
        }, isLiteFX ? 220 : 400);
    }, isLiteFX ? 1050 : 1500);
}

function checkMysteryCardTrigger() {
    if (!isGameMode) return;

    // Check if current streak hits the threshold
    if (currentStreak > 0 && currentStreak === mysteryCardThreshold) {
        mysteryCardPending = true;
        showMysteryStreakNotification();
    }
}

/* ==========================================================================
   HARD MODE TYPE-ANSWER ENHANCEMENT
   ========================================================================== */

let isHardModeActive = false;
let hardModeRevealed = false;

function setupHardModeInput() {
    const container = $('hard-mode-input-container');
    const input = $('hard-mode-input');
    const revealBtn = $('hard-mode-reveal-btn');
    const feedback = $('hard-mode-feedback');

    if (!revealBtn) return;

    revealBtn.addEventListener('click', () => {
        if (hardModeRevealed) return;

        const userGuess = input.value.trim().toLowerCase();
        const correctAnswer = getCurrentCorrectAnswer();

        // Check similarity
        const similarity = calculateSimilarity(userGuess, correctAnswer.toLowerCase());

        // Show feedback
        if (similarity >= 0.8) {
            feedback.className = 'hard-mode-feedback correct';
            feedback.textContent = `&#10003; Great guess! "${correctAnswer}" is correct.`;
        } else if (similarity >= 0.5) {
            feedback.className = 'hard-mode-feedback partial';
            feedback.textContent = `~ Close! The answer is "${correctAnswer}"`;
        } else if (userGuess.length > 0) {
            feedback.className = 'hard-mode-feedback wrong';
            feedback.textContent = `✗ The correct answer is "${correctAnswer}"`;
        } else {
            feedback.className = 'hard-mode-feedback';
            feedback.style.display = 'none';
        }

        // Reveal choices
        revealHardModeChoices();
        hardModeRevealed = true;
        revealBtn.textContent = 'Choices Revealed';
        revealBtn.disabled = true;
    });

    // Enter key reveals
    input.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' && !hardModeRevealed) {
            revealBtn.click();
        }
    });
}

function getCurrentCorrectAnswer() {
    const realIdx = filteredIndices[currentQuestionIndex];
    const q = questions[realIdx];
    if (!q) return '';

    const correctIdx = q.a;
    const choices = q.o || [];
    return choices[correctIdx] || '';
}

function revealHardModeChoices() {
    const choicesEl = $('choices');
    if (choicesEl) {
        choicesEl.classList.remove('hard-mode-hidden');
    }
}

function hideHardModeChoices() {
    const choicesEl = $('choices');
    if (choicesEl && isHardModeActive) {
        choicesEl.classList.add('hard-mode-hidden');
    }
}

function resetHardModeInput() {
    const container = $('hard-mode-input-container');
    const input = $('hard-mode-input');
    const revealBtn = $('hard-mode-reveal-btn');
    const feedback = $('hard-mode-feedback');

    if (input) input.value = '';
    if (feedback) {
        feedback.className = 'hard-mode-feedback';
        feedback.textContent = '';
    }
    if (revealBtn) {
        revealBtn.textContent = 'Reveal Choices';
        revealBtn.disabled = false;
    }

    hardModeRevealed = false;

    // Show/hide based on hard mode state
    if (container) {
        container.classList.toggle('active', isHardModeActive);
    }

    // Hide choices if hard mode is active
    if (isHardModeActive) {
        hideHardModeChoices();
    }
}

// Simple similarity calculation (Jaccard-like)
function calculateSimilarity(str1, str2) {
    if (!str1 || !str2) return 0;

    const words1 = str1.split(/\s+/).filter(w => w.length > 2);
    const words2 = str2.split(/\s+/).filter(w => w.length > 2);

    if (words1.length === 0 || words2.length === 0) {
        // Fall back to character comparison for short answers
        const longer = str1.length > str2.length ? str1 : str2;
        const shorter = str1.length > str2.length ? str2 : str1;
        if (longer.includes(shorter)) return 0.9;
        return 0;
    }

    const set1 = new Set(words1);
    const set2 = new Set(words2);

    let intersection = 0;
    set1.forEach(word => {
        if (set2.has(word)) intersection++;
    });

    const union = set1.size + set2.size - intersection;
    return union > 0 ? intersection / union : 0;
}

// Initialize hard mode input on DOM ready
document.addEventListener('DOMContentLoaded', setupHardModeInput);

/* ==========================================================================
   STATS MODAL FUNCTIONS
   ========================================================================== */

let activeStatsTabId = 'overview';
let statsLectureCache = [];

function getStatsTabId(tabId) {
    const id = String(tabId || 'overview').trim().toLowerCase();
    return ['overview', 'lectures', 'hardest', 'badges'].includes(id) ? id : 'overview';
}

function switchStatsTab(tabId) {
    const nextTabId = getStatsTabId(tabId);
    const changed = activeStatsTabId !== nextTabId;
    activeStatsTabId = nextTabId;

    ['overview', 'lectures', 'hardest', 'badges'].forEach((id) => {
        const isActive = id === nextTabId;
        const btn = $(`stats-tab-${id}`);
        const view = $(`stats-view-${id}`);
        if (btn) {
            btn.classList.toggle('active', isActive);
            btn.setAttribute('aria-selected', isActive ? 'true' : 'false');
            btn.tabIndex = isActive ? 0 : -1;
        }
        if (view) {
            view.classList.toggle('active', isActive);
            view.hidden = !isActive;
        }
    });
    if (changed) {
        const content = $('stats-content');
        if (content) content.scrollTop = 0;
    }

    if (nextTabId === 'lectures') {
        renderLectureStatsDashboard();
    } else if (nextTabId === 'hardest') {
        populateStatsInsights();
    } else if (nextTabId === 'badges') {
        renderStatsBadgeShowcase();
    }
}
window.switchStatsTab = switchStatsTab;

function openStatsModal() {
    populateStatsModal();
    populateStatsInsights();
    setupStatsLectureDashboard();
    renderLectureStatsDashboard();
    renderStatsBadgeShowcase();
    switchStatsTab(activeStatsTabId || 'overview');
    // Routing suspends overlays it did not admit, so Stats opened from the level-up card or the
    // agent (not the rail) stayed inert and ignored every click and scroll.
    const statsOpener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    window.MemoraLiveLeftRailRouting?.admitNativeTransient?.('statistics', statsOpener);
    $('stats-modal').classList.add('active');
}

function closeStatsModal(event) {
    if (event.target === $('stats-modal') || event.target.classList.contains('welcome-btn')) {
        $('stats-modal').classList.remove('active');
    }
}

function setupStatsLectureDashboard() {
    const searchInput = $('stats-lecture-search');
    if (searchInput && searchInput.dataset.statsSearchBound !== '1') {
        searchInput.dataset.statsSearchBound = '1';
        searchInput.addEventListener('input', () => renderLectureStatsDashboard(searchInput.value));
    }

    const sortSelect = $('stats-lecture-sort');
    if (sortSelect && sortSelect.dataset.statsSortBound !== '1') {
        sortSelect.dataset.statsSortBound = '1';
        sortSelect.addEventListener('change', () => renderLectureStatsDashboard());
    }

    const content = $('stats-content');
    if (content && content.dataset.statsQuestionBound !== '1') {
        content.dataset.statsQuestionBound = '1';
        content.addEventListener('click', (event) => {
            const row = event.target.closest('[data-stats-question]');
            if (row) openStatsQuestionFromHardest(row.dataset.statsQuestion);
        });
    }

    const tabBar = getStatsTabBar();
    if (tabBar && tabBar.dataset.statsKeysBound !== '1') {
        tabBar.dataset.statsKeysBound = '1';
        tabBar.addEventListener('keydown', (event) => {
            if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
            const ids = ['overview', 'lectures', 'hardest', 'badges'];
            const current = ids.indexOf(activeStatsTabId);
            const next = event.key === 'Home' ? 0
                : event.key === 'End' ? ids.length - 1
                    : (current + (event.key === 'ArrowRight' ? 1 : -1) + ids.length) % ids.length;
            event.preventDefault();
            switchStatsTab(ids[next]);
            $(`stats-tab-${ids[next]}`)?.focus();
        });
    }

    const grid = $('lecture-card-grid');
    if (grid && grid.dataset.statsLectureBound !== '1') {
        grid.dataset.statsLectureBound = '1';
        grid.addEventListener('click', (event) => {
            const btn = event.target.closest('[data-stats-lecture-action]');
            if (!btn) return;
            const sourceKey = btn.dataset.sourceKey || '';
            const action = btn.dataset.statsLectureAction || '';
            if (action === 'toggle') {
                toggleStatsLectureRow(grid, btn.closest('.mstat-lecture'), sourceKey);
            } else if (action === 'practice') {
                practiceStatsLectureDeck(sourceKey);
            } else if (action === 'study-pdf') {
                studyStatsLecturePdf(sourceKey);
            } else if (action === 'generate-quiz') {
                openStatsLectureQuizGenerator(sourceKey);
            } else if (action === 'reset-stats') {
                void resetStatsLectureProgress(sourceKey);
            }
        });
    }
}

function getStatsTabBar() {
    return document.querySelector('#stats-modal .stats-tab-bar');
}

let statsExpandedLectureKey = '';

function toggleStatsLectureRow(grid, article, sourceKey) {
    if (!grid || !article) return;
    const open = !article.classList.contains('is-open');
    grid.querySelectorAll('.mstat-lecture').forEach((row) => {
        const isOpen = open && row === article;
        row.classList.toggle('is-open', isOpen);
        row.querySelector('.mstat-lecture-row')?.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
        const actions = row.querySelector('.mstat-lecture-actions');
        if (actions) actions.hidden = !isOpen;
    });
    statsExpandedLectureKey = open ? sourceKey : '';
}

function normalizeStatsLectureKey(value) {
    return String(value || '').trim().replace(/\\/g, '/').toLowerCase();
}

function getStatsLectureBaseKey(value) {
    return normalizeStatsLectureKey(value)
        .replace(/\.(pdf|json|txt)$/i, '')
        .replace(/_quiz$/i, '')
        .replace(/\bquiz$/i, '')
        .replace(/[_-]+/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
}

function getStatsLectureType(name, fallback = 'json') {
    const value = String(name || '').trim().toLowerCase();
    if (value.endsWith('.pdf')) return 'pdf';
    if (value.endsWith('.json') || value.endsWith('.txt')) return 'json';
    return fallback;
}

function addStatsLectureLookup(lookup, rawName, key) {
    const exact = normalizeStatsLectureKey(rawName);
    const base = getStatsLectureBaseKey(rawName);
    if (exact && !lookup.exact.has(exact)) lookup.exact.set(exact, key);
    if (base && !lookup.base.has(base)) lookup.base.set(base, key);
}

function resolveStatsLectureKey(lookup, rawName) {
    const exact = normalizeStatsLectureKey(rawName);
    if (exact && lookup.exact.has(exact)) return lookup.exact.get(exact);
    const base = getStatsLectureBaseKey(rawName);
    if (base && lookup.base.has(base)) return lookup.base.get(base);
    return '';
}

function createStatsLectureEntry(rawName, type, order) {
    const normalizedRaw = String(rawName || '').trim() || 'Unknown';
    return {
        key: `${type}:${normalizeStatsLectureKey(normalizedRaw) || `unknown-${order}`}`,
        rawName: normalizedRaw,
        title: formatResultsReviewSourceTitle(normalizedRaw),
        type,
        order,
        questionIndexSet: new Set(),
        questionIndices: [],
        questionCount: 0,
        reviewedCount: 0,
        attempts: 0,
        correctAttempts: 0,
        wrongAttempts: 0,
        avgSeconds: 0,
        completionPercent: 0,
        grade: 'N/A',
        gradeClass: 'grade-none'
    };
}

function addStatsLectureEntry(entries, byKey, lookup, rawName, type) {
    const normalizedRaw = String(rawName || '').trim();
    if (!normalizedRaw) return null;
    const key = `${type}:${normalizeStatsLectureKey(normalizedRaw)}`;
    if (byKey.has(key)) return byKey.get(key);

    const entry = createStatsLectureEntry(normalizedRaw, type, entries.length);
    entry.key = key;
    entries.push(entry);
    byKey.set(key, entry);
    addStatsLectureLookup(lookup, normalizedRaw, key);
    return entry;
}

function buildStatsLectureData() {
    const entries = [];
    const byKey = new Map();
    const lookup = { exact: new Map(), base: new Map() };
    const pdfNames = Array.isArray(sessionManifest?.pdfs)
        ? sessionManifest.pdfs.map(name => String(name || '').trim()).filter(Boolean)
        : [];
    const jsonNames = Array.isArray(sessionManifest?.jsons)
        ? sessionManifest.jsons.map(name => String(name || '').trim()).filter(Boolean)
        : [];

    pdfNames.forEach(name => addStatsLectureEntry(entries, byKey, lookup, name, 'pdf'));
    jsonNames.forEach(name => addStatsLectureEntry(entries, byKey, lookup, name, 'json'));

    const indexToSetKey = typeof buildResultsQuestionSetKeyByIndexMap === 'function'
        ? buildResultsQuestionSetKeyByIndexMap()
        : {};

    const attachQuestionToSource = (rawName, idx, fallbackType, attachedKeys) => {
        const normalizedRaw = String(rawName || '').trim();
        if (!normalizedRaw || !Number.isInteger(idx)) return;

        let key = resolveStatsLectureKey(lookup, normalizedRaw);
        if (!key) {
            const type = getStatsLectureType(normalizedRaw, fallbackType);
            const entry = addStatsLectureEntry(entries, byKey, lookup, normalizedRaw, type);
            key = entry?.key || '';
        }

        if (!key || attachedKeys.has(key)) return;
        const entry = byKey.get(key);
        if (!entry) return;
        entry.questionIndexSet.add(idx);
        attachedKeys.add(key);
    };

    questions.forEach((question, idx) => {
        if (!question || typeof question !== 'object') return;
        const attachedKeys = new Set();
        const setKey = String(indexToSetKey[idx] || '').trim();
        const sourceFile = String(question.sourceFile || '').trim();
        const src = String(question.src || '').trim();
        const setName = String(question.setName || '').trim();

        if (sourceFile) attachQuestionToSource(sourceFile, idx, getStatsLectureType(sourceFile, 'json'), attachedKeys);
        if (src && src !== sourceFile) attachQuestionToSource(src, idx, getStatsLectureType(src, 'json'), attachedKeys);
        if (setName && setName !== sourceFile && setName !== src) attachQuestionToSource(setName, idx, getStatsLectureType(setName, 'json'), attachedKeys);
        if (setKey) {
            attachQuestionToSource(setKey, idx, 'json', attachedKeys);
            const linkedPdfs = pdfAssociations?.[setKey];
            if (Array.isArray(linkedPdfs)) {
                linkedPdfs.forEach(pdfName => attachQuestionToSource(pdfName, idx, 'pdf', attachedKeys));
            }
        }
    });

    entries.forEach((entry) => {
        entry.questionIndices = Array.from(entry.questionIndexSet).sort((a, b) => a - b);
        const loadedSetCount = entry.type === 'json' && Array.isArray(loadedQuestionSets?.[entry.rawName])
            ? loadedQuestionSets[entry.rawName].length
            : 0;
        entry.questionCount = Math.max(entry.questionIndices.length, loadedSetCount);

        let durationTotal = 0;
        entry.questionIndices.forEach((idx) => {
            const attempts = Array.isArray(statsData?.[idx]) ? statsData[idx] : [];
            if (attempts.length > 0) entry.reviewedCount++;
            attempts.forEach((attempt) => {
                entry.attempts++;
                if (attempt?.correct) entry.correctAttempts++;
                else entry.wrongAttempts++;
                durationTotal += Number(attempt?.duration) || 0;
            });
        });

        entry.avgSeconds = entry.attempts > 0 ? Math.round((durationTotal / entry.attempts) / 1000) : 0;
        entry.completionPercent = entry.questionCount > 0
            ? Math.min(100, Math.round((entry.reviewedCount / entry.questionCount) * 100))
            : 0;
        const gradeInfo = calculateStatsLectureGrade(entry.correctAttempts, entry.attempts);
        entry.grade = gradeInfo.grade;
        entry.gradeClass = gradeInfo.gradeClass;
    });

    return entries.sort((a, b) => {
        if (a.type !== b.type) return a.type === 'pdf' ? -1 : 1;
        return a.order - b.order;
    });
}

function calculateStatsLectureGrade(correctAttempts, totalAttempts) {
    if (!totalAttempts) return { grade: 'N/A', gradeClass: 'grade-none' };
    const ratio = correctAttempts / totalAttempts;
    if (ratio >= 0.97) return { grade: 'A+', gradeClass: '' };
    if (ratio >= 0.93) return { grade: 'A', gradeClass: '' };
    if (ratio >= 0.90) return { grade: 'A-', gradeClass: '' };
    if (ratio >= 0.87) return { grade: 'B+', gradeClass: 'grade-mid' };
    if (ratio >= 0.83) return { grade: 'B', gradeClass: 'grade-mid' };
    if (ratio >= 0.80) return { grade: 'B-', gradeClass: 'grade-mid' };
    if (ratio >= 0.77) return { grade: 'C+', gradeClass: 'grade-mid' };
    if (ratio >= 0.73) return { grade: 'C', gradeClass: 'grade-mid' };
    if (ratio >= 0.70) return { grade: 'C-', gradeClass: 'grade-mid' };
    if (ratio >= 0.60) return { grade: 'D', gradeClass: 'grade-low' };
    return { grade: 'F', gradeClass: 'grade-low' };
}

function getStatsLectureByKey(sourceKey) {
    return statsLectureCache.find(entry => entry.key === sourceKey) || null;
}

function countStatsReviewedQuestions() {
    return questions.reduce((count, _question, idx) => {
        const attempts = Array.isArray(statsData?.[idx]) ? statsData[idx] : [];
        return count + (attempts.length > 0 ? 1 : 0);
    }, 0);
}

function renderLectureStatsDashboard(searchValue = null) {
    const grid = $('lecture-card-grid');
    if (!grid) return;

    statsLectureCache = buildStatsLectureData();

    const totalEl = $('stats-total-lectures');
    const reviewedEl = $('stats-reviewed-questions');
    if (totalEl) totalEl.textContent = statsLectureCache.length;
    if (reviewedEl) reviewedEl.textContent = countStatsReviewedQuestions();

    const input = $('stats-lecture-search');
    const query = String(searchValue ?? input?.value ?? '').trim().toLowerCase();
    const visibleEntries = query
        ? statsLectureCache.filter((entry) => {
            const haystack = [
                entry.rawName,
                entry.title,
                entry.type,
                `${entry.reviewedCount}/${entry.questionCount}`,
                entry.grade
            ].join(' ').toLowerCase();
            return haystack.includes(query);
        })
        : statsLectureCache;

    if (statsLectureCache.length === 0) {
        grid.innerHTML = '<div class="mstat-empty">No loaded lectures or question banks yet.</div>';
        return;
    }
    if (visibleEntries.length === 0) {
        grid.innerHTML = '<div class="mstat-empty">No lectures match that search.</div>';
        return;
    }

    const sorted = sortStatsLectureEntries(visibleEntries, $('stats-lecture-sort')?.value || 'recent');
    grid.innerHTML = sorted.map(renderStatsLectureCard).join('');
}

function getStatsLectureLastAttempt(entry) {
    let latest = 0;
    entry.questionIndices.forEach((idx) => {
        const attempts = Array.isArray(statsData?.[idx]) ? statsData[idx] : [];
        attempts.forEach((attempt) => {
            const at = Number(attempt?.date) || 0;
            if (at > latest) latest = at;
        });
    });
    return latest;
}

function sortStatsLectureEntries(entries, mode) {
    const rank = new Map(entries.map((entry, i) => [entry.key, i]));
    const byRank = (a, b) => rank.get(a.key) - rank.get(b.key);
    const list = [...entries];
    if (mode === 'weakest') {
        const accuracy = entry => (entry.attempts > 0 ? entry.correctAttempts / entry.attempts : 2);
        return list.sort((a, b) => accuracy(a) - accuracy(b) || b.wrongAttempts - a.wrongAttempts || byRank(a, b));
    }
    if (mode === 'progress') {
        return list.sort((a, b) => b.completionPercent - a.completionPercent || b.reviewedCount - a.reviewedCount || byRank(a, b));
    }
    if (mode === 'name') {
        return list.sort((a, b) => a.title.localeCompare(b.title, undefined, { numeric: true, sensitivity: 'base' }) || byRank(a, b));
    }
    const lastAttempt = new Map(list.map(entry => [entry.key, getStatsLectureLastAttempt(entry)]));
    return list.sort((a, b) => lastAttempt.get(b.key) - lastAttempt.get(a.key) || byRank(a, b));
}

function renderStatsLectureCard(entry) {
    const sourceKey = escapeHtml(entry.key);
    const isOpen = entry.key === statsExpandedLectureKey;
    const hasQuestions = entry.questionIndices.length > 0;
    const hasAttempts = entry.attempts > 0;
    const canStudyPdf = entry.type === 'pdf' && !!loadedPdfFiles?.[entry.rawName];
    const canGenerateQuiz = canStudyPdf && typeof showQuizGenerationModal === 'function';
    const accuracy = hasAttempts ? `${Math.round((entry.correctAttempts / entry.attempts) * 100)}%` : '—';
    const typeLabel = entry.type === 'pdf' ? 'PDF' : 'Question bank';
    const countLabel = `${entry.questionCount} question${entry.questionCount === 1 ? '' : 's'}`;
    const pace = hasAttempts
        ? ` · ${entry.attempts} attempt${entry.attempts === 1 ? '' : 's'}${entry.avgSeconds ? ` · ${entry.avgSeconds}s avg` : ''}`
        : '';
    const actionMarkup = entry.type === 'pdf'
        ? `
            <button type="button" class="mstat-lecture-action" data-stats-lecture-action="study-pdf" data-source-key="${sourceKey}" ${canStudyPdf ? '' : 'disabled'}>Study PDF</button>
            <button type="button" class="mstat-lecture-action" data-stats-lecture-action="practice" data-source-key="${sourceKey}" ${hasQuestions ? '' : 'disabled'}>Practice deck</button>
            <button type="button" class="mstat-lecture-action" data-stats-lecture-action="generate-quiz" data-source-key="${sourceKey}" ${canGenerateQuiz ? '' : 'disabled'}>Generate quiz</button>
            <button type="button" class="mstat-lecture-action is-danger" data-stats-lecture-action="reset-stats" data-source-key="${sourceKey}" ${hasAttempts ? '' : 'disabled'}>Reset stats</button>
        `
        : `
            <button type="button" class="mstat-lecture-action" data-stats-lecture-action="practice" data-source-key="${sourceKey}" ${hasQuestions ? '' : 'disabled'}>Practice deck</button>
            <button type="button" class="mstat-lecture-action is-danger" data-stats-lecture-action="reset-stats" data-source-key="${sourceKey}" ${hasAttempts ? '' : 'disabled'}>Reset stats</button>
        `;

    return `
        <article class="mstat-lecture${isOpen ? ' is-open' : ''}" data-source-key="${sourceKey}">
            <button type="button" class="mstat-lecture-row" data-stats-lecture-action="toggle" data-source-key="${sourceKey}" aria-expanded="${isOpen ? 'true' : 'false'}">
                <span class="mstat-lecture-name">
                    <strong title="${escapeHtml(entry.rawName)}">${escapeHtml(entry.title)}</strong>
                    <span>${typeLabel} · ${countLabel}${pace}</span>
                </span>
                <span class="mstat-lecture-stat">${entry.reviewedCount} / ${entry.questionCount}<small>reviewed</small></span>
                <span class="mstat-lecture-stat">${accuracy}<small>accuracy</small></span>
                <span class="mstat-mini-bar" aria-hidden="true"><i style="width: ${entry.completionPercent}%"></i></span>
            </button>
            <div class="mstat-lecture-actions"${isOpen ? '' : ' hidden'}>${actionMarkup}</div>
        </article>
    `;
}

function launchStatsFocusedDeck(indices, label, popupMessage) {
    const uniqueIndices = Array.from(new Set((Array.isArray(indices) ? indices : [])
        .map(idx => Number(idx))
        .filter(idx => Number.isInteger(idx) && idx >= 0 && idx < questions.length)));

    if (uniqueIndices.length === 0) {
        showPopup('No questions are available for that deck.', null, true);
        return false;
    }

    if (isAdaptiveReviewMode) deactivateAdaptiveReviewMode(false);
    if (isFlashcardAdaptiveReviewMode) deactivateFlashcardAdaptiveReviewMode(false);

    closeStatsModal({ target: els.statsModal });
    resetFilter();
    filteredIndices = uniqueIndices;
    if (isShuffled) applySmartShuffle({ showNotice: false });
    isFilterActive = true;
    const firstUnanswered = filteredIndices.findIndex(idx => userAnswers[idx] === undefined);
    currentQIndex = firstUnanswered >= 0 ? firstUnanswered : 0;
    isFlashcardMode = false;
    setModeToggleButtonLabel(false);
    if (els.filterBanner) els.filterBanner.style.display = 'flex';
    const bannerText = getFilterBannerTextEl();
    if (bannerText) bannerText.innerText = label;
    setAdaptiveSnapshotButtonVisible(false);
    setAdaptiveSkipUndoneButtonVisible(false);
    showQuiz();
    setStudyDashboardLastMode(STUDY_ACTION_KEYS.quiz);
    if (popupMessage) showPopup(popupMessage, null, false);
    markSessionStateMutation();
    saveSessionDebounced();
    return true;
}

function practiceStatsLectureDeck(sourceKey) {
    const entry = getStatsLectureByKey(sourceKey);
    if (!entry) {
        showPopup('Lecture stats are no longer available. Reopen the dashboard and try again.', null, true);
        return false;
    }
    return launchStatsFocusedDeck(
        entry.questionIndices,
        `Lecture Deck: ${entry.title} (${entry.questionIndices.length} questions)`,
        `Practice Deck started: ${entry.title}`
    );
}

function studyStatsLecturePdf(sourceKey) {
    const entry = getStatsLectureByKey(sourceKey);
    if (!entry || entry.type !== 'pdf') return false;
    if (!loadedPdfFiles?.[entry.rawName]) {
        showPopupText(`"${entry.rawName}" is not loaded in the PDF viewer.`, null, true);
        return false;
    }
    closeStatsModal({ target: els.statsModal });
    openPdf(entry.rawName);
    showPopupText(`Opened ${entry.title}.`, null, false);
    return true;
}

function openStatsLectureQuizGenerator(sourceKey) {
    const entry = getStatsLectureByKey(sourceKey);
    if (!entry || entry.type !== 'pdf') return false;
    if (!loadedPdfFiles?.[entry.rawName] || typeof showQuizGenerationModal !== 'function') {
        showPopup('Quiz generation is not available for that lecture right now.', null, true);
        return false;
    }
    closeStatsModal({ target: els.statsModal });
    showQuizGenerationModal([{ name: entry.rawName, url: loadedPdfFiles[entry.rawName] }]);
    return true;
}

async function resetStatsLectureProgress(sourceKey) {
    const entry = getStatsLectureByKey(sourceKey);
    if (!entry) return false;
    const indicesWithStats = entry.questionIndices.filter(idx => Array.isArray(statsData?.[idx]) && statsData[idx].length > 0);
    if (indicesWithStats.length === 0) {
        showPopup('No lecture attempts to reset yet.', null, false);
        return false;
    }

    const confirmed = await showThemeConfirm(
        `Reset lecture progress for "${entry.title}"? This clears ${indicesWithStats.length} reviewed question record(s) but keeps XP, streaks, and badges.`,
        {
            title: 'Reset Lecture Stats',
            okText: 'Reset Stats',
            cancelText: 'Keep Stats',
            danger: true
        }
    );
    if (!confirmed) return false;

    indicesWithStats.forEach(idx => { delete statsData[idx]; });
    markSessionStateMutation();
    saveSessionDebounced();
    populateStatsInsights();
    renderLectureStatsDashboard();
    showPopupText(`Reset stats for ${entry.title}.`, null, false);
    return true;
}

function buildHardestQuestionStats(limit = 20) {
    const qStats = [];
    questions.forEach((q, idx) => {
        const history = Array.isArray(statsData?.[idx]) ? statsData[idx] : [];
        if (history.length === 0) return;
        const wrongCount = history.filter(h => !h?.correct).length;
        if (wrongCount <= 0) return;
        const total = history.length;
        const ratio = wrongCount / total;
        qStats.push({
            idx,
            q: q?.q || q?.question || 'Question unavailable',
            ratio,
            wrongCount,
            total,
            sourceFile: q?.sourceFile || q?.src || q?.setName || 'Unknown'
        });
    });

    qStats.sort((a, b) => {
        if (b.ratio !== a.ratio) return b.ratio - a.ratio;
        if (b.wrongCount !== a.wrongCount) return b.wrongCount - a.wrongCount;
        return b.total - a.total;
    });
    return qStats.slice(0, Math.max(0, Number(limit) || 20));
}

function openStatsQuestionFromHardest(idx) {
    const questionIdx = Number(idx);
    if (!Number.isInteger(questionIdx) || questionIdx < 0 || questionIdx >= questions.length) return false;
    closeStatsModal({ target: els.statsModal });
    if (!Array.isArray(filteredIndices) || !filteredIndices.includes(questionIdx)) {
        resetFilter();
    }
    const nextPos = filteredIndices.indexOf(questionIdx);
    currentQIndex = nextPos >= 0 ? nextPos : 0;
    isFlashcardMode = false;
    setModeToggleButtonLabel(false);
    showQuiz();
    return true;
}

function startHardestStatsFocusDeck() {
    const hardest = buildHardestQuestionStats(20);
    return launchStatsFocusedDeck(
        hardest.map(item => item.idx),
        `Hardest Questions Focus: ${hardest.length} questions`,
        `Hardest Questions Focus started: ${hardest.length} questions queued.`
    );
}
window.startHardestStatsFocusDeck = startHardestStatsFocusDeck;

function renderStatsBadgeShowcase() {
    const summary = $('stats-badge-summary');
    const grid = $('stats-badges-grid');
    if (!summary || !grid) return;

    const entries = Object.entries(ACHIEVEMENTS || {}).map(([id, ach], order) => {
        const isUnlocked = !!achievementsData?.[id];
        const unlockTs = getAchievementUnlockTimestamp(id);
        const rarity = ACHIEVEMENT_RARITY_ORDER[ach.rarity] !== undefined ? ach.rarity : 'common';
        const progress = isUnlocked ? null : getAchievementProgress(id);
        const progressRatio = progress ? Math.min(1, progress.current / Math.max(progress.target, 1)) : 0;
        return { id, ach, order, isUnlocked, unlockTs, rarity, progress, progressRatio };
    });
    const unlockedCount = entries.filter(entry => entry.isUnlocked).length;
    const totalCount = entries.length;
    const completionRate = totalCount > 0 ? Math.round((unlockedCount / totalCount) * 100) : 0;

    // Unlocked badges lead, newest first; locked ones follow closest-to-earned first,
    // so the grid opens on what is reachable rather than on the rarest long shots.
    const sorted = [...entries].sort((a, b) => {
        if (a.isUnlocked !== b.isUnlocked) return Number(b.isUnlocked) - Number(a.isUnlocked);
        if (a.isUnlocked) return (b.unlockTs || 0) - (a.unlockTs || 0) || a.order - b.order;
        return b.progressRatio - a.progressRatio
            || ACHIEVEMENT_RARITY_ORDER[a.rarity] - ACHIEVEMENT_RARITY_ORDER[b.rarity]
            || a.order - b.order;
    });
    const nextLocked = sorted.find(entry => !entry.isUnlocked) || null;

    summary.textContent = `${unlockedCount} of ${totalCount} unlocked \u00b7 ${completionRate}% complete`
        + (nextLocked ? ` \u00b7 Next up: ${nextLocked.ach.name}` : '');

    grid.innerHTML = sorted.map((entry) => {
        const rarityLabel = ACHIEVEMENT_RARITY_LABELS[entry.rarity] || 'Standard';
        const desc = getAchievementDescription(entry.id);
        const statusLabel = entry.isUnlocked
            ? (entry.unlockTs ? `Unlocked ${formatAchievementTimestamp(entry.unlockTs, false)}` : 'Unlocked')
            : rarityLabel;
        const progressMarkup = entry.progress
            ? `<div class="mstat-badge-progress" aria-hidden="true"><i style="width: ${Math.round(entry.progressRatio * 100)}%"></i></div>`
            : '';

        return `
            <article class="mstat-badge ${entry.isUnlocked ? 'is-unlocked' : 'is-locked'}" data-rarity="${entry.rarity}">
                <div class="mstat-badge-icon">${achievementGlyph(entry.ach)}</div>
                <div class="mstat-badge-body">
                    <strong>${escapeHtml(entry.ach.name)}</strong>
                    <span>${escapeHtml(desc)}</span>
                    ${progressMarkup}
                    <em class="mstat-badge-status">${escapeHtml(statusLabel)}</em>
                </div>
            </article>
        `;
    }).join('');
}

function populateStatsModal() {
    const hero = $('xp-stats-grid');
    if (!hero) return;

    const currentLevelXP = getXPForLevel(xpData.level);
    const levelSpan = Math.max(1, getXPForLevel(xpData.level + 1) - currentLevelXP);
    const intoLevel = Math.max(0, xpData.totalXP - currentLevelXP);
    const progressPercent = Math.min(100, Math.floor((intoLevel / levelSpan) * 100));
    const tier = getLevelTier(xpData.level);
    const tierLabel = tier.charAt(0).toUpperCase() + tier.slice(1);
    const metrics = [
        [xpData.totalXP.toLocaleString(), 'XP'],
        [sessionXP.toLocaleString(), 'Session XP'],
        [countStatsDayStreak(buildStatsDailyActivity()), 'Day streak']
    ];

    hero.innerHTML = `
        <div class="mstat-level-row">
            <div>
                <div class="mstat-label">Level</div>
                <div class="mstat-level-number">${xpData.level} <span>${escapeHtml(tierLabel)}</span></div>
            </div>
            <div class="mstat-label">${progressPercent}% to next level</div>
        </div>
        <div class="mstat-progress" role="progressbar" aria-label="Progress to next level"
            aria-valuemin="0" aria-valuemax="100" aria-valuenow="${progressPercent}"><i style="width: ${progressPercent}%"></i></div>
        <div class="mstat-metric-line">
            ${metrics.map(([value, label]) => `<div class="mstat-metric"><b>${escapeHtml(String(value))}</b>${escapeHtml(label)}</div>`).join('')}
        </div>
    `;
}

// Hook up stats button
document.addEventListener('DOMContentLoaded', () => {
    const statsBtn = $('stats-menu-btn');
    if (statsBtn) {
        statsBtn.addEventListener('click', openStatsModal);
    }
});









































/* Application bootstrap. app.quizgen.js used to own this call; it moved here when
   generation became a lazily loaded feature, so init() no longer waits for a module
   that may never load. The eager scripts after this one have not evaluated yet, so
   the call is deferred to document-ready rather than made inline. */
window.MemoraRuntime.whenDocumentReady(() => { void init(); });
